import React, { startTransition, useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import {
  Alert,
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Autocomplete,
  Box,
  Card,
  CardContent,
  Checkbox,
  Chip,
  CircularProgress,
  Collapse,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Drawer,
  Divider,
  FormControl,
  FormControlLabel,
  FormGroup,
  FormHelperText,
  GlobalStyles,
  IconButton,
  InputAdornment,
  InputLabel,
  List,
  ListItem,
  ListItemText,
  MenuItem,
  Menu,
  Popover,
  Paper,
  Portal,
  Select,
  Snackbar,
  Stack,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TableSortLabel,
  Tab,
  Tabs,
  TextField,
  Tooltip,
  Typography,
  Badge,
  Button,
  Link as MuiLink,
  useMediaQuery,
  useTheme
} from '@mui/material';
import type { TextFieldProps } from '@mui/material/TextField';
import {
  Add as AddIcon,
  Comment as CommentIcon,
  CommentOutlined as CommentOutlinedIcon,
  Flag as FlagIcon,
  FlagOutlined as FlagOutlinedIcon,
  DataObject as DataObjectIcon,
  DeleteOutline as DeleteOutlineIcon,
  Edit as EditIcon,
  InfoOutlined as InfoOutlinedIcon,
  Link as LinkIcon,
  ExpandMore as ExpandMoreIcon,
  PlayArrow as PlayArrowIcon,
  PostAdd as PostAddIcon,
  SaveOutlined as SaveOutlinedIcon,
  CheckCircleOutline as CheckCircleOutlineIcon,
  ContentCopy as ContentCopyIcon,
  Restore as RestoreIcon,
  Star as StarIcon,
  StarBorder as StarBorderIcon,
  Visibility as VisibilityIcon,
  Close as CloseIcon,
  CloseRounded as CloseRoundedIcon,
  Send as SendIcon
} from '@mui/icons-material';
import {
  ResponsiveContainer,
  BarChart,
  LineChart,
  CartesianGrid,
  XAxis,
  YAxis,
  Tooltip as RechartsTooltip,
  Legend,
  Bar,
  Line
} from 'recharts';
import { useSearchParams, useLocation, useNavigate, Link as RouterLink } from 'react-router-dom';
import { OPEN_RULES_QUICKVIEW_EVENT } from '../components/AppAssistantFab';
import type { Theme } from '@mui/material/styles';
import { alpha } from '@mui/material/styles';
import axios from 'axios';
import { api } from '../contexts/ApiContext';
import { BG_JOB_COMPLETED_EVENT, useBackgroundJobs } from '../contexts/BackgroundJobsContext';
import { resolveReactAppApiBaseUrl } from '../utils/reactAppApiBaseUrl';
import {
  compareCategoryNamesUnknownFirst,
  defaultCategoryIdUnknownFirst,
  isActiveCategoryApiRow,
  sortCategoriesUnknownFirst
} from '../utils/categoryActive';
import { effectiveMerchantName } from '../utils/effectiveMerchant';
import { nextClientToastCode } from '../utils/toastCodes';
import { ToastCodeCaption } from '../components/ToastCodeCaption';
import ProbabilisticTxTab from '../components/ProbabilisticTxTab';
import ProbabilisticRulesCompareModal, {
  probabilisticAgreeChipColor,
  type ProbabilisticComparePayload
} from '../components/ProbabilisticRulesCompareModal';
import { useAuth } from '../contexts/AuthContext';
import {
  AFTER_RULE_SYNC_RELOAD_KEY,
  AFTER_TAG_WIPE_COMPLETION_KEY,
  AFTER_TAG_WIPE_RELOAD_MSG_KEY,
  buildTagWipeCompletionToastFromAggregate,
  type RuleSyncCompletionToastPayload,
  type RuleSyncUiMode
} from '../utils/ruleSyncJobToasts';
import { computeRulesFilterAccordionOpenState } from '../utils/rulesFilterAccordionOpen';
import { categoryChipSx, incomeExpenseChipSx } from '../utils/categoryChipColor';
import { propertyNicknameChipSx } from '../components/PropertyNicknameChip';
import {
  whoChipSx,
  whoInitialsFromCardholder,
  whoTooltipLabel,
  type WhoInitials,
} from '../utils/whoChip';
import { formatVatSuggestionConfidence, vatSuggestionExplanation } from '../utils/vatSuggestions';
import { promisePool } from '../utils/promisePool';
import { getHttpErrorMessageForUser } from '../utils/httpClientErrorMessage';
import { FIT_TABLE_SX, FIT_TABLE_CONTAINER_SX, FitColgroup, equalFitCols } from '../utils/fitTable';
import {
  mergeUnifiedTxCategoryTagChipsByTxId,
  type MergeUnifiedTxCategoryTagChipsOptions
} from '../utils/unifiedTxCategoryTagCache';
import {
  MISSING_SUPPLIER_AUDIT_STORAGE_KEY,
  clearMissingSupplierAuditSnapshot,
  formatMissingSupplierAuditTtlRemaining,
  loadMissingSupplierAuditSnapshot,
  missingSupplierAuditTtlMs,
  saveMissingSupplierAuditSnapshot
} from '../utils/missingSupplierAuditSessionStorage';
import {
  AuditProposalCandidatesModal,
  parseCandidates,
  type AuditProposalCandidatesModalKind,
  type ProposalAuditCandidateRow
} from '../components/AuditProposalCandidatesModal';
import {
  SupplierAliasMappingsDialog,
  formatSupplierAliasLabel,
  truncateMiddle,
  type SupplierAliasApi
} from '../components/SupplierAliasMappingsDialog';
import {
  formatClassificationDraftToastLine,
  formatSupplierDraftToastLine,
  postRuleProposalsV2,
  RULE_DRAFT_TOAST_ORIGINS,
  type PostRuleProposalsV2Options
} from '../utils/ruleProposalGeneration';

/** user scope + optional v2 audit snapshot from missing-* audits modal. */
type RuleProposalTxnGenerateOpts = {
  user_id?: number | null;
  audit?: { auditRunId: string; candidateIds: string[] };
  /** Sample tx IDs from buckets (supplier proposals list match when using ``audit_candidates`` body). */
  auditMatchTransactionIds?: number[];
  /** Similar-transactions modal: pass through to rule-proposals v2 (supplier + classification pairing). */
  similar_tx_modal?: boolean;
  /** Anchor transaction for Similar-TX classification proposal notes. */
  similar_tx_anchor_transaction_id?: number;
  /** Supplier proposal DB ids from the same modal run (classification notes). */
  similar_tx_supplier_proposal_db_ids?: number[];
  /**
   * When true, callers handle Rules tab navigation / filters (e.g. Similar TX runs supplier then classification).
   */
  deferRulesTabNavigation?: boolean;
};

type ClearableSearchTextFieldProps = Omit<TextFieldProps, 'onChange' | 'value'> & {
  value: string;
  onChange: (next: string) => void;
};

/** Outlined text fields with an end “clear” (X) control when non-empty. */
function ClearableSearchTextField(props: ClearableSearchTextFieldProps): React.ReactElement {
  const { value, onChange, InputProps, ...rest } = props;
  const hasVal = (value ?? '').length > 0;
  return (
    <TextField
      {...rest}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      InputProps={{
        ...InputProps,
        endAdornment: hasVal ? (
          <>
            {InputProps?.endAdornment}
            <InputAdornment position="end">
              <IconButton
                size="small"
                aria-label="Clear search text"
                edge="end"
                tabIndex={-1}
                onMouseDown={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                }}
                onClick={(e) => {
                  e.stopPropagation();
                  onChange('');
                }}
              >
                <CloseIcon fontSize="small" />
              </IconButton>
            </InputAdornment>
          </>
        ) : (
          InputProps?.endAdornment
        )
      }}
    />
  );
}

type RenderErrorBoundaryProps = {
  resetKey: string;
  fallback: React.ReactNode;
  children: React.ReactNode;
};

type RenderErrorBoundaryState = {
  hasError: boolean;
};

/** Keeps one failing widget (e.g. Similar TX modal body) from blanking the full page. */
class RenderErrorBoundary extends React.Component<RenderErrorBoundaryProps, RenderErrorBoundaryState> {
  constructor(props: RenderErrorBoundaryProps) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(): RenderErrorBoundaryState {
    return { hasError: true };
  }

  componentDidUpdate(prevProps: RenderErrorBoundaryProps) {
    if (prevProps.resetKey !== this.props.resetKey && this.state.hasError) {
      this.setState({ hasError: false });
    }
  }

  componentDidCatch(error: unknown) {
    // Keep diagnostics in console while preventing a full-page blank screen.
    console.error('RenderErrorBoundary caught render error', error);
  }

  render() {
    if (this.state.hasError) return <>{this.props.fallback}</>;
    return <>{this.props.children}</>;
  }
}

function buildClearableInputProps(
  value: string | number | null | undefined,
  onClear: () => void,
  inputProps?: TextFieldProps['InputProps'],
  clearAriaLabel = 'Clear field'
): TextFieldProps['InputProps'] {
  const hasVal = String(value ?? '').length > 0;
  if (!hasVal) return inputProps;
  return {
    ...inputProps,
    endAdornment: (
      <>
        {inputProps?.endAdornment}
        <InputAdornment position="end">
          <IconButton
            size="small"
            aria-label={clearAriaLabel}
            edge="end"
            tabIndex={-1}
            onMouseDown={(e) => {
              e.preventDefault();
              e.stopPropagation();
            }}
            onClick={(e) => {
              e.stopPropagation();
              onClear();
            }}
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        </InputAdornment>
      </>
    )
  };
}

/**
 * POST a manual Revolut category tag. The DB enforces one tag per (transaction, category);
 * if that tag already exists the API upserts (200). A rare IntegrityError race returns 400 —
 * retry once so VAT/supplier drafts still land on the existing row.
 */
async function postManualRevolutCategoryTagIgnoreDuplicate(body: {
  revolut_transaction_id: number;
  category_id: number;
  source: string;
  vat_rate?: string;
  supplier_name?: string;
}): Promise<void> {
  try {
    await api.post('/api/revolut-transaction-category-tags/', body);
  } catch (err: any) {
    const status = err?.response?.status;
    const msg = String(err?.response?.data?.error || err?.message || '').toLowerCase();
    if (status === 400 && msg.includes('already exists')) {
      // Concurrent create won the race — second call should hit the upsert-existing path.
      await api.post('/api/revolut-transaction-category-tags/', body);
      return;
    }
    throw err;
  }
}

/** Set canonical VAT via tx_vat_tags (does not write legacy category-tag vat_rate). */
async function setManualTxVatRate(txId: number, vatRate: string): Promise<void> {
  await api.post('/api/revolut-transaction-category-tags/set-vat', {
    revolut_transaction_id: txId,
    vat_rate: vatRate,
  });
}

const CLASSIFICATION_VISIBLE_TAB_KEYS = [
  'needs-review',
  'rules',
  'probabilistic',
  'suppliers',
  'supplier-audit',
  'categories',
  'supplier-vat-invoices',
  'vat-conflicts',
  'pipeline',
  'rule-run-history',
  'rerun-commentary'
];

const SUPPLIER_CRUD_INVOICE_FREQUENCIES = ['Weekly', 'Monthly', 'Quarterly', 'Annually', 'Ad hoc'];
const SUPPLIER_CRUD_VAT_STATUSES = ['VAT Registered', 'VAT Exempt', 'VAT Reverse Charge', 'Unknown'];
const SUPPLIER_CRUD_CURRENCIES = ['EUR', 'GBP', 'USD', 'Other'];

/** Row from GET /rule-proposals (atlas virtual or saved pending). */
type RuleProposalRow = {
  id: number | null;
  virtual?: boolean;
  source: string;
  status: string;
  /** 1-based index among eligible virtual atlas rows for this user (from API). */
  atlas_display_seq?: number | null;
  atlas_key?: string | null;
  proposed_name: string;
  category_name?: string;
  supplier_contains?: string | null;
  description_contains?: string | null;
  reference_contains?: string | null;
  account_scope?: string | null;
  is_active?: boolean;
  amount_min?: number | null;
  amount_max?: number | null;
  priority?: number;
  notes?: string | null;
  supplier_name_override?: string | null;
  supplier_name_overrides?: string[] | null;
  revolut_transaction_id?: number | null;
  payload?: Record<string, unknown>;
};

/** Aligns with backend proposal match scope (text, amount bounds, or canonical-only). */
function proposalRowCanListMatches(row: RuleProposalRow): boolean {
  const p = row.payload || {};
  const supplier_contains = String(p.supplier_contains ?? row.supplier_contains ?? '').trim();
  const description_contains = String(p.description_contains ?? row.description_contains ?? '').trim();
  const reference_contains = String(p.reference_contains ?? row.reference_contains ?? '').trim();
  const hasText = !!(supplier_contains || description_contains || reference_contains);
  const hasAmt =
    (p.amount_min != null && String(p.amount_min).trim() !== '') ||
    (p.amount_max != null && String(p.amount_max).trim() !== '') ||
    (row.amount_min != null && String(row.amount_min).trim() !== '') ||
    (row.amount_max != null && String(row.amount_max).trim() !== '');
  const rawOvr = p.supplier_name_overrides ?? row.supplier_name_overrides;
  const hasMulti = Array.isArray(rawOvr) && rawOvr.some((x) => String(x ?? '').trim());
  const hasSingle = String(p.supplier_name_override ?? row.supplier_name_override ?? '').trim() !== '';
  const hasCurrency = String(p.currency ?? (row as any).currency ?? '').trim() !== '';
  const hasScopedAccount = String(p.account_scope ?? row.account_scope ?? '').trim().toLowerCase() === 'ireland'
    || String(p.account_scope ?? row.account_scope ?? '').trim().toLowerCase() === 'uk';
  return hasText || hasAmt || hasMulti || hasSingle || hasCurrency || hasScopedAccount;
}

function supplierRuleCanListMatches(row: SupplierRuleRow): boolean {
  const criteria = supplierRuleCriteriaFromAliasForDisplay(row);
  if (criteria.length > 0) return true;
  return (
    String(row.pattern_raw || '').trim().length > 0 ||
    String(row.description_contains || '').trim().length > 0 ||
    String(row.reference_contains || '').trim().length > 0
  );
}

function vatRuleCanListMatches(row: CategoryVatRuleRow): boolean {
  const ids = row.category_ids || [];
  const hasIds = ids.some((id) => Number.isFinite(Number(id)) && Number(id) > 0);
  return hasIds || Number(row.category_id) > 0;
}

function specialCaseRuleCanListMatches(row: SpecialCaseRuleRow): boolean {
  if (row.category_id != null && Number(row.category_id) > 0) return true;
  if (String(row.supplier_contains || '').trim()) return true;
  if (String(row.description_contains || '').trim()) return true;
  if (String(row.reference_contains || '').trim()) return true;
  if (String(row.current_vat_rate || '').trim()) return true;
  if (String((row as any).currency || '').trim()) return true;
  if (String(row.account_scope || '').trim().toLowerCase() === 'ireland') return true;
  if (String(row.account_scope || '').trim().toLowerCase() === 'uk') return true;
  return false;
}

interface RuleProposalsPayload {
  proposals: RuleProposalRow[];
  count_atlas_virtual?: number;
  count_saved_pending?: number;
  count_saved_rejected?: number;
}

/** Top-level pipeline accordion title suffix: confirmed / proposed / rejected. */
function formatPipelineStageCounts(
  confirmed: number,
  proposed: number,
  rejected: number,
  filtersActive = false
): string {
  const base = `(${confirmed} confirmed, ${proposed} proposed, ${rejected} rejected)`;
  return filtersActive ? `${base} match the filters` : base;
}

type SupplierRuleProposalRow = {
  id: number | null;
  source: string;
  status: string;
  proposed_name: string;
  revolut_transaction_id?: number | null;
  account_scope?: string | null;
  payload?: Record<string, unknown>;
};

interface SupplierRuleProposalsPayload {
  proposals: SupplierRuleProposalRow[];
  count_saved_pending?: number;
  count_saved_rejected?: number;
}

type RuleFireCountsPayload = {
  classification_rule_tx_counts?: Record<string, number>;
  supplier_alias_tx_counts?: Record<string, number>;
  vat_rule_tx_counts?: Record<string, number>;
  special_case_rule_tx_counts?: Record<string, number>;
};

/** Must match `params.limit` for manual-review-items and suggestions. */
const REVIEW_NEEDS_REVIEW_LIMIT = 5000;
/** Small first-page batch for canonical rows to avoid 90s request timeouts. */
const CANONICAL_REVIEW_BATCH_LIMIT = 500;
/** Cap parallel review-* fetches on initial load (avoid 502 under a thundering herd). */
/** Keep modest: parallel bursts can 502 workers if too high. */
const FETCH_REVIEW_DATA_CONCURRENCY = 3;
/** Keep suggestion prefill cheap; load only first chunk asynchronously. */
const SUGGESTIONS_PREFILL_LIMIT = 500;
type RuleRowState = 'proposed' | 'confirmed';
type RuleIdKind = 'classification' | 'supplier' | 'vat' | 'specialCase';
const REVIEW_TAGGED_ITEMS_PAGE_LIMIT = 1000;
const REVIEW_CONFIRMED_ITEMS_PAGE_LIMIT = 1000;
/** Page size for supplier-name backfill (keep small so each request finishes within proxy/client limits). */
const SUPPLIER_BACKFILL_PAGE_LIMIT = 900;
const SUPPLIER_BACKFILL_MIN_PAGE_LIMIT = 250;
/** Longer than default API timeout — heavy Postgres pages can exceed 90s on cold/small instances. */
const HEAVY_RULE_JOB_TIMEOUT_MS = 300000;
/** Conflict simulation on save can be heavy; keep client aligned with long-running rule jobs. */
const CLASSIFICATION_RULE_SAVE_CONFIG = { timeout: HEAVY_RULE_JOB_TIMEOUT_MS } as const;
/** Server cap for POST /bulk-reprocess-transactions */
const BULK_REPROCESS_TRANSACTIONS_MAX = 400;
/** Client table cap for Supplier Audit inferred/catalog sections (detail lives in downloadable JSON). */
const SUPPLIER_CORPUS_AUDIT_UI_ROW_CAP = 150;
/** Max rule rows rendered per accordion in the re-run commentary drawer (rest summarized). */
const RERUN_LOG_RULE_UI_CAP = 150;
/** Page size for re-apply rules on unconfirmed txs (smaller batches avoid 90s request timeouts). */
const REAPPLY_UNCONFIRMED_PAGE_LIMIT = 96;
const REAPPLY_UNCONFIRMED_MIN_PAGE_LIMIT = 28;
const REAPPLY_VAT_UNCONFIRMED_PAGE_LIMIT = 500;
const REAPPLY_VAT_UNCONFIRMED_MIN_PAGE_LIMIT = 200;
const PIPELINE_SYSTEM_HEADING_RULE_PREFIXES = [
  'Cash flow - In',
  'Cash flow - Out',
  'Cash flow - N/A',
  'System Heading - Income',
  'System Heading - Expense'
] as const;
/** Page size for oldest-first proposal sweep across all transactions. */
const PROPOSAL_SWEEP_PAGE_LIMIT = 120;
const PROPOSAL_SWEEP_MIN_PAGE_LIMIT = 40;
const SUPPLIER_PROPOSAL_SWEEP_PAGE_LIMIT = 100;
const SUPPLIER_PROPOSAL_SWEEP_MIN_PAGE_LIMIT = 30;
/** Query params for missing-* audits so the API returns grouped ``proposal_audit_candidates`` for the Rules tab. */
const AUDIT_INCLUDE_PROPOSAL_CANDIDATE_PARAMS = {
  include_proposal_candidates: true,
  max_proposal_candidates: 100,
  max_tx_sample_per_candidate: 50,
} as const;
const VAT_MISSING_SWEEP_PAGE_LIMIT = 120;
const VAT_MISSING_SWEEP_MIN_PAGE_LIMIT = 40;
/** Transient poll failures before abandoning an async rule-sync / tag-wipe job. */
const RULE_SYNC_POLL_MAX_TRANSIENT_FAILURES = 10;

/**
 * POST one sweep page with a long client timeout; on timeout, shrink ``limit`` and retry the same offset.
 */
/** Percent complete for offset-paged sweeps (caps at 99 while has_more). */
function sweepProgressPercent(opts: {
  nextOffset: number;
  hasMore: boolean;
  totalInScope: number | null | undefined;
}): number {
  if (!opts.hasMore) return 100;
  const total = Number(opts.totalInScope);
  if (Number.isFinite(total) && total > 0) {
    return Math.min(99, Math.max(0, Math.round((100 * Math.max(0, opts.nextOffset)) / total)));
  }
  return 0;
}

function formatSweepProgressMessage(opts: {
  label: string;
  percent: number;
  createdCount: number;
  createdNoun?: string;
}): string {
  const noun = opts.createdNoun || 'proposed rules created';
  return `${opts.label}: ${opts.percent}% · ${opts.createdCount.toLocaleString()} ${noun}`;
}


const PIPELINE_NESTED_SECTION_ACCORDION_SX = {
  mt: 1.5,
  border: 1,
  borderColor: 'divider',
  borderRadius: 1,
  '&:before': { display: 'none' },
} as const;

type PipelineNestedStage = 'step1' | 'step2a' | 'step2b' | 'step3' | 'step4' | 'step5';
type PipelineNestedSection = 'confirmed' | 'proposed' | 'rejected';

const EMPTY_PIPELINE_NESTED_SECTIONS: Record<PipelineNestedSection, boolean> = {
  confirmed: false,
  proposed: false,
  rejected: false,
};

const PIPELINE_NESTED_SECTION_DEFAULTS: Record<
  PipelineNestedStage,
  Record<PipelineNestedSection, boolean>
> = {
  step1: { ...EMPTY_PIPELINE_NESTED_SECTIONS },
  step2a: { ...EMPTY_PIPELINE_NESTED_SECTIONS },
  step2b: { ...EMPTY_PIPELINE_NESTED_SECTIONS },
  step3: { ...EMPTY_PIPELINE_NESTED_SECTIONS },
  step4: { ...EMPTY_PIPELINE_NESTED_SECTIONS },
  step5: { ...EMPTY_PIPELINE_NESTED_SECTIONS },
};

function PipelineNestedSectionAccordion(props: {
  title: string;
  count: number;
  expanded: boolean;
  onExpandedChange: (expanded: boolean) => void;
  info?: React.ReactNode;
  children: React.ReactNode;
  first?: boolean;
  /** When Rules tab filters are active, append “match the filters” after the count. */
  filtersActive?: boolean;
}) {
  return (
    <Accordion
      expanded={props.expanded}
      onChange={(_, expanded) => props.onExpandedChange(expanded)}
      disableGutters
      sx={{
        ...PIPELINE_NESTED_SECTION_ACCORDION_SX,
        ...(props.first ? { mt: 0 } : null),
      }}
    >
      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
        <Stack direction="row" spacing={0.75} alignItems="center">
          <Typography variant="subtitle1">{props.title}</Typography>
          {props.info}
          <Typography component="span" variant="body2" color="text.secondary" sx={{ fontWeight: 400 }}>
            ({props.count.toLocaleString()})
            {props.filtersActive ? ' match the filters' : ''}
          </Typography>
        </Stack>
      </AccordionSummary>
      <AccordionDetails>{props.children}</AccordionDetails>
    </Accordion>
  );
}

/** Shared look for Tx Review JSON side-panel section accordions. */
const TX_JSON_VIEWER_SECTION_ACCORDION_SX = {
  '&:before': { display: 'none' },
  border: '1px solid',
  borderColor: 'grey.200',
  borderRadius: '4px !important',
  backgroundColor: 'grey.50',
  boxShadow: 'none'
} as const;

/** Shift+click selects/deselects the contiguous range from the last clicked row to this one. */
function applyCheckboxShiftRangeSelection<T extends string | number>(opts: {
  orderedKeys: readonly T[];
  clickedKey: T;
  checked: boolean;
  shiftKey: boolean;
  previousSelection: readonly T[];
  anchorKey: T | null | undefined;
}): { nextSelection: T[]; nextAnchor: T } {
  const { orderedKeys, clickedKey, checked, shiftKey, previousSelection } = opts;
  const anchorKey = opts.anchorKey ?? null;
  const clickedIdx = orderedKeys.indexOf(clickedKey);
  if (shiftKey && anchorKey != null && clickedIdx >= 0) {
    const anchorIdx = orderedKeys.indexOf(anchorKey);
    if (anchorIdx >= 0) {
      const lo = Math.min(anchorIdx, clickedIdx);
      const hi = Math.max(anchorIdx, clickedIdx);
      const range = orderedKeys.slice(lo, hi + 1);
      if (checked) {
        return {
          nextSelection: Array.from(new Set<T>([...previousSelection, ...range])),
          nextAnchor: anchorKey
        };
      }
      const drop = new Set(range);
      return {
        nextSelection: previousSelection.filter((k) => !drop.has(k)),
        nextAnchor: anchorKey
      };
    }
  }
  const nextSelection = checked
    ? Array.from(new Set<T>([...previousSelection, clickedKey]))
    : previousSelection.filter((k) => k !== clickedKey);
  return { nextSelection, nextAnchor: clickedKey };
}

function readSweepTotalInScope(data: Record<string, unknown>): number | null {
  const n = Number(data.total_in_scope ?? data.transactions_total_in_scope ?? data.total);
  return Number.isFinite(n) && n > 0 ? n : null;
}

async function postSweepPageWithTimeoutRetry(options: {
  url: string;
  body: Record<string, unknown>;
  pageLimit: number;
  minPageLimit: number;
  progressLabel: string;
  onProgress?: (message: string) => void;
}): Promise<{ data: Record<string, unknown>; pageLimit: number }> {
  let pageLimit = options.pageLimit;
  for (;;) {
    try {
      const res = await api.post(
        options.url,
        { ...options.body, limit: pageLimit },
        { timeout: HEAVY_RULE_JOB_TIMEOUT_MS }
      );
      return { data: (res.data && typeof res.data === 'object' ? res.data : {}) as Record<string, unknown>, pageLimit };
    } catch (err: any) {
      if (err?.code === 'ECONNABORTED' && pageLimit > options.minPageLimit) {
        pageLimit = Math.max(options.minPageLimit, Math.floor(pageLimit / 2));
        options.onProgress?.(
          `${options.progressLabel}: timed out — retrying with a smaller page…`
        );
        continue;
      }
      throw err;
    }
  }
}

function isTransientHttpError(err: unknown): boolean {
  const e = err as { code?: string; message?: string; response?: { status?: number } };
  const status = e?.response?.status;
  if (e?.code === 'ECONNABORTED' || e?.code === 'ERR_NETWORK') return true;
  if (!e?.response) return true;
  if (status === 408 || status === 425 || status === 429) return true;
  if (status === 500 || status === 502 || status === 503 || status === 504) return true;
  const msg = String(e?.message || '').toLowerCase();
  return msg.includes('timeout') || msg.includes('network error');
}
const UNIFIED_ROWS_RENDER_BATCH_SIZE = 25;
/** Numeric page sizes; ``-1`` means All (MUI TablePagination convention). */
const UNIFIED_ROWS_PER_PAGE_NUMERIC = [10, 20, 50, 100, 200, 500, 1000] as const;
const UNIFIED_ROWS_PER_PAGE_ALL = -1;
const UNIFIED_ROWS_PER_PAGE_OPTIONS: Array<number | { value: number; label: string }> = [
  ...UNIFIED_ROWS_PER_PAGE_NUMERIC,
  { value: UNIFIED_ROWS_PER_PAGE_ALL, label: 'All' }
];

function isUnifiedRowsPerPageAll(rowsPerPage: number): boolean {
  return rowsPerPage === UNIFIED_ROWS_PER_PAGE_ALL;
}

/** Effective page size for slice/math; All → full loaded row count. */
function unifiedEffectivePageSize(rowsPerPage: number, rowCount: number): number {
  if (isUnifiedRowsPerPageAll(rowsPerPage)) return Math.max(0, rowCount);
  return rowsPerPage;
}

/** Smallest allowed page size that fits `count` rows; All when larger than every numeric option. */
function unifiedRowsPerPageToFitCount(count: number): number {
  const n = Math.max(0, Math.floor(Number(count) || 0));
  if (n <= 0) return 20;
  for (const opt of UNIFIED_ROWS_PER_PAGE_NUMERIC) {
    if (opt >= n) return opt;
  }
  return UNIFIED_ROWS_PER_PAGE_ALL;
}

function parseUnifiedRowsPerPageParam(raw: string | null | undefined): number | null {
  const trimmed = String(raw || '').trim().toLowerCase();
  if (!trimmed) return null;
  if (trimmed === 'all' || trimmed === '-1') return UNIFIED_ROWS_PER_PAGE_ALL;
  const n = Number(trimmed);
  if (
    Number.isFinite(n) &&
    UNIFIED_ROWS_PER_PAGE_NUMERIC.includes(n as (typeof UNIFIED_ROWS_PER_PAGE_NUMERIC)[number])
  ) {
    return n;
  }
  return null;
}

const ALL_TRANSACTIONS_HYDRATE_PAGE_SIZE = 300;
const TX_REVIEW_URL_KEYS = {
  status: 'tx_status',
  supplier: 'tx_supplier',
  text: 'tx_text',
  amountMin: 'tx_amt_min',
  amountMax: 'tx_amt_max',
  /** When ``1``, GET /review-rows returns only rows whose ledger amount is NULL or exactly zero. */
  zeroNullAmount: 'tx_zero_null_amt',
  dateFrom: 'tx_date_from',
  dateTo: 'tx_date_to',
  frequency: 'tx_frequency',
  currency: 'tx_currency',
  heading: 'tx_heading',
  category: 'tx_category',
  account: 'tx_account',
  committed: 'tx_committed',
  hideCommitted: 'tx_hide_committed',
  /** When absent or truthy, GET /review-rows omits uncommitted rows with Applied status "No Tags Missing" (default on). Committed rows use committed / hide-committed filters. */
  excludeConfirmedComplete: 'tx_exclude_confirmed_complete',
  ids: 'tx_ids',
  rowsPerPage: 'tx_rows_per_page',
  revolutState: 'tx_revolut_state',
  hideDeclined: 'tx_hide_declined',
  vat: 'tx_vat',
  /** When ``1``, show only rows with ``flags.is_unclassified_needs_review`` (canonical) or legacy unclassified queue rows. */
  unclassifiedNeedsReview: 'tx_uc_needs_review',
  /** When ``flagged`` / ``1``, show only rows marked look-into-later (`is_flagged`). */
  flagged: 'tx_flagged',
  appliedTag: 'tx_applied_tag',
  /** `no_dup` | `dup` — rows with >1 tag in any type bucket (Cash flow / Supplier / Category / VAT). */
  tagDup: 'tx_tag_dup',
  /** Ledger transaction id — Similar transactions modal deep link (`?similar-tx=<id>`). */
  similarTx: 'similar-tx',
} as const;

/** Must match `WIPE_ALL_CLASSIFICATION_TAGS_CONFIRM` in `transaction_classification_rules.py`. */
const WIPE_ALL_TAGS_CONFIRM = 'WIPE_ALL_REVOLUT_CLASSIFICATION_TAGS';

const RULE_SYNC_SUCCESS_TOAST_CHIP_SX = {
  backgroundColor: 'rgba(255,255,255,0.18)',
  color: '#fff',
  border: '1px solid rgba(255,255,255,0.35)',
  maxWidth: { xs: '100%', sm: 320 },
  height: 'auto',
  '& .MuiChip-label': {
    px: 0.75,
    py: 0.5,
    fontWeight: 600,
    whiteSpace: 'normal',
    textAlign: 'left' as const,
    display: 'block'
  }
} as const;

/** True when the rule name matches pipeline Step 1 cash-flow rules (`Cash flow - In/Out …` or legacy `System Heading - …`). */
function isPipelineSystemHeadingRuleName(name: unknown): boolean {
  const normalized = String(name || '').trim().toLowerCase();
  if (!normalized) return false;
  return PIPELINE_SYSTEM_HEADING_RULE_PREFIXES.some((prefix) => normalized.startsWith(prefix.toLowerCase()));
}

/** Rule display status lettering: confirmed has no prefix; proposed ``P-``; rejected ``R-``. */
type RuleDisplayStatus = 'confirmed' | 'proposed' | 'rejected';

function ruleStatusIdPrefix(status?: RuleDisplayStatus | null): string {
  if (status === 'proposed') return 'P-';
  if (status === 'rejected') return 'R-';
  return '';
}

/**
 * Display id for classification rules: **CF** for pipeline Step 1 cash-flow rules (In/Out bucket; DB `Cash flow - …` or legacy `System Heading - …`),
 * **C** for all other classification rules. Optional status → ``P-C206`` / ``R-CF1`` / ``C206``.
 */
function formatClassificationRulePrefixedId(
  id: number | null | undefined,
  nameHint?: string | null | { name?: string | null },
  status?: RuleDisplayStatus | null
): string {
  const parsed = Number(id);
  const name =
    typeof nameHint === 'string'
      ? nameHint
      : String((nameHint as { name?: string | null } | undefined)?.name ?? '').trim();
  const heading = isPipelineSystemHeadingRuleName(name);
  const typePrefix = heading ? 'CF' : 'C';
  const statusPrefix = ruleStatusIdPrefix(status);
  if (!Number.isFinite(parsed) || parsed <= 0) return `${statusPrefix}${typePrefix}?`;
  return `${statusPrefix}${typePrefix}${Math.trunc(parsed)}`;
}

/** Pipeline Step 1 table; DB rule names are ``Cash flow - In/Out/N/A …`` (legacy ``System Heading - …`` still recognised). */
function formatPipelineCashflowRuleDisplayName(name: unknown): string {
  const s = String(name || '').trim();
  if (!s) return s;
  if (/^cash flow - n\/?a/i.test(s)) return s.replace(/^cash flow - n\/?a/i, 'Cashflow — N/A');
  if (/^cash flow - in/i.test(s)) return s.replace(/^cash flow - in/i, 'Cashflow — In');
  if (/^cash flow - out/i.test(s)) return s.replace(/^cash flow - out/i, 'Cashflow — Out');
  if (/^system heading - income/i.test(s)) return s.replace(/^system heading - income/i, 'Cashflow — In');
  if (/^system heading - expense/i.test(s)) return s.replace(/^system heading - expense/i, 'Cashflow — Out');
  return s;
}

/** Canonical heading stored as ``In`` / ``Out`` (legacy Income/Expense normalised for display). */
function formatCashflowInOutHeadingLabel(categoryName: unknown): string {
  const s = String(categoryName ?? '').trim();
  if (!s) return '—';
  const lower = s.toLowerCase();
  if (lower === 'in' || lower === 'income') return 'In';
  if (lower === 'out' || lower === 'expense') return 'Out';
  if (lower === 'n/a' || lower === 'na') return 'N/A';
  return s;
}

/** Empty cell, em dash, ASCII hyphen — "no heading tag applied yet" (distinct from substantive ``N/A``). */
function cashFlowCellIsPlaceholder(value: unknown): boolean {
  const s = String(value ?? '').trim();
  if (!s) return true;
  return s === '—' || s === '-';
}

function explicitCashFlowInOutFromTaggedValue(taggedCashFlow: unknown): 'In' | 'Out' | null {
  if (cashFlowCellIsPlaceholder(taggedCashFlow)) return null;
  const s = String(taggedCashFlow ?? '').trim();
  const lower = s.toLowerCase();
  if (lower === 'in' || lower === 'income') return 'In';
  if (lower === 'out' || lower === 'expense') return 'Out';
  return null;
}

/**
 * Cash flow column shows **persisted** Pipeline Step 1 heading tags only (``In`` / ``Out`` / ``N/A``).
 * Missing tags render as ``—`` — do not infer from amount (amount still drives which rule applies).
 */
function resolveCashFlowHeadingFromAmountAndTag(amount: number | null | undefined, taggedCashFlow: unknown): string {
  void amount;
  if (cashFlowCellIsPlaceholder(taggedCashFlow)) return '—';
  const s = String(taggedCashFlow ?? '').trim();
  if (s === 'N/A' || s.toLowerCase() === 'n/a') return 'N/A';
  const io = explicitCashFlowInOutFromTaggedValue(taggedCashFlow);
  return io || '—';
}

const UNIFIED_FILTER_CONTROL_ORDER = [
  'text',
  'status',
  'committed',
  'flagged',
  'heading',
  'category',
  'vat',
  'appliedTag',
  'tagDuplicate',
  'account',
  'dateFrom',
  'dateTo',
  'supplier',
  'amountMin',
  'amountMax',
  'zeroNullAmount',
  'frequency',
  'currency',
  'revolutState',
] as const;
type UnifiedFilterControlKey = (typeof UNIFIED_FILTER_CONTROL_ORDER)[number];
const RULES_URL_KEYS = {
  text: 'rules_text',
  id: 'rules_id',
  proposalIds: 'rules_proposal_ids',
  supplier: 'rules_supplier',
  category: 'rules_category',
  frequency: 'rules_frequency',
  status: 'rules_status',
} as const;
/** Right drawer "Rules quickview" on Classification Review (any tab; usually needs-review). */
const RULES_QV_URL_KEYS = {
  open: 'rules_qv',
  filter: 'rules_qv_q',
} as const;

/** Paper width for supplier/VAT/special-case/classification rule editor dialogs (Rules tab). */
const RULES_RULE_EDITOR_DIALOG_PAPER_SX = { width: 'min(96vw, 920px)' } as const;

/**
 * Tx Review right drawers (JSON / Rules quickview / re-run log).
 * Base alignment is the VAT column’s right edge; we pull further left so the panel is
 * actually readable (Category + Supplier band), with a solid min / max vs viewport.
 */
const TX_REVIEW_RIGHT_PANEL_MIN_PX = 520;
const TX_REVIEW_RIGHT_PANEL_MAX_FRAC = 0.72;
/** Extra width left of the VAT column (≈ Category + Supplier + padding). */
const TX_REVIEW_RIGHT_PANEL_EXTRA_LEFT_PX = 340;

function stripRulesQuickviewSearchParams(params: URLSearchParams): void {
  params.delete(RULES_QV_URL_KEYS.open);
  params.delete(RULES_QV_URL_KEYS.filter);
}

/**
 * Rules Quickview `Drawer` uses `ModalProps` with `pointerEvents: 'none'` so clicks reach the
 * page outside the paper. `Select` menus default to portaling to `document.body`, which leaves
 * them under that layer — clicks never reach the menu. Render menus inside the drawer instead.
 */
const QUICKVIEW_DRAWER_SELECT_MENU_PROPS = { disablePortal: true } as const;

/**
 * Tx Reviews filter-bar Selects also portal to `document.body` at the default modal z-index.
 * Quickview / JSON drawers sit at snackbar+15/+20, so their ModalManager layer wins and status
 * (and other) filter menus open but stay unclickable. Keep filter menus above those drawers.
 */
const TX_REVIEW_FILTER_SELECT_MENU_PROPS = {
  sx: { zIndex: (theme: { zIndex: { snackbar: number } }) => theme.zIndex.snackbar + 30 }
} as const;

/** GET /api/qv-feature-requests/ row shape (stored in Postgres for cloud). */
type QvFeatureRequestApiRow = {
  id: number;
  request_key: string;
  chat_name: string;
  detail: string | null;
  /** Verbatim Cursor / user message when stored via POST `cursor_prompt`. */
  cursor_prompt?: string | null;
  status: string;
  status_label: string;
  requested_at: string | null;
  commit_id: string | null;
  sort_order: number;
};

/** API returns requested_at as UTC-naive ISO with a Z suffix (backend convention). */
function formatQvFeatureRequestAt(iso: string | null | undefined): string {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return String(iso);
  return d.toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' });
}

type QuickviewMainTab =
  | 'chat'
  | 'rules'
  | 'categories'
  | 'suppliers'
  | 'other'
  | 'requests';

const SUPPLIERS_URL_KEYS = {
  search: 'sup_search',
  id: 'sup_id',
  name: 'sup_name',
  active: 'sup_active',
  vat: 'sup_vat',
  currency: 'sup_currency',
  hideNonKey: 'sup_hide_non_key',
  source: 'sup_source',
} as const;
const CATEGORIES_URL_KEYS = {
  search: 'cat_search',
  name: 'cat_name',
  active: 'cat_active',
  hideNonKey: 'cat_hide_non_key',
  source: 'cat_source',
} as const;

function readSearchParamList(params: URLSearchParams, key: string): string[] {
  return params
    .getAll(key)
    .map((v) => (v || '').trim())
    .filter((v) => v.length > 0);
}

function writeSearchParamList(params: URLSearchParams, key: string, values: string[]): void {
  params.delete(key);
  values
    .map((v) => (v || '').trim())
    .filter((v) => v.length > 0)
    .forEach((v) => params.append(key, v));
}

const PAYMENT_FREQUENCY_OPTIONS: { value: string; label: string }[] = [
  { value: 'once_off', label: 'Once-off' },
  { value: 'sporadic', label: 'Sporadic' },
  { value: 'weekly', label: 'Weekly' },
  { value: 'monthly', label: 'Monthly' },
  { value: 'quarterly', label: 'Quarterly' },
  { value: 'annually', label: 'Annually' }
];

const VAT_RATE_PICK_OPTIONS = ['23%', '13.5%', '9%', '0%', 'exempt'];
const BULK_TAG_NO_CHANGE = '__bulk_tag_no_change__';
/** Bulk toolbar " - ": delete legacy Revolut category-tag rows in that column's chip bucket for selected rows. */
const BULK_TAG_REMOVE_TAGS = '__bulk_tag_remove_tags__';
const RULE_BULK_NO_CHANGE = '__rule_bulk_no_change__';
const RULE_TEXT_MATCH_OPERATOR_OPTIONS: Array<{ value: string; label: string }> = [
  { value: 'exact', label: 'Is exactly' },
  { value: 'contains', label: 'Contains (whole words)' },
  { value: 'contains_substring', label: 'Contains (substring)' },
  { value: 'not_contains', label: 'Does not contain' }
];
const RULE_ACCOUNT_SCOPE_OPTIONS: Array<{ value: 'both' | 'ireland' | 'uk'; label: string }> = [
  { value: 'both', label: 'Both' },
  { value: 'ireland', label: 'Revolut Ire' },
  { value: 'uk', label: 'Revolut UK' }
];

/**
 * Type letters for rule display IDs (UI/URL).
 * Format: ``{status?}{type}{id}`` — confirmed bare (``S1``, ``C1``, ``CF1``); proposed ``P-``; rejected ``R-``.
 * Cashflow classification uses **CF** (via name helper); legacy filter alias **H** still parses.
 * Atlas virtual proposals: ``P-A{seq}`` (legacy ``A{seq}`` still parses).
 */
const RULE_ID_PREFIX: Record<RuleIdKind, string> = {
  classification: 'C',
  supplier: 'S',
  vat: 'V',
  specialCase: 'SC'
};

type ParsedRulesIdFilter =
  | { hasFilter: false; kind: null; id: null; classificationProposalId: null; classificationAtlasSeq: null }
  | {
      hasFilter: true;
      kind: RuleIdKind | null;
      id: number | null;
      /** Saved classification proposal DB id (from Rules ID filter `P-C123` / legacy `P123`). */
      classificationProposalId: number | null;
      /** Virtual atlas row sequence from GET /rule-proposals (`P-A4` / legacy `A4`). */
      classificationAtlasSeq: number | null;
      /**
       * ``P-S`` / ``R-V`` / ``R-SC`` typed into ``rules_id`` (proposal lettering).
       * Confirmed catalog rows never match; use ``rules_proposal_ids`` for proposal lists.
       */
      statusPrefixedNonClassification?: boolean;
    };

/** Comma/whitespace-separated proposal selectors: `P-C154` / `P-S12` / `P-A3` (legacy `P154` / `S12` / `A3`). */
type RulesTabProposalIdFilter = {
  supplierProposalIds: Set<number>;
  classificationProposalIds: Set<number>;
  atlasSeqs: Set<number>;
};

function proposalRowDisplayStatus(row: { status?: string | null }): RuleDisplayStatus {
  const s = String(row.status || '').trim().toLowerCase();
  if (s === 'rejected') return 'rejected';
  if (s === 'pending' || s === 'proposed') return 'proposed';
  return 'proposed';
}

/**
 * Stable label for classification proposals: ``P-C###`` / ``R-C###`` (saved) or ``P-A###`` (atlas).
 * Legacy bare ``P###`` / ``A###`` still parse in filters.
 */
function classificationProposalStableIdLabel(row: RuleProposalRow): string {
  const status = proposalRowDisplayStatus(row);
  const statusPrefix = ruleStatusIdPrefix(status);
  const pid = row.id != null && Number(row.id) > 0 ? Math.trunc(Number(row.id)) : null;
  if (pid != null) return `${statusPrefix}C${pid}`;
  if (row.virtual) {
    const seqRaw = row.atlas_display_seq != null ? Number(row.atlas_display_seq) : NaN;
    if (Number.isFinite(seqRaw) && seqRaw > 0) return `${statusPrefix}A${Math.trunc(seqRaw)}`;
    const k = String(row.atlas_key || '').trim();
    if (k) return k.length > 36 ? `Atlas:${k.slice(0, 33)}…` : `Atlas:${k}`;
    return 'Atlas';
  }
  return '—';
}

/** Supplier proposal display id: ``P-S###`` / ``R-S###``. */
function formatSupplierProposalPrefixedId(
  id: number | null | undefined,
  status?: RuleDisplayStatus | null
): string {
  return formatPrefixedRuleId('supplier', id, status ?? 'proposed');
}

/** Numeric sort key for the unified classification table ID column (confirmed rule id, P id, or atlas seq). */
function proposalNumericSortId(p: RuleProposalRow): number {
  const pid = p.id != null && Number(p.id) > 0 ? Math.trunc(Number(p.id)) : null;
  if (pid != null) return pid;
  const seq = p.atlas_display_seq != null ? Number(p.atlas_display_seq) : NaN;
  if (p.virtual && Number.isFinite(seq) && seq > 0) return Math.trunc(seq);
  return 0;
}

function formatPrefixedRuleId(
  kind: RuleIdKind,
  id: number | null | undefined,
  status?: RuleDisplayStatus | null
): string {
  const parsed = Number(id);
  const statusPrefix = ruleStatusIdPrefix(status);
  const typePrefix = RULE_ID_PREFIX[kind];
  if (!Number.isFinite(parsed) || parsed <= 0) return `${statusPrefix}${typePrefix}?`;
  return `${statusPrefix}${typePrefix}${Math.trunc(parsed)}`;
}

function formatClassificationRuleToastLabel(
  rule: Partial<ClassificationRule> | null | undefined,
  fallbackName?: string | null
): string {
  const namePart = String((rule as any)?.name || fallbackName || '').trim();
  const idPart = formatClassificationRulePrefixedId(Number((rule as any)?.id || 0), namePart || rule);
  return namePart ? `${idPart} — ${namePart}` : idPart;
}

type FriendlyToastDetailPair = { tag: string; value: string };

function isToastTagOrEntityLabel(raw: string): boolean {
  const normalized = String(raw || '')
    .toLowerCase()
    .replace(/\s+/g, ' ')
    .trim();
  if (!normalized) return false;

  const base = normalized.replace(/[()[\]{}.,:;!?]/g, '').trim();
  const known = new Set([
    'expense/income',
    'income/expense',
    'supplier',
    'category',
    'vat',
    'vat rate',
    'special case',
    'merchant',
    'counterparty',
    'description',
    'reference',
    'currency',
    'bank account',
    'account',
    'rule',
    'entity',
  ]);
  if (known.has(base)) return true;

  // Combined tag labels, e.g. "Cash flow + Supplier + Category + VAT Rate"
  if (base.includes('+')) {
    const parts = base
      .split('+')
      .map((p) => p.trim())
      .filter(Boolean);
    if (
      parts.length > 1 &&
      parts.every((p) => known.has(p))
    ) {
      return true;
    }
  }

  return false;
}

function parseFriendlyToastMessage(raw: string): {
  insight: string;
  detailPairs: FriendlyToastDetailPair[];
  detailTokens: string[];
} {
  const text = String(raw || '')
    .replace(/\s+/g, ' ')
    .trim();
  if (!text) return { insight: '', detailPairs: [], detailTokens: [] };

  let insight = text;
  let detailRaw = '';

  const bracketWrapped = text.match(/^(.*?)[\s:;.-]*\((.+)\)\s*$/);
  if (bracketWrapped) {
    insight = String(bracketWrapped[1] || '')
      .trim()
      .replace(/[.:;\-]\s*$/, '')
      .trim();
    detailRaw = String(bracketWrapped[2] || '').trim();
  } else {
    const delimiters = [' — ', ' - ', ': '];
    let pickedIdx = -1;
    let picked = '';
    delimiters.forEach((d) => {
      const i = text.indexOf(d);
      if (i > 6 && (pickedIdx === -1 || i < pickedIdx)) {
        pickedIdx = i;
        picked = d;
      }
    });
    if (pickedIdx > -1 && picked) {
      insight = text
        .slice(0, pickedIdx)
        .trim()
        .replace(/[.:;\-]\s*$/, '')
        .trim();
      detailRaw = text.slice(pickedIdx + picked.length).trim();
    }
  }

  const detailPairs: FriendlyToastDetailPair[] = [];
  const detailTokens: string[] = [];
  if (detailRaw) {
    const normalized = detailRaw
      .replace(/\s*·\s*/g, ' | ')
      .replace(/\s+-\s+/g, ' | ')
      .replace(/\s+\/\s+/g, '/')
      .trim();
    const parts = normalized
      .split(/\s*\|\s*/)
      .map((p) => p.trim())
      .filter(Boolean);
    parts.forEach((part) => {
      const pair = part.match(/^([A-Za-z][A-Za-z0-9_\/ ]{1,48}?):\s*(.+)$/);
      if (pair) {
        detailPairs.push({ tag: pair[1].trim(), value: pair[2].trim() });
      } else {
        detailTokens.push(part);
      }
    });
  }

  return {
    insight: insight || text,
    detailPairs,
    detailTokens
  };
}

/** Headline + detail for inline progress banner (rule sync / batched jobs). */
function operationProgressPertinentDisplay(raw: string): { headline: string; detail: string } {
  const fb = String(raw || '').trim();
  // Rule-sync jobs now publish compact "Cashflow 42% · Overall 7%" lines — show as-is.
  if (/\bOverall\s+\d{1,3}%\b/i.test(fb) || /\b\d{1,3}%\s*·\s*Overall\b/i.test(fb)) {
    return { headline: fb || 'Working…', detail: '' };
  }
  const parsed = parseFriendlyToastMessage(fb);
  const detailParts: string[] = [];
  for (const p of parsed.detailPairs) {
    detailParts.push(`${p.tag}: ${p.value}`);
  }
  for (const t of parsed.detailTokens) {
    detailParts.push(t);
  }
  const detailJoined = detailParts.join(' · ').trim();
  const headline = (parsed.insight || '').trim();
  if (!detailJoined) {
    return { headline: fb || 'Working…', detail: '' };
  }
  return {
    headline: headline || 'Progress',
    detail: detailJoined
  };
}

/** Hard-refresh backlog toast (Tx Review load) — coverage-style, multi-line. */
function buildTxReviewHardRefreshToast(
  counts: Pick<
    TxReviewPipelineCounts,
    | 'uncommitted_still_need_tags'
    | 'uncommitted_still_need_tags_revolut_complete_or_blank'
    | 'uncommitted_still_need_tags_revolut_non_complete'
    | 'uncommitted_still_need_tags_by_reason'
    | 'uncommitted_missing_cash_flow_heading_tag'
    | 'uncommitted_missing_supplier'
    | 'uncommitted_missing_category'
    | 'uncommitted_missing_vat_rate'
    | 'uncommitted_missing_all_tags'
    | 'uncommitted_unclassified_needs_review'
    | 'uncommitted_vat_conflict'
  >
): string | null {
  const needTags = Math.max(
    0,
    Number(
      counts.uncommitted_still_need_tags ??
        Math.max(
          0,
          Number(counts.uncommitted_missing_all_tags || 0) +
            Number(counts.uncommitted_unclassified_needs_review || 0) +
            Number(counts.uncommitted_vat_conflict || 0)
        )
    )
  );
  if (needTags <= 0) return null;

  const nonComplete = Math.max(0, Number(counts.uncommitted_still_need_tags_revolut_non_complete || 0));
  const completeOrBlank = Math.max(
    0,
    Number(
      counts.uncommitted_still_need_tags_revolut_complete_or_blank ??
        Math.max(0, needTags - nonComplete)
    )
  );
  const reasonRows = Array.isArray(counts.uncommitted_still_need_tags_by_reason)
    ? counts.uncommitted_still_need_tags_by_reason
        .map((r) => ({
          label: String(r?.label || '').trim(),
          count: Math.max(0, Number(r?.count || 0))
        }))
        .filter((r) => r.label && r.count > 0)
    : [];
  const reasonSum = reasonRows.reduce((acc, r) => acc + r.count, 0);
  const lines = [
    `Tx Review: ${needTags.toLocaleString()} uncommitted TXs still need tags.`,
    `Revolut status among those: ${completeOrBlank.toLocaleString()} Complete (or blank), ${nonComplete.toLocaleString()} Non-Complete.`,
    ''
  ];
  if (reasonRows.length > 0 && reasonSum === needTags) {
    lines.push('By Applied status (exclusive; sums to the total above):');
    for (const r of reasonRows) {
      lines.push(`- ${r.count.toLocaleString()} ${r.label}`);
    }
  } else {
    // Fallback for older API payloads: overlapping dimension counts (a TX can miss more than one).
    const needCashFlow = Math.max(0, Number(counts.uncommitted_missing_cash_flow_heading_tag || 0));
    const needSupplier = Math.max(0, Number(counts.uncommitted_missing_supplier || 0));
    const needCategory = Math.max(0, Number(counts.uncommitted_missing_category || 0));
    const needVat = Math.max(0, Number(counts.uncommitted_missing_vat_rate || 0));
    lines.push('Missing tag dimensions (overlapping; a TX can miss more than one):');
    lines.push(`- ${needCashFlow.toLocaleString()} missing Cash Flow`);
    lines.push(`- ${needSupplier.toLocaleString()} missing Supplier`);
    lines.push(`- ${needCategory.toLocaleString()} missing Category`);
    lines.push(`- ${needVat.toLocaleString()} missing VAT Rate`);
  }
  return lines.join('\n');
}

/** Multi-line toast for classification proposal sweeps (avoids FriendlyToastMessage bracket parsing). */
function buildClassificationProposalSweepToast(opts: {
  title: string;
  created: number;
  scanned: number;
  fallbackCreated?: number;
  skips: Array<{ label: string; count: number }>;
  footer?: string;
}): RuleSyncCompletionToastPayload {
  const segments: string[] = [
    `Created ${opts.created.toLocaleString()} new proposal${opts.created === 1 ? '' : 's'} after scanning ${opts.scanned.toLocaleString()} ledger row${opts.scanned === 1 ? '' : 's'}.`
  ];
  if (opts.fallbackCreated != null && Number(opts.fallbackCreated) > 0) {
    segments.push(
      `Catch-all category fallback used: ${opts.fallbackCreated.toLocaleString()}`
    );
  }
  const nonzeroSkips = opts.skips.filter((s) => Number(s.count) > 0);
  if (nonzeroSkips.length > 0) {
    segments.push('Skipped:');
    for (const s of nonzeroSkips) {
      segments.push(`· ${s.label}: ${Number(s.count).toLocaleString()}`);
    }
  } else if (opts.skips.length > 0 && opts.created === 0) {
    segments.push('No skip reasons recorded (and nothing was created).');
  }
  if (opts.footer) {
    segments.push(opts.footer);
  }
  return { title: opts.title, segments };
}

function FriendlyToastMessage(props: { message: string; filled?: boolean }): React.ReactElement {
  const { message, filled = true } = props;
  const parsed = parseFriendlyToastMessage(message);
  const pillSx = filled
    ? {
        backgroundColor: 'rgba(255,255,255,0.18)',
        color: '#fff',
        border: '1px solid rgba(255,255,255,0.35)',
        '& .MuiChip-label': { px: 0.75, fontWeight: 700 }
      }
    : {
        backgroundColor: 'rgba(37,99,235,0.08)',
        color: 'text.primary',
        border: '1px solid rgba(37,99,235,0.3)',
        '& .MuiChip-label': { px: 0.75, fontWeight: 700 }
      };

  return (
    <Stack spacing={0.45}>
      <Typography variant="body2" component="div" sx={{ fontWeight: 600 }}>
        {parsed.insight}
      </Typography>
      {parsed.detailPairs.length > 0 || parsed.detailTokens.length > 0 ? (
        <Stack direction="row" spacing={0.6} alignItems="center" flexWrap="wrap" useFlexGap>
          <Typography variant="caption" component="span" sx={{ opacity: 0.95 }}>
            [
          </Typography>
          {parsed.detailPairs.length > 0
            ? parsed.detailPairs.map((item, idx) => (
                <React.Fragment key={`${item.tag}-${idx}`}>
                  {isToastTagOrEntityLabel(item.tag) ? (
                    <Chip size="small" label={item.tag} sx={pillSx} />
                  ) : (
                    <Typography variant="caption" component="span" sx={{ fontWeight: 700 }}>
                      {item.tag}
                    </Typography>
                  )}
                  <Typography variant="caption" component="span">
                    {item.value}
                  </Typography>
                  {idx < parsed.detailPairs.length - 1 ? (
                    <Typography variant="caption" component="span" sx={{ opacity: 0.9 }}>
                      |
                    </Typography>
                  ) : null}
                </React.Fragment>
              ))
            : parsed.detailTokens.map((token, idx) => (
                <React.Fragment key={`${token}-${idx}`}>
                  {isToastTagOrEntityLabel(token) ? (
                    <Chip size="small" label={token} sx={pillSx} />
                  ) : (
                    <Typography variant="caption" component="span">
                      {token}
                    </Typography>
                  )}
                  {idx < parsed.detailTokens.length - 1 ? (
                    <Typography variant="caption" component="span" sx={{ opacity: 0.9 }}>
                      |
                    </Typography>
                  ) : null}
                </React.Fragment>
              ))}
          <Typography variant="caption" component="span" sx={{ opacity: 0.95 }}>
            ]
          </Typography>
        </Stack>
      ) : null}
    </Stack>
  );
}

type RerunSummaryLine = { label: string; detail: string };

type RerunComparisonReadout = {
  pipeline: RerunSummaryLine[];
  diagnostics: RerunSummaryLine[] | null;
  notes: Array<{ severity: 'info' | 'warning'; text: string }>;
};

/** Append-only entries for the rule re-run commentary drawer (structured; not plain lines). */
type UnifiedRerunLogBlock =
  | { kind: 'request'; txId: number; timeLabel: string; pipelineLine: string }
  | {
      kind: 'run';
      txId: number;
      responseTimeLabel: string;
      d: Record<string, any>;
      diag: Record<string, any> | null;
      diagnosticSentence: string;
      notificationLines: RerunSummaryLine[];
      comparison: RerunComparisonReadout;
    }
  | { kind: 'note'; variant: 'error' | 'info' | 'warning'; text: string; timeLabel?: string };

function buildRerunPipelineResponseLines(d: Record<string, any>): RerunSummaryLine[] {
  const lines: RerunSummaryLine[] = [];
  if (d.supplier_refresh) {
    const s = d.supplier_refresh;
    const updated = Number(s.tags_updated ?? 0);
    const scanned = Number(s.tags_scanned ?? 0);
    if (updated > 0) {
      lines.push({
        label: 'Supplier names on tags',
        detail: `${updated} tag row${updated === 1 ? '' : 's'} updated${scanned > 0 ? ` · ${scanned} scanned` : ''}.`
      });
    } else if (scanned > 0) {
      lines.push({
        label: 'Supplier names on tags',
        detail: `No changes needed · ${scanned} tag row${scanned === 1 ? '' : 's'} checked.`
      });
    }
  }
  if (d.classification) {
    const c = d.classification;
    const created = Number(c.created_tags ?? 0);
    const removed = Number(c.rule_tags_deleted ?? 0);
    if (typeof c.transactions_scanned === 'number') {
      if (c.strip_all_classification_tags) {
        const cleared = Number(c.all_tags_deleted ?? removed ?? 0);
        lines.push({
          label: 'Classification rules',
          detail: `Removed all ${cleared} tag(s) on this transaction, then re-ran rules · ${created} tag(s) created.`
        });
      } else if (created > 0 || removed > 0) {
        lines.push({
          label: 'Classification rules',
          detail: `${created} tag row(s) inserted · ${removed} rule tag row(s) removed (table rows, not UI columns).`
        });
      } else {
        lines.push({
          label: 'Classification rules',
          detail: 'No new match — categories from rules unchanged.'
        });
      }
    } else if (c.message) {
      lines.push({ label: 'Classification', detail: `${String(c.message).replace(/\.$/, '')}.` });
    }
  }
  if (d.vat_reapply) {
    const v = d.vat_reapply;
    const updated = Number(v.tags_updated ?? 0);
    const scanned = Number(v.tags_scanned ?? 0);
    if (updated > 0) {
      lines.push({
        label: 'VAT',
        detail: `${updated} tag row${updated === 1 ? '' : 's'} updated with new VAT rates${scanned > 0 ? ` · ${scanned} scanned` : ''}.`
      });
    } else if (scanned > 0) {
      lines.push({
        label: 'VAT',
        detail: `Rates unchanged · ${scanned} tag row${scanned === 1 ? '' : 's'} checked.`
      });
    }
  }
  if (d.special_case_reapply) {
    const s = d.special_case_reapply;
    const updated = Number(s.tags_updated ?? 0);
    const scanned = Number(s.tags_scanned ?? 0);
    if (updated > 0) {
      lines.push({
        label: 'Special-case rules',
        detail: `${updated} tag row${updated === 1 ? '' : 's'} updated${scanned > 0 ? ` · ${scanned} scanned` : ''}.`
      });
    } else if (scanned > 0) {
      lines.push({
        label: 'Special-case rules',
        detail: `No changes · ${scanned} row${scanned === 1 ? '' : 's'} checked.`
      });
    }
  }
  return lines;
}

function buildRerunDiagnosticLines(diagRaw: any): RerunSummaryLine[] {
  const diag = (diagRaw || {}) as Record<string, any>;
  const matchedCls = Array.isArray(diag.matched_classification_rules) ? diag.matched_classification_rules.length : 0;
  const matchedHead = Array.isArray(diag.matched_heading_rules) ? diag.matched_heading_rules.length : 0;
  const counts = (diag.final_tag_counts || {}) as Record<string, any>;
  const nonHeading = Number(counts.non_heading || 0);
  const unclassified = Number(counts.unclassified || 0);
  const vatRows = Array.isArray(diag.expected_vat_by_non_heading_tag) ? diag.expected_vat_by_non_heading_tag : [];
  const vatExpected = vatRows.filter((x) => String(x?.expected_vat_rate || '').trim()).length;
  const vatPresent = vatRows.filter((x) => String(x?.tag_vat_rate || '').trim()).length;
  const skipReason = String(diag.classification_skip_reason || '').trim();
  return [
    {
      label: 'Matched rules (diagnostics)',
      detail: `${matchedHead} cashflow · ${matchedCls} granular`
    },
    {
      label: 'Final tag counts',
      detail: `${nonHeading} granular · ${unclassified} unclassified`
    },
    {
      label: 'VAT on granular tags',
      detail: `Expected rate populated: ${vatExpected} · Present on tag: ${vatPresent}`
    },
    ...(skipReason
      ? [
          {
            label: 'Classification write skip reason',
            detail: skipReason
          }
        ]
      : [])
  ];
}

function summarizeSingleTxDiagnosticText(diagRaw: any): string {
  return buildRerunDiagnosticLines(diagRaw)
    .map((l) => `${l.label}: ${l.detail}`)
    .join(' ');
}

function buildRerunNotificationLines(
  txId: number,
  d: Record<string, any>,
  diag: Record<string, any> | null | undefined
): RerunSummaryLine[] {
  const out: RerunSummaryLine[] = [
    { label: 'Transaction', detail: `#${txId}` },
    { label: 'Status', detail: 'Re-run finished' }
  ];
  out.push(...buildRerunPipelineResponseLines(d));
  if (diag && typeof diag === 'object') {
    out.push(...buildRerunDiagnosticLines(diag));
  }
  return out;
}

/** Local structured readout comparing pipeline counters vs tx diagnostics (no external LLM). */
function buildRerunComparisonReadout(
  txId: number,
  d: Record<string, any>,
  diag: Record<string, any> | null | undefined
): RerunComparisonReadout {
  const head = (d?.heading_stage || null) as Record<string, any> | null;
  const cls = (d?.classification || null) as Record<string, any> | null;
  const headEv = head ? Number(head.rules_evaluated ?? 0) : null;
  const clsEv = cls ? Number(cls.rules_evaluated ?? 0) : null;
  const headCreated =
    head && typeof head.transactions_scanned === 'number' ? Number(head.created_tags ?? 0) : null;
  const clsCreated =
    cls && typeof cls.transactions_scanned === 'number' ? Number(cls.created_tags ?? 0) : null;
  const clsGran = cls ? Number(cls.created_non_heading_tags ?? 0) : null;
  const clsHeadTags = cls ? Number(cls.created_heading_tags ?? 0) : null;

  const pipeline: RerunSummaryLine[] = [
    { label: 'Applies to', detail: `Transaction #${txId}` },
    {
      label: 'Cashflow stage',
      detail:
        headCreated != null && headEv != null
          ? `Created ${headCreated} tag(s) · ${headEv} rule(s) evaluated`
          : 'Not reported in this response'
    },
    {
      label: 'Classification pass',
      detail:
        clsCreated != null && clsEv != null
          ? `${clsEv} rule(s) evaluated · ${clsCreated} tag(s) written (cashflow ${clsHeadTags ?? 'n/a'}, granular ${clsGran ?? 'n/a'})`
          : 'Not reported in this response'
    }
  ];

  const notes: Array<{ severity: 'info' | 'warning'; text: string }> = [];
  let diagnostics: RerunSummaryLine[] | null = null;

  if (diag && typeof diag === 'object') {
    const mh = Array.isArray(diag.matched_heading_rules) ? diag.matched_heading_rules.length : 0;
    const mc = Array.isArray(diag.matched_classification_rules) ? diag.matched_classification_rules.length : 0;
    const nmc = Array.isArray(diag.non_matching_classification_rules)
      ? diag.non_matching_classification_rules.length
      : 0;
    const counts = (diag.final_tag_counts || {}) as Record<string, any>;
    const nonHeading = Number(counts.non_heading ?? 0);
    const unclassified = Number(counts.unclassified ?? 0);

    diagnostics = [
      { label: 'Matched cashflow rules', detail: String(mh) },
      { label: 'Matched granular rules', detail: String(mc) },
      { label: 'Granular rules tested but not applicable', detail: String(nmc) },
      { label: 'End-state tags', detail: `${nonHeading} granular · ${unclassified} unclassified` }
    ];

    if ((clsCreated ?? 0) > 0 && mc === 0) {
      notes.push({
        severity: 'warning',
        text:
          'Those views can diverge: the stage counts tag writes from the classification pass, while “matched” only lists granular rules that fully satisfied a category assignment—inspect the rule lists below for the exact rule IDs that interacted with this transaction.'
      });
    }
    if (mc > 0 && nonHeading === 0) {
      notes.push({
        severity: 'warning',
        text:
          'Granular rules matched while granular counts read zero—if the grid still looks off after refresh, inspect tags JSON for cashflow-only vs granular placement.'
      });
    }
    if (nonHeading === 0 && unclassified > 0) {
      notes.push({
        severity: 'warning',
        text:
          'Only unclassified tags remain—the review row should still flag missing category until a granular rule applies.'
      });
    }
  } else {
    notes.push({
      severity: 'info',
      text:
        'Diagnostics were omitted from the response, so matched-rule detail and VAT expectations are unavailable in this drawer.'
    });
  }

  return { pipeline, diagnostics, notes };
}

function RerunSummaryKeyValueList(props: {
  lines: RerunSummaryLine[];
  variant?: 'default' | 'toastFilled';
}): React.ReactElement {
  const { lines, variant = 'default' } = props;
  const filled = variant === 'toastFilled';
  if (lines.length === 0) {
    return (
      <Typography variant="body2" color={filled ? 'inherit' : 'text.secondary'} sx={{ opacity: filled ? 0.92 : 1 }}>
        No details.
      </Typography>
    );
  }
  return (
    <Stack spacing={0.85} sx={{ width: '100%', mt: filled ? 0.25 : 0 }}>
      {lines.map((row, i) => (
        <Box
          key={i}
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', sm: 'minmax(8.5rem, 11rem) 1fr' },
            columnGap: 1.25,
            rowGap: 0.1,
            alignItems: 'baseline'
          }}
        >
          <Typography
            component="div"
            variant="caption"
            sx={{
              fontWeight: 700,
              color: filled ? 'rgba(255,255,255,0.92)' : 'text.secondary'
            }}
          >
            {row.label}
          </Typography>
          <Typography
            component="div"
            variant="body2"
            sx={{
              color: filled ? 'inherit' : 'text.primary',
              lineHeight: 1.45,
              wordBreak: 'break-word',
              overflowWrap: 'anywhere'
            }}
          >
            {row.detail}
          </Typography>
        </Box>
      ))}
    </Stack>
  );
}

function RerunComparisonReadoutBody(props: { comparison: RerunComparisonReadout }): React.ReactElement {
  const { comparison } = props;
  return (
    <Stack spacing={1.25} sx={{ width: '100%', mt: 0.25 }}>
      <Box>
        <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 700, display: 'block', mb: 0.35 }}>
          Pipeline snapshot (API payload)
        </Typography>
        <RerunSummaryKeyValueList lines={comparison.pipeline} />
      </Box>
      {comparison.diagnostics && comparison.diagnostics.length > 0 ? (
        <Box>
          <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 700, display: 'block', mb: 0.35 }}>
            Diagnostic counts
          </Typography>
          <RerunSummaryKeyValueList lines={comparison.diagnostics} />
        </Box>
      ) : null}
      {comparison.notes.length > 0 ? (
        <Stack component="ul" spacing={0.65} sx={{ m: 0, pl: 2.25, listStyleType: 'disc' }}>
          {comparison.notes.map((n, i) => (
            <Typography
              key={i}
              component="li"
              variant="body2"
              sx={{
                display: 'list-item',
                color: n.severity === 'warning' ? 'warning.dark' : 'text.secondary',
                pl: 0.25
              }}
            >
              {n.text}
            </Typography>
          ))}
        </Stack>
      ) : null}
    </Stack>
  );
}

function fmtDiagRuleLine(rid: unknown, name: unknown): string {
  const n = Number(rid);
  const nm = String(name || '').trim();
  return `${formatClassificationRulePrefixedId(Number.isFinite(n) && n > 0 ? n : null, nm)}${nm ? ` — ${nm}` : ''}`;
}

function UnifiedRerunLogRuleAccordion(props: {
  title: string;
  rows: unknown[];
  matched: boolean;
  defaultExpanded?: boolean;
}): React.ReactElement | null {
  const { title, rows, matched, defaultExpanded } = props;
  if (!Array.isArray(rows) || rows.length === 0) return null;
  const cap = Math.min(rows.length, RERUN_LOG_RULE_UI_CAP);
  const omitted = rows.length - cap;
  return (
    <Accordion disableGutters defaultExpanded={defaultExpanded ?? rows.length <= 6} sx={{ '&:before': { display: 'none' } }}>
      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
        <Stack direction="row" alignItems="center" spacing={1} sx={{ pr: 1 }}>
          <Typography variant="body2" fontWeight={600}>
            {title}
          </Typography>
          <Chip size="small" label={rows.length} variant="outlined" />
        </Stack>
      </AccordionSummary>
      <AccordionDetails sx={{ pt: 0 }}>
        <List dense disablePadding sx={{ maxHeight: 280, overflow: 'auto' }}>
          {rows.slice(0, cap).map((raw, idx) => {
            const r = raw as Record<string, any>;
            return (
              <ListItem key={idx} alignItems="flex-start" sx={{ py: 0.35, px: 0 }}>
                <ListItemText
                  primary={fmtDiagRuleLine(r?.rule_id, r?.rule_name)}
                  primaryTypographyProps={{ variant: 'body2' }}
                  secondary={matched ? 'Matched — applies to this transaction.' : 'Tested — conditions not met.'}
                  secondaryTypographyProps={{ variant: 'caption' }}
                />
              </ListItem>
            );
          })}
        </List>
        {omitted > 0 ? (
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
            … {omitted} more omitted (cap {RERUN_LOG_RULE_UI_CAP}).
          </Typography>
        ) : null}
      </AccordionDetails>
    </Accordion>
  );
}

function UnifiedRerunLogRunBlock(props: { block: Extract<UnifiedRerunLogBlock, { kind: 'run' }> }): React.ReactElement {
  const { txId, responseTimeLabel, d, diag, diagnosticSentence, notificationLines, comparison } = props.block;

  const exec = Array.isArray(d?.execution_order) ? (d.execution_order as string[]) : [];

  const stageRows: { label: string; detail: string }[] = [];
  const pushStage = (label: string, body: Record<string, any> | null | undefined) => {
    if (!body || typeof body !== 'object') return;
    const rulesEv = Number(body.rules_evaluated ?? 0);
    const scanned = Number(body.transactions_scanned ?? 0);
    const created = Number(body.created_tags ?? 0);
    const ch = Number(body.created_heading_tags ?? 0);
    const cnh = Number(body.created_non_heading_tags ?? 0);
    stageRows.push({
      label,
      detail: `evaluated ${rulesEv} · scanned ${scanned} · created ${created} (cashflow ${ch}, granular ${cnh})`
    });
  };
  pushStage('Cashflow', d.heading_stage as Record<string, any>);
  pushStage('Classification (granular pass)', d.classification as Record<string, any>);
  const sr = d.supplier_refresh as Record<string, any> | undefined;
  if (sr && typeof sr === 'object') {
    stageRows.push({
      label: 'Supplier text refresh',
      detail: `updated ${Number(sr.tags_updated ?? 0)} / scanned ${Number(sr.tags_scanned ?? 0)} tag rows`
    });
  }
  const vr = d.vat_reapply as Record<string, any> | undefined;
  if (vr && typeof vr === 'object') {
    stageRows.push({
      label: 'VAT re-apply',
      detail: `updated ${Number(vr.tags_updated ?? 0)} / scanned ${Number(vr.tags_scanned ?? 0)} tag rows`
    });
  }
  const sc = d.special_case_reapply as Record<string, any> | undefined;
  if (sc && typeof sc === 'object') {
    stageRows.push({
      label: 'Special-case rules',
      detail: `updated ${Number(sc.tags_updated ?? 0)} / scanned ${Number(sc.tags_scanned ?? 0)} rows`
    });
  }

  const diagText =
    diag && typeof diag === 'object'
      ? [
          `Cashflow matches: ${Array.isArray(diag.matched_heading_rules) ? diag.matched_heading_rules.length : 0}`,
          `Granular matches: ${Array.isArray(diag.matched_classification_rules) ? diag.matched_classification_rules.length : 0}`,
          `Final tags — granular: ${Number((diag.final_tag_counts as any)?.non_heading ?? 0)}, unclassified: ${Number((diag.final_tag_counts as any)?.unclassified ?? 0)}`
        ].join(' · ')
      : null;

  return (
    <Card variant="outlined" sx={{ mb: 1.5 }}>
      <CardContent sx={{ '&:last-child': { pb: 2 } }}>
        <Typography variant="subtitle2" color="text.secondary">
          {responseTimeLabel} · Transaction #{txId} · server response
        </Typography>

        {exec.length > 0 ? (
          <Box sx={{ mt: 1.25 }}>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
              Execution order
            </Typography>
            <Stack direction="row" flexWrap="wrap" useFlexGap spacing={0.5}>
              {exec.map((step, i) => (
                <React.Fragment key={`${step}-${i}`}>
                  <Chip size="small" label={step} variant="outlined" />
                  {i < exec.length - 1 ? (
                    <Typography component="span" variant="caption" color="text.secondary" sx={{ lineHeight: '24px' }}>
                      →
                    </Typography>
                  ) : null}
                </React.Fragment>
              ))}
            </Stack>
          </Box>
        ) : null}

        {stageRows.length > 0 ? (
          <Table size="small" sx={{ mt: 1.5 }}>
            <TableHead>
              <TableRow>
                <TableCell>Stage</TableCell>
                <TableCell>Metrics</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {stageRows.map((row) => (
                <TableRow key={row.label}>
                  <TableCell sx={{ verticalAlign: 'top', fontWeight: 600, whiteSpace: 'nowrap' }}>{row.label}</TableCell>
                  <TableCell sx={{ verticalAlign: 'top' }}>
                    <Typography variant="body2">{row.detail}</Typography>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        ) : null}

        {diagText ? (
          <Box sx={{ mt: 1.25 }}>
            <Typography variant="body2">
              <Box component="span" sx={{ fontWeight: 600 }}>
                Diagnostics (counts)
              </Box>
              : {diagText}
            </Typography>
            {diagnosticSentence ? (
              <Typography variant="body2" color="text.secondary" sx={{ mt: 0.75 }}>
                {diagnosticSentence}
              </Typography>
            ) : null}
          </Box>
        ) : (
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1.25 }}>
            Post-run diagnostic: not returned (include_tx_diagnostics=false or empty).
          </Typography>
        )}

        {diag && typeof diag === 'object' ? (
          <Stack spacing={0.5} sx={{ mt: 1 }}>
            <UnifiedRerunLogRuleAccordion
              title="Cashflow rules — matched"
              rows={diag.matched_heading_rules || []}
              matched
              defaultExpanded
            />
            <UnifiedRerunLogRuleAccordion
              title="Cashflow rules — tested, not applicable"
              rows={diag.non_matching_heading_rules || []}
              matched={false}
            />
            <UnifiedRerunLogRuleAccordion
              title="Granular rules — matched"
              rows={diag.matched_classification_rules || []}
              matched
              defaultExpanded
            />
            <UnifiedRerunLogRuleAccordion
              title="Granular rules — tested, not applicable"
              rows={diag.non_matching_classification_rules || []}
              matched={false}
            />
          </Stack>
        ) : null}

        <Divider sx={{ my: 1.5 }} />

        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.75 }}>
          Summaries (same data as the success toast + a second readout)
        </Typography>
        <Alert severity="success" variant="outlined" sx={{ alignItems: 'flex-start' }}>
          <Stack spacing={0.5} sx={{ width: '100%' }}>
            <Typography variant="subtitle2">Notification (banner / toast)</Typography>
            <RerunSummaryKeyValueList lines={notificationLines} />
          </Stack>
        </Alert>
        <Alert severity="info" icon={<InfoOutlinedIcon fontSize="inherit" />} sx={{ mt: 1, alignItems: 'flex-start' }}>
          <Stack spacing={0.35} sx={{ width: '100%' }}>
            <Typography variant="subtitle2">Comparison readout</Typography>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.35 }}>
              Generated in-app from the response and diagnostics below—no external LLM call. Use it to cross-check the
              notification wording.
            </Typography>
            <RerunComparisonReadoutBody comparison={comparison} />
          </Stack>
        </Alert>
      </CardContent>
    </Card>
  );
}

function parseRulesIdFilter(rawValue: string): ParsedRulesIdFilter {
  const empty = {
    hasFilter: false as const,
    kind: null,
    id: null,
    classificationProposalId: null,
    classificationAtlasSeq: null
  };
  const trimmed = rawValue.trim();
  if (!trimmed) return empty;

  // Status-prefixed atlas: P-A4 / R-A4 (and P:A4)
  const statusAtlasMatch = trimmed.match(/^[pr]\s*[-#: ]?\s*a\s*[-#: ]?\s*(\d+)$/i);
  if (statusAtlasMatch) {
    const n = Number(statusAtlasMatch[1]);
    if (Number.isFinite(n) && n > 0) {
      return {
        hasFilter: true,
        kind: null,
        id: null,
        classificationProposalId: null,
        classificationAtlasSeq: Math.trunc(n)
      };
    }
  }

  // Legacy / bare atlas: A4
  const atlasProposalMatch = trimmed.match(/^a\s*[-#: ]?\s*(\d+)$/i);
  if (atlasProposalMatch) {
    const n = Number(atlasProposalMatch[1]);
    if (Number.isFinite(n) && n > 0) {
      return {
        hasFilter: true,
        kind: null,
        id: null,
        classificationProposalId: null,
        classificationAtlasSeq: Math.trunc(n)
      };
    }
  }

  // Status + type: P-C123 / R-S12 / P-CF1 / R-SC3 (also P-C 123)
  const statusTyped = trimmed.match(/^[pr]\s*[-#: ]?\s*(sc|cf|c|h|s|v)\s*[-#: ]?\s*(\d+)$/i);
  if (statusTyped) {
    const typePrefix = statusTyped[1].toLowerCase();
    const n = Number(statusTyped[2]);
    if (Number.isFinite(n) && n > 0) {
      if (typePrefix === 'c' || typePrefix === 'h' || typePrefix === 'cf') {
        return {
          hasFilter: true,
          kind: null,
          id: null,
          classificationProposalId: Math.trunc(n),
          classificationAtlasSeq: null
        };
      }
      // P-/R- + S/V/SC belong in proposal-ids filtering, not confirmed catalog ``rules_id``.
      return {
        hasFilter: true,
        kind: null,
        id: null,
        classificationProposalId: null,
        classificationAtlasSeq: null,
        statusPrefixedNonClassification: true
      };
    }
  }

  // Legacy bare P123 → classification proposal id
  const proposalMatch = trimmed.match(/^p\s*[-#: ]?\s*(\d+)$/i);
  if (proposalMatch) {
    const n = Number(proposalMatch[1]);
    if (Number.isFinite(n) && n > 0) {
      return {
        hasFilter: true,
        kind: null,
        id: null,
        classificationProposalId: Math.trunc(n),
        classificationAtlasSeq: null
      };
    }
  }

  const numericOnly = trimmed.match(/^(\d+)$/);
  if (numericOnly) {
    return {
      hasFilter: true,
      kind: null,
      id: Number(numericOnly[1]),
      classificationProposalId: null,
      classificationAtlasSeq: null
    };
  }

  // Confirmed (no status): CF1 / C1 / H1 (legacy) / S1 / V1 / SC1
  const prefixed = trimmed.match(/^(sc|cf|c|h|s|v)\s*[-#: ]?\s*(\d+)$/i);
  if (!prefixed) {
    return { hasFilter: true, kind: null, id: null, classificationProposalId: null, classificationAtlasSeq: null };
  }

  const prefix = prefixed[1].toLowerCase();
  const id = Number(prefixed[2]);
  const kind: RuleIdKind =
    prefix === 'c' || prefix === 'h' || prefix === 'cf'
      ? 'classification'
      : prefix === 's'
        ? 'supplier'
        : prefix === 'v'
          ? 'vat'
          : 'specialCase';

  return { hasFilter: true, kind, id, classificationProposalId: null, classificationAtlasSeq: null };
}

function matchesParsedRulesIdFilter(parsed: ParsedRulesIdFilter, kind: RuleIdKind, id: number): boolean {
  if (!parsed.hasFilter) return true;
  if (parsed.classificationProposalId != null) return false;
  if (parsed.classificationAtlasSeq != null) return false;
  if (parsed.statusPrefixedNonClassification) return false;
  if (parsed.id == null) return true;
  if (parsed.kind && parsed.kind !== kind) return false;
  return Number(id) === parsed.id;
}

/** Confirmed catalog id for post-accept backfill (``C316`` / ``S12`` / ``V3`` / ``SC1``). */
type BackfillAfterAcceptTarget = { kind: RuleIdKind; id: number };

function backfillTargetFromConfirmedPrefixedId(prefixed: string): BackfillAfterAcceptTarget | null {
  const parsed = parseRulesIdFilter(prefixed);
  if (!parsed.hasFilter || parsed.kind == null || parsed.id == null) return null;
  const id = Math.trunc(Number(parsed.id));
  if (!Number.isFinite(id) || id <= 0) return null;
  return { kind: parsed.kind, id };
}

/** Comma/whitespace-separated ids: ``P-C154`` / ``P-S12`` / ``P-A2`` (legacy ``P154`` / ``S12`` / ``A2``). */
function parseRulesProposalIdFilter(raw: string): RulesTabProposalIdFilter | null {
  const trimmed = (raw || '').trim();
  if (!trimmed) return null;
  const supplierProposalIds = new Set<number>();
  const classificationProposalIds = new Set<number>();
  const atlasSeqs = new Set<number>();
  for (const part of trimmed.split(/[\s,]+/)) {
    const p = part.trim();
    if (!p) continue;
    const statusAtlas = p.match(/^[pr]\s*[-#: ]?\s*a\s*[-#: ]?\s*(\d+)$/i);
    if (statusAtlas) {
      const n = Number(statusAtlas[1]);
      if (Number.isFinite(n) && n > 0) atlasSeqs.add(Math.trunc(n));
      continue;
    }
    const aMatch = p.match(/^a\s*[-#: ]?\s*(\d+)$/i);
    if (aMatch) {
      const n = Number(aMatch[1]);
      if (Number.isFinite(n) && n > 0) atlasSeqs.add(Math.trunc(n));
      continue;
    }
    const statusSupplier = p.match(/^[pr]\s*[-#: ]?\s*s\s*[-#: ]?\s*(\d+)$/i);
    if (statusSupplier) {
      const n = Number(statusSupplier[1]);
      if (Number.isFinite(n) && n > 0) supplierProposalIds.add(Math.trunc(n));
      continue;
    }
    const sMatch = p.match(/^s\s*[-#: ]?\s*(\d+)$/i);
    if (sMatch) {
      const n = Number(sMatch[1]);
      if (Number.isFinite(n) && n > 0) supplierProposalIds.add(Math.trunc(n));
      continue;
    }
    const statusClass = p.match(/^[pr]\s*[-#: ]?\s*c\s*[-#: ]?\s*(\d+)$/i);
    if (statusClass) {
      const n = Number(statusClass[1]);
      if (Number.isFinite(n) && n > 0) classificationProposalIds.add(Math.trunc(n));
      continue;
    }
    const pExplicit = p.match(/^p\s*[-#: ]?\s*(\d+)$/i);
    if (pExplicit) {
      const n = Number(pExplicit[1]);
      if (Number.isFinite(n) && n > 0) classificationProposalIds.add(Math.trunc(n));
      continue;
    }
    const cleaned = p.replace(/^P/i, '').trim();
    const n = Number(cleaned);
    if (Number.isFinite(n) && n > 0) classificationProposalIds.add(Math.trunc(n));
  }
  if (supplierProposalIds.size === 0 && classificationProposalIds.size === 0 && atlasSeqs.size === 0) return null;
  return { supplierProposalIds, classificationProposalIds, atlasSeqs };
}

function rebuildProposalIdsAfterSupplierDraftAccepted(currentParam: string, acceptedSupplierProposalId: number): string {
  const parsed = parseRulesProposalIdFilter(currentParam);
  if (!parsed) return '';
  const keptS = Array.from(parsed.supplierProposalIds)
    .filter((x) => x !== acceptedSupplierProposalId)
    .sort((a, b) => a - b);
  const keptP = Array.from(parsed.classificationProposalIds).sort((a, b) => a - b);
  const keptA = Array.from(parsed.atlasSeqs).sort((a, b) => a - b);
  const parts = [
    ...keptS.map((id) => `P-S${id}`),
    ...keptP.map((id) => `P-C${id}`),
    ...keptA.map((id) => `P-A${id}`)
  ];
  return parts.join(',');
}

function rebuildProposalIdsAfterClassificationProposalAccepted(
  currentParam: string,
  acceptedClassificationProposalId: number
): string {
  const parsed = parseRulesProposalIdFilter(currentParam);
  if (!parsed) return '';
  const keptS = Array.from(parsed.supplierProposalIds).sort((a, b) => a - b);
  const keptP = Array.from(parsed.classificationProposalIds)
    .filter((x) => x !== acceptedClassificationProposalId)
    .sort((a, b) => a - b);
  const keptA = Array.from(parsed.atlasSeqs).sort((a, b) => a - b);
  const parts = [
    ...keptS.map((id) => `P-S${id}`),
    ...keptP.map((id) => `P-C${id}`),
    ...keptA.map((id) => `P-A${id}`)
  ];
  return parts.join(',');
}

function parseTxIdFilterList(raw: string): number[] {
  const trimmed = (raw || '').trim();
  if (!trimmed) return [];
  const out: number[] = [];
  for (const part of trimmed.split(/[\s,]+/)) {
    const n = Number(part.trim());
    if (Number.isFinite(n) && n > 0) out.push(Math.trunc(n));
  }
  return Array.from(new Set(out));
}

/** After Similar transactions batch draft generate: prefixes for Rules tab ``rules_proposal_ids``. */
function formatSimilarTxRulesProposalIdsParam(supplierDbIds: number[], classificationDbIds: number[]): string {
  const su = supplierDbIds.filter((n) => Number.isFinite(n) && n > 0).map((n) => Math.trunc(n));
  const pu = classificationDbIds.filter((n) => Number.isFinite(n) && n > 0).map((n) => Math.trunc(n));
  const s = Array.from(new Set(su)).sort((a, b) => a - b);
  const p = Array.from(new Set(pu)).sort((a, b) => a - b);
  const parts = [...s.map((id) => `P-S${id}`), ...p.map((id) => `P-C${id}`)];
  return parts.join(',');
}

/** ZWSP/BOM etc. — common in copy-pasted bank/merchant strings and breaks naive `.includes` search. */
const RULES_SEARCH_STRIP_INVISIBLE = /[\u200B-\u200D\uFEFF]/g;

function normalizeRulesSearchChunk(raw: unknown): string {
  return String(raw ?? '')
    .replace(RULES_SEARCH_STRIP_INVISIBLE, '')
    .normalize('NFKC')
    .toLowerCase();
}

/** Case-insensitive substring match with Unicode normalization (Rules tab text search). */
function rulesSearchNeedleMatchesHay(hayParts: unknown[], needleRaw: string): boolean {
  const needle = normalizeRulesSearchChunk((needleRaw || '').trim());
  if (!needle) return true;
  const hay = hayParts.map(normalizeRulesSearchChunk).join(' ');
  return hay.includes(needle);
}

function normalizeRuleAccountScope(value: string | null | undefined): 'both' | 'ireland' | 'uk' {
  const v = (value || '').trim().toLowerCase();
  if (v === 'ireland' || v === 'revolut_ire' || v === 'ire' || v === 'ie') return 'ireland';
  if (v === 'uk' || v === 'revolut_uk' || v === 'gb') return 'uk';
  return 'both';
}

/** Cash-flow heading categories (`In` / `Out` / `N/A`; legacy Income/Expense during transition). */
function isTopLevelCashflowHeadingName(name: string): boolean {
  const n = (name || '').trim().toLowerCase();
  return n === 'in' || n === 'out' || n === 'n/a' || n === 'na' || n === 'income' || n === 'expense';
}

/** Matches backend `UNCLASSIFIED_REVIEW_CATEGORY_NAMES` for tag picking. */
function isUnclassifiedReviewCategoryName(name: string): boolean {
  const s = (name || '').trim();
  return s === 'Unclassified' || s === 'Unclassified - Needs Review';
}

/**
 * Current rule tag uid for `correct-tag` when the row's `tag_uid` is missing or stale
 * (canonical review-rows can drift after sync; GET tags is source of truth).
 */
async function fetchRuleTagUidForCorrectTag(
  txId: number,
  displayCategory: string,
  preferredCategoryId: number | null
): Promise<string> {
  const res = await api.get('/api/revolut-transaction-category-tags/', {
    params: { revolut_transaction_id: txId }
  });
  const tags = Array.isArray(res.data?.tags) ? res.data.tags : [];
  const needle = (displayCategory || '').trim().toLowerCase();

  const ruleNonHeading = tags.filter((t: any) => {
    if (String(t?.source || '').toLowerCase() !== 'rule') return false;
    const catName = String(t?.category?.name || '').trim();
    if (!catName || isTopLevelCashflowHeadingName(catName) || isUnclassifiedReviewCategoryName(catName)) {
      return false;
    }
    return true;
  });

  let chosen: any;
  if (preferredCategoryId != null && Number.isFinite(Number(preferredCategoryId))) {
    const pid = Number(preferredCategoryId);
    chosen = ruleNonHeading.find((t: any) => Number(t?.category_id) === pid);
  }
  if (!chosen && needle.length > 0) {
    chosen = ruleNonHeading.find((t: any) => String(t?.category?.name || '').trim().toLowerCase() === needle);
  }
  if (!chosen) {
    chosen = ruleNonHeading[0];
  }
  const uid = chosen?.uid;
  return uid != null && String(uid).trim() ? String(uid).trim() : '';
}

/** Prefer granular category when set; otherwise Income/Expense heading. */
function resolvePickedCategoryId(
  granular: number | '' | undefined,
  heading: number | '' | undefined
): number | null {
  if (granular !== undefined && granular !== '') return Number(granular);
  if (heading !== undefined && heading !== '') return Number(heading);
  return null;
}

/** First granular category id in the TX Review draft map (multi-tag aware). */
function primaryGranularCategoryId(selectedGranularByTx: Record<number, number[]>, txId: number): number | '' {
  const arr = selectedGranularByTx[txId];
  if (!Array.isArray(arr) || arr.length === 0) return '';
  const first = Number(arr[0]);
  return Number.isFinite(first) && first > 0 ? first : '';
}

function txReviewHasGranularDraft(selectedGranularByTx: Record<number, number[]>, txId: number): boolean {
  return primaryGranularCategoryId(selectedGranularByTx, txId) !== '';
}

function selectHasChoice(value: string | number): boolean {
  return String(value) !== '';
}

/** MUI Select sentinel — opens create-category dialog; never persist as a category id. */
const ADD_NEW_CATEGORY_SELECT_VALUE = '__add_new_category__';

function normalizeRuleMatchText(value: string | null | undefined): string {
  return (value || '').trim().toLowerCase().replace(/\s+/g, ' ');
}

function vatRuleCategoryNames(row: CategoryVatRuleRow): string[] {
  const names = (row.categories || [])
    .map((c) => String(c?.name || '').trim())
    .filter((n) => n.length > 0);
  if (names.length > 0) return names;
  const fromIds = (row.category_ids || [])
    .map((id) => Number(id))
    .filter((id) => Number.isFinite(id) && id > 0)
    .map((id) => `Category #${id}`);
  if (fromIds.length > 0) return fromIds;
  const fallback = String(row.category?.name || '').trim();
  if (fallback) return [fallback];
  return [`Category #${row.category_id}`];
}

function classificationProposalOutputCategoryId(row: RuleProposalRow, categoryList: CategoryItem[]): number | null {
  const p = row.payload || {};
  const raw = p.category_id;
  if (raw != null && String(raw).trim() !== '') {
    const n = Number(raw);
    if (Number.isFinite(n) && n > 0) return n;
  }
  const name = String(
    row.category_name || (p.category_name as string) || (p.category as string) || ''
  ).trim();
  if (!name) return null;
  const found = categoryList.find((c) => (c.name || '').trim().toLowerCase() === name.toLowerCase());
  return found?.id && found.id > 0 ? found.id : null;
}

function vatRuleAppliesToCategoryId(row: CategoryVatRuleRow, categoryId: number): boolean {
  if (!Number.isFinite(categoryId) || categoryId <= 0) return false;
  if (Number(row.category_id) === categoryId) return true;
  if (Array.isArray(row.category_ids) && row.category_ids.some((id) => Number(id) === categoryId)) return true;
  if (Array.isArray(row.categories) && row.categories.some((c) => Number(c?.id) === categoryId)) return true;
  if (row.category && Number(row.category.id) === categoryId) return true;
  return false;
}

/** When the ID filter targets classification (incl. bare numeric), VAT rows may still be relevant via output category. */
function linkedVatRulesBypassVatIdFilter(parsed: ParsedRulesIdFilter): boolean {
  if (!parsed.hasFilter) return false;
  if (parsed.classificationProposalId != null) return true;
  if (parsed.classificationAtlasSeq != null) return true;
  if (parsed.kind === 'classification') return true;
  if (parsed.kind == null && parsed.id != null) return true;
  return false;
}

/** Row for the Suppliers tab (GET /api/suppliers/). */
type SupplierPendingAliasProposal = {
  proposal_id: number;
  previews: string[];
  variant_count: number;
};

type SupplierCrudRow = {
  id: number;
  name: string;
  is_key_supplier: boolean;
  tx_associated_count: number;
  invoice_frequency: string;
  invoice_currency: string;
  invoice_url: string;
  vat_status: string;
  notes: string;
  is_active: boolean;
  /** Pending supplier-alias drafts from audit / sweeps (confirm under Rules tab). */
  pending_alias_proposals?: SupplierPendingAliasProposal[];
};

type SupplierCrudDraft = {
  id: number | null;
  name: string;
  is_key_supplier: boolean;
  invoice_frequency: string;
  invoice_currency: string;
  invoice_url: string;
  vat_status: string;
  notes: string;
  is_active: boolean;
};

type SupplierCorpusAuditTxBucket = {
  canonical_normalized: string;
  canonical_display: string;
  transaction_count: number;
  observed_aliases: string[];
  /** Merchant/counterparty wording when the audit split fields (preferred for Step 2). */
  observed_merchants?: string[];
  observed_descriptions?: string[];
  observed_references?: string[];
  /** How the bucket key was primarily derived: merchant | description | reference. */
  bucket_basis?: 'merchant' | 'description' | 'reference' | string;
  observed_alias_normalized_tokens?: string[];
  /** All matching ledger TX ids for this bucket (present after re-running corpus audit). */
  transaction_ids?: number[];
  /** Short preview ids for the collapsed table cell. */
  sample_tx_ids: number[];
};

function supplierCorpusAuditBucketTxIds(bucket: SupplierCorpusAuditTxBucket): number[] {
  const full = Array.isArray(bucket.transaction_ids) ? bucket.transaction_ids : [];
  if (full.length) {
    return Array.from(
      new Set(full.map((x) => Number(x)).filter((id) => Number.isFinite(id) && id > 0))
    );
  }
  const sample = Array.isArray(bucket.sample_tx_ids) ? bucket.sample_tx_ids : [];
  return Array.from(
    new Set(sample.map((x) => Number(x)).filter((id) => Number.isFinite(id) && id > 0))
  );
}

type SupplierAuditProposalGroup = {
  supplierId: number;
  supplierName: string;
  txIds: number[];
  matchedBucketLabels: string[];
};

/** Score how well an inferred bucket looks like a catalog supplier (0 = no match). */
function scoreSupplierAuditBucketToCatalog(
  bucket: SupplierCorpusAuditTxBucket,
  catalog: SupplierCorpusAuditCatalogSupplier
): number {
  const cat = normalizeRuleMatchText(catalog.name_normalized || catalog.name || '');
  if (!cat || cat.length < 2) return 0;
  const texts = [
    bucket.canonical_normalized,
    bucket.canonical_display,
    ...(bucket.observed_merchants || []),
    ...(bucket.observed_aliases || [])
  ]
    .map((t) => normalizeRuleMatchText(String(t || '')))
    .filter((t) => t.length >= 2);
  let best = 0;
  for (const t of texts) {
    if (t === cat) best = Math.max(best, 1000 + cat.length);
    else if (t.includes(cat) && cat.length >= 3) best = Math.max(best, 200 + cat.length);
    else if (cat.includes(t) && t.length >= 4) best = Math.max(best, 80 + t.length);
  }
  // Also score catalog alias patterns against bucket wording.
  for (const ap of catalog.alias_patterns || []) {
    const a = normalizeRuleMatchText(ap);
    if (!a || a.length < 3) continue;
    for (const t of [
      bucket.canonical_normalized,
      bucket.canonical_display,
      ...(bucket.observed_merchants || []),
      ...(bucket.observed_aliases || [])
    ].map((x) => normalizeRuleMatchText(String(x || '')))) {
      if (!t) continue;
      if (t === a) best = Math.max(best, 900 + a.length);
      else if (t.includes(a) || a.includes(t)) best = Math.max(best, 120 + Math.min(a.length, t.length));
    }
  }
  return best;
}

function buildSupplierAuditProposalGroups(
  audit: SupplierCorpusAuditPayload | null
): SupplierAuditProposalGroup[] {
  if (!audit) return [];
  // Prefer server-precomputed groups (avoids O(buckets×catalog) on the SPA main thread).
  // Trust an empty array too — falling through would re-score the full corpus in the browser
  // and can freeze/crash the page right after the success toast.
  const serverGroups = audit.proposed_supplier_mappings;
  if (Array.isArray(serverGroups)) {
    return serverGroups
      .map((g) => {
        const supplierId = Number(g.supplier_id);
        const txIds = Array.from(
          new Set(
            (Array.isArray(g.transaction_ids) ? g.transaction_ids : [])
              .map((x) => Number(x))
              .filter((id) => Number.isFinite(id) && id > 0)
          )
        );
        return {
          supplierId,
          supplierName: String(g.supplier_name || '').trim() || `#${supplierId}`,
          txIds,
          matchedBucketLabels: Array.isArray(g.matched_bucket_labels)
            ? g.matched_bucket_labels.map((x) => String(x || '').trim()).filter(Boolean)
            : []
        } as SupplierAuditProposalGroup;
      })
      .filter((g) => Number.isFinite(g.supplierId) && g.supplierId > 0 && g.txIds.length > 0)
      .sort((a, b) => b.txIds.length - a.txIds.length || a.supplierName.localeCompare(b.supplierName));
  }

  const catalog = audit.catalog_suppliers_sorted || [];
  if (!catalog.length) return [];
  const bySupplier = new Map<number, SupplierAuditProposalGroup>();
  // Legacy API fallback only (no proposed_supplier_mappings field). Cap work to stay interactive.
  const buckets = (audit.from_transactions_sorted || []).slice(0, SUPPLIER_CORPUS_AUDIT_UI_ROW_CAP);
  for (const bucket of buckets) {
    let best: SupplierCorpusAuditCatalogSupplier | null = null;
    let bestScore = 0;
    for (const c of catalog) {
      const score = scoreSupplierAuditBucketToCatalog(bucket, c);
      if (score > bestScore) {
        bestScore = score;
        best = c;
      }
    }
    if (!best || bestScore <= 0) continue;
    const sid = Number(best.supplier_id);
    if (!Number.isFinite(sid) || sid <= 0) continue;
    const txIds = supplierCorpusAuditBucketTxIds(bucket);
    if (!txIds.length) continue;
    const label = String(bucket.canonical_display || bucket.canonical_normalized || '').trim();
    const existing = bySupplier.get(sid);
    if (!existing) {
      bySupplier.set(sid, {
        supplierId: sid,
        supplierName: String(best.name || '').trim() || `#${sid}`,
        txIds: [...txIds],
        matchedBucketLabels: label ? [label] : []
      });
    } else {
      const merged = new Set(existing.txIds);
      txIds.forEach((id) => merged.add(id));
      existing.txIds = Array.from(merged);
      if (label && !existing.matchedBucketLabels.includes(label)) {
        existing.matchedBucketLabels.push(label);
      }
    }
  }
  return Array.from(bySupplier.values()).sort(
    (a, b) => b.txIds.length - a.txIds.length || a.supplierName.localeCompare(b.supplierName)
  );
}

function supplierAuditTxLooksLinked(
  row: CanonicalReviewRow | undefined,
  supplierId: number,
  supplierName: string
): boolean {
  if (!row) return false;
  if (row.supplier_id != null && Number(row.supplier_id) === Number(supplierId)) return true;
  const tagged = normalizeRuleMatchText(row.supplier || '');
  const want = normalizeRuleMatchText(supplierName);
  return !!tagged && !!want && tagged === want;
}

type SupplierCorpusAuditCatalogSupplier = {
  supplier_id: number;
  name: string;
  name_normalized: string;
  alias_patterns: string[];
};

type SupplierCorpusAuditAliasGapHint = {
  supplier_id: number;
  supplier_name: string;
  canonical_normalized: string;
  normalized_tokens_in_tx_but_not_catalog?: string[];
  hint?: string;
};

type SupplierCorpusAuditComparison = {
  inferred_canonical_keys_without_supplier_row?: string[];
  transactions_suggest_aliases_not_in_normalized_catalog_hints?: SupplierCorpusAuditAliasGapHint[];
  supplier_names_normalized_in_catalog_but_no_transactions_bucket?: string[];
};

type SupplierCorpusAuditProposedMapping = {
  supplier_id: number;
  supplier_name: string;
  transaction_ids: number[];
  matched_bucket_labels?: string[];
  transaction_count?: number;
};

type SupplierCorpusAuditPayload = {
  ledger_user_id: number;
  catalog_user_id: number;
  all_ledger_users?: boolean;
  ledger_user_ids_scanned?: number[];
  max_transactions_requested?: number | null;
  total_transactions_scanned: number;
  scan_capped_hint?: boolean;
  distinct_inferred_buckets: number;
  from_transactions_sorted: SupplierCorpusAuditTxBucket[];
  catalog_suppliers_sorted: SupplierCorpusAuditCatalogSupplier[];
  /** Server-precomputed groups for Proposed Supplier mappings (preferred). */
  proposed_supplier_mappings?: SupplierCorpusAuditProposedMapping[];
  comparison: SupplierCorpusAuditComparison;
  documentation?: string;
  /** Present when the audit was persisted server-side. */
  saved_run_id?: string | null;
  saved_at?: string | null;
};

type CategoryCrudRow = {
  id: number;
  name: string;
  description: string;
  /** Server slug when description no longer carries provenance (e.g. auto_created). */
  provenance?: string | null;
  is_key_category: boolean;
  is_active: boolean;
};

type CategoryCrudDraft = {
  id: number | null;
  name: string;
  description: string;
  is_key_category: boolean;
  is_active: boolean;
};

const ENTITY_SOURCE_SLUG_BY_LABEL = {
  Manual: 'manual',
  'Pandle seed': 'pandle',
  'Auto-created': 'auto_created',
  System: 'system',
  Custom: 'custom',
  'Auto (statement)': 'auto_statement',
  'Auto (sweep)': 'auto_sweep'
} as const;

type EntitySourceTagLabel = keyof typeof ENTITY_SOURCE_SLUG_BY_LABEL;
type EntitySourceFilterValue = 'all' | EntitySourceTagLabel;

type EntitySourceTagStyle = {
  label: EntitySourceTagLabel;
  chipColor: 'default' | 'primary' | 'secondary' | 'error' | 'info' | 'success' | 'warning';
};

/** Derive Source tag from category `description` or supplier `notes` (shared provenance strings). */
function deriveEntitySourceTag(text: string): EntitySourceTagStyle {
  const t = (text || '').trim();
  if (!t) {
    return { label: 'Manual', chipColor: 'default' };
  }
  const lower = t.toLowerCase();
  if (lower.includes('seeded from transactions_pandle')) {
    return { label: 'Pandle seed', chipColor: 'info' };
  }
  if (/auto-created category:/i.test(t)) {
    return { label: 'Auto-created', chipColor: 'warning' };
  }
  if (lower.includes('system base category')) {
    return { label: 'System', chipColor: 'secondary' };
  }
  if (lower.includes('auto-generated for transactions with no merchant')) {
    return { label: 'Auto (statement)', chipColor: 'warning' };
  }
  if (lower.includes('auto-generated from full oldest-first supplier sweep')) {
    return { label: 'Auto (sweep)', chipColor: 'warning' };
  }
  if (lower.includes('auto-generated') || /\bauto[- ]?created\b/i.test(t)) {
    return { label: 'Auto-created', chipColor: 'warning' };
  }
  return { label: 'Custom', chipColor: 'default' };
}

const CATEGORY_PROVENANCE_SLUG_TO_STYLE: Record<string, EntitySourceTagStyle> = {
  auto_created: { label: 'Auto-created', chipColor: 'warning' },
  pandle_seed: { label: 'Pandle seed', chipColor: 'info' },
  system: { label: 'System', chipColor: 'secondary' },
  auto_statement: { label: 'Auto (statement)', chipColor: 'warning' },
  auto_sweep: { label: 'Auto (sweep)', chipColor: 'warning' },
  custom: { label: 'Custom', chipColor: 'default' },
  manual: { label: 'Manual', chipColor: 'default' }
};

/** Category Source: prefer persisted `provenance`, else infer from `description`. */
function categorySourceTagFromRow(row: { description: string; provenance?: string | null }): EntitySourceTagStyle {
  const slug = String(row.provenance ?? '').trim().toLowerCase();
  if (slug && CATEGORY_PROVENANCE_SLUG_TO_STYLE[slug]) {
    return CATEGORY_PROVENANCE_SLUG_TO_STYLE[slug];
  }
  return deriveEntitySourceTag(row.description);
}

function supplierSourceTagFromNotes(notes: string): EntitySourceTagStyle {
  return deriveEntitySourceTag(notes);
}

/** Source tag "System" — base heading categories; not editable or deletable in the UI. */
function isCategoryRowImmutableSystem(row: CategoryCrudRow): boolean {
  return categorySourceTagFromRow(row).label === 'System';
}

function entitySourceFilterLabelFromUrlParam(raw: string | null | undefined): EntitySourceFilterValue {
  const s = (raw || '').trim().toLowerCase();
  if (!s) return 'all';
  const hit = (Object.entries(ENTITY_SOURCE_SLUG_BY_LABEL) as [EntitySourceTagLabel, string][]).find(
    ([, slug]) => slug === s
  );
  return hit ? hit[0] : 'all';
}

function entitySourceUrlParamFromFilterLabel(label: EntitySourceFilterValue): string | null {
  if (label === 'all') return null;
  return ENTITY_SOURCE_SLUG_BY_LABEL[label];
}

/** Fixed order for Source filter dropdowns (Categories + Suppliers). */
const ENTITY_SOURCE_FILTER_SELECT_LABELS: EntitySourceTagLabel[] = [
  'Manual',
  'Pandle seed',
  'Auto-created',
  'Auto (statement)',
  'Auto (sweep)',
  'System',
  'Custom'
];

/** Suppliers explicitly on the VAT lookback list (GET /api/suppliers/vat-invoice-policies). */
type SupplierVatInvoiceRowUi = {
  supplier_id: number;
  name: string;
  is_active?: boolean;
  vat_invoice_lookback_months: number | null;
  vat_invoices_last_retrieved_at: string | null;
  retrieval_warning: string | null;
  lookbackInput: string;
  lastRetrievedInput: string;
};

type SupplierRuleRow = {
  id: number;
  rule_name?: string | null;
  notes?: string | null;
  pattern_raw: string;
  pattern_normalized: string;
  priority?: number | null;
  text_match_operator?: string | null;
  description_contains?: string | null;
  description_text_match_operator?: string | null;
  reference_contains?: string | null;
  reference_text_match_operator?: string | null;
  match_criteria?: SupplierRuleMatchCriterionApi[] | null;
  account_scope?: string | null;
  is_active?: boolean;
  supplier_id: number;
  supplier_name: string | null;
};

type SupplierRuleMatchCriterionApi = {
  kind: 'merchant' | 'description' | 'reference' | 'canonical_supplier' | 'currency' | 'amount';
  value: string;
  operator?: string | null;
};

type SupplierRuleMatchCriterionDraft = {
  id: string;
  kind: 'merchant' | 'description' | 'reference' | 'canonical_supplier' | 'currency' | 'amount';
  value: string;
  operator: string;
};

function isValidSupplierRuleAmountValue(raw: string): boolean {
  const t = String(raw || '')
    .replace(/,/g, '')
    .trim();
  if (!t) return false;
  return /^-?\d+(\.\d+)?$/.test(t);
}


function isMerchantSourcedSupplierRuleLike(row: {
  match_criteria?: Array<{ kind?: string | null; value?: string | null }> | null;
  pattern_raw?: string | null;
  description_contains?: string | null;
  reference_contains?: string | null;
}): boolean {
  const criteria = Array.isArray(row.match_criteria) ? row.match_criteria : [];
  if (
    criteria.some(
      (c) =>
        String(c?.kind || '').trim().toLowerCase() === 'merchant' &&
        String(c?.value || '').trim().length > 0
    )
  ) {
    return true;
  }
  if (criteria.length > 0) return false;
  const hasPattern = String(row.pattern_raw || '').trim().length > 0;
  const hasDesc = String(row.description_contains || '').trim().length > 0;
  const hasRef = String(row.reference_contains || '').trim().length > 0;
  return hasPattern && !hasDesc && !hasRef;
}

const SUPPLIER_RULE_CRITERIA_KIND_OPTIONS: Array<{
  value: SupplierRuleMatchCriterionDraft['kind'];
  label: string;
}> = [
  { value: 'merchant', label: 'Merchant / Counterparty' },
  { value: 'description', label: 'Description' },
  { value: 'reference', label: 'Reference' },
  { value: 'canonical_supplier', label: 'Supplier' },
  { value: 'currency', label: 'Currency' },
  { value: 'amount', label: 'Amount (exact)' }
];

function defaultSupplierRuleCriterion(
  kind: SupplierRuleMatchCriterionDraft['kind'] = 'description'
): SupplierRuleMatchCriterionDraft {
  return {
    id: `src-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
    kind,
    value: kind === 'currency' ? 'EUR' : '',
    operator: kind === 'currency' || kind === 'amount' ? 'exact' : 'contains'
  };
}

function normalizeSupplierRuleCriteriaFromRow(
  row: SupplierRuleRow
): SupplierRuleMatchCriterionDraft[] {
  const fromApi = Array.isArray(row.match_criteria)
    ? row.match_criteria
        .map((c) => ({
          id: `src-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
          kind: String(c?.kind || '').trim().toLowerCase() as SupplierRuleMatchCriterionDraft['kind'],
          value: String(c?.value || ''),
          operator: String(c?.operator || 'contains').trim() || 'contains'
        }))
        .filter((c) => {
          if (!['merchant', 'description', 'reference', 'canonical_supplier', 'currency', 'amount'].includes(c.kind)) {
            return false;
          }
          const v = c.value.trim();
          if (!v) return false;
          if (c.kind === 'currency') return /^[A-Za-z]{3}$/.test(v);
          if (c.kind === 'amount') return isValidSupplierRuleAmountValue(v);
          return true;
        })
    : [];
  if (fromApi.length > 0) return fromApi;
  const legacy: SupplierRuleMatchCriterionDraft[] = [];
  if (String(row.pattern_raw || '').trim()) {
    legacy.push({
      id: `src-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
      kind: 'merchant',
      value: String(row.pattern_raw || ''),
      operator: String(row.text_match_operator || 'contains').trim() || 'contains'
    });
  }
  if (String(row.description_contains || '').trim()) {
    legacy.push({
      id: `src-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
      kind: 'description',
      value: String(row.description_contains || ''),
      operator:
        String(row.description_text_match_operator || row.text_match_operator || 'contains').trim() || 'contains'
    });
  }
  if (String(row.reference_contains || '').trim()) {
    legacy.push({
      id: `src-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
      kind: 'reference',
      value: String(row.reference_contains || ''),
      operator:
        String(row.reference_text_match_operator || row.text_match_operator || 'contains').trim() || 'contains'
    });
  }
  return legacy;
}

type SupplierRuleCriteriaCell = {
  key: string;
  labelText: string;
  labelNode: React.ReactNode;
};

function normalizeSupplierRuleCriteriaForDisplay(
  raw: unknown
): SupplierRuleMatchCriterionApi[] {
  let parsed: unknown = raw;
  if (typeof parsed === 'string') {
    try {
      parsed = JSON.parse(parsed);
    } catch {
      return [];
    }
  }
  if (!Array.isArray(parsed)) return [];
  return parsed
    .map((item) => ({
      kind: String((item as any)?.kind || '').trim().toLowerCase() as SupplierRuleMatchCriterionApi['kind'],
      value: String((item as any)?.value || ''),
      operator: String((item as any)?.operator || 'contains').trim() || 'contains'
    }))
    .filter((item) => {
      if (!['merchant', 'description', 'reference', 'canonical_supplier', 'currency', 'amount'].includes(item.kind)) {
        return false;
      }
      const v = item.value.trim();
      if (!v) return false;
      if (item.kind === 'currency') return /^[A-Za-z]{3}$/.test(v);
      if (item.kind === 'amount') return isValidSupplierRuleAmountValue(v);
      return true;
    });
}

function supplierRuleCriteriaFromAliasForDisplay(
  row: SupplierRuleRow
): SupplierRuleMatchCriterionApi[] {
  const normalized = normalizeSupplierRuleCriteriaForDisplay(row.match_criteria);
  if (normalized.length > 0) return normalized;
  const legacy: SupplierRuleMatchCriterionApi[] = [];
  if (String(row.pattern_raw || '').trim()) {
    legacy.push({
      kind: 'merchant',
      value: String(row.pattern_raw || ''),
      operator: String(row.text_match_operator || 'contains').trim() || 'contains'
    });
  }
  if (String(row.description_contains || '').trim()) {
    legacy.push({
      kind: 'description',
      value: String(row.description_contains || ''),
      operator:
        String(row.description_text_match_operator || row.text_match_operator || 'contains').trim() || 'contains'
    });
  }
  if (String(row.reference_contains || '').trim()) {
    legacy.push({
      kind: 'reference',
      value: String(row.reference_contains || ''),
      operator:
        String(row.reference_text_match_operator || row.text_match_operator || 'contains').trim() || 'contains'
    });
  }
  return legacy;
}

function supplierRuleCriteriaFromProposalPayloadForDisplay(
  payload: Record<string, unknown>
): SupplierRuleMatchCriterionApi[] {
  const normalized = normalizeSupplierRuleCriteriaForDisplay(payload.match_criteria);
  if (normalized.length > 0) return normalized;
  const legacy: SupplierRuleMatchCriterionApi[] = [];
  const patternRaw = String((payload.pattern_raw as string) || '').trim();
  const descriptionContains = String((payload.description_contains as string) || '').trim();
  const referenceContains = String((payload.reference_contains as string) || '').trim();
  const textMatchOperator = String((payload.text_match_operator as string) || 'contains').trim() || 'contains';
  const descriptionOperator =
    String((payload.description_text_match_operator as string) || textMatchOperator).trim() || 'contains';
  const referenceOperator =
    String((payload.reference_text_match_operator as string) || textMatchOperator).trim() || 'contains';
  if (patternRaw) {
    legacy.push({
      kind: 'merchant',
      value: patternRaw,
      operator: textMatchOperator
    });
  }
  if (descriptionContains) {
    legacy.push({
      kind: 'description',
      value: descriptionContains,
      operator: descriptionOperator
    });
  }
  if (referenceContains) {
    legacy.push({
      kind: 'reference',
      value: referenceContains,
      operator: referenceOperator
    });
  }
  return legacy;
}

function supplierRuleCriteriaDraftsFromApiCriteria(
  rows: SupplierRuleMatchCriterionApi[]
): SupplierRuleMatchCriterionDraft[] {
  return rows.map((c, idx) => ({
    id: `spc-${Date.now()}-${idx}-${Math.random().toString(36).slice(2, 9)}`,
    kind: c.kind,
    value: String(c.value || ''),
    operator: String(c.operator || 'contains').trim() || 'contains'
  }));
}

function supplierRuleCriteriaDraftsFromProposalPayload(
  payload: Record<string, unknown>
): SupplierRuleMatchCriterionDraft[] {
  return supplierRuleCriteriaDraftsFromApiCriteria(supplierRuleCriteriaFromProposalPayloadForDisplay(payload));
}

function supplierRuleCriteriaCellsForColumn(
  criteria: SupplierRuleMatchCriterionApi[],
  column: 'merchant' | 'description' | 'reference'
): SupplierRuleCriteriaCell[] {
  const kindsForColumn =
    column === 'merchant'
      ? new Set<SupplierRuleMatchCriterionApi['kind']>(['merchant', 'canonical_supplier'])
      : column === 'description'
        ? new Set<SupplierRuleMatchCriterionApi['kind']>(['description', 'currency', 'amount'])
        : new Set<SupplierRuleMatchCriterionApi['kind']>([column]);
  return criteria
    .filter((item) => kindsForColumn.has(item.kind))
    .map((item, idx) => {
      const baseLabelText = (() => {
        if (item.kind === 'currency') {
          return `Currency ${String(item.value || '').toUpperCase()}`;
        }
        if (item.kind === 'amount') {
          return `Amount ${String(item.value || '').trim()}`;
        }
        return classificationRuleTextMatchDisplay(item.value, item.operator || 'contains');
      })();
      const baseLabelNode = (() => {
        if (item.kind === 'currency') {
          return <>Currency {String(item.value || '').toUpperCase()}</>;
        }
        if (item.kind === 'amount') {
          return <>Amount {String(item.value || '').trim()}</>;
        }
        return classificationRuleTextMatchDisplayNode(item.value, item.operator || 'contains');
      })();
      const labelText = item.kind === 'canonical_supplier' ? `Canonical supplier: ${baseLabelText}` : baseLabelText;
      const labelNode = item.kind === 'canonical_supplier' ? (
        <>
          Canonical supplier:{' '}
          {baseLabelNode}
        </>
      ) : (
        baseLabelNode
      );
      return {
        key: `${item.kind}-${item.operator || ''}-${item.value}-${idx}`,
        labelText,
        labelNode
      };
    })
    .filter((item) => item.labelText !== '—');
}

function supplierRuleCriteriaSortValue(
  criteria: SupplierRuleMatchCriterionApi[],
  column: 'merchant' | 'description' | 'reference'
): string {
  const cells = supplierRuleCriteriaCellsForColumn(criteria, column);
  if (cells.length === 0) return '—';
  return cells.map((cell) => cell.labelText).join(' | ');
}

function buildSupplierRulePersistPayload(
  row: SupplierRuleRow,
  criteria: SupplierRuleMatchCriterionApi[]
): Record<string, unknown> | null {
  const cleaned = criteria
    .map((c) => ({
      kind: c.kind,
      value:
        c.kind === 'currency'
          ? String(c.value || '')
              .trim()
              .toUpperCase()
          : c.kind === 'amount'
            ? String(c.value || '')
                .replace(/,/g, '')
                .trim()
            : String(c.value || '').trim(),
      operator:
        c.kind === 'currency' || c.kind === 'amount'
          ? 'exact'
          : String(c.operator || 'contains').trim() || 'contains'
    }))
    .filter((c) => c.value.length > 0);
  if (cleaned.length === 0) return null;
  const firstMerchant = cleaned.find((c) => c.kind === 'merchant');
  const firstDescription = cleaned.find((c) => c.kind === 'description');
  const firstReference = cleaned.find((c) => c.kind === 'reference');
  const canonicalName = String(row.supplier_name || '').trim();
  if (!canonicalName) return null;
  return {
    rule_name: String(row.rule_name || '').trim() || 'Supplier rule',
    notes: row.notes != null && String(row.notes).trim() ? String(row.notes).trim() : null,
    pattern_raw: firstMerchant?.value || '',
    description_contains: firstDescription?.value ? firstDescription.value : null,
    description_text_match_operator: firstDescription?.operator || row.description_text_match_operator || 'exact',
    reference_contains: firstReference?.value ? firstReference.value : null,
    reference_text_match_operator: firstReference?.operator || row.reference_text_match_operator || 'exact',
    match_criteria: cleaned,
    canonical_name: canonicalName,
    priority: Number(row.priority ?? 100),
    text_match_operator: firstMerchant?.operator || String(row.text_match_operator || 'exact').trim() || 'exact',
    account_scope: normalizeRuleAccountScope(row.account_scope),
    is_active: row.is_active !== false
  };
}

function supplierRuleRowStubFromProposal(row: SupplierRuleProposalRow): SupplierRuleRow | null {
  const p = row.payload || {};
  const canonicalName = String((p.canonical_name as string) || '').trim();
  if (!canonicalName) return null;
  const criteria = supplierRuleCriteriaFromProposalPayloadForDisplay(p);
  const ruleName = String((p.rule_name as string) || row.proposed_name || '').trim() || 'Supplier rule';
  return {
    id: 0,
    rule_name: ruleName,
    notes: p.notes != null && String(p.notes).trim() ? String(p.notes).trim() : null,
    pattern_raw: String((p.pattern_raw as string) || '').trim(),
    pattern_normalized: String((p.pattern_normalized as string) || '').trim(),
    priority: Number(p.priority ?? 100),
    text_match_operator: String((p.text_match_operator as string) || 'exact'),
    description_contains: (p.description_contains as string) ?? null,
    description_text_match_operator: (p.description_text_match_operator as string) ?? null,
    reference_contains: (p.reference_contains as string) ?? null,
    reference_text_match_operator: (p.reference_text_match_operator as string) ?? null,
    match_criteria: criteria.length > 0 ? criteria : null,
    account_scope: normalizeRuleAccountScope(String((p.account_scope as string) || row.account_scope || 'both')),
    is_active: p.is_active !== false,
    supplier_id: 0,
    supplier_name: canonicalName
  };
}

function supplierRuleCriterionKey(c: SupplierRuleMatchCriterionApi): string {
  return `${c.kind}:${String(c.value || '').trim()}:${String(c.operator || '').trim()}`;
}

function renderSupplierRuleCriteriaCell(
  criteria: SupplierRuleMatchCriterionApi[],
  column: 'merchant' | 'description' | 'reference',
  keyPrefix: string,
  interactive?: {
    busy: boolean;
    onRemoveCriterion: (criterion: SupplierRuleMatchCriterionApi) => void;
    onCanonicalSupplierClick?: (name: string) => void;
  }
): React.ReactNode {
  const kindsForColumn =
    column === 'merchant'
      ? new Set<SupplierRuleMatchCriterionApi['kind']>(['merchant', 'canonical_supplier'])
      : column === 'description'
        ? new Set<SupplierRuleMatchCriterionApi['kind']>(['description', 'currency', 'amount'])
        : new Set<SupplierRuleMatchCriterionApi['kind']>([column]);
  const columnCriteria = criteria.filter((item) => kindsForColumn.has(item.kind));
  if (columnCriteria.length === 0) return '—';
  return (
    <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
      {columnCriteria.map((item, idx) => {
        const baseLabelNode = (() => {
          if (item.kind === 'currency') {
            return <>Currency {String(item.value || '').toUpperCase()}</>;
          }
          if (item.kind === 'amount') {
            return <>Amount {String(item.value || '').trim()}</>;
          }
          return classificationRuleTextMatchDisplayNode(item.value, item.operator || 'contains');
        })();
        const labelNode =
          item.kind === 'canonical_supplier' ? (
            <>
              Canonical supplier:{' '}
              {baseLabelNode}
            </>
          ) : (
            baseLabelNode
          );
        const chipKey = `${keyPrefix}-${supplierRuleCriterionKey(item)}-${idx}`;
        const onDelete =
          interactive && !interactive.busy
            ? () => interactive.onRemoveCriterion(item)
            : undefined;
        const canonName = String(item.value || '').trim();
        const canClickCanon =
          item.kind === 'canonical_supplier' && Boolean(interactive?.onCanonicalSupplierClick) && canonName;
        return (
          <Chip
            key={chipKey}
            size="small"
            variant="outlined"
            label={labelNode}
            clickable={Boolean(canClickCanon)}
            onClick={
              canClickCanon ? () => interactive?.onCanonicalSupplierClick?.(canonName) : undefined
            }
            onDelete={onDelete}
          />
        );
      })}
    </Stack>
  );
}

type CategoryVatRuleRow = {
  id: number;
  user_id: number;
  category_id: number;
  category_ids?: number[];
  categories?: Array<{ id: number; name: string }>;
  name?: string | null;
  priority?: number | null;
  vat_rate: string;
  account_scope?: string | null;
  is_active?: boolean;
  category?: {
    id: number;
    name: string;
  } | null;
};

type VatRuleSuggestionRow = {
  id: number;
  user_id: number;
  category_id: number;
  category_name?: string | null;
  account_scope: string;
  suggested_vat_rate: string;
  suggested_band?: string | null;
  confidence_score: number;
  rationale?: Record<string, any>;
  status: 'pending' | 'approved' | 'rejected';
  created_vat_rule_id?: number | null;
  category?: {
    id: number;
    name: string;
  } | null;
};

type VatRuleEditDraft = {
  id: number;
  category_ids: number[];
  name: string;
  priority: number;
  vat_rate: string;
  account_scope: string;
  is_active: boolean;
};

type SpecialCaseRuleRow = {
  id: number;
  user_id: number;
  category_id?: number | null;
  name: string;
  priority: number;
  is_active?: boolean;
  account_scope?: string | null;
  supplier_contains?: string | null;
  description_contains?: string | null;
  description_text_match_operator?: string | null;
  reference_contains?: string | null;
  reference_text_match_operator?: string | null;
  current_vat_rate?: string | null;
  currency?: string | null;
  match_criteria?: SpecialCaseMatchCriterionApi[] | null;
  text_match_operator?: string | null;
  set_vat_rate: string;
  notes?: string | null;
  category?: {
    id: number;
    name: string;
  } | null;
};

type SpecialCaseRuleDraft = {
  id: number;
  name: string;
  priority: number;
  is_active: boolean;
  account_scope: string;
  match_criteria: SpecialCaseMatchCriterionDraft[];
  set_vat_rate: string;
  notes: string;
};

type SpecialCaseMatchCriterionApi = {
  kind:
    | 'canonical_supplier'
    | 'merchant'
    | 'description'
    | 'reference'
    | 'currency'
    | 'amount'
    | 'category'
    | 'current_vat';
  value: string;
  operator?: string | null;
};

type SpecialCaseMatchCriterionDraft = {
  id: string;
  kind: SpecialCaseMatchCriterionApi['kind'];
  value: string;
  operator: string;
};

const SPECIAL_CASE_MATCH_CRITERIA_KIND_OPTIONS: Array<{
  value: SpecialCaseMatchCriterionDraft['kind'];
  label: string;
}> = [
  { value: 'canonical_supplier', label: 'Supplier' },
  { value: 'merchant', label: 'Merchant / Counterparty' },
  { value: 'description', label: 'Description' },
  { value: 'reference', label: 'Reference' },
  { value: 'currency', label: 'Currency' },
  { value: 'amount', label: 'Amount (exact)' },
  { value: 'category', label: 'Category' },
  { value: 'current_vat', label: 'VAT Rate' }
];

function supplierVatRowFromApi(r: {
  supplier_id: number;
  name: string;
  is_active?: boolean;
  vat_invoice_lookback_months?: number | null;
  vat_invoices_last_retrieved_at?: string | null;
  retrieval_warning?: string | null;
}): SupplierVatInvoiceRowUi {
  return {
    supplier_id: r.supplier_id,
    name: r.name,
    is_active: r.is_active,
    vat_invoice_lookback_months: r.vat_invoice_lookback_months ?? null,
    vat_invoices_last_retrieved_at: r.vat_invoices_last_retrieved_at ?? null,
    retrieval_warning: r.retrieval_warning ?? null,
    lookbackInput: r.vat_invoice_lookback_months != null ? String(r.vat_invoice_lookback_months) : '',
    lastRetrievedInput: r.vat_invoices_last_retrieved_at ?? ''
  };
}

type SortDir = 'asc' | 'desc';

type ReviewTableType = 'untagged' | 'unclassified' | 'conflicting_vat';

function nextSortState(
  prev: { key: string; dir: SortDir } | null | undefined,
  key: string
): { key: string; dir: SortDir } {
  if (prev?.key === key) {
    return { key, dir: prev.dir === 'asc' ? 'desc' : 'asc' };
  }
  return { key, dir: 'asc' };
}

function compareStrings(a: string, b: string): number {
  return a.localeCompare(b, undefined, { sensitivity: 'base', numeric: true });
}

function compareNullableDates(a: string | null | undefined, b: string | null | undefined): number {
  if (!a && !b) return 0;
  if (!a) return -1;
  if (!b) return 1;
  const ta = new Date(a).getTime();
  const tb = new Date(b).getTime();
  if (!Number.isNaN(ta) && !Number.isNaN(tb)) return ta - tb;
  return compareStrings(a, b);
}

function formatCount(n: number | null | undefined): string {
  return Number(n || 0).toLocaleString();
}

function canonicalizeVatRate(value: string | null | undefined): string {
  const raw = String(value || '').trim();
  if (!raw) return '';
  const lowered = raw.toLowerCase();
  if (lowered === 'exempt') return 'exempt';
  /* Blank VAT is never displayed as the literal "none" — use empty and render as "—" in cells. */
  if (lowered === 'none' || lowered === 'n/a' || lowered === 'na') return '';
  const numericRaw = raw.endsWith('%') ? raw.slice(0, -1).trim() : raw;
  if (/^-?\d+(\.\d+)?$/.test(numericRaw)) {
    const n = Number(numericRaw);
    if (Number.isFinite(n)) return `${String(Number(numericRaw))}%`;
  }
  return raw;
}

/** Special-case rule VAT matcher may use `rate|variant` (e.g. `20%|uk`); normalize for Select value. */
function normalizeSpecialCaseVatMatcherSelectValue(value: string | null | undefined): string {
  const raw = String(value || '').trim();
  if (!raw) return '';
  const pipe = raw.indexOf('|');
  if (pipe === -1) return canonicalizeVatRate(raw);
  const left = canonicalizeVatRate(raw.slice(0, pipe));
  const right = raw
    .slice(pipe + 1)
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9_]/g, '');
  return left && right ? `${left}|${right}` : left;
}

function categorySortLabel(categories: CategoryItem[], id: number | ''): string {
  if (id === '' || id == null) return '';
  const c = categories.find((x) => x.id === id);
  return (c?.name || '').toLowerCase();
}

function reviewTimestamp(tx: ReviewTx): number {
  const raw = tx.completed_at || tx.date;
  if (!raw) return 0;
  const t = new Date(raw).getTime();
  return Number.isNaN(t) ? 0 : t;
}

/** Tx Reviews / unified row date for secondary sort (newest → oldest). */
function unifiedRowTimestamp(row: { date?: string | null }): number {
  const raw = row.date;
  if (!raw) return 0;
  const t = new Date(raw).getTime();
  return Number.isNaN(t) ? 0 : t;
}

function mapApiTransactionToReviewTx(tx: any): ReviewTx {
  return {
    id: Number(tx?.id),
    user_id: tx?.user_id != null ? Number(tx.user_id) : null,
    revolut_id: tx?.revolut_id || '',
    date: tx?.created_at_revolut || tx?.date || null,
    completed_at: tx?.completed_at || null,
    amount:
      tx?.amount != null
        ? Number(tx.amount)
        : tx?.amount_display != null
          ? Number(tx.amount_display)
          : null,
    amount_fallback_value: tx?.amount_fallback_value ?? null,
    amount_effective: tx?.amount_effective ?? null,
    amount_source: tx?.amount_source ?? null,
    currency: tx?.currency || null,
    merchant_name: tx?.merchant_name || null,
    merchant_city: tx?.merchant_city || tx?.merchant?.city || null,
    counterparty_name: tx?.counterparty_name || null,
    description: tx?.description || null,
    reference: tx?.reference || null,
    account_source: tx?.account_source || null,
    supplier_name: tx?.supplier_name || null,
    payment_frequency: tx?.payment_frequency || null,
    classification_review_confirmed_at: tx?.classification_review_confirmed_at || null
  };
}

function upsertReviewTxById(rows: ReviewTx[], txId: number, next: ReviewTx | null): ReviewTx[] {
  const remaining = rows.filter((row) => row.id !== txId);
  if (!next) return remaining;
  return [next, ...remaining];
}

function upsertTaggedItemsByTxId(rows: TaggedItem[], txId: number, nextRows: TaggedItem[]): TaggedItem[] {
  const remaining = rows.filter((row) => row.transaction.id !== txId);
  if (!nextRows.length) return remaining;
  return [...nextRows, ...remaining];
}

function upsertConfirmedItemsByTxId(
  rows: ClassificationConfirmedItem[],
  txId: number,
  nextRows: ClassificationConfirmedItem[]
): ClassificationConfirmedItem[] {
  const remaining = rows.filter((row) => row.transaction.id !== txId);
  if (!nextRows.length) return remaining;
  return [...nextRows, ...remaining];
}

function mergeReviewTxCollections(existing: ReviewTx[], incoming: ReviewTx[]): ReviewTx[] {
  const byId = new Map<number, ReviewTx>();
  (existing || []).forEach((row) => byId.set(row.id, row));
  (incoming || []).forEach((row) => byId.set(row.id, row));
  return Array.from(byId.values()).sort((a, b) => reviewTimestamp(b) - reviewTimestamp(a));
}

function mergeConfirmedCollections(
  existing: ClassificationConfirmedItem[],
  incoming: ClassificationConfirmedItem[]
): ClassificationConfirmedItem[] {
  const byTxId = new Map<number, ClassificationConfirmedItem>();
  (existing || []).forEach((row) => {
    const tid = Number(row?.transaction?.id || 0);
    if (tid > 0) byTxId.set(tid, row);
  });
  (incoming || []).forEach((row) => {
    const tid = Number(row?.transaction?.id || 0);
    if (tid > 0) byTxId.set(tid, row);
  });
  return Array.from(byTxId.values()).sort(
    (a, b) =>
      new Date(b.classification_review_confirmed_at || b.transaction.completed_at || 0).getTime() -
      new Date(a.classification_review_confirmed_at || a.transaction.completed_at || 0).getTime()
  );
}

function supplierRulePriorityBand(priorityRaw: number | null | undefined): string {
  const priority = Number(priorityRaw ?? 100);
  if (!Number.isFinite(priority)) return 'late';
  if (priority <= 10) return 'v. early';
  if (priority <= 25) return 'early';
  if (priority <= 60) return 'mid';
  if (priority <= 100) return 'late';
  return 'v. late';
}

function isUnifiedRowCommitted(row: UnifiedReviewRow): boolean {
  const committed = row.flags?.is_committed;
  if (typeof committed === 'boolean') {
    return committed;
  }
  return row.source_state === 'confirmed';
}

function unifiedCommittedLabel(row: UnifiedReviewRow): 'Committed' | 'Uncommitted' {
  return isUnifiedRowCommitted(row) ? 'Committed' : 'Uncommitted';
}

function unifiedTagOriginLabel(
  sourceState: UnifiedRowSourceState,
  ruleId: number | null | undefined,
  backendTagSource?: string | null
): 'Auto' | 'Manual' | 'Hybrid' | 'Not tagged' {
  const normalized = String(backendTagSource || '').trim().toLowerCase();
  if (normalized === 'auto') return 'Auto';
  if (normalized === 'manual') return 'Manual';
  if (normalized === 'hybrid') return 'Hybrid';
  if (sourceState === 'tagged_by_rules') return 'Auto';
  if (sourceState === 'confirmed') return Number.isFinite(Number(ruleId)) && Number(ruleId) > 0 ? 'Auto' : 'Manual';
  return 'Not tagged';
}

function sortReviewRows(
  rows: ReviewTx[],
  sort: { key: string; dir: SortDir } | null | undefined,
  categories: CategoryItem[],
  selectedHeadingByTx: Record<number, number | ''>,
  selectedGranularByTx: Record<number, number[]>,
  headingCategories: CategoryItem[]
): ReviewTx[] {
  if (!sort?.key) return rows;
  const mul = sort.dir === 'asc' ? 1 : -1;
  const out = [...rows];
  out.sort((a, b) => {
    let cmp = 0;
    switch (sort.key) {
      case 'date':
        cmp = reviewTimestamp(a) - reviewTimestamp(b);
        break;
      case 'supplier':
        cmp = compareStrings(
          (a.supplier_name || '').toLowerCase(),
          (b.supplier_name || '').toLowerCase()
        );
        break;
      case 'description':
        cmp = compareStrings(
          (a.description || a.reference || '').toLowerCase(),
          (b.description || b.reference || '').toLowerCase()
        );
        break;
      case 'amount':
        cmp = (a.amount ?? 0) - (b.amount ?? 0);
        break;
      case 'id':
        cmp = Number(a.id || 0) - Number(b.id || 0);
        break;
      case 'account':
        cmp = compareStrings(
          (a.account_source || '').toLowerCase(),
          (b.account_source || '').toLowerCase()
        );
        break;
      case 'frequency': {
        const freqLabel = (tx: ReviewTx) => {
          const g = primaryGranularCategoryId(selectedGranularByTx, tx.id);
          const nm = selectHasChoice(g) ? categories.find((c) => c.id === g)?.name : undefined;
          return (tx.payment_frequency || inferPaymentFrequencyFromCategoryName(nm) || '').toLowerCase();
        };
        cmp = compareStrings(freqLabel(a), freqLabel(b));
        break;
      }
      case 'heading': {
        const ga = primaryGranularCategoryId(selectedGranularByTx, a.id);
        const gb = primaryGranularCategoryId(selectedGranularByTx, b.id);
        const ha =
          a.id in selectedHeadingByTx
            ? selectedHeadingByTx[a.id]
            : selectHasChoice(ga)
              ? inferHeadingCategoryIdFromAmount(headingCategories, a.amount)
              : '';
        const hb =
          b.id in selectedHeadingByTx
            ? selectedHeadingByTx[b.id]
            : selectHasChoice(gb)
              ? inferHeadingCategoryIdFromAmount(headingCategories, b.amount)
              : '';
        cmp = compareStrings(categorySortLabel(categories, ha), categorySortLabel(categories, hb));
        break;
      }
      case 'granular': {
        const ga = primaryGranularCategoryId(selectedGranularByTx, a.id);
        const gb = primaryGranularCategoryId(selectedGranularByTx, b.id);
        cmp = compareStrings(categorySortLabel(categories, ga), categorySortLabel(categories, gb));
        break;
      }
      default:
        return 0;
    }
    if (cmp !== 0) return cmp * mul;
    // Secondary: always newest → oldest by date (unless date is already primary).
    if (sort.key !== 'date') {
      const dateCmp = reviewTimestamp(b) - reviewTimestamp(a);
      if (dateCmp !== 0) return dateCmp;
    }
    return a.id - b.id;
  });
  return out;
}

function sortRulesRows(
  rules: ClassificationRule[],
  sort: { key: string; dir: SortDir } | null | undefined
): ClassificationRule[] {
  if (!sort?.key) return rules;
  const mul = sort.dir === 'asc' ? 1 : -1;
  const out = [...rules];
  out.sort((a, b) => {
    let cmp = 0;
    switch (sort.key) {
      case 'priority':
        cmp = a.priority - b.priority;
        break;
      case 'name':
        cmp = compareStrings((a.name || '').toLowerCase(), (b.name || '').toLowerCase());
        break;
      case 'category':
        cmp = compareStrings(
          (a.category?.name || `Category #${a.category_id}`).toLowerCase(),
          (b.category?.name || `Category #${b.category_id}`).toLowerCase()
        );
        break;
      case 'supplier':
        cmp = compareStrings(
          classificationRuleSupplierDisplay(a).toLowerCase(),
          classificationRuleSupplierDisplay(b).toLowerCase()
        );
        break;
      case 'description':
        cmp = compareStrings(
          (a.description_contains || a.reference_contains || '').toLowerCase(),
          (b.description_contains || b.reference_contains || '').toLowerCase()
        );
        break;
      case 'amountRange': {
        const amin = a.amount_min ?? Number.NEGATIVE_INFINITY;
        const bmin = b.amount_min ?? Number.NEGATIVE_INFINITY;
        cmp = amin - bmin;
        if (cmp === 0) {
          const amax = a.amount_max ?? Number.POSITIVE_INFINITY;
          const bmax = b.amount_max ?? Number.POSITIVE_INFINITY;
          cmp = amax - bmax;
        }
        break;
      }
      case 'currency':
        cmp = compareStrings((a.currency || '').toLowerCase(), (b.currency || '').toLowerCase());
        break;
      case 'recurrence':
        cmp = compareStrings(
          (a.recurrence_type || '').toLowerCase(),
          (b.recurrence_type || '').toLowerCase()
        );
        break;
      case 'vat':
        cmp = compareStrings(
          (a.vat_rate || a.category?.vat_rate || '').toLowerCase(),
          (b.vat_rate || b.category?.vat_rate || '').toLowerCase()
        );
        break;
      case 'status':
        cmp = (a.is_active ? 1 : 0) - (b.is_active ? 1 : 0);
        break;
      default:
        return 0;
    }
    if (cmp !== 0) return cmp * mul;
    return a.id - b.id;
  });
  return out;
}

interface ReviewTx {
  id: number;
  user_id?: number | null;
  revolut_id: string;
  date: string | null;
  completed_at: string | null;
  amount: number | null;
  /** Read-only fallback when Revolut amount is blank but balance didn't change vs previous tx. */
  amount_fallback_value?: number | null;
  /** Preferred display amount (`amount` if present, else inferred fallback). */
  amount_effective?: number | null;
  /** `revolut` | `balance_unchanged_zero_fallback` | `missing` */
  amount_source?: string | null;
  /** Original merchant charge (pre-FX), when different from ledger `amount`/`currency`. */
  bill_amount?: number | null;
  bill_currency?: string | null;
  currency: string | null;
  merchant_name: string | null;
  merchant_city?: string | null;
  counterparty_name: string | null;
  description: string | null;
  reference: string | null;
  account_source: string | null;
  supplier_name?: string | null;
  vat_conflict_detail?: {
    vat_rates?: string[];
    categories?: string[];
    suppliers?: string[];
    entries?: Array<{
      tag_uid?: string;
      category: string;
      vat_rate: string;
      supplier_name?: string | null;
      source?: string;
      rule_name?: string | null;
    }>;
  } | null;
  /** once_off | sporadic | weekly | monthly | quarterly | annually */
  payment_frequency?: string | null;
  /** Revolut transaction lifecycle state (e.g. completed, declined, reverted). */
  state?: string | null;
  /** Revolut transaction type (e.g. card_payment, transfer). */
  type?: string | null;
  /** Present for `unclassified_needs_review` rows — delete before adding a real category */
  unclassified_tag_uid?: string;
  classification_review_confirmed_at?: string | null;
}

interface ReviewPayload {
  untagged: ReviewTx[];
  unclassified_needs_review: ReviewTx[];
  conflicting_vat: ReviewTx[];
  counts: {
    untagged_total: number;
    unclassified_needs_review_total: number;
    conflicting_vat_total: number;
    untagged_returned: number;
    unclassified_needs_review_returned: number;
    conflicting_vat_returned: number;
    limit: number;
  };
}

interface TaggedItem {
  tag_uid: string;
  rule_id: number | null;
  category_id: number;
  category_name: string;
  vat_rate: string | null;
  supplier_name: string | null;
  confidence_score: number | null;
  tagged_at: string | null;
  /** Same shape as review rows; includes payment_frequency when API provides it */
  transaction: ReviewTx;
}

interface TaggedPayload {
  tagged: TaggedItem[];
  count_total: number;
  count_returned: number;
  limit: number;
  offset?: number;
  next_offset?: number;
  has_more?: boolean;
}

interface ClassificationConfirmedItem {
  classification_review_confirmed_at: string | null;
  category_id: number | null;
  category_name: string | null;
  vat_rate: string | null;
  rule_id: number | null;
  transaction: ReviewTx;
}

interface ConfirmedPayload {
  items: ClassificationConfirmedItem[];
  count_total: number;
  count_returned: number;
  limit: number;
  offset?: number;
  next_offset?: number;
  has_more?: boolean;
}

type VatConflictInspectorSummary = {
  categories: string[];
  headingCategories: string[];
  vatRates: string[];
  suppliers: string[];
  sources: string[];
  tagUids: string[];
};

function vatConflictInspectorSummary(tx: ReviewTx): VatConflictInspectorSummary {
  const detail = tx.vat_conflict_detail;
  const entries = Array.isArray(detail?.entries) ? detail!.entries! : [];
  const categories =
    entries.length > 0
      ? Array.from(new Set(entries.map((e) => String(e.category || '').trim()).filter(Boolean))).sort((a, b) =>
          a.localeCompare(b, undefined, { sensitivity: 'base' })
        )
      : Array.from(new Set((detail?.categories || []).map((x) => String(x || '').trim()).filter(Boolean))).sort((a, b) =>
          a.localeCompare(b, undefined, { sensitivity: 'base' })
        );
  const headingCategories = categories.filter((name) => isTopLevelCashflowHeadingName(name));
  const vatRatesRaw =
    entries.length > 0
      ? entries.map((e) => {
          const v = String(e.vat_rate || '').trim();
          if (!v || v.toLowerCase() === 'none' || v.toLowerCase() === 'n/a' || v.toLowerCase() === 'na') {
            return '__blank__';
          }
          return v;
        })
      : (detail?.vat_rates || []).map((x) => {
          const v = String(x || '').trim();
          if (!v || v.toLowerCase() === 'none' || v.toLowerCase() === 'n/a' || v.toLowerCase() === 'na') {
            return '__blank__';
          }
          return v;
        });
  const vatRates = Array.from(new Set(vatRatesRaw.map((x) => x.toLowerCase()))).sort();
  const suppliers =
    entries.length > 0
      ? Array.from(
          new Set(
            entries
              .map((e) => String(e.supplier_name || '').trim())
              .filter(Boolean)
          )
        ).sort((a, b) => a.localeCompare(b, undefined, { sensitivity: 'base' }))
      : Array.from(new Set((detail?.suppliers || []).map((x) => String(x || '').trim()).filter(Boolean))).sort((a, b) =>
          a.localeCompare(b, undefined, { sensitivity: 'base' })
        );
  const sources = Array.from(
    new Set(
      entries
        .map((e) => String(e.source || '').trim().toLowerCase())
        .filter(Boolean)
        .map((s) => (s === 'rule' ? 'Rule' : s === 'manual' ? 'Manual' : s))
    )
  ).sort((a, b) => a.localeCompare(b, undefined, { sensitivity: 'base' }));
  const tagUids = Array.from(
    new Set(entries.map((e) => String(e.tag_uid || '').trim()).filter(Boolean))
  ).sort((a, b) => a.localeCompare(b, undefined, { sensitivity: 'base' }));
  return { categories, headingCategories, vatRates, suppliers, sources, tagUids };
}

function vatConflictProblemDescription(tx: ReviewTx): string {
  const s = vatConflictInspectorSummary(tx);
  if (s.vatRates.length <= 1) {
    const only = s.vatRates[0] || 'blank';
    return `Conflict flagged but payload currently shows one VAT value (${only}); likely stale state or hidden tag differences.`;
  }
  const includesNone = s.vatRates.includes('__blank__') || s.vatRates.includes('none');
  if (includesNone) {
    const others = s.vatRates.filter((v) => v !== '__blank__' && v !== 'none');
    return `Blank VAT conflicts with ${others.join(' / ') || 'other VAT values'} across tags.`;
  }
  const catTxt = s.categories.length > 0 ? ` Categories: ${s.categories.join(', ')}.` : '';
  return `Multiple VAT values on one transaction: ${s.vatRates.join(' vs ')}.${catTxt}`;
}

interface CanonicalReviewRow extends UnifiedReviewRow {
  transaction?: ReviewTx;
  vat_conflict_detail?: ReviewTx['vat_conflict_detail'];
  classification_review_confirmed_at?: string | null;
  /** Full tag chips from GET /review-rows (DB truth for Cash flow / Category / Supplier / VAT). */
  tags?: any[] | null;
}

interface CanonicalReviewRowsPayload {
  rows: CanonicalReviewRow[];
  count_total: number;
  /**
   * Next SQL offset after this page when streaming GET /review-rows without `transaction_ids`.
   * Differs from `rows.length` when rows are dropped post-query (e.g. `exclude_confirmed_complete`).
   */
  stream_next_offset?: number | null;
  /**
   * Review-row metadata and aggregates. Numeric fields are counts; `applied_tag_labels` is a full-scope
   * histogram of Applied column chip labels (canonical GET /review-rows only).
   */
  counts?: {
    committed_in_scope?: number;
    uncommitted_in_scope?: number;
    total_distinct_in_scope?: number;
    returned_rows?: number;
    returned_committed?: number;
    returned_uncommitted?: number;
    applied_tag_labels?: Record<string, number>;
    [key: string]: number | Record<string, number> | undefined;
  };
  scope?: {
    requester_user_id?: number;
    requester_role?: string;
    requested_user_id?: number | null;
    target_user_id?: number;
    policy?: string;
  };
}

/** After bulk reprocess: merge GET /review-rows?transaction_ids=… rows into the loaded canonical page. */
function mergeCanonicalRowsAfterBulkRefresh(
  prev: CanonicalReviewRowsPayload | null,
  freshRows: CanonicalReviewRow[],
  affectedIds: Set<number>
): CanonicalReviewRowsPayload | null {
  if (!prev) return null;
  const freshById = new Map<number, CanonicalReviewRow>();
  for (const r of freshRows) {
    const id = Number(r.id);
    if (Number.isFinite(id) && id > 0) {
      freshById.set(id, r);
    }
  }
  const prevRows = Array.isArray(prev.rows) ? prev.rows : [];
  const nextRows: CanonicalReviewRow[] = [];
  for (const r of prevRows) {
    const id = Number(r.id);
    if (!affectedIds.has(id)) {
      nextRows.push(r);
      continue;
    }
    const n = freshById.get(id);
    if (n) {
      nextRows.push(n);
    } else {
      // Targeted review-rows fetch can legitimately omit an id (timing, scope); keep the prior row
      // so the table does not go blank after bulk tag / reprocess merges.
      nextRows.push(r);
    }
  }
  const seen = new Set(nextRows.map((r) => Number(r.id)));
  freshById.forEach((row, id) => {
    if (!seen.has(id)) {
      nextRows.push(row);
      seen.add(id);
    }
  });
  nextRows.sort((a, b) => {
    const da = String(a.date || '');
    const db = String(b.date || '');
    if (da !== db) {
      return db.localeCompare(da);
    }
    return Number(b.id) - Number(a.id);
  });
  return {
    ...prev,
    rows: nextRows
  };
}

/**
 * Map GET /review-rows canonical payloads to the same UnifiedReviewRow shape as the main All Reviews grid
 * (canonical path in ``unifiedRows``). Required so Similar transactions matches Category / Supplier / VAT columns.
 */
function mapCanonicalReviewRowToUnifiedFromApi(
  raw: CanonicalReviewRow,
  resolveSupplierId: (supplierName: string) => number | null
): UnifiedReviewRow {
  const supplier = (raw.supplier || '').trim() || '—';
  const category = raw.category || '—';
  const vatRate = raw.vat_rate || null;
  const tx = raw.transaction as ReviewTx | undefined;
  const legBill = firstLegBillFields(tx);
  const bill_amount = coerceFiniteAmount(
    (raw as UnifiedReviewRow).bill_amount ?? tx?.bill_amount ?? (tx as any)?.bill_amount ?? legBill.bill_amount
  );
  const bill_currency =
    String(
      (raw as UnifiedReviewRow).bill_currency ??
        tx?.bill_currency ??
        (tx as any)?.bill_currency ??
        legBill.bill_currency ??
        ''
    )
      .trim()
      .toUpperCase() || null;
  const revolut_state =
    trimRevolutStateDisplay((raw as CanonicalReviewRow & { revolut_state?: string | null }).revolut_state) ??
    trimRevolutStateDisplay(tx?.state);
  return {
    ...raw,
    revolut_id: String(tx?.revolut_id ?? '').trim(),
    bill_amount,
    bill_currency,
    supplier,
    supplier_id: raw.supplier_id ?? resolveSupplierId(supplier),
    status:
      (raw.status || '').trim() ||
      buildUnifiedCompletenessStatus({
        supplier,
        category,
        vat_rate: vatRate
      }),
    cashFlow: resolveCashFlowHeadingFromAmountAndTag(
      raw.amount ?? (raw.transaction as ReviewTx | undefined)?.amount ?? null,
      raw.cashFlow
    ),
    category,
    vat_rate: vatRate,
    revolut_state
  } as UnifiedReviewRow;
}

/** After confirm-classification: drop or patch canonical rows without refetching the full review-rows page. */
function applyCommittedIdsToCanonicalPayload(
  prev: CanonicalReviewRowsPayload | null,
  confirmedIds: number[],
  hideCommitted: boolean
): CanonicalReviewRowsPayload | null {
  if (!prev) return prev;
  const idSet = new Set(
    confirmedIds.map((x) => Number(x)).filter((n) => Number.isFinite(n) && n > 0)
  );
  if (!idSet.size) return prev;
  const rowsIn = Array.isArray(prev.rows) ? prev.rows : [];
  const nowIso = new Date().toISOString();

  if (hideCommitted) {
    const nextRows = rowsIn.filter((r) => !idSet.has(Number(r.id)));
    const removed = rowsIn.length - nextRows.length;
    if (removed === 0) return prev;
    const ct = Number(prev.count_total || 0);
    const nextTotal = ct > 0 ? Math.max(0, ct - removed) : ct;
    const uc = Number(prev.counts?.uncommitted_in_scope);
    const cm = Number(prev.counts?.committed_in_scope);
    const counts =
      prev.counts && Number.isFinite(uc) && Number.isFinite(cm)
        ? {
            ...prev.counts,
            returned_rows: nextRows.length,
            uncommitted_in_scope: Math.max(0, uc - removed),
            committed_in_scope: cm + removed
          }
        : prev.counts
          ? { ...prev.counts, returned_rows: nextRows.length }
          : prev.counts;
    return { ...prev, rows: nextRows, count_total: nextTotal, counts };
  }

  let touched = false;
  const nextRows = rowsIn.map((r) => {
    if (!idSet.has(Number(r.id))) return r;
    touched = true;
    const flags = { ...(r.flags || {}), is_committed: true };
    const tx = r.transaction
      ? { ...r.transaction, classification_review_confirmed_at: nowIso }
      : r.transaction;
    return {
      ...r,
      flags,
      classification_review_confirmed_at: nowIso,
      source_state: 'confirmed',
      transaction: tx
    } as CanonicalReviewRow;
  });
  if (!touched) return prev;
  return { ...prev, rows: nextRows };
}

/** After uncommit-classification: patch canonical rows and scope counts when review-rows totals lag the DB. */
function applyUncommittedIdsToCanonicalPayload(
  prev: CanonicalReviewRowsPayload | null,
  uncommittedIds: number[],
  committedOnlyFetch: boolean
): CanonicalReviewRowsPayload | null {
  if (!prev) return prev;
  const idSet = new Set(
    uncommittedIds.map((x) => Number(x)).filter((n) => Number.isFinite(n) && n > 0)
  );
  if (!idSet.size) return prev;
  const rowsIn = Array.isArray(prev.rows) ? prev.rows : [];
  const n = idSet.size;
  const uc = Number(prev.counts?.uncommitted_in_scope);
  const cm = Number(prev.counts?.committed_in_scope);
  const canAdjustCounts = Number.isFinite(uc) && Number.isFinite(cm) && cm >= n;

  if (committedOnlyFetch) {
    const nextRows = rowsIn.filter((r) => !idSet.has(Number(r.id)));
    const removed = rowsIn.length - nextRows.length;
    const ct = Number(prev.count_total || 0);
    let nextTotal = prev.count_total;
    if (ct > 0) {
      if (canAdjustCounts) {
        nextTotal = Math.max(0, ct - n);
      } else if (removed > 0) {
        nextTotal = Math.max(0, ct - removed);
      }
    } else {
      nextTotal = Math.max(0, nextRows.length);
    }
    const counts =
      canAdjustCounts && prev.counts
        ? {
            ...prev.counts,
            returned_rows: nextRows.length,
            committed_in_scope: cm - n,
            uncommitted_in_scope: uc + n
          }
        : prev.counts
          ? { ...prev.counts, returned_rows: nextRows.length }
          : prev.counts;
    if (removed === 0 && nextTotal === ct && !canAdjustCounts) return prev;
    return { ...prev, rows: nextRows, count_total: nextTotal, counts };
  }

  let touched = false;
  const nextRows = rowsIn.map((r) => {
    if (!idSet.has(Number(r.id))) return r;
    touched = true;
    const flags = { ...(r.flags || {}), is_committed: false };
    const tx = r.transaction
      ? { ...r.transaction, classification_review_confirmed_at: null }
      : r.transaction;
    return {
      ...r,
      flags,
      classification_review_confirmed_at: null,
      source_state: r.source_state === 'confirmed' ? 'tagged_by_rules' : r.source_state,
      transaction: tx
    } as CanonicalReviewRow;
  });

  if (!touched && !canAdjustCounts) return prev;

  const counts =
    canAdjustCounts && prev.counts
      ? {
          ...prev.counts,
          returned_rows: nextRows.length,
          committed_in_scope: cm - n,
          uncommitted_in_scope: uc + n
        }
      : prev.counts
        ? { ...prev.counts, returned_rows: nextRows.length }
        : prev.counts;

  return { ...prev, rows: nextRows, counts };
}

interface TxReviewPipelineCounts {
  total: number;
  confirmed: number;
  unconfirmed: number;
  total_txs_in_db: number;
  txs_owned_per_user: Array<{
    user_id: number;
    user_label: string;
    count: number;
  }>;
  committed_all_required_no_vat_conflict: number;
  uncommitted_total: number;
  uncommitted_missing_all_tags: number;
  uncommitted_no_tags_missing?: number;
  /** Uncommitted rows missing any required tag dimension (or VAT conflict). */
  uncommitted_still_need_tags?: number;
  /** Subset of still-need-tags with Revolut state completed or blank. */
  uncommitted_still_need_tags_revolut_complete_or_blank?: number;
  /** Subset of still-need-tags with declined/reverted/pending/etc. */
  uncommitted_still_need_tags_revolut_non_complete?: number;
  /**
   * Exclusive Applied-status buckets among still-need-tags rows.
   * Counts sum to ``uncommitted_still_need_tags`` (unlike overlapping missing-* dimension counts).
   */
  uncommitted_still_need_tags_by_reason?: Array<{ label: string; count: number }>;
  uncommitted_missing_cash_flow_heading_tag: number;
  /** Subset of missing heading: amount > 0 (In side from sign). */
  uncommitted_missing_heading_in_by_amount?: number;
  /** Subset of missing heading: amount missing or <= 0 (Out side from sign). */
  uncommitted_missing_heading_out_by_amount?: number;
  uncommitted_missing_supplier: number;
  uncommitted_missing_category: number;
  uncommitted_missing_vat_rate: number;
  /** Uncommitted (non–VAT-conflict) rows whose VAT cell is explicitly ``none`` (not blank). */
  uncommitted_vat_rate_value_none?: number;
  /** Uncommitted rows with ``flags.is_unclassified_needs_review`` (canonical unclassified queue). */
  uncommitted_unclassified_needs_review?: number;
  uncommitted_vat_conflict: number;
  uncommitted_in_out_heading_tags_count?: number;
  uncommitted_in_out_heading_tag_tx_ids?: number[];
  uncommitted_missing_permutations: Array<{
    label: string;
    count: number;
  }>;
  manual_untagged: number;
  manual_unclassified_needs_review: number;
  manual_conflicting_vat: number;
  manual_review_items_total: number;
  tagged_items_total: number;
  confirmed_items_total: number;
  review_rows_total: number;
  tx_reviews_visible_default: number;
  manual_nonheading_unconfirmed: number;
  excluded_from_tx_reviews: number;
  /** Uncommitted txs with this composite label on at least one category tag (canonical SQL). */
  applied_tag_in_at_none?: number;
  applied_tag_out_at_none?: number;
  applied_tag_unclassified_at_none?: number;
  uncommitted_category_tag_counts?: Array<{
    label: string;
    count: number;
  }>;
  pipeline_dashboard?: PipelineDashboardPayload;
}

type PipelineDashBreakdownEntry = { label: string; count: number };

type PipelineDashboardSubset = {
  subset_total: number;
  stage1: {
    in_heading_count: number;
    out_heading_count: number;
    na_heading_count?: number;
    heading_rows_total_sum: number;
    missing_neither_heading_count: number;
    both_in_out_heading_tag_count: number;
    both_in_out_heading_tag_tx_ids: number[];
  };
  stage2: {
    with_supplier: number;
    without_supplier: number;
    total: number;
    supplier_breakdown: PipelineDashBreakdownEntry[];
    multi_supplier_count: number;
    multi_supplier_tx_ids: number[];
  };
  stage3: {
    with_category: number;
    without_category: number;
    total: number;
    category_breakdown: PipelineDashBreakdownEntry[];
    multi_category_count: number;
    multi_category_tx_ids: number[];
  };
  stage4: {
    with_vat_rate: number;
    without_vat_rate: number;
    total: number;
    vat_breakdown: PipelineDashBreakdownEntry[];
    vat_conflict_count: number;
    multi_vat_rate_count: number;
    multi_vat_rate_tx_ids: number[];
  };
  missing_tags: {
    missing_all_four: number;
    no_tags_missing_ready: number;
    unclassified_needs_review: number;
    missing_single_dimension: Record<'Cash flow' | 'Supplier' | 'Category' | 'VAT Rate', number>;
    missing_permutations: Array<{ label: string; count: number }>;
  };
};

type PipelineDashboardPayload = {
  uncommitted: PipelineDashboardSubset;
  committed: PipelineDashboardSubset;
};

type PipelineBarTone = 'default' | 'missing' | 'problem' | 'ready';

/** Inline horizontal data-bar for Pipeline tab metric rows (scaled to total DB TXs). */
function PipelineMetricBar(props: {
  value: number;
  total: number;
  tone?: PipelineBarTone;
}): React.ReactElement {
  const value = Math.max(0, Number(props.value) || 0);
  const total = Math.max(0, Number(props.total) || 0);
  const pct = total > 0 ? Math.min(100, (value / total) * 100) : 0;
  const tone = props.tone || 'default';
  const fill =
    tone === 'missing'
      ? 'warning.main'
      : tone === 'problem'
        ? 'error.main'
        : tone === 'ready'
          ? 'success.main'
          : 'primary.main';
  return (
    <Box
      sx={{ display: 'flex', alignItems: 'center', minWidth: 112, maxWidth: 180 }}
      title={total > 0 ? `${pct.toFixed(1)}% of DB` : undefined}
    >
      <Box
        sx={{
          flex: 1,
          height: 8,
          bgcolor: 'action.hover',
          borderRadius: 1,
          overflow: 'hidden'
        }}
      >
        <Box
          sx={{
            width: `${pct}%`,
            height: '100%',
            bgcolor: fill,
            borderRadius: 1,
            transition: 'width 0.2s ease'
          }}
        />
      </Box>
    </Box>
  );
}

function PipelineBarTableCell(props: {
  value: number;
  total: number;
  tone?: PipelineBarTone;
}): React.ReactElement {
  return (
    <TableCell sx={{ width: 160, minWidth: 140, maxWidth: 180, py: 0.75 }}>
      <PipelineMetricBar value={props.value} total={props.total} tone={props.tone} />
    </TableCell>
  );
}

interface TxReviewPipelineCountsPayload {
  counts: TxReviewPipelineCounts;
  scope?: {
    requester_user_id?: number;
    requester_role?: string;
    requested_user_id?: number | null;
    target_user_id?: number;
    policy?: string;
  };
  target_user_ids?: number[];
}

interface RuleRunHistoryRow {
  tx_id: number;
  user_id: number;
  last_rules_run_at: string;
  last_ruleset_hash?: string | null;
  tx_date?: string | null;
  amount?: number | null;
  currency?: string | null;
  supplier?: string | null;
  description?: string | null;
  account_source?: string | null;
}

interface RuleRunHistoryBucket {
  bucket_start: string;
  bucket_end: string;
  count: number;
  tx_ids: number[];
}

interface RuleRunHistoryPayload {
  rows: RuleRunHistoryRow[];
  histogram: RuleRunHistoryBucket[];
  meta?: {
    bucket_minutes?: number;
    returned_rows?: number;
    total_rows?: number;
    scope_user_ids?: number[];
  };
}

type UnifiedRowStatus = string;
type UnifiedRowSourceState = 'confirmed' | 'tagged_by_rules' | 'needs_review' | 'review_and_confirm';

function sanitizeHeadingFilterValues(values: unknown[]): Array<'In' | 'Out' | 'N/A'> {
  return (values || [])
    .map((v) => String(v || '').trim())
    .map((raw) => {
      const lower = raw.toLowerCase();
      if (raw === 'In' || lower === 'income') return 'In';
      if (raw === 'Out' || lower === 'expense') return 'Out';
      if (raw === 'N/A' || lower === 'n/a' || lower === 'na') return 'N/A';
      return '';
    })
    .filter((v): v is 'In' | 'Out' | 'N/A' => v === 'In' || v === 'Out' || v === 'N/A');
}

const TAGGED_BY_ALL_RULES_STATUS = 'No Tags Missing';
const MISSING_DIM_CASH_FLOW = 'Cash flow';
const MISSING_SUPPLIER = 'Supplier';
const MISSING_CATEGORY = 'Category';
const MISSING_VAT = 'VAT Rate';
/** Exact `buildUnifiedCompletenessStatus` string when cashflow, supplier, category, and VAT are all missing. */
const ALL_TAGS_MISSING_STATUS = `${MISSING_DIM_CASH_FLOW}, ${MISSING_SUPPLIER}, ${MISSING_CATEGORY} & ${MISSING_VAT} Missing`;
const STATUS_FILTER_ANY_CASH_FLOW_MISSING = '__status_any_cash_flow_missing__';
const STATUS_FILTER_ANY_SUPPLIER_MISSING = '__status_any_supplier_missing__';
const STATUS_FILTER_ANY_CATEGORY_MISSING = '__status_any_category_missing__';
const STATUS_FILTER_ANY_VAT_MISSING = '__status_any_vat_missing__';
const STATUS_FILTER_MISSING_ALL_TAGS = '__status_missing_all_tags__';
const STATUS_FILTER_VAT_CONFLICT = '__status_vat_conflict__';
const STATUS_FILTER_MISSING_DIMS_PREFIX = '__status_missing_dims__:';
const STATUS_FILTER_AGGREGATE_VALUES = [
  STATUS_FILTER_ANY_CASH_FLOW_MISSING,
  STATUS_FILTER_ANY_SUPPLIER_MISSING,
  STATUS_FILTER_ANY_CATEGORY_MISSING,
  STATUS_FILTER_ANY_VAT_MISSING,
  STATUS_FILTER_MISSING_ALL_TAGS,
  STATUS_FILTER_VAT_CONFLICT
] as const;

const PIPELINE_MISSING_DIM_ORDER = [MISSING_DIM_CASH_FLOW, 'Supplier', 'Category', 'VAT Rate'] as const;
type PipelineMissingDim = (typeof PIPELINE_MISSING_DIM_ORDER)[number];

function isMissingSupplierValue(value: string | null | undefined): boolean {
  return !(value || '').trim() || (value || '').trim() === '—';
}

function isMissingCashFlowHeadingValue(value: string | null | undefined): boolean {
  return !(value || '').trim() || (value || '').trim() === '—';
}

function isMissingCategoryValue(value: string | null | undefined): boolean {
  const v = (value || '').trim().toLowerCase();
  return (
    !v ||
    v === '—' ||
    v === 'unclassified' ||
    v === 'unclassified - needs review' ||
    v === 'confirmed (historic)' ||
    v === 'in' ||
    v === 'out' ||
    v === 'n/a' ||
    v === 'na' ||
    v === 'income' ||
    v === 'expense'
  );
}

function isMissingVatRateValue(value: string | null | undefined): boolean {
  const v = (value || '').trim();
  if (!v) return true;
  if (v === '-' || v === '—') return true;
  const l = v.toLowerCase();
  if (l === 'none' || l === 'n/a' || l === 'na') return true;
  return false;
}

/** Lowercase key after canonicalize — used for Irish schedule style rates + exempt/none. */
function vatRateChipPaletteKey(value: string | null | undefined): string {
  if (isMissingVatRateValue(value)) return '';
  return (canonicalizeVatRate(value) || '').trim().toLowerCase();
}

function vatRateChipSxForKey(paletteKey: string) {
  /** Filled pastel + darker text, same visual language as category chips (no outline). */
  const defaults = {
    border: 'none',
    boxShadow: 'none'
  };
  const palette: Record<string, { bg: string; fg: string }> = {
    '23%': { bg: '#E7F5FF', fg: '#0A4B84' },
    '13.5%': { bg: '#E6FCF5', fg: '#0B6B44' },
    '9%': { bg: '#E3FAFC', fg: '#0B5D6B' },
    '0%': { bg: '#F1F3F5', fg: '#344054' },
    exempt: { bg: '#F3F0FF', fg: '#4A2A8A' },
    none: { bg: '#F1F3F5', fg: '#344054' }
  };
  const pick = palette[paletteKey];
  if (pick) {
    return {
      ...defaults,
      backgroundColor: pick.bg,
      color: pick.fg,
      borderColor: pick.bg
    };
  }
  if (paletteKey) {
    return {
      ...defaults,
      backgroundColor: '#FFF3BF',
      color: '#8A5A00',
      borderColor: '#FFF3BF'
    };
  }
  return {
    ...defaults,
    backgroundColor: '#F1F3F5',
    color: '#344054',
    borderColor: '#F1F3F5'
  };
}

function CanonicalVatRateChip(props: { value: string | null | undefined; maxWidth?: number }): React.ReactElement {
  const { value, maxWidth = 78 } = props;
  if (isMissingVatRateValue(value)) {
    return <span>—</span>;
  }
  const label = (canonicalizeVatRate(value) || '').trim();
  if (!label || isMissingVatRateValue(label)) {
    return <span>—</span>;
  }
  const key = vatRateChipPaletteKey(value);
  return (
    <Chip
      size="small"
      variant="filled"
      label={label}
      sx={{
        maxWidth,
        '& .MuiChip-label': { px: 0.75, overflow: 'hidden', textOverflow: 'ellipsis' },
        ...vatRateChipSxForKey(key)
      }}
    />
  );
}

/** User-facing message when commit must be blocked (null = allowed). */
function unifiedRowCommitBlockedMessage(row: UnifiedReviewRow, rowTags?: UnifiedTxCategoryTagChip[] | undefined): string | null {
  if (row.source_state === 'confirmed') return null;
  const st = String(unifiedRowDisplayedCompletenessStatus(row, rowTags) || '').trim();
  if (st === TAGGED_BY_ALL_RULES_STATUS) return null;
  if (st === 'VAT Conflict') {
    return (
      `Transaction #${row.id} cannot be committed while it has a VAT conflict. ` +
      `Resolve it under the VAT Conflicts tab first.`
    );
  }
  return (
    `Transaction #${row.id} cannot be committed yet — tags are incomplete (status: "${st}"). ` +
    `Add a cashflow tag (In/Out/N/A), supplier, a granular category (not only cashflow), and a VAT rate until Status shows "${TAGGED_BY_ALL_RULES_STATUS}".`
  );
}

function buildUnifiedCompletenessStatus(row: {
  cashFlow?: string | null | undefined;
  supplier: string | null | undefined;
  category: string | null | undefined;
  vat_rate: string | null | undefined;
}): UnifiedRowStatus {
  const missing: string[] = [];
  if (isMissingCashFlowHeadingValue(row.cashFlow)) missing.push(MISSING_DIM_CASH_FLOW);
  if (isMissingSupplierValue(row.supplier)) missing.push(MISSING_SUPPLIER);
  if (isMissingCategoryValue(row.category)) missing.push(MISSING_CATEGORY);
  if (isMissingVatRateValue(row.vat_rate)) missing.push(MISSING_VAT);
  if (missing.length === 0) return TAGGED_BY_ALL_RULES_STATUS;
  if (missing.length === 1) return `${missing[0]} Missing`;
  if (missing.length === 2) return `${missing[0]} & ${missing[1]} Missing`;
  if (missing.length === 3) return `${missing[0]}, ${missing[1]} & ${missing[2]} Missing`;
  return `${missing[0]}, ${missing[1]}, ${missing[2]} & ${missing[3]} Missing`;
}

/**
 * Same supplier/category/VAT primitives the unified grid uses for cells (prefer loaded tag chips, else canonical row).
 */
function unifiedRowEffectiveTagDerivedCells(
  row: UnifiedReviewRow,
  rowTags: UnifiedTxCategoryTagChip[] | undefined
): { supplier: string; category: string; vat_rate: string | null } {
  const tags = rowTags ?? [];
  let supplierShown = '';
  const supplierSeen = new Set<string>();
  for (const t of tags) {
    const raw = String(t.supplier || '').trim();
    if (!raw) continue;
    const lk = raw.toLowerCase();
    if (supplierSeen.has(lk)) continue;
    supplierSeen.add(lk);
    supplierShown = raw;
    break;
  }
  if (!supplierShown) supplierShown = (row.supplier || '').trim() || '—';

  const granularFromTags = tags.filter((t) => !isTopLevelCashflowHeadingName(t.category));
  const granularFirst = granularFromTags.find((t) => !isMissingCategoryValue(t.category));
  const categoryShown = granularFirst ? granularFirst.category : row.category || '—';

  const vatsWithRates = tags.filter((t) => String(t.vatRate || '').trim().length > 0);
  let vatShown: string | null;
  if (vatsWithRates.length > 0) {
    const raw = vatsWithRates[0].vatRate;
    const c = canonicalizeVatRate(raw);
    const label = ((c || '').trim() ? c : raw) ?? '';
    vatShown = String(label || '').trim() ? String(label || raw) : raw;
  } else {
    vatShown = row.vat_rate;
  }

  return { supplier: supplierShown, category: categoryShown, vat_rate: vatShown };
}

/**
 * Completeness Status chip aligned with Supplier / Category / VAT columns (tags hydrate async from category-tags API).
 */
function unifiedRowDisplayedCompletenessStatus(
  row: UnifiedReviewRow,
  rowTags: UnifiedTxCategoryTagChip[] | undefined
): UnifiedRowStatus {
  const tags = rowTags ?? [];
  const nonHeading = tags.filter((t) => !isTopLevelCashflowHeadingName(t.category));
  const distinctVatKeys = new Set(nonHeading.map((t) => vatFilterKeyFromRaw(t.vatRate)));
  if (row.flags?.has_vat_conflict || distinctVatKeys.size > 1) {
    return 'VAT Conflict';
  }

  const cashflowFromTags = tags.find((t) => isTopLevelCashflowHeadingName(t.category));
  const cashFlow = cashflowFromTags?.category || row.cashFlow;
  const { supplier, category, vat_rate } = unifiedRowEffectiveTagDerivedCells(row, rowTags);
  return buildUnifiedCompletenessStatus({ cashFlow, supplier, category, vat_rate });
}

function unifiedStatusMissingCount(status: UnifiedRowStatus): number {
  const str = String(status ?? '');
  if (str === TAGGED_BY_ALL_RULES_STATUS) return 0;
  let c = 0;
  if (str.includes(MISSING_DIM_CASH_FLOW)) c += 1;
  if (str.includes(MISSING_SUPPLIER)) c += 1;
  if (str.includes(MISSING_CATEGORY)) c += 1;
  if (str.includes(MISSING_VAT)) c += 1;
  return c || 1;
}

function compareUnifiedStatusLabel(a: UnifiedRowStatus, b: UnifiedRowStatus): number {
  const ar = unifiedStatusMissingCount(a);
  const br = unifiedStatusMissingCount(b);
  if (ar !== br) return ar - br;
  return String(a ?? '').localeCompare(String(b ?? ''), undefined, { sensitivity: 'base' });
}

type UnifiedStatusVisual = { chart: string; chipBg: string; chipFg: string };

/** Softer table chips (tint + dark text); ``chart`` is slightly stronger for stacked bar visibility. */
function unifiedStatusVisual(status: UnifiedRowStatus): UnifiedStatusVisual {
  const s = String(status ?? '').trim().toLowerCase();
  const ok: UnifiedStatusVisual = {
    chart: '#5CBD88',
    chipBg: '#DCFCE7',
    chipFg: '#166534'
  };
  const supplierOnly: UnifiedStatusVisual = {
    chart: '#5B9BD5',
    chipBg: '#DBEAFE',
    chipFg: '#1E40AF'
  };
  const categoryOnly: UnifiedStatusVisual = {
    chart: '#9B85E0',
    chipBg: '#EDE9FE',
    chipFg: '#5B21B6'
  };
  const vatOnly: UnifiedStatusVisual = {
    chart: '#E5A23F',
    chipBg: '#FEF3C7',
    chipFg: '#B45309'
  };
  const supplierCategory: UnifiedStatusVisual = {
    chart: '#3EAEA2',
    chipBg: '#CCFBF1',
    chipFg: '#115E59'
  };
  const supplierVat: UnifiedStatusVisual = {
    chart: '#D966A5',
    chipBg: '#FCE7F3',
    chipFg: '#9D174D'
  };
  const categoryVat: UnifiedStatusVisual = {
    chart: '#E56767',
    chipBg: '#FEE2E2',
    chipFg: '#B91C1C'
  };
  const allThree: UnifiedStatusVisual = {
    chart: '#6B7280',
    chipBg: '#E5E7EB',
    chipFg: '#1F2937'
  };
  if (s === TAGGED_BY_ALL_RULES_STATUS.toLowerCase()) return ok;
  if (s === `${MISSING_SUPPLIER.toLowerCase()} missing`) return supplierOnly;
  if (s === `${MISSING_CATEGORY.toLowerCase()} missing`) return categoryOnly;
  if (s === `${MISSING_VAT.toLowerCase()} missing`) return vatOnly;
  if (s === `${MISSING_SUPPLIER.toLowerCase()} & ${MISSING_CATEGORY.toLowerCase()} missing`) return supplierCategory;
  if (s === `${MISSING_SUPPLIER.toLowerCase()} & ${MISSING_VAT.toLowerCase()} missing`) return supplierVat;
  if (s === `${MISSING_CATEGORY.toLowerCase()} & ${MISSING_VAT.toLowerCase()} missing`) return categoryVat;
  if (
    s ===
      `${MISSING_SUPPLIER.toLowerCase()}, ${MISSING_CATEGORY.toLowerCase()} & ${MISSING_VAT.toLowerCase()} missing` ||
    s === ALL_TAGS_MISSING_STATUS.toLowerCase()
  ) {
    return allThree;
  }
  const missing = unifiedStatusMissingCount(status);
  if (missing <= 0) return ok;
  if (missing === 1) return supplierOnly;
  if (missing === 2) return categoryOnly;
  return categoryVat;
}

function unifiedStatusFillColor(status: UnifiedRowStatus): string {
  return unifiedStatusVisual(status).chart;
}

function unifiedStatusChipSx(
  status: UnifiedRowStatus,
  opts?: { maxWidth?: number; labelNowrap?: boolean }
): Record<string, unknown> {
  const v = unifiedStatusVisual(status);
  const maxW = opts?.maxWidth;
  return {
    backgroundColor: v.chipBg,
    color: v.chipFg,
    border: 'none',
    boxShadow: 'none',
    borderColor: v.chipBg,
    fontWeight: 400,
    ...(maxW != null ? { maxWidth: maxW } : {}),
    '& .MuiChip-label':
      opts?.labelNowrap === false
        ? { whiteSpace: 'normal' as const }
        : {
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap' as const
          }
  };
}

function unifiedStatusFilterOptionLabel(statusValue: string): string {
  if (statusValue === STATUS_FILTER_ANY_CASH_FLOW_MISSING) return `${MISSING_DIM_CASH_FLOW} Missing`;
  if (statusValue === STATUS_FILTER_ANY_SUPPLIER_MISSING) return `${MISSING_SUPPLIER} Missing`;
  if (statusValue === STATUS_FILTER_ANY_CATEGORY_MISSING) return `${MISSING_CATEGORY} Missing`;
  if (statusValue === STATUS_FILTER_ANY_VAT_MISSING) return `${MISSING_VAT} Missing`;
  if (statusValue === STATUS_FILTER_MISSING_ALL_TAGS) return 'Missing All tags';
  if (statusValue === STATUS_FILTER_VAT_CONFLICT) return 'VAT Conflict';
  if (statusValue.startsWith(STATUS_FILTER_MISSING_DIMS_PREFIX)) {
    const raw = statusValue.slice(STATUS_FILTER_MISSING_DIMS_PREFIX.length);
    const dims = raw
      .split('|')
      .map((d) => d.trim())
      .filter((d) => d.length > 0);
    return dims.length > 0 ? `Missing ${dims.join(' + ')}` : 'Missing tags';
  }
  return statusValue;
}

function unifiedStatusFilterMatches(
  row: UnifiedReviewRow,
  filterValue: string,
  rowTags?: UnifiedTxCategoryTagChip[] | undefined
): boolean {
  const rowStatus = unifiedRowDisplayedCompletenessStatus(row, rowTags);
  const hasVatConflictFlag = Boolean(row.flags?.has_vat_conflict);
  if (filterValue.startsWith(STATUS_FILTER_MISSING_DIMS_PREFIX)) {
    const raw = filterValue.slice(STATUS_FILTER_MISSING_DIMS_PREFIX.length);
    const dims = raw
      .split('|')
      .map((d) => d.trim())
      .filter((d) => d.length > 0);
    if (dims.length === 0) return true;
    const derived = unifiedRowEffectiveTagDerivedCells(row, rowTags);
    return dims.every((dim) => {
      if (dim === MISSING_DIM_CASH_FLOW) return isMissingCashFlowHeadingValue(row.cashFlow);
      if (dim === MISSING_SUPPLIER) return isMissingSupplierValue(derived.supplier);
      if (dim === MISSING_CATEGORY) return isMissingCategoryValue(derived.category);
      if (dim === MISSING_VAT) return isMissingVatRateValue(derived.vat_rate);
      return false;
    });
  }
  if (filterValue === STATUS_FILTER_ANY_CASH_FLOW_MISSING) return rowStatus.includes(MISSING_DIM_CASH_FLOW);
  if (filterValue === STATUS_FILTER_ANY_SUPPLIER_MISSING) return rowStatus.includes(MISSING_SUPPLIER);
  if (filterValue === STATUS_FILTER_ANY_CATEGORY_MISSING) return rowStatus.includes(MISSING_CATEGORY);
  if (filterValue === STATUS_FILTER_ANY_VAT_MISSING) return rowStatus.includes(MISSING_VAT);
  if (filterValue === STATUS_FILTER_MISSING_ALL_TAGS) return rowStatus === ALL_TAGS_MISSING_STATUS;
  if (filterValue === STATUS_FILTER_VAT_CONFLICT) {
    return String(rowStatus || '').trim().toLowerCase() === 'vat conflict' || Boolean(hasVatConflictFlag);
  }
  return rowStatus === filterValue;
}

function buildMissingDimsStatusFilterValue(dimensions: string[]): string {
  const normalized = Array.from(
    new Set(
      (dimensions || [])
        .map((d) => String(d || '').trim())
        .filter((d): d is PipelineMissingDim =>
          (PIPELINE_MISSING_DIM_ORDER as readonly string[]).includes(d)
        )
    )
  );
  const ordered = PIPELINE_MISSING_DIM_ORDER.filter((d) => normalized.includes(d));
  return `${STATUS_FILTER_MISSING_DIMS_PREFIX}${ordered.join('|')}`;
}

function parsePipelineMissingDimsLabel(label: string): string[] {
  return String(label || '')
    .split('+')
    .map((part) => part.trim())
    .filter((part) => part.length > 0);
}

type UnifiedChartBreakdown =
  | 'status'
  | 'category'
  | 'cash_flow'
  | 'account'
  | 'supplier'
  | 'amount_income'
  | 'amount_expense'
  | 'balance';

const UNIFIED_CHART_BREAKDOWN_OPTIONS: Array<{ value: UnifiedChartBreakdown; label: string }> = [
  { value: 'status', label: 'Status' },
  { value: 'category', label: 'Categories' },
  { value: 'cash_flow', label: 'Cash flow' },
  { value: 'account', label: 'Account' },
  { value: 'supplier', label: 'Supplier' },
  { value: 'amount_income', label: 'Amount of income' },
  { value: 'amount_expense', label: 'Amount of expense' },
  { value: 'balance', label: 'Balance' }
];

/** Linear least-squares fit y ≈ a + b·x for a light trend line over daily balance points. */
function linearLeastSquaresLine(xs: number[], ys: number[]): { a: number; b: number } {
  const n = xs.length;
  if (n === 0) return { a: 0, b: 0 };
  if (n === 1) return { a: ys[0] ?? 0, b: 0 };
  let sumX = 0;
  let sumY = 0;
  let sumXY = 0;
  let sumXX = 0;
  for (let i = 0; i < n; i++) {
    sumX += xs[i]!;
    sumY += ys[i]!;
    sumXY += xs[i]! * ys[i]!;
    sumXX += xs[i]! * xs[i]!;
  }
  const denom = n * sumXX - sumX * sumX;
  if (Math.abs(denom) < 1e-12) return { a: sumY / n, b: 0 };
  const b = (n * sumXY - sumX * sumY) / denom;
  const a = (sumY - b * sumX) / n;
  return { a, b };
}

const CHART_SERIES_PALETTE = [
  '#2563EB',
  '#7C3AED',
  '#0891B2',
  '#16A34A',
  '#EA580C',
  '#DC2626',
  '#4F46E5',
  '#0D9488',
  '#A16207',
  '#BE185D',
  '#0369A1',
  '#6D28D9'
];

const OUTPUT_COLUMN_SX = {
  backgroundColor: 'rgba(37, 99, 235, 0.08)',
  textAlign: 'left'
} as const;

const RULE_ACCOUNT_COLUMN_SX = {
  width: 78,
  minWidth: 78,
  whiteSpace: 'nowrap'
} as const;

/** Supplier rules “Output: Canonical Supplier” — keep one line like shorter names (ellipsis + hover for full text). */
const SUPPLIER_RULE_CANONICAL_CELL_SX = {
  ...OUTPUT_COLUMN_SX,
  maxWidth: 220,
  whiteSpace: 'nowrap',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  verticalAlign: 'middle'
} as const;

const SUPPLIER_RULE_CANONICAL_LINK_BUTTON_SX = {
  p: 0,
  minWidth: 0,
  maxWidth: '100%',
  width: '100%',
  textTransform: 'none',
  justifyContent: 'flex-start',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
  display: 'block',
  textAlign: 'left'
} as const;

/** Default dense header look (matches inspected “Actions”: #eaeaea, neutral text, bottom border). */
const NEUTRAL_TABLE_HEAD_CELL_SX = {
  backgroundColor: '#eaeaea',
  borderBottom: '1px solid #c1c1c1',
  color: 'rgba(43, 43, 43, 0.87)',
  fontWeight: 500,
  lineHeight: '1.5rem',
  padding: '6px 16px'
} as const;

/** Tx Reviews unified table: grey header cells (not raw-tx blue / output yellow). Overrides CardContent spread when needed. */
const UNIFIED_GREY_HEAD_PLAIN_SX = {
  ...NEUTRAL_TABLE_HEAD_CELL_SX,
  '&&': {
    backgroundColor: '#eaeaea !important'
  }
} as const;

const UNIFIED_GREY_HEAD_SORT_SX = {
  ...UNIFIED_GREY_HEAD_PLAIN_SX,
  '& .MuiTableSortLabel-root': {
    color: 'rgba(43, 43, 43, 0.87)'
  },
  '& .MuiTableSortLabel-root.Mui-active': {
    color: 'rgba(43, 43, 43, 0.87)'
  },
  '& .MuiTableSortLabel-icon': {
    color: 'rgba(43, 43, 43, 0.87) !important'
  }
} as const;

/** Enlarged Actions column controls (width recovered from a slightly narrower Description). */
const UNIFIED_ACTION_BTN_PX = 24;
const UNIFIED_ACTION_CHIP_PX = 28;
/** Max cluster: comment, flag, similar, JSON, isolate, play, edit/close, save, commit, info. */
const UNIFIED_ACTION_ICON_COUNT_MAX = 10;
const UNIFIED_ACTIONS_COL_PX =
  UNIFIED_ACTION_ICON_COUNT_MAX * UNIFIED_ACTION_BTN_PX + UNIFIED_ACTION_CHIP_PX;

/**
 * Unified Tx Review column widths under `table-layout: fixed; width: 100%`.
 * Compact columns: fixed px (pinned). Description/Supplier/Category share leftover only.
 */
const UNIFIED_COL = {
  /** Native checkbox only — avoid MUI `padding="checkbox"` (it forces ~48px). */
  checkbox: 20,
  /** 5-digit ledger ids at 0.75rem tabular-nums. */
  id: 36,
  /** e.g. -€12.79 / -€1,234.56 — left of Date (header “Amount”). */
  amount: 78,
  /** `DD/MM/YYYY` @ 0.75rem + sort caret. */
  date: 72,
  /** Cardholder Who chip (Om / Dw / Le / He / EFT / ?). */
  who: 48,
  /** UK / IE region codes — wide enough for header “A/C” + sort caret. */
  account: 44,
  cashFlow: 42,
  vat: 44,
  /** Prop chips (2SA / 28LC / …); +50% vs prior 45px — width taken from Category. */
  prop: 68,
  /** Exact fit for enlarged Actions icon cluster (see UNIFIED_ACTION_*). */
  actions: UNIFIED_ACTIONS_COL_PX
} as const;

/**
 * Flex text/chip columns (Description / Supplier / Category): no fixed width — they share
 * only leftover space after compact px cols. Mixing % + px previously caused horizontal scroll.
 */
const UNIFIED_FLEX_CELL_SX = {
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap' as const,
  maxWidth: 0,
  width: 'auto',
  boxSizing: 'border-box' as const,
  px: '4px'
} as const;

/** @deprecated prefer UNIFIED_FLEX_CELL_SX */
const UNIFIED_TRUNC_CELL_SX = UNIFIED_FLEX_CELL_SX;

const UNIFIED_CHIP_TRUNC_SX = {
  maxWidth: '100%',
  height: 22,
  '& .MuiChip-label': {
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    px: 0.75
  }
} as const;

/** Pin a compact column so leftover table width cannot inflate it. */
function unifiedCompactColSx(px: number) {
  return {
    width: `${px}px !important`,
    minWidth: `${px}px !important`,
    maxWidth: `${px}px !important`,
    boxSizing: 'border-box' as const,
    overflow: 'hidden' as const
  };
}

/**
 * Like {@link unifiedCompactColSx} but never ellipsis-truncates cell content.
 * Width must be computed from the widest value in the column.
 */
function unifiedFitColSx(px: number) {
  return {
    width: `${px}px !important`,
    minWidth: `${px}px !important`,
    maxWidth: `${px}px !important`,
    boxSizing: 'border-box' as const,
    whiteSpace: 'nowrap' as const,
    overflow: 'visible' as const
  };
}

/** Approximate rendered width (px) for 0.75rem tabular-ish UI text. */
function estimateUnifiedTextPx(text: string): number {
  let w = 0;
  for (const ch of String(text || '')) {
    if (ch >= '0' && ch <= '9') w += 7.4;
    else if (ch === '.' || ch === ',' || ch === ' ') w += 3.6;
    else if (ch === '€' || ch === '£' || ch === '¥') w += 8.5;
    else if (ch === '-' || ch === '+') w += 5.5;
    else w += 7.2;
  }
  return w;
}

/** Small filled Chip + delete icon (Cash Flow column). */
function estimateUnifiedChipPx(label: string): number {
  // label + horizontal padding + delete icon + borders
  return Math.ceil(estimateUnifiedTextPx(label) + 14 + 22 + 4);
}

function clampUnifiedColPx(px: number, min: number, max: number): number {
  return Math.min(max, Math.max(min, Math.ceil(px)));
}

/** Narrow columns cannot use `NEUTRAL_TABLE_HEAD_CELL_SX`’s 16px horizontal padding with tiny widths — it overflows into the next column under `table-layout: fixed`. */
const UNIFIED_TABLE_CHECKBOX_HEAD_SX = {
  ...UNIFIED_GREY_HEAD_PLAIN_SX,
  ...unifiedCompactColSx(UNIFIED_COL.checkbox),
  padding: '4px 0 !important',
  verticalAlign: 'middle' as const,
  textAlign: 'center' as const
} as const;

const UNIFIED_TABLE_ID_HEAD_SX = {
  ...UNIFIED_GREY_HEAD_SORT_SX,
  ...unifiedCompactColSx(UNIFIED_COL.id),
  padding: '4px 1px !important',
  '& .MuiTableSortLabel-root': {
    color: 'rgba(43, 43, 43, 0.87)',
    fontSize: '0.75rem'
  },
  '& .MuiTableSortLabel-root.Mui-active': {
    color: 'rgba(43, 43, 43, 0.87)'
  },
  '& .MuiTableSortLabel-icon': {
    fontSize: '0.9rem !important',
    marginLeft: '0 !important',
    marginRight: '0 !important',
    color: 'rgba(43, 43, 43, 0.87) !important'
  }
} as const;

/** Date header/body — match ID denseness (0.75rem tabular). */
const UNIFIED_TABLE_DATE_CELL_SX = {
  ...unifiedCompactColSx(UNIFIED_COL.date),
  px: '2px !important',
  py: 0.5,
  fontSize: '0.75rem',
  fontVariantNumeric: 'tabular-nums' as const,
  whiteSpace: 'nowrap' as const
} as const;

const UNIFIED_TABLE_AMOUNT_CELL_SX = {
  ...unifiedCompactColSx(UNIFIED_COL.amount),
  px: '2px !important'
} as const;

/**
 * Unified Tx review — four classification columns (Cash flow In/Out, Supplier, Category, VAT rate):
 * `#888edf` + white sort labels (devtools `rgb(136, 142, 223)`). CardContent paints thead grey first;
 * keep `&&` + !important on fills.
 */
const UNIFIED_CLASSIFICATION_BAND_HEAD_SX = {
  ...NEUTRAL_TABLE_HEAD_CELL_SX,
  color: '#ffffff',
  padding: '4px 4px',
  '&&': {
    backgroundColor: '#888edf !important'
  },
  '& .MuiTableSortLabel-root': {
    color: '#ffffff',
    fontSize: '0.8rem'
  },
  '& .MuiTableSortLabel-root.Mui-active': {
    color: '#ffffff'
  },
  '& .MuiTableSortLabel-icon': {
    fontSize: '0.95rem !important',
    marginLeft: '0 !important',
    color: '#ffffff !important'
  }
} as const;

const UNIFIED_HEADING_COLUMN_HEAD_SX = {
  ...UNIFIED_CLASSIFICATION_BAND_HEAD_SX
} as const;

const UNIFIED_SUPPLIER_COLUMN_HEAD_SX = {
  ...UNIFIED_CLASSIFICATION_BAND_HEAD_SX
} as const;

const UNIFIED_CATEGORY_COLUMN_HEAD_SX = {
  ...UNIFIED_CLASSIFICATION_BAND_HEAD_SX
} as const;

const UNIFIED_VAT_RATE_COLUMN_HEAD_SX = {
  ...UNIFIED_CLASSIFICATION_BAND_HEAD_SX
} as const;

const UNIFIED_PROP_COLUMN_HEAD_SX = {
  ...UNIFIED_CLASSIFICATION_BAND_HEAD_SX
} as const;

/** Portfolio nicknames offered in the Tx Review Prop column (None = not property-specific). */
const TX_PROP_NICKNAMES = ['2SA', '28LC', '87HP', '67RR'] as const;

/** Opaque sky tint (was rgba(14,165,233,0.16) over white) so sticky headers don't show body text through. */
const RAW_TX_HEADER_BG = '#d8f0fb';

const RAW_TX_HEADER_SX = {
  ...NEUTRAL_TABLE_HEAD_CELL_SX,
  padding: '4px 4px',
  backgroundColor: RAW_TX_HEADER_BG,
  '&&': {
    backgroundColor: `${RAW_TX_HEADER_BG} !important`
  },
  '&.MuiTableCell-stickyHeader': {
    backgroundColor: `${RAW_TX_HEADER_BG} !important`
  },
  '& .MuiTableSortLabel-root': {
    color: 'rgb(12, 74, 110)',
    fontSize: '0.8rem'
  },
  '& .MuiTableSortLabel-root.Mui-active': {
    color: 'rgb(3, 105, 161)'
  },
  '& .MuiTableSortLabel-icon': {
    fontSize: '0.95rem !important',
    marginLeft: '0 !important',
    color: 'rgb(3, 105, 161) !important'
  }
} as const;

/** @deprecated Prefer UNIFIED_COL.*; kept as aliases for any straggling references. */
const UNIFIED_REVIEW_TABLE_DATE_COLUMN_WIDTH = UNIFIED_COL.date;
const UNIFIED_REVIEW_TABLE_ACCOUNT_COLUMN_WIDTH = UNIFIED_COL.account;
const UNIFIED_REVIEW_TABLE_CASH_FLOW_COLUMN_WIDTH = UNIFIED_COL.cashFlow;
const UNIFIED_PROP_COLUMN_WIDTH = UNIFIED_COL.prop;

function formatUnifiedReviewTableDate(iso: string | null | undefined): string {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return String(iso);
  return d.toLocaleDateString('en-GB', { day: '2-digit', month: '2-digit', year: 'numeric' });
}

/** Side-panel Date: compact local `DD/MM/YYYY HH:mm` (no seconds / ISO clutter). */
function formatTxJsonPanelDateTime(iso: string | null | undefined): string {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return String(iso).trim() || '—';
  const date = d.toLocaleDateString('en-GB', { day: '2-digit', month: '2-digit', year: 'numeric' });
  const time = d.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit', hour12: false });
  return `${date} ${time}`;
}

/** Parse Revolut JSON text/object blobs used on ledger rows (`card_data`, etc.). */
function parseUnifiedTxJsonObject(value: unknown): Record<string, any> {
  if (value && typeof value === 'object' && !Array.isArray(value)) {
    return value as Record<string, any>;
  }
  if (typeof value === 'string' && value.trim()) {
    try {
      const parsed = JSON.parse(value);
      if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) {
        return parsed as Record<string, any>;
      }
    } catch {
      /* ignore */
    }
  }
  return {};
}

/** Cardholder display name from nested `transaction.card_data` / `card` on a review row. */
function cardholderDisplayNameFromUnifiedRow(row: {
  transaction?: ReviewTx | Record<string, unknown> | null;
} | null | undefined): string {
  const tx = row?.transaction;
  if (!tx || typeof tx !== 'object') return '';
  const card = {
    ...parseUnifiedTxJsonObject((tx as any).card_data),
    ...parseUnifiedTxJsonObject((tx as any).card)
  };
  const parts = [card.first_name, card.last_name]
    .map((p) => (p == null ? '' : String(p).trim()))
    .filter(Boolean);
  if (parts.length) return parts.join(' ');
  const direct = String((tx as any).cardholder || (tx as any).card_holder || '').trim();
  return direct;
}

/** @deprecated Prefer ``WhoInitials`` / ``whoInitialsFromCardholder`` from ``utils/whoChip``. */
type UnifiedWhoInitials = WhoInitials;

const unifiedWhoInitialsFromCardholder = whoInitialsFromCardholder;

function unifiedWhoInitialsForRow(row: {
  transaction?: ReviewTx | Record<string, unknown> | null;
}): WhoInitials {
  return whoInitialsFromCardholder(cardholderDisplayNameFromUnifiedRow(row));
}

/** Open external http(s) URLs without waiting on React’s default-action scheduling. */
function openExternalHrefInNewTab(href: string): void {
  const url = String(href || '').trim();
  if (!/^https?:\/\//i.test(url)) return;
  const win = window.open(url, '_blank', 'noopener,noreferrer');
  if (win) win.opener = null;
}

/**
 * Merchant / external links on this heavy page: open the tab on pointerdown so a busy
 * main thread cannot delay the browser’s target=_blank navigation until after React work.
 * Cmd/Ctrl/Shift/Alt-click and middle-click keep native browser behavior.
 */
function TxExternalLink({
  href,
  children,
  title,
  variant = 'caption',
  underline = 'hover',
  sx
}: {
  href: string;
  children: React.ReactNode;
  title?: string;
  variant?: 'body1' | 'body2' | 'caption';
  underline?: 'none' | 'hover' | 'always';
  sx?: Record<string, unknown>;
}) {
  return (
    <MuiLink
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      underline={underline}
      title={title || href}
      variant={variant}
      sx={sx}
      onPointerDown={(e) => {
        e.stopPropagation();
        if (e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
        (e.currentTarget as HTMLElement).setAttribute('data-ext-opened', '1');
        e.preventDefault();
        openExternalHrefInNewTab(href);
      }}
      onClick={(e) => {
        e.stopPropagation();
        const el = e.currentTarget as HTMLElement;
        if (el.getAttribute('data-ext-opened') === '1') {
          el.removeAttribute('data-ext-opened');
          e.preventDefault();
          return;
        }
        // Keyboard activation (Enter) has no prior pointerdown open.
        if (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
        e.preventDefault();
        openExternalHrefInNewTab(href);
      }}
      onMouseDown={(e) => e.stopPropagation()}
    >
      {children}
    </MuiLink>
  );
}

function formatUnifiedReviewTableDateTooltip(iso: string | null | undefined): string {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return String(iso);
  return d.toLocaleString('en-GB', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: false
  });
}

/** Optional flags from canonical GET /review-rows; aligns Hide committed with backend `flags.is_committed`. */
interface UnifiedReviewRowFlags {
  is_committed?: boolean;
  needs_granular?: boolean;
  is_unclassified_needs_review?: boolean;
  has_vat_conflict?: boolean;
  is_tagged_by_rules?: boolean;
  has_manual_nonheading_nonunclassified_tag?: boolean;
  is_flagged?: boolean;
}

interface UnifiedReviewRow {
  key: string;
  id: number;
  /** Revolut-side transaction id (distinct from internal DB `id`). */
  revolut_id: string;
  tag_uid?: string | null;
  user_id: number | null;
  source_state: UnifiedRowSourceState;
  status: UnifiedRowStatus;
  date: string | null;
  amount: number | null;
  currency: string | null;
  /** Original merchant charge (pre-FX); also often nested on `transaction`. */
  bill_amount?: number | null;
  bill_currency?: string | null;
  reference: string | null;
  vat_rate: string | null;
  supplier: string;
  supplier_id: number | null;
  merchant_city: string | null;
  description: string;
  account_source: string | null;
  frequency: string | null;
  cashFlow: string;
  category: string;
  ruleId: number | null;
  tagOrigin: 'Auto' | 'Manual' | 'Hybrid' | 'Not tagged';
  /** Sidecar notes from `tx_appended_data` (private). */
  comment_internal?: string | null;
  /** Sidecar notes from `tx_appended_data` (accountant-safe). */
  comment_external?: string | null;
  /** Look-into-later marker from `tx_appended_data.is_flagged`. */
  is_flagged?: boolean | null;
  /** First property id (compat); prefer `property_ids` / `properties`. */
  property_id?: number | null;
  /** Nickname for first property (compat). */
  property_nickname?: string | null;
  /** One or more property associations (Prop tags). */
  property_ids?: number[] | null;
  property_nicknames?: Array<string | null> | null;
  properties?: Array<{ id: number; nickname?: string | null }> | null;
  /** Likely merchant URL from persisted AI Summary (`tx_appended_data.ai_summary_merchant_url`). */
  merchant_likely_url?: string | null;
  /** Up to 3 ranked AI Summary merchant URL alternatives (best first; includes primary). */
  merchant_url_alternatives?: string[] | null;
  /** Nested ledger payload from GET /review-rows (includes `card_data` for Who initials). */
  transaction?: ReviewTx | Record<string, unknown> | null;
  flags?: UnifiedReviewRowFlags;
  /** Present on canonical `/review-rows` rows when the API attaches conflict metadata. */
  vat_conflict_detail?: ReviewTx['vat_conflict_detail'];
  /** Revolut `state` (e.g. completed, declined, reverted); used for filters and row context. */
  revolut_state?: string | null;
  /** Rules vs probabilistic agreement (from GET /review-rows when feature enabled). */
  probabilistic_compare?: ProbabilisticComparePayload | null;
}

function unifiedRowHasComment(row: Pick<UnifiedReviewRow, 'comment_internal' | 'comment_external'> | null | undefined): boolean {
  return Boolean(String(row?.comment_internal || '').trim() || String(row?.comment_external || '').trim());
}

function unifiedRowIsFlagged(
  row: Pick<UnifiedReviewRow, 'is_flagged' | 'flags'> | null | undefined
): boolean {
  if (Boolean(row?.is_flagged)) return true;
  return Boolean(row?.flags?.is_flagged);
}

function unifiedRowPropertyIds(
  row: Pick<UnifiedReviewRow, 'property_id' | 'property_ids' | 'properties'> | null | undefined
): number[] {
  if (!row) return [];
  if (Array.isArray(row.property_ids) && row.property_ids.length > 0) {
    const seen = new Set<number>();
    const out: number[] = [];
    for (const raw of row.property_ids) {
      const n = Number(raw);
      if (!Number.isFinite(n) || n <= 0 || seen.has(n)) continue;
      seen.add(n);
      out.push(n);
    }
    return out;
  }
  if (Array.isArray(row.properties) && row.properties.length > 0) {
    const seen = new Set<number>();
    const out: number[] = [];
    for (const p of row.properties) {
      const n = Number(p?.id);
      if (!Number.isFinite(n) || n <= 0 || seen.has(n)) continue;
      seen.add(n);
      out.push(n);
    }
    return out;
  }
  if (row.property_id != null && Number.isFinite(Number(row.property_id)) && Number(row.property_id) > 0) {
    return [Number(row.property_id)];
  }
  return [];
}

function unifiedRowPropertyChips(
  row: Pick<
    UnifiedReviewRow,
    'property_id' | 'property_ids' | 'property_nickname' | 'property_nicknames' | 'properties'
  > | null | undefined
): Array<{ id: number; nickname: string }> {
  if (!row) return [];
  if (Array.isArray(row.properties) && row.properties.length > 0) {
    return row.properties
      .map((p) => ({
        id: Number(p?.id),
        nickname: String(p?.nickname || '').trim()
      }))
      .filter((p) => Number.isFinite(p.id) && p.id > 0 && p.nickname.length > 0);
  }
  const ids = unifiedRowPropertyIds(row);
  const nicks = Array.isArray(row.property_nicknames) ? row.property_nicknames : [];
  return ids.map((id, i) => ({
    id,
    nickname:
      String(nicks[i] || '').trim() ||
      (i === 0 ? String(row.property_nickname || '').trim() : '') ||
      String(id)
  }));
}

/** Minimal row when opening Similar TX from URL before `unifiedRows` contains that ledger id. */
function buildMinimalUnifiedReviewRowForSimilarDeepLink(id: number): UnifiedReviewRow {
  return {
    key: `deeplink-similar-${id}`,
    id,
    revolut_id: '',
    user_id: null,
    source_state: 'needs_review',
    status: buildUnifiedCompletenessStatus({
      supplier: '—',
      category: 'Unclassified',
      vat_rate: null
    }),
    date: null,
    amount: null,
    currency: null,
    reference: null,
    vat_rate: null,
    supplier: '—',
    supplier_id: null,
    merchant_city: null,
    description: '—',
    account_source: null,
    frequency: null,
    cashFlow: 'Out',
    category: 'Unclassified',
    ruleId: null,
    tagOrigin: 'Not tagged'
  };
}

function guessSpecialCaseDraftFromUnifiedRows(rows: UnifiedReviewRow[]): SpecialCaseRuleDraft {
  const first = rows[0];
  if (!first) {
    return {
      id: 0,
      name: 'Similar TX rule',
      priority: 100,
      is_active: true,
      account_scope: 'both',
      match_criteria: [],
      set_vat_rate: '',
      notes: ''
    };
  }
  const criteria: SpecialCaseMatchCriterionDraft[] = [];
  const desc = String(first.description || '').trim();
  const ref = String(first.reference || '').trim();
  const sup = String(first.supplier || '').trim();
  const uid = `sc-sim-${Date.now()}`;
  if (desc) {
    criteria.push({ id: `${uid}-d`, kind: 'description', value: desc.slice(0, 512), operator: 'contains' });
  } else if (ref) {
    criteria.push({ id: `${uid}-r`, kind: 'reference', value: ref.slice(0, 512), operator: 'contains' });
  } else if (sup) {
    criteria.push({ id: `${uid}-s`, kind: 'canonical_supplier', value: sup.slice(0, 512), operator: 'contains' });
  }
  const catName = String(first.category || '').trim();
  if (catName && !isTopLevelCashflowHeadingName(catName)) {
    criteria.push({ id: `${uid}-c`, kind: 'category', value: catName.slice(0, 512), operator: 'equals' });
  }
  const scopeRaw = String(first.account_source || '').toLowerCase();
  let account_scope: 'both' | 'ireland' | 'uk' = 'both';
  if (scopeRaw.includes('uk') && !scopeRaw.includes('ire')) account_scope = 'uk';
  else if (scopeRaw.includes('ire') || scopeRaw.includes('ireland')) account_scope = 'ireland';

  const vat = canonicalizeVatRate(first.vat_rate) || '';

  return {
    id: 0,
    name: rows.length > 1 ? `Similar TX batch (${rows.length} rows)` : `Similar TX #${first.id}`,
    priority: 100,
    is_active: true,
    account_scope,
    match_criteria:
      criteria.length > 0
        ? criteria
        : [{ id: `${uid}-blank`, kind: 'description', value: '', operator: 'contains' }],
    set_vat_rate: vat,
    notes: 'Pre-filled from Similar transactions — edit matchers and VAT output before saving.'
  };
}

/** Higher rank wins when two unified rows refer to the same ledger / Revolut transaction. */
function unifiedRowDuplicatePreferenceRank(sourceState: UnifiedReviewRow['source_state']): number {
  switch (sourceState) {
    case 'confirmed':
      return 4;
    case 'tagged_by_rules':
      return 3;
    case 'review_and_confirm':
      return 2;
    case 'needs_review':
      return 1;
    default:
      return 0;
  }
}

/** Prefer one representative row when legacy queues emit duplicates (e.g. tagged-items: one row per tag). */
function preferUnifiedRowWhenDuplicateLedgerTx(a: UnifiedReviewRow, b: UnifiedReviewRow): UnifiedReviewRow {
  const ra = unifiedRowDuplicatePreferenceRank(a.source_state);
  const rb = unifiedRowDuplicatePreferenceRank(b.source_state);
  if (ra !== rb) return ra > rb ? a : b;
  const ta = String(a.tag_uid || '').trim();
  const tb = String(b.tag_uid || '').trim();
  if (ta && !tb) return a;
  if (!ta && tb) return b;
  return a.key.localeCompare(b.key) <= 0 ? a : b;
}

/**
 * At most one row per internal ledger id, then at most one per non-empty revolut_id.
 * Prevents duplicate grid lines when multiple tags exist for one tx or rare duplicate Revolut ids in data.
 */
function dedupeUnifiedRowsOnePerLedgerTx(rows: UnifiedReviewRow[]): UnifiedReviewRow[] {
  if (rows.length <= 1) return rows;
  const byId = new Map<number, UnifiedReviewRow>();
  for (const row of rows) {
    const id = Number(row.id);
    if (!Number.isFinite(id) || id <= 0) continue;
    const prev = byId.get(id);
    if (!prev) {
      byId.set(id, row);
      continue;
    }
    byId.set(id, preferUnifiedRowWhenDuplicateLedgerTx(prev, row));
  }
  const stage1 = Array.from(byId.values());

  const byRid = new Map<string, UnifiedReviewRow>();
  const noRid: UnifiedReviewRow[] = [];
  for (const row of stage1) {
    const rid = String(row.revolut_id || '').trim().toLowerCase();
    if (!rid) {
      noRid.push(row);
      continue;
    }
    const prev = byRid.get(rid);
    if (!prev) {
      byRid.set(rid, row);
      continue;
    }
    byRid.set(rid, preferUnifiedRowWhenDuplicateLedgerTx(prev, row));
  }
  return [...noRid, ...Array.from(byRid.values())];
}

type UnifiedTxCategoryTagChip = {
  uid: string;
  category: string;
  vatRate: string;
  supplier: string;
  source: string;
};

/** Normalise GET /revolut-transaction-category-tags into the per–tx-id chip map used by the unified table. */
function buildUnifiedTxCategoryTagChipsByTxId(
  tags: any[],
  txIds: number[]
): Record<number, UnifiedTxCategoryTagChip[]> {
  const idSet = new Set(txIds.filter((x) => Number.isFinite(x) && x > 0));
  const next: Record<number, UnifiedTxCategoryTagChip[]> = {};
  idSet.forEach((id) => {
    next[id] = [];
  });
  (Array.isArray(tags) ? tags : []).forEach((tag: any) => {
    const txId = Number(tag?.revolut_transaction_id || 0);
    if (!idSet.has(txId)) return;
    const bucket = next[txId];
    if (!bucket) return;
    const source = String(tag?.source || '').trim() || 'rule';
    const catFromNested = tag?.category && typeof tag.category === 'object' ? tag.category.name : null;
    const catFromName =
      tag?.category_name != null && String(tag.category_name).trim() !== ''
        ? String(tag.category_name).trim()
        : '';
    const categoryRaw =
      catFromNested != null && String(catFromNested).trim() !== ''
        ? String(catFromNested).trim()
        : catFromName ||
          (typeof tag?.category === 'string' ? String(tag.category).trim() : '');
    // Canonical supplier / VAT chips have no category; do not invent "Uncategorized".
    const category =
      source === 'canonical_supplier' || source === 'canonical_vat'
        ? ''
        : categoryRaw || 'Uncategorized';
    bucket.push({
      uid: String(tag?.uid || ''),
      category,
      vatRate: String(tag?.vat_rate || '').trim(),
      supplier: String(tag?.supplier_name || '').trim(),
      source
    });
  });
  return next;
}


/**
 * Tags to delete when the bulk toolbar uses " - " for a column — matches the Needs Review table chip buckets
 * (heading / supplier / category / VAT column lists).
 */
function unifiedTxCategoryTagChipsMatchingBulkClearBucket(
  tags: UnifiedTxCategoryTagChip[] | undefined,
  bucket: 'heading' | 'supplier' | 'category' | 'vat'
): UnifiedTxCategoryTagChip[] {
  const list = (tags || []).filter((t) => String(t.uid || '').trim());
  if (bucket === 'heading') return list.filter((t) => isTopLevelCashflowHeadingName(t.category));
  if (bucket === 'supplier') {
    // Canonical ``cst:`` chips and any legacy category-tag rows that still carry supplier_name.
    return list.filter((t) => String(t.supplier || '').trim().length > 0);
  }
  if (bucket === 'category') {
    return list.filter(
      (t) =>
        t.source !== 'canonical_supplier' &&
        t.source !== 'canonical_vat' &&
        !String(t.uid || '').startsWith('cvt:') &&
        String(t.category || '').trim().length > 0 &&
        !isTopLevelCashflowHeadingName(t.category)
    );
  }
  // VAT column clear: prefer first-class VAT chips, else legacy rows that carry a rate.
  const canonicalVat = list.filter(
    (t) => t.source === 'canonical_vat' || String(t.uid || '').startsWith('cvt:')
  );
  if (canonicalVat.length > 0) return canonicalVat;
  return list.filter(
    (t) =>
      t.source !== 'canonical_supplier' &&
      String(t.vatRate || '').trim().length > 0
  );
}

/** Normalised lowercase key for VAT rate filter / row matching. */
function vatFilterKeyFromRaw(raw: string | null | undefined): string {
  const s = String(raw ?? '').trim();
  if (!s) return 'none';
  const c = canonicalizeVatRate(s);
  return (c || s).toLowerCase();
}

/** VAT rates present on a row: from loaded tag chips when available, else `vat_rate` on the row. */
function unifiedRowVatRateKeySet(
  r: { id: number; vat_rate?: string | null },
  tagsByTx: Record<number, UnifiedTxCategoryTagChip[]>
): Set<string> {
  const keys = new Set<string>();
  const rowTags = tagsByTx[r.id];
  if (rowTags && rowTags.length > 0) {
    rowTags.forEach((t) => {
      keys.add(vatFilterKeyFromRaw(t.vatRate));
    });
    return keys;
  }
  keys.add(vatFilterKeyFromRaw(r.vat_rate));
  return keys;
}

/** Composite labels `category @ vatRate` for loaded legacy tag chips on this row (Applied column). */
function unifiedRowAppliedTagLabelSet(
  r: UnifiedReviewRow,
  tagsByTx: Record<number, UnifiedTxCategoryTagChip[]>
): Set<string> {
  const keys = new Set<string>();
  (tagsByTx[r.id] || []).forEach((t) => {
    const uid = String(t.uid || '').trim();
    if (!uid) return;
    keys.add(formatUnifiedAppliedTagChipLabel(t));
  });
  return keys;
}

/**
 * True when a row has more than one tag in any review type bucket
 * (Cash flow heading / Supplier / Category / VAT) — the “Tag duplicates” filter cohort.
 *
 * Same-value display duplicates (what De-dup removes) are a subset of this.
 */
function unifiedRowHasDisplayDuplicateTags(chips: UnifiedTxCategoryTagChip[]): boolean {
  const list = (chips || []).filter((t) => String(t.uid || '').trim());
  if (list.length < 2) return false;

  const heading = list.filter((t) => isTopLevelCashflowHeadingName(t.category));
  const supplier = list.filter((t) => (t.supplier || '').trim());
  const granular = list.filter(
    (t) =>
      t.source !== 'canonical_supplier' &&
      t.source !== 'canonical_vat' &&
      String(t.category || '').trim().length > 0 &&
      !isTopLevelCashflowHeadingName(t.category)
  );
  const vatBucket = list.filter(
    (t) =>
      t.source === 'canonical_vat' ||
      String(t.uid || '').startsWith('cvt:') ||
      (t.source !== 'canonical_supplier' && String(t.vatRate || '').trim().length > 0)
  );

  return heading.length > 1 || supplier.length > 1 || granular.length > 1 || vatBucket.length > 1;
}

function formatVatFilterMenuLabel(key: string): string {
  const k = (key || '').toLowerCase();
  if (k === 'none') return 'None / blank';
  if (k === 'exempt') return 'Exempt';
  return key;
}

/** Region code for ledger account_source (TX Review column, filters, similar-TX). UK / IE; unknown non-empty sources unchanged. */
function formatRevolutAccountLabel(source: unknown): string {
  const raw = source == null ? '' : typeof source === 'string' ? source : String(source);
  const s = raw.trim().toLowerCase();
  if (!s) return '—';
  if (s.includes('uk')) return 'UK';
  if (s.includes('ireland') || s.includes('ire')) return 'IE';
  return raw.trim();
}

/** Parse Similar TX modal score bounds (strip commas; invalid → null). */
function parseSimilarTxScoreBound(raw: string | null | undefined): number | null {
  const t = String(raw ?? '').trim().replace(/,/g, '');
  if (t === '') return null;
  const n = Number(t);
  return Number.isFinite(n) ? n : null;
}

/** Must match the Applied tag column chip label (`category @ vatRate`) exactly for bulk matching. */
function formatUnifiedAppliedTagChipLabel(tag: UnifiedTxCategoryTagChip): string {
  if (isTopLevelCashflowHeadingName(tag.category)) {
    return String(tag.category || '').trim();
  }
  const vatDisp = (() => {
    const s = String(tag.vatRate || '').trim();
    if (!s || s.toLowerCase() === 'none' || s.toLowerCase() === 'n/a') {
      return '—';
    }
    return canonicalizeVatRate(s) || s;
  })();
  return `${tag.category} @ ${vatDisp}`;
}

/** True only for standalone heading chips shown as plain "Income" / "Expense" in Applied tags. */
function isStandaloneIncomeExpenseAppliedTag(tag: UnifiedTxCategoryTagChip): boolean {
  if (!isTopLevelCashflowHeadingName(tag.category)) return false;
  const vat = String(tag.vatRate || '').trim().toLowerCase();
  if (vat && vat !== 'none' && vat !== 'n/a') return false;
  return true;
}

type PendingUnifiedTagDelete = {
  txId: number;
  tagUid: string;
  label: string;
  /** When deleting from the VAT column on a legacy category chip, clear rate only. */
  clearVatOnly?: boolean;
};

/** Which column opened delete — confirmation text should match that chip, not always category @ VAT. */
type UnifiedTagDeletePromptKind = 'applied' | 'heading' | 'supplier' | 'category' | 'vat';

function unifiedTagDeletePromptLabel(tag: UnifiedTxCategoryTagChip, kind: UnifiedTagDeletePromptKind): string {
  const cat = (tag.category || '').trim() || 'Uncategorized';
  const vatRaw = (tag.vatRate || '').trim();
  const vat = vatRaw && vatRaw.toLowerCase() !== 'none' && vatRaw.toLowerCase() !== 'n/a' ? vatRaw : '—';
  const sup = (tag.supplier || '').trim();
  switch (kind) {
    case 'supplier':
      return sup.length ? `Supplier tag “${sup}”` : 'Supplier tag';
    case 'heading':
      return `Cash flow tag “${cat}”`;
    case 'category':
      return `Category tag “${cat}”`;
    case 'vat':
      return `VAT tag “${vat}”`;
    case 'applied':
    default:
      return formatUnifiedAppliedTagChipLabel(tag);
  }
}

function normalizeRevolutTxState(raw: unknown): string {
  return String(raw ?? '')
    .trim()
    .toLowerCase();
}

/** True when Revolut `state` is set and not the successful terminal `completed` state (e.g. declined, reverted, pending). */
function isNonCompleteRevolutTxState(raw: unknown): boolean {
  const s = normalizeRevolutTxState(raw);
  if (!s) return false;
  return s !== 'completed';
}

function trimRevolutStateDisplay(raw: unknown): string | null {
  if (raw == null) return null;
  const s = String(raw).trim();
  return s.length ? s : null;
}

function formatRevolutStateFilterLabel(s: string): string {
  const t = (s || '').trim();
  if (!t) return '—';
  return t.charAt(0).toUpperCase() + t.slice(1).toLowerCase();
}

function revolutStateSummaryChipColor(
  state: string
): 'default' | 'primary' | 'secondary' | 'error' | 'info' | 'success' | 'warning' {
  const s = (state || '').toLowerCase();
  if (s === 'declined') return 'warning';
  if (s === 'failed' || s === 'reverted') return 'error';
  if (s === 'completed') return 'success';
  if (s === 'pending') return 'info';
  return 'default';
}

/** Fields concatenated for the main review table text search (all visible row data + stable ids). */
function unifiedReviewRowTextSearchHaystack(r: UnifiedReviewRow): string {
  const amountStr =
    r.amount != null && Number.isFinite(Number(r.amount)) ? String(r.amount) : '';
  const vatRaw = String(r.vat_rate ?? '').trim();
  const vatCanon = (canonicalizeVatRate(r.vat_rate) || '').trim();
  const vatForSearch = vatCanon && vatCanon !== vatRaw ? `${vatCanon} ${vatRaw}` : vatCanon || vatRaw;
  return [
    String(r.id ?? ''),
    r.revolut_id ?? '',
    r.tag_uid ?? '',
    String(r.user_id ?? ''),
    r.source_state ?? '',
    r.status ?? '',
    r.date ?? '',
    amountStr,
    r.currency ?? '',
    r.reference ?? '',
    r.description ?? '',
    r.account_source ?? '',
    r.frequency ?? '',
    r.cashFlow ?? '',
    r.supplier ?? '',
    r.category ?? '',
    vatForSearch,
    r.tagOrigin ?? '',
    r.merchant_city ?? '',
    String(r.ruleId ?? ''),
    String(r.supplier_id ?? ''),
    normalizeRevolutTxState(r.revolut_state)
  ]
    .join(' ')
    .toLowerCase();
}

interface UnifiedClassifyOverrides {
  headingId?: number | '';
  granularId?: number | '';
  /** When set, replaces row draft granular ids for this classify call (e.g. bulk slow path). */
  granularIds?: number[];
  vatRate?: string;
  supplierName?: string;
  /**
   * Bulk commit: skip the extra `review-decision` POST used only to refine inferred payment frequency
   * (local category-based inference is enough). Saves one HTTP round-trip per row on large selections.
   */
  lightweightForBulkCommit?: boolean;
}

interface SimilarUnifiedRow extends UnifiedReviewRow {
  similarityScore: number;
  similarityReasons: string[];
}

type SimilarTxSortKey =
  | 'date'
  | 'id'
  | 'amount'
  | 'description'
  | 'cashFlow'
  | 'supplierTag'
  | 'category'
  | 'vat'
  | 'committed'
  | 'similarityScore'
  | 'sharedDescriptionTerms'
  | 'similarAccount'
  | 'otherSimilarities';

/** Similar-transactions modal → Rules: which proposal generators to run (order fixed). */
type SimilarTxGenerateKind = 'category' | 'supplier' | 'vat' | 'special';
/** Matches Rules tab pipeline: Step 2 supplier → Step 3 classification → Step 4 VAT → Step 5 special (Step 1 cashflow is not generated here). */
const SIMILAR_TX_GENERATE_KIND_ORDER: SimilarTxGenerateKind[] = ['supplier', 'category', 'vat', 'special'];

/** Default Similar-transactions modal filter: hide very weak matches until user lowers the bar. */
const SIMILAR_TX_DEFAULT_SCORE_MIN = '6';

/** Column count for Similar-transactions table header (checkbox + data columns). */
const SIMILAR_TX_MODAL_TABLE_COL_SPAN = 14;

type SimilarUnifiedRowView = SimilarUnifiedRow & {
  sharedDescriptionTerms: string;
  /** Y/N vs opened row's account; — for anchor row or unknown account on either side. */
  similarAccount: 'Y' | 'N' | '—';
  otherSimilarities: string;
};

function sortSimilarUnifiedRowViews(
  rows: SimilarUnifiedRowView[],
  similarTxSort: { key: SimilarTxSortKey; dir: SortDir }
): SimilarUnifiedRowView[] {
  const out = [...rows];
  const mul = similarTxSort.dir === 'asc' ? 1 : -1;
  out.sort((a, b) => {
    const dateA = a.date ? new Date(a.date).getTime() : Number.NaN;
    const dateB = b.date ? new Date(b.date).getTime() : Number.NaN;
    switch (similarTxSort.key) {
      case 'date':
        return (((Number.isFinite(dateA) ? dateA : 0) - (Number.isFinite(dateB) ? dateB : 0)) || (a.id - b.id)) * mul;
      case 'id':
        return (a.id - b.id) * mul;
      case 'amount':
        return ((Number(a.amount || 0) - Number(b.amount || 0)) || (a.id - b.id)) * mul;
      case 'similarityScore':
        return ((Number(a.similarityScore || 0) - Number(b.similarityScore || 0)) || (a.id - b.id)) * mul;
      case 'description':
        return compareStrings(String(a.description || '').toLowerCase(), String(b.description || '').toLowerCase()) * mul;
      case 'cashFlow':
        return compareStrings(String(a.cashFlow || '').toLowerCase(), String(b.cashFlow || '').toLowerCase()) * mul;
      case 'supplierTag':
        return compareStrings(String(a.supplier || '').toLowerCase(), String(b.supplier || '').toLowerCase()) * mul;
      case 'category':
        return compareStrings(String(a.category || '').toLowerCase(), String(b.category || '').toLowerCase()) * mul;
      case 'vat':
        return compareStrings(String(a.vat_rate || '').toLowerCase(), String(b.vat_rate || '').toLowerCase()) * mul;
      case 'committed':
        return (Number(isUnifiedRowCommitted(a)) - Number(isUnifiedRowCommitted(b)) || a.id - b.id) * mul;
      case 'sharedDescriptionTerms':
        return compareStrings(a.sharedDescriptionTerms.toLowerCase(), b.sharedDescriptionTerms.toLowerCase()) * mul;
      case 'similarAccount':
        return compareStrings(a.similarAccount, b.similarAccount) * mul;
      case 'otherSimilarities':
        return compareStrings(a.otherSimilarities.toLowerCase(), b.otherSimilarities.toLowerCase()) * mul;
      default:
        return 0;
    }
  });
  return out;
}

function sanitizeSimilarReasonList(value: unknown): string[] {
  if (!Array.isArray(value)) return [];
  return value
    .map((reason) => String(reason ?? '').trim())
    .filter((reason) => reason.length > 0);
}

/** Defensive coercion so malformed similar-tx payloads cannot crash dialog rendering. */
function sanitizeSimilarUnifiedRow(row: SimilarUnifiedRow): SimilarUnifiedRow {
  const numericId = Number((row as any)?.id);
  const numericAmount = Number((row as any)?.amount);
  const numericScore = Number((row as any)?.similarityScore);
  const dateRaw = String((row as any)?.date ?? '').trim();
  const supplierRaw = String((row as any)?.supplier ?? '').trim();
  const descriptionRaw = String((row as any)?.description ?? '').trim();
  const accountRaw = String((row as any)?.account_source ?? '').trim();
  const frequencyRaw = String((row as any)?.frequency ?? '').trim();
  const currencyRaw = String((row as any)?.currency ?? '').trim().toUpperCase();
  const categoryRaw = String((row as any)?.category ?? '').trim();
  const statusRaw = String((row as any)?.status ?? '').trim();
  const headingRaw = (row as any)?.cashFlow;
  const amount = Number.isFinite(numericAmount) ? numericAmount : null;
  const cashFlowResolved = resolveCashFlowHeadingFromAmountAndTag(amount, headingRaw);

  return {
    ...row,
    key: String((row as any)?.key ?? `similar-${Number.isFinite(numericId) ? numericId : 'unknown'}`),
    id: Number.isFinite(numericId) ? numericId : 0,
    date: dateRaw || null,
    amount,
    currency: currencyRaw || null,
    supplier: supplierRaw || '—',
    description: descriptionRaw || '—',
    account_source: accountRaw || null,
    frequency: frequencyRaw || null,
    category: categoryRaw || '—',
    status: statusRaw || 'Unknown',
    cashFlow: cashFlowResolved,
    revolut_id: String((row as any)?.revolut_id ?? '').trim(),
    similarityScore: Number.isFinite(numericScore) ? numericScore : 0,
    similarityReasons: sanitizeSimilarReasonList((row as any)?.similarityReasons)
  };
}

interface ClassificationRule {
  id: number;
  name: string;
  priority: number;
  is_active: boolean;
  supplier_contains: string | null;
  supplier_canonical_name?: string | null;
  description_contains: string | null;
  description_text_match_operator?: string | null;
  reference_contains: string | null;
  reference_text_match_operator?: string | null;
  text_match_operator: string | null;
  account_scope?: string | null;
  amount_min: number | null;
  amount_max: number | null;
  currency: string | null;
  recurrence_type: string | null;
  vat_rate: string | null;
  confidence_score: number | null;
  notes?: string | null;
  supplier_name_override?: string | null;
  supplier_name_overrides?: string[] | null;
  category_id: number;
  category?: {
    id: number;
    name: string;
    vat_rate?: string | null;
  } | null;
}

/** Row from GET /transaction-classification-rules/:id/matching-transactions */
interface MatchingTransactionRow {
  id: number;
  completed_at: string | null;
  date: string | null;
  amount: number | null;
  currency: string | null;
  supplier_name?: string | null;
  merchant_name: string | null;
  counterparty_name: string | null;
  description: string | null;
  reference: string | null;
  account_source: string | null;
}

/** Modal lists either a persisted rule or a pending proposal (atlas or saved). */
type MatchTxModalSource =
  | { kind: 'rule'; rule: ClassificationRule }
  | { kind: 'proposal'; label: string; proposalId: number | null; atlasKey: string | null }
  | { kind: 'supplierRule'; row: SupplierRuleRow }
  | { kind: 'supplierProposal'; proposalId: number; label: string }
  | { kind: 'vatRule'; row: CategoryVatRuleRow }
  | { kind: 'specialCaseRule'; row: SpecialCaseRuleRow };

/** Supplier column shows canonical rule output for supplier rules / proposals only. */
function matchTxModalSupplierIsRuleOutput(kind: MatchTxModalSource['kind']): boolean {
  return kind === 'supplierRule' || kind === 'supplierProposal';
}

function parseApplySingleAffectedTxIds(summary: unknown): {
  ids: number[];
  truncated: boolean;
  total: number;
} {
  if (!summary || typeof summary !== 'object' || Array.isArray(summary)) {
    return { ids: [], truncated: false, total: 0 };
  }
  const s = summary as Record<string, unknown>;
  const raw = s.affected_transaction_ids;
  const ids = Array.isArray(raw)
    ? raw.map((x) => Number(x)).filter((n) => Number.isFinite(n) && n > 0)
    : [];
  const totalRaw = s.affected_transaction_ids_total;
  const total =
    typeof totalRaw === 'number' && Number.isFinite(totalRaw) ? Math.trunc(totalRaw) : ids.length;
  return {
    ids,
    truncated: Boolean(s.affected_transaction_ids_truncated),
    total
  };
}

function parseSpecialCaseSupplierContainsForDraft(
  raw: string | null | undefined,
  knownSuppliers: string[]
): { supplier_name_overrides: string[]; supplier_contains: string } {
  const rawStr = String(raw || '').trim();
  if (!rawStr) return { supplier_name_overrides: [], supplier_contains: '' };
  const parts = rawStr.split('|').map((p) => p.trim()).filter(Boolean);
  const overrides: string[] = [];
  const freeParts: string[] = [];
  const lowerByName = new Map(knownSuppliers.map((k) => [k.trim().toLowerCase(), k.trim()] as const));
  for (const p of parts) {
    const canon = lowerByName.get(p.toLowerCase());
    if (canon) overrides.push(canon);
    else freeParts.push(p);
  }
  return {
    supplier_name_overrides: dedupeSupplierNameList(overrides),
    supplier_contains: freeParts.join('|')
  };
}

function composeSpecialCaseSupplierContainsForApi(draft: SpecialCaseRuleDraft): string | undefined {
  const supplierCriteria = (draft.match_criteria || [])
    .filter((c) => c.kind === 'canonical_supplier' || c.kind === 'merchant')
    .map((c) => c.value.trim())
    .filter(Boolean);
  const s = supplierCriteria.join('|').trim();
  return s || undefined;
}

function splitRuleOrValues(raw: string | null | undefined): string[] {
  const parts = String(raw || '').split('|').map((part) => part.trim());
  return parts.length > 0 ? parts : [''];
}

function joinRuleOrValues(parts: string[]): string {
  return parts.join(' | ');
}

function normalizeRuleOrValues(raw: string | null | undefined): string {
  return splitRuleOrValues(raw)
    .map((part) => part.trim())
    .filter((part) => part.length > 0)
    .join('|');
}

function defaultSpecialCaseMatchCriterion(
  kind: SpecialCaseMatchCriterionDraft['kind'] = 'description'
): SpecialCaseMatchCriterionDraft {
  return {
    id: `scc-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
    kind,
    value: '',
    operator:
      kind === 'amount' || kind === 'currency' || kind === 'category' || kind === 'current_vat'
        ? 'exact'
        : 'contains'
  };
}

function normalizeSpecialCaseCriteriaFromRow(row: SpecialCaseRuleRow): SpecialCaseMatchCriterionDraft[] {
  const fromApi = Array.isArray(row.match_criteria)
    ? row.match_criteria
        .map((c) => ({
          id: `scc-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
          kind: String(c?.kind || '').trim().toLowerCase() as SpecialCaseMatchCriterionDraft['kind'],
          value: String(c?.value || ''),
          operator: String(c?.operator || 'contains').trim() || 'contains'
        }))
        .filter((c) =>
          [
            'canonical_supplier',
            'merchant',
            'description',
            'reference',
            'currency',
            'amount',
            'category',
            'current_vat'
          ].includes(c.kind) && c.value.trim()
        )
    : [];
  if (fromApi.length > 0) return fromApi;
  const parsed = parseSpecialCaseSupplierContainsForDraft(row.supplier_contains, []);
  const legacy: SpecialCaseMatchCriterionDraft[] = [];
  parsed.supplier_name_overrides.forEach((name) => {
    legacy.push({
      id: `scc-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
      kind: 'canonical_supplier',
      value: name,
      operator: String(row.text_match_operator || 'contains').trim() || 'contains'
    });
  });
  if (parsed.supplier_contains.trim()) {
    legacy.push({
      id: `scc-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
      kind: 'merchant',
      value: parsed.supplier_contains.trim(),
      operator: String(row.text_match_operator || 'contains').trim() || 'contains'
    });
  }
  if (String(row.description_contains || '').trim()) {
    legacy.push({
      id: `scc-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
      kind: 'description',
      value: String(row.description_contains || '').trim(),
      operator: String(row.description_text_match_operator || row.text_match_operator || 'contains').trim() || 'contains'
    });
  }
  if (String(row.reference_contains || '').trim()) {
    legacy.push({
      id: `scc-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
      kind: 'reference',
      value: String(row.reference_contains || '').trim(),
      operator: String(row.reference_text_match_operator || row.text_match_operator || 'contains').trim() || 'contains'
    });
  }
  if (String(row.currency || '').trim()) {
    legacy.push({
      id: `scc-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
      kind: 'currency',
      value: String(row.currency || '').trim().toUpperCase(),
      operator: 'exact'
    });
  }
  if (row.category_id != null && Number(row.category_id) > 0) {
    legacy.push({
      id: `scc-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
      kind: 'category',
      value: String(row.category_id),
      operator: 'exact'
    });
  }
  if (String(row.current_vat_rate || '').trim()) {
    legacy.push({
      id: `scc-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
      kind: 'current_vat',
      value: normalizeSpecialCaseVatMatcherSelectValue(String(row.current_vat_rate || '').trim()),
      operator: 'exact'
    });
  }
  return legacy;
}

function tryBuildSpecialCaseRulePayloadFromDraft(
  draft: SpecialCaseRuleDraft
): { payload: Record<string, unknown> } | { error: string } {
  for (const c of draft.match_criteria || []) {
    if (c.kind !== 'amount') continue;
    const raw = String(c.value || '').trim();
    if (!raw) continue;
    if (!isValidSupplierRuleAmountValue(raw)) {
      return { error: 'Amount matcher must be a numeric value (e.g. -1425 or 1200.50).' };
    }
  }
  const normalizedCriteria = (draft.match_criteria || [])
    .map((criterion) => {
      const kind = criterion.kind;
      const baseValue = String(criterion.value || '').trim();
      const value =
        kind === 'currency'
          ? baseValue.toUpperCase()
          : kind === 'current_vat'
            ? normalizeSpecialCaseVatMatcherSelectValue(baseValue)
            : baseValue;
      const operator = ['canonical_supplier', 'merchant', 'description', 'reference'].includes(kind)
        ? (criterion.operator || 'contains').trim() || 'contains'
        : 'exact';
      return { kind, value, operator };
    })
    .filter((criterion) => criterion.value);
  const supplierApi = composeSpecialCaseSupplierContainsForApi({
    ...draft,
    match_criteria: normalizedCriteria.map((c) => ({
      id: `scc-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
      kind: c.kind,
      value: c.value,
      operator: c.operator
    }))
  });
  const fallbackCategory = normalizedCriteria.find((criterion) => criterion.kind === 'category');
  const fallbackDescription = normalizedCriteria.find((criterion) => criterion.kind === 'description');
  const fallbackReference = normalizedCriteria.find((criterion) => criterion.kind === 'reference');
  const fallbackVat = normalizedCriteria.find((criterion) => criterion.kind === 'current_vat');
  const fallbackCurrency = normalizedCriteria.find((criterion) => criterion.kind === 'currency');
  const payload: Record<string, unknown> = {
    name: draft.name.trim(),
    priority: Number(draft.priority || 100),
    is_active: draft.is_active,
    account_scope: normalizeRuleAccountScope(draft.account_scope),
    category_id: fallbackCategory ? Number(fallbackCategory.value) : null,
    supplier_contains: supplierApi,
    description_contains: fallbackDescription?.value || undefined,
    description_text_match_operator:
      (fallbackDescription?.operator || 'contains').trim() || 'contains',
    reference_contains: fallbackReference?.value || undefined,
    reference_text_match_operator:
      (fallbackReference?.operator || 'contains').trim() || 'contains',
    current_vat_rate: fallbackVat?.value || undefined,
    text_match_operator: 'contains',
    currency: fallbackCurrency?.value || undefined,
    match_criteria: normalizedCriteria.map((criterion) => ({
      kind: criterion.kind,
      value: criterion.value,
      operator: criterion.operator
    })),
    set_vat_rate: canonicalizeVatRate(draft.set_vat_rate),
    notes: draft.notes.trim() || undefined
  };
  if (!String(payload.name || '').trim()) {
    return { error: 'Special case rule name is required.' };
  }
  if (!payload.set_vat_rate) {
    return { error: 'Special case output VAT rate is required.' };
  }
  const scopeNorm = normalizeRuleAccountScope(draft.account_scope);
  const hasScopedAccount = scopeNorm === 'ireland' || scopeNorm === 'uk';
  if (normalizedCriteria.length === 0 && !hasScopedAccount) {
    return {
      error:
        'Add at least one matcher (supplier, merchant, description, reference, category, current VAT, currency, or exact amount), or narrow Bank account to Ireland or UK only.'
    };
  }
  return { payload };
}

function dedupeSupplierNameList(names: Array<string | null | undefined>): string[] {
  const out: string[] = [];
  const seen = new Set<string>();
  for (const raw of names) {
    const s = String(raw ?? '').trim();
    if (!s) continue;
    const k = s.toLowerCase();
    if (seen.has(k)) continue;
    seen.add(k);
    out.push(s);
  }
  return out;
}

/** Collapse names that differ only by capitalisation (keeps first spelling seen). */
function uniqueSupplierNamesCaseInsensitive(names: string[]): string[] {
  const byLower = new Map<string, string>();
  for (const raw of names) {
    const s = String(raw || '').trim();
    if (!s) continue;
    const k = s.toLowerCase();
    if (!byLower.has(k)) byLower.set(k, s);
  }
  return Array.from(byLower.values()).sort((a, b) =>
    a.localeCompare(b, undefined, { sensitivity: 'base' })
  );
}

const SUPPLIER_NAME_SIMILARITY_THRESHOLD = 0.82;

function levenshtein(a: string, b: string): number {
  const m = a.length;
  const n = b.length;
  if (m === 0) return n;
  if (n === 0) return m;
  const dp: number[][] = Array.from({ length: m + 1 }, () => Array(n + 1).fill(0));
  for (let i = 0; i <= m; i++) dp[i][0] = i;
  for (let j = 0; j <= n; j++) dp[0][j] = j;
  for (let i = 1; i <= m; i++) {
    for (let j = 1; j <= n; j++) {
      const cost = a[i - 1] === b[j - 1] ? 0 : 1;
      dp[i][j] = Math.min(dp[i - 1][j] + 1, dp[i][j - 1] + 1, dp[i - 1][j - 1] + cost);
    }
  }
  return dp[m][n];
}

/** Returns the existing display name if ``input`` matches ignoring case. */
function supplierDuplicateCaseInsensitive(input: string, existing: readonly string[]): string | null {
  const t = input.trim();
  if (!t) return null;
  const k = t.toLowerCase();
  for (const ex of existing) {
    const e = String(ex || '').trim();
    if (e && e.toLowerCase() === k) return e;
  }
  return null;
}

/** Best fuzzy spelling match (typo helper), excluding case-identical hits. */
function supplierClosestSpellingMatch(input: string, existing: readonly string[]): string | null {
  const t = input.trim().toLowerCase();
  if (!t) return null;
  let best: string | null = null;
  let bestScore = 0;
  for (const ex of existing) {
    const raw = String(ex || '').trim();
    if (!raw) continue;
    const r = raw.toLowerCase();
    if (r === t) continue;
    const d = levenshtein(t, r);
    const maxLen = Math.max(t.length, r.length, 1);
    const score = 1 - d / maxLen;
    if (score > bestScore) {
      bestScore = score;
      best = raw;
    }
  }
  if (best && bestScore >= SUPPLIER_NAME_SIMILARITY_THRESHOLD) return best;
  return null;
}

function supplierOverridesFromClassificationRule(rule: ClassificationRule): string[] {
  const arr = rule.supplier_name_overrides;
  if (Array.isArray(arr) && arr.length > 0) {
    return dedupeSupplierNameList(arr as string[]);
  }
  const one = (rule.supplier_name_override ?? '').trim();
  return one ? [one] : [];
}

function classificationRuleCanListMatches(rule: ClassificationRule): boolean {
  const hasText = !!(
    (rule.supplier_contains || '').trim() ||
    (rule.description_contains || '').trim() ||
    (rule.reference_contains || '').trim()
  );
  const hasAmt = rule.amount_min != null || rule.amount_max != null;
  const hasCanonical = supplierOverridesFromClassificationRule(rule).length > 0;
  const hasCurrency = String(rule.currency || '').trim() !== '';
  const hasScopedAccount = String(rule.account_scope || '').trim().toLowerCase() === 'ireland'
    || String(rule.account_scope || '').trim().toLowerCase() === 'uk';
  return hasText || hasAmt || hasCanonical || hasCurrency || hasScopedAccount;
}

/** True when the rule uses amount bounds, currency, recurrence, or rule-level VAT — beyond canonical/merchant/description/reference. */
function classificationRuleHasOtherCriteria(rule: ClassificationRule): boolean {
  if (rule.amount_min != null || rule.amount_max != null) return true;
  if (String(rule.currency || '').trim()) return true;
  if (String(rule.recurrence_type || '').trim()) return true;
  if (String(rule.vat_rate || '').trim()) return true;
  return false;
}

function classificationProposalHasOtherCriteria(row: RuleProposalRow): boolean {
  const p = row.payload || {};
  const amtMin = p.amount_min ?? row.amount_min;
  const amtMax = p.amount_max ?? row.amount_max;
  if (amtMin != null && String(amtMin).trim() !== '') return true;
  if (amtMax != null && String(amtMax).trim() !== '') return true;
  if (String(p.currency ?? '').trim()) return true;
  if (String(p.recurrence_type ?? '').trim()) return true;
  if (String(p.vat_rate ?? '').trim()) return true;
  return false;
}

/** Body shape for PUT /api/transaction-classification-rules/:id (aligned with save dialog). */
type ClassificationRulePersistPayload = {
  name: string;
  priority: number;
  category_id: number;
  is_active: boolean;
  supplier_name_overrides: string[] | null;
  supplier_name_override: string | null;
  supplier_contains: string | null;
  description_contains: string | null;
  description_text_match_operator: string;
  reference_contains: string | null;
  reference_text_match_operator: string;
  text_match_operator: string;
  amount_min: number | null;
  amount_max: number | null;
  account_scope: string;
  currency: string | null;
  recurrence_type: string | null;
  notes: string | null;
};

function classificationRuleToPersistPayload(rule: ClassificationRule): ClassificationRulePersistPayload {
  const canonicalList = dedupeSupplierNameList(supplierOverridesFromClassificationRule(rule));
  const normalizedSupplierContains = normalizeRuleOrValues(rule.supplier_contains);
  const normalizedDescriptionContains = normalizeRuleOrValues(rule.description_contains);
  const normalizedReferenceContains = normalizeRuleOrValues(rule.reference_contains);
  const supplierOpRaw = (rule.text_match_operator || 'exact').trim() || 'exact';
  const hasSupplierText = Boolean(normalizedSupplierContains);
  const supplierOp = hasSupplierText ? supplierOpRaw : 'exact';
  const hasDescriptionText = Boolean(normalizedDescriptionContains);
  const hasReferenceText = Boolean(normalizedReferenceContains);
  return {
    name: String(rule.name || '').trim(),
    priority: Number(rule.priority ?? 100),
    category_id: rule.category_id,
    is_active: rule.is_active !== false,
    supplier_name_overrides: canonicalList.length > 0 ? canonicalList : null,
    supplier_name_override: canonicalList.length > 0 ? canonicalList[0] : null,
    supplier_contains: normalizedSupplierContains || null,
    description_contains: normalizedDescriptionContains || null,
    description_text_match_operator: hasDescriptionText
      ? ((rule.description_text_match_operator || supplierOpRaw).trim() || supplierOpRaw)
      : 'exact',
    reference_contains: normalizedReferenceContains || null,
    reference_text_match_operator: hasReferenceText
      ? ((rule.reference_text_match_operator || supplierOpRaw).trim() || supplierOpRaw)
      : 'exact',
    text_match_operator: supplierOp,
    amount_min: rule.amount_min,
    amount_max: rule.amount_max,
    account_scope: normalizeRuleAccountScope(rule.account_scope),
    currency: String(rule.currency || '').trim() || null,
    recurrence_type: String(rule.recurrence_type || '').trim() || null,
    notes: String(rule.notes || '').trim() || null
  };
}

function validateClassificationPersistPayload(p: ClassificationRulePersistPayload): string | null {
  const hasText = !!(
    String(p.supplier_contains || '').trim() ||
    String(p.description_contains || '').trim() ||
    String(p.reference_contains || '').trim()
  );
  const hasAmt = p.amount_min != null || p.amount_max != null;
  const hasCanon = Array.isArray(p.supplier_name_overrides) && p.supplier_name_overrides.length > 0;
  if (!hasText && !hasAmt && !hasCanon) {
    return 'That change would leave the rule with no match scope (add another matcher in Edit or cancel).';
  }
  return null;
}

function duplicateOfRuleName(original: string): string {
  const t = String(original ?? '').trim();
  const label = t.length > 0 ? t : 'unnamed rule';
  return `Duplicate of ${label}`;
}

function classificationRuleOrFieldParts(raw: string | null | undefined): string[] {
  return splitRuleOrValues(raw)
    .map((part) => part.trim())
    .filter((part) => part.length > 0);
}

function classificationRuleRemoveOrPartAtIndex(
  raw: string | null | undefined,
  removeIndex: number
): string | null {
  const parts = classificationRuleOrFieldParts(raw);
  if (removeIndex < 0 || removeIndex >= parts.length) return normalizeRuleOrValues(raw) || null;
  const next = parts.filter((_, i) => i !== removeIndex);
  return normalizeRuleOrValues(joinRuleOrValues(next)) || null;
}

function classificationRuleTextMatchChips(params: {
  parts: string[];
  operator: string | null | undefined;
  chipKeyPrefix: string;
  busy?: boolean;
  onDeleteIndex?: (index: number) => void;
  onPartClick?: (part: string) => void;
}): React.ReactNode {
  const { parts, operator, chipKeyPrefix, busy, onDeleteIndex, onPartClick } = params;
  if (parts.length === 0) return '—';
  return (
    <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
      {parts.map((part, idx) => {
        const trimmed = part.trim();
        const deleteHandler = onDeleteIndex && !busy ? () => onDeleteIndex(idx) : undefined;
        const clickHandler = onPartClick && trimmed ? () => onPartClick(trimmed) : undefined;
        return (
          <Chip
            key={`${chipKeyPrefix}-${idx}-${trimmed}`}
            size="small"
            variant="outlined"
            label={classificationRuleTextMatchDisplay(trimmed, operator)}
            clickable={Boolean(clickHandler)}
            onClick={clickHandler}
            onDelete={deleteHandler}
          />
        );
      })}
    </Stack>
  );
}

function classificationCanonicalSupplierChips(params: {
  names: string[];
  chipKeyPrefix: string;
  busy?: boolean;
  onDeleteName?: (name: string) => void;
  onNameClick?: (name: string) => void;
}): React.ReactNode {
  const { names, chipKeyPrefix, busy, onDeleteName, onNameClick } = params;
  if (names.length === 0) return '—';
  return (
    <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
      {names.map((rawName, idx) => {
        const name = String(rawName || '').trim();
        if (!name) return null;
        const label = canonicalSupplierLabel(name, name);
        return (
          <Chip
            key={`${chipKeyPrefix}-${idx}-${name}`}
            size="small"
            variant="outlined"
            label={label}
            clickable={Boolean(onNameClick)}
            onClick={onNameClick ? () => onNameClick(name) : undefined}
            onDelete={onDeleteName && !busy ? () => onDeleteName(name) : undefined}
          />
        );
      })}
    </Stack>
  );
}

function canonicalSupplierLabel(name: string | null | undefined, fallback: string): string {
  const raw = (name || '').trim();
  if (!raw) return fallback;
  return raw.toLowerCase() === 'ring' ? 'Ring' : raw;
}

function classificationRuleSupplierDisplay(rule: ClassificationRule): string {
  const canon = supplierOverridesFromClassificationRule(rule);
  if (canon.length > 1) {
    return canon.map((n) => canonicalSupplierLabel(n, n)).join(', ');
  }
  if (canon.length === 1) {
    return canonicalSupplierLabel(canon[0], canon[0]);
  }
  const canonical = (rule.supplier_canonical_name || '').trim();
  if (canonical) return canonical.toLowerCase() === 'ring' ? 'Ring' : canonical;
  const pattern = (rule.supplier_contains || '').trim();
  return pattern || '—';
}

function classificationRuleCanonicalSupplierDisplay(rule: ClassificationRule): string {
  const canon = supplierOverridesFromClassificationRule(rule);
  if (canon.length > 1) {
    return canon.map((n) => canonicalSupplierLabel(n, n)).join(', ');
  }
  if (canon.length === 1) {
    return canonicalSupplierLabel(canon[0], canon[0]);
  }
  const canonical = (rule.supplier_canonical_name || '').trim();
  if (canonical) return canonicalSupplierLabel(canonical, canonical);
  return '—';
}

function classificationProposalCanonicalSupplierDisplay(row: RuleProposalRow): string {
  const payload = (row.payload || {}) as Record<string, unknown>;
  const payloadList = payload.supplier_name_overrides;
  let names: string[] = [];
  if (Array.isArray(payloadList)) {
    names = dedupeSupplierNameList(payloadList.map((x) => String(x ?? '')));
  } else if (Array.isArray(row.supplier_name_overrides)) {
    names = dedupeSupplierNameList(row.supplier_name_overrides.map((x) => String(x ?? '')));
  } else {
    const single = String(
      payload.supplier_name_override ??
      row.supplier_name_override ??
      payload.supplier_canonical_name ??
      ''
    ).trim();
    names = single ? [single] : [];
  }
  if (names.length > 1) {
    return names.map((n) => canonicalSupplierLabel(n, n)).join(', ');
  }
  if (names.length === 1) {
    return canonicalSupplierLabel(names[0], names[0]);
  }
  return '—';
}

function classificationProposalCanonicalSupplierNames(row: RuleProposalRow): string[] {
  const payload = (row.payload || {}) as Record<string, unknown>;
  const payloadList = payload.supplier_name_overrides;
  if (Array.isArray(payloadList)) {
    return dedupeSupplierNameList(payloadList.map((x) => String(x ?? '')));
  }
  if (Array.isArray(row.supplier_name_overrides)) {
    return dedupeSupplierNameList(row.supplier_name_overrides.map((x) => String(x ?? '')));
  }
  const single = String(
    payload.supplier_name_override ??
      row.supplier_name_override ??
      payload.supplier_canonical_name ??
      ''
  ).trim();
  return single ? [single] : [];
}

/** Stable DB id for supplier description-alias proposals (avoids silent no-ops when `id` is missing). */
function supplierRuleProposalDbId(row: SupplierRuleProposalRow | null | undefined): number | null {
  if (!row) return null;
  const raw = row.id as unknown;
  const n = typeof raw === 'number' ? raw : Number(raw);
  if (!Number.isFinite(n) || n <= 0) return null;
  return n;
}

function scrollPageToClassificationErrorBanner() {
  try {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  } catch {
    window.scrollTo(0, 0);
  }
}

function classificationRuleTextOperatorLabel(value: string | null | undefined): string {
  const v = (value || 'exact').trim().toLowerCase();
  if (v === 'contains') return 'Contains (whole words)';
  if (v === 'contains_substring') return 'Contains (substring)';
  if (v === 'not_contains') return 'Does not contain';
  return 'Is exactly';
}

function classificationRuleTextMatchDisplay(
  rawText: string | null | undefined,
  operator: string | null | undefined
): string {
  const text = String(rawText || '').trim();
  if (!text) return '—';
  return `${text} (${classificationRuleTextOperatorLabel(operator).toLowerCase()})`;
}

function classificationRuleTextMatchDisplayNode(
  rawText: string | null | undefined,
  operator: string | null | undefined
): React.ReactNode {
  const text = String(rawText || '').trim();
  if (!text) return '—';
  const mode = `(${classificationRuleTextOperatorLabel(operator).toLowerCase()})`;
  return (
    <>
      {text}{' '}
      <Box component="span" sx={{ color: 'text.disabled' }}>
        {mode}
      </Box>
    </>
  );
}

type SpecialCaseMatcherDisplayPart = {
  key: string;
  label: string;
  value: string;
  operator?: string | null;
};

function specialCaseMatcherShortLabel(kind: string): string {
  const k = String(kind || '').trim().toLowerCase();
  if (k === 'merchant') return 'Merch';
  if (k === 'description') return 'Desc';
  if (k === 'reference') return 'Ref';
  if (k === 'canonical_supplier') return 'Supp';
  if (k === 'category') return 'Cat';
  if (k === 'current_vat') return 'VAT';
  if (k === 'currency') return 'Cur';
  if (k === 'amount') return 'Amt';
  return 'Match';
}

function specialCaseMatcherDisplayParts(row: SpecialCaseRuleRow): SpecialCaseMatcherDisplayPart[] {
  const out: SpecialCaseMatcherDisplayPart[] = [];
  const criteria = Array.isArray(row.match_criteria) ? row.match_criteria : [];
  for (const criterion of criteria) {
    const kind = String(criterion?.kind || '').trim().toLowerCase();
    const value = String(criterion?.value || '').trim();
    if (!kind || !value) continue;
    const hasTextOperator =
      kind === 'merchant' ||
      kind === 'description' ||
      kind === 'reference' ||
      kind === 'canonical_supplier';
    out.push({
      key: `${kind}:${value}:${String(criterion?.operator || '')}`,
      label: specialCaseMatcherShortLabel(kind),
      value,
      operator: hasTextOperator ? String(criterion?.operator || 'exact').trim() || 'exact' : 'exact'
    });
  }
  if (out.length > 0) return out;

  const categoryName = String(row.category?.name || '').trim();
  if (categoryName) {
    out.push({ key: `cat:${categoryName}`, label: 'Cat', value: categoryName, operator: 'exact' });
  }
  const merchant = String(row.supplier_contains || '').trim();
  if (merchant) {
    out.push({
      key: `merch:${merchant}`,
      label: 'Merch',
      value: merchant,
      operator: String(row.text_match_operator || 'exact').trim() || 'exact'
    });
  }
  const desc = String(row.description_contains || '').trim();
  if (desc) {
    out.push({
      key: `desc:${desc}`,
      label: 'Desc',
      value: desc,
      operator: String(row.description_text_match_operator || row.text_match_operator || 'exact').trim() || 'exact'
    });
  }
  const ref = String(row.reference_contains || '').trim();
  if (ref) {
    out.push({
      key: `ref:${ref}`,
      label: 'Ref',
      value: ref,
      operator: String(row.reference_text_match_operator || row.text_match_operator || 'exact').trim() || 'exact'
    });
  }
  const curVat = String(row.current_vat_rate || '').trim();
  if (curVat) {
    out.push({ key: `vat:${curVat}`, label: 'VAT', value: curVat, operator: 'exact' });
  }
  return out;
}

function specialCaseMatcherDisplayNode(row: SpecialCaseRuleRow): React.ReactNode {
  const parts = specialCaseMatcherDisplayParts(row);
  if (parts.length === 0) return '—';
  return (
    <Stack direction="row" spacing={0.5} useFlexGap sx={{ flexWrap: 'wrap' }}>
      {parts.map((part, idx) => (
        <React.Fragment key={part.key}>
          <Box component="span">
            <Box component="span" sx={{ fontWeight: 700 }}>
              {part.label}:
            </Box>{' '}
            <Box component="span">{part.value}</Box>
            {part.operator ? (
              <Box component="span" sx={{ color: 'text.disabled', ml: 0.5 }}>
                ({classificationRuleTextOperatorLabel(part.operator)})
              </Box>
            ) : null}
          </Box>
          {idx < parts.length - 1 ? (
            <Box component="span" sx={{ color: 'text.disabled' }}>
              |
            </Box>
          ) : null}
        </React.Fragment>
      ))}
    </Stack>
  );
}

/**
 * Rules quickview: pseudo-logic line — classification rules combine matchers with AND
 * (same semantics as the editor); shown as `[…] AND […] → category` (no grouping braces
 * unless we later render nested boolean logic that needs them).
 */
function classificationRuleQuickviewLogicLine(rule: ClassificationRule): string {
  const squish = (s: string) => String(s || '').replace(/\s+/g, ' ').trim();
  const clauses: string[] = [];

  const canon = classificationRuleCanonicalSupplierDisplay(rule);
  if (canon && canon !== '—') {
    clauses.push(`[Canonical → ${squish(canon)}]`);
  }

  const merch = String(rule.supplier_contains || '').trim();
  if (merch) {
    const op = classificationRuleTextOperatorLabel(rule.text_match_operator).toLowerCase();
    clauses.push(`[Merchant: ${squish(merch)} (${op})]`);
  }

  const desc = String(rule.description_contains || '').trim();
  if (desc) {
    const op = classificationRuleTextOperatorLabel(
      rule.description_text_match_operator || rule.text_match_operator
    ).toLowerCase();
    clauses.push(`[Description: ${squish(desc)} (${op})]`);
  }

  const ref = String(rule.reference_contains || '').trim();
  if (ref) {
    const op = classificationRuleTextOperatorLabel(
      rule.reference_text_match_operator || rule.text_match_operator
    ).toLowerCase();
    clauses.push(`[Reference: ${squish(ref)} (${op})]`);
  }

  const amin = rule.amount_min;
  const amax = rule.amount_max;
  if (amin != null || amax != null) {
    const cur = String(rule.currency || '').trim();
    if (amin != null && amax != null && Number(amin) === Number(amax)) {
      clauses.push(`[Amount exact: ${amin}${cur ? ` ${cur}` : ''}]`);
    } else {
      const lo = amin != null ? String(amin) : '…';
      const hi = amax != null ? String(amax) : '…';
      clauses.push(`[Amount: ${lo}–${hi}${cur ? ` ${cur}` : ''}]`);
    }
  }

  const curOnly = String(rule.currency || '').trim();
  if (curOnly && amin == null && amax == null) {
    clauses.push(`[Currency: ${curOnly}]`);
  }

  const freq = String(rule.recurrence_type || '').trim();
  if (freq) {
    clauses.push(`[Frequency: ${freq}]`);
  }

  const scope = normalizeRuleAccountScope(rule.account_scope);
  if (scope !== 'both') {
    clauses.push(`[Bank: ${ruleAccountScopeLabel(rule.account_scope)}]`);
  }

  if (clauses.length === 0) {
    clauses.push('[No explicit text matchers]');
  }

  const cat = squish(rule.category?.name || '') || '—';
  const inner = clauses.join(' AND ');
  return `{ ${inner} } → ${cat}`;
}

const RULES_QV_LOGIC_MAX_LEN = 260;

function truncateRulesQuickviewLogicLine(logic: string): { full: string; shown: string } {
  const full = logic;
  const shown =
    full.length > RULES_QV_LOGIC_MAX_LEN ? `${full.slice(0, RULES_QV_LOGIC_MAX_LEN - 1)}…` : full;
  return { full, shown };
}

/** Monospace pseudo–boolean line under the rule title (Quickview only). */
const rulesQuickviewBooleanLogicCaptionSx = {
  mt: 0.75,
  display: 'block',
  fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace',
  fontSize: 11,
  lineHeight: 1.5,
  wordBreak: 'break-word',
  whiteSpace: 'pre-wrap'
} as const;

function supplierRuleQuickviewLogicLine(rule: SupplierRuleRow): string {
  const squish = (s: string) => String(s || '').replace(/\s+/g, ' ').trim();
  const clauses: string[] = [];

  // `supplier_name` is the canonical **output** supplier, not a text-match predicate — do not
  // show it as `[Supplier → …]` in the AND-clauses (that implied a supplier-field condition).

  const sn = String(rule.supplier_name || '').trim();

  const pattern = String(rule.pattern_raw || '').trim();
  if (pattern) {
    const op = classificationRuleTextOperatorLabel(rule.text_match_operator).toLowerCase();
    clauses.push(`[Merchant: ${squish(pattern)} (${op})]`);
  }

  const desc = String(rule.description_contains || '').trim();
  if (desc) {
    const op = classificationRuleTextOperatorLabel(
      rule.description_text_match_operator || rule.text_match_operator
    ).toLowerCase();
    clauses.push(`[Description: ${squish(desc)} (${op})]`);
  }

  const ref = String(rule.reference_contains || '').trim();
  if (ref) {
    const op = classificationRuleTextOperatorLabel(
      rule.reference_text_match_operator || rule.text_match_operator
    ).toLowerCase();
    clauses.push(`[Reference: ${squish(ref)} (${op})]`);
  }

  const scope = normalizeRuleAccountScope(rule.account_scope);
  if (scope !== 'both') {
    clauses.push(`[Bank: ${ruleAccountScopeLabel(rule.account_scope)}]`);
  }

  if (clauses.length === 0) {
    clauses.push('[No explicit matchers]');
  }

  const out =
    (sn ? squish(canonicalSupplierLabel(sn, sn)) : '') ||
    squish(rule.rule_name || '') ||
    squish(pattern) ||
    `Supplier #${rule.supplier_id}`;
  const inner = clauses.join(' AND ');
  return `{ ${inner} } → ${out}`;
}

function categoryVatRuleQuickviewLogicLine(rule: CategoryVatRuleRow): string {
  const squish = (s: string) => String(s || '').replace(/\s+/g, ' ').trim();
  const clauses: string[] = [];
  const catNames = vatRuleCategoryNames(rule);
  const catPart = catNames.length ? catNames.map((n) => squish(n)).join(', ') : '—';
  clauses.push(`[Categories: ${catPart}]`);
  const scope = normalizeRuleAccountScope(rule.account_scope);
  if (scope !== 'both') {
    clauses.push(`[Bank: ${ruleAccountScopeLabel(rule.account_scope)}]`);
  }
  const inner = clauses.join(' AND ');
  const rate = squish(rule.vat_rate || '') || '—';
  return `{ ${inner} } → VAT ${rate}`;
}

function specialCaseRuleQuickviewLogicLine(rule: SpecialCaseRuleRow): string {
  const squish = (s: string) => String(s || '').replace(/\s+/g, ' ').trim();
  const parts = specialCaseMatcherDisplayParts(rule);
  const pieces: string[] = [];
  parts.forEach((p) => {
    pieces.push(`[${p.label}: ${squish(p.value)}]`);
  });
  const scope = normalizeRuleAccountScope(rule.account_scope);
  if (scope !== 'both') {
    pieces.push(`[Bank: ${ruleAccountScopeLabel(rule.account_scope)}]`);
  }
  const matcher = pieces.length > 0 ? pieces.join(' OR ') : '[No explicit matchers]';
  const setRate = squish(rule.set_vat_rate || '') || '—';
  return `{ ${matcher} } → Set VAT ${setRate}`;
}

function classificationProposalQuickviewLogicLine(row: RuleProposalRow): string {
  const squish = (s: string) => String(s || '').replace(/\s+/g, ' ').trim();
  const p = row.payload || {};
  const clauses: string[] = [];

  const canon = classificationProposalCanonicalSupplierDisplay(row);
  if (canon && canon !== '—') {
    clauses.push(`[Canonical → ${squish(canon)}]`);
  }

  const merch = String(p.supplier_contains ?? row.supplier_contains ?? '').trim();
  if (merch) {
    const op = classificationRuleTextOperatorLabel(p.text_match_operator as string).toLowerCase();
    clauses.push(`[Merchant: ${squish(merch)} (${op})]`);
  }

  const desc = String(p.description_contains ?? row.description_contains ?? '').trim();
  if (desc) {
    const op = classificationRuleTextOperatorLabel(
      (p.description_text_match_operator as string) || (p.text_match_operator as string)
    ).toLowerCase();
    clauses.push(`[Description: ${squish(desc)} (${op})]`);
  }

  const ref = String(p.reference_contains ?? row.reference_contains ?? '').trim();
  if (ref) {
    const op = classificationRuleTextOperatorLabel(
      (p.reference_text_match_operator as string) || (p.text_match_operator as string)
    ).toLowerCase();
    clauses.push(`[Reference: ${squish(ref)} (${op})]`);
  }

  const amin = p.amount_min ?? row.amount_min;
  const amax = p.amount_max ?? row.amount_max;
  if (amin != null || amax != null) {
    const lo = amin != null ? String(amin) : '…';
    const hi = amax != null ? String(amax) : '…';
    const cur = String(p.currency ?? '').trim();
    clauses.push(`[Amount: ${lo}–${hi}${cur ? ` ${cur}` : ''}]`);
  }

  const curOnly = String(p.currency ?? '').trim();
  if (curOnly && amin == null && amax == null) {
    clauses.push(`[Currency: ${curOnly}]`);
  }

  const freq = String(p.recurrence_type ?? '').trim();
  if (freq) {
    clauses.push(`[Frequency: ${freq}]`);
  }

  const scope = normalizeRuleAccountScope((p.account_scope as string) || row.account_scope);
  if (scope !== 'both') {
    clauses.push(`[Bank: ${ruleAccountScopeLabel((p.account_scope as string) || row.account_scope)}]`);
  }

  if (clauses.length === 0) {
    clauses.push('[No explicit text matchers]');
  }

  const cat =
    squish(row.proposed_name || row.category_name || String(p.category_name || '') || '') || '—';
  const inner = clauses.join(' AND ');
  return `{ ${inner} } → ${cat}`;
}

function supplierProposalQuickviewLogicLine(row: SupplierRuleProposalRow): string {
  const squish = (s: string) => String(s || '').replace(/\s+/g, ' ').trim();
  const p = row.payload || {};
  const clauses: string[] = [];

  // `canonical_name` is the proposed **output** supplier, not an AND-clause matcher.

  const canon = String((p.canonical_name as string) || '').trim();

  const pattern = String((p.pattern_raw as string) || '').trim();
  if (pattern) {
    const op = classificationRuleTextOperatorLabel(p.text_match_operator as string).toLowerCase();
    clauses.push(`[Merchant: ${squish(pattern)} (${op})]`);
  }

  const desc = String((p.description_contains as string) || '').trim();
  if (desc) {
    const op = classificationRuleTextOperatorLabel(
      (p.description_text_match_operator as string) || (p.text_match_operator as string)
    ).toLowerCase();
    clauses.push(`[Description: ${squish(desc)} (${op})]`);
  }

  const ref = String((p.reference_contains as string) || '').trim();
  if (ref) {
    const op = classificationRuleTextOperatorLabel(
      (p.reference_text_match_operator as string) || (p.text_match_operator as string)
    ).toLowerCase();
    clauses.push(`[Reference: ${squish(ref)} (${op})]`);
  }

  const scope = normalizeRuleAccountScope(String(p.account_scope || row.account_scope || 'both'));
  if (scope !== 'both') {
    clauses.push(`[Bank: ${ruleAccountScopeLabel(String(p.account_scope || row.account_scope))}]`);
  }

  if (clauses.length === 0) {
    clauses.push('[No explicit matchers]');
  }

  const out =
    squish(row.proposed_name) ||
    (canon ? squish(canonicalSupplierLabel(canon, canon)) : '') ||
    pattern ||
    '—';
  const inner = clauses.join(' AND ');
  return `{ ${inner} } → ${out}`;
}

function rulesQuickviewListRowSx(theme: Theme) {
  return {
    mb: 1.25,
    py: 1.35,
    px: 1.75,
    borderRadius: 2,
    border: `1px solid ${theme.palette.divider}`,
    bgcolor:
      theme.palette.mode === 'dark'
        ? alpha(theme.palette.common.white, 0.06)
        : alpha(theme.palette.primary.main, 0.04),
    boxShadow: theme.shadows[1],
    transition: theme.transitions.create(['box-shadow', 'border-color'], {
      duration: theme.transitions.duration.shorter
    }),
    '&:hover': {
      boxShadow: theme.shadows[3],
      borderColor: alpha(theme.palette.primary.main, 0.45)
    }
  } as const;
}

const RULES_QV_SECTION_KEYS = [
  'classProposals',
  'supplierProposals',
  'classifications',
  'supplierRules',
  'vatRules',
  'specialCaseRules'
] as const;
type RulesQvSectionKey = (typeof RULES_QV_SECTION_KEYS)[number];

function defaultRulesQvSectionsOpen(): Record<RulesQvSectionKey, boolean> {
  return {
    classProposals: true,
    supplierProposals: true,
    classifications: true,
    supplierRules: true,
    vatRules: true,
    specialCaseRules: true
  };
}

function rulesQuickviewAccordionRootSx(theme: Theme) {
  return {
    mb: 1,
    '&:before': { display: 'none' },
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: 1,
    overflow: 'hidden',
    boxShadow: 'none'
  } as const;
}

function rulesQuickviewAccordionSummarySx(theme: Theme) {
  return {
    minHeight: 48,
    px: 1.25,
    borderLeft: '4px solid',
    borderLeftColor: theme.palette.primary.main,
    borderBottom: `2px solid ${theme.palette.divider}`,
    '& .MuiAccordionSummary-content': {
      margin: '10px 0',
      alignItems: 'center',
      width: '100%',
      minWidth: 0,
      overflow: 'visible'
    }
  } as const;
}

type EditExistingRuleSuggestion = {
  transaction_id: number | null;
  rule_id: number;
  rule_name: string;
  block_reason: string;
  shared_canonical: string[];
  suggested_changes: Array<{ field?: string; op?: string; value?: unknown }>;
};

/** Parse generate API `edit_existing_rule_suggestions` (prefer existing rule over new draft). */
function editExistingRuleSuggestionsFromApi(
  data: Record<string, unknown> | undefined
): EditExistingRuleSuggestion[] {
  if (!data || typeof data !== 'object') return [];
  const raw = (data as Record<string, unknown>).edit_existing_rule_suggestions;
  if (!Array.isArray(raw)) return [];
  const out: EditExistingRuleSuggestion[] = [];
  for (const row of raw) {
    if (!row || typeof row !== 'object') continue;
    const r = row as Record<string, unknown>;
    const ruleId = Math.trunc(Number(r.rule_id));
    if (!Number.isFinite(ruleId) || ruleId <= 0) continue;
    const tidRaw = r.transaction_id != null ? Math.trunc(Number(r.transaction_id)) : NaN;
    out.push({
      transaction_id: Number.isFinite(tidRaw) && tidRaw > 0 ? tidRaw : null,
      rule_id: ruleId,
      rule_name: String(r.rule_name || '').trim() || `Rule ${ruleId}`,
      block_reason: String(r.block_reason || '').trim(),
      shared_canonical: Array.isArray(r.shared_canonical)
        ? r.shared_canonical.map((x) => String(x || '').trim()).filter(Boolean)
        : [],
      suggested_changes: Array.isArray(r.suggested_changes)
        ? (r.suggested_changes as Array<{ field?: string; op?: string; value?: unknown }>)
        : []
    });
  }
  return out;
}

function summarizeEditExistingRuleSuggestion(s: EditExistingRuleSuggestion): string {
  const idLabel = formatPrefixedRuleId('classification', s.rule_id);
  const reason = s.block_reason || '';
  if (reason.startsWith('amount_null_blocked')) {
    return (
      `${idLabel} (“${s.rule_name}”) already targets this supplier; it cannot fire because the ` +
      `transaction has no amount (only the N/A heading rule applies to blank amounts)`
    );
  }
  if (reason.startsWith('broader_than_existing_rule') || reason.startsWith('would_conflict_existing_rule')) {
    return (
      `${idLabel} (“${s.rule_name}”) already covers this supplier more specifically — ` +
      `a new broad draft would conflict; review/edit that rule instead`
    );
  }
  if (reason.startsWith('amount_range_blocked')) {
    return `${idLabel} (“${s.rule_name}”) almost matches but the amount is outside the rule’s range — review/edit that rule before creating a new draft`;
  }
  if (reason.startsWith('text_mismatch')) {
    return `${idLabel} (“${s.rule_name}”) almost matches — consider widening its text matchers instead of a new draft`;
  }
  if (reason.startsWith('canonical_supplier_mismatch')) {
    return `${idLabel} (“${s.rule_name}”) is related — consider adding this supplier to that rule instead of a new draft`;
  }
  if (s.shared_canonical.length > 0) {
    return (
      `${idLabel} (“${s.rule_name}”) already uses canonical supplier ` +
      `${s.shared_canonical.join(', ')} — review/edit that rule before creating a new draft`
    );
  }
  return `${idLabel} (“${s.rule_name}”) looks related — review/edit that rule before creating a new draft`;
}

/** Human-readable reasons when POST …/generate-from-transaction-ids returns created === 0. */
function summarizeProposalGenerationSkipsFromApi(data: Record<string, unknown> | undefined): string {
  if (!data || typeof data !== 'object') return '';
  const n = (k: string) => Math.max(0, Math.trunc(Number((data as Record<string, unknown>)[k] ?? 0)));
  const parts: string[] = [];
  const editSuggestions = editExistingRuleSuggestionsFromApi(data);
  if (editSuggestions.length > 0) {
    parts.push(summarizeEditExistingRuleSuggestion(editSuggestions[0]));
  } else if (n('skipped_prefer_existing_rule')) {
    parts.push('an existing classification rule already covers this case — review/edit it instead of a new draft');
  }
  if (n('skipped_matching_rule')) {
    parts.push('a granular classification rule already matches this transaction');
  }
  if (n('skipped_matching_pending_proposal')) {
    parts.push('a pending proposal already matches the same text');
  }
  if (n('skipped_rule_applied')) {
    parts.push('a rule-applied category tag is already on the transaction');
  }
  if (n('skipped_existing_proposal_for_tx')) {
    parts.push('a proposal is already linked to this transaction');
  }
  if (n('skipped_no_granular_tag')) {
    parts.push(
      'no category the generator could use (tags are only headings/unclassified, and no merchant keyword or LLM suggestion mapped to a real category)'
    );
  }
  if (n('skipped_no_match_text')) {
    parts.push('missing merchant/description text to build match fields');
  }
  if (parts.length === 0) return '';
  return ` ${parts.join(' · ')}`;
}

/** Numeric proposal ids from generate API (`created_proposal_ids` / `existing_proposal_ids`). */
function proposalIdListFromApi(data: Record<string, unknown> | undefined, key: string): number[] {
  if (!data || typeof data !== 'object') return [];
  const raw = (data as Record<string, unknown>)[key];
  if (!Array.isArray(raw)) return [];
  const out: number[] = [];
  const seen = new Set<number>();
  for (const x of raw) {
    const n = Math.trunc(Number(x));
    if (!Number.isFinite(n) || n <= 0 || seen.has(n)) continue;
    seen.add(n);
    out.push(n);
  }
  return out;
}

function ruleAccountScopeLabel(value: string | null | undefined): string {
  const scope = normalizeRuleAccountScope(value);
  if (scope === 'ireland') return 'Revolut Ire';
  if (scope === 'uk') return 'Revolut UK';
  return 'Both';
}

function ruleAccountScopeTableLabel(value: string | null | undefined): string {
  const scope = normalizeRuleAccountScope(value);
  if (scope === 'ireland') return 'IE';
  if (scope === 'uk') return 'UK';
  return 'Both';
}

function appendBulkComment(existing: string | null | undefined, commentToAppend: string): string | null {
  const add = String(commentToAppend || '').trim();
  const base = String(existing || '').trim();
  if (!add) return base || null;
  if (!base) return add;
  return `${base}\n${add}`;
}

/** Local form state for the “Edit rule” dialog (strings for inputs). */
type RuleEditDraft = {
  id: number;
  isNew?: boolean;
  proposal_id?: number | null;
  proposal_atlas_key?: string | null;
  name: string;
  priority: string;
  category_id: number;
  /** Canonical supplier names (OR); shown on classified transactions when used alone as match scope. */
  supplier_name_overrides: string[];
  supplier_contains: string;
  description_contains: string;
  description_text_match_operator: string;
  reference_contains: string;
  reference_text_match_operator: string;
  text_match_operator: string;
  account_scope: string;
  amount_min: string;
  amount_max: string;
  currency: string;
  recurrence_type: string;
  is_active: boolean;
  notes: string;
};

function classificationExactAmountMirror(draft: RuleEditDraft): string {
  const a = draft.amount_min.trim();
  const b = draft.amount_max.trim();
  if (!a || !b || a !== b) return '';
  return a;
}

/** Which rule table row is currently persisting (modal save in flight). */
type RuleTableSaveHighlight =
  | { kind: 'classification'; rowKey: string }
  | { kind: 'supplier'; rowKey: string }
  | { kind: 'vat'; rowKey: string }
  | { kind: 'specialCase'; rowKey: string };

function classificationSaveHighlightRowKey(
  draft: RuleEditDraft,
  filteredProposals: RuleProposalRow[]
): string | null {
  const atlasKey = draft.proposal_atlas_key ? String(draft.proposal_atlas_key).trim() : '';
  if (atlasKey) {
    const idx = filteredProposals.findIndex((p) => p.virtual && String(p.atlas_key || '').trim() === atlasKey);
    if (idx >= 0) return `cp-${filteredProposals[idx].id ?? 'v'}-${idx}`;
  }
  const propId = draft.proposal_id != null ? Math.trunc(Number(draft.proposal_id)) : NaN;
  if (Number.isFinite(propId) && propId > 0) {
    const idx = filteredProposals.findIndex((p) => Number(p.id || 0) === propId);
    if (idx >= 0) return `cp-${filteredProposals[idx].id ?? 'v'}-${idx}`;
  }
  const ruleId = Math.trunc(Number(draft.id));
  if (Number.isFinite(ruleId) && ruleId > 0 && !draft.proposal_id && !draft.proposal_atlas_key) {
    return `cc-${ruleId}`;
  }
  return null;
}

function ruleTableRowSavingSx(theme: Theme, active: boolean): Record<string, unknown> | undefined {
  if (!active) return undefined;
  return {
    backgroundColor: alpha(theme.palette.primary.main, theme.palette.mode === 'dark' ? 0.2 : 0.12),
    boxShadow: `inset 4px 0 0 ${theme.palette.primary.main}`
  };
}

type RuleEditMatchCriteriaEnabled = {
  canonicalSupplier: boolean;
  merchantCounterparty: boolean;
  description: boolean;
  reference: boolean;
  amountCurrency: boolean;
  frequency: boolean;
};

function initialRuleEditMatchCriteriaEnabled(draft: RuleEditDraft): RuleEditMatchCriteriaEnabled {
  return {
    canonicalSupplier: (draft.supplier_name_overrides || []).length > 0,
    merchantCounterparty: Boolean((draft.supplier_contains || '').trim()),
    description: Boolean((draft.description_contains || '').trim()),
    reference: Boolean((draft.reference_contains || '').trim()),
    amountCurrency: Boolean((draft.amount_min || '').trim() || (draft.amount_max || '').trim() || (draft.currency || '').trim()),
    frequency: Boolean((draft.recurrence_type || '').trim())
  };
}

type EditableRuleType = 'classification' | 'supplier';

function classificationRuleToEditDraft(rule: ClassificationRule): RuleEditDraft {
  const supplierOpRaw = (rule.text_match_operator || 'exact').trim() || 'exact';
  const hasSupplierText = Boolean((rule.supplier_contains ?? '').trim());
  const hasDescriptionText = Boolean((rule.description_contains ?? '').trim());
  const hasReferenceText = Boolean((rule.reference_contains ?? '').trim());
  const supplierOp = hasSupplierText ? supplierOpRaw : 'exact';
  return {
    id: rule.id,
    isNew: false,
    proposal_id: null,
    proposal_atlas_key: null,
    name: rule.name || '',
    priority: String(rule.priority ?? 100),
    category_id: rule.category_id,
    supplier_name_overrides: supplierOverridesFromClassificationRule(rule),
    supplier_contains: rule.supplier_contains ?? '',
    description_contains: rule.description_contains ?? '',
    description_text_match_operator: hasDescriptionText
      ? ((rule.description_text_match_operator || supplierOpRaw).trim() || supplierOpRaw)
      : 'exact',
    reference_contains: rule.reference_contains ?? '',
    reference_text_match_operator: hasReferenceText
      ? ((rule.reference_text_match_operator || supplierOpRaw).trim() || supplierOpRaw)
      : 'exact',
    text_match_operator: supplierOp,
    account_scope: normalizeRuleAccountScope(rule.account_scope),
    amount_min: rule.amount_min != null ? String(rule.amount_min) : '',
    amount_max: rule.amount_max != null ? String(rule.amount_max) : '',
    currency: rule.currency ?? '',
    recurrence_type: rule.recurrence_type ?? '',
    is_active: !!rule.is_active,
    notes: rule.notes ?? ''
  };
}

type ClassificationRuleMatchOutputBlockProps = {
  idPrefix: string;
  draft: RuleEditDraft;
  setDraft: React.Dispatch<React.SetStateAction<RuleEditDraft | null>>;
  readOnly: boolean;
  saving: boolean;
  matchCriteriaEnabled: RuleEditMatchCriteriaEnabled;
  setMatchCriteriaEnabled: React.Dispatch<React.SetStateAction<RuleEditMatchCriteriaEnabled>>;
  matchCriteriaMenuAnchorEl: HTMLButtonElement | null;
  setMatchCriteriaMenuAnchorEl: (el: HTMLButtonElement | null) => void;
  outputMenuAnchorEl: HTMLButtonElement | null;
  setOutputMenuAnchorEl: (el: HTMLButtonElement | null) => void;
  outputCategoryEnabled: boolean;
  setOutputCategoryEnabled: (v: boolean) => void;
  outputSupplierEnabled: boolean;
  setOutputSupplierEnabled: (v: boolean) => void;
  ruleEditTargetType: EditableRuleType;
  supplierOptions: string[];
  categorySelectOptions: Array<{ id: number; name: string }>;
  explanationSentence: string;
  onOpenCategoryCrudRuleEdit: () => void;
  onNewSupplierFromDraft: (draft: RuleEditDraft) => void;
};

function ClassificationRuleMatchOutputBlock({
  idPrefix,
  draft,
  setDraft,
  readOnly,
  saving,
  matchCriteriaEnabled,
  setMatchCriteriaEnabled,
  matchCriteriaMenuAnchorEl,
  setMatchCriteriaMenuAnchorEl,
  outputMenuAnchorEl,
  setOutputMenuAnchorEl,
  outputCategoryEnabled,
  setOutputCategoryEnabled,
  outputSupplierEnabled,
  setOutputSupplierEnabled,
  ruleEditTargetType,
  supplierOptions,
  categorySelectOptions,
  explanationSentence,
  onOpenCategoryCrudRuleEdit,
  onNewSupplierFromDraft
}: ClassificationRuleMatchOutputBlockProps) {
  const hasCanonicalSupplier = draft.supplier_name_overrides.length > 0;
  const hasMerchantCounterparty = Boolean(draft.supplier_contains.trim());
  const hasDescription = Boolean(draft.description_contains.trim());
  const hasReference = Boolean(draft.reference_contains.trim());
  const hasAmountCurrency = Boolean(
    draft.amount_min.trim() || draft.amount_max.trim() || draft.currency.trim()
  );
  const hasFrequency = Boolean(draft.recurrence_type.trim());
  const showCanonicalSupplier = readOnly
    ? hasCanonicalSupplier
    : hasCanonicalSupplier || matchCriteriaEnabled.canonicalSupplier;
  const showMerchantCounterparty = readOnly
    ? hasMerchantCounterparty
    : hasMerchantCounterparty || matchCriteriaEnabled.merchantCounterparty;
  const showDescription = readOnly
    ? hasDescription
    : hasDescription || matchCriteriaEnabled.description;
  const showReference = readOnly
    ? hasReference
    : hasReference || matchCriteriaEnabled.reference;
  const showAmountCurrency = readOnly
    ? hasAmountCurrency
    : hasAmountCurrency || matchCriteriaEnabled.amountCurrency;
  const showFrequency = readOnly
    ? hasFrequency
    : hasFrequency || matchCriteriaEnabled.frequency;
  return (
    <>
              <Typography variant="subtitle2" color="text.secondary" sx={{ fontWeight: 700, pt: 0.5 }}>
                TX Matching Method(s)
              </Typography>
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems={{ sm: 'center' }}>
                <FormControl size="small" sx={{ flex: 1, minWidth: 0 }} fullWidth>
                  <InputLabel id={`${idPrefix}-rule-edit-account-scope-label`}>Bank account</InputLabel>
                  <Select
                    labelId={`${idPrefix}-rule-edit-account-scope-label`}
                    label="Bank account"
                    value={draft.account_scope}
                    onChange={(e) =>
                      setDraft((d) =>
                        d ? { ...d, account_scope: normalizeRuleAccountScope(String(e.target.value || 'both')) } : d
                      )
                    }
                  >
                    {RULE_ACCOUNT_SCOPE_OPTIONS.map((opt) => (
                      <MenuItem key={`${idPrefix}-rule-account-scope-${opt.value}`} value={opt.value}>
                        {opt.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <Button
                  variant="outlined"
                  size="small"
                  startIcon={<AddIcon />}
                  onClick={(e) => setMatchCriteriaMenuAnchorEl(e.currentTarget)}
                  disabled={readOnly}
                  sx={{ whiteSpace: 'nowrap', alignSelf: { xs: 'flex-start', sm: 'center' } }}
                >
                  Add Match Criteria
                </Button>
              </Stack>
              <Typography variant="caption" color="text.secondary">
                {readOnly
                  ? 'Read-only mode: only currently used criteria are shown.'
                  : 'Add only the criteria you want this rule to use.'}
              </Typography>
              <Stack spacing={1}>
                <Menu
                  anchorEl={matchCriteriaMenuAnchorEl}
                  open={Boolean(matchCriteriaMenuAnchorEl)}
                  onClose={() => setMatchCriteriaMenuAnchorEl(null)}
                >
                  <MenuItem
                    disabled={showCanonicalSupplier}
                    onClick={() => {
                      setMatchCriteriaEnabled((prev) => ({ ...prev, canonicalSupplier: true }));
                      setMatchCriteriaMenuAnchorEl(null);
                    }}
                  >
                    Canonical supplier
                  </MenuItem>
                  <MenuItem
                    onClick={() => {
                      setDraft((d) => {
                        if (!d) return d;
                        const next = splitRuleOrValues(d.supplier_contains);
                        if (showMerchantCounterparty) next.push('');
                        return { ...d, supplier_contains: joinRuleOrValues(next) };
                      });
                      setMatchCriteriaEnabled((prev) => ({ ...prev, merchantCounterparty: true }));
                      setMatchCriteriaMenuAnchorEl(null);
                    }}
                  >
                    Merchant / Counterparty match
                  </MenuItem>
                  <MenuItem
                    onClick={() => {
                      setDraft((d) => {
                        if (!d) return d;
                        const next = splitRuleOrValues(d.description_contains);
                        if (showDescription) next.push('');
                        return { ...d, description_contains: joinRuleOrValues(next) };
                      });
                      setMatchCriteriaEnabled((prev) => ({ ...prev, description: true }));
                      setMatchCriteriaMenuAnchorEl(null);
                    }}
                  >
                    Description match
                  </MenuItem>
                  <MenuItem
                    onClick={() => {
                      setDraft((d) => {
                        if (!d) return d;
                        const next = splitRuleOrValues(d.reference_contains);
                        if (showReference) next.push('');
                        return { ...d, reference_contains: joinRuleOrValues(next) };
                      });
                      setMatchCriteriaEnabled((prev) => ({ ...prev, reference: true }));
                      setMatchCriteriaMenuAnchorEl(null);
                    }}
                  >
                    Reference match
                  </MenuItem>
                  <MenuItem
                    disabled={showAmountCurrency}
                    onClick={() => {
                      setMatchCriteriaEnabled((prev) => ({ ...prev, amountCurrency: true }));
                      setMatchCriteriaMenuAnchorEl(null);
                    }}
                  >
                    Amount / currency
                  </MenuItem>
                  <MenuItem
                    disabled={showFrequency}
                    onClick={() => {
                      setMatchCriteriaEnabled((prev) => ({ ...prev, frequency: true }));
                      setMatchCriteriaMenuAnchorEl(null);
                    }}
                  >
                    Frequency
                  </MenuItem>
                </Menu>
                {showCanonicalSupplier ? (
                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems={{ sm: 'flex-start' }}>
                  <Autocomplete
                    size="small"
                    fullWidth
                    multiple
                    freeSolo
                    filterSelectedOptions
                    selectOnFocus
                    options={supplierOptions}
                    value={draft.supplier_name_overrides}
                    onChange={(_, v) => {
                      const next = dedupeSupplierNameList(
                        (v || []).map((item) => (typeof item === 'string' ? item : String(item)))
                      );
                      setDraft((d) => (d ? { ...d, supplier_name_overrides: next } : d));
                      setMatchCriteriaEnabled((prev) => ({ ...prev, canonicalSupplier: next.length > 0 }));
                    }}
                    renderTags={() => null}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Canonical supplier"
                        InputProps={{
                          ...params.InputProps,
                          endAdornment: (
                            <>
                              <Tooltip
                                title="Pick one or more, or type names and press Enter. When set, the transaction must already have that name as an active supplier tag from pipeline Step 2 (tx_supplier_tags). Classification does not re-derive suppliers from statement text. Combined with any merchant, description, or reference conditions below using AND (see “How this Rule Works”)."
                              >
                                <InfoOutlinedIcon
                                  fontSize="small"
                                  sx={{ color: 'text.secondary', mr: 0.5, cursor: 'help' }}
                                />
                              </Tooltip>
                              {params.InputProps.endAdornment}
                            </>
                          )
                        }}
                      />
                    )}
                  />
                  <Button
                    variant="outlined"
                    size="small"
                    sx={{ mt: { sm: 0.5 }, flexShrink: 0 }}
                    disabled={saving}
                    onClick={() => onNewSupplierFromDraft(draft)}
                  >
                    New supplier…
                  </Button>
                  <IconButton
                    size="small"
                    color="error"
                    aria-label="Remove match criterion"
                    onClick={() => {
                      setMatchCriteriaEnabled((prev) => ({ ...prev, canonicalSupplier: false }));
                      setDraft((d) => (d ? { ...d, supplier_name_overrides: [] } : d));
                    }}
                  >
                    <DeleteOutlineIcon fontSize="small" />
                  </IconButton>
                </Stack>
                ) : null}
                {draft.supplier_name_overrides.length > 0 ? (
                  <Stack direction="row" flexWrap="wrap" useFlexGap spacing={0.5} sx={{ pl: 0.5 }}>
                    {draft.supplier_name_overrides.map((name, idx) => (
                      <Chip
                        key={`${name}-${idx}`}
                        size="small"
                        label={canonicalSupplierLabel(name, name)}
                        onDelete={() =>
                          setDraft((d) => {
                            if (!d) return d;
                            const nextOverrides = d.supplier_name_overrides.filter((_, i) => i !== idx);
                            setMatchCriteriaEnabled((prev) => ({
                              ...prev,
                              canonicalSupplier: nextOverrides.length > 0
                            }));
                            return {
                              ...d,
                              supplier_name_overrides: nextOverrides
                            };
                          })
                        }
                      />
                    ))}
                  </Stack>
                ) : null}
                {showMerchantCounterparty ? (
                <Stack spacing={1}>
                  {splitRuleOrValues(draft.supplier_contains).map((merchantValue, merchantIdx, merchantParts) => (
                    <Stack key={`${idPrefix}-merchant-match-${merchantIdx}`} direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                      <FormControl size="small" sx={{ width: { sm: 220 } }}>
                        <InputLabel>{`Type ${merchantIdx + 1}`}</InputLabel>
                        <Select
                          label={`Type ${merchantIdx + 1}`}
                          value="merchant"
                          onChange={(e) => {
                            const target = String(e.target.value || 'merchant');
                            if (target === 'merchant') return;
                            const source = [...merchantParts];
                            const movedValue = source[merchantIdx] || '';
                            source.splice(merchantIdx, 1);
                            const desc = splitRuleOrValues(draft.description_contains);
                            const ref = splitRuleOrValues(draft.reference_contains);
                            if (target === 'description') desc.push(movedValue);
                            if (target === 'reference') ref.push(movedValue);
                            const nextSupplier = joinRuleOrValues(source);
                            const nextDesc = joinRuleOrValues(desc);
                            const nextRef = joinRuleOrValues(ref);
                            setDraft((d) =>
                              d
                                ? {
                                    ...d,
                                    supplier_contains: nextSupplier,
                                    description_contains: nextDesc,
                                    reference_contains: nextRef
                                  }
                                : d
                            );
                            setMatchCriteriaEnabled((prev) => ({
                              ...prev,
                              merchantCounterparty: source.some((part) => part.trim().length > 0),
                              description: desc.some((part) => part.trim().length > 0),
                              reference: ref.some((part) => part.trim().length > 0)
                            }));
                          }}
                        >
                          <MenuItem value="merchant">Merchant / Counterparty</MenuItem>
                          <MenuItem value="description">Description</MenuItem>
                          <MenuItem value="reference">Reference</MenuItem>
                        </Select>
                      </FormControl>
                      <TextField
                        label={`Merchant / Counterparty match${merchantIdx > 0 ? ` ${merchantIdx + 1}` : ' (optional)'}`}
                        value={merchantValue}
                        onChange={(e) => {
                          const v = e.target.value;
                          const next = [...merchantParts];
                          next[merchantIdx] = v;
                          setMatchCriteriaEnabled((prev) => ({
                            ...prev,
                            merchantCounterparty: next.some((part) => part.trim().length > 0)
                          }));
                          setDraft((d) => {
                            if (!d) return d;
                            const draftNext: RuleEditDraft = { ...d, supplier_contains: joinRuleOrValues(next) };
                            if (!next.some((part) => part.trim().length > 0)) draftNext.text_match_operator = 'exact';
                            return draftNext;
                          });
                        }}
                        fullWidth
                        size="small"
                        InputProps={buildClearableInputProps(
                          merchantValue,
                          () => {
                            const next = [...merchantParts];
                            next[merchantIdx] = '';
                            setMatchCriteriaEnabled((prev) => ({
                              ...prev,
                              merchantCounterparty: next.some((part) => part.trim().length > 0)
                            }));
                            setDraft((d) => {
                              if (!d) return d;
                              const draftNext: RuleEditDraft = { ...d, supplier_contains: joinRuleOrValues(next) };
                              if (!next.some((part) => part.trim().length > 0)) draftNext.text_match_operator = 'exact';
                              return draftNext;
                            });
                          },
                          {
                            endAdornment: (
                              <Tooltip title="If set, matches merchant or counterparty text using the merchant/counterparty match mode.">
                                <InfoOutlinedIcon fontSize="small" sx={{ color: 'text.secondary', cursor: 'help' }} />
                              </Tooltip>
                            )
                          },
                          'Clear merchant match'
                        )}
                      />
                  <Box
                    aria-hidden
                    sx={{
                      display: { xs: 'none', sm: 'flex' },
                      alignItems: 'center',
                      justifyContent: 'center',
                      color: 'text.disabled'
                    }}
                  >
                    <LinkIcon fontSize="small" />
                  </Box>
                  <FormControl size="small" sx={{ width: { sm: 220 } }}>
                    <InputLabel id={`${idPrefix}-rule-edit-supplier-text-match-operator-label`}>Text Match Mode</InputLabel>
                    <Select
                      labelId={`${idPrefix}-rule-edit-supplier-text-match-operator-label`}
                      label="Text Match Mode"
                      value={draft.text_match_operator}
                      disabled={!draft.supplier_contains.trim()}
                      onChange={(e) =>
                        setDraft((d) =>
                          d ? { ...d, text_match_operator: String(e.target.value || 'exact') } : d
                        )
                      }
                    >
                      {RULE_TEXT_MATCH_OPERATOR_OPTIONS.map((opt) => (
                        <MenuItem key={`${idPrefix}-rule-supplier-match-op-${opt.value}`} value={opt.value}>
                          {opt.label}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                  <IconButton
                    size="small"
                    color="error"
                    aria-label="Remove match criterion"
                    onClick={() => {
                      const next = merchantParts.filter((_, idx) => idx !== merchantIdx);
                      const hasAny = next.some((part) => part.trim().length > 0);
                      setMatchCriteriaEnabled((prev) => ({ ...prev, merchantCounterparty: hasAny }));
                      setDraft((d) => {
                        if (!d) return d;
                        const draftNext: RuleEditDraft = { ...d, supplier_contains: joinRuleOrValues(next) };
                        if (!hasAny) draftNext.text_match_operator = 'exact';
                        return draftNext;
                      });
                    }}
                  >
                    <DeleteOutlineIcon fontSize="small" />
                  </IconButton>
                </Stack>
                  ))}
                </Stack>
                ) : null}
              </Stack>
              {showDescription ? (
              <Stack spacing={1}>
                {splitRuleOrValues(draft.description_contains).map((descriptionValue, descriptionIdx, descriptionParts) => (
                  <Stack key={`${idPrefix}-description-match-${descriptionIdx}`} direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                    <FormControl size="small" sx={{ width: { sm: 220 } }}>
                      <InputLabel>{`Type ${descriptionIdx + 1}`}</InputLabel>
                      <Select
                        label={`Type ${descriptionIdx + 1}`}
                        value="description"
                        onChange={(e) => {
                          const target = String(e.target.value || 'description');
                          if (target === 'description') return;
                          const source = [...descriptionParts];
                          const movedValue = source[descriptionIdx] || '';
                          source.splice(descriptionIdx, 1);
                          const merch = splitRuleOrValues(draft.supplier_contains);
                          const ref = splitRuleOrValues(draft.reference_contains);
                          if (target === 'merchant') merch.push(movedValue);
                          if (target === 'reference') ref.push(movedValue);
                          const nextSupplier = joinRuleOrValues(merch);
                          const nextDesc = joinRuleOrValues(source);
                          const nextRef = joinRuleOrValues(ref);
                          setDraft((d) =>
                            d
                              ? {
                                  ...d,
                                  supplier_contains: nextSupplier,
                                  description_contains: nextDesc,
                                  reference_contains: nextRef
                                }
                              : d
                          );
                          setMatchCriteriaEnabled((prev) => ({
                            ...prev,
                            merchantCounterparty: merch.some((part) => part.trim().length > 0),
                            description: source.some((part) => part.trim().length > 0),
                            reference: ref.some((part) => part.trim().length > 0)
                          }));
                        }}
                      >
                        <MenuItem value="merchant">Merchant / Counterparty</MenuItem>
                        <MenuItem value="description">Description</MenuItem>
                        <MenuItem value="reference">Reference</MenuItem>
                      </Select>
                    </FormControl>
                    <TextField
                      label={`Description match${descriptionIdx > 0 ? ` ${descriptionIdx + 1}` : ''}`}
                      value={descriptionValue}
                      onChange={(e) => {
                        const v = e.target.value;
                        const next = [...descriptionParts];
                        next[descriptionIdx] = v;
                        setMatchCriteriaEnabled((prev) => ({
                          ...prev,
                          description: next.some((part) => part.trim().length > 0)
                        }));
                        setDraft((d) => {
                          if (!d) return d;
                          const draftNext: RuleEditDraft = { ...d, description_contains: joinRuleOrValues(next) };
                          if (!next.some((part) => part.trim().length > 0)) {
                            draftNext.description_text_match_operator = 'exact';
                          }
                          return draftNext;
                        });
                      }}
                      fullWidth
                      size="small"
                      InputProps={buildClearableInputProps(
                        descriptionValue,
                        () => {
                          const next = [...descriptionParts];
                          next[descriptionIdx] = '';
                          setMatchCriteriaEnabled((prev) => ({
                            ...prev,
                            description: next.some((part) => part.trim().length > 0)
                          }));
                          setDraft((d) => {
                            if (!d) return d;
                            const draftNext: RuleEditDraft = { ...d, description_contains: joinRuleOrValues(next) };
                            if (!next.some((part) => part.trim().length > 0)) {
                              draftNext.description_text_match_operator = 'exact';
                            }
                            return draftNext;
                          });
                        },
                        {
                          endAdornment: (
                            <Tooltip title="Rule text to compare against the transaction description.">
                              <InfoOutlinedIcon fontSize="small" sx={{ color: 'text.secondary', cursor: 'help' }} />
                            </Tooltip>
                          )
                        },
                        'Clear description match'
                      )}
                    />
                <Box
                  aria-hidden
                  sx={{
                    display: { xs: 'none', sm: 'flex' },
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: 'text.disabled'
                  }}
                >
                  <LinkIcon fontSize="small" />
                </Box>
                <FormControl size="small" sx={{ width: { sm: 220 } }}>
                  <InputLabel id={`${idPrefix}-rule-edit-description-text-match-operator-label`}>Text Match Mode</InputLabel>
                  <Select
                    labelId={`${idPrefix}-rule-edit-description-text-match-operator-label`}
                    label="Text Match Mode"
                    value={draft.description_text_match_operator}
                    disabled={!draft.description_contains.trim()}
                    onChange={(e) =>
                      setDraft((d) =>
                        d ? { ...d, description_text_match_operator: String(e.target.value || 'exact') } : d
                      )
                    }
                  >
                    {RULE_TEXT_MATCH_OPERATOR_OPTIONS.map((opt) => (
                      <MenuItem key={`${idPrefix}-rule-description-match-op-${opt.value}`} value={opt.value}>
                        {opt.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <IconButton
                  size="small"
                  color="error"
                  aria-label="Remove match criterion"
                  onClick={() => {
                    const next = descriptionParts.filter((_, idx) => idx !== descriptionIdx);
                    const hasAny = next.some((part) => part.trim().length > 0);
                    setMatchCriteriaEnabled((prev) => ({ ...prev, description: hasAny }));
                    setDraft((d) => {
                      if (!d) return d;
                      const draftNext: RuleEditDraft = { ...d, description_contains: joinRuleOrValues(next) };
                      if (!hasAny) draftNext.description_text_match_operator = 'exact';
                      return draftNext;
                    });
                  }}
                >
                  <DeleteOutlineIcon fontSize="small" />
                </IconButton>
              </Stack>
                ))}
              </Stack>
              ) : null}
              {showReference ? (
              <Stack spacing={1}>
                {splitRuleOrValues(draft.reference_contains).map((referenceValue, referenceIdx, referenceParts) => (
                  <Stack key={`${idPrefix}-reference-match-${referenceIdx}`} direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                    <FormControl size="small" sx={{ width: { sm: 220 } }}>
                      <InputLabel>{`Type ${referenceIdx + 1}`}</InputLabel>
                      <Select
                        label={`Type ${referenceIdx + 1}`}
                        value="reference"
                        onChange={(e) => {
                          const target = String(e.target.value || 'reference');
                          if (target === 'reference') return;
                          const source = [...referenceParts];
                          const movedValue = source[referenceIdx] || '';
                          source.splice(referenceIdx, 1);
                          const merch = splitRuleOrValues(draft.supplier_contains);
                          const desc = splitRuleOrValues(draft.description_contains);
                          if (target === 'merchant') merch.push(movedValue);
                          if (target === 'description') desc.push(movedValue);
                          const nextSupplier = joinRuleOrValues(merch);
                          const nextDesc = joinRuleOrValues(desc);
                          const nextRef = joinRuleOrValues(source);
                          setDraft((d) =>
                            d
                              ? {
                                  ...d,
                                  supplier_contains: nextSupplier,
                                  description_contains: nextDesc,
                                  reference_contains: nextRef
                                }
                              : d
                          );
                          setMatchCriteriaEnabled((prev) => ({
                            ...prev,
                            merchantCounterparty: merch.some((part) => part.trim().length > 0),
                            description: desc.some((part) => part.trim().length > 0),
                            reference: source.some((part) => part.trim().length > 0)
                          }));
                        }}
                      >
                        <MenuItem value="merchant">Merchant / Counterparty</MenuItem>
                        <MenuItem value="description">Description</MenuItem>
                        <MenuItem value="reference">Reference</MenuItem>
                      </Select>
                    </FormControl>
                    <TextField
                      label={`Reference match${referenceIdx > 0 ? ` ${referenceIdx + 1}` : ''}`}
                      value={referenceValue}
                      onChange={(e) => {
                        const v = e.target.value;
                        const next = [...referenceParts];
                        next[referenceIdx] = v;
                        setMatchCriteriaEnabled((prev) => ({
                          ...prev,
                          reference: next.some((part) => part.trim().length > 0)
                        }));
                        setDraft((d) => {
                          if (!d) return d;
                          const draftNext: RuleEditDraft = { ...d, reference_contains: joinRuleOrValues(next) };
                          if (!next.some((part) => part.trim().length > 0)) {
                            draftNext.reference_text_match_operator = 'exact';
                          }
                          return draftNext;
                        });
                      }}
                      fullWidth
                      size="small"
                      InputProps={buildClearableInputProps(
                        referenceValue,
                        () => {
                          const next = [...referenceParts];
                          next[referenceIdx] = '';
                          setMatchCriteriaEnabled((prev) => ({
                            ...prev,
                            reference: next.some((part) => part.trim().length > 0)
                          }));
                          setDraft((d) => {
                            if (!d) return d;
                            const draftNext: RuleEditDraft = { ...d, reference_contains: joinRuleOrValues(next) };
                            if (!next.some((part) => part.trim().length > 0)) {
                              draftNext.reference_text_match_operator = 'exact';
                            }
                            return draftNext;
                          });
                        },
                        {
                          endAdornment: (
                            <Tooltip title="Rule text to compare against the transaction bank reference.">
                              <InfoOutlinedIcon fontSize="small" sx={{ color: 'text.secondary', cursor: 'help' }} />
                            </Tooltip>
                          )
                        },
                        'Clear reference match'
                      )}
                    />
                <Box
                  aria-hidden
                  sx={{
                    display: { xs: 'none', sm: 'flex' },
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: 'text.disabled'
                  }}
                >
                  <LinkIcon fontSize="small" />
                </Box>
                <FormControl size="small" sx={{ width: { sm: 220 } }}>
                  <InputLabel id={`${idPrefix}-rule-edit-reference-text-match-operator-label`}>Text Match Mode</InputLabel>
                  <Select
                    labelId={`${idPrefix}-rule-edit-reference-text-match-operator-label`}
                    label="Text Match Mode"
                    value={draft.reference_text_match_operator}
                    disabled={!draft.reference_contains.trim()}
                    onChange={(e) =>
                      setDraft((d) =>
                        d ? { ...d, reference_text_match_operator: String(e.target.value || 'exact') } : d
                      )
                    }
                  >
                    {RULE_TEXT_MATCH_OPERATOR_OPTIONS.map((opt) => (
                      <MenuItem key={`${idPrefix}-rule-reference-match-op-${opt.value}`} value={opt.value}>
                        {opt.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <IconButton
                  size="small"
                  color="error"
                  aria-label="Remove match criterion"
                  onClick={() => {
                    const next = referenceParts.filter((_, idx) => idx !== referenceIdx);
                    const hasAny = next.some((part) => part.trim().length > 0);
                    setMatchCriteriaEnabled((prev) => ({ ...prev, reference: hasAny }));
                    setDraft((d) => {
                      if (!d) return d;
                      const draftNext: RuleEditDraft = { ...d, reference_contains: joinRuleOrValues(next) };
                      if (!hasAny) draftNext.reference_text_match_operator = 'exact';
                      return draftNext;
                    });
                  }}
                >
                  <DeleteOutlineIcon fontSize="small" />
                </IconButton>
              </Stack>
                ))}
              </Stack>
              ) : null}
              {showAmountCurrency ? (
              <Stack spacing={1.25}>
                <TextField
                  label="Exact amount (optional)"
                  value={classificationExactAmountMirror(draft)}
                  onChange={(e) => {
                    const raw = e.target.value;
                    setDraft((d) => {
                      if (!d) return d;
                      if (!raw.trim()) {
                        const hadMirror =
                          d.amount_min.trim() &&
                          d.amount_max.trim() &&
                          d.amount_min.trim() === d.amount_max.trim();
                        setMatchCriteriaEnabled((prev) => ({
                          ...prev,
                          amountCurrency: Boolean(
                            (hadMirror ? false : d.amount_min.trim() || d.amount_max.trim()) || d.currency.trim()
                          )
                        }));
                        return hadMirror ? { ...d, amount_min: '', amount_max: '' } : d;
                      }
                      setMatchCriteriaEnabled((prev) => ({
                        ...prev,
                        amountCurrency: true
                      }));
                      return { ...d, amount_min: raw, amount_max: raw };
                    });
                  }}
                  size="small"
                  fullWidth
                  placeholder="e.g. 1425 or -589.84"
                  helperText="Sets Amount min and max to the same value. Edit min/max below for a range, or clear to remove amount bounds."
                />
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                <TextField
                  label="Amount min"
                  value={draft.amount_min}
                  onChange={(e) =>
                    setDraft((d) => {
                      if (!d) return d;
                      const amountMin = e.target.value;
                      setMatchCriteriaEnabled((prev) => ({
                        ...prev,
                        amountCurrency: Boolean(amountMin.trim() || d.amount_max.trim() || d.currency.trim())
                      }));
                      return { ...d, amount_min: amountMin };
                    })
                  }
                  size="small"
                  fullWidth
                  InputProps={buildClearableInputProps(
                    draft.amount_min,
                    () =>
                      setDraft((d) => {
                        if (!d) return d;
                        setMatchCriteriaEnabled((prev) => ({
                          ...prev,
                          amountCurrency: Boolean(d.amount_max.trim() || d.currency.trim())
                        }));
                        return { ...d, amount_min: '' };
                      }),
                    undefined,
                    'Clear amount minimum'
                  )}
                />
                <TextField
                  label="Amount max"
                  value={draft.amount_max}
                  onChange={(e) =>
                    setDraft((d) => {
                      if (!d) return d;
                      const amountMax = e.target.value;
                      setMatchCriteriaEnabled((prev) => ({
                        ...prev,
                        amountCurrency: Boolean(d.amount_min.trim() || amountMax.trim() || d.currency.trim())
                      }));
                      return { ...d, amount_max: amountMax };
                    })
                  }
                  size="small"
                  fullWidth
                  InputProps={buildClearableInputProps(
                    draft.amount_max,
                    () =>
                      setDraft((d) => {
                        if (!d) return d;
                        setMatchCriteriaEnabled((prev) => ({
                          ...prev,
                          amountCurrency: Boolean(d.amount_min.trim() || d.currency.trim())
                        }));
                        return { ...d, amount_max: '' };
                      }),
                    undefined,
                    'Clear amount maximum'
                  )}
                />
                <FormControl size="small" sx={{ width: { sm: 150 } }}>
                  <InputLabel id={`${idPrefix}-rule-edit-currency-label`}>Currency</InputLabel>
                  <Select
                    labelId={`${idPrefix}-rule-edit-currency-label`}
                  label="Currency"
                  value={draft.currency}
                  onChange={(e) =>
                      setDraft((d) => {
                        if (!d) return d;
                        const currency = String(e.target.value || '').toUpperCase();
                        setMatchCriteriaEnabled((prev) => ({
                          ...prev,
                          amountCurrency: Boolean(d.amount_min.trim() || d.amount_max.trim() || currency.trim())
                        }));
                        return { ...d, currency };
                      })
                    }
                  >
                    <MenuItem value="">
                      <em>Any</em>
                    </MenuItem>
                    <MenuItem value="EUR">EUR</MenuItem>
                    <MenuItem value="GBP">GBP</MenuItem>
                    <MenuItem value="USD">USD</MenuItem>
                  </Select>
                </FormControl>
              <IconButton
                size="small"
                color="error"
                aria-label="Remove match criterion"
                onClick={() => {
                  setMatchCriteriaEnabled((prev) => ({ ...prev, amountCurrency: false }));
                  setDraft((d) =>
                    d ? { ...d, amount_min: '', amount_max: '', currency: '' } : d
                  );
                }}
              >
                <DeleteOutlineIcon fontSize="small" />
              </IconButton>
              </Stack>
              </Stack>
              ) : null}
              {showFrequency ? (
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                <FormControl size="small" fullWidth>
                  <InputLabel id={`${idPrefix}-rule-edit-frequency-label`}>Frequency</InputLabel>
                  <Select
                    labelId={`${idPrefix}-rule-edit-frequency-label`}
                    label="Frequency"
                  value={draft.recurrence_type}
                  onChange={(e) =>
                      setDraft((d) => {
                        if (!d) return d;
                        const recurrence_type = String(e.target.value || '');
                        setMatchCriteriaEnabled((prev) => ({
                          ...prev,
                          frequency: Boolean(recurrence_type.trim())
                        }));
                        return { ...d, recurrence_type };
                      })
                    }
                  >
                    <MenuItem value="">
                      <em>Unset</em>
                    </MenuItem>
                    {PAYMENT_FREQUENCY_OPTIONS.map((opt) => (
                      <MenuItem key={`${idPrefix}-rule-frequency-${opt.value}`} value={opt.value}>
                        {opt.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Stack>
              ) : null}
              <Typography variant="subtitle2" color="text.secondary" sx={{ fontWeight: 700, pt: 0.5 }}>
                Output
              </Typography>
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems={{ sm: 'center' }}>
                {outputCategoryEnabled || outputSupplierEnabled ? (
                  <TextField
                    label="Priority"
                    type="number"
                    value={draft.priority}
                    onChange={(e) =>
                      setDraft((d) => (d ? { ...d, priority: e.target.value } : d))
                    }
                    size="small"
                    sx={{ width: { xs: '100%', sm: 140 } }}
                    helperText="Lower runs first"
                  />
                ) : null}
                <Button
                  size="small"
                  variant="outlined"
                  startIcon={<AddIcon />}
                  onClick={(e) => setOutputMenuAnchorEl(e.currentTarget)}
                  disabled={readOnly}
                  sx={{ whiteSpace: 'nowrap', alignSelf: { xs: 'flex-start', sm: 'center' } }}
                >
                  Add Output
                </Button>
              </Stack>
              <Menu
                anchorEl={outputMenuAnchorEl}
                open={Boolean(outputMenuAnchorEl)}
                onClose={() => setOutputMenuAnchorEl(null)}
              >
                <MenuItem
                  disabled={ruleEditTargetType !== 'supplier' || readOnly}
                  onClick={() => {
                    setOutputSupplierEnabled(true);
                    setOutputCategoryEnabled(false);
                    setOutputMenuAnchorEl(null);
                  }}
                >
                  Supplier
                </MenuItem>
                <MenuItem
                  disabled={ruleEditTargetType !== 'classification' || readOnly}
                  onClick={() => {
                    setOutputCategoryEnabled(true);
                    setOutputSupplierEnabled(false);
                    setOutputMenuAnchorEl(null);
                  }}
                >
                  Category
                </MenuItem>
                <MenuItem disabled>VAT Rate</MenuItem>
              </Menu>
              {outputCategoryEnabled ? (
                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                  <FormControl size="small" fullWidth>
                    <InputLabel id={`${idPrefix}-rule-edit-category-label`}>Category</InputLabel>
                    <Select
                      labelId={`${idPrefix}-rule-edit-category-label`}
                      label="Category"
                      value={Math.trunc(Number(draft.category_id)) || ''}
                      onChange={(e) =>
                        setDraft((d) =>
                          d ? { ...d, category_id: Number(e.target.value) } : d
                        )
                      }
                      MenuProps={{
                        disablePortal: true,
                        PaperProps: {
                          sx: { maxHeight: 360 }
                        }
                      }}
                    >
                      {categorySelectOptions.map((c) => (
                        <MenuItem key={c.id} value={c.id}>
                          {c.name}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                  <Button
                    variant="outlined"
                    size="small"
                    sx={{ mt: { sm: 0.5 }, flexShrink: 0 }}
                    disabled={saving}
                    onClick={() => onOpenCategoryCrudRuleEdit()}
                  >
                    New Category...
                  </Button>
                  <IconButton
                    size="small"
                    color="error"
                    aria-label="Remove output"
                    onClick={() => setOutputCategoryEnabled(false)}
                  >
                    <DeleteOutlineIcon fontSize="small" />
                  </IconButton>
                </Stack>
              ) : null}
              {outputSupplierEnabled ? (
                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems={{ xs: 'stretch', sm: 'center' }}>
                  <Autocomplete
                    size="small"
                    options={supplierOptions}
                    value={
                      dedupeSupplierNameList(draft.supplier_name_overrides || []).find(
                        (s) => String(s || '').trim().length > 0
                      ) || null
                    }
                    onChange={(_, value) => {
                      const v = String(value || '').trim();
                      setDraft((d) =>
                        d
                          ? {
                              ...d,
                              supplier_name_overrides: v ? dedupeSupplierNameList([v]) : []
                            }
                          : d
                      );
                    }}
                    fullWidth
                    autoHighlight
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Output: canonical supplier"
                        required
                        helperText="Used when you save this row as a Supplier rule."
                      />
                    )}
                  />
                  <Button
                    variant="outlined"
                    size="small"
                    sx={{ mt: { sm: 0.5 }, flexShrink: 0 }}
                    disabled={saving}
                    onClick={() => onNewSupplierFromDraft(draft)}
                  >
                    New supplier…
                  </Button>
                  <IconButton
                    size="small"
                    color="error"
                    aria-label="Remove supplier output"
                    onClick={() => {
                      setOutputSupplierEnabled(false);
                      setDraft((d) => (d ? { ...d, supplier_name_overrides: [] } : d));
                    }}
                  >
                    <DeleteOutlineIcon fontSize="small" />
                  </IconButton>
                </Stack>
              ) : null}
              <Typography variant="subtitle2" color="text.secondary" sx={{ fontWeight: 700, pt: 0.5 }}>
                How this Rule Works
              </Typography>
              <Alert severity="info" icon={false}>
                <Typography variant="body2">
                  {explanationSentence}
                </Typography>
              </Alert>
              <Typography variant="subtitle2" color="text.secondary" sx={{ fontWeight: 700, pt: 0.5 }}>
                Notes
              </Typography>
              <TextField
                label="Notes"
                value={draft.notes}
                onChange={(e) =>
                  setDraft((d) => (d ? { ...d, notes: e.target.value } : d))
                }
                fullWidth
                size="small"
                multiline
                minRows={4}
                InputProps={buildClearableInputProps(
                  draft.notes,
                  () => setDraft((d) => (d ? { ...d, notes: '' } : d)),
                  undefined,
                  'Clear notes'
                )}
              />
    </>
  );
}

function newClassificationRuleDraft(defaultCategoryId: number): RuleEditDraft {
  return {
    id: 0,
    isNew: true,
    name: '',
    priority: '100',
    category_id: defaultCategoryId,
    supplier_name_overrides: [],
    supplier_contains: '',
    description_contains: '',
    description_text_match_operator: 'exact',
    reference_contains: '',
    reference_text_match_operator: 'exact',
    text_match_operator: 'exact',
    account_scope: 'both',
    amount_min: '',
    amount_max: '',
    currency: '',
    recurrence_type: '',
    is_active: true,
    notes: ''
  };
}

/** Same persist shape as the classification rule dialog save path (new rule / non-accept). */
function classificationPersistPayloadFromRuleEditDraft(
  draft: RuleEditDraft
): { payload: ClassificationRulePersistPayload } | { error: string } {
  const name = draft.name.trim();
  if (!name) return { error: 'Rule name is required.' };
  if (!Number.isFinite(draft.category_id) || draft.category_id <= 0) {
    return { error: 'Select a valid category.' };
  }
  const priorityParsed = parseInt(String(draft.priority ?? '100'), 10);
  if (!Number.isFinite(priorityParsed)) {
    return { error: 'Priority must be a whole number.' };
  }
  const parseAmount = (s: string): number | null | undefined => {
    const t = s.trim();
    if (!t) return null;
    const n = Number(t);
    if (!Number.isFinite(n)) return undefined;
    return n;
  };
  const canonicalList = dedupeSupplierNameList(draft.supplier_name_overrides);
  const amountMin = parseAmount(draft.amount_min);
  if (amountMin === undefined) return { error: 'Amount min must be a valid number or empty.' };
  const amountMax = parseAmount(draft.amount_max);
  if (amountMax === undefined) return { error: 'Amount max must be a valid number or empty.' };

  const normalizedSupplierContains = normalizeRuleOrValues(draft.supplier_contains);
  const normalizedDescriptionContains = normalizeRuleOrValues(draft.description_contains);
  const normalizedReferenceContains = normalizeRuleOrValues(draft.reference_contains);
  const hasTextMatch =
    normalizedSupplierContains !== '' ||
    normalizedDescriptionContains !== '' ||
    normalizedReferenceContains !== '';
  const hasAmountRange = amountMin != null || amountMax != null;
  const hasCanonicalSupplierOnlyMatch =
    canonicalList.length > 0 &&
    normalizedSupplierContains === '' &&
    normalizedDescriptionContains === '' &&
    normalizedReferenceContains === '';
  if (!hasTextMatch && !hasAmountRange && !hasCanonicalSupplierOnlyMatch) {
    return {
      error:
        'Add a supplier/description/reference match, set amount min/max, or choose a canonical supplier alone (no separate text matchers) so the rule does not match every transaction.'
    };
  }

  const payload: ClassificationRulePersistPayload = {
    name,
    priority: priorityParsed,
    category_id: draft.category_id,
    is_active: draft.is_active,
    supplier_name_overrides: canonicalList.length ? canonicalList : null,
    supplier_name_override: canonicalList.length ? canonicalList[0] : null,
    supplier_contains: normalizedSupplierContains || null,
    description_contains: normalizedDescriptionContains || null,
    description_text_match_operator:
      (draft.description_text_match_operator || draft.text_match_operator || 'exact').trim() || 'exact',
    reference_contains: normalizedReferenceContains || null,
    reference_text_match_operator:
      (draft.reference_text_match_operator || draft.text_match_operator || 'exact').trim() || 'exact',
    text_match_operator: (draft.text_match_operator || 'exact').trim() || 'exact',
    amount_min: amountMin,
    amount_max: amountMax,
    account_scope: normalizeRuleAccountScope(draft.account_scope),
    currency: draft.currency.trim() || null,
    recurrence_type: draft.recurrence_type.trim() || null,
    notes: draft.notes.trim() || null
  };
  const err = validateClassificationPersistPayload(payload);
  if (err) return { error: err };
  return { payload };
}

function getClassificationRuleExplanationSentence(
  ruleEditDraft: RuleEditDraft,
  ruleEditTargetType: EditableRuleType,
  categoriesSortedByName: Array<{ id: number; name: string }>,
  categorySelectOptions: Array<{ id: number; name: string }>
): string {
  const conditions: string[] = [];
  const canonicalSuppliers = dedupeSupplierNameList(ruleEditDraft.supplier_name_overrides || []);
  if (canonicalSuppliers.length > 0) {
    if (canonicalSuppliers.length === 1) {
      conditions.push(`active supplier tag (Step 2) is "${canonicalSuppliers[0]}"`);
    } else {
      conditions.push(
        `active supplier tag (Step 2) is one of [${canonicalSuppliers.map((s) => `"${s}"`).join(', ')}]`
      );
    }
  }
  if (ruleEditDraft.supplier_contains.trim()) {
    conditions.push(
      `supplier ${classificationRuleTextOperatorLabel(ruleEditDraft.text_match_operator).toLowerCase()} "${ruleEditDraft.supplier_contains.trim()}"`
    );
  }
  if (ruleEditDraft.description_contains.trim()) {
    conditions.push(
      `description ${classificationRuleTextOperatorLabel(
        ruleEditDraft.description_text_match_operator
      ).toLowerCase()} "${ruleEditDraft.description_contains.trim()}"`
    );
  }
  if (ruleEditDraft.reference_contains.trim()) {
    conditions.push(
      `reference ${classificationRuleTextOperatorLabel(
        ruleEditDraft.reference_text_match_operator
      ).toLowerCase()} "${ruleEditDraft.reference_contains.trim()}"`
    );
  }
  const aminTrim = ruleEditDraft.amount_min.trim();
  const amaxTrim = ruleEditDraft.amount_max.trim();
  if (aminTrim && amaxTrim && aminTrim === amaxTrim) {
    conditions.push(`amount is exactly ${aminTrim}`);
  } else {
    if (aminTrim) conditions.push(`amount >= ${aminTrim}`);
    if (amaxTrim) conditions.push(`amount <= ${amaxTrim}`);
  }
  if (ruleEditDraft.currency.trim()) conditions.push(`currency is ${ruleEditDraft.currency.trim().toUpperCase()}`);
  if (ruleEditDraft.recurrence_type.trim()) conditions.push(`frequency is ${ruleEditDraft.recurrence_type.trim()}`);
  const scopeLabel = ruleAccountScopeLabel(ruleEditDraft.account_scope);
  if (scopeLabel.toLowerCase() !== 'both') conditions.push(`bank account is ${scopeLabel}`);

  const priorityText = ruleEditDraft.priority.trim() || '100';

  if (ruleEditTargetType === 'supplier') {
    const nm = ruleEditDraft.name.trim() || 'this rule';
    const canonOut =
      dedupeSupplierNameList(ruleEditDraft.supplier_name_overrides || []).find((s) => String(s || '').trim())?.trim() ||
      ruleEditDraft.supplier_contains.trim() ||
      nm;
    if (conditions.length === 0) {
      return `When saved as a Supplier rule, canonical supplier "${canonOut}" will be applied with priority ${priorityText} (add match criteria so the rule does not match every transaction).`;
    }
    return `If a Tx has ${conditions.join(' AND ')}, then saving as a Supplier rule will set canonical supplier "${canonOut}" with priority ${priorityText}.`;
  }

  const categoryIdNum = Math.trunc(Number(ruleEditDraft.category_id));
  const categoryName =
    categoriesSortedByName.find((c) => c.id === categoryIdNum)?.name ||
    categorySelectOptions.find((c) => c.id === categoryIdNum)?.name ||
    `Category #${categoryIdNum}`;

  let body: string;
  if (conditions.length === 0) {
    body = `If a Tx has no specific matching constraints set, then category "${categoryName}" will be applied with priority ${priorityText}.`;
  } else {
    body = `If a Tx has ${conditions.join(' AND ')}, then category "${categoryName}" will be applied with priority ${priorityText}.`;
  }
  return body;
}

function classificationProposalToEditDraft(
  row: RuleProposalRow,
  categories: CategoryItem[],
  defaultCategoryId: number
): RuleEditDraft {
  const payload = row.payload || {};
  const payloadCategoryIdRaw = payload.category_id;
  const payloadCategoryId =
    typeof payloadCategoryIdRaw === 'number'
      ? payloadCategoryIdRaw
      : Number.isFinite(Number(payloadCategoryIdRaw))
        ? Number(payloadCategoryIdRaw)
        : 0;
  const payloadCategoryName = String(
    row.category_name || (payload.category_name as string) || (payload.category as string) || ''
  ).trim();
  const categoryFromName =
    categories.find((c) => c.name.trim().toLowerCase() === payloadCategoryName.toLowerCase())?.id || 0;
  const category_id = payloadCategoryId > 0 ? payloadCategoryId : categoryFromName > 0 ? categoryFromName : defaultCategoryId;
  const payloadOverridesRaw = payload.supplier_name_overrides;
  let supplier_name_overrides: string[] = [];
  if (Array.isArray(payloadOverridesRaw) && payloadOverridesRaw.length > 0) {
    supplier_name_overrides = dedupeSupplierNameList(payloadOverridesRaw.map((x) => String(x)));
  } else {
    const supplierNameOverride = String(
      row.supplier_name_override ?? (payload.supplier_name_override as string) ?? ''
    ).trim();
    supplier_name_overrides = supplierNameOverride ? [supplierNameOverride] : [];
  }
  const supplier_contains = String(row.supplier_contains ?? payload.supplier_contains ?? '');
  const description_contains = String(row.description_contains ?? payload.description_contains ?? '');
  const reference_contains = String(row.reference_contains ?? payload.reference_contains ?? '');
  const payloadSupplierOp = String(payload.text_match_operator || 'exact');
  const supplierOp = supplier_contains.trim() ? payloadSupplierOp : 'exact';
  return {
    id: 0,
    isNew: false,
    proposal_id: row.id ?? null,
    proposal_atlas_key: row.virtual && row.atlas_key ? row.atlas_key : null,
    name: String(payload.name || row.proposed_name || '').trim(),
    priority: String(row.priority ?? payload.priority ?? 100),
    category_id,
    supplier_name_overrides,
    supplier_contains,
    description_contains,
    description_text_match_operator: description_contains.trim()
      ? String(payload.description_text_match_operator || payload.text_match_operator || 'exact')
      : 'exact',
    reference_contains,
    reference_text_match_operator: reference_contains.trim()
      ? String(payload.reference_text_match_operator || payload.text_match_operator || 'exact')
      : 'exact',
    text_match_operator: supplierOp,
    account_scope: normalizeRuleAccountScope(String(row.account_scope ?? payload.account_scope ?? 'both')),
    amount_min: payload.amount_min != null ? String(payload.amount_min) : '',
    amount_max: payload.amount_max != null ? String(payload.amount_max) : '',
    currency: String(payload.currency || ''),
    recurrence_type: String(payload.recurrence_type || ''),
    is_active: payload.is_active !== false,
    notes: String(row.notes ?? payload.notes ?? '')
  };
}

/** Sample rows returned when `include_activity` is sent with reapply / supplier backfill. */
type RuleApplyActivityItem = {
  kind?: string;
  transaction_id?: number;
  category_name?: string;
  rule_name?: string | null;
  supplier_name?: string;
};

type AsyncRuleSyncJobStatus =
  | 'queued'
  | 'running'
  | 'cancelling'
  | 'completed'
  | 'cancelled'
  | 'failed';

function formatRuleReapplyProgressLine(
  progress: { percent: number; createdTotal: number },
  data: {
    offset?: number;
    transactions_reprocessed?: number;
    rule_tags_deleted?: number;
    created_tags?: number;
    activity?: RuleApplyActivityItem[];
  }
): string {
  const cre = data.created_tags ?? 0;
  // Distinct from post-accept "Backfill accepted rule" (apply-single for one rule).
  let line = `Re-run ALL Classification Rules: ${progress.percent}% · ${progress.createdTotal.toLocaleString()} tags added`;
  const act = data.activity || [];
  if (act.length > 0) {
    const last = act[act.length - 1];
    const tid = last.transaction_id != null ? String(last.transaction_id) : '?';
    const cat = (last.category_name || '').trim() || 'category';
    if ((last.kind || '') === 'unclassified') {
      line += ` · latest: Tx ${tid} → ${cat}`;
    } else {
      const rulePart = (last.rule_name || '').trim();
      line += ` · latest: Tx ${tid} → ${cat}`;
      if (rulePart) line += ` (${rulePart})`;
    }
  } else if (cre === 0) {
    line += ' · no new tags on this page';
  }
  return line;
}

function formatSupplierBackfillProgressLine(
  progress: { percent: number; updatedTotal: number },
  data: {
    tags_scanned?: number;
    tags_updated?: number;
    tags_unchanged?: number;
    activity?: RuleApplyActivityItem[];
  }
): string {
  let line = `Supplier rules: ${progress.percent}% · ${progress.updatedTotal.toLocaleString()} supplier tags updated`;
  const act = data.activity || [];
  if (act.length > 0) {
    const last = act[act.length - 1];
    const tid = last.transaction_id != null ? String(last.transaction_id) : '?';
    const sup = (last.supplier_name || '').trim() || '?';
    line += ` · latest: Tx ${tid} → “${sup}”`;
  }
  return line;
}

interface RulesPayload {
  rules: ClassificationRule[];
}

interface CategoryItem {
  id: number;
  name: string;
  is_key_category?: boolean;
  /** false = deactivated; omit or true = show in pickers */
  is_active?: boolean;
}

/** In vs Out heading from transaction sign (matches cash-flow rules: In if amount > 0). */
function inferHeadingCategoryIdFromAmount(
  headingCats: CategoryItem[],
  amount: number | null | undefined
): number | '' {
  const inc = headingCats.find((c) => {
    const n = c.name.trim().toLowerCase();
    return n === 'in' || n === 'income';
  });
  const exp = headingCats.find((c) => {
    const n = c.name.trim().toLowerCase();
    return n === 'out' || n === 'expense';
  });
  if (amount != null && amount > 0 && inc) return inc.id;
  if (amount != null && amount <= 0 && exp) return exp.id;
  if (inc && !exp) return inc.id;
  if (exp && !inc) return exp.id;
  return '';
}

function headingCategoryIdFromCashFlowCell(
  headingCats: CategoryItem[],
  cashFlow: string | null | undefined
): number | '' {
  const cf = String(cashFlow ?? '').trim().toLowerCase();
  if (cf === 'in' || cf === 'income') {
    return headingCats.find((c) => ['in', 'income'].includes(c.name.trim().toLowerCase()))?.id ?? '';
  }
  if (cf === 'out' || cf === 'expense') {
    return headingCats.find((c) => ['out', 'expense'].includes(c.name.trim().toLowerCase()))?.id ?? '';
  }
  return '';
}

/**
 * Value for the cash-flow heading `Select` so it does not go blank when a granular
 * category is chosen (we still resolve `new_category_id` from granular first).
 *
 * - `unified`: when no heading draft and no granular, default from amount (existing table behavior).
 * - `resolution`: when no heading draft and no granular, show empty until the user picks a heading.
 */
function headingSelectValueForTx(
  txId: number,
  selectedHeadingByTx: Record<number, number | ''>,
  selectedGranularByTx: Record<number, number[]>,
  headingCats: CategoryItem[],
  amount: number | null | undefined,
  mode: 'unified' | 'resolution'
): number | '' {
  const granularVal = primaryGranularCategoryId(selectedGranularByTx, txId);
  const headingStored = txId in selectedHeadingByTx ? selectedHeadingByTx[txId] : undefined;
  const inferAmt = () => inferHeadingCategoryIdFromAmount(headingCats, amount);

  if (headingStored !== undefined && headingStored !== '') return headingStored;
  if (headingStored === '' && selectHasChoice(granularVal)) return inferAmt();

  if (headingStored === undefined) {
    if (selectHasChoice(granularVal)) return inferAmt();
    return mode === 'resolution' ? '' : inferAmt();
  }

  return '';
}

/** Heuristic default payment frequency from granular P&L category name. */
function inferPaymentFrequencyFromCategoryName(name: string | null | undefined): string | null {
  const n = (name || '').toLowerCase();
  if (!n) return null;
  if (/rental|airbnb|short-term|long-term|letting|accommodation|guest/.test(n)) return 'sporadic';
  if (/tax|revenue|paye|preliminary|usc/.test(n)) return 'quarterly';
  if (
    /subscription|utilities|insurance|electric|gas|phone|broadband|internet|water|waste|council|tv|software|cursor|microsoft|spotify/.test(
      n
    )
  ) {
    return 'monthly';
  }
  if (/salary|wage|payroll|pension/.test(n)) return 'monthly';
  if (/mortgage|loan/.test(n)) return 'monthly';
  return 'once_off';
}

function effectiveTxAmount(tx: Pick<ReviewTx, 'amount' | 'amount_effective' | 'amount_fallback_value'>): number | null {
  if (tx.amount_effective != null) return tx.amount_effective;
  if (tx.amount != null) return tx.amount;
  if (tx.amount_fallback_value != null) return tx.amount_fallback_value;
  return null;
}

function coerceFiniteAmount(raw: unknown): number | null {
  if (raw == null || raw === '') return null;
  const n = Number(raw);
  return Number.isFinite(n) ? n : null;
}

function firstLegBillFields(tx: ReviewTx | Record<string, unknown> | null | undefined): {
  bill_amount: unknown;
  bill_currency: unknown;
} {
  if (!tx || typeof tx !== 'object') return { bill_amount: undefined, bill_currency: undefined };
  const asRec = tx as Record<string, unknown>;
  const pickLeg = (legsRaw: unknown): Record<string, unknown> | null => {
    let legs = legsRaw;
    if (typeof legs === 'string' && legs.trim()) {
      try {
        legs = JSON.parse(legs);
      } catch {
        return null;
      }
    }
    if (!Array.isArray(legs) || !legs.length) return null;
    const first = legs[0];
    return first && typeof first === 'object' && !Array.isArray(first)
      ? (first as Record<string, unknown>)
      : null;
  };
  const leg = pickLeg(asRec.legs_data) || pickLeg(asRec.legs);
  if (!leg) return { bill_amount: undefined, bill_currency: undefined };
  return { bill_amount: leg.bill_amount, bill_currency: leg.bill_currency };
}

/** Original merchant charge (bill_*) for FX card payments, when distinct from ledger amount/currency. */
function resolveUnifiedRowOriginalBill(
  row: Pick<UnifiedReviewRow, 'bill_amount' | 'bill_currency' | 'transaction' | 'amount' | 'currency'>
): { amount: number; currency: string } | null {
  const tx = (row.transaction || null) as ReviewTx | Record<string, unknown> | null | undefined;
  const legBill = firstLegBillFields(tx);
  const billAmount = coerceFiniteAmount(
    row.bill_amount ??
      (tx as ReviewTx | null | undefined)?.bill_amount ??
      (tx as any)?.bill_amount ??
      legBill.bill_amount
  );
  const billCurrency = String(
    row.bill_currency ??
      (tx as ReviewTx | null | undefined)?.bill_currency ??
      (tx as any)?.bill_currency ??
      legBill.bill_currency ??
      ''
  )
    .trim()
    .toUpperCase();
  if (billAmount == null || !billCurrency) return null;
  const ledgerCurrency = String(row.currency || (tx as any)?.currency || '')
    .trim()
    .toUpperCase();
  const ledgerAmount = coerceFiniteAmount(row.amount ?? (tx as any)?.amount);
  if (ledgerCurrency && billCurrency !== ledgerCurrency) {
    return { amount: billAmount, currency: billCurrency };
  }
  // Same currency: only stack when the billed amount clearly differs from ledger.
  if (ledgerAmount != null && Math.abs(billAmount - ledgerAmount) >= 0.01) {
    return { amount: billAmount, currency: billCurrency };
  }
  return null;
}

function normalizedText(value: string | null | undefined): string {
  return (value || '').toLowerCase().replace(/[^a-z0-9]+/g, ' ').trim();
}

function tokenizeText(value: string | null | undefined): string[] {
  const stop = new Set(['the', 'and', 'for', 'with', 'from', 'payment', 'card', 'to', 'at', 'of', 'a', 'an']);
  return normalizedText(value)
    .split(' ')
    .map((t) => t.trim())
    .filter((t) => t.length >= 3 && !stop.has(t));
}

function collapseSimilarityPhraseKey(value: string | null | undefined): string {
  return (value || '').toLowerCase().replace(/[^a-z0-9]+/g, '');
}

/** Keep in sync with backend _is_strong_similarity_candidate (no weak account+amount-only). */
function isStrongSimilarityCandidateFromReasons(similarityScore: number, reasons: string[]): boolean {
  if (Number(similarityScore) < 3) return false;
  return reasons.some((r) => {
    const s = (r || '').toLowerCase().trim();
    return s === 'same supplier' || s.startsWith('shared description terms');
  });
}

function scoreSimilarUnifiedRows(base: UnifiedReviewRow, candidate: UnifiedReviewRow): SimilarUnifiedRow | null {
  if (!base || !candidate) return null;
  if (base.id === candidate.id) return null;

  let score = 0;
  const reasons: string[] = [];

  const supplierBase = normalizedText(base.supplier);
  const supplierCandidate = normalizedText(candidate.supplier);
  if (supplierBase && supplierBase === supplierCandidate) {
    score += 5;
    reasons.push('same supplier');
  }

  const baseDesc =
    (base.description || '').trim() ||
    (base.reference != null && String(base.reference).trim() ? String(base.reference).trim() : '');
  const candDesc =
    (candidate.description || '').trim() ||
    (candidate.reference != null && String(candidate.reference).trim() ? String(candidate.reference).trim() : '');
  const bkey = collapseSimilarityPhraseKey(baseDesc);
  const ckey = collapseSimilarityPhraseKey(candDesc);
  if (bkey && ckey && bkey === ckey && bkey.length >= 5) {
    score += 8;
    reasons.push('shared description terms (full phrasing match)');
  } else {
    const baseTokens = new Set(tokenizeText(baseDesc));
    const candTokens = new Set(tokenizeText(candDesc));
    const sharedTokens = Array.from(baseTokens).filter((t) => candTokens.has(t));
    if (sharedTokens.length >= 2) {
      const tokenScore = Math.min(2 * sharedTokens.length, 12);
      score += tokenScore;
      reasons.push(`shared description terms (${sharedTokens.slice(0, 4).join(', ')})`);
    } else if (sharedTokens.length === 1 && sharedTokens[0].length >= 5) {
      score += 5;
      reasons.push(`shared description terms (${sharedTokens[0]})`);
    } else if (bkey.length >= 6 && ckey.length >= 6 && (bkey.includes(ckey) || ckey.includes(bkey))) {
      score += 6;
      reasons.push('shared description terms (substring phrasing)');
    }
  }

  if ((base.account_source || '') === (candidate.account_source || '') && base.account_source) {
    score += 1;
    reasons.push('same account');
  }

  if ((base.cashFlow || '') === (candidate.cashFlow || '') && base.cashFlow) {
    score += 1;
    reasons.push('same heading');
  }

  if (base.amount != null && candidate.amount != null) {
    const a = Math.abs(base.amount);
    const b = Math.abs(candidate.amount);
    const sameSign = Math.sign(base.amount) === Math.sign(candidate.amount);
    const diffRatio = Math.abs(a - b) / Math.max(a, b, 1);
    if (sameSign && diffRatio <= 0.1) {
      score += 2;
      reasons.push('very similar amount');
    } else if (sameSign && diffRatio <= 0.25) {
      score += 1;
      reasons.push('similar amount');
    }
  }

  if (score < 3) return null;
  if (!isStrongSimilarityCandidateFromReasons(score, reasons)) return null;

  return {
    ...candidate,
    similarityScore: score,
    similarityReasons: reasons,
  };
}

/**
 * Strip noisy trailing tokens (Twilio/Stripe-style hashes, Spotify ids, ".com", corporate tails)
 * before suggesting a canonical supplier label for merge.
 */
function stripSupplierMergeNoise(raw: string): string {
  let s = (raw || '').trim();
  if (!s) return '';
  s = s.replace(/\s*\([^)]{0,120}\)\s*$/g, '').trim();
  s = s.replace(/\s+[A-Za-z0-9]{14,}\s*$/g, '').trim();
  s = s.replace(/\s+P[0-9a-f]{6,}\s*$/gi, '').trim();
  s = s.replace(/\s+[0-9]{2,}[A-Za-z][A-Za-z0-9-]*\s*$/g, '').trim();
  s = s.replace(/\.com$/i, '').trim();
  s = s.replace(/\s*,?\s*(inc\.?|ltd\.?|limited)\b.*$/i, '').trim();
  return s.trim();
}

/** Pick a short sensible default for the canonical name from the selected supplier labels. */
function suggestCanonicalSupplierName(names: string[]): string {
  const stripped = names.map((n) => stripSupplierMergeNoise(n)).filter((n) => n.length > 0);
  if (!stripped.length) return (names[0] || '').trim();
  const uniq = Array.from(new Set(stripped));
  uniq.sort((a, b) => a.length - b.length || a.localeCompare(b, undefined, { sensitivity: 'base' }));
  return uniq[0] || (names[0] || '').trim();
}

const TransactionClassificationReview: React.FC = () => {
  const { user } = useAuth();
  const bgJobs = useBackgroundJobs();
  const canMergeSuppliersCatalog = user?.role === 'admin' || user?.role === 'owner';
  const theme = useTheme();
  const canonicalHybridEnabled = String(process.env.REACT_APP_TX_REVIEW_CANONICAL_HYBRID ?? 'true').toLowerCase() !== 'false';
  const [loading, setLoading] = useState(true);
  const [error, setErrorText] = useState('');
  const [errorToastCode, setErrorToastCode] = useState('');
  const setError = useCallback((message: string) => {
    setErrorText(message);
    setErrorToastCode(String(message || '').trim() ? nextClientToastCode() : '');
  }, []);
  const [payload, setPayload] = useState<ReviewPayload | null>(null);
  const [taggedPayload, setTaggedPayload] = useState<TaggedPayload | null>(null);
  const [confirmedPayload, setConfirmedPayload] = useState<ConfirmedPayload | null>(null);
  const [canonicalRowsPayload, setCanonicalRowsPayload] = useState<CanonicalReviewRowsPayload | null>(null);
  const [canonicalRowsCommittedMode, setCanonicalRowsCommittedMode] = useState<'all' | 'only' | 'exclude'>('exclude');
  const [pipelineCountsAllScope, setPipelineCountsAllScope] = useState<TxReviewPipelineCountsPayload | null>(null);
  const [pipelineCountsLoading, setPipelineCountsLoading] = useState(false);
  const [pipelineCountsError, setPipelineCountsError] = useState('');
  const [ruleRunHistory, setRuleRunHistory] = useState<RuleRunHistoryPayload | null>(null);
  const [ruleRunHistoryError, setRuleRunHistoryError] = useState('');
  const [confirmedPayloadHydrating, setConfirmedPayloadHydrating] = useState(false);
  const [allTransactionsPayload, setAllTransactionsPayload] = useState<ReviewTx[]>([]);
  const [allTransactionsHydrating, setAllTransactionsHydrating] = useState(false);
  const [pendingManualRuleSyncCount, setPendingManualRuleSyncCount] = useState(0);
  const [rulesPayload, setRulesPayload] = useState<RulesPayload | null>(null);
  const [proposalsPayload, setProposalsPayload] = useState<RuleProposalsPayload | null>(null);
  /** Rejected Step 3 classification/atlas drafts (tombstones until restored/deleted). */
  const [rejectedProposalsPayload, setRejectedProposalsPayload] = useState<RuleProposalsPayload | null>(null);
  const [supplierProposalsPayload, setSupplierProposalsPayload] = useState<SupplierRuleProposalsPayload | null>(null);
  /** Rejected Step 2B supplier drafts (tombstones that block regenerate until restored/deleted). */
  const [rejectedSupplierProposalsPayload, setRejectedSupplierProposalsPayload] =
    useState<SupplierRuleProposalsPayload | null>(null);
  /** Rejected Step 4 VAT suggestions (list + count for title/accordion). */
  const [rejectedVatRuleSuggestions, setRejectedVatRuleSuggestions] = useState<VatRuleSuggestionRow[]>([]);
  const [selectedRejectedClassificationProposalIds, setSelectedRejectedClassificationProposalIds] = useState<
    number[]
  >([]);
  const [selectedRejectedVatSuggestionIds, setSelectedRejectedVatSuggestionIds] = useState<number[]>([]);
  /** Nested Confirmed / Proposed / Rejected sections inside each pipeline stage (default closed). */
  const [pipelineNestedSectionOpen, setPipelineNestedSectionOpen] = useState(PIPELINE_NESTED_SECTION_DEFAULTS);
  const setPipelineNestedSection = useCallback(
    (stage: PipelineNestedStage, section: PipelineNestedSection, open: boolean) => {
      setPipelineNestedSectionOpen((prev) => ({
        ...prev,
        [stage]: { ...prev[stage], [section]: open },
      }));
    },
    []
  );
  const [categories, setCategories] = useState<CategoryItem[]>([]);
  /** Income / Expense heading category id per transaction (Needs Review). */
  const [selectedHeadingByTx, setSelectedHeadingByTx] = useState<Record<number, number | ''>>({});
  /** Detailed P&L category id per transaction (Needs Review). */
  const [selectedGranularByTx, setSelectedGranularByTx] = useState<Record<number, number[]>>({});
  const [savingTxId, setSavingTxId] = useState<number | null>(null);
  const [busyAction] = useState<string>('');
  const [successMessage, setSuccessMessageText] = useState('');
  const [successToastCode, setSuccessToastCode] = useState('');
  const [ruleSyncCompletionToast, setRuleSyncCompletionToast] = useState<RuleSyncCompletionToastPayload | null>(null);
  const setSuccessMessage = useCallback((message: string) => {
    setRuleSyncCompletionToast(null);
    setSuccessMessageText(message);
    setSuccessToastCode(String(message || '').trim() ? nextClientToastCode() : '');
  }, []);
  const [stackedToasts, setStackedToasts] = useState<
    Array<{
      id: number;
      code: string;
      requestId?: string;
      jobId?: string;
      errorCode?: string;
      severity: 'success' | 'error' | 'info' | 'warning';
      message: string;
      /** Structured re-run summary (same rows as the commentary drawer notification block). */
      rerunSummaryLines?: RerunSummaryLine[];
      actionLabel?: string;
      onAction?: () => void;
    }>
  >([]);
  const stackedToastIdRef = useRef(1);
  /** When set, classification re-run uses stacked toast only; ``requestStopOperation`` must not open the banner. */
  const reapplyUnconfirmedToastIdRef = useRef<number | null>(null);
  const supplierProposalsHydrateGenRef = useRef(0);
  /** Bumped on each canonical full refresh so stale hydrateCanonicalRowsInBackground runs exit early. */
  const canonicalHydrateGenRef = useRef(0);
  /** Bumped at the start of each fetchData so stale completions cannot wipe a newer load. */
  const fetchDataGenRef = useRef(0);
  const [refreshInfoMessage, setRefreshInfoMessage] = useState('');
  const [refreshInfoToastCode, setRefreshInfoToastCode] = useState('');
  const [selectedTxIds, setSelectedTxIds] = useState<number[]>([]);
  const [selectedVatRateByTx, setSelectedVatRateByTx] = useState<Record<number, string>>({});
  const [selectedPropertyIdsByTx, setSelectedPropertyIdsByTx] = useState<Record<number, number[]>>({});
  const [deletingVatTagUid, setDeletingVatTagUid] = useState<string | null>(null);
  const [pendingUnifiedTagDelete, setPendingUnifiedTagDelete] = useState<PendingUnifiedTagDelete | null>(null);
  const [deletingUnifiedTagUid, setDeletingUnifiedTagUid] = useState<string | null>(null);
  const [applyingRules, setApplyingRules] = useState(false);
  const [supplierBackfillBusy, setSupplierBackfillBusy] = useState(false);
  const [reapplyUnconfirmedBusy, setReapplyUnconfirmedBusy] = useState(false);
  const [reapplyVatUnconfirmedBusy, setReapplyVatUnconfirmedBusy] = useState(false);
  const [reapplySpecialCaseUnconfirmedBusy, setReapplySpecialCaseUnconfirmedBusy] = useState(false);
  const [rerunAllRulesBusy, setRerunAllRulesBusy] = useState(false);
  /** After tag wipe succeeds, automatically start Full rule sync (Delete and re-tag). */
  const pendingDeleteAndRetagRef = useRef(false);
  /** True from Delete and re-tag click until wipe+Full chain finishes or fails. */
  const [deleteAndRetagInFlight, setDeleteAndRetagInFlight] = useState(false);
  const [dedupeDisplayDupTagsDialogOpen, setDedupeDisplayDupTagsDialogOpen] = useState(false);
  type DeleteAndRetagPreviewRow = {
    id: number;
    description?: string | null;
    amount?: number | null;
    currency?: string | null;
    account_source?: string | null;
    revolut_state?: string | null;
    date?: string | null;
    manual_dimensions?: string[];
  };
  const [deleteAndRetagDialogOpen, setDeleteAndRetagDialogOpen] = useState(false);
  const [deleteAndRetagPreviewBusy, setDeleteAndRetagPreviewBusy] = useState(false);
  const [deleteAndRetagPreviewError, setDeleteAndRetagPreviewError] = useState('');
  const [deleteAndRetagPreview, setDeleteAndRetagPreview] = useState<{
    manual_tx_count: number;
    transactions: DeleteAndRetagPreviewRow[];
  } | null>(null);
  const [dedupeDisplayDupTagsBusy, setDedupeDisplayDupTagsBusy] = useState(false);
  const [removeAllTagsBusy, setRemoveAllTagsBusy] = useState(false);
  /** Live status while supplier refresh / classification re-run batches are in flight. */
  const [operationProgressMessage, setOperationProgressMessage] = useState<string | null>(null);
  /** User minimized the inline progress banner; job may still run — chip restores full banner. */
  const [operationProgressBannerDismissed, setOperationProgressBannerDismissed] = useState(false);
  const [operationCancelRequested, setOperationCancelRequested] = useState(false);
  const operationCancelRequestedRef = useRef(false);
  /** Set true when user taps Cancel on the TX review bulk-action toast (commit / apply tags / reprocess). */
  const txReviewBulkCancelRequestedRef = useRef(false);
  /** Populated after `refreshUnifiedRowAfterRerun` is defined (bulk reprocess uses it for legacy unified refresh). */
  const refreshUnifiedRowAfterRerunRef = useRef<(row: UnifiedReviewRow) => Promise<boolean>>(async () => false);
  /** Server rule-sync / tag-wipe jobs are tracked app-wide so navigation does not stop them. */
  const activeRuleSyncJobId = bgJobs.activeJobId;
  const activeRuleSyncCancelRequested = bgJobs.cancelRequested;
  const [savingFrequencyTxId, setSavingFrequencyTxId] = useState<number | null>(null);
  const [searchParams, setSearchParams] = useSearchParams();
  const location = useLocation();
  const navigate = useNavigate();
  const txReviewFiltersHydratedFromUrlRef = useRef(false);
  const rulesFiltersHydratedFromUrlRef = useRef(false);
  /**
   * When true, skip Rules filter → URL write-back. Programmatic navigators (e.g. toast View Now)
   * set this before updating the URL so a stale empty local filter cannot wipe the new params
   * before the URL→local hydrate effect runs (first click otherwise looks like a no-op).
   */
  const rulesFiltersUrlWriteSuspendedRef = useRef(false);
  const suppliersFiltersHydratedFromUrlRef = useRef(false);
  const categoriesFiltersHydratedFromUrlRef = useRef(false);
  const [reviewSort, setReviewSort] = useState<
    Partial<Record<ReviewTableType, { key: string; dir: SortDir }>>
  >({});
  const [rulesSort, setRulesSort] = useState<{ key: string; dir: SortDir } | null>(null);
  const [supplierVatRows, setSupplierVatRows] = useState<SupplierVatInvoiceRowUi[]>([]);
  const [activeSuppliersPickerList, setActiveSuppliersPickerList] = useState<Array<{ id: number; name: string }>>([]);
  const [vatAddSupplierId, setVatAddSupplierId] = useState<number | null>(null);
  const [supplierVatAddOpen, setSupplierVatAddOpen] = useState(false);
  const [supplierVatAddError, setSupplierVatAddError] = useState('');
  const [addingSupplierVatRow, setAddingSupplierVatRow] = useState(false);
  const [removingSupplierVatId, setRemovingSupplierVatId] = useState<number | null>(null);
  const [clearingAllSupplierVat, setClearingAllSupplierVat] = useState(false);
  const [selectedSupplierVatIds, setSelectedSupplierVatIds] = useState<number[]>([]);
  const [bulkDeletingSupplierVat, setBulkDeletingSupplierVat] = useState(false);
  const [supplierRuleRows, setSupplierRuleRows] = useState<SupplierRuleRow[]>([]);
  const [ruleFireCounts, setRuleFireCounts] = useState<{
    classification: Record<number, number>;
    supplier: Record<number, number>;
    vat: Record<number, number>;
    specialCase: Record<number, number>;
  }>({
    classification: {},
    supplier: {},
    vat: {},
    specialCase: {}
  });
  const [vatRuleRows, setVatRuleRows] = useState<CategoryVatRuleRow[]>([]);
  const [vatRuleSuggestions, setVatRuleSuggestions] = useState<VatRuleSuggestionRow[]>([]);
  const [vatSuggestionActionBusyId, setVatSuggestionActionBusyId] = useState<number | null>(null);
  const [selectedVatSuggestionIds, setSelectedVatSuggestionIds] = useState<number[]>([]);
  const [vatSuggestionGenerateBusy, setVatSuggestionGenerateBusy] = useState(false);
  const [canonicalVatRateOptions, setCanonicalVatRateOptions] = useState<Array<{ value: string; label: string }>>([]);
  const [supplierRuleOpen, setSupplierRuleOpen] = useState(false);
  const [supplierRuleEditingId, setSupplierRuleEditingId] = useState<number | null>(null);
  const [supplierRuleName, setSupplierRuleName] = useState('');
  const [supplierRuleNotes, setSupplierRuleNotes] = useState('');
  const [supplierRulePattern, setSupplierRulePattern] = useState('');
  const [supplierRuleDescriptionContains, setSupplierRuleDescriptionContains] = useState('');
  const [supplierRuleDescriptionTextMatchOperator, setSupplierRuleDescriptionTextMatchOperator] =
    useState<string>('exact');
  const [supplierRuleReferenceContains, setSupplierRuleReferenceContains] = useState('');
  const [supplierRuleReferenceTextMatchOperator, setSupplierRuleReferenceTextMatchOperator] =
    useState<string>('exact');
  const [supplierRuleCriteria, setSupplierRuleCriteria] = useState<SupplierRuleMatchCriterionDraft[]>([]);
  const [supplierRuleCanonicalName, setSupplierRuleCanonicalName] = useState('');
  const [supplierRulePriority, setSupplierRulePriority] = useState('100');
  const [supplierRuleTextMatchOperator, setSupplierRuleTextMatchOperator] = useState<string>('exact');
  const [supplierRuleAccountScope, setSupplierRuleAccountScope] = useState<string>('both');
  const [supplierRuleIsActive, setSupplierRuleIsActive] = useState(true);
  const [supplierRuleSaving, setSupplierRuleSaving] = useState(false);
  const [supplierRuleCriteriaMenuAnchorEl, setSupplierRuleCriteriaMenuAnchorEl] =
    useState<HTMLButtonElement | null>(null);
  const [supplierRuleOutputMenuAnchorEl, setSupplierRuleOutputMenuAnchorEl] =
    useState<HTMLButtonElement | null>(null);
  const [supplierRuleOutputSupplierEnabled, setSupplierRuleOutputSupplierEnabled] = useState(false);
  const [supplierRuleFormError, setSupplierRuleFormError] = useState('');
  const [supplierRuleFieldErrors, setSupplierRuleFieldErrors] = useState<{
    rule_name?: string;
    pattern_raw?: string;
    canonical_name?: string;
    priority?: string;
  }>({});
  const [supplierProposalAcceptId, setSupplierProposalAcceptId] = useState<number | null>(null);
  const [vatRuleOpen, setVatRuleOpen] = useState(false);
  const [vatRuleSaving, setVatRuleSaving] = useState(false);
  const [vatRuleCategoryDeleteBusyKey, setVatRuleCategoryDeleteBusyKey] = useState<string | null>(null);
  const [classificationTablePatchBusyId, setClassificationTablePatchBusyId] = useState<number | null>(null);
  const [supplierRuleTablePatchBusyId, setSupplierRuleTablePatchBusyId] = useState<number | null>(null);
  const [vatRuleDraft, setVatRuleDraft] = useState<VatRuleEditDraft | null>(null);
  const [vatRuleMatchCriteriaMenuAnchorEl, setVatRuleMatchCriteriaMenuAnchorEl] = useState<HTMLButtonElement | null>(
    null
  );
  const [vatRuleOutputMenuAnchorEl, setVatRuleOutputMenuAnchorEl] = useState<HTMLButtonElement | null>(null);
  const [vatRuleOutputVatEnabled, setVatRuleOutputVatEnabled] = useState(false);
  const [vatRuleFormError, setVatRuleFormError] = useState('');
  const [vatRuleFieldErrors, setVatRuleFieldErrors] = useState<{
    category_ids?: string;
    vat_rate?: string;
    priority?: string;
  }>({});
  const [specialCaseRuleRows, setSpecialCaseRuleRows] = useState<SpecialCaseRuleRow[]>([]);
  const [specialCaseRuleOpen, setSpecialCaseRuleOpen] = useState(false);
  const [specialCaseRuleSaving, setSpecialCaseRuleSaving] = useState(false);
  const [specialCaseRuleDraft, setSpecialCaseRuleDraft] = useState<SpecialCaseRuleDraft | null>(null);
  const [specialCaseRuleMatchHelpAnchorEl, setSpecialCaseRuleMatchHelpAnchorEl] =
    useState<HTMLButtonElement | null>(null);
  const [specialCaseRuleCriteriaMenuAnchorEl, setSpecialCaseRuleCriteriaMenuAnchorEl] =
    useState<HTMLButtonElement | null>(null);
  const [specialCaseRuleOutputMenuAnchorEl, setSpecialCaseRuleOutputMenuAnchorEl] =
    useState<HTMLButtonElement | null>(null);
  const [specialCaseRuleOutputVatEnabled, setSpecialCaseRuleOutputVatEnabled] = useState(false);
  const [savingSupplierVatId, setSavingSupplierVatId] = useState<number | null>(null);
  const [supplierVatSearch, setSupplierVatSearch] = useState('');
  const [ruleEditDraft, setRuleEditDraft] = useState<RuleEditDraft | null>(null);
  const [ruleEditReadOnly, setRuleEditReadOnly] = useState(false);
  const [ruleEditTargetType, setRuleEditTargetType] = useState<EditableRuleType>('classification');
  const [ruleEditMatchCriteriaMenuAnchorEl, setRuleEditMatchCriteriaMenuAnchorEl] =
    useState<HTMLButtonElement | null>(null);
  const [ruleEditMatchCriteriaEnabled, setRuleEditMatchCriteriaEnabled] = useState<RuleEditMatchCriteriaEnabled>({
    canonicalSupplier: false,
    merchantCounterparty: false,
    description: false,
    reference: false,
    amountCurrency: false,
    frequency: false
  });
  const [ruleEditOutputMenuAnchorEl, setRuleEditOutputMenuAnchorEl] = useState<HTMLButtonElement | null>(null);
  const [ruleEditOutputCategoryEnabled, setRuleEditOutputCategoryEnabled] = useState(false);
  const [ruleEditOutputSupplierEnabled, setRuleEditOutputSupplierEnabled] = useState(false);
  const [ruleEditSaving, setRuleEditSaving] = useState(false);
  const [ruleEditSaveInFlight, setRuleEditSaveInFlight] = useState(false);
  const [queuedRuleEditSaveRequested, setQueuedRuleEditSaveRequested] = useState(false);
  const [ruleTableSaveHighlight, setRuleTableSaveHighlight] = useState<RuleTableSaveHighlight | null>(null);
  const [ruleActiveSavingId, setRuleActiveSavingId] = useState<number | null>(null);
  const [ruleEditError, setRuleEditError] = useState('');
  const [ruleEditConflictRuleId, setRuleEditConflictRuleId] = useState<number | null>(null);
  /** When set, show the conflicting rule editor beside the primary rule in the same dialog. */
  const [conflictPairEditDraft, setConflictPairEditDraft] = useState<RuleEditDraft | null>(null);
  const [conflictPairEditError, setConflictPairEditError] = useState('');
  const [conflictPairEditSaving, setConflictPairEditSaving] = useState(false);
  const [conflictPairMatchCriteriaEnabled, setConflictPairMatchCriteriaEnabled] =
    useState<RuleEditMatchCriteriaEnabled>({
      canonicalSupplier: false,
      merchantCounterparty: false,
      description: false,
      reference: false,
      amountCurrency: false,
      frequency: false
    });
  const [conflictPairMatchCriteriaMenuAnchorEl, setConflictPairMatchCriteriaMenuAnchorEl] =
    useState<HTMLButtonElement | null>(null);
  const [conflictPairOutputMenuAnchorEl, setConflictPairOutputMenuAnchorEl] =
    useState<HTMLButtonElement | null>(null);
  const [conflictPairOutputCategoryEnabled, setConflictPairOutputCategoryEnabled] = useState(false);
  const [conflictPairOutputSupplierEnabled, setConflictPairOutputSupplierEnabled] = useState(false);
  const resetConflictPairRuleEditorUi = useCallback(() => {
    setConflictPairMatchCriteriaMenuAnchorEl(null);
    setConflictPairOutputMenuAnchorEl(null);
    setConflictPairOutputCategoryEnabled(false);
    setConflictPairOutputSupplierEnabled(false);
    setConflictPairMatchCriteriaEnabled({
      canonicalSupplier: false,
      merchantCounterparty: false,
      description: false,
      reference: false,
      amountCurrency: false,
      frequency: false
    });
  }, []);
  const [ruleMatchHelpAnchorEl, setRuleMatchHelpAnchorEl] = useState<HTMLButtonElement | null>(null);
  const [addCanonicalSupplierOpen, setAddCanonicalSupplierOpen] = useState(false);
  const [addCanonicalSupplierTarget, setAddCanonicalSupplierTarget] = useState<
    'classification' | 'classificationConflictPair' | 'supplier' | 'specialCase'
  >('classification');
  const [addCanonicalSupplierName, setAddCanonicalSupplierName] = useState('');
  const [addCanonicalSupplierBusy, setAddCanonicalSupplierBusy] = useState(false);
  const [addCanonicalSupplierError, setAddCanonicalSupplierError] = useState('');
  const [proposeRuleOpen, setProposeRuleOpen] = useState(false);
  const [proposeRuleTx, setProposeRuleTx] = useState<ReviewTx | null>(null);
  const [proposeRuleName, setProposeRuleName] = useState('');
  const [proposeRuleCategoryId, setProposeRuleCategoryId] = useState<number | ''>('');
  const [proposeRuleDescription, setProposeRuleDescription] = useState('');
  const [proposeRuleSupplier, setProposeRuleSupplier] = useState('');
  const [proposeRuleSaving, setProposeRuleSaving] = useState(false);
  const ruleSaveBusyMessage = 'Another rule save is already in progress. Please wait for it to finish, then try again.';
  const isAnyRuleSaveInFlight =
    ruleEditSaveInFlight || supplierRuleSaving || vatRuleSaving || specialCaseRuleSaving;
  const [proposalBusyKey, setProposalBusyKey] = useState<string>('');
  const [ruleDuplicateBusyKey, setRuleDuplicateBusyKey] = useState<string>('');
  const [proposalSweepBusy, setProposalSweepBusy] = useState(false);
  const [supplierProposalSweepBusy, setSupplierProposalSweepBusy] = useState(false);
  const [missingSupplierAuditBusy, setMissingSupplierAuditBusy] = useState(false);
  const [merchantSupplierSyncBusy, setMerchantSupplierSyncBusy] = useState(false);
  const [merchantSupplierApplyBusy, setMerchantSupplierApplyBusy] = useState(false);
  type MerchantSupplierPreviewRow = {
    key: string;
    display: string;
    canonical: string;
    tx_count: number;
    already_exists: boolean;
    supplier_exists: boolean;
  };
  const [merchantSupplierPreviewOpen, setMerchantSupplierPreviewOpen] = useState(false);
  const [merchantSupplierPreviewRows, setMerchantSupplierPreviewRows] = useState<MerchantSupplierPreviewRow[]>([]);
  const [merchantSupplierPreviewSummary, setMerchantSupplierPreviewSummary] = useState<Record<string, number> | null>(
    null
  );
  const [merchantSupplierSelectedKeys, setMerchantSupplierSelectedKeys] = useState<string[]>([]);
  const [missingSupplierAudit, setMissingSupplierAudit] = useState<Record<string, unknown> | null>(null);
  /** When non-null, audit summary was persisted locally (saved-at epoch ms). */
  const [missingSupplierAuditSavedAt, setMissingSupplierAuditSavedAt] = useState<number | null>(null);
  const missingSupplierAuditAnchorRef = useRef<HTMLDivElement | null>(null);
  const [missingCategoryAuditBusy, setMissingCategoryAuditBusy] = useState(false);
  const [missingCategoryAudit, setMissingCategoryAudit] = useState<Record<string, unknown> | null>(null);
  const [missingVatAuditBusy, setMissingVatAuditBusy] = useState(false);
  const [missingVatAudit, setMissingVatAudit] = useState<Record<string, unknown> | null>(null);
  const [auditCandidatesModal, setAuditCandidatesModal] = useState<{
    kind: AuditProposalCandidatesModalKind;
    title: string;
    candidates: ProposalAuditCandidateRow[];
    proposalAuditRunId: string | null;
  } | null>(null);
  const [auditCandidatesGenerating, setAuditCandidatesGenerating] = useState(false);
  const [backfillAfterAcceptOpen, setBackfillAfterAcceptOpen] = useState(false);
  const [backfillAfterAcceptRuleName, setBackfillAfterAcceptRuleName] = useState('');
  /** Display id for backfill modal (e.g. S783 / C301) after accept/create. */
  const [backfillAfterAcceptPrefixedId, setBackfillAfterAcceptPrefixedId] = useState('');
  /** Exact rule to apply-single on Yes — never a full unconfirmed re-run. */
  const [backfillAfterAcceptTarget, setBackfillAfterAcceptTarget] = useState<BackfillAfterAcceptTarget | null>(
    null
  );
  const [backfillAfterAcceptBusy, setBackfillAfterAcceptBusy] = useState(false);
  const [matchTxSource, setMatchTxSource] = useState<MatchTxModalSource | null>(null);
  const [matchTxRows, setMatchTxRows] = useState<MatchingTransactionRow[]>([]);
  const [matchTxLoading, setMatchTxLoading] = useState(false);
  const [matchTxLoadingMore, setMatchTxLoadingMore] = useState(false);
  const [matchTxHasMore, setMatchTxHasMore] = useState(false);
  const [matchTxCursorId, setMatchTxCursorId] = useState<number | null>(null);
  const [matchTxDateFrom, setMatchTxDateFrom] = useState('');
  const [matchTxDateTo, setMatchTxDateTo] = useState('');

  const operationIsRunning =
    supplierBackfillBusy ||
    reapplyUnconfirmedBusy ||
    reapplyVatUnconfirmedBusy ||
    reapplySpecialCaseUnconfirmedBusy ||
    rerunAllRulesBusy ||
    dedupeDisplayDupTagsBusy;

  const operationProgressCancelSupported =
    operationIsRunning || removeAllTagsBusy || Boolean(activeRuleSyncJobId);

  const resetOperationCancellation = useCallback(() => {
    operationCancelRequestedRef.current = false;
    setOperationCancelRequested(false);
  }, []);

  const requestStopOperation = useCallback(() => {
    if (activeRuleSyncJobId) {
      if (activeRuleSyncCancelRequested) return;
      setOperationProgressMessage((prev) =>
        prev ? `${prev} Cancellation requested…` : 'Cancellation requested…'
      );
      void bgJobs.requestCancel();
      return;
    }
    const toastIdForReapply = reapplyUnconfirmedToastIdRef.current;
    if (toastIdForReapply != null) {
      if (operationCancelRequestedRef.current) return;
      operationCancelRequestedRef.current = true;
      setOperationCancelRequested(true);
      const suffix = ' Stopping after the current batch…';
      setStackedToasts((prev) =>
        prev.map((t) =>
          t.id === toastIdForReapply && !t.message.includes('Stopping after the current batch')
            ? { ...t, message: `${t.message}${suffix}`, actionLabel: undefined, onAction: undefined }
            : t.id === toastIdForReapply
              ? { ...t, actionLabel: undefined, onAction: undefined }
              : t
        )
      );
      return;
    }
    if (operationCancelRequestedRef.current) return;
    operationCancelRequestedRef.current = true;
    setOperationCancelRequested(true);
    setOperationProgressMessage((prev) =>
      prev ? `${prev} Stopping after the current batch…` : 'Stopping after the current batch…'
    );
  }, [activeRuleSyncCancelRequested, activeRuleSyncJobId, bgJobs]);

  const throwIfOperationCancelled = useCallback(() => {
    if (!operationCancelRequestedRef.current) return;
    const err = new Error('Operation stopped by user.') as Error & { code?: string };
    err.code = 'OPERATION_CANCELLED';
    throw err;
  }, []);
  const [matchTxAnchorDate, setMatchTxAnchorDate] = useState('');
  const [matchTxFilterAccount, setMatchTxFilterAccount] = useState<'all' | string>('all');
  const [matchTxFilterHeading, setMatchTxFilterHeading] = useState<'all' | 'income' | 'expense'>('all');
  const [matchTxFilterAmountMin, setMatchTxFilterAmountMin] = useState('');
  const [matchTxFilterAmountMax, setMatchTxFilterAmountMax] = useState('');
  const [matchTxFilterText, setMatchTxFilterText] = useState('');
  const [matchTxError, setMatchTxError] = useState('');
  const [matchTxFooterNote, setMatchTxFooterNote] = useState('');
  const [matchTxGenerateProposalsBusy, setMatchTxGenerateProposalsBusy] = useState(false);
  const [unifiedStatusFilter, setUnifiedStatusFilter] = useState<UnifiedRowStatus[]>([]);
  const [unifiedSupplierFilter, setUnifiedSupplierFilter] = useState<string[]>([]);
  const [hideCommittedUnifiedRows, setHideCommittedUnifiedRows] = useState(true);
  /** Default on: server omits committed ledger rows with supplier + category + VAT applied (lighter /review-rows payloads). */
  const [hideConfirmedCompleteUnifiedRows, setHideConfirmedCompleteUnifiedRows] = useState(true);
  const [unifiedCommittedFilter, setUnifiedCommittedFilter] = useState<'all' | 'committed' | 'uncommitted'>('all');
  /** `flagged` = show only look-into-later rows (`tx_appended_data.is_flagged`). */
  const [unifiedFlaggedFilter, setUnifiedFlaggedFilter] = useState<'all' | 'flagged'>('all');
  const [unifiedFlagBusyTxId, setUnifiedFlagBusyTxId] = useState<number | null>(null);
  const [txPropOptions, setTxPropOptions] = useState<Array<{ id: number; nickname: string }>>([]);
  const [unifiedPropBusyTxId, setUnifiedPropBusyTxId] = useState<number | null>(null);
  const [unifiedTextFilter, setUnifiedTextFilter] = useState('');
  const [unifiedTxIdFilter, setUnifiedTxIdFilter] = useState<number[]>([]);
  const [unifiedAmountMin, setUnifiedAmountMin] = useState('');
  const [unifiedAmountMax, setUnifiedAmountMax] = useState('');
  const [unifiedZeroOrNullAmountOnly, setUnifiedZeroOrNullAmountOnly] = useState(false);
  const [unifiedDateFrom, setUnifiedDateFrom] = useState('');
  const [unifiedDateTo, setUnifiedDateTo] = useState('');
  const [unifiedFrequencyFilter, setUnifiedFrequencyFilter] = useState<string[]>([]);
  const [unifiedCurrencyFilter, setUnifiedCurrencyFilter] = useState<string[]>([]);
  const [unifiedCashFlowFilter, setUnifiedCashFlowFilter] = useState<Array<'In' | 'Out' | 'N/A'>>([]);
  const [unifiedCategoryFilter, setUnifiedCategoryFilter] = useState<string[]>([]);
  const [unifiedAccountFilter, setUnifiedAccountFilter] = useState<string[]>([]);
  const [unifiedStatusMenuOpen, setUnifiedStatusMenuOpen] = useState(false);
  const [unifiedHeadingMenuOpen, setUnifiedHeadingMenuOpen] = useState(false);
  const [unifiedCategoryMenuOpen, setUnifiedCategoryMenuOpen] = useState(false);
  const [unifiedCategorySearchText, setUnifiedCategorySearchText] = useState('');
  const [unifiedAccountMenuOpen, setUnifiedAccountMenuOpen] = useState(false);
  const [unifiedSupplierMenuOpen, setUnifiedSupplierMenuOpen] = useState(false);
  const [unifiedFrequencyMenuOpen, setUnifiedFrequencyMenuOpen] = useState(false);
  const [unifiedCurrencyMenuOpen, setUnifiedCurrencyMenuOpen] = useState(false);
  const [unifiedRevolutStateFilter, setUnifiedRevolutStateFilter] = useState<string[]>([]);
  const [unifiedRevolutStateMenuOpen, setUnifiedRevolutStateMenuOpen] = useState(false);
  const [unifiedVatFilter, setUnifiedVatFilter] = useState<string[]>([]);
  const [unifiedVatMenuOpen, setUnifiedVatMenuOpen] = useState(false);
  const [unifiedAppliedTagFilter, setUnifiedAppliedTagFilter] = useState<string[]>([]);
  const [unifiedAppliedTagMenuOpen, setUnifiedAppliedTagMenuOpen] = useState(false);
  const [unifiedUnclassifiedNeedsReviewOnly, setUnifiedUnclassifiedNeedsReviewOnly] = useState(false);
  const [unifiedTagDuplicateFilter, setUnifiedTagDuplicateFilter] = useState<
    'all' | 'no_duplicates' | 'duplicates'
  >('all');
  /** When true (default), non-`completed` Revolut `state` rows are excluded (declined, reverted, pending, etc.). URL: `tx_hide_declined=0` still means “show them” for bookmarks. */
  const [hideDeclinedRevolutTransactions, setHideDeclinedRevolutTransactions] = useState(true);
  const [unifiedMoreFiltersOpen, setUnifiedMoreFiltersOpen] = useState(false);
  const [unifiedSort, setUnifiedSort] = useState<{ key: string; dir: SortDir }>({
    key: 'date',
    dir: 'desc'
  });
  const [unifiedPage, setUnifiedPage] = useState(0);
  /** Default All: continuous scroll loads more rows as you near the bottom (no page clicks). */
  const [unifiedRowsPerPage, setUnifiedRowsPerPage] = useState(UNIFIED_ROWS_PER_PAGE_ALL);
  const [visibleUnifiedRowsCount, setVisibleUnifiedRowsCount] = useState(UNIFIED_ROWS_RENDER_BATCH_SIZE);
  const unifiedTableScrollRef = useRef<HTMLDivElement | null>(null);
  const unifiedTableSentinelRef = useRef<HTMLDivElement | null>(null);
  const [showUnifiedStatusChart, setShowUnifiedStatusChart] = useState(false);
  const [unifiedChartBreakdown, setUnifiedChartBreakdown] = useState<UnifiedChartBreakdown>('status');
  const [selectedUnifiedRowKeys, setSelectedUnifiedRowKeys] = useState<string[]>([]);
  const [selectAllFilteredPromptOpen, setSelectAllFilteredPromptOpen] = useState(false);
  const [unifiedEditingTxIds, setUnifiedEditingTxIds] = useState<number[]>([]);
  const [selectedFrequencyByTx, setSelectedFrequencyByTx] = useState<Record<number, string>>({});
  const [selectedSupplierByTx, setSelectedSupplierByTx] = useState<Record<number, string>>({});
  const [unifiedTxCategoryTagsByTxId, setUnifiedTxCategoryTagsByTxId] = useState<
    Record<number, UnifiedTxCategoryTagChip[]>
  >({});
  /**
   * Tx ids seeded from review-rows.tags or a successful tags GET/refresh.
   * After load, Cash flow / Category / Supplier / VAT render cache chips only (empty ⇒ —).
   */
  const [unifiedTxCategoryTagsLoadedTxIds, setUnifiedTxCategoryTagsLoadedTxIds] = useState<Set<number>>(
    () => new Set()
  );
  /** Bumps when Refresh completes so tag GET runs even if the current page’s tx id set is unchanged. */
  const [unifiedTxCategoryTagsFetchEpoch, setUnifiedTxCategoryTagsFetchEpoch] = useState(0);
  /** Abort in-flight tag GET so a stale response cannot overwrite after delete / page change. */
  const unifiedTxCategoryTagsAbortRef = useRef<AbortController | null>(null);
  /** Single-row tint + spinner while a tag delete + tag re-fetch runs for that transaction. */
  const [unifiedTxTagSyncBusyByTxId, setUnifiedTxTagSyncBusyByTxId] = useState<Record<number, boolean>>({});
  const beginUnifiedTxCategoryTagsFetch = useCallback((): AbortSignal => {
    unifiedTxCategoryTagsAbortRef.current?.abort();
    const c = new AbortController();
    unifiedTxCategoryTagsAbortRef.current = c;
    return c.signal;
  }, []);
  const haltUnifiedTxCategoryTagsFetch = useCallback(() => {
    unifiedTxCategoryTagsAbortRef.current?.abort();
    unifiedTxCategoryTagsAbortRef.current = null;
  }, []);
  const reviewRowsTagsSeededTxIdsRef = useRef<Set<number>>(new Set());
  const lastTagsPageFetchEpochRef = useRef<number>(-1);
  const applyUnifiedTxCategoryTagsSlice = useCallback(
    (
      slice: Record<number, UnifiedTxCategoryTagChip[]>,
      opts?: MergeUnifiedTxCategoryTagChipsOptions
    ) => {
      setUnifiedTxCategoryTagsByTxId((prev) => mergeUnifiedTxCategoryTagChipsByTxId(prev, slice, opts));
      setUnifiedTxCategoryTagsLoadedTxIds((prev) => {
        const next = new Set(prev);
        Object.keys(slice).forEach((k) => {
          const id = Number(k);
          if (Number.isFinite(id) && id > 0) next.add(id);
        });
        return next;
      });
    },
    []
  );

  /** Seed chip cache from review-rows.tags (authoritative DB payload, including filter refetches). */
  useEffect(() => {
    const rows = canonicalRowsPayload?.rows;
    if (!Array.isArray(rows) || rows.length === 0) return;
    const tagApiRows: any[] = [];
    const seededIds: number[] = [];
    for (const row of rows) {
      if (!Array.isArray(row.tags)) continue;
      const id = Number(row.id);
      if (!Number.isFinite(id) || id <= 0) continue;
      seededIds.push(id);
      for (const t of row.tags) {
        tagApiRows.push({
          ...t,
          revolut_transaction_id: Number(t?.revolut_transaction_id) || id
        });
      }
    }
    if (!seededIds.length) return;
    const slice = buildUnifiedTxCategoryTagChipsByTxId(tagApiRows, seededIds);
    seededIds.forEach((id) => reviewRowsTagsSeededTxIdsRef.current.add(id));
    // Empty tags[] from review-rows is true DB emptiness — allow overwrite.
    applyUnifiedTxCategoryTagsSlice(slice, { forceEmpty: seededIds });
  }, [canonicalRowsPayload, applyUnifiedTxCategoryTagsSlice]);
  const [bulkTagEditOpen, setBulkTagEditOpen] = useState(false);
  const [bulkAppliedTagDeleteComposite, setBulkAppliedTagDeleteComposite] = useState('');
  const [bulkAppliedTagDeleteSelectOpen, setBulkAppliedTagDeleteSelectOpen] = useState(false);
  const [pendingBulkAppliedTagDelete, setPendingBulkAppliedTagDelete] = useState<{
    compositeLabel: string;
    deletions: Array<{ txId: number; uid: string }>;
  } | null>(null);
  const [bulkTagSupplierDraft, setBulkTagSupplierDraft] = useState(BULK_TAG_NO_CHANGE);
  const [bulkTagHeadingDraft, setBulkTagHeadingDraft] = useState<string>(BULK_TAG_NO_CHANGE);
  const [bulkTagCategoryDraft, setBulkTagCategoryDraft] = useState<string>(BULK_TAG_NO_CHANGE);
  const [bulkTagVatDraft, setBulkTagVatDraft] = useState<string>(BULK_TAG_NO_CHANGE);
  const [bulkTagPropDraft, setBulkTagPropDraft] = useState<string>(BULK_TAG_NO_CHANGE);
  const [unifiedActionBusy, setUnifiedActionBusy] = useState(false);
  const [unifiedActionBusyTxId, setUnifiedActionBusyTxId] = useState<number | null>(null);
  const [unifiedIsolateRulesBusyTxId, setUnifiedIsolateRulesBusyTxId] = useState<number | null>(null);
  const [unifiedBulkBusy, setUnifiedBulkBusy] = useState(false);
  const [unifiedBulkUncommitBusy, setUnifiedBulkUncommitBusy] = useState(false);
  const [unifiedStripTagsBusy, setUnifiedStripTagsBusy] = useState(false);
  const unifiedBulkLock = unifiedBulkBusy || unifiedBulkUncommitBusy || unifiedStripTagsBusy;
  /** Rows currently saving tag drafts (single-row Save) — row tint + save icon spinner. */
  const [unifiedSaveBusyTxIds, setUnifiedSaveBusyTxIds] = useState<Set<number>>(() => new Set());
  /** Rows currently in a commit flow (single-row or bulk) — row tint + commit icon spinner. */
  const [unifiedCommitBusyTxIds, setUnifiedCommitBusyTxIds] = useState<Set<number>>(() => new Set());
  const [txJsonViewerOpen, setTxJsonViewerOpen] = useState(false);
  const [txJsonViewerBusy, setTxJsonViewerBusy] = useState(false);
  const [txJsonViewerTitle, setTxJsonViewerTitle] = useState('');
  const [txJsonViewerContent, setTxJsonViewerContent] = useState('');
  const [txJsonViewerError, setTxJsonViewerError] = useState('');
  /** Row whose JSON/tags were loaded (for VAT conflict summary); kept for Quickview → Other snapshot. */
  const [txJsonViewerContextRow, setTxJsonViewerContextRow] = useState<UnifiedReviewRow | null>(null);
  /** `null` = not loaded for the current open yet; array (possibly empty) after GET category tags. */
  const [txJsonViewerCategoryTags, setTxJsonViewerCategoryTags] = useState<any[] | null>(null);
  const [txJsonViewerTagsError, setTxJsonViewerTagsError] = useState('');
  type TxJsonAppliedRuleRow = {
    dimension?: string;
    tag_label?: string | null;
    rule_kind?: string;
    rule_id?: number | null;
    rule_prefixed_id?: string | null;
    rule_name?: string | null;
    source?: string | null;
  };
  type TxJsonProbabilisticMeta = {
    prediction_id?: number;
    model_version?: string | null;
    llm_model?: string | null;
    scored_at?: string | null;
    promoted_at?: string | null;
    promoted_dims?: string[] | null;
    promoted_by_user_id?: number | null;
  };
  /** Cross-dimension winning-tag rule attribution from GET .../applied-rules. */
  const [txJsonViewerAppliedRules, setTxJsonViewerAppliedRules] = useState<TxJsonAppliedRuleRow[] | null>(
    null
  );
  const [txJsonViewerAppliedRulesProbabilistic, setTxJsonViewerAppliedRulesProbabilistic] =
    useState<TxJsonProbabilisticMeta | null>(null);
  const [txJsonViewerAppliedRulesError, setTxJsonViewerAppliedRulesError] = useState('');
  const [txJsonViewerAiSummary, setTxJsonViewerAiSummary] = useState<{
    narrative?: string;
    merchant_likely_url?: string | null;
    candidate_urls?: Array<{ url?: string; title?: string } | string> | null;
    confidence?: string;
    probabilistic?: any;
    llm_error?: string | null;
  } | null>(null);
  const [txJsonViewerAiBusy, setTxJsonViewerAiBusy] = useState(false);
  const [txJsonViewerAiError, setTxJsonViewerAiError] = useState('');
  type TxJsonNeighborRow = {
    id: number;
    date?: string | null;
    amount?: number | null;
    currency?: string | null;
    description?: string | null;
    reference?: string | null;
    account_source?: string | null;
    cardholder?: string | null;
  };
  const [txJsonViewerNeighbors, setTxJsonViewerNeighbors] = useState<{
    preceding: TxJsonNeighborRow[];
    subsequent: TxJsonNeighborRow[];
    current?: TxJsonNeighborRow | null;
  } | null>(null);
  const [txJsonViewerNeighborsBusy, setTxJsonViewerNeighborsBusy] = useState(false);
  const [txJsonViewerNeighborsError, setTxJsonViewerNeighborsError] = useState('');
  const [txJsonViewerSectionsExpanded, setTxJsonViewerSectionsExpanded] = useState({
    keySummary: false,
    aiSummary: false,
    neighbors: false,
    tags: false,
    rawJson: false
  });
  const txJsonViewerFetchGenRef = useRef(0);
  const [txReviewRightPanelWidthPx, setTxReviewRightPanelWidthPx] = useState(() =>
    typeof window !== 'undefined'
      ? Math.min(
          Math.floor(window.innerWidth * TX_REVIEW_RIGHT_PANEL_MAX_FRAC),
          Math.max(TX_REVIEW_RIGHT_PANEL_MIN_PX, Math.floor(window.innerWidth * 0.55))
        )
      : TX_REVIEW_RIGHT_PANEL_MIN_PX
  );
  const [unifiedMoreRow, setUnifiedMoreRow] = useState<UnifiedReviewRow | null>(null);
  const [probabilisticCompareRow, setProbabilisticCompareRow] = useState<UnifiedReviewRow | null>(null);
  const [bulkReprocessBusy, setBulkReprocessBusy] = useState(false);
  const [similarTxBaseRow, setSimilarTxBaseRow] = useState<UnifiedReviewRow | null>(null);
  const [similarTxRows, setSimilarTxRows] = useState<SimilarUnifiedRow[]>([]);
  const [similarTxLoading, setSimilarTxLoading] = useState(false);
  const [similarTxError, setSimilarTxError] = useState('');
  const [similarTxFilterAccount, setSimilarTxFilterAccount] = useState<'all' | string>('all');
  const [similarTxFilterHeading, setSimilarTxFilterHeading] = useState<'all' | 'in' | 'out'>('all');
  const [similarTxFilterAmountMin, setSimilarTxFilterAmountMin] = useState('');
  const [similarTxFilterAmountMax, setSimilarTxFilterAmountMax] = useState('');
  const [similarTxFilterDateFrom, setSimilarTxFilterDateFrom] = useState('');
  const [similarTxFilterDateTo, setSimilarTxFilterDateTo] = useState('');
  const [similarTxFilterScoreMin, setSimilarTxFilterScoreMin] = useState(SIMILAR_TX_DEFAULT_SCORE_MIN);
  const [similarTxFilterScoreMax, setSimilarTxFilterScoreMax] = useState('');
  const [similarTxFilterText, setSimilarTxFilterText] = useState('');
  const [similarTxSelectedIds, setSimilarTxSelectedIds] = useState<number[]>([]);
  const [similarTxSort, setSimilarTxSort] = useState<{ key: SimilarTxSortKey; dir: SortDir }>({
    key: 'similarityScore',
    dir: 'desc'
  });
  const [similarTxRunAllBusy, setSimilarTxRunAllBusy] = useState(false);
  const [similarTxCommitBusy, setSimilarTxCommitBusy] = useState(false);
  const [similarTxUncommitBusy, setSimilarTxUncommitBusy] = useState(false);
  const [similarTxStripBusy, setSimilarTxStripBusy] = useState(false);
  const [similarTxBulkHeadingDraft, setSimilarTxBulkHeadingDraft] = useState<string>(BULK_TAG_NO_CHANGE);
  const [similarTxBulkSupplierDraft, setSimilarTxBulkSupplierDraft] = useState(BULK_TAG_NO_CHANGE);
  const [similarTxBulkCategoryDraft, setSimilarTxBulkCategoryDraft] = useState<string>(BULK_TAG_NO_CHANGE);
  const [similarTxBulkVatDraft, setSimilarTxBulkVatDraft] = useState<string>(BULK_TAG_NO_CHANGE);
  const [similarTxGenerateRuleBusy, setSimilarTxGenerateRuleBusy] = useState<
    null | SimilarTxGenerateKind
  >(null);
  const [similarTxGenerateRulesPickerOpen, setSimilarTxGenerateRulesPickerOpen] = useState(false);
  const [similarTxGenerateKindsDraft, setSimilarTxGenerateKindsDraft] = useState<
    Record<SimilarTxGenerateKind, boolean>
  >({
    category: false,
    supplier: false,
    vat: false,
    special: false
  });
  /**
   * While closing Similar transactions, React state can clear before `useSearchParams` drops `similar-tx`,
   * so the deep-link effect would reopen the modal. Suppress until the param is actually gone from the URL.
   */
  const similarTxSuppressDeepLinkRef = useRef(false);
  /** Bumped when closing Similar TX so filter→URL sync re-runs and cannot resurrect `similar-tx` from a stale `searchParams` snapshot. */
  const [similarTxUrlCloseEpoch, setSimilarTxUrlCloseEpoch] = useState(0);

  /** After Similar-modal “Generate … Rule”, restore modal + selection when user finishes backfill (or declines). */
  const similarTxRulesReturnRef = useRef<{
    reviewTab: string;
    baseTxId: number;
    selectedIds: number[];
  } | null>(null);

  const clearSimilarTxModalSurfaceStateOnly = useCallback(() => {
    setSimilarTxGenerateRuleBusy(null);
    setSimilarTxBaseRow(null);
    setSimilarTxRows([]);
    setSimilarTxLoading(false);
    setSimilarTxError('');
    setSimilarTxFilterAccount('all');
    setSimilarTxFilterHeading('all');
    setSimilarTxFilterAmountMin('');
    setSimilarTxFilterAmountMax('');
    setSimilarTxFilterDateFrom('');
    setSimilarTxFilterDateTo('');
    setSimilarTxFilterScoreMin(SIMILAR_TX_DEFAULT_SCORE_MIN);
    setSimilarTxFilterScoreMax('');
    setSimilarTxFilterText('');
    setSimilarTxSelectedIds([]);
    setSimilarTxRunAllBusy(false);
    setSimilarTxCommitBusy(false);
    setSimilarTxUncommitBusy(false);
    setSimilarTxStripBusy(false);
    setSimilarTxBulkHeadingDraft(BULK_TAG_NO_CHANGE);
    setSimilarTxBulkSupplierDraft(BULK_TAG_NO_CHANGE);
    setSimilarTxBulkCategoryDraft(BULK_TAG_NO_CHANGE);
    setSimilarTxBulkVatDraft(BULK_TAG_NO_CHANGE);
  }, []);

  const stripSimilarTxUrlParam = useCallback(() => {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      if (!next.has(TX_REVIEW_URL_KEYS.similarTx)) return prev;
      next.delete(TX_REVIEW_URL_KEYS.similarTx);
      return next;
    }, { replace: true });
  }, [setSearchParams]);

  /** Same as stripSimilarTxUrlParam but commits immediately via the router (avoids one-frame lag vs. React state). */
  const stripSimilarTxUrlParamViaNavigate = useCallback(() => {
    const next = new URLSearchParams(location.search || '');
    if (!next.has(TX_REVIEW_URL_KEYS.similarTx)) return;
    next.delete(TX_REVIEW_URL_KEYS.similarTx);
    const qs = next.toString();
    navigate(
      { pathname: location.pathname, search: qs ? `?${qs}` : '' },
      { replace: true }
    );
  }, [location.pathname, location.search, navigate]);

  const closeSimilarTransactionsDialog = useCallback(() => {
    similarTxSuppressDeepLinkRef.current = true;
    similarTxRulesReturnRef.current = null;
    stripSimilarTxUrlParamViaNavigate();
    clearSimilarTxModalSurfaceStateOnly();
    stripSimilarTxUrlParam();
    setSimilarTxUrlCloseEpoch((n) => n + 1);
  }, [
    clearSimilarTxModalSurfaceStateOnly,
    stripSimilarTxUrlParam,
    stripSimilarTxUrlParamViaNavigate
  ]);

  /** VAT Conflicts tab — filter which rows are listed (separate from row-level VAT resolution). */
  const [vatConflictSearch, setVatConflictSearch] = useState('');
  const [vatConflictAccount, setVatConflictAccount] = useState<'all' | 'ireland' | 'uk'>('all');
  const [vatConflictAmtMin, setVatConflictAmtMin] = useState('');
  const [vatConflictAmtMax, setVatConflictAmtMax] = useState('');
  const [rulesSupplierFilter, setRulesSupplierFilter] = useState<'all' | string>('all');
  const [rulesIdFilter, setRulesIdFilter] = useState('');
  const [rulesProposalIdsFilter, setRulesProposalIdsFilter] = useState('');
  /** Right drawer on Tx Reviews: browse all rules without switching tabs. */
  const [rulesQuickviewOpen, setRulesQuickviewOpen] = useState(false);
  const [rulesQuickviewMainTab, setRulesQuickviewMainTab] = useState<QuickviewMainTab>('other');
  const [rulesApplyRunSnapshot, setRulesApplyRunSnapshot] = useState<{
    ruleTitle: string;
    summaryLine: string;
    affectedTxIds: number[];
    idsTruncated: boolean;
    idsTotal: number;
  } | null>(null);
  const rulesJsonImportInputRef = useRef<HTMLInputElement>(null);
  const [rulesJsonTransferBusy, setRulesJsonTransferBusy] = useState<'export' | 'import' | null>(null);
  const [qvFeatureRequests, setQvFeatureRequests] = useState<QvFeatureRequestApiRow[]>([]);
  const [qvFeatureRequestsLoading, setQvFeatureRequestsLoading] = useState(false);
  const [qvFeatureRequestsError, setQvFeatureRequestsError] = useState('');
  const [qvFeatureRequestDetail, setQvFeatureRequestDetail] = useState<QvFeatureRequestApiRow | null>(null);
  const [rulesQvSectionsOpen, setRulesQvSectionsOpen] =
    useState<Record<RulesQvSectionKey, boolean>>(defaultRulesQvSectionsOpen);
  const [rulesQvChatMessages, setRulesQvChatMessages] = useState<
    { role: 'user' | 'assistant'; content: string; suggestions?: { label: string; to: string }[] }[]
  >([]);
  const [rulesQvChatInput, setRulesQvChatInput] = useState('');
  const [rulesQvChatLoading, setRulesQvChatLoading] = useState(false);
  const [rulesQvChatError, setRulesQvChatError] = useState<string | null>(null);
  const lastRulesQvHydrationKeyRef = useRef<string>('');
  /** Append-only commentary when re-running all rules on a single unified row (right drawer). */
  const [unifiedRerunLogOpen, setUnifiedRerunLogOpen] = useState(false);
  const [unifiedRerunLogBlocks, setUnifiedRerunLogBlocks] = useState<UnifiedRerunLogBlock[]>([]);
  /** Per-row TX comments (tx_appended_data) modal. */
  const [txCommentModalRow, setTxCommentModalRow] = useState<UnifiedReviewRow | null>(null);
  const [txCommentInternalDraft, setTxCommentInternalDraft] = useState('');
  const [txCommentExternalDraft, setTxCommentExternalDraft] = useState('');
  const [txCommentSaving, setTxCommentSaving] = useState(false);
  const [txCommentError, setTxCommentError] = useState<string | null>(null);
  /**
   * After "Generate Rule" returns 0 new proposals for a tx, hide the Quickview action for that tx until
   * a new re-run completes or Row details is opened again for the same tx (fresh attempt).
   */
  const [proposalGenerateEmptySuppressedTxIds, setProposalGenerateEmptySuppressedTxIds] = useState<number[]>([]);
  const rerunLogBlocksLenForSuppressRef = useRef(0);
  const [rulesTextFilter, setRulesTextFilter] = useState('');
  const [rulesFrequencyFilter, setRulesFrequencyFilter] = useState<'all' | string>('all');
  const [rulesCategoryFilter, setRulesCategoryFilter] = useState<'all' | string>('all');
  const [rulesStatusFilter, setRulesStatusFilter] = useState<'all' | RuleRowState>('all');
  const parsedRulesIdFilter = useMemo(() => parseRulesIdFilter(rulesIdFilter), [rulesIdFilter]);
  const parsedRulesProposalIdFilter = useMemo(
    () => parseRulesProposalIdFilter(rulesProposalIdsFilter),
    [rulesProposalIdsFilter]
  );
  const [classificationRulesTableSort, setClassificationRulesTableSort] = useState<{ key: string; dir: SortDir } | null>({
    key: 'priority',
    dir: 'asc'
  });
  const [supplierRulesTableSort, setSupplierRulesTableSort] = useState<{ key: string; dir: SortDir } | null>({
    key: 'priority',
    dir: 'asc'
  });
  const [vatRulesTableSort, setVatRulesTableSort] = useState<{ key: string; dir: SortDir } | null>(null);
  const [specialCaseRulesTableSort, setSpecialCaseRulesTableSort] = useState<{ key: string; dir: SortDir } | null>(null);
  const [canonicalSupplierOptions, setCanonicalSupplierOptions] = useState<string[]>([]);
  const [suppliersCrudRows, setSuppliersCrudRows] = useState<SupplierCrudRow[]>([]);
  const [suppliersCrudSort, setSuppliersCrudSort] = useState<{ key: string; dir: SortDir }>({
    key: 'name',
    dir: 'asc'
  });
  const [suppliersCrudChartOpen, setSuppliersCrudChartOpen] = useState(false);
  const [suppliersCrudSearch, setSuppliersCrudSearch] = useState('');
  const [suppliersCrudNameFilter, setSuppliersCrudNameFilter] = useState('');
  const [suppliersCrudIdFilter, setSuppliersCrudIdFilter] = useState<number | null>(null);
  const [suppliersCrudActiveFilter, setSuppliersCrudActiveFilter] = useState<'all' | 'active' | 'inactive'>('all');
  const [suppliersCrudVatFilter, setSuppliersCrudVatFilter] = useState<'all' | string>('all');
  const [suppliersCrudCurrencyFilter, setSuppliersCrudCurrencyFilter] = useState<'all' | string>('all');
  const [suppliersCrudSourceFilter, setSuppliersCrudSourceFilter] = useState<EntitySourceFilterValue>('all');
  const [suppliersCrudHideNonKey, setSuppliersCrudHideNonKey] = useState(false);
  const [suppliersCrudLoading, setSuppliersCrudLoading] = useState(false);
  const [suppliersCrudError, setSuppliersCrudError] = useState('');
  const [supplierCorpusAuditResult, setSupplierCorpusAuditResult] = useState<SupplierCorpusAuditPayload | null>(null);
  const [supplierCorpusAuditLoading, setSupplierCorpusAuditLoading] = useState(false);
  const [supplierCorpusAuditElapsedSec, setSupplierCorpusAuditElapsedSec] = useState(0);
  const [supplierCorpusAuditError, setSupplierCorpusAuditError] = useState('');
  /** Expanded proposal-group keys (`supplier-<id>`) in Supplier Audit section A. */
  const [supplierCorpusAuditExpandedBuckets, setSupplierCorpusAuditExpandedBuckets] = useState<Record<string, boolean>>(
    {}
  );
  const [supplierCorpusAuditSampleTxById, setSupplierCorpusAuditSampleTxById] = useState<
    Record<number, CanonicalReviewRow>
  >({});
  const [supplierCorpusAuditSampleTxBusyKeys, setSupplierCorpusAuditSampleTxBusyKeys] = useState<Record<string, boolean>>(
    {}
  );
  const [supplierCorpusAuditSampleTxErrors, setSupplierCorpusAuditSampleTxErrors] = useState<Record<string, string>>({});
  /** Selected TX ids inside Proposed Supplier mappings. */
  const [supplierAuditSelectedTxIds, setSupplierAuditSelectedTxIds] = useState<number[]>([]);
  /** Per-TX override of which canonical supplier to assign (defaults to proposal group). */
  const [supplierAuditAssignSupplierByTxId, setSupplierAuditAssignSupplierByTxId] = useState<Record<number, number>>(
    {}
  );
  /** Selected unused catalog supplier ids in Manage Canonical list. */
  const [supplierCorpusAuditSelectedUnusedIds, setSupplierCorpusAuditSelectedUnusedIds] = useState<number[]>([]);
  const [supplierCorpusAuditActionBusy, setSupplierCorpusAuditActionBusy] = useState(false);
  const [supplierCrudOpen, setSupplierCrudOpen] = useState(false);
  const [supplierCrudSaving, setSupplierCrudSaving] = useState(false);
  const [supplierKeyToggleBusyId, setSupplierKeyToggleBusyId] = useState<number | null>(null);
  const [supplierCrudDraft, setSupplierCrudDraft] = useState<SupplierCrudDraft | null>(null);
  const [supplierCrudFormError, setSupplierCrudFormError] = useState('');
  const [categoriesCrudRows, setCategoriesCrudRows] = useState<CategoryCrudRow[]>([]);
  const [categoriesCrudSort, setCategoriesCrudSort] = useState<{ key: string; dir: SortDir }>({
    key: 'name',
    dir: 'asc'
  });
  const [categoriesCrudChartOpen, setCategoriesCrudChartOpen] = useState(false);
  const [categoriesCrudSearch, setCategoriesCrudSearch] = useState('');
  const [categoriesCrudNameFilter, setCategoriesCrudNameFilter] = useState('');
  const [categoriesCrudActiveFilter, setCategoriesCrudActiveFilter] = useState<'all' | 'active' | 'inactive'>('all');
  const [categoriesCrudSourceFilter, setCategoriesCrudSourceFilter] = useState<EntitySourceFilterValue>('all');
  const [categoriesCrudHideNonKey, setCategoriesCrudHideNonKey] = useState(false);
  const [categoriesCrudLoading, setCategoriesCrudLoading] = useState(false);
  const [categoriesCrudError, setCategoriesCrudError] = useState('');
  const [categoryCrudOpen, setCategoryCrudOpen] = useState(false);
  const [categoryCrudSaving, setCategoryCrudSaving] = useState(false);
  const [categoryCrudDraft, setCategoryCrudDraft] = useState<CategoryCrudDraft | null>(null);
  const [categoryCrudFormError, setCategoryCrudFormError] = useState('');
  const [categoryCrudTarget, setCategoryCrudTarget] = useState<
    'categories' | 'ruleEdit' | 'ruleEditConflictPair'
  >('categories');
  /** When set, creating a category applies it as granular category for this transaction row. */
  const [categoryCrudPendingGranularTxId, setCategoryCrudPendingGranularTxId] = useState<number | null>(null);
  const [categoryKeyToggleBusyId, setCategoryKeyToggleBusyId] = useState<number | null>(null);
  const [selectedSupplierCrudIds, setSelectedSupplierCrudIds] = useState<number[]>([]);
  const [selectedCategoryCrudIds, setSelectedCategoryCrudIds] = useState<number[]>([]);
  const [suppliersBulkEditOpen, setSuppliersBulkEditOpen] = useState(false);
  const [categoriesBulkEditOpen, setCategoriesBulkEditOpen] = useState(false);
  const [suppliersBulkFavourite, setSuppliersBulkFavourite] = useState<'no_change' | 'favourite' | 'not_favourite'>(
    'no_change'
  );
  const [suppliersBulkActive, setSuppliersBulkActive] = useState<'no_change' | 'active' | 'inactive'>('no_change');
  const [categoriesBulkFavourite, setCategoriesBulkFavourite] = useState<'no_change' | 'favourite' | 'not_favourite'>(
    'no_change'
  );
  const [categoriesBulkActive, setCategoriesBulkActive] = useState<'no_change' | 'active' | 'inactive'>('no_change');
  const [suppliersBulkBusy, setSuppliersBulkBusy] = useState(false);
  const [suppliersMergeDialogOpen, setSuppliersMergeDialogOpen] = useState(false);
  const [suppliersMergeCanonicalDraft, setSuppliersMergeCanonicalDraft] = useState('');
  const [suppliersMergeCreateDescRule, setSuppliersMergeCreateDescRule] = useState(true);
  const [suppliersMergeDeactivateSources, setSuppliersMergeDeactivateSources] = useState(true);
  const [suppliersMergeBusy, setSuppliersMergeBusy] = useState(false);
  const [suppliersBulkDeleteBusy, setSuppliersBulkDeleteBusy] = useState(false);
  const [supplierAliasMappingsTarget, setSupplierAliasMappingsTarget] = useState<{ id: number; name: string } | null>(
    null
  );
  const [categoriesBulkBusy, setCategoriesBulkBusy] = useState(false);
  const [applySingleRuleBusyKey, setApplySingleRuleBusyKey] = useState('');
  const [selectedClassificationRuleKeys, setSelectedClassificationRuleKeys] = useState<string[]>([]);
  const [selectedSupplierRuleKeys, setSelectedSupplierRuleKeys] = useState<string[]>([]);
  /** Step 2A merchant-alias inventory selection (separate from 2B `selectedSupplierRuleKeys`). */
  const [selectedMerchantSupplierRuleKeys, setSelectedMerchantSupplierRuleKeys] = useState<string[]>([]);
  /** Step 2B Rejected nested section selection (proposal DB ids). */
  const [selectedRejectedSupplierProposalIds, setSelectedRejectedSupplierProposalIds] = useState<number[]>([]);
  const [selectedVatRuleIds, setSelectedVatRuleIds] = useState<number[]>([]);
  const [selectedSpecialCaseRuleIds, setSelectedSpecialCaseRuleIds] = useState<number[]>([]);
  /** Last clicked row key per Rules table — used for Shift+click range select. */
  const rulesRowSelectAnchorRef = useRef<Record<string, string | number | null>>({});
  const applyRulesRowCheckboxSelection = useCallback(
    <T extends string | number>(opts: {
      tableKey: string;
      orderedKeys: readonly T[];
      clickedKey: T;
      checked: boolean;
      shiftKey: boolean;
      setSelection: React.Dispatch<React.SetStateAction<T[]>>;
    }) => {
      const anchor = rulesRowSelectAnchorRef.current[opts.tableKey] as T | null | undefined;
      opts.setSelection((prev) => {
        const result = applyCheckboxShiftRangeSelection({
          orderedKeys: opts.orderedKeys,
          clickedKey: opts.clickedKey,
          checked: opts.checked,
          shiftKey: opts.shiftKey,
          previousSelection: prev,
          anchorKey: anchor ?? null
        });
        rulesRowSelectAnchorRef.current[opts.tableKey] = result.nextAnchor;
        return result.nextSelection;
      });
    },
    []
  );
  const [bulkClassificationPriority, setBulkClassificationPriority] = useState('');
  const [bulkClassificationAccountScope, setBulkClassificationAccountScope] = useState<
    typeof RULE_BULK_NO_CHANGE | 'both' | 'ireland' | 'uk'
  >(RULE_BULK_NO_CHANGE);
  const [bulkClassificationComment, setBulkClassificationComment] = useState('');
  const [bulkSupplierPriority, setBulkSupplierPriority] = useState('');
  const [bulkSupplierAccountScope, setBulkSupplierAccountScope] = useState<
    typeof RULE_BULK_NO_CHANGE | 'both' | 'ireland' | 'uk'
  >(RULE_BULK_NO_CHANGE);
  const [bulkSupplierComment, setBulkSupplierComment] = useState('');
  const [bulkVatPriority, setBulkVatPriority] = useState('');
  const [bulkVatAccountScope, setBulkVatAccountScope] = useState<
    typeof RULE_BULK_NO_CHANGE | 'both' | 'ireland' | 'uk'
  >(RULE_BULK_NO_CHANGE);
  const [bulkSpecialCasePriority, setBulkSpecialCasePriority] = useState('');
  const [bulkSpecialCaseAccountScope, setBulkSpecialCaseAccountScope] = useState<
    typeof RULE_BULK_NO_CHANGE | 'both' | 'ireland' | 'uk'
  >(RULE_BULK_NO_CHANGE);
  const [bulkSpecialCaseComment, setBulkSpecialCaseComment] = useState('');
  const [crossBulkAccountScope, setCrossBulkAccountScope] = useState<
    typeof RULE_BULK_NO_CHANGE | 'both' | 'ireland' | 'uk'
  >(RULE_BULK_NO_CHANGE);
  const [crossBulkComment, setCrossBulkComment] = useState('');
  const [rulesAccordionOpen, setRulesAccordionOpen] = useState<{
    proposals: boolean;
    pipelineStepRules: boolean;
    merchantSuppliers: boolean;
    supplierProposals: boolean;
    appRules: boolean;
    supplierRules: boolean;
    vatRules: boolean;
    specialCaseRules: boolean;
  }>({
    proposals: false,
    pipelineStepRules: false,
    merchantSuppliers: false,
    supplierProposals: false,
    appRules: false,
    supplierRules: false,
    vatRules: false,
    specialCaseRules: false
  });
  /** Avoid resetting Rules accordions when filter-derived visibility has not actually changed. */
  const rulesAccordionFilterSigRef = useRef<string>('');
  const [supplierRulesSort, setSupplierRulesSort] = useState<{ key: 'canonical' | 'pattern'; dir: SortDir }>({
    key: 'canonical',
    dir: 'asc'
  });
  const classificationPageRef = useRef<HTMLDivElement | null>(null);
  /** VAT rate header — anchor for JSON / Rules right-drawer width (extended left for readability). */
  const unifiedVatRateColumnHeaderRef = useRef<HTMLTableCellElement | null>(null);
  const rerunLogScrollBoxRef = useRef<HTMLDivElement | null>(null);
  /** Quickview → Other tab: scroll container for Revolut JSON + re-run log (append scroll on new blocks). */
  const qvOtherTabScrollRef = useRef<HTMLDivElement | null>(null);
  const matchTxScrollRef = useRef<HTMLDivElement | null>(null);
  const matchTxSentinelRef = useRef<HTMLDivElement | null>(null);
  const allTransactionsHydrateRunRef = useRef(0);
  const confirmedHydrateRunRef = useRef(0);
  /** Below this width, hide A/C so the remaining columns still fit without horizontal scroll. */
  const useUnifiedOverflowColumns = useMediaQuery('(max-width:1280px)');
  const showAllUnifiedFilterControls = useMediaQuery('(min-width:2500px)');
  const show12UnifiedFilterControls = useMediaQuery('(min-width:2350px)');
  const show11UnifiedFilterControls = useMediaQuery('(min-width:2200px)');
  const show10UnifiedFilterControls = useMediaQuery('(min-width:2050px)');
  const show9UnifiedFilterControls = useMediaQuery('(min-width:1900px)');
  const show8UnifiedFilterControls = useMediaQuery('(min-width:1775px)');
  const show7UnifiedFilterControls = useMediaQuery('(min-width:1650px)');
  const show6UnifiedFilterControls = useMediaQuery('(min-width:1550px)');
  const show5UnifiedFilterControls = useMediaQuery('(min-width:1450px)');
  const show4UnifiedFilterControls = useMediaQuery('(min-width:1325px)');
  const unifiedInlineFilterCount = useMemo(() => {
    if (showAllUnifiedFilterControls) return UNIFIED_FILTER_CONTROL_ORDER.length;
    if (show12UnifiedFilterControls) return 12;
    if (show11UnifiedFilterControls) return 11;
    if (show10UnifiedFilterControls) return 10;
    if (show9UnifiedFilterControls) return 9;
    if (show8UnifiedFilterControls) return 8;
    if (show7UnifiedFilterControls) return 7;
    if (show6UnifiedFilterControls) return 6;
    if (show5UnifiedFilterControls) return 5;
    if (show4UnifiedFilterControls) return 4;
    return 3;
  }, [
    showAllUnifiedFilterControls,
    show12UnifiedFilterControls,
    show11UnifiedFilterControls,
    show10UnifiedFilterControls,
    show9UnifiedFilterControls,
    show8UnifiedFilterControls,
    show7UnifiedFilterControls,
    show6UnifiedFilterControls,
    show5UnifiedFilterControls,
    show4UnifiedFilterControls,
  ]);
  const unifiedInlineFilterSet = useMemo(() => {
    const next = new Set<UnifiedFilterControlKey>(
      UNIFIED_FILTER_CONTROL_ORDER.slice(0, unifiedInlineFilterCount)
    );
    // Keep Bank Account visible on the main TX Review filter bar.
    next.add('account');
    // Always show Tag duplicates — De-dup only appears when this filter is "Duplicate tags".
    next.add('tagDuplicate');
    return next;
  }, [unifiedInlineFilterCount]);
  const unifiedModalFilterCount = UNIFIED_FILTER_CONTROL_ORDER.length - unifiedInlineFilterSet.size;
  const isUnifiedFilterInline = useCallback(
    (key: UnifiedFilterControlKey) => unifiedInlineFilterSet.has(key),
    [unifiedInlineFilterSet]
  );
  const isUnifiedFilterInModal = useCallback(
    (key: UnifiedFilterControlKey) => !unifiedInlineFilterSet.has(key),
    [unifiedInlineFilterSet]
  );

  const attachOverflowTooltipIfNeeded = useCallback((event: React.MouseEvent<HTMLDivElement>) => {
    const root = classificationPageRef.current;
    if (!root) return;
    const path = typeof event.nativeEvent.composedPath === 'function' ? event.nativeEvent.composedPath() : [];
    for (const node of path) {
      if (!(node instanceof HTMLElement)) continue;
      if (node === root) break;
      // Limit auto-overflow tooltips to actual table row content.
      // This avoids noisy browser tooltips on top filter controls.
      if (!node.closest('tbody')) continue;
      const tag = node.tagName.toLowerCase();
      if (tag === 'svg' || tag === 'path' || tag === 'button' || tag === 'input' || tag === 'textarea' || tag === 'select') {
        continue;
      }
      const text = (node.innerText || node.textContent || '').trim();
      if (!text) continue;
      const overflowed =
        (node.scrollWidth > node.clientWidth + 1 || node.scrollHeight > node.clientHeight + 1) &&
        (node.clientWidth > 0 || node.clientHeight > 0);
      if (!overflowed) {
        if (node.dataset.ffOverflowTooltip === '1') {
          node.removeAttribute('title');
          delete node.dataset.ffOverflowTooltip;
        }
        continue;
      }
      // Preserve explicit author-provided titles; only manage titles we create.
      if (!node.getAttribute('title') || node.dataset.ffOverflowTooltip === '1') {
        node.setAttribute('title', text);
        node.dataset.ffOverflowTooltip = '1';
      }
      return;
    }
  }, []);

  const showRefreshInfoToast = useCallback((message: string) => {
    setRefreshInfoMessage(message);
    setRefreshInfoToastCode(String(message || '').trim() ? nextClientToastCode() : '');
  }, []);

  const diagnosticsFromApiError = useCallback((err: any) => {
    const data = err?.response?.data || {};
    const headers = err?.response?.headers || {};
    const requestId =
      String(data?.request_id || headers?.['x-request-id'] || headers?.['X-Request-ID'] || '').trim() || undefined;
    const jobId =
      String(data?.job_id || data?.job?.job_id || headers?.['x-job-id'] || headers?.['X-Job-ID'] || '').trim() || undefined;
    const errorCode = String(data?.code || data?.error_code || '').trim() || undefined;
    return { requestId, jobId, errorCode };
  }, []);

  useEffect(() => {
    if (!operationProgressMessage) {
      setOperationProgressBannerDismissed(false);
    }
  }, [operationProgressMessage]);

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        const res = await api.get('/api/properties/');
        const list = Array.isArray(res.data)
          ? res.data
          : Array.isArray(res.data?.properties)
            ? res.data.properties
            : [];
        const nickRank = new Map<string, number>(
          TX_PROP_NICKNAMES.map((n, i) => [n.toUpperCase(), i])
        );
        const opts = (list as Array<{ id?: unknown; nickname?: unknown }>)
          .map((p) => ({
            id: Number(p.id),
            nickname: String(p.nickname || '').trim()
          }))
          .filter(
            (p) =>
              Number.isFinite(p.id) &&
              p.id > 0 &&
              nickRank.has(p.nickname.toUpperCase())
          )
          .sort(
            (a, b) =>
              (nickRank.get(a.nickname.toUpperCase()) ?? 99) -
              (nickRank.get(b.nickname.toUpperCase()) ?? 99)
          );
        if (!cancelled) setTxPropOptions(opts);
      } catch {
        if (!cancelled) setTxPropOptions([]);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const categoriesForPickers = useMemo(
    () => categories.filter((c) => c.is_active !== false),
    [categories]
  );

  const categoriesSortedByName = useMemo(
    () => sortCategoriesUnknownFirst(categoriesForPickers),
    [categoriesForPickers]
  );

  const headingCategories = useMemo(() => {
    const list = categoriesForPickers.filter((c) => isTopLevelCashflowHeadingName(c.name));
    return [...list].sort((a, b) => a.name.localeCompare(b.name));
  }, [categoriesForPickers]);

  const granularCategories = useMemo(
    () =>
      sortCategoriesUnknownFirst(
        categoriesForPickers.filter((c) => !isTopLevelCashflowHeadingName(c.name))
      ),
    [categoriesForPickers]
  );
  const granularCategoriesSortedByName = granularCategories;

  /**
   * Classification rule edit dialog: always include the draft's category_id in the menu so MUI Select
   * stays valid (heading-only rows are omitted from granular lists). Missing ids use a placeholder row.
   */
  const ruleEditCategorySelectOptions = useMemo(() => {
    const base = granularCategoriesSortedByName;
    const cidRaw = ruleEditDraft?.category_id;
    const cid = cidRaw != null && Number.isFinite(Number(cidRaw)) ? Math.trunc(Number(cidRaw)) : 0;
    if (!cid || cid <= 0) return base;
    if (base.some((c) => c.id === cid)) return base;
    const fromAll = categoriesSortedByName.find((c) => c.id === cid);
    if (fromAll) {
      const merged = [...base];
      if (!merged.some((c) => c.id === fromAll.id)) merged.push(fromAll);
      return merged.sort((a, b) => compareCategoryNamesUnknownFirst(a.name, b.name));
    }
    return [
      ...base,
      { id: cid, name: `Category #${cid}`, is_key_category: false }
    ].sort((a, b) => compareCategoryNamesUnknownFirst(a.name, b.name));
  }, [granularCategoriesSortedByName, categoriesSortedByName, ruleEditDraft?.category_id]);

  const openNewClassificationRuleDialog = useCallback(() => {
    setRuleEditError('');
    setRuleEditReadOnly(false);
    setRuleEditSaveInFlight(false);
    setRuleEditTargetType('classification');
    setRuleEditMatchCriteriaMenuAnchorEl(null);
    setRuleEditOutputMenuAnchorEl(null);
    setRuleEditOutputCategoryEnabled(false);
    setRuleEditOutputSupplierEnabled(false);
    setConflictPairEditDraft(null);
    setConflictPairEditError('');
    resetConflictPairRuleEditorUi();
    const defaultCategoryId = defaultCategoryIdUnknownFirst(granularCategoriesSortedByName);
    const draft = newClassificationRuleDraft(defaultCategoryId);
    setRuleEditDraft(draft);
    setRuleEditMatchCriteriaEnabled(initialRuleEditMatchCriteriaEnabled(draft));
  }, [granularCategoriesSortedByName, resetConflictPairRuleEditorUi]);

  const openClassificationRuleEditDialog = useCallback((rule: ClassificationRule) => {
    setRuleEditError('');
    setRuleEditConflictRuleId(null);
    setRuleEditReadOnly(false);
    setRuleEditSaveInFlight(false);
    setRuleEditTargetType('classification');
    setRuleEditMatchCriteriaMenuAnchorEl(null);
    setRuleEditOutputMenuAnchorEl(null);
    setRuleEditOutputCategoryEnabled(true);
    setRuleEditOutputSupplierEnabled(false);
    setConflictPairEditDraft(null);
    setConflictPairEditError('');
    resetConflictPairRuleEditorUi();
    const draft = classificationRuleToEditDraft(rule);
    setRuleEditDraft(draft);
    setRuleEditMatchCriteriaEnabled(initialRuleEditMatchCriteriaEnabled(draft));
  }, [resetConflictPairRuleEditorUi]);

  /** Load a confirmed classification rule by id (for conflict side-by-side), refreshing catalog if needed. */
  const loadClassificationRuleById = useCallback(
    async (ruleId: number): Promise<ClassificationRule | null> => {
      const want = Math.trunc(Number(ruleId));
      if (!Number.isFinite(want) || want <= 0) return null;
      const fromState = (rulesPayload?.rules || []).find(
        (x) => Math.trunc(Number(x.id)) === want
      );
      if (fromState) return fromState;
      try {
        const res = await api.get('/api/transaction-classification-rules/', {
          params: { lite: 1 }
        });
        const list = Array.isArray(res.data?.rules) ? (res.data.rules as ClassificationRule[]) : [];
        setRulesPayload((prev) => {
          if (!prev) return { rules: list };
          const byId = new Map<number, ClassificationRule>();
          for (const r of prev.rules || []) {
            const id = Math.trunc(Number(r.id));
            if (Number.isFinite(id) && id > 0) byId.set(id, r);
          }
          for (const r of list) {
            const id = Math.trunc(Number(r.id));
            if (Number.isFinite(id) && id > 0) byId.set(id, r);
          }
          return { rules: Array.from(byId.values()) };
        });
        return list.find((x) => Math.trunc(Number(x.id)) === want) || null;
      } catch {
        return null;
      }
    },
    [rulesPayload?.rules]
  );

  /** Open dual-pane editor with the conflicting confirmed rule on the right. */
  const openClassificationConflictSideBySide = useCallback(
    async (conflictRuleId: number): Promise<boolean> => {
      const cid = Math.trunc(Number(conflictRuleId));
      if (!Number.isFinite(cid) || cid <= 0) return false;
      const r = await loadClassificationRuleById(cid);
      if (!r) {
        setRuleEditError(
          (prev) =>
            (String(prev).trim() ? `${String(prev).trim()}\n\n` : '') +
            `Could not load ${formatClassificationRulePrefixedId(cid)} for side-by-side compare.`
        );
        return false;
      }
      const cpDraft = classificationRuleToEditDraft(r);
      setConflictPairEditDraft(cpDraft);
      setConflictPairMatchCriteriaEnabled(initialRuleEditMatchCriteriaEnabled(cpDraft));
      setConflictPairMatchCriteriaMenuAnchorEl(null);
      setConflictPairOutputMenuAnchorEl(null);
      setConflictPairOutputCategoryEnabled(true);
      setConflictPairOutputSupplierEnabled(false);
      setConflictPairEditError('');
      setRuleEditConflictRuleId(null);
      return true;
    },
    [loadClassificationRuleById]
  );

  const openClassificationRuleReadOnlyDialog = useCallback((rule: ClassificationRule) => {
    setRuleEditError('');
    setRuleEditConflictRuleId(null);
    setConflictPairEditDraft(null);
    setConflictPairEditError('');
    resetConflictPairRuleEditorUi();
    setRuleEditReadOnly(true);
    setRuleEditSaveInFlight(false);
    setRuleEditTargetType('classification');
    setRuleEditMatchCriteriaMenuAnchorEl(null);
    setRuleEditOutputMenuAnchorEl(null);
    setRuleEditOutputCategoryEnabled(true);
    setRuleEditOutputSupplierEnabled(false);
    const draft = classificationRuleToEditDraft(rule);
    setRuleEditDraft(draft);
    setRuleEditMatchCriteriaEnabled(initialRuleEditMatchCriteriaEnabled(draft));
  }, [resetConflictPairRuleEditorUi]);

  const openNewSupplierRuleDialog = useCallback(() => {
    setSupplierRuleEditingId(null);
    setSupplierProposalAcceptId(null);
    setSupplierRuleName('');
    setSupplierRuleNotes('');
    setSupplierRulePattern('');
    setSupplierRuleDescriptionContains('');
    setSupplierRuleDescriptionTextMatchOperator('exact');
    setSupplierRuleReferenceContains('');
    setSupplierRuleReferenceTextMatchOperator('exact');
    setSupplierRuleCriteria([]);
    setSupplierRuleCanonicalName('');
    setSupplierRuleOutputSupplierEnabled(false);
    setSupplierRuleOutputMenuAnchorEl(null);
    setSupplierRulePriority('100');
    setSupplierRuleTextMatchOperator('exact');
    setSupplierRuleAccountScope('both');
    setSupplierRuleIsActive(true);
    setSupplierRuleFormError('');
    setSupplierRuleFieldErrors({});
    setSupplierRuleOpen(true);
  }, []);

  const sortedRules = useMemo(
    () => sortRulesRows(rulesPayload?.rules ?? [], rulesSort),
    [rulesPayload, rulesSort]
  );

  const filteredRuleProposals = useMemo(() => {
    const selectedSupplier = rulesSupplierFilter === 'all' ? '' : rulesSupplierFilter.trim().toLowerCase();
    const idf = parsedRulesIdFilter;
    return (proposalsPayload?.proposals || []).filter((row) => {
      if (parsedRulesProposalIdFilter) {
        const { classificationProposalIds, atlasSeqs } = parsedRulesProposalIdFilter;
        const needDb = classificationProposalIds.size > 0;
        const needAtlas = atlasSeqs.size > 0;
        if (needDb || needAtlas) {
          const pid = Number(row.id);
          const isDb = Number.isFinite(pid) && pid > 0;
          const seqRaw = row.atlas_display_seq != null ? Number(row.atlas_display_seq) : NaN;
          const seq = Number.isFinite(seqRaw) && seqRaw > 0 ? Math.trunc(seqRaw) : null;
          const dbMatch = needDb && isDb && classificationProposalIds.has(pid);
          const atlasMatch = needAtlas && Boolean(row.virtual) && seq != null && atlasSeqs.has(seq);
          if (needDb && needAtlas) {
            if (!dbMatch && !atlasMatch) return false;
          } else if (needDb) {
            if (!dbMatch) return false;
          } else if (!atlasMatch) {
            return false;
          }
        }
      }
      if (idf.hasFilter && idf.classificationAtlasSeq != null) {
        const seqRaw = row.atlas_display_seq != null ? Number(row.atlas_display_seq) : NaN;
        const seq = Number.isFinite(seqRaw) && seqRaw > 0 ? Math.trunc(seqRaw) : NaN;
        if (!row.virtual || !Number.isFinite(seq) || seq !== idf.classificationAtlasSeq) return false;
      } else if (idf.hasFilter && idf.classificationProposalId != null) {
        const pid = row.id != null ? Number(row.id) : NaN;
        if (!Number.isFinite(pid) || pid !== idf.classificationProposalId) return false;
      } else if (idf.hasFilter && idf.kind === 'classification' && idf.id != null && idf.classificationProposalId == null) {
        // `C###` isolates a confirmed app rule — hide proposals for that filter.
        return false;
      } else if (idf.hasFilter && idf.kind === null && idf.id != null && idf.classificationProposalId == null) {
        const pid = row.id != null ? Number(row.id) : NaN;
        if (!Number.isFinite(pid) || pid !== idf.id) return false;
      }
      const p = row.payload || {};
      const supplierRaw = row.supplier_contains ?? (p.supplier_contains as string) ?? '';
      const supplierOverride = (p.supplier_name_override as string) || row.supplier_name_override || '';
      const supplierOverridesList = Array.isArray(p.supplier_name_overrides)
        ? (p.supplier_name_overrides as unknown[]).map((x) => String(x ?? ''))
        : [];
      const categoryRaw = row.category_name || (p.category_name as string) || (p.category as string) || '';
      const descRaw =
        row.description_contains ??
        row.reference_contains ??
        (p.description_contains as string) ??
        (p.reference_contains as string) ??
        '';
      const recurrenceRaw =
        (p.recurrence_type as string) ??
        (p.payment_frequency as string) ??
        row.status ??
        '';
      const supplierCandidates = [supplierOverride, supplierRaw, ...supplierOverridesList];
      if (
        selectedSupplier &&
        !supplierCandidates.some((v) => String(v || '').toLowerCase() === selectedSupplier)
      ) {
        return false;
      }
      if (rulesCategoryFilter !== 'all' && String(categoryRaw || '').toLowerCase() !== rulesCategoryFilter.toLowerCase()) {
        return false;
      }
      if (rulesFrequencyFilter !== 'all' && String(recurrenceRaw || '').toLowerCase() !== rulesFrequencyFilter) {
        return false;
      }
      if (rulesTextFilter.trim()) {
        const hayParts = [
          row.id ?? '',
          classificationProposalStableIdLabel(row),
          row.source,
          row.status,
          row.proposed_name,
          categoryRaw,
          supplierOverride,
          ...supplierOverridesList,
          supplierRaw,
          row.reference_contains ?? (p.reference_contains as string) ?? '',
          descRaw,
          recurrenceRaw,
          row.priority ?? (p.priority as number) ?? '',
          row.notes ?? (p.notes as string) ?? '',
          p.amount_min ?? '',
          p.amount_max ?? '',
          p.currency ?? '',
          p.vat_rate ?? '',
          (p.text_match_operator as string) || '',
          (p.description_text_match_operator as string) || '',
          (p.reference_text_match_operator as string) || '',
          normalizeRuleAccountScope((p.account_scope as string) || undefined)
        ];
        if (!rulesSearchNeedleMatchesHay(hayParts, rulesTextFilter)) return false;
      }
      return true;
    });
  }, [
    proposalsPayload,
    rulesSupplierFilter,
    rulesTextFilter,
    rulesFrequencyFilter,
    rulesCategoryFilter,
    parsedRulesProposalIdFilter,
    parsedRulesIdFilter
  ]);

  const filterStep2bSupplierProposals = useCallback(
    (rows: SupplierRuleProposalRow[]) => {
      const selectedSupplier = rulesSupplierFilter === 'all' ? '' : rulesSupplierFilter.trim().toLowerCase();
      return (rows || []).filter((row) => {
        if (parsedRulesProposalIdFilter) {
          const { supplierProposalIds, atlasSeqs } = parsedRulesProposalIdFilter;
          const atlasOnly = atlasSeqs.size > 0 && supplierProposalIds.size === 0;
          if (atlasOnly) {
            return false;
          }
          if (supplierProposalIds.size > 0) {
            const pid = Number(row.id);
            const isDb = Number.isFinite(pid) && pid > 0;
            if (!isDb || !supplierProposalIds.has(pid)) return false;
          }
        }
        const p = row.payload || {};
        // Pipeline Step 2B: exclude merchant-sourced proposals (owned by 2A sync)
        if (
          isMerchantSourcedSupplierRuleLike({
            match_criteria: Array.isArray(p.match_criteria)
              ? (p.match_criteria as Array<{ kind?: string | null; value?: string | null }>)
              : null,
            pattern_raw: String((p.pattern_raw as string) || ''),
            description_contains: String((p.description_contains as string) || ''),
            reference_contains: String((p.reference_contains as string) || '')
          })
        ) {
          return false;
        }
        const supplierRaw = String((p.canonical_name as string) || '').trim();
        const patternRaw = String((p.pattern_raw as string) || '').trim();
        if (selectedSupplier && supplierRaw.toLowerCase() !== selectedSupplier) return false;
        if (rulesTextFilter.trim()) {
          const hayParts = [
            row.id ?? '',
            formatSupplierProposalPrefixedId(
              typeof row.id === 'number' ? row.id : Number(row.id) || 0,
              proposalRowDisplayStatus(row)
            ),
            row.source,
            row.status,
            row.proposed_name,
            String((p.rule_name as string) || ''),
            supplierRaw,
            patternRaw,
            String((p.text_match_operator as string) || 'exact'),
            String((p.notes as string) || ''),
            row.revolut_transaction_id ?? '',
            normalizeRuleAccountScope((p.account_scope as string) || undefined)
          ];
          if (!rulesSearchNeedleMatchesHay(hayParts, rulesTextFilter)) return false;
        }
        return true;
      });
    },
    [parsedRulesProposalIdFilter, rulesSupplierFilter, rulesTextFilter]
  );

  const filteredSupplierRuleProposals = useMemo(
    () => filterStep2bSupplierProposals(supplierProposalsPayload?.proposals || []),
    [filterStep2bSupplierProposals, supplierProposalsPayload]
  );

  const filteredRejectedSupplierRuleProposals = useMemo(
    () => filterStep2bSupplierProposals(rejectedSupplierProposalsPayload?.proposals || []),
    [filterStep2bSupplierProposals, rejectedSupplierProposalsPayload]
  );

  const selectableRejectedSupplierProposalIds = useMemo(() => {
    const ids: number[] = [];
    for (const row of filteredRejectedSupplierRuleProposals) {
      const pid = supplierRuleProposalDbId(row);
      if (pid != null) ids.push(pid);
    }
    return ids;
  }, [filteredRejectedSupplierRuleProposals]);

  useEffect(() => {
    const allowed = new Set(selectableRejectedSupplierProposalIds);
    setSelectedRejectedSupplierProposalIds((prev) => {
      if (prev.length === 0) return prev;
      const next = prev.filter((id) => allowed.has(id));
      return next.length === prev.length ? prev : next;
    });
  }, [selectableRejectedSupplierProposalIds]);

  const filteredAppRules = useMemo(() => {
    const selectedSupplier = rulesSupplierFilter === 'all' ? '' : rulesSupplierFilter.trim().toLowerCase();
    return sortedRules.filter((rule) => {
      if (!matchesParsedRulesIdFilter(parsedRulesIdFilter, 'classification', rule.id)) return false;
      if (selectedSupplier) {
        const displayLower = classificationRuleSupplierDisplay(rule).toLowerCase();
        const overrideMatch = supplierOverridesFromClassificationRule(rule).some(
          (n) => n.trim().toLowerCase() === selectedSupplier
        );
        if (!overrideMatch && displayLower !== selectedSupplier) {
          return false;
        }
      }
      if (rulesCategoryFilter !== 'all' && (rule.category?.name || '').toLowerCase() !== rulesCategoryFilter.toLowerCase()) {
        return false;
      }
      if (rulesFrequencyFilter !== 'all' && (rule.recurrence_type || '').toLowerCase() !== rulesFrequencyFilter) {
        return false;
      }
      if (rulesTextFilter.trim()) {
        const hayParts = [
          rule.id,
          formatClassificationRulePrefixedId(rule.id, rule.name),
          formatPrefixedRuleId('classification', rule.id),
          rule.name,
          rule.priority,
          rule.is_active ? 'active' : 'inactive',
          rule.category?.name || `category #${rule.category_id}`,
          classificationRuleSupplierDisplay(rule),
          classificationRuleCanonicalSupplierDisplay(rule),
          rule.supplier_canonical_name || '',
          rule.supplier_contains || '',
          rule.supplier_name_override || '',
          ...(Array.isArray(rule.supplier_name_overrides) ? rule.supplier_name_overrides : []),
          rule.description_contains || '',
          rule.reference_contains || '',
          rule.text_match_operator || '',
          rule.description_text_match_operator || '',
          rule.reference_text_match_operator || '',
          rule.notes || '',
          rule.amount_min ?? '',
          rule.amount_max ?? '',
          rule.currency || '',
          rule.recurrence_type || '',
          rule.vat_rate || '',
          rule.confidence_score ?? '',
          normalizeRuleAccountScope(rule.account_scope)
        ];
        if (!rulesSearchNeedleMatchesHay(hayParts, rulesTextFilter)) return false;
      }
      return true;
    });
  }, [sortedRules, rulesSupplierFilter, rulesTextFilter, rulesFrequencyFilter, rulesCategoryFilter, parsedRulesIdFilter]);

  const filteredSupplierRules = useMemo(() => {
    const selectedSupplier = rulesSupplierFilter === 'all' ? '' : rulesSupplierFilter.trim().toLowerCase();
    return supplierRuleRows.filter((row) => {
      // Pipeline Step 2B: non-merchant aliases only
      if (isMerchantSourcedSupplierRuleLike(row)) return false;
      if (!matchesParsedRulesIdFilter(parsedRulesIdFilter, 'supplier', row.id)) return false;
      const supplierName = canonicalSupplierLabel(row.supplier_name, `supplier #${row.supplier_id}`);
      if (selectedSupplier && supplierName.toLowerCase() !== selectedSupplier) return false;
      if (rulesTextFilter.trim()) {
        const hayParts = [
          row.id,
          formatPrefixedRuleId('supplier', row.id),
          row.rule_name || '',
          row.pattern_raw,
          row.pattern_normalized,
          supplierName,
          row.supplier_id,
          row.description_contains || '',
          row.description_text_match_operator || '',
          row.reference_contains || '',
          row.reference_text_match_operator || '',
          row.text_match_operator || '',
          classificationRuleTextOperatorLabel(row.text_match_operator || 'exact'),
          classificationRuleTextOperatorLabel(row.description_text_match_operator || row.text_match_operator || 'exact'),
          classificationRuleTextOperatorLabel(row.reference_text_match_operator || row.text_match_operator || 'exact'),
          normalizeRuleAccountScope(row.account_scope),
          row.is_active === false ? 'inactive' : 'active'
        ];
        if (!rulesSearchNeedleMatchesHay(hayParts, rulesTextFilter)) return false;
      }
      return true;
    });
  }, [supplierRuleRows, rulesSupplierFilter, rulesTextFilter, parsedRulesIdFilter]);

  const filteredMerchantSupplierRules = useMemo(() => {
    const selectedSupplier = rulesSupplierFilter === 'all' ? '' : rulesSupplierFilter.trim().toLowerCase();
    return supplierRuleRows.filter((row) => {
      if (!isMerchantSourcedSupplierRuleLike(row)) return false;
      if (!matchesParsedRulesIdFilter(parsedRulesIdFilter, 'supplier', row.id)) return false;
      const supplierName = canonicalSupplierLabel(row.supplier_name, `supplier #${row.supplier_id}`);
      if (selectedSupplier && supplierName.toLowerCase() !== selectedSupplier) return false;
      if (rulesTextFilter.trim()) {
        const hayParts = [
          row.id,
          formatPrefixedRuleId('supplier', row.id),
          row.rule_name || '',
          row.pattern_raw,
          row.pattern_normalized,
          supplierName,
          row.supplier_id,
          row.text_match_operator || '',
          classificationRuleTextOperatorLabel(row.text_match_operator || 'exact'),
          normalizeRuleAccountScope(row.account_scope),
          row.is_active === false ? 'inactive' : 'active'
        ];
        if (!rulesSearchNeedleMatchesHay(hayParts, rulesTextFilter)) return false;
      }
      return true;
    });
  }, [supplierRuleRows, rulesSupplierFilter, rulesTextFilter, parsedRulesIdFilter]);

  const visibleMerchantSupplierRuleRows = useMemo(() => {
    if (rulesStatusFilter === 'proposed') return [] as SupplierRuleRow[];
    return filteredMerchantSupplierRules;
  }, [filteredMerchantSupplierRules, rulesStatusFilter]);

  const sortedVisibleMerchantSupplierRuleRows = useMemo(() => {
    return [...visibleMerchantSupplierRuleRows].sort((a, b) => {
      const ac = canonicalSupplierLabel(a.supplier_name, `supplier #${a.supplier_id}`).toLowerCase();
      const bc = canonicalSupplierLabel(b.supplier_name, `supplier #${b.supplier_id}`).toLowerCase();
      if (ac !== bc) return ac < bc ? -1 : 1;
      return String(a.pattern_raw || '').localeCompare(String(b.pattern_raw || ''));
    });
  }, [visibleMerchantSupplierRuleRows]);

  const filteredVatRules = useMemo(() => {
    return vatRuleRows.filter((row) => {
      if (!matchesParsedRulesIdFilter(parsedRulesIdFilter, 'vat', row.id)) return false;
      const categoryNames = vatRuleCategoryNames(row);
      const categoryNameJoined = categoryNames.join(', ');
      if (
        rulesCategoryFilter !== 'all' &&
        !categoryNames.some((name) => name.toLowerCase() === rulesCategoryFilter.toLowerCase())
      ) {
        return false;
      }
      if (rulesTextFilter.trim()) {
        const hayParts = [
          row.id,
          formatPrefixedRuleId('vat', row.id),
          row.name || '',
          categoryNameJoined,
          row.category_id,
          ...(Array.isArray(row.category_ids) ? row.category_ids : []),
          row.vat_rate || '',
          normalizeRuleAccountScope(row.account_scope),
          row.is_active === false ? 'inactive' : 'active'
        ];
        if (!rulesSearchNeedleMatchesHay(hayParts, rulesTextFilter)) return false;
      }
      return true;
    });
  }, [vatRuleRows, rulesTextFilter, rulesCategoryFilter, parsedRulesIdFilter]);

  const filteredVatRuleSuggestions = useMemo(() => {
    return vatRuleSuggestions.filter((row) => {
      if (row.status !== 'pending') return false;
      const categoryName = String(row.category_name || row.category?.name || '').trim();
      if (
        rulesCategoryFilter !== 'all' &&
        categoryName &&
        categoryName.toLowerCase() !== rulesCategoryFilter.toLowerCase()
      ) {
        return false;
      }
      if (rulesTextFilter.trim()) {
        const hayParts = [
          row.id,
          formatPrefixedRuleId('vat', row.id, 'proposed'),
          categoryName,
          row.suggested_vat_rate || '',
          row.suggested_band || '',
          normalizeRuleAccountScope(row.account_scope),
          row.rationale?.explanation || '',
          row.rationale?.rule_key || ''
        ];
        if (!rulesSearchNeedleMatchesHay(hayParts, rulesTextFilter)) return false;
      }
      return true;
    });
  }, [vatRuleSuggestions, rulesTextFilter, rulesCategoryFilter]);

  const filteredRejectedVatRuleSuggestions = useMemo(() => {
    return rejectedVatRuleSuggestions.filter((row) => {
      const categoryName = String(row.category_name || row.category?.name || '').trim();
      if (
        rulesCategoryFilter !== 'all' &&
        categoryName &&
        categoryName.toLowerCase() !== rulesCategoryFilter.toLowerCase()
      ) {
        return false;
      }
      if (rulesTextFilter.trim()) {
        const hayParts = [
          row.id,
          formatPrefixedRuleId('vat', row.id, 'rejected'),
          categoryName,
          row.suggested_vat_rate || '',
          row.suggested_band || '',
          normalizeRuleAccountScope(row.account_scope),
          row.rationale?.explanation || '',
          row.rationale?.rule_key || '',
          'rejected'
        ];
        if (!rulesSearchNeedleMatchesHay(hayParts, rulesTextFilter)) return false;
      }
      return true;
    });
  }, [rejectedVatRuleSuggestions, rulesTextFilter, rulesCategoryFilter]);

  const filteredRejectedClassificationProposals = useMemo(() => {
    const selectedSupplier = rulesSupplierFilter === 'all' ? '' : rulesSupplierFilter.trim().toLowerCase();
    return (rejectedProposalsPayload?.proposals || []).filter((row) => {
      const p = row.payload || {};
      const supplierRaw = row.supplier_contains ?? (p.supplier_contains as string) ?? '';
      const supplierOverride = (p.supplier_name_override as string) || row.supplier_name_override || '';
      const supplierOverridesList = Array.isArray(p.supplier_name_overrides)
        ? (p.supplier_name_overrides as unknown[]).map((x) => String(x ?? ''))
        : [];
      const categoryRaw = row.category_name || (p.category_name as string) || (p.category as string) || '';
      const descRaw =
        row.description_contains ??
        row.reference_contains ??
        (p.description_contains as string) ??
        (p.reference_contains as string) ??
        '';
      const supplierCandidates = [supplierOverride, supplierRaw, ...supplierOverridesList];
      if (
        selectedSupplier &&
        !supplierCandidates.some((v) => String(v || '').toLowerCase() === selectedSupplier)
      ) {
        return false;
      }
      if (
        rulesCategoryFilter !== 'all' &&
        String(categoryRaw || '').toLowerCase() !== rulesCategoryFilter.toLowerCase()
      ) {
        return false;
      }
      if (rulesTextFilter.trim()) {
        const hayParts = [
          row.id ?? '',
          classificationProposalStableIdLabel(row),
          row.source,
          row.status,
          row.proposed_name,
          row.atlas_key || '',
          categoryRaw,
          supplierOverride,
          ...supplierOverridesList,
          supplierRaw,
          descRaw,
          row.notes ?? (p.notes as string) ?? '',
          normalizeRuleAccountScope((p.account_scope as string) || undefined),
          'rejected'
        ];
        if (!rulesSearchNeedleMatchesHay(hayParts, rulesTextFilter)) return false;
      }
      return true;
    });
  }, [
    rejectedProposalsPayload,
    rulesSupplierFilter,
    rulesTextFilter,
    rulesCategoryFilter
  ]);

  const selectableRejectedClassificationProposalIds = useMemo(() => {
    const ids: number[] = [];
    for (const row of filteredRejectedClassificationProposals) {
      const pid = row.id != null ? Number(row.id) : NaN;
      if (Number.isFinite(pid) && pid > 0) ids.push(Math.trunc(pid));
    }
    return ids;
  }, [filteredRejectedClassificationProposals]);

  const selectableRejectedVatSuggestionIds = useMemo(
    () =>
      filteredRejectedVatRuleSuggestions
        .map((r) => Number(r.id))
        .filter((id) => Number.isFinite(id) && id > 0),
    [filteredRejectedVatRuleSuggestions]
  );

  const filteredSpecialCaseRules = useMemo(() => {
    return specialCaseRuleRows.filter((row) => {
      if (!matchesParsedRulesIdFilter(parsedRulesIdFilter, 'specialCase', row.id)) return false;
      const categoryName = (row.category?.name || '').trim();
      if (
        rulesCategoryFilter !== 'all' &&
        categoryName &&
        categoryName.toLowerCase() !== rulesCategoryFilter.toLowerCase()
      ) {
        return false;
      }
      if (rulesTextFilter.trim()) {
        const hayParts = [
          row.id,
          formatPrefixedRuleId('specialCase', row.id),
          row.name,
          row.priority,
          row.is_active === false ? 'inactive' : 'active',
          categoryName,
          row.supplier_contains || '',
          row.description_contains || '',
          row.description_text_match_operator || '',
          row.reference_contains || '',
          row.reference_text_match_operator || '',
          row.current_vat_rate || '',
          row.set_vat_rate || '',
          row.notes || '',
          normalizeRuleAccountScope(row.account_scope),
          row.text_match_operator || '',
          classificationRuleTextOperatorLabel(row.text_match_operator || 'contains')
        ];
        if (!rulesSearchNeedleMatchesHay(hayParts, rulesTextFilter)) return false;
      }
      return true;
    });
  }, [specialCaseRuleRows, rulesTextFilter, rulesCategoryFilter, parsedRulesIdFilter]);

  const sortedSupplierRules = useMemo(() => {
    const rows = [...filteredSupplierRules];
    const mul = supplierRulesSort.dir === 'asc' ? 1 : -1;
    rows.sort((a, b) => {
      const canonicalA = canonicalSupplierLabel(a.supplier_name, `Supplier #${a.supplier_id}`).toLowerCase();
      const canonicalB = canonicalSupplierLabel(b.supplier_name, `Supplier #${b.supplier_id}`).toLowerCase();
      const patternA = (a.pattern_raw || '').toLowerCase();
      const patternB = (b.pattern_raw || '').toLowerCase();
      const cmp =
        supplierRulesSort.key === 'canonical'
          ? compareStrings(canonicalA, canonicalB)
          : compareStrings(patternA, patternB);
      if (cmp !== 0) return cmp * mul;
      return a.id - b.id;
    });
    return rows;
  }, [filteredSupplierRules, supplierRulesSort]);

  const mergedClassificationRuleRows = useMemo(
    () => [
      ...filteredRuleProposals.map((row, idx) => ({
        key: `cp-${row.id ?? 'v'}-${idx}`,
        status: 'proposed' as const,
        proposal: row,
        rule: null as ClassificationRule | null
      })),
      ...filteredAppRules.map((rule) => ({
        key: `cc-${rule.id}`,
        status: 'confirmed' as const,
        proposal: null as RuleProposalRow | null,
        rule
      }))
    ],
    [filteredRuleProposals, filteredAppRules]
  );

  const visibleClassificationRuleRows = useMemo(
    () =>
      rulesStatusFilter === 'all'
        ? mergedClassificationRuleRows
        : mergedClassificationRuleRows.filter((r) => r.status === rulesStatusFilter),
    [mergedClassificationRuleRows, rulesStatusFilter]
  );

  /** VAT rules that apply to any output category shown in the Classification table (incl. when VAT text search would exclude them). */
  const vatRulesLinkedToVisibleClassification = useMemo(() => {
    const catIds = new Set<number>();
    for (const r of visibleClassificationRuleRows) {
      if (r.status === 'confirmed' && r.rule) {
        const id = Number(r.rule.category_id);
        if (Number.isFinite(id) && id > 0) catIds.add(id);
      } else if (r.status === 'proposed' && r.proposal) {
        const cid = classificationProposalOutputCategoryId(r.proposal, categoriesSortedByName);
        if (cid != null) catIds.add(cid);
      }
    }
    if (catIds.size === 0) return [];

    const bypassVatId = linkedVatRulesBypassVatIdFilter(parsedRulesIdFilter);
    const catIdList = Array.from(catIds);
    const seen = new Set<number>();
    const out: CategoryVatRuleRow[] = [];

    for (const vr of vatRuleRows) {
      if (!bypassVatId && !matchesParsedRulesIdFilter(parsedRulesIdFilter, 'vat', vr.id)) continue;

      const categoryNames = vatRuleCategoryNames(vr);
      if (
        rulesCategoryFilter !== 'all' &&
        !categoryNames.some((name) => name.toLowerCase() === rulesCategoryFilter.toLowerCase())
      ) {
        continue;
      }

      let applies = false;
      for (const cid of catIdList) {
        if (vatRuleAppliesToCategoryId(vr, cid)) {
          applies = true;
          break;
        }
      }
      if (!applies) continue;

      if (!seen.has(vr.id)) {
        seen.add(vr.id);
        out.push(vr);
      }
    }
    out.sort((a, b) => a.id - b.id);
    return out;
  }, [
    visibleClassificationRuleRows,
    vatRuleRows,
    categoriesSortedByName,
    rulesCategoryFilter,
    parsedRulesIdFilter
  ]);

  const mergedSupplierRuleRows = useMemo(
    () => [
      ...filteredSupplierRuleProposals.map((row, idx) => ({
        key: `sp-${row.id ?? 'x'}-${idx}`,
        status: 'proposed' as const,
        proposal: row,
        alias: null as SupplierRuleRow | null
      })),
      ...sortedSupplierRules.map((alias) => ({
        key: `sc-${alias.id}`,
        status: 'confirmed' as const,
        proposal: null as SupplierRuleProposalRow | null,
        alias
      }))
    ],
    [filteredSupplierRuleProposals, sortedSupplierRules]
  );

  const visibleSupplierRuleRows = useMemo(
    () =>
      rulesStatusFilter === 'all'
        ? mergedSupplierRuleRows
        : mergedSupplierRuleRows.filter((r) => r.status === rulesStatusFilter),
    [mergedSupplierRuleRows, rulesStatusFilter]
  );

  const sortedVisibleClassificationRuleRows = useMemo(() => {
    if (!classificationRulesTableSort?.key) return visibleClassificationRuleRows;
    const rows = [...visibleClassificationRuleRows];
    const mul = classificationRulesTableSort.dir === 'asc' ? 1 : -1;
    rows.sort((a, b) => {
      const key = classificationRulesTableSort.key;
      const aProposal = a.proposal;
      const bProposal = b.proposal;
      const aPayload = (aProposal?.payload || {}) as Record<string, unknown>;
      const bPayload = (bProposal?.payload || {}) as Record<string, unknown>;
      const aRule = a.rule;
      const bRule = b.rule;
      let cmp = 0;
      switch (key) {
        case 'status':
          cmp = compareStrings(a.status, b.status);
          break;
        case 'id':
          cmp =
            (aRule?.id ?? (aProposal ? proposalNumericSortId(aProposal) : 0)) -
            (bRule?.id ?? (bProposal ? proposalNumericSortId(bProposal) : 0));
          break;
        case 'name':
          cmp = compareStrings(
            (aProposal?.proposed_name || aRule?.name || '').toLowerCase(),
            (bProposal?.proposed_name || bRule?.name || '').toLowerCase()
          );
          break;
        case 'category': {
          const aCategory = (
            aProposal?.category_name ||
            String((aPayload.category_name as string) || (aPayload.category as string) || '') ||
            aRule?.category?.name ||
            (aRule ? `Category #${aRule.category_id}` : '')
          ).toLowerCase();
          const bCategory = (
            bProposal?.category_name ||
            String((bPayload.category_name as string) || (bPayload.category as string) || '') ||
            bRule?.category?.name ||
            (bRule ? `Category #${bRule.category_id}` : '')
          ).toLowerCase();
          cmp = compareStrings(aCategory, bCategory);
          break;
        }
        case 'account':
          cmp = compareStrings(
            ruleAccountScopeLabel(
              aRule?.account_scope || String((aPayload.account_scope as string) || 'both')
            ).toLowerCase(),
            ruleAccountScopeLabel(
              bRule?.account_scope || String((bPayload.account_scope as string) || 'both')
            ).toLowerCase()
          );
          break;
        case 'supplier':
          cmp = compareStrings(
            (aRule
              ? classificationRuleSupplierDisplay(aRule)
              : String((aProposal?.supplier_contains as string) || (aPayload.supplier_contains as string) || '')
            ).toLowerCase(),
            (bRule
              ? classificationRuleSupplierDisplay(bRule)
              : String((bProposal?.supplier_contains as string) || (bPayload.supplier_contains as string) || '')
            ).toLowerCase()
          );
          break;
        case 'canonicalSupplier':
          cmp = compareStrings(
            (aRule
              ? classificationRuleCanonicalSupplierDisplay(aRule)
              : (aProposal ? classificationProposalCanonicalSupplierDisplay(aProposal) : '—')
            ).toLowerCase(),
            (bRule
              ? classificationRuleCanonicalSupplierDisplay(bRule)
              : (bProposal ? classificationProposalCanonicalSupplierDisplay(bProposal) : '—')
            ).toLowerCase()
          );
          break;
        case 'description':
          cmp = compareStrings(
            (aRule
              ? aRule.description_contains || aRule.reference_contains || ''
              : String((aProposal?.description_contains as string) || (aPayload.description_contains as string) || '')
            ).toLowerCase(),
            (bRule
              ? bRule.description_contains || bRule.reference_contains || ''
              : String((bProposal?.description_contains as string) || (bPayload.description_contains as string) || '')
            ).toLowerCase()
          );
          break;
        case 'textMatch':
          cmp = compareStrings(
            classificationRuleTextOperatorLabel(
              aRule?.text_match_operator || String((aPayload.text_match_operator as string) || 'exact')
            ).toLowerCase(),
            classificationRuleTextOperatorLabel(
              bRule?.text_match_operator || String((bPayload.text_match_operator as string) || 'exact')
            ).toLowerCase()
          );
          break;
        case 'priority':
          cmp = (aRule?.priority ?? (aProposal?.priority ?? Number(aPayload.priority ?? 0))) -
            (bRule?.priority ?? (bProposal?.priority ?? Number(bPayload.priority ?? 0)));
          break;
        case 'firedCount':
          cmp =
            Number(aRule ? (ruleFireCounts.classification[aRule.id] || 0) : 0) -
            Number(bRule ? (ruleFireCounts.classification[bRule.id] || 0) : 0);
          break;
        case 'active':
          cmp = Number(!!aRule?.is_active) - Number(!!bRule?.is_active);
          break;
        case 'otherCriteria': {
          const aYes = aRule
            ? classificationRuleHasOtherCriteria(aRule)
            : aProposal
              ? classificationProposalHasOtherCriteria(aProposal)
              : false;
          const bYes = bRule
            ? classificationRuleHasOtherCriteria(bRule)
            : bProposal
              ? classificationProposalHasOtherCriteria(bProposal)
              : false;
          cmp = Number(aYes) - Number(bYes);
          break;
        }
        default:
          cmp = 0;
      }
      if (cmp !== 0) return cmp * mul;
      return compareStrings(a.key, b.key);
    });
    return rows;
  }, [classificationRulesTableSort, ruleFireCounts.classification, visibleClassificationRuleRows]);

  const sortedVisibleClassificationConfirmedRows = useMemo(
    () => sortedVisibleClassificationRuleRows.filter((r) => r.status === 'confirmed'),
    [sortedVisibleClassificationRuleRows]
  );

  const pipelineClassificationConfirmedRows = useMemo(
    () =>
      sortedVisibleClassificationConfirmedRows.filter((entry) =>
        isPipelineSystemHeadingRuleName(entry.rule?.name || '')
      ),
    [sortedVisibleClassificationConfirmedRows]
  );

  const sortedVisibleClassificationConfirmedRowsExcludingPipeline = useMemo(
    () =>
      sortedVisibleClassificationConfirmedRows.filter(
        (entry) => !isPipelineSystemHeadingRuleName(entry.rule?.name || '')
      ),
    [sortedVisibleClassificationConfirmedRows]
  );

  const sortedVisibleClassificationProposedRows = useMemo(
    () => sortedVisibleClassificationRuleRows.filter((r) => r.status === 'proposed'),
    [sortedVisibleClassificationRuleRows]
  );

  const sortedVisibleSupplierRuleRows = useMemo(() => {
    if (!supplierRulesTableSort?.key) return visibleSupplierRuleRows;
    const rows = [...visibleSupplierRuleRows];
    const mul = supplierRulesTableSort.dir === 'asc' ? 1 : -1;
    rows.sort((a, b) => {
      const key = supplierRulesTableSort.key;
      const aProposal = a.proposal;
      const bProposal = b.proposal;
      const aPayload = (aProposal?.payload || {}) as Record<string, unknown>;
      const bPayload = (bProposal?.payload || {}) as Record<string, unknown>;
      const aAlias = a.alias;
      const bAlias = b.alias;
      const aProposalId = aProposal ? supplierRuleProposalDbId(aProposal) : null;
      const bProposalId = bProposal ? supplierRuleProposalDbId(bProposal) : null;
      let cmp = 0;
      switch (key) {
        case 'status':
          cmp = compareStrings(a.status, b.status);
          break;
        case 'id':
          cmp = (aAlias?.id ?? aProposalId ?? 0) - (bAlias?.id ?? bProposalId ?? 0);
          break;
        case 'name':
          cmp = compareStrings(
            (aAlias?.rule_name || String((aPayload.rule_name as string) || aProposal?.proposed_name || '')).toLowerCase(),
            (bAlias?.rule_name || String((bPayload.rule_name as string) || bProposal?.proposed_name || '')).toLowerCase()
          );
          break;
        case 'canonical':
          cmp = compareStrings(
            (aAlias
              ? canonicalSupplierLabel(aAlias.supplier_name, `Supplier #${aAlias.supplier_id}`)
              : String((aPayload.canonical_name as string) || '')
            ).toLowerCase(),
            (bAlias
              ? canonicalSupplierLabel(bAlias.supplier_name, `Supplier #${bAlias.supplier_id}`)
              : String((bPayload.canonical_name as string) || '')
            ).toLowerCase()
          );
          break;
        case 'supplierMatch':
          {
            const aCriteria = aAlias
              ? supplierRuleCriteriaFromAliasForDisplay(aAlias)
              : supplierRuleCriteriaFromProposalPayloadForDisplay(aPayload);
            const bCriteria = bAlias
              ? supplierRuleCriteriaFromAliasForDisplay(bAlias)
              : supplierRuleCriteriaFromProposalPayloadForDisplay(bPayload);
          cmp = compareStrings(
              supplierRuleCriteriaSortValue(aCriteria, 'merchant').toLowerCase(),
              supplierRuleCriteriaSortValue(bCriteria, 'merchant').toLowerCase()
          );
          }
          break;
        case 'descriptionMatch':
          {
            const aCriteria = aAlias
              ? supplierRuleCriteriaFromAliasForDisplay(aAlias)
              : supplierRuleCriteriaFromProposalPayloadForDisplay(aPayload);
            const bCriteria = bAlias
              ? supplierRuleCriteriaFromAliasForDisplay(bAlias)
              : supplierRuleCriteriaFromProposalPayloadForDisplay(bPayload);
          cmp = compareStrings(
              supplierRuleCriteriaSortValue(aCriteria, 'description').toLowerCase(),
              supplierRuleCriteriaSortValue(bCriteria, 'description').toLowerCase()
          );
          }
          break;
        case 'referenceMatch':
          {
            const aCriteria = aAlias
              ? supplierRuleCriteriaFromAliasForDisplay(aAlias)
              : supplierRuleCriteriaFromProposalPayloadForDisplay(aPayload);
            const bCriteria = bAlias
              ? supplierRuleCriteriaFromAliasForDisplay(bAlias)
              : supplierRuleCriteriaFromProposalPayloadForDisplay(bPayload);
          cmp = compareStrings(
              supplierRuleCriteriaSortValue(aCriteria, 'reference').toLowerCase(),
              supplierRuleCriteriaSortValue(bCriteria, 'reference').toLowerCase()
          );
          }
          break;
        case 'account':
          cmp = compareStrings(
            ruleAccountScopeLabel(
              aAlias?.account_scope || String((aPayload.account_scope as string) || 'both')
            ).toLowerCase(),
            ruleAccountScopeLabel(
              bAlias?.account_scope || String((bPayload.account_scope as string) || 'both')
            ).toLowerCase()
          );
          break;
        case 'active':
          cmp =
            Number(aAlias ? (aAlias.is_active !== false) : (aPayload.is_active !== false)) -
            Number(bAlias ? (bAlias.is_active !== false) : (bPayload.is_active !== false));
          break;
        case 'priority':
          cmp =
            Number(aAlias?.priority ?? aPayload.priority ?? 100) -
            Number(bAlias?.priority ?? bPayload.priority ?? 100);
          break;
        case 'firedCount':
          cmp =
            Number(aAlias ? (ruleFireCounts.supplier[aAlias.id] || 0) : 0) -
            Number(bAlias ? (ruleFireCounts.supplier[bAlias.id] || 0) : 0);
          break;
        case 'pattern':
          cmp = compareStrings(
            (aAlias?.pattern_raw || String((aPayload.pattern_raw as string) || '')).toLowerCase(),
            (bAlias?.pattern_raw || String((bPayload.pattern_raw as string) || '')).toLowerCase()
          );
          break;
        default:
          cmp = 0;
      }
      if (cmp !== 0) return cmp * mul;
      return compareStrings(a.key, b.key);
    });
    return rows;
  }, [ruleFireCounts.supplier, supplierRulesTableSort, visibleSupplierRuleRows]);

  const sortedVisibleSupplierConfirmedRows = useMemo(
    () => sortedVisibleSupplierRuleRows.filter((r) => r.status === 'confirmed'),
    [sortedVisibleSupplierRuleRows]
  );

  const sortedVisibleSupplierProposedRows = useMemo(
    () => sortedVisibleSupplierRuleRows.filter((r) => r.status === 'proposed'),
    [sortedVisibleSupplierRuleRows]
  );

  const visibleClassificationRuleCounts = useMemo(() => {
    let proposed = 0;
    let confirmed = 0;
    visibleClassificationRuleRows.forEach((r) => {
      if (r.status === 'proposed') proposed += 1;
      else confirmed += 1;
    });
    return {
      proposed,
      confirmed,
      rejected: filteredRejectedClassificationProposals.length,
    };
  }, [visibleClassificationRuleRows, filteredRejectedClassificationProposals.length]);

  const visibleSupplierRuleCounts = useMemo(() => {
    let proposed = 0;
    let confirmed = 0;
    visibleSupplierRuleRows.forEach((r) => {
      if (r.status === 'proposed') proposed += 1;
      else confirmed += 1;
    });
    return {
      proposed,
      confirmed,
      rejected: filteredRejectedSupplierRuleProposals.length,
    };
  }, [visibleSupplierRuleRows, filteredRejectedSupplierRuleProposals.length]);

  const visibleVatRules = useMemo(() => {
    if (rulesStatusFilter === 'proposed') return [];
    const seen = new Set<number>();
    const merged: CategoryVatRuleRow[] = [];
    for (const r of filteredVatRules) {
      if (!seen.has(r.id)) {
        seen.add(r.id);
        merged.push(r);
      }
    }
    for (const r of vatRulesLinkedToVisibleClassification) {
      if (!seen.has(r.id)) {
        seen.add(r.id);
        merged.push(r);
      }
    }
    return merged;
  }, [filteredVatRules, vatRulesLinkedToVisibleClassification, rulesStatusFilter]);

  const visibleVatRuleSuggestions = useMemo(
    () => (rulesStatusFilter === 'confirmed' ? [] : filteredVatRuleSuggestions),
    [filteredVatRuleSuggestions, rulesStatusFilter]
  );

  useEffect(() => {
    setSelectedVatSuggestionIds((prev) =>
      prev.filter((id) => visibleVatRuleSuggestions.some((r) => r.id === id))
    );
  }, [visibleVatRuleSuggestions]);

  useEffect(() => {
    setSelectedRejectedClassificationProposalIds((prev) =>
      prev.filter((id) => selectableRejectedClassificationProposalIds.includes(id))
    );
  }, [selectableRejectedClassificationProposalIds]);

  useEffect(() => {
    setSelectedRejectedVatSuggestionIds((prev) =>
      prev.filter((id) => selectableRejectedVatSuggestionIds.includes(id))
    );
  }, [selectableRejectedVatSuggestionIds]);

  const visibleVatRuleCounts = useMemo(
    () => ({
      confirmed: visibleVatRules.length,
      proposed: visibleVatRuleSuggestions.length,
      rejected: filteredRejectedVatRuleSuggestions.length,
    }),
    [visibleVatRules.length, visibleVatRuleSuggestions.length, filteredRejectedVatRuleSuggestions.length]
  );

  const visibleSpecialCaseRules = useMemo(
    () => (rulesStatusFilter === 'proposed' ? [] : filteredSpecialCaseRules),
    [filteredSpecialCaseRules, rulesStatusFilter]
  );

  const sortedVisibleVatRules = useMemo(() => {
    if (!vatRulesTableSort?.key) return visibleVatRules;
    const rows = [...visibleVatRules];
    const mul = vatRulesTableSort.dir === 'asc' ? 1 : -1;
    rows.sort((a, b) => {
      let cmp = 0;
      switch (vatRulesTableSort.key) {
        case 'status':
          cmp = 0;
          break;
        case 'id':
          cmp = Number(a.id || 0) - Number(b.id || 0);
          break;
        case 'priority':
          cmp = Number(a.priority ?? 100) - Number(b.priority ?? 100);
          break;
        case 'name':
          cmp = compareStrings(String(a.name || '').toLowerCase(), String(b.name || '').toLowerCase());
          break;
        case 'category':
          cmp = compareStrings(
            vatRuleCategoryNames(a).join(', ').toLowerCase(),
            vatRuleCategoryNames(b).join(', ').toLowerCase()
          );
          break;
        case 'vat':
          cmp = compareStrings(String(a.vat_rate || '').toLowerCase(), String(b.vat_rate || '').toLowerCase());
          break;
        case 'account':
          cmp = compareStrings(
            ruleAccountScopeLabel(a.account_scope).toLowerCase(),
            ruleAccountScopeLabel(b.account_scope).toLowerCase()
          );
          break;
        case 'firedCount':
          cmp = Number(ruleFireCounts.vat[a.id] || 0) - Number(ruleFireCounts.vat[b.id] || 0);
          break;
        case 'active':
          cmp = Number(a.is_active !== false) - Number(b.is_active !== false);
          break;
        default:
          cmp = 0;
      }
      if (cmp !== 0) return cmp * mul;
      return Number(a.id || 0) - Number(b.id || 0);
    });
    return rows;
  }, [ruleFireCounts.vat, vatRulesTableSort, visibleVatRules]);

  const sortedVisibleSpecialCaseRules = useMemo(() => {
    if (!specialCaseRulesTableSort?.key) return visibleSpecialCaseRules;
    const rows = [...visibleSpecialCaseRules];
    const mul = specialCaseRulesTableSort.dir === 'asc' ? 1 : -1;
    rows.sort((a, b) => {
      let cmp = 0;
      switch (specialCaseRulesTableSort.key) {
        case 'status':
          cmp = 0;
          break;
        case 'id':
          cmp = Number(a.id || 0) - Number(b.id || 0);
          break;
        case 'priority':
          cmp = Number(a.priority || 0) - Number(b.priority || 0);
          break;
        case 'name':
          cmp = compareStrings(String(a.name || '').toLowerCase(), String(b.name || '').toLowerCase());
          break;
        case 'matchers': {
          const aMatchers = [
            a.category?.name || '',
            a.supplier_contains || '',
            a.description_contains || '',
            a.reference_contains || '',
            a.current_vat_rate || ''
          ].join(' ').toLowerCase();
          const bMatchers = [
            b.category?.name || '',
            b.supplier_contains || '',
            b.description_contains || '',
            b.reference_contains || '',
            b.current_vat_rate || ''
          ].join(' ').toLowerCase();
          cmp = compareStrings(aMatchers, bMatchers);
          break;
        }
        case 'setVat':
          cmp = compareStrings(String(a.set_vat_rate || '').toLowerCase(), String(b.set_vat_rate || '').toLowerCase());
          break;
        case 'account':
          cmp = compareStrings(
            ruleAccountScopeLabel(a.account_scope).toLowerCase(),
            ruleAccountScopeLabel(b.account_scope).toLowerCase()
          );
          break;
        case 'firedCount':
          cmp = Number(ruleFireCounts.specialCase[a.id] || 0) - Number(ruleFireCounts.specialCase[b.id] || 0);
          break;
        case 'active':
          cmp = Number(a.is_active !== false) - Number(b.is_active !== false);
          break;
        default:
          cmp = 0;
      }
      if (cmp !== 0) return cmp * mul;
      return Number(a.id || 0) - Number(b.id || 0);
    });
    return rows;
  }, [ruleFireCounts.specialCase, specialCaseRulesTableSort, visibleSpecialCaseRules]);

  const rulesFrequencyOptions = useMemo(() => {
    const values = new Set<string>();
    (rulesPayload?.rules || []).forEach((rule) => {
      if (rule.recurrence_type) values.add(rule.recurrence_type.toLowerCase());
    });
    (proposalsPayload?.proposals || []).forEach((row) => {
      const p = row.payload || {};
      const v = ((p.recurrence_type as string) || (p.payment_frequency as string) || '').toLowerCase();
      if (v) values.add(v);
    });
    return Array.from(values).sort();
  }, [rulesPayload, proposalsPayload]);

  const rulesCategoryOptions = useMemo(
    () =>
      categoriesSortedByName
        .map((c) => (c.name || '').trim())
        .filter((n) => n.length > 0),
    [categoriesSortedByName]
  );

  const rulesSupplierOptions = useMemo(() => {
    const values = new Set<string>();
    canonicalSupplierOptions.forEach((s) => {
      const v = (s || '').trim();
      if (v) values.add(v);
    });
    (rulesPayload?.rules || []).forEach((rule) => {
      const v = (rule.supplier_canonical_name || '').trim();
      if (v) values.add(v);
      supplierOverridesFromClassificationRule(rule).forEach((n) => {
        if (n.trim()) values.add(n.trim());
      });
    });
    supplierRuleRows.forEach((row) => {
      const v = canonicalSupplierLabel(row.supplier_name, '').trim();
      if (v) values.add(v);
    });
    (supplierProposalsPayload?.proposals || []).forEach((row) => {
      const p = row.payload || {};
      const v = String((p.canonical_name as string) || '').trim();
      if (v) values.add(v);
    });
    return uniqueSupplierNamesCaseInsensitive(Array.from(values));
  }, [canonicalSupplierOptions, rulesPayload, supplierRuleRows, supplierProposalsPayload]);

  const supplierAliasesBySupplierId = useMemo(() => {
    const m: Record<number, SupplierRuleRow[]> = {};
    supplierRuleRows.forEach((row) => {
      const id = Number(row.supplier_id);
      if (!Number.isFinite(id)) return;
      if (!m[id]) m[id] = [];
      m[id].push(row);
    });
    return m;
  }, [supplierRuleRows]);

  const addCanonicalSupplierHints = useMemo(() => {
    const input = addCanonicalSupplierName.trim();
    if (!input) return { duplicateOf: null as string | null, suggestion: null as string | null };
    const dup = supplierDuplicateCaseInsensitive(input, canonicalSupplierOptions);
    if (dup) return { duplicateOf: dup, suggestion: null };
    const sug = supplierClosestSpellingMatch(input, canonicalSupplierOptions);
    return { duplicateOf: null, suggestion: sug };
  }, [addCanonicalSupplierName, canonicalSupplierOptions]);

  const supplierCrudNameHints = useMemo(() => {
    if (!supplierCrudDraft) return { duplicateOf: null as string | null, suggestion: null as string | null };
    const input = supplierCrudDraft.name.trim();
    if (!input) return { duplicateOf: null, suggestion: null };
    const others = suppliersCrudRows
      .filter((r) => supplierCrudDraft.id == null || r.id !== supplierCrudDraft.id)
      .map((r) => r.name);
    const dup = supplierDuplicateCaseInsensitive(input, others);
    if (dup) return { duplicateOf: dup, suggestion: null };
    const sug = supplierClosestSpellingMatch(input, others);
    return { duplicateOf: null, suggestion: sug };
  }, [supplierCrudDraft, suppliersCrudRows]);

  const suppliersCrudVatOptions = useMemo(() => {
    const values = new Set<string>();
    suppliersCrudRows.forEach((row) => {
      const v = String(row.vat_status || '').trim();
      if (v) values.add(v);
    });
    return Array.from(values).sort((a, b) => compareStrings(a, b));
  }, [suppliersCrudRows]);

  const suppliersCrudCurrencyOptions = useMemo(() => {
    const values = new Set<string>();
    suppliersCrudRows.forEach((row) => {
      const v = String(row.invoice_currency || '').trim();
      if (v) values.add(v);
    });
    return Array.from(values).sort((a, b) => compareStrings(a, b));
  }, [suppliersCrudRows]);

  const suppliersCrudRowsFiltered = useMemo(() => {
    const needle = suppliersCrudSearch.trim().toLowerCase();
    const exactName = suppliersCrudNameFilter.trim().toLowerCase();
    return suppliersCrudRows.filter((row) => {
      if (suppliersCrudIdFilter != null && row.id !== suppliersCrudIdFilter) return false;
      if (exactName && String(row.name || '').trim().toLowerCase() !== exactName) return false;
      if (suppliersCrudActiveFilter === 'active' && !row.is_active) return false;
      if (suppliersCrudActiveFilter === 'inactive' && row.is_active) return false;
      if (suppliersCrudVatFilter !== 'all' && String(row.vat_status || '').trim() !== suppliersCrudVatFilter) {
        return false;
      }
      if (
        suppliersCrudCurrencyFilter !== 'all' &&
        String(row.invoice_currency || '').trim() !== suppliersCrudCurrencyFilter
      ) {
        return false;
      }
      if (suppliersCrudHideNonKey && !row.is_key_supplier) return false;
      if (suppliersCrudSourceFilter !== 'all') {
        if (deriveEntitySourceTag(row.notes).label !== suppliersCrudSourceFilter) return false;
      }
      if (needle) {
        const hay = [
          row.name,
          row.notes || '',
          row.vat_status || '',
          row.invoice_currency || '',
          row.invoice_frequency || '',
          row.invoice_url || '',
          row.is_key_supplier ? 'key' : 'ancillary',
          row.is_active ? 'active' : 'inactive',
          deriveEntitySourceTag(row.notes).label
        ]
          .join(' ')
          .toLowerCase();
        if (!hay.includes(needle)) return false;
      }
      return true;
    });
  }, [
    suppliersCrudRows,
    suppliersCrudSearch,
    suppliersCrudNameFilter,
    suppliersCrudIdFilter,
    suppliersCrudActiveFilter,
    suppliersCrudVatFilter,
    suppliersCrudCurrencyFilter,
    suppliersCrudSourceFilter,
    suppliersCrudHideNonKey
  ]);

  /** Same as list filters except Source — used for per-source counts in the Source dropdown. */
  const suppliersCrudRowsForSourceCounts = useMemo(() => {
    const needle = suppliersCrudSearch.trim().toLowerCase();
    const exactName = suppliersCrudNameFilter.trim().toLowerCase();
    return suppliersCrudRows.filter((row) => {
      if (suppliersCrudIdFilter != null && row.id !== suppliersCrudIdFilter) return false;
      if (exactName && String(row.name || '').trim().toLowerCase() !== exactName) return false;
      if (suppliersCrudActiveFilter === 'active' && !row.is_active) return false;
      if (suppliersCrudActiveFilter === 'inactive' && row.is_active) return false;
      if (suppliersCrudVatFilter !== 'all' && String(row.vat_status || '').trim() !== suppliersCrudVatFilter) {
        return false;
      }
      if (
        suppliersCrudCurrencyFilter !== 'all' &&
        String(row.invoice_currency || '').trim() !== suppliersCrudCurrencyFilter
      ) {
        return false;
      }
      if (suppliersCrudHideNonKey && !row.is_key_supplier) return false;
      if (needle) {
        const hay = [
          row.name,
          row.notes || '',
          row.vat_status || '',
          row.invoice_currency || '',
          row.invoice_frequency || '',
          row.invoice_url || '',
          row.is_key_supplier ? 'key' : 'ancillary',
          row.is_active ? 'active' : 'inactive',
          deriveEntitySourceTag(row.notes).label
        ]
          .join(' ')
          .toLowerCase();
        if (!hay.includes(needle)) return false;
      }
      return true;
    });
  }, [
    suppliersCrudRows,
    suppliersCrudSearch,
    suppliersCrudNameFilter,
    suppliersCrudIdFilter,
    suppliersCrudActiveFilter,
    suppliersCrudVatFilter,
    suppliersCrudCurrencyFilter,
    suppliersCrudHideNonKey
  ]);

  const suppliersCrudSourceLabelCounts = useMemo(() => {
    const counts = new Map<EntitySourceTagLabel, number>();
    ENTITY_SOURCE_FILTER_SELECT_LABELS.forEach((lab) => counts.set(lab, 0));
    suppliersCrudRowsForSourceCounts.forEach((row) => {
      const label = deriveEntitySourceTag(row.notes).label;
      if (counts.has(label)) counts.set(label, (counts.get(label) || 0) + 1);
    });
    return counts;
  }, [suppliersCrudRowsForSourceCounts]);

  const suppliersCrudHasFilters =
    suppliersCrudIdFilter != null ||
    suppliersCrudNameFilter.trim() !== '' ||
    suppliersCrudSearch.trim() !== '' ||
    suppliersCrudActiveFilter !== 'all' ||
    suppliersCrudVatFilter !== 'all' ||
    suppliersCrudCurrencyFilter !== 'all' ||
    suppliersCrudSourceFilter !== 'all' ||
    suppliersCrudHideNonKey;

  const supplierIdByName = useMemo(() => {
    const out = new Map<string, number>();
    suppliersCrudRows.forEach((row) => {
      const key = String(row.name || '').trim().toLowerCase();
      if (!key) return;
      if (!out.has(key)) out.set(key, row.id);
    });
    return out;
  }, [suppliersCrudRows]);

  const suppliersCrudChartRows = useMemo(() => {
    const top = [...suppliersCrudRowsFiltered]
      .sort((a, b) => {
        const cmp = Number(b.tx_associated_count || 0) - Number(a.tx_associated_count || 0);
        if (cmp !== 0) return cmp;
        return compareStrings(a.name || '', b.name || '');
      })
      .slice(0, 20);
    return top.map((row) => ({
      name: row.name,
      shortName: row.name.length > 24 ? `${row.name.slice(0, 24)}…` : row.name,
      txCount: Number(row.tx_associated_count || 0),
      isKey: !!row.is_key_supplier
    }));
  }, [suppliersCrudRowsFiltered]);

  const sortedSuppliersCrudRows = useMemo(() => {
    const rows = [...suppliersCrudRowsFiltered];
    const mul = suppliersCrudSort.dir === 'asc' ? 1 : -1;
    rows.sort((a, b) => {
      const cmp =
        suppliersCrudSort.key === 'name'
          ? compareStrings(a.name || '', b.name || '')
          : suppliersCrudSort.key === 'is_key_supplier'
            ? Number(a.is_key_supplier) - Number(b.is_key_supplier)
            : suppliersCrudSort.key === 'tx_associated_count'
              ? Number(a.tx_associated_count || 0) - Number(b.tx_associated_count || 0)
          : suppliersCrudSort.key === 'active'
            ? Number(a.is_active) - Number(b.is_active)
            : suppliersCrudSort.key === 'source'
              ? compareStrings(
                  deriveEntitySourceTag(a.notes).label,
                  deriveEntitySourceTag(b.notes).label
                )
            : suppliersCrudSort.key === 'invoice_frequency'
              ? compareStrings(a.invoice_frequency || '', b.invoice_frequency || '')
              : suppliersCrudSort.key === 'invoice_currency'
                ? compareStrings(a.invoice_currency || '', b.invoice_currency || '')
                : suppliersCrudSort.key === 'invoice_url'
                  ? compareStrings(a.invoice_url || '', b.invoice_url || '')
                : suppliersCrudSort.key === 'vat_status'
                  ? compareStrings(a.vat_status || '', b.vat_status || '')
                  : compareStrings(a.notes || '', b.notes || '');
      if (cmp !== 0) return cmp * mul;
      return compareStrings(a.name || '', b.name || '');
    });
    return rows;
  }, [suppliersCrudRowsFiltered, suppliersCrudSort]);

  const categoryCrudNameHints = useMemo(() => {
    if (!categoryCrudDraft) return { duplicateOf: null as string | null, suggestion: null as string | null };
    const input = categoryCrudDraft.name.trim();
    if (!input) return { duplicateOf: null, suggestion: null };
    const others = categoriesCrudRows
      .filter((r) => categoryCrudDraft.id == null || r.id !== categoryCrudDraft.id)
      .map((r) => r.name);
    const dup = supplierDuplicateCaseInsensitive(input, others);
    if (dup) return { duplicateOf: dup, suggestion: null };
    const sug = supplierClosestSpellingMatch(input, others);
    return { duplicateOf: null, suggestion: sug };
  }, [categoryCrudDraft, categoriesCrudRows]);

  const categoriesCrudRowsFiltered = useMemo(() => {
    const needle = categoriesCrudSearch.trim().toLowerCase();
    const exactName = categoriesCrudNameFilter.trim().toLowerCase();
    return categoriesCrudRows.filter((row) => {
      if (exactName && String(row.name || '').trim().toLowerCase() !== exactName) return false;
      if (categoriesCrudActiveFilter === 'active' && !row.is_active) return false;
      if (categoriesCrudActiveFilter === 'inactive' && row.is_active) return false;
      if (categoriesCrudHideNonKey && !row.is_key_category) return false;
      if (categoriesCrudSourceFilter !== 'all') {
        if (categorySourceTagFromRow(row).label !== categoriesCrudSourceFilter) return false;
      }
      if (needle) {
        const hay = [
          row.name,
          row.description || '',
          row.is_active ? 'active' : 'inactive',
          categorySourceTagFromRow(row).label
        ]
          .join(' ')
          .toLowerCase();
        if (!hay.includes(needle)) return false;
      }
      return true;
    });
  }, [
    categoriesCrudRows,
    categoriesCrudSearch,
    categoriesCrudNameFilter,
    categoriesCrudActiveFilter,
    categoriesCrudSourceFilter,
    categoriesCrudHideNonKey
  ]);

  /** Same as list filters except Source — used for per-source counts in the Source dropdown. */
  const categoriesCrudRowsForSourceCounts = useMemo(() => {
    const needle = categoriesCrudSearch.trim().toLowerCase();
    const exactName = categoriesCrudNameFilter.trim().toLowerCase();
    return categoriesCrudRows.filter((row) => {
      if (exactName && String(row.name || '').trim().toLowerCase() !== exactName) return false;
      if (categoriesCrudActiveFilter === 'active' && !row.is_active) return false;
      if (categoriesCrudActiveFilter === 'inactive' && row.is_active) return false;
      if (categoriesCrudHideNonKey && !row.is_key_category) return false;
      if (needle) {
        const hay = [
          row.name,
          row.description || '',
          row.is_active ? 'active' : 'inactive',
          categorySourceTagFromRow(row).label
        ]
          .join(' ')
          .toLowerCase();
        if (!hay.includes(needle)) return false;
      }
      return true;
    });
  }, [categoriesCrudRows, categoriesCrudSearch, categoriesCrudNameFilter, categoriesCrudActiveFilter, categoriesCrudHideNonKey]);

  const categoriesCrudSourceLabelCounts = useMemo(() => {
    const counts = new Map<EntitySourceTagLabel, number>();
    ENTITY_SOURCE_FILTER_SELECT_LABELS.forEach((lab) => counts.set(lab, 0));
    categoriesCrudRowsForSourceCounts.forEach((row) => {
      const label = categorySourceTagFromRow(row).label;
      if (counts.has(label)) counts.set(label, (counts.get(label) || 0) + 1);
    });
    return counts;
  }, [categoriesCrudRowsForSourceCounts]);

  const categoriesCrudHasFilters =
    categoriesCrudNameFilter.trim() !== '' ||
    categoriesCrudSearch.trim() !== '' ||
    categoriesCrudActiveFilter !== 'all' ||
    categoriesCrudSourceFilter !== 'all' ||
    categoriesCrudHideNonKey;

  const resetSuppliersCrudFilters = useCallback(() => {
    setSuppliersCrudIdFilter(null);
    setSuppliersCrudNameFilter('');
    setSuppliersCrudSearch('');
    setSuppliersCrudActiveFilter('all');
    setSuppliersCrudVatFilter('all');
    setSuppliersCrudCurrencyFilter('all');
    setSuppliersCrudSourceFilter('all');
    setSuppliersCrudHideNonKey(false);
    setSelectedSupplierCrudIds([]);
  }, []);

  const suppliersCrudFilterPills: Array<{ key: string; label: string; onDelete: () => void }> = [];
  if (suppliersCrudSearch.trim()) {
    suppliersCrudFilterPills.push({
      key: 'search',
      label: `Search: ${suppliersCrudSearch.trim()}`,
      onDelete: () => setSuppliersCrudSearch('')
    });
  }
  if (suppliersCrudActiveFilter !== 'all') {
    suppliersCrudFilterPills.push({
      key: 'active',
      label: `Active: ${suppliersCrudActiveFilter === 'active' ? 'Active only' : 'Inactive only'}`,
      onDelete: () => setSuppliersCrudActiveFilter('all')
    });
  }
  if (suppliersCrudVatFilter !== 'all') {
    suppliersCrudFilterPills.push({
      key: 'vat',
      label: `VAT status: ${suppliersCrudVatFilter}`,
      onDelete: () => setSuppliersCrudVatFilter('all')
    });
  }
  if (suppliersCrudCurrencyFilter !== 'all') {
    suppliersCrudFilterPills.push({
      key: 'currency',
      label: `Currency: ${suppliersCrudCurrencyFilter}`,
      onDelete: () => setSuppliersCrudCurrencyFilter('all')
    });
  }
  if (suppliersCrudSourceFilter !== 'all') {
    suppliersCrudFilterPills.push({
      key: 'source',
      label: `Source: ${suppliersCrudSourceFilter}`,
      onDelete: () => setSuppliersCrudSourceFilter('all')
    });
  }
  if (suppliersCrudIdFilter != null) {
    suppliersCrudFilterPills.push({
      key: 'pinned-id',
      label: `Pinned supplier ID: ${suppliersCrudIdFilter}`,
      onDelete: () => setSuppliersCrudIdFilter(null)
    });
  }
  if (suppliersCrudNameFilter.trim()) {
    suppliersCrudFilterPills.push({
      key: 'pinned-name',
      label: `Pinned supplier: ${suppliersCrudNameFilter.trim()}`,
      onDelete: () => setSuppliersCrudNameFilter('')
    });
  }
  if (suppliersCrudHideNonKey) {
    suppliersCrudFilterPills.push({
      key: 'hide-non-key',
      label: 'Hide non-key Supplier',
      onDelete: () => setSuppliersCrudHideNonKey(false)
    });
  }

  const resetCategoriesCrudFilters = useCallback(() => {
    setCategoriesCrudNameFilter('');
    setCategoriesCrudSearch('');
    setCategoriesCrudActiveFilter('all');
    setCategoriesCrudSourceFilter('all');
    setCategoriesCrudHideNonKey(false);
    setSelectedCategoryCrudIds([]);
  }, []);

  const categoriesCrudFilterPills: Array<{ key: string; label: string; onDelete: () => void }> = [];
  if (categoriesCrudSearch.trim()) {
    categoriesCrudFilterPills.push({
      key: 'search',
      label: `Search: ${categoriesCrudSearch.trim()}`,
      onDelete: () => setCategoriesCrudSearch('')
    });
  }
  if (categoriesCrudActiveFilter !== 'all') {
    categoriesCrudFilterPills.push({
      key: 'active',
      label: `Active: ${categoriesCrudActiveFilter === 'active' ? 'Active only' : 'Inactive only'}`,
      onDelete: () => setCategoriesCrudActiveFilter('all')
    });
  }
  if (categoriesCrudSourceFilter !== 'all') {
    categoriesCrudFilterPills.push({
      key: 'source',
      label: `Source: ${categoriesCrudSourceFilter}`,
      onDelete: () => setCategoriesCrudSourceFilter('all')
    });
  }
  if (categoriesCrudNameFilter.trim()) {
    categoriesCrudFilterPills.push({
      key: 'pinned-name',
      label: `Pinned category: ${categoriesCrudNameFilter.trim()}`,
      onDelete: () => setCategoriesCrudNameFilter('')
    });
  }
  if (categoriesCrudHideNonKey) {
    categoriesCrudFilterPills.push({
      key: 'hide-non-key',
      label: 'Hide non-key Category',
      onDelete: () => setCategoriesCrudHideNonKey(false)
    });
  }

  const categoryTxAssociationCountByName = useMemo(() => {
    const byName = new Map<string, Set<number>>();
    const add = (nameRaw: string | null | undefined, txIdRaw: number | null | undefined) => {
      const name = String(nameRaw || '').trim();
      const txId = Number(txIdRaw);
      if (!name || !Number.isFinite(txId) || txId <= 0) return;
      const key = name.toLowerCase();
      const current = byName.get(key) || new Set<number>();
      current.add(txId);
      byName.set(key, current);
    };
    (taggedPayload?.tagged || []).forEach((row) => add(row.category_name, row.transaction?.id));
    (confirmedPayload?.items || []).forEach((row) => add(row.category_name, row.transaction?.id));

    const out = new Map<string, number>();
    byName.forEach((txIds, key) => out.set(key, txIds.size));
    return out;
  }, [taggedPayload, confirmedPayload]);

  const categoriesCrudChartRows = useMemo(() => {
    const top = [...categoriesCrudRowsFiltered]
      .map((row) => ({
        name: row.name,
        shortName: row.name.length > 24 ? `${row.name.slice(0, 24)}…` : row.name,
        txCount: Number(categoryTxAssociationCountByName.get(row.name.toLowerCase()) || 0)
      }))
      .sort((a, b) => b.txCount - a.txCount || compareStrings(a.name || '', b.name || ''))
      .slice(0, 20);
    return top;
  }, [categoriesCrudRowsFiltered, categoryTxAssociationCountByName]);

  const sortedCategoriesCrudRows = useMemo(() => {
    const rows = [...categoriesCrudRowsFiltered];
    const mul = categoriesCrudSort.dir === 'asc' ? 1 : -1;
    rows.sort((a, b) => {
      const cmp =
        categoriesCrudSort.key === 'name'
          ? compareStrings(a.name || '', b.name || '')
          : categoriesCrudSort.key === 'is_key_category'
            ? Number(a.is_key_category) - Number(b.is_key_category)
            : categoriesCrudSort.key === 'active'
              ? Number(a.is_active) - Number(b.is_active)
              : categoriesCrudSort.key === 'source'
                ? compareStrings(
                    categorySourceTagFromRow(a).label,
                    categorySourceTagFromRow(b).label
                  )
              : compareStrings(a.description || '', b.description || '');
      if (cmp !== 0) return cmp * mul;
      return compareStrings(a.name || '', b.name || '');
    });
    return rows;
  }, [categoriesCrudRowsFiltered, categoriesCrudSort]);

  const visibleSupplierCrudRowIds = useMemo(
    () => sortedSuppliersCrudRows.map((r) => r.id),
    [sortedSuppliersCrudRows]
  );
  const supplierCrudSelectAllCheckboxState = useMemo(() => {
    const visible = visibleSupplierCrudRowIds;
    if (visible.length === 0) return { checked: false, indeterminate: false };
    const n = visible.filter((id) => selectedSupplierCrudIds.includes(id)).length;
    return { checked: n === visible.length, indeterminate: n > 0 && n < visible.length };
  }, [visibleSupplierCrudRowIds, selectedSupplierCrudIds]);

  const selectableCategoryCrudRowIds = useMemo(
    () => sortedCategoriesCrudRows.filter((r) => !isCategoryRowImmutableSystem(r)).map((r) => r.id),
    [sortedCategoriesCrudRows]
  );
  const categoryCrudSelectAllCheckboxState = useMemo(() => {
    const visible = selectableCategoryCrudRowIds;
    if (visible.length === 0) return { checked: false, indeterminate: false };
    const n = visible.filter((id) => selectedCategoryCrudIds.includes(id)).length;
    return { checked: n === visible.length, indeterminate: n > 0 && n < visible.length };
  }, [selectableCategoryCrudRowIds, selectedCategoryCrudIds]);

  const vatRulesByCategoryId = useMemo(() => {
    const out = new Map<number, Array<{ id: number; name: string; vat_rate: string; priority: number }>>();
    vatRuleRows.forEach((row) => {
      if (row.is_active === false) return;
      const ruleId = Number(row.id);
      if (!Number.isFinite(ruleId) || ruleId <= 0) return;
      const categoryIds = Array.from(
        new Set(
          (Array.isArray(row.category_ids) && row.category_ids.length > 0 ? row.category_ids : [row.category_id])
            .map((id) => Number(id))
            .filter((id) => Number.isFinite(id) && id > 0)
        )
      );
      if (categoryIds.length === 0) return;
      const item = {
        id: ruleId,
        name: String(row.name || '').trim(),
        vat_rate: canonicalizeVatRate(row.vat_rate),
        priority: Number.isFinite(Number(row.priority)) ? Number(row.priority) : 100
      };
      categoryIds.forEach((categoryId) => {
        const existing = out.get(categoryId) || [];
        existing.push(item);
        out.set(categoryId, existing);
      });
    });
    out.forEach((rules) => {
      rules.sort((a, b) => a.priority - b.priority || a.id - b.id);
    });
    return out;
  }, [vatRuleRows]);

  const vatRateMatcherPickOptions = useMemo(() => {
    if (canonicalVatRateOptions.length > 0) {
      return canonicalVatRateOptions;
    }
    return [
      { value: '23%', label: '23% (Standard Rate IRE)' },
      { value: '20%|ire_covid', label: '20% (Standard Rate IRE Covid)' },
      { value: '20%|uk', label: '20% (Standard Rate UK)' },
      { value: '13.5%', label: '13.5% (Reduced rate)' },
      { value: '9%', label: '9% (Second reduced rate)' },
      { value: '0%', label: '0% (Zero rate)' },
      { value: 'exempt', label: 'Exempt (VAT exempt)' }
    ];
  }, [canonicalVatRateOptions]);

  /** Rates applied to tags / category VAT rules — no matcher-only `|` suffixes. */
  const vatRatePickOptions = useMemo(() => {
    const base = vatRateMatcherPickOptions.filter((o) => !String(o.value).includes('|'));
    const hasPlain20 = base.some((o) => o.value === '20%');
    const has20Matchers = vatRateMatcherPickOptions.some((o) => String(o.value).startsWith('20%|'));
    if (has20Matchers && !hasPlain20) {
      const out = [...base];
      const idx = out.findIndex((o) => o.value === '23%');
      out.splice(idx >= 0 ? idx + 1 : 0, 0, { value: '20%', label: '20%' });
      return out;
    }
    return base;
  }, [vatRateMatcherPickOptions]);

  const categoryNameById = useMemo(() => {
    const out = new Map<number, string>();
    categoriesSortedByName.forEach((c) => {
      const id = Number(c.id);
      if (Number.isFinite(id) && id > 0) {
        out.set(id, c.name);
      }
    });
    return out;
  }, [categoriesSortedByName]);

  const vatRuleCategoryNamesForDisplay = useCallback(
    (row: CategoryVatRuleRow): string[] => {
      const fromEmbedded = (row.categories || [])
        .map((c) => String(c?.name || '').trim())
        .filter((n) => n.length > 0);
      if (fromEmbedded.length > 0) return Array.from(new Set(fromEmbedded));

      const fromIds = (row.category_ids || [])
        .map((id) => Number(id))
        .filter((id) => Number.isFinite(id) && id > 0)
        .map((id) => categoryNameById.get(id) || `Category #${id}`);
      if (fromIds.length > 0) return Array.from(new Set(fromIds));

      return vatRuleCategoryNames(row);
    },
    [categoryNameById]
  );

  const ruleEditSupplierOptions = useMemo(() => {
    const merged: string[] = [...rulesSupplierOptions];
    const curPat = (ruleEditDraft?.supplier_contains || '').trim();
    (ruleEditDraft?.supplier_name_overrides || []).forEach((c) => {
      const t = c.trim();
      if (t) merged.push(t);
    });
    if (curPat) merged.push(curPat);
    return uniqueSupplierNamesCaseInsensitive(merged);
  }, [rulesSupplierOptions, ruleEditDraft?.supplier_contains, ruleEditDraft?.supplier_name_overrides]);

  const conflictPairCategorySelectOptions = useMemo(() => {
    const base = granularCategoriesSortedByName;
    const cidRaw = conflictPairEditDraft?.category_id;
    const cid = cidRaw != null && Number.isFinite(Number(cidRaw)) ? Math.trunc(Number(cidRaw)) : 0;
    if (!cid || cid <= 0) return base;
    if (base.some((c) => c.id === cid)) return base;
    const fromAll = categoriesSortedByName.find((c) => c.id === cid);
    if (fromAll) {
      const merged = [...base];
      if (!merged.some((c) => c.id === fromAll.id)) merged.push(fromAll);
      return merged.sort((a, b) => compareCategoryNamesUnknownFirst(a.name, b.name));
    }
    return [
      ...base,
      { id: cid, name: `Category #${cid}`, is_key_category: false }
    ].sort((a, b) => compareCategoryNamesUnknownFirst(a.name, b.name));
  }, [granularCategoriesSortedByName, categoriesSortedByName, conflictPairEditDraft?.category_id]);

  const conflictPairSupplierOptions = useMemo(() => {
    if (!conflictPairEditDraft) return [] as string[];
    const merged: string[] = [...rulesSupplierOptions];
    const curPat = (conflictPairEditDraft.supplier_contains || '').trim();
    (conflictPairEditDraft.supplier_name_overrides || []).forEach((c) => {
      const t = c.trim();
      if (t) merged.push(t);
    });
    if (curPat) merged.push(curPat);
    return uniqueSupplierNamesCaseInsensitive(merged);
  }, [rulesSupplierOptions, conflictPairEditDraft]);

  const conflictPairExplanationSentence = useMemo(
    () =>
      conflictPairEditDraft
        ? getClassificationRuleExplanationSentence(
            conflictPairEditDraft,
            'classification',
            categoriesSortedByName,
            conflictPairCategorySelectOptions
          )
        : '',
    [conflictPairEditDraft, categoriesSortedByName, conflictPairCategorySelectOptions]
  );

  const ruleEditExplanationSentence = useMemo(
    () =>
      ruleEditDraft
        ? getClassificationRuleExplanationSentence(
            ruleEditDraft,
            ruleEditTargetType,
            categoriesSortedByName,
            ruleEditCategorySelectOptions
          )
        : '',
    [ruleEditDraft, ruleEditTargetType, categoriesSortedByName, ruleEditCategorySelectOptions]
  );

  const supplierRuleExplanationSentence = useMemo(() => {
    const ruleName = supplierRuleName.trim();
    const canonical = supplierRuleCanonicalName.trim();
    const scopeLabel = ruleAccountScopeLabel(supplierRuleAccountScope);
    const priorityText = supplierRulePriority.trim() || '100';
    const scopeText = scopeLabel.toLowerCase() === 'both' ? 'across both bank accounts' : `for ${scopeLabel}`;
    if (!ruleName || !canonical) {
      return 'Set rule name, at least one match criterion, and canonical supplier.';
    }
    const conditions: string[] = supplierRuleCriteria
      .map((c) => ({
        kind: c.kind,
        value: c.value.trim(),
        op: classificationRuleTextOperatorLabel(c.operator || 'contains').toLowerCase()
      }))
      .filter((c) => c.value)
      .map((c) => {
        if (c.kind === 'merchant') return `merchant/counterparty text ${c.op} "${c.value}"`;
        if (c.kind === 'description') return `description ${c.op} "${c.value}"`;
        if (c.kind === 'reference') return `reference ${c.op} "${c.value}"`;
        if (c.kind === 'currency') return `currency is "${c.value.toUpperCase()}"`;
        if (c.kind === 'amount') return `amount is exactly ${c.value}`;
        return `canonical supplier ${c.op} "${c.value}"`;
      });
    if (conditions.length === 0) {
      return `Rule "${ruleName}": add at least one match criterion before saving.`;
    }
    return `Rule "${ruleName}": if any of [${conditions.join(' OR ')}], then canonical supplier "${canonical}" will be set with priority ${priorityText} ${scopeText}.`;
  }, [
    supplierRuleName,
    supplierRuleCriteria,
    supplierRuleCanonicalName,
    supplierRulePriority,
    supplierRuleAccountScope
  ]);

  const vatRuleExplanationSentence = useMemo(() => {
    if (!vatRuleDraft) return '';
    const categoryNames = vatRuleDraft.category_ids
      .map((id) => categoriesSortedByName.find((c) => c.id === id)?.name || `Category #${id}`)
      .filter((name) => name.trim().length > 0);
    const categoriesText =
      categoryNames.length > 0 ? categoryNames.map((name) => `"${name}"`).join(', ') : 'no categories selected yet';
    const rate = canonicalizeVatRate(vatRuleDraft.vat_rate) || 'a VAT rate';
    const priorityText = Number.isFinite(Number(vatRuleDraft.priority)) ? String(vatRuleDraft.priority) : '100';
    const scopeLabel = ruleAccountScopeLabel(vatRuleDraft.account_scope);
    const scopeText = scopeLabel.toLowerCase() === 'both' ? 'across both bank accounts' : `for ${scopeLabel}`;
    return `If a transaction tag is in ${categoriesText}, then VAT rate "${rate}" will be applied with priority ${priorityText} ${scopeText}.`;
  }, [vatRuleDraft, categoriesSortedByName]);

  const specialCaseRuleExplanationSentence = useMemo(() => {
    if (!specialCaseRuleDraft) return '';
    const conditions: string[] = (specialCaseRuleDraft.match_criteria || [])
      .map((criterion) => {
        const kind = criterion.kind;
        const value = criterion.value.trim();
        if (!value) return '';
        const opText = classificationRuleTextOperatorLabel(criterion.operator || 'contains').toLowerCase();
        if (kind === 'canonical_supplier') return `canonical supplier ${opText} "${value}"`;
        if (kind === 'merchant') return `merchant/counterparty ${opText} "${value}"`;
        if (kind === 'description') return `description ${opText} "${value}"`;
        if (kind === 'reference') return `reference ${opText} "${value}"`;
        if (kind === 'currency') return `currency is "${value.toUpperCase()}"`;
        if (kind === 'amount') return `amount is exactly ${value}`;
        if (kind === 'category') {
          const categoryName = categoriesSortedByName.find((c) => c.id === Number(value))?.name || `Category #${value}`;
          return `category is "${categoryName}"`;
        }
        if (kind === 'current_vat') {
          const vNorm = normalizeSpecialCaseVatMatcherSelectValue(value);
          const opt = vatRateMatcherPickOptions.find((o) => o.value === vNorm);
          return `current VAT is "${opt?.label || vNorm}"`;
        }
        return '';
      })
      .filter(Boolean);
    const scopeLabel = ruleAccountScopeLabel(specialCaseRuleDraft.account_scope);
    if (scopeLabel.toLowerCase() !== 'both') {
      conditions.push(`bank account is ${scopeLabel}`);
    }
    const targetRate = canonicalizeVatRate(specialCaseRuleDraft.set_vat_rate) || '(select VAT rate)';
    const priorityText = Number.isFinite(Number(specialCaseRuleDraft.priority))
      ? String(specialCaseRuleDraft.priority)
      : '100';
    if (conditions.length === 0) {
      return `If a Tx has no specific matching constraints set, then special-case VAT "${targetRate}" will be applied with priority ${priorityText}.`;
    }
    return `If a Tx matches any of [${conditions.join('; ')}], then special-case VAT "${targetRate}" will be applied with priority ${priorityText}.`;
  }, [specialCaseRuleDraft, categoriesSortedByName, vatRateMatcherPickOptions]);

  const resetRulesFilters = useCallback(() => {
    setRulesStatusFilter('all');
    setRulesSupplierFilter('all');
    setRulesIdFilter('');
    setRulesProposalIdsFilter('');
    setRulesTextFilter('');
    setRulesFrequencyFilter('all');
    setRulesCategoryFilter('all');
  }, []);

  const rulesHasFilters =
    rulesStatusFilter !== 'all' ||
    rulesSupplierFilter !== 'all' ||
    rulesIdFilter.trim() !== '' ||
    rulesProposalIdsFilter.trim() !== '' ||
    rulesTextFilter.trim() !== '' ||
    rulesFrequencyFilter !== 'all' ||
    rulesCategoryFilter !== 'all';

  /** Deep links (P### / A### in Rules ID or proposal-ids param) should keep Classification expanded for empty-state copy. */
  const rulesTabKeepClassificationAccordionOpen = useMemo(
    () =>
      rulesProposalIdsFilter.trim() !== '' ||
      (parsedRulesIdFilter.hasFilter && parsedRulesIdFilter.classificationProposalId != null) ||
      (parsedRulesIdFilter.hasFilter && parsedRulesIdFilter.classificationAtlasSeq != null),
    [rulesProposalIdsFilter, parsedRulesIdFilter]
  );

  /** Pending list only includes open proposals — accepted/rejected IDs disappear, which looks like "no data". */
  const rulesTabClassificationProposalFilterMiss = useMemo(
    () =>
      !loading &&
      filteredRuleProposals.length === 0 &&
      ((parsedRulesProposalIdFilter != null &&
        (parsedRulesProposalIdFilter.classificationProposalIds.size > 0 ||
          parsedRulesProposalIdFilter.atlasSeqs.size > 0)) ||
        (parsedRulesIdFilter.hasFilter && parsedRulesIdFilter.classificationProposalId != null) ||
        (parsedRulesIdFilter.hasFilter && parsedRulesIdFilter.classificationAtlasSeq != null)),
    [loading, filteredRuleProposals.length, parsedRulesProposalIdFilter, parsedRulesIdFilter]
  );

  useEffect(() => {
    if (!rulesHasFilters) {
      rulesAccordionFilterSigRef.current = '';
      return;
    }
    const { parents: next, nested: nestedNext } = computeRulesFilterAccordionOpenState({
      classificationConfirmedProposedRows: visibleClassificationRuleRows.length,
      classificationConfirmedExcludingPipeline:
        sortedVisibleClassificationConfirmedRowsExcludingPipeline.length,
      classificationProposed: visibleClassificationRuleCounts.proposed,
      classificationRejected: visibleClassificationRuleCounts.rejected,
      keepClassificationOpen: rulesTabKeepClassificationAccordionOpen,
      pipelineStep1Confirmed: pipelineClassificationConfirmedRows.length,
      merchantSupplierConfirmed: visibleMerchantSupplierRuleRows.length,
      supplierConfirmed: visibleSupplierRuleCounts.confirmed,
      supplierProposed: visibleSupplierRuleCounts.proposed,
      supplierRejected: visibleSupplierRuleCounts.rejected,
      supplierConfirmedProposedRows: visibleSupplierRuleRows.length,
      vatConfirmed: visibleVatRuleCounts.confirmed,
      vatProposed: visibleVatRuleCounts.proposed,
      vatRejected: visibleVatRuleCounts.rejected,
      specialCaseConfirmed: visibleSpecialCaseRules.length,
    });
    const sig = [
      next.proposals ? '1' : '0',
      next.pipelineStepRules ? '1' : '0',
      next.merchantSuppliers ? '1' : '0',
      next.supplierProposals ? '1' : '0',
      next.vatRules ? '1' : '0',
      next.specialCaseRules ? '1' : '0',
      nestedNext.step1.confirmed ? '1' : '0',
      nestedNext.step2a.confirmed ? '1' : '0',
      nestedNext.step2b.confirmed ? '1' : '0',
      nestedNext.step2b.proposed ? '1' : '0',
      nestedNext.step2b.rejected ? '1' : '0',
      nestedNext.step3.confirmed ? '1' : '0',
      nestedNext.step3.proposed ? '1' : '0',
      nestedNext.step3.rejected ? '1' : '0',
      nestedNext.step4.confirmed ? '1' : '0',
      nestedNext.step4.proposed ? '1' : '0',
      nestedNext.step4.rejected ? '1' : '0',
      nestedNext.step5.confirmed ? '1' : '0',
      rulesStatusFilter,
      rulesSupplierFilter,
      rulesIdFilter,
      rulesProposalIdsFilter,
      rulesTextFilter,
      rulesFrequencyFilter,
      rulesCategoryFilter
    ].join('\u001f');
    if (sig === rulesAccordionFilterSigRef.current) return;
    rulesAccordionFilterSigRef.current = sig;
    setRulesAccordionOpen(next);
    // Open nested Confirmed/Proposed/Rejected that have filter hits; close empty ones while filters are active.
    setPipelineNestedSectionOpen(nestedNext);
  }, [
    rulesHasFilters,
    rulesStatusFilter,
    rulesSupplierFilter,
    rulesIdFilter,
    rulesProposalIdsFilter,
    rulesTextFilter,
    rulesFrequencyFilter,
    rulesCategoryFilter,
    visibleClassificationRuleRows.length,
    sortedVisibleClassificationConfirmedRowsExcludingPipeline.length,
    visibleClassificationRuleCounts.proposed,
    visibleClassificationRuleCounts.rejected,
    pipelineClassificationConfirmedRows.length,
    visibleMerchantSupplierRuleRows.length,
    visibleSupplierRuleRows.length,
    visibleSupplierRuleCounts.confirmed,
    visibleSupplierRuleCounts.proposed,
    visibleSupplierRuleCounts.rejected,
    visibleVatRuleCounts.confirmed,
    visibleVatRuleCounts.proposed,
    visibleVatRuleCounts.rejected,
    visibleSpecialCaseRules.length,
    rulesTabKeepClassificationAccordionOpen
  ]);

  const allRulesSectionsOpen =
    rulesAccordionOpen.proposals &&
    rulesAccordionOpen.pipelineStepRules &&
    rulesAccordionOpen.merchantSuppliers &&
    rulesAccordionOpen.supplierProposals &&
    rulesAccordionOpen.appRules &&
    rulesAccordionOpen.supplierRules &&
    rulesAccordionOpen.vatRules &&
    rulesAccordionOpen.specialCaseRules;
  const allRulesSectionsClosed =
    !rulesAccordionOpen.proposals &&
    !rulesAccordionOpen.pipelineStepRules &&
    !rulesAccordionOpen.merchantSuppliers &&
    !rulesAccordionOpen.supplierProposals &&
    !rulesAccordionOpen.appRules &&
    !rulesAccordionOpen.supplierRules &&
    !rulesAccordionOpen.vatRules &&
    !rulesAccordionOpen.specialCaseRules;
  const rulesAccordionMixedState = !allRulesSectionsOpen && !allRulesSectionsClosed;

  const selectablePipelineClassificationRuleKeys = useMemo(
    () => pipelineClassificationConfirmedRows.map((r) => String(r.key)),
    [pipelineClassificationConfirmedRows]
  );
  const selectableClassificationRuleKeys = useMemo(
    () =>
      [
        ...sortedVisibleClassificationConfirmedRowsExcludingPipeline,
        ...sortedVisibleClassificationProposedRows
      ].map((r) => String(r.key)),
    [sortedVisibleClassificationConfirmedRowsExcludingPipeline, sortedVisibleClassificationProposedRows]
  );
  const selectableSupplierRuleKeys = useMemo(
    () => visibleSupplierRuleRows.map((r) => String(r.key)),
    [visibleSupplierRuleRows]
  );
  const selectableMerchantSupplierRuleKeys = useMemo(
    () => sortedVisibleMerchantSupplierRuleRows.map((r) => `mc-${r.id}`),
    [sortedVisibleMerchantSupplierRuleRows]
  );
  const selectableVatRuleIds = useMemo(
    () => sortedVisibleVatRules.map((r) => Number(r.id)).filter((id) => Number.isFinite(id) && id > 0),
    [sortedVisibleVatRules]
  );
  const selectableVatSuggestionIds = useMemo(
    () =>
      visibleVatRuleSuggestions
        .map((r) => Number(r.id))
        .filter((id) => Number.isFinite(id) && id > 0),
    [visibleVatRuleSuggestions]
  );
  const selectableSpecialCaseRuleIds = useMemo(
    () =>
      sortedVisibleSpecialCaseRules
        .map((r) => Number(r.id))
        .filter((id) => Number.isFinite(id) && id > 0),
    [sortedVisibleSpecialCaseRules]
  );
  const totalSelectedRulesAcrossAccordions =
    selectedClassificationRuleKeys.length +
    selectedSupplierRuleKeys.length +
    selectedMerchantSupplierRuleKeys.length +
    selectedVatRuleIds.length +
    selectedVatSuggestionIds.length +
    selectedSpecialCaseRuleIds.length;
  const pipelineClassificationGroupSelectableCount = selectablePipelineClassificationRuleKeys.length;
  const pipelineClassificationGroupSelectedCount = useMemo(() => {
    const selectable = new Set(selectablePipelineClassificationRuleKeys.map((k) => String(k)));
    return selectedClassificationRuleKeys.filter((k) => selectable.has(String(k))).length;
  }, [selectablePipelineClassificationRuleKeys, selectedClassificationRuleKeys]);
  const pipelineClassificationGroupAllSelected =
    pipelineClassificationGroupSelectableCount > 0 &&
    pipelineClassificationGroupSelectedCount === pipelineClassificationGroupSelectableCount;
  const pipelineClassificationGroupIndeterminate =
    pipelineClassificationGroupSelectedCount > 0 &&
    pipelineClassificationGroupSelectedCount < pipelineClassificationGroupSelectableCount;

  const classificationGroupSelectableCount = selectableClassificationRuleKeys.length;
  const classificationGroupSelectedCount = useMemo(() => {
    const selectable = new Set(selectableClassificationRuleKeys.map((k) => String(k)));
    return selectedClassificationRuleKeys.filter((k) => selectable.has(String(k))).length;
  }, [selectableClassificationRuleKeys, selectedClassificationRuleKeys]);
  const classificationGroupAllSelected =
    classificationGroupSelectableCount > 0 && classificationGroupSelectedCount === classificationGroupSelectableCount;
  const classificationGroupIndeterminate =
    classificationGroupSelectedCount > 0 && classificationGroupSelectedCount < classificationGroupSelectableCount;

  const supplierGroupSelectableCount = selectableSupplierRuleKeys.length;
  const supplierGroupSelectedCount = useMemo(() => {
    const selectable = new Set(selectableSupplierRuleKeys.map((k) => String(k)));
    return selectedSupplierRuleKeys.filter((k) => selectable.has(String(k))).length;
  }, [selectableSupplierRuleKeys, selectedSupplierRuleKeys]);
  const supplierGroupAllSelected =
    supplierGroupSelectableCount > 0 && supplierGroupSelectedCount === supplierGroupSelectableCount;
  const supplierGroupIndeterminate =
    supplierGroupSelectedCount > 0 && supplierGroupSelectedCount < supplierGroupSelectableCount;

  const merchantSupplierGroupSelectableCount = selectableMerchantSupplierRuleKeys.length;
  const merchantSupplierGroupSelectedCount = useMemo(() => {
    const selectable = new Set(selectableMerchantSupplierRuleKeys.map((k) => String(k)));
    return selectedMerchantSupplierRuleKeys.filter((k) => selectable.has(String(k))).length;
  }, [selectableMerchantSupplierRuleKeys, selectedMerchantSupplierRuleKeys]);
  const merchantSupplierGroupAllSelected =
    merchantSupplierGroupSelectableCount > 0 &&
    merchantSupplierGroupSelectedCount === merchantSupplierGroupSelectableCount;
  const merchantSupplierGroupIndeterminate =
    merchantSupplierGroupSelectedCount > 0 &&
    merchantSupplierGroupSelectedCount < merchantSupplierGroupSelectableCount;

  const vatGroupSelectableCount = selectableVatRuleIds.length + selectableVatSuggestionIds.length;
  const vatGroupSelectedCount = useMemo(() => {
    const selectableRules = new Set(selectableVatRuleIds.map((id) => Number(id)));
    const selectableProposals = new Set(selectableVatSuggestionIds.map((id) => Number(id)));
    const selectedRules = selectedVatRuleIds.filter((id) => selectableRules.has(Number(id))).length;
    const selectedProposals = selectedVatSuggestionIds.filter((id) => selectableProposals.has(Number(id))).length;
    return selectedRules + selectedProposals;
  }, [selectableVatRuleIds, selectableVatSuggestionIds, selectedVatRuleIds, selectedVatSuggestionIds]);
  const vatGroupAllSelected = vatGroupSelectableCount > 0 && vatGroupSelectedCount === vatGroupSelectableCount;
  const vatGroupIndeterminate = vatGroupSelectedCount > 0 && vatGroupSelectedCount < vatGroupSelectableCount;

  const specialCaseGroupSelectableCount = selectableSpecialCaseRuleIds.length;
  const specialCaseGroupSelectedCount = useMemo(() => {
    const selectable = new Set(selectableSpecialCaseRuleIds.map((id) => Number(id)));
    return selectedSpecialCaseRuleIds.filter((id) => selectable.has(Number(id))).length;
  }, [selectableSpecialCaseRuleIds, selectedSpecialCaseRuleIds]);
  const specialCaseGroupAllSelected =
    specialCaseGroupSelectableCount > 0 && specialCaseGroupSelectedCount === specialCaseGroupSelectableCount;
  const specialCaseGroupIndeterminate =
    specialCaseGroupSelectedCount > 0 && specialCaseGroupSelectedCount < specialCaseGroupSelectableCount;

  const rulesJsonExportPayload = useMemo(() => {
    const classification_rule_ids = Array.from(
      new Set(
        selectedClassificationRuleKeys
          .map((k) => {
            const m = /^cc-(\d+)$/.exec(String(k));
            return m ? Number(m[1]) : null;
          })
          .filter((id): id is number => id != null && Number.isFinite(id) && id > 0)
      )
    ).sort((a, b) => a - b);
    const supplier_alias_ids = Array.from(
      new Set(
        [
          ...selectedSupplierRuleKeys.map((k) => {
            const m = /^sc-(\d+)$/.exec(String(k));
            return m ? Number(m[1]) : null;
          }),
          ...selectedMerchantSupplierRuleKeys.map((k) => {
            const m = /^mc-(\d+)$/.exec(String(k));
            return m ? Number(m[1]) : null;
          })
        ].filter((id): id is number => id != null && Number.isFinite(id) && id > 0)
      )
    ).sort((a, b) => a - b);
    const vat_rule_ids = Array.from(
      new Set(
        selectedVatRuleIds
          .map((id) => Number(id))
          .filter((id) => Number.isFinite(id) && id > 0)
      )
    ).sort((a, b) => a - b);
    const special_case_rule_ids = Array.from(
      new Set(
        selectedSpecialCaseRuleIds
          .map((id) => Number(id))
          .filter((id) => Number.isFinite(id) && id > 0)
      )
    ).sort((a, b) => a - b);
    return { classification_rule_ids, supplier_alias_ids, vat_rule_ids, special_case_rule_ids };
  }, [
    selectedClassificationRuleKeys,
    selectedSupplierRuleKeys,
    selectedMerchantSupplierRuleKeys,
    selectedVatRuleIds,
    selectedSpecialCaseRuleIds
  ]);

  const exportableRulesSelectionCount = useMemo(() => {
    const p = rulesJsonExportPayload;
    return (
      p.classification_rule_ids.length +
      p.supplier_alias_ids.length +
      p.vat_rule_ids.length +
      p.special_case_rule_ids.length
    );
  }, [rulesJsonExportPayload]);

  const selectedRulesSectionCount =
    Number(pipelineClassificationGroupSelectedCount > 0) +
    Number(classificationGroupSelectedCount > 0) +
    Number(merchantSupplierGroupSelectedCount > 0) +
    Number(supplierGroupSelectedCount > 0) +
    Number(vatGroupSelectedCount > 0) +
    Number(specialCaseGroupSelectedCount > 0);
  const showCrossTypeBulkActions = selectedRulesSectionCount > 1;

  const toggleClassificationGroupSelection = useCallback(() => {
    const subset = Array.from(new Set(selectableClassificationRuleKeys.map((k) => String(k))));
    const subsetSet = new Set(subset);
    setSelectedClassificationRuleKeys((prev) => {
      if (classificationGroupAllSelected) return prev.filter((k) => !subsetSet.has(String(k)));
      return Array.from(new Set(prev.concat(subset)));
    });
  }, [classificationGroupAllSelected, selectableClassificationRuleKeys]);

  const togglePipelineClassificationGroupSelection = useCallback(() => {
    const subset = Array.from(new Set(selectablePipelineClassificationRuleKeys.map((k) => String(k))));
    const subsetSet = new Set(subset);
    setSelectedClassificationRuleKeys((prev) => {
      if (pipelineClassificationGroupAllSelected) return prev.filter((k) => !subsetSet.has(String(k)));
      return Array.from(new Set(prev.concat(subset)));
    });
  }, [pipelineClassificationGroupAllSelected, selectablePipelineClassificationRuleKeys]);

  const toggleSupplierGroupSelection = useCallback(() => {
    if (supplierGroupAllSelected) {
      setSelectedSupplierRuleKeys([]);
      return;
    }
    setSelectedSupplierRuleKeys(Array.from(new Set(selectableSupplierRuleKeys)));
  }, [selectableSupplierRuleKeys, supplierGroupAllSelected]);

  const toggleMerchantSupplierGroupSelection = useCallback(() => {
    if (merchantSupplierGroupAllSelected) {
      setSelectedMerchantSupplierRuleKeys([]);
      return;
    }
    setSelectedMerchantSupplierRuleKeys(Array.from(new Set(selectableMerchantSupplierRuleKeys)));
  }, [selectableMerchantSupplierRuleKeys, merchantSupplierGroupAllSelected]);

  const toggleVatGroupSelection = useCallback(() => {
    if (vatGroupAllSelected) {
      setSelectedVatRuleIds([]);
      setSelectedVatSuggestionIds([]);
      return;
    }
    setSelectedVatRuleIds(Array.from(new Set(selectableVatRuleIds)));
    setSelectedVatSuggestionIds(Array.from(new Set(selectableVatSuggestionIds)));
  }, [selectableVatRuleIds, selectableVatSuggestionIds, vatGroupAllSelected]);

  const toggleSpecialCaseGroupSelection = useCallback(() => {
    if (specialCaseGroupAllSelected) {
      setSelectedSpecialCaseRuleIds([]);
      return;
    }
    setSelectedSpecialCaseRuleIds(Array.from(new Set(selectableSpecialCaseRuleIds)));
  }, [selectableSpecialCaseRuleIds, specialCaseGroupAllSelected]);

  const clearAllRuleSelectionsAcrossAccordions = useCallback(() => {
    setSelectedClassificationRuleKeys([]);
    setSelectedSupplierRuleKeys([]);
    setSelectedMerchantSupplierRuleKeys([]);
    setSelectedVatRuleIds([]);
    setSelectedVatSuggestionIds([]);
    setSelectedSpecialCaseRuleIds([]);
  }, []);

  const rulesFilterPills: Array<{ key: string; label: string; onDelete: () => void }> = [];
  if (rulesStatusFilter !== 'all') {
    rulesFilterPills.push({
      key: 'status',
      label: `Status: ${rulesStatusFilter === 'confirmed' ? 'Confirmed' : 'Proposed'}`,
      onDelete: () => setRulesStatusFilter('all')
    });
  }
  if (rulesTextFilter.trim()) {
    rulesFilterPills.push({
      key: 'search',
      label: `Search: ${rulesTextFilter.trim()}`,
      onDelete: () => setRulesTextFilter('')
    });
  }
  if (rulesIdFilter.trim()) {
    rulesFilterPills.push({
      key: 'id',
      label: `ID: ${rulesIdFilter.trim()}`,
      onDelete: () => setRulesIdFilter('')
    });
  }
  if (rulesProposalIdsFilter.trim()) {
    const short =
      rulesProposalIdsFilter.trim().length > 48
        ? `${rulesProposalIdsFilter.trim().slice(0, 45)}…`
        : rulesProposalIdsFilter.trim();
    rulesFilterPills.push({
      key: 'proposalIds',
      label: `Rules focus: ${short}`,
      onDelete: () => setRulesProposalIdsFilter('')
    });
  }
  if (rulesSupplierFilter !== 'all') {
    rulesFilterPills.push({
      key: 'supplier',
      label: `Supplier: ${rulesSupplierFilter}`,
      onDelete: () => setRulesSupplierFilter('all')
    });
  }
  if (rulesCategoryFilter !== 'all') {
    rulesFilterPills.push({
      key: 'category',
      label: `Category: ${rulesCategoryFilter}`,
      onDelete: () => setRulesCategoryFilter('all')
    });
  }
  if (rulesFrequencyFilter !== 'all') {
    const freqLabel =
      PAYMENT_FREQUENCY_OPTIONS.find((opt) => opt.value === rulesFrequencyFilter)?.label || rulesFrequencyFilter;
    rulesFilterPills.push({
      key: 'frequency',
      label: `Frequency: ${freqLabel}`,
      onDelete: () => setRulesFrequencyFilter('all')
    });
  }

  const jumpToRuleProposalsInRulesTab = useCallback(() => {
    const next = new URLSearchParams(searchParams);
    next.set('tab', 'rules');
    setSearchParams(next, { replace: true });
    setRulesAccordionOpen({
      proposals: true,
      pipelineStepRules: false,
      merchantSuppliers: false,
      supplierProposals: false,
      appRules: false,
      supplierRules: false,
      vatRules: false,
      specialCaseRules: false
    });
  }, [searchParams, setSearchParams]);

  const openSuppliersTabForName = useCallback(
    (name: string) => {
      const value = (name || '').trim();
      if (!value) return;
      const next = new URLSearchParams(searchParams);
      next.set('tab', 'suppliers');
      setSearchParams(next, { replace: true });
      setSuppliersCrudIdFilter(null);
      setSuppliersCrudNameFilter(value);
      setSuppliersCrudSearch('');
      setSuppliersCrudActiveFilter('all');
      setSuppliersCrudVatFilter('all');
      setSuppliersCrudCurrencyFilter('all');
    },
    [searchParams, setSearchParams]
  );

  const openSuppliersTabForId = useCallback(
    (supplierId: number | null | undefined) => {
      const id = Number(supplierId);
      if (!Number.isFinite(id) || id <= 0) return;
      const next = new URLSearchParams(searchParams);
      next.set('tab', 'suppliers');
      setSearchParams(next, { replace: true });
      setSuppliersCrudIdFilter(id);
      setSuppliersCrudNameFilter('');
      setSuppliersCrudSearch('');
      setSuppliersCrudActiveFilter('all');
      setSuppliersCrudVatFilter('all');
      setSuppliersCrudCurrencyFilter('all');
    },
    [searchParams, setSearchParams]
  );

  const openCategoriesTabForName = useCallback(
    (name: string) => {
      const value = (name || '').trim();
      if (!value) return;
      const next = new URLSearchParams(searchParams);
      next.set('tab', 'categories');
      setSearchParams(next, { replace: true });
      setCategoriesCrudNameFilter(value);
      setCategoriesCrudSearch('');
      setCategoriesCrudActiveFilter('all');
    },
    [searchParams, setSearchParams]
  );

  /** Supplier column / payloads: tag-derived ``supplier_name`` only (no merchant/JSON fallbacks). */
  const supplierDisplayForTx = useCallback((tx: ReviewTx, preferredSupplier?: string | null): string => {
    const normalizeDirect = (value?: string | null): string => {
      const raw = (value || '').trim();
      if (!raw) return '';
      const lower = raw.toLowerCase();
      if (lower === '-' || lower === '—' || lower === 'n/a' || lower === 'na' || lower === 'null') return '';
      return raw;
    };
    const direct = normalizeDirect(preferredSupplier) || normalizeDirect(tx.supplier_name);
    return direct || '—';
  }, []);

  const needsReviewSections = useMemo(() => {
    const untaggedRows = payload?.untagged || [];
    const legacyUnclassifiedRows = payload?.unclassified_needs_review || [];
    const hasSuggestedCategory = (tx: ReviewTx) => txReviewHasGranularDraft(selectedGranularByTx, tx.id);

    const unclassifiedNeedsReview = untaggedRows.filter((tx) => !hasSuggestedCategory(tx));
    const reviewAndConfirm = [
      ...legacyUnclassifiedRows,
      ...untaggedRows.filter((tx) => hasSuggestedCategory(tx))
    ];

    const seen = new Set<number>();
    const reviewAndConfirmDeduped = reviewAndConfirm.filter((tx) => {
      if (seen.has(tx.id)) return false;
      seen.add(tx.id);
      return true;
    });

    return {
      unclassifiedNeedsReview,
      reviewAndConfirm: reviewAndConfirmDeduped
    };
  }, [payload, selectedGranularByTx]);

  const hasPrimaryUnifiedDatasets = useMemo(
    () =>
      (canonicalRowsPayload?.rows || []).length > 0 ||
      needsReviewSections.unclassifiedNeedsReview.length > 0 ||
      needsReviewSections.reviewAndConfirm.length > 0 ||
      (taggedPayload?.tagged || []).length > 0 ||
      (confirmedPayload?.items || []).length > 0,
    [canonicalRowsPayload, needsReviewSections, taggedPayload, confirmedPayload]
  );

  const unifiedRows = useMemo<UnifiedReviewRow[]>(() => {
    const categoryById = new Map<number, string>();
    categoriesForPickers.forEach((c) => categoryById.set(c.id, c.name));
    const headingByAmount = (amount: number | null | undefined, taggedCf?: unknown) =>
      resolveCashFlowHeadingFromAmountAndTag(amount, taggedCf);
    const suggestedCategoryNameForTx = (tx: ReviewTx) => {
      const g = primaryGranularCategoryId(selectedGranularByTx, tx.id);
      if (selectHasChoice(g)) return categoryById.get(Number(g)) || '—';
      return '—';
    };
    const resolveSupplierId = (supplierName: string): number | null => {
      const key = String(supplierName || '').trim().toLowerCase();
      if (!key) return null;
      return supplierIdByName.get(key) ?? null;
    };

    if ((canonicalRowsPayload?.rows || []).length > 0) {
      const seenTx = new Set<number>();
      const canonicalUnique: CanonicalReviewRow[] = [];
      for (const row of canonicalRowsPayload?.rows || []) {
        const tid = Number(row.id);
        if (!Number.isFinite(tid) || tid <= 0) continue;
        if (seenTx.has(tid)) continue;
        seenTx.add(tid);
        canonicalUnique.push(row);
      }
      return dedupeUnifiedRowsOnePerLedgerTx(
        canonicalUnique.map((row) => {
          const supplier = (row.supplier || '').trim() || '—';
          const category = row.category || '—';
          const vatRate = row.vat_rate || null;
          const amount = row.amount ?? null;
          const tx = row.transaction as ReviewTx | undefined;
          const legBill = firstLegBillFields(tx);
          const bill_amount =
            coerceFiniteAmount(
              (row as UnifiedReviewRow).bill_amount ?? tx?.bill_amount ?? (tx as any)?.bill_amount ?? legBill.bill_amount
            );
          const bill_currency =
            String(
              (row as UnifiedReviewRow).bill_currency ??
                tx?.bill_currency ??
                (tx as any)?.bill_currency ??
                legBill.bill_currency ??
                ''
            )
              .trim()
              .toUpperCase() || null;
          const revolut_state =
            trimRevolutStateDisplay((row as CanonicalReviewRow & { revolut_state?: string | null }).revolut_state) ??
            trimRevolutStateDisplay(tx?.state);
          return {
            ...row,
            revolut_id: String(tx?.revolut_id ?? '').trim(),
            amount,
            bill_amount,
            bill_currency,
            supplier,
            supplier_id: row.supplier_id ?? resolveSupplierId(supplier),
            status:
              (row.status || '').trim() ||
              buildUnifiedCompletenessStatus({
                supplier,
                category,
                vat_rate: vatRate
              }),
            cashFlow: resolveCashFlowHeadingFromAmountAndTag(row.amount ?? null, row.cashFlow),
            category,
            vat_rate: vatRate,
            revolut_state
          } as UnifiedReviewRow;
        })
      );
    }

    const rows: UnifiedReviewRow[] = [];

    needsReviewSections.unclassifiedNeedsReview.forEach((tx) => {
      const amount = effectiveTxAmount(tx);
      const supplier = supplierDisplayForTx(tx);
      rows.push({
        key: `needs-${tx.id}`,
        id: tx.id,
        revolut_id: String(tx.revolut_id ?? '').trim(),
        user_id: tx.user_id ?? null,
        source_state: 'needs_review',
        date: tx.completed_at || tx.date,
        amount,
        currency: tx.currency,
        reference: tx.reference || null,
        vat_rate: null,
        supplier,
        supplier_id: resolveSupplierId(supplier),
        merchant_city: tx.merchant_city || null,
        description: tx.description || tx.reference || '—',
        account_source: tx.account_source,
        frequency: tx.payment_frequency || null,
        cashFlow: headingByAmount(amount),
        category: 'Unclassified',
        ruleId: null,
        tagOrigin: unifiedTagOriginLabel('needs_review', null),
        status: buildUnifiedCompletenessStatus({
          supplier,
          category: 'Unclassified',
          vat_rate: null
        }),
        revolut_state: trimRevolutStateDisplay(tx.state)
      });
    });

    needsReviewSections.reviewAndConfirm.forEach((tx) => {
      const amount = effectiveTxAmount(tx);
      const supplier = supplierDisplayForTx(tx);
      rows.push({
        key: `review-${tx.id}`,
        id: tx.id,
        revolut_id: String(tx.revolut_id ?? '').trim(),
        user_id: tx.user_id ?? null,
        source_state: 'review_and_confirm',
        date: tx.completed_at || tx.date,
        amount,
        currency: tx.currency,
        reference: tx.reference || null,
        vat_rate: null,
        supplier,
        supplier_id: resolveSupplierId(supplier),
        merchant_city: tx.merchant_city || null,
        description: tx.description || tx.reference || '—',
        account_source: tx.account_source,
        frequency: tx.payment_frequency || null,
        cashFlow: headingByAmount(amount),
        category: suggestedCategoryNameForTx(tx),
        ruleId: null,
        tagOrigin: unifiedTagOriginLabel('review_and_confirm', null),
        status: buildUnifiedCompletenessStatus({
          supplier,
          category: suggestedCategoryNameForTx(tx),
          vat_rate: null
        }),
        revolut_state: trimRevolutStateDisplay(tx.state)
      });
    });

    (taggedPayload?.tagged || []).forEach((row) => {
      const amount = effectiveTxAmount(row.transaction);
      const supplier = supplierDisplayForTx(row.transaction, row.supplier_name);
      rows.push({
        key: `tagged-${row.tag_uid}`,
        id: row.transaction.id,
        revolut_id: String(row.transaction.revolut_id ?? '').trim(),
        user_id: row.transaction.user_id ?? null,
        source_state: 'tagged_by_rules',
        date: row.transaction.completed_at || row.transaction.date,
        amount,
        currency: row.transaction.currency,
        reference: row.transaction.reference || null,
        vat_rate: row.vat_rate || null,
        supplier,
        supplier_id: resolveSupplierId(supplier),
        merchant_city: row.transaction.merchant_city || null,
        description: row.transaction.description || row.transaction.reference || '—',
        account_source: row.transaction.account_source,
        frequency: row.transaction.payment_frequency || inferPaymentFrequencyFromCategoryName(row.category_name) || null,
        cashFlow: headingByAmount(amount),
        category: row.category_name || '—',
        ruleId: row.rule_id ?? null,
        tagOrigin: unifiedTagOriginLabel('tagged_by_rules', row.rule_id ?? null),
        tag_uid: row.tag_uid,
        status: buildUnifiedCompletenessStatus({
          supplier,
          category: row.category_name || '—',
          vat_rate: row.vat_rate || null
        }),
        revolut_state: trimRevolutStateDisplay(row.transaction.state)
      });
    });

    (confirmedPayload?.items || []).forEach((row) => {
      const amount = effectiveTxAmount(row.transaction);
      const supplier = supplierDisplayForTx(row.transaction);
      rows.push({
        key: `confirmed-${row.transaction.id}`,
        id: row.transaction.id,
        revolut_id: String(row.transaction.revolut_id ?? '').trim(),
        user_id: row.transaction.user_id ?? null,
        source_state: 'confirmed',
        date: row.transaction.completed_at || row.transaction.date,
        amount,
        currency: row.transaction.currency,
        reference: row.transaction.reference || null,
        vat_rate: row.vat_rate || null,
        supplier,
        supplier_id: resolveSupplierId(supplier),
        merchant_city: row.transaction.merchant_city || null,
        description: row.transaction.description || row.transaction.reference || '—',
        account_source: row.transaction.account_source,
        frequency: row.transaction.payment_frequency || null,
        cashFlow: headingByAmount(amount),
        category: row.category_name || '—',
        ruleId: row.rule_id ?? null,
        tagOrigin: unifiedTagOriginLabel('confirmed', row.rule_id ?? null),
        status: buildUnifiedCompletenessStatus({
          supplier,
          category: row.category_name || '—',
          vat_rate: row.vat_rate || null
        }),
        revolut_state: trimRevolutStateDisplay(row.transaction.state)
      });
    });

    if (!hasPrimaryUnifiedDatasets) {
      const existingIds = new Set(rows.map((r) => r.id));
      const scopedUserIds = new Set(
        rows
          .map((r) => Number(r.user_id))
          .filter((uid) => Number.isFinite(uid) && uid > 0)
      );
      (allTransactionsPayload || []).forEach((tx) => {
        const txUserId = Number(tx.user_id);
        if (scopedUserIds.size > 0) {
          if (!Number.isFinite(txUserId) || txUserId <= 0 || !scopedUserIds.has(txUserId)) return;
        }
        if (existingIds.has(tx.id)) return;
        const amount = effectiveTxAmount(tx);
        const isConfirmed = !!(tx.classification_review_confirmed_at || '').trim();
        const supplier = supplierDisplayForTx(tx);
        rows.push({
          key: `alltx-${tx.id}`,
          id: tx.id,
          revolut_id: String(tx.revolut_id ?? '').trim(),
          user_id: tx.user_id ?? null,
          source_state: isConfirmed ? 'confirmed' : 'needs_review',
          date: tx.completed_at || tx.date,
          amount,
          currency: tx.currency,
          reference: tx.reference || null,
          vat_rate: null,
          supplier,
          supplier_id: resolveSupplierId(supplier),
          merchant_city: tx.merchant_city || null,
          description: tx.description || tx.reference || '—',
          account_source: tx.account_source,
          frequency: tx.payment_frequency || null,
          cashFlow: headingByAmount(amount),
          category: isConfirmed ? 'Confirmed (historic)' : 'Unclassified',
          ruleId: null,
          tagOrigin: unifiedTagOriginLabel(isConfirmed ? 'confirmed' : 'needs_review', null),
          status: buildUnifiedCompletenessStatus({
            supplier,
            category: isConfirmed ? 'Confirmed (historic)' : 'Unclassified',
            vat_rate: null
          }),
          revolut_state: trimRevolutStateDisplay(tx.state)
        });
      });
    }

    return dedupeUnifiedRowsOnePerLedgerTx(rows);
  }, [
    canonicalRowsPayload,
    categoriesForPickers,
    selectedGranularByTx,
    needsReviewSections,
      taggedPayload,
    confirmedPayload,
    allTransactionsPayload,
    hasPrimaryUnifiedDatasets,
    supplierDisplayForTx,
    supplierIdByName
  ]);

  const resolveSupplierIdForSimilarModal = useCallback(
    (supplierName: string): number | null => {
      const key = String(supplierName || '').trim().toLowerCase();
      if (!key) return null;
      return supplierIdByName.get(key) ?? null;
    },
    [supplierIdByName]
  );

  const openTxCommentModal = useCallback((row: UnifiedReviewRow) => {
    if (!row || typeof row.id !== 'number' || !Number.isFinite(row.id) || row.id <= 0) return;
    setTxCommentModalRow(row);
    setTxCommentInternalDraft(String(row.comment_internal || ''));
    setTxCommentExternalDraft(String(row.comment_external || ''));
    setTxCommentError(null);
  }, []);

  const closeTxCommentModal = useCallback(() => {
    if (txCommentSaving) return;
    setTxCommentModalRow(null);
    setTxCommentError(null);
  }, [txCommentSaving]);

  const applyTxCommentsToLocalState = useCallback(
    (txId: number, commentInternal: string, commentExternal: string) => {
      setCanonicalRowsPayload((prev) => {
        if (!prev) return prev;
        const rows = Array.isArray(prev.rows) ? prev.rows : [];
        let changed = false;
        const nextRows = rows.map((r) => {
          if (Number(r.id) !== txId) return r;
          changed = true;
          return {
            ...r,
            comment_internal: commentInternal,
            comment_external: commentExternal
          };
        });
        return changed ? { ...prev, rows: nextRows } : prev;
      });
      setTxCommentModalRow((prev) =>
        prev && Number(prev.id) === txId
          ? { ...prev, comment_internal: commentInternal, comment_external: commentExternal }
          : prev
      );
    },
    []
  );

  const applyTxFlagToLocalState = useCallback((txId: number, isFlagged: boolean) => {
    setCanonicalRowsPayload((prev) => {
      if (!prev) return prev;
      const rows = Array.isArray(prev.rows) ? prev.rows : [];
      let changed = false;
      const nextRows = rows.map((r) => {
        if (Number(r.id) !== txId) return r;
        changed = true;
        return {
          ...r,
          is_flagged: isFlagged,
          flags: {
            ...(r.flags || {}),
            is_flagged: isFlagged
          }
        };
      });
      return changed ? { ...prev, rows: nextRows } : prev;
    });
  }, []);

  const applyTxPropertyToLocalState = useCallback(
    (
      txId: number,
      propertyIds: number[],
      properties: Array<{ id: number; nickname: string | null }>
    ) => {
      const primaryId = propertyIds.length > 0 ? propertyIds[0] : null;
      const primaryNick =
        properties.length > 0 && properties[0]?.nickname
          ? String(properties[0].nickname).trim() || null
          : null;
      setCanonicalRowsPayload((prev) => {
        if (!prev) return prev;
        const rows = Array.isArray(prev.rows) ? prev.rows : [];
        let changed = false;
        const nextRows = rows.map((r) => {
          if (Number(r.id) !== txId) return r;
          changed = true;
          return {
            ...r,
            property_id: primaryId,
            property_nickname: primaryNick,
            property_ids: propertyIds,
            property_nicknames: properties.map((p) => p.nickname),
            properties
          };
        });
        return changed ? { ...prev, rows: nextRows } : prev;
      });
    },
    []
  );

  const toggleUnifiedRowFlag = useCallback(
    async (row: UnifiedReviewRow) => {
      if (!row || typeof row.id !== 'number' || row.id <= 0) return;
      if (unifiedFlagBusyTxId != null) return;
      const next = !unifiedRowIsFlagged(row);
      setUnifiedFlagBusyTxId(row.id);
      try {
        const res = await api.patch(`/api/revolut/transactions/complete/${row.id}/appended-flag`, {
          is_flagged: next
        });
        const flagged =
          typeof res.data?.is_flagged === 'boolean' ? Boolean(res.data.is_flagged) : next;
        applyTxFlagToLocalState(row.id, flagged);
      } catch (e: unknown) {
        setError(getHttpErrorMessageForUser(e, 'Failed to update investigation flag'));
      } finally {
        setUnifiedFlagBusyTxId(null);
      }
    },
    [applyTxFlagToLocalState, unifiedFlagBusyTxId]
  );

  const saveUnifiedRowProperties = useCallback(
    async (row: UnifiedReviewRow, nextPropertyIds: number[]) => {
      if (!row || typeof row.id !== 'number' || row.id <= 0) return;
      const currentIds = unifiedRowPropertyIds(row);
      const nextIds = Array.from(
        new Set(
          nextPropertyIds
            .map((n) => Number(n))
            .filter((n) => Number.isFinite(n) && n > 0)
        )
      );
      if (
        currentIds.length === nextIds.length &&
        currentIds.every((id, i) => id === nextIds[i])
      ) {
        return;
      }
      if (unifiedPropBusyTxId != null) return;
      setUnifiedPropBusyTxId(row.id);
      try {
        const res = await api.patch(`/api/revolut/transactions/complete/${row.id}/appended-property`, {
          property_ids: nextIds
        });
        const fromApi = Array.isArray(res.data?.properties)
          ? res.data.properties
              .map((p: { id?: number; nickname?: string | null }) => ({
                id: Number(p?.id),
                nickname:
                  typeof p?.nickname === 'string' && p.nickname.trim()
                    ? p.nickname.trim()
                    : null
              }))
              .filter((p: { id: number }) => Number.isFinite(p.id) && p.id > 0)
          : nextIds.map((id) => {
              const opt = txPropOptions.find((o) => o.id === id);
              return { id, nickname: opt?.nickname || null };
            });
        const ids = fromApi.map((p: { id: number }) => p.id);
        applyTxPropertyToLocalState(row.id, ids, fromApi);
        setSelectedPropertyIdsByTx((prev) => {
          const next = { ...prev };
          delete next[row.id];
          return next;
        });
      } catch (e: unknown) {
        setError(getHttpErrorMessageForUser(e, 'Failed to update property association'));
      } finally {
        setUnifiedPropBusyTxId(null);
      }
    },
    [applyTxPropertyToLocalState, txPropOptions, unifiedPropBusyTxId]
  );

  const saveTxCommentModal = useCallback(async () => {
    const row = txCommentModalRow;
    if (!row || typeof row.id !== 'number' || row.id <= 0) return;
    setTxCommentSaving(true);
    setTxCommentError(null);
    try {
      const res = await api.patch(`/api/revolut/transactions/complete/${row.id}/appended-comments`, {
        comment_internal: txCommentInternalDraft,
        comment_external: txCommentExternalDraft
      });
      const ci =
        typeof res.data?.comment_internal === 'string'
          ? res.data.comment_internal
          : String(txCommentInternalDraft || '');
      const ce =
        typeof res.data?.comment_external === 'string'
          ? res.data.comment_external
          : String(txCommentExternalDraft || '');
      applyTxCommentsToLocalState(row.id, ci, ce);
      setTxCommentInternalDraft(ci);
      setTxCommentExternalDraft(ce);
      setTxCommentModalRow(null);
    } catch (e: unknown) {
      setTxCommentError(getHttpErrorMessageForUser(e, 'Failed to save comments'));
    } finally {
      setTxCommentSaving(false);
    }
  }, [
    applyTxCommentsToLocalState,
    txCommentExternalDraft,
    txCommentInternalDraft,
    txCommentModalRow
  ]);

  const clearTxCommentModal = useCallback(async () => {
    const row = txCommentModalRow;
    if (!row || typeof row.id !== 'number' || row.id <= 0) return;
    setTxCommentSaving(true);
    setTxCommentError(null);
    try {
      await api.patch(`/api/revolut/transactions/complete/${row.id}/appended-comments`, {
        comment_internal: null,
        comment_external: null
      });
      applyTxCommentsToLocalState(row.id, '', '');
      setTxCommentInternalDraft('');
      setTxCommentExternalDraft('');
      setTxCommentModalRow(null);
    } catch (e: unknown) {
      setTxCommentError(getHttpErrorMessageForUser(e, 'Failed to clear comments'));
    } finally {
      setTxCommentSaving(false);
    }
  }, [applyTxCommentsToLocalState, txCommentModalRow]);

  const openSimilarTransactionsModal = useCallback(
    async (row: UnifiedReviewRow, opts?: { restoreSelectedIds?: number[] }) => {
      if (!row || typeof row.id !== 'number' || !Number.isFinite(row.id)) return;
      similarTxSuppressDeepLinkRef.current = false;
      setSearchParams((prev) => {
        const want = String(row.id);
        if ((prev.get(TX_REVIEW_URL_KEYS.similarTx) || '').trim() === want) return prev;
        const next = new URLSearchParams(prev);
        next.set(TX_REVIEW_URL_KEYS.similarTx, want);
        return next;
      }, { replace: true });
      setSimilarTxError('');
      setSimilarTxRows([]);
      setSimilarTxLoading(true);
      setSimilarTxBaseRow(row);
      setSimilarTxFilterAccount('all');
      setSimilarTxFilterHeading('all');
      setSimilarTxFilterAmountMin('');
      setSimilarTxFilterAmountMax('');
      setSimilarTxFilterDateFrom('');
      setSimilarTxFilterDateTo('');
      setSimilarTxFilterScoreMin(SIMILAR_TX_DEFAULT_SCORE_MIN);
      setSimilarTxFilterScoreMax('');
      setSimilarTxFilterText('');
      setSimilarTxSelectedIds([]);
      setSimilarTxBulkHeadingDraft(BULK_TAG_NO_CHANGE);
      setSimilarTxBulkSupplierDraft(BULK_TAG_NO_CHANGE);
      setSimilarTxBulkCategoryDraft(BULK_TAG_NO_CHANGE);
      setSimilarTxBulkVatDraft(BULK_TAG_NO_CHANGE);
      setSimilarTxUncommitBusy(false);
      setSimilarTxStripBusy(false);

      let ranked: SimilarUnifiedRow[] = [];
      const liveRowsById = new Map<number, UnifiedReviewRow>();
      try {
        const res = await api.get('/api/transaction-classification-rules/review-similar-transactions', {
          params: { transaction_id: row.id, limit: 50, _t: Date.now() }
        });
        const rows = Array.isArray(res.data?.rows) ? res.data.rows : [];
        const candidateIds = rows
          .map((candidate: any) => Number(candidate?.id))
          .filter((id: number) => Number.isFinite(id));
        const hydrateIds = Array.from(new Set([row.id, ...candidateIds])).sort((a, b) => a - b);
        if (hydrateIds.length > 0) {
          try {
            const live = await api.get('/api/transaction-classification-rules/review-rows', {
              params: {
                transaction_ids: hydrateIds.join(','),
                committed: 'all',
                _t: Date.now()
              }
            });
            const liveRows = Array.isArray(live.data?.rows) ? live.data.rows : [];
            for (const liveRow of liveRows) {
              const lid = Number(liveRow?.id);
              if (Number.isFinite(lid)) {
                liveRowsById.set(
                  lid,
                  mapCanonicalReviewRowToUnifiedFromApi(liveRow as CanonicalReviewRow, resolveSupplierIdForSimilarModal)
                );
              }
            }
          } catch {
            // Fallback: unifiedRows merge path below when canonical hydration fails.
          }
        }
        try {
          ranked = rows
            .filter((candidate: any) => candidate != null && Number.isFinite(Number(candidate.id)))
            .map((candidate: any) => {
              const candidateId = Number(candidate.id);
              const live = liveRowsById.get(candidateId) || null;
              const existing = unifiedRows.find((r) => Number(r.id) === candidateId) || null;
              const resolved = live || existing;
              const amount = Number.isFinite(Number(candidate.amount))
                ? Number(candidate.amount)
                : (resolved?.amount ?? null);
              const canonicalSupplier =
                resolved && String(resolved.supplier || '').trim()
                  ? String(resolved.supplier).trim()
                  : candidate.supplier != null && String(candidate.supplier).trim()
                    ? String(candidate.supplier)
                    : '—';
              const canonicalDescription = (() => {
                const fromUnified = resolved
                  ? String(resolved.description ?? '').trim() ||
                      (resolved.reference != null ? String(resolved.reference).trim() : '')
                  : '';
                if (fromUnified) return fromUnified;
                const fromApi =
                  candidate.description != null && String(candidate.description).trim()
                    ? String(candidate.description).trim()
                    : '';
                if (fromApi) return fromApi;
                return '—';
              })();
              const merged: SimilarUnifiedRow = {
                ...(resolved || {}),
                key: `similar-${candidateId}`,
                id: candidateId,
                source_state: resolved?.source_state ?? 'tagged_by_rules',
                date:
                  candidate.date != null && String(candidate.date).trim()
                    ? String(candidate.date).trim()
                    : (resolved?.date ?? null),
                amount,
                currency:
                  candidate.currency != null && String(candidate.currency).trim() !== ''
                    ? String(candidate.currency).trim().toUpperCase()
                    : resolved?.currency ?? null,
                supplier: canonicalSupplier,
                supplier_id: resolved?.supplier_id ?? null,
                revolut_id: String((resolved as UnifiedReviewRow | null)?.revolut_id ?? '').trim(),
                description: canonicalDescription,
                account_source:
                  candidate.account_source == null || candidate.account_source === ''
                    ? (resolved?.account_source ?? null)
                    : String(candidate.account_source),
                reference: resolved?.reference ?? null,
                vat_rate: resolved?.vat_rate ?? null,
                cashFlow:
                  resolved?.cashFlow ??
                  (amount != null && Number.isFinite(amount) && amount !== 0
                    ? amount > 0
                      ? 'In'
                      : 'Out'
                    : '—'),
                category: resolved?.category ?? 'Unclassified',
                ruleId: resolved?.ruleId ?? null,
                status:
                  resolved?.status ??
                  buildUnifiedCompletenessStatus({
                    supplier: canonicalSupplier,
                    category: resolved?.category ?? 'Unclassified',
                    vat_rate: resolved?.vat_rate ?? null
                  }),
                frequency: resolved?.frequency ?? null,
                merchant_city: resolved?.merchant_city ?? null,
                tagOrigin: resolved?.tagOrigin ?? 'Not tagged',
                user_id: resolved?.user_id ?? row.user_id ?? null,
                similarityScore: Number(candidate.similarityScore || 0),
                similarityReasons: sanitizeSimilarReasonList(candidate.similarityReasons)
              };
              return sanitizeSimilarUnifiedRow(merged);
            });
        } catch {
          ranked = [];
          setSimilarTxError('Could not parse similar-transactions data from the server. Try refreshing the page.');
        }
      } catch {
        try {
          ranked = unifiedRows
            .filter((candidate): candidate is UnifiedReviewRow => !!candidate && typeof candidate.id === 'number')
            .map((candidate) => scoreSimilarUnifiedRows(row, candidate))
            .filter((v): v is SimilarUnifiedRow => v != null)
            .sort((a, b) => {
              if (b.similarityScore !== a.similarityScore) return b.similarityScore - a.similarityScore;
              return compareNullableDates(b.date, a.date);
            })
            .slice(0, 50)
            .map((r) => sanitizeSimilarUnifiedRow(r));
        } catch {
          ranked = [];
          setSimilarTxError('Could not compute similar transactions from the loaded review list.');
        }
      }

      let sanitizedRows: SimilarUnifiedRow[] = [];
      try {
        sanitizedRows = [...ranked];
        const anchorUnified =
          liveRowsById.get(row.id) || unifiedRows.find((r) => Number(r.id) === row.id) || row;
        const anchorSimilar = sanitizeSimilarUnifiedRow({
          ...anchorUnified,
          key: `similar-anchor-${row.id}`,
          id: row.id,
          similarityScore: 999,
          similarityReasons: ['Opened row (eye icon)']
        });
        sanitizedRows = [anchorSimilar, ...sanitizedRows.filter((r) => Number(r.id) !== row.id)];
      } catch {
        sanitizedRows = [];
        setSimilarTxError('Could not prepare similar transactions for display. Please refresh and retry.');
      } finally {
        setSimilarTxRows(sanitizedRows);
        setSimilarTxLoading(false);
        const restore = opts?.restoreSelectedIds;
        if (restore && restore.length > 0) {
          const allowed = new Set(sanitizedRows.map((r) => r.id));
          setSimilarTxSelectedIds(restore.filter((id) => allowed.has(id)));
        }
      }
    },
    [unifiedRows, resolveSupplierIdForSimilarModal, setSearchParams]
  );

  const finishSimilarTxRulesReturnIfPending = useCallback(() => {
    const p = similarTxRulesReturnRef.current;
    if (!p) return;
    similarTxRulesReturnRef.current = null;
    setRulesQuickviewOpen(false);
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      stripRulesQuickviewSearchParams(next);
      next.set('tab', p.reviewTab);
      return next;
    }, { replace: true });
    const anchor = unifiedRows.find((r) => r.id === p.baseTxId);
    if (anchor) {
      void openSimilarTransactionsModal(anchor, { restoreSelectedIds: p.selectedIds });
    }
  }, [openSimilarTransactionsModal, setSearchParams, unifiedRows]);

  const similarTxAccountOptions = useMemo(() => {
    return Array.from(
      new Set(
        similarTxRows
          .map((tx) => formatRevolutAccountLabel(tx.account_source))
          .filter((v) => (v || '').trim() && v !== '—')
      )
    ).sort((a, b) => compareStrings(a.toLowerCase(), b.toLowerCase()));
  }, [similarTxRows]);

  const filteredSimilarTxRows = useMemo(() => {
    const minRaw = (similarTxFilterAmountMin || '').trim();
    const maxRaw = (similarTxFilterAmountMax || '').trim();
    const min = minRaw === '' ? null : Number(minRaw);
    const max = maxRaw === '' ? null : Number(maxRaw);
    const fromRaw = (similarTxFilterDateFrom || '').trim();
    const toRaw = (similarTxFilterDateTo || '').trim();
    const fromTs = fromRaw ? new Date(`${fromRaw}T00:00:00`).getTime() : null;
    const toTs = toRaw ? new Date(`${toRaw}T23:59:59.999`).getTime() : null;
    const scoreMin = parseSimilarTxScoreBound(similarTxFilterScoreMin);
    const scoreMax = parseSimilarTxScoreBound(similarTxFilterScoreMax);
    const textNeedle = (similarTxFilterText || '').trim().toLowerCase();

    return similarTxRows.filter((tx) => {
      const amount = tx.amount ?? null;
      const scoreN = Number(tx.similarityScore);
      const score = Number.isFinite(scoreN) ? scoreN : 0;
      if (min != null && Number.isFinite(min) && (amount == null || amount < min)) return false;
      if (max != null && Number.isFinite(max) && (amount == null || amount > max)) return false;
      if (scoreMin != null && score < scoreMin) return false;
      if (scoreMax != null && score > scoreMax) return false;
      if ((fromTs != null && Number.isFinite(fromTs)) || (toTs != null && Number.isFinite(toTs))) {
        const txTs = tx.date ? new Date(tx.date).getTime() : Number.NaN;
        if (!Number.isFinite(txTs)) return false;
        if (fromTs != null && Number.isFinite(fromTs) && txTs < fromTs) return false;
        if (toTs != null && Number.isFinite(toTs) && txTs > toTs) return false;
      }

      if (similarTxFilterHeading === 'in') {
        if (tx.cashFlow !== 'In') return false;
      } else if (similarTxFilterHeading === 'out') {
        if (tx.cashFlow !== 'Out') return false;
      }

      if (similarTxFilterAccount !== 'all') {
        const label = formatRevolutAccountLabel(tx.account_source);
        const want = similarTxFilterAccount.replace(/\u00a0/g, ' ').trim().toLowerCase();
        const got = label.replace(/\u00a0/g, ' ').trim().toLowerCase();
        if (got !== want) return false;
      }

      if (textNeedle) {
        const hay = [tx.supplier || '', tx.description || '', formatRevolutAccountLabel(tx.account_source)]
          .join(' ')
          .toLowerCase();
        if (!hay.includes(textNeedle)) return false;
      }

      return true;
    });
  }, [
    similarTxRows,
    similarTxFilterAmountMin,
    similarTxFilterAmountMax,
    similarTxFilterDateFrom,
    similarTxFilterDateTo,
    similarTxFilterScoreMin,
    similarTxFilterScoreMax,
    similarTxFilterHeading,
    similarTxFilterAccount,
    similarTxFilterText
  ]);

  const similarTxRowsForDisplay = useMemo<SimilarUnifiedRowView[]>(() => {
    const baseLabel = similarTxBaseRow ? formatRevolutAccountLabel(similarTxBaseRow.account_source) : '—';
    const baseComparable =
      baseLabel !== '—' && String(baseLabel).trim() ? baseLabel.trim().toLowerCase() : '';

    return filteredSimilarTxRows.map((tx) => {
      let sharedDescriptionTerms = '';
      const other: string[] = [];
      for (const reasonRaw of tx.similarityReasons || []) {
        const reason = String(reasonRaw || '').trim();
        if (!reason) continue;
        const lc = reason.toLowerCase();
        if (!sharedDescriptionTerms && lc.startsWith('shared description terms')) {
          const idx = reason.indexOf('(');
          if (idx >= 0 && reason.endsWith(')')) {
            sharedDescriptionTerms = reason.slice(idx + 1, -1).trim();
          } else {
            sharedDescriptionTerms = reason;
          }
          continue;
        }
        if (lc === 'same account' || lc.includes('same account')) {
          continue;
        }
        if (lc.startsWith('score ')) continue;
        other.push(reason);
      }
      let similarAccount: 'Y' | 'N' | '—' = '—';
      if (similarTxBaseRow != null && Number(tx.id) === Number(similarTxBaseRow.id)) {
        similarAccount = '—';
      } else if (!baseComparable) {
        similarAccount = '—';
      } else {
        const candLabel = formatRevolutAccountLabel(tx.account_source);
        const candComparable =
          candLabel !== '—' && String(candLabel).trim() ? candLabel.trim().toLowerCase() : '';
        similarAccount = !candComparable ? '—' : candComparable === baseComparable ? 'Y' : 'N';
      }
      return {
        ...tx,
        sharedDescriptionTerms,
        similarAccount,
        otherSimilarities: other.join(' | ')
      };
    });
  }, [filteredSimilarTxRows, similarTxBaseRow]);

  const excludedSimilarTxRows = useMemo(() => {
    const included = new Set(
      filteredSimilarTxRows.map((r) => Number(r.id)).filter((id) => Number.isFinite(id) && id > 0)
    );
    return similarTxRows.filter((tx) => {
      const id = Number(tx.id);
      return Number.isFinite(id) && id > 0 && !included.has(id);
    });
  }, [similarTxRows, filteredSimilarTxRows]);

  const similarTxExcludedRowsForDisplay = useMemo<SimilarUnifiedRowView[]>(() => {
    const baseLabel = similarTxBaseRow ? formatRevolutAccountLabel(similarTxBaseRow.account_source) : '—';
    const baseComparable =
      baseLabel !== '—' && String(baseLabel).trim() ? baseLabel.trim().toLowerCase() : '';

    return excludedSimilarTxRows.map((tx) => {
      let sharedDescriptionTerms = '';
      const other: string[] = [];
      for (const reasonRaw of tx.similarityReasons || []) {
        const reason = String(reasonRaw || '').trim();
        if (!reason) continue;
        const lc = reason.toLowerCase();
        if (!sharedDescriptionTerms && lc.startsWith('shared description terms')) {
          const idx = reason.indexOf('(');
          if (idx >= 0 && reason.endsWith(')')) {
            sharedDescriptionTerms = reason.slice(idx + 1, -1).trim();
          } else {
            sharedDescriptionTerms = reason;
          }
          continue;
        }
        if (lc === 'same account' || lc.includes('same account')) {
          continue;
        }
        if (lc.startsWith('score ')) continue;
        other.push(reason);
      }
      let similarAccount: 'Y' | 'N' | '—' = '—';
      if (similarTxBaseRow != null && Number(tx.id) === Number(similarTxBaseRow.id)) {
        similarAccount = '—';
      } else if (!baseComparable) {
        similarAccount = '—';
      } else {
        const candLabel = formatRevolutAccountLabel(tx.account_source);
        const candComparable =
          candLabel !== '—' && String(candLabel).trim() ? candLabel.trim().toLowerCase() : '';
        similarAccount = !candComparable ? '—' : candComparable === baseComparable ? 'Y' : 'N';
      }
      return {
        ...tx,
        sharedDescriptionTerms,
        similarAccount,
        otherSimilarities: other.join(' | ')
      };
    });
  }, [excludedSimilarTxRows, similarTxBaseRow]);

  const sortedSimilarTxRows = useMemo<SimilarUnifiedRowView[]>(
    () => sortSimilarUnifiedRowViews(similarTxRowsForDisplay, similarTxSort),
    [similarTxRowsForDisplay, similarTxSort]
  );

  const sortedSimilarTxExcludedRows = useMemo<SimilarUnifiedRowView[]>(
    () => sortSimilarUnifiedRowViews(similarTxExcludedRowsForDisplay, similarTxSort),
    [similarTxExcludedRowsForDisplay, similarTxSort]
  );

  const handleSimilarTxSort = useCallback((key: SimilarTxSortKey) => {
    setSimilarTxSort((prev) =>
      prev.key === key
        ? { key, dir: prev.dir === 'asc' ? 'desc' : 'asc' }
        : { key, dir: key === 'similarityScore' ? 'desc' : 'asc' }
    );
  }, []);

  const filteredSimilarTxIdSet = useMemo(
    () =>
      new Set(
        filteredSimilarTxRows
          .map((tx) => Number(tx.id))
          .filter((id) => Number.isFinite(id) && id > 0)
      ),
    [filteredSimilarTxRows]
  );
  const filteredSimilarTxIds = useMemo(() => Array.from(filteredSimilarTxIdSet), [filteredSimilarTxIdSet]);
  const selectedSimilarTxFilteredCount = useMemo(
    () => similarTxSelectedIds.filter((id) => filteredSimilarTxIdSet.has(id)).length,
    [similarTxSelectedIds, filteredSimilarTxIdSet]
  );
  const allFilteredSimilarTxSelected =
    filteredSimilarTxIds.length > 0 && selectedSimilarTxFilteredCount === filteredSimilarTxIds.length;

  useEffect(() => {
    setSimilarTxSelectedIds((prev) => prev.filter((id) => filteredSimilarTxIdSet.has(id)));
  }, [filteredSimilarTxIdSet]);

  const rerunRulesForSimilarModalRows = useCallback(async () => {
    if (!similarTxBaseRow || similarTxRunAllBusy) return;
    const ids = Array.from(new Set(similarTxSelectedIds.filter((id) => filteredSimilarTxIdSet.has(id))));
    if (ids.length === 0) {
      setSimilarTxError('Select at least one row in Similar transactions first.');
      return;
    }
    if (ids.length > BULK_REPROCESS_TRANSACTIONS_MAX) {
      setSimilarTxError(`Select at most ${BULK_REPROCESS_TRANSACTIONS_MAX} rows per run (current filtered list: ${ids.length}).`);
      return;
    }
    const postBody = {
      ...(similarTxBaseRow.user_id != null ? { user_id: similarTxBaseRow.user_id } : {}),
      revolut_transaction_ids: ids,
      include_tx_diagnostics: true,
      apply_classification_rules: true,
      refresh_supplier_names: true,
      apply_vat_rules: true,
      apply_special_case_rules: true,
      strip_all_classification_tags: true
    };
    try {
      setSimilarTxRunAllBusy(true);
      setSimilarTxError('');
      const confirmed = await ensureRerunConfirmedForModes(ids, similarTxBaseRow.user_id);
      if (!confirmed) return;
      let res: { data?: Record<string, any> };
      try {
        res = await api.post(
          '/api/transaction-classification-rules/bulk-reprocess-transactions',
          postBody,
          { timeout: HEAVY_RULE_JOB_TIMEOUT_MS }
        );
      } catch (firstErr: any) {
        const raw = String(firstErr?.response?.data?.error || firstErr?.message || '').trim();
        const committedIds = Array.isArray(firstErr?.response?.data?.committed_ids)
          ? firstErr.response.data.committed_ids
              .map((x: unknown) => Number(x))
              .filter((n: number) => Number.isFinite(n) && n > 0)
          : [];
        if (raw.toLowerCase().includes('committed') && committedIds.length > 0) {
          const shouldUncommit = window.confirm(
            `${committedIds.length} selected transaction(s) are committed and blocked the re-run. Uncommit them now and retry rules for all ${ids.length} selected?`
          );
          if (!shouldUncommit) {
            setSimilarTxError(raw || 'Committed transactions cannot be retagged. Uncommit first.');
            return;
          }
          await api.post('/api/transaction-classification-rules/uncommit-classification', {
            ...(similarTxBaseRow.user_id != null ? { user_id: similarTxBaseRow.user_id } : {}),
            revolut_transaction_ids: committedIds
          });
          res = await api.post(
            '/api/transaction-classification-rules/bulk-reprocess-transactions',
            postBody,
            { timeout: HEAVY_RULE_JOB_TIMEOUT_MS }
          );
        } else {
          throw firstErr;
        }
      }
      const d = (res.data || {}) as Record<string, any>;
      const diagRows = Array.isArray(d.tx_diagnostics) ? d.tx_diagnostics : [];
      const missingGranular = diagRows.filter((row: any) => {
        const counts = (row?.final_tag_counts || {}) as Record<string, any>;
        const nonHeading = Number(counts.non_heading || 0);
        const outcome = String(row?.classification_write_outcome || '').trim();
        return nonHeading <= 0 || outcome === 'not_written';
      });
      const notFound = Array.isArray(d.not_found_ids) ? d.not_found_ids.length : 0;
      if (canonicalHybridEnabled) {
        await refreshCanonicalRowsForTransactionIds(0, ids);
      }
      await refreshTransactionReviewCore({ skipCanonical: canonicalHybridEnabled });
      setUnifiedTxCategoryTagsFetchEpoch((n) => n + 1);
      const skipHints = missingGranular
        .slice(0, 5)
        .map((row: any) => {
          const tid = Number(row?.transaction_id);
          const reason = String(row?.classification_skip_reason || 'no granular tag').trim();
          const near = Array.isArray(row?.near_miss_classification_rules)
            ? row.near_miss_classification_rules[0]
            : null;
          const nearBit =
            near && (near.rule_name || near.rule_id)
              ? ` · near-miss C${near.rule_id ?? '?'}${near.rule_name ? ` ${near.rule_name}` : ''}: ${String(near.block_reason || '').trim()}`
              : '';
          return Number.isFinite(tid) ? `#${tid}: ${reason}${nearBit}` : `${reason}${nearBit}`;
        })
        .filter(Boolean);
      const baseMsg = `Rule re-run finished for ${ids.length} Similar transaction(s).`;
      const gapMsg =
        missingGranular.length > 0
          ? ` ${missingGranular.length} still lack a granular category${
              skipHints.length ? ` (${skipHints.join('; ')}${missingGranular.length > 5 ? '…' : ''})` : ''
            }.`
          : '';
      const scopeMsg =
        notFound > 0 ? ` ${notFound} id(s) were outside the target user scope and were skipped.` : '';
      setSuccessMessage(`${baseMsg}${gapMsg}${scopeMsg}`);
      await openSimilarTransactionsModal(similarTxBaseRow, { restoreSelectedIds: ids });
    } catch (err: any) {
      setSimilarTxError(err?.response?.data?.error || err?.message || 'Could not rerun rules for filtered similar transactions.');
    } finally {
      setSimilarTxRunAllBusy(false);
    }
    // ensureRerunConfirmedForModes / refresh* are declared later in this component; omit from deps
    // (same pattern as other Similar-modal actions) to avoid TDZ on render.
  }, [
    similarTxBaseRow,
    similarTxRunAllBusy,
    similarTxSelectedIds,
    filteredSimilarTxIdSet,
    openSimilarTransactionsModal,
    canonicalHybridEnabled
  ]);

  const commitSimilarModalRows = useCallback(async () => {
    if (!similarTxBaseRow || similarTxCommitBusy) return;
    const ids = Array.from(new Set(similarTxSelectedIds.filter((id) => filteredSimilarTxIdSet.has(id))));
    if (ids.length === 0) {
      setSimilarTxError('Select at least one row in Similar transactions first.');
      return;
    }
    try {
      setSimilarTxCommitBusy(true);
      setSimilarTxError('');
      const res = await api.post('/api/transaction-classification-rules/confirm-classification', {
        ...(similarTxBaseRow.user_id != null ? { user_id: similarTxBaseRow.user_id } : {}),
        transaction_ids: ids
      });
      const confirmedIds = Array.isArray(res.data?.confirmed_ids)
        ? res.data.confirmed_ids
            .map((id: unknown) => Number(id))
            .filter((id: number) => Number.isFinite(id) && id > 0)
        : ids;
      if (canonicalHybridEnabled && confirmedIds.length > 0) {
        setCanonicalRowsPayload((prev) =>
          applyCommittedIdsToCanonicalPayload(prev, confirmedIds, hideCommittedUnifiedRows)
        );
      }
      setSuccessMessage(`Committed ${confirmedIds.length} transaction(s) from Similar transactions.`);
      await openSimilarTransactionsModal(similarTxBaseRow);
    } catch (err: any) {
      setSimilarTxError(err?.response?.data?.error || err?.message || 'Could not commit selected similar transactions.');
    } finally {
      setSimilarTxCommitBusy(false);
    }
  }, [
    similarTxBaseRow,
    similarTxCommitBusy,
    similarTxSelectedIds,
    filteredSimilarTxIdSet,
    canonicalHybridEnabled,
    hideCommittedUnifiedRows,
    openSimilarTransactionsModal
  ]);

  const unifiedRowsByKey = useMemo(() => {
    const m = new Map<string, UnifiedReviewRow>();
    unifiedRows.forEach((r) => m.set(r.key, r));
    return m;
  }, [unifiedRows]);
  const unifiedRowKeyByTxId = useMemo(() => {
    const m = new Map<number, string>();
    unifiedRows.forEach((r) => {
      if (typeof r.id === 'number' && Number.isFinite(r.id) && !m.has(r.id)) {
        m.set(r.id, r.key);
      }
    });
    return m;
  }, [unifiedRows]);

  const selectedUnifiedRows = useMemo(
    () =>
      selectedUnifiedRowKeys
        .map((key) => unifiedRowsByKey.get(key))
        .filter((row): row is UnifiedReviewRow => !!row),
    [selectedUnifiedRowKeys, unifiedRowsByKey]
  );
  const selectedEditableUnifiedRows = useMemo(
    () =>
      selectedUnifiedRows.filter(
        (row) =>
          row.source_state === 'needs_review' ||
          row.source_state === 'review_and_confirm' ||
          row.source_state === 'tagged_by_rules'
      ),
    [selectedUnifiedRows]
  );
  const selectedCommittedUnifiedCount = selectedUnifiedRows.length - selectedEditableUnifiedRows.length;
  const selectedStrippableUnifiedRows = useMemo(
    () => selectedEditableUnifiedRows.filter((r) => !isUnifiedRowCommitted(r)),
    [selectedEditableUnifiedRows]
  );

  const similarModalSelectedUnifiedRows = useMemo(() => {
    const ids = Array.from(new Set(similarTxSelectedIds.filter((id) => filteredSimilarTxIdSet.has(id))));
    const out: UnifiedReviewRow[] = [];
    const seen = new Set<number>();
    for (const id of ids) {
      const key = unifiedRowKeyByTxId.get(id);
      if (!key) continue;
      const row = unifiedRowsByKey.get(key);
      if (!row || seen.has(row.id)) continue;
      seen.add(row.id);
      out.push(row);
    }
    return out;
  }, [similarTxSelectedIds, filteredSimilarTxIdSet, unifiedRowKeyByTxId, unifiedRowsByKey]);

  const similarModalEditableRows = useMemo(
    () =>
      similarModalSelectedUnifiedRows.filter(
        (row) =>
          row.source_state === 'needs_review' ||
          row.source_state === 'review_and_confirm' ||
          row.source_state === 'tagged_by_rules'
      ),
    [similarModalSelectedUnifiedRows]
  );

  const similarModalCommittedRows = useMemo(
    () => similarModalSelectedUnifiedRows.filter((r) => isUnifiedRowCommitted(r)),
    [similarModalSelectedUnifiedRows]
  );

  const similarModalBulkTagHasApply = useMemo(() => {
    const h = similarTxBulkHeadingDraft;
    const s = similarTxBulkSupplierDraft;
    const c = similarTxBulkCategoryDraft;
    const v = similarTxBulkVatDraft;
    return (
      (h !== BULK_TAG_NO_CHANGE && h !== BULK_TAG_REMOVE_TAGS) ||
      (s !== BULK_TAG_NO_CHANGE && s !== BULK_TAG_REMOVE_TAGS) ||
      (c !== BULK_TAG_NO_CHANGE && c !== BULK_TAG_REMOVE_TAGS) ||
      (v !== BULK_TAG_NO_CHANGE && v !== BULK_TAG_REMOVE_TAGS)
    );
  }, [similarTxBulkHeadingDraft, similarTxBulkSupplierDraft, similarTxBulkCategoryDraft, similarTxBulkVatDraft]);

  const bulkAppliedTagLabelOptions = useMemo(() => {
    const labels = new Set<string>();
    selectedEditableUnifiedRows.forEach((row) => {
      (unifiedTxCategoryTagsByTxId[row.id] || []).forEach((tag) => {
        const uid = String(tag.uid || '').trim();
        if (!uid) return;
        labels.add(formatUnifiedAppliedTagChipLabel(tag));
      });
    });
    return Array.from(labels).sort((a, b) => a.localeCompare(b, undefined, { sensitivity: 'base' }));
  }, [selectedEditableUnifiedRows, unifiedTxCategoryTagsByTxId]);

  /** Tag-instance counts per composite label (matches bulk delete: one row per matching tag uid). */
  const bulkAppliedTagLabelOptionCounts = useMemo(() => {
    const counts = new Map<string, number>();
    selectedEditableUnifiedRows.forEach((row) => {
      (unifiedTxCategoryTagsByTxId[row.id] || []).forEach((tag) => {
        const uid = String(tag.uid || '').trim();
        if (!uid) return;
        const label = formatUnifiedAppliedTagChipLabel(tag);
        counts.set(label, (counts.get(label) || 0) + 1);
      });
    });
    return counts;
  }, [selectedEditableUnifiedRows, unifiedTxCategoryTagsByTxId]);

  useEffect(() => {
    if (
      bulkAppliedTagDeleteComposite &&
      !bulkAppliedTagLabelOptions.includes(bulkAppliedTagDeleteComposite)
    ) {
      setBulkAppliedTagDeleteComposite('');
    }
  }, [bulkAppliedTagLabelOptions, bulkAppliedTagDeleteComposite]);

  const unifiedTxById = useMemo(() => {
    const m = new Map<number, ReviewTx>();
    if ((canonicalRowsPayload?.rows || []).length > 0) {
      (canonicalRowsPayload?.rows || []).forEach((row) => {
        const tx = row.transaction as ReviewTx | undefined;
        if (tx && typeof tx.id === 'number') {
          m.set(tx.id, tx);
          return;
        }
        m.set(row.id, {
          id: row.id,
          user_id: row.user_id ?? null,
          revolut_id: '',
          date: row.date,
          completed_at: row.date,
          amount: row.amount,
          currency: row.currency,
          merchant_name: row.supplier || null,
          merchant_city: row.merchant_city || null,
          counterparty_name: null,
          description: row.description || null,
          reference: row.reference || null,
          account_source: row.account_source || null,
          supplier_name: row.supplier || null,
          payment_frequency: row.frequency || null,
          state:
            trimRevolutStateDisplay((row.transaction as ReviewTx | undefined)?.state) ??
            trimRevolutStateDisplay((row as { revolut_state?: string | null }).revolut_state)
        });
      });
      return m;
    }
    [...needsReviewSections.unclassifiedNeedsReview, ...needsReviewSections.reviewAndConfirm].forEach((tx) => {
      m.set(tx.id, tx);
    });
    (taggedPayload?.tagged || []).forEach((row) => m.set(row.transaction.id, row.transaction));
    (confirmedPayload?.items || []).forEach((row) => m.set(row.transaction.id, row.transaction));
    if (!hasPrimaryUnifiedDatasets) {
      (allTransactionsPayload || []).forEach((tx) => {
        if (!m.has(tx.id)) m.set(tx.id, tx);
      });
    }
    return m;
  }, [canonicalRowsPayload, needsReviewSections, taggedPayload, confirmedPayload, allTransactionsPayload, hasPrimaryUnifiedDatasets]);

  const explicitReviewScopeUserId = useMemo<number | null>(() => {
    if (!user?.id) return null;
    // Owner/admin default to all-users scope unless an explicit user filter is chosen.
    return user.role === 'owner' || user.role === 'admin' ? null : Number(user.id);
  }, [user]);
  const explicitReviewScopeParams = useMemo(
    () => (explicitReviewScopeUserId != null ? { user_id: explicitReviewScopeUserId } : {}),
    [explicitReviewScopeUserId]
  );
  const effectiveReviewScopeTargetUserId = useMemo<number | null>(() => {
    const candidates = [
      canonicalRowsPayload?.scope?.target_user_id,
      pipelineCountsAllScope?.scope?.target_user_id,
      explicitReviewScopeUserId,
      user?.id,
    ];
    for (const raw of candidates) {
      const n = Number(raw);
      if (Number.isFinite(n) && n > 0) return Math.floor(n);
    }
    return null;
  }, [
    canonicalRowsPayload?.scope?.target_user_id,
    pipelineCountsAllScope?.scope?.target_user_id,
    explicitReviewScopeUserId,
    user?.id,
  ]);
  const ownerGlobalScopeParams = useMemo(
    () => ((user?.role === 'owner' || user?.role === 'admin') ? { all_users: true } : {}),
    [user?.role]
  );

  const fetchPipelineCounts = useCallback(async (): Promise<TxReviewPipelineCountsPayload | null> => {
    setPipelineCountsLoading(true);
    setPipelineCountsError('');
    try {
      const response = await api.get('/api/transaction-classification-rules/tx-review-pipeline-counts', {
        params: { _t: Date.now(), ...ownerGlobalScopeParams },
      });
      const payload = (response?.data || null) as TxReviewPipelineCountsPayload | null;
      setPipelineCountsAllScope(payload);
      return payload;
    } catch (err: any) {
      setPipelineCountsError(
        err?.response?.data?.error || err?.message || 'Failed to load TX review pipeline counts'
      );
      return null;
    } finally {
      setPipelineCountsLoading(false);
    }
  }, [ownerGlobalScopeParams]);

  const unifiedFilterOptions = useMemo(() => {
    const frequencies = Array.from(new Set(unifiedRows.map((r) => r.frequency).filter(Boolean) as string[])).sort();
    const currencies = Array.from(
      new Set(unifiedRows.map((r) => String(r.currency || '').trim().toUpperCase()).filter(Boolean))
    ).sort();
    const categories = Array.from(new Set(unifiedRows.map((r) => r.category).filter(Boolean))).sort();
    const accounts = Array.from(new Set(unifiedRows.map((r) => r.account_source).filter(Boolean) as string[])).sort();
    const revolutStates = Array.from(
      new Set(unifiedRows.map((r) => normalizeRevolutTxState(r.revolut_state)).filter(Boolean))
    ).sort();
    const vatKeySet = new Set<string>();
    VAT_RATE_PICK_OPTIONS.forEach((opt) => vatKeySet.add(vatFilterKeyFromRaw(opt)));
    unifiedRows.forEach((r) => {
      unifiedRowVatRateKeySet(r, unifiedTxCategoryTagsByTxId).forEach((k) => vatKeySet.add(k));
    });
    const pickOrder = VAT_RATE_PICK_OPTIONS.map((o) => vatFilterKeyFromRaw(o));
    const vatRates = [
      ...pickOrder.filter((k) => vatKeySet.has(k)),
      ...Array.from(vatKeySet)
        .filter((k) => !pickOrder.includes(k))
        .sort((a, b) => a.localeCompare(b)),
    ];
    return { frequencies, currencies, categories, accounts, revolutStates, vatRates };
  }, [unifiedRows, unifiedTxCategoryTagsByTxId]);

  const unifiedCategoryStarByName = useMemo(() => {
    const out = new Map<string, boolean>();
    granularCategories.forEach((c) => {
      out.set((c.name || '').trim().toLowerCase(), !!c.is_key_category);
    });
    return out;
  }, [granularCategories]);

  const unifiedCategorySearchNeedle = useMemo(
    () => (unifiedCategorySearchText || '').trim().toLowerCase(),
    [unifiedCategorySearchText]
  );

  const unifiedCategoryMenuBuckets = useMemo(() => {
    const starred: string[] = [];
    const regular: string[] = [];
    unifiedFilterOptions.categories
      .filter((name) => {
        const n = (name || '').toLowerCase();
        return !unifiedCategorySearchNeedle || n.includes(unifiedCategorySearchNeedle);
      })
      .forEach((name) => {
        if (unifiedCategoryStarByName.get((name || '').trim().toLowerCase())) starred.push(name);
        else regular.push(name);
      });
    return { starred, regular };
  }, [unifiedCategorySearchNeedle, unifiedCategoryStarByName, unifiedFilterOptions.categories]);

  const unifiedSupplierOptions = useMemo(
    () =>
      uniqueSupplierNamesCaseInsensitive(
        (canonicalSupplierOptions || [])
          .map((s) => (s || '').trim())
          .filter((s) => s.length > 0)
      ),
    [canonicalSupplierOptions]
  );

  const toggleUnifiedCategoryFilterValue = useCallback((categoryName: string) => {
    setUnifiedCategoryFilter((prev) =>
      prev.includes(categoryName) ? prev.filter((x) => x !== categoryName) : [...prev, categoryName]
    );
  }, []);

  type UnifiedFilterKey =
    | 'status'
    | 'supplier'
    | 'frequency'
    | 'currency'
    | 'heading'
    | 'category'
    | 'vat'
    | 'account'
    | 'committed'
    | 'flagged'
    | 'revolutState'
    | 'appliedTag'
    | 'tagDuplicate';

  const filterUnifiedRowsWithIgnored = useCallback(
    (ignored: UnifiedFilterKey[] = []): UnifiedReviewRow[] => {
      const ignoredSet = new Set<UnifiedFilterKey>(ignored);
      const selectedSuppliers = ignoredSet.has('supplier')
        ? new Set<string>()
        : new Set(unifiedSupplierFilter.map((s) => (s || '').trim().toLowerCase()).filter(Boolean));
      const selectedStatuses = ignoredSet.has('status') ? new Set<string>() : new Set(unifiedStatusFilter);
      const selectedFrequencies = ignoredSet.has('frequency') ? new Set<string>() : new Set(unifiedFrequencyFilter);
      const selectedCurrencies = ignoredSet.has('currency')
        ? new Set<string>()
        : new Set(unifiedCurrencyFilter.map((c) => String(c || '').trim().toUpperCase()).filter(Boolean));
      const selectedHeadings = ignoredSet.has('heading')
        ? new Set<'In' | 'Out' | 'N/A'>()
        : new Set(sanitizeHeadingFilterValues(unifiedCashFlowFilter));
      const selectedCategories = ignoredSet.has('category') ? new Set<string>() : new Set(unifiedCategoryFilter);
      const selectedVat = ignoredSet.has('vat')
        ? new Set<string>()
        : new Set(unifiedVatFilter.map((v) => vatFilterKeyFromRaw(v)));
      const selectedAppliedTags = ignoredSet.has('appliedTag')
        ? new Set<string>()
        : new Set(unifiedAppliedTagFilter.map((s) => String(s || '').trim()).filter(Boolean));
      const selectedAccounts = ignoredSet.has('account') ? new Set<string>() : new Set(unifiedAccountFilter);
      const selectedRevolutStates = ignoredSet.has('revolutState')
        ? new Set<string>()
        : new Set(unifiedRevolutStateFilter.map((s) => normalizeRevolutTxState(s)).filter(Boolean));
      const committedFilter = ignoredSet.has('committed') ? 'all' : unifiedCommittedFilter;
      const hideCommitted = ignoredSet.has('committed') ? false : hideCommittedUnifiedRows;
      const flaggedFilter = ignoredSet.has('flagged') ? 'all' : unifiedFlaggedFilter;
      const tagDuplicateFilter = ignoredSet.has('tagDuplicate') ? 'all' : unifiedTagDuplicateFilter;
      const textNeedle = unifiedTextFilter.trim().toLowerCase();
      const selectedTxIds = new Set(unifiedTxIdFilter);
      const minAmount =
        unifiedZeroOrNullAmountOnly || unifiedAmountMin.trim() === '' ? null : Number(unifiedAmountMin);
      const maxAmount =
        unifiedZeroOrNullAmountOnly || unifiedAmountMax.trim() === '' ? null : Number(unifiedAmountMax);
      const fromMs = unifiedDateFrom ? new Date(`${unifiedDateFrom}T00:00:00`).getTime() : null;
      const toMs = unifiedDateTo ? new Date(`${unifiedDateTo}T23:59:59`).getTime() : null;

      return unifiedRows.filter((r) => {
        if (hideDeclinedRevolutTransactions && isNonCompleteRevolutTxState(r.revolut_state)) return false;
        const rowCommitted = isUnifiedRowCommitted(r);
        if (committedFilter === 'committed' && !rowCommitted) return false;
        if (committedFilter === 'uncommitted' && rowCommitted) return false;
        if (committedFilter === 'all' && hideCommitted && rowCommitted) return false;
        // Mirror GET /review-rows ``exclude_confirmed_complete``: after bulk/single re-run, targeted
        // merges keep updated rows in memory even when they are now "No Tags Missing". Without this
        // client filter they stay visible until a full refetch.
        if (
          hideConfirmedCompleteUnifiedRows &&
          !rowCommitted &&
          unifiedRowDisplayedCompletenessStatus(r, unifiedTxCategoryTagsByTxId[r.id]) ===
            TAGGED_BY_ALL_RULES_STATUS
        ) {
          return false;
        }
        if (selectedTxIds.size > 0 && !selectedTxIds.has(Number(r.id))) return false;
        if (
          selectedStatuses.size > 0 &&
          !Array.from(selectedStatuses).some((value) => unifiedStatusFilterMatches(r, value, unifiedTxCategoryTagsByTxId[r.id]))
        ) {
          return false;
        }
        if (selectedSuppliers.size > 0 && !selectedSuppliers.has((r.supplier || '').toLowerCase())) return false;
        if (textNeedle && !unifiedReviewRowTextSearchHaystack(r).includes(textNeedle)) return false;
        if (unifiedZeroOrNullAmountOnly) {
          const raw = r.amount;
          const n = Number(raw);
          const isNullOrNonFinite = raw == null || !Number.isFinite(n);
          if (!isNullOrNonFinite && n !== 0) return false;
        }
        if (minAmount != null && Number.isFinite(minAmount) && (r.amount ?? 0) < minAmount) return false;
        if (maxAmount != null && Number.isFinite(maxAmount) && (r.amount ?? 0) > maxAmount) return false;
        const t = reviewTimestamp({
          id: r.id,
          revolut_id: '',
          date: r.date,
          completed_at: r.date,
          amount: r.amount,
          currency: r.currency,
          merchant_name: r.supplier,
          counterparty_name: null,
          description: r.description,
          reference: null,
          account_source: r.account_source,
          payment_frequency: r.frequency
        });
        if (fromMs != null && t < fromMs) return false;
        if (toMs != null && t > toMs) return false;
        if (selectedFrequencies.size > 0 && !selectedFrequencies.has(r.frequency || '')) return false;
        if (selectedCurrencies.size > 0 && !selectedCurrencies.has(String(r.currency || '').trim().toUpperCase())) return false;
        if (selectedHeadings.size > 0 && !selectedHeadings.has(r.cashFlow as 'In' | 'Out' | 'N/A')) return false;
        if (selectedCategories.size > 0 && !selectedCategories.has(r.category)) return false;
        if (selectedVat.size > 0) {
          const vk = unifiedRowVatRateKeySet(r, unifiedTxCategoryTagsByTxId);
          const hit = Array.from(selectedVat).some((sel) => vk.has(sel));
          if (!hit) return false;
        }
        if (selectedAppliedTags.size > 0) {
          const appliedLabels = unifiedRowAppliedTagLabelSet(r, unifiedTxCategoryTagsByTxId);
          const hit = Array.from(selectedAppliedTags).some((sel) => appliedLabels.has(sel));
          if (!hit) return false;
        }
        if (selectedAccounts.size > 0 && !selectedAccounts.has(r.account_source || '')) return false;
        if (
          selectedRevolutStates.size > 0 &&
          !selectedRevolutStates.has(normalizeRevolutTxState(r.revolut_state))
        ) {
          return false;
        }
        if (tagDuplicateFilter === 'duplicates') {
          const chips = (unifiedTxCategoryTagsByTxId[r.id] || []).filter((t) => String(t.uid || '').trim());
          if (!unifiedRowHasDisplayDuplicateTags(chips)) return false;
        }
        if (tagDuplicateFilter === 'no_duplicates') {
          const chips = (unifiedTxCategoryTagsByTxId[r.id] || []).filter((t) => String(t.uid || '').trim());
          if (unifiedRowHasDisplayDuplicateTags(chips)) return false;
        }
        if (unifiedUnclassifiedNeedsReviewOnly) {
          const cat = (r.category || '').trim();
          const matchesUcNr =
            Boolean(r.flags?.is_unclassified_needs_review) ||
            (r.source_state === 'needs_review' &&
              (cat === 'Unclassified' || cat === 'Unclassified - Needs Review'));
          if (!matchesUcNr) return false;
        }
        if (flaggedFilter === 'flagged' && !unifiedRowIsFlagged(r)) return false;
        return true;
      });
    },
    [
      unifiedRows,
      unifiedTxIdFilter,
      unifiedStatusFilter,
      unifiedSupplierFilter,
      unifiedTextFilter,
      unifiedAmountMin,
      unifiedAmountMax,
      unifiedZeroOrNullAmountOnly,
      unifiedDateFrom,
      unifiedDateTo,
      unifiedFrequencyFilter,
      unifiedCurrencyFilter,
      unifiedCashFlowFilter,
      unifiedCategoryFilter,
      unifiedVatFilter,
      unifiedAccountFilter,
      unifiedCommittedFilter,
      unifiedFlaggedFilter,
      hideCommittedUnifiedRows,
      hideConfirmedCompleteUnifiedRows,
      unifiedRevolutStateFilter,
      hideDeclinedRevolutTransactions,
      unifiedTxCategoryTagsByTxId,
      unifiedAppliedTagFilter,
      unifiedTagDuplicateFilter,
      unifiedUnclassifiedNeedsReviewOnly
    ]
  );

  const resetUnifiedFilters = useCallback(() => {
    setUnifiedStatusFilter([]);
    setUnifiedAmountMin('');
    setUnifiedAmountMax('');
    setUnifiedZeroOrNullAmountOnly(false);
    setUnifiedTxIdFilter([]);
    setUnifiedDateFrom('');
    setUnifiedDateTo('');
    setUnifiedSupplierFilter([]);
    setUnifiedTextFilter('');
    setUnifiedFrequencyFilter([]);
    setUnifiedCurrencyFilter([]);
    setUnifiedCashFlowFilter([]);
    setUnifiedCategoryFilter([]);
    setUnifiedVatFilter([]);
    setUnifiedAppliedTagFilter([]);
    setUnifiedUnclassifiedNeedsReviewOnly(false);
    setUnifiedTagDuplicateFilter('all');
    setUnifiedAccountFilter([]);
    setUnifiedStatusMenuOpen(false);
    setUnifiedHeadingMenuOpen(false);
    setUnifiedCategoryMenuOpen(false);
    setUnifiedAccountMenuOpen(false);
    setUnifiedSupplierMenuOpen(false);
    setUnifiedFrequencyMenuOpen(false);
    setUnifiedCurrencyMenuOpen(false);
    setUnifiedCommittedFilter('all');
    setUnifiedFlaggedFilter('all');
    setHideCommittedUnifiedRows(true);
    setHideConfirmedCompleteUnifiedRows(true);
    setUnifiedRevolutStateFilter([]);
    setUnifiedVatMenuOpen(false);
    setUnifiedAppliedTagMenuOpen(false);
    setHideDeclinedRevolutTransactions(true);
  }, []);

  const unifiedHasAdvancedFilters =
    unifiedZeroOrNullAmountOnly ||
    unifiedAmountMin.trim() !== '' ||
    unifiedAmountMax.trim() !== '' ||
    unifiedDateFrom !== '' ||
    unifiedDateTo !== '' ||
    unifiedTxIdFilter.length > 0 ||
    unifiedSupplierFilter.length > 0 ||
    unifiedFrequencyFilter.length > 0 ||
    unifiedCurrencyFilter.length > 0 ||
    unifiedRevolutStateFilter.length > 0 ||
    unifiedVatFilter.length > 0 ||
    unifiedAppliedTagFilter.length > 0 ||
    unifiedUnclassifiedNeedsReviewOnly ||
    unifiedTagDuplicateFilter !== 'all' ||
    unifiedCommittedFilter !== 'all' ||
    unifiedFlaggedFilter !== 'all' ||
    !hideCommittedUnifiedRows ||
    !hideConfirmedCompleteUnifiedRows ||
    !hideDeclinedRevolutTransactions;

  const filteredUnifiedRows = useMemo(() => {
    return filterUnifiedRowsWithIgnored([]);
  }, [filterUnifiedRowsWithIgnored]);

  const unifiedFilterOptionCounts = useMemo(() => {
    const statusRows = filterUnifiedRowsWithIgnored(['status']);
    const headingRows = filterUnifiedRowsWithIgnored(['heading']);
    const categoryRows = filterUnifiedRowsWithIgnored(['category']);
    const accountRows = filterUnifiedRowsWithIgnored(['account']);
    const supplierRows = filterUnifiedRowsWithIgnored(['supplier']);
    const currencyRows = filterUnifiedRowsWithIgnored(['currency']);
    const committedRows = filterUnifiedRowsWithIgnored(['committed']);
    const statuses = new Map<string, number>();
    let cashFlowMissingCount = 0;
    let supplierMissingCount = 0;
    let categoryMissingCount = 0;
    let vatMissingCount = 0;
    let allTagsMissingCount = 0;
    statusRows.forEach((r) => {
      const dsp = unifiedRowDisplayedCompletenessStatus(r, unifiedTxCategoryTagsByTxId[r.id]);
      statuses.set(dsp, (statuses.get(dsp) || 0) + 1);
      if (dsp.includes(MISSING_DIM_CASH_FLOW)) cashFlowMissingCount += 1;
      if (dsp.includes(MISSING_SUPPLIER)) supplierMissingCount += 1;
      if (dsp.includes(MISSING_CATEGORY)) categoryMissingCount += 1;
      if (dsp.includes(MISSING_VAT)) vatMissingCount += 1;
      if (dsp === ALL_TAGS_MISSING_STATUS) allTagsMissingCount += 1;
    });
    const vatConflictCountFromRows = statusRows.filter(
      (r) =>
        String(unifiedRowDisplayedCompletenessStatus(r, unifiedTxCategoryTagsByTxId[r.id]) || '')
          .trim()
          .toLowerCase() === 'vat conflict' || Boolean(r.flags?.has_vat_conflict)
    ).length;
    const pipelineCounts = pipelineCountsAllScope?.counts;
    const usePipelineCanonicalStatusCounts =
      Boolean(pipelineCounts) &&
      !unifiedHasAdvancedFilters &&
      !unifiedTextFilter.trim();
    const vatConflictCount = usePipelineCanonicalStatusCounts
      ? Number(pipelineCounts?.uncommitted_vat_conflict || 0)
      : Math.max(vatConflictCountFromRows, Number(payload?.counts?.conflicting_vat_total || 0));
    statuses.set(STATUS_FILTER_ANY_CASH_FLOW_MISSING, cashFlowMissingCount);
    statuses.set(STATUS_FILTER_ANY_SUPPLIER_MISSING, supplierMissingCount);
    statuses.set(STATUS_FILTER_ANY_CATEGORY_MISSING, categoryMissingCount);
    statuses.set(STATUS_FILTER_ANY_VAT_MISSING, vatMissingCount);
    statuses.set(STATUS_FILTER_MISSING_ALL_TAGS, allTagsMissingCount);
    statuses.set(STATUS_FILTER_VAT_CONFLICT, vatConflictCount);
    if (usePipelineCanonicalStatusCounts) {
      statuses.set(TAGGED_BY_ALL_RULES_STATUS, Number(pipelineCounts?.uncommitted_no_tags_missing || 0));
      statuses.set(
        STATUS_FILTER_ANY_CASH_FLOW_MISSING,
        Number(pipelineCounts?.uncommitted_missing_cash_flow_heading_tag || 0)
      );
      statuses.set(
        STATUS_FILTER_ANY_SUPPLIER_MISSING,
        Number(pipelineCounts?.uncommitted_missing_supplier || 0)
      );
      statuses.set(
        STATUS_FILTER_ANY_CATEGORY_MISSING,
        Number(pipelineCounts?.uncommitted_missing_category || 0)
      );
      statuses.set(
        STATUS_FILTER_ANY_VAT_MISSING,
        Number(pipelineCounts?.uncommitted_missing_vat_rate || 0)
      );
      statuses.set(STATUS_FILTER_MISSING_ALL_TAGS, Number(pipelineCounts?.uncommitted_missing_all_tags || 0));
      statuses.set(STATUS_FILTER_VAT_CONFLICT, Number(pipelineCounts?.uncommitted_vat_conflict || 0));
    }
    const headings: Record<'In' | 'Out' | 'N/A', number> = { In: 0, Out: 0, 'N/A': 0 };
    headingRows.forEach((r) => {
      if (r.cashFlow === 'In' || r.cashFlow === 'Out' || r.cashFlow === 'N/A') headings[r.cashFlow] += 1;
    });
    const categories = new Map<string, number>();
    categoryRows.forEach((r) => {
      const key = (r.category || '').trim();
      if (!key) return;
      categories.set(key, (categories.get(key) || 0) + 1);
    });
    const accounts = new Map<string, number>();
    accountRows.forEach((r) => {
      const key = (r.account_source || '').trim();
      if (!key) return;
      accounts.set(key, (accounts.get(key) || 0) + 1);
    });
    const suppliers = new Map<string, number>();
    supplierRows.forEach((r) => {
      const key = (r.supplier || '').trim();
      if (!key) return;
      suppliers.set(key, (suppliers.get(key) || 0) + 1);
    });
    const currencies = new Map<string, number>();
    currencyRows.forEach((r) => {
      const key = String(r.currency || '').trim().toUpperCase();
      if (!key) return;
      currencies.set(key, (currencies.get(key) || 0) + 1);
    });
    const revolutStateRows = filterUnifiedRowsWithIgnored(['revolutState']);
    const revolutStates = new Map<string, number>();
    revolutStateRows.forEach((r) => {
      const key = normalizeRevolutTxState(r.revolut_state);
      if (!key) return;
      revolutStates.set(key, (revolutStates.get(key) || 0) + 1);
    });
    const vatRateRows = filterUnifiedRowsWithIgnored(['vat']);
    const vatRates = new Map<string, number>();
    vatRateRows.forEach((r) => {
      unifiedRowVatRateKeySet(r, unifiedTxCategoryTagsByTxId).forEach((k) => {
        vatRates.set(k, (vatRates.get(k) || 0) + 1);
      });
    });
    const appliedTagRows = filterUnifiedRowsWithIgnored(['appliedTag']);
    const appliedTags = new Map<string, number>();
    const serverAppliedLabels =
      canonicalHybridEnabled && canonicalRowsPayload?.counts?.applied_tag_labels
        ? canonicalRowsPayload.counts.applied_tag_labels
        : null;
    if (serverAppliedLabels && typeof serverAppliedLabels === 'object') {
      Object.entries(serverAppliedLabels).forEach(([lab, cnt]) => {
        if (typeof cnt === 'number' && Number.isFinite(cnt) && cnt >= 0) {
          appliedTags.set(lab, Math.round(cnt));
        }
      });
    } else {
      appliedTagRows.forEach((r) => {
        unifiedRowAppliedTagLabelSet(r, unifiedTxCategoryTagsByTxId).forEach((lab) => {
          appliedTags.set(lab, (appliedTags.get(lab) || 0) + 1);
        });
      });
    }
    const tagDupCountRows = filterUnifiedRowsWithIgnored(['tagDuplicate']);
    let tagDupNoDuplicate = 0;
    let tagDupDuplicate = 0;
    tagDupCountRows.forEach((r) => {
      const chips = (unifiedTxCategoryTagsByTxId[r.id] || []).filter((t) => String(t.uid || '').trim());
      if (unifiedRowHasDisplayDuplicateTags(chips)) tagDupDuplicate += 1;
      else tagDupNoDuplicate += 1;
    });
    let committed = 0;
    let uncommitted = 0;
    const scopeCommitted = Number(canonicalRowsPayload?.counts?.committed_in_scope);
    const scopeUncommitted = Number(canonicalRowsPayload?.counts?.uncommitted_in_scope);
    const useCanonicalScopeCommittedCounts =
      canonicalHybridEnabled &&
      (canonicalRowsPayload?.rows || []).length > 0 &&
      Number.isFinite(scopeCommitted) &&
      Number.isFinite(scopeUncommitted) &&
      scopeCommitted + scopeUncommitted > 0;
    if (useCanonicalScopeCommittedCounts) {
      committed = Math.max(0, Math.round(scopeCommitted));
      uncommitted = Math.max(0, Math.round(scopeUncommitted));
    } else {
      committedRows.forEach((r) => {
        if (isUnifiedRowCommitted(r)) committed += 1;
        else uncommitted += 1;
      });
    }
    return {
      statuses,
      headings,
      categories,
      accounts,
      suppliers,
      currencies,
      revolutStates,
      vatRates,
      appliedTags,
      tagDupNoDuplicate,
      tagDupDuplicate,
      committed,
      uncommitted,
    };
  }, [
    filterUnifiedRowsWithIgnored,
    unifiedTxCategoryTagsByTxId,
    canonicalHybridEnabled,
    canonicalRowsPayload?.rows,
    canonicalRowsPayload?.counts,
    canonicalRowsPayload?.counts?.applied_tag_labels,
    payload?.counts?.conflicting_vat_total,
    pipelineCountsAllScope?.counts,
    unifiedHasAdvancedFilters,
    unifiedTextFilter,
  ]);

  const unifiedAppliedTagMenuLabels = useMemo(() => {
    const keys = new Set<string>(unifiedFilterOptionCounts.appliedTags.keys());
    unifiedAppliedTagFilter.forEach((lab) => {
      const s = String(lab || '').trim();
      if (s) keys.add(s);
    });
    return Array.from(keys).sort((a, b) => a.localeCompare(b, undefined, { sensitivity: 'base' }));
  }, [unifiedFilterOptionCounts.appliedTags, unifiedAppliedTagFilter]);

  const unifiedStatusOptions = useMemo(() => {
    const aggregateSet = new Set<string>(STATUS_FILTER_AGGREGATE_VALUES);
    const exactStatuses = Array.from(unifiedFilterOptionCounts.statuses.keys())
      .filter((status) => !aggregateSet.has(status))
      .filter((status) => unifiedStatusMissingCount(status) !== 1)
      .sort(compareUnifiedStatusLabel);

    const missingInIsolation = [...STATUS_FILTER_AGGREGATE_VALUES];
    const base = [...exactStatuses, ...missingInIsolation];
    const baseSet = new Set(base);
    // Keep deep-linked / URL-selected values (e.g. __status_missing_dims__:…) selectable.
    const selectedExtras = unifiedStatusFilter.filter((status) => status && !baseSet.has(status));
    return selectedExtras.length > 0 ? [...base, ...selectedExtras] : base;
  }, [unifiedFilterOptionCounts.statuses, unifiedStatusFilter]);

  const sortedUnifiedRows = useMemo(() => {
    const rows = [...filteredUnifiedRows];
    const mul = unifiedSort.dir === 'asc' ? 1 : -1;
    rows.sort((a, b) => {
      let cmp = 0;
      switch (unifiedSort.key) {
        case 'status':
          cmp = compareStrings(
            unifiedRowDisplayedCompletenessStatus(a, unifiedTxCategoryTagsByTxId[a.id]).toLowerCase(),
            unifiedRowDisplayedCompletenessStatus(b, unifiedTxCategoryTagsByTxId[b.id]).toLowerCase()
          );
          break;
        case 'committed':
          cmp = Number(isUnifiedRowCommitted(a)) - Number(isUnifiedRowCommitted(b));
          break;
        case 'id':
          cmp = a.id - b.id;
          break;
        case 'date':
          cmp = unifiedRowTimestamp(a) - unifiedRowTimestamp(b);
          break;
        case 'supplier':
          cmp = compareStrings((a.supplier || '').toLowerCase(), (b.supplier || '').toLowerCase());
          break;
        case 'description':
          cmp = compareStrings((a.description || '').toLowerCase(), (b.description || '').toLowerCase());
          break;
        case 'reference':
          cmp = compareStrings((a.reference || '').toLowerCase(), (b.reference || '').toLowerCase());
          break;
        case 'amount':
          cmp = (a.amount ?? 0) - (b.amount ?? 0);
          break;
        case 'heading':
          cmp = compareStrings((a.cashFlow || '').toLowerCase(), (b.cashFlow || '').toLowerCase());
          break;
        case 'category':
          cmp = compareStrings((a.category || '').toLowerCase(), (b.category || '').toLowerCase());
          break;
        case 'vat':
          cmp = compareStrings((a.vat_rate || '').toLowerCase(), (b.vat_rate || '').toLowerCase());
          break;
        case 'account':
          cmp = compareStrings((a.account_source || '').toLowerCase(), (b.account_source || '').toLowerCase());
          break;
        default:
          cmp = 0;
      }
      if (cmp !== 0) return cmp * mul;
      // Secondary: always newest → oldest by date when sorting any other column
      // (e.g. Supplier A–Z still lists that supplier’s newest TXs first).
      if (unifiedSort.key !== 'date') {
        const dateCmp = unifiedRowTimestamp(b) - unifiedRowTimestamp(a);
        if (dateCmp !== 0) return dateCmp;
      }
      return a.key.localeCompare(b.key);
    });
    return rows;
  }, [filteredUnifiedRows, unifiedSort, unifiedTxCategoryTagsByTxId]);

  const unifiedMonthlyChart = useMemo(() => {
    if (unifiedChartBreakdown === 'balance') {
      const byDay = new Map<string, number>();
      for (const row of filteredUnifiedRows) {
        if (!row.date || row.amount == null || Number.isNaN(Number(row.amount))) continue;
        const d = new Date(row.date);
        if (Number.isNaN(d.getTime())) continue;
        const dayKey = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(
          d.getDate()
        ).padStart(2, '0')}`;
        byDay.set(dayKey, (byDay.get(dayKey) || 0) + Number(row.amount));
      }
      if (byDay.size === 0) {
        return { chartMode: 'balance' as const, rows: [] as { xIndex: number; dayKey: string; dayLabel: string; balance: number; trend: number }[], valueType: 'amount' as const };
      }
      const sorted = Array.from(byDay.keys()).sort();
      const [fy, fm, fd] = sorted[0]!.split('-').map((x) => Number(x));
      const [ly, lm, ld] = sorted[sorted.length - 1]!.split('-').map((x) => Number(x));
      const rangeDayKeys: string[] = [];
      for (
        let cur = new Date(fy, fm - 1, fd), end = new Date(ly, lm - 1, ld);
        cur.getTime() <= end.getTime();
        cur.setDate(cur.getDate() + 1)
      ) {
        rangeDayKeys.push(
          `${cur.getFullYear()}-${String(cur.getMonth() + 1).padStart(2, '0')}-${String(
            cur.getDate()
          ).padStart(2, '0')}`
        );
      }
      let running = 0;
      const rows = rangeDayKeys.map((dayKey, i) => {
        running += byDay.get(dayKey) || 0;
        const [y, m, d] = dayKey.split('-').map((x) => Number(x));
        const dayLabel = new Date(y, m - 1, d).toLocaleDateString(undefined, {
          year: 'numeric',
          month: 'short',
          day: 'numeric'
        });
        return { xIndex: i, dayKey, dayLabel, balance: running, trend: running };
      });
      const xs = rows.map((r) => r.xIndex);
      const balances = rows.map((r) => r.balance);
      const { a, b } = linearLeastSquaresLine(xs, balances);
      const withTrend = rows.map((r) => ({
        ...r,
        trend: a + b * r.xIndex
      }));
      return {
        chartMode: 'balance' as const,
        rows: withTrend,
        valueType: 'amount' as const
      };
    }

    const isAmountIncome = unifiedChartBreakdown === 'amount_income';
    const isAmountExpense = unifiedChartBreakdown === 'amount_expense';
    const isAmountMode = isAmountIncome || isAmountExpense;

    const rowMatchesAmountMode = (row: UnifiedReviewRow): boolean => {
      if (!isAmountMode) return true;
      if (row.amount == null || Number.isNaN(Number(row.amount))) return false;
      if (isAmountIncome) return row.cashFlow === 'In';
      return row.cashFlow === 'Out';
    };

    const labelForRow = (row: UnifiedReviewRow): string => {
      if (isAmountMode) return (row.supplier || '').trim() || 'Unspecified';
      if (unifiedChartBreakdown === 'status') {
        return unifiedRowDisplayedCompletenessStatus(row, unifiedTxCategoryTagsByTxId[row.id]);
      }
      if (unifiedChartBreakdown === 'category') return (row.category || '').trim() || 'Unspecified';
      if (unifiedChartBreakdown === 'cash_flow') return (row.cashFlow || '').trim() || 'Unspecified';
      if (unifiedChartBreakdown === 'account') {
        const acct = formatRevolutAccountLabel(row.account_source);
        return acct !== '—' ? acct : 'Unspecified';
      }
      return (row.supplier || '').trim() || 'Unspecified';
    };

    const labelWeights = new Map<string, number>();
    filteredUnifiedRows.forEach((row) => {
      if (!row.date) return;
      const d = new Date(row.date);
      if (Number.isNaN(d.getTime())) return;
      if (!rowMatchesAmountMode(row)) return;
      const label = labelForRow(row);
      const delta = isAmountMode ? Math.abs(Number(row.amount)) : 1;
      labelWeights.set(label, (labelWeights.get(label) || 0) + delta);
    });

    let seriesKeys: string[] = [];
    let overflowLabel: string | null = null;
    if (unifiedChartBreakdown === 'status') {
      seriesKeys = Array.from(labelWeights.keys())
        .filter((k) => (labelWeights.get(k) || 0) > 0)
        .sort(compareUnifiedStatusLabel);
    } else if (unifiedChartBreakdown === 'cash_flow') {
      seriesKeys = ['In', 'Out', 'Unspecified'].filter((k) => (labelWeights.get(k) || 0) > 0);
    } else if (unifiedChartBreakdown === 'supplier' || isAmountMode) {
      const sortedByWeight = Array.from(labelWeights.entries()).sort((a, b) => b[1] - a[1]);
      const limit = 15;
      seriesKeys = sortedByWeight.slice(0, limit).map(([k]) => k);
      if (sortedByWeight.length > limit) overflowLabel = 'Other suppliers';
    } else {
      seriesKeys = Array.from(labelWeights.keys()).sort((a, b) =>
        a.localeCompare(b, undefined, { sensitivity: 'base' })
      );
    }

    const included = new Set(seriesKeys);
    const byMonth = new Map<string, Record<string, number | string>>();
    filteredUnifiedRows.forEach((row) => {
      if (!row.date) return;
      const d = new Date(row.date);
      if (Number.isNaN(d.getTime())) return;
      if (!rowMatchesAmountMode(row)) return;
      const monthKey = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
      if (!byMonth.has(monthKey)) {
        byMonth.set(monthKey, {
          monthKey,
          monthLabel: new Date(d.getFullYear(), d.getMonth(), 1).toLocaleDateString(undefined, {
            month: 'short',
            year: 'numeric'
          }),
          total: 0
        });
      }
      const bucket = byMonth.get(monthKey);
      if (!bucket) return;
      let label = labelForRow(row);
      if (overflowLabel && !included.has(label)) label = overflowLabel;
      if (!included.has(label) && label !== overflowLabel) return;
      const increment = isAmountMode ? Math.abs(Number(row.amount)) : 1;
      bucket[label] = Number(bucket[label] || 0) + increment;
      bucket.total = Number(bucket.total || 0) + increment;
    });

    if (overflowLabel) seriesKeys.push(overflowLabel);
    const rows = Array.from(byMonth.values()).sort((a, b) =>
      String(a.monthKey || '').localeCompare(String(b.monthKey || ''))
    );
    const seriesTotals: Record<string, number> = {};
    seriesKeys.forEach((key) => {
      seriesTotals[key] = rows.reduce((sum, row) => sum + Number(row[key] || 0), 0);
    });
    return {
      chartMode: 'stacked' as const,
      rows,
      seriesKeys,
      seriesTotals,
      overflowLabel,
      valueType: isAmountMode ? ('amount' as const) : ('count' as const)
    };
  }, [filteredUnifiedRows, unifiedChartBreakdown, unifiedTxCategoryTagsByTxId]);

  /** Paginate by rows actually loaded (sorted), not the smaller progressive-render window. */
  useEffect(() => {
    const loaded = sortedUnifiedRows.length;
    if (loaded <= 0) return;
    if (isUnifiedRowsPerPageAll(unifiedRowsPerPage)) {
      if (unifiedPage !== 0) setUnifiedPage(0);
      return;
    }
    const maxPage = Math.max(0, Math.ceil(loaded / unifiedRowsPerPage) - 1);
    if (unifiedPage > maxPage) {
      setUnifiedPage(maxPage);
    }
  }, [sortedUnifiedRows.length, unifiedPage, unifiedRowsPerPage]);

  // Only react to row *count* changes. `sortedUnifiedRows` gets a new array reference whenever
  // `unifiedTxCategoryTagsByTxId` updates (tag fetch for the current page); resetting visible count
  // on that churn caused the progressive loader to loop (25, 50, … ~450 → back to 25).
  useEffect(() => {
    const n = sortedUnifiedRows.length;
    setVisibleUnifiedRowsCount((prev) => {
      if (n === 0) return UNIFIED_ROWS_RENDER_BATCH_SIZE;
      return Math.min(prev, n);
    });
  }, [sortedUnifiedRows.length]);

  // Numeric page sizes: keep auto progressive materialisation for the current page.
  // All (continuous scroll): growth is driven by the table IntersectionObserver (below paged rows).
  useEffect(() => {
    if (isUnifiedRowsPerPageAll(unifiedRowsPerPage)) return;
    if (visibleUnifiedRowsCount >= sortedUnifiedRows.length) return;
    const timer = window.setTimeout(() => {
      setVisibleUnifiedRowsCount((prev) =>
        Math.min(prev + UNIFIED_ROWS_RENDER_BATCH_SIZE, sortedUnifiedRows.length)
      );
    }, 50);
    return () => window.clearTimeout(timer);
  }, [visibleUnifiedRowsCount, sortedUnifiedRows.length, unifiedRowsPerPage]);

  /** Ensure enough rows are materialised for the current page (progressive DOM cap). */
  useEffect(() => {
    const loaded = sortedUnifiedRows.length;
    if (loaded <= 0) return;
    const pageSize = unifiedEffectivePageSize(unifiedRowsPerPage, loaded);
    const needed = isUnifiedRowsPerPageAll(unifiedRowsPerPage)
      ? loaded
      : (unifiedPage + 1) * pageSize;
    const target = Math.min(needed, loaded);
    setVisibleUnifiedRowsCount((prev) => (prev >= target ? prev : target));
  }, [unifiedPage, unifiedRowsPerPage, sortedUnifiedRows.length]);

  const progressivelyRenderedUnifiedRows = useMemo(
    () => sortedUnifiedRows.slice(0, visibleUnifiedRowsCount),
    [sortedUnifiedRows, visibleUnifiedRowsCount]
  );

  const pagedUnifiedRows = useMemo(() => {
    if (isUnifiedRowsPerPageAll(unifiedRowsPerPage)) {
      return progressivelyRenderedUnifiedRows;
    }
    const start = unifiedPage * unifiedRowsPerPage;
    return progressivelyRenderedUnifiedRows.slice(start, start + unifiedRowsPerPage);
  }, [progressivelyRenderedUnifiedRows, unifiedPage, unifiedRowsPerPage]);

  // Continuous scroll (All): append the next DOM batch when the sentinel nears the viewport.
  useEffect(() => {
    if (!isUnifiedRowsPerPageAll(unifiedRowsPerPage)) return;
    if (visibleUnifiedRowsCount >= sortedUnifiedRows.length) return;
    const root = unifiedTableScrollRef.current;
    const sent = unifiedTableSentinelRef.current;
    if (!root || !sent) return;
    const obs = new IntersectionObserver(
      (entries) => {
        if (!entries[0]?.isIntersecting) return;
        setVisibleUnifiedRowsCount((prev) =>
          Math.min(prev + UNIFIED_ROWS_RENDER_BATCH_SIZE, sortedUnifiedRows.length)
        );
      },
      { root, rootMargin: '240px', threshold: 0 }
    );
    obs.observe(sent);
    return () => obs.disconnect();
  }, [
    unifiedRowsPerPage,
    visibleUnifiedRowsCount,
    sortedUnifiedRows.length,
    pagedUnifiedRows.length
  ]);

  const pagedUnifiedRowIdsKey = useMemo(
    () => Array.from(new Set(pagedUnifiedRows.map((r) => r.id))).sort((a, b) => a - b).join(','),
    [pagedUnifiedRows]
  );

  useEffect(() => {
    const ids = Array.from(
      new Set(pagedUnifiedRows.map((r) => Number(r.id)).filter((id) => Number.isFinite(id) && id > 0))
    );
    if (!ids.length) {
      haltUnifiedTxCategoryTagsFetch();
      // Keep cached chips — clearing here blanked Cash flow / Category during brief empty pages.
      return () => {};
    }
    const epoch = unifiedTxCategoryTagsFetchEpoch;
    const epochBumped = epoch !== lastTagsPageFetchEpochRef.current;
    const allSeededFromReviewRows = ids.every((id) => reviewRowsTagsSeededTxIdsRef.current.has(id));
    // Skip redundant page GET when review-rows already seeded every visible id (filter path).
    // Epoch bump / post-mutation refresh still fetches.
    if (allSeededFromReviewRows && !epochBumped) {
      return () => {};
    }
    const signal = beginUnifiedTxCategoryTagsFetch();
    (async () => {
      try {
        const res = await api.get('/api/revolut-transaction-category-tags/', {
          params: { transaction_ids: ids.join(','), _t: String(Date.now()) },
          signal
        });
        if (signal.aborted) return;
        const rows = Array.isArray(res.data?.tags) ? res.data.tags : [];
        const slice = buildUnifiedTxCategoryTagChipsByTxId(rows, ids);
        // Do not forceEmpty — raced empty responses must not blank seeded chips.
        applyUnifiedTxCategoryTagsSlice(slice);
        lastTagsPageFetchEpochRef.current = epoch;
      } catch (err: any) {
        if (axios.isCancel(err) || err?.code === 'ERR_CANCELED') return;
        // Keep last successful chips — a failed/aborted refresh must not wipe DB-backed UI.
      }
    })();
    return () => {
      haltUnifiedTxCategoryTagsFetch();
    };
  }, [
    pagedUnifiedRowIdsKey,
    unifiedTxCategoryTagsFetchEpoch,
    beginUnifiedTxCategoryTagsFetch,
    haltUnifiedTxCategoryTagsFetch,
    applyUnifiedTxCategoryTagsSlice
  ]);

  const allFilteredUnifiedKeys = useMemo(
    () => sortedUnifiedRows.map((row) => row.key),
    [sortedUnifiedRows]
  );

  /** Server-reported total for the unfiltered canonical queue while rows are still streaming in. */
  const unifiedCanonicalServerTotalPending = useMemo(() => {
    const loaded = sortedUnifiedRows.length;
    const serverCt = Number(canonicalRowsPayload?.count_total || 0);
    if (!canonicalHybridEnabled || serverCt <= 0) return null;
    if (loaded !== unifiedRows.length) return null;
    if (serverCt <= loaded) return null;
    return serverCt;
  }, [
    sortedUnifiedRows.length,
    unifiedRows.length,
    canonicalRowsPayload?.count_total,
    canonicalHybridEnabled
  ]);

  const unifiedPaginationLabel = useCallback(
    (_info?: { from: number; to: number; count: number }) => {
      const fmt = (n: number) => n.toLocaleString();
      const loaded = sortedUnifiedRows.length;
      const page = unifiedPage;
      const rpp = unifiedEffectivePageSize(unifiedRowsPerPage, loaded);
      const pendingServerTotal = unifiedCanonicalServerTotalPending != null;

      if (loaded <= 0 && !pendingServerTotal) {
        return unifiedRows.length > 0
          ? `showing 0 of 0 (${fmt(unifiedRows.length)} rows in queue before current filters)`
          : 'showing 0 of 0';
      }

      const from = loaded === 0 ? 0 : isUnifiedRowsPerPageAll(unifiedRowsPerPage) ? 1 : page * rpp + 1;
      const to =
        loaded === 0
          ? 0
          : isUnifiedRowsPerPageAll(unifiedRowsPerPage)
            ? loaded
            : Math.min(loaded, (page + 1) * rpp);
      const denom = pendingServerTotal ? unifiedCanonicalServerTotalPending! : loaded;
      const base = `showing ${fmt(from)}–${fmt(to)} of ${fmt(denom)}`;
      if (pendingServerTotal) {
        return `${base} (${fmt(loaded)} loaded)`;
      }
      if (loaded === unifiedRows.length) return base;
      return `${base} (${fmt(unifiedRows.length)} rows in queue before current filters)`;
    },
    [
      sortedUnifiedRows.length,
      unifiedPage,
      unifiedRowsPerPage,
      unifiedRows.length,
      unifiedCanonicalServerTotalPending
    ]
  );

  useEffect(() => {
    setSelectedClassificationRuleKeys((prev) =>
      prev.filter((k) => visibleClassificationRuleRows.some((r) => r.key === k))
    );
  }, [visibleClassificationRuleRows]);

  useEffect(() => {
    setSelectedSupplierRuleKeys((prev) =>
      prev.filter((k) => visibleSupplierRuleRows.some((r) => r.key === k))
    );
  }, [visibleSupplierRuleRows]);

  useEffect(() => {
    const valid = new Set(selectableMerchantSupplierRuleKeys);
    setSelectedMerchantSupplierRuleKeys((prev) => prev.filter((k) => valid.has(k)));
  }, [selectableMerchantSupplierRuleKeys]);

  useEffect(() => {
    const valid = new Set(sortedVisibleVatRules.map((r) => Number(r.id)));
    setSelectedVatRuleIds((prev) => prev.filter((id) => valid.has(Number(id))));
  }, [sortedVisibleVatRules]);

  useEffect(() => {
    const valid = new Set(sortedVisibleSpecialCaseRules.map((r) => Number(r.id)));
    setSelectedSpecialCaseRuleIds((prev) => prev.filter((id) => valid.has(Number(id))));
  }, [sortedVisibleSpecialCaseRules]);

  const tabParam = searchParams.get('tab');
  const tabIndex =
    tabParam != null && CLASSIFICATION_VISIBLE_TAB_KEYS.indexOf(tabParam) >= 0
      ? CLASSIFICATION_VISIBLE_TAB_KEYS.indexOf(tabParam)
      : 0;

  // URL is the single source of truth. Normalize missing/invalid ?tab= once (avoids fighting useEffects).
  useLayoutEffect(() => {
    const p = searchParams.get('tab');
    const legacyToCurrent: Record<string, string> = {
      tagged: 'needs-review',
      'category-confirmed': 'needs-review',
      'rule-proposals': 'rules',
      'supplier-rules': 'rules'
    };
    const normalized = p ? legacyToCurrent[p] || p : null;
    if (normalized == null || CLASSIFICATION_VISIBLE_TAB_KEYS.indexOf(normalized) < 0) {
      const next = new URLSearchParams(searchParams);
      next.set('tab', CLASSIFICATION_VISIBLE_TAB_KEYS[0]);
      setSearchParams(next, { replace: true });
    } else if (normalized !== p) {
      const next = new URLSearchParams(searchParams);
      next.set('tab', normalized);
      setSearchParams(next, { replace: true });
    }
  }, [searchParams, setSearchParams]);

  useEffect(() => {
    const s = loadMissingSupplierAuditSnapshot();
    if (s) {
      setMissingSupplierAudit(s.payload);
      setMissingSupplierAuditSavedAt(s.savedAt);
    }
  }, []);

  useEffect(() => {
    const onStorage = (e: StorageEvent) => {
      if (e.storageArea !== localStorage || e.key !== MISSING_SUPPLIER_AUDIT_STORAGE_KEY) return;
      if (e.newValue == null) {
        setMissingSupplierAudit(null);
        setMissingSupplierAuditSavedAt(null);
        return;
      }
      try {
        const parsed = JSON.parse(e.newValue) as { savedAt?: number; payload?: Record<string, unknown> };
        const savedAt = Number(parsed?.savedAt);
        const payload = parsed?.payload;
        if (!Number.isFinite(savedAt) || payload == null || typeof payload !== 'object' || Array.isArray(payload)) {
          return;
        }
        if (Date.now() - savedAt > missingSupplierAuditTtlMs()) {
          return;
        }
        setMissingSupplierAudit(payload as Record<string, unknown>);
        setMissingSupplierAuditSavedAt(savedAt);
      } catch {
        // ignore
      }
    };
    window.addEventListener('storage', onStorage);
    return () => window.removeEventListener('storage', onStorage);
  }, []);

  useEffect(() => {
    if (tabIndex !== 1) return;
    const focus = (searchParams.get('focus') || '').trim();
    if (focus !== 'missing-supplier-audit') return;
    setRulesAccordionOpen((prev) => ({ ...prev, supplierProposals: true }));
    const id = window.setTimeout(() => {
      missingSupplierAuditAnchorRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      const next = new URLSearchParams(searchParams);
      next.delete('focus');
      setSearchParams(next, { replace: true });
    }, 120);
    return () => window.clearTimeout(id);
  }, [tabIndex, searchParams, setSearchParams]);

  // ?similar-tx=<ledger id> — open Similar transactions on Tx Reviews (needs-review tab).
  useEffect(() => {
    if (tabIndex !== 0) return;
    const raw = (searchParams.get(TX_REVIEW_URL_KEYS.similarTx) || '').trim();
    if (!raw) {
      similarTxSuppressDeepLinkRef.current = false;
      return;
    }
    if (similarTxSuppressDeepLinkRef.current) return;
    const id = Number(raw);
    if (!Number.isFinite(id) || id <= 0) return;
    if (similarTxBaseRow?.id === id) return;
    const anchor = unifiedRows.find((r) => r.id === id) ?? buildMinimalUnifiedReviewRowForSimilarDeepLink(id);
    void openSimilarTransactionsModal(anchor);
  }, [tabIndex, searchParams, unifiedRows, similarTxBaseRow?.id, openSimilarTransactionsModal]);

  /** If close stripped state but the URL still shows similar-tx (router/React ordering), strip again before paint. */
  useLayoutEffect(() => {
    if (!similarTxSuppressDeepLinkRef.current) return;
    const raw = (searchParams.get(TX_REVIEW_URL_KEYS.similarTx) || '').trim();
    if (!raw) {
      similarTxSuppressDeepLinkRef.current = false;
      return;
    }
    stripSimilarTxUrlParamViaNavigate();
    stripSimilarTxUrlParam();
  }, [searchParams, stripSimilarTxUrlParam, stripSimilarTxUrlParamViaNavigate]);

  useEffect(() => {
    if (similarTxBaseRow == null) setSimilarTxGenerateRulesPickerOpen(false);
  }, [similarTxBaseRow]);

  // Browser back / manual URL edit removed `similar-tx` — close the modal to match.
  useEffect(() => {
    const raw = (searchParams.get(TX_REVIEW_URL_KEYS.similarTx) || '').trim();
    if (raw) return;
    if (similarTxBaseRow == null) return;
    similarTxRulesReturnRef.current = null;
    clearSimilarTxModalSurfaceStateOnly();
  }, [searchParams, similarTxBaseRow, clearSimilarTxModalSurfaceStateOnly]);

  // Similar modal only applies on Tx Reviews; leaving the tab dismisses it and clears stale deep-link state.
  useEffect(() => {
    if (tabIndex === 0) return;
    if (similarTxBaseRow == null) return;
    similarTxRulesReturnRef.current = null;
    clearSimilarTxModalSurfaceStateOnly();
    stripSimilarTxUrlParam();
  }, [tabIndex, similarTxBaseRow, clearSimilarTxModalSurfaceStateOnly, stripSimilarTxUrlParam]);

  const measureTxReviewRightPanelWidthFromVatColumn = useCallback(() => {
    if (typeof window === 'undefined') return;
    const el = unifiedVatRateColumnHeaderRef.current;
    const maxPx = Math.floor(window.innerWidth * TX_REVIEW_RIGHT_PANEL_MAX_FRAC);
    if (!el) {
      setTxReviewRightPanelWidthPx(
        Math.min(maxPx, Math.max(TX_REVIEW_RIGHT_PANEL_MIN_PX, Math.floor(window.innerWidth * 0.55)))
      );
      return;
    }
    const rect = el.getBoundingClientRect();
    // Align past VAT, then extend left across Category/Supplier so JSON is readable.
    const fromVatEdge = Math.round(window.innerWidth - rect.right + TX_REVIEW_RIGHT_PANEL_EXTRA_LEFT_PX);
    const clamped = Math.min(maxPx, Math.max(TX_REVIEW_RIGHT_PANEL_MIN_PX, fromVatEdge));
    setTxReviewRightPanelWidthPx(clamped);
  }, []);

  useLayoutEffect(() => {
    if (tabIndex !== 0) return;
    measureTxReviewRightPanelWidthFromVatColumn();
  }, [
    tabIndex,
    measureTxReviewRightPanelWidthFromVatColumn,
    useUnifiedOverflowColumns,
    loading,
    visibleUnifiedRowsCount,
    unifiedPage,
    unifiedRowsPerPage
  ]);

  useEffect(() => {
    if (tabIndex !== 0) return;
    let raf = 0;
    const schedule = () => {
      if (raf) return;
      raf = window.requestAnimationFrame(() => {
        raf = 0;
        measureTxReviewRightPanelWidthFromVatColumn();
      });
    };
    window.addEventListener('resize', schedule);
    window.addEventListener('scroll', schedule, true);
    return () => {
      window.removeEventListener('resize', schedule);
      window.removeEventListener('scroll', schedule, true);
      if (raf) window.cancelAnimationFrame(raf);
    };
  }, [tabIndex, measureTxReviewRightPanelWidthFromVatColumn]);

  useLayoutEffect(() => {
    if (!rulesQuickviewOpen && !txJsonViewerOpen && !unifiedRerunLogOpen) return;
    measureTxReviewRightPanelWidthFromVatColumn();
  }, [rulesQuickviewOpen, txJsonViewerOpen, unifiedRerunLogOpen, measureTxReviewRightPanelWidthFromVatColumn]);

  useEffect(() => {
    const scrollBottom = (el: HTMLDivElement | null) => {
      if (!el) return;
      el.scrollTop = el.scrollHeight;
    };
    if (unifiedRerunLogOpen) {
      scrollBottom(rerunLogScrollBoxRef.current);
      return;
    }
    if (rulesQuickviewOpen && rulesQuickviewMainTab === 'other') {
      scrollBottom(qvOtherTabScrollRef.current);
    }
  }, [unifiedRerunLogBlocks, unifiedRerunLogOpen, rulesQuickviewOpen, rulesQuickviewMainTab]);

  useEffect(() => {
    if (!rulesApplyRunSnapshot || !rulesQuickviewOpen || rulesQuickviewMainTab !== 'other') return;
    qvOtherTabScrollRef.current?.scrollTo({ top: 0, behavior: 'smooth' });
  }, [rulesApplyRunSnapshot, rulesQuickviewOpen, rulesQuickviewMainTab]);

  useEffect(() => {
    const sanitized = sanitizeHeadingFilterValues(unifiedCashFlowFilter);
    if (
      sanitized.length !== unifiedCashFlowFilter.length ||
      !sanitized.every((value, idx) => value === unifiedCashFlowFilter[idx])
    ) {
      setUnifiedCashFlowFilter(sanitized);
    }
  }, [unifiedCashFlowFilter]);

  useEffect(() => {
    const status = readSearchParamList(searchParams, TX_REVIEW_URL_KEYS.status);
    const suppliers = readSearchParamList(searchParams, TX_REVIEW_URL_KEYS.supplier);
    const txIds = parseTxIdFilterList((searchParams.get(TX_REVIEW_URL_KEYS.ids) || '').trim());
    const text = (searchParams.get(TX_REVIEW_URL_KEYS.text) || '').trim();
    const amountMin = (searchParams.get(TX_REVIEW_URL_KEYS.amountMin) || '').trim();
    const amountMax = (searchParams.get(TX_REVIEW_URL_KEYS.amountMax) || '').trim();
    const zeroNullRaw = (searchParams.get(TX_REVIEW_URL_KEYS.zeroNullAmount) || '').trim().toLowerCase();
    const zeroNullOn =
      zeroNullRaw === '1' || zeroNullRaw === 'true' || zeroNullRaw === 'yes' || zeroNullRaw === 'on';
    const dateFrom = (searchParams.get(TX_REVIEW_URL_KEYS.dateFrom) || '').trim();
    const dateTo = (searchParams.get(TX_REVIEW_URL_KEYS.dateTo) || '').trim();
    const frequencies = readSearchParamList(searchParams, TX_REVIEW_URL_KEYS.frequency);
    const currencies = readSearchParamList(searchParams, TX_REVIEW_URL_KEYS.currency);
    const headings = sanitizeHeadingFilterValues(readSearchParamList(searchParams, TX_REVIEW_URL_KEYS.heading));
    const categories = readSearchParamList(searchParams, TX_REVIEW_URL_KEYS.category);
    const vatRatesFromUrl = Array.from(
      new Set(readSearchParamList(searchParams, TX_REVIEW_URL_KEYS.vat).map((v) => vatFilterKeyFromRaw(v)))
    );
    const appliedTagsFromUrl = readSearchParamList(searchParams, TX_REVIEW_URL_KEYS.appliedTag);
    const accounts = readSearchParamList(searchParams, TX_REVIEW_URL_KEYS.account);
    const committedRaw = (searchParams.get(TX_REVIEW_URL_KEYS.committed) || '').trim();
    const committed =
      committedRaw === 'committed' || committedRaw === 'uncommitted' ? committedRaw : 'all';
    const hideCommittedRaw = (searchParams.get(TX_REVIEW_URL_KEYS.hideCommitted) || '').trim().toLowerCase();
    const hideCommitted = hideCommittedRaw === '0' || hideCommittedRaw === 'false' ? false : true;
    const excludeCompleteRaw = (searchParams.get(TX_REVIEW_URL_KEYS.excludeConfirmedComplete) || '')
      .trim()
      .toLowerCase();
    const hideConfirmedComplete =
      excludeCompleteRaw === '0' || excludeCompleteRaw === 'false' ? false : true;
    const hideDeclinedParam = searchParams.get(TX_REVIEW_URL_KEYS.hideDeclined);
    const hideDeclinedRaw = (hideDeclinedParam || '').trim().toLowerCase();
    let hideDeclined = hideDeclinedRaw === '0' || hideDeclinedRaw === 'false' ? false : true;
    if (hideDeclinedParam == null || hideDeclinedParam === '') {
      const legacyInc = (searchParams.get('tx_include_declined') || '').trim().toLowerCase();
      if (legacyInc === '1' || legacyInc === 'true' || legacyInc === 'yes') {
        hideDeclined = false;
      }
    }
    const revolutStateFromUrl = readSearchParamList(searchParams, TX_REVIEW_URL_KEYS.revolutState)
      .map((s) => normalizeRevolutTxState(s))
      .filter(Boolean);
    const tagDupRaw = (searchParams.get(TX_REVIEW_URL_KEYS.tagDup) || '').trim().toLowerCase();
    const tagDupFromUrl: 'all' | 'no_duplicates' | 'duplicates' =
      tagDupRaw === 'dup' || tagDupRaw === 'duplicates' || tagDupRaw === '1'
        ? 'duplicates'
        : tagDupRaw === 'no_dup' || tagDupRaw === 'no_duplicates' || tagDupRaw === 'none' || tagDupRaw === '0'
          ? 'no_duplicates'
          : 'all';
    const rowsPerPage = parseUnifiedRowsPerPageParam(searchParams.get(TX_REVIEW_URL_KEYS.rowsPerPage));

    setUnifiedStatusFilter(status);
    setUnifiedSupplierFilter(suppliers);
    setUnifiedTxIdFilter(txIds);
    setUnifiedTextFilter(text);
    setUnifiedAmountMin(zeroNullOn ? '' : amountMin);
    setUnifiedAmountMax(zeroNullOn ? '' : amountMax);
    setUnifiedZeroOrNullAmountOnly(zeroNullOn);
    setUnifiedDateFrom(dateFrom);
    setUnifiedDateTo(dateTo);
    setUnifiedFrequencyFilter(frequencies);
    setUnifiedCurrencyFilter(currencies);
    setUnifiedCashFlowFilter(headings);
    setUnifiedCategoryFilter(categories);
    setUnifiedVatFilter(vatRatesFromUrl);
    setUnifiedAppliedTagFilter(appliedTagsFromUrl);
    const ucNrRaw = (searchParams.get(TX_REVIEW_URL_KEYS.unclassifiedNeedsReview) || '').trim().toLowerCase();
    setUnifiedUnclassifiedNeedsReviewOnly(
      ucNrRaw === '1' || ucNrRaw === 'true' || ucNrRaw === 'yes' || ucNrRaw === 'on'
    );
    setUnifiedAccountFilter(accounts);
    setUnifiedCommittedFilter(committed);
    const flaggedRaw = (searchParams.get(TX_REVIEW_URL_KEYS.flagged) || '').trim().toLowerCase();
    setUnifiedFlaggedFilter(
      flaggedRaw === '1' ||
        flaggedRaw === 'true' ||
        flaggedRaw === 'yes' ||
        flaggedRaw === 'on' ||
        flaggedRaw === 'flagged' ||
        flaggedRaw === 'only'
        ? 'flagged'
        : 'all'
    );
    setHideCommittedUnifiedRows(hideCommitted);
    setHideConfirmedCompleteUnifiedRows(hideConfirmedComplete);
    setHideDeclinedRevolutTransactions(hideDeclined);
    setUnifiedRevolutStateFilter(revolutStateFromUrl);
    setUnifiedTagDuplicateFilter(tagDupFromUrl);
    if (rowsPerPage != null) {
      setUnifiedRowsPerPage(rowsPerPage);
      setUnifiedPage(0);
    }
    txReviewFiltersHydratedFromUrlRef.current = true;
  }, [searchParams]);

  useEffect(() => {
    if (!txReviewFiltersHydratedFromUrlRef.current) return;
    const next = new URLSearchParams(searchParams);
    writeSearchParamList(next, TX_REVIEW_URL_KEYS.status, unifiedStatusFilter);
    writeSearchParamList(next, TX_REVIEW_URL_KEYS.supplier, unifiedSupplierFilter);
    if (unifiedTxIdFilter.length > 0) next.set(TX_REVIEW_URL_KEYS.ids, unifiedTxIdFilter.join(','));
    else next.delete(TX_REVIEW_URL_KEYS.ids);

    const text = unifiedTextFilter.trim();
    if (text) next.set(TX_REVIEW_URL_KEYS.text, text);
    else next.delete(TX_REVIEW_URL_KEYS.text);

    const amountMin = unifiedAmountMin.trim();
    if (amountMin) next.set(TX_REVIEW_URL_KEYS.amountMin, amountMin);
    else next.delete(TX_REVIEW_URL_KEYS.amountMin);

    const amountMax = unifiedAmountMax.trim();
    if (amountMax) next.set(TX_REVIEW_URL_KEYS.amountMax, amountMax);
    else next.delete(TX_REVIEW_URL_KEYS.amountMax);

    if (unifiedZeroOrNullAmountOnly) next.set(TX_REVIEW_URL_KEYS.zeroNullAmount, '1');
    else next.delete(TX_REVIEW_URL_KEYS.zeroNullAmount);

    if (unifiedDateFrom) next.set(TX_REVIEW_URL_KEYS.dateFrom, unifiedDateFrom);
    else next.delete(TX_REVIEW_URL_KEYS.dateFrom);

    if (unifiedDateTo) next.set(TX_REVIEW_URL_KEYS.dateTo, unifiedDateTo);
    else next.delete(TX_REVIEW_URL_KEYS.dateTo);

    writeSearchParamList(next, TX_REVIEW_URL_KEYS.frequency, unifiedFrequencyFilter);
    writeSearchParamList(next, TX_REVIEW_URL_KEYS.currency, unifiedCurrencyFilter);
    writeSearchParamList(next, TX_REVIEW_URL_KEYS.heading, sanitizeHeadingFilterValues(unifiedCashFlowFilter));
    writeSearchParamList(next, TX_REVIEW_URL_KEYS.category, unifiedCategoryFilter);
    writeSearchParamList(next, TX_REVIEW_URL_KEYS.vat, unifiedVatFilter);
    writeSearchParamList(next, TX_REVIEW_URL_KEYS.appliedTag, unifiedAppliedTagFilter);
    writeSearchParamList(next, TX_REVIEW_URL_KEYS.account, unifiedAccountFilter);
    if (unifiedUnclassifiedNeedsReviewOnly) next.set(TX_REVIEW_URL_KEYS.unclassifiedNeedsReview, '1');
    else next.delete(TX_REVIEW_URL_KEYS.unclassifiedNeedsReview);

    if (unifiedCommittedFilter !== 'all') next.set(TX_REVIEW_URL_KEYS.committed, unifiedCommittedFilter);
    else next.delete(TX_REVIEW_URL_KEYS.committed);

    if (unifiedFlaggedFilter === 'flagged') next.set(TX_REVIEW_URL_KEYS.flagged, 'flagged');
    else next.delete(TX_REVIEW_URL_KEYS.flagged);

    if (!hideCommittedUnifiedRows) next.set(TX_REVIEW_URL_KEYS.hideCommitted, '0');
    else next.delete(TX_REVIEW_URL_KEYS.hideCommitted);

    if (!hideConfirmedCompleteUnifiedRows) next.set(TX_REVIEW_URL_KEYS.excludeConfirmedComplete, '0');
    else next.delete(TX_REVIEW_URL_KEYS.excludeConfirmedComplete);

    if (!hideDeclinedRevolutTransactions) next.set(TX_REVIEW_URL_KEYS.hideDeclined, '0');
    else next.delete(TX_REVIEW_URL_KEYS.hideDeclined);
    next.delete('tx_include_declined');

    writeSearchParamList(next, TX_REVIEW_URL_KEYS.revolutState, unifiedRevolutStateFilter);

    if (unifiedTagDuplicateFilter === 'no_duplicates') next.set(TX_REVIEW_URL_KEYS.tagDup, 'no_dup');
    else if (unifiedTagDuplicateFilter === 'duplicates') next.set(TX_REVIEW_URL_KEYS.tagDup, 'dup');
    else next.delete(TX_REVIEW_URL_KEYS.tagDup);

    if (isUnifiedRowsPerPageAll(unifiedRowsPerPage)) next.set(TX_REVIEW_URL_KEYS.rowsPerPage, 'all');
    else if (unifiedRowsPerPage !== 20) next.set(TX_REVIEW_URL_KEYS.rowsPerPage, String(unifiedRowsPerPage));
    else next.delete(TX_REVIEW_URL_KEYS.rowsPerPage);

    if (similarTxSuppressDeepLinkRef.current) {
      next.delete(TX_REVIEW_URL_KEYS.similarTx);
    }

    if (next.toString() !== searchParams.toString()) {
      setSearchParams(next, { replace: true });
    }
  }, [
    hideCommittedUnifiedRows,
    hideConfirmedCompleteUnifiedRows,
    hideDeclinedRevolutTransactions,
    unifiedRevolutStateFilter,
    unifiedTagDuplicateFilter,
    searchParams,
    setSearchParams,
    unifiedAccountFilter,
    unifiedAmountMax,
    unifiedAmountMin,
    unifiedZeroOrNullAmountOnly,
    unifiedCategoryFilter,
    unifiedVatFilter,
    unifiedAppliedTagFilter,
    unifiedUnclassifiedNeedsReviewOnly,
    unifiedCommittedFilter,
    unifiedFlaggedFilter,
    unifiedCurrencyFilter,
    unifiedDateFrom,
    unifiedDateTo,
    unifiedFrequencyFilter,
    unifiedCashFlowFilter,
    unifiedRowsPerPage,
    unifiedStatusFilter,
    unifiedSupplierFilter,
    unifiedTxIdFilter,
    unifiedTextFilter,
    similarTxUrlCloseEpoch
  ]);

  useEffect(() => {
    const rulesText = (searchParams.get(RULES_URL_KEYS.text) || '').trim();
    const rulesId = (searchParams.get(RULES_URL_KEYS.id) || '').trim();
    const rulesProposalIds = (searchParams.get(RULES_URL_KEYS.proposalIds) || '').trim();
    const rulesSupplier = (searchParams.get(RULES_URL_KEYS.supplier) || '').trim();
    const rulesCategory = (searchParams.get(RULES_URL_KEYS.category) || '').trim();
    const rulesFrequency = (searchParams.get(RULES_URL_KEYS.frequency) || '').trim();
    const rulesStatusRaw = (searchParams.get(RULES_URL_KEYS.status) || '').trim().toLowerCase();
    const rulesStatus =
      rulesStatusRaw === 'proposed' || rulesStatusRaw === 'confirmed'
        ? (rulesStatusRaw as RuleRowState)
        : 'all';

    setRulesTextFilter(rulesText);
    setRulesIdFilter(rulesId);
    setRulesProposalIdsFilter(rulesProposalIds);
    setRulesSupplierFilter(rulesSupplier || 'all');
    setRulesCategoryFilter(rulesCategory || 'all');
    setRulesFrequencyFilter(rulesFrequency || 'all');
    setRulesStatusFilter(rulesStatus);
    rulesFiltersHydratedFromUrlRef.current = true;
    // URL is source of truth after hydrate — allow write-back again.
    rulesFiltersUrlWriteSuspendedRef.current = false;
  }, [searchParams]);

  useEffect(() => {
    if (!rulesFiltersHydratedFromUrlRef.current) return;
    if (rulesFiltersUrlWriteSuspendedRef.current) return;
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);

      const rulesText = rulesTextFilter.trim();
      if (rulesText) next.set(RULES_URL_KEYS.text, rulesText);
      else next.delete(RULES_URL_KEYS.text);

      const rulesId = rulesIdFilter.trim();
      if (rulesId) next.set(RULES_URL_KEYS.id, rulesId);
      else next.delete(RULES_URL_KEYS.id);

      const proposalIds = rulesProposalIdsFilter.trim();
      if (proposalIds) next.set(RULES_URL_KEYS.proposalIds, proposalIds);
      else next.delete(RULES_URL_KEYS.proposalIds);

      if (rulesSupplierFilter !== 'all') next.set(RULES_URL_KEYS.supplier, rulesSupplierFilter);
      else next.delete(RULES_URL_KEYS.supplier);

      if (rulesCategoryFilter !== 'all') next.set(RULES_URL_KEYS.category, rulesCategoryFilter);
      else next.delete(RULES_URL_KEYS.category);

      if (rulesFrequencyFilter !== 'all') next.set(RULES_URL_KEYS.frequency, rulesFrequencyFilter);
      else next.delete(RULES_URL_KEYS.frequency);

      if (rulesStatusFilter !== 'all') next.set(RULES_URL_KEYS.status, rulesStatusFilter);
      else next.delete(RULES_URL_KEYS.status);

      return next.toString() === prev.toString() ? prev : next;
    }, { replace: true });
  }, [
    rulesCategoryFilter,
    rulesFrequencyFilter,
    rulesIdFilter,
    rulesProposalIdsFilter,
    rulesStatusFilter,
    rulesSupplierFilter,
    rulesTextFilter,
    setSearchParams,
  ]);

  /** Suspend Rules filter write-back around programmatic URL updates; release if URL is unchanged. */
  const setRulesSearchParamsProgrammatically = useCallback(
    (updater: (prev: URLSearchParams) => URLSearchParams, opts?: { replace?: boolean }) => {
      rulesFiltersUrlWriteSuspendedRef.current = true;
      setSearchParams((prev) => {
        const next = updater(new URLSearchParams(prev));
        if (next.toString() === prev.toString()) {
          queueMicrotask(() => {
            rulesFiltersUrlWriteSuspendedRef.current = false;
          });
          return prev;
        }
        return next;
      }, { replace: opts?.replace !== false });
    },
    [setSearchParams]
  );

  const navigateRulesTabToPrefixedId = useCallback(
    (prefixedRuleId: string) => {
      const id = (prefixedRuleId || '').trim();
      if (!id) return;
      setRulesQuickviewOpen(false);
      setRulesSearchParamsProgrammatically((next) => {
        stripRulesQuickviewSearchParams(next);
        next.set('tab', 'rules');
        next.set(RULES_URL_KEYS.id, id);
        next.delete(RULES_URL_KEYS.proposalIds);
        next.delete(RULES_URL_KEYS.text);
        next.delete(RULES_URL_KEYS.status);
        return next;
      });
    },
    [setRulesSearchParamsProgrammatically]
  );

  const navigateRulesTabToClassificationProposalId = useCallback(
    (proposalId: number) => {
      const pid = Math.trunc(Number(proposalId));
      if (!Number.isFinite(pid) || pid <= 0) return;
      const idFilter = `P-C${pid}`;
      setRulesQuickviewOpen(false);
      // Status is encoded in the ID prefix (P-); do not also filter Status=Proposed.
      setRulesStatusFilter('all');
      setRulesIdFilter(idFilter);
      setRulesProposalIdsFilter('');
      setRulesTextFilter('');
      setRulesSearchParamsProgrammatically((next) => {
        stripRulesQuickviewSearchParams(next);
        next.set('tab', 'rules');
        next.set(RULES_URL_KEYS.id, idFilter);
        next.delete(RULES_URL_KEYS.proposalIds);
        next.delete(RULES_URL_KEYS.status);
        next.delete(RULES_URL_KEYS.text);
        return next;
      });
      setRulesAccordionOpen((prev) => ({ ...prev, proposals: true }));
      setPipelineNestedSection('step3', 'proposed', true);
    },
    [setPipelineNestedSection, setRulesSearchParamsProgrammatically]
  );

  /**
   * After creating draft rule(s) from Tx Review: switch to Rules in one URL update
   * (tab + optional P-C id filter). Status stays All — ``P-`` already marks proposed.
   * Opens Step 3 Classification and its nested Proposed accordion.
   */
  const openRulesTabAfterProposedClassification = useCallback(
    (proposalId?: number | null) => {
      const pidRaw = proposalId != null ? Math.trunc(Number(proposalId)) : NaN;
      const hasPid = Number.isFinite(pidRaw) && pidRaw > 0;
      const idFilter = hasPid ? `P-C${pidRaw}` : '';
      setRulesQuickviewOpen(false);
      // Sync local filter state with URL so the write-back effect cannot re-add Status=proposed.
      setRulesStatusFilter('all');
      setRulesIdFilter(idFilter);
      setRulesProposalIdsFilter('');
      setRulesTextFilter('');
      setRulesSupplierFilter('all');
      setRulesCategoryFilter('all');
      setRulesFrequencyFilter('all');
      setRulesSearchParamsProgrammatically((next) => {
        stripRulesQuickviewSearchParams(next);
        next.set('tab', 'rules');
        next.delete(RULES_URL_KEYS.status);
        next.delete(RULES_URL_KEYS.text);
        next.delete(RULES_URL_KEYS.proposalIds);
        next.delete(RULES_URL_KEYS.supplier);
        next.delete(RULES_URL_KEYS.category);
        next.delete(RULES_URL_KEYS.frequency);
        if (hasPid) next.set(RULES_URL_KEYS.id, idFilter);
        else next.delete(RULES_URL_KEYS.id);
        return next;
      });
      setRulesAccordionOpen({
        proposals: true,
        pipelineStepRules: false,
        merchantSuppliers: true,
        supplierProposals: true,
        appRules: false,
        supplierRules: false,
        vatRules: false,
        specialCaseRules: false
      });
      setPipelineNestedSection('step3', 'proposed', true);
    },
    [setPipelineNestedSection, setRulesSearchParamsProgrammatically]
  );

  const navigateRulesTabToClassificationAtlasDisplaySeq = useCallback(
    (displaySeq: number) => {
      const n = Math.trunc(Number(displaySeq));
      if (!Number.isFinite(n) || n <= 0) return;
      const idFilter = `P-A${n}`;
      setRulesQuickviewOpen(false);
      setRulesStatusFilter('all');
      setRulesIdFilter(idFilter);
      setRulesProposalIdsFilter('');
      setRulesTextFilter('');
      setRulesSearchParamsProgrammatically((next) => {
        stripRulesQuickviewSearchParams(next);
        next.set('tab', 'rules');
        next.set(RULES_URL_KEYS.id, idFilter);
        next.delete(RULES_URL_KEYS.proposalIds);
        next.delete(RULES_URL_KEYS.status);
        next.delete(RULES_URL_KEYS.text);
        return next;
      });
      setRulesAccordionOpen((prev) => ({ ...prev, proposals: true }));
      setPipelineNestedSection('step3', 'proposed', true);
    },
    [setPipelineNestedSection, setRulesSearchParamsProgrammatically]
  );

  const navigateRulesTabToSupplierProposalId = useCallback(
    (proposalId: number) => {
      const pid = Math.trunc(Number(proposalId));
      if (!Number.isFinite(pid) || pid <= 0) return;
      const proposalFilter = `P-S${pid}`;
      setRulesQuickviewOpen(false);
      setRulesStatusFilter('all');
      setRulesIdFilter('');
      setRulesProposalIdsFilter(proposalFilter);
      setRulesTextFilter('');
      setRulesSearchParamsProgrammatically((next) => {
        stripRulesQuickviewSearchParams(next);
        next.set('tab', 'rules');
        next.delete(RULES_URL_KEYS.id);
        next.set(RULES_URL_KEYS.proposalIds, proposalFilter);
        next.delete(RULES_URL_KEYS.status);
        next.delete(RULES_URL_KEYS.text);
        return next;
      });
      setRulesAccordionOpen((prev) => ({ ...prev, supplierProposals: true }));
      setPipelineNestedSection('step2b', 'proposed', true);
    },
    [setPipelineNestedSection, setRulesSearchParamsProgrammatically]
  );

  /**
   * Open Rules with proposal-id filters for drafts (classification P-C / supplier P-S).
   * Used by toast "View Now" for newly created or already-linked proposals.
   */
  const openRulesTabForNewDraftProposals = useCallback(
    (classificationIds: number[], supplierIds: number[]) => {
      const cls = classificationIds
        .map((x) => Math.trunc(Number(x)))
        .filter((n) => Number.isFinite(n) && n > 0);
      const sup = supplierIds
        .map((x) => Math.trunc(Number(x)))
        .filter((n) => Number.isFinite(n) && n > 0);
      if (cls.length === 0 && sup.length === 0) {
        openRulesTabAfterProposedClassification(null);
        return;
      }
      if (cls.length > 0 && sup.length === 0) {
        openRulesTabAfterProposedClassification(cls[0]);
        return;
      }
      if (cls.length === 0 && sup.length > 0) {
        navigateRulesTabToSupplierProposalId(sup[0]);
        return;
      }
      const joined = formatSimilarTxRulesProposalIdsParam(sup, cls);
      setRulesQuickviewOpen(false);
      setRulesStatusFilter('all');
      setRulesIdFilter('');
      setRulesProposalIdsFilter(joined);
      setRulesTextFilter('');
      setRulesSupplierFilter('all');
      setRulesCategoryFilter('all');
      setRulesFrequencyFilter('all');
      setRulesSearchParamsProgrammatically((next) => {
        stripRulesQuickviewSearchParams(next);
        next.set('tab', 'rules');
        next.delete(RULES_URL_KEYS.id);
        next.delete(RULES_URL_KEYS.status);
        next.delete(RULES_URL_KEYS.text);
        if (joined) next.set(RULES_URL_KEYS.proposalIds, joined);
        else next.delete(RULES_URL_KEYS.proposalIds);
        return next;
      });
      setRulesAccordionOpen({
        proposals: true,
        pipelineStepRules: false,
        merchantSuppliers: true,
        supplierProposals: true,
        appRules: false,
        supplierRules: false,
        vatRules: false,
        specialCaseRules: false
      });
      setPipelineNestedSection('step2b', 'proposed', true);
      setPipelineNestedSection('step3', 'proposed', true);
    },
    [
      navigateRulesTabToSupplierProposalId,
      openRulesTabAfterProposedClassification,
      setPipelineNestedSection,
      setRulesSearchParamsProgrammatically
    ]
  );

  const navigateRulesTabToRulesTextSearch = useCallback(
    (q: string, opts?: { status?: 'proposed' | 'confirmed' }) => {
      const t = (q || '').trim();
      if (!t) return;
      setRulesQuickviewOpen(false);
      setRulesSearchParamsProgrammatically((next) => {
        stripRulesQuickviewSearchParams(next);
        next.set('tab', 'rules');
        next.delete(RULES_URL_KEYS.id);
        next.delete(RULES_URL_KEYS.proposalIds);
        next.set(RULES_URL_KEYS.text, t);
        if (opts?.status) next.set(RULES_URL_KEYS.status, opts.status);
        else next.delete(RULES_URL_KEYS.status);
        return next;
      });
    },
    [setRulesSearchParamsProgrammatically]
  );

  const navigateMainTabToCategories = useCallback(
    (opts?: { search?: string }) => {
      setRulesQuickviewOpen(false);
      setSearchParams((prev) => {
        const next = new URLSearchParams(prev);
        stripRulesQuickviewSearchParams(next);
        next.set('tab', 'categories');
        const q = (opts?.search ?? '').trim();
        if (q) next.set(CATEGORIES_URL_KEYS.search, q);
        else next.delete(CATEGORIES_URL_KEYS.search);
        return next;
      });
    },
    [setSearchParams]
  );

  const navigateMainTabToSuppliers = useCallback(
    (opts?: { search?: string }) => {
      setRulesQuickviewOpen(false);
      setSearchParams((prev) => {
        const next = new URLSearchParams(prev);
        stripRulesQuickviewSearchParams(next);
        next.set('tab', 'suppliers');
        const q = (opts?.search ?? '').trim();
        if (q) next.set(SUPPLIERS_URL_KEYS.search, q);
        else next.delete(SUPPLIERS_URL_KEYS.search);
        return next;
      });
    },
    [setSearchParams]
  );

  const closeRulesQuickview = useCallback(() => {
    setRulesQuickviewOpen(false);
    setRulesQuickviewMainTab('other');
    setUnifiedMoreRow(null);
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      stripRulesQuickviewSearchParams(next);
      return next.toString() === prev.toString() ? prev : next;
    }, { replace: true });
  }, [setSearchParams]);

  const sendRulesQuickviewChat = useCallback(async () => {
    const text = rulesQvChatInput.trim();
    if (!text || rulesQvChatLoading) return;
    const nextMessages = [...rulesQvChatMessages, { role: 'user' as const, content: text }];
    setRulesQvChatMessages(nextMessages);
    setRulesQvChatInput('');
    setRulesQvChatError(null);
    setRulesQvChatLoading(true);
    try {
      const res = await api.post<{
        reply?: string;
        suggestions?: { label: string; to: string }[];
        message?: string;
      }>('/api/app-assistant/chat', {
        messages: nextMessages.map(({ role, content }) => ({ role, content })),
        page: { pathname: location.pathname, search: location.search || '' }
      });
      const reply = (res.data.reply || '').trim();
      const suggestions = Array.isArray(res.data.suggestions) ? res.data.suggestions : [];
      setRulesQvChatMessages((prev) => [
        ...prev,
        { role: 'assistant', content: reply || 'No response text returned.', suggestions }
      ]);
    } catch (e: unknown) {
      const err = e as { response?: { data?: { message?: string; error?: string } }; message?: string };
      const msg =
        err?.response?.data?.message ||
        err?.response?.data?.error ||
        err?.message ||
        'Request failed';
      setRulesQvChatError(String(msg));
      setRulesQvChatMessages((prev) => prev.slice(0, -1));
    } finally {
      setRulesQvChatLoading(false);
    }
  }, [rulesQvChatInput, rulesQvChatLoading, rulesQvChatMessages, location.pathname, location.search]);

  const openRulesQuickview = useCallback(
    (opts?: { filter?: string }) => {
      const q = (opts?.filter ?? '').trim();
      if (q) setRulesTextFilter(q);
      setRulesQuickviewOpen(true);
      setSearchParams((prev) => {
        const next = new URLSearchParams(prev);
        next.set(RULES_QV_URL_KEYS.open, '1');
        next.delete(RULES_QV_URL_KEYS.filter);
        return next.toString() === prev.toString() ? prev : next;
      }, { replace: true });
    },
    [setSearchParams]
  );

  useEffect(() => {
    const onOpenFromFab = (ev: Event) => {
      const raw = (ev as CustomEvent<{ tab?: string }>).detail?.tab;
      const map: Record<string, QuickviewMainTab> = {
        chat: 'chat',
        rules: 'rules',
        categories: 'categories',
        suppliers: 'suppliers',
        vatRules: 'rules',
        specialCaseRules: 'rules',
        other: 'other',
        requests: 'requests'
      };
      setRulesQuickviewMainTab(raw && map[raw] ? map[raw] : 'other');
      openRulesQuickview();
    };
    window.addEventListener(OPEN_RULES_QUICKVIEW_EVENT, onOpenFromFab as EventListener);
    return () => window.removeEventListener(OPEN_RULES_QUICKVIEW_EVENT, onOpenFromFab as EventListener);
  }, [openRulesQuickview]);

  useEffect(() => {
    const openRaw = (searchParams.get(RULES_QV_URL_KEYS.open) || '').trim().toLowerCase();
    const wantsOpen =
      openRaw === '1' || openRaw === 'true' || openRaw === 'yes' || openRaw === 'open';
    const qFromUrl = searchParams.get(RULES_QV_URL_KEYS.filter) ?? '';
    const key = `${wantsOpen ? '1' : '0'}|${qFromUrl}`;
    if (key === lastRulesQvHydrationKeyRef.current) return;
    lastRulesQvHydrationKeyRef.current = key;

    if (wantsOpen) {
      setRulesQuickviewOpen(true);
      const qq = qFromUrl.trim();
      if (qq) setRulesTextFilter(qq);
    } else {
      setRulesQuickviewOpen(false);
    }
  }, [searchParams]);

  /** Same rule rows as the main Rules tab (`visible*` lists), sorted by name for browsing. */
  const rulesQuickviewFiltered = useMemo(() => {
    const sortByName = <T,>(list: T[], nameOf: (x: T) => string) =>
      [...list].sort((a, b) =>
        nameOf(a).localeCompare(nameOf(b), undefined, { sensitivity: 'base' })
      );

    const classProposals = visibleClassificationRuleRows
      .filter((r) => r.status === 'proposed' && r.proposal)
      .map((r) => r.proposal as RuleProposalRow);
    const classifications = visibleClassificationRuleRows
      .filter((r) => r.status === 'confirmed' && r.rule)
      .map((r) => r.rule as ClassificationRule);
    const supplierProposals = visibleSupplierRuleRows
      .filter((r) => r.status === 'proposed' && r.proposal)
      .map((r) => r.proposal as SupplierRuleProposalRow);
    const supplierRules = visibleSupplierRuleRows
      .filter((r) => r.status === 'confirmed' && r.alias)
      .map((r) => r.alias as SupplierRuleRow);

    return {
      classProposals: sortByName(classProposals, (row) => row.proposed_name || ''),
      classifications: sortByName(classifications, (rule) => rule.name || ''),
      supplierProposals: sortByName(supplierProposals, (row) => row.proposed_name || ''),
      supplierRules: sortByName(supplierRules, (row) =>
        String(row.supplier_name || row.rule_name || row.pattern_raw || '')
      ),
      vatRules: sortByName([...visibleVatRules], (r) => r.name || r.category?.name || ''),
      specialCaseRules: sortByName([...visibleSpecialCaseRules], (r) => r.name || '')
    };
  }, [
    visibleClassificationRuleRows,
    visibleSupplierRuleRows,
    visibleVatRules,
    visibleSpecialCaseRules
  ]);

  const rulesQvVisibleSectionKeys = useMemo((): RulesQvSectionKey[] => {
    const f = rulesQuickviewFiltered;
    const keys: RulesQvSectionKey[] = [];
    if (f.classProposals.length > 0) keys.push('classProposals');
    if (f.supplierProposals.length > 0) keys.push('supplierProposals');
    if (f.classifications.length > 0) keys.push('classifications');
    if (f.supplierRules.length > 0) keys.push('supplierRules');
    if (f.vatRules.length > 0) keys.push('vatRules');
    if (f.specialCaseRules.length > 0) keys.push('specialCaseRules');
    return keys;
  }, [rulesQuickviewFiltered]);

  const toggleAllRulesQvSections = useCallback(() => {
    setRulesQvSectionsOpen((prev) => {
      if (rulesQvVisibleSectionKeys.length === 0) return prev;
      const allExpanded = rulesQvVisibleSectionKeys.every((k) => prev[k]);
      const nextVal = !allExpanded;
      const next = { ...prev };
      rulesQvVisibleSectionKeys.forEach((k) => {
        next[k] = nextVal;
      });
      return next;
    });
  }, [rulesQvVisibleSectionKeys]);

  useEffect(() => {
    const supSearch = (searchParams.get(SUPPLIERS_URL_KEYS.search) || '').trim();
    const supIdRaw = (searchParams.get(SUPPLIERS_URL_KEYS.id) || '').trim();
    const supIdNum = Number(supIdRaw);
    const supId = Number.isFinite(supIdNum) && supIdNum > 0 ? Math.floor(supIdNum) : null;
    const supName = (searchParams.get(SUPPLIERS_URL_KEYS.name) || '').trim();
    const supActiveRaw = (searchParams.get(SUPPLIERS_URL_KEYS.active) || '').trim().toLowerCase();
    const supActive =
      supActiveRaw === 'active' || supActiveRaw === 'inactive' ? supActiveRaw : 'all';
    const supVat = (searchParams.get(SUPPLIERS_URL_KEYS.vat) || '').trim();
    const supCurrency = (searchParams.get(SUPPLIERS_URL_KEYS.currency) || '').trim();
    const supSourceRaw = (searchParams.get(SUPPLIERS_URL_KEYS.source) || '').trim();
    const supHideNkRaw = (searchParams.get(SUPPLIERS_URL_KEYS.hideNonKey) || '').trim().toLowerCase();

    setSuppliersCrudSearch(supSearch);
    setSuppliersCrudIdFilter(supId);
    setSuppliersCrudNameFilter(supName);
    setSuppliersCrudActiveFilter(supActive as 'all' | 'active' | 'inactive');
    setSuppliersCrudVatFilter(supVat || 'all');
    setSuppliersCrudCurrencyFilter(supCurrency || 'all');
    setSuppliersCrudSourceFilter(entitySourceFilterLabelFromUrlParam(supSourceRaw));
    setSuppliersCrudHideNonKey(
      supHideNkRaw === '1' || supHideNkRaw === 'true' || supHideNkRaw === 'yes' || supHideNkRaw === 'on'
    );
    suppliersFiltersHydratedFromUrlRef.current = true;
  }, [searchParams]);

  useEffect(() => {
    if (!suppliersFiltersHydratedFromUrlRef.current) return;
    const next = new URLSearchParams(searchParams);

    const supSearch = suppliersCrudSearch.trim();
    if (supSearch) next.set(SUPPLIERS_URL_KEYS.search, supSearch);
    else next.delete(SUPPLIERS_URL_KEYS.search);

    if (suppliersCrudIdFilter != null && Number.isFinite(Number(suppliersCrudIdFilter)) && Number(suppliersCrudIdFilter) > 0) {
      next.set(SUPPLIERS_URL_KEYS.id, String(Math.floor(Number(suppliersCrudIdFilter))));
    } else {
      next.delete(SUPPLIERS_URL_KEYS.id);
    }

    const supName = suppliersCrudNameFilter.trim();
    if (supName) next.set(SUPPLIERS_URL_KEYS.name, supName);
    else next.delete(SUPPLIERS_URL_KEYS.name);

    if (suppliersCrudActiveFilter !== 'all') next.set(SUPPLIERS_URL_KEYS.active, suppliersCrudActiveFilter);
    else next.delete(SUPPLIERS_URL_KEYS.active);

    if (suppliersCrudVatFilter !== 'all') next.set(SUPPLIERS_URL_KEYS.vat, suppliersCrudVatFilter);
    else next.delete(SUPPLIERS_URL_KEYS.vat);

    if (suppliersCrudCurrencyFilter !== 'all') next.set(SUPPLIERS_URL_KEYS.currency, suppliersCrudCurrencyFilter);
    else next.delete(SUPPLIERS_URL_KEYS.currency);

    const supSourceParam = entitySourceUrlParamFromFilterLabel(suppliersCrudSourceFilter);
    if (supSourceParam) next.set(SUPPLIERS_URL_KEYS.source, supSourceParam);
    else next.delete(SUPPLIERS_URL_KEYS.source);

    if (suppliersCrudHideNonKey) next.set(SUPPLIERS_URL_KEYS.hideNonKey, '1');
    else next.delete(SUPPLIERS_URL_KEYS.hideNonKey);

    if (next.toString() !== searchParams.toString()) {
      setSearchParams(next, { replace: true });
    }
  }, [
    searchParams,
    setSearchParams,
    suppliersCrudActiveFilter,
    suppliersCrudCurrencyFilter,
    suppliersCrudHideNonKey,
    suppliersCrudIdFilter,
    suppliersCrudNameFilter,
    suppliersCrudSearch,
    suppliersCrudSourceFilter,
    suppliersCrudVatFilter,
  ]);

  useEffect(() => {
    const catSearch = (searchParams.get(CATEGORIES_URL_KEYS.search) || '').trim();
    const catName = (searchParams.get(CATEGORIES_URL_KEYS.name) || '').trim();
    const catActiveRaw = (searchParams.get(CATEGORIES_URL_KEYS.active) || '').trim().toLowerCase();
    const catActive =
      catActiveRaw === 'active' || catActiveRaw === 'inactive' ? catActiveRaw : 'all';
    const catSourceRaw = (searchParams.get(CATEGORIES_URL_KEYS.source) || '').trim();
    const catHideNkRaw = (searchParams.get(CATEGORIES_URL_KEYS.hideNonKey) || '').trim().toLowerCase();

    setCategoriesCrudSearch(catSearch);
    setCategoriesCrudNameFilter(catName);
    setCategoriesCrudActiveFilter(catActive as 'all' | 'active' | 'inactive');
    setCategoriesCrudSourceFilter(entitySourceFilterLabelFromUrlParam(catSourceRaw));
    setCategoriesCrudHideNonKey(
      catHideNkRaw === '1' || catHideNkRaw === 'true' || catHideNkRaw === 'yes' || catHideNkRaw === 'on'
    );
    categoriesFiltersHydratedFromUrlRef.current = true;
  }, [searchParams]);

  useEffect(() => {
    if (!categoriesFiltersHydratedFromUrlRef.current) return;
    const next = new URLSearchParams(searchParams);

    const catSearch = categoriesCrudSearch.trim();
    if (catSearch) next.set(CATEGORIES_URL_KEYS.search, catSearch);
    else next.delete(CATEGORIES_URL_KEYS.search);

    const catName = categoriesCrudNameFilter.trim();
    if (catName) next.set(CATEGORIES_URL_KEYS.name, catName);
    else next.delete(CATEGORIES_URL_KEYS.name);

    if (categoriesCrudActiveFilter !== 'all') next.set(CATEGORIES_URL_KEYS.active, categoriesCrudActiveFilter);
    else next.delete(CATEGORIES_URL_KEYS.active);

    const catSourceParam = entitySourceUrlParamFromFilterLabel(categoriesCrudSourceFilter);
    if (catSourceParam) next.set(CATEGORIES_URL_KEYS.source, catSourceParam);
    else next.delete(CATEGORIES_URL_KEYS.source);

    if (categoriesCrudHideNonKey) next.set(CATEGORIES_URL_KEYS.hideNonKey, '1');
    else next.delete(CATEGORIES_URL_KEYS.hideNonKey);

    if (next.toString() !== searchParams.toString()) {
      setSearchParams(next, { replace: true });
    }
  }, [
    categoriesCrudActiveFilter,
    categoriesCrudHideNonKey,
    categoriesCrudNameFilter,
    categoriesCrudSearch,
    categoriesCrudSourceFilter,
    searchParams,
    setSearchParams,
  ]);

  useEffect(() => {
    if (tabIndex !== CLASSIFICATION_VISIBLE_TAB_KEYS.indexOf('pipeline')) return;
    if (pipelineCountsLoading) return;
    if (pipelineCountsAllScope) return;
    if (pipelineCountsError) return;
    void fetchPipelineCounts();
  }, [tabIndex, pipelineCountsLoading, pipelineCountsAllScope, pipelineCountsError, fetchPipelineCounts]);

  const hydrateAllTransactionsInBackground = useCallback(async () => {
    const runId = ++allTransactionsHydrateRunRef.current;
    setAllTransactionsHydrating(true);
    setAllTransactionsPayload([]);
    try {
      let page = 1;
      let totalPages = 1;
      for (;;) {
        const res = await api.get('/api/revolut/transactions/complete', {
          params: { page, per_page: ALL_TRANSACTIONS_HYDRATE_PAGE_SIZE, account: 'all' }
        });
        if (runId !== allTransactionsHydrateRunRef.current) return;
        const rows = Array.isArray(res.data?.transactions) ? res.data.transactions : [];
        const mappedRows = rows.map((tx: any) => mapApiTransactionToReviewTx(tx));
        if (mappedRows.length > 0) {
          setAllTransactionsPayload((prev) => mergeReviewTxCollections(prev || [], mappedRows));
        }
        const p = res.data?.pagination || {};
        totalPages = Number(p.total_pages || 1);
        if (page >= totalPages || rows.length === 0) break;
        page += 1;
      }
    } catch {
      // Non-blocking hydration: keep the primary datasets already rendered.
    } finally {
      if (runId === allTransactionsHydrateRunRef.current) {
        setAllTransactionsHydrating(false);
      }
    }
  }, []);

  const hydrateConfirmedItemsInBackground = useCallback(async () => {
    const runId = ++confirmedHydrateRunRef.current;
    setConfirmedPayloadHydrating(true);
    setConfirmedPayload({
      items: [],
      count_total: 0,
      count_returned: 0,
      limit: REVIEW_CONFIRMED_ITEMS_PAGE_LIMIT,
      offset: 0,
      next_offset: 0,
      has_more: false
    });
    try {
      let offset = 0;
      let total = 0;
      for (;;) {
        const res = await api.get('/api/transaction-classification-rules/classification-confirmed-items', {
          params: { limit: REVIEW_CONFIRMED_ITEMS_PAGE_LIMIT, offset, ...explicitReviewScopeParams }
        });
        if (runId !== confirmedHydrateRunRef.current) return;
        const data = (res.data || {}) as ConfirmedPayload;
        const rows = data.items || [];
        total = Number.isFinite(data.count_total) ? Number(data.count_total) : total;
        if (rows.length > 0) {
          setConfirmedPayload((prev) => {
            const merged = mergeConfirmedCollections(prev?.items || [], rows);
            return {
              items: merged,
              count_total: total || merged.length,
              count_returned: merged.length,
              limit: REVIEW_CONFIRMED_ITEMS_PAGE_LIMIT,
              offset: 0,
              next_offset: merged.length,
              has_more: false
            };
          });
        }
        if (!data.has_more) break;
        const next = typeof data.next_offset === 'number' ? data.next_offset : offset + rows.length;
        if (next <= offset || rows.length === 0) break;
        offset = next;
      }
    } catch {
      // Non-blocking hydration for committed rows.
    } finally {
      if (runId === confirmedHydrateRunRef.current) {
        setConfirmedPayloadHydrating(false);
      }
    }
  }, [explicitReviewScopeParams]);

  const fetchCanonicalRows = useCallback(
    async (
      committed: 'all' | 'only' | 'exclude',
      options?: { limit?: number; offset?: number }
    ): Promise<CanonicalReviewRowsPayload> => {
      if (!canonicalHybridEnabled) {
        return { rows: [], count_total: 0, counts: {}, scope: {} };
      }
      const limit = options?.limit ?? CANONICAL_REVIEW_BATCH_LIMIT;
      const offset = options?.offset ?? 0;
      const flaggedOnly = unifiedFlaggedFilter === 'flagged';
      const res = await api.get('/api/transaction-classification-rules/review-rows', {
        params: {
          limit,
          offset,
          // Flagged-only should surface look-into-later rows even when default hide-committed /
          // hide-complete switches would otherwise omit them from the fetch scope.
          committed: flaggedOnly ? 'all' : committed,
          ...(flaggedOnly || !hideConfirmedCompleteUnifiedRows
            ? { exclude_confirmed_complete: '0' }
            : { exclude_confirmed_complete: '1' }),
          ...(hideDeclinedRevolutTransactions ? {} : { include_declined: '1' }),
          ...(unifiedZeroOrNullAmountOnly ? { zero_or_null_amount: '1' } : {}),
          ...(flaggedOnly ? { flagged: 'only' } : {}),
          ...explicitReviewScopeParams
        }
      });
      const canonicalRows = (res.data || { rows: [], count_total: 0 }) as CanonicalReviewRowsPayload;
      const sn = canonicalRows.stream_next_offset;
      return {
        rows: Array.isArray(canonicalRows.rows) ? canonicalRows.rows : [],
        count_total: Number(canonicalRows.count_total || 0),
        counts: canonicalRows.counts || {},
        scope: canonicalRows.scope || {},
        ...(typeof sn === 'number' && Number.isFinite(sn) ? { stream_next_offset: sn } : {})
      };
    },
    [
      canonicalHybridEnabled,
      explicitReviewScopeParams,
      hideConfirmedCompleteUnifiedRows,
      hideDeclinedRevolutTransactions,
      unifiedZeroOrNullAmountOnly,
      unifiedFlaggedFilter
    ]
  );

  /** Targeted canonical row refresh for ids touched by bulk reprocess (avoids refetching the full ledger page). */
  const refreshCanonicalRowsForTransactionIds = useCallback(
    async (_ledgerOwnerUserId: number, txIds: number[]) => {
      if (!canonicalHybridEnabled || !txIds.length) return;
      const uniq = Array.from(new Set(txIds.map((x) => Number(x)).filter((x) => Number.isFinite(x) && x > 0)));
      if (!uniq.length) return;
      const chunkSize = Math.min(BULK_REPROCESS_TRANSACTIONS_MAX, CANONICAL_REVIEW_BATCH_LIMIT);
      for (let i = 0; i < uniq.length; i += chunkSize) {
        const chunk = uniq.slice(i, i + chunkSize);
        const affected = new Set(chunk);
        // Do not pass per-ledger user_id here: default TX review uses full review-rows tag scope; a narrow
        // user_id would merge rows that disagree with the table (e.g. bulk category change reverts visually).

        const pullRowsForChunk = async (): Promise<CanonicalReviewRow[]> => {
          const res = await api.get('/api/transaction-classification-rules/review-rows', {
            params: {
              limit: chunk.length,
              offset: 0,
              committed: canonicalRowsCommittedMode,
              transaction_ids: chunk.join(','),
              ...(hideDeclinedRevolutTransactions ? {} : { include_declined: '1' }),
              _t: String(Date.now()),
              ...explicitReviewScopeParams
            }
          });
          const page = (res.data || {}) as CanonicalReviewRowsPayload;
          return Array.isArray(page.rows) ? (page.rows as CanonicalReviewRow[]) : [];
        };

        const haveIdsPass = (
          agg: Map<number, CanonicalReviewRow>,
          passRows: CanonicalReviewRow[]
        ): Map<number, CanonicalReviewRow> => {
          for (const r of passRows) {
            const nid = Number(r.id);
            if (Number.isFinite(nid) && nid > 0) {
              agg.set(nid, r);
            }
          }
          return agg;
        };

        let byPass = new Map<number, CanonicalReviewRow>();
        haveIdsPass(byPass, await pullRowsForChunk());
        const maxPasses = 5;
        for (let pass = 1; pass < maxPasses; pass += 1) {
          const haveIds = new Set(
            Array.from(byPass.keys()).filter((id) => Number.isFinite(id) && id > 0)
          );
          const missing = chunk.filter((id) => !haveIds.has(id));
          if (missing.length === 0) break;
          await new Promise((resolve) => window.setTimeout(resolve, 350 + pass * 150));
          haveIdsPass(byPass, await pullRowsForChunk());
        }

        let rows = Array.from(byPass.values());

        setCanonicalRowsPayload((prev) => mergeCanonicalRowsAfterBulkRefresh(prev, rows, affected));
      }
    },
    [canonicalHybridEnabled, canonicalRowsCommittedMode, explicitReviewScopeParams, hideDeclinedRevolutTransactions]
  );

  const hydrateCanonicalRowsInBackground = useCallback(
    async (
      committed: 'all' | 'only' | 'exclude',
      firstPage: CanonicalReviewRowsPayload,
      hydrateGeneration: number
    ) => {
      if (!canonicalHybridEnabled) return;
      if (hydrateGeneration !== canonicalHydrateGenRef.current) return;
      const initialRows = Array.isArray(firstPage.rows) ? firstPage.rows : [];
      const total = Number(firstPage.count_total || 0);
      if (total <= 0) return;
      const firstSn = firstPage.stream_next_offset;
      let offset =
        typeof firstSn === 'number' && Number.isFinite(firstSn) && firstSn >= 0
          ? firstSn
          : initialRows.length;
      if (offset >= total) return;

      // exhausted = true means we kept looping until the server confirmed there are no more rows
      // for this scope. When that happens we reconcile count_total downward to match what we
      // actually merged — which fixes banners like "182 / 182 · total 208 from server" when
      // exclude_confirmed_complete drops rows in Python after the SQL slice.
      //
      // We do NOT reconcile when the loop ended due to a transient fetch error: in that case the
      // server may still have more rows we couldn't observe, and lowering count_total would lie
      // about completeness.
      let exhausted = false;
      while (offset < total) {
        if (hydrateGeneration !== canonicalHydrateGenRef.current) return;
        let page: CanonicalReviewRowsPayload;
        try {
          page = await fetchCanonicalRows(committed, {
            limit: CANONICAL_REVIEW_BATCH_LIMIT,
            offset
          });
        } catch {
          break;
        }
        if (hydrateGeneration !== canonicalHydrateGenRef.current) return;
        const rows = Array.isArray(page.rows) ? page.rows : [];
        if (rows.length > 0) {
          setCanonicalRowsPayload((prev) => {
            const existing = Array.isArray(prev?.rows) ? prev!.rows : [];
            const existingKeys = new Set(existing.map((r) => String(r.key || r.id)));
            const netNew = rows.filter((r) => {
              const key = String(r.key || r.id);
              return key && !existingKeys.has(key);
            });
            if (!netNew.length) return prev;
            return {
              rows: [...existing, ...netNew],
              count_total: Number(page.count_total || prev?.count_total || 0),
              counts: { ...(prev?.counts || {}), ...(page.counts || {}) },
              scope: page.scope || prev?.scope || {}
            };
          });
        }
        // Advance via stream_next_offset so we can step past SQL slices where every row was
        // dropped by post-SQL filters (e.g. exclude_confirmed_complete). Without this, an empty
        // page early-broke the loop and left the client below count_total forever.
        const pageSn = page.stream_next_offset;
        const sn = typeof pageSn === 'number' && Number.isFinite(pageSn) ? pageSn : null;
        const nextOffset = sn != null && sn >= 0 ? sn : offset + rows.length;
        if (nextOffset <= offset) {
          // Server cannot advance further — treat as authoritative end-of-stream regardless of
          // whether the last page contained rows. Without this, "rows.length > 0 but no more
          // pages" left exhausted=false and the banner stayed at e.g. 182 / 208 forever.
          exhausted = true;
          break;
        }
        offset = nextOffset;
        if (offset >= total) {
          exhausted = true;
        }
      }
      if (hydrateGeneration !== canonicalHydrateGenRef.current) return;
      if (exhausted) {
        // Normalize count_total to actual merged length so the "loaded N / total M" banner
        // reflects what the user can actually see (post-filter), not the pre-filter scope size.
        setCanonicalRowsPayload((prev) => {
          if (!prev) return prev;
          const merged = Array.isArray(prev.rows) ? prev.rows.length : 0;
          if (merged > 0 && merged < Number(prev.count_total || 0)) {
            return { ...prev, count_total: merged };
          }
          return prev;
        });
      }
    },
    [canonicalHybridEnabled, fetchCanonicalRows]
  );

  /** Paginated tagged-items load — shared by full fetch and fast review-table refresh. */
  const loadAllTaggedItemsForReview = useCallback(async (): Promise<TaggedPayload> => {
    let offset = 0;
    let total = 0;
    const all: TaggedItem[] = [];
    for (;;) {
      const res = await api.get('/api/transaction-classification-rules/tagged-items', {
        params: { limit: REVIEW_TAGGED_ITEMS_PAGE_LIMIT, offset, ...explicitReviewScopeParams }
      });
      const data = (res.data || {}) as TaggedPayload;
      const rows = data.tagged || [];
      all.push(...rows);
      total = Number.isFinite(data.count_total) ? Number(data.count_total) : total;
      if (!data.has_more) break;
      const next = typeof data.next_offset === 'number' ? data.next_offset : offset + rows.length;
      if (next <= offset || rows.length === 0) break;
      offset = next;
    }
    return {
      tagged: all,
      count_total: total || all.length,
      count_returned: all.length,
      limit: REVIEW_TAGGED_ITEMS_PAGE_LIMIT
    };
  }, [explicitReviewScopeParams]);

  /**
   * Refresh only data that feeds the Tx Review unified table (canonical rows, manual queues, tagged).
   * Avoids re-fetching rules, categories, suppliers, rule history, etc. — use after re-run / bulk reprocess.
   */
  const refreshTransactionReviewCore = useCallback(
    async (opts?: { skipCanonical?: boolean }) => {
      const emptyCanonical: CanonicalReviewRowsPayload = {
        rows: [],
        count_total: 0,
        counts: {},
        scope: {}
      };
      let canonicalRowsResponse: CanonicalReviewRowsPayload | null = null;
      if (canonicalHybridEnabled && !opts?.skipCanonical) {
        try {
          canonicalRowsResponse = await fetchCanonicalRows(canonicalRowsCommittedMode);
        } catch {
          canonicalRowsResponse = emptyCanonical;
        }
      }

      let reviewPayload: ReviewPayload | null = null;
      try {
        const response = await api.get('/api/transaction-classification-rules/manual-review-items', {
          params: { limit: CANONICAL_REVIEW_BATCH_LIMIT, ...explicitReviewScopeParams }
        });
        reviewPayload = response.data as ReviewPayload;
      } catch {
        // Keep existing queues / canonical table if this endpoint errors (e.g. transient 502).
      }
      if (reviewPayload) {
        setPayload(reviewPayload);
        const pendingRuleSyncCount = Math.max(
          0,
          Number(reviewPayload?.counts?.untagged_total || 0) +
            Number(reviewPayload?.counts?.unclassified_needs_review_total || 0) +
            Number(reviewPayload?.counts?.conflicting_vat_total || 0)
        );
        setPendingManualRuleSyncCount(pendingRuleSyncCount);
      }

      if (canonicalHybridEnabled && canonicalRowsResponse) {
        canonicalHydrateGenRef.current += 1;
        const hydrateGen = canonicalHydrateGenRef.current;
        const sn = canonicalRowsResponse.stream_next_offset;
        setCanonicalRowsPayload({
          rows: Array.isArray(canonicalRowsResponse.rows) ? canonicalRowsResponse.rows : [],
          count_total: Number(canonicalRowsResponse.count_total || 0),
          counts: canonicalRowsResponse.counts || {},
          scope: canonicalRowsResponse.scope || {},
          ...(typeof sn === 'number' && Number.isFinite(sn) ? { stream_next_offset: sn } : {})
        });
        void hydrateCanonicalRowsInBackground(canonicalRowsCommittedMode, canonicalRowsResponse, hydrateGen);
      }

      void (async () => {
        try {
          const taggedResponse = await loadAllTaggedItemsForReview();
          setTaggedPayload(taggedResponse as TaggedPayload);
        } catch {
          // Non-blocking.
        }
      })();
    },
    [
      canonicalHybridEnabled,
      canonicalRowsCommittedMode,
      explicitReviewScopeParams,
      fetchCanonicalRows,
      hydrateCanonicalRowsInBackground,
      loadAllTaggedItemsForReview
    ]
  );

  const fetchData = useCallback(async (): Promise<ReviewPayload | null> => {
    const fetchGen = ++fetchDataGenRef.current;
    allTransactionsHydrateRunRef.current += 1;
    confirmedHydrateRunRef.current += 1;
    setAllTransactionsHydrating(false);
    setConfirmedPayloadHydrating(false);
    setLoading(true);
    setError('');
    let canonicalAppliedEarly = false;
    try {
      const fetchDataPartialWarnings: string[] = [];
      const emptyCanonical: CanonicalReviewRowsPayload = {
        rows: [],
        count_total: 0,
        counts: {},
        scope: {}
      };

      const applyCanonicalRowsForFetch = (canonicalRows: CanonicalReviewRowsPayload, failed: boolean) => {
        if (fetchGen !== fetchDataGenRef.current) return;
        if (failed) {
          // Keep any already-loaded rows; an empty failure must not blank the grid.
          setCanonicalRowsPayload((prev) => {
            if (prev && Array.isArray(prev.rows) && prev.rows.length > 0) return prev;
            return emptyCanonical;
          });
          setLoading(false);
          return;
        }
        const canonicalSn = canonicalRows.stream_next_offset;
        setCanonicalRowsPayload({
          rows: Array.isArray(canonicalRows.rows) ? canonicalRows.rows : [],
          count_total: Number(canonicalRows.count_total || 0),
          counts: canonicalRows.counts || {},
          scope: canonicalRows.scope || {},
          ...(typeof canonicalSn === 'number' && Number.isFinite(canonicalSn)
            ? { stream_next_offset: canonicalSn }
            : {})
        });
        setCanonicalRowsCommittedMode('exclude');
        canonicalHydrateGenRef.current += 1;
        void hydrateCanonicalRowsInBackground(
          'exclude',
          canonicalRows,
          canonicalHydrateGenRef.current
        );
        canonicalAppliedEarly = true;
        // Paint the Tx Reviews table as soon as review-rows returns; do not wait on rules/history.
        setLoading(false);
      };

      // Resolve review-rows on its own promise so we can paint/hydrate before slower siblings finish.
      // A hung GET /transaction-classification-rules/ previously blocked the whole pool (~90s) and
      // left the grid empty even though review-rows had already succeeded.
      let canonicalFetchFailed = false;
      const canonicalRowsPromise = fetchCanonicalRows('exclude')
        .then((canonicalRows) => {
          applyCanonicalRowsForFetch(canonicalRows, false);
          return canonicalRows;
        })
        .catch(() => {
          canonicalFetchFailed = true;
          fetchDataPartialWarnings.push('transaction reviews (review-rows)');
          applyCanonicalRowsForFetch(emptyCanonical, true);
          return emptyCanonical;
        });

      // Bounded concurrency: starting every request at once often triggers 502 under load.
      const [
        canonicalRowsResponse,
        response,
        rulesResponse,
        categoriesResponse,
        vatPolicyRows,
        supplierRuleRowsData,
        vatRuleRowsData,
        vatSuggestionRowsData,
        specialCaseRuleRowsData,
        ruleRunHistoryData,
        canonicalVatRatesData,
        proposalsResponse,
        rejectedProposalsResponse,
        rejectedVatSuggestionRowsData,
        suppliersData
      ] = await promisePool(
        [
          () => canonicalRowsPromise,
          () =>
            api
              .get('/api/transaction-classification-rules/manual-review-items', {
                params: { limit: CANONICAL_REVIEW_BATCH_LIMIT, ...explicitReviewScopeParams }
              })
              .catch(
                () =>
                  ({
                    data: {
                      untagged: [],
                      unclassified_needs_review: [],
                      conflicting_vat: [],
                      counts: {
                        untagged_total: 0,
                        unclassified_needs_review_total: 0,
                        conflicting_vat_total: 0
                      }
                    }
                  }) as any
              ),
          () =>
            // Lite list: no heading seed / alias enrichment. Cap wait so a stuck call cannot
            // block paint; failures retry silently below (no scary secondary-load toast).
            api
              .get('/api/transaction-classification-rules/', {
                params: { lite: 1 },
                timeout: 15000
              })
              .catch(() => ({ data: { rules: [] }, __rulesListFailed: true } as any)),
          () =>
            api.get('/api/categories/', { params: { is_active: 'true' } }).catch(() => {
              fetchDataPartialWarnings.push('categories');
              return { data: { categories: [] } } as any;
            }),
          () =>
            api
              .get('/api/suppliers/vat-invoice-policies')
              .then((r) => (Array.isArray(r.data?.rows) ? r.data.rows : []))
              .catch(() => [] as any[]),
          () =>
            api
              .get('/api/suppliers/description-aliases')
              .then((r) => (Array.isArray(r.data?.aliases) ? r.data.aliases : []))
              .catch(() => [] as any[]),
          () =>
            api
              .get('/api/transaction-classification-rules/vat-rules')
              .then((r) => (Array.isArray(r.data?.vat_rules) ? r.data.vat_rules : []))
              .catch(() => [] as any[]),
          () =>
            api
              .get('/api/transaction-classification-rules/vat-rules/suggestions', {
                params: { status: 'pending', account_scope: 'both' }
              })
              .then((r) => ({
                suggestions: Array.isArray(r.data?.suggestions) ? r.data.suggestions : [],
                count_saved_rejected: Number(r.data?.count_saved_rejected || 0),
              }))
              .catch(() => ({ suggestions: [] as any[], count_saved_rejected: 0 })),
          () =>
            api
              .get('/api/transaction-classification-rules/special-case-rules')
              .then((r) => (Array.isArray(r.data?.special_case_rules) ? r.data.special_case_rules : []))
              .catch(() => [] as any[]),
          () =>
            api
              .get('/api/transaction-classification-rules/rule-run-history', {
                params: { limit: 1200, bucket_minutes: 60, ...explicitReviewScopeParams }
              })
              .then((r) => {
                const payload = (r.data || {}) as RuleRunHistoryPayload;
                return {
                  rows: Array.isArray(payload.rows) ? payload.rows : [],
                  histogram: Array.isArray(payload.histogram) ? payload.histogram : [],
                  meta: payload.meta || {}
                } as RuleRunHistoryPayload;
              })
              .catch(() => null),
          () =>
            api
              .get('/api/transaction-classification-rules/vat-rate-options')
              .then((r) => (Array.isArray(r.data?.vat_rate_options) ? r.data.vat_rate_options : []))
              .catch(() => [] as any[]),
          () =>
            api.get('/api/transaction-classification-rules/rule-proposals').catch(() => ({ data: { proposals: [] } })),
          () =>
            api
              .get('/api/transaction-classification-rules/rule-proposals', { params: { status: 'rejected' } })
              .catch(() => ({ data: { proposals: [], count_saved_rejected: 0 } })),
          () =>
            api
              .get('/api/transaction-classification-rules/vat-rules/suggestions', {
                params: { status: 'rejected', account_scope: 'both' }
              })
              .then((r) => (Array.isArray(r.data?.suggestions) ? r.data.suggestions : []))
              .catch(() => [] as any[]),
          () =>
            api
              .get('/api/suppliers/', { params: { is_active: 'true' } })
              .then((r) => (Array.isArray(r.data?.suppliers) ? r.data.suppliers : []))
              .catch(() => [] as any[])
        ],
        FETCH_REVIEW_DATA_CONCURRENCY
      );

      // `description-alias-proposals` can be slow; do not block TX review table hydration on it.
      const supplierProposalsGen = ++supplierProposalsHydrateGenRef.current;
      void Promise.all([
        api.get('/api/suppliers/description-alias-proposals', { params: { status: 'pending', limit: 2000 } }),
        api
          .get('/api/suppliers/description-alias-proposals', { params: { status: 'rejected', limit: 2000 } })
          .catch(() => ({ data: { proposals: [], count_saved_rejected: 0 } }))
      ])
        .then(([pendingRes, rejectedRes]) => {
          if (supplierProposalsGen !== supplierProposalsHydrateGenRef.current) return;
          setSupplierProposalsPayload({
            proposals: Array.isArray(pendingRes.data?.proposals) ? pendingRes.data.proposals : [],
            count_saved_pending: Number(pendingRes.data?.count_saved_pending || 0)
          });
          setRejectedSupplierProposalsPayload({
            proposals: Array.isArray(rejectedRes.data?.proposals) ? rejectedRes.data.proposals : [],
            count_saved_rejected: Number(rejectedRes.data?.count_saved_rejected || 0)
          });
        })
        .catch(() => {
          if (supplierProposalsGen !== supplierProposalsHydrateGenRef.current) return;
          setSupplierProposalsPayload({ proposals: [], count_saved_pending: 0 });
          setRejectedSupplierProposalsPayload({ proposals: [], count_saved_rejected: 0 });
        });
      setSupplierVatRows((vatPolicyRows || []).map((row: any) => supplierVatRowFromApi(row)));
      setActiveSuppliersPickerList(
        (suppliersData || [])
          .map((s: { id?: number; name?: string }) => ({
            id: Number(s.id),
            name: String(s.name || '').trim()
          }))
          .filter((s: { id: number; name: string }) => Number.isFinite(s.id) && s.id > 0 && s.name)
          .sort((a: { id: number; name: string }, b: { id: number; name: string }) =>
            a.name.localeCompare(b.name, undefined, { sensitivity: 'base' })
          )
      );
      setSupplierRuleRows(supplierRuleRowsData || []);
      setVatRuleRows(Array.isArray(vatRuleRowsData) ? vatRuleRowsData : []);
      setVatRuleSuggestions(
        Array.isArray(vatSuggestionRowsData?.suggestions)
          ? vatSuggestionRowsData.suggestions
          : Array.isArray(vatSuggestionRowsData)
            ? vatSuggestionRowsData
            : []
      );
      setRejectedVatRuleSuggestions(
        Array.isArray(rejectedVatSuggestionRowsData) ? rejectedVatSuggestionRowsData : []
      );
      setSpecialCaseRuleRows(Array.isArray(specialCaseRuleRowsData) ? specialCaseRuleRowsData : []);
      if (ruleRunHistoryData) {
        setRuleRunHistory(ruleRunHistoryData);
        setRuleRunHistoryError('');
      } else {
        setRuleRunHistory(null);
        setRuleRunHistoryError('Rule Run History is unavailable right now.');
      }
      setCanonicalVatRateOptions(
        Array.isArray(canonicalVatRatesData)
          ? canonicalVatRatesData
              .map((x: any) => ({
                value: canonicalizeVatRate(String(x?.value || '')),
                label: String(x?.label || x?.value || '').trim()
              }))
              .filter((x: { value: string; label: string }) => x.value && x.label)
          : []
      );
      const canonicalSuppliersFromApi: string[] = (suppliersData || [])
        .map((s: any) => (s?.name || '').trim())
        .filter((n: string) => n.length > 0);
      setCanonicalSupplierOptions(
        Array.from(new Set(canonicalSuppliersFromApi)).sort((a, b) =>
          a.localeCompare(b, undefined, { sensitivity: 'base' })
        )
      );
      if (fetchGen !== fetchDataGenRef.current) return null;

      const reviewPayload = response.data as ReviewPayload;
      const canonicalRows = canonicalRowsResponse || { rows: [], count_total: 0, counts: {}, scope: {} };
      const pendingRuleSyncCount = Math.max(
        0,
        Number(reviewPayload?.counts?.untagged_total || 0) +
          Number(reviewPayload?.counts?.unclassified_needs_review_total || 0) +
          Number(reviewPayload?.counts?.conflicting_vat_total || 0)
      );
      setPayload(reviewPayload);
      // Early-apply already painted + started hydrate when review-rows resolved; only fill gaps here.
      if (!canonicalAppliedEarly) {
        applyCanonicalRowsForFetch(canonicalRows, canonicalFetchFailed);
      }
      setPendingManualRuleSyncCount(pendingRuleSyncCount);
      setTaggedPayload({
        tagged: [],
        count_total: 0,
        count_returned: 0,
        limit: REVIEW_TAGGED_ITEMS_PAGE_LIMIT,
        offset: 0,
        next_offset: 0,
        has_more: false
      });
      setConfirmedPayload({
        items: [],
        count_total: 0,
        count_returned: 0,
        limit: REVIEW_CONFIRMED_ITEMS_PAGE_LIMIT,
        offset: 0,
        next_offset: 0,
        has_more: false
      });
      setAllTransactionsPayload([]);
      setRulesPayload(((rulesResponse as { data?: RulesPayload })?.data ?? { rules: [] }) as RulesPayload);
      if ((rulesResponse as { __rulesListFailed?: boolean })?.__rulesListFailed) {
        const rulesRetryGen = fetchDataGenRef.current;
        void (async () => {
          for (const delayMs of [1500, 4000]) {
            await new Promise((r) => window.setTimeout(r, delayMs));
            if (fetchDataGenRef.current !== rulesRetryGen) return;
            try {
              const retry = await api.get('/api/transaction-classification-rules/', {
                params: { lite: 1 },
                timeout: 20000
              });
              if (fetchDataGenRef.current !== rulesRetryGen) return;
              setRulesPayload(((retry.data as RulesPayload) || { rules: [] }) as RulesPayload);
              return;
            } catch {
              // keep trying / give up quietly — Rules tab can Refresh later
            }
          }
        })();
      }
      const praw = proposalsResponse.data as RuleProposalsPayload | undefined;
      setProposalsPayload({
        proposals: praw?.proposals ?? [],
        count_atlas_virtual: praw?.count_atlas_virtual,
        count_saved_pending: praw?.count_saved_pending,
        count_saved_rejected: Number(praw?.count_saved_rejected || 0),
      });
      const rejectedPraw = rejectedProposalsResponse?.data as RuleProposalsPayload | undefined;
      setRejectedProposalsPayload({
        proposals: Array.isArray(rejectedPraw?.proposals) ? rejectedPraw!.proposals : [],
        count_saved_rejected: Number(
          rejectedPraw?.count_saved_rejected ?? rejectedPraw?.proposals?.length ?? 0
        ),
      });
      let categoryRows: CategoryItem[] = (categoriesResponse.data?.categories || [])
        .filter(isActiveCategoryApiRow)
        .map((c: any) => ({
          id: Number(c?.id),
          name: String(c?.name || '').trim(),
          is_key_category: Boolean(c?.is_key_category),
          is_active: true
        }))
        .filter((c: CategoryItem) => Number.isFinite(c.id) && c.id > 0 && c.name.length > 0);
      // If category list is empty, seed from P&L-like imported categories and refetch.
      if (categoryRows.length === 0) {
        try {
          await api.post('/api/categories/seed-pnl', { source: 'pandle' });
          const refetch = await api.get('/api/categories/', { params: { is_active: 'true' } });
          categoryRows = (refetch.data?.categories || [])
            .filter(isActiveCategoryApiRow)
            .map((c: any) => ({
              id: Number(c?.id),
              name: String(c?.name || '').trim(),
              is_key_category: Boolean(c?.is_key_category),
              is_active: true
            }))
            .filter((c: CategoryItem) => Number.isFinite(c.id) && c.id > 0 && c.name.length > 0);
          if (categoryRows.length > 0) {
            setSuccessMessage(`Loaded ${categoryRows.length} categories from P&L source.`);
          }
        } catch (seedErr: any) {
          setError(seedErr?.response?.data?.error || 'Could not auto-seed categories from P&L source.');
        }
      }
      setCategories(categoryRows);
      // Soft secondaries (rules list, history) retry quietly and must not spam warning toasts when
      // review-rows already painted — those failures are usually API busy / restart, not user action.
      const softSecondaryWarnings = new Set(['classification rules list', 'rule run history']);
      const notableWarnings = fetchDataPartialWarnings.filter((w) => !softSecondaryWarnings.has(w));
      if (notableWarnings.length > 0) {
        const critical = notableWarnings.some((w) => w.includes('review-rows'));
        if (critical) {
          const base = (api.defaults.baseURL || '').replace(/\/$/, '') || resolveReactAppApiBaseUrl();
          const isLocalApi = /^(https?:\/\/)?(localhost|127\.0\.0\.1)(:\d+)?$/i.test(base);
          setError(
            isLocalApi
              ? `Could not load ${notableWarnings.join(', ')}. Local API at ${base} is unreachable. ` +
                  `Run bash scripts/run_local_dev.sh (API + DB tunnel + frontend on dedicated ports), then refresh.`
              : `Could not load ${notableWarnings.join(', ')}. This session is using API base: ${base}. ` +
                  `Production API must be same-origin https://reinventedsolutions.xyz (calls go to /api/...). ` +
                  `If REACT_APP_API_URL points elsewhere, rebuild/redeploy the frontend. ` +
                  `502 responses often appear as CORS errors in DevTools.`
          );
        } else {
          setStackedToasts((prev) => [
            ...prev,
            {
              id: stackedToastIdRef.current++,
              code: nextClientToastCode(),
              severity: 'warning',
              message: `Core review data loaded, but some secondary requests failed: ${notableWarnings.join(
                ', '
              )}. Try Refresh. If the API was restarting, wait a few seconds and refresh again.`
            }
          ]);
        }
      }
      void (async () => {
        try {
          const taggedResponse = await loadAllTaggedItemsForReview();
          setTaggedPayload(taggedResponse as TaggedPayload);
        } catch {
          // Non-blocking hydration for tagged rows.
        }
      })();

      void (async () => {
        try {
          const [suggestionsResponse, unclassifiedSuggestionsResponse] = await Promise.all([
            api.get('/api/transaction-classification-rules/suggestions', {
              params: { limit: SUGGESTIONS_PREFILL_LIMIT, queue: 'untagged' }
            }),
            api.get('/api/transaction-classification-rules/suggestions', {
              params: { limit: SUGGESTIONS_PREFILL_LIMIT, queue: 'unclassified' }
            })
          ]);
          const suggestions = [
            ...(suggestionsResponse.data?.suggestions || []),
            ...(unclassifiedSuggestionsResponse.data?.suggestions || [])
          ];
          const nextSuggestions: Record<number, number | null> = {};
          const categoryNameToId = new Map<string, number>();
          categoryRows.forEach((c) => categoryNameToId.set((c.name || '').toLowerCase(), c.id));
          suggestions.forEach((s: any) => {
            const explicitId = s.suggested_category_id || null;
            const nameBasedId = categoryNameToId.get((s.suggested_category_name || '').toLowerCase()) || null;
            nextSuggestions[s.transaction_id] = explicitId || nameBasedId;
          });
          const initialHeadings: Record<number, number | ''> = {};
          const initialGranular: Record<number, number[]> = {};
          [...(reviewPayload?.untagged || []), ...(reviewPayload?.unclassified_needs_review || [])].forEach(
            (tx: ReviewTx) => {
              const suggested = nextSuggestions[tx.id];
              if (!suggested) return;
              const row = categoryRows.find((c) => c.id === suggested);
              if (!row) return;
              if (isTopLevelCashflowHeadingName(row.name)) {
                initialHeadings[tx.id] = suggested;
              } else {
                initialGranular[tx.id] = [suggested];
                const headingCats = categoryRows.filter((c) => isTopLevelCashflowHeadingName(c.name));
                const hid = inferHeadingCategoryIdFromAmount(headingCats, effectiveTxAmount(tx));
                if (hid !== '') initialHeadings[tx.id] = hid;
              }
            }
          );
          setSelectedHeadingByTx(initialHeadings);
          setSelectedGranularByTx(initialGranular);
        } catch {
          // Non-blocking suggestion prefill.
        }
      })();

      setSelectedTxIds([]);
      void hydrateConfirmedItemsInBackground();
      setUnifiedTxCategoryTagsFetchEpoch((n) => n + 1);
      return reviewPayload;
    } catch (err: any) {
      if (err?.code === 'ECONNABORTED') {
        setError(
          'Request timed out (90s). The API may be restarting or the query was too heavy — try Refresh in a moment.'
        );
      } else {
        setError(err?.response?.data?.error || err?.message || 'Failed to load manual review items');
      }
      return null;
    } finally {
      setLoading(false);
    }
  }, [
    explicitReviewScopeParams,
    fetchCanonicalRows,
    hydrateCanonicalRowsInBackground,
    hydrateConfirmedItemsInBackground,
    loadAllTaggedItemsForReview
  ]);

  const reapplyRulesUnconfirmedBatched = useCallback(
    async (onProgress?: (message: string) => void) => {
      let offset = 0;
      let pageLimit = REAPPLY_UNCONFIRMED_PAGE_LIMIT;
      let totalReprocessed = 0;
      let totalDeleted = 0;
      let totalCreated = 0;
      let batches = 0;
      let totalInScope: number | null = null;
      for (;;) {
        throwIfOperationCancelled();
        let res: any;
        try {
          res = await api.post(
            '/api/transaction-classification-rules/reapply-unconfirmed',
            {
              limit: pageLimit,
              offset,
              include_activity: false,
              activity_limit: 30
            },
            { timeout: HEAVY_RULE_JOB_TIMEOUT_MS }
          );
        } catch (err: any) {
          if (err?.code === 'ECONNABORTED' && pageLimit > REAPPLY_UNCONFIRMED_MIN_PAGE_LIMIT) {
            pageLimit = Math.max(REAPPLY_UNCONFIRMED_MIN_PAGE_LIMIT, Math.floor(pageLimit / 2));
            continue;
          }
          throw err;
        }
        batches += 1;
        totalReprocessed += res.data.transactions_reprocessed ?? 0;
        totalDeleted += res.data.rule_tags_deleted ?? 0;
        totalCreated += res.data.created_tags ?? 0;
        const pageTotal = readSweepTotalInScope(res.data || {});
        if (pageTotal != null) totalInScope = pageTotal;
        const nextOffset =
          typeof res.data.next_offset === 'number' ? res.data.next_offset : offset + pageLimit;
        const hasMore = Boolean(res.data.has_more);
        onProgress?.(
          formatRuleReapplyProgressLine(
            {
              percent: sweepProgressPercent({ nextOffset, hasMore, totalInScope }),
              createdTotal: totalCreated
            },
            res.data
          )
        );
        throwIfOperationCancelled();
        if (!hasMore) break;
        offset = nextOffset;
        if ((res.data.transactions_reprocessed ?? 0) === 0) break;
      }
      return { totalReprocessed, totalDeleted, totalCreated, batches };
    },
    [throwIfOperationCancelled]
  );

  const supplierNameBackfillBatched = useCallback(async (onProgress?: (message: string) => void) => {
    let offset = 0;
    let pageLimit = SUPPLIER_BACKFILL_PAGE_LIMIT;
    let totalScanned = 0;
    let totalUpdated = 0;
    let batches = 0;
    let totalInScope: number | null = null;
    for (;;) {
      throwIfOperationCancelled();
      let res: any;
      try {
        res = await api.post(
          '/api/transaction-classification-rules/backfill-supplier-names',
          {
            limit: pageLimit,
            offset,
            include_manual: true,
            include_activity: true,
            activity_limit: 30
          },
          { timeout: HEAVY_RULE_JOB_TIMEOUT_MS }
        );
      } catch (err: any) {
        if (err?.code === 'ECONNABORTED' && pageLimit > SUPPLIER_BACKFILL_MIN_PAGE_LIMIT) {
          pageLimit = Math.max(SUPPLIER_BACKFILL_MIN_PAGE_LIMIT, Math.floor(pageLimit / 2));
          onProgress?.('Supplier rules: timed out — retrying with a smaller page…');
          continue;
        }
        throw err;
      }
      batches += 1;
      const scanned = res.data.tags_scanned ?? 0;
      const updated = res.data.tags_updated ?? 0;
      totalScanned += scanned;
      totalUpdated += updated;
      const pageTotal = readSweepTotalInScope(res.data || {});
      if (pageTotal != null) totalInScope = pageTotal;
      const nextOffset =
        typeof res.data.next_offset === 'number' ? res.data.next_offset : offset + pageLimit;
      const hasMore = Boolean(res.data.has_more);
      onProgress?.(
        formatSupplierBackfillProgressLine(
          {
            percent: sweepProgressPercent({ nextOffset, hasMore, totalInScope }),
            updatedTotal: totalUpdated
          },
          res.data
        )
      );
      throwIfOperationCancelled();
      if (!hasMore) break;
      offset = nextOffset;
      if (scanned === 0) break;
    }
    return { totalScanned, totalUpdated, batches };
  }, [throwIfOperationCancelled]);

  const reapplyVatRulesUnconfirmedBatched = useCallback(async (onProgress?: (message: string) => void) => {
    let offset = 0;
    let pageLimit = REAPPLY_VAT_UNCONFIRMED_PAGE_LIMIT;
    let totalReprocessed = 0;
    let totalScanned = 0;
    let totalUpdated = 0;
    let batches = 0;
    let totalInScope: number | null = null;
    for (;;) {
      throwIfOperationCancelled();
      let res: any;
      try {
        res = await api.post(
          '/api/transaction-classification-rules/reapply-vat-unconfirmed',
          {
            limit: pageLimit,
            offset,
            include_activity: true,
            activity_limit: 30
          },
          { timeout: HEAVY_RULE_JOB_TIMEOUT_MS }
        );
      } catch (err: any) {
        if (err?.code === 'ECONNABORTED' && pageLimit > REAPPLY_VAT_UNCONFIRMED_MIN_PAGE_LIMIT) {
          pageLimit = Math.max(REAPPLY_VAT_UNCONFIRMED_MIN_PAGE_LIMIT, Math.floor(pageLimit / 2));
          onProgress?.('VAT rules: timed out — retrying with a smaller page…');
          continue;
        }
        throw err;
      }
      batches += 1;
      const reprocessed = Number(res.data?.transactions_reprocessed || 0);
      const scanned = Number(res.data?.tags_scanned || 0);
      const updated = Number(res.data?.tags_updated || 0);
      totalReprocessed += reprocessed;
      totalScanned += scanned;
      totalUpdated += updated;
      const pageTotal = readSweepTotalInScope(res.data || {});
      if (pageTotal != null) totalInScope = pageTotal;
      const nextOffset =
        typeof res.data?.next_offset === 'number' ? res.data.next_offset : offset + pageLimit;
      const hasMore = Boolean(res.data?.has_more);
      onProgress?.(
        `VAT rules: ${sweepProgressPercent({ nextOffset, hasMore, totalInScope })}% · ${totalUpdated.toLocaleString()} VAT tags updated`
      );
      throwIfOperationCancelled();
      if (!hasMore) break;
      offset = nextOffset;
      if (reprocessed === 0) break;
    }
    return { totalReprocessed, totalScanned, totalUpdated, batches };
  }, [throwIfOperationCancelled]);

  const reapplySpecialCaseRulesUnconfirmedBatched = useCallback(async (onProgress?: (message: string) => void) => {
    let offset = 0;
    let pageLimit = REAPPLY_VAT_UNCONFIRMED_PAGE_LIMIT;
    let totalReprocessed = 0;
    let totalScanned = 0;
    let totalUpdated = 0;
    let batches = 0;
    let totalInScope: number | null = null;
    for (;;) {
      throwIfOperationCancelled();
      let res: any;
      try {
        res = await api.post(
          '/api/transaction-classification-rules/reapply-special-cases-unconfirmed',
          {
            limit: pageLimit,
            offset
          },
          { timeout: HEAVY_RULE_JOB_TIMEOUT_MS }
        );
      } catch (err: any) {
        if (err?.code === 'ECONNABORTED' && pageLimit > REAPPLY_VAT_UNCONFIRMED_MIN_PAGE_LIMIT) {
          pageLimit = Math.max(REAPPLY_VAT_UNCONFIRMED_MIN_PAGE_LIMIT, Math.floor(pageLimit / 2));
          onProgress?.('Special case rules: timed out — retrying with a smaller page…');
          continue;
        }
        throw err;
      }
      batches += 1;
      const reprocessed = Number(res.data?.transactions_reprocessed || 0);
      const scanned = Number(res.data?.tags_scanned || 0);
      const updated = Number(res.data?.tags_updated || 0);
      totalReprocessed += reprocessed;
      totalScanned += scanned;
      totalUpdated += updated;
      const pageTotal = readSweepTotalInScope(res.data || {});
      if (pageTotal != null) totalInScope = pageTotal;
      const nextOffset =
        typeof res.data?.next_offset === 'number' ? res.data.next_offset : offset + pageLimit;
      const hasMore = Boolean(res.data?.has_more);
      onProgress?.(
        `Special case rules: ${sweepProgressPercent({ nextOffset, hasMore, totalInScope })}% · ${totalUpdated.toLocaleString()} special-case tags updated`
      );
      throwIfOperationCancelled();
      if (!hasMore) break;
      offset = nextOffset;
      if (reprocessed === 0) break;
    }
    return { totalReprocessed, totalScanned, totalUpdated, batches };
  }, [throwIfOperationCancelled]);

  const runSupplierNameBackfill = useCallback(async () => {
    resetOperationCancellation();
    setSupplierBackfillBusy(true);
    setError('');
    setOperationProgressMessage('Supplier rules: recalculating supplier display names from description mappings…');
    try {
      const result = await supplierNameBackfillBatched(setOperationProgressMessage);
      setSuccessMessage(
        `Supplier names from description mappings: updated ${result.totalUpdated} of ${result.totalScanned} tagged rows.`
      );
      await fetchData();
    } catch (err: any) {
      if (err?.code === 'OPERATION_CANCELLED') {
        setSuccessMessage('Supplier rules run stopped. Completed batches were kept; no further batches were run.');
      } else if (err?.code === 'ECONNABORTED') {
        setError(
          'Request timed out. Supplier refresh now uses smaller pages and automatic smaller retries — wait a moment and try again.'
        );
      } else {
        setError(err?.response?.data?.error || err?.message || 'Supplier name backfill failed');
      }
    } finally {
      setOperationProgressMessage(null);
      setSupplierBackfillBusy(false);
      resetOperationCancellation();
    }
  }, [fetchData, resetOperationCancellation, supplierNameBackfillBatched]);

  const runReapplyVatRulesUnconfirmed = useCallback(async () => {
    resetOperationCancellation();
    setReapplyVatUnconfirmedBusy(true);
    setError('');
    setOperationProgressMessage('VAT rules: starting re-run on unconfirmed transactions…');
    try {
      const result = await reapplyVatRulesUnconfirmedBatched(setOperationProgressMessage);
      setSuccessMessage(
        `Re-ran VAT rules on ${result.totalReprocessed} unconfirmed transaction(s): ` +
          `${result.totalUpdated} VAT tag(s) updated out of ${result.totalScanned} scanned.`
      );
      await fetchData();
    } catch (err: any) {
      if (err?.code === 'OPERATION_CANCELLED') {
        setSuccessMessage('VAT rules run stopped. Completed batches were kept; no further batches were run.');
      } else if (err?.code === 'ECONNABORTED') {
        setError(
          'Request timed out (90s). Run VAT re-apply again and it will continue in smaller batches.'
        );
      } else {
        setError(err?.response?.data?.error || err?.message || 'Could not re-apply VAT rules');
      }
    } finally {
      setOperationProgressMessage(null);
      setReapplyVatUnconfirmedBusy(false);
      resetOperationCancellation();
    }
  }, [fetchData, reapplyVatRulesUnconfirmedBatched, resetOperationCancellation]);

  const runReapplySpecialCaseRulesUnconfirmed = useCallback(async () => {
    resetOperationCancellation();
    setReapplySpecialCaseUnconfirmedBusy(true);
    setError('');
    setOperationProgressMessage('Special case rules: starting re-run on unconfirmed transactions…');
    try {
      const result = await reapplySpecialCaseRulesUnconfirmedBatched(setOperationProgressMessage);
      setSuccessMessage(
        `Re-ran Special Case rules on ${result.totalReprocessed} unconfirmed transaction(s): ` +
          `${result.totalUpdated} tag(s) updated out of ${result.totalScanned} scanned.`
      );
      await fetchData();
    } catch (err: any) {
      if (err?.code === 'OPERATION_CANCELLED') {
        setSuccessMessage('Special Case rules run stopped. Completed batches were kept; no further batches were run.');
      } else if (err?.code === 'ECONNABORTED') {
        setError(
          'Request timed out (90s). Run Special Case re-apply again and it will continue in smaller batches.'
        );
      } else {
        setError(err?.response?.data?.error || err?.message || 'Could not re-apply Special Case rules');
      }
    } finally {
      setOperationProgressMessage(null);
      setReapplySpecialCaseUnconfirmedBusy(false);
      resetOperationCancellation();
    }
  }, [fetchData, reapplySpecialCaseRulesUnconfirmedBatched, resetOperationCancellation]);

  const runGenerateProposalsFromAllTransactions = useCallback(async () => {
    setProposalSweepBusy(true);
    setError('');
    setOperationProgressMessage('Classification proposals: starting oldest-first sweep…');
    try {
      let offset = 0;
      let pageLimit = PROPOSAL_SWEEP_PAGE_LIMIT;
      let batches = 0;
      let scannedTotal = 0;
      let createdTotal = 0;
      let totalInScope: number | null = null;
      let skippedRuleApplied = 0;
      let skippedMatchingRule = 0;
      let skippedMatchingProposal = 0;
      let skippedExistingTxProposal = 0;
      let skippedNoGranularTag = 0;
      let skippedNoMatchText = 0;
      for (;;) {
        const { data, pageLimit: usedLimit } = await postSweepPageWithTimeoutRetry({
          url: '/api/transaction-classification-rules/rule-proposals/generate-from-all-transactions',
          body: { offset, include_all_users: true },
          pageLimit,
          minPageLimit: PROPOSAL_SWEEP_MIN_PAGE_LIMIT,
          progressLabel: 'Classification proposals',
          onProgress: setOperationProgressMessage
        });
        pageLimit = usedLimit;
        batches += 1;
        const scanned = Number(data.transactions_scanned || 0);
        scannedTotal += scanned;
        createdTotal += Number(data.created || 0);
        skippedRuleApplied += Number(data.skipped_rule_applied || 0);
        skippedMatchingRule += Number(data.skipped_matching_rule || 0);
        skippedMatchingProposal += Number(data.skipped_matching_pending_proposal || 0);
        skippedExistingTxProposal += Number(data.skipped_existing_proposal_for_tx || 0);
        skippedNoGranularTag += Number(data.skipped_no_granular_tag || 0);
        skippedNoMatchText += Number(data.skipped_no_match_text || 0);
        const pageTotal = readSweepTotalInScope(data);
        if (pageTotal != null) totalInScope = pageTotal;
        const nextOffset = typeof data.next_offset === 'number' ? data.next_offset : offset + pageLimit;
        const hasMore = Boolean(data.has_more);
        setOperationProgressMessage(
          formatSweepProgressMessage({
            label: 'Classification proposals',
            percent: sweepProgressPercent({ nextOffset, hasMore, totalInScope }),
            createdCount: createdTotal
          })
        );
        if (!hasMore) break;
        offset = nextOffset;
        if (scanned === 0) break;
      }

      const pr = await api.get('/api/transaction-classification-rules/rule-proposals');
      const praw = pr.data as RuleProposalsPayload | undefined;
      setProposalsPayload({
        proposals: Array.isArray(praw?.proposals) ? praw!.proposals : [],
        count_atlas_virtual: praw?.count_atlas_virtual,
        count_saved_pending: praw?.count_saved_pending,
        count_saved_rejected: Number(praw?.count_saved_rejected || 0),
      });
      setRulesAccordionOpen((prev) => ({ ...prev, proposals: true }));
      setPipelineNestedSection('step3', 'proposed', true);
      setSuccessMessageText('');
      setRuleSyncCompletionToast(
        buildClassificationProposalSweepToast({
          title: 'Classification proposal sweep finished',
          created: createdTotal,
          scanned: scannedTotal,
          skips: [
            { label: 'Rule already applied', count: skippedRuleApplied },
            { label: 'Existing rule match', count: skippedMatchingRule },
            { label: 'Existing proposal match', count: skippedMatchingProposal },
            { label: 'Existing proposal for TX', count: skippedExistingTxProposal },
            { label: 'No granular category / no DB bucket', count: skippedNoGranularTag },
            { label: 'No match text', count: skippedNoMatchText }
          ],
          footer: 'Full sweep includes all users when you are admin/owner.'
        })
      );
      setSuccessToastCode(nextClientToastCode());
    } catch (err: any) {
      if (err?.code === 'ECONNABORTED') {
        setError(
          'Request timed out even after smaller batches. Wait a moment and run Generate Proposed rules again — completed batches were kept.'
        );
      } else {
        setError(
          err?.response?.data?.error ||
            err?.message ||
            'Could not generate proposals from all transactions'
        );
      }
    } finally {
      setOperationProgressMessage(null);
      setProposalSweepBusy(false);
    }
  }, []);

  const runMissingCategoryAudit = useCallback(async () => {
    setMissingCategoryAuditBusy(true);
    setError('');
    try {
      const res = await api.get('/api/transaction-classification-rules/missing-category-audit', {
        params: { ...AUDIT_INCLUDE_PROPOSAL_CANDIDATE_PARAMS },
      });
      const payload = (res.data && typeof res.data === 'object' ? res.data : {}) as Record<string, unknown>;
      setMissingCategoryAudit(payload);
      const d = payload;
      if (d.ok === false) {
        setAuditCandidatesModal(null);
        setSuccessMessage(
          typeof d.message === 'string' ? d.message : 'Missing-category audit is not available for this database shape.'
        );
      } else {
        setSuccessMessage(
          `Missing-category audit: ${Number(d.missing_category_display_count || 0).toLocaleString()} of ${Number(
            d.total_transactions || 0
          ).toLocaleString()} transactions count as missing category (same logic as the review grid). ` +
            `${Number(d.missing_with_match_text || 0).toLocaleString()} have merchant/description/reference text; ` +
            `${Number(d.missing_no_match_text || 0).toLocaleString()} have no match text for rule conditions.`
        );
        const cands = parseCandidates(d.proposal_audit_candidates);
        if (cands.length > 0) {
          const rid = d.proposal_audit_run_id;
          setAuditCandidatesModal({
            kind: 'classification',
            title: 'Missing category — generate drafts from selected buckets',
            candidates: cands,
            proposalAuditRunId: typeof rid === 'string' && rid.trim().length > 0 ? rid.trim() : null,
          });
        } else {
          setAuditCandidatesModal(null);
        }
      }
    } catch (err: any) {
      setError(err?.response?.data?.error || err?.message || 'Could not run missing-category audit');
    } finally {
      setMissingCategoryAuditBusy(false);
    }
  }, []);

  const runGenerateProposalsMissingCategoryOnly = useCallback(async () => {
    setProposalSweepBusy(true);
    setError('');
    setOperationProgressMessage('Missing-category proposals: starting oldest-first sweep…');
    try {
      let offset = 0;
      let pageLimit = PROPOSAL_SWEEP_PAGE_LIMIT;
      let batches = 0;
      let scannedTotal = 0;
      let createdTotal = 0;
      let totalInScope: number | null = null;
      let skippedRuleApplied = 0;
      let skippedMatchingRule = 0;
      let skippedMatchingProposal = 0;
      let skippedExistingTxProposal = 0;
      let skippedNoGranularTag = 0;
      let skippedNoMatchText = 0;
      let skippedNotMissingCategory = 0;
      let createdUsingFallbackCategory = 0;
      for (;;) {
        const { data, pageLimit: usedLimit } = await postSweepPageWithTimeoutRetry({
          url: '/api/transaction-classification-rules/rule-proposals/generate-from-all-transactions',
          body: {
            offset,
            include_all_users: false,
            only_missing_category_display: true
          },
          pageLimit,
          minPageLimit: PROPOSAL_SWEEP_MIN_PAGE_LIMIT,
          progressLabel: 'Missing-category proposals',
          onProgress: setOperationProgressMessage
        });
        pageLimit = usedLimit;
        batches += 1;
        const scanned = Number(data.transactions_scanned || 0);
        scannedTotal += scanned;
        createdTotal += Number(data.created || 0);
        skippedRuleApplied += Number(data.skipped_rule_applied || 0);
        skippedMatchingRule += Number(data.skipped_matching_rule || 0);
        skippedMatchingProposal += Number(data.skipped_matching_pending_proposal || 0);
        skippedExistingTxProposal += Number(data.skipped_existing_proposal_for_tx || 0);
        skippedNoGranularTag += Number(data.skipped_no_granular_tag || 0);
        skippedNoMatchText += Number(data.skipped_no_match_text || 0);
        skippedNotMissingCategory += Number(data.skipped_not_missing_category || 0);
        createdUsingFallbackCategory += Number(data.created_using_fallback_category || 0);
        const pageTotal = readSweepTotalInScope(data);
        if (pageTotal != null) totalInScope = pageTotal;
        const nextOffset = typeof data.next_offset === 'number' ? data.next_offset : offset + pageLimit;
        const hasMore = Boolean(data.has_more);
        setOperationProgressMessage(
          formatSweepProgressMessage({
            label: 'Missing-category proposals',
            percent: sweepProgressPercent({ nextOffset, hasMore, totalInScope }),
            createdCount: createdTotal
          })
        );
        if (!hasMore) break;
        offset = nextOffset;
        if (scanned === 0) break;
      }

      const pr = await api.get('/api/transaction-classification-rules/rule-proposals');
      const praw = pr.data as RuleProposalsPayload | undefined;
      setProposalsPayload({
        proposals: Array.isArray(praw?.proposals) ? praw!.proposals : [],
        count_atlas_virtual: praw?.count_atlas_virtual,
        count_saved_pending: praw?.count_saved_pending,
        count_saved_rejected: Number(praw?.count_saved_rejected || 0),
      });
      setRulesAccordionOpen((prev) => ({ ...prev, proposals: true }));
      setPipelineNestedSection('step3', 'proposed', true);
      setSuccessMessageText('');
      setRuleSyncCompletionToast(
        buildClassificationProposalSweepToast({
          title: 'Missing-category proposal sweep finished',
          created: createdTotal,
          scanned: scannedTotal,
          fallbackCreated: createdUsingFallbackCategory,
          skips: [
            { label: 'Already had category in review', count: skippedNotMissingCategory },
            { label: 'Rule already applied', count: skippedRuleApplied },
            { label: 'Existing rule match', count: skippedMatchingRule },
            { label: 'Existing proposal match', count: skippedMatchingProposal },
            { label: 'Existing proposal for TX', count: skippedExistingTxProposal },
            { label: 'No granular category / no DB bucket', count: skippedNoGranularTag },
            { label: 'No match text', count: skippedNoMatchText }
          ]
        })
      );
      setSuccessToastCode(nextClientToastCode());
    } catch (err: any) {
      if (err?.code === 'ECONNABORTED') {
        setError(
          'Request timed out even after smaller batches. Wait a moment and run again — completed batches were kept.'
        );
      } else {
        setError(
          err?.response?.data?.error ||
            err?.message ||
            'Could not generate classification proposals for missing-category transactions'
        );
      }
    } finally {
      setOperationProgressMessage(null);
      setProposalSweepBusy(false);
    }
  }, []);

  const runGenerateSupplierProposalsFromAllTransactions = useCallback(async () => {
    setSupplierProposalSweepBusy(true);
    setError('');
    setOperationProgressMessage('Supplier proposals: starting oldest-first sweep…');
    try {
      let offset = 0;
      let pageLimit = SUPPLIER_PROPOSAL_SWEEP_PAGE_LIMIT;
      let batches = 0;
      let scannedTotal = 0;
      let createdTotal = 0;
      let totalInScope: number | null = null;
      let skippedExistingRule = 0;
      let skippedMatchingPendingProposal = 0;
      let skippedExistingProposalForTx = 0;
      let skippedNoSupplierCandidate = 0;
      let skippedNoMatchText = 0;
      for (;;) {
        const { data, pageLimit: usedLimit } = await postSweepPageWithTimeoutRetry({
          url: '/api/suppliers/description-alias-proposals/generate-from-all-transactions',
          body: { offset, include_all_users: true },
          pageLimit,
          minPageLimit: SUPPLIER_PROPOSAL_SWEEP_MIN_PAGE_LIMIT,
          progressLabel: 'Supplier proposals',
          onProgress: setOperationProgressMessage
        });
        pageLimit = usedLimit;
        batches += 1;
        const scanned = Number(data.transactions_scanned || 0);
        scannedTotal += scanned;
        createdTotal += Number(data.created || 0);
        skippedExistingRule += Number(data.skipped_existing_rule || 0);
        skippedMatchingPendingProposal += Number(data.skipped_matching_pending_proposal || 0);
        skippedExistingProposalForTx += Number(data.skipped_existing_proposal_for_tx || 0);
        skippedNoSupplierCandidate += Number(data.skipped_no_supplier_candidate || 0);
        skippedNoMatchText += Number(data.skipped_no_match_text || 0);
        const pageTotal = readSweepTotalInScope(data);
        if (pageTotal != null) totalInScope = pageTotal;
        const nextOffset = typeof data.next_offset === 'number' ? data.next_offset : offset + pageLimit;
        const hasMore = Boolean(data.has_more);
        setOperationProgressMessage(
          formatSweepProgressMessage({
            label: 'Supplier proposals',
            percent: sweepProgressPercent({ nextOffset, hasMore, totalInScope }),
            createdCount: createdTotal
          })
        );
        if (!hasMore) break;
        offset = nextOffset;
        if (scanned === 0) break;
      }

      const [pr, rejectedPr] = await Promise.all([
        api.get('/api/suppliers/description-alias-proposals', { params: { status: 'pending', limit: 2000 } }),
        api
          .get('/api/suppliers/description-alias-proposals', { params: { status: 'rejected', limit: 2000 } })
          .catch(() => ({ data: { proposals: [], count_saved_rejected: 0 } }))
      ]);
      setSupplierProposalsPayload({
        proposals: Array.isArray(pr.data?.proposals) ? pr.data.proposals : [],
        count_saved_pending: pr.data?.count_saved_pending
      });
      setRejectedSupplierProposalsPayload({
        proposals: Array.isArray(rejectedPr.data?.proposals) ? rejectedPr.data.proposals : [],
        count_saved_rejected: Number(rejectedPr.data?.count_saved_rejected || 0)
      });
      setRulesAccordionOpen((prev) => ({ ...prev, supplierProposals: true }));
      setPipelineNestedSection('step2b', 'proposed', true);
      setSuccessMessage(
        `Supplier proposal sweep finished: created ${createdTotal} new proposals after scanning ${scannedTotal} transaction(s).` +
          ` Skipped — existing supplier rule: ${skippedExistingRule}, existing pending supplier proposal match: ${skippedMatchingPendingProposal}, existing proposal for tx: ${skippedExistingProposalForTx}, no supplier candidate: ${skippedNoSupplierCandidate}, no match text: ${skippedNoMatchText}. (Full sweep: all users when you are admin/owner.)`
      );
    } catch (err: any) {
      if (err?.code === 'ECONNABORTED') {
        setError(
          'Request timed out even after smaller batches. Wait a moment and run again — completed batches were kept.'
        );
      } else {
        setError(
          err?.response?.data?.error ||
            err?.message ||
            'Could not generate supplier proposals from all transactions'
        );
      }
    } finally {
      setOperationProgressMessage(null);
      setSupplierProposalSweepBusy(false);
    }
  }, []);

  const goToMissingSupplierAuditSection = useCallback(() => {
    const next = new URLSearchParams(searchParams);
    next.set('tab', 'rules');
    next.set('focus', 'missing-supplier-audit');
    setSearchParams(next, { replace: true });
  }, [searchParams, setSearchParams]);

  const discardMissingSupplierAuditResults = useCallback(() => {
    clearMissingSupplierAuditSnapshot();
    setMissingSupplierAudit(null);
    setMissingSupplierAuditSavedAt(null);
    setAuditCandidatesModal((prev) => (prev?.kind === 'supplier_alias' ? null : prev));
  }, []);

  const copyMissingSupplierAuditDeepLink = useCallback(async () => {
    try {
      const path = `${location.pathname}?tab=rules&focus=missing-supplier-audit`;
      const url = `${window.location.origin}${path}`;
      await navigator.clipboard.writeText(url);
      setSuccessMessage('Link copied. Paste into the address bar to open Rules → missing-supplier audit.');
    } catch {
      setError('Could not copy link (clipboard permission).');
    }
  }, [location.pathname]);

  const runMissingSupplierAudit = useCallback(async () => {
    setMissingSupplierAuditBusy(true);
    setError('');
    try {
      const res = await api.get('/api/suppliers/missing-supplier-audit', {
        params: { ...AUDIT_INCLUDE_PROPOSAL_CANDIDATE_PARAMS },
      });
      const payload = (res.data && typeof res.data === 'object' ? res.data : {}) as Record<string, unknown>;
      setMissingSupplierAudit(payload);
      saveMissingSupplierAuditSnapshot(payload);
      setMissingSupplierAuditSavedAt(Date.now());
      const d = payload;
      if (d.ok === false) {
        setAuditCandidatesModal(null);
        setSuccessMessage(
          typeof d.message === 'string' ? d.message : 'Missing-supplier audit is not available for this database shape.'
        );
      } else {
        setSuccessMessage(
          `Missing-supplier audit: ${Number(d.missing_supplier_display_count || 0).toLocaleString()} of ${Number(
            d.total_transactions || 0
          ).toLocaleString()} transactions show no supplier (same logic as the review grid). ` +
            `${Number(d.missing_with_description_or_reference || 0).toLocaleString()} have description/reference text; ` +
            `${Number(d.missing_no_statement_text || 0).toLocaleString()} have no description or reference.`
        );
        const cands = parseCandidates(d.proposal_audit_candidates);
        if (cands.length > 0) {
          const rid = d.proposal_audit_run_id;
          setAuditCandidatesModal({
            kind: 'supplier_alias',
            title: 'Missing supplier — generate drafts from selected buckets',
            candidates: cands,
            proposalAuditRunId: typeof rid === 'string' && rid.trim().length > 0 ? rid.trim() : null,
          });
        } else {
          setAuditCandidatesModal(null);
        }
      }
    } catch (err: any) {
      setError(err?.response?.data?.error || err?.message || 'Could not run missing-supplier audit');
    } finally {
      setMissingSupplierAuditBusy(false);
    }
  }, []);

  const runGenerateSupplierProposalsMissingSupplierOnly = useCallback(async () => {
    setSupplierProposalSweepBusy(true);
    setError('');
    try {
      const res = await api.post('/api/transaction-classification-rules/supplier-2b-proposal-jobs/start', {});
      const jobId = String(res.data?.job?.job_id || '').trim();
      if (!(res.status === 202 && jobId)) {
        throw new Error('Step 2B proposal job was started but no job id was returned.');
      }
      bgJobs.trackJob(jobId, 'supplier_2b_proposals');
      setRulesAccordionOpen((prev) => ({ ...prev, supplierProposals: true }));
      setPipelineNestedSection('step2b', 'proposed', true);
      setOperationProgressMessage(
        String(
          res.data?.job?.progress_message ||
            'Step 2B non-merchant proposals: 0% · 0 proposed rules created'
        ).trim()
      );
      setSuccessMessage(
        "Step 2B supplier proposal generation started on the server — you can leave this page; we'll notify you when it finishes."
      );
    } catch (err: any) {
      if (err?.response?.status === 409 && err?.response?.data?.job?.job_id) {
        const jobId = String(err.response.data.job.job_id || '').trim();
        if (jobId) {
          bgJobs.trackJob(jobId, 'supplier_2b_proposals');
          setOperationProgressMessage(
            String(
              err?.response?.data?.job?.progress_message ||
                'Step 2B non-merchant proposals: 0% · 0 proposed rules created'
            ).trim()
          );
          setSuccessMessage(
            "A Step 2B proposal job is already running — tracking it. You'll get a notification when it finishes."
          );
          return;
        }
      }
      setError(
        err?.response?.data?.error ||
          err?.message ||
          'Could not start Step 2B supplier proposal generation'
      );
      setSupplierProposalSweepBusy(false);
    }
  }, [bgJobs]);

  const runMissingVatAudit = useCallback(async () => {
    setMissingVatAuditBusy(true);
    setError('');
    try {
      const res = await api.get('/api/transaction-classification-rules/missing-vat-audit', {
        params: { ...AUDIT_INCLUDE_PROPOSAL_CANDIDATE_PARAMS },
      });
      const payload = (res.data && typeof res.data === 'object' ? res.data : {}) as Record<string, unknown>;
      setMissingVatAudit(payload);
      const d = payload;
      if (d.ok === false) {
        setAuditCandidatesModal(null);
        setSuccessMessage(
          typeof d.message === 'string' ? d.message : 'Missing-VAT audit is not available for this database shape.'
        );
      } else {
        setSuccessMessage(
          `Missing-VAT audit: ${Number(d.missing_vat_rate_display_count || 0).toLocaleString()} of ${Number(
            d.total_transactions || 0
          ).toLocaleString()} transactions show no VAT rate (review grid; VAT Conflict rows excluded). ` +
            `${Number(d.missing_vat_with_granular_category || 0).toLocaleString()} have a granular category for VAT-rule suggestions; ` +
            `${Number(d.missing_vat_without_granular_category || 0).toLocaleString()} need category first. ` +
            `${Number(d.vat_conflict_transaction_count || 0).toLocaleString()} VAT conflict row(s).`
        );
        const cands = parseCandidates(d.proposal_audit_candidates);
        if (cands.length > 0) {
          const rid = d.proposal_audit_run_id;
          setAuditCandidatesModal({
            kind: 'vat_suggestion',
            title: 'Missing VAT — generate suggestions from selected buckets',
            candidates: cands,
            proposalAuditRunId: typeof rid === 'string' && rid.trim().length > 0 ? rid.trim() : null,
          });
        } else {
          setAuditCandidatesModal(null);
        }
      }
    } catch (err: any) {
      setError(err?.response?.data?.error || err?.message || 'Could not run missing-VAT audit');
    } finally {
      setMissingVatAuditBusy(false);
    }
  }, []);

  const runGenerateVatSuggestionsMissingVatOnly = useCallback(async () => {
    setError('');
    setVatSuggestionGenerateBusy(true);
    setOperationProgressMessage('Missing-VAT suggestions: starting oldest-first sweep…');
    try {
      let offset = 0;
      let pageLimit = VAT_MISSING_SWEEP_PAGE_LIMIT;
      let batches = 0;
      let scannedTotal = 0;
      let generatedTotal = 0;
      let updatedTotal = 0;
      let totalInScope: number | null = null;
      let skippedEngineTotal = 0;
      let missingVatOnPageTotal = 0;
      let skippedVatConflict = 0;
      let skippedNotMissingVat = 0;
      let skippedNoGranularCategory = 0;
      for (;;) {
        const { data, pageLimit: usedLimit } = await postSweepPageWithTimeoutRetry({
          url: '/api/transaction-classification-rules/vat-rules/suggestions/generate-from-missing-vat-transactions',
          body: { offset, account_scope: 'both' },
          pageLimit,
          minPageLimit: VAT_MISSING_SWEEP_MIN_PAGE_LIMIT,
          progressLabel: 'Missing-VAT suggestions',
          onProgress: setOperationProgressMessage
        });
        pageLimit = usedLimit;
        batches += 1;
        const scanned = Number(data.transactions_scanned || 0);
        scannedTotal += scanned;
        generatedTotal += Number(data.generated || 0);
        updatedTotal += Number(data.updated || 0);
        skippedEngineTotal += Number(data.skipped_suggestion_engine || 0);
        missingVatOnPageTotal += Number(data.transactions_missing_vat_on_page || 0);
        skippedVatConflict += Number(data.skipped_vat_conflict || 0);
        skippedNotMissingVat += Number(data.skipped_not_missing_vat || 0);
        skippedNoGranularCategory += Number(data.skipped_no_granular_category || 0);
        const pageTotal = readSweepTotalInScope(data);
        if (pageTotal != null) totalInScope = pageTotal;
        const nextOffset = typeof data.next_offset === 'number' ? data.next_offset : offset + pageLimit;
        const hasMore = Boolean(data.has_more);
        setOperationProgressMessage(
          formatSweepProgressMessage({
            label: 'Missing-VAT suggestions',
            percent: sweepProgressPercent({ nextOffset, hasMore, totalInScope }),
            createdCount: generatedTotal,
            createdNoun: 'suggestions generated'
          })
        );
        if (!hasMore) break;
        offset = nextOffset;
        if (scanned === 0) break;
      }

      const listRes = await api.get('/api/transaction-classification-rules/vat-rules/suggestions', {
        params: { status: 'pending', account_scope: 'both' }
      });
      setVatRuleSuggestions(Array.isArray(listRes.data?.suggestions) ? listRes.data.suggestions : []);
      {
        const rejectedListRes = await api.get('/api/transaction-classification-rules/vat-rules/suggestions', {
          params: { status: 'rejected', account_scope: 'both' }
        }).catch(() => ({ data: { suggestions: [] } }));
        setRejectedVatRuleSuggestions(
          Array.isArray(rejectedListRes.data?.suggestions) ? rejectedListRes.data.suggestions : []
        );
      }
      setSuccessMessage(
        `Missing-VAT VAT-suggestion sweep finished: ${generatedTotal} new, ${updatedTotal} refreshed` +
          (skippedEngineTotal > 0 ? `, ${skippedEngineTotal} skipped by suggestion engine.` : '.') +
          ` Scanned ${scannedTotal} ledger row(s); ` +
          `${missingVatOnPageTotal} row(s) missing VAT (on scanned pages); ` +
          `skipped VAT conflict ${skippedVatConflict}, already had VAT ${skippedNotMissingVat}, no granular category ${skippedNoGranularCategory}.`
      );
    } catch (err: any) {
      if (err?.code === 'ECONNABORTED') {
        setError(
          'Request timed out even after smaller batches. Wait a moment and run again — completed batches were kept.'
        );
      } else {
        setError(
          err?.response?.data?.error ||
            err?.message ||
            'Could not generate VAT suggestions for missing-VAT transactions'
        );
      }
    } finally {
      setOperationProgressMessage(null);
      setVatSuggestionGenerateBusy(false);
    }
  }, []);

  const runGenerateVatRuleProposals = useCallback(async () => {
    setError('');
    setVatSuggestionGenerateBusy(true);
    try {
      const res = await api.post(
        '/api/transaction-classification-rules/vat-rules/suggestions/generate',
        { account_scope: 'both' },
        { timeout: HEAVY_RULE_JOB_TIMEOUT_MS }
      );
      const data = res.data || {};
      const generated = Number(data.generated || 0);
      const updated = Number(data.updated || 0);
      const skipped = Number(data.skipped || 0);
      const listRes = await api.get('/api/transaction-classification-rules/vat-rules/suggestions', {
        params: { status: 'pending', account_scope: 'both' }
      });
      setVatRuleSuggestions(Array.isArray(listRes.data?.suggestions) ? listRes.data.suggestions : []);
      {
        const rejectedListRes = await api.get('/api/transaction-classification-rules/vat-rules/suggestions', {
          params: { status: 'rejected', account_scope: 'both' }
        }).catch(() => ({ data: { suggestions: [] } }));
        setRejectedVatRuleSuggestions(
          Array.isArray(rejectedListRes.data?.suggestions) ? rejectedListRes.data.suggestions : []
        );
      }
      setSuccessMessage(
        `Generated VAT suggestions: ${generated} new, ${updated} refreshed` +
          (skipped > 0 ? `, ${skipped} skipped.` : '.')
      );
    } catch (err: any) {
      setError(err?.response?.data?.error || err?.message || 'Could not generate VAT suggestions');
    } finally {
      setVatSuggestionGenerateBusy(false);
    }
  }, []);

  const openBackfillAfterAccept = useCallback(
    (opts: { ruleName: string; prefixedId?: string; target: BackfillAfterAcceptTarget | null }) => {
      setBackfillAfterAcceptRuleName((opts.ruleName || '').trim() || 'accepted rule');
      setBackfillAfterAcceptPrefixedId((opts.prefixedId || '').trim());
      setBackfillAfterAcceptTarget(opts.target);
      setBackfillAfterAcceptOpen(true);
    },
    []
  );

  const closeBackfillAfterAccept = useCallback(() => {
    setBackfillAfterAcceptOpen(false);
    setBackfillAfterAcceptPrefixedId('');
    setBackfillAfterAcceptTarget(null);
    setBackfillAfterAcceptRuleName('');
  }, []);

  const approveVatRuleSuggestion = useCallback(
    async (row: VatRuleSuggestionRow) => {
      try {
        setError('');
        setVatSuggestionActionBusyId(row.id);
        const approveRes = await api.post(
          `/api/transaction-classification-rules/vat-rules/suggestions/${row.id}/approve`,
          {}
        );
        const createdVatRule = approveRes.data?.vat_rule as CategoryVatRuleRow | undefined;
        const createdVatRuleId = createdVatRule?.id != null ? Math.trunc(Number(createdVatRule.id)) : NaN;
        const [rulesRes, suggestionsRes] = await Promise.all([
          api.get('/api/transaction-classification-rules/vat-rules'),
          api.get('/api/transaction-classification-rules/vat-rules/suggestions', {
            params: { status: 'pending', account_scope: 'both' }
          })
        ]);
        setVatRuleRows(Array.isArray(rulesRes.data?.vat_rules) ? rulesRes.data.vat_rules : []);
        setVatRuleSuggestions(
          Array.isArray(suggestionsRes.data?.suggestions) ? suggestionsRes.data.suggestions : []
        );
        {
          const rejectedListRes = await api
            .get('/api/transaction-classification-rules/vat-rules/suggestions', {
              params: { status: 'rejected', account_scope: 'both' }
            })
            .catch(() => ({ data: { suggestions: [] } }));
          setRejectedVatRuleSuggestions(
            Array.isArray(rejectedListRes.data?.suggestions) ? rejectedListRes.data.suggestions : []
          );
        }
        setSuccessMessage('VAT suggestion approved and converted to an inactive VAT rule.');
        const cat = String(row.category_name || row.category?.name || '').trim();
        const rate = String(row.suggested_vat_rate || '').trim();
        openBackfillAfterAccept({
          ruleName: cat ? `${cat} → ${rate || 'VAT rule'}` : rate || 'VAT rule',
          prefixedId:
            Number.isFinite(createdVatRuleId) && createdVatRuleId > 0
              ? formatPrefixedRuleId('vat', createdVatRuleId)
              : '',
          target:
            Number.isFinite(createdVatRuleId) && createdVatRuleId > 0
              ? { kind: 'vat', id: createdVatRuleId }
              : null
        });
      } catch (err: any) {
        setError(err?.response?.data?.error || err?.message || 'Could not approve VAT suggestion');
      } finally {
        setVatSuggestionActionBusyId(null);
      }
    },
    [openBackfillAfterAccept]
  );

  const rejectVatRuleSuggestion = useCallback(async (row: VatRuleSuggestionRow) => {
    try {
      setError('');
      setVatSuggestionActionBusyId(row.id);
      await api.post(`/api/transaction-classification-rules/vat-rules/suggestions/${row.id}/reject`, {});
      setVatRuleSuggestions((prev) => prev.filter((x) => x.id !== row.id));
      setRejectedVatRuleSuggestions((prev) => [{ ...row, status: 'rejected' as const }, ...prev.filter((x) => x.id !== row.id)]);
      setSuccessMessage('VAT suggestion rejected.');
    } catch (err: any) {
      setError(err?.response?.data?.error || err?.message || 'Could not reject VAT suggestion');
    } finally {
      setVatSuggestionActionBusyId(null);
    }
  }, []);

  const runBulkVatSuggestionAction = useCallback(
    async (action: 'approve' | 'reject') => {
      const targets = visibleVatRuleSuggestions.filter((r) => selectedVatSuggestionIds.includes(r.id));
      if (targets.length === 0) return;
      setProposalBusyKey(`bulk-vat-${action}`);
      setError('');
      try {
        if (action === 'approve' && targets.length > 0) {
          setOperationProgressMessage(
            `Started background approve for ${targets.length} proposed VAT rule(s). You can keep working.`
          );
        }
        const chunkSize = action === 'approve' ? 12 : 1;
        let processedCount = 0;
        let failed = 0;
        for (let i = 0; i < targets.length; i += chunkSize) {
          const batch = targets.slice(i, i + chunkSize);
          const settled = await Promise.allSettled(
            batch.map(async (row) => {
              if (action === 'approve') {
                await api.post(`/api/transaction-classification-rules/vat-rules/suggestions/${row.id}/approve`, {});
              } else {
                await api.post(`/api/transaction-classification-rules/vat-rules/suggestions/${row.id}/reject`, {});
              }
            })
          );
          processedCount += settled.length;
          failed += settled.filter((r) => r.status === 'rejected').length;
          if (action === 'approve') {
            setOperationProgressMessage(
              `Background approve in progress: processed ${processedCount}/${targets.length} VAT suggestion(s)…`
            );
          }
        }
        if (action === 'approve') {
          setOperationProgressMessage(null);
        }
        const [rulesRes, suggestionsRes] = await Promise.all([
          api.get('/api/transaction-classification-rules/vat-rules'),
          api.get('/api/transaction-classification-rules/vat-rules/suggestions', {
            params: { status: 'pending', account_scope: 'both' }
          })
        ]);
        setVatRuleRows(Array.isArray(rulesRes.data?.vat_rules) ? rulesRes.data.vat_rules : []);
        setVatRuleSuggestions(
          Array.isArray(suggestionsRes.data?.suggestions) ? suggestionsRes.data.suggestions : []
        );
        {
          const rejectedListRes = await api
            .get('/api/transaction-classification-rules/vat-rules/suggestions', {
              params: { status: 'rejected', account_scope: 'both' }
            })
            .catch(() => ({ data: { suggestions: [] } }));
          setRejectedVatRuleSuggestions(
            Array.isArray(rejectedListRes.data?.suggestions) ? rejectedListRes.data.suggestions : []
          );
        }
        const successCount = Math.max(0, targets.length - failed);
        if (failed === 0) {
          setSuccessMessage(
            `${action === 'approve' ? 'Approved' : 'Rejected'} ${successCount} proposed VAT rule(s).`
          );
        } else if (successCount > 0) {
          setError(
            `${action === 'approve' ? 'Approved' : 'Rejected'} ${successCount} of ${targets.length}; ${failed} failed. Refresh, then retry the rest.`
          );
        } else {
          setError(
            `All ${failed} VAT proposal(s) failed. Refresh the list and try again, or check your connection.`
          );
        }
        setSelectedVatSuggestionIds([]);
      } catch (err: any) {
        setError(err?.response?.data?.error || err?.message || 'Bulk VAT suggestion action failed');
      } finally {
        setProposalBusyKey('');
      }
    },
    [selectedVatSuggestionIds, visibleVatRuleSuggestions]
  );

  const runReapplyRulesUnconfirmed = useCallback(async () => {
    resetOperationCancellation();
    setReapplyUnconfirmedBusy(true);
    setError('');
    const toastId = stackedToastIdRef.current++;
    const settleToast = (
      message: string,
      severity: 'info' | 'success' | 'error' | 'warning',
      withStop: boolean
    ) =>
      setStackedToasts((prev) =>
        prev.map((t) => {
          if (t.id !== toastId) return t;
          if (withStop) {
            return { ...t, message, severity, actionLabel: 'Stop', onAction: () => requestStopOperation() };
          }
          const { actionLabel: _a, onAction: _o, ...rest } = t;
          return { ...rest, message, severity };
        })
      );

    setStackedToasts((prev) => [
      ...prev,
      {
        id: toastId,
        code: nextClientToastCode(),
        severity: 'info',
        message: 'Re-run ALL Classification Rules: starting on unconfirmed transactions…',
        actionLabel: 'Stop',
        onAction: () => requestStopOperation()
      }
    ]);
    reapplyUnconfirmedToastIdRef.current = toastId;

    try {
      const { totalReprocessed, totalDeleted, totalCreated } = await reapplyRulesUnconfirmedBatched(
        (line) => settleToast(line, 'info', true)
      );
      settleToast(
        `Re-run ALL Classification Rules: finished on ${totalReprocessed} unconfirmed transaction(s)` +
          ` (${totalDeleted} rule tag row(s) removed, ${totalCreated} tag row(s) inserted).` +
          ' Confirmed rows and manual tags were left unchanged. Dismiss when you are done.',
        'success',
        false
      );
      await fetchData();
    } catch (err: any) {
      if (err?.code === 'OPERATION_CANCELLED') {
        settleToast(
          'Re-run ALL Classification Rules: stopped by you. Completed batches were kept; run again to continue.',
          'warning',
          false
        );
      } else if (err?.code === 'ECONNABORTED') {
        settleToast(
          'Re-run ALL Classification Rules: a batch timed out — run again to continue in smaller steps.',
          'error',
          false
        );
      } else {
        const raw = String(err?.response?.data?.error || err?.message || 'Could not re-apply rules').trim();
        settleToast(raw || 'Could not re-apply rules.', 'error', false);
      }
    } finally {
      setReapplyUnconfirmedBusy(false);
      resetOperationCancellation();
      reapplyUnconfirmedToastIdRef.current = null;
    }
  }, [fetchData, reapplyRulesUnconfirmedBatched, requestStopOperation, resetOperationCancellation]);

  const startRuleSyncJob = useCallback(
    async (
      payload: Record<string, unknown>,
      startMessage: string,
      fallbackProgress: string,
      syncMode: RuleSyncUiMode = 'full'
    ) => {
      setRerunAllRulesBusy(true);
      setError('');
      setOperationProgressMessage(startMessage);
      try {
        const res = await api.post('/api/transaction-classification-rules/rule-sync-jobs/start', payload);
        const jobId = String(res.data?.job?.job_id || '').trim();
        if (!jobId) {
          throw new Error('Rule sync job was started but no job id was returned.');
        }
        bgJobs.trackJob(jobId, 'rule_sync', syncMode);
        // Top banner keeps detailed server progress; snackbar uses the short friendly copy.
        setOperationProgressMessage(
          String(res.data?.job?.progress_message || fallbackProgress).trim() || fallbackProgress
        );
      } catch (err: any) {
        if (err?.response?.status === 409 && err?.response?.data?.job?.job_id) {
          const jobId = String(err.response.data.job.job_id || '').trim();
          if (jobId) {
            bgJobs.trackJob(jobId, 'rule_sync', syncMode);
            setOperationProgressMessage(
              String(err?.response?.data?.job?.progress_message || fallbackProgress).trim() ||
                fallbackProgress
            );
          } else {
            setError(err?.response?.data?.error || 'A rule sync job is already running.');
            setOperationProgressMessage(null);
            setRerunAllRulesBusy(false);
          }
        } else if (err?.code === 'ECONNABORTED') {
          setError('Request timed out while starting the rule sync job. Please try again.');
          setOperationProgressMessage(null);
          setRerunAllRulesBusy(false);
        } else {
          setError(err?.response?.data?.error || err?.message || 'Could not start rule sync job');
          setOperationProgressMessage(null);
          setRerunAllRulesBusy(false);
        }
      }
    },
    [bgJobs]
  );

  const runReapplyAllRulesUnconfirmed = useCallback(async () => {
    // Manual sync must include txs that already have TxState.rules_ran=true; otherwise every
    // batch finds zero rows and the job completes with "0/0" (see backend
    // `_rule_sync_get_unconfirmed_tx_ids_page(..., include_previously_initialized)`).
    const payload: Record<string, unknown> = { include_previously_initialized: true };
    if (user?.role === 'owner' || user?.role === 'admin') {
      payload.all_users = true;
    }
    await startRuleSyncJob(
      payload,
      'Starting Delete and re-tag (rule pipeline)…',
      'Re-applying Cashflow, Supplier, Category, VAT, and Special-case rules to all uncommitted transactions (includes Non-Complete Revolut states)',
      'full'
    );
  }, [startRuleSyncJob, user?.role]);

  const runAddressMissingTagsProcess = useCallback(async () => {
    const payload: Record<string, unknown> = {
      include_previously_initialized: true,
      address_missing_tags: true
    };
    if (user?.role === 'owner' || user?.role === 'admin') {
      payload.all_users = true;
    }
    await startRuleSyncJob(
      payload,
      'Starting Fill missing tags…',
      'Filling missing tags on uncommitted transactions (Cashflow, Supplier, Category, VAT, Special case)',
      'gaps'
    );
  }, [startRuleSyncJob, user?.role]);

  const runDedupeDisplayDuplicateTags = useCallback(async () => {
    setDedupeDisplayDupTagsBusy(true);
    setError('');
    try {
      const res = await api.post('/api/revolut-transaction-category-tags/dedupe-display-duplicates', {});
      const deleted = Number(res.data?.deleted ?? 0);
      const scanned = Number(res.data?.transactions_scanned ?? 0);
      const skippedCommitted = Number(res.data?.skipped_committed_tx ?? 0);
      setSuccessMessage(
        `De-dup finished: removed ${deleted.toLocaleString()} duplicate tag row(s) across ${scanned.toLocaleString()} uncommitted transaction(s)` +
          (skippedCommitted > 0 ? ` (${skippedCommitted.toLocaleString()} committed TX skipped).` : '.')
      );
      setDedupeDisplayDupTagsDialogOpen(false);
      await fetchData();
    } catch (err: any) {
      setError(err?.response?.data?.error || err?.message || 'De-dup failed');
    } finally {
      setDedupeDisplayDupTagsBusy(false);
    }
  }, [fetchData]);

  const startTagWipeJob = useCallback(
    async (opts?: { chainFullSyncAfter?: boolean }): Promise<boolean> => {
      const chain = Boolean(opts?.chainFullSyncAfter);
      pendingDeleteAndRetagRef.current = chain;
      if (chain) setDeleteAndRetagInFlight(true);
      setRemoveAllTagsBusy(true);
      setError('');
      let pollHandoff = false;
      try {
        const wipeBody: Record<string, unknown> = {
          confirm: WIPE_ALL_TAGS_CONFIRM
        };
        if (user?.role === 'owner' || user?.role === 'admin') {
          wipeBody.all_users = true;
        }
        const res = await api.post('/api/transaction-classification-rules/wipe-all-transaction-tags', wipeBody);
        const jobId = String(res.data?.job?.job_id || '').trim();
        if (res.status === 202 && jobId) {
          bgJobs.trackJob(jobId, 'tag_wipe');
          setOperationProgressMessage(
            chain
              ? String(res.data?.job?.progress_message || 'Delete and re-tag: removing tags…').trim()
              : String(res.data?.job?.progress_message || 'Removing classification tags 0%').trim()
          );
          pollHandoff = true;
          return true;
        }
        // Synchronous wipe path (rare): continue immediately into Full when chaining.
        const msg = String(res.data?.message || 'All matching transaction tags were removed.');
        const deleted = res.data?.deleted;
        if (chain) {
          setOperationProgressMessage('Tags removed — starting rule pipeline…');
          setRemoveAllTagsBusy(false);
          await runReapplyAllRulesUnconfirmed();
          return true;
        }
        const wipeToast = buildTagWipeCompletionToastFromAggregate(msg, deleted);
        await fetchData();
        if (canonicalHybridEnabled) {
          await refreshTransactionReviewCore();
        }
        void fetchPipelineCounts();
        try {
          sessionStorage.setItem(AFTER_TAG_WIPE_COMPLETION_KEY, JSON.stringify(wipeToast).slice(0, 12_000));
        } catch {
          // ignore quota / private mode
        }
        window.location.reload();
        return true;
      } catch (err: any) {
        pendingDeleteAndRetagRef.current = false;
        if (chain) setDeleteAndRetagInFlight(false);
        if (err?.response?.status === 409 && err?.response?.data?.job?.job_id) {
          const jobId = String(err.response.data.job.job_id || '').trim();
          if (jobId) {
            bgJobs.trackJob(jobId, 'tag_wipe');
            setOperationProgressMessage(
              String(
                err?.response?.data?.job?.progress_message ||
                  (chain ? 'Delete and re-tag: removing tags…' : 'Removing classification tags 0%')
              ).trim()
            );
            pollHandoff = true;
            return true;
          }
        }
        setError(
          err?.response?.data?.error ||
            err?.message ||
            (chain ? 'Delete and re-tag failed while removing tags' : 'Remove all tags failed')
        );
        return false;
      } finally {
        if (!pollHandoff) {
          setRemoveAllTagsBusy(false);
        }
      }
    },
    [
      bgJobs,
      fetchData,
      canonicalHybridEnabled,
      refreshTransactionReviewCore,
      fetchPipelineCounts,
      runReapplyAllRulesUnconfirmed,
      user?.role
    ]
  );

  const openDeleteAndRetagDialog = useCallback(async () => {
    setDeleteAndRetagDialogOpen(true);
    setDeleteAndRetagPreviewBusy(true);
    setDeleteAndRetagPreviewError('');
    setDeleteAndRetagPreview(null);
    try {
      const params: Record<string, string | number | boolean> = {};
      if (user?.role === 'owner' || user?.role === 'admin') {
        params.all_users = true;
      }
      const res = await api.get('/api/transaction-classification-rules/delete-and-retag-preview', {
        params
      });
      const rows = Array.isArray(res.data?.transactions) ? res.data.transactions : [];
      setDeleteAndRetagPreview({
        manual_tx_count: Math.max(0, Number(res.data?.manual_tx_count ?? rows.length) || 0),
        transactions: rows
      });
    } catch (err: any) {
      setDeleteAndRetagPreviewError(
        err?.response?.data?.error || err?.message || 'Could not load manual-tag preview'
      );
      setDeleteAndRetagPreview({ manual_tx_count: 0, transactions: [] });
    } finally {
      setDeleteAndRetagPreviewBusy(false);
    }
  }, [api, user?.role]);

  const confirmDeleteAndRetag = useCallback(async () => {
    setDeleteAndRetagDialogOpen(false);
    await startTagWipeJob({ chainFullSyncAfter: true });
  }, [startTagWipeJob]);

  // Mirror detailed server progress into the Tx Review top banner (not the short snackbar copy).
  useEffect(() => {
    if (!bgJobs.isRunning) {
      setRerunAllRulesBusy(false);
      setRemoveAllTagsBusy(false);
      setSupplierProposalSweepBusy(false);
      return;
    }
    if (bgJobs.activeJobKind === 'tag_wipe') {
      setRemoveAllTagsBusy(true);
      setRerunAllRulesBusy(false);
      setSupplierProposalSweepBusy(false);
    } else if (bgJobs.activeJobKind === 'supplier_2b_proposals') {
      setSupplierProposalSweepBusy(true);
      setRerunAllRulesBusy(false);
      setRemoveAllTagsBusy(false);
    } else {
      setRerunAllRulesBusy(true);
      setRemoveAllTagsBusy(false);
      setSupplierProposalSweepBusy(false);
    }
    if (bgJobs.detailedProgressMessage) {
      setOperationProgressMessage(bgJobs.detailedProgressMessage);
    }
  }, [bgJobs.isRunning, bgJobs.activeJobKind, bgJobs.detailedProgressMessage]);

  useEffect(() => {
    const onDone = (ev: Event) => {
      const detail =
        (
          ev as CustomEvent<{
            jobKind?: string;
            reloadTxReview?: boolean;
            notice?: {
              title?: string;
              segments?: string[];
              requestId?: string;
              jobId?: string;
              jobKind?: string;
              severity?: string;
            };
          }>
        ).detail || {};
      setRerunAllRulesBusy(false);
      setRemoveAllTagsBusy(false);
      setSupplierProposalSweepBusy(false);
      setOperationProgressMessage(null);
      if (detail.jobKind === 'rule_sync') {
        setPendingManualRuleSyncCount(0);
        if (deleteAndRetagInFlight) setDeleteAndRetagInFlight(false);
      }

      const notice = detail.notice;
      const severity = String(notice?.severity || 'success').toLowerCase();

      // Delete and re-tag: wipe finished → start Full pipeline (skip wipe-only toast).
      if (
        detail.jobKind === 'tag_wipe' &&
        severity === 'success' &&
        pendingDeleteAndRetagRef.current
      ) {
        pendingDeleteAndRetagRef.current = false;
        try {
          sessionStorage.removeItem(AFTER_TAG_WIPE_COMPLETION_KEY);
          sessionStorage.removeItem(AFTER_TAG_WIPE_RELOAD_MSG_KEY);
        } catch {
          // ignore
        }
        bgJobs.clearCompletionNotice();
        setOperationProgressMessage('Tags removed — starting rule pipeline…');
        void runReapplyAllRulesUnconfirmed();
        return;
      }
      if (detail.jobKind === 'tag_wipe' && severity !== 'success') {
        pendingDeleteAndRetagRef.current = false;
        setDeleteAndRetagInFlight(false);
      }

      const shouldRefreshTable =
        Boolean(detail.reloadTxReview) &&
        (detail.jobKind === 'rule_sync' || detail.jobKind === 'tag_wipe') &&
        severity === 'success';

      if (shouldRefreshTable) {
        // Drop persisted post-reload toast keys — we show the toast here and soft-refresh.
        try {
          sessionStorage.removeItem(AFTER_RULE_SYNC_RELOAD_KEY);
          sessionStorage.removeItem(AFTER_TAG_WIPE_COMPLETION_KEY);
          sessionStorage.removeItem(AFTER_TAG_WIPE_RELOAD_MSG_KEY);
        } catch {
          // ignore
        }
        // Avoid a second global snackbar from BackgroundJobsContext for the same completion.
        bgJobs.clearCompletionNotice();
        if (notice?.title) {
          setSuccessMessageText('');
          setRuleSyncCompletionToast({
            title: notice.title,
            segments: (notice.segments || []).map((s) => String(s || '').trim()).filter(Boolean),
            requestId: notice.requestId,
            jobId: notice.jobId
          });
          setSuccessToastCode(nextClientToastCode());
        }
        void Promise.all([fetchData(), fetchPipelineCounts()]).catch(() => {
          // Completion toast already informs the user; table refresh is best-effort.
        });
      }

      if (detail.jobKind === 'supplier_2b_proposals') {
        // Prefer structured multi-line toast over FriendlyToastMessage bracket parsing.
        if (notice?.title) {
          setSuccessMessageText('');
          setRuleSyncCompletionToast({
            title: notice.title,
            segments: (notice.segments || []).map((s) => String(s || '').trim()).filter(Boolean),
            requestId: notice.requestId,
            jobId: notice.jobId
          });
          setSuccessToastCode(nextClientToastCode());
        }
        void Promise.all([
          api.get('/api/suppliers/description-alias-proposals', { params: { status: 'pending', limit: 2000 } }),
          api
            .get('/api/suppliers/description-alias-proposals', { params: { status: 'rejected', limit: 2000 } })
            .catch(() => ({ data: { proposals: [], count_saved_rejected: 0 } }))
        ])
          .then(([pr, rejectedPr]) => {
            setSupplierProposalsPayload({
              proposals: Array.isArray(pr.data?.proposals) ? pr.data.proposals : [],
              count_saved_pending: pr.data?.count_saved_pending
            });
            setRejectedSupplierProposalsPayload({
              proposals: Array.isArray(rejectedPr.data?.proposals) ? rejectedPr.data.proposals : [],
              count_saved_rejected: Number(rejectedPr.data?.count_saved_rejected || 0)
            });
            setRulesAccordionOpen((prev) => ({ ...prev, supplierProposals: true }));
            setPipelineNestedSection('step2b', 'proposed', true);
          })
          .catch(() => {
            // Completion toast already informs the user; list refresh is best-effort.
          });
      }
    };
    window.addEventListener(BG_JOB_COMPLETED_EVENT, onDone as EventListener);
    return () => window.removeEventListener(BG_JOB_COMPLETED_EVENT, onDone as EventListener);
  }, [api, bgJobs, deleteAndRetagInFlight, fetchData, fetchPipelineCounts, runReapplyAllRulesUnconfirmed]);

  /** After async tag wipe or rule sync we full-reload; restore success UI from sessionStorage. */
  useEffect(() => {
    const looksLikeFailureNotice = (title: string, severity?: string) => {
      const t = String(title || '').toLowerCase();
      const sev = String(severity || '').toLowerCase();
      if (sev === 'error' || sev === 'warning') return true;
      return (
        t.includes('failed') ||
        t.includes('infailedsqltransaction') ||
        t.includes('psycopg') ||
        t.includes('sqlalchemy') ||
        t.includes('[sql:') ||
        t.includes('deadlock')
      );
    };
    try {
      const wipeJson = sessionStorage.getItem(AFTER_TAG_WIPE_COMPLETION_KEY);
      if (wipeJson) {
        try {
          const parsed = JSON.parse(wipeJson) as RuleSyncCompletionToastPayload & { severity?: string };
          if (parsed?.title && Array.isArray(parsed.segments)) {
            if (looksLikeFailureNotice(parsed.title, parsed.severity)) {
              sessionStorage.removeItem(AFTER_TAG_WIPE_COMPLETION_KEY);
            } else {
              /* Keep key until the user dismisses the toast so React 18 Strict Mode remounts can re-read it. */
              setSuccessMessageText('');
              setRuleSyncCompletionToast({
                title: parsed.title,
                segments: parsed.segments.map((s) => String(s || '').trim()).filter(Boolean),
                requestId: String(parsed.requestId || '').trim() || undefined,
                jobId: String(parsed.jobId || '').trim() || undefined
              });
              setSuccessToastCode(nextClientToastCode());
              return;
            }
          }
        } catch {
          sessionStorage.removeItem(AFTER_TAG_WIPE_COMPLETION_KEY);
        }
      }
      const wipe = sessionStorage.getItem(AFTER_TAG_WIPE_RELOAD_MSG_KEY);
      if (wipe) {
        sessionStorage.removeItem(AFTER_TAG_WIPE_RELOAD_MSG_KEY);
        setRuleSyncCompletionToast(null);
        setSuccessMessage(wipe);
        return;
      }
      const raw = sessionStorage.getItem(AFTER_RULE_SYNC_RELOAD_KEY);
      if (raw) {
        sessionStorage.removeItem(AFTER_RULE_SYNC_RELOAD_KEY);
        const parsed = JSON.parse(raw) as RuleSyncCompletionToastPayload;
        if (parsed?.title && Array.isArray(parsed.segments) && parsed.segments.length > 0) {
          setSuccessMessageText('');
          setRuleSyncCompletionToast(parsed);
          setSuccessToastCode(nextClientToastCode());
        }
      }
    } catch {
      // ignore
    }
  }, [setSuccessMessage]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const initial = await fetchData();
      if (cancelled || !initial) return;
      const pipeline = await fetchPipelineCounts();
      if (cancelled) return;
      const counts = pipeline?.counts;
      if (counts) {
        const toastMsg = buildTxReviewHardRefreshToast(counts);
        const needTags = Math.max(0, Number(counts.uncommitted_still_need_tags || 0));
        setPendingManualRuleSyncCount(needTags);
        if (toastMsg) {
          showRefreshInfoToast(toastMsg);
        } else {
          setRefreshInfoMessage('');
          setRefreshInfoToastCode('');
        }
        return;
      }
      // Fallback if pipeline counts fail: legacy untagged queue totals from fetchData.
      const untaggedTotal = initial.counts?.untagged_total ?? 0;
      const unclassifiedTotal = initial.counts?.unclassified_needs_review_total ?? 0;
      const conflictingTotal = initial.counts?.conflicting_vat_total ?? 0;
      const pendingCount = Math.max(0, untaggedTotal + unclassifiedTotal + conflictingTotal);
      setPendingManualRuleSyncCount(pendingCount);
      if (pendingCount > 0) {
        showRefreshInfoToast(
          [
            `Tx Review: ${pendingCount.toLocaleString()} uncommitted TXs still need tags.`,
            'Use Fill missing tags, or Delete and re-tag if rules changed and existing tags may be wrong.'
          ].join('\n')
        );
      } else {
        setRefreshInfoMessage('');
        setRefreshInfoToastCode('');
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [fetchData, fetchPipelineCounts, showRefreshInfoToast]);

  useEffect(() => {
    const counts = pipelineCountsAllScope?.counts;
    if (!counts) return;
    setPendingManualRuleSyncCount(Math.max(0, Number(counts.uncommitted_still_need_tags || 0)));
  }, [pipelineCountsAllScope?.counts]);

  // Refetch when Hide committed / Hide rows with all tags / Hide Non-Complete change.
  // Do not call refresh inside those Switch onChange handlers: setState is async, so an
  // immediate refresh closes over the previous filter values (stale) and the grid can stay
  // at e.g. 4368 after turning Hide Non-Complete off. Initial page load is owned by fetchData.
  const skipCanonicalDefaultFilterEffectRef = useRef(true);
  useEffect(() => {
    if (!canonicalHybridEnabled) return;
    const desiredMode: 'all' | 'only' | 'exclude' =
      unifiedFlaggedFilter === 'flagged'
        ? 'all'
        : unifiedCommittedFilter === 'committed'
          ? 'only'
          : unifiedCommittedFilter === 'all' && hideCommittedUnifiedRows
            ? 'exclude'
            : unifiedCommittedFilter === 'uncommitted'
              ? 'exclude'
              : 'all';
    if (skipCanonicalDefaultFilterEffectRef.current) {
      skipCanonicalDefaultFilterEffectRef.current = false;
      if (desiredMode !== canonicalRowsCommittedMode) {
        setCanonicalRowsCommittedMode(desiredMode);
      }
      // Still fetch when deep-linked/defaulting into Flagged only — initial page load may have
      // used the broader unfiltered scope before URL hydration set unifiedFlaggedFilter.
      if (unifiedFlaggedFilter !== 'flagged') {
        return;
      }
    }

    let cancelled = false;
    (async () => {
      try {
        const payload = await fetchCanonicalRows(desiredMode);
        if (cancelled) return;
        canonicalHydrateGenRef.current += 1;
        const hydrateGen = canonicalHydrateGenRef.current;
        setCanonicalRowsPayload(payload);
        void hydrateCanonicalRowsInBackground(desiredMode, payload, hydrateGen);
        setCanonicalRowsCommittedMode(desiredMode);
      } catch (err: any) {
        if (cancelled) return;
        setError(err?.response?.data?.error || err?.message || 'Failed to refresh TX review rows');
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [
    canonicalHybridEnabled,
    unifiedCommittedFilter,
    unifiedFlaggedFilter,
    hideCommittedUnifiedRows,
    hideConfirmedCompleteUnifiedRows,
    hideDeclinedRevolutTransactions,
    fetchCanonicalRows,
    hydrateCanonicalRowsInBackground
  ]);

  const formatAmount = (amount: number | null, currency: string | null) => {
    if (amount == null) return '—';
    const numeric = Number(amount);
    if (!Number.isFinite(numeric)) return '—';
    const cur = String(currency || 'EUR').trim().toUpperCase() || 'EUR';
    try {
      return new Intl.NumberFormat(undefined, {
        style: 'currency',
        currency: cur,
        maximumFractionDigits: 2
      }).format(numeric);
    } catch {
      const fallback = new Intl.NumberFormat(undefined, {
        maximumFractionDigits: 2
      }).format(numeric);
      return `${fallback} ${cur}`;
    }
  };

  /**
   * UX-only display helper: treat likely card-validation auth checks as £0.00 instead of "—".
   * Keeps underlying row.amount untouched so classification logic remains unchanged.
   */
  const unifiedDisplayAmount = (row: UnifiedReviewRow): number | null => {
    if (row.amount != null && Number.isFinite(Number(row.amount))) return Number(row.amount);
    const tx = (row as any)?.transaction as ReviewTx | undefined;
    if (!tx) return null;
    const txType = String(tx.type || '').trim().toLowerCase();
    const acct = String(row.account_source || tx.account_source || '').trim().toLowerCase();
    if (txType === 'card_payment' && acct === 'uk') {
      return 0;
    }
    return null;
  };

  /**
   * ID / Amount / Cash Flow: size to the widest value in the current filtered set
   * so those columns never ellipsis-truncate.
   */
  const unifiedFitColWidths = useMemo(() => {
    let idPx = estimateUnifiedTextPx('ID') + 18; // header + sort caret
    let amountPx = estimateUnifiedTextPx('Amount') + 18;
    // "Cash"/"Flow" stacked header is narrow; chips drive the width.
    let cashFlowPx = 48;

    for (const row of sortedUnifiedRows) {
      idPx = Math.max(idPx, estimateUnifiedTextPx(String(row.id)) + 8);
      const ledgerLabel = formatAmount(unifiedDisplayAmount(row), row.currency);
      amountPx = Math.max(amountPx, estimateUnifiedTextPx(ledgerLabel) + 10);
      const originalBill = resolveUnifiedRowOriginalBill(row);
      if (originalBill) {
        const originalLabel = formatAmount(originalBill.amount, originalBill.currency);
        amountPx = Math.max(amountPx, estimateUnifiedTextPx(originalLabel) + 10);
      }

      const headingTags = (unifiedTxCategoryTagsByTxId[row.id] || []).filter((t) =>
        isTopLevelCashflowHeadingName(t.category)
      );
      if (headingTags.length > 0) {
        let rowCf = 0;
        headingTags.forEach((tag, idx) => {
          const lab = formatCashflowInOutHeadingLabel(tag.category) || String(tag.category || '');
          rowCf += estimateUnifiedChipPx(lab) + (idx > 0 ? 4 : 0);
        });
        cashFlowPx = Math.max(cashFlowPx, rowCf + 6);
      } else {
        const fallback = formatCashflowInOutHeadingLabel(row.cashFlow) || String(row.cashFlow || '—');
        cashFlowPx = Math.max(cashFlowPx, estimateUnifiedChipPx(fallback) + 6);
      }
    }

    return {
      id: clampUnifiedColPx(idPx, 34, 96),
      amount: clampUnifiedColPx(amountPx, 72, 168),
      cashFlow: clampUnifiedColPx(cashFlowPx, 52, 200)
    };
  }, [sortedUnifiedRows, unifiedTxCategoryTagsByTxId]);

  /** Income (credit) vs expense (debit) for TX Review tables — matches Revolut-style green / red. */
  const txReviewAmountColor = (amount: number | null | undefined): string => {
    if (amount == null || !Number.isFinite(Number(amount))) return 'text.primary';
    const n = Number(amount);
    if (n > 0) return 'success.dark';
    if (n < 0) return 'error.main';
    return 'text.secondary';
  };

  const formatDate = (iso: string | null) => {
    if (!iso) return '—';
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return iso;
    return d.toLocaleString();
  };

  const renderUnifiedRowDetailsStack = (row: UnifiedReviewRow) => {
    const tagChipsForStatusRow = unifiedTxCategoryTagsByTxId[row.id];
    const completenessStatusDisplayed = unifiedRowDisplayedCompletenessStatus(row, tagChipsForStatusRow);
    return (
    <Stack spacing={2} sx={{ pt: 0.5 }}>
      <Box>
        <Typography variant="caption" color="text.secondary">
          Transaction
        </Typography>
        <Stack spacing={0.5} sx={{ mt: 0.5 }}>
          <Typography variant="body2">ID: {row.id}</Typography>
          <Typography
            variant="body2"
            sx={{ wordBreak: 'break-all', fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace' }}
          >
            Revolut ID: {row.revolut_id?.trim() ? row.revolut_id : '—'}
          </Typography>
          <Typography variant="body2">Date: {formatDate(row.date)}</Typography>
          <Typography variant="body2">
            Amount:{' '}
            {(() => {
              const ledgerAmt = unifiedDisplayAmount(row);
              const originalBill = resolveUnifiedRowOriginalBill(row);
              if (originalBill) {
                return (
                  <Box component="span">
                    <Box component="span" sx={{ color: txReviewAmountColor(originalBill.amount) }}>
                      {formatAmount(originalBill.amount, originalBill.currency)}
                    </Box>
                    <Box component="span" sx={{ color: 'text.secondary' }}>
                      {' '}
                      (
                    </Box>
                    <Box component="span" sx={{ color: txReviewAmountColor(ledgerAmt) }}>
                      {formatAmount(ledgerAmt, row.currency)} after FX
                    </Box>
                    <Box component="span" sx={{ color: 'text.secondary' }}>
                      )
                    </Box>
                  </Box>
                );
              }
              return (
                <Box component="span" sx={{ color: txReviewAmountColor(ledgerAmt) }}>
                  {formatAmount(ledgerAmt, row.currency)}
                </Box>
              );
            })()}
          </Typography>
          <Typography variant="body2" sx={{ wordBreak: 'break-word' }}>
            Description: {row.description || '—'}
          </Typography>
          <Typography variant="body2" sx={{ wordBreak: 'break-word' }}>
            Reference: {row.reference || '—'}
          </Typography>
          <Typography variant="body2">Account: {formatRevolutAccountLabel(row.account_source)}</Typography>
        </Stack>
      </Box>
      <Box>
        <Typography variant="caption" color="text.secondary">
          Status
        </Typography>
        <Box sx={{ mt: 0.5 }}>
          <Chip
            size="small"
            label={completenessStatusDisplayed}
            variant="filled"
            color="default"
            sx={unifiedStatusChipSx(completenessStatusDisplayed, { labelNowrap: false })}
          />
        </Box>
      </Box>
      <Box>
        <Typography variant="caption" color="text.secondary">
          Applied tags
        </Typography>
        <Box sx={{ mt: 0.75 }}>
          {(() => {
            const rawTags = unifiedTxCategoryTagsByTxId[row.id];
            const tagsLoaded = unifiedTxCategoryTagsLoadedTxIds.has(row.id);
            if (!tagsLoaded && rawTags == null) {
              return (
                <Typography variant="caption" color="text.secondary">
                  Loading...
                </Typography>
              );
            }
            const tags = (rawTags || []).filter(
              (tag) =>
                tag.source !== 'canonical_supplier' &&
                tag.source !== 'canonical_vat' &&
                !String(tag.uid || '').startsWith('cvt:') &&
                !isStandaloneIncomeExpenseAppliedTag(tag)
            );
            if (!tags.length) {
              return (
                <Typography variant="caption" color="text.secondary">
                  {tagsLoaded ? '—' : '…'}
                </Typography>
              );
            }
            return (
              <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
                {tags.map((tag) => {
                  const label = formatUnifiedAppliedTagChipLabel(tag);
                  const tip =
                    `${label} | ${tag.source === 'manual' ? 'Manual' : 'Rule'}` +
                    (tag.supplier ? ` | Supplier: ${tag.supplier}` : '');
                  return (
                    <Tooltip key={`qv-applied-${row.id}-${tag.uid}`} title={tip}>
                      <Chip
                        size="small"
                        variant="outlined"
                        label={label}
                        onDelete={() => requestDeleteUnifiedTag(row.id, tag, 'applied')}
                        deleteIcon={<CloseIcon fontSize="small" />}
                        disabled={loading || unifiedActionBusy || deletingUnifiedTagUid === tag.uid}
                      />
                    </Tooltip>
                  );
                })}
              </Stack>
            );
          })()}
        </Box>
      </Box>
      <Box>
        <Typography variant="caption" color="text.secondary">
          Tag source
        </Typography>
        <Box sx={{ mt: 0.5 }}>
          <Chip
            size="small"
            label={row.tagOrigin}
            color={
              row.tagOrigin === 'Auto'
                ? 'info'
                : row.tagOrigin === 'Manual'
                  ? 'secondary'
                  : row.tagOrigin === 'Hybrid'
                    ? 'warning'
                    : 'default'
            }
            variant="outlined"
          />
        </Box>
      </Box>
      <Box>
        <Typography variant="caption" color="text.secondary">
          Committed
        </Typography>
        <Box sx={{ mt: 0.5 }}>
          <Chip
            size="small"
            label={unifiedCommittedLabel(row)}
            color={isUnifiedRowCommitted(row) ? 'success' : 'default'}
            variant={isUnifiedRowCommitted(row) ? 'filled' : 'outlined'}
          />
        </Box>
      </Box>
    </Stack>
    );
  };

  const ruleRunHistoryRows = useMemo(
    () => ((ruleRunHistory?.rows || []) as RuleRunHistoryRow[]),
    [ruleRunHistory?.rows]
  );
  const ruleRunHistoryBuckets = useMemo(
    () => ((ruleRunHistory?.histogram || []) as RuleRunHistoryBucket[]),
    [ruleRunHistory?.histogram]
  );
  const ruleRunHistoryBucketsAsc = useMemo(
    () =>
      [...ruleRunHistoryBuckets].sort(
        (a, b) =>
          new Date(a.bucket_start || 0).getTime() - new Date(b.bucket_start || 0).getTime()
      ),
    [ruleRunHistoryBuckets]
  );
  const ruleRunHistogramChartRows = useMemo(
    () =>
      ruleRunHistoryBucketsAsc.map((b) => ({
        bucketLabel: formatDate(b.bucket_start),
        count: Number(b.count || 0),
        bucket_start: b.bucket_start,
        bucket_end: b.bucket_end,
        tx_ids: Array.isArray(b.tx_ids) ? b.tx_ids : [],
      })),
    [ruleRunHistoryBucketsAsc]
  );
  const openTxReviewForIds = useCallback(
    (txIds: number[]) => {
      const ids = Array.from(new Set((txIds || []).map((id) => Number(id)).filter((id) => Number.isFinite(id) && id > 0)));
      if (ids.length === 0) return;
      const next = new URLSearchParams(searchParams);
      next.set('tab', 'needs-review');
      next.set(TX_REVIEW_URL_KEYS.ids, ids.join(','));
      // Keep this drill-down focused on exactly those transactions.
      [
        TX_REVIEW_URL_KEYS.status,
        TX_REVIEW_URL_KEYS.supplier,
        TX_REVIEW_URL_KEYS.text,
        TX_REVIEW_URL_KEYS.amountMin,
        TX_REVIEW_URL_KEYS.amountMax,
        TX_REVIEW_URL_KEYS.dateFrom,
        TX_REVIEW_URL_KEYS.dateTo,
        TX_REVIEW_URL_KEYS.frequency,
        TX_REVIEW_URL_KEYS.currency,
        TX_REVIEW_URL_KEYS.heading,
        TX_REVIEW_URL_KEYS.category,
        TX_REVIEW_URL_KEYS.vat,
        TX_REVIEW_URL_KEYS.unclassifiedNeedsReview,
        TX_REVIEW_URL_KEYS.account,
      ].forEach((key) => next.delete(key));
      next.delete(TX_REVIEW_URL_KEYS.committed);
      next.set(TX_REVIEW_URL_KEYS.hideCommitted, '0');
      next.set(TX_REVIEW_URL_KEYS.rowsPerPage, '200');
      Object.values(RULES_URL_KEYS).forEach((key) => next.delete(key));
      stripRulesQuickviewSearchParams(next);
      setRulesTextFilter('');
      setRulesIdFilter('');
      setRulesProposalIdsFilter('');
      setRulesSupplierFilter('all');
      setRulesCategoryFilter('all');
      setRulesFrequencyFilter('all');
      setRulesStatusFilter('all');
      setSearchParams(next, { replace: true });
    },
    [
      searchParams,
      setSearchParams,
      setRulesTextFilter,
      setRulesIdFilter,
      setRulesProposalIdsFilter,
      setRulesSupplierFilter,
      setRulesCategoryFilter,
      setRulesFrequencyFilter,
      setRulesStatusFilter
    ]
  );

  const presentRulesApplyRunInQuickview = useCallback(
    (payload: { ruleTitle: string; summaryLine: string; summary: unknown }) => {
      const parsed = parseApplySingleAffectedTxIds(payload.summary);
      setRulesApplyRunSnapshot({
        ruleTitle: payload.ruleTitle,
        summaryLine: payload.summaryLine,
        affectedTxIds: parsed.ids,
        idsTruncated: parsed.truncated,
        idsTotal: parsed.total
      });
      setRulesQuickviewOpen(true);
      setRulesQuickviewMainTab('other');
    },
    []
  );

  const openTxReviewFromPipeline = useCallback(
    (opts: {
      committed?: 'all' | 'committed' | 'uncommitted';
      statuses?: string[];
      appliedTags?: string[];
      amountMin?: string;
      amountMax?: string;
      /** Normalised VAT keys (e.g. ``none``) — same as TX Reviews VAT filter chips. */
      vat?: string[];
      /** Restrict to unclassified-needs-review rows (canonical flag or legacy queue shape). */
      unclassifiedNeedsReviewOnly?: boolean;
      headings?: Array<'In' | 'Out' | 'N/A'>;
      suppliers?: string[];
      categories?: string[];
    }) => {
      const next = new URLSearchParams(searchParams);
      next.set('tab', 'needs-review');
      Object.values(TX_REVIEW_URL_KEYS).forEach((key) => next.delete(key));

      const committed = opts.committed || 'all';
      if (committed !== 'all') {
        next.set(TX_REVIEW_URL_KEYS.committed, committed);
      }
      const statuses = Array.from(new Set((opts.statuses || []).map((s) => String(s || '').trim()).filter(Boolean)));
      if (statuses.length > 0) {
        writeSearchParamList(next, TX_REVIEW_URL_KEYS.status, statuses);
      }
      const headingsFiltered = sanitizeHeadingFilterValues(opts.headings || []);
      if (headingsFiltered.length > 0) {
        writeSearchParamList(next, TX_REVIEW_URL_KEYS.heading, headingsFiltered);
      }
      const suppliers = Array.from(new Set((opts.suppliers || []).map((s) => String(s || '').trim()).filter(Boolean)));
      if (suppliers.length > 0) {
        writeSearchParamList(next, TX_REVIEW_URL_KEYS.supplier, suppliers);
      }
      const categories = Array.from(new Set((opts.categories || []).map((s) => String(s || '').trim()).filter(Boolean)));
      if (categories.length > 0) {
        writeSearchParamList(next, TX_REVIEW_URL_KEYS.category, categories);
      }
      const appliedTags = Array.from(
        new Set((opts.appliedTags || []).map((s) => String(s || '').trim()).filter(Boolean))
      );
      if (appliedTags.length > 0) {
        writeSearchParamList(next, TX_REVIEW_URL_KEYS.appliedTag, appliedTags);
      }
      const amountMin = String(opts.amountMin ?? '').trim();
      if (amountMin) {
        next.set(TX_REVIEW_URL_KEYS.amountMin, amountMin);
      }
      const amountMax = String(opts.amountMax ?? '').trim();
      if (amountMax) {
        next.set(TX_REVIEW_URL_KEYS.amountMax, amountMax);
      }
      const vatKeys = Array.from(new Set((opts.vat || []).map((s) => String(s || '').trim()).filter(Boolean)));
      if (vatKeys.length > 0) {
        writeSearchParamList(next, TX_REVIEW_URL_KEYS.vat, vatKeys);
      }
      if (opts.unclassifiedNeedsReviewOnly) {
        next.set(TX_REVIEW_URL_KEYS.unclassifiedNeedsReview, '1');
      }
      next.set(TX_REVIEW_URL_KEYS.hideCommitted, '0');
      next.set(TX_REVIEW_URL_KEYS.hideDeclined, '0');
      next.set(TX_REVIEW_URL_KEYS.rowsPerPage, '200');
      setSearchParams(next, { replace: true });
    },
    [searchParams, setSearchParams]
  );

  const defaultMatchWindowAround = useCallback((anchorIso: string | null | undefined) => {
    if (!anchorIso) return { anchor: '', from: '', to: '' };
    const anchorDate = new Date(anchorIso);
    if (Number.isNaN(anchorDate.getTime())) return { anchor: '', from: '', to: '' };
    const fromDate = new Date(anchorDate);
    const toDate = new Date(anchorDate);
    fromDate.setFullYear(fromDate.getFullYear() - 1);
    toDate.setFullYear(toDate.getFullYear() + 1);
    return {
      anchor: anchorDate.toISOString().slice(0, 10),
      from: fromDate.toISOString().slice(0, 10),
      to: toDate.toISOString().slice(0, 10)
    };
  }, []);

  const patchPaymentFrequency = useCallback(
    async (txId: number, value: string | null, opts?: { skipRefetch?: boolean }) => {
      try {
        setSavingFrequencyTxId(txId);
        await api.patch(`/api/revolut/transactions/complete/${txId}/payment-frequency`, {
          payment_frequency: value
        });
        if (!opts?.skipRefetch) {
          await fetchData();
        }
      } catch (err: any) {
        setError(err?.response?.data?.error || 'Failed to update frequency');
      } finally {
        setSavingFrequencyTxId(null);
      }
    },
    [fetchData]
  );

  const confirmClassificationIds = async (
    txIds: number[]
  ): Promise<{ confirmedIds: number[]; errors: any[] }> => {
    const unique = Array.from(new Set(txIds.filter((n) => Number.isFinite(n))));
    if (!unique.length) return { confirmedIds: [], errors: [] };
    const res = await api.post('/api/transaction-classification-rules/confirm-classification', {
      revolut_transaction_ids: unique
    });
    const errs = Array.isArray(res.data?.errors) ? res.data.errors : [];
    const confirmedIds = (res.data?.confirmed_ids || [])
      .map((x: unknown) => Number(x))
      .filter((n: number) => Number.isFinite(n) && n > 0);
    const committed = confirmedIds.length;
    if (errs.length > 0) {
      const detail = errs
        .slice(0, 8)
        .map((e: any) => {
          const id = e?.id != null ? `#${e.id}` : '';
          const reason = e?.reason;
          if (reason === 'incomplete_tags' && e?.status) {
            return `TX${id}: incomplete tags (${e.status})`;
          }
          if (reason === 'vat_conflict') return `TX${id}: VAT conflict`;
          if (reason === 'needs_category_tag') return `TX${id}: needs at least one category tag`;
          if (reason === 'not_found') return `TX${id}: not found`;
          if (reason === 'forbidden') return `TX${id}: forbidden`;
          return `TX${id}: ${reason || 'unknown'}`;
        })
        .join('; ');
      const more = errs.length > 8 ? ` (+${errs.length - 8} more)` : '';
      setError(
        `${errs.length} row(s) could not be committed; ${committed} committed. ${detail}${more}`
      );
    } else if (committed > 0) {
      setSuccessMessage(`Committed ${committed} transaction(s).`);
    }
    return { confirmedIds, errors: errs };
  };

  const uncommitClassificationIds = async (
    txIds: number[]
  ): Promise<{ uncommittedIds: number[]; errors: any[] }> => {
    const unique = Array.from(new Set(txIds.filter((n) => Number.isFinite(n))));
    if (!unique.length) return { uncommittedIds: [], errors: [] };
    const res = await api.post('/api/transaction-classification-rules/uncommit-classification', {
      revolut_transaction_ids: unique
    });
    const errs = Array.isArray(res.data?.errors) ? res.data.errors : [];
    const uncommittedIds = (res.data?.uncommitted_ids || [])
      .map((x: unknown) => Number(x))
      .filter((n: number) => Number.isFinite(n) && n > 0);
    const done = uncommittedIds.length;
    if (errs.length > 0) {
      const detail = errs
        .slice(0, 8)
        .map((e: any) => {
          const id = e?.id != null ? `#${e.id}` : '';
          const reason = e?.reason;
          if (reason === 'not_found') return `TX${id}: not found`;
          if (reason === 'forbidden') return `TX${id}: forbidden`;
          return `TX${id}: ${reason || 'unknown'}`;
        })
        .join('; ');
      const more = errs.length > 8 ? ` (+${errs.length - 8} more)` : '';
      setError(`${errs.length} row(s) could not be uncommitted; ${done} uncommitted. ${detail}${more}`);
    } else if (done > 0) {
      setSuccessMessage(`Uncommitted ${done} transaction(s).`);
    }
    return { uncommittedIds, errors: errs };
  };

  const dismissTxReviewBulkToast = useCallback((toastId: number | null) => {
    if (toastId == null) return;
    setStackedToasts((prev) => prev.filter((x) => x.id !== toastId));
  }, []);

  /** Long-running rule ops: info toast without Cancel (unlike bulk TX actions). */
  const beginRuleOperationInfoToast = useCallback((message: string): number => {
    const id = stackedToastIdRef.current++;
    setStackedToasts((prev) => [
      ...prev,
      { id, code: nextClientToastCode(), severity: 'info' as const, message }
    ]);
    return id;
  }, []);

  const beginTxReviewBulkActionWithToast = useCallback((message: string): number => {
    txReviewBulkCancelRequestedRef.current = false;
    const id = stackedToastIdRef.current++;
    setStackedToasts((prev) => [
      ...prev,
      {
        id,
        code: nextClientToastCode(),
        severity: 'info',
        message,
        actionLabel: 'Cancel',
        onAction: () => {
          txReviewBulkCancelRequestedRef.current = true;
        }
      }
    ]);
    return id;
  }, []);

  const resolveVatConflictIds = async (txIds: number[], categoryId: number, vatRate?: string | null) => {
    const unique = Array.from(new Set(txIds.filter((n) => Number.isFinite(n))));
    if (!unique.length) return;
    const res = await api.post('/api/transaction-classification-rules/resolve-vat-conflicts', {
      revolut_transaction_ids: unique,
      category_id: categoryId,
      vat_rate: (vatRate || '').trim() || undefined
    });
    const errs = res.data?.errors || [];
    const resolved = (res.data?.resolved_ids || []).length;
    if (errs.length > 0) {
      setError(`${errs.length} row(s) could not be resolved. Resolved ${resolved} row(s).`);
      } else {
      setSuccessMessage(`Resolved VAT conflicts for ${resolved} transaction(s).`);
    }
  };

  /**
   * Manual-review `conflicting_vat` is capped (e.g. 500 newest) and can omit rows that still show
   * `VAT Conflict` on the canonical TX Reviews table. Merge in canonical rows with
   * `flags.has_vat_conflict` so the VAT Conflicts tab stays aligned with the grid.
   */
  const vatConflictRowsEffective = useMemo((): ReviewTx[] => {
    const manual = payload?.conflicting_vat || [];
    if (!canonicalHybridEnabled) {
      return manual;
    }
    const byId = new Map<number, ReviewTx>();
    manual.forEach((tx) => byId.set(tx.id, tx));
    for (const row of canonicalRowsPayload?.rows || []) {
      if (!row.flags?.has_vat_conflict) continue;
      const id = Number(row.id);
      if (!Number.isFinite(id) || id <= 0 || byId.has(id)) continue;
      const tx = row.transaction as ReviewTx | undefined;
      if (!tx || !Number.isFinite(Number(tx.id))) continue;
      const raw = row.vat_conflict_detail as
        | (ReviewTx['vat_conflict_detail'] & { distinct_vat_rates?: string[] })
        | null
        | undefined;
      let detail: ReviewTx['vat_conflict_detail'] | null = raw || null;
      if (detail && !Array.isArray(detail.vat_rates) && Array.isArray(raw?.distinct_vat_rates)) {
        detail = {
          vat_rates: raw!.distinct_vat_rates,
          categories: detail.categories || [],
          entries: detail.entries || []
        };
      }
      byId.set(id, { ...tx, vat_conflict_detail: detail });
    }
    return Array.from(byId.values());
  }, [canonicalHybridEnabled, payload?.conflicting_vat, canonicalRowsPayload?.rows]);

  const conflictingTxById = useMemo(() => {
    const m = new Map<number, ReviewTx>();
    vatConflictRowsEffective.forEach((tx) => {
      m.set(tx.id, tx);
    });
    return m;
  }, [vatConflictRowsEffective]);

  const vatConflictIdSet = useMemo(
    () => new Set(vatConflictRowsEffective.map((tx) => tx.id)),
    [vatConflictRowsEffective]
  );
  const vatConflictSelectedCount = useMemo(
    () => selectedTxIds.filter((id) => vatConflictIdSet.has(id)).length,
    [selectedTxIds, vatConflictIdSet]
  );

  const filteredVatConflictRows = useMemo(() => {
    const rows = vatConflictRowsEffective;
    const needle = (
      vatConflictSearch.trim() ||
      (canonicalHybridEnabled ? unifiedTextFilter.trim() : '')
    ).toLowerCase();
    const minT = vatConflictAmtMin.trim();
    const maxT = vatConflictAmtMax.trim();
    const minN = parseFloat(minT);
    const maxN = parseFloat(maxT);
    const hasMin = minT !== '' && Number.isFinite(minN);
    const hasMax = maxT !== '' && Number.isFinite(maxN);
    return rows.filter((tx) => {
      if (vatConflictAccount === 'ireland') {
        const s = (tx.account_source || '').toLowerCase();
        if (!(s.includes('ireland') || s.includes('ire'))) return false;
      } else if (vatConflictAccount === 'uk') {
        const s = (tx.account_source || '').toLowerCase();
        if (!s.includes('uk')) return false;
      }
      if (hasMin || hasMax) {
        const a = effectiveTxAmount(tx);
        if (a == null) return false;
        if (hasMin && a < minN) return false;
        if (hasMax && a > maxN) return false;
      }
      if (needle) {
        const parts: string[] = [
          tx.supplier_name || '',
          tx.description || '',
          tx.reference || '',
          tx.account_source || ''
        ];
        const d = tx.vat_conflict_detail;
        if (d) {
          (d.vat_rates || []).forEach((x) => parts.push(String(x)));
          (d.categories || []).forEach((x) => parts.push(String(x)));
          (d.entries || []).forEach((e) => {
            parts.push(e.category, e.vat_rate, e.rule_name || '', e.source || '');
          });
        }
        const hay = parts.join(' ').toLowerCase();
        if (!hay.includes(needle)) return false;
      }
      return true;
    });
  }, [
    vatConflictRowsEffective,
    vatConflictSearch,
    unifiedTextFilter,
    canonicalHybridEnabled,
    vatConflictAccount,
    vatConflictAmtMin,
    vatConflictAmtMax
  ]);

  const vatConflictFiltersActive = useMemo(
    () =>
      vatConflictSearch.trim() !== '' ||
      (canonicalHybridEnabled && unifiedTextFilter.trim() !== '') ||
      vatConflictAccount !== 'all' ||
      vatConflictAmtMin.trim() !== '' ||
      vatConflictAmtMax.trim() !== '',
    [vatConflictSearch, unifiedTextFilter, canonicalHybridEnabled, vatConflictAccount, vatConflictAmtMin, vatConflictAmtMax]
  );

  const resetVatConflictFilters = useCallback(() => {
    setVatConflictSearch('');
    setVatConflictAccount('all');
    setVatConflictAmtMin('');
    setVatConflictAmtMax('');
  }, []);

  const vatConflictFilterPills: Array<{ key: string; label: string; onDelete: () => void }> = [];
  if (vatConflictSearch.trim()) {
    vatConflictFilterPills.push({
      key: 'search',
      label: `Search: ${vatConflictSearch.trim()}`,
      onDelete: () => setVatConflictSearch('')
    });
  }
  if (vatConflictAccount !== 'all') {
    vatConflictFilterPills.push({
      key: 'account',
      label: `Account: ${vatConflictAccount === 'ireland' ? 'Revolut IRE' : 'Revolut UK'}`,
      onDelete: () => setVatConflictAccount('all')
    });
  }
  if (vatConflictAmtMin.trim()) {
    vatConflictFilterPills.push({
      key: 'min-amount',
      label: `Min amount: ${vatConflictAmtMin.trim()}`,
      onDelete: () => setVatConflictAmtMin('')
    });
  }
  if (vatConflictAmtMax.trim()) {
    vatConflictFilterPills.push({
      key: 'max-amount',
      label: `Max amount: ${vatConflictAmtMax.trim()}`,
      onDelete: () => setVatConflictAmtMax('')
    });
  }

  const supplierVatRowsFiltered = useMemo(() => {
    const needle = supplierVatSearch.trim().toLowerCase();
    if (!needle) return supplierVatRows;
    return supplierVatRows.filter((row) => {
      const hay = [
        row.name,
        row.lookbackInput,
        row.lastRetrievedInput,
        row.retrieval_warning || ''
      ]
        .join(' ')
        .toLowerCase();
      return hay.includes(needle);
    });
  }, [supplierVatRows, supplierVatSearch]);

  const supplierVatHasFilters = supplierVatSearch.trim() !== '';
  const resetSupplierVatFilters = useCallback(() => {
    setSupplierVatSearch('');
  }, []);
  const supplierVatFilterPills: Array<{ key: string; label: string; onDelete: () => void }> = [];
  if (supplierVatSearch.trim()) {
    supplierVatFilterPills.push({
      key: 'search',
      label: `Search: ${supplierVatSearch.trim()}`,
      onDelete: () => setSupplierVatSearch('')
    });
  }

  const suppliersAvailableForVatAdd = useMemo(() => {
    const inTable = new Set(supplierVatRows.map((r) => r.supplier_id));
    return activeSuppliersPickerList.filter((s) => !inTable.has(s.id));
  }, [activeSuppliersPickerList, supplierVatRows]);

  const visibleSupplierVatRowIds = useMemo(
    () => supplierVatRowsFiltered.map((r) => r.supplier_id),
    [supplierVatRowsFiltered]
  );

  const supplierVatSelectAllCheckboxState = useMemo(() => {
    const visible = visibleSupplierVatRowIds;
    if (visible.length === 0) return { checked: false, indeterminate: false };
    const n = visible.filter((id) => selectedSupplierVatIds.includes(id)).length;
    return { checked: n === visible.length, indeterminate: n > 0 && n < visible.length };
  }, [visibleSupplierVatRowIds, selectedSupplierVatIds]);

  const shouldProposeVatOverrideRule = useCallback(
    (_categoryId: number, chosenVatRate: string | null | undefined): boolean => {
      // Hard-off: no implicit proposal creation from VAT actions.
      // Proposals must be generated only via explicit user-triggered Generate actions.
      void _categoryId;
      void chosenVatRate;
      return false;
    },
    []
  );

  const createVatOverrideRuleProposals = useCallback(
    async (txIds: number[], categoryId: number, chosenVatRate: string): Promise<number> => {
      const vat = (chosenVatRate || '').trim();
      if (!vat) return 0;
      const categoryName =
        categories.find((c) => c.id === categoryId)?.name || `Category #${categoryId}`;
      let created = 0;
      for (const txId of txIds) {
        const tx = conflictingTxById.get(txId);
        if (!tx) continue;
        const supplier = (tx.supplier_name || '').trim();
        const description = (tx.description || tx.reference || '').trim();
        const supplierMatch = supplier || undefined;
        const descriptionMatch = !supplierMatch && description ? description.slice(0, 180) : undefined;
        if (!supplierMatch && !descriptionMatch) continue;
        try {
          await api.post('/api/transaction-classification-rules/rule-proposals', {
            name: `VAT override - ${categoryName} ${vat} (tx ${tx.id})`,
            category_id: categoryId,
            supplier_contains: supplierMatch,
            description_contains: descriptionMatch,
            revolut_transaction_id: tx.id,
            priority: 20,
            vat_rate: vat,
            notes: `Created from VAT conflicts tab after overriding category VAT to ${vat}.`
          });
          created += 1;
        } catch {
          // Non-blocking: keep VAT resolution applied even if proposal creation fails for one tx.
        }
      }
      if (created > 0) {
        const pr = await api.get('/api/transaction-classification-rules/rule-proposals');
        const praw = pr.data as RuleProposalsPayload | undefined;
        setProposalsPayload({
          proposals: praw?.proposals ?? [],
          count_atlas_virtual: praw?.count_atlas_virtual,
          count_saved_pending: praw?.count_saved_pending,
          count_saved_rejected: Number(praw?.count_saved_rejected || 0),
        });
      }
      return created;
    },
    [categories, conflictingTxById]
  );

  const removeVatConflictTag = async (
    tagUid: string,
    tx: ReviewTx,
    entry?: { category?: string; vat_rate?: string; source?: string; rule_name?: string | null; tag_uid?: string }
  ) => {
    const uid = (tagUid || '').trim();
    if (!uid) return;
    try {
      setDeletingVatTagUid(uid);
      setError('');
      try {
        await api.delete(`/api/revolut-transaction-category-tags/${encodeURIComponent(uid)}`);
      } catch (primaryErr: any) {
        // Legacy rows can carry stale tag_uid values in vat_conflict_detail. Best effort:
        // refetch live tx tags and delete matching candidates by category/VAT/source fingerprint.
        const txId = Number(tx.id);
        const params: Record<string, string | number> = { revolut_transaction_id: txId };
        if ((user?.role === 'admin' || user?.role === 'owner') && tx.user_id != null) {
          params.user_id = Number(tx.user_id);
        }
        const tagRes = await api.get('/api/revolut-transaction-category-tags/', { params });
        const liveTags = Array.isArray(tagRes.data?.tags) ? tagRes.data.tags : [];
        const wantedCategory = String(entry?.category || '').trim().toLowerCase();
        const wantedVat = (String(entry?.vat_rate || '').trim() || 'none').toLowerCase();
        const wantedSource = String(entry?.source || '').trim().toLowerCase();
        const fallbackCandidates = liveTags.filter((t: any) => {
          const cat = String(t?.category?.name || '').trim().toLowerCase();
          const vat = (String(t?.vat_rate || '').trim() || 'none').toLowerCase();
          const src = String(t?.source || '').trim().toLowerCase();
          if (wantedCategory && cat !== wantedCategory) return false;
          if (wantedVat && vat !== wantedVat) return false;
          if (wantedSource && src && src !== wantedSource) return false;
          return true;
        });
        if (!fallbackCandidates.length) {
          throw primaryErr;
        }
        // Delete the first candidate deterministically (oldest uid sort).
        fallbackCandidates.sort((a: any, b: any) =>
          String(a?.uid || '').localeCompare(String(b?.uid || ''), undefined, { sensitivity: 'base' })
        );
        const fallbackUid = String(fallbackCandidates[0]?.uid || '').trim();
        if (!fallbackUid) {
          throw primaryErr;
        }
        await api.delete(`/api/revolut-transaction-category-tags/${encodeURIComponent(fallbackUid)}`);
      }
      const targetTxId = Number(tx.id);
      if (Number.isFinite(targetTxId) && targetTxId > 0) {
        setPayload((prev) => {
          if (!prev) return prev;
          const nextConflicts = (prev.conflicting_vat || []).map((rowTx) => {
            if (rowTx.id !== targetTxId || !rowTx.vat_conflict_detail) return rowTx;
            const wantedCategory = String(entry?.category || '').trim().toLowerCase();
            const wantedVat = (String(entry?.vat_rate || '').trim() || 'none').toLowerCase();
            const wantedSource = String(entry?.source || '').trim().toLowerCase();
            let removedEquivalent = false;
            const nextEntries = (rowTx.vat_conflict_detail.entries || []).filter((e) => {
              const isUidMatch = String(e.tag_uid || '').trim() === uid;
              if (isUidMatch) return false;
              // If uid was stale and fallback delete was used, also drop one equivalent entry from local view.
              const cat = String(e.category || '').trim().toLowerCase();
              const vat = (String(e.vat_rate || '').trim() || 'none').toLowerCase();
              const src = String(e.source || '').trim().toLowerCase();
              const isEquivalent =
                (!wantedCategory || cat === wantedCategory) &&
                (!wantedVat || vat === wantedVat) &&
                (!wantedSource || src === wantedSource);
              if (isEquivalent && !removedEquivalent) {
                removedEquivalent = true;
                return false;
              }
              return true;
            });
            const nextVatRates = Array.from(
              new Set(nextEntries.map((e) => ((e.vat_rate || 'none').trim() || 'none')))
            );
            const nextCategories = Array.from(
              new Set(nextEntries.map((e) => (e.category || '').trim()).filter(Boolean))
            );
            return {
              ...rowTx,
              vat_conflict_detail: {
                ...rowTx.vat_conflict_detail,
                entries: nextEntries,
                vat_rates: nextVatRates,
                categories: nextCategories
              }
            };
          });
          return { ...prev, conflicting_vat: nextConflicts };
        });
      }
      setSuccessMessage('Tag deleted. If conflict remains, pick a category/VAT and click Apply VAT.');
    } catch (err: any) {
      setError(err?.response?.data?.error || err?.message || 'Failed to delete VAT tag');
    } finally {
      setDeletingVatTagUid(null);
    }
  };

  const requestDeleteUnifiedTag = useCallback(
    (txId: number, tag: UnifiedTxCategoryTagChip, promptKind: UnifiedTagDeletePromptKind = 'applied') => {
      const uid = String(tag.uid || '').trim();
      if (!uid) return;
      const isCanonicalVat = uid.startsWith('cvt:') || tag.source === 'canonical_vat';
      const clearVatOnly =
        promptKind === 'vat' && !isCanonicalVat && !uid.startsWith('cst:');
      setPendingUnifiedTagDelete({
        txId,
        tagUid: uid,
        label: unifiedTagDeletePromptLabel(tag, promptKind),
        clearVatOnly
      });
    },
    []
  );

  /**
   * Append a manual category tag to a TX immediately. Includes any VAT/supplier drafts
   * already chosen for the row so Save is not required just to attach those dimensions.
   */
  const addManualCategoryTagToRow = useCallback(
    async (txId: number, categoryId: number, categoryName: string) => {
      if (!Number.isFinite(categoryId) || categoryId <= 0) return;
      setUnifiedTxTagSyncBusyByTxId((prev) => ({ ...prev, [txId]: true }));
      try {
        setError('');
        const vatDraft =
          txId in selectedVatRateByTx
            ? canonicalizeVatRate(String(selectedVatRateByTx[txId] || '').trim()) || undefined
            : undefined;
        const supplierDraft =
          txId in selectedSupplierByTx
            ? String(selectedSupplierByTx[txId] || '').trim() || undefined
            : undefined;
        await postManualRevolutCategoryTagIgnoreDuplicate({
          revolut_transaction_id: txId,
          category_id: categoryId,
          source: 'manual',
          ...(vatDraft ? { vat_rate: vatDraft } : {}),
          ...(supplierDraft ? { supplier_name: supplierDraft } : {})
        });
        // Mirror to draft so any later Save / classify path can stamp updates.
        setSelectedGranularByTx((prev) => {
          const cur = prev[txId] || [];
          if (cur.includes(categoryId)) return prev;
          return { ...prev, [txId]: [...cur, categoryId] };
        });
        // Refresh this row's category tags so the new chip appears.
        try {
          const tagsRes = await api.get('/api/revolut-transaction-category-tags/', {
            params: { transaction_ids: String(txId), _t: String(Date.now()) }
          });
          const tagRows = Array.isArray(tagsRes.data?.tags) ? tagsRes.data.tags : [];
          const slice = buildUnifiedTxCategoryTagChipsByTxId(tagRows, [txId]);
          applyUnifiedTxCategoryTagsSlice(slice);
        } catch {
          // Non-blocking.
        }
        if (canonicalHybridEnabled) {
          try {
            await refreshCanonicalRowsForTransactionIds(0, [txId]);
          } catch {
            // Non-blocking.
          }
        }
        setSuccessMessage(
          vatDraft
            ? `Added category "${categoryName}" (@ ${vatDraft}) to TX ${txId}.`
            : `Added category "${categoryName}" to TX ${txId}.`
        );
      } catch (err: any) {
        setError(err?.response?.data?.error || err?.message || 'Failed to add category tag');
      } finally {
        setUnifiedTxTagSyncBusyByTxId((prev) => {
          const next = { ...prev };
          delete next[txId];
          return next;
        });
      }
    },
    [
      canonicalHybridEnabled,
      refreshCanonicalRowsForTransactionIds,
      selectedVatRateByTx,
      selectedSupplierByTx
    ]
  );

  /**
   * Persist a VAT draft as the sole active canonical ``tx_vat_tags`` rate.
   * Does not require category chips; Save is not required for VAT alone.
   */
  const applyManualVatRateToRow = useCallback(
    async (txId: number, vatRateRaw: string) => {
      const vatRate = canonicalizeVatRate(String(vatRateRaw || '').trim()) || '';
      setSelectedVatRateByTx((prev) => ({ ...prev, [txId]: vatRate }));
      if (!vatRate) {
        return;
      }
      setUnifiedTxTagSyncBusyByTxId((prev) => ({ ...prev, [txId]: true }));
      try {
        setError('');
        await setManualTxVatRate(txId, vatRate);
        try {
          const tagsRes = await api.get('/api/revolut-transaction-category-tags/', {
            params: { transaction_ids: String(txId), _t: String(Date.now()) }
          });
          const tagRows = Array.isArray(tagsRes.data?.tags) ? tagsRes.data.tags : [];
          const slice = buildUnifiedTxCategoryTagChipsByTxId(tagRows, [txId]);
          applyUnifiedTxCategoryTagsSlice(slice);
        } catch {
          /* non-blocking */
        }
        if (canonicalHybridEnabled) {
          try {
            await refreshCanonicalRowsForTransactionIds(0, [txId]);
          } catch {
            /* non-blocking */
          }
        }
        setSuccessMessage(`Set VAT ${vatRate} on TX ${txId}.`);
      } catch (err: any) {
        setError(err?.response?.data?.error || err?.message || 'Failed to set VAT rate');
      } finally {
        setUnifiedTxTagSyncBusyByTxId((prev) => {
          const next = { ...prev };
          delete next[txId];
          return next;
        });
      }
    },
    [
      canonicalHybridEnabled,
      refreshCanonicalRowsForTransactionIds,
    ]
  );

  const confirmDeleteUnifiedTag = useCallback(async () => {
    const pending = pendingUnifiedTagDelete;
    if (!pending) return;
    const { txId, tagUid, label, clearVatOnly } = pending;
    setUnifiedTxTagSyncBusyByTxId((prev) => ({ ...prev, [txId]: true }));
    try {
      setDeletingUnifiedTagUid(tagUid);
      setError('');
      await api.delete(`/api/revolut-transaction-category-tags/${encodeURIComponent(tagUid)}`, {
        params: clearVatOnly ? { dimension: 'vat' } : undefined
      });
      setPendingUnifiedTagDelete(null);
      if (canonicalHybridEnabled) {
        await refreshCanonicalRowsForTransactionIds(0, [txId]);
      } else {
        const row = unifiedRows.find((r) => r.id === txId) ?? null;
        if (row) {
          await refreshUnifiedRowAfterRerunRef.current(row);
        }
      }
      haltUnifiedTxCategoryTagsFetch();
      const tagResCtrl = new AbortController();
      const res = await api.get('/api/revolut-transaction-category-tags/', {
        params: { transaction_ids: String(txId), _t: String(Date.now()) },
        signal: tagResCtrl.signal
      });
      const rows = Array.isArray(res.data?.tags) ? res.data.tags : [];
      const slice = buildUnifiedTxCategoryTagChipsByTxId(rows, [txId]);
      // Delete refresh may legitimately leave this tx with zero chips.
      applyUnifiedTxCategoryTagsSlice(slice, { forceEmpty: [txId] });
      setSuccessMessage(
        clearVatOnly
          ? `Cleared VAT on TX ${txId} (category kept).`
          : `Deleted tag "${label}" from TX ${txId}.`
      );
    } catch (err: any) {
      if (axios.isCancel(err) || err?.code === 'ERR_CANCELED') {
        return;
      }
      setError(err?.response?.data?.error || err?.message || 'Failed to delete tag');
    } finally {
      setDeletingUnifiedTagUid(null);
      setUnifiedTxTagSyncBusyByTxId((prev) => {
        const next = { ...prev };
        delete next[txId];
        return next;
      });
    }
  }, [
    pendingUnifiedTagDelete,
    canonicalHybridEnabled,
    refreshCanonicalRowsForTransactionIds,
    unifiedRows,
    haltUnifiedTxCategoryTagsFetch
  ]);

  const requestBulkAppliedTagDelete = useCallback(() => {
    const composite = bulkAppliedTagDeleteComposite.trim();
    if (!selectedEditableUnifiedRows.length) {
      setError('Bulk delete applies to uncommitted rows only. Deselect committed rows.');
      return;
    }
    if (!composite) {
      setError('Choose which composite applied tag to remove (e.g. Uncategorized @ none, Expense @ none).');
      return;
    }
    const deletions: Array<{ txId: number; uid: string }> = [];
    selectedEditableUnifiedRows.forEach((row) => {
      (unifiedTxCategoryTagsByTxId[row.id] || []).forEach((tag) => {
        if (formatUnifiedAppliedTagChipLabel(tag) !== composite) return;
        const uid = String(tag.uid || '').trim();
        if (uid) deletions.push({ txId: row.id, uid });
      });
    });
    if (!deletions.length) {
      setError(`No applied tags matching "${composite}" on the selected rows.`);
      return;
    }
    setPendingBulkAppliedTagDelete({ compositeLabel: composite, deletions });
  }, [bulkAppliedTagDeleteComposite, selectedEditableUnifiedRows, unifiedTxCategoryTagsByTxId]);

  const confirmBulkAppliedTagDelete = useCallback(async () => {
    const pending = pendingBulkAppliedTagDelete;
    if (!pending?.deletions.length) return;
    setUnifiedBulkBusy(true);
    setError('');
    const affectedTxIds = new Set<number>();
    let ok = 0;
    let fail = 0;
    const errBits: string[] = [];
    try {
      for (const { txId, uid } of pending.deletions) {
        try {
          await api.delete(`/api/revolut-transaction-category-tags/${encodeURIComponent(uid)}`);
          ok += 1;
          affectedTxIds.add(txId);
        } catch (err: any) {
          fail += 1;
          if (errBits.length < 4) {
            errBits.push(err?.response?.data?.error || err?.message || 'Failed to delete tag');
          }
        }
      }
      setPendingBulkAppliedTagDelete(null);
      setBulkAppliedTagDeleteComposite('');
      const ids = Array.from(affectedTxIds);
      if (ids.length) {
        if (canonicalHybridEnabled) {
          await refreshCanonicalRowsForTransactionIds(0, ids);
        } else {
          for (const id of ids) {
            const row = unifiedRows.find((r) => r.id === id) ?? null;
            if (row) {
              await refreshUnifiedRowAfterRerunRef.current(row);
            }
          }
        }
        haltUnifiedTxCategoryTagsFetch();
        const res = await api.get('/api/revolut-transaction-category-tags/', {
          params: { transaction_ids: ids.join(','), _t: String(Date.now()) },
        });
        const tagRows = Array.isArray(res.data?.tags) ? res.data.tags : [];
        const slice = buildUnifiedTxCategoryTagChipsByTxId(tagRows, ids);
        applyUnifiedTxCategoryTagsSlice(slice, { forceEmpty: ids });
      }
      if (fail && errBits.length) {
        setError(errBits.join('; '));
      }
      if (ok > 0) {
        setSuccessMessage(
          fail
            ? `Deleted ${ok} applied tag(s) matching "${pending.compositeLabel}" (${fail} failed).`
            : `Deleted ${ok} applied tag(s) matching "${pending.compositeLabel}".`
        );
      }
    } finally {
      setUnifiedBulkBusy(false);
    }
  }, [
    pendingBulkAppliedTagDelete,
    canonicalHybridEnabled,
    refreshCanonicalRowsForTransactionIds,
    unifiedRows,
    haltUnifiedTxCategoryTagsFetch,
  ]);

  const classifyIfNeeded = async (row: UnifiedReviewRow, overrides?: UnifiedClassifyOverrides) => {
    if (
      row.source_state !== 'needs_review' &&
      row.source_state !== 'review_and_confirm' &&
      row.source_state !== 'tagged_by_rules'
    ) {
      return;
    }
    const tx = unifiedTxById.get(row.id);
    if (!tx) return;
    let headingVal: number | '' =
      overrides?.headingId !== undefined
        ? overrides.headingId
        : tx.id in selectedHeadingByTx
          ? selectedHeadingByTx[tx.id]
          : inferHeadingCategoryIdFromAmount(headingCategories, effectiveTxAmount(tx));

    let granularIdsList: number[] = [];
    if (Array.isArray(overrides?.granularIds) && overrides.granularIds.length > 0) {
      granularIdsList = overrides.granularIds
        .map((id) => Number(id))
        .filter((id) => Number.isFinite(id) && id > 0);
    } else if (overrides?.granularId !== undefined && overrides.granularId !== '') {
      granularIdsList = [Number(overrides.granularId)];
    } else {
      granularIdsList = (selectedGranularByTx[tx.id] || []).filter((id) => Number.isFinite(id) && id > 0);
    }

    const resolvedFromGranular: number[] = [];
    for (const gid of granularIdsList) {
      const p = resolvePickedCategoryId(gid, '');
      if (p != null) resolvedFromGranular.push(p);
    }
    let dedupCategoryIds = Array.from(new Set(resolvedFromGranular));
    let headingPick = resolvePickedCategoryId('', headingVal);
    if (dedupCategoryIds.length === 0 && headingPick != null) {
      dedupCategoryIds = [headingPick];
    }
    if (dedupCategoryIds.length === 0) {
      try {
        const decisionRes = await api.post('/api/transaction-classification-rules/review-decision', {
          transaction_id: tx.id,
        });
        const backendHeading = Number(decisionRes.data?.heading_category_id);
        if (Number.isFinite(backendHeading) && backendHeading > 0) {
          headingVal = backendHeading;
          headingPick = resolvePickedCategoryId('', headingVal);
          if (headingPick != null) dedupCategoryIds = [headingPick];
        }
      } catch {
        // Fall back to local inference behavior.
      }
    }
    if (dedupCategoryIds.length === 0) {
      throw new Error('Pick a category first (or use Review and Confirm with a suggested category).');
    }
    const picked = dedupCategoryIds[0];
    const vatOverrideRaw =
      overrides?.vatRate !== undefined
        ? String(overrides.vatRate || '').trim()
        : row.id in selectedVatRateByTx
          ? String(selectedVatRateByTx[row.id] || '').trim()
          : '';
    const vatOverride = canonicalizeVatRate(vatOverrideRaw) || undefined;
    const supplierOverride = (overrides?.supplierName !== undefined
      ? String(overrides.supplierName || '').trim()
      : row.id in selectedSupplierByTx
        ? String(selectedSupplierByTx[row.id] || '').trim()
        : '') || undefined;
    /** Stamp VAT/supplier onto an existing category tag without clearing the other field. */
    const stampDraftsOnExistingCategory = async (catId: number) => {
      if (!vatOverride && !supplierOverride) return;
      const chip = (unifiedTxCategoryTagsByTxId[tx.id] || []).find((t) => {
        const id = categories.find((c) => (c.name || '').trim() === (t.category || '').trim())?.id;
        return id === catId;
      });
      const preservedVat = canonicalizeVatRate(chip?.vatRate) || undefined;
      const preservedSupplier =
        String(chip?.supplier || '').trim() || tx.supplier_name || undefined;
      await postManualRevolutCategoryTagIgnoreDuplicate({
        revolut_transaction_id: tx.id,
        category_id: catId,
        source: 'manual',
        vat_rate: vatOverride || preservedVat,
        supplier_name: supplierOverride || preservedSupplier
      });
    };

    if (row.source_state === 'tagged_by_rules') {
      // Categories already attached to this row (manual + rule), excluding cash-flow heading tags.
      const existingTags = unifiedTxCategoryTagsByTxId[tx.id] || [];
      const existingNonHeadingCategoryIds = new Set<number>(
        existingTags
          .filter((t) => !isTopLevelCashflowHeadingName(t.category))
          .map((t) => categories.find((c) => (c.name || '').trim() === (t.category || '').trim())?.id)
          .filter((id): id is number => id != null && Number.isFinite(id) && id > 0)
      );
      const ruleTagDisplayCategoryId =
        categories.find((c) => (c.name || '').trim() === (row.category || '').trim())?.id ?? null;
      const userKeptRuleCategory =
        ruleTagDisplayCategoryId != null && dedupCategoryIds.includes(ruleTagDisplayCategoryId);

      if (userKeptRuleCategory) {
        // Keep the rule category tag; append new categories; always stamp VAT/supplier drafts
        // onto categories already on the row (dropdown Save used to skip them → "VAT Rate Missing").
        for (const catId of dedupCategoryIds) {
          if (existingNonHeadingCategoryIds.has(catId)) {
            await stampDraftsOnExistingCategory(catId);
            continue;
          }
          await postManualRevolutCategoryTagIgnoreDuplicate({
            revolut_transaction_id: tx.id,
            category_id: catId,
            source: 'manual',
            vat_rate: vatOverride,
            supplier_name: supplierOverride || tx.supplier_name || undefined
          });
        }
        return;
      }

      // User changed the rule tag's primary category — correct it, then add extras.
      let tagUid = String(
        (row.tag_uid || '').trim() || (row.key.startsWith('tagged-') ? row.key.slice('tagged-'.length) : '')
      ).trim();
      if (!tagUid) {
        tagUid = await fetchRuleTagUidForCorrectTag(tx.id, row.category || '', picked);
      }
      if (!tagUid) {
        throw new Error('Could not resolve the existing rule tag for this row.');
      }
      const correctBody = {
        tag_uid: tagUid,
        new_category_id: picked,
        supplier_name: supplierOverride,
        vat_rate: vatOverride,
        update_rule: false
      };
      try {
        await api.post('/api/transaction-classification-rules/correct-tag', correctBody);
      } catch (err: any) {
        if (err?.response?.status === 404) {
          const fresh = await fetchRuleTagUidForCorrectTag(tx.id, row.category || '', picked);
          if (!fresh) throw err;
          await api.post('/api/transaction-classification-rules/correct-tag', {
            ...correctBody,
            tag_uid: fresh
          });
        } else {
          throw err;
        }
      }
      for (const extraCatId of dedupCategoryIds.slice(1)) {
        if (existingNonHeadingCategoryIds.has(extraCatId)) {
          await stampDraftsOnExistingCategory(extraCatId);
          continue;
        }
        await postManualRevolutCategoryTagIgnoreDuplicate({
          revolut_transaction_id: tx.id,
          category_id: extraCatId,
          source: 'manual',
          vat_rate: vatOverride,
          supplier_name: supplierOverride || tx.supplier_name || undefined
        });
      }
      return;
    }
    if (tx.unclassified_tag_uid) {
      await api.delete(`/api/revolut-transaction-category-tags/${encodeURIComponent(tx.unclassified_tag_uid)}`);
    }
    // Categories already on the row still need a stamp when VAT/supplier drafts changed
    // (category chips are added immediately on select; VAT was draft-only until Save).
    const alreadyTaggedCategoryIds = new Set<number>(
      (unifiedTxCategoryTagsByTxId[tx.id] || [])
        .filter((t) => !isTopLevelCashflowHeadingName(t.category))
        .map((t) => categories.find((c) => (c.name || '').trim() === (t.category || '').trim())?.id)
        .filter((id): id is number => id != null && Number.isFinite(id) && id > 0)
    );
    for (const catId of dedupCategoryIds) {
      if (alreadyTaggedCategoryIds.has(catId)) {
        await stampDraftsOnExistingCategory(catId);
        continue;
      }
      await postManualRevolutCategoryTagIgnoreDuplicate({
        revolut_transaction_id: tx.id,
        category_id: catId,
        source: 'manual',
        vat_rate: vatOverride,
        supplier_name: supplierOverride || tx.supplier_name || undefined
      });
    }
    const pickedName = categories.find((c) => c.id === picked)?.name;
    let autoFreq = inferPaymentFrequencyFromCategoryName(pickedName);
    if (!overrides?.lightweightForBulkCommit) {
      try {
        const decisionRes = await api.post('/api/transaction-classification-rules/review-decision', {
          transaction_id: tx.id,
          category_id: picked,
          category_name: pickedName || null
        });
        const backendFreq = String(decisionRes.data?.inferred_payment_frequency || '').trim();
        if (backendFreq) {
          autoFreq = backendFreq;
        }
      } catch {
        // Keep local fallback behavior.
      }
    }
    if (autoFreq && !tx.payment_frequency) {
      try {
        await api.patch(`/api/revolut/transactions/complete/${tx.id}/payment-frequency`, {
          payment_frequency: autoFreq
        });
      } catch {
        /* non-fatal */
      }
    }
  };

  const pickedCategoryForTx = useCallback(
    (tx: ReviewTx): number | null => {
      const headingVal =
        tx.id in selectedHeadingByTx
          ? selectedHeadingByTx[tx.id]
          : inferHeadingCategoryIdFromAmount(headingCategories, effectiveTxAmount(tx));
      const granularVal = primaryGranularCategoryId(selectedGranularByTx, tx.id);
      return resolvePickedCategoryId(granularVal, headingVal);
    },
    [headingCategories, selectedGranularByTx, selectedHeadingByTx]
  );

  const createAutoRuleProposalIfNeeded = useCallback(
    async (tx: ReviewTx, categoryId: number): Promise<boolean> => {
      // Hard-off: no implicit proposal creation from commit/manual-tag actions.
      void tx;
      void categoryId;
      return false;
    },
    []
  );

  const applyFrequencyDraftIfNeeded = useCallback(
    async (txId: number, opts?: { skipRefetch?: boolean }) => {
      const tx = unifiedTxById.get(txId);
      if (!tx) return;
      if (!(txId in selectedFrequencyByTx)) return;
      const nextRaw = selectedFrequencyByTx[txId];
      const currentRaw = (tx.payment_frequency || '').trim();
      if ((nextRaw || '').trim() === currentRaw) return;
      await patchPaymentFrequency(txId, nextRaw === '' ? null : nextRaw, opts);
    },
    [patchPaymentFrequency, selectedFrequencyByTx, unifiedTxById]
  );

  const setRowEditMode = useCallback(
    (row: UnifiedReviewRow, enabled: boolean) => {
      const tx = unifiedTxById.get(row.id);
      if (!tx) return;
      setUnifiedEditingTxIds((prev) =>
        enabled ? (prev.includes(tx.id) ? prev : [...prev, tx.id]) : prev.filter((id) => id !== tx.id)
      );
      if (!(tx.id in selectedFrequencyByTx)) {
        setSelectedFrequencyByTx((prev) => ({
          ...prev,
          [tx.id]: tx.payment_frequency || row.frequency || ''
        }));
      }
      if (!(tx.id in selectedSupplierByTx)) {
        setSelectedSupplierByTx((prev) => ({
          ...prev,
          [tx.id]: (row.supplier || tx.supplier_name || '').trim()
        }));
      }
      if (!(tx.id in selectedGranularByTx)) {
        const tagRows = unifiedTxCategoryTagsByTxId[tx.id] || [];
        const fromTags = Array.from(
          new Set(
            tagRows
              .filter((t) => !isTopLevelCashflowHeadingName(t.category))
              .map((t) => categories.find((c) => (c.name || '').trim() === (t.category || '').trim())?.id)
              .filter((id): id is number => id != null && Number.isFinite(id) && id > 0)
          )
        );
        const rowHeadingId = headingCategoryIdFromCashFlowCell(headingCategories, row.cashFlow);
        if (fromTags.length > 0) {
          setSelectedGranularByTx((prev) => ({ ...prev, [tx.id]: fromTags }));
          setSelectedHeadingByTx((prev) => ({ ...prev, [tx.id]: rowHeadingId }));
        } else {
          const existingCategoryId = categories.find((c) => c.name === row.category)?.id;
          if (existingCategoryId && !isTopLevelCashflowHeadingName(row.category || '')) {
            setSelectedGranularByTx((prev) => ({ ...prev, [tx.id]: [existingCategoryId] }));
            setSelectedHeadingByTx((prev) => ({ ...prev, [tx.id]: rowHeadingId }));
          } else if (!(tx.id in selectedHeadingByTx)) {
            setSelectedHeadingByTx((prev) => ({
              ...prev,
              [tx.id]: rowHeadingId || inferHeadingCategoryIdFromAmount(headingCategories, effectiveTxAmount(tx))
            }));
          }
        }
      }
      if (!(tx.id in selectedVatRateByTx)) {
        setSelectedVatRateByTx((prev) => ({
          ...prev,
          [tx.id]: canonicalizeVatRate(row.vat_rate) || ''
        }));
      }
      if (!(tx.id in selectedPropertyIdsByTx)) {
        setSelectedPropertyIdsByTx((prev) => ({
          ...prev,
          [tx.id]: unifiedRowPropertyIds(row)
        }));
      }
    },
    [
      categories,
      headingCategories,
      selectedFrequencyByTx,
      selectedSupplierByTx,
      selectedGranularByTx,
      selectedHeadingByTx,
      selectedVatRateByTx,
      selectedPropertyIdsByTx,
      unifiedTxById,
      unifiedTxCategoryTagsByTxId
    ]
  );

  /** Exit row edit mode and drop unsaved dropdown drafts for that transaction. */
  const cancelUnifiedRowEdit = useCallback((row: UnifiedReviewRow) => {
    const id = row.id;
    setUnifiedEditingTxIds((prev) => prev.filter((x) => x !== id));
    setSelectedHeadingByTx((prev) => {
      const next = { ...prev };
      delete next[id];
      return next;
    });
    setSelectedSupplierByTx((prev) => {
      const next = { ...prev };
      delete next[id];
      return next;
    });
    setSelectedGranularByTx((prev) => {
      const next = { ...prev };
      delete next[id];
      return next;
    });
    setSelectedVatRateByTx((prev) => {
      const next = { ...prev };
      delete next[id];
      return next;
    });
    setSelectedPropertyIdsByTx((prev) => {
      const next = { ...prev };
      delete next[id];
      return next;
    });
    setSelectedFrequencyByTx((prev) => {
      const next = { ...prev };
      delete next[id];
      return next;
    });
  }, []);

  const resetBulkTagDrafts = useCallback(() => {
    setBulkTagSupplierDraft(BULK_TAG_NO_CHANGE);
    setBulkTagHeadingDraft(BULK_TAG_NO_CHANGE);
    setBulkTagCategoryDraft(BULK_TAG_NO_CHANGE);
    setBulkTagVatDraft(BULK_TAG_NO_CHANGE);
    setBulkTagPropDraft(BULK_TAG_NO_CHANGE);
  }, []);

  const openBulkTagEditDialog = useCallback(() => {
    resetBulkTagDrafts();
    setBulkTagEditOpen(true);
  }, [resetBulkTagDrafts]);
  const runStripAllTagsForSelectedUnifiedRowsFromFilter = useCallback(async () => {
    setError('');
    if (!selectedUnifiedRows.length) {
      setError('Select one or more rows first.');
      return;
    }
    const eligible = selectedStrippableUnifiedRows;
    if (!eligible.length) {
      setError('Selected rows are committed and cannot be retagged. Uncommit first.');
      return;
    }
    const txIds = eligible.map((r) => r.id).filter((id) => Number.isFinite(id) && id > 0);
    if (!txIds.length) {
      setError('No valid transaction ids in the current selection.');
      return;
    }
    if (txIds.length > BULK_REPROCESS_TRANSACTIONS_MAX) {
      setError(
        `Select at most ${BULK_REPROCESS_TRANSACTIONS_MAX} transactions for this action (currently ${txIds.length}).`
      );
      return;
    }
    let progressToastId: number | null = null;
    try {
      setUnifiedStripTagsBusy(true);
      progressToastId = beginTxReviewBulkActionWithToast(
        `Removing all tags from ${eligible.length} selected row(s)…`
      );
      const res = await api.post('/api/transaction-classification-rules/strip-all-category-tags-for-transactions', {
        revolut_transaction_ids: txIds
      });
      const affectedIds = (Array.isArray(res.data?.affected_transaction_ids)
        ? res.data.affected_transaction_ids
        : txIds
      ).map((x: unknown) => Number(x)) as number[];
      const uniqueAffected = Array.from(
        new Set(affectedIds.filter((id) => Number.isFinite(id) && id > 0))
      ) as number[];
      haltUnifiedTxCategoryTagsFetch();
      await refreshTransactionReviewCore();
      if (uniqueAffected.length) {
        try {
          const tagRes = await api.get('/api/revolut-transaction-category-tags/', {
            params: { transaction_ids: uniqueAffected.join(','), _t: String(Date.now()) }
          });
          const tagRows = Array.isArray(tagRes.data?.tags) ? tagRes.data.tags : [];
          const slice = buildUnifiedTxCategoryTagChipsByTxId(tagRows, uniqueAffected);
          applyUnifiedTxCategoryTagsSlice(slice, { forceEmpty: uniqueAffected });
        } catch {
          /* non-fatal */
        }
      }
      void fetchPipelineCounts();
      setSuccessMessage(String(res.data?.message || `Removed all tags from ${eligible.length} row(s).`));
      setSelectedUnifiedRowKeys([]);
    } catch (err: any) {
      setError(String(err?.response?.data?.error || err?.message || 'Failed to remove tags'));
    } finally {
      dismissTxReviewBulkToast(progressToastId);
      setUnifiedStripTagsBusy(false);
    }
  }, [
    beginTxReviewBulkActionWithToast,
    buildUnifiedTxCategoryTagChipsByTxId,
    dismissTxReviewBulkToast,
    fetchPipelineCounts,
    haltUnifiedTxCategoryTagsFetch,
    refreshTransactionReviewCore,
    selectedStrippableUnifiedRows,
    selectedUnifiedRows,
    setError,
    setSelectedUnifiedRowKeys,
    setSuccessMessage,
    applyUnifiedTxCategoryTagsSlice
  ]);

  const openVatConflictBulkTagEditDialog = useCallback(() => {
    const keys = Array.from(
      new Set(
        selectedTxIds
          .filter((id) => vatConflictIdSet.has(id))
          .map((id) => unifiedRowKeyByTxId.get(id))
          .filter((key): key is string => !!key)
      )
    );
    if (!keys.length) {
      setError('Selected VAT-conflict rows are not available in the unified dataset for bulk tag editing.');
      return;
    }
    setSelectedUnifiedRowKeys(keys);
    openBulkTagEditDialog();
  }, [openBulkTagEditDialog, selectedTxIds, unifiedRowKeyByTxId, vatConflictIdSet]);

  const runUnifiedBulkManualTagUpdate = useCallback(
    async (
      opts?: {
        explicitRows?: UnifiedReviewRow[];
        draftOverrides?: {
          heading: string;
          supplier: string;
          category: string;
          vat: string;
          prop?: string;
        };
        skipClearUnifiedSelection?: boolean;
        similarModalErrors?: boolean;
      }
    ): Promise<boolean> => {
      const similarErr = opts?.similarModalErrors === true;
      const reportErr = similarErr ? (msg: string) => setSimilarTxError(msg) : setError;

      const baseRows =
        opts?.explicitRows && opts.explicitRows.length > 0 ? opts.explicitRows : selectedUnifiedRows;

      let editableRows: UnifiedReviewRow[];
      if (opts?.explicitRows && opts.explicitRows.length > 0) {
        editableRows = baseRows.filter(
          (row) =>
            row.source_state === 'needs_review' ||
            row.source_state === 'review_and_confirm' ||
            row.source_state === 'tagged_by_rules'
        );
      } else {
        editableRows = selectedEditableUnifiedRows;
      }

      const hDraft = opts?.draftOverrides?.heading ?? bulkTagHeadingDraft;
      const sDraft = opts?.draftOverrides?.supplier ?? bulkTagSupplierDraft;
      const cDraft = opts?.draftOverrides?.category ?? bulkTagCategoryDraft;
      const vDraft = opts?.draftOverrides?.vat ?? bulkTagVatDraft;
      const pDraft = opts?.draftOverrides?.prop ?? bulkTagPropDraft;

      if (!baseRows.length) {
        reportErr('Select one or more rows first.');
        return false;
      }
      if (!editableRows.length) {
        reportErr('Selected rows are committed and cannot be bulk-edited. Select uncommitted rows.');
        return false;
      }
      const clearHeading = hDraft === BULK_TAG_REMOVE_TAGS;
      const clearSupplier = sDraft === BULK_TAG_REMOVE_TAGS;
      const clearCategory = cDraft === BULK_TAG_REMOVE_TAGS;
      const clearVat = vDraft === BULK_TAG_REMOVE_TAGS;
      const clearProp = pDraft === BULK_TAG_REMOVE_TAGS;
      const hasClassificationRemove = clearHeading || clearSupplier || clearCategory || clearVat;
      const hasPropApply = pDraft !== BULK_TAG_NO_CHANGE && pDraft !== BULK_TAG_REMOVE_TAGS;
      const hasClassificationPositiveApply =
        (sDraft !== BULK_TAG_NO_CHANGE && sDraft !== BULK_TAG_REMOVE_TAGS) ||
        (hDraft !== BULK_TAG_NO_CHANGE && hDraft !== BULK_TAG_REMOVE_TAGS) ||
        (cDraft !== BULK_TAG_NO_CHANGE && cDraft !== BULK_TAG_REMOVE_TAGS) ||
        (vDraft !== BULK_TAG_NO_CHANGE && vDraft !== BULK_TAG_REMOVE_TAGS);
      const hasRemove = hasClassificationRemove || clearProp;
      const hasPositiveApply = hasClassificationPositiveApply || hasPropApply;
      const hasAnyOverride = hasRemove || hasPositiveApply;
      if (!hasAnyOverride) {
        reportErr('Pick at least one tag to update (supplier, heading/category, VAT rate, or Prop).');
        return false;
      }
      let progressToastId: number | null = null;
      try {
        setUnifiedBulkBusy(true);
        if (similarErr) setSimilarTxError('');
        else setError('');
        progressToastId = beginTxReviewBulkActionWithToast(
          hasRemove && !hasPositiveApply
            ? `Removing tags from ${editableRows.length} selected row(s)…`
            : `Applying tags to ${editableRows.length} selected row(s)…`
        );
        let updated = 0;
        const skippedConfirmed = baseRows.length - editableRows.length;
        let failed = 0;
        const failMessages: string[] = [];
        const successTxIdsByUser = new Map<number, number[]>();
        const noteSuccessForRow = (row: UnifiedReviewRow | undefined) => {
          if (!row) return;
          const uid = Number(row.user_id);
          const tid = Number(row.id);
          if (!Number.isFinite(uid) || uid <= 0 || !Number.isFinite(tid) || tid <= 0) return;
          const arr = successTxIdsByUser.get(uid) ?? [];
          if (!arr.includes(tid)) {
            arr.push(tid);
            successTxIdsByUser.set(uid, arr);
          }
        };

        if (hasClassificationRemove && !txReviewBulkCancelRequestedRef.current) {
          const txIds = editableRows.map((r) => r.id).filter((id) => Number.isFinite(id) && id > 0);
          const uidSeen = new Set<string>();
          const deletionPairs: Array<{ uid: string; txId: number }> = [];
          const chunkSize = 200;
          for (let i = 0; i < txIds.length; i += chunkSize) {
            const chunkIds = txIds.slice(i, i + chunkSize);
            if (!chunkIds.length) continue;
            try {
              const res = await api.get('/api/revolut-transaction-category-tags/', {
                params: { transaction_ids: chunkIds.join(','), _t: String(Date.now()) }
              });
              const tagRows = Array.isArray(res.data?.tags) ? res.data.tags : [];
              const byTx = buildUnifiedTxCategoryTagChipsByTxId(tagRows, chunkIds);
              chunkIds.forEach((tid) => {
                const chips = byTx[tid] || [];
                const pick = (bucket: 'heading' | 'supplier' | 'category' | 'vat') =>
                  unifiedTxCategoryTagChipsMatchingBulkClearBucket(chips, bucket);
                if (clearHeading) {
                  pick('heading').forEach((t) => {
                    if (uidSeen.has(t.uid)) return;
                    uidSeen.add(t.uid);
                    deletionPairs.push({ uid: t.uid, txId: tid });
                  });
                }
                if (clearSupplier) {
                  pick('supplier').forEach((t) => {
                    if (uidSeen.has(t.uid)) return;
                    uidSeen.add(t.uid);
                    deletionPairs.push({ uid: t.uid, txId: tid });
                  });
                }
                if (clearCategory) {
                  pick('category').forEach((t) => {
                    if (uidSeen.has(t.uid)) return;
                    uidSeen.add(t.uid);
                    deletionPairs.push({ uid: t.uid, txId: tid });
                  });
                }
                if (clearVat) {
                  pick('vat').forEach((t) => {
                    if (uidSeen.has(t.uid)) return;
                    uidSeen.add(t.uid);
                    deletionPairs.push({ uid: t.uid, txId: tid });
                  });
                }
              });
            } catch (err: any) {
              failed += chunkIds.length;
              if (failMessages.length < 3) {
                failMessages.push(
                  `Load tags for bulk remove: ${String(err?.response?.data?.error || err?.message || 'Request failed')}`
                );
              }
            }
          }
          for (const { uid, txId } of deletionPairs) {
            if (txReviewBulkCancelRequestedRef.current) break;
            try {
              await api.delete(`/api/revolut-transaction-category-tags/${encodeURIComponent(uid)}`);
              updated += 1;
              noteSuccessForRow(editableRows.find((x) => x.id === txId));
            } catch (err: any) {
              failed += 1;
              if (failMessages.length < 3) {
                failMessages.push(
                  `TX ${txId}: ${String(err?.response?.data?.error || err?.message || 'Delete failed')}`
                );
              }
            }
          }
          if (deletionPairs.length && !txReviewBulkCancelRequestedRef.current) {
            haltUnifiedTxCategoryTagsFetch();
            await refreshTransactionReviewCore();
            const affectedIds = Array.from(new Set(deletionPairs.map((p) => p.txId)));
            if (affectedIds.length) {
              try {
                const res = await api.get('/api/revolut-transaction-category-tags/', {
                  params: { transaction_ids: affectedIds.join(','), _t: String(Date.now()) }
                });
                const rows = Array.isArray(res.data?.tags) ? res.data.tags : [];
                const slice = buildUnifiedTxCategoryTagChipsByTxId(rows, affectedIds);
                applyUnifiedTxCategoryTagsSlice(slice, { forceEmpty: affectedIds });
              } catch {
                /* non-fatal */
              }
            }
          }
        }

        const forceSlowPathForRules = hasClassificationRemove && hasClassificationPositiveApply;

        const desiredPatchById = new Map<
          number,
          { supplier: string; category: string; vat_rate: string | null }
        >();

        const bulkRulePayloads: {
          rowId: number;
          item: {
            revolut_transaction_id: number;
            new_category_id: number;
            update_rule: boolean;
            category_display_name?: string;
            supplier_name?: string;
            vat_rate?: string;
            tag_uid?: string;
          };
        }[] = [];
        const slowRowJobs: {
          row: UnifiedReviewRow;
          headingId: number | '';
          granularId: number | '';
          supplierName: string;
          vatRate: string;
        }[] = [];

        if (hasClassificationPositiveApply && !txReviewBulkCancelRequestedRef.current) {
          for (const row of editableRows) {
            const existingCategoryId = categories.find((c) => c.name === row.category)?.id;
            const baseHeadingId =
              headingCategoryIdFromCashFlowCell(headingCategories, row.cashFlow) ||
              inferHeadingCategoryIdFromAmount(headingCategories, row.amount);
            const baseGranularId =
              existingCategoryId && !isTopLevelCashflowHeadingName(row.category || '') ? existingCategoryId : '';

            const hasCategoryOverride =
              cDraft !== BULK_TAG_NO_CHANGE && cDraft !== BULK_TAG_REMOVE_TAGS;
            const hasHeadingOverride =
              !hasCategoryOverride && hDraft !== BULK_TAG_NO_CHANGE && hDraft !== BULK_TAG_REMOVE_TAGS;
            const resolvedHeading = hasCategoryOverride
              ? ''
              : hasHeadingOverride
                ? Number(hDraft)
                : baseHeadingId;
            const resolvedGranular = hasCategoryOverride ? Number(cDraft) : hasHeadingOverride ? '' : baseGranularId;
            const supplierOverride =
              sDraft === BULK_TAG_NO_CHANGE
                ? (row.supplier || '').trim()
                : sDraft === BULK_TAG_REMOVE_TAGS
                  ? (row.supplier || '').trim()
                  : sDraft;
            const vatOverride =
              vDraft === BULK_TAG_NO_CHANGE
                ? canonicalizeVatRate(row.vat_rate) || ''
                : vDraft === BULK_TAG_REMOVE_TAGS
                  ? canonicalizeVatRate(row.vat_rate) || ''
                  : vDraft;

            const picked = resolvePickedCategoryId(resolvedGranular, resolvedHeading);
            let categoryDisplay = row.category;
            if (cDraft !== BULK_TAG_NO_CHANGE && cDraft !== BULK_TAG_REMOVE_TAGS) {
              const cid = Number(cDraft);
              categoryDisplay =
                (Number.isFinite(cid) && cid > 0 ? categories.find((c) => c.id === cid)?.name : null) || row.category;
            } else if (picked != null) {
              categoryDisplay = categories.find((c) => c.id === picked)?.name || row.category;
            }
            const vatFinal =
              vDraft === BULK_TAG_NO_CHANGE
                ? row.vat_rate
                : vDraft === BULK_TAG_REMOVE_TAGS
                  ? row.vat_rate
                  : canonicalizeVatRate(vDraft) || null;
            desiredPatchById.set(row.id, {
              supplier: (supplierOverride || '').trim() || row.supplier,
              category: String(categoryDisplay || '').trim() || row.category,
              vat_rate: vatFinal
            });

            if (row.source_state === 'tagged_by_rules' && picked != null && !forceSlowPathForRules) {
              const tagUid = String((row.tag_uid || '').trim() || '');
              const vatApi =
                vDraft === BULK_TAG_NO_CHANGE || vDraft === BULK_TAG_REMOVE_TAGS
                  ? canonicalizeVatRate(row.vat_rate) || undefined
                  : canonicalizeVatRate(vDraft) || undefined;
              const catDisplay = String(row.category || '').trim();
              bulkRulePayloads.push({
                rowId: row.id,
                item: {
                  revolut_transaction_id: row.id,
                  new_category_id: picked,
                  update_rule: false,
                  ...(catDisplay ? { category_display_name: catDisplay } : {}),
                  ...(supplierOverride.trim() ? { supplier_name: supplierOverride.trim() } : {}),
                  ...(vatApi ? { vat_rate: vatApi } : {}),
                  ...(tagUid ? { tag_uid: tagUid } : {})
                }
              });
            } else if (hasClassificationPositiveApply) {
              slowRowJobs.push({
                row,
                headingId: resolvedHeading,
                granularId: resolvedGranular,
                supplierName: supplierOverride,
                vatRate: vatOverride
              });
            }
          }
        }

        if ((clearProp || hasPropApply) && !txReviewBulkCancelRequestedRef.current) {
          const propIdsToSet: number[] = clearProp
            ? []
            : (() => {
                const n = Number(pDraft);
                return Number.isFinite(n) && n > 0 ? [n] : [];
              })();
          const propOpt = propIdsToSet.length
            ? txPropOptions.find((o) => o.id === propIdsToSet[0])
            : null;
          for (const row of editableRows) {
            if (txReviewBulkCancelRequestedRef.current) break;
            try {
              const res = await api.patch(
                `/api/revolut/transactions/complete/${row.id}/appended-property`,
                { property_ids: propIdsToSet }
              );
              const fromApi = Array.isArray(res.data?.properties)
                ? res.data.properties
                    .map((p: { id?: number; nickname?: string | null }) => ({
                      id: Number(p?.id),
                      nickname:
                        typeof p?.nickname === 'string' && p.nickname.trim()
                          ? p.nickname.trim()
                          : null
                    }))
                    .filter((p: { id: number }) => Number.isFinite(p.id) && p.id > 0)
                : propIdsToSet.map((id) => ({
                    id,
                    nickname: propOpt?.nickname || null
                  }));
              applyTxPropertyToLocalState(
                row.id,
                fromApi.map((p: { id: number }) => p.id),
                fromApi
              );
              updated += 1;
              noteSuccessForRow(row);
            } catch (err: any) {
              failed += 1;
              if (failMessages.length < 3) {
                failMessages.push(
                  `TX ${row.id} Prop: ${String(err?.response?.data?.error || err?.message || 'Update failed')}`
                );
              }
            }
          }
        }

        if (!txReviewBulkCancelRequestedRef.current && bulkRulePayloads.length > 0) {
          try {
            const res = await api.post('/api/transaction-classification-rules/correct-tags-bulk', {
              items: bulkRulePayloads.map((b) => b.item)
            });
            const results = (res.data?.results || []) as { ok?: boolean; error?: string }[];
            results.forEach((r, i) => {
              const rowId = bulkRulePayloads[i]?.rowId ?? '?';
              if (r.ok) {
                updated += 1;
                noteSuccessForRow(editableRows.find((x) => x.id === rowId));
              } else {
                failed += 1;
                if (failMessages.length < 3) {
                  failMessages.push(`TX ${rowId}: ${String(r.error || 'Update failed')}`);
                }
              }
            });
          } catch (err: any) {
            failed += bulkRulePayloads.length;
            if (failMessages.length < 3) {
              failMessages.push(
                `Bulk rule-tag update failed: ${String(err?.response?.data?.error || err?.message || 'Request failed')}`
              );
            }
          }
        }

        for (const job of slowRowJobs) {
          if (txReviewBulkCancelRequestedRef.current) break;
          try {
            await classifyIfNeeded(job.row, {
              headingId: job.headingId,
              ...(job.granularId !== undefined && job.granularId !== ''
                ? { granularIds: [Number(job.granularId)] }
                : {}),
              supplierName: job.supplierName,
              vatRate: job.vatRate
            });
            updated += 1;
            noteSuccessForRow(job.row);
          } catch (err: any) {
            failed += 1;
            if (failMessages.length < 3) {
              const msg = String(err?.response?.data?.error || err?.message || 'Update failed');
              failMessages.push(`TX ${job.row.id}: ${msg}`);
            }
          }
        }

        await refreshTransactionReviewCore();
        if (canonicalHybridEnabled && !txReviewBulkCancelRequestedRef.current && successTxIdsByUser.size > 0) {
          const successGroups = Array.from(successTxIdsByUser.entries());
          for (let gi = 0; gi < successGroups.length; gi += 1) {
            const uid = successGroups[gi][0];
            const ids = successGroups[gi][1];
            const uniq = Array.from(new Set(ids));
            if (uniq.length) {
              await refreshCanonicalRowsForTransactionIds(uid, uniq);
            }
          }
        }
        if (canonicalHybridEnabled && !txReviewBulkCancelRequestedRef.current && desiredPatchById.size > 0) {
          const successIdSet = new Set<number>();
          successTxIdsByUser.forEach((ids) => {
            ids.forEach((id) => successIdSet.add(id));
          });
          if (successIdSet.size > 0) {
            setCanonicalRowsPayload((prev) => {
              if (!prev?.rows?.length) return prev;
              let touched = false;
              const nextRows = prev.rows.map((r) => {
                const id = Number(r.id);
                if (!successIdSet.has(id)) return r;
                const p = desiredPatchById.get(id);
                if (!p) return r;
                touched = true;
                const status = buildUnifiedCompletenessStatus({
                  supplier: p.supplier,
                  category: p.category,
                  vat_rate: p.vat_rate
                });
                const nextFlags = {
                  ...(r.flags || {}),
                  is_tagged_by_rules: false,
                  has_manual_nonheading_nonunclassified_tag: true
                };
                return {
                  ...r,
                  supplier: p.supplier,
                  category: p.category,
                  vat_rate: p.vat_rate,
                  status,
                  tagOrigin: 'Manual',
                  tag_uid: null,
                  ruleId: null,
                  source_state:
                    r.source_state === 'tagged_by_rules' ? 'needs_review' : r.source_state,
                  flags: nextFlags
                } as CanonicalReviewRow;
              });
              return touched ? { ...prev, rows: nextRows } : prev;
            });
          }
        }
        void fetchPipelineCounts();
        const summaryParts = [
          `${updated} updated`,
          skippedConfirmed > 0 ? `${skippedConfirmed} committed skipped` : '',
          failed > 0 ? `${failed} failed` : ''
        ].filter(Boolean);
        if (txReviewBulkCancelRequestedRef.current) {
          setSuccessMessage(
            `Bulk tag update cancelled after ${updated} row(s). Partial changes may exist — use Refresh if counts look off.`
          );
        } else if (updated > 0) {
          setSuccessMessage(`Bulk manual tag update finished: ${summaryParts.join(', ')}.`);
        }
        if (!txReviewBulkCancelRequestedRef.current && failed > 0) {
          reportErr(failMessages.join(' | '));
        }
        if (!txReviewBulkCancelRequestedRef.current && updated === 0 && failed === 0) {
          if (hasRemove && !hasPositiveApply) {
            setSuccessMessage(
              'Removal finished: no matching legacy tag rows on the selected transactions (nothing to delete).'
            );
          } else {
            reportErr('No tag changes were applied for this selection.');
          }
        }
        if (!txReviewBulkCancelRequestedRef.current) {
          if (!opts?.skipClearUnifiedSelection) {
            setSelectedUnifiedRowKeys([]);
            resetBulkTagDrafts();
          }
        }
        return true;
      } finally {
        dismissTxReviewBulkToast(progressToastId);
        setUnifiedBulkBusy(false);
      }
    },
    [
      beginTxReviewBulkActionWithToast,
      dismissTxReviewBulkToast,
      applyTxPropertyToLocalState,
      bulkTagCategoryDraft,
      bulkTagHeadingDraft,
      bulkTagSupplierDraft,
      bulkTagVatDraft,
      bulkTagPropDraft,
      canonicalHybridEnabled,
      txPropOptions,
      categories,
      classifyIfNeeded,
      fetchPipelineCounts,
      headingCategories,
      refreshCanonicalRowsForTransactionIds,
      refreshTransactionReviewCore,
      resetBulkTagDrafts,
      haltUnifiedTxCategoryTagsFetch,
      selectedEditableUnifiedRows,
      selectedUnifiedRows,
      setError,
      setSimilarTxError
    ]
  );

  const uncommitSimilarModalRows = useCallback(async () => {
    if (!similarTxBaseRow || similarTxUncommitBusy) return;
    const committedRows = similarModalCommittedRows;
    if (!committedRows.length) {
      setSimilarTxError('Select at least one committed row to uncommit.');
      return;
    }
    const ok = window.confirm(
      `Uncommit ${committedRows.length} transaction(s)? They return to the classification review queue and tags can be edited again.`
    );
    if (!ok) return;
    let progressToastId: number | null = null;
    try {
      setSimilarTxUncommitBusy(true);
      setSimilarTxError('');
      progressToastId = beginTxReviewBulkActionWithToast(`Uncommitting ${committedRows.length} selected row(s)…`);
      const res = await api.post('/api/transaction-classification-rules/uncommit-classification', {
        revolut_transaction_ids: committedRows.map((r) => r.id)
      });
      const errs = Array.isArray(res.data?.errors) ? res.data.errors : [];
      const uncommittedIds = (res.data?.uncommitted_ids || [])
        .map((x: unknown) => Number(x))
        .filter((n: number) => Number.isFinite(n) && n > 0);
      const done = uncommittedIds.length;
      if (errs.length > 0) {
        const detail = errs
          .slice(0, 8)
          .map((e: any) => {
            const id = e?.id != null ? `#${e.id}` : '';
            const reason = e?.reason;
            if (reason === 'not_found') return `TX${id}: not found`;
            if (reason === 'forbidden') return `TX${id}: forbidden`;
            return `TX${id}: ${reason || 'unknown'}`;
          })
          .join('; ');
        const more = errs.length > 8 ? ` (+${errs.length - 8} more)` : '';
        setSimilarTxError(`${errs.length} row(s) could not be uncommitted; ${done} uncommitted. ${detail}${more}`);
      } else if (done > 0) {
        setSuccessMessage(`Uncommitted ${done} transaction(s).`);
      }
      setSelectedUnifiedRowKeys([]);
      setUnifiedEditingTxIds([]);
      if (uncommittedIds.length > 0) {
        if (canonicalHybridEnabled) {
          await refreshTransactionReviewCore();
          canonicalHydrateGenRef.current += 1;
          const hydrateAfterUncommitGen = canonicalHydrateGenRef.current;
          setCanonicalRowsPayload((prev) => {
            const next = applyUncommittedIdsToCanonicalPayload(
              prev,
              uncommittedIds,
              canonicalRowsCommittedMode === 'only'
            );
            if (next && next !== prev) {
              const tot = Number(next.count_total || 0);
              const off = Array.isArray(next.rows) ? next.rows.length : 0;
              if (off > 0 && tot > off && hydrateAfterUncommitGen === canonicalHydrateGenRef.current) {
                void hydrateCanonicalRowsInBackground(
                  canonicalRowsCommittedMode,
                  next,
                  hydrateAfterUncommitGen
                );
              }
            }
            return next;
          });
        } else {
          await fetchData();
        }
      }
      void fetchPipelineCounts();
      await openSimilarTransactionsModal(similarTxBaseRow);
    } catch (err: any) {
      setSimilarTxError(err?.response?.data?.error || err?.message || 'Bulk uncommit failed');
    } finally {
      dismissTxReviewBulkToast(progressToastId);
      setSimilarTxUncommitBusy(false);
    }
  }, [
    similarTxBaseRow,
    similarTxUncommitBusy,
    similarModalCommittedRows,
    beginTxReviewBulkActionWithToast,
    dismissTxReviewBulkToast,
    canonicalHybridEnabled,
    canonicalRowsCommittedMode,
    fetchData,
    fetchPipelineCounts,
    hydrateCanonicalRowsInBackground,
    openSimilarTransactionsModal,
    refreshTransactionReviewCore,
    setCanonicalRowsPayload,
    setSelectedUnifiedRowKeys,
    setUnifiedEditingTxIds
  ]);

  const stripTagsSimilarModalRows = useCallback(async () => {
    if (!similarTxBaseRow || similarTxStripBusy) return;
    const eligible = similarModalSelectedUnifiedRows.filter((r) => !isUnifiedRowCommitted(r));
    if (!eligible.length) {
      setSimilarTxError('Selected rows are committed or missing from the grid. Uncommit first.');
      return;
    }
    const txIds = eligible.map((r) => r.id).filter((id) => Number.isFinite(id) && id > 0);
    if (!txIds.length) {
      setSimilarTxError('No valid transaction ids in the current selection.');
      return;
    }
    if (txIds.length > BULK_REPROCESS_TRANSACTIONS_MAX) {
      setSimilarTxError(
        `Select at most ${BULK_REPROCESS_TRANSACTIONS_MAX} transactions for this action (currently ${txIds.length}).`
      );
      return;
    }
    let progressToastId: number | null = null;
    try {
      setSimilarTxStripBusy(true);
      setSimilarTxError('');
      progressToastId = beginTxReviewBulkActionWithToast(`Removing all tags from ${eligible.length} selected row(s)…`);
      const res = await api.post('/api/transaction-classification-rules/strip-all-category-tags-for-transactions', {
        revolut_transaction_ids: txIds
      });
      const affectedIds = (Array.isArray(res.data?.affected_transaction_ids)
        ? res.data.affected_transaction_ids
        : txIds
      ).map((x: unknown) => Number(x)) as number[];
      const uniqueAffected = Array.from(new Set(affectedIds.filter((id) => Number.isFinite(id) && id > 0))) as number[];
      haltUnifiedTxCategoryTagsFetch();
      await refreshTransactionReviewCore();
      if (uniqueAffected.length) {
        try {
          const tagRes = await api.get('/api/revolut-transaction-category-tags/', {
            params: { transaction_ids: uniqueAffected.join(','), _t: String(Date.now()) }
          });
          const tagRows = Array.isArray(tagRes.data?.tags) ? tagRes.data.tags : [];
          const slice = buildUnifiedTxCategoryTagChipsByTxId(tagRows, uniqueAffected);
          applyUnifiedTxCategoryTagsSlice(slice, { forceEmpty: uniqueAffected });
        } catch {
          /* non-fatal */
        }
      }
      void fetchPipelineCounts();
      setSuccessMessage(String(res.data?.message || `Removed all tags from ${eligible.length} row(s).`));
      await openSimilarTransactionsModal(similarTxBaseRow);
    } catch (err: any) {
      setSimilarTxError(String(err?.response?.data?.error || err?.message || 'Failed to remove tags'));
    } finally {
      dismissTxReviewBulkToast(progressToastId);
      setSimilarTxStripBusy(false);
    }
  }, [
    similarTxBaseRow,
    similarTxStripBusy,
    similarModalSelectedUnifiedRows,
    beginTxReviewBulkActionWithToast,
    dismissTxReviewBulkToast,
    fetchPipelineCounts,
    haltUnifiedTxCategoryTagsFetch,
    openSimilarTransactionsModal,
    refreshTransactionReviewCore,
    applyUnifiedTxCategoryTagsSlice
  ]);

  const applyTagsSimilarModalRows = useCallback(async () => {
    if (!similarTxBaseRow || unifiedBulkBusy) return;
    if (!similarModalEditableRows.length) {
      setSimilarTxError('Select at least one uncommitted row for bulk tag edits.');
      return;
    }
    if (!similarModalBulkTagHasApply) {
      setSimilarTxError('Pick at least one column other than No change to apply.');
      return;
    }
    setSimilarTxError('');
    const ran = await runUnifiedBulkManualTagUpdate({
      explicitRows: similarModalSelectedUnifiedRows,
      draftOverrides: {
        heading: similarTxBulkHeadingDraft,
        supplier: similarTxBulkSupplierDraft,
        category: similarTxBulkCategoryDraft,
        vat: similarTxBulkVatDraft
      },
      skipClearUnifiedSelection: true,
      similarModalErrors: true
    });
    if (ran) {
      setSimilarTxBulkHeadingDraft(BULK_TAG_NO_CHANGE);
      setSimilarTxBulkSupplierDraft(BULK_TAG_NO_CHANGE);
      setSimilarTxBulkCategoryDraft(BULK_TAG_NO_CHANGE);
      setSimilarTxBulkVatDraft(BULK_TAG_NO_CHANGE);
      await openSimilarTransactionsModal(similarTxBaseRow);
    }
  }, [
    similarTxBaseRow,
    unifiedBulkBusy,
    similarModalEditableRows,
    similarModalBulkTagHasApply,
    similarModalSelectedUnifiedRows,
    similarTxBulkCategoryDraft,
    similarTxBulkHeadingDraft,
    similarTxBulkSupplierDraft,
    similarTxBulkVatDraft,
    openSimilarTransactionsModal,
    runUnifiedBulkManualTagUpdate
  ]);

  /** Persist row tag drafts only — never commits. Use Commit (row or bulk) separately. */
  const runUnifiedRowSave = async (row: UnifiedReviewRow) => {
    setUnifiedSaveBusyTxIds((prev) => new Set(prev).add(row.id));
    try {
      setUnifiedActionBusy(true);
      setError('');
      await applyFrequencyDraftIfNeeded(row.id);
      let refreshedRowTags: UnifiedTxCategoryTagChip[] | undefined;
      if (row.source_state !== 'confirmed') {
        await classifyIfNeeded(row);
        try {
          const tagsRes = await api.get('/api/revolut-transaction-category-tags/', {
            params: { transaction_ids: String(row.id), _t: String(Date.now()) }
          });
          const tagRows = Array.isArray(tagsRes.data?.tags) ? tagsRes.data.tags : [];
          const slice = buildUnifiedTxCategoryTagChipsByTxId(tagRows, [row.id]);
          refreshedRowTags = slice[row.id];
          applyUnifiedTxCategoryTagsSlice(slice);
        } catch {
          // Non-blocking: keep stale chips rather than fail the whole save.
        }
        if (canonicalHybridEnabled) {
          try {
            await refreshCanonicalRowsForTransactionIds(0, [row.id]);
          } catch {
            // Non-blocking row refresh.
          }
        }
      }
      const tagsForStatus = refreshedRowTags ?? unifiedTxCategoryTagsByTxId[row.id];
      const status = String(unifiedRowDisplayedCompletenessStatus(row, tagsForStatus) || '').trim();
      setSuccessMessage(
        status && status !== TAGGED_BY_ALL_RULES_STATUS
          ? `Saved tags for transaction #${row.id} (status: "${status}"). Use Commit when ready.`
          : `Saved tags for transaction #${row.id}.`
      );
      void fetchPipelineCounts();
    } catch (err: any) {
      setError(err?.response?.data?.error || err?.message || 'Could not save tags for this row');
    } finally {
      setUnifiedSaveBusyTxIds((prev) => {
        const next = new Set(prev);
        next.delete(row.id);
        return next;
      });
      setUnifiedEditingTxIds((prev) => prev.filter((id) => id !== row.id));
      setUnifiedActionBusy(false);
    }
  };

  /** Commit a single uncommitted row. Does not apply unsaved dropdown drafts — Save first. */
  const runUnifiedRowCommit = async (row: UnifiedReviewRow) => {
    if (row.source_state === 'confirmed' || isUnifiedRowCommitted(row)) {
      setError(`Transaction #${row.id} is already committed.`);
      return;
    }
    setUnifiedCommitBusyTxIds((prev) => new Set(prev).add(row.id));
    try {
      setUnifiedActionBusy(true);
      setError('');
      const tagsForStatus = unifiedTxCategoryTagsByTxId[row.id];
      const commitBlock = unifiedRowCommitBlockedMessage(row, tagsForStatus);
      if (commitBlock) {
        setError(commitBlock);
        return;
      }
      const cr = await confirmClassificationIds([row.id]);
      const confirmedIds = cr.confirmedIds;
      if (confirmedIds.length > 0) {
        if (canonicalHybridEnabled) {
          setCanonicalRowsPayload((prev) =>
            applyCommittedIdsToCanonicalPayload(prev, confirmedIds, hideCommittedUnifiedRows)
          );
        } else {
          await refreshTransactionReviewCore();
        }
        // confirmClassificationIds already toasts committed count; keep a row-specific note when single.
        if (confirmedIds.length === 1) {
          setSuccessMessage(`Committed transaction #${row.id}.`);
        }
      }
      void fetchPipelineCounts();
    } catch (err: any) {
      setError(err?.response?.data?.error || err?.message || 'Could not commit this row');
    } finally {
      setUnifiedCommitBusyTxIds((prev) => {
        const next = new Set(prev);
        next.delete(row.id);
        return next;
      });
      setUnifiedActionBusy(false);
    }
  };

  const getIdsForBulkReprocess = useCallback((): number[] => {
    if (tabIndex === 0) {
      return Array.from(
        new Set(
          selectedUnifiedRowKeys
            .map((k) => unifiedRowsByKey.get(k)?.id)
            .filter((id): id is number => typeof id === 'number' && Number.isFinite(id))
        )
      );
    }
    if (tabIndex === CLASSIFICATION_VISIBLE_TAB_KEYS.indexOf('vat-conflicts')) {
      return selectedTxIds.filter((id) => vatConflictIdSet.has(id));
    }
    return [];
  }, [tabIndex, selectedUnifiedRowKeys, unifiedRowsByKey, selectedTxIds, vatConflictIdSet]);

  const ensureRerunConfirmedForModes = useCallback(
    async (txIds: number[], userId?: number | null): Promise<boolean> => {
      const ids = Array.from(new Set((txIds || []).map((id) => Number(id)).filter((id) => Number.isFinite(id) && id > 0)));
      if (!ids.length) return false;
      const res = await api.post('/api/transaction-classification-rules/rerun-preflight', {
        ...(userId != null ? { user_id: userId } : {}),
        revolut_transaction_ids: ids
      });
      const requires = Boolean(res?.data?.requires_confirmation);
      if (!requires) return true;
      const counts = res?.data?.counts || {};
      const manualCount = Number(counts?.Manual || 0);
      const hybridCount = Number(counts?.Hybrid || 0);
      const labelParts = [
        manualCount > 0 ? `${manualCount} Manual` : '',
        hybridCount > 0 ? `${hybridCount} Hybrid` : ''
      ].filter(Boolean);
      const ok = window.confirm(
        `This rerun includes ${labelParts.join(' + ')} transaction(s). Continue and allow rules to overwrite tags?`
      );
      return ok;
    },
    []
  );

  const summarizeBulkReprocessResponse = useCallback((d: Record<string, any>): string => {
    const parts: string[] = [];
    const matched = Number(d.matched_count);
    const requested = Number(d.requested_count);
    const notFound = Array.isArray(d.not_found_ids) ? d.not_found_ids.length : 0;
    let scopeSentence = '';
    if (Number.isFinite(matched) && matched > 0) {
      let s = `Across ${matched} transaction${matched === 1 ? '' : 's'}`;
      if (Number.isFinite(requested) && requested > matched) {
        const gap = requested - matched;
        s += gap === notFound && notFound > 0 ? ` (${notFound} id(s) not found)` : ` (${gap} id(s) not processed)`;
      }
      scopeSentence = `${s}.`;
    }

    // Counts are Revolut category-tag rows unless explicitly "transaction(s)".
    if (d.supplier_refresh) {
      const s = d.supplier_refresh;
      const updated = Number(s.tags_updated ?? 0);
      const scanned = Number(s.tags_scanned ?? 0);
      parts.push(
        `Supplier: ${updated} tag supplier name field(s) changed${
          scanned > 0 ? ` (${scanned} category tag row(s) checked).` : '.'
        }`
      );
    }

    if (d.classification) {
      const c = d.classification;
      const created = Number(c.created_tags ?? 0);
      const removed = Number(c.rule_tags_deleted ?? 0);
      const skipped = (c.skipped_confirmed_ids as number[] | undefined)?.length ?? 0;
      const strip = Boolean(c.strip_all_classification_tags);
      if (typeof c.transactions_scanned === 'number') {
        if (created > 0 || removed > 0) {
          const bits: string[] = [];
          if (created > 0) {
            bits.push(`+${created} category tag row(s) written`);
          }
          if (removed > 0) {
            bits.push(`−${removed} category tag row(s) removed${strip ? ' (full strip + re-apply)' : ''}`);
          }
          parts.push(
            `Classification: ${bits.join(', ')}${
              skipped > 0 ? `, ${skipped} transaction(s) skipped (confirmed)` : ''
            }.`
          );
        } else {
          parts.push(
            `Classification: no category tag changes from rules${
              skipped > 0 ? ` (${skipped} confirmed skipped)` : ''
            }.`
          );
        }
      } else if (c.message) {
        parts.push(`Classification: ${String(c.message).replace(/\.$/, '')}.`);
      }
    }

    if (d.vat_reapply) {
      const v = d.vat_reapply;
      const updated = Number(v.tags_updated ?? 0);
      const scanned = Number(v.tags_scanned ?? 0);
      const txRep = Number(v.transactions_reprocessed ?? 0);
      parts.push(
        `VAT: ${updated} tag VAT field(s) updated${
          scanned > 0 ? ` (${scanned} category tag row(s) checked${txRep > 0 ? `, ${txRep} transaction(s)` : ''}).` : '.'
        }`
      );
    }

    if (d.special_case_reapply) {
      const s = d.special_case_reapply;
      const updated = Number(s.tags_updated ?? 0);
      const scanned = Number(s.tags_scanned ?? 0);
      const txRep = Number(s.transactions_reprocessed ?? 0);
      parts.push(
        `Special case: ${updated} tag field(s) updated${
          scanned > 0 ? ` (${scanned} category tag row(s) checked${txRep > 0 ? `, ${txRep} transaction(s)` : ''}).` : '.'
        }`
      );
    }

    const body = parts.join(' ');
    return scopeSentence ? `${scopeSentence} ${body}` : body;
  }, []);

  const shouldForceFullRefreshFromDiagnostic = useCallback((diagRaw: any): boolean => {
    const diag = (diagRaw || null) as Record<string, any> | null;
    if (!diag) return true;
    const counts = (diag.final_tag_counts || {}) as Record<string, any>;
    const nonHeading = Number(counts.non_heading || 0);
    const matchedCls = Array.isArray(diag.matched_classification_rules) ? diag.matched_classification_rules.length : 0;
    const expectedVatRows = Array.isArray(diag.expected_vat_by_non_heading_tag) ? diag.expected_vat_by_non_heading_tag : [];
    const hasVatMismatch = expectedVatRows.some((x) => {
      const expected = String(x?.expected_vat_rate || '').trim();
      const actual = String(x?.tag_vat_rate || '').trim();
      return expected && expected !== actual;
    });
    // Only force full refresh on clear diagnostic contradictions.
    if (matchedCls > 0 && nonHeading === 0) return true;
    if (hasVatMismatch) return true;
    return false;
  }, []);

  const runBulkReprocessSelected = useCallback(
    async (opts: { classification?: boolean; supplier?: boolean; vat?: boolean; specialCase?: boolean }) => {
      const applyClassification = Boolean(opts.classification);
      const applySupplier = Boolean(opts.supplier);
      const applyVat = Boolean(opts.vat);
      const applySpecialCase = Boolean(opts.specialCase);
      const fullRebuildAllRules =
        applyClassification && applySupplier && applyVat && applySpecialCase;
      if (!applyClassification && !applySupplier && !applyVat && !applySpecialCase) {
        setError('Pick at least one action (supplier, classification, VAT, and/or Special Case rules).');
        return;
      }
      let progressToastId: number | null = null;
      setBulkReprocessBusy(true);
      setError('');
      let skipCanonicalAfterBulk = false;
      try {
        progressToastId = beginTxReviewBulkActionWithToast('Running bulk rule reprocess…');
        if (tabIndex === 0) {
          const selectedRows = selectedUnifiedRowKeys
            .map((k) => unifiedRowsByKey.get(k))
            .filter((r): r is UnifiedReviewRow => !!r);
          if (selectedRows.length === 0) {
            setError('Select one or more rows first.');
            return;
          }
          const byUser = new Map<number, number[]>();
          selectedRows.forEach((row) => {
            const id = Number(row.id);
            const uid = Number(row.user_id);
            if (!Number.isFinite(id) || id <= 0) return;
            if (!Number.isFinite(uid) || uid <= 0) return;
            const current = byUser.get(uid) || [];
            current.push(id);
            byUser.set(uid, current);
          });
          const groups = Array.from(byUser.entries()).map(([uid, ids]) => [uid, Array.from(new Set(ids)).sort((a, b) => a - b)] as const);
          if (groups.length === 0) {
            setError('Could not resolve selected rows to a valid user scope.');
            return;
          }
          const summaries: string[] = [];
          const succeededGroups: { uid: number; ids: number[] }[] = [];
          for (const [uid, ids] of groups) {
            if (txReviewBulkCancelRequestedRef.current) {
              setSuccessMessage('Bulk reprocess cancelled.');
              break;
            }
            if (ids.length > BULK_REPROCESS_TRANSACTIONS_MAX) {
              throw new Error(
                `User ${uid}: at most ${BULK_REPROCESS_TRANSACTIONS_MAX} rows per run (received ${ids.length}).`
              );
            }
            const confirmed = await ensureRerunConfirmedForModes(ids, uid);
            if (!confirmed) {
              setSuccessMessage('Bulk rerun cancelled.');
              continue;
            }
            const res = await api.post(
              '/api/transaction-classification-rules/bulk-reprocess-transactions',
              {
                user_id: uid,
                revolut_transaction_ids: ids,
                apply_classification_rules: applyClassification,
                refresh_supplier_names: applySupplier,
                apply_vat_rules: applyVat,
                apply_special_case_rules: applySpecialCase,
                strip_all_classification_tags: fullRebuildAllRules
              },
              { timeout: HEAVY_RULE_JOB_TIMEOUT_MS }
            );
            const d = res.data as Record<string, any>;
            const summary = summarizeBulkReprocessResponse(d) || 'Bulk reprocess completed.';
            summaries.push(groups.length > 1 ? `User ${uid}: ${summary}` : summary);
            succeededGroups.push({ uid, ids });
          }
          if (!txReviewBulkCancelRequestedRef.current) {
            setSuccessMessage(summaries.join(' | ') || 'Bulk reprocess completed.');
          }
          if (succeededGroups.length > 0) {
            if (canonicalHybridEnabled) {
              skipCanonicalAfterBulk = true;
              for (const { uid, ids } of succeededGroups) {
                await refreshCanonicalRowsForTransactionIds(uid, ids);
              }
              /** Tags chips load from GET /revolut-transaction-category-tags (see unifiedRowEffectiveTagDerivedCells). */
              if (!txReviewBulkCancelRequestedRef.current && tabIndex === 0) {
                setUnifiedTxCategoryTagsFetchEpoch((n) => n + 1);
              }
            } else {
              const idSet = new Set(succeededGroups.flatMap((g) => g.ids));
              const rowsToPatch = selectedRows.filter((r) => idSet.has(Number(r.id)));
              const PAR = 8;
              for (let i = 0; i < rowsToPatch.length; i += PAR) {
                const slice = rowsToPatch.slice(i, i + PAR);
                await Promise.all(slice.map((row) => refreshUnifiedRowAfterRerunRef.current(row)));
              }
              if (!txReviewBulkCancelRequestedRef.current && tabIndex === 0) {
                setUnifiedTxCategoryTagsFetchEpoch((n) => n + 1);
              }
            }
          }
          if (!txReviewBulkCancelRequestedRef.current) {
            setSelectedUnifiedRowKeys([]);
          }
        } else {
          if (txReviewBulkCancelRequestedRef.current) {
            setSuccessMessage('Bulk reprocess cancelled.');
          } else {
          const ids = getIdsForBulkReprocess();
          if (ids.length === 0) {
            setError('Select one or more rows first.');
            return;
          }
          if (ids.length > BULK_REPROCESS_TRANSACTIONS_MAX) {
            setError(
              `Select at most ${BULK_REPROCESS_TRANSACTIONS_MAX} rows per run. Narrow filters or run in batches.`
            );
            return;
          }
          const confirmed = await ensureRerunConfirmedForModes(ids, null);
          if (!confirmed) {
            setSuccessMessage('Bulk rerun cancelled.');
            return;
          }
          const res = await api.post(
            '/api/transaction-classification-rules/bulk-reprocess-transactions',
            {
              revolut_transaction_ids: ids,
              apply_classification_rules: applyClassification,
              refresh_supplier_names: applySupplier,
              apply_vat_rules: applyVat,
              apply_special_case_rules: applySpecialCase,
              strip_all_classification_tags: fullRebuildAllRules
            },
            { timeout: HEAVY_RULE_JOB_TIMEOUT_MS }
          );
          const d = res.data as Record<string, any>;
          setSuccessMessage(summarizeBulkReprocessResponse(d) || 'Bulk reprocess completed.');
          if (tabIndex === CLASSIFICATION_VISIBLE_TAB_KEYS.indexOf('vat-conflicts')) {
            setSelectedTxIds((prev) => prev.filter((id) => !ids.includes(id)));
          }
          }
        }
        await refreshTransactionReviewCore({ skipCanonical: skipCanonicalAfterBulk });
      } catch (err: any) {
        setError(err?.response?.data?.error || err?.message || 'Bulk reprocess failed');
      } finally {
        dismissTxReviewBulkToast(progressToastId);
        setBulkReprocessBusy(false);
      }
    },
    [
      beginTxReviewBulkActionWithToast,
      dismissTxReviewBulkToast,
      refreshTransactionReviewCore,
      refreshCanonicalRowsForTransactionIds,
      canonicalHybridEnabled,
      getIdsForBulkReprocess,
      selectedUnifiedRowKeys,
      summarizeBulkReprocessResponse,
      tabIndex,
      unifiedRowsByKey,
      ensureRerunConfirmedForModes
    ]
  );

  const refreshUnifiedRowAfterRerun = useCallback(async (row: UnifiedReviewRow) => {
    const currentTx = unifiedTxById.get(row.id);
    const revolutId = (currentTx?.revolut_id || '').trim();
    const userScopeParams = row.user_id != null ? { user_id: row.user_id } : {};
    const [reviewRes, taggedRes, confirmedRes, txRes] = await Promise.all([
      api.get('/api/transaction-classification-rules/manual-review-items', {
        params: { limit: REVIEW_NEEDS_REVIEW_LIMIT, transaction_id: row.id, ...userScopeParams }
      }),
      api.get('/api/transaction-classification-rules/tagged-items', {
        params: { limit: 50, offset: 0, transaction_id: row.id, ...userScopeParams }
      }),
      api.get('/api/transaction-classification-rules/classification-confirmed-items', {
        params: { limit: 50, offset: 0, transaction_id: row.id, ...userScopeParams }
      }),
      api.get('/api/revolut/transactions/complete', {
        params: revolutId ? { revolut_id: revolutId } : { transaction_id: row.id }
      })
    ]);

    const reviewData = (reviewRes?.data || {}) as ReviewPayload;
    const taggedData = (taggedRes?.data || {}) as TaggedPayload;
    const confirmedData = (confirmedRes?.data || {}) as ConfirmedPayload;
    const nextUntagged = (reviewData.untagged || []).find((tx) => tx.id === row.id) || null;
    const nextUnclassified = (reviewData.unclassified_needs_review || []).find((tx) => tx.id === row.id) || null;
    const nextConflicting = (reviewData.conflicting_vat || []).find((tx) => tx.id === row.id) || null;
    const nextTagged = (taggedData.tagged || []).filter((item) => item.transaction.id === row.id);
    const nextConfirmed = (confirmedData.items || []).filter((item) => item.transaction.id === row.id);
    const txPayload = txRes?.data?.transaction || txRes?.data || null;
    const nextTx = txPayload && txPayload.id != null ? mapApiTransactionToReviewTx(txPayload) : null;

    setPayload((prev) => {
      if (!prev) return prev;
      return {
        ...prev,
        untagged: upsertReviewTxById(prev.untagged || [], row.id, nextUntagged),
        unclassified_needs_review: upsertReviewTxById(prev.unclassified_needs_review || [], row.id, nextUnclassified),
        conflicting_vat: upsertReviewTxById(prev.conflicting_vat || [], row.id, nextConflicting)
      };
    });
    setTaggedPayload((prev) => {
      if (!prev) return prev;
      return {
        ...prev,
        tagged: upsertTaggedItemsByTxId(prev.tagged || [], row.id, nextTagged)
      };
    });
    setConfirmedPayload((prev) => {
      if (!prev) return prev;
      return {
        ...prev,
        items: upsertConfirmedItemsByTxId(prev.items || [], row.id, nextConfirmed)
      };
    });
    if (nextTx) {
      setAllTransactionsPayload((prev) => upsertReviewTxById(prev || [], row.id, nextTx));
    }
    return !!(nextUntagged || nextUnclassified || nextConflicting || nextTagged.length > 0 || nextConfirmed.length > 0);
  }, [unifiedTxById]);

  useEffect(() => {
    refreshUnifiedRowAfterRerunRef.current = refreshUnifiedRowAfterRerun;
  }, [refreshUnifiedRowAfterRerun]);

  const refreshRulesTabDatasets = useCallback(async (): Promise<{ pendingSupplierProposals: number }> => {
    const [
      classPropsRes,
      rejectedClassPropsRes,
      classRulesRes,
      supplierPropsRes,
      rejectedSupplierPropsRes,
      supplierRulesRes,
      vatRulesRes,
      vatSuggestionsRes,
      rejectedVatSuggestionsRes,
      specialCaseRulesRes,
      vatRateOptionsRes,
      ruleFireCountsRes
    ] = await Promise.all([
      api.get('/api/transaction-classification-rules/rule-proposals').catch(() => ({ data: { proposals: [] } })),
      api
        .get('/api/transaction-classification-rules/rule-proposals', { params: { status: 'rejected' } })
        .catch(() => ({ data: { proposals: [], count_saved_rejected: 0 } })),
      api.get('/api/transaction-classification-rules/').catch(() => ({ data: { rules: [] } })),
      api
        .get('/api/suppliers/description-alias-proposals', { params: { status: 'pending', limit: 2000 } })
        .catch(() => ({ data: { proposals: [] } })),
      api
        .get('/api/suppliers/description-alias-proposals', { params: { status: 'rejected', limit: 2000 } })
        .catch(() => ({ data: { proposals: [], count_saved_rejected: 0 } })),
      api.get('/api/suppliers/description-aliases').catch(() => ({ data: { aliases: [] } })),
      api.get('/api/transaction-classification-rules/vat-rules').catch(() => ({ data: { vat_rules: [] } })),
      api.get('/api/transaction-classification-rules/vat-rules/suggestions', {
        params: { status: 'pending', account_scope: 'both' }
      }).catch(() => ({ data: { suggestions: [] } })),
      api.get('/api/transaction-classification-rules/vat-rules/suggestions', {
        params: { status: 'rejected', account_scope: 'both' }
      }).catch(() => ({ data: { suggestions: [] } })),
      api.get('/api/transaction-classification-rules/special-case-rules').catch(() => ({ data: { special_case_rules: [] } })),
      api.get('/api/transaction-classification-rules/vat-rate-options').catch(() => ({ data: { vat_rate_options: [] } })),
      api.get('/api/transaction-classification-rules/rule-fire-counts', {
        params: ownerGlobalScopeParams
      }).catch(() => ({ data: {} }))
    ]);
    const praw = classPropsRes.data as RuleProposalsPayload | undefined;
    setProposalsPayload({
      proposals: praw?.proposals ?? [],
      count_atlas_virtual: praw?.count_atlas_virtual,
      count_saved_pending: praw?.count_saved_pending,
      count_saved_rejected: Number(praw?.count_saved_rejected || 0),
    });
    const rejectedPraw = rejectedClassPropsRes.data as RuleProposalsPayload | undefined;
    setRejectedProposalsPayload({
      proposals: Array.isArray(rejectedPraw?.proposals) ? rejectedPraw!.proposals : [],
      count_saved_rejected: Number(
        rejectedPraw?.count_saved_rejected ?? rejectedPraw?.proposals?.length ?? 0
      ),
    });
    setRulesPayload(classRulesRes.data as RulesPayload);
    const supplierProposalList = Array.isArray(supplierPropsRes.data?.proposals)
      ? supplierPropsRes.data.proposals
      : [];
    setSupplierProposalsPayload({
      proposals: supplierProposalList,
      count_saved_pending: supplierPropsRes.data?.count_saved_pending
    });
    const rejectedSupplierProposalList = Array.isArray(rejectedSupplierPropsRes.data?.proposals)
      ? rejectedSupplierPropsRes.data.proposals
      : [];
    setRejectedSupplierProposalsPayload({
      proposals: rejectedSupplierProposalList,
      count_saved_rejected: Number(rejectedSupplierPropsRes.data?.count_saved_rejected || 0)
    });
    setSupplierRuleRows(Array.isArray(supplierRulesRes.data?.aliases) ? supplierRulesRes.data.aliases : []);
    setVatRuleRows(Array.isArray(vatRulesRes.data?.vat_rules) ? vatRulesRes.data.vat_rules : []);
    setVatRuleSuggestions(
      Array.isArray(vatSuggestionsRes.data?.suggestions) ? vatSuggestionsRes.data.suggestions : []
    );
    setRejectedVatRuleSuggestions(
      Array.isArray(rejectedVatSuggestionsRes.data?.suggestions)
        ? rejectedVatSuggestionsRes.data.suggestions
        : []
    );
    setSpecialCaseRuleRows(Array.isArray(specialCaseRulesRes.data?.special_case_rules) ? specialCaseRulesRes.data.special_case_rules : []);
    const fireCountsPayload = (ruleFireCountsRes.data || {}) as RuleFireCountsPayload;
    const toNumericMap = (input?: Record<string, number>): Record<number, number> =>
      Object.entries(input || {}).reduce<Record<number, number>>((acc, [k, v]) => {
        const id = Number(k);
        if (Number.isFinite(id) && id > 0) {
          acc[id] = Number(v || 0);
        }
        return acc;
      }, {});
    setRuleFireCounts({
      classification: toNumericMap(fireCountsPayload.classification_rule_tx_counts),
      supplier: toNumericMap(fireCountsPayload.supplier_alias_tx_counts),
      vat: toNumericMap(fireCountsPayload.vat_rule_tx_counts),
      specialCase: toNumericMap(fireCountsPayload.special_case_rule_tx_counts)
    });
    setCanonicalVatRateOptions(
      Array.isArray(vatRateOptionsRes.data?.vat_rate_options)
        ? vatRateOptionsRes.data.vat_rate_options
            .map((x: any) => ({
              value: canonicalizeVatRate(String(x?.value || '')),
              label: String(x?.label || x?.value || '').trim()
            }))
            .filter((x: { value: string; label: string }) => x.value && x.label)
        : []
    );
    return { pendingSupplierProposals: supplierProposalList.length };
  }, [ownerGlobalScopeParams]);

  const runMerchantSupplierPreview = useCallback(async () => {
    setMerchantSupplierSyncBusy(true);
    setError('');
    setOperationProgressMessage('Merchant sync: analyzing uncommitted merchants…');
    try {
      const res = await api.post('/api/suppliers/merchant-supplier-sync/preview', {});
      const d = (res.data && typeof res.data === 'object' ? res.data : {}) as Record<string, unknown>;
      const rows = Array.isArray(d.aliases)
        ? (d.aliases as MerchantSupplierPreviewRow[]).map((r) => ({
            key: String(r.key || ''),
            display: String(r.display || ''),
            canonical: String(r.canonical || ''),
            tx_count: Number(r.tx_count || 0),
            already_exists: Boolean(r.already_exists),
            supplier_exists: Boolean(r.supplier_exists)
          }))
        : [];
      const summaryRaw = (d.summary && typeof d.summary === 'object' ? d.summary : {}) as Record<string, unknown>;
      const summary: Record<string, number> = {
        txs_scanned: Number(summaryRaw.txs_scanned || 0),
        merchants: Number(summaryRaw.merchants || 0),
        new_aliases: Number(summaryRaw.new_aliases || 0),
        existing_aliases: Number(summaryRaw.existing_aliases || 0),
        new_suppliers_estimate: Number(summaryRaw.new_suppliers_estimate || 0)
      };
      setMerchantSupplierPreviewRows(rows);
      setMerchantSupplierPreviewSummary(summary);
      setMerchantSupplierSelectedKeys(rows.filter((r) => r.key && !r.already_exists).map((r) => r.key));
      setMerchantSupplierPreviewOpen(true);
      setRulesAccordionOpen((prev) => ({ ...prev, merchantSuppliers: true }));
    } catch (err: any) {
      setError(err?.response?.data?.error || err?.message || 'Merchant supplier preview failed');
    } finally {
      setOperationProgressMessage(null);
      setMerchantSupplierSyncBusy(false);
    }
  }, [api]);

  const applyMerchantSupplierPreviewSelection = useCallback(async () => {
    const selected = merchantSupplierPreviewRows.filter(
      (r) => merchantSupplierSelectedKeys.includes(r.key) && !r.already_exists
    );
    if (selected.length === 0) {
      setError('Select at least one new merchant alias to apply.');
      return;
    }
    setMerchantSupplierApplyBusy(true);
    setError('');
    setOperationProgressMessage(`Merchant sync: applying ${selected.length.toLocaleString()} alias(es)…`);
    try {
      const res = await api.post('/api/suppliers/merchant-supplier-sync/apply', {
        apply_tags: true,
        aliases: selected.map((r) => ({ display: r.display, canonical: r.canonical }))
      });
      const d = (res.data && typeof res.data === 'object' ? res.data : {}) as Record<string, unknown>;
      const tagSummary =
        d.tag_summary && typeof d.tag_summary === 'object'
          ? (d.tag_summary as Record<string, unknown>)
          : {};
      setMerchantSupplierPreviewOpen(false);
      await refreshRulesTabDatasets();
      setRulesAccordionOpen((prev) => ({ ...prev, merchantSuppliers: true }));
      setSuccessMessage(
        `Merchant sync applied: ${Number(d.suppliers_added || 0).toLocaleString()} new suppliers, ` +
          `${Number(d.aliases_added || 0).toLocaleString()} new aliases, ` +
          `${Number(tagSummary.tags_updated || 0).toLocaleString()} tags updated ` +
          `(${Number(tagSummary.txs_tagged_scope || 0).toLocaleString()} uncommitted TX matches).`
      );
    } catch (err: any) {
      setError(err?.response?.data?.error || err?.message || 'Merchant supplier apply failed');
    } finally {
      setOperationProgressMessage(null);
      setMerchantSupplierApplyBusy(false);
    }
  }, [api, merchantSupplierPreviewRows, merchantSupplierSelectedKeys, refreshRulesTabDatasets]);

  const downloadRulesJsonExport = useCallback(async () => {
    setError('');
    setRulesJsonTransferBusy('export');
    try {
      const res = await api.post('/api/transaction-classification-rules/rules-json-export', rulesJsonExportPayload, {
        responseType: 'blob'
      });
      const blob =
        res.data instanceof Blob ? res.data : new Blob([res.data], { type: 'application/json;charset=utf-8' });
      const cd = (res.headers?.['content-disposition'] || res.headers?.['Content-Disposition'] || '') as string;
      let fname = 'family_finance_rules_backup.json';
      const m = /filename\*?=(?:UTF-8'')?["']?([^"';]+)["']?/i.exec(cd);
      if (m?.[1]) fname = decodeURIComponent(m[1].trim());
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = fname;
      a.rel = 'noopener';
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      setSuccessMessage('Rules export downloaded.');
    } catch (err: unknown) {
      const ax = err as { response?: { data?: unknown } };
      const d = ax.response?.data;
      if (d instanceof Blob) {
        try {
          const text = await d.text();
          try {
            const j = JSON.parse(text) as { error?: string };
            setError(String(j.error || text).trim() || 'Could not export rules');
          } catch {
            setError(text.trim().slice(0, 400) || 'Could not export rules');
          }
        } catch {
          setError(getHttpErrorMessageForUser(err, 'Could not export rules'));
        }
      } else {
        setError(getHttpErrorMessageForUser(err, 'Could not export rules'));
      }
    } finally {
      setRulesJsonTransferBusy(null);
    }
  }, [rulesJsonExportPayload]);

  const onRulesJsonImportFileSelected = useCallback(
    async (fileList: FileList | null) => {
      const file = fileList?.[0];
      if (!file) return;
      setError('');
      const ok = window.confirm(
        'Import rules from this JSON file?\n\n' +
          'New rules are appended to your account. Existing rules are not deleted.\n' +
          'Rows that exactly match a rule you already have are skipped.\n' +
          'Categories and suppliers must already exist under your login. The file includes IDs and names: ' +
          'import matches by ID first (most accurate on this account), then falls back to name if the ID ' +
          'is missing or wrong (e.g. after a rename or copying from another environment).\n\n' +
          'Use export files from Family Finance (Rules tab).'
      );
      if (!ok) return;
      setRulesJsonTransferBusy('import');
      try {
        const fd = new FormData();
        fd.append('file', file);
        const importRes = await api.post<{
          summary?: {
            classification_inserted?: number;
            classification_skipped_duplicate?: number;
            supplier_aliases_inserted?: number;
            supplier_aliases_skipped_duplicate?: number;
            vat_rules_inserted?: number;
            vat_rules_skipped_duplicate?: number;
            vat_rules_skipped_overlap?: number;
            special_case_inserted?: number;
            special_case_skipped_duplicate?: number;
            errors?: string[];
          };
        }>('/api/transaction-classification-rules/rules-json-import', fd, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });
        await refreshRulesTabDatasets();
        const s = importRes.data?.summary;
        const ins = s
          ? (s.classification_inserted || 0) +
            (s.supplier_aliases_inserted || 0) +
            (s.vat_rules_inserted || 0) +
            (s.special_case_inserted || 0)
          : 0;
        const sk = s
          ? (s.classification_skipped_duplicate || 0) +
            (s.supplier_aliases_skipped_duplicate || 0) +
            (s.vat_rules_skipped_duplicate || 0) +
            (s.vat_rules_skipped_overlap || 0) +
            (s.special_case_skipped_duplicate || 0)
          : 0;
        let sm = `Rules import finished: ${ins} new row(s) added.`;
        if (sk > 0) sm += ` ${sk} skipped (duplicate or overlap).`;
        const errTail = Array.isArray(s?.errors) ? s!.errors!.filter(Boolean).slice(0, 2).join(' ') : '';
        if (errTail) sm += ` ${errTail}`;
        setSuccessMessage(`${sm} Review the Rules tab.`);
        if (rulesJsonImportInputRef.current) rulesJsonImportInputRef.current.value = '';
      } catch (err: unknown) {
        const ax = err as { response?: { data?: unknown } };
        const d = ax.response?.data;
        if (d instanceof Blob) {
          try {
            const text = await d.text();
            try {
              const j = JSON.parse(text) as { error?: string };
              setError(String(j.error || text).trim() || 'Could not import rules');
            } catch {
              setError(text.trim().slice(0, 400) || 'Could not import rules');
            }
          } catch {
            setError(getHttpErrorMessageForUser(err, 'Could not import rules'));
          }
        } else {
          setError(getHttpErrorMessageForUser(err, 'Could not import rules'));
        }
      } finally {
        setRulesJsonTransferBusy(null);
      }
    },
    [refreshRulesTabDatasets]
  );

  /** Confirmed rules are hidden when Status = Proposed; bump to Confirmed so a newly saved rule appears under current text filters. */
  const promoteRulesStatusForConfirmedEdits = useCallback(() => {
    setRulesStatusFilter((prev) => (prev === 'proposed' ? 'confirmed' : prev));
  }, []);

  /** Target tx for Quickview footer "Generate Rule" (same API as toast): row details, else last re-run response. */
  const quickviewGenerateRuleTarget = useMemo(() => {
    if (unifiedMoreRow && typeof unifiedMoreRow.id === 'number' && unifiedMoreRow.id > 0) {
      return { txId: unifiedMoreRow.id, userId: unifiedMoreRow.user_id ?? null };
    }
    for (let i = unifiedRerunLogBlocks.length - 1; i >= 0; i -= 1) {
      const b = unifiedRerunLogBlocks[i];
      if (b.kind === 'run' && typeof b.txId === 'number' && b.txId > 0) {
        const row = unifiedRows.find((r) => r.id === b.txId);
        return { txId: b.txId, userId: row?.user_id ?? null };
      }
    }
    return null;
  }, [unifiedMoreRow, unifiedRerunLogBlocks, unifiedRows]);

  const showQuickviewGenerateRuleFooter = useMemo(() => {
    if (!quickviewGenerateRuleTarget) return false;
    return !proposalGenerateEmptySuppressedTxIds.includes(quickviewGenerateRuleTarget.txId);
  }, [quickviewGenerateRuleTarget, proposalGenerateEmptySuppressedTxIds]);

  useEffect(() => {
    const id = unifiedMoreRow?.id;
    if (typeof id !== 'number' || id <= 0) return;
    setProposalGenerateEmptySuppressedTxIds((prev) => prev.filter((x) => x !== id));
  }, [unifiedMoreRow?.id]);

  useEffect(() => {
    const len = unifiedRerunLogBlocks.length;
    if (len > rerunLogBlocksLenForSuppressRef.current) {
      const last = unifiedRerunLogBlocks[len - 1];
      if (last && last.kind === 'run') {
        setProposalGenerateEmptySuppressedTxIds((prev) => prev.filter((x) => x !== last.txId));
      }
    }
    rerunLogBlocksLenForSuppressRef.current = len;
  }, [unifiedRerunLogBlocks]);

  const handleGenerateRuleFromRerunToast = useCallback(async (txId: number, transactionOwnerUserId?: number | null) => {
    try {
      setError('');
      const uid = transactionOwnerUserId != null && Number.isFinite(Number(transactionOwnerUserId)) ? Number(transactionOwnerUserId) : null;
      const res = await postRuleProposalsV2(
        api,
        'classification',
        [txId],
        uid != null && Number(uid) > 0 ? { userId: Number(uid) } : undefined
      );
      const resData = (res.data || {}) as Record<string, unknown>;
      const createdIds = proposalIdListFromApi(resData, 'created_proposal_ids');
      const existingIds = proposalIdListFromApi(resData, 'existing_proposal_ids');
      const viewIds = createdIds.length > 0 ? createdIds : existingIds;
      const created = Number(res.data?.created ?? createdIds.length);
      const scanned = Number(res.data?.transactions_scanned ?? 1);

      await refreshRulesTabDatasets();

      const primaryProposalId = viewIds.length > 0 ? viewIds[0] : null;

      if (scanned <= 0) {
        setStackedToasts((prev) => [
          ...prev,
          {
            id: stackedToastIdRef.current++,
            code: nextClientToastCode(),
            severity: 'info',
            message: 'No valid transaction IDs were provided to generate proposals.'
          }
        ]);
      } else {
        const editSuggestions = editExistingRuleSuggestionsFromApi(resData);
        const preferRuleId = editSuggestions[0]?.rule_id ?? null;
        const skipHint = created > 0 ? '' : summarizeProposalGenerationSkipsFromApi(resData);
        const idHint =
          created > 0 && primaryProposalId != null
            ? ` ID filter P-C${primaryProposalId}${createdIds.length > 1 ? ` (first of ${createdIds.length})` : ''}.`
            : '';
        setStackedToasts((prev) => [
          ...prev,
          {
            id: stackedToastIdRef.current++,
            code: nextClientToastCode(),
            severity: created > 0 ? 'success' : 'info',
            message:
              created > 0
                ? `Generated ${created} proposed rule${created === 1 ? '' : 's'} from transaction #${txId}.${idHint} Review or accept on the Rules tab.`
                : `No new proposed rules were generated for transaction #${txId}.${skipHint}`,
            ...(preferRuleId != null
              ? {
                  actionLabel: 'View Rule',
                  onAction: () =>
                    navigateRulesTabToPrefixedId(formatPrefixedRuleId('classification', preferRuleId))
                }
              : viewIds.length > 0
                ? {
                    actionLabel: 'View Now',
                    onAction: () => openRulesTabForNewDraftProposals(viewIds, [])
                  }
                : {})
          }
        ]);
        if (created <= 0) {
          setProposalGenerateEmptySuppressedTxIds((prev) => (prev.includes(txId) ? prev : [...prev, txId]));
        } else {
          setProposalGenerateEmptySuppressedTxIds((prev) => prev.filter((id) => id !== txId));
        }
      }
    } catch (err: any) {
      const raw = String(err?.response?.data?.error || err?.message || '').trim();
      const diag = diagnosticsFromApiError(err);
      setStackedToasts((prev) => [
        ...prev,
        {
          id: stackedToastIdRef.current++,
          code: nextClientToastCode(),
          requestId: diag.requestId,
          jobId: diag.jobId,
          errorCode: diag.errorCode,
          severity: 'error',
          message: raw || `Failed to generate proposed rules for transaction #${txId}.`
        }
      ]);
    }
  }, [navigateRulesTabToPrefixedId, openRulesTabForNewDraftProposals, refreshRulesTabDatasets]);

  const generateIsolatedRuleProposalsForUnifiedRow = useCallback(
    async (row: UnifiedReviewRow) => {
      if (unifiedIsolateRulesBusyTxId != null) return;
      const txId = row.id;
      try {
        setUnifiedIsolateRulesBusyTxId(txId);
        setError('');
        const uid =
          row.user_id != null && Number.isFinite(Number(row.user_id)) && Number(row.user_id) > 0
            ? Number(row.user_id)
            : null;
        const [clsOutcome, supOutcome] = await Promise.allSettled([
          postRuleProposalsV2(
            api,
            'classification',
            [txId],
            uid != null && Number(uid) > 0 ? { userId: Number(uid) } : undefined
          ),
          postRuleProposalsV2(
            api,
            'supplier_alias',
            [txId],
            uid != null && Number(uid) > 0 ? { userId: Number(uid) } : undefined
          )
        ]);

        let clsCreated = 0;
        let clsIds: number[] = [];
        let clsViewIds: number[] = [];
        let clsData: Record<string, unknown> | undefined;
        if (clsOutcome.status === 'fulfilled') {
          const res = clsOutcome.value;
          clsData = (res.data || {}) as Record<string, unknown>;
          clsIds = proposalIdListFromApi(clsData, 'created_proposal_ids');
          const clsExisting = proposalIdListFromApi(clsData, 'existing_proposal_ids');
          clsViewIds = clsIds.length > 0 ? clsIds : clsExisting;
          clsCreated = Number(res.data?.created ?? clsIds.length);
        }

        let supCreated = 0;
        let supIds: number[] = [];
        let supViewIds: number[] = [];
        if (supOutcome.status === 'fulfilled') {
          const res = supOutcome.value;
          const supData = (res.data || {}) as Record<string, unknown>;
          supIds = proposalIdListFromApi(supData, 'created_proposal_ids');
          const supExisting = proposalIdListFromApi(supData, 'existing_proposal_ids');
          supViewIds = supIds.length > 0 ? supIds : supExisting;
          supCreated = Number(res.data?.created ?? supIds.length);
        }

        const clsRej = clsOutcome.status === 'rejected' ? clsOutcome.reason : null;
        const supRej = supOutcome.status === 'rejected' ? supOutcome.reason : null;
        if (clsRej || supRej) {
          const clsMsg = clsRej
            ? getHttpErrorMessageForUser(clsRej, 'Classification draft request failed')
            : '';
          const supMsg = supRej
            ? getHttpErrorMessageForUser(supRej, 'Supplier draft request failed')
            : '';
          const diag = diagnosticsFromApiError(clsRej ?? supRej ?? '');
          let message: string;
          if (clsRej && supRej) {
            message =
              clsMsg === supMsg
                ? `Transaction #${txId}: draft rule generation failed for both classification and supplier (${clsMsg}). This row fires two parallel API calls — one toast summarizes both failures.`
                : `Transaction #${txId}: classification draft (${clsMsg}) · supplier draft (${supMsg}).`;
          } else if (clsRej) {
            message = `Transaction #${txId}: classification draft generation failed — ${clsMsg}`;
          } else {
            message = `Transaction #${txId}: supplier draft generation failed — ${supMsg}`;
          }
          setStackedToasts((prev) => [
            ...prev,
            {
              id: stackedToastIdRef.current++,
              code: nextClientToastCode(),
              requestId: diag.requestId,
              jobId: diag.jobId,
              errorCode: diag.errorCode,
              severity: 'error',
              message
            }
          ]);
        }

        await refreshRulesTabDatasets();

        const clsOk = clsOutcome.status === 'fulfilled';
        const supOk = supOutcome.status === 'fulfilled';

        const editSuggestions = clsOk ? editExistingRuleSuggestionsFromApi(clsData) : [];
        const preferRuleId = editSuggestions[0]?.rule_id ?? null;
        const skipHint = clsOk && clsCreated <= 0 ? summarizeProposalGenerationSkipsFromApi(clsData) : '';
        const totalNew = clsCreated + supCreated;
        const canViewExistingOrNew = clsViewIds.length > 0 || supViewIds.length > 0;
        if (clsOk || supOk) {
          const countParts = [
            clsOk ? `${clsCreated} classification` : null,
            supOk ? `${supCreated} supplier` : null
          ].filter(Boolean);
          const countReadout = countParts.join(' and ');
          setStackedToasts((prev) => [
            ...prev,
            {
              id: stackedToastIdRef.current++,
              code: nextClientToastCode(),
              severity: totalNew > 0 ? 'success' : 'info',
              message:
                totalNew > 0
                  ? `Added ${countReadout} draft rule(s) for transaction #${txId}. Review or accept on the Rules tab, then use Re-run all rules on this row to apply tags.${skipHint ? ` ${skipHint}` : ''}`
                  : `No new draft rules were created for transaction #${txId}.${skipHint}`,
              ...(preferRuleId != null
                ? {
                    actionLabel: 'View Rule',
                    onAction: () =>
                      navigateRulesTabToPrefixedId(formatPrefixedRuleId('classification', preferRuleId))
                  }
                : canViewExistingOrNew
                  ? {
                      actionLabel: 'View Now',
                      onAction: () => openRulesTabForNewDraftProposals(clsViewIds, supViewIds)
                    }
                  : {})
            }
          ]);
        }
      } catch (err: any) {
        const message = getHttpErrorMessageForUser(
          err,
          `Failed to generate isolated draft rules for transaction #${txId}.`
        );
        setStackedToasts((prev) => [
          ...prev,
          {
            id: stackedToastIdRef.current++,
            code: nextClientToastCode(),
            severity: 'error',
            message: `Transaction #${txId}: ${message}`
          }
        ]);
      } finally {
        setUnifiedIsolateRulesBusyTxId(null);
      }
    },
    [
      navigateRulesTabToPrefixedId,
      openRulesTabForNewDraftProposals,
      refreshRulesTabDatasets,
      unifiedIsolateRulesBusyTxId
    ]
  );

  const runUnifiedRowRerunAllRules = useCallback(async (row: UnifiedReviewRow) => {
    if (unifiedActionBusyTxId != null) return;
    try {
      setUnifiedActionBusy(true);
      setUnifiedActionBusyTxId(row.id);
      setError('');
      const confirmed = await ensureRerunConfirmedForModes([row.id], row.user_id);
      if (!confirmed) {
        return;
      }
      await applyFrequencyDraftIfNeeded(row.id);
      const queuedTs = new Date().toLocaleTimeString();
      setUnifiedRerunLogOpen(true);
      setUnifiedRerunLogBlocks((prev) => [
        ...prev,
        {
          kind: 'request',
          txId: row.id,
          timeLabel: queuedTs,
          pipelineLine:
            'POST /bulk-reprocess-transactions (full pipeline: strip classification tags, heading + granular rules, supplier refresh, VAT, special case)…'
        }
      ]);
      const res = await api.post(
        '/api/transaction-classification-rules/bulk-reprocess-transactions',
        {
          ...(row.user_id != null ? { user_id: row.user_id } : {}),
          revolut_transaction_ids: [row.id],
          include_tx_diagnostics: true,
          apply_classification_rules: true,
          strip_all_classification_tags: true,
          refresh_supplier_names: true,
          apply_vat_rules: true,
          apply_special_case_rules: true
        },
        { timeout: HEAVY_RULE_JOB_TIMEOUT_MS }
      );
      const d = res.data as Record<string, any>;
      for (let attempt = 0; attempt < 8; attempt += 1) {
        const observed = await refreshUnifiedRowAfterRerun(row);
        if (observed) break;
        await new Promise((resolve) => setTimeout(resolve, 250));
      }
      const diagRows = Array.isArray(d.tx_diagnostics) ? d.tx_diagnostics : [];
      const diagForTx = diagRows.find((x) => Number(x?.transaction_id) === row.id) || diagRows[0];
      const diagObj = (diagForTx || null) as Record<string, any> | null;
      const diagSummary = diagForTx ? summarizeSingleTxDiagnosticText(diagForTx) : '';
      const notificationLines = buildRerunNotificationLines(row.id, d, diagObj);
      const comparison = buildRerunComparisonReadout(row.id, d, diagObj);
      setUnifiedRerunLogBlocks((prev) => [
        ...prev,
        {
          kind: 'run',
          txId: row.id,
          responseTimeLabel: new Date().toLocaleTimeString(),
          d,
          diag: diagObj,
          diagnosticSentence: diagSummary,
          notificationLines,
          comparison
        }
      ]);
      if (shouldForceFullRefreshFromDiagnostic(diagForTx)) {
        await fetchData();
      } else {
        // Align with bulk reprocess: merge this tx's canonical /review-rows row (status, VAT summary,
        // applied-tag histogram fields), refresh manual/tagged queues without replacing the whole
        // canonical table with page 0 only, and bump the tag-fetch epoch so Applied tags + VAT chips
        // reload from GET /revolut-transaction-category-tags (otherwise row.status can update from
        // partial merges while chips stay stale until top Refresh).
        if (canonicalHybridEnabled) {
          await refreshCanonicalRowsForTransactionIds(0, [row.id]);
        }
        await refreshTransactionReviewCore({ skipCanonical: canonicalHybridEnabled });
        setUnifiedTxCategoryTagsFetchEpoch((n) => n + 1);
      }
      setStackedToasts((prev) => [
        ...prev,
        {
          id: stackedToastIdRef.current++,
          code: nextClientToastCode(),
          severity: 'success',
          message: `Transaction #${row.id}: re-run finished`,
          rerunSummaryLines: notificationLines,
          actionLabel: 'Generate Rule',
          onAction: () => {
            void handleGenerateRuleFromRerunToast(row.id, row.user_id);
          }
        }
      ]);
    } catch (err: any) {
      const raw = String(err?.response?.data?.error || err?.message || '').trim();
      if (raw.toLowerCase().includes('committed')) {
        const shouldUncommit = window.confirm(
          `Transaction #${row.id} is committed. Uncommit it now so rules can run?`
        );
        if (shouldUncommit) {
          try {
            await api.post('/api/transaction-classification-rules/uncommit-classification', {
              ...(row.user_id != null ? { user_id: row.user_id } : {}),
              revolut_transaction_ids: [row.id]
            });
            setStackedToasts((prev) => [
              ...prev,
              {
                id: stackedToastIdRef.current++,
                code: nextClientToastCode(),
                severity: 'success',
                message: `Transaction #${row.id} uncommitted.`
              }
            ]);
            await fetchData();
            return;
          } catch (unlockErr: any) {
            setStackedToasts((prev) => [
              ...prev,
              {
                id: stackedToastIdRef.current++,
                code: nextClientToastCode(),
                severity: 'error',
                message: `Transaction #${row.id}: could not uncommit (${unlockErr?.response?.data?.error || unlockErr?.message || 'unknown error'}).`
              }
            ]);
            return;
          }
        } else {
          setUnifiedRerunLogBlocks((prev) => [
            ...prev,
            { kind: 'note', variant: 'warning', text: 'Stopped: transaction is committed; uncommit was declined.' }
          ]);
        }
      }
      const fallback =
        `TX ${row.id}: Could not re-run rules for this transaction. Please try again; if it keeps failing, click Refresh once.`;
      const bulkHint =
        raw && raw.toLowerCase().includes('failed to bulk reprocess')
          ? ' This is a server-side rule re-run failure (not the same as blocking commit for incomplete tags).'
          : '';
      const rerunError = raw
        ? `TX ${row.id}: Could not re-run rules for this transaction: ${raw}${bulkHint}`
        : fallback;
      setUnifiedRerunLogBlocks((prev) => [
        ...prev,
        {
          kind: 'note',
          variant: 'error',
          timeLabel: new Date().toLocaleTimeString(),
          text: rerunError
        }
      ]);
      setStackedToasts((prev) => [
        ...prev,
        { id: stackedToastIdRef.current++, code: nextClientToastCode(), severity: 'error', message: rerunError }
      ]);
    } finally {
      setUnifiedEditingTxIds((prev) => prev.filter((id) => id !== row.id));
      setUnifiedActionBusyTxId(null);
      setUnifiedActionBusy(false);
    }
  }, [
    applyFrequencyDraftIfNeeded,
    ensureRerunConfirmedForModes,
    fetchData,
    refreshCanonicalRowsForTransactionIds,
    refreshTransactionReviewCore,
    refreshUnifiedRowAfterRerun,
    shouldForceFullRefreshFromDiagnostic,
    handleGenerateRuleFromRerunToast,
    canonicalHybridEnabled,
    unifiedActionBusyTxId
  ]);

  const runUnifiedBulkConfirm = async () => {
    const selectedRows = selectedUnifiedRowKeys
      .map((k) => unifiedRowsByKey.get(k))
      .filter((r): r is UnifiedReviewRow => !!r);
    if (!selectedRows.length) {
      setError('Select one or more rows first.');
      return;
    }
    const bulkHadFrequencyDraft = selectedRows.some((r) => r.id in selectedFrequencyByTx);
    let progressToastId: number | null = null;
    try {
      setUnifiedBulkBusy(true);
      setUnifiedCommitBusyTxIds(new Set(selectedRows.map((r) => r.id)));
      setError('');
      progressToastId = beginTxReviewBulkActionWithToast(
        `Committing ${selectedRows.length} selected row(s)…`
      );
      let autoProposalsCreated = 0;
      let bulkCommitCancelled = false;
      const multiRowBulk = selectedRows.length > 1;
      const classifyOpts: UnifiedClassifyOverrides | undefined = multiRowBulk
        ? { lightweightForBulkCommit: true }
        : undefined;
      for (const row of selectedRows) {
        if (txReviewBulkCancelRequestedRef.current) {
          bulkCommitCancelled = true;
          break;
        }
        await applyFrequencyDraftIfNeeded(row.id, { skipRefetch: true });
        if (row.source_state !== 'confirmed') {
          await classifyIfNeeded(row, classifyOpts);
          if (!multiRowBulk && row.source_state !== 'tagged_by_rules') {
            const tx = unifiedTxById.get(row.id);
            if (tx) {
              const picked = pickedCategoryForTx(tx);
              if (picked != null) {
                try {
                  if (await createAutoRuleProposalIfNeeded(tx, picked)) {
                    autoProposalsCreated += 1;
                  }
                } catch {
                  // Non-blocking: confirmation should still complete even if a proposal fails for one row.
                }
              }
            }
          }
        }
      }
      let confirmedIds: number[] = [];
      if (!bulkCommitCancelled) {
        const confirmIds = selectedRows
          .filter((r) => r.source_state !== 'confirmed')
          .map((r) => r.id);
        if (confirmIds.length > 0) {
          const cr = await confirmClassificationIds(confirmIds);
          confirmedIds = cr.confirmedIds;
        }
        if (autoProposalsCreated > 0) {
          const pr = await api.get('/api/transaction-classification-rules/rule-proposals');
          const praw = pr.data as RuleProposalsPayload | undefined;
          setProposalsPayload({
            proposals: praw?.proposals ?? [],
            count_atlas_virtual: praw?.count_atlas_virtual,
            count_saved_pending: praw?.count_saved_pending,
            count_saved_rejected: Number(praw?.count_saved_rejected || 0),
          });
          setSuccessMessage(
            `Committed ${confirmedIds.length} transaction(s) and created ${autoProposalsCreated} pending rule proposal(s).`
          );
        }
        setSelectedUnifiedRowKeys([]);
        setUnifiedEditingTxIds([]);
        if (confirmedIds.length > 0) {
          if (canonicalHybridEnabled) {
            setCanonicalRowsPayload((prev) =>
              applyCommittedIdsToCanonicalPayload(prev, confirmedIds, hideCommittedUnifiedRows)
            );
          } else {
            await refreshTransactionReviewCore();
          }
        }
        void fetchPipelineCounts();
        // Per-row frequency patches used to call fetchData() each time — dozens of rows could mean
        // dozens of full review loads, 400s, and timeouts. Refresh once after bulk if drafts applied.
        if (bulkHadFrequencyDraft) {
          await fetchData();
        }
      } else {
        setSuccessMessage('Commit cancelled. Remaining rows were not processed or confirmed.');
        void fetchPipelineCounts();
      }
    } catch (err: any) {
      setError(err?.response?.data?.error || err?.message || 'Bulk action failed');
    } finally {
      dismissTxReviewBulkToast(progressToastId);
      setUnifiedBulkBusy(false);
      setUnifiedCommitBusyTxIds(new Set());
    }
  };

  const runUnifiedBulkUncommit = async () => {
    const selectedRows = selectedUnifiedRowKeys
      .map((k) => unifiedRowsByKey.get(k))
      .filter((r): r is UnifiedReviewRow => !!r);
    if (!selectedRows.length) {
      setError('Select one or more rows first.');
      return;
    }
    const committedRows = selectedRows.filter((r) => isUnifiedRowCommitted(r));
    if (!committedRows.length) {
      setError('No committed rows in the selection.');
      return;
    }
    const ok = window.confirm(
      `Uncommit ${committedRows.length} transaction(s)? They return to the classification review queue and tags can be edited again.`
    );
    if (!ok) return;
    let progressToastId: number | null = null;
    try {
      setUnifiedBulkUncommitBusy(true);
      setError('');
      progressToastId = beginTxReviewBulkActionWithToast(
        `Uncommitting ${committedRows.length} selected row(s)…`
      );
      const { uncommittedIds } = await uncommitClassificationIds(committedRows.map((r) => r.id));
      setSelectedUnifiedRowKeys([]);
      setUnifiedEditingTxIds([]);
      if (uncommittedIds.length > 0) {
        if (canonicalHybridEnabled) {
          await refreshTransactionReviewCore();
          // Refresh can return stale scope totals; background hydrate may keep paging against an old count_total.
          canonicalHydrateGenRef.current += 1;
          const hydrateAfterUncommitGen = canonicalHydrateGenRef.current;
          setCanonicalRowsPayload((prev) => {
            const next = applyUncommittedIdsToCanonicalPayload(
              prev,
              uncommittedIds,
              canonicalRowsCommittedMode === 'only'
            );
            if (next && next !== prev) {
              const tot = Number(next.count_total || 0);
              const off = Array.isArray(next.rows) ? next.rows.length : 0;
              if (off > 0 && tot > off && hydrateAfterUncommitGen === canonicalHydrateGenRef.current) {
                void hydrateCanonicalRowsInBackground(
                  canonicalRowsCommittedMode,
                  next,
                  hydrateAfterUncommitGen
                );
              }
            }
            return next;
          });
        } else {
          await fetchData();
        }
      }
      void fetchPipelineCounts();
    } catch (err: any) {
      setError(err?.response?.data?.error || err?.message || 'Bulk uncommit failed');
    } finally {
      dismissTxReviewBulkToast(progressToastId);
      setUnifiedBulkUncommitBusy(false);
    }
  };

  const openUnifiedRowJsonViewer = useCallback(async (row: UnifiedReviewRow) => {
    const gen = ++txJsonViewerFetchGenRef.current;
    setTxJsonViewerOpen(true);
    setTxJsonViewerBusy(true);
    setTxJsonViewerError('');
    setTxJsonViewerTagsError('');
    setTxJsonViewerAiError('');
    setTxJsonViewerAiSummary(null);
    setTxJsonViewerAiBusy(true);
    setTxJsonViewerNeighbors(null);
    setTxJsonViewerNeighborsBusy(true);
    setTxJsonViewerNeighborsError('');
    setTxJsonViewerSectionsExpanded({
      keySummary: false,
      aiSummary: false,
      neighbors: false,
      tags: false,
      rawJson: false
    });
    setTxJsonViewerContent('');
    setTxJsonViewerTitle(`Revolut JSON — TX ${row.id}`);
    setTxJsonViewerContextRow(row);
    setTxJsonViewerCategoryTags(null);
    setTxJsonViewerAppliedRules(null);
    setTxJsonViewerAppliedRulesProbabilistic(null);
    setTxJsonViewerAppliedRulesError('');

    const tx = unifiedTxById.get(row.id);
    const nestedRevolutId = String((row as CanonicalReviewRow).transaction?.revolut_id || '').trim();
    const revolutId = (tx?.revolut_id || row.revolut_id || nestedRevolutId || '').trim();
    const completeParams = revolutId ? { revolut_id: revolutId } : { transaction_id: row.id };

    const tagParams: Record<string, string | number> = { revolut_transaction_id: row.id };
    if ((user?.role === 'admin' || user?.role === 'owner') && row.user_id != null) {
      tagParams.user_id = Number(row.user_id);
    }

    try {
      const [completeSettled, tagsSettled, aiSettled, neighborsSettled, appliedRulesSettled] =
        await Promise.allSettled([
        api.get('/api/revolut/transactions/complete', { params: completeParams }),
        api.get('/api/revolut-transaction-category-tags/', { params: tagParams }),
        api.get(`/api/revolut/transactions/complete/${row.id}/ai-summary`),
        api.get(`/api/revolut/transactions/complete/${row.id}/neighbors`, { params: { limit: 5 } }),
        api.get(`/api/revolut/transactions/complete/${row.id}/applied-rules`)
      ]);

      if (gen !== txJsonViewerFetchGenRef.current) return;

      if (completeSettled.status === 'fulfilled') {
        const payload = completeSettled.value.data?.transaction ?? completeSettled.value.data;
        setTxJsonViewerTitle(
          revolutId ? `Revolut JSON — ${revolutId}` : `Revolut JSON — TX ${row.id} (db id)`
        );
        setTxJsonViewerContent(JSON.stringify(payload, null, 2));
      } else {
        const err: any = completeSettled.reason;
        setTxJsonViewerError(err?.response?.data?.error || err?.message || 'Could not load Revolut JSON');
      }

      if (tagsSettled.status === 'fulfilled') {
        const raw = tagsSettled.value.data?.tags;
        setTxJsonViewerCategoryTags(Array.isArray(raw) ? raw : []);
      } else {
        const err: any = tagsSettled.reason;
        setTxJsonViewerCategoryTags([]);
        setTxJsonViewerTagsError(err?.response?.data?.error || err?.message || 'Could not load category tags');
      }

      if (aiSettled.status === 'fulfilled') {
        const aiData = aiSettled.value.data || null;
        setTxJsonViewerAiSummary(aiData);
        setTxJsonViewerAiError('');
        const urlFromAi = String(aiData?.merchant_likely_url || '').trim();
        if (urlFromAi.startsWith('http')) {
          // Keep the URL Link column in sync after on-demand / pipeline persist.
          setCanonicalRowsPayload((prev) => {
            if (!prev) return prev;
            const rows = Array.isArray(prev.rows) ? prev.rows : [];
            let changed = false;
            const nextRows = rows.map((r) => {
              if (Number(r.id) !== Number(row.id)) return r;
              if (String(r.merchant_likely_url || '').trim() === urlFromAi) return r;
              changed = true;
              return { ...r, merchant_likely_url: urlFromAi };
            });
            return changed ? { ...prev, rows: nextRows } : prev;
          });
        }
      } else {
        const err: any = aiSettled.reason;
        setTxJsonViewerAiSummary(null);
        setTxJsonViewerAiError(err?.response?.data?.error || err?.message || 'Could not load AI summary');
      }

      if (neighborsSettled.status === 'fulfilled') {
        const data = neighborsSettled.value.data || {};
        setTxJsonViewerNeighbors({
          preceding: Array.isArray(data.preceding) ? data.preceding : [],
          subsequent: Array.isArray(data.subsequent) ? data.subsequent : [],
          current: data.current || null
        });
        setTxJsonViewerNeighborsError('');
      } else {
        const err: any = neighborsSettled.reason;
        setTxJsonViewerNeighbors({ preceding: [], subsequent: [] });
        setTxJsonViewerNeighborsError(
          err?.response?.data?.error || err?.message || 'Could not load neighbouring transactions'
        );
      }

      if (appliedRulesSettled.status === 'fulfilled') {
        const raw = appliedRulesSettled.value.data?.applied_rules;
        setTxJsonViewerAppliedRules(Array.isArray(raw) ? raw : []);
        const pMeta = appliedRulesSettled.value.data?.probabilistic;
        setTxJsonViewerAppliedRulesProbabilistic(
          pMeta && typeof pMeta === 'object' ? (pMeta as TxJsonProbabilisticMeta) : null
        );
        setTxJsonViewerAppliedRulesError('');
      } else {
        const err: any = appliedRulesSettled.reason;
        setTxJsonViewerAppliedRules([]);
        setTxJsonViewerAppliedRulesProbabilistic(null);
        setTxJsonViewerAppliedRulesError(
          err?.response?.data?.error || err?.message || 'Could not load applied rules'
        );
      }
    } catch (err: any) {
      if (gen !== txJsonViewerFetchGenRef.current) return;
      setTxJsonViewerError(err?.response?.data?.error || err?.message || 'Could not load Revolut JSON');
      setTxJsonViewerCategoryTags([]);
      setTxJsonViewerAppliedRules([]);
      setTxJsonViewerAppliedRulesProbabilistic(null);
      setTxJsonViewerAiSummary(null);
      setTxJsonViewerNeighbors(null);
    } finally {
      if (gen === txJsonViewerFetchGenRef.current) {
        setTxJsonViewerBusy(false);
        setTxJsonViewerAiBusy(false);
        setTxJsonViewerNeighborsBusy(false);
      }
    }
  }, [unifiedTxById, user?.role]);

  const txJsonViewerParsed = useMemo(() => {
    if (!txJsonViewerContent.trim()) return null;
    try {
      return JSON.parse(txJsonViewerContent) as Record<string, any>;
    } catch {
      return null;
    }
  }, [txJsonViewerContent]);

  const txJsonViewerKeyData = useMemo(() => {
    if (!txJsonViewerParsed) return [] as Array<{ label: string; value: string; variant?: 'pill' }>;
    const payload = txJsonViewerParsed as Record<string, any>;
    const parseMaybeObj = (value: unknown): Record<string, any> => {
      if (value && typeof value === 'object' && !Array.isArray(value)) {
        return value as Record<string, any>;
      }
      if (typeof value === 'string' && value.trim()) {
        try {
          const parsed = JSON.parse(value);
          if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) {
            return parsed as Record<string, any>;
          }
        } catch {
          /* ignore non-JSON strings */
        }
      }
      return {};
    };
    const merchantObj = parseMaybeObj(payload.merchant);
    const merchantDataObj = parseMaybeObj(payload.merchant_data);
    /** Revolut DB/API mixes `merchant`, `merchant_data`, and top-level merchant_* fields. */
    const merchant = { ...merchantDataObj, ...merchantObj };
    const cardObj = parseMaybeObj(payload.card);
    const cardDataObj = parseMaybeObj(payload.card_data);
    const card = { ...cardDataObj, ...cardObj };
    const firstLeg =
      Array.isArray(payload.legs) && payload.legs.length > 0 && payload.legs[0] && typeof payload.legs[0] === 'object'
        ? (payload.legs[0] as Record<string, any>)
        : {};
    const firstLegData =
      Array.isArray(payload.legs_data) &&
      payload.legs_data.length > 0 &&
      payload.legs_data[0] &&
      typeof payload.legs_data[0] === 'object'
        ? (payload.legs_data[0] as Record<string, any>)
        : {};
    const rows: Array<{ label: string; value: string; variant?: 'pill' }> = [];
    const add = (label: string, value: unknown) => {
      const text = value == null ? '' : String(value).trim();
      if (!text || text.toLowerCase() === 'null' || text.toLowerCase() === 'undefined') return;
      rows.push({ label, value: text });
    };
    const coalesceDisplayString = (...candidates: unknown[]): string => {
      for (const c of candidates) {
        const text = c == null ? '' : String(c).trim();
        if (text && text.toLowerCase() !== 'null' && text.toLowerCase() !== 'undefined') return text;
      }
      return '';
    };
    const joinName = (first: unknown, last: unknown): string =>
      [first, last]
        .map((p) => (p == null ? '' : String(p).trim()))
        .filter(Boolean)
        .join(' ')
        .trim();
    {
      // Internal ledger id (e.g. 15162) — distinct from Revolut's UUID.
      const dbId = coalesceDisplayString(
        payload.id,
        txJsonViewerContextRow?.id,
        (txJsonViewerContextRow as CanonicalReviewRow | null)?.transaction?.id
      );
      rows.push({ label: 'TX ID', value: dbId || ' - ' });
    }
    add('Revolut ID', payload.revolut_id);
    const stateRaw = payload.state;
    const stateTxt = stateRaw == null ? '' : String(stateRaw).trim();
    if (stateTxt && stateTxt.toLowerCase() !== 'null' && stateTxt.toLowerCase() !== 'undefined') {
      rows.push({ label: 'State', value: stateTxt, variant: 'pill' });
    }
    add('Type', payload.type);
    {
      const dateRaw = coalesceDisplayString(
        payload.completed_at,
        payload.created_at_revolut,
        payload.created_at,
        payload.display_date
      );
      if (dateRaw) {
        rows.push({ label: 'Date', value: formatTxJsonPanelDateTime(dateRaw) });
      }
    }
    add('Amount', payload.amount ?? firstLeg.amount);
    add('Currency', payload.currency ?? firstLeg.currency);
    {
      // Revolut FX card payments: bill_* = original merchant charge; amount/currency = account debit.
      const billAmount = coalesceDisplayString(
        payload.bill_amount,
        firstLeg.bill_amount,
        firstLegData.bill_amount
      );
      const billCurrency = coalesceDisplayString(
        payload.bill_currency,
        firstLeg.bill_currency,
        firstLegData.bill_currency
      );
      const accountCurrency = coalesceDisplayString(
        payload.currency,
        firstLeg.currency,
        firstLegData.currency
      );
      const hasDistinctBill =
        Boolean(billAmount) &&
        Boolean(billCurrency) &&
        (billCurrency.toUpperCase() !== (accountCurrency || '').toUpperCase() ||
          billAmount !== String(payload.amount ?? firstLeg.amount ?? '').trim());
      if (hasDistinctBill) {
        rows.push({ label: 'Original amount', value: billAmount });
        rows.push({ label: 'Original currency', value: billCurrency });
      }
    }
    {
      const merchantSummary = coalesceDisplayString(
        merchant.name,
        merchantObj.name,
        merchantDataObj.name,
        payload.merchant_name,
        firstLeg.merchant_name,
        firstLegData.merchant_name,
        payload.counterparty_name,
        payload.counterparty?.name
      );
      rows.push({ label: 'Merchant', value: merchantSummary || ' - ' });
    }
    {
      const cardholder = coalesceDisplayString(
        joinName(card.first_name, card.last_name),
        joinName(cardDataObj.first_name, cardDataObj.last_name),
        joinName(payload.card_holder_first_name, payload.card_holder_last_name),
        joinName(payload.card_holder?.first_name, payload.card_holder?.last_name)
      );
      rows.push({ label: 'Cardholder', value: cardholder || ' - ' });
    }
    {
      const city = coalesceDisplayString(merchant.city, merchantDataObj.city, payload.merchant_city);
      const country = coalesceDisplayString(
        merchant.country,
        merchantDataObj.country,
        payload.merchant_country
      );
      const location = [city, country].filter(Boolean).join(', ');
      rows.push({ label: 'Location', value: location || ' - ' });
    }
    add('Category code', merchant.category_code || payload.merchant_category_code);
    add('Description', payload.description);
    add('Reference', payload.reference);
    add('Account source', payload.account_source);
    add('Card last4', payload.card_last4 || payload.card?.last4 || card.card_number);
    add('Counterparty', payload.counterparty_name || payload.counterparty?.name);
    const derivedMeta = (payload.derived_metadata || {}) as Record<string, any>;
    if (derivedMeta.is_card_validation) {
      rows.push({ label: 'Metadata', value: 'Card validation', variant: 'pill' });
      add('Metadata signals', Array.isArray(derivedMeta.signals) ? derivedMeta.signals.join(', ') : '');
    }
    return rows;
  }, [txJsonViewerParsed, txJsonViewerContextRow]);

  /** Split API title so long UUIDs don’t hide the close control or clip in the drawer header. */
  const txJsonViewerDrawerTitleParts = useMemo(() => {
    const t = (txJsonViewerTitle || '').trim();
    if (!t) return { headline: 'Revolut JSON', detail: '' };
    const sep = ' — ';
    const i = t.indexOf(sep);
    if (i < 0) {
      return { headline: t.length > 56 ? `${t.slice(0, 53)}…` : t, detail: '' };
    }
    const headline = (t.slice(0, i).trim() || 'Revolut JSON').trim();
    const detail = t.slice(i + sep.length).trim();
    return { headline, detail };
  }, [txJsonViewerTitle]);

  const renderRevolutJsonInspectorInner = useCallback(() => {
    return (
      <>
        {txJsonViewerBusy ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
            <CircularProgress size={28} />
          </Box>
        ) : null}
        {txJsonViewerError ? (
          <Alert severity="error" sx={{ mb: 2 }}>
            {txJsonViewerError}
          </Alert>
        ) : null}
        {!txJsonViewerBusy && !txJsonViewerError ? (
          !txJsonViewerContent.trim() ? (
            <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.55 }}>
              No Revolut JSON loaded yet. In the transaction table, use a row&apos;s <strong>Revolut JSON</strong> action
              (or equivalent) to fetch the payload and classification tags — they will appear here and in the JSON side panel.
            </Typography>
          ) : (
            <Stack spacing={1.25}>
              <Accordion
                disableGutters
                expanded={txJsonViewerSectionsExpanded.keySummary}
                onChange={(_, expanded) =>
                  setTxJsonViewerSectionsExpanded((prev) => ({ ...prev, keySummary: expanded }))
                }
                sx={TX_JSON_VIEWER_SECTION_ACCORDION_SX}
              >
                <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={{ minHeight: 40, px: 1.5 }}>
                  <Typography variant="subtitle2">1) Key transaction summary</Typography>
                </AccordionSummary>
                <AccordionDetails sx={{ pt: 0, px: 1.5, pb: 1.5 }}>
                {txJsonViewerParsed ? (
                  txJsonViewerKeyData.length > 0 ? (
                    <Box
                      sx={{
                        display: 'grid',
                        gridTemplateColumns: { xs: '1fr', sm: '180px 1fr' },
                        gap: 1,
                        p: 1.5,
                        borderRadius: 1,
                        backgroundColor: 'background.paper',
                        border: '1px solid',
                        borderColor: 'grey.200'
                      }}
                    >
                      {txJsonViewerKeyData.map((item) => (
                        <React.Fragment key={`tx-json-summary-${item.label}`}>
                          <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 700 }}>
                            {item.label}
                          </Typography>
                          {item.variant === 'pill' ? (
                            <Box sx={{ display: 'flex', alignItems: 'center' }}>
                              <Chip
                                size="small"
                                variant="outlined"
                                label={formatRevolutStateFilterLabel(item.value)}
                                color={revolutStateSummaryChipColor(item.value)}
                              />
                            </Box>
                          ) : (
                            <Typography variant="body2" sx={{ wordBreak: 'break-word' }}>
                              {item.value}
                            </Typography>
                          )}
                        </React.Fragment>
                      ))}
                    </Box>
                  ) : (
                    <Typography variant="body2" color="text.secondary">
                      No key fields were detected in this payload.
                    </Typography>
                  )
                ) : (
                  <Alert severity="warning" sx={{ mb: 1 }}>
                    Could not parse JSON for summary view. Raw JSON is still shown below.
                  </Alert>
                )}
                </AccordionDetails>
              </Accordion>
              <Accordion
                disableGutters
                expanded={txJsonViewerSectionsExpanded.aiSummary}
                onChange={(_, expanded) =>
                  setTxJsonViewerSectionsExpanded((prev) => ({ ...prev, aiSummary: expanded }))
                }
                sx={TX_JSON_VIEWER_SECTION_ACCORDION_SX}
              >
                <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={{ minHeight: 40, px: 1.5 }}>
                  <Typography variant="subtitle2">2) AI Summary</Typography>
                </AccordionSummary>
                <AccordionDetails sx={{ pt: 0, px: 1.5, pb: 1.5 }}>
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1.25, lineHeight: 1.5 }}>
                  LLM narrative grounded on ledger facts, a web search for a likely merchant URL, and stored
                  probabilistic tags (when scored).
                </Typography>
                {txJsonViewerAiBusy ? (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 1 }}>
                    <CircularProgress size={18} />
                    <Typography variant="body2" color="text.secondary">
                      Generating AI summary…
                    </Typography>
                  </Box>
                ) : null}
                {txJsonViewerAiError ? (
                  <Alert severity="warning" sx={{ mb: 1 }}>
                    {txJsonViewerAiError}
                  </Alert>
                ) : null}
                {!txJsonViewerAiBusy && !txJsonViewerAiError && txJsonViewerAiSummary ? (
                  <Box
                    sx={{
                      p: 1.5,
                      borderRadius: 1,
                      backgroundColor: 'background.paper',
                      border: '1px solid',
                      borderColor: 'grey.200'
                    }}
                  >
                    {(() => {
                      const narrative = String(txJsonViewerAiSummary.narrative || '').trim();
                      const paragraphs = narrative
                        ? narrative.split(/\n\s*\n/).map((p) => p.replace(/\s*\n\s*/g, ' ').trim()).filter(Boolean)
                        : [];
                      if (paragraphs.length === 0) {
                        return (
                          <Typography variant="body2" color="text.secondary">
                            No narrative returned.
                          </Typography>
                        );
                      }
                      return (
                        <Stack spacing={1.25}>
                          {paragraphs.map((para, idx) => (
                            <Typography key={`ai-sum-p-${idx}`} variant="body2" sx={{ lineHeight: 1.55 }}>
                              {para}
                            </Typography>
                          ))}
                        </Stack>
                      );
                    })()}
                    {(() => {
                      const urls: Array<{ href: string; label: string }> = [];
                      const seen = new Set<string>();
                      const pushUrl = (hrefRaw: unknown, labelRaw?: unknown) => {
                        const href = String(hrefRaw || '').trim();
                        if (!href.startsWith('http') || seen.has(href)) return;
                        seen.add(href);
                        const label = String(labelRaw || '').trim() || href;
                        urls.push({ href, label });
                      };
                      pushUrl(txJsonViewerAiSummary.merchant_likely_url, 'Likely merchant site');
                      const candidates = Array.isArray(txJsonViewerAiSummary.candidate_urls)
                        ? txJsonViewerAiSummary.candidate_urls
                        : [];
                      candidates.forEach((c) => {
                        if (typeof c === 'string') pushUrl(c);
                        else if (c && typeof c === 'object') pushUrl(c.url, c.title || c.url);
                      });
                      if (urls.length === 0) {
                        return (
                          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1.5 }}>
                            No merchant URLs found from web search for this TX.
                          </Typography>
                        );
                      }
                      return (
                        <Box sx={{ mt: 1.5 }}>
                          <Typography
                            variant="caption"
                            sx={{ display: 'block', fontWeight: 700, color: 'text.secondary', mb: 0.5 }}
                          >
                            Useful links
                          </Typography>
                          <Stack spacing={0.5}>
                            {urls.map((u) => (
                              <Typography key={u.href} variant="body2" sx={{ lineHeight: 1.4 }}>
                                <TxExternalLink href={u.href} variant="body2">
                                  {u.label === u.href ? u.href : `${u.label} — ${u.href}`}
                                </TxExternalLink>
                              </Typography>
                            ))}
                          </Stack>
                        </Box>
                      );
                    })()}
                    {(() => {
                      const p = txJsonViewerAiSummary.probabilistic;
                      if (!p || typeof p !== 'object') {
                        return (
                          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1.25 }}>
                            Probabilistic tags: not available for this TX yet.
                          </Typography>
                        );
                      }
                      const cf = p.cashflow?.label ?? p.cashflow?.name;
                      const sup = p.supplier?.name ?? p.supplier?.label;
                      const cat = p.category?.name ?? p.category?.label;
                      const vat = p.vat?.vat_rate ?? p.vat?.label ?? p.vat?.name;
                      const fmt = (label: string, value: unknown, conf: unknown) => {
                        const v = value == null || String(value).trim() === '' ? '—' : String(value);
                        const c =
                          typeof conf === 'number' && Number.isFinite(conf)
                            ? ` (${Math.round(conf * 100)}%)`
                            : '';
                        return `${label}: ${v}${c}`;
                      };
                      return (
                        <Stack direction="row" spacing={0.75} useFlexGap flexWrap="wrap" sx={{ mt: 1.25 }}>
                          <Chip size="small" variant="outlined" label={fmt('Cash flow', cf, p.cashflow?.confidence)} />
                          <Chip size="small" variant="outlined" label={fmt('Supplier', sup, p.supplier?.confidence)} />
                          <Chip size="small" variant="outlined" label={fmt('Category', cat, p.category?.confidence)} />
                          <Chip size="small" variant="outlined" label={fmt('VAT', vat, p.vat?.confidence)} />
                          {p.status ? (
                            <Chip size="small" color="default" label={`Status: ${String(p.status)}`} />
                          ) : null}
                        </Stack>
                      );
                    })()}
                    {txJsonViewerAiSummary.llm_error ? (
                      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                        Note: LLM fallback used ({String(txJsonViewerAiSummary.llm_error)}).
                      </Typography>
                    ) : null}
                  </Box>
                ) : null}
                </AccordionDetails>
              </Accordion>
              <Accordion
                  disableGutters
                  expanded={txJsonViewerSectionsExpanded.neighbors}
                  onChange={(_, expanded) =>
                    setTxJsonViewerSectionsExpanded((prev) => ({ ...prev, neighbors: expanded }))
                  }
                  sx={TX_JSON_VIEWER_SECTION_ACCORDION_SX}
                >
                  <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={{ minHeight: 40, px: 1.5 }}>
                    <Typography variant="subtitle2">
                      3) Preceding/Subsequent Tx&apos;s
                      {!txJsonViewerNeighborsBusy && txJsonViewerNeighbors
                        ? ` (${(txJsonViewerNeighbors.preceding?.length || 0) + (txJsonViewerNeighbors.subsequent?.length || 0)})`
                        : ''}
                    </Typography>
                  </AccordionSummary>
                  <AccordionDetails sx={{ pt: 0, px: 1.5, pb: 1.5 }}>
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      sx={{ display: 'block', mb: 1.25, lineHeight: 1.5 }}
                    >
                      Up to 5 earlier and 5 later ledger transactions on the same account (by completed / Revolut
                      created time). The open TX is shown in bold in the middle of the list.
                    </Typography>
                    {txJsonViewerNeighborsBusy ? (
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 0.5 }}>
                        <CircularProgress size={18} />
                        <Typography variant="body2" color="text.secondary">
                          Loading neighbouring transactions…
                        </Typography>
                      </Box>
                    ) : null}
                    {txJsonViewerNeighborsError ? (
                      <Alert severity="warning" sx={{ mb: 1 }}>
                        {txJsonViewerNeighborsError}
                      </Alert>
                    ) : null}
                    {!txJsonViewerNeighborsBusy && txJsonViewerNeighbors ? (
                      <Box>
                        <Box
                          sx={{
                            display: 'grid',
                            gridTemplateColumns: '52px 36px 118px 88px 1fr',
                            gap: 0.75,
                            alignItems: 'baseline',
                            py: 0.35,
                            mb: 0.25,
                            borderBottom: '1px solid',
                            borderColor: 'grey.300'
                          }}
                        >
                          {(['ID', 'Who', 'Date', 'Amount', 'Description'] as const).map((h) => (
                            <Typography
                              key={h}
                              variant="caption"
                              sx={{
                                fontWeight: 700,
                                color: 'text.secondary',
                                textAlign: h === 'Amount' ? 'right' : 'left'
                              }}
                            >
                              {h}
                            </Typography>
                          ))}
                        </Box>
                        <Stack spacing={0}>
                          {(
                            [
                              ...(txJsonViewerNeighbors.preceding || []).map((n) => ({
                                row: n,
                                isCurrent: false
                              })),
                              ...(txJsonViewerNeighbors.current
                                ? [{ row: txJsonViewerNeighbors.current, isCurrent: true }]
                                : txJsonViewerContextRow
                                  ? [
                                      {
                                        row: {
                                          id: txJsonViewerContextRow.id,
                                          date: txJsonViewerContextRow.date,
                                          amount: txJsonViewerContextRow.amount,
                                          currency: txJsonViewerContextRow.currency,
                                          description: txJsonViewerContextRow.description,
                                          reference: txJsonViewerContextRow.reference,
                                          account_source: txJsonViewerContextRow.account_source,
                                          cardholder: cardholderDisplayNameFromUnifiedRow(txJsonViewerContextRow)
                                        } as TxJsonNeighborRow,
                                        isCurrent: true
                                      }
                                    ]
                                  : []),
                              ...(txJsonViewerNeighbors.subsequent || []).map((n) => ({
                                row: n,
                                isCurrent: false
                              }))
                            ] as Array<{ row: TxJsonNeighborRow; isCurrent: boolean }>
                          ).map(({ row: n, isCurrent }) => {
                            const who = unifiedWhoInitialsFromCardholder(n.cardholder);
                            const whoSx = whoChipSx(who);
                            return (
                              <Box
                                key={`${isCurrent ? 'current' : 'n'}-${n.id}`}
                                sx={{
                                  display: 'grid',
                                  gridTemplateColumns: '52px 36px 118px 88px 1fr',
                                  gap: 0.75,
                                  alignItems: 'center',
                                  py: 0.4,
                                  borderBottom: '1px solid',
                                  borderColor: 'grey.200',
                                  bgcolor: isCurrent ? 'action.hover' : 'transparent',
                                  '&:last-of-type': { borderBottom: 'none' }
                                }}
                              >
                                <Typography
                                  variant="caption"
                                  sx={{
                                    fontVariantNumeric: 'tabular-nums',
                                    fontWeight: isCurrent ? 800 : 600
                                  }}
                                >
                                  {n.id}
                                </Typography>
                                <Chip
                                  size="small"
                                  label={who}
                                  title={whoTooltipLabel(n.cardholder, who)}
                                  sx={{
                                    height: 18,
                                    minWidth: 28,
                                    fontWeight: isCurrent ? 800 : 700,
                                    fontSize: '0.65rem',
                                    ...whoSx,
                                    border: '1px solid',
                                    '& .MuiChip-label': { px: 0.5 }
                                  }}
                                />
                                <Typography
                                  variant="caption"
                                  color={isCurrent ? 'text.primary' : 'text.secondary'}
                                  sx={{
                                    fontVariantNumeric: 'tabular-nums',
                                    fontWeight: isCurrent ? 800 : 400,
                                    whiteSpace: 'nowrap'
                                  }}
                                  title={formatUnifiedReviewTableDateTooltip(n.date ?? null)}
                                >
                                  {formatTxJsonPanelDateTime(n.date ?? null)}
                                </Typography>
                                <Typography
                                  variant="caption"
                                  sx={{
                                    fontVariantNumeric: 'tabular-nums',
                                    color: txReviewAmountColor(n.amount ?? null),
                                    textAlign: 'right',
                                    fontWeight: isCurrent ? 800 : 400
                                  }}
                                >
                                  {formatAmount(n.amount ?? null, n.currency ?? null)}
                                </Typography>
                                <Typography
                                  variant="caption"
                                  noWrap
                                  title={[n.description, n.reference].filter(Boolean).join(' · ') || '—'}
                                  sx={{ minWidth: 0, fontWeight: isCurrent ? 800 : 400 }}
                                >
                                  {n.description || '—'}
                                </Typography>
                              </Box>
                            );
                          })}
                        </Stack>
                      </Box>
                    ) : null}
                  </AccordionDetails>
                </Accordion>
              <Accordion
                disableGutters
                expanded={txJsonViewerSectionsExpanded.tags}
                onChange={(_, expanded) =>
                  setTxJsonViewerSectionsExpanded((prev) => ({ ...prev, tags: expanded }))
                }
                sx={TX_JSON_VIEWER_SECTION_ACCORDION_SX}
              >
                <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={{ minHeight: 40, px: 1.5 }}>
                  <Typography variant="subtitle2">
                    4) Classification category tags (all)
                    {txJsonViewerCategoryTags != null ? ` (${txJsonViewerCategoryTags.length})` : ''}
                  </Typography>
                </AccordionSummary>
                <AccordionDetails sx={{ pt: 0, px: 1.5, pb: 1.5 }}>
                <Typography variant="subtitle2" sx={{ mb: 0.75 }}>
                  Applied rules
                </Typography>
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1, lineHeight: 1.5 }}>
                  Winning-tag rule attribution for each dimension (cashflow, category, supplier, VAT, special). Source may
                  be rule, manual, or probabilistic. Manual overwrites clear that dimension&apos;s rule id and set origin
                  to manual.
                </Typography>
                {txJsonViewerAppliedRulesError ? (
                  <Alert severity="warning" sx={{ mb: 1 }}>
                    {txJsonViewerAppliedRulesError}
                  </Alert>
                ) : null}
                {txJsonViewerAppliedRulesProbabilistic ? (
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1, lineHeight: 1.5 }}>
                    Probabilistic accept
                    {txJsonViewerAppliedRulesProbabilistic.model_version
                      ? ` · ${txJsonViewerAppliedRulesProbabilistic.model_version}`
                      : ''}
                    {txJsonViewerAppliedRulesProbabilistic.llm_model
                      ? ` · ${txJsonViewerAppliedRulesProbabilistic.llm_model}`
                      : ''}
                    {Array.isArray(txJsonViewerAppliedRulesProbabilistic.promoted_dims) &&
                    txJsonViewerAppliedRulesProbabilistic.promoted_dims.length
                      ? ` · dims: ${txJsonViewerAppliedRulesProbabilistic.promoted_dims.join(', ')}`
                      : ''}
                  </Typography>
                ) : null}
                {txJsonViewerAppliedRules == null ? (
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                    Loading applied rules…
                  </Typography>
                ) : txJsonViewerAppliedRules.length === 0 ? (
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                    No active tags with rule attribution on this transaction.
                  </Typography>
                ) : (
                  <TableContainer
                    sx={{
                      border: '1px solid',
                      borderColor: 'divider',
                      borderRadius: 1,
                      mb: 1.5,
                      maxHeight: 220
                    }}
                  >
                    <Table size="small" stickyHeader>
                      <TableHead>
                        <TableRow>
                          <TableCell>Dimension</TableCell>
                          <TableCell>Tag</TableCell>
                          <TableCell>Source</TableCell>
                          <TableCell>Rule</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {txJsonViewerAppliedRules.map((r, idx) => {
                          const dim = String(r.dimension || '').trim() || '—';
                          const label = String(r.tag_label || '').trim() || '—';
                          const src = String(r.source || '').trim() || '—';
                          const rid = String(r.rule_prefixed_id || '').trim();
                          const rname = String(r.rule_name || '').trim();
                          const showProbMeta =
                            src === 'probabilistic' && txJsonViewerAppliedRulesProbabilistic;
                          return (
                            <TableRow key={`${dim}-${label}-${rid || 'none'}-${idx}`}>
                              <TableCell sx={{ textTransform: 'capitalize', fontWeight: 600 }}>{dim}</TableCell>
                              <TableCell>{label}</TableCell>
                              <TableCell>
                                <Typography variant="body2" component="span" sx={{ display: 'block' }}>
                                  {src}
                                </Typography>
                                {showProbMeta ? (
                                  <Typography
                                    variant="caption"
                                    color="text.secondary"
                                    sx={{ display: 'block', lineHeight: 1.3 }}
                                  >
                                    {[
                                      txJsonViewerAppliedRulesProbabilistic?.model_version,
                                      txJsonViewerAppliedRulesProbabilistic?.llm_model
                                    ]
                                      .filter(Boolean)
                                      .join(' · ')}
                                  </Typography>
                                ) : null}
                              </TableCell>
                              <TableCell sx={{ maxWidth: 180 }}>
                                <Typography variant="body2" component="span" sx={{ display: 'block' }}>
                                  {rid || '—'}
                                </Typography>
                                {rname ? (
                                  <Typography
                                    variant="caption"
                                    color="text.secondary"
                                    sx={{ display: 'block', lineHeight: 1.3 }}
                                  >
                                    {rname}
                                  </Typography>
                                ) : null}
                              </TableCell>
                            </TableRow>
                          );
                        })}
                      </TableBody>
                    </Table>
                  </TableContainer>
                )}
                <Typography variant="subtitle2" sx={{ mb: 0.75 }}>
                  Category tags (all)
                </Typography>
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1.25, lineHeight: 1.5 }}>
                  Every <code>revolut_transaction_category_tags</code> row for this transaction. The review grid marks{' '}
                  <strong>VAT Conflict</strong> when, among non–Income/Expense tags, more than one distinct VAT value
                  appears (blank VAT counts as &quot;none&quot;). Compare rows here to the single preferred category/VAT
                  shown in the table.
                </Typography>
                {txJsonViewerTagsError ? (
                  <Alert severity="warning" sx={{ mb: 1 }}>
                    {txJsonViewerTagsError}
                  </Alert>
                ) : null}
                {txJsonViewerCategoryTags == null ? (
                  <Typography variant="body2" color="text.secondary">
                    Loading category tags…
                  </Typography>
                ) : txJsonViewerTagsError ? null : txJsonViewerCategoryTags.length === 0 ? (
                  <Typography variant="body2" color="text.secondary">
                    No category tags on this transaction.
                  </Typography>
                ) : (
                  <>
                    {(() => {
                      const tags = txJsonViewerCategoryTags;
                      const vatNorm = tags.map((t: any) => {
                        const v = String(t?.vat_rate ?? '').trim().toLowerCase();
                        return v.length && v !== 'none' && v !== 'n/a' && v !== 'na' ? v : 'blank';
                      });
                      const distinctVat = Array.from(new Set(vatNorm)).sort();
                      const multiVat = distinctVat.length > 1;
                      const detail = txJsonViewerContextRow?.vat_conflict_detail;
                      const rowFlagConflict = Boolean(txJsonViewerContextRow?.flags?.has_vat_conflict);
                      return (
                        <>
                          {(multiVat || rowFlagConflict) && (
                            <Alert severity={multiVat ? 'warning' : 'info'} sx={{ mb: 1.5 }}>
                              {multiVat ? (
                                <>
                                  <strong>Distinct VAT values</strong> across these tags: {distinctVat.join(' · ')}.
                                  {Array.isArray(detail?.vat_rates) && detail!.vat_rates!.length > 0 ? (
                                    <>
                                      {' '}
                                      Review-rows summary: {detail!.vat_rates!.join(' vs ')}.
                                    </>
                                  ) : null}
                                </>
                              ) : (
                                <>
                                  Review grid flagged <strong>VAT Conflict</strong> for this transaction. If distinct
                                  VATs are not obvious below, refresh the page after tag changes or inspect heading vs
                                  granular rows.
                                  {Array.isArray(detail?.vat_rates) && detail!.vat_rates!.length > 0 ? (
                                    <>
                                      {' '}
                                      API detail: {detail!.vat_rates!.join(' vs ')}
                                      {Array.isArray(detail?.categories) && detail!.categories!.length > 0
                                        ? ` (categories: ${detail!.categories!.join(', ')})`
                                        : ''}
                                      .
                                    </>
                                  ) : null}
                                </>
                              )}
                            </Alert>
                          )}
                          <TableContainer
                            sx={{
                              border: '1px solid',
                              borderColor: 'divider',
                              borderRadius: 1,
                              maxHeight: 280
                            }}
                          >
                            <Table size="small" stickyHeader>
                              <TableHead>
                                <TableRow>
                                  <TableCell>Category</TableCell>
                                  <TableCell>VAT</TableCell>
                                  <TableCell>Source</TableCell>
                                  <TableCell>Rule</TableCell>
                                  <TableCell>Supplier (tag)</TableCell>
                                  <TableCell>Tag UID</TableCell>
                                </TableRow>
                              </TableHead>
                              <TableBody>
                                {[...tags]
                                  .sort((a: any, b: any) => {
                                    const na = String(a?.category?.name || '').localeCompare(
                                      String(b?.category?.name || ''),
                                      undefined,
                                      { sensitivity: 'base' }
                                    );
                                    if (na !== 0) return na;
                                    return String(a?.uid || '').localeCompare(String(b?.uid || ''));
                                  })
                                  .map((t: any) => {
                                    const catName = String(t?.category?.name || '').trim() || '—';
                                    const vatCell = String(t?.vat_rate ?? '').trim() || '—';
                                    const src = String(t?.source || '').trim() || '—';
                                    const rname = String(t?.rule?.name || '').trim();
                                    const rid =
                                      t?.rule_id != null && t.rule_id !== ''
                                        ? formatClassificationRulePrefixedId(Number(t.rule_id), rname || t?.rule)
                                        : '—';
                                    const sup = String(t?.supplier_name || '').trim() || '—';
                                    const uid = String(t?.uid || '').trim() || '—';
                                    return (
                                      <TableRow key={uid}>
                                        <TableCell sx={{ fontWeight: 600 }}>{catName}</TableCell>
                                        <TableCell>{vatCell}</TableCell>
                                        <TableCell>{src}</TableCell>
                                        <TableCell sx={{ maxWidth: 140 }}>
                                          <Typography variant="body2" component="span" sx={{ display: 'block' }}>
                                            {rid}
                                          </Typography>
                                          {rname ? (
                                            <Typography
                                              variant="caption"
                                              color="text.secondary"
                                              sx={{ display: 'block', lineHeight: 1.35 }}
                                            >
                                              {rname.length > 48 ? `${rname.slice(0, 45)}…` : rname}
                                            </Typography>
                                          ) : null}
                                        </TableCell>
                                        <TableCell
                                          sx={{ maxWidth: 160, overflow: 'hidden', textOverflow: 'ellipsis' }}
                                          title={sup}
                                        >
                                          {sup}
                                        </TableCell>
                                        <TableCell
                                          sx={{
                                            fontFamily: 'ui-monospace, Menlo, Monaco, Consolas, monospace',
                                            fontSize: 11,
                                            wordBreak: 'break-all'
                                          }}
                                        >
                                          {uid}
                                        </TableCell>
                                      </TableRow>
                                    );
                                  })}
                              </TableBody>
                            </Table>
                          </TableContainer>
                        </>
                      );
                    })()}
                  </>
                )}
                </AccordionDetails>
              </Accordion>
              <Accordion
                disableGutters
                expanded={txJsonViewerSectionsExpanded.rawJson}
                onChange={(_, expanded) =>
                  setTxJsonViewerSectionsExpanded((prev) => ({ ...prev, rawJson: expanded }))
                }
                sx={TX_JSON_VIEWER_SECTION_ACCORDION_SX}
              >
                <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={{ minHeight: 40, px: 1.5 }}>
                  <Typography variant="subtitle2">5) Raw JSON</Typography>
                </AccordionSummary>
                <AccordionDetails sx={{ pt: 0, px: 1.5, pb: 1.5 }}>
                <Box
                  component="pre"
                  sx={{
                    m: 0,
                    p: 1.5,
                    borderRadius: 1,
                    backgroundColor: 'grey.100',
                    fontSize: 12,
                    lineHeight: 1.5,
                    overflowX: 'hidden',
                    whiteSpace: 'pre'
                  }}
                >
                  {txJsonViewerContent || '{}'}
                </Box>
                </AccordionDetails>
              </Accordion>
            </Stack>
          )
        ) : null}
      </>
    );
  }, [
    txJsonViewerBusy,
    txJsonViewerError,
    txJsonViewerContent,
    txJsonViewerParsed,
    txJsonViewerKeyData,
    txJsonViewerCategoryTags,
    txJsonViewerTagsError,
    txJsonViewerAppliedRules,
    txJsonViewerAppliedRulesProbabilistic,
    txJsonViewerAppliedRulesError,
    txJsonViewerContextRow,
    txJsonViewerAiSummary,
    txJsonViewerAiBusy,
    txJsonViewerAiError,
    txJsonViewerNeighbors,
    txJsonViewerNeighborsBusy,
    txJsonViewerNeighborsError,
    txJsonViewerSectionsExpanded,
    formatAmount,
    txReviewAmountColor
  ]);

  const renderUnifiedRerunLogBlocksList = useCallback(() => {
    if (unifiedRerunLogBlocks.length === 0) {
      return (
        <Typography variant="body2" color="text.secondary">
          No entries yet.
        </Typography>
      );
    }
    return (
      <>
        {unifiedRerunLogBlocks.map((block, idx) => {
          if (block.kind === 'request') {
            return (
              <Card key={`unified-rerun-block-${idx}`} variant="outlined" sx={{ mb: 1.5, borderStyle: 'dashed' }}>
                <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
                  <Typography variant="caption" color="text.secondary">
                    {block.timeLabel} · Transaction #{block.txId}
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{ mt: 0.75, fontFamily: 'ui-monospace, Menlo, Monaco, Consolas, monospace', fontSize: 12 }}
                  >
                    {block.pipelineLine}
                  </Typography>
                </CardContent>
              </Card>
            );
          }
          if (block.kind === 'run') {
            return <UnifiedRerunLogRunBlock key={`unified-rerun-block-${idx}`} block={block} />;
          }
          return (
            <Alert key={`unified-rerun-block-${idx}`} severity={block.variant} sx={{ mb: 1.5 }}>
              {block.timeLabel ? (
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                  {block.timeLabel}
                </Typography>
              ) : null}
              <Typography variant="body2">{block.text}</Typography>
            </Alert>
          );
        })}
      </>
    );
  }, [unifiedRerunLogBlocks]);

  const mergeCanonicalSupplierOption = useCallback((name: string) => {
    const n = (name || '').trim();
    if (!n) return;
    setCanonicalSupplierOptions((prev) => uniqueSupplierNamesCaseInsensitive([...(prev || []), n]));
  }, []);

  const refreshSuppliersCrudList = useCallback(async () => {
    setSuppliersCrudLoading(true);
    setSuppliersCrudError('');
    try {
      const res = await api.get('/api/suppliers/');
      const raw = Array.isArray(res.data?.suppliers) ? res.data.suppliers : [];
      const rows: SupplierCrudRow[] = raw
        .map((s: any) => ({
          id: Number(s.id),
          name: String(s.name || '').trim(),
          is_key_supplier: Boolean(s.is_key_supplier),
          tx_associated_count: Number(s.tx_associated_count || 0),
          invoice_frequency: s.invoice_frequency != null ? String(s.invoice_frequency) : '',
          invoice_currency: s.invoice_currency != null ? String(s.invoice_currency) : '',
          invoice_url: s.invoice_url != null ? String(s.invoice_url) : '',
          vat_status: s.vat_status != null ? String(s.vat_status) : '',
          notes: s.notes != null ? String(s.notes) : '',
          is_active: Boolean(s.is_active),
          pending_alias_proposals: Array.isArray(s.pending_alias_proposals)
            ? s.pending_alias_proposals
                .map((pa: any) => ({
                  proposal_id: Number(pa.proposal_id),
                  previews: Array.isArray(pa.previews)
                    ? pa.previews.map((x: any) => String(x || '')).filter(Boolean)
                    : [],
                  variant_count: Number(pa.variant_count || 0)
                }))
                .filter((pa: SupplierPendingAliasProposal) => Number.isFinite(pa.proposal_id) && pa.proposal_id > 0)
            : undefined
        }))
        .filter((s: SupplierCrudRow) => Number.isFinite(s.id) && s.id > 0 && s.name.length > 0)
        .sort((a: SupplierCrudRow, b: SupplierCrudRow) =>
          a.name.localeCompare(b.name, undefined, { sensitivity: 'base' })
        );
      setSuppliersCrudRows(rows);
      const activeNames = rows.filter((s) => s.is_active).map((s) => s.name);
      setCanonicalSupplierOptions(uniqueSupplierNamesCaseInsensitive(activeNames));
    } catch (err: any) {
      setSuppliersCrudError(err?.response?.data?.error || err?.message || 'Failed to load suppliers');
    } finally {
      setSuppliersCrudLoading(false);
    }
  }, []);

  const applySupplierCorpusAuditPayload = useCallback((payload: SupplierCorpusAuditPayload | null) => {
    setSupplierCorpusAuditExpandedBuckets({});
    setSupplierCorpusAuditSampleTxById({});
    setSupplierCorpusAuditSampleTxBusyKeys({});
    setSupplierCorpusAuditSampleTxErrors({});
    setSupplierAuditSelectedTxIds([]);
    setSupplierAuditAssignSupplierByTxId({});
    setSupplierCorpusAuditSelectedUnusedIds([]);
    setSupplierCorpusAuditResult(payload);
  }, []);

  const runSupplierCorpusAudit = useCallback(async () => {
    setSupplierCorpusAuditError('');
    setSupplierCorpusAuditLoading(true);
    setSupplierCorpusAuditElapsedSec(0);
    applySupplierCorpusAuditPayload(null);
    const startedAt = Date.now();
    const tick = window.setInterval(() => {
      setSupplierCorpusAuditElapsedSec(Math.max(0, Math.round((Date.now() - startedAt) / 1000)));
    }, 500);
    try {
      // Server always scans all ledger users / all transactions (no client caps).
      const res = await api.post(
        '/api/suppliers/corpus-supplier-audit',
        {},
        { timeout: HEAVY_RULE_JOB_TIMEOUT_MS }
      );
      const payload = res.data as SupplierCorpusAuditPayload;
      // Keep the spinner responsive; paint heavy proposal trees as a transition.
      startTransition(() => {
        applySupplierCorpusAuditPayload(payload);
      });
      setSuccessMessage('Supplier corpus audit finished. Review proposed mappings below.');
    } catch (err: any) {
      const code = err?.code || '';
      const rawMsg = String(err?.message || '');
      const timedOut = code === 'ECONNABORTED' || /timeout/i.test(rawMsg);
      const networkDown =
        code === 'ERR_NETWORK' || rawMsg === 'Network Error' || /network error/i.test(rawMsg);
      const msg = timedOut
        ? 'Corpus audit timed out. Check the API is up and the DB tunnel is connected, then try again.'
        : networkDown
          ? 'Cannot reach the local API (Network Error). Run bash scripts/run_local_dev.sh, then retry.'
          : err?.response?.data?.detail || err?.response?.data?.error || rawMsg || 'Supplier corpus audit failed';
      setSupplierCorpusAuditError(String(msg));
      applySupplierCorpusAuditPayload(null);
    } finally {
      window.clearInterval(tick);
      setSupplierCorpusAuditLoading(false);
    }
  }, [applySupplierCorpusAuditPayload]);

  const toggleSupplierCorpusAuditBucket = useCallback(
    async (bucketKey: string, matchTxIds: number[]) => {
      const key = String(bucketKey || '').trim();
      if (!key) return;
      const willExpand = !supplierCorpusAuditExpandedBuckets[key];
      setSupplierCorpusAuditExpandedBuckets((prev) => ({ ...prev, [key]: willExpand }));
      if (!willExpand) return;

      const ids = Array.from(
        new Set(
          (matchTxIds || [])
            .map((x) => Number(x))
            .filter((id) => Number.isFinite(id) && id > 0)
        )
      );
      if (!ids.length) return;

      const fromCache = new Map<number, CanonicalReviewRow>();
      ids.forEach((id) => {
        const cached = supplierCorpusAuditSampleTxById[id];
        if (cached) fromCache.set(id, cached);
      });
      (canonicalRowsPayload?.rows || []).forEach((r) => {
        const nid = Number(r.id);
        if (ids.includes(nid) && !fromCache.has(nid)) {
          fromCache.set(nid, r as CanonicalReviewRow);
        }
      });
      if (fromCache.size) {
        setSupplierCorpusAuditSampleTxById((prev) => {
          const next = { ...prev };
          fromCache.forEach((row, id) => {
            next[id] = row;
          });
          return next;
        });
      }
      const missing = ids.filter((id) => !fromCache.has(id));
      if (!missing.length) {
        setSupplierCorpusAuditSampleTxErrors((prev) => {
          if (!prev[key]) return prev;
          const next = { ...prev };
          delete next[key];
          return next;
        });
        return;
      }

      setSupplierCorpusAuditSampleTxBusyKeys((prev) => ({ ...prev, [key]: true }));
      setSupplierCorpusAuditSampleTxErrors((prev) => {
        if (!prev[key]) return prev;
        const next = { ...prev };
        delete next[key];
        return next;
      });
      // review-rows caps transaction_ids at 400 per request — chunk for large buckets.
      const CHUNK = 200;
      try {
        const stillMissing: number[] = [];
        for (let i = 0; i < missing.length; i += CHUNK) {
          const chunk = missing.slice(i, i + CHUNK);
          const res = await api.get('/api/transaction-classification-rules/review-rows', {
            params: {
              limit: chunk.length,
              offset: 0,
              committed: 'all',
              transaction_ids: chunk.join(','),
              include_declined: '1',
              _t: String(Date.now())
            }
          });
          const page = (res.data || {}) as CanonicalReviewRowsPayload;
          const rows = Array.isArray(page.rows) ? (page.rows as CanonicalReviewRow[]) : [];
          setSupplierCorpusAuditSampleTxById((prev) => {
            const next = { ...prev };
            rows.forEach((r) => {
              const nid = Number(r.id);
              if (Number.isFinite(nid) && nid > 0) next[nid] = r;
            });
            return next;
          });
          const got = new Set(rows.map((r) => Number(r.id)).filter((id) => Number.isFinite(id) && id > 0));
          chunk.forEach((id) => {
            if (!got.has(id)) stillMissing.push(id);
          });
        }
        if (stillMissing.length) {
          setSupplierCorpusAuditSampleTxErrors((prev) => ({
            ...prev,
            [key]: `Could not load ${stillMissing.length} of ${ids.length} matching TX(s)${
              stillMissing.length <= 12 ? `: ${stillMissing.join(', ')}` : ''
            }`
          }));
        }
      } catch (err: any) {
        setSupplierCorpusAuditSampleTxErrors((prev) => ({
          ...prev,
          [key]: String(err?.response?.data?.error || err?.message || 'Failed to load matching transactions')
        }));
      } finally {
        setSupplierCorpusAuditSampleTxBusyKeys((prev) => {
          const next = { ...prev };
          delete next[key];
          return next;
        });
      }
    },
    [
      canonicalRowsPayload?.rows,
      supplierCorpusAuditExpandedBuckets,
      supplierCorpusAuditSampleTxById
    ]
  );

  const supplierAuditProposalGroups = useMemo(
    () => buildSupplierAuditProposalGroups(supplierCorpusAuditResult),
    [supplierCorpusAuditResult]
  );

  const supplierAuditSelectedTxIdSet = useMemo(
    () => new Set(supplierAuditSelectedTxIds),
    [supplierAuditSelectedTxIds]
  );

  const supplierAuditProposedTxCount = useMemo(() => {
    const ids = new Set<number>();
    supplierAuditProposalGroups.forEach((g) => g.txIds.forEach((id) => ids.add(id)));
    return ids.size;
  }, [supplierAuditProposalGroups]);

  const supplierAuditUnusedCatalogSuppliers = useMemo(() => {
    const used = new Set(supplierAuditProposalGroups.map((g) => g.supplierId));
    return (supplierCorpusAuditResult?.catalog_suppliers_sorted || []).filter((c) => {
      const id = Number(c.supplier_id);
      return Number.isFinite(id) && id > 0 && !used.has(id);
    });
  }, [supplierAuditProposalGroups, supplierCorpusAuditResult]);

  const supplierAuditCanonicalOptions = useMemo(() => {
    const fromAudit = (supplierCorpusAuditResult?.catalog_suppliers_sorted || []).map((c) => ({
      id: Number(c.supplier_id),
      name: String(c.name || '').trim() || `#${c.supplier_id}`
    }));
    const fromCrud = suppliersCrudRows.map((r) => ({
      id: r.id,
      name: String(r.name || '').trim() || `#${r.id}`
    }));
    const fromProposed = supplierAuditProposalGroups.map((g) => ({
      id: g.supplierId,
      name: g.supplierName
    }));
    const map = new Map<number, string>();
    [...fromAudit, ...fromCrud, ...fromProposed].forEach((x) => {
      if (Number.isFinite(x.id) && x.id > 0) map.set(x.id, x.name || `#${x.id}`);
    });
    return Array.from(map.entries())
      .map(([id, name]) => ({ id, name }))
      .sort((a, b) => a.name.localeCompare(b.name, undefined, { sensitivity: 'base' }));
  }, [supplierAuditProposalGroups, supplierCorpusAuditResult, suppliersCrudRows]);

  const createSupplierDescriptionAliasFromWording = useCallback(
    async (
      canonicalName: string,
      match: { kind: 'merchant' | 'description' | 'reference'; value: string }
    ) => {
      const name = String(canonicalName || '').trim();
      const text = String(match.value || '').trim();
      if (!name || !text) return null;
      const kind = match.kind;
      const res = await api.post('/api/suppliers/description-aliases', {
        rule_name: `Audit: ${name}`.slice(0, 200),
        notes: 'Created from Supplier Audit proposed mapping',
        pattern_raw: kind === 'merchant' ? text : '',
        description_contains: kind === 'description' ? text : null,
        description_text_match_operator: kind === 'description' ? 'contains' : 'exact',
        reference_contains: kind === 'reference' ? text : null,
        reference_text_match_operator: kind === 'reference' ? 'contains' : 'exact',
        match_criteria: [{ kind, value: text, operator: 'contains' }],
        canonical_name: name,
        priority: 100,
        text_match_operator: 'contains',
        account_scope: 'both',
        is_active: true
      });
      return res.data?.alias || null;
    },
    []
  );

  const merchantMatchFromAuditTx = useCallback((row: CanonicalReviewRow | undefined) => {
    if (!row) return null as null | { kind: 'merchant' | 'description' | 'reference'; value: string };
    const nested = (row.transaction || {}) as ReviewTx & { merchant_data?: string | null };
    const merchant = effectiveMerchantName(nested);
    if (merchant) return { kind: 'merchant' as const, value: merchant };
    const desc = String(row.description || nested.description || '').trim();
    if (desc) return { kind: 'description' as const, value: desc };
    const ref = String(row.reference || nested.reference || '').trim();
    if (ref) return { kind: 'reference' as const, value: ref };
    return null;
  }, []);

  const assignSupplierAuditSelectedTxs = useCallback(
    async (txIds: number[], defaultSupplierId: number, defaultSupplierName: string) => {
      const ids = Array.from(new Set(txIds.map((n) => Number(n)).filter((n) => Number.isFinite(n) && n > 0)));
      if (!ids.length) return;
      setSupplierCorpusAuditActionBusy(true);
      setSupplierCorpusAuditError('');
      let aliasesCreated = 0;
      let aliasesSkipped = 0;
      const failures: string[] = [];
      const seen = new Set<string>();
      try {
        for (const txId of ids) {
          const chosenId = Number(supplierAuditAssignSupplierByTxId[txId] || defaultSupplierId);
          const chosen =
            supplierAuditCanonicalOptions.find((o) => o.id === chosenId) ||
            ({ id: defaultSupplierId, name: defaultSupplierName } as { id: number; name: string });
          const row = supplierCorpusAuditSampleTxById[txId];
          const match = merchantMatchFromAuditTx(row);
          if (!match) {
            aliasesSkipped += 1;
            failures.push(`TX ${txId}: no merchant/description wording to map`);
            continue;
          }
          const dedupe = `${chosen.name}::${match.kind}::${normalizeRuleMatchText(match.value)}`;
          if (seen.has(dedupe)) {
            aliasesSkipped += 1;
            continue;
          }
          seen.add(dedupe);
          try {
            const alias = await createSupplierDescriptionAliasFromWording(chosen.name, match);
            aliasesCreated += 1;
            const aliasId = Number(alias?.id || 0);
            if (aliasId > 0) {
              try {
                await api.post(`/api/suppliers/description-aliases/${aliasId}/apply-single`, {});
              } catch {
                // Alias created; apply may require admin — keep going.
              }
            }
          } catch (err: any) {
            const status = err?.response?.status;
            const msg = String(err?.response?.data?.error || err?.message || 'alias failed');
            if (status === 409 || /already exists|duplicate/i.test(msg)) aliasesSkipped += 1;
            else failures.push(`TX ${txId}: ${msg}`);
          }
        }
        await refreshSuppliersCrudList();
        void refreshRulesTabDatasets().catch(() => undefined);
        // Refresh loaded TX rows for this assignment set.
        const CHUNK = 200;
        for (let i = 0; i < ids.length; i += CHUNK) {
          const chunk = ids.slice(i, i + CHUNK);
          try {
            const res = await api.get('/api/transaction-classification-rules/review-rows', {
              params: {
                limit: chunk.length,
                offset: 0,
                committed: 'all',
                transaction_ids: chunk.join(','),
                include_declined: '1',
                _t: String(Date.now())
              }
            });
            const rows = Array.isArray(res.data?.rows) ? (res.data.rows as CanonicalReviewRow[]) : [];
            setSupplierCorpusAuditSampleTxById((prev) => {
              const next = { ...prev };
              rows.forEach((r) => {
                const nid = Number(r.id);
                if (Number.isFinite(nid) && nid > 0) next[nid] = r;
              });
              return next;
            });
          } catch {
            // ignore refresh blip
          }
        }
        setSupplierAuditSelectedTxIds((prev) => prev.filter((id) => !ids.includes(id)));
        setSuccessMessage(
          `Assigned mappings for ${ids.length} TX(s): ${aliasesCreated} alias(es) created` +
            (aliasesSkipped ? `, ${aliasesSkipped} skipped` : '') +
            '.'
        );
        if (failures.length) {
          setSupplierCorpusAuditError(
            `Some assigns failed: ${failures.slice(0, 5).join('; ')}${failures.length > 5 ? '…' : ''}`
          );
        }
      } finally {
        setSupplierCorpusAuditActionBusy(false);
      }
    },
    [
      createSupplierDescriptionAliasFromWording,
      merchantMatchFromAuditTx,
      refreshRulesTabDatasets,
      refreshSuppliersCrudList,
      supplierAuditAssignSupplierByTxId,
      supplierAuditCanonicalOptions,
      supplierCorpusAuditSampleTxById
    ]
  );

  const deleteSuppliersFromCorpusAudit = useCallback(
    async (entries: Array<{ id: number; name: string }>) => {
      if (!canMergeSuppliersCatalog) {
        setSupplierCorpusAuditError('Only owner or admin can delete suppliers.');
        return;
      }
      const unique = Array.from(
        new Map(
          entries
            .filter((e) => Number.isFinite(e.id) && e.id > 0)
            .map((e) => [e.id, { id: e.id, name: e.name || `#${e.id}` }])
        ).values()
      );
      if (!unique.length) return;
      const preview =
        unique.length <= 5
          ? unique.map((e) => `“${e.name}”`).join(', ')
          : `${unique
              .slice(0, 3)
              .map((e) => `“${e.name}”`)
              .join(', ')} and ${unique.length - 3} more`;
      if (
        !window.confirm(
          `Delete ${unique.length} supplier(s) from the database?\n\n${preview}\n\nThis cannot be undone.`
        )
      ) {
        return;
      }
      setSupplierCorpusAuditActionBusy(true);
      setSupplierCorpusAuditError('');
      let deleted = 0;
      const failures: string[] = [];
      try {
        for (const entry of unique) {
          try {
            await api.delete(`/api/suppliers/${entry.id}`);
            deleted += 1;
          } catch (err: any) {
            failures.push(
              `${entry.name}: ${String(err?.response?.data?.error || err?.message || 'delete failed')}`
            );
          }
        }
        await refreshSuppliersCrudList();
        if (deleted > 0) {
          setSuccessMessage(`Deleted ${deleted} supplier(s). Re-run corpus audit to refresh.`);
          // Drop deleted ids from the in-memory audit catalog list so Manage updates immediately.
          setSupplierCorpusAuditResult((prev) => {
            if (!prev) return prev;
            const gone = new Set(unique.map((u) => u.id));
            return {
              ...prev,
              catalog_suppliers_sorted: (prev.catalog_suppliers_sorted || []).filter(
                (c) => !gone.has(Number(c.supplier_id))
              )
            };
          });
        }
        if (failures.length) {
          setSupplierCorpusAuditError(
            `Could not delete ${failures.length}: ${failures.slice(0, 5).join('; ')}${
              failures.length > 5 ? '…' : ''
            }`
          );
        }
        setSupplierCorpusAuditSelectedUnusedIds((prev) =>
          prev.filter((id) => !unique.some((u) => u.id === id))
        );
      } finally {
        setSupplierCorpusAuditActionBusy(false);
      }
    },
    [canMergeSuppliersCatalog, refreshSuppliersCrudList]
  );


  const openSupplierCrudCreate = useCallback(() => {
    setSupplierCrudFormError('');
    setSupplierCrudDraft({
      id: null,
      name: '',
      is_key_supplier: false,
      invoice_frequency: '',
      invoice_currency: '',
      invoice_url: '',
      vat_status: '',
      notes: '',
      is_active: true
    });
    setSupplierCrudOpen(true);
  }, []);

  const openSupplierCrudEdit = useCallback((row: SupplierCrudRow) => {
    setSupplierCrudFormError('');
    setSupplierCrudDraft({
      id: row.id,
      name: row.name,
      is_key_supplier: !!row.is_key_supplier,
      invoice_frequency: row.invoice_frequency || '',
      invoice_currency: row.invoice_currency || '',
      invoice_url: row.invoice_url || '',
      vat_status: row.vat_status || '',
      notes: row.notes || '',
      is_active: !!row.is_active
    });
    setSupplierCrudOpen(true);
  }, []);

  const deleteSupplierCrud = useCallback(
    async (row: SupplierCrudRow) => {
      if (
        !window.confirm(
          `Delete supplier “${row.name}” from the database? This cannot be undone.`
        )
      ) {
        return;
      }
      setSuppliersCrudError('');
      try {
        await api.delete(`/api/suppliers/${row.id}`);
        setSuccessMessage(`Supplier “${row.name}” deleted.`);
        setSelectedSupplierCrudIds((prev) => prev.filter((id) => id !== row.id));
        await refreshSuppliersCrudList();
      } catch (err: any) {
        setSuppliersCrudError(err?.response?.data?.error || err?.message || 'Failed to delete supplier');
      }
    },
    [refreshSuppliersCrudList]
  );

  const bulkDeleteSelectedSuppliers = useCallback(async () => {
    if (!canMergeSuppliersCatalog) {
      setSuppliersCrudError('Only owner or admin can delete suppliers.');
      return;
    }
    const ids = selectedSupplierCrudIds.filter((id) => Number.isFinite(id) && id > 0);
    if (!ids.length) return;
    const namesById = new Map(suppliersCrudRows.map((r) => [r.id, r.name]));
    const names = ids.map((id) => namesById.get(id) || `#${id}`);
    const preview =
      names.length <= 5
        ? names.map((n) => `“${n}”`).join(', ')
        : `${names
            .slice(0, 3)
            .map((n) => `“${n}”`)
            .join(', ')} and ${names.length - 3} more`;
    if (
      !window.confirm(
        `Delete ${ids.length} selected supplier(s) from the database?\n\n${preview}\n\nThis cannot be undone.`
      )
    ) {
      return;
    }
    setSuppliersCrudError('');
    setSuppliersBulkDeleteBusy(true);
    let deleted = 0;
    const failures: string[] = [];
    try {
      for (const id of ids) {
        const label = namesById.get(id) || `#${id}`;
        try {
          await api.delete(`/api/suppliers/${id}`);
          deleted += 1;
        } catch (err: any) {
          failures.push(
            `${label}: ${String(err?.response?.data?.error || err?.message || 'delete failed')}`
          );
        }
      }
      setSelectedSupplierCrudIds([]);
      await refreshSuppliersCrudList();
      if (deleted > 0) {
        setSuccessMessage(`Deleted ${deleted} supplier(s).`);
      }
      if (failures.length) {
        setSuppliersCrudError(
          `Could not delete ${failures.length} supplier(s): ${failures.slice(0, 5).join('; ')}${
            failures.length > 5 ? '…' : ''
          }`
        );
      }
    } finally {
      setSuppliersBulkDeleteBusy(false);
    }
  }, [
    canMergeSuppliersCatalog,
    refreshSuppliersCrudList,
    selectedSupplierCrudIds,
    suppliersCrudRows
  ]);

  const reactivateSupplierCrud = useCallback(
    async (row: SupplierCrudRow) => {
      setSuppliersCrudError('');
      try {
        await api.put(`/api/suppliers/${row.id}`, {
          name: row.name,
          is_key_supplier: !!row.is_key_supplier,
          invoice_frequency: row.invoice_frequency || null,
          invoice_currency: row.invoice_currency || null,
          invoice_url: row.invoice_url || null,
          vat_status: row.vat_status || null,
          notes: row.notes || null,
          is_active: true
        });
        setSuccessMessage(`Supplier “${row.name}” restored.`);
        await refreshSuppliersCrudList();
      } catch (err: any) {
        setSuppliersCrudError(err?.response?.data?.error || err?.message || 'Failed to restore supplier');
      }
    },
    [refreshSuppliersCrudList]
  );

  const toggleSupplierKeySupplier = useCallback(async (row: SupplierCrudRow) => {
    setSuppliersCrudError('');
    setSupplierKeyToggleBusyId(row.id);
    try {
      const nextIsKey = !row.is_key_supplier;
      await api.put(`/api/suppliers/${row.id}`, {
        name: row.name,
        is_key_supplier: nextIsKey,
        invoice_frequency: row.invoice_frequency || null,
        invoice_currency: row.invoice_currency || null,
        invoice_url: row.invoice_url || null,
        vat_status: row.vat_status || null,
        notes: row.notes || null,
        is_active: row.is_active
      });
      setSuppliersCrudRows((prev) =>
        prev.map((s) => (s.id === row.id ? { ...s, is_key_supplier: nextIsKey } : s))
      );
    } catch (err: any) {
      setSuppliersCrudError(err?.response?.data?.error || err?.message || 'Failed to update favourite');
    } finally {
      setSupplierKeyToggleBusyId(null);
    }
  }, []);

  const toggleCategoryKeyCategory = useCallback(async (row: CategoryCrudRow) => {
    if (isCategoryRowImmutableSystem(row)) {
      setCategoriesCrudError('System categories cannot be changed.');
      return;
    }
    setCategoriesCrudError('');
    setCategoryKeyToggleBusyId(row.id);
    try {
      const nextIsKey = !row.is_key_category;
      await api.put(`/api/categories/${row.id}`, {
        name: row.name,
        description: (row.description || '').trim() || null,
        is_key_category: nextIsKey,
        is_active: row.is_active
      });
      setCategoriesCrudRows((prev) =>
        prev.map((c) => (c.id === row.id ? { ...c, is_key_category: nextIsKey } : c))
      );
      setCategories((prev) =>
        prev.map((c) => (c.id === row.id ? { ...c, is_key_category: nextIsKey } : c))
      );
    } catch (err: any) {
      setCategoriesCrudError(err?.response?.data?.error || err?.message || 'Failed to update favourite');
    } finally {
      setCategoryKeyToggleBusyId(null);
    }
  }, []);

  const toggleSupplierCrudRowSelected = useCallback((id: number, checked: boolean) => {
    setSelectedSupplierCrudIds((prev) =>
      checked ? (prev.includes(id) ? prev : [...prev, id]) : prev.filter((x) => x !== id)
    );
  }, []);

  const toggleSelectAllVisibleSuppliers = useCallback(
    (checked: boolean) => {
      const ids = sortedSuppliersCrudRows.map((r) => r.id);
      setSelectedSupplierCrudIds((prev) => {
        if (checked) return Array.from(new Set([...prev, ...ids]));
        return prev.filter((id) => !ids.includes(id));
      });
    },
    [sortedSuppliersCrudRows]
  );

  const toggleCategoryCrudRowSelected = useCallback((id: number, checked: boolean) => {
    setSelectedCategoryCrudIds((prev) =>
      checked ? (prev.includes(id) ? prev : [...prev, id]) : prev.filter((x) => x !== id)
    );
  }, []);

  const toggleSelectAllVisibleCategories = useCallback(
    (checked: boolean) => {
      const ids = sortedCategoriesCrudRows.filter((r) => !isCategoryRowImmutableSystem(r)).map((r) => r.id);
      setSelectedCategoryCrudIds((prev) => {
        if (checked) return Array.from(new Set([...prev, ...ids]));
        return prev.filter((id) => !ids.includes(id));
      });
    },
    [sortedCategoriesCrudRows]
  );

  const openSuppliersBulkEditDialog = useCallback(() => {
    setSuppliersBulkFavourite('no_change');
    setSuppliersBulkActive('no_change');
    setSuppliersBulkEditOpen(true);
  }, []);

  const openCategoriesBulkEditDialog = useCallback(() => {
    setCategoriesBulkFavourite('no_change');
    setCategoriesBulkActive('no_change');
    setCategoriesBulkEditOpen(true);
  }, []);

  const submitSupplierCrudDialog = useCallback(async () => {
    if (!supplierCrudDraft) return;
    const name = supplierCrudDraft.name.trim();
    if (!name) {
      setSupplierCrudFormError('Supplier name is required.');
      return;
    }
    if (supplierCrudNameHints.duplicateOf) {
      setSupplierCrudFormError(
        `A supplier already exists as “${supplierCrudNameHints.duplicateOf}” (capitalisation is ignored).`
      );
      return;
    }
    setSupplierCrudSaving(true);
    setSupplierCrudFormError('');
    setSuppliersCrudError('');
    try {
      const payload = {
        name,
        is_key_supplier: supplierCrudDraft.is_key_supplier,
        invoice_frequency: supplierCrudDraft.invoice_frequency.trim() || null,
        invoice_currency: supplierCrudDraft.invoice_currency.trim() || null,
        invoice_url: supplierCrudDraft.invoice_url.trim() || null,
        vat_status: supplierCrudDraft.vat_status.trim() || null,
        notes: supplierCrudDraft.notes.trim() || null,
        is_active: supplierCrudDraft.is_active
      };
      if (supplierCrudDraft.id == null) {
        await api.post('/api/suppliers/', { ...payload, is_active: true });
        setSuccessMessage(`Supplier “${name}” created.`);
      } else {
        await api.put(`/api/suppliers/${supplierCrudDraft.id}`, payload);
        setSuccessMessage(`Supplier “${name}” updated.`);
      }
      setSupplierCrudOpen(false);
      setSupplierCrudDraft(null);
      await refreshSuppliersCrudList();
    } catch (err: any) {
      const data = err?.response?.data;
      const msg = String(data?.error || err?.message || 'Could not save supplier');
      const existing = data?.existing_name != null ? String(data.existing_name) : '';
      if (err?.response?.status === 409 && existing) {
        setSupplierCrudFormError(
          `${msg} Did you mean to keep the name “${existing}”?`
        );
      } else {
        setSupplierCrudFormError(msg);
      }
    } finally {
      setSupplierCrudSaving(false);
    }
  }, [supplierCrudDraft, supplierCrudNameHints.duplicateOf, refreshSuppliersCrudList]);

  useEffect(() => {
    if (tabParam === 'suppliers' || tabParam === 'supplier-audit') {
      void refreshSuppliersCrudList();
    }
  }, [tabParam, refreshSuppliersCrudList]);

  const refreshCategoriesCrudList = useCallback(async () => {
    setCategoriesCrudLoading(true);
    setCategoriesCrudError('');
    try {
      const res = await api.get('/api/categories/');
      const raw = Array.isArray(res.data?.categories) ? res.data.categories : [];
      const rows: CategoryCrudRow[] = raw
        .map((c: any) => ({
          id: Number(c.id),
          name: String(c.name || '').trim(),
          description: c.description != null ? String(c.description) : '',
          provenance: c.provenance != null && String(c.provenance).trim() ? String(c.provenance).trim() : null,
          is_key_category: Boolean(c.is_key_category),
          is_active: isActiveCategoryApiRow(c)
        }))
        .filter((c: CategoryCrudRow) => Number.isFinite(c.id) && c.id > 0 && c.name.length > 0)
        .sort((a: CategoryCrudRow, b: CategoryCrudRow) => compareStrings(a.name, b.name));
      setCategoriesCrudRows(rows);
      setCategories(
        rows
          .filter((c) => c.is_active)
          .map((c) => ({
            id: c.id,
            name: c.name,
            is_key_category: c.is_key_category,
            is_active: true
          }))
      );
    } catch (err: any) {
      setCategoriesCrudError(err?.response?.data?.error || err?.message || 'Failed to load categories');
    } finally {
      setCategoriesCrudLoading(false);
    }
  }, []);

  const deleteCategoryCrud = useCallback(
    async (row: CategoryCrudRow) => {
      if (isCategoryRowImmutableSystem(row)) {
        setCategoriesCrudError('System categories cannot be deleted.');
        return;
      }
      if (
        !window.confirm(`Delete category "${row.name}" from the database? This cannot be undone.`)
      ) {
        return;
      }
      setCategoriesCrudError('');
      try {
        await api.delete(`/api/categories/${row.id}`);
        setSuccessMessage(`Category "${row.name}" deleted.`);
        await refreshCategoriesCrudList();
      } catch (err: any) {
        setCategoriesCrudError(err?.response?.data?.error || err?.message || 'Failed to delete category');
      }
    },
    [refreshCategoriesCrudList]
  );

  const reactivateCategoryCrud = useCallback(
    async (row: CategoryCrudRow) => {
      if (isCategoryRowImmutableSystem(row)) {
        setCategoriesCrudError('System categories cannot be modified.');
        return;
      }
      setCategoriesCrudError('');
      try {
        await api.put(`/api/categories/${row.id}`, {
          name: row.name,
          description: (row.description || '').trim() || null,
          is_key_category: !!row.is_key_category,
          is_active: true
        });
        setSuccessMessage(`Category "${row.name}" restored.`);
        await refreshCategoriesCrudList();
      } catch (err: any) {
        setCategoriesCrudError(err?.response?.data?.error || err?.message || 'Failed to restore category');
      }
    },
    [refreshCategoriesCrudList]
  );

  const applySuppliersBulkEdit = useCallback(async () => {
    if (selectedSupplierCrudIds.length === 0) return;
    if (suppliersBulkFavourite === 'no_change' && suppliersBulkActive === 'no_change') {
      setSuppliersCrudError('Choose at least one field to update.');
      return;
    }
    setSuppliersBulkBusy(true);
    setSuppliersCrudError('');
    try {
      await Promise.all(
        selectedSupplierCrudIds.map(async (id) => {
          const row = suppliersCrudRows.find((s) => s.id === id);
          if (!row) return;
          let is_key_supplier = row.is_key_supplier;
          if (suppliersBulkFavourite === 'favourite') is_key_supplier = true;
          else if (suppliersBulkFavourite === 'not_favourite') is_key_supplier = false;
          let is_active = row.is_active;
          if (suppliersBulkActive === 'active') is_active = true;
          else if (suppliersBulkActive === 'inactive') is_active = false;
          await api.put(`/api/suppliers/${id}`, {
            name: row.name,
            is_key_supplier,
            invoice_frequency: row.invoice_frequency || null,
            invoice_currency: row.invoice_currency || null,
            invoice_url: row.invoice_url || null,
            vat_status: row.vat_status || null,
            notes: row.notes || null,
            is_active
          });
        })
      );
      setSuccessMessage(`Updated ${selectedSupplierCrudIds.length} supplier(s).`);
      setSuppliersBulkEditOpen(false);
      setSelectedSupplierCrudIds([]);
      await refreshSuppliersCrudList();
    } catch (err: any) {
      setSuppliersCrudError(err?.response?.data?.error || err?.message || 'Bulk supplier update failed');
    } finally {
      setSuppliersBulkBusy(false);
    }
  }, [
    selectedSupplierCrudIds,
    suppliersCrudRows,
    suppliersBulkFavourite,
    suppliersBulkActive,
    refreshSuppliersCrudList
  ]);

  const openSuppliersMergeDialog = useCallback(() => {
    if (!canMergeSuppliersCatalog) {
      setSuppliersCrudError('Only owner or admin can merge suppliers into a canonical row.');
      return;
    }
    if (selectedSupplierCrudIds.length < 2) {
      setSuppliersCrudError('Select at least two suppliers to merge.');
      return;
    }
    const names = selectedSupplierCrudIds
      .map((id) => suppliersCrudRows.find((r) => r.id === id)?.name)
      .filter((n): n is string => !!(n && String(n).trim()));
    if (names.length < 2) {
      setSuppliersCrudError('Could not resolve supplier names for the current selection.');
      return;
    }
    setSuppliersMergeCanonicalDraft(suggestCanonicalSupplierName(names));
    setSuppliersMergeCreateDescRule(true);
    setSuppliersMergeDeactivateSources(true);
    setSuppliersMergeDialogOpen(true);
    setSuppliersCrudError('');
  }, [canMergeSuppliersCatalog, selectedSupplierCrudIds, suppliersCrudRows]);

  const applySuppliersMergeIntoCanonical = useCallback(async () => {
    const name = suppliersMergeCanonicalDraft.trim();
    if (!name) {
      setSuppliersCrudError('Enter the canonical supplier name to keep.');
      return;
    }
    if (selectedSupplierCrudIds.length < 2) {
      setSuppliersCrudError('Select at least two suppliers.');
      return;
    }
    const keepRow = suppliersCrudRows.find((r) => r.name.trim().toLowerCase() === name.toLowerCase());
    const sourceIds = selectedSupplierCrudIds.filter((id) => id !== keepRow?.id);
    if (sourceIds.length === 0) {
      setSuppliersCrudError(
        'Nothing to merge — every selected row already matches that canonical name, or there is no second row to fold in.'
      );
      return;
    }
    setSuppliersMergeBusy(true);
    setSuppliersCrudError('');
    try {
      const canonicalNameToSend = (keepRow ? keepRow.name.trim() : name).slice(0, 200);
      const descStem = stripSupplierMergeNoise(canonicalNameToSend) || canonicalNameToSend;
      const res = await api.post('/api/suppliers/merge-into-canonical', {
        canonical_name: canonicalNameToSend,
        source_supplier_ids: sourceIds,
        create_description_rule: suppliersMergeCreateDescRule,
        deactivate_sources: suppliersMergeDeactivateSources,
        description_contains: descStem.slice(0, 120),
        description_text_match_operator: 'contains'
      });
      const merged = Array.isArray(res.data?.merged_source_ids) ? res.data.merged_source_ids.length : 0;
      const tagUp = res.data?.summary?.category_tags_supplier_name_updated ?? 0;
      setSuccessMessage(
        `Merged ${merged} supplier row(s) into "${canonicalNameToSend}". Updated ${tagUp} classification tag supplier field(s).`
      );
      setSuppliersMergeDialogOpen(false);
      setSelectedSupplierCrudIds([]);
      await refreshSuppliersCrudList();
      await fetchData();
    } catch (err: any) {
      setSuppliersCrudError(err?.response?.data?.error || err?.message || 'Supplier merge failed');
    } finally {
      setSuppliersMergeBusy(false);
    }
  }, [
    suppliersMergeCanonicalDraft,
    selectedSupplierCrudIds,
    suppliersCrudRows,
    suppliersMergeCreateDescRule,
    suppliersMergeDeactivateSources,
    refreshSuppliersCrudList,
    fetchData
  ]);

  const applyCategoriesBulkEdit = useCallback(async () => {
    if (selectedCategoryCrudIds.length === 0) return;
    if (categoriesBulkFavourite === 'no_change' && categoriesBulkActive === 'no_change') {
      setCategoriesCrudError('Choose at least one field to update.');
      return;
    }
    setCategoriesBulkBusy(true);
    setCategoriesCrudError('');
    try {
      await Promise.all(
        selectedCategoryCrudIds.map(async (id) => {
          const row = categoriesCrudRows.find((c) => c.id === id);
          if (!row || isCategoryRowImmutableSystem(row)) return;
          let is_key_category = row.is_key_category;
          if (categoriesBulkFavourite === 'favourite') is_key_category = true;
          else if (categoriesBulkFavourite === 'not_favourite') is_key_category = false;
          let is_active = row.is_active;
          if (categoriesBulkActive === 'active') is_active = true;
          else if (categoriesBulkActive === 'inactive') is_active = false;
          await api.put(`/api/categories/${id}`, {
            name: row.name,
            description: (row.description || '').trim() || null,
            is_key_category,
            is_active
          });
        })
      );
      setSuccessMessage(`Updated ${selectedCategoryCrudIds.length} categor(ies).`);
      setCategoriesBulkEditOpen(false);
      setSelectedCategoryCrudIds([]);
      await refreshCategoriesCrudList();
    } catch (err: any) {
      setCategoriesCrudError(err?.response?.data?.error || err?.message || 'Bulk category update failed');
    } finally {
      setCategoriesBulkBusy(false);
    }
  }, [
    selectedCategoryCrudIds,
    categoriesCrudRows,
    categoriesBulkFavourite,
    categoriesBulkActive,
    refreshCategoriesCrudList
  ]);

  const openCategoryCrudCreate = useCallback(
    (target: 'categories' | 'ruleEdit' | 'ruleEditConflictPair' = 'categories') => {
      setCategoryCrudPendingGranularTxId(null);
      setCategoryCrudTarget(target);
      setCategoryCrudFormError('');
      setCategoryCrudDraft({
        id: null,
        name: '',
        description: '',
        is_key_category: false,
        is_active: true
      });
      setCategoryCrudOpen(true);
    },
    []
  );

  const openCategoryCrudCreateForManualRow = useCallback((txId: number) => {
    setCategoryCrudPendingGranularTxId(txId);
    setCategoryCrudTarget('categories');
    setCategoryCrudFormError('');
    setCategoryCrudDraft({
      id: null,
      name: '',
      description: '',
      is_key_category: false,
      is_active: true
    });
    setCategoryCrudOpen(true);
  }, []);

  const openCategoryCrudEdit = useCallback((row: CategoryCrudRow) => {
    if (isCategoryRowImmutableSystem(row)) {
      setCategoriesCrudError('System categories cannot be edited.');
      return;
    }
    setCategoryCrudTarget('categories');
    setCategoryCrudFormError('');
    setCategoryCrudDraft({
      id: row.id,
      name: row.name,
      description: row.description || '',
      is_key_category: !!row.is_key_category,
      is_active: !!row.is_active
    });
    setCategoryCrudOpen(true);
  }, []);

  const submitCategoryCrudDialog = useCallback(async () => {
    if (!categoryCrudDraft) return;
    const name = categoryCrudDraft.name.trim();
    if (!name) {
      setCategoryCrudFormError('Category name is required.');
      return;
    }
    if (categoryCrudNameHints.duplicateOf) {
      setCategoryCrudFormError(
        `A category already exists as "${categoryCrudNameHints.duplicateOf}" (capitalisation is ignored).`
      );
      return;
    }
    if (categoryCrudDraft.id != null) {
      const existingRow = categoriesCrudRows.find((c) => c.id === categoryCrudDraft.id);
      if (existingRow && isCategoryRowImmutableSystem(existingRow)) {
        setCategoryCrudFormError('System categories cannot be edited.');
        return;
      }
    }
    setCategoryCrudSaving(true);
    setCategoryCrudFormError('');
    setCategoriesCrudError('');
    try {
      const payload = {
        name,
        description: categoryCrudDraft.description.trim() || null,
        is_key_category: categoryCrudDraft.is_key_category,
        is_active: categoryCrudDraft.is_active
      };
      let createdCategoryId: number | null = null;
      if (categoryCrudDraft.id == null) {
        const createRes = await api.post('/api/categories/', { ...payload, is_active: true });
        const createdRaw = Number(createRes?.data?.category?.id || 0);
        createdCategoryId = Number.isFinite(createdRaw) && createdRaw > 0 ? createdRaw : null;
        setSuccessMessage(`Category "${name}" created.`);
      } else {
        await api.put(`/api/categories/${categoryCrudDraft.id}`, payload);
        setSuccessMessage(`Category "${name}" updated.`);
      }
      setCategoryCrudOpen(false);
      setCategoryCrudDraft(null);
      if (createdCategoryId != null && categoryCrudTarget === 'ruleEdit') {
        setRuleEditDraft((d) => (d ? { ...d, category_id: createdCategoryId as number } : d));
      }
      if (createdCategoryId != null && categoryCrudTarget === 'ruleEditConflictPair') {
        setConflictPairEditDraft((d) => (d ? { ...d, category_id: createdCategoryId as number } : d));
      }
      if (createdCategoryId != null && categoryCrudPendingGranularTxId != null) {
        const tid = categoryCrudPendingGranularTxId;
        const cid = createdCategoryId as number;
        // Persist immediately so the new category becomes a tag chip without needing the row Save.
        void addManualCategoryTagToRow(tid, cid, name);
        setSelectedHeadingByTx((prev) => {
          const next = { ...prev };
          delete next[tid];
          return next;
        });
      }
      setCategoryCrudPendingGranularTxId(null);
      setCategoryCrudTarget('categories');
      await refreshCategoriesCrudList();
    } catch (err: any) {
      setCategoryCrudFormError(err?.response?.data?.error || err?.message || 'Could not save category');
    } finally {
      setCategoryCrudSaving(false);
    }
  }, [
    addManualCategoryTagToRow,
    categoryCrudDraft,
    categoryCrudNameHints.duplicateOf,
    categoryCrudTarget,
    categoryCrudPendingGranularTxId,
    categoriesCrudRows,
    refreshCategoriesCrudList
  ]);

  useEffect(() => {
    if (tabParam === 'categories') {
      void refreshCategoriesCrudList();
    }
  }, [tabParam, refreshCategoriesCrudList]);

  useEffect(() => {
    const valid = new Set(suppliersCrudRows.map((r) => r.id));
    setSelectedSupplierCrudIds((prev) => prev.filter((id) => valid.has(id)));
  }, [suppliersCrudRows]);

  useEffect(() => {
    const valid = new Set(categoriesCrudRows.map((r) => r.id));
    setSelectedCategoryCrudIds((prev) =>
      prev.filter((id) => {
        if (!valid.has(id)) return false;
        const row = categoriesCrudRows.find((c) => c.id === id);
        return row ? !isCategoryRowImmutableSystem(row) : false;
      })
    );
  }, [categoriesCrudRows]);

  useEffect(() => {
    if (!rulesQuickviewOpen) return;
    if (rulesQuickviewMainTab === 'categories') void refreshCategoriesCrudList();
    if (rulesQuickviewMainTab === 'suppliers') void refreshSuppliersCrudList();
  }, [rulesQuickviewOpen, rulesQuickviewMainTab, refreshCategoriesCrudList, refreshSuppliersCrudList]);

  useEffect(() => {
    if (!rulesQuickviewOpen || rulesQuickviewMainTab !== 'requests') return;
    let cancelled = false;
    setQvFeatureRequestsLoading(true);
    setQvFeatureRequestsError('');
    void api
      .get<{ requests: QvFeatureRequestApiRow[] }>('/api/qv-feature-requests/')
      .then((res) => {
        if (cancelled) return;
        setQvFeatureRequests(Array.isArray(res.data?.requests) ? res.data.requests : []);
      })
      .catch((err: any) => {
        if (cancelled) return;
        setQvFeatureRequestsError(err?.response?.data?.error || err?.message || 'Could not load requests');
        setQvFeatureRequests([]);
      })
      .finally(() => {
        if (!cancelled) setQvFeatureRequestsLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [rulesQuickviewOpen, rulesQuickviewMainTab]);

  const submitNewCanonicalSupplier = async () => {
    const name = addCanonicalSupplierName.trim();
    if (!name) {
      setAddCanonicalSupplierError('Supplier name is required.');
      return;
    }
    const dup = supplierDuplicateCaseInsensitive(name, canonicalSupplierOptions);
    if (dup) {
      setAddCanonicalSupplierError(
        `A supplier already exists as “${dup}” (capitalisation is ignored). Did you mean to pick that name from the list?`
      );
      return;
    }
    setAddCanonicalSupplierBusy(true);
    setAddCanonicalSupplierError('');
    try {
      await api.post('/api/suppliers/', { name, is_active: true });
      mergeCanonicalSupplierOption(name);
      if (addCanonicalSupplierTarget === 'supplier') {
        setSupplierRuleCanonicalName(name);
      } else if (addCanonicalSupplierTarget === 'specialCase') {
        setSpecialCaseRuleDraft((d) =>
          d
            ? {
                ...d,
                match_criteria: [
                  ...d.match_criteria,
                  {
                    id: `scc-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
                    kind: 'canonical_supplier',
                    value: name,
                    operator: 'contains'
                  }
                ]
              }
            : d
        );
      } else if (addCanonicalSupplierTarget === 'classificationConflictPair') {
        setConflictPairEditDraft((d) =>
          d
            ? {
                ...d,
                supplier_name_overrides: dedupeSupplierNameList([...d.supplier_name_overrides, name])
              }
            : d
        );
      } else {
        setRuleEditDraft((d) =>
          d
            ? {
                ...d,
                supplier_name_overrides: dedupeSupplierNameList([...d.supplier_name_overrides, name])
              }
            : d
        );
      }
      setAddCanonicalSupplierOpen(false);
      setAddCanonicalSupplierName('');
      setSuccessMessage(`Supplier “${name}” added.`);
    } catch (err: any) {
      const data = err?.response?.data;
      const status = err?.response?.status;
      const msg = String(data?.error || err?.message || '');
      const existingName = data?.existing_name != null ? String(data.existing_name) : '';
      if (status === 409 && existingName) {
        mergeCanonicalSupplierOption(existingName);
        if (addCanonicalSupplierTarget === 'supplier') {
          setSupplierRuleCanonicalName(existingName);
        } else if (addCanonicalSupplierTarget === 'specialCase') {
          setSpecialCaseRuleDraft((d) =>
            d
              ? {
                  ...d,
                  match_criteria: [
                    ...d.match_criteria,
                    {
                      id: `scc-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
                      kind: 'canonical_supplier',
                      value: existingName,
                      operator: 'contains'
                    }
                  ]
                }
              : d
          );
        } else if (addCanonicalSupplierTarget === 'classificationConflictPair') {
          setConflictPairEditDraft((d) =>
            d
              ? {
                  ...d,
                  supplier_name_overrides: dedupeSupplierNameList([
                    ...d.supplier_name_overrides,
                    existingName
                  ])
                }
              : d
          );
        } else {
          setRuleEditDraft((d) =>
            d
              ? {
                  ...d,
                  supplier_name_overrides: dedupeSupplierNameList([
                    ...d.supplier_name_overrides,
                    existingName
                  ])
                }
              : d
          );
        }
        setAddCanonicalSupplierOpen(false);
        setAddCanonicalSupplierName('');
        setSuccessMessage(`Using existing supplier “${existingName}”.`);
      } else if (/already exists/i.test(msg) && existingName) {
        mergeCanonicalSupplierOption(existingName);
        if (addCanonicalSupplierTarget === 'supplier') {
          setSupplierRuleCanonicalName(existingName);
        } else if (addCanonicalSupplierTarget === 'specialCase') {
          setSpecialCaseRuleDraft((d) =>
            d
              ? {
                  ...d,
                  match_criteria: [
                    ...d.match_criteria,
                    {
                      id: `scc-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
                      kind: 'canonical_supplier',
                      value: existingName,
                      operator: 'contains'
                    }
                  ]
                }
              : d
          );
        } else if (addCanonicalSupplierTarget === 'classificationConflictPair') {
          setConflictPairEditDraft((d) =>
            d
              ? {
                  ...d,
                  supplier_name_overrides: dedupeSupplierNameList([
                    ...d.supplier_name_overrides,
                    existingName
                  ])
                }
              : d
          );
        } else {
          setRuleEditDraft((d) =>
            d
              ? {
                  ...d,
                  supplier_name_overrides: dedupeSupplierNameList([
                    ...d.supplier_name_overrides,
                    existingName
                  ])
                }
              : d
          );
        }
        setAddCanonicalSupplierOpen(false);
        setAddCanonicalSupplierName('');
        setSuccessMessage(`Using existing supplier “${existingName}”.`);
      } else {
        setAddCanonicalSupplierError(msg || 'Could not create supplier');
      }
    } finally {
      setAddCanonicalSupplierBusy(false);
    }
  };

  const saveRuleFromDialog = () => {
    if (!ruleEditDraft) return;
    if (ruleEditReadOnly) return;
    if (conflictPairEditSaving) {
      setRuleEditError('Conflicting rule save is in progress. Wait for it to finish, or close that panel and try again.');
      return;
    }
    if (supplierRuleSaving || vatRuleSaving || specialCaseRuleSaving) {
      setQueuedRuleEditSaveRequested(true);
      setRuleEditError('');
      setSuccessMessage('Rule save queued. It will run automatically when the current save finishes.');
      return;
    }
    if (ruleEditSaveInFlight) {
      setQueuedRuleEditSaveRequested(true);
      setRuleEditError('');
      setSuccessMessage('Rule save queued. It will run automatically when the current save finishes.');
      return;
    }
    const canonicalList = dedupeSupplierNameList(ruleEditDraft.supplier_name_overrides);
    const isProposalAccept = !!ruleEditDraft.proposal_atlas_key || !!ruleEditDraft.proposal_id;
    const persistedRuleId = Math.trunc(Number(ruleEditDraft.id));
    const hasPersistedRuleId = Number.isFinite(persistedRuleId) && persistedRuleId > 0;
    // Do not use `isNew` for this decision — form updates use `{ ...d }` and could preserve a stale flag.
    const isNewRule = !isProposalAccept && !hasPersistedRuleId;
    const name = ruleEditDraft.name.trim();
    if (!name) {
      setRuleEditError('Rule name is required.');
      return;
    }
    if (ruleEditTargetType === 'classification' && !ruleEditOutputCategoryEnabled) {
      setRuleEditError('Add an output and select Category.');
      return;
    }
    if (ruleEditTargetType === 'classification') {
      if (!Number.isFinite(ruleEditDraft.category_id) || ruleEditDraft.category_id <= 0) {
        setRuleEditError('Select a valid category.');
        return;
      }
    }
    const priorityParsed = parseInt(ruleEditDraft.priority, 10);
    if (!Number.isFinite(priorityParsed)) {
      setRuleEditError('Priority must be a whole number.');
      return;
    }
    const parseAmount = (s: string, label: string): number | null | undefined => {
      const t = s.trim();
      if (!t) return null;
      const n = Number(t);
      if (!Number.isFinite(n)) {
        setRuleEditError(`${label} must be a valid number or empty.`);
        return undefined;
      }
      return n;
    };
    const amountMin = parseAmount(ruleEditDraft.amount_min, 'Amount min');
    if (amountMin === undefined) return;
    const amountMax = parseAmount(ruleEditDraft.amount_max, 'Amount max');
    if (amountMax === undefined) return;

    const normalizedSupplierContains = normalizeRuleOrValues(ruleEditDraft.supplier_contains);
    const normalizedDescriptionContains = normalizeRuleOrValues(ruleEditDraft.description_contains);
    const normalizedReferenceContains = normalizeRuleOrValues(ruleEditDraft.reference_contains);
    const hasTextMatch =
      normalizedSupplierContains !== '' ||
      normalizedDescriptionContains !== '' ||
      normalizedReferenceContains !== '';
    const hasAmountRange = amountMin != null || amountMax != null;
    const hasCanonicalSupplierOnlyMatch =
      canonicalList.length > 0 &&
      normalizedSupplierContains === '' &&
      normalizedDescriptionContains === '' &&
      normalizedReferenceContains === '';
    if (!hasTextMatch && !hasAmountRange && !hasCanonicalSupplierOnlyMatch) {
      setRuleEditError(
        'Add a supplier/description/reference match, set amount min/max, or choose a canonical supplier alone (no separate text matchers) so the rule does not match every transaction.'
      );
      return;
    }

    const draftForRetry: RuleEditDraft = {
      ...ruleEditDraft,
      supplier_name_overrides: [...ruleEditDraft.supplier_name_overrides]
    };
    const targetTypeForRetry = ruleEditTargetType;

    setRuleEditError('');
    setRuleEditConflictRuleId(null);
    setRuleEditSaveInFlight(true);
    setRuleEditSaving(false);
    setAddCanonicalSupplierOpen(false);
    setAddCanonicalSupplierName('');
    setAddCanonicalSupplierError('');
    setRuleMatchHelpAnchorEl(null);
    setRuleEditMatchCriteriaMenuAnchorEl(null);
    const clsHighlightKey = classificationSaveHighlightRowKey(ruleEditDraft, filteredRuleProposals);
    setRuleTableSaveHighlight(clsHighlightKey ? { kind: 'classification', rowKey: clsHighlightKey } : null);
    setRuleEditDraft(null);
    setRuleEditReadOnly(false);
    setRuleEditTargetType('classification');
    setRuleEditOutputMenuAnchorEl(null);
    setRuleEditOutputCategoryEnabled(false);
    setRuleEditOutputSupplierEnabled(false);
    setSuccessMessage('Saving rule (please wait, do not close the tab)…');
    const refreshRulesTabDatasetsInBackground = () => {
      void refreshRulesTabDatasets().catch(() => undefined);
    };
    void (async () => {
      try {
      const payload = {
        name,
        priority: priorityParsed,
        category_id: ruleEditDraft.category_id,
        is_active: ruleEditDraft.is_active,
        supplier_name_overrides: canonicalList.length ? canonicalList : null,
        supplier_name_override: canonicalList.length ? canonicalList[0] : null,
        supplier_contains: normalizedSupplierContains || null,
        description_contains: normalizedDescriptionContains || null,
        description_text_match_operator:
          (ruleEditDraft.description_text_match_operator || ruleEditDraft.text_match_operator || 'exact').trim() ||
          'exact',
        reference_contains: normalizedReferenceContains || null,
        reference_text_match_operator:
          (ruleEditDraft.reference_text_match_operator || ruleEditDraft.text_match_operator || 'exact').trim() ||
          'exact',
        text_match_operator: (ruleEditDraft.text_match_operator || 'exact').trim() || 'exact',
        amount_min: amountMin,
        amount_max: amountMax,
        account_scope: normalizeRuleAccountScope(ruleEditDraft.account_scope),
        currency: ruleEditDraft.currency.trim() || null,
        recurrence_type: ruleEditDraft.recurrence_type.trim() || null,
        notes: ruleEditDraft.notes.trim() || null
      };
      const conversionAllowed = !ruleEditReadOnly;
      if (ruleEditTargetType === 'supplier') {
        if (!conversionAllowed) {
          setRuleEditError('Rule type conversion is available only for editable rules.');
          return;
        }
        const canonicalName = canonicalList[0]?.trim() || ruleEditDraft.supplier_contains.trim() || name;
        if (!canonicalName) {
          setRuleEditError(
            'To convert to a Supplier rule, set a Canonical supplier (or provide a merchant/supplier match to derive one).'
          );
          return;
        }
        const supplierCriteria: Array<{ kind: 'merchant' | 'description' | 'reference' | 'canonical_supplier'; value: string; operator: string }> = [];
        for (const supplierName of canonicalList) {
          const trimmed = String(supplierName || '').trim();
          if (trimmed) supplierCriteria.push({ kind: 'canonical_supplier', value: trimmed, operator: 'exact' });
        }
        const merchantText = ruleEditDraft.supplier_contains.trim();
        if (merchantText) {
          supplierCriteria.push({
            kind: 'merchant',
            value: merchantText,
            operator: (ruleEditDraft.text_match_operator || 'exact').trim() || 'exact'
          });
        }
        const descriptionText = ruleEditDraft.description_contains.trim();
        if (descriptionText) {
          supplierCriteria.push({
            kind: 'description',
            value: descriptionText,
            operator: (ruleEditDraft.description_text_match_operator || ruleEditDraft.text_match_operator || 'exact').trim() || 'exact'
          });
        }
        const referenceText = ruleEditDraft.reference_contains.trim();
        if (referenceText) {
          supplierCriteria.push({
            kind: 'reference',
            value: referenceText,
            operator: (ruleEditDraft.reference_text_match_operator || ruleEditDraft.text_match_operator || 'exact').trim() || 'exact'
          });
        }
        if (supplierCriteria.length === 0) {
          setRuleEditError(
            'This classification rule cannot be converted to Supplier yet: Supplier rules need merchant/description/reference/canonical-supplier criteria (amount-only rules are not convertible).'
          );
          return;
        }
        const firstMerchant = supplierCriteria.find((c) => c.kind === 'merchant');
        const firstDescription = supplierCriteria.find((c) => c.kind === 'description');
        const firstReference = supplierCriteria.find((c) => c.kind === 'reference');
        const supplierPayload = {
          rule_name: name,
          notes: ruleEditDraft.notes.trim() || null,
          pattern_raw: firstMerchant?.value || '',
          description_contains: firstDescription?.value || null,
          description_text_match_operator: firstDescription?.operator || 'exact',
          reference_contains: firstReference?.value || null,
          reference_text_match_operator: firstReference?.operator || 'exact',
          match_criteria: supplierCriteria,
          canonical_name: canonicalName,
          priority: priorityParsed,
          text_match_operator: firstMerchant?.operator || 'exact',
          account_scope: normalizeRuleAccountScope(ruleEditDraft.account_scope),
          is_active: ruleEditDraft.is_active
        };
        const res = await api.post('/api/suppliers/description-aliases', supplierPayload);
        const createdAlias = res.data?.alias as SupplierRuleRow | undefined;
        if (createdAlias) {
          setSupplierRuleRows((prev) => [createdAlias, ...prev]);
        }
        if (ruleEditDraft.proposal_atlas_key) {
          await api.post('/api/transaction-classification-rules/rule-proposals/reject-atlas', {
            atlas_key: ruleEditDraft.proposal_atlas_key
          });
          setProposalsPayload((prev) => {
            if (!prev) return prev;
            return {
              ...prev,
              proposals: prev.proposals.filter((p) => p.atlas_key !== ruleEditDraft.proposal_atlas_key)
            };
          });
        } else if (ruleEditDraft.proposal_id) {
          await api.post(`/api/transaction-classification-rules/rule-proposals/${ruleEditDraft.proposal_id}/reject`, {});
          setProposalsPayload((prev) => {
            if (!prev) return prev;
            return {
              ...prev,
              proposals: prev.proposals.filter((p) => Number(p.id || 0) !== Number(ruleEditDraft.proposal_id))
            };
          });
        }
        if (hasPersistedRuleId) {
          await api.delete(`/api/transaction-classification-rules/${persistedRuleId}`);
          setRulesPayload((prev) =>
            prev ? { ...prev, rules: prev.rules.filter((r) => Number(r.id) !== Number(persistedRuleId)) } : prev
          );
        }
        void refreshRulesTabDatasets().catch(() => undefined);
        setRuleEditConflictRuleId(null);
        setRuleEditMatchCriteriaMenuAnchorEl(null);
        setRuleEditDraft(null);
        setRuleEditTargetType('classification');
        setRuleEditReadOnly(false);
        promoteRulesStatusForConfirmedEdits();
        setSuccessMessage(
          ruleEditDraft.proposal_atlas_key || ruleEditDraft.proposal_id
            ? 'Converted proposed classification rule to a Supplier rule.'
            : hasPersistedRuleId
            ? `Converted ${formatClassificationRulePrefixedId(persistedRuleId, ruleEditDraft.name)} to a Supplier rule.`
            : 'Created Supplier rule from classification draft.'
        );
        return;
      }
      if (isProposalAccept) {
        let acceptedRule: ClassificationRule | undefined;
        if (ruleEditDraft.proposal_atlas_key) {
          const res = await api.post(
            '/api/transaction-classification-rules/rule-proposals/accept-atlas',
            {
              atlas_key: ruleEditDraft.proposal_atlas_key,
              payload_overrides: payload
            },
            CLASSIFICATION_RULE_SAVE_CONFIG
          );
          acceptedRule = res.data?.rule as ClassificationRule | undefined;
        } else         if (ruleEditDraft.proposal_id) {
          const res = await api.post(
            `/api/transaction-classification-rules/rule-proposals/${ruleEditDraft.proposal_id}/accept`,
            { payload_overrides: payload },
            CLASSIFICATION_RULE_SAVE_CONFIG
          );
          acceptedRule = res.data?.rule as ClassificationRule | undefined;
        }
        const accepted = acceptedRule;
        if (accepted) {
          setRulesPayload((prev) => {
            if (!prev) return { rules: [accepted] };
            const exists = prev.rules.some((r) => r.id === accepted.id);
            if (exists) {
              return { rules: prev.rules.map((r) => (r.id === accepted.id ? { ...r, ...accepted } : r)) };
            }
            return { rules: [accepted, ...prev.rules] };
          });
        }
        setProposalsPayload((prev) => {
          if (!prev) return prev;
          return {
            ...prev,
            proposals: prev.proposals.filter((p) => {
              if (ruleEditDraft.proposal_atlas_key) return p.atlas_key !== ruleEditDraft.proposal_atlas_key;
              if (ruleEditDraft.proposal_id) return Number(p.id || 0) !== Number(ruleEditDraft.proposal_id);
              return true;
            })
          };
        });
        refreshRulesTabDatasetsInBackground();
        const acceptedPrefixed =
          accepted?.id != null ? formatClassificationRulePrefixedId(accepted.id, (accepted as any)?.name || name) : '';
        const accProposalNum =
          ruleEditDraft.proposal_id != null ? Math.trunc(Number(ruleEditDraft.proposal_id)) : NaN;
        if (Number.isFinite(accProposalNum) && accProposalNum > 0) {
          const nextP = rebuildProposalIdsAfterClassificationProposalAccepted(
            rulesProposalIdsFilter,
            accProposalNum
          );
          setRulesProposalIdsFilter(nextP);
          if (accepted?.id) {
            setRulesIdFilter(acceptedPrefixed);
          }
          setRulesStatusFilter(nextP.trim() ? 'all' : 'confirmed');
        } else {
          promoteRulesStatusForConfirmedEdits();
          if (accepted?.id) {
            setRulesIdFilter(acceptedPrefixed);
          }
        }
        const acceptedLabel = formatClassificationRuleToastLabel(accepted, name || 'accepted rule');
        const acceptedIdNum = accepted?.id != null ? Math.trunc(Number(accepted.id)) : NaN;
        openBackfillAfterAccept({
          ruleName: String((accepted as any)?.name || name || 'accepted rule'),
          prefixedId: acceptedPrefixed || '',
          target:
            Number.isFinite(acceptedIdNum) && acceptedIdNum > 0
              ? { kind: 'classification', id: acceptedIdNum }
              : null
        });
        setSuccessMessage(`Accepted edited proposal: ${acceptedLabel}.`);
      } else {
        const res = isNewRule
          ? await api.post('/api/transaction-classification-rules/', payload, CLASSIFICATION_RULE_SAVE_CONFIG)
          : await api.put(
              `/api/transaction-classification-rules/${persistedRuleId}`,
              payload,
              CLASSIFICATION_RULE_SAVE_CONFIG
            );
        const updated = res.data?.rule as ClassificationRule | undefined;
        if (updated) {
          setRulesPayload((prev) => {
            if (!prev) return { rules: [updated] };
            if (isNewRule) return { rules: [updated, ...prev.rules] };
            return {
              rules: prev.rules.map((r) => (r.id === updated.id ? { ...r, ...updated } : r))
            };
          });
        }
        refreshRulesTabDatasetsInBackground();
        promoteRulesStatusForConfirmedEdits();
        const ruleLabel = formatClassificationRuleToastLabel(updated, name);
        setSuccessMessage(isNewRule ? `Rule added: ${ruleLabel}.` : `Rule saved: ${ruleLabel}.`);
      }
        setRuleEditConflictRuleId(null);
      } catch (err: any) {
        const data = err?.response?.data;
        const st = err?.response?.status;
        const rawCid = data?.conflict_rule_id;
        const cid = rawCid != null ? Math.trunc(Number(rawCid)) : NaN;
        const conflictReason = String(data?.conflict_reason || '').trim();
        setRuleEditDraft(draftForRetry);
        setRuleEditMatchCriteriaEnabled(initialRuleEditMatchCriteriaEnabled(draftForRetry));
        setRuleEditReadOnly(false);
        setRuleEditTargetType(targetTypeForRetry);
        if (st === 409 && Number.isFinite(cid) && cid > 0) {
          setRuleEditConflictRuleId(cid);
        } else {
          setRuleEditConflictRuleId(null);
        }
        const baseMessage = getHttpErrorMessageForUser(err, 'Could not save rule');
        setSuccessMessage('');
        setRuleEditError(conflictReason ? `${baseMessage} Why: ${conflictReason}` : baseMessage);
        // Auto-open side-by-side so the conflict is resolvable without hunting for C#.
        if (st === 409 && Number.isFinite(cid) && cid > 0) {
          void openClassificationConflictSideBySide(cid);
        }
      } finally {
        setRuleEditSaveInFlight(false);
        setRuleEditSaving(false);
        setRuleTableSaveHighlight(null);
      }
    })();
  };

  const saveConflictRuleFromDialog = () => {
    if (!conflictPairEditDraft) return;
    if (conflictPairEditSaving) return;
    if (isAnyRuleSaveInFlight) {
      setConflictPairEditError(ruleSaveBusyMessage);
      return;
    }
    if (ruleEditReadOnly) return;
    const d = conflictPairEditDraft;
    const persistedId = Math.trunc(Number(d.id));
    if (!Number.isFinite(persistedId) || persistedId <= 0) {
      setConflictPairEditError('Conflicting rule is missing a valid id.');
      return;
    }
    const name = d.name.trim();
    if (!name) {
      setConflictPairEditError('Rule name is required.');
      return;
    }
    if (!Number.isFinite(d.category_id) || d.category_id <= 0) {
      setConflictPairEditError('Select a valid category.');
      return;
    }
    const priorityParsed = parseInt(d.priority, 10);
    if (!Number.isFinite(priorityParsed)) {
      setConflictPairEditError('Priority must be a whole number.');
      return;
    }
    const parseAmount = (s: string, label: string): number | null | undefined => {
      const t = s.trim();
      if (!t) return null;
      const n = Number(t);
      if (!Number.isFinite(n)) {
        setConflictPairEditError(`${label} must be a valid number or empty.`);
        return undefined;
      }
      return n;
    };
    const amountMin = parseAmount(d.amount_min, 'Amount min');
    if (amountMin === undefined) return;
    const amountMax = parseAmount(d.amount_max, 'Amount max');
    if (amountMax === undefined) return;
    const canonicalList = dedupeSupplierNameList(d.supplier_name_overrides);
    const normalizedSupplierContains = normalizeRuleOrValues(d.supplier_contains);
    const normalizedDescriptionContains = normalizeRuleOrValues(d.description_contains);
    const normalizedReferenceContains = normalizeRuleOrValues(d.reference_contains);
    const hasTextMatch =
      normalizedSupplierContains !== '' ||
      normalizedDescriptionContains !== '' ||
      normalizedReferenceContains !== '';
    const hasAmountRange = amountMin != null || amountMax != null;
    const hasCanonicalSupplierOnlyMatch =
      canonicalList.length > 0 &&
      normalizedSupplierContains === '' &&
      normalizedDescriptionContains === '' &&
      normalizedReferenceContains === '';
    if (!hasTextMatch && !hasAmountRange && !hasCanonicalSupplierOnlyMatch) {
      setConflictPairEditError(
        'Add a supplier/description/reference match, set amount min/max, or choose a canonical supplier alone (no separate text matchers) so the rule does not match every transaction.'
      );
      return;
    }
    const payload = {
      name,
      priority: priorityParsed,
      category_id: d.category_id,
      is_active: d.is_active,
      supplier_name_overrides: canonicalList.length ? canonicalList : null,
      supplier_name_override: canonicalList.length ? canonicalList[0] : null,
      supplier_contains: normalizedSupplierContains || null,
      description_contains: normalizedDescriptionContains || null,
      description_text_match_operator:
        (d.description_text_match_operator || d.text_match_operator || 'exact').trim() || 'exact',
      reference_contains: normalizedReferenceContains || null,
      reference_text_match_operator:
        (d.reference_text_match_operator || d.text_match_operator || 'exact').trim() || 'exact',
      text_match_operator: (d.text_match_operator || 'exact').trim() || 'exact',
      amount_min: amountMin,
      amount_max: amountMax,
      account_scope: normalizeRuleAccountScope(d.account_scope),
      currency: d.currency.trim() || null,
      recurrence_type: d.recurrence_type.trim() || null,
      notes: d.notes.trim() || null
    };
    setConflictPairEditError('');
    setConflictPairEditSaving(true);
    void (async () => {
      try {
        const res = await api.put(
          `/api/transaction-classification-rules/${persistedId}`,
          payload,
          CLASSIFICATION_RULE_SAVE_CONFIG
        );
        const updated = res.data?.rule as ClassificationRule | undefined;
        if (updated) {
          setRulesPayload((prev) => {
            if (!prev) return { rules: [updated] };
            return { rules: prev.rules.map((r) => (r.id === updated.id ? { ...r, ...updated } : r)) };
          });
          const cpDraft = classificationRuleToEditDraft(updated);
          setConflictPairEditDraft(cpDraft);
          setConflictPairMatchCriteriaEnabled(initialRuleEditMatchCriteriaEnabled(cpDraft));
        }
        setSuccessMessage(`Conflicting rule saved: ${formatClassificationRuleToastLabel(updated, name)}.`);
        void refreshRulesTabDatasets().catch(() => undefined);
        promoteRulesStatusForConfirmedEdits();
      } catch (err: any) {
        const data = err?.response?.data;
        const st = err?.response?.status;
        const rawCid = data?.conflict_rule_id;
        const cid = rawCid != null ? Math.trunc(Number(rawCid)) : NaN;
        const conflictReason = String(data?.conflict_reason || '').trim();
        if (st === 409 && Number.isFinite(cid) && cid > 0) {
          setRuleEditConflictRuleId(cid);
        } else {
          setRuleEditConflictRuleId(null);
        }
        const baseMessage = getHttpErrorMessageForUser(err, 'Could not save rule');
        setConflictPairEditError(conflictReason ? `${baseMessage} Why: ${conflictReason}` : baseMessage);
      } finally {
        setConflictPairEditSaving(false);
      }
    })();
  };

  useEffect(() => {
    if (!queuedRuleEditSaveRequested) return;
    if (isAnyRuleSaveInFlight) return;
    setQueuedRuleEditSaveRequested(false);
    setRuleEditError('');
    setSuccessMessage('Running queued rule save…');
    saveRuleFromDialog();
  }, [queuedRuleEditSaveRequested, isAnyRuleSaveInFlight]);

  const openVatRuleCreate = useCallback(() => {
    setVatRuleFormError('');
    setVatRuleFieldErrors({});
    setVatRuleMatchCriteriaMenuAnchorEl(null);
    setVatRuleOutputMenuAnchorEl(null);
    setVatRuleOutputVatEnabled(false);
    setVatRuleDraft({
      id: 0,
      category_ids: [],
      name: '',
      priority: 100,
      vat_rate: '',
      account_scope: 'both',
      is_active: true
    });
    setVatRuleOpen(true);
  }, []);

  const openVatRuleEditor = useCallback((row: CategoryVatRuleRow) => {
    setVatRuleFormError('');
    setVatRuleFieldErrors({});
    setVatRuleMatchCriteriaMenuAnchorEl(null);
    setVatRuleOutputMenuAnchorEl(null);
    setVatRuleOutputVatEnabled(true);
    const categoryIdsRaw =
      Array.isArray(row.category_ids) && row.category_ids.length > 0
        ? row.category_ids
        : [row.category_id];
    setVatRuleDraft({
      id: row.id,
      category_ids: Array.from(
        new Set(
          categoryIdsRaw
            .map((id) => Number(id))
            .filter((id) => Number.isFinite(id) && id > 0)
        )
      ),
      name: String(row.name || '').trim(),
      priority: Number.isFinite(Number(row.priority)) ? Number(row.priority) : 100,
      vat_rate: String(row.vat_rate || '').trim(),
      account_scope: normalizeRuleAccountScope(row.account_scope),
      is_active: row.is_active !== false
    });
    setVatRuleOpen(true);
  }, []);

  const submitVatRule = useCallback(async () => {
    if (!vatRuleDraft) return;
    if (vatRuleSaving) return;
    if (supplierRuleSaving || specialCaseRuleSaving) {
      setVatRuleFormError(ruleSaveBusyMessage);
      return;
    }
    const categoryIds = Array.from(new Set(vatRuleDraft.category_ids.filter((id) => Number.isFinite(id) && id > 0)));
    const vatRate = canonicalizeVatRate(vatRuleDraft.vat_rate);
    const ruleName = String(vatRuleDraft.name || '').trim();
    const priorityParsed = parseInt(String(vatRuleDraft.priority ?? 100), 10);
    const nextFieldErrors: { category_ids?: string; vat_rate?: string; priority?: string } = {};
    if (categoryIds.length === 0) {
      nextFieldErrors.category_ids = 'Select at least one category.';
    }
    if (!vatRuleOutputVatEnabled) {
      nextFieldErrors.vat_rate = 'Add an output and select VAT Rate.';
    } else if (!vatRate) {
      nextFieldErrors.vat_rate = 'VAT rate is required.';
    }
    if (!Number.isFinite(priorityParsed)) {
      nextFieldErrors.priority = 'Priority must be a whole number.';
    }
    if (Object.keys(nextFieldErrors).length > 0) {
      setVatRuleFieldErrors(nextFieldErrors);
      setVatRuleFormError(
        nextFieldErrors.vat_rate ||
          nextFieldErrors.category_ids ||
          nextFieldErrors.priority ||
          'Please fix the highlighted fields.'
      );
      return;
    }
    setVatRuleFieldErrors({});
    setVatRuleFormError('');
    setVatRuleSaving(true);
    const vatHighlightId = Math.trunc(Number(vatRuleDraft.id));
    setRuleTableSaveHighlight(
      Number.isFinite(vatHighlightId) && vatHighlightId > 0
        ? { kind: 'vat', rowKey: `vat-rule-${vatHighlightId}` }
        : null
    );
    setError('');
    try {
      if (vatRuleDraft.id > 0) {
        const res = await api.put(`/api/transaction-classification-rules/vat-rules/${vatRuleDraft.id}`, {
          category_ids: categoryIds,
          name: ruleName,
          priority: priorityParsed,
          vat_rate: vatRate,
          account_scope: normalizeRuleAccountScope(vatRuleDraft.account_scope),
          is_active: vatRuleDraft.is_active
        });
        const updated = res.data?.vat_rule as CategoryVatRuleRow | undefined;
        if (updated) {
          setVatRuleRows((prev) => prev.map((r) => (r.id === updated.id ? updated : r)));
        } else {
          const rr = await api.get('/api/transaction-classification-rules/vat-rules');
          setVatRuleRows(Array.isArray(rr.data?.vat_rules) ? rr.data.vat_rules : []);
        }
      } else {
        const res = await api.post('/api/transaction-classification-rules/vat-rules', {
          category_ids: categoryIds,
          name: ruleName,
          priority: priorityParsed,
          vat_rate: vatRate,
          account_scope: normalizeRuleAccountScope(vatRuleDraft.account_scope),
          is_active: vatRuleDraft.is_active
        });
        const rows = Array.isArray(res.data?.vat_rules) ? res.data.vat_rules : null;
        if (rows) {
          setVatRuleRows(rows);
        } else {
          const rr = await api.get('/api/transaction-classification-rules/vat-rules');
          setVatRuleRows(Array.isArray(rr.data?.vat_rules) ? rr.data.vat_rules : []);
        }
      }
      setVatRuleOpen(false);
      setVatRuleDraft(null);
      setVatRuleMatchCriteriaMenuAnchorEl(null);
      setVatRuleOutputMenuAnchorEl(null);
      setVatRuleOutputVatEnabled(false);
      setVatRuleFieldErrors({});
      setVatRuleFormError('');
      void refreshRulesTabDatasets().catch(() => undefined);
      promoteRulesStatusForConfirmedEdits();
      setSuccessMessage(vatRuleDraft.id > 0 ? 'VAT rule updated.' : 'VAT rule(s) added.');
    } catch (err: any) {
      setVatRuleFormError(err?.response?.data?.error || err?.message || 'Failed to save VAT rule');
    } finally {
      setVatRuleSaving(false);
      setRuleTableSaveHighlight(null);
    }
  }, [
    vatRuleDraft,
    vatRuleOutputVatEnabled,
    ruleEditSaveInFlight,
    supplierRuleSaving,
    specialCaseRuleSaving,
    ruleSaveBusyMessage,
    promoteRulesStatusForConfirmedEdits,
    refreshRulesTabDatasets
  ]);

  const deactivateVatRule = useCallback(async (row: CategoryVatRuleRow) => {
    try {
      setError('');
      await api.delete(`/api/transaction-classification-rules/vat-rules/${row.id}`);
      setVatRuleRows((prev) => prev.filter((r) => r.id !== row.id));
      setSuccessMessage('VAT rule deleted.');
    } catch (err: any) {
      setError(err?.response?.data?.error || err?.message || 'Failed to delete VAT rule');
    }
  }, []);

  const removeVatRuleCategoryQuick = useCallback(
    async (row: CategoryVatRuleRow, categoryId: number, categoryName: string) => {
      const rid = Number(row.id);
      const cid = Number(categoryId);
      if (!Number.isFinite(rid) || rid <= 0 || !Number.isFinite(cid) || cid <= 0) return;
      if (vatRuleCategoryDeleteBusyKey) return;
      const existingIds = Array.from(
        new Set((row.category_ids || []).map((id) => Number(id)).filter((id) => Number.isFinite(id) && id > 0))
      );
      if (!existingIds.includes(cid)) return;
      if (existingIds.length <= 1) {
        setError('VAT rules must keep at least one category. Add another category first, or delete the rule.');
        return;
      }
      const ok = window.confirm(
        `Are you sure you want to delete "${categoryName}" from VAT rule ${formatPrefixedRuleId('vat', rid)}?`
      );
      if (!ok) return;
      const nextCategoryIds = existingIds.filter((id) => id !== cid);
      const busyKey = `${rid}-${cid}`;
      try {
        setVatRuleCategoryDeleteBusyKey(busyKey);
        setError('');
        const res = await api.put(`/api/transaction-classification-rules/vat-rules/${rid}`, {
          category_ids: nextCategoryIds
        });
        const updated = res.data?.vat_rule as CategoryVatRuleRow | undefined;
        if (updated) {
          setVatRuleRows((prev) => prev.map((r) => (Number(r.id) === rid ? updated : r)));
        } else {
          setVatRuleRows((prev) =>
            prev.map((r) => (Number(r.id) === rid ? { ...r, category_ids: nextCategoryIds } : r))
          );
        }
        setSuccessMessage(
          `Removed category "${categoryName}" from ${formatPrefixedRuleId('vat', rid)}.`
        );
      } catch (err: any) {
        setError(err?.response?.data?.error || err?.message || 'Failed to remove category from VAT rule');
      } finally {
        setVatRuleCategoryDeleteBusyKey(null);
      }
    },
    [vatRuleCategoryDeleteBusyKey]
  );

  const patchClassificationRuleFromTable = useCallback(
    async (rule: ClassificationRule, patch: Partial<ClassificationRulePersistPayload>) => {
      const rid = Number(rule.id);
      if (!Number.isFinite(rid) || rid <= 0) return;
      if (classificationTablePatchBusyId != null) return;
      const base = classificationRuleToPersistPayload(rule);
      const next = { ...base, ...patch } as ClassificationRulePersistPayload;
      if (!String(next.description_contains || '').trim()) {
        next.description_text_match_operator = 'exact';
      }
      if (!String(next.reference_contains || '').trim()) {
        next.reference_text_match_operator = 'exact';
      }
      if (!String(next.supplier_contains || '').trim()) {
        next.text_match_operator = 'exact';
      }
      const err = validateClassificationPersistPayload(next);
      if (err) {
        setError(err);
        return;
      }
      if (
        !window.confirm(
          `Update classification rule ${formatClassificationRulePrefixedId(rid, rule.name)}? This saves immediately.`
        )
      ) {
        return;
      }
      try {
        setClassificationTablePatchBusyId(rid);
        setError('');
        const res = await api.put(
          `/api/transaction-classification-rules/${rid}`,
          next,
          CLASSIFICATION_RULE_SAVE_CONFIG
        );
        const updated = res.data?.rule as ClassificationRule | undefined;
        if (updated) {
          setRulesPayload((prev) =>
            prev
              ? { ...prev, rules: prev.rules.map((r) => (r.id === updated.id ? { ...r, ...updated } : r)) }
              : { rules: [updated] }
          );
        }
        setSuccessMessage(`Updated ${formatClassificationRulePrefixedId(rid, rule.name)}.`);
        void refreshRulesTabDatasets().catch(() => undefined);
        promoteRulesStatusForConfirmedEdits();
      } catch (e: any) {
        setError(e?.response?.data?.error || e?.message || 'Could not update classification rule');
      } finally {
        setClassificationTablePatchBusyId(null);
      }
    },
    [classificationTablePatchBusyId, promoteRulesStatusForConfirmedEdits, refreshRulesTabDatasets]
  );

  const removeSupplierRuleCriterionQuick = useCallback(
    async (row: SupplierRuleRow, criterion: SupplierRuleMatchCriterionApi) => {
      if (supplierRuleTablePatchBusyId != null) return;
      if (
        !window.confirm(
          `Remove this match from supplier rule ${formatPrefixedRuleId('supplier', row.id)}? This saves immediately.`
        )
      ) {
        return;
      }
      const full = supplierRuleCriteriaFromAliasForDisplay(row);
      const idx = full.findIndex(
        (c) =>
          c.kind === criterion.kind &&
          String(c.value || '').trim() === String(criterion.value || '').trim() &&
          String(c.operator || 'contains').trim() === String(criterion.operator || 'contains').trim()
      );
      if (idx < 0) return;
      const next = full.filter((_, i) => i !== idx);
      const payload = buildSupplierRulePersistPayload(row, next);
      if (!payload) {
        setError('Supplier rules must keep at least one match criterion.');
        return;
      }
      try {
        setSupplierRuleTablePatchBusyId(row.id);
        setError('');
        const res = await api.put(`/api/suppliers/description-aliases/${row.id}`, payload);
        const alias = res.data?.alias as SupplierRuleRow | undefined;
        if (alias) {
          setSupplierRuleRows((prev) => prev.map((r) => (r.id === alias.id ? alias : r)));
        } else {
          const aliasRes = await api.get('/api/suppliers/description-aliases');
          setSupplierRuleRows(Array.isArray(aliasRes.data?.aliases) ? aliasRes.data.aliases : []);
        }
        setSuccessMessage(`Updated ${formatPrefixedRuleId('supplier', row.id)}.`);
        void refreshRulesTabDatasets().catch(() => undefined);
        promoteRulesStatusForConfirmedEdits();
      } catch (e: any) {
        setError(e?.response?.data?.error || e?.message || 'Could not update supplier rule');
      } finally {
        setSupplierRuleTablePatchBusyId(null);
      }
    },
    [promoteRulesStatusForConfirmedEdits, refreshRulesTabDatasets, supplierRuleTablePatchBusyId]
  );

  const openSpecialCaseRuleCreate = useCallback(() => {
    setSpecialCaseRuleCriteriaMenuAnchorEl(null);
    setSpecialCaseRuleOutputMenuAnchorEl(null);
    setSpecialCaseRuleOutputVatEnabled(false);
    setSpecialCaseRuleDraft({
      id: 0,
      name: '',
      priority: 100,
      is_active: true,
      account_scope: 'both',
      match_criteria: [],
      set_vat_rate: '',
      notes: ''
    });
    setSpecialCaseRuleOpen(true);
  }, []);

  const openSpecialCaseRuleEditor = useCallback(
    (row: SpecialCaseRuleRow) => {
      setSpecialCaseRuleCriteriaMenuAnchorEl(null);
      setSpecialCaseRuleOutputMenuAnchorEl(null);
      setSpecialCaseRuleOutputVatEnabled(true);
      setSpecialCaseRuleDraft({
        id: row.id,
        name: String(row.name || '').trim(),
        priority: Number.isFinite(Number(row.priority)) ? Number(row.priority) : 100,
        is_active: row.is_active !== false,
        account_scope: normalizeRuleAccountScope(row.account_scope),
        match_criteria: normalizeSpecialCaseCriteriaFromRow(row),
        set_vat_rate: String(row.set_vat_rate || ''),
        notes: String(row.notes || '')
      });
      setSpecialCaseRuleOpen(true);
    },
    []
  );

  const submitSpecialCaseRule = useCallback(async () => {
    if (!specialCaseRuleDraft) return;
    if (specialCaseRuleSaving) return;
    if (addCanonicalSupplierBusy) {
      setError('Finish creating the supplier, then try again.');
      return;
    }
    if (supplierRuleSaving || vatRuleSaving) {
      setError(ruleSaveBusyMessage);
      return;
    }
    const built = tryBuildSpecialCaseRulePayloadFromDraft(specialCaseRuleDraft);
    if ('error' in built) {
      setError(built.error);
      return;
    }
    const payload = built.payload;
    if (!specialCaseRuleOutputVatEnabled) {
      setError('Add an output and select VAT Rate.');
      return;
    }
    setSpecialCaseRuleSaving(true);
    const scHighlightId = Math.trunc(Number(specialCaseRuleDraft.id));
    setRuleTableSaveHighlight(
      Number.isFinite(scHighlightId) && scHighlightId > 0
        ? { kind: 'specialCase', rowKey: `special-case-rule-${scHighlightId}` }
        : null
    );
    setError('');
    try {
      let createdSpecialCase: SpecialCaseRuleRow | undefined;
      if (specialCaseRuleDraft.id > 0) {
        const res = await api.put(
          `/api/transaction-classification-rules/special-case-rules/${specialCaseRuleDraft.id}`,
          payload
        );
        const updated = res.data?.special_case_rule as SpecialCaseRuleRow | undefined;
        if (updated) {
          setSpecialCaseRuleRows((prev) => prev.map((r) => (r.id === updated.id ? updated : r)));
        } else {
          const rr = await api.get('/api/transaction-classification-rules/special-case-rules');
          setSpecialCaseRuleRows(Array.isArray(rr.data?.special_case_rules) ? rr.data.special_case_rules : []);
        }
      } else {
        const res = await api.post('/api/transaction-classification-rules/special-case-rules', payload);
        const created = res.data?.special_case_rule as SpecialCaseRuleRow | undefined;
        if (created) {
          createdSpecialCase = created;
          setSpecialCaseRuleRows((prev) =>
            [created, ...prev].sort((a, b) => Number(a.priority || 0) - Number(b.priority || 0) || a.id - b.id)
          );
        } else {
          const rr = await api.get('/api/transaction-classification-rules/special-case-rules');
          setSpecialCaseRuleRows(Array.isArray(rr.data?.special_case_rules) ? rr.data.special_case_rules : []);
        }
      }
      const wasNewSpecialCaseRule = specialCaseRuleDraft.id <= 0;
      const savedSpecialCaseName = String(specialCaseRuleDraft.name || '').trim();
      const createdSpecialCaseId =
        createdSpecialCase?.id != null ? Math.trunc(Number(createdSpecialCase.id)) : NaN;
      setSpecialCaseRuleOpen(false);
      setSpecialCaseRuleDraft(null);
      setSpecialCaseRuleOutputMenuAnchorEl(null);
      setSpecialCaseRuleOutputVatEnabled(false);
      void refreshRulesTabDatasets().catch(() => undefined);
      promoteRulesStatusForConfirmedEdits();
      setSuccessMessage(wasNewSpecialCaseRule ? 'Special Case rule added.' : 'Special Case rule updated.');
      if (wasNewSpecialCaseRule) {
        openBackfillAfterAccept({
          ruleName: savedSpecialCaseName || 'Special Case rule',
          prefixedId:
            Number.isFinite(createdSpecialCaseId) && createdSpecialCaseId > 0
              ? formatPrefixedRuleId('specialCase', createdSpecialCaseId)
              : '',
          target:
            Number.isFinite(createdSpecialCaseId) && createdSpecialCaseId > 0
              ? { kind: 'specialCase', id: createdSpecialCaseId }
              : null
        });
      }
    } catch (err: any) {
      setError(err?.response?.data?.error || err?.message || 'Failed to save Special Case rule');
    } finally {
      setSpecialCaseRuleSaving(false);
      setRuleTableSaveHighlight(null);
    }
  }, [
    specialCaseRuleDraft,
    specialCaseRuleOutputVatEnabled,
    ruleEditSaveInFlight,
    supplierRuleSaving,
    vatRuleSaving,
    ruleSaveBusyMessage,
    promoteRulesStatusForConfirmedEdits,
    refreshRulesTabDatasets,
    openBackfillAfterAccept
  ]);

  const deactivateSpecialCaseRule = useCallback(async (row: SpecialCaseRuleRow) => {
    try {
      setError('');
      await api.delete(`/api/transaction-classification-rules/special-case-rules/${row.id}`);
      setSpecialCaseRuleRows((prev) => prev.map((r) => (r.id === row.id ? { ...r, is_active: false } : r)));
      setSuccessMessage('Special Case rule deactivated.');
    } catch (err: any) {
      setError(err?.response?.data?.error || err?.message || 'Failed to deactivate Special Case rule');
    }
  }, []);

  const duplicateClassificationRule = useCallback(
    async (rule: ClassificationRule) => {
      const rid = Number(rule.id);
      if (!Number.isFinite(rid) || rid <= 0) return;
      if (ruleDuplicateBusyKey || isAnyRuleSaveInFlight || proposalBusyKey) {
        setError(ruleSaveBusyMessage);
        return;
      }
      const busyKey = `dup-cls-${rid}`;
      try {
        setRuleDuplicateBusyKey(busyKey);
        setError('');
        const base = classificationRuleToPersistPayload(rule);
        const payload = { ...base, name: duplicateOfRuleName(base.name) };
        const err = validateClassificationPersistPayload(payload);
        if (err) {
          setError(err);
          return;
        }
        const res = await api.post('/api/transaction-classification-rules/', payload, CLASSIFICATION_RULE_SAVE_CONFIG);
        const created = res.data?.rule as ClassificationRule | undefined;
        if (created) {
          setRulesPayload((prev) => {
            if (!prev) return { rules: [created] };
            return { rules: [created, ...prev.rules] };
          });
        }
        promoteRulesStatusForConfirmedEdits();
        setSuccessMessage(`Duplicated as ${payload.name}.`);
        void refreshRulesTabDatasets().catch(() => undefined);
      } catch (e: any) {
        setError(e?.response?.data?.error || e?.message || 'Could not duplicate classification rule');
      } finally {
        setRuleDuplicateBusyKey('');
      }
    },
    [
      ruleDuplicateBusyKey,
      isAnyRuleSaveInFlight,
      proposalBusyKey,
      promoteRulesStatusForConfirmedEdits,
      refreshRulesTabDatasets,
      ruleSaveBusyMessage
    ]
  );

  const duplicateClassificationRuleFromDialog = useCallback(async () => {
    if (ruleEditTargetType !== 'classification' || !ruleEditDraft || conflictPairEditDraft) return;
    if (
      ruleDuplicateBusyKey ||
      ruleEditSaveInFlight ||
      addCanonicalSupplierBusy ||
      conflictPairEditSaving ||
      supplierRuleSaving ||
      vatRuleSaving ||
      specialCaseRuleSaving
    ) {
      setRuleEditError(ruleSaveBusyMessage);
      return;
    }
    const busyKey = 'dup-dialog-cls';
    try {
      setRuleDuplicateBusyKey(busyKey);
      setRuleEditError('');
      const dupDraft: RuleEditDraft = {
        ...ruleEditDraft,
        id: 0,
        isNew: true,
        proposal_id: null,
        proposal_atlas_key: null,
        name: duplicateOfRuleName(ruleEditDraft.name.trim() || String(ruleEditDraft.name || '').trim())
      };
      const built = classificationPersistPayloadFromRuleEditDraft(dupDraft);
      if ('error' in built) {
        setRuleEditError(built.error);
        return;
      }
      const res = await api.post(
        '/api/transaction-classification-rules/',
        built.payload,
        CLASSIFICATION_RULE_SAVE_CONFIG
      );
      const created = res.data?.rule as ClassificationRule | undefined;
      if (created) {
        setRulesPayload((prev) => {
          if (!prev) return { rules: [created] };
          return { rules: [created, ...prev.rules] };
        });
      }
      promoteRulesStatusForConfirmedEdits();
      setSuccessMessage(`Duplicated as ${built.payload.name}.`);
      void refreshRulesTabDatasets().catch(() => undefined);
      setAddCanonicalSupplierOpen(false);
      setAddCanonicalSupplierName('');
      setAddCanonicalSupplierError('');
      setRuleMatchHelpAnchorEl(null);
      setRuleEditMatchCriteriaMenuAnchorEl(null);
      setRuleEditDraft(null);
      setRuleEditReadOnly(false);
      setRuleEditTargetType('classification');
      setRuleEditOutputMenuAnchorEl(null);
      setRuleEditOutputCategoryEnabled(false);
      setRuleEditOutputSupplierEnabled(false);
      setRuleEditSaveInFlight(false);
      setRuleEditConflictRuleId(null);
      setConflictPairEditDraft(null);
      setConflictPairEditError('');
      resetConflictPairRuleEditorUi();
    } catch (e: any) {
      setRuleEditError(e?.response?.data?.error || e?.message || 'Could not duplicate rule');
    } finally {
      setRuleDuplicateBusyKey('');
    }
  }, [
    ruleEditTargetType,
    ruleEditDraft,
    conflictPairEditDraft,
    ruleDuplicateBusyKey,
    ruleEditSaveInFlight,
    addCanonicalSupplierBusy,
    conflictPairEditSaving,
    supplierRuleSaving,
    vatRuleSaving,
    specialCaseRuleSaving,
    ruleSaveBusyMessage,
    promoteRulesStatusForConfirmedEdits,
    refreshRulesTabDatasets,
    resetConflictPairRuleEditorUi
  ]);

  const duplicateClassificationProposalAsRule = useCallback(
    async (row: RuleProposalRow, proposalCategoryFallbackId: number) => {
      if (ruleDuplicateBusyKey || isAnyRuleSaveInFlight || proposalBusyKey) {
        setError(ruleSaveBusyMessage);
        return;
      }
      const busyKey = `dup-cp-${row.id ?? 'v'}-${row.virtual ? String(row.atlas_key || '') : 'db'}`;
      try {
        setRuleDuplicateBusyKey(busyKey);
        setError('');
        const draft = classificationProposalToEditDraft(row, categories, proposalCategoryFallbackId);
        const dupDraft: RuleEditDraft = {
          ...draft,
          id: 0,
          proposal_id: null,
          proposal_atlas_key: null,
          name: duplicateOfRuleName(draft.name.trim() || String(row.proposed_name || '').trim())
        };
        const built = classificationPersistPayloadFromRuleEditDraft(dupDraft);
        if ('error' in built) {
          setError(built.error);
          return;
        }
        const res = await api.post(
          '/api/transaction-classification-rules/',
          built.payload,
          CLASSIFICATION_RULE_SAVE_CONFIG
        );
        const created = res.data?.rule as ClassificationRule | undefined;
        if (created) {
          setRulesPayload((prev) => {
            if (!prev) return { rules: [created] };
            return { rules: [created, ...prev.rules] };
          });
        }
        promoteRulesStatusForConfirmedEdits();
        setSuccessMessage(`Duplicated as ${built.payload.name}.`);
        void refreshRulesTabDatasets().catch(() => undefined);
      } catch (e: any) {
        setError(e?.response?.data?.error || e?.message || 'Could not duplicate proposal as new rule');
      } finally {
        setRuleDuplicateBusyKey('');
      }
    },
    [
      categories,
      ruleDuplicateBusyKey,
      isAnyRuleSaveInFlight,
      proposalBusyKey,
      promoteRulesStatusForConfirmedEdits,
      refreshRulesTabDatasets,
      ruleSaveBusyMessage
    ]
  );

  const duplicateSupplierRule = useCallback(
    async (row: SupplierRuleRow) => {
      const id = row.id;
      if (!Number.isFinite(id) || id <= 0) return;
      if (ruleDuplicateBusyKey || isAnyRuleSaveInFlight || supplierRuleTablePatchBusyId != null) {
        setError(ruleSaveBusyMessage);
        return;
      }
      const busyKey = `dup-sup-${id}`;
      try {
        setRuleDuplicateBusyKey(busyKey);
        setError('');
        const criteria = supplierRuleCriteriaFromAliasForDisplay(row);
        const base = buildSupplierRulePersistPayload(row, criteria);
        if (!base) {
          setError('Could not duplicate supplier rule (missing match criteria).');
          return;
        }
        const payload = {
          ...base,
          rule_name: duplicateOfRuleName(String(row.rule_name || '').trim())
        };
        const res = await api.post('/api/suppliers/description-aliases', payload);
        const createdAlias = res.data?.alias as SupplierRuleRow | undefined;
        if (createdAlias) {
          setSupplierRuleRows((prev) => [createdAlias, ...prev]);
        } else {
          const aliasRes = await api.get('/api/suppliers/description-aliases');
          setSupplierRuleRows(Array.isArray(aliasRes.data?.aliases) ? aliasRes.data.aliases : []);
        }
        setSuccessMessage(`Duplicated supplier rule as ${String(payload.rule_name)}.`);
        void refreshRulesTabDatasets().catch(() => undefined);
        promoteRulesStatusForConfirmedEdits();
      } catch (e: any) {
        setError(e?.response?.data?.error || e?.message || 'Could not duplicate supplier rule');
      } finally {
        setRuleDuplicateBusyKey('');
      }
    },
    [
      ruleDuplicateBusyKey,
      isAnyRuleSaveInFlight,
      supplierRuleTablePatchBusyId,
      promoteRulesStatusForConfirmedEdits,
      refreshRulesTabDatasets,
      ruleSaveBusyMessage
    ]
  );

  const duplicateSupplierRuleProposal = useCallback(
    async (row: SupplierRuleProposalRow) => {
      if (ruleDuplicateBusyKey || isAnyRuleSaveInFlight || proposalBusyKey) {
        setError(ruleSaveBusyMessage);
        return;
      }
      const stub = supplierRuleRowStubFromProposal(row);
      if (!stub) {
        setError('Cannot duplicate: proposal is missing canonical supplier name.');
        return;
      }
      const pid = supplierRuleProposalDbId(row);
      const busyKey = `dup-sp-${pid ?? 'na'}`;
      try {
        setRuleDuplicateBusyKey(busyKey);
        setError('');
        const criteria = supplierRuleCriteriaFromProposalPayloadForDisplay(row.payload || {});
        const base = buildSupplierRulePersistPayload(stub, criteria);
        if (!base) {
          setError('Could not duplicate supplier proposal (missing match criteria).');
          return;
        }
        const payload = {
          ...base,
          rule_name: duplicateOfRuleName(String(stub.rule_name || '').trim())
        };
        const res = await api.post('/api/suppliers/description-aliases', payload);
        const createdAlias = res.data?.alias as SupplierRuleRow | undefined;
        if (createdAlias) {
          setSupplierRuleRows((prev) => [createdAlias, ...prev]);
        } else {
          const aliasRes = await api.get('/api/suppliers/description-aliases');
          setSupplierRuleRows(Array.isArray(aliasRes.data?.aliases) ? aliasRes.data.aliases : []);
        }
        setSuccessMessage(`Duplicated supplier rule as ${String(payload.rule_name)}.`);
        void refreshRulesTabDatasets().catch(() => undefined);
        promoteRulesStatusForConfirmedEdits();
      } catch (e: any) {
        setError(e?.response?.data?.error || e?.message || 'Could not duplicate supplier proposal');
      } finally {
        setRuleDuplicateBusyKey('');
      }
    },
    [
      ruleDuplicateBusyKey,
      isAnyRuleSaveInFlight,
      proposalBusyKey,
      promoteRulesStatusForConfirmedEdits,
      refreshRulesTabDatasets,
      ruleSaveBusyMessage
    ]
  );

  const duplicateVatRule = useCallback(
    async (row: CategoryVatRuleRow) => {
      const id = Number(row.id);
      if (!Number.isFinite(id) || id <= 0) return;
      if (ruleDuplicateBusyKey || isAnyRuleSaveInFlight || vatRuleCategoryDeleteBusyKey) {
        setError(ruleSaveBusyMessage);
        return;
      }
      const busyKey = `dup-vat-${id}`;
      try {
        setRuleDuplicateBusyKey(busyKey);
        setError('');
        const categoryIdsRaw =
          Array.isArray(row.category_ids) && row.category_ids.length > 0
            ? row.category_ids
            : [row.category_id];
        const categoryIds = Array.from(
          new Set(categoryIdsRaw.map((cid) => Number(cid)).filter((cid) => Number.isFinite(cid) && cid > 0))
        );
        const vatRate = canonicalizeVatRate(String(row.vat_rate || ''));
        const priorityParsed = parseInt(String(row.priority ?? 100), 10);
        if (categoryIds.length === 0 || !vatRate || !Number.isFinite(priorityParsed)) {
          setError('Could not duplicate VAT rule (invalid category, VAT rate, or priority).');
          return;
        }
        const newName = duplicateOfRuleName(String(row.name || '').trim());
        const res = await api.post('/api/transaction-classification-rules/vat-rules', {
          category_ids: categoryIds,
          name: newName,
          priority: priorityParsed,
          vat_rate: vatRate,
          account_scope: normalizeRuleAccountScope(row.account_scope),
          is_active: row.is_active !== false
        });
        const rows = Array.isArray(res.data?.vat_rules) ? res.data.vat_rules : null;
        if (rows) {
          setVatRuleRows(rows);
        } else {
          const rr = await api.get('/api/transaction-classification-rules/vat-rules');
          setVatRuleRows(Array.isArray(rr.data?.vat_rules) ? rr.data.vat_rules : []);
        }
        setSuccessMessage(`Duplicated VAT rule as ${newName}.`);
        void refreshRulesTabDatasets().catch(() => undefined);
        promoteRulesStatusForConfirmedEdits();
      } catch (e: any) {
        setError(e?.response?.data?.error || e?.message || 'Could not duplicate VAT rule');
      } finally {
        setRuleDuplicateBusyKey('');
      }
    },
    [
      ruleDuplicateBusyKey,
      isAnyRuleSaveInFlight,
      vatRuleCategoryDeleteBusyKey,
      promoteRulesStatusForConfirmedEdits,
      refreshRulesTabDatasets,
      ruleSaveBusyMessage
    ]
  );

  const duplicateSpecialCaseRule = useCallback(
    async (row: SpecialCaseRuleRow) => {
      const id = Number(row.id);
      if (!Number.isFinite(id) || id <= 0) return;
      if (ruleDuplicateBusyKey || isAnyRuleSaveInFlight || specialCaseRuleSaving) {
        setError(ruleSaveBusyMessage);
        return;
      }
      const busyKey = `dup-sc-${id}`;
      try {
        setRuleDuplicateBusyKey(busyKey);
        setError('');
        const draft: SpecialCaseRuleDraft = {
          id: 0,
          name: duplicateOfRuleName(String(row.name || '').trim()),
          priority: Number.isFinite(Number(row.priority)) ? Number(row.priority) : 100,
          is_active: row.is_active !== false,
          account_scope: normalizeRuleAccountScope(row.account_scope),
          match_criteria: normalizeSpecialCaseCriteriaFromRow(row),
          set_vat_rate: String(row.set_vat_rate || ''),
          notes: String(row.notes || '')
        };
        const built = tryBuildSpecialCaseRulePayloadFromDraft(draft);
        if ('error' in built) {
          setError(built.error);
          return;
        }
        const res = await api.post('/api/transaction-classification-rules/special-case-rules', built.payload);
        const created = res.data?.special_case_rule as SpecialCaseRuleRow | undefined;
        if (created) {
          setSpecialCaseRuleRows((prev) =>
            [created, ...prev].sort((a, b) => Number(a.priority || 0) - Number(b.priority || 0) || a.id - b.id)
          );
        } else {
          const rr = await api.get('/api/transaction-classification-rules/special-case-rules');
          setSpecialCaseRuleRows(Array.isArray(rr.data?.special_case_rules) ? rr.data.special_case_rules : []);
        }
        setSuccessMessage(`Duplicated special case rule as ${String(built.payload.name)}.`);
        void refreshRulesTabDatasets().catch(() => undefined);
        promoteRulesStatusForConfirmedEdits();
      } catch (e: any) {
        setError(e?.response?.data?.error || e?.message || 'Could not duplicate special case rule');
      } finally {
        setRuleDuplicateBusyKey('');
      }
    },
    [
      ruleDuplicateBusyKey,
      isAnyRuleSaveInFlight,
      specialCaseRuleSaving,
      promoteRulesStatusForConfirmedEdits,
      refreshRulesTabDatasets,
      ruleSaveBusyMessage
    ]
  );

  const saveSupplierVatPolicy = async (supplierId: number) => {
    const row = supplierVatRows.find((r) => r.supplier_id === supplierId);
    if (!row) return;
    const m = row.lookbackInput.trim();
    let months: number | null = null;
    if (m !== '') {
      const n = parseInt(m, 10);
      if (Number.isNaN(n) || n < 1 || n > 240) {
        setError('VAT invoice lookback must be blank or an integer from 1 to 240.');
        return;
      }
      months = n;
    }
    const dateRaw = row.lastRetrievedInput.trim();
    if (dateRaw && !/^\d{4}-\d{2}-\d{2}$/.test(dateRaw)) {
      setError('Last retrieved date must be YYYY-MM-DD or blank.');
      return;
    }
    try {
      setSavingSupplierVatId(supplierId);
      setError('');
      const res = await api.patch(`/api/suppliers/vat-invoice-policies/${supplierId}`, {
        vat_invoice_lookback_months: months,
        vat_invoices_last_retrieved_at: dateRaw || null
      });
      const d = res.data;
      setSupplierVatRows((prev) =>
        prev.map((r) =>
          r.supplier_id === supplierId
            ? supplierVatRowFromApi({
                supplier_id: supplierId,
                name: r.name,
                is_active: r.is_active,
                vat_invoice_lookback_months: d.vat_invoice_lookback_months ?? null,
                vat_invoices_last_retrieved_at: d.vat_invoices_last_retrieved_at ?? null,
                retrieval_warning: d.retrieval_warning ?? null
              })
            : r
        )
      );
      setSuccessMessage(d.message || 'Supplier VAT invoice settings saved.');
    } catch (err: any) {
      setError(err?.response?.data?.error || 'Failed to save supplier VAT invoice settings');
    } finally {
      setSavingSupplierVatId(null);
    }
  };

  const refreshSupplierVatPolicies = useCallback(async () => {
    const res = await api.get('/api/suppliers/vat-invoice-policies');
    const rows = Array.isArray(res.data?.rows) ? res.data.rows : [];
    setSupplierVatRows(rows.map((row: any) => supplierVatRowFromApi(row)));
  }, []);

  const addSupplierVatRow = async () => {
    if (vatAddSupplierId == null) return;
    try {
      setAddingSupplierVatRow(true);
      setSupplierVatAddError('');
      setError('');
      const res = await api.post('/api/suppliers/vat-invoice-policies', { supplier_id: vatAddSupplierId });
      const row = res.data?.row;
      if (row) {
        setSupplierVatRows((prev) => {
          const next = [...prev, supplierVatRowFromApi(row)];
          next.sort((a, b) => a.name.localeCompare(b.name, undefined, { sensitivity: 'base' }));
          return next;
        });
      } else {
        await refreshSupplierVatPolicies();
      }
      setVatAddSupplierId(null);
      setSupplierVatAddOpen(false);
      setSuccessMessage(res.data?.message || 'Supplier added to VAT lookback list.');
    } catch (err: any) {
      setSupplierVatAddError(err?.response?.data?.error || 'Failed to add supplier to VAT lookback list');
    } finally {
      setAddingSupplierVatRow(false);
    }
  };

  const openSupplierVatAddDialog = () => {
    setVatAddSupplierId(null);
    setSupplierVatAddError('');
    setSupplierVatAddOpen(true);
  };

  const closeSupplierVatAddDialog = () => {
    if (addingSupplierVatRow) return;
    setSupplierVatAddOpen(false);
    setVatAddSupplierId(null);
    setSupplierVatAddError('');
  };

  const removeSupplierVatRow = async (supplierId: number) => {
    try {
      setRemovingSupplierVatId(supplierId);
      setError('');
      await api.delete(`/api/suppliers/vat-invoice-policies/${supplierId}`);
      setSupplierVatRows((prev) => prev.filter((r) => r.supplier_id !== supplierId));
      setSelectedSupplierVatIds((prev) => prev.filter((id) => id !== supplierId));
      setSuccessMessage('Supplier deleted from VAT lookback list.');
    } catch (err: any) {
      setError(err?.response?.data?.error || 'Failed to delete supplier from VAT lookback list');
    } finally {
      setRemovingSupplierVatId(null);
    }
  };

  const toggleSupplierVatRowSelected = useCallback((supplierId: number, checked: boolean) => {
    setSelectedSupplierVatIds((prev) =>
      checked ? (prev.includes(supplierId) ? prev : [...prev, supplierId]) : prev.filter((x) => x !== supplierId)
    );
  }, []);

  const toggleSelectAllVisibleSupplierVat = useCallback(
    (checked: boolean) => {
      const ids = supplierVatRowsFiltered.map((r) => r.supplier_id);
      setSelectedSupplierVatIds((prev) => {
        if (checked) return Array.from(new Set([...prev, ...ids]));
        return prev.filter((id) => !ids.includes(id));
      });
    },
    [supplierVatRowsFiltered]
  );

  const bulkDeleteSelectedSupplierVatRows = async () => {
    const ids = selectedSupplierVatIds.filter((id) => supplierVatRows.some((r) => r.supplier_id === id));
    if (ids.length === 0) return;
    if (
      !window.confirm(
        `Delete ${ids.length} supplier(s) from the VAT lookback list? Saved lookback months and last-retrieved dates will be removed.`
      )
    ) {
      return;
    }
    try {
      setBulkDeletingSupplierVat(true);
      setError('');
      await Promise.all(ids.map((id) => api.delete(`/api/suppliers/vat-invoice-policies/${id}`)));
      const idSet = new Set(ids);
      setSupplierVatRows((prev) => prev.filter((r) => !idSet.has(r.supplier_id)));
      setSelectedSupplierVatIds((prev) => prev.filter((id) => !idSet.has(id)));
      setSuccessMessage(`Deleted ${ids.length} supplier(s) from VAT lookback list.`);
    } catch (err: any) {
      setError(err?.response?.data?.error || 'Failed to delete selected suppliers from VAT lookback list');
    } finally {
      setBulkDeletingSupplierVat(false);
    }
  };

  const clearAllSupplierVatRows = async () => {
    if (
      !window.confirm(
        'Remove every supplier from the VAT lookback list? Saved lookback months and last-retrieved dates will be deleted.'
      )
    ) {
      return;
    }
    try {
      setClearingAllSupplierVat(true);
      setError('');
      const res = await api.delete('/api/suppliers/vat-invoice-policies');
      setSupplierVatRows([]);
      setSelectedSupplierVatIds([]);
      setSuccessMessage(res.data?.message || 'VAT lookback list cleared.');
    } catch (err: any) {
      setError(err?.response?.data?.error || 'Failed to clear VAT lookback list');
    } finally {
      setClearingAllSupplierVat(false);
    }
  };

  const submitSupplierRule = async (supplierProposalAction?: 'accept' | 'saveDraft') => {
    if (supplierRuleSaving) return;
    if (addCanonicalSupplierBusy) {
      setSupplierRuleFormError('Finish creating the supplier, then try again.');
      return;
    }
    if (vatRuleSaving || specialCaseRuleSaving) {
      setSupplierRuleFormError(ruleSaveBusyMessage);
      return;
    }
    const ruleName = supplierRuleName.trim();
    const criteria = supplierRuleCriteria
      .map((c) => ({
        kind: c.kind,
        value:
          c.kind === 'currency'
            ? c.value.trim().toUpperCase()
            : c.kind === 'amount'
              ? c.value.replace(/,/g, '').trim()
              : c.value.trim(),
        operator:
          c.kind === 'currency' || c.kind === 'amount'
            ? 'exact'
            : (c.operator || 'contains').trim() || 'contains'
      }))
      .filter((c) => c.value.length > 0);
    const firstMerchant = criteria.find((c) => c.kind === 'merchant');
    const firstDescription = criteria.find((c) => c.kind === 'description');
    const firstReference = criteria.find((c) => c.kind === 'reference');
    const pattern = firstMerchant?.value || '';
    const descriptionContains = firstDescription?.value || '';
    const referenceContains = firstReference?.value || '';
    const canonical = supplierRuleCanonicalName.trim();
    const priorityParsed = parseInt(supplierRulePriority, 10);
    const nextFieldErrors: {
      rule_name?: string;
      pattern_raw?: string;
      canonical_name?: string;
      priority?: string;
    } = {};
    if (!ruleName) {
      nextFieldErrors.rule_name = 'Enter a rule name.';
    }
    for (const criterion of criteria) {
      if (criterion.kind === 'currency') {
        const cur = criterion.value.trim().toUpperCase();
        if (!/^[A-Z]{3}$/.test(cur)) {
          nextFieldErrors.pattern_raw = 'Currency must be a 3-letter code (e.g. EUR, GBP).';
          break;
        }
        continue;
      }
      if (criterion.kind === 'amount') {
        if (!isValidSupplierRuleAmountValue(criterion.value)) {
          nextFieldErrors.pattern_raw = 'Amount must be a numeric value (e.g. -1200.50).';
          break;
        }
        continue;
      }
      if (criterion.kind !== 'canonical_supplier' && criterion.value.length < 2) {
        nextFieldErrors.pattern_raw = `${criterion.kind} matcher must be at least 2 characters when provided.`;
        break;
      }
    }
    if (!supplierRuleOutputSupplierEnabled) {
      nextFieldErrors.canonical_name = 'Add an output and select Supplier.';
    } else if (!canonical) {
      nextFieldErrors.canonical_name = 'Select a supplier output.';
    }
    if (!Number.isFinite(priorityParsed)) {
      nextFieldErrors.priority = 'Priority must be a whole number.';
    }
    if (criteria.length === 0) {
      nextFieldErrors.pattern_raw =
        'Add at least one matcher: merchant/counterparty, description, reference, canonical supplier, currency, or exact amount.';
    }
    if (Object.keys(nextFieldErrors).length > 0) {
      setSupplierRuleFieldErrors(nextFieldErrors);
      setSupplierRuleFormError(
        nextFieldErrors.pattern_raw ||
          nextFieldErrors.rule_name ||
          nextFieldErrors.canonical_name ||
          nextFieldErrors.priority ||
          'Please fix the highlighted fields.'
      );
      return;
    }
    const aliasIdNum = Math.trunc(Number(supplierRuleEditingId));
    let hasPersistedAliasId = Number.isFinite(aliasIdNum) && aliasIdNum > 0;
    try {
      setSupplierRuleSaving(true);
      const supplierHighlightId = Math.trunc(Number(supplierRuleEditingId));
      setRuleTableSaveHighlight(
        Number.isFinite(supplierHighlightId) && supplierHighlightId > 0
          ? { kind: 'supplier', rowKey: `sc-${supplierHighlightId}` }
          : null
      );
      setSupplierRuleFormError('');
      setSupplierRuleFieldErrors({});
      const matchCriteriaPayload = criteria.map((c) => ({
        kind: c.kind,
        value: c.value,
        operator: c.operator
      }));
      const payload = {
        rule_name: ruleName,
        notes: supplierRuleNotes.trim() || null,
        pattern_raw: pattern,
        description_contains: descriptionContains || null,
        description_text_match_operator: firstDescription?.operator || supplierRuleDescriptionTextMatchOperator,
        reference_contains: referenceContains || null,
        reference_text_match_operator: firstReference?.operator || supplierRuleReferenceTextMatchOperator,
        match_criteria: matchCriteriaPayload,
        canonical_name: canonical,
        priority: priorityParsed,
        text_match_operator: firstMerchant?.operator || supplierRuleTextMatchOperator,
        account_scope: normalizeRuleAccountScope(supplierRuleAccountScope),
        is_active: supplierRuleIsActive
      };
      const acceptProposalId = Math.trunc(Number(supplierProposalAcceptId ?? NaN));
      if (Number.isFinite(acceptProposalId) && acceptProposalId > 0) {
        if (supplierProposalAction === 'saveDraft') {
          setProposalBusyKey(`sp-save-${acceptProposalId}`);
          try {
            await api.patch(`/api/suppliers/description-alias-proposals/${acceptProposalId}`, { payload });
            await refreshRulesTabDatasets();
            setSupplierRuleOpen(false);
            setSupplierRuleCriteriaMenuAnchorEl(null);
            setSupplierRuleOutputMenuAnchorEl(null);
            setSupplierRuleOutputSupplierEnabled(false);
            setSupplierRuleEditingId(null);
            setSupplierProposalAcceptId(null);
            setSupplierRuleName('');
            setSupplierRuleNotes('');
            setSupplierRulePattern('');
            setSupplierRuleDescriptionContains('');
            setSupplierRuleDescriptionTextMatchOperator('exact');
            setSupplierRuleReferenceContains('');
            setSupplierRuleReferenceTextMatchOperator('exact');
            setSupplierRuleCriteria([]);
            setSupplierRuleCanonicalName('');
            setSupplierRulePriority('100');
            setSupplierRuleTextMatchOperator('exact');
            setSupplierRuleAccountScope('both');
            setSupplierRuleIsActive(true);
            setSupplierRuleFormError('');
            setSupplierRuleFieldErrors({});
            setSuccessMessage('Supplier proposal saved. It stays Proposed until you accept it.');
          } catch (err: any) {
            setSupplierRuleFormError(
              err?.response?.data?.error || err?.message || 'Failed to save supplier proposal'
            );
          } finally {
            setProposalBusyKey('');
            setSupplierRuleSaving(false);
            setRuleTableSaveHighlight(null);
          }
          return;
        }
        setProposalBusyKey(`sp-edit-${acceptProposalId}`);
        const accRes = await api.post(`/api/suppliers/description-alias-proposals/${acceptProposalId}/accept`, {
          payload_overrides: {
            rule_name: ruleName,
            notes: supplierRuleNotes.trim() || null,
            pattern_raw: pattern,
            description_contains: descriptionContains.trim() ? descriptionContains.trim() : null,
            description_text_match_operator:
              (firstDescription?.operator || supplierRuleDescriptionTextMatchOperator || supplierRuleTextMatchOperator || 'exact')
                .trim() || 'exact',
            reference_contains: referenceContains.trim() ? referenceContains.trim() : null,
            reference_text_match_operator:
              (firstReference?.operator || supplierRuleReferenceTextMatchOperator || supplierRuleTextMatchOperator || 'exact')
                .trim() || 'exact',
            canonical_name: canonical,
            priority: priorityParsed,
            text_match_operator: (firstMerchant?.operator || supplierRuleTextMatchOperator || 'exact').trim() || 'exact',
            account_scope: normalizeRuleAccountScope(supplierRuleAccountScope),
            is_active: supplierRuleIsActive,
            match_criteria: matchCriteriaPayload
          }
        });
        const aliasObj = accRes.data?.alias as SupplierRuleRow | undefined;
        await refreshRulesTabDatasets();
        const nextProposalParam = rebuildProposalIdsAfterSupplierDraftAccepted(
          rulesProposalIdsFilter,
          acceptProposalId
        );
        setRulesProposalIdsFilter(nextProposalParam);
        if (aliasObj?.id) {
          setRulesIdFilter(formatPrefixedRuleId('supplier', aliasObj.id));
        }
        setRulesStatusFilter('all');
        setSupplierRuleOpen(false);
        setSupplierRuleCriteriaMenuAnchorEl(null);
        setSupplierRuleOutputMenuAnchorEl(null);
        setSupplierRuleOutputSupplierEnabled(false);
        setSupplierRuleEditingId(null);
        setSupplierProposalAcceptId(null);
        setSupplierRuleName('');
        setSupplierRuleNotes('');
        setSupplierRulePattern('');
        setSupplierRuleDescriptionContains('');
        setSupplierRuleDescriptionTextMatchOperator('exact');
        setSupplierRuleReferenceContains('');
        setSupplierRuleReferenceTextMatchOperator('exact');
        setSupplierRuleCriteria([]);
        setSupplierRuleCanonicalName('');
        setSupplierRulePriority('100');
        setSupplierRuleTextMatchOperator('exact');
        setSupplierRuleAccountScope('both');
        setSupplierRuleIsActive(true);
        setSupplierRuleFormError('');
        setSupplierRuleFieldErrors({});
        setProposalBusyKey('');
        setSupplierRuleSaving(false);
        setRuleTableSaveHighlight(null);
        setSuccessMessage('Accepted edited supplier proposal and created supplier rule.');
        const aliasIdAccepted = aliasObj?.id != null ? Math.trunc(Number(aliasObj.id)) : NaN;
        openBackfillAfterAccept({
          ruleName: (ruleName || canonical || pattern || 'Supplier rule').trim(),
          prefixedId:
            Number.isFinite(aliasIdAccepted) && aliasIdAccepted > 0
              ? formatPrefixedRuleId('supplier', aliasIdAccepted)
              : '',
          target:
            Number.isFinite(aliasIdAccepted) && aliasIdAccepted > 0
              ? { kind: 'supplier', id: aliasIdAccepted }
              : null
        });
        return;
      }
      const res = hasPersistedAliasId
        ? await api.put(`/api/suppliers/description-aliases/${aliasIdNum}`, payload)
        : await api.post('/api/suppliers/description-aliases', payload);
      const alias = res.data?.alias as SupplierRuleRow | undefined;
      if (alias) {
        setSupplierRuleRows((prev) =>
          hasPersistedAliasId
            ? prev.map((r) => (r.id === alias.id ? alias : r))
            : [alias, ...prev]
        );
      } else {
        const aliasRes = await api.get('/api/suppliers/description-aliases');
        setSupplierRuleRows(Array.isArray(aliasRes.data?.aliases) ? aliasRes.data.aliases : []);
      }
      setSupplierRuleOpen(false);
      setSupplierRuleCriteriaMenuAnchorEl(null);
      setSupplierRuleOutputMenuAnchorEl(null);
      setSupplierRuleOutputSupplierEnabled(false);
      setSupplierRuleEditingId(null);
      setSupplierProposalAcceptId(null);
      setSupplierRuleName('');
      setSupplierRuleNotes('');
      setSupplierRulePattern('');
      setSupplierRuleDescriptionContains('');
      setSupplierRuleDescriptionTextMatchOperator('exact');
      setSupplierRuleReferenceContains('');
      setSupplierRuleReferenceTextMatchOperator('exact');
      setSupplierRuleCriteria([]);
      setSupplierRuleCanonicalName('');
      setSupplierRulePriority('100');
      setSupplierRuleTextMatchOperator('exact');
      setSupplierRuleAccountScope('both');
      setSupplierRuleIsActive(true);
      setSupplierRuleFormError('');
      setSupplierRuleFieldErrors({});
      void refreshRulesTabDatasets().catch(() => undefined);
      promoteRulesStatusForConfirmedEdits();
      setSuccessMessage(hasPersistedAliasId ? 'Supplier rule updated.' : 'Supplier rule added.');
    } catch (err: any) {
      const data = err?.response?.data;
      const st = err?.response?.status;
      const rawCid = data?.conflict_rule_id;
      const cid = rawCid != null ? Math.trunc(Number(rawCid)) : NaN;
      const conflictReason = String(data?.conflict_reason || '').trim();
      let msg =
        data?.error ||
        err?.message ||
        (hasPersistedAliasId ? 'Failed to update supplier rule' : 'Failed to add supplier rule');
      if (st === 409 && Number.isFinite(cid) && cid > 0) {
        const prefix = formatPrefixedRuleId('supplier', cid);
        if (!String(msg).includes(prefix)) {
          msg = `${msg} [${prefix}]`;
        }
        if (conflictReason && !String(msg).includes(conflictReason)) {
          msg = `${msg} Why: ${conflictReason}`;
        }
      }
      setSupplierRuleFormError(msg);
      setProposalBusyKey('');
    } finally {
      setSupplierRuleSaving(false);
      setRuleTableSaveHighlight(null);
    }
  };

  const openSupplierRuleEditor = useCallback((row: SupplierRuleRow) => {
    setSupplierProposalAcceptId(null);
    setSupplierRuleEditingId(row.id);
    setSupplierRuleName((row.rule_name || '').trim());
    setSupplierRuleNotes(String(row.notes || ''));
    setSupplierRulePattern(row.pattern_raw || '');
    setSupplierRuleDescriptionContains(row.description_contains || '');
    setSupplierRuleDescriptionTextMatchOperator(
      (row.description_text_match_operator || row.text_match_operator || 'exact').trim() || 'exact'
    );
    setSupplierRuleReferenceContains(row.reference_contains || '');
    setSupplierRuleReferenceTextMatchOperator(
      (row.reference_text_match_operator || row.text_match_operator || 'exact').trim() || 'exact'
    );
    setSupplierRuleCriteria(normalizeSupplierRuleCriteriaFromRow(row));
    setSupplierRuleCanonicalName(row.supplier_name || '');
    setSupplierRuleOutputSupplierEnabled(true);
    setSupplierRuleOutputMenuAnchorEl(null);
    setSupplierRulePriority(String(row.priority ?? 100));
    setSupplierRuleTextMatchOperator((row.text_match_operator || 'exact').trim() || 'exact');
    setSupplierRuleAccountScope(normalizeRuleAccountScope(row.account_scope));
    setSupplierRuleIsActive(row.is_active !== false);
    setSupplierRuleFormError('');
    setSupplierRuleFieldErrors({});
    setSupplierRuleOpen(true);
  }, []);

  const deleteSupplierRule = async (aliasId: number) => {
    try {
      setError('');
      await api.delete(`/api/suppliers/description-aliases/${aliasId}`);
      setSupplierRuleRows((prev) => prev.filter((r) => r.id !== aliasId));
      setSuccessMessage('Supplier rule deleted.');
    } catch (err: any) {
      setError(err?.response?.data?.error || err?.message || 'Failed to delete supplier rule');
    }
  };

  const deleteClassificationRule = async (ruleId: number, nameHint?: string | null) => {
    const label = formatClassificationRulePrefixedId(ruleId, nameHint);
    let progressToastId: number | null = null;
    try {
      setError('');
      progressToastId = beginRuleOperationInfoToast(`Deleting classification rule ${label}…`);
      await api.delete(`/api/transaction-classification-rules/${ruleId}`);
      setRulesPayload((prev) =>
        prev ? { ...prev, rules: prev.rules.filter((r) => r.id !== ruleId) } : prev
      );
      if (ruleEditDraft?.id === ruleId) {
        setRuleEditMatchCriteriaMenuAnchorEl(null);
        setRuleEditDraft(null);
        setRuleEditReadOnly(false);
        setRuleEditTargetType('classification');
        setRuleEditOutputMenuAnchorEl(null);
        setRuleEditOutputCategoryEnabled(false);
        setRuleEditOutputSupplierEnabled(false);
        setRuleEditError('');
        setRuleEditConflictRuleId(null);
        setConflictPairEditDraft(null);
        setConflictPairEditError('');
        resetConflictPairRuleEditorUi();
      } else if (conflictPairEditDraft && Math.trunc(Number(conflictPairEditDraft.id)) === Math.trunc(ruleId)) {
        setConflictPairEditDraft(null);
        setConflictPairEditError('');
        resetConflictPairRuleEditorUi();
      }
      setSuccessMessage(`Classification rule ${label} deleted.`);
    } catch (err: any) {
      const base =
        err?.response?.data?.error ||
        err?.message ||
        `Failed to delete classification rule ${label}`;
      const detail = String(err?.response?.data?.detail || '').trim();
      setError(detail ? `${base} (${detail})` : base);
    } finally {
      dismissTxReviewBulkToast(progressToastId);
    }
  };

  const toggleClassificationRuleActive = async (rule: ClassificationRule, nextActive: boolean) => {
    try {
      setRuleActiveSavingId(rule.id);
      setError('');
      const res = await api.put(
        `/api/transaction-classification-rules/${rule.id}`,
        { is_active: nextActive },
        CLASSIFICATION_RULE_SAVE_CONFIG
      );
      const updated = res.data?.rule as ClassificationRule | undefined;
      setRulesPayload((prev) => {
        if (!prev) return prev;
        return {
          rules: prev.rules.map((r) =>
            r.id === rule.id ? { ...r, ...(updated || {}), is_active: nextActive } : r
          )
        };
      });
      setSuccessMessage(`Rule ${nextActive ? 'activated' : 'deactivated'}.`);
    } catch (err: any) {
      setError(err?.response?.data?.error || err?.message || 'Failed to update rule active status');
    } finally {
      setRuleActiveSavingId(null);
    }
  };

  const openSupplierProposalEditor = useCallback((row: SupplierRuleProposalRow) => {
    const pid = supplierRuleProposalDbId(row);
    if (pid == null) {
      setError('This supplier proposal has no id — refresh the page and try again.');
      scrollPageToClassificationErrorBanner();
      return;
    }
    const p = row.payload || {};
    setSupplierRuleEditingId(null);
    setSupplierProposalAcceptId(pid);
    setSupplierRuleName(String((p.rule_name as string) || row.proposed_name || '').trim() || 'Supplier rule');
    setSupplierRuleNotes(String((p.notes as string) || ''));
    setSupplierRulePattern(String((p.pattern_raw as string) || '').trim());
    setSupplierRuleDescriptionContains(String((p.description_contains as string) || '').trim());
    setSupplierRuleDescriptionTextMatchOperator(
      String((p.description_text_match_operator as string) || (p.text_match_operator as string) || 'exact').trim() ||
        'exact'
    );
    setSupplierRuleReferenceContains(String((p.reference_contains as string) || '').trim());
    setSupplierRuleReferenceTextMatchOperator(
      String((p.reference_text_match_operator as string) || (p.text_match_operator as string) || 'exact').trim() ||
        'exact'
    );
    setSupplierRuleCriteria(supplierRuleCriteriaDraftsFromProposalPayload(p));
    setSupplierRuleCanonicalName(String((p.canonical_name as string) || '').trim());
    setSupplierRuleOutputSupplierEnabled(true);
    setSupplierRuleOutputMenuAnchorEl(null);
    setSupplierRulePriority(String(p.priority ?? 100));
    setSupplierRuleTextMatchOperator(String((p.text_match_operator as string) || 'exact').trim() || 'exact');
    setSupplierRuleAccountScope(normalizeRuleAccountScope(String((p.account_scope as string) || 'both')));
    setSupplierRuleIsActive(p.is_active !== false);
    setSupplierRuleFormError('');
    setSupplierRuleFieldErrors({});
    setSupplierRuleOpen(true);
  }, []);

  const rejectSupplierProposalFromDialog = useCallback(async () => {
    const pid = Math.trunc(Number(supplierProposalAcceptId ?? NaN));
    if (!Number.isFinite(pid) || pid <= 0) return;
    if (supplierRuleSaving || proposalBusyKey) return;
    try {
      setProposalBusyKey(`sp-reject-${pid}`);
      setError('');
      await api.post(`/api/suppliers/description-alias-proposals/${pid}/reject`, {});
      await refreshRulesTabDatasets();
      setSupplierRuleOpen(false);
      setSupplierRuleCriteriaMenuAnchorEl(null);
      setSupplierRuleOutputMenuAnchorEl(null);
      setSupplierRuleOutputSupplierEnabled(false);
      setSupplierRuleEditingId(null);
      setSupplierProposalAcceptId(null);
      setSupplierRuleName('');
      setSupplierRuleNotes('');
      setSupplierRuleCriteria([]);
      setSupplierRuleCanonicalName('');
      setSuccessMessage(`Supplier proposal ${formatSupplierProposalPrefixedId(pid, 'rejected')} rejected.`);
    } catch (err: any) {
      setError(err?.response?.data?.error || err?.message || 'Could not reject supplier proposal');
      scrollPageToClassificationErrorBanner();
    } finally {
      setProposalBusyKey('');
    }
  }, [proposalBusyKey, refreshRulesTabDatasets, scrollPageToClassificationErrorBanner, supplierProposalAcceptId, supplierRuleSaving]);

  const rejectClassificationProposalFromEditDialog = useCallback(async () => {
    if (!ruleEditDraft || ruleEditReadOnly) return;
    const ak = ruleEditDraft.proposal_atlas_key?.trim();
    const pidRaw = ruleEditDraft.proposal_id != null ? Math.trunc(Number(ruleEditDraft.proposal_id)) : NaN;
    if (!ak && (!Number.isFinite(pidRaw) || pidRaw <= 0)) return;
    if (ruleEditSaveInFlight || ruleEditSaving || proposalBusyKey) return;
    try {
      setError('');
      if (ak) {
        setProposalBusyKey(`cls-rejatlas-${ak.slice(0, 24)}`);
        await api.post('/api/transaction-classification-rules/rule-proposals/reject-atlas', { atlas_key: ak });
        setProposalsPayload((prev) => {
          if (!prev) return prev;
          return { ...prev, proposals: (prev.proposals || []).filter((p) => p.atlas_key !== ak) };
        });
      } else {
        setProposalBusyKey(`cls-rej-${pidRaw}`);
        await api.post(`/api/transaction-classification-rules/rule-proposals/${pidRaw}/reject`, {});
        setProposalsPayload((prev) => {
          if (!prev) return prev;
          return {
            ...prev,
            proposals: (prev.proposals || []).filter((p) => Number(p.id || 0) !== Number(pidRaw))
          };
        });
      }
      await refreshRulesTabDatasets();
      setRuleEditMatchCriteriaMenuAnchorEl(null);
      setRuleEditDraft(null);
      setRuleEditReadOnly(false);
      setRuleEditTargetType('classification');
      setRuleEditOutputMenuAnchorEl(null);
      setRuleEditOutputCategoryEnabled(false);
      setRuleEditOutputSupplierEnabled(false);
      setRuleEditError('');
      setSuccessMessage('Classification proposal rejected.');
    } catch (err: any) {
      setError(err?.response?.data?.error || err?.message || 'Could not reject proposal');
    } finally {
      setProposalBusyKey('');
    }
  }, [proposalBusyKey, refreshRulesTabDatasets, ruleEditDraft, ruleEditReadOnly, ruleEditSaveInFlight, ruleEditSaving]);

  const buildSupplierAliasUpdatePayload = useCallback(
    (
      alias: SupplierRuleRow,
      options?: {
        notes?: string | null;
        priority?: number;
        account_scope?: 'both' | 'ireland' | 'uk';
        is_active?: boolean;
      }
    ) => {
      const criteria = normalizeSupplierRuleCriteriaFromRow(alias)
        .map((c) => ({
          kind: c.kind,
          value: c.value.trim(),
          operator: (c.operator || 'contains').trim() || 'contains'
        }))
        .filter((c) => c.value.length > 0);
      if (criteria.length === 0) {
        throw new Error(`Supplier rule ${formatPrefixedRuleId('supplier', alias.id)} has no match criteria.`);
      }
      const canonical = String(alias.supplier_name || '').trim();
      if (!canonical) {
        throw new Error(`Supplier rule ${formatPrefixedRuleId('supplier', alias.id)} has no canonical supplier.`);
      }
      const firstMerchant = criteria.find((c) => c.kind === 'merchant');
      const firstDescription = criteria.find((c) => c.kind === 'description');
      const firstReference = criteria.find((c) => c.kind === 'reference');
      return {
        rule_name: String(alias.rule_name || canonical).trim() || canonical,
        notes: options?.notes ?? (alias.notes ?? null),
        pattern_raw: firstMerchant?.value || '',
        description_contains: firstDescription?.value || null,
        description_text_match_operator:
          firstDescription?.operator ||
          String(alias.description_text_match_operator || alias.text_match_operator || 'contains').trim() ||
          'contains',
        reference_contains: firstReference?.value || null,
        reference_text_match_operator:
          firstReference?.operator ||
          String(alias.reference_text_match_operator || alias.text_match_operator || 'contains').trim() ||
          'contains',
        match_criteria: criteria,
        canonical_name: canonical,
        priority:
          options?.priority ??
          (Number.isFinite(Number(alias.priority)) ? Number(alias.priority) : 100),
        text_match_operator:
          firstMerchant?.operator || String(alias.text_match_operator || 'contains').trim() || 'contains',
        account_scope:
          options?.account_scope ?? normalizeRuleAccountScope(String(alias.account_scope || 'both')),
        is_active: options?.is_active ?? (alias.is_active !== false)
      };
    },
    []
  );

  const bulkClassificationSelection = useMemo(() => {
    const rows = visibleClassificationRuleRows.filter((r) => selectedClassificationRuleKeys.includes(r.key));
    const proposed = rows.filter((r) => r.status === 'proposed' && r.proposal);
    const confirmed = rows
      .filter((r) => r.status === 'confirmed' && r.rule)
      .map((r) => r.rule as ClassificationRule);
    return {
      count: rows.length,
      showDelete: rows.length > 0,
      showAccept: proposed.length > 0,
      showReject: proposed.length > 0,
      showRun: confirmed.length > 0,
      showDeactivate: confirmed.some((rule) => rule.is_active !== false),
      showActivate: confirmed.some((rule) => rule.is_active === false),
      showApplyEdits: confirmed.length > 0
    };
  }, [visibleClassificationRuleRows, selectedClassificationRuleKeys]);

  const bulkSupplierSelection = useMemo(() => {
    const rows = visibleSupplierRuleRows.filter((r) => selectedSupplierRuleKeys.includes(r.key));
    const proposed = rows.filter((r) => r.status === 'proposed' && r.proposal);
    const confirmed = rows
      .filter((r) => r.status === 'confirmed' && r.alias)
      .map((r) => r.alias as SupplierRuleRow);
    return {
      count: rows.length,
      showDelete: rows.length > 0,
      showAccept: proposed.length > 0,
      showReject: proposed.length > 0,
      showRun: confirmed.length > 0,
      showDeactivate: confirmed.some((a) => a.is_active !== false),
      showActivate: confirmed.some((a) => a.is_active === false),
      showApplyEdits: confirmed.length > 0
    };
  }, [visibleSupplierRuleRows, selectedSupplierRuleKeys]);

  /** Proposed-row selection counts for nested Proposed bulk bars (stage-level bar sits above Confirmed). */
  const selectedClassificationProposedCount = useMemo(() => {
    const keys = new Set(sortedVisibleClassificationProposedRows.map((r) => r.key));
    return selectedClassificationRuleKeys.filter((k) => keys.has(k)).length;
  }, [sortedVisibleClassificationProposedRows, selectedClassificationRuleKeys]);

  const selectedSupplierProposedCount = useMemo(() => {
    const keys = new Set(sortedVisibleSupplierProposedRows.map((r) => r.key));
    return selectedSupplierRuleKeys.filter((k) => keys.has(k)).length;
  }, [sortedVisibleSupplierProposedRows, selectedSupplierRuleKeys]);

  const selectedVatProposedCount = useMemo(
    () => visibleVatRuleSuggestions.filter((r) => selectedVatSuggestionIds.includes(r.id)).length,
    [visibleVatRuleSuggestions, selectedVatSuggestionIds]
  );

  const bulkMerchantSupplierSelection = useMemo(() => {
    const selected = new Set(selectedMerchantSupplierRuleKeys);
    const rows = sortedVisibleMerchantSupplierRuleRows.filter((r) => selected.has(`mc-${r.id}`));
    return {
      count: rows.length,
      showDelete: rows.length > 0,
      showRun: rows.length > 0,
      showDeactivate: rows.some((a) => a.is_active !== false),
      showActivate: rows.some((a) => a.is_active === false)
    };
  }, [sortedVisibleMerchantSupplierRuleRows, selectedMerchantSupplierRuleKeys]);

  const bulkVatSelection = useMemo(() => {
    const vatRuleTargets = sortedVisibleVatRules.filter((r) => selectedVatRuleIds.includes(Number(r.id)));
    const vatSuggTargets = visibleVatRuleSuggestions.filter((r) => selectedVatSuggestionIds.includes(Number(r.id)));
    const n = vatRuleTargets.length + vatSuggTargets.length;
    return {
      count: n,
      vatRuleCount: vatRuleTargets.length,
      vatSuggCount: vatSuggTargets.length,
      showDelete: n > 0,
      showAccept: vatSuggTargets.length > 0,
      showReject: vatSuggTargets.length > 0,
      showRun: vatRuleTargets.length > 0,
      showDeactivate: vatRuleTargets.some((r) => r.is_active !== false),
      showActivate: vatRuleTargets.some((r) => r.is_active === false),
      showApplyEdits: vatRuleTargets.length > 0
    };
  }, [sortedVisibleVatRules, selectedVatRuleIds, visibleVatRuleSuggestions, selectedVatSuggestionIds]);

  const bulkSpecialCaseSelection = useMemo(() => {
    const target = sortedVisibleSpecialCaseRules.filter((r) => selectedSpecialCaseRuleIds.includes(Number(r.id)));
    return {
      count: target.length,
      showDelete: target.length > 0,
      showAccept: false,
      showReject: false,
      showRun: target.length > 0,
      showDeactivate: target.some((r) => r.is_active !== false),
      showActivate: target.some((r) => r.is_active === false),
      showApplyEdits: target.length > 0
    };
  }, [sortedVisibleSpecialCaseRules, selectedSpecialCaseRuleIds]);

  const crossTypeBulkActionVisibility = useMemo(() => {
    const cRows = visibleClassificationRuleRows.filter((r) => selectedClassificationRuleKeys.includes(r.key));
    const sRows = visibleSupplierRuleRows.filter((r) => selectedSupplierRuleKeys.includes(r.key));
    const merchantSelected = new Set(selectedMerchantSupplierRuleKeys);
    const mRows = sortedVisibleMerchantSupplierRuleRows.filter((r) => merchantSelected.has(`mc-${r.id}`));
    const vatRows = sortedVisibleVatRules.filter((r) => selectedVatRuleIds.includes(Number(r.id)));
    const vatSugg = visibleVatRuleSuggestions.filter((r) => selectedVatSuggestionIds.includes(Number(r.id)));
    const spRows = sortedVisibleSpecialCaseRules.filter((r) => selectedSpecialCaseRuleIds.includes(Number(r.id)));
    const cProp = cRows.some((r) => r.status === 'proposed' && r.proposal);
    const cConf = cRows.filter((r) => r.status === 'confirmed' && r.rule);
    const sProp = sRows.some((r) => r.status === 'proposed' && r.proposal);
    const sConf = sRows.filter((r) => r.status === 'confirmed' && r.alias);
    return {
      showDelete:
        cRows.length + sRows.length + mRows.length + vatRows.length + vatSugg.length + spRows.length > 0,
      showAccept: cProp || sProp || vatSugg.length > 0,
      showReject: cProp || sProp || vatSugg.length > 0,
      showRun:
        cConf.length > 0 || sConf.length > 0 || mRows.length > 0 || vatRows.length > 0 || spRows.length > 0,
      showDeactivate:
        cConf.some((r) => (r.rule as ClassificationRule).is_active !== false) ||
        sConf.some((r) => (r.alias as SupplierRuleRow).is_active !== false) ||
        mRows.some((r) => r.is_active !== false) ||
        vatRows.some((r) => r.is_active !== false) ||
        spRows.some((r) => r.is_active !== false),
      showActivate:
        cConf.some((r) => (r.rule as ClassificationRule).is_active === false) ||
        sConf.some((r) => (r.alias as SupplierRuleRow).is_active === false) ||
        mRows.some((r) => r.is_active === false) ||
        vatRows.some((r) => r.is_active === false) ||
        spRows.some((r) => r.is_active === false)
    };
  }, [
    visibleClassificationRuleRows,
    selectedClassificationRuleKeys,
    visibleSupplierRuleRows,
    selectedSupplierRuleKeys,
    selectedMerchantSupplierRuleKeys,
    sortedVisibleMerchantSupplierRuleRows,
    sortedVisibleVatRules,
    selectedVatRuleIds,
    visibleVatRuleSuggestions,
    selectedVatSuggestionIds,
    sortedVisibleSpecialCaseRules,
    selectedSpecialCaseRuleIds
  ]);

  const runBulkClassificationRuleAction = useCallback(
    async (action: 'accept' | 'reject' | 'activate' | 'deactivate' | 'delete' | 'run' | 'applyEdits') => {
      const rows = visibleClassificationRuleRows.filter((r) => selectedClassificationRuleKeys.includes(r.key));
      if (rows.length === 0) return;
      setProposalBusyKey(`bulk-class-${action}`);
      setError('');
      let bulkClassDeleteProgressToastId: number | null = null;
      try {
        const priorityRaw = bulkClassificationPriority.trim();
        const hasPriorityOverride = priorityRaw !== '';
        const priorityOverride = hasPriorityOverride ? parseInt(priorityRaw, 10) : null;
        if (hasPriorityOverride && !Number.isFinite(priorityOverride)) {
          setError('Bulk priority must be a whole number.');
          return;
        }
        const hasAccountScopeOverride = bulkClassificationAccountScope !== RULE_BULK_NO_CHANGE;
        const commentToAppend = bulkClassificationComment.trim();
        const hasCommentAppend = commentToAppend.length > 0;
        const hasBulkFieldOverride = hasPriorityOverride || hasAccountScopeOverride || hasCommentAppend;
        if (action === 'accept' || action === 'reject') {
          const target = rows.filter((r) => r.status === 'proposed' && r.proposal);
          if (target.length === 0) {
            setError(
              `No proposed classification ${action === 'accept' ? 'rules to accept' : 'rules to reject'} in the current selection. ` +
                'Only rows with status Proposed are included; deselect confirmed rules or switch the Status filter.'
            );
            await refreshRulesTabDatasets();
            setSelectedClassificationRuleKeys([]);
            return;
          }
          if (action === 'accept' && target.length > 0) {
            setOperationProgressMessage(
              `Started background accept for ${target.length} proposed classification rule(s). You can keep working.`
            );
          }
          const chunkSize = action === 'accept' ? 12 : 1;
          let processedCount = 0;
          let failed = 0;
          for (let i = 0; i < target.length; i += chunkSize) {
            const batch = target.slice(i, i + chunkSize);
            const settled = await Promise.allSettled(
              batch.map(async (row) => {
                const proposal = row.proposal as RuleProposalRow;
                const vkey = proposal.virtual && proposal.atlas_key ? proposal.atlas_key : '';
                const payloadOverrides =
                  action === 'accept' && hasBulkFieldOverride
                    ? {
                        ...(hasPriorityOverride ? { priority: Number(priorityOverride) } : {}),
                        ...(hasAccountScopeOverride
                          ? {
                              account_scope: normalizeRuleAccountScope(String(bulkClassificationAccountScope))
                            }
                          : {}),
                        ...(hasCommentAppend
                          ? {
                              notes: appendBulkComment(
                                String((proposal.payload as any)?.notes || ''),
                                commentToAppend
                              )
                            }
                          : {})
                      }
                    : undefined;
                if (action === 'accept') {
                  if (proposal.virtual && vkey) {
                    await api.post(
                      '/api/transaction-classification-rules/rule-proposals/accept-atlas',
                      {
                        atlas_key: vkey,
                        ...(payloadOverrides ? { payload_overrides: payloadOverrides } : {})
                      },
                      CLASSIFICATION_RULE_SAVE_CONFIG
                    );
                    return;
                  }
                  if (proposal.id) {
                    await api.post(
                      `/api/transaction-classification-rules/rule-proposals/${proposal.id}/accept`,
                      { ...(payloadOverrides ? { payload_overrides: payloadOverrides } : {}) },
                      CLASSIFICATION_RULE_SAVE_CONFIG
                    );
                    return;
                  }
                  throw new Error('Missing proposal id');
                }
                if (proposal.virtual && vkey) {
                  await api.post(
                    '/api/transaction-classification-rules/rule-proposals/reject-atlas',
                    { atlas_key: vkey },
                    CLASSIFICATION_RULE_SAVE_CONFIG
                  );
                  return;
                }
                if (proposal.id) {
                  await api.post(
                    `/api/transaction-classification-rules/rule-proposals/${proposal.id}/reject`,
                    {},
                    CLASSIFICATION_RULE_SAVE_CONFIG
                  );
                  return;
                }
                throw new Error('Missing proposal id');
              })
            );
            processedCount += settled.length;
            failed += settled.filter((r) => r.status === 'rejected').length;
            if (action === 'accept') {
              setOperationProgressMessage(
                `Background accept in progress: processed ${processedCount}/${target.length} classification proposal(s)…`
              );
            }
          }
          if (action === 'accept') {
            setOperationProgressMessage(null);
          }
          const successCount = Math.max(0, target.length - failed);
          if (failed === 0) {
            setSuccessMessage(
              `${action === 'accept' ? 'Accepted' : 'Rejected'} ${successCount} proposed classification rule(s).`
            );
          } else if (successCount > 0) {
            setError(
              `${action === 'accept' ? 'Accepted' : 'Rejected'} ${successCount} of ${target.length}; ${failed} failed. Refresh, then retry the ones that are still in the list.`
            );
          } else {
            setError(
              `All ${failed} classification proposal(s) failed. Refresh the rules list and try again, or check your connection.`
            );
          }
        } else if (action === 'activate' || action === 'deactivate') {
          const target = rows.filter((r) => r.status === 'confirmed' && r.rule).map((r) => r.rule as ClassificationRule);
          const nextActive = action === 'activate';
          for (const rule of target) {
            await api.put(
              `/api/transaction-classification-rules/${rule.id}`,
              { is_active: nextActive },
              CLASSIFICATION_RULE_SAVE_CONFIG
            );
          }
          setSuccessMessage(`${nextActive ? 'Activated' : 'Deactivated'} ${target.length} classification rule(s).`);
        } else if (action === 'delete') {
          if (
            !window.confirm(
              'Are you sure? Proposed rules will be rejected; confirmed rules will be permanently deleted.'
            )
          ) {
            return;
          }
          bulkClassDeleteProgressToastId = beginRuleOperationInfoToast(
            'Deleting selected classification rules…'
          );
          const proposedRows = rows.filter((r) => r.status === 'proposed' && r.proposal);
          let rejected = 0;
          for (const row of proposedRows) {
            const proposal = row.proposal as RuleProposalRow;
            const vkey = proposal.virtual && proposal.atlas_key ? proposal.atlas_key : '';
            if (proposal.virtual && vkey) {
              await api.post('/api/transaction-classification-rules/rule-proposals/reject-atlas', {
                atlas_key: vkey
              });
              rejected += 1;
            } else if (proposal.id) {
              await api.post(`/api/transaction-classification-rules/rule-proposals/${proposal.id}/reject`, {});
              rejected += 1;
            }
          }
          const confirmedTarget = rows.filter((r) => r.status === 'confirmed' && r.rule).map((r) => r.rule as ClassificationRule);
          for (const rule of confirmedTarget) {
            await api.delete(`/api/transaction-classification-rules/${rule.id}`);
          }
          const parts: string[] = [];
          if (confirmedTarget.length > 0) parts.push(`deleted ${confirmedTarget.length} confirmed`);
          if (rejected > 0) parts.push(`rejected ${rejected} proposed`);
          setSuccessMessage(
            parts.length > 0
              ? `Classification rules: ${parts.join(', ')}.`
              : 'No rules were removed.'
          );
        } else if (action === 'run') {
          const target = rows.filter((r) => r.status === 'confirmed' && r.rule).map((r) => r.rule as ClassificationRule);
          for (const rule of target) {
            await api.post(`/api/transaction-classification-rules/${rule.id}/apply-single`, {});
          }
          setSuccessMessage(`Ran ${target.length} selected classification rule(s) on all matching transactions.`);
        } else {
          const target = rows.filter((r) => r.status === 'confirmed' && r.rule).map((r) => r.rule as ClassificationRule);
          if (!hasBulkFieldOverride) {
            setError('Set at least one bulk field: priority, bank account, or comment.');
            return;
          }
          for (const rule of target) {
            const payload: Record<string, any> = {};
            if (hasPriorityOverride) payload.priority = Number(priorityOverride);
            if (hasAccountScopeOverride) {
              payload.account_scope = normalizeRuleAccountScope(String(bulkClassificationAccountScope));
            }
            if (hasCommentAppend) payload.notes = appendBulkComment(rule.notes ?? null, commentToAppend);
            await api.put(
              `/api/transaction-classification-rules/${rule.id}`,
              payload,
              CLASSIFICATION_RULE_SAVE_CONFIG
            );
          }
          setSuccessMessage(`Updated ${target.length} confirmed classification rule(s).`);
        }
        await refreshRulesTabDatasets();
        setSelectedClassificationRuleKeys([]);
      } catch (err: any) {
        setError(err?.response?.data?.error || err?.message || 'Bulk classification rule action failed');
      } finally {
        dismissTxReviewBulkToast(bulkClassDeleteProgressToastId);
        setProposalBusyKey('');
      }
    },
    [
      beginRuleOperationInfoToast,
      bulkClassificationAccountScope,
      bulkClassificationComment,
      bulkClassificationPriority,
      dismissTxReviewBulkToast,
      refreshRulesTabDatasets,
      selectedClassificationRuleKeys,
      visibleClassificationRuleRows
    ]
  );

  const runBulkSupplierRuleAction = useCallback(
    async (action: 'accept' | 'reject' | 'delete' | 'activate' | 'deactivate' | 'run' | 'applyEdits') => {
      const rows = visibleSupplierRuleRows.filter((r) => selectedSupplierRuleKeys.includes(r.key));
      if (rows.length === 0) return;
      setProposalBusyKey(`bulk-supplier-${action}`);
      setError('');
      try {
        const priorityRaw = bulkSupplierPriority.trim();
        const hasPriorityOverride = priorityRaw !== '';
        const priorityOverride = hasPriorityOverride ? parseInt(priorityRaw, 10) : null;
        if (hasPriorityOverride && !Number.isFinite(priorityOverride)) {
          setError('Bulk priority must be a whole number.');
          return;
        }
        const hasAccountScopeOverride = bulkSupplierAccountScope !== RULE_BULK_NO_CHANGE;
        const commentToAppend = bulkSupplierComment.trim();
        const hasCommentAppend = commentToAppend.length > 0;
        const hasBulkFieldOverride = hasPriorityOverride || hasAccountScopeOverride || hasCommentAppend;

        if (action === 'accept' || action === 'reject') {
          const target = rows.filter((r) => r.status === 'proposed' && r.proposal);
          let skipped = 0;
          if (action === 'accept' && target.length > 0) {
            setOperationProgressMessage(
              `Started background accept for ${target.length} proposed supplier rule(s). You can keep working.`
            );
          }
          const chunkSize = action === 'accept' ? 12 : 1;
          let processedCount = 0;
          let failed = 0;
          for (let i = 0; i < target.length; i += chunkSize) {
            const batch = target.slice(i, i + chunkSize);
            const settled = await Promise.allSettled(
              batch.map(async (row) => {
                const proposal = row.proposal as SupplierRuleProposalRow;
                const pid = supplierRuleProposalDbId(proposal);
                if (pid == null) {
                  skipped += 1;
                  return;
                }
                if (action === 'accept') {
                  const payloadOverrides =
                    hasBulkFieldOverride
                      ? {
                          ...(hasPriorityOverride ? { priority: Number(priorityOverride) } : {}),
                          ...(hasAccountScopeOverride
                            ? {
                                account_scope: normalizeRuleAccountScope(String(bulkSupplierAccountScope))
                              }
                            : {}),
                          ...(hasCommentAppend
                            ? {
                                notes: appendBulkComment(
                                  String(((proposal as any).payload?.notes as string) || ''),
                                  commentToAppend
                                )
                              }
                            : {})
                        }
                      : undefined;
                  await api.post(`/api/suppliers/description-alias-proposals/${pid}/accept`, {
                    ...(payloadOverrides ? { payload_overrides: payloadOverrides } : {})
                  });
                  return;
                }
                await api.post(`/api/suppliers/description-alias-proposals/${pid}/reject`, {});
              })
            );
            processedCount += settled.length;
            failed += settled.filter((r) => r.status === 'rejected').length;
            if (action === 'accept') {
              setOperationProgressMessage(
                `Background accept in progress: processed ${processedCount}/${target.length} supplier proposal(s)…`
              );
            }
          }
          if (action === 'accept') {
            setOperationProgressMessage(null);
          }
          const processedValid = target.length - skipped;
          const acceptedOrRejected = Math.max(0, processedValid - failed);
          if (failed === 0) {
            if (skipped > 0) {
              setSuccessMessage(
                `${action === 'accept' ? 'Accepted' : 'Rejected'} ${acceptedOrRejected} proposed supplier rule(s). ` +
                  `(${skipped} row(s) had no proposal id and were skipped — refresh if that persists.)`
              );
            } else {
              setSuccessMessage(
                `${action === 'accept' ? 'Accepted' : 'Rejected'} ${acceptedOrRejected} proposed supplier rule(s).`
              );
            }
          } else if (acceptedOrRejected > 0) {
            setError(
              `${action === 'accept' ? 'Accepted' : 'Rejected'} ${acceptedOrRejected} of ${target.length}; ${failed} failed.` +
                (skipped > 0 ? ` ${skipped} skipped (missing id).` : '') +
                ' Refresh, then retry failed rows.'
            );
            scrollPageToClassificationErrorBanner();
          } else {
            setError(
              `All ${failed} supplier proposal(s) failed${skipped > 0 ? ` (${skipped} skipped for missing id)` : ''}. ` +
                'Refresh the list and try again.'
            );
            scrollPageToClassificationErrorBanner();
          }
        } else if (action === 'delete') {
          if (
            !window.confirm(
              'Are you sure? This will delete selected confirmed supplier rules and reject selected proposed ones.'
            )
          ) {
            return;
          }
          const propRows = rows.filter((r) => r.status === 'proposed' && r.proposal);
          let rejected = 0;
          for (const row of propRows) {
            const proposal = row.proposal as SupplierRuleProposalRow;
            const pid = supplierRuleProposalDbId(proposal);
            if (pid == null) continue;
            await api.post(`/api/suppliers/description-alias-proposals/${pid}/reject`, {});
            rejected += 1;
          }
          const target = rows.filter((r) => r.status === 'confirmed' && r.alias).map((r) => r.alias as SupplierRuleRow);
          for (const alias of target) {
            await api.delete(`/api/suppliers/description-aliases/${alias.id}`);
          }
          const parts: string[] = [];
          if (target.length > 0) parts.push(`deleted ${target.length} confirmed`);
          if (rejected > 0) parts.push(`rejected ${rejected} proposed`);
          setSuccessMessage(parts.length > 0 ? `Supplier rules: ${parts.join(', ')}.` : 'No supplier rules were removed.');
        } else if (action === 'activate' || action === 'deactivate') {
          const target = rows.filter((r) => r.status === 'confirmed' && r.alias).map((r) => r.alias as SupplierRuleRow);
          const nextActive = action === 'activate';
          for (const alias of target) {
            const payload = buildSupplierAliasUpdatePayload(alias, { is_active: nextActive });
            await api.put(`/api/suppliers/description-aliases/${alias.id}`, payload);
          }
          setSuccessMessage(`${nextActive ? 'Activated' : 'Deactivated'} ${target.length} supplier rule(s).`);
        } else if (action === 'run') {
          const target = rows.filter((r) => r.status === 'confirmed' && r.alias).map((r) => r.alias as SupplierRuleRow);
          for (const alias of target) {
            await api.post(`/api/suppliers/description-aliases/${alias.id}/apply-single`, {});
          }
          setSuccessMessage(`Ran ${target.length} selected supplier rule(s) on all matching transactions.`);
        } else {
          const target = rows.filter((r) => r.status === 'confirmed' && r.alias).map((r) => r.alias as SupplierRuleRow);
          if (!hasBulkFieldOverride) {
            setError('Set at least one bulk field: priority, bank account, or comment.');
            return;
          }
          for (const alias of target) {
            const payload = buildSupplierAliasUpdatePayload(alias, {
              ...(hasPriorityOverride ? { priority: Number(priorityOverride) } : {}),
              ...(hasAccountScopeOverride
                ? { account_scope: normalizeRuleAccountScope(String(bulkSupplierAccountScope)) }
                : {}),
              ...(hasCommentAppend ? { notes: appendBulkComment(alias.notes ?? null, commentToAppend) } : {})
            });
            await api.put(`/api/suppliers/description-aliases/${alias.id}`, payload);
          }
          setSuccessMessage(`Updated ${target.length} confirmed supplier rule(s).`);
        }
        await refreshRulesTabDatasets();
        setSelectedSupplierRuleKeys([]);
      } catch (err: any) {
        setError(err?.response?.data?.error || err?.message || 'Bulk supplier rule action failed');
        scrollPageToClassificationErrorBanner();
      } finally {
        setProposalBusyKey('');
      }
    },
    [
      buildSupplierAliasUpdatePayload,
      bulkSupplierAccountScope,
      bulkSupplierComment,
      bulkSupplierPriority,
      refreshRulesTabDatasets,
      selectedSupplierRuleKeys,
      visibleSupplierRuleRows
    ]
  );

  const runBulkRejectedSupplierProposalAction = useCallback(
    async (action: 'restore' | 'delete') => {
      const idSet = new Set(selectedRejectedSupplierProposalIds);
      const targets = selectableRejectedSupplierProposalIds.filter((id) => idSet.has(id));
      if (targets.length === 0) return;
      if (action === 'delete') {
        if (
          !window.confirm(
            `Delete ${targets.length} rejected supplier proposal(s) permanently? Generate proposals can recreate matching drafts afterward.`
          )
        ) {
          return;
        }
      }
      setProposalBusyKey(`bulk-supplier-rejected-${action}`);
      setError('');
      try {
        let failed = 0;
        let done = 0;
        const chunkSize = 12;
        for (let i = 0; i < targets.length; i += chunkSize) {
          const batch = targets.slice(i, i + chunkSize);
          const settled = await Promise.allSettled(
            batch.map(async (pid) => {
              if (action === 'restore') {
                await api.post(`/api/suppliers/description-alias-proposals/${pid}/restore`, {});
              } else {
                await api.delete(`/api/suppliers/description-alias-proposals/${pid}`);
              }
            })
          );
          done += settled.filter((r) => r.status === 'fulfilled').length;
          failed += settled.filter((r) => r.status === 'rejected').length;
        }
        await refreshRulesTabDatasets();
        setSelectedRejectedSupplierProposalIds([]);
        if (failed === 0) {
          setSuccessMessage(
            action === 'restore'
              ? `Restored ${done} rejected supplier proposal(s) to Proposed.`
              : `Deleted ${done} rejected supplier proposal(s).`
          );
        } else if (done > 0) {
          setError(
            `${action === 'restore' ? 'Restored' : 'Deleted'} ${done} of ${targets.length}; ${failed} failed. Refresh, then retry the rest.`
          );
          scrollPageToClassificationErrorBanner();
        } else {
          setError(
            action === 'restore'
              ? 'Could not restore selected rejected supplier proposals'
              : 'Could not delete selected rejected supplier proposals'
          );
          scrollPageToClassificationErrorBanner();
        }
      } catch (err: any) {
        setError(
          err?.response?.data?.error ||
            err?.message ||
            (action === 'restore'
              ? 'Could not restore selected rejected supplier proposals'
              : 'Could not delete selected rejected supplier proposals')
        );
        scrollPageToClassificationErrorBanner();
      } finally {
        setProposalBusyKey('');
      }
    },
    [refreshRulesTabDatasets, selectableRejectedSupplierProposalIds, selectedRejectedSupplierProposalIds]
  );

  const runBulkRejectedClassificationProposalAction = useCallback(
    async (action: 'restore' | 'delete') => {
      const idSet = new Set(selectedRejectedClassificationProposalIds);
      const targets = selectableRejectedClassificationProposalIds.filter((id) => idSet.has(id));
      if (targets.length === 0) return;
      if (action === 'delete') {
        if (
          !window.confirm(
            `Delete ${targets.length} rejected classification proposal(s) permanently? ` +
              `Atlas (P-A) items stay permanently dismissed and will not return to Proposed; ` +
              `other drafts can be recreated by Generate afterward.`
          )
        ) {
          return;
        }
      }
      setProposalBusyKey(`bulk-classification-rejected-${action}`);
      setError('');
      try {
        let failed = 0;
        let done = 0;
        const chunkSize = 12;
        for (let i = 0; i < targets.length; i += chunkSize) {
          const batch = targets.slice(i, i + chunkSize);
          const settled = await Promise.allSettled(
            batch.map(async (pid) => {
              if (action === 'restore') {
                await api.post(
                  `/api/transaction-classification-rules/rule-proposals/${pid}/restore`,
                  {}
                );
              } else {
                await api.delete(`/api/transaction-classification-rules/rule-proposals/${pid}`);
              }
            })
          );
          done += settled.filter((r) => r.status === 'fulfilled').length;
          failed += settled.filter((r) => r.status === 'rejected').length;
        }
        await refreshRulesTabDatasets();
        setSelectedRejectedClassificationProposalIds([]);
        if (failed === 0) {
          setSuccessMessage(
            action === 'restore'
              ? `Restored ${done} rejected classification proposal(s) to Proposed.`
              : `Deleted ${done} rejected classification proposal(s).`
          );
        } else if (done > 0) {
          setError(
            `${action === 'restore' ? 'Restored' : 'Deleted'} ${done} of ${targets.length}; ${failed} failed.`
          );
          scrollPageToClassificationErrorBanner();
        } else {
          setError(
            action === 'restore'
              ? 'Could not restore selected rejected classification proposals'
              : 'Could not delete selected rejected classification proposals'
          );
          scrollPageToClassificationErrorBanner();
        }
      } catch (err: any) {
        setError(
          err?.response?.data?.error ||
            err?.message ||
            (action === 'restore'
              ? 'Could not restore selected rejected classification proposals'
              : 'Could not delete selected rejected classification proposals')
        );
        scrollPageToClassificationErrorBanner();
      } finally {
        setProposalBusyKey('');
      }
    },
    [
      refreshRulesTabDatasets,
      selectableRejectedClassificationProposalIds,
      selectedRejectedClassificationProposalIds
    ]
  );

  const runBulkRejectedVatSuggestionAction = useCallback(
    async (action: 'restore' | 'delete') => {
      const idSet = new Set(selectedRejectedVatSuggestionIds);
      const targets = selectableRejectedVatSuggestionIds.filter((id) => idSet.has(id));
      if (targets.length === 0) return;
      if (action === 'delete') {
        if (
          !window.confirm(`Delete ${targets.length} rejected VAT suggestion(s) permanently?`)
        ) {
          return;
        }
      }
      setProposalBusyKey(`bulk-vat-rejected-${action}`);
      setError('');
      try {
        let failed = 0;
        let done = 0;
        const chunkSize = 12;
        for (let i = 0; i < targets.length; i += chunkSize) {
          const batch = targets.slice(i, i + chunkSize);
          const settled = await Promise.allSettled(
            batch.map(async (sid) => {
              if (action === 'restore') {
                await api.post(
                  `/api/transaction-classification-rules/vat-rules/suggestions/${sid}/restore`,
                  {}
                );
              } else {
                await api.delete(
                  `/api/transaction-classification-rules/vat-rules/suggestions/${sid}`
                );
              }
            })
          );
          done += settled.filter((r) => r.status === 'fulfilled').length;
          failed += settled.filter((r) => r.status === 'rejected').length;
        }
        await refreshRulesTabDatasets();
        setSelectedRejectedVatSuggestionIds([]);
        if (failed === 0) {
          setSuccessMessage(
            action === 'restore'
              ? `Restored ${done} rejected VAT suggestion(s) to Proposed.`
              : `Deleted ${done} rejected VAT suggestion(s).`
          );
        } else if (done > 0) {
          setError(
            `${action === 'restore' ? 'Restored' : 'Deleted'} ${done} of ${targets.length}; ${failed} failed.`
          );
          scrollPageToClassificationErrorBanner();
        } else {
          setError(
            action === 'restore'
              ? 'Could not restore selected rejected VAT suggestions'
              : 'Could not delete selected rejected VAT suggestions'
          );
          scrollPageToClassificationErrorBanner();
        }
      } catch (err: any) {
        setError(
          err?.response?.data?.error ||
            err?.message ||
            (action === 'restore'
              ? 'Could not restore selected rejected VAT suggestions'
              : 'Could not delete selected rejected VAT suggestions')
        );
        scrollPageToClassificationErrorBanner();
      } finally {
        setProposalBusyKey('');
      }
    },
    [refreshRulesTabDatasets, selectableRejectedVatSuggestionIds, selectedRejectedVatSuggestionIds]
  );

  const runBulkMerchantSupplierRuleAction = useCallback(
    async (action: 'delete' | 'activate' | 'deactivate' | 'run') => {
      const selected = new Set(selectedMerchantSupplierRuleKeys);
      const target = sortedVisibleMerchantSupplierRuleRows.filter((r) => selected.has(`mc-${r.id}`));
      if (target.length === 0) return;
      setProposalBusyKey(`bulk-merchant-supplier-${action}`);
      setError('');
      try {
        if (action === 'delete') {
          if (
            !window.confirm(
              `Are you sure? This will delete ${target.length} selected merchant supplier rule(s) from Step 2A.`
            )
          ) {
            return;
          }
          for (const alias of target) {
            await api.delete(`/api/suppliers/description-aliases/${alias.id}`);
          }
          setSuccessMessage(`Deleted ${target.length} merchant supplier rule(s).`);
        } else if (action === 'activate' || action === 'deactivate') {
          const nextActive = action === 'activate';
          for (const alias of target) {
            const payload = buildSupplierAliasUpdatePayload(alias, { is_active: nextActive });
            await api.put(`/api/suppliers/description-aliases/${alias.id}`, payload);
          }
          setSuccessMessage(`${nextActive ? 'Activated' : 'Deactivated'} ${target.length} merchant supplier rule(s).`);
        } else {
          for (const alias of target) {
            await api.post(`/api/suppliers/description-aliases/${alias.id}/apply-single`, {});
          }
          setSuccessMessage(`Ran ${target.length} selected merchant supplier rule(s) on all matching transactions.`);
        }
        await refreshRulesTabDatasets();
        setSelectedMerchantSupplierRuleKeys([]);
      } catch (err: any) {
        setError(err?.response?.data?.error || err?.message || 'Bulk merchant supplier rule action failed');
        scrollPageToClassificationErrorBanner();
      } finally {
        setProposalBusyKey('');
      }
    },
    [
      buildSupplierAliasUpdatePayload,
      refreshRulesTabDatasets,
      selectedMerchantSupplierRuleKeys,
      sortedVisibleMerchantSupplierRuleRows
    ]
  );

  const runBulkVatRuleAction = useCallback(
    async (action: 'activate' | 'deactivate' | 'delete' | 'run' | 'applyEdits') => {
      const target = sortedVisibleVatRules.filter((r) => selectedVatRuleIds.includes(Number(r.id)));
      const suggTarget = visibleVatRuleSuggestions.filter((r) => selectedVatSuggestionIds.includes(Number(r.id)));
      if (action === 'delete') {
        if (target.length === 0 && suggTarget.length === 0) return;
      } else if (target.length === 0) {
        return;
      }
      setProposalBusyKey(`bulk-vat-rules-${action}`);
      setError('');
      try {
        const priorityRaw = bulkVatPriority.trim();
        const hasPriorityOverride = priorityRaw !== '';
        const priorityOverride = hasPriorityOverride ? parseInt(priorityRaw, 10) : null;
        if (hasPriorityOverride && !Number.isFinite(priorityOverride)) {
          setError('Bulk priority must be a whole number.');
          return;
        }
        const hasAccountScopeOverride = bulkVatAccountScope !== RULE_BULK_NO_CHANGE;

        if (action === 'activate' || action === 'deactivate') {
          const nextActive = action === 'activate';
          for (const row of target) {
            await api.put(`/api/transaction-classification-rules/vat-rules/${row.id}`, {
              is_active: nextActive
            });
          }
          setSuccessMessage(`${nextActive ? 'Activated' : 'Deactivated'} ${target.length} VAT rule(s).`);
        } else if (action === 'delete') {
          if (
            !window.confirm(
              'Are you sure? This will delete selected VAT rules and reject selected proposed VAT rules.'
            )
          ) {
            return;
          }
          for (const row of target) {
            await api.delete(`/api/transaction-classification-rules/vat-rules/${row.id}`);
          }
          for (const row of suggTarget) {
            await api.post(`/api/transaction-classification-rules/vat-rules/suggestions/${row.id}/reject`, {});
          }
          const parts: string[] = [];
          if (target.length > 0) parts.push(`deleted ${target.length} rule(s)`);
          if (suggTarget.length > 0) parts.push(`rejected ${suggTarget.length} proposed`);
          setSuccessMessage(parts.length > 0 ? `VAT: ${parts.join(', ')}.` : 'No changes.');
        } else if (action === 'run') {
          for (const row of target) {
            await api.post(`/api/transaction-classification-rules/vat-rules/${row.id}/apply-single`, {});
          }
          setSuccessMessage(`Ran ${target.length} selected VAT rule(s) on all matching transactions.`);
        } else {
          if (!hasPriorityOverride && !hasAccountScopeOverride) {
            setError('Set at least one bulk field for VAT rules: priority or bank account.');
            return;
          }
          for (const row of target) {
            const payload: Record<string, any> = {};
            if (hasPriorityOverride) payload.priority = Number(priorityOverride);
            if (hasAccountScopeOverride) {
              payload.account_scope = normalizeRuleAccountScope(String(bulkVatAccountScope));
            }
            await api.put(`/api/transaction-classification-rules/vat-rules/${row.id}`, payload);
          }
          setSuccessMessage(`Updated ${target.length} VAT rule(s).`);
        }
        await refreshRulesTabDatasets();
        setSelectedVatRuleIds([]);
        setSelectedVatSuggestionIds([]);
      } catch (err: any) {
        setError(err?.response?.data?.error || err?.message || 'Bulk VAT rule action failed');
        scrollPageToClassificationErrorBanner();
      } finally {
        setProposalBusyKey('');
      }
    },
    [
      bulkVatAccountScope,
      bulkVatPriority,
      refreshRulesTabDatasets,
      selectedVatRuleIds,
      selectedVatSuggestionIds,
      sortedVisibleVatRules,
      visibleVatRuleSuggestions
    ]
  );

  const runBulkSpecialCaseRuleAction = useCallback(
    async (action: 'activate' | 'deactivate' | 'delete' | 'run' | 'applyEdits') => {
      const target = sortedVisibleSpecialCaseRules.filter((r) =>
        selectedSpecialCaseRuleIds.includes(Number(r.id))
      );
      if (target.length === 0) return;
      setProposalBusyKey(`bulk-special-rules-${action}`);
      setError('');
      try {
        const priorityRaw = bulkSpecialCasePriority.trim();
        const hasPriorityOverride = priorityRaw !== '';
        const priorityOverride = hasPriorityOverride ? parseInt(priorityRaw, 10) : null;
        if (hasPriorityOverride && !Number.isFinite(priorityOverride)) {
          setError('Bulk priority must be a whole number.');
          return;
        }
        const hasAccountScopeOverride = bulkSpecialCaseAccountScope !== RULE_BULK_NO_CHANGE;
        const commentToAppend = bulkSpecialCaseComment.trim();
        const hasCommentAppend = commentToAppend.length > 0;

        if (action === 'activate' || action === 'deactivate') {
          const nextActive = action === 'activate';
          for (const row of target) {
            await api.put(`/api/transaction-classification-rules/special-case-rules/${row.id}`, {
              is_active: nextActive
            });
          }
          setSuccessMessage(`${nextActive ? 'Activated' : 'Deactivated'} ${target.length} special case rule(s).`);
        } else if (action === 'delete') {
          if (!window.confirm('Are you sure you want to delete the selected special case rules?')) {
            return;
          }
          for (const row of target) {
            await api.delete(`/api/transaction-classification-rules/special-case-rules/${row.id}`);
          }
          setSuccessMessage(`Deleted/deactivated ${target.length} special case rule(s).`);
        } else if (action === 'run') {
          let tagsUpdated = 0;
          let txsMatched = 0;
          for (const row of target) {
            const res = await api.post(
              `/api/transaction-classification-rules/special-case-rules/${row.id}/apply-single`,
              {}
            );
            const summary = (res?.data?.summary || {}) as Record<string, unknown>;
            tagsUpdated += Number(summary.tags_updated || 0);
            txsMatched += Number(summary.transactions_matched || 0);
          }
          setSuccessMessage(
            `Ran ${target.length} special case rule(s): ` +
              `${tagsUpdated.toLocaleString()} tag VAT value(s) updated · ` +
              `${txsMatched.toLocaleString()} transaction(s) matched.`
          );
          await refreshRulesTabDatasets();
        } else {
          if (!hasPriorityOverride && !hasAccountScopeOverride && !hasCommentAppend) {
            setError('Set at least one bulk field: priority, bank account, or comment.');
            return;
          }
          for (const row of target) {
            const payload: Record<string, any> = {};
            if (hasPriorityOverride) payload.priority = Number(priorityOverride);
            if (hasAccountScopeOverride) {
              payload.account_scope = normalizeRuleAccountScope(String(bulkSpecialCaseAccountScope));
            }
            if (hasCommentAppend) payload.notes = appendBulkComment(row.notes ?? null, commentToAppend);
            await api.put(`/api/transaction-classification-rules/special-case-rules/${row.id}`, payload);
          }
          setSuccessMessage(`Updated ${target.length} special case rule(s).`);
        }
        await refreshRulesTabDatasets();
        setSelectedSpecialCaseRuleIds([]);
      } catch (err: any) {
        setError(err?.response?.data?.error || err?.message || 'Bulk Special Case rule action failed');
        scrollPageToClassificationErrorBanner();
      } finally {
        setProposalBusyKey('');
      }
    },
    [
      bulkSpecialCaseAccountScope,
      bulkSpecialCaseComment,
      bulkSpecialCasePriority,
      refreshRulesTabDatasets,
      selectedSpecialCaseRuleIds,
      sortedVisibleSpecialCaseRules
    ]
  );

  const runCrossTypeBulkRuleAction = useCallback(
    async (action: 'delete' | 'activate' | 'deactivate' | 'run' | 'accept' | 'reject' | 'applyMeta') => {
      const selectedClassRows = visibleClassificationRuleRows.filter((r) =>
        selectedClassificationRuleKeys.includes(r.key)
      );
      const selectedSupplierRows = visibleSupplierRuleRows.filter((r) =>
        selectedSupplierRuleKeys.includes(r.key)
      );
      const merchantSelected = new Set(selectedMerchantSupplierRuleKeys);
      const selectedMerchantRows = sortedVisibleMerchantSupplierRuleRows.filter((r) =>
        merchantSelected.has(`mc-${r.id}`)
      );
      const selectedVatRows = sortedVisibleVatRules.filter((r) => selectedVatRuleIds.includes(Number(r.id)));
      const selectedVatProposalRows = visibleVatRuleSuggestions.filter((r) =>
        selectedVatSuggestionIds.includes(Number(r.id))
      );
      const selectedSpecialRows = sortedVisibleSpecialCaseRules.filter((r) =>
        selectedSpecialCaseRuleIds.includes(Number(r.id))
      );
      const totalSelected =
        selectedClassRows.length +
        selectedSupplierRows.length +
        selectedMerchantRows.length +
        selectedVatRows.length +
        selectedVatProposalRows.length +
        selectedSpecialRows.length;
      if (totalSelected === 0) return;
      setProposalBusyKey(`bulk-cross-${action}`);
      setError('');
      try {
        let done = 0;
        let skipped = 0;
        const accountOverrideEnabled = crossBulkAccountScope !== RULE_BULK_NO_CHANGE;
        const accountScopeOverride = accountOverrideEnabled
          ? normalizeRuleAccountScope(String(crossBulkAccountScope))
          : null;
        const commentToAppend = crossBulkComment.trim();
        const commentEnabled = commentToAppend.length > 0;

        if (action === 'delete') {
          if (!window.confirm('Are you sure? This removes selected rules: confirmed are deleted, proposed are rejected, VAT suggestions are rejected.')) {
            return;
          }
          for (const row of selectedClassRows) {
            if (row.status === 'confirmed' && row.rule) {
              await api.delete(`/api/transaction-classification-rules/${(row.rule as ClassificationRule).id}`);
              done += 1;
            } else if (row.status === 'proposed' && row.proposal) {
              const proposal = row.proposal as RuleProposalRow;
              const vkey = proposal.virtual && proposal.atlas_key ? proposal.atlas_key : '';
              if (proposal.virtual && vkey) {
                await api.post('/api/transaction-classification-rules/rule-proposals/reject-atlas', {
                  atlas_key: vkey
                });
                done += 1;
              } else if (proposal.id) {
                await api.post(`/api/transaction-classification-rules/rule-proposals/${proposal.id}/reject`, {});
                done += 1;
              } else {
                skipped += 1;
              }
            } else {
              skipped += 1;
            }
          }
          for (const row of selectedSupplierRows) {
            if (row.status === 'confirmed' && row.alias) {
              await api.delete(`/api/suppliers/description-aliases/${(row.alias as SupplierRuleRow).id}`);
              done += 1;
            } else if (row.status === 'proposed' && row.proposal) {
              const pr = row.proposal as SupplierRuleProposalRow;
              const pid = supplierRuleProposalDbId(pr);
              if (pid == null) {
                skipped += 1;
                continue;
              }
              await api.post(`/api/suppliers/description-alias-proposals/${pid}/reject`, {});
              done += 1;
            } else {
              skipped += 1;
            }
          }
          for (const alias of selectedMerchantRows) {
            await api.delete(`/api/suppliers/description-aliases/${alias.id}`);
            done += 1;
          }
          for (const row of selectedVatRows) {
            await api.delete(`/api/transaction-classification-rules/vat-rules/${row.id}`);
            done += 1;
          }
          for (const row of selectedVatProposalRows) {
            await api.post(`/api/transaction-classification-rules/vat-rules/suggestions/${row.id}/reject`, {});
            done += 1;
          }
          for (const row of selectedSpecialRows) {
            await api.delete(`/api/transaction-classification-rules/special-case-rules/${row.id}`);
            done += 1;
          }
          setSuccessMessage(`Deleted or rejected ${done} selected row(s).${skipped > 0 ? ` Skipped ${skipped} selection(s).` : ''}`);
        } else if (action === 'activate' || action === 'deactivate') {
          const nextActive = action === 'activate';
          for (const row of selectedClassRows) {
            if (row.status === 'confirmed' && row.rule) {
              await api.put(
                `/api/transaction-classification-rules/${(row.rule as ClassificationRule).id}`,
                { is_active: nextActive },
                CLASSIFICATION_RULE_SAVE_CONFIG
              );
              done += 1;
            } else {
              skipped += 1;
            }
          }
          for (const row of selectedSupplierRows) {
            if (row.status === 'confirmed' && row.alias) {
              const alias = row.alias as SupplierRuleRow;
              const payload = buildSupplierAliasUpdatePayload(alias, { is_active: nextActive });
              await api.put(`/api/suppliers/description-aliases/${alias.id}`, payload);
              done += 1;
            } else {
              skipped += 1;
            }
          }
          for (const alias of selectedMerchantRows) {
            const payload = buildSupplierAliasUpdatePayload(alias, { is_active: nextActive });
            await api.put(`/api/suppliers/description-aliases/${alias.id}`, payload);
            done += 1;
          }
          for (const row of selectedVatRows) {
            await api.put(`/api/transaction-classification-rules/vat-rules/${row.id}`, { is_active: nextActive });
            done += 1;
          }
          for (const row of selectedSpecialRows) {
            await api.put(`/api/transaction-classification-rules/special-case-rules/${row.id}`, { is_active: nextActive });
            done += 1;
          }
          skipped += selectedVatProposalRows.length;
          setSuccessMessage(
            `${nextActive ? 'Activated' : 'Deactivated'} ${done} selected rule(s).${skipped > 0 ? ` Skipped ${skipped} non-confirmed selection(s).` : ''}`
          );
        } else if (action === 'run') {
          for (const row of selectedClassRows) {
            if (row.status === 'confirmed' && row.rule) {
              await api.post(`/api/transaction-classification-rules/${(row.rule as ClassificationRule).id}/apply-single`, {});
              done += 1;
            } else {
              skipped += 1;
            }
          }
          for (const row of selectedSupplierRows) {
            if (row.status === 'confirmed' && row.alias) {
              await api.post(`/api/suppliers/description-aliases/${(row.alias as SupplierRuleRow).id}/apply-single`, {});
              done += 1;
            } else {
              skipped += 1;
            }
          }
          for (const alias of selectedMerchantRows) {
            await api.post(`/api/suppliers/description-aliases/${alias.id}/apply-single`, {});
            done += 1;
          }
          for (const row of selectedVatRows) {
            await api.post(`/api/transaction-classification-rules/vat-rules/${row.id}/apply-single`, {});
            done += 1;
          }
          for (const row of selectedSpecialRows) {
            await api.post(`/api/transaction-classification-rules/special-case-rules/${row.id}/apply-single`, {});
            done += 1;
          }
          skipped += selectedVatProposalRows.length;
          setSuccessMessage(`Ran ${done} selected confirmed rule(s) on all matching transactions.${skipped > 0 ? ` Skipped ${skipped} proposed row(s).` : ''}`);
        } else if (action === 'accept' || action === 'reject') {
          // Classification proposals.
          for (const row of selectedClassRows) {
            if (!(row.status === 'proposed' && row.proposal)) {
              skipped += 1;
              continue;
            }
            const proposal = row.proposal as RuleProposalRow;
            const vkey = proposal.virtual && proposal.atlas_key ? proposal.atlas_key : '';
            if (action === 'accept') {
              if (proposal.virtual && vkey) {
                await api.post('/api/transaction-classification-rules/rule-proposals/accept-atlas', {
                  atlas_key: vkey
                });
              } else if (proposal.id) {
                await api.post(`/api/transaction-classification-rules/rule-proposals/${proposal.id}/accept`, {});
              } else {
                skipped += 1;
                continue;
              }
            } else {
              if (proposal.virtual && vkey) {
                await api.post('/api/transaction-classification-rules/rule-proposals/reject-atlas', {
                  atlas_key: vkey
                });
              } else if (proposal.id) {
                await api.post(`/api/transaction-classification-rules/rule-proposals/${proposal.id}/reject`, {});
              } else {
                skipped += 1;
                continue;
              }
            }
            done += 1;
          }
          // Supplier proposals.
          for (const row of selectedSupplierRows) {
            if (!(row.status === 'proposed' && row.proposal)) {
              skipped += 1;
              continue;
            }
            const proposal = row.proposal as SupplierRuleProposalRow;
            const pid = supplierRuleProposalDbId(proposal);
            if (pid == null) {
              skipped += 1;
              continue;
            }
            await api.post(`/api/suppliers/description-alias-proposals/${pid}/${action}`, {});
            done += 1;
          }
          // VAT proposals.
          for (const row of selectedVatProposalRows) {
            await api.post(`/api/transaction-classification-rules/vat-rules/suggestions/${row.id}/${action === 'accept' ? 'approve' : 'reject'}`, {});
            done += 1;
          }
          // Confirmed rows do not apply for accept/reject.
          skipped += selectedVatRows.length + selectedSpecialRows.length + selectedMerchantRows.length;
          setSuccessMessage(
            `${action === 'accept' ? 'Accepted' : 'Rejected'} ${done} selected proposed rule(s).${skipped > 0 ? ` Skipped ${skipped} confirmed/non-proposed selection(s).` : ''}`
          );
        } else {
          // applyMeta: account scope + comment append where supported
          if (!accountOverrideEnabled && !commentEnabled) {
            setError('Set at least one field to apply: Bank account and/or Comment.');
            return;
          }
          for (const row of selectedClassRows) {
            if (!(row.status === 'confirmed' && row.rule)) {
              skipped += 1;
              continue;
            }
            const rule = row.rule as ClassificationRule;
            const payload: Record<string, any> = {};
            if (accountOverrideEnabled) payload.account_scope = accountScopeOverride;
            if (commentEnabled) payload.notes = appendBulkComment(rule.notes ?? null, commentToAppend);
            await api.put(
              `/api/transaction-classification-rules/${rule.id}`,
              payload,
              CLASSIFICATION_RULE_SAVE_CONFIG
            );
            done += 1;
          }
          for (const row of selectedSupplierRows) {
            if (!(row.status === 'confirmed' && row.alias)) {
              skipped += 1;
              continue;
            }
            const alias = row.alias as SupplierRuleRow;
            const payload = buildSupplierAliasUpdatePayload(alias, {
              ...(accountOverrideEnabled ? { account_scope: accountScopeOverride as 'both' | 'ireland' | 'uk' } : {}),
              ...(commentEnabled ? { notes: appendBulkComment(alias.notes ?? null, commentToAppend) } : {})
            });
            await api.put(`/api/suppliers/description-aliases/${alias.id}`, payload);
            done += 1;
          }
          for (const alias of selectedMerchantRows) {
            const payload = buildSupplierAliasUpdatePayload(alias, {
              ...(accountOverrideEnabled ? { account_scope: accountScopeOverride as 'both' | 'ireland' | 'uk' } : {}),
              ...(commentEnabled ? { notes: appendBulkComment(alias.notes ?? null, commentToAppend) } : {})
            });
            await api.put(`/api/suppliers/description-aliases/${alias.id}`, payload);
            done += 1;
          }
          for (const row of selectedVatRows) {
            // VAT rules do not expose notes/comment field.
            if (accountOverrideEnabled) {
              await api.put(`/api/transaction-classification-rules/vat-rules/${row.id}`, {
                account_scope: accountScopeOverride
              });
              done += 1;
            } else {
              skipped += 1;
            }
          }
          for (const row of selectedSpecialRows) {
            const payload: Record<string, any> = {};
            if (accountOverrideEnabled) payload.account_scope = accountScopeOverride;
            if (commentEnabled) payload.notes = appendBulkComment(row.notes ?? null, commentToAppend);
            await api.put(`/api/transaction-classification-rules/special-case-rules/${row.id}`, payload);
            done += 1;
          }
          skipped += selectedVatProposalRows.length;
          setSuccessMessage(
            `Applied metadata changes to ${done} selected rule(s).${skipped > 0 ? ` Skipped ${skipped} selection(s) where fields do not apply.` : ''}`
          );
        }
        await refreshRulesTabDatasets();
        clearAllRuleSelectionsAcrossAccordions();
      } catch (err: any) {
        setError(err?.response?.data?.error || err?.message || 'Cross-type bulk action failed');
        scrollPageToClassificationErrorBanner();
      } finally {
        setProposalBusyKey('');
      }
    },
    [
      buildSupplierAliasUpdatePayload,
      clearAllRuleSelectionsAcrossAccordions,
      crossBulkAccountScope,
      crossBulkComment,
      refreshRulesTabDatasets,
      selectedClassificationRuleKeys,
      selectedMerchantSupplierRuleKeys,
      selectedSpecialCaseRuleIds,
      selectedSupplierRuleKeys,
      selectedVatRuleIds,
      selectedVatSuggestionIds,
      sortedVisibleMerchantSupplierRuleRows,
      sortedVisibleSpecialCaseRules,
      sortedVisibleVatRules,
      visibleClassificationRuleRows,
      visibleSupplierRuleRows,
      visibleVatRuleSuggestions
    ]
  );

  const openProposeRuleForTx = useCallback(
    (tx: ReviewTx) => {
      // Default classification drafts to canonical supplier (not merchant text match).
      const tagSup = (tx.supplier_name || '').trim();
      const merchant = (tx.merchant_name || tx.counterparty_name || '').trim();
      const canon = tagSup || merchant;
      const desc = (tx.description || tx.reference || '').trim();
      const g = primaryGranularCategoryId(selectedGranularByTx, tx.id);
      const catId: number | '' = selectHasChoice(g) ? Number(g) : '';
      setProposeRuleTx(tx);
      setProposeRuleName(
        canon
          ? `Match: ${canon}`
          : desc
            ? `Match: ${desc.length > 48 ? `${desc.slice(0, 48)}…` : desc}`
            : `Rule from transaction ${tx.id}`
      );
      setProposeRuleCategoryId(catId);
      setProposeRuleSupplier(canon || (!merchant && desc ? desc : ''));
      setProposeRuleDescription('');
      setProposeRuleOpen(true);
    },
    [selectedGranularByTx]
  );

  const submitProposeRule = useCallback(async () => {
    if (!proposeRuleTx) return;
    const name = proposeRuleName.trim();
    const sup = proposeRuleSupplier.trim();
    const desc = proposeRuleDescription.trim();
    if (!name) {
      setError('Enter a rule name.');
      return;
    }
    if (!sup && !desc) {
      setError('Add a canonical supplier and/or description match (at least one).');
      return;
    }
    if (proposeRuleCategoryId === '') {
      setError('Pick a category for this rule.');
      return;
    }
    setProposeRuleSaving(true);
    setError('');
    try {
      const createRes = await api.post('/api/transaction-classification-rules/rule-proposals', {
        name,
        category_id: proposeRuleCategoryId,
        // Default: canonical supplier match (not merchant supplier_contains).
        ...(sup
          ? { supplier_name_override: sup, supplier_name_overrides: [sup] }
          : {}),
        description_contains: desc || undefined,
        revolut_transaction_id: proposeRuleTx.id,
        priority: 20
      });
      const createdProposalId = Math.trunc(Number(createRes.data?.proposal?.id));
      setProposeRuleOpen(false);
      setProposeRuleTx(null);
      const pr = await api.get('/api/transaction-classification-rules/rule-proposals');
      const praw = pr.data as RuleProposalsPayload | undefined;
      setProposalsPayload({
        proposals: praw?.proposals ?? [],
        count_atlas_virtual: praw?.count_atlas_virtual,
        count_saved_pending: praw?.count_saved_pending,
        count_saved_rejected: Number(praw?.count_saved_rejected || 0),
      });
      openRulesTabAfterProposedClassification(
        Number.isFinite(createdProposalId) && createdProposalId > 0 ? createdProposalId : null
      );
      setSuccessMessage(
        Number.isFinite(createdProposalId) && createdProposalId > 0
          ? `Proposal saved (P-C${createdProposalId}). Rules tab is open on Proposed.`
          : 'Proposal saved. Rules tab is open on Proposed.'
      );
    } catch (err: any) {
      setError(err?.response?.data?.error || err?.message || 'Could not save proposal');
    } finally {
      setProposeRuleSaving(false);
    }
  }, [
    proposeRuleTx,
    proposeRuleName,
    proposeRuleSupplier,
    proposeRuleDescription,
    proposeRuleCategoryId,
    openRulesTabAfterProposedClassification
  ]);

  const matchTxLoadMoreInFlightRef = useRef(false);

  const MATCH_TX_PAGE_LIMIT = 50;

  const fetchMatchesPage = useCallback(
    async (source: MatchTxModalSource, cursorId: number | null, dateFrom?: string, dateTo?: string) => {
      const params: Record<string, string | number> = { limit: MATCH_TX_PAGE_LIMIT };
      if (cursorId != null) params.cursor_id = cursorId;
      const from = (dateFrom || '').trim();
      const to = (dateTo || '').trim();
      if (from) params.date_from = from;
      if (to) params.date_to = to;
      if (source.kind === 'rule') {
        return api.get(`/api/transaction-classification-rules/${source.rule.id}/matching-transactions`, {
          params
        });
      }
      if (source.kind === 'supplierRule') {
        return api.get(`/api/suppliers/description-aliases/${source.row.id}/matching-transactions`, {
          params
        });
      }
      if (source.kind === 'supplierProposal') {
        return api.get(
          `/api/suppliers/description-alias-proposals/${source.proposalId}/matching-transactions`,
          { params }
        );
      }
      if (source.kind === 'vatRule') {
        return api.get(`/api/transaction-classification-rules/vat-rules/${source.row.id}/matching-transactions`, {
          params
        });
      }
      if (source.kind === 'specialCaseRule') {
        return api.get(
          `/api/transaction-classification-rules/special-case-rules/${source.row.id}/matching-transactions`,
          { params }
        );
      }
      if (source.proposalId) {
        params.proposal_id = source.proposalId;
      } else if (source.atlasKey) {
        params.atlas_key = source.atlasKey;
      }
      return api.get('/api/transaction-classification-rules/rule-proposals/matching-transactions', { params });
    },
    []
  );

  const loadMoreRuleMatches = useCallback(async () => {
    if (
      !matchTxSource ||
      matchTxLoadMoreInFlightRef.current ||
      matchTxLoadingMore ||
      matchTxLoading ||
      !matchTxHasMore ||
      matchTxCursorId == null
    ) {
      return;
    }
    matchTxLoadMoreInFlightRef.current = true;
    setMatchTxLoadingMore(true);
    setMatchTxError('');
    try {
      const res = await fetchMatchesPage(matchTxSource, matchTxCursorId, matchTxDateFrom, matchTxDateTo);
      const txs = (res.data.transactions || []) as MatchingTransactionRow[];
      setMatchTxRows((prev) => [...prev, ...txs]);
      const hasMore = !!res.data.has_more;
      setMatchTxHasMore(hasMore);
      setMatchTxCursorId(hasMore ? (res.data.next_cursor_id ?? (txs.length ? txs[txs.length - 1].id : null)) : null);
    } catch (err: any) {
      setMatchTxError(err?.response?.data?.error || err?.message || 'Could not load more');
    } finally {
      matchTxLoadMoreInFlightRef.current = false;
      setMatchTxLoadingMore(false);
    }
  }, [
    matchTxSource,
    matchTxCursorId,
    matchTxDateFrom,
    matchTxDateTo,
    matchTxHasMore,
    fetchMatchesPage,
    matchTxLoading,
    matchTxLoadingMore
  ]);

  const loadMoreRuleMatchesRef = useRef(loadMoreRuleMatches);
  loadMoreRuleMatchesRef.current = loadMoreRuleMatches;

  const matchTxAccountOptions = useMemo(() => {
    return Array.from(
      new Set(
        matchTxRows
          .map((tx) => formatRevolutAccountLabel(tx.account_source))
          .filter((v) => (v || '').trim() && v !== '—')
      )
    ).sort((a, b) => compareStrings(a.toLowerCase(), b.toLowerCase()));
  }, [matchTxRows]);

  const filteredMatchTxRows = useMemo(() => {
    const minRaw = (matchTxFilterAmountMin || '').trim();
    const maxRaw = (matchTxFilterAmountMax || '').trim();
    const min = minRaw === '' ? null : Number(minRaw);
    const max = maxRaw === '' ? null : Number(maxRaw);
    const textNeedle = (matchTxFilterText || '').trim().toLowerCase();

    return matchTxRows.filter((tx) => {
      const amount = tx.amount ?? null;
      if (min != null && Number.isFinite(min) && (amount == null || amount < min)) return false;
      if (max != null && Number.isFinite(max) && (amount == null || amount > max)) return false;

      if (matchTxFilterHeading === 'income') {
        if (amount == null || amount <= 0) return false;
      } else if (matchTxFilterHeading === 'expense') {
        if (amount == null || amount > 0) return false;
      }

      if (matchTxFilterAccount !== 'all') {
        const label = formatRevolutAccountLabel(tx.account_source);
        if (label.toLowerCase() !== matchTxFilterAccount.toLowerCase()) return false;
      }

      if (textNeedle) {
        const hay = [
          tx.id,
          tx.supplier_name || '',
          tx.reference || '',
          tx.description || '',
          formatRevolutAccountLabel(tx.account_source)
        ]
          .join(' ')
          .toLowerCase();
        if (!hay.includes(textNeedle)) return false;
      }
      return true;
    });
  }, [
    matchTxRows,
    matchTxFilterAmountMin,
    matchTxFilterAmountMax,
    matchTxFilterHeading,
    matchTxFilterAccount,
    matchTxFilterText
  ]);

  const generateRuleProposalsForTxIdsAndOpenRules = useCallback(
    async (
      ids: number[],
      opts?: RuleProposalTxnGenerateOpts
    ): Promise<{
      createdIds: number[];
      scanned: number;
      created: number;
      rawSummary?: Record<string, unknown>;
    }> => {
      const audit = opts?.audit;
      const useAudit = Boolean(audit?.auditRunId?.trim() && audit.candidateIds.length > 0);
      const validIds = ids.filter((id) => typeof id === 'number' && Number.isFinite(id) && id > 0);
      if (!useAudit && validIds.length === 0) return { createdIds: [], scanned: 0, created: 0 };

      const uid = opts?.user_id;

      const v2opts: PostRuleProposalsV2Options = {};
      if (uid != null && Number(uid) > 0) {
        v2opts.userId = Number(uid);
      }
      if (opts?.similar_tx_modal) {
        v2opts.similar_tx_modal = true;
      }
      if (
        opts?.similar_tx_anchor_transaction_id != null &&
        Number(opts.similar_tx_anchor_transaction_id) > 0
      ) {
        v2opts.similar_tx_anchor_transaction_id = Math.trunc(Number(opts.similar_tx_anchor_transaction_id));
      }
      if (opts?.similar_tx_supplier_proposal_db_ids && opts.similar_tx_supplier_proposal_db_ids.length > 0) {
        const sp = opts.similar_tx_supplier_proposal_db_ids
          .map((n) => Math.trunc(Number(n)))
          .filter((n) => Number.isFinite(n) && n > 0);
        if (sp.length > 0) {
          v2opts.similar_tx_supplier_proposal_db_ids = sp;
        }
      }
      if (useAudit) {
        v2opts.auditCandidates = {
          auditRunId: audit!.auditRunId.trim(),
          candidateIds: audit!.candidateIds
        };
      }

      const res = await postRuleProposalsV2(
        api,
        'classification',
        useAudit ? [] : validIds,
        Object.keys(v2opts).length > 0 ? v2opts : undefined
      );
      const createdRaw = res.data?.created_proposal_ids;
      const createdIds: number[] = Array.isArray(createdRaw)
        ? createdRaw.map((x: unknown) => Number(x)).filter((n) => Number.isFinite(n) && n > 0)
        : [];
      const scanned = Number(res.data?.transactions_scanned ?? validIds.length);
      const created = Number(res.data?.created ?? createdIds.length);

      await refreshRulesTabDatasets();

      if (!opts?.deferRulesTabNavigation) {
        const primaryProposalId = createdIds.length > 0 ? createdIds[0] : null;
        openRulesTabAfterProposedClassification(primaryProposalId);
      }
      const rawSummary =
        res.data && typeof res.data === 'object'
          ? (res.data as Record<string, unknown>)
          : undefined;
      return { createdIds, scanned, created, rawSummary };
    },
    [api, openRulesTabAfterProposedClassification, refreshRulesTabDatasets]
  );

  const generateSupplierProposalsForTxIdsAndOpenRules = useCallback(
    async (
      ids: number[],
      opts?: RuleProposalTxnGenerateOpts
    ): Promise<{
      created: number;
      scanned: number;
      primaryProposalId: number | null;
      createdProposalDbIds: number[];
      rawSummary?: Record<string, unknown>;
    }> => {
      const audit = opts?.audit;
      const useAudit = Boolean(audit?.auditRunId?.trim() && audit.candidateIds.length > 0);
      const validIds = ids.filter((id) => typeof id === 'number' && Number.isFinite(id) && id > 0);
      if (!useAudit && validIds.length === 0) {
        return { created: 0, scanned: 0, primaryProposalId: null, createdProposalDbIds: [] };
      }

      const uid = opts?.user_id;

      const v2opts: PostRuleProposalsV2Options = {};
      if (uid != null && Number(uid) > 0) {
        v2opts.userId = Number(uid);
      }
      if (opts?.similar_tx_modal) {
        v2opts.similar_tx_modal = true;
      }
      if (useAudit) {
        v2opts.auditCandidates = {
          auditRunId: audit!.auditRunId.trim(),
          candidateIds: audit!.candidateIds
        };
      }

      const postRes = await postRuleProposalsV2(
        api,
        'supplier_alias',
        useAudit ? [] : validIds,
        Object.keys(v2opts).length > 0 ? v2opts : undefined
      );
      const rawSummary =
        postRes.data && typeof postRes.data === 'object'
          ? (postRes.data as Record<string, unknown>)
          : undefined;

      const idsFromApi = rawSummary?.created_proposal_ids;
      const createdProposalDbIds: number[] = Array.isArray(idsFromApi)
        ? idsFromApi
            .map((x: unknown) => Number(x))
            .filter((n: number) => Number.isFinite(n) && n > 0)
            .map(Math.trunc)
        : [];

      await refreshRulesTabDatasets();
      const listRes = await api.get('/api/suppliers/description-alias-proposals');
      const proposals = Array.isArray(listRes.data?.proposals) ? listRes.data.proposals : [];
      const matchSource =
        useAudit && opts?.auditMatchTransactionIds?.length
          ? opts.auditMatchTransactionIds
          : validIds;
      const idSet = new Set(matchSource.filter((id) => Number.isFinite(id) && id > 0));
      type SupplierProposalPick = { p: SupplierRuleProposalRow; id: number };
      const matching: SupplierProposalPick[] = [];
      for (const p of proposals as SupplierRuleProposalRow[]) {
        if (!idSet.has(Number(p.revolut_transaction_id || 0))) continue;
        const pid = supplierRuleProposalDbId(p);
        if (pid != null && pid > 0) matching.push({ p, id: pid });
      }
      matching.sort((a, b) => b.id - a.id);
      const primaryProposalId =
        createdProposalDbIds.length > 0
          ? Math.max(...createdProposalDbIds)
          : matching.length > 0
            ? matching[0].id
            : null;
      const apiScanned = Number(rawSummary?.transactions_scanned ?? validIds.length);
      const scanned = Number.isFinite(apiScanned) ? apiScanned : validIds.length;
      const apiCreatedRaw = Number(rawSummary?.created);
      const created = Number.isFinite(apiCreatedRaw)
        ? apiCreatedRaw
        : createdProposalDbIds.length || matching.length;

      if (!opts?.deferRulesTabNavigation) {
        setRulesTextFilter('');
        setRulesSupplierFilter('all');
        setRulesCategoryFilter('all');
        setRulesFrequencyFilter('all');
        setRulesStatusFilter('proposed');
        setRulesProposalIdsFilter('');
        setRulesIdFilter('');

        setSearchParams((prev) => {
          const next = new URLSearchParams(prev);
          stripRulesQuickviewSearchParams(next);
          next.set('tab', 'rules');
          next.set(RULES_URL_KEYS.status, 'proposed');
          next.delete(RULES_URL_KEYS.proposalIds);
          next.delete(RULES_URL_KEYS.id);
          if (primaryProposalId != null) next.set(RULES_URL_KEYS.text, String(primaryProposalId));
          else next.delete(RULES_URL_KEYS.text);
          return next;
        }, { replace: true });

        setRulesAccordionOpen({
          proposals: false,
          pipelineStepRules: false,
      merchantSuppliers: true,
          supplierProposals: true,
          appRules: false,
          supplierRules: false,
          vatRules: false,
          specialCaseRules: false
        });
      }
      return { created, scanned, primaryProposalId, rawSummary, createdProposalDbIds };
    },
    [api, refreshRulesTabDatasets, setSearchParams]
  );

  const generateVatSuggestionsFromSimilarRowsAndOpenRules = useCallback(
    async (
      rows: UnifiedReviewRow[],
      opts?: { user_id?: number | null }
    ): Promise<{ suggestionId: number | null }> => {
      const body: Record<string, unknown> = { account_scope: 'both' };
      const uid = opts?.user_id;
      if (uid != null && Number.isFinite(Number(uid)) && Number(uid) > 0) {
        body.user_id = Number(uid);
      }
      await api.post('/api/transaction-classification-rules/vat-rules/suggestions/generate', body);
      const listRes = await api.get('/api/transaction-classification-rules/vat-rules/suggestions', {
        params: { status: 'pending', account_scope: 'both' }
      });
      const suggestions = Array.isArray(listRes.data?.suggestions) ? listRes.data.suggestions : [];
      setVatRuleSuggestions(suggestions);
      {
        const rejectedListRes = await api
          .get('/api/transaction-classification-rules/vat-rules/suggestions', {
            params: { status: 'rejected', account_scope: 'both' }
          })
          .catch(() => ({ data: { suggestions: [] } }));
        setRejectedVatRuleSuggestions(
          Array.isArray(rejectedListRes.data?.suggestions) ? rejectedListRes.data.suggestions : []
        );
      }

      const categoryNames = new Set(
        rows
          .map((r) => String(r.category || '').trim())
          .filter((c) => c && !isTopLevelCashflowHeadingName(c))
      );
      let pick: VatRuleSuggestionRow | null =
        suggestions.find((s: VatRuleSuggestionRow) =>
          categoryNames.has(String(s.category_name || '').trim())
        ) || null;
      if (!pick && suggestions.length > 0) {
        pick = [...suggestions].sort((a: VatRuleSuggestionRow, b: VatRuleSuggestionRow) => Number(b.id) - Number(a.id))[0];
      }
      const primaryId = pick?.id != null ? Number(pick.id) : null;

      setRulesTextFilter(primaryId != null && Number.isFinite(primaryId) ? String(primaryId) : '');
      setRulesSupplierFilter('all');
      setRulesCategoryFilter('all');
      setRulesFrequencyFilter('all');
      setRulesStatusFilter('proposed');
      setRulesProposalIdsFilter('');
      setRulesIdFilter('');

      const next = new URLSearchParams(searchParams);
      next.set('tab', 'rules');
      next.set(RULES_URL_KEYS.status, 'proposed');
      next.delete(RULES_URL_KEYS.id);
      next.delete(RULES_URL_KEYS.proposalIds);
      if (primaryId != null && Number.isFinite(primaryId)) next.set(RULES_URL_KEYS.text, String(primaryId));
      else next.delete(RULES_URL_KEYS.text);
      setSearchParams(next, { replace: true });

      setRulesAccordionOpen({
        proposals: false,
        pipelineStepRules: false,
      merchantSuppliers: false,
        supplierProposals: false,
        appRules: false,
        supplierRules: false,
        vatRules: true,
        specialCaseRules: false
      });
      await refreshRulesTabDatasets();
      return { suggestionId: primaryId != null && Number.isFinite(primaryId) ? primaryId : null };
    },
    [refreshRulesTabDatasets, searchParams, setSearchParams]
  );

  const generateVatSuggestionsForTxIdsAndOpenRules = useCallback(
    async (
      ids: number[],
      opts?: RuleProposalTxnGenerateOpts
    ): Promise<{ suggestionId: number | null; generated: number; updated: number }> => {
      const audit = opts?.audit;
      const useAudit = Boolean(audit?.auditRunId?.trim() && audit.candidateIds.length > 0);
      const validIds = ids.filter((id) => typeof id === 'number' && Number.isFinite(id) && id > 0);
      if (!useAudit && validIds.length === 0) return { suggestionId: null, generated: 0, updated: 0 };

      const uid = opts?.user_id;
      const v2opts: PostRuleProposalsV2Options = { account_scope: 'both' };
      if (uid != null && Number.isFinite(Number(uid)) && Number(uid) > 0) {
        v2opts.userId = Number(uid);
      }
      if (useAudit) {
        v2opts.auditCandidates = {
          auditRunId: audit!.auditRunId.trim(),
          candidateIds: audit!.candidateIds
        };
      }

      const res = await postRuleProposalsV2(api, 'vat_suggestion', useAudit ? [] : validIds, v2opts);
      const data = (res.data || {}) as Record<string, unknown>;
      const generated = Number(data.generated ?? 0);
      const updated = Number(data.updated ?? 0);

      const listRes = await api.get('/api/transaction-classification-rules/vat-rules/suggestions', {
        params: { status: 'pending', account_scope: 'both' }
      });
      const suggestions = Array.isArray(listRes.data?.suggestions) ? listRes.data.suggestions : [];
      setVatRuleSuggestions(suggestions);
      {
        const rejectedListRes = await api
          .get('/api/transaction-classification-rules/vat-rules/suggestions', {
            params: { status: 'rejected', account_scope: 'both' }
          })
          .catch(() => ({ data: { suggestions: [] } }));
        setRejectedVatRuleSuggestions(
          Array.isArray(rejectedListRes.data?.suggestions) ? rejectedListRes.data.suggestions : []
        );
      }

      let pick: VatRuleSuggestionRow | null = null;
      if (suggestions.length > 0) {
        pick = [...suggestions].sort(
          (a: VatRuleSuggestionRow, b: VatRuleSuggestionRow) => Number(b.id) - Number(a.id)
        )[0];
      }
      const primaryId = pick?.id != null ? Number(pick.id) : null;

      setRulesTextFilter(primaryId != null && Number.isFinite(primaryId) ? String(primaryId) : '');
      setRulesSupplierFilter('all');
      setRulesCategoryFilter('all');
      setRulesFrequencyFilter('all');
      setRulesStatusFilter('proposed');
      setRulesProposalIdsFilter('');
      setRulesIdFilter('');

      const next = new URLSearchParams(searchParams);
      next.set('tab', 'rules');
      next.set(RULES_URL_KEYS.status, 'proposed');
      next.delete(RULES_URL_KEYS.id);
      next.delete(RULES_URL_KEYS.proposalIds);
      if (primaryId != null && Number.isFinite(primaryId)) next.set(RULES_URL_KEYS.text, String(primaryId));
      else next.delete(RULES_URL_KEYS.text);
      setSearchParams(next, { replace: true });

      setRulesAccordionOpen({
        proposals: false,
        pipelineStepRules: false,
      merchantSuppliers: false,
        supplierProposals: false,
        appRules: false,
        supplierRules: false,
        vatRules: true,
        specialCaseRules: false
      });
      await refreshRulesTabDatasets();
      return {
        suggestionId: primaryId != null && Number.isFinite(primaryId) ? primaryId : null,
        generated,
        updated
      };
    },
    [api, refreshRulesTabDatasets, searchParams, setSearchParams]
  );

  const handleAuditCandidatesModalGenerate = useCallback(
    async (kind: AuditProposalCandidatesModalKind, payload: { candidateIds: string[]; unionTransactionIds: number[] }) => {
      const { candidateIds, unionTransactionIds } = payload;
      const runId =
        auditCandidatesModal != null && typeof auditCandidatesModal.proposalAuditRunId === 'string'
          ? auditCandidatesModal.proposalAuditRunId.trim()
          : '';
      const audit =
        runId.length > 0 && candidateIds.length > 0
          ? { auditRunId: runId, candidateIds }
          : null;
      if (!audit && unionTransactionIds.length === 0) return;

      setAuditCandidatesGenerating(true);
      setError('');
      try {
        const uid =
          effectiveReviewScopeTargetUserId != null &&
          Number.isFinite(effectiveReviewScopeTargetUserId) &&
          effectiveReviewScopeTargetUserId > 0
            ? Math.floor(Number(effectiveReviewScopeTargetUserId))
            : null;
        const userOpt = uid != null ? ({ user_id: uid } satisfies RuleProposalTxnGenerateOpts) : undefined;

        if (kind === 'classification') {
          setProposalBusyKey('audit-buckets-classification');
          if (audit) {
            await generateRuleProposalsForTxIdsAndOpenRules([], { ...userOpt, audit });
          } else {
            await generateRuleProposalsForTxIdsAndOpenRules(unionTransactionIds, userOpt);
          }
        } else if (kind === 'supplier_alias') {
          setProposalBusyKey('audit-buckets-supplier');
          if (audit) {
            await generateSupplierProposalsForTxIdsAndOpenRules([], {
              ...userOpt,
              audit,
              auditMatchTransactionIds: unionTransactionIds,
            });
          } else {
            await generateSupplierProposalsForTxIdsAndOpenRules(unionTransactionIds, userOpt);
          }
        } else {
          setVatSuggestionGenerateBusy(true);
          if (audit) {
            await generateVatSuggestionsForTxIdsAndOpenRules([], { ...userOpt, audit });
          } else {
            await generateVatSuggestionsForTxIdsAndOpenRules(unionTransactionIds, userOpt);
          }
        }
        setAuditCandidatesModal(null);
      } catch (err: any) {
        setError(err?.response?.data?.error || err?.message || 'Bucket generation failed');
      } finally {
        setAuditCandidatesGenerating(false);
        setProposalBusyKey('');
        setVatSuggestionGenerateBusy(false);
      }
    },
    [
      auditCandidatesModal?.proposalAuditRunId,
      effectiveReviewScopeTargetUserId,
      generateRuleProposalsForTxIdsAndOpenRules,
      generateSupplierProposalsForTxIdsAndOpenRules,
      generateVatSuggestionsForTxIdsAndOpenRules
    ]
  );

  const captureSimilarTxRulesReturnAndCloseModal = useCallback(() => {
    if (!similarTxBaseRow) return false;
    similarTxSuppressDeepLinkRef.current = true;
    const reviewTabRaw = (searchParams.get('tab') || '').trim();
    const reviewTab =
      reviewTabRaw && CLASSIFICATION_VISIBLE_TAB_KEYS.indexOf(reviewTabRaw) >= 0
        ? reviewTabRaw
        : 'needs-review';
    const selectedIds = Array.from(
      new Set(similarTxSelectedIds.filter((id) => filteredSimilarTxIdSet.has(id)))
    );
    similarTxRulesReturnRef.current = {
      reviewTab,
      baseTxId: similarTxBaseRow.id,
      selectedIds
    };
    stripSimilarTxUrlParamViaNavigate();
    clearSimilarTxModalSurfaceStateOnly();
    stripSimilarTxUrlParam();
    setSimilarTxUrlCloseEpoch((n) => n + 1);
    return true;
  }, [
    similarTxBaseRow,
    similarTxSelectedIds,
    filteredSimilarTxIdSet,
    searchParams,
    clearSimilarTxModalSurfaceStateOnly,
    stripSimilarTxUrlParam,
    stripSimilarTxUrlParamViaNavigate
  ]);

  const restoreSimilarTxRulesFlowAfterError = useCallback(() => {
    const p = similarTxRulesReturnRef.current;
    similarTxRulesReturnRef.current = null;
    if (!p) return;
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      stripRulesQuickviewSearchParams(next);
      next.set('tab', p.reviewTab);
      return next;
    }, { replace: true });
    const anchor = unifiedRows.find((r) => r.id === p.baseTxId);
    if (anchor) {
      void openSimilarTransactionsModal(anchor, { restoreSelectedIds: p.selectedIds });
    }
  }, [openSimilarTransactionsModal, setSearchParams, unifiedRows]);

  const runSimilarTxGenerateRulesForKinds = useCallback(
    async (kinds: SimilarTxGenerateKind[]) => {
      if (!similarTxBaseRow || similarTxGenerateRuleBusy) return;
      const ordered = SIMILAR_TX_GENERATE_KIND_ORDER.filter((k) => kinds.includes(k));
      if (ordered.length === 0) return;

      const ids = similarModalSelectedUnifiedRows
        .map((r) => r.id)
        .filter((id): id is number => typeof id === 'number' && Number.isFinite(id) && id > 0);
      const rows = similarModalSelectedUnifiedRows;
      const needsIds = ordered.some((k) => k === 'category' || k === 'supplier');
      const needsRows = ordered.some((k) => k === 'vat' || k === 'special');
      if (needsIds && ids.length === 0) {
        setSimilarTxError('Select at least one row in Similar transactions first.');
        return;
      }
      if (needsRows && rows.length === 0) {
        setSimilarTxError('Select at least one row in Similar transactions first.');
        return;
      }

      const uid = similarTxBaseRow.user_id;
      if (!captureSimilarTxRulesReturnAndCloseModal()) return;

      const summaries: string[] = [];
      const accumulatedSupplierProposalDbIds: number[] = [];
      const accumulatedClassificationProposalDbIds: number[] = [];

      try {
        setError('');
        const preKinds = SIMILAR_TX_GENERATE_KIND_ORDER.filter(
          (k) => ordered.includes(k) && (k === 'supplier' || k === 'category')
        );
        const postKinds = ordered.filter((k) => !preKinds.includes(k));

        for (const kind of preKinds) {
          setSimilarTxGenerateRuleBusy(kind);
          if (kind === 'category') {
            const anchorTxId =
              typeof similarTxBaseRow.id === 'number' && Number.isFinite(similarTxBaseRow.id) && similarTxBaseRow.id > 0
                ? similarTxBaseRow.id
                : undefined;
            const { scanned, created, rawSummary, createdIds } =
              await generateRuleProposalsForTxIdsAndOpenRules(ids, {
                user_id: uid,
                similar_tx_modal: true,
                deferRulesTabNavigation: true,
                ...(anchorTxId != null ? { similar_tx_anchor_transaction_id: anchorTxId } : {}),
                ...(accumulatedSupplierProposalDbIds.length > 0
                  ? { similar_tx_supplier_proposal_db_ids: [...accumulatedSupplierProposalDbIds] }
                  : {})
              });
            accumulatedClassificationProposalDbIds.push(...createdIds);
            summaries.push(
              formatClassificationDraftToastLine({
                origin: RULE_DRAFT_TOAST_ORIGINS.similarTransactionsSelected,
                scanned,
                created,
                apiData: rawSummary
              })
            );
          } else if (kind === 'supplier') {
            const { scanned, created, rawSummary, createdProposalDbIds } =
              await generateSupplierProposalsForTxIdsAndOpenRules(ids, {
                user_id: uid,
                similar_tx_modal: true,
                deferRulesTabNavigation: true
              });
            accumulatedSupplierProposalDbIds.push(...createdProposalDbIds);
            summaries.push(
              formatSupplierDraftToastLine({
                origin: RULE_DRAFT_TOAST_ORIGINS.similarTransactionsSelected,
                scanned,
                created,
                apiData: rawSummary
              })
            );
          }
        }

        if (
          accumulatedSupplierProposalDbIds.length > 0 ||
          accumulatedClassificationProposalDbIds.length > 0
        ) {
          const joined = formatSimilarTxRulesProposalIdsParam(
            accumulatedSupplierProposalDbIds,
            accumulatedClassificationProposalDbIds
          );
          setRulesTextFilter('');
          setRulesSupplierFilter('all');
          setRulesCategoryFilter('all');
          setRulesFrequencyFilter('all');
          setRulesStatusFilter('proposed');
          setRulesIdFilter('');
          setRulesProposalIdsFilter(joined.trim());
          setSearchParams((prev) => {
            const next = new URLSearchParams(prev);
            stripRulesQuickviewSearchParams(next);
            next.set('tab', 'rules');
            next.set(RULES_URL_KEYS.status, 'proposed');
            next.delete(RULES_URL_KEYS.id);
            next.delete(RULES_URL_KEYS.text);
            const j = joined.trim();
            if (j) next.set(RULES_URL_KEYS.proposalIds, j);
            else next.delete(RULES_URL_KEYS.proposalIds);
            return next;
          }, { replace: true });

          setRulesAccordionOpen({
            proposals: true,
            pipelineStepRules: false,
      merchantSuppliers: true,
            supplierProposals: true,
            appRules: false,
            supplierRules: false,
            vatRules: false,
            specialCaseRules: false
          });

          summaries.push(`Rules tab: proposal id filter applied (${joined.trim()}).`);

          await refreshRulesTabDatasets();
        }

        for (const kind of postKinds) {
          setSimilarTxGenerateRuleBusy(kind);
          if (kind === 'vat') {
            const { suggestionId } = await generateVatSuggestionsFromSimilarRowsAndOpenRules(rows, { user_id: uid });
            summaries.push(
              suggestionId != null
                ? `${RULE_DRAFT_TOAST_ORIGINS.similarTransactionsSelected}: VAT suggestions API refreshed; list highlights pending #${suggestionId} when it matches your categories. Rules tab → VAT.`
                : `${RULE_DRAFT_TOAST_ORIGINS.similarTransactionsSelected}: VAT suggestions API refreshed for your catalogue. Open Rules → VAT to review pending rows.`
            );
          } else if (kind === 'special') {
            const draft = guessSpecialCaseDraftFromUnifiedRows(rows);
            setSpecialCaseRuleCriteriaMenuAnchorEl(null);
            setSpecialCaseRuleOutputMenuAnchorEl(null);
            setSpecialCaseRuleOutputVatEnabled(!!draft.set_vat_rate);
            setSpecialCaseRuleDraft(draft);
            setSpecialCaseRuleOpen(true);
            setRulesQuickviewOpen(false);
            setSearchParams((prev) => {
              const next = new URLSearchParams(prev);
              stripRulesQuickviewSearchParams(next);
              next.set('tab', 'rules');
              next.delete(RULES_URL_KEYS.text);
              next.delete(RULES_URL_KEYS.id);
              next.delete(RULES_URL_KEYS.proposalIds);
              next.delete(RULES_URL_KEYS.status);
              return next;
            }, { replace: true });
            setRulesAccordionOpen({
              proposals: false,
              pipelineStepRules: false,
      merchantSuppliers: false,
              supplierProposals: false,
              appRules: false,
              supplierRules: false,
              vatRules: false,
              specialCaseRules: true
            });
            summaries.push(
              `${RULE_DRAFT_TOAST_ORIGINS.similarTransactionsSelected}: opened Special case rule draft (fields prefilled from selected rows). Finish under Rules → Special case.`
            );
          }
        }

        setSuccessMessage(summaries.join(' · '));
      } catch (err: any) {
        setError(
          `Similar transactions → Generate rule drafts: ${
            err?.response?.data?.error || err?.message || 'Could not generate rule drafts'
          }`
        );
        restoreSimilarTxRulesFlowAfterError();
      } finally {
        setSimilarTxGenerateRuleBusy(null);
      }
    },
    [
      similarTxBaseRow,
      similarTxGenerateRuleBusy,
      similarModalSelectedUnifiedRows,
      captureSimilarTxRulesReturnAndCloseModal,
      generateRuleProposalsForTxIdsAndOpenRules,
      generateSupplierProposalsForTxIdsAndOpenRules,
      generateVatSuggestionsFromSimilarRowsAndOpenRules,
      refreshRulesTabDatasets,
      restoreSimilarTxRulesFlowAfterError,
      setSearchParams
    ]
  );

  const confirmSimilarTxGenerateRulesFromPicker = useCallback(() => {
    const kinds = SIMILAR_TX_GENERATE_KIND_ORDER.filter((k) => similarTxGenerateKindsDraft[k]);
    if (kinds.length === 0) return;
    setSimilarTxGenerateRulesPickerOpen(false);
    void runSimilarTxGenerateRulesForKinds(kinds);
  }, [similarTxGenerateKindsDraft, runSimilarTxGenerateRulesForKinds]);

  /** Uses transactions visible in the match modal after client-side filters (same list as the table). */
  const handleGenerateProposalsFromMatchModal = useCallback(async () => {
    const ids = filteredMatchTxRows
      .map((tx) => tx.id)
      .filter((id): id is number => typeof id === 'number' && Number.isFinite(id) && id > 0);
    if (ids.length === 0) {
      setMatchTxError('No transactions in the current modal list to generate proposals from.');
      return;
    }
    setMatchTxGenerateProposalsBusy(true);
    setMatchTxError('');
    try {
      const { scanned, created: cr, rawSummary } = await generateRuleProposalsForTxIdsAndOpenRules(ids);

      setMatchTxSource(null);
      setMatchTxRows([]);
      setMatchTxCursorId(null);
      setMatchTxHasMore(false);
      setMatchTxDateFrom('');
      setMatchTxDateTo('');
      setMatchTxAnchorDate('');
      setMatchTxFilterAccount('all');
      setMatchTxFilterHeading('all');
      setMatchTxFilterAmountMin('');
      setMatchTxFilterAmountMax('');
      setMatchTxFilterText('');
      setMatchTxError('');
      setMatchTxFooterNote('');

      setSuccessMessage(
        formatClassificationDraftToastLine({
          origin: RULE_DRAFT_TOAST_ORIGINS.ruleMatchingModal,
          scanned,
          created: cr,
          apiData: rawSummary
        })
      );
    } catch (err: any) {
      setMatchTxError(err?.response?.data?.error || err?.message || 'Failed to generate proposals');
    } finally {
      setMatchTxGenerateProposalsBusy(false);
    }
  }, [filteredMatchTxRows, generateRuleProposalsForTxIdsAndOpenRules]);

  const applyRuleMatchDateWindow = useCallback(async () => {
    if (!matchTxSource) return;
    const from = (matchTxDateFrom || '').trim();
    const to = (matchTxDateTo || '').trim();
    if (from && to && from > to) {
      setMatchTxError('Date from must be on or before Date to.');
      return;
    }
    setMatchTxLoading(true);
    setMatchTxError('');
    setMatchTxRows([]);
    setMatchTxCursorId(null);
    setMatchTxHasMore(false);
    setMatchTxFooterNote('');
    try {
      const res = await fetchMatchesPage(matchTxSource, null, from, to);
      const txs = (res.data.transactions || []) as MatchingTransactionRow[];
      const hasMore = !!res.data.has_more;
      setMatchTxRows(txs);
      setMatchTxHasMore(hasMore);
      setMatchTxCursorId(hasMore ? (res.data.next_cursor_id ?? (txs.length ? txs[txs.length - 1].id : null)) : null);
      if (res.data.scan_capped && hasMore) {
        setMatchTxFooterNote(
          'Each batch scans up to a safety limit of transactions. Keep scrolling to load older matches.'
        );
      }
    } catch (err: any) {
      setMatchTxError(err?.response?.data?.error || err?.message || 'Could not load matches');
    } finally {
      setMatchTxLoading(false);
    }
  }, [fetchMatchesPage, matchTxDateFrom, matchTxDateTo, matchTxSource]);

  const openMatchingTransactionsForSource = useCallback(
    async (source: MatchTxModalSource) => {
      setMatchTxSource(source);
      setMatchTxRows([]);
      setMatchTxCursorId(null);
      setMatchTxHasMore(false);
      setMatchTxDateFrom('');
      setMatchTxDateTo('');
      setMatchTxAnchorDate('');
      setMatchTxFilterAccount('all');
      setMatchTxFilterHeading('all');
      setMatchTxFilterAmountMin('');
      setMatchTxFilterAmountMax('');
      setMatchTxFilterText('');
      setMatchTxError('');
      setMatchTxFooterNote('');
      setMatchTxLoading(true);
      try {
        // First fetch determines anchor date, then defaults to +/- 1 year.
        let res = await fetchMatchesPage(source, null);
        let txs = (res.data.transactions || []) as MatchingTransactionRow[];
        let hasMore = !!res.data.has_more;
        const anchorIso = txs[0]?.completed_at || txs[0]?.date || null;
        if (anchorIso) {
          const win = defaultMatchWindowAround(anchorIso);
          setMatchTxAnchorDate(win.anchor);
          setMatchTxDateFrom(win.from);
          setMatchTxDateTo(win.to);
          res = await fetchMatchesPage(source, null, win.from, win.to);
          txs = (res.data.transactions || []) as MatchingTransactionRow[];
          hasMore = !!res.data.has_more;
        }
        setMatchTxRows(txs);
        setMatchTxHasMore(hasMore);
        setMatchTxCursorId(hasMore ? (res.data.next_cursor_id ?? (txs.length ? txs[txs.length - 1].id : null)) : null);
        if (res.data.scan_capped && hasMore) {
          setMatchTxFooterNote(
            'Each batch scans up to a safety limit of transactions. Keep scrolling to load older matches.'
          );
        }
      } catch (err: any) {
        setMatchTxError(err?.response?.data?.error || err?.message || 'Could not load matches');
      } finally {
        setMatchTxLoading(false);
      }
    },
    [defaultMatchWindowAround, fetchMatchesPage]
  );

  const openMatchingTransactionsModal = useCallback(async (rule: ClassificationRule) => {
    if (!classificationRuleCanListMatches(rule)) return;
    await openMatchingTransactionsForSource({ kind: 'rule', rule });
  }, [openMatchingTransactionsForSource]);

  const openProposalMatchingTransactionsModal = useCallback(
    async (row: RuleProposalRow) => {
      if (!proposalRowCanListMatches(row)) return;
      const proposalIdRaw = row.id;
      const proposalId =
        proposalIdRaw != null && Number.isFinite(Number(proposalIdRaw)) && Number(proposalIdRaw) > 0
          ? Number(proposalIdRaw)
          : null;
      const atlasKey = row.virtual && row.atlas_key ? String(row.atlas_key).trim() : null;
      if (!proposalId && !atlasKey) return;
      const label = String(row.proposed_name || '').trim() || 'Proposal';
      await openMatchingTransactionsForSource({
        kind: 'proposal',
        label,
        proposalId,
        atlasKey
      });
    },
    [openMatchingTransactionsForSource]
  );

  const openSupplierRuleMatchingTransactionsModal = useCallback(
    async (row: SupplierRuleRow) => {
      if (!supplierRuleCanListMatches(row)) return;
      await openMatchingTransactionsForSource({ kind: 'supplierRule', row });
    },
    [openMatchingTransactionsForSource]
  );

  const openSupplierProposalMatchingTransactionsModal = useCallback(
    async (row: SupplierRuleProposalRow) => {
      const pid = supplierRuleProposalDbId(row);
      if (pid == null) return;
      const stub = supplierRuleRowStubFromProposal(row);
      if (!stub || !supplierRuleCanListMatches(stub)) return;
      const label =
        String((row.payload?.rule_name as string) || row.proposed_name || '').trim() || 'Supplier proposal';
      await openMatchingTransactionsForSource({ kind: 'supplierProposal', proposalId: pid, label });
    },
    [openMatchingTransactionsForSource]
  );

  const openVatRuleMatchingTransactionsModal = useCallback(
    async (row: CategoryVatRuleRow) => {
      if (!vatRuleCanListMatches(row)) return;
      await openMatchingTransactionsForSource({ kind: 'vatRule', row });
    },
    [openMatchingTransactionsForSource]
  );

  const openSpecialCaseRuleMatchingTransactionsModal = useCallback(
    async (row: SpecialCaseRuleRow) => {
      if (!specialCaseRuleCanListMatches(row)) return;
      await openMatchingTransactionsForSource({ kind: 'specialCaseRule', row });
    },
    [openMatchingTransactionsForSource]
  );

  /** Post-accept Yes: apply-single for only that rule — never reapply-unconfirmed / full ruleset. */
  const runBackfillAcceptedRule = useCallback(async () => {
    const target =
      backfillAfterAcceptTarget ??
      backfillTargetFromConfirmedPrefixedId(backfillAfterAcceptPrefixedId);
    const ruleNameSnapshot = (backfillAfterAcceptRuleName || '').trim();
    const prefixedSnapshot = (backfillAfterAcceptPrefixedId || '').trim();
    const label = prefixedSnapshot || ruleNameSnapshot || 'accepted rule';
    closeBackfillAfterAccept();
    if (!target) {
      setError(
        `Could not backfill ${label}: missing confirmed rule id. Use Play on that rule row instead.`
      );
      finishSimilarTxRulesReturnIfPending();
      return;
    }
    // Cancel any in-flight "Re-run ALL Classification Rules" so its toast cannot be mistaken
    // for this single-rule backfill (and so it does not keep writing unrelated tags).
    requestStopOperation();
    setBackfillAfterAcceptBusy(true);
    setError('');
    const toastId = stackedToastIdRef.current++;
    const settleToast = (
      message: string,
      severity: 'info' | 'success' | 'error' | 'warning'
    ) =>
      setStackedToasts((prev) =>
        prev.map((t) => {
          if (t.id !== toastId) return t;
          const { actionLabel: _a, onAction: _o, ...rest } = t;
          return { ...rest, message, severity };
        })
      );
    setStackedToasts((prev) => [
      ...prev,
      {
        id: toastId,
        code: nextClientToastCode(),
        severity: 'info',
        message: `Backfilling ${label} only (this rule’s matches)…`
      }
    ]);
    try {
      let summaryLine = '';
      let summary: Record<string, unknown> = {};
      let ruleTitle = '';
      if (target.kind === 'classification') {
        // Explicit apply-single path — do not call /reapply-unconfirmed.
        const res = await api.post(`/api/transaction-classification-rules/${target.id}/apply-single`, {
          // Empty body = all ledger rows that match THIS rule only.
        });
        summary = (res?.data?.summary || {}) as Record<string, unknown>;
        const matched = Number(summary.transactions_matched || 0);
        summaryLine =
          `${Number(summary.tags_created || 0).toLocaleString()} tag(s) created on ` +
          `${matched.toLocaleString()} matching transaction(s)` +
          ` (${Number(summary.already_tagged || 0).toLocaleString()} already tagged).`;
        ruleTitle = `Backfilled ${formatClassificationRulePrefixedId(target.id, ruleNameSnapshot)}`;
      } else if (target.kind === 'supplier') {
        const res = await api.post(`/api/suppliers/description-aliases/${target.id}/apply-single`, {});
        summary = (res?.data?.summary || {}) as Record<string, unknown>;
        summaryLine =
          `${Number(summary.tags_updated || 0).toLocaleString()} tag(s) updated across ` +
          `${Number(summary.transactions_matched || 0).toLocaleString()} transaction(s).`;
        ruleTitle = `Backfilled ${formatPrefixedRuleId('supplier', target.id)}`;
      } else if (target.kind === 'vat') {
        const res = await api.post(
          `/api/transaction-classification-rules/vat-rules/${target.id}/apply-single`,
          {}
        );
        summary = (res?.data?.summary || {}) as Record<string, unknown>;
        const txMatched = Number(summary.transactions_matched || 0);
        summaryLine =
          `${Number(summary.tags_updated || 0).toLocaleString()} tag VAT value(s) updated` +
          (txMatched > 0 ? ` · ${txMatched.toLocaleString()} transaction(s) in scope` : '');
        ruleTitle = `Backfilled ${formatPrefixedRuleId('vat', target.id)}`;
      } else {
        const res = await api.post(
          `/api/transaction-classification-rules/special-case-rules/${target.id}/apply-single`,
          {}
        );
        summary = (res?.data?.summary || {}) as Record<string, unknown>;
        const txMatched = Number(summary.transactions_matched || 0);
        summaryLine =
          `${Number(summary.tags_updated || 0).toLocaleString()} tag(s) updated` +
          (txMatched > 0 ? ` · ${txMatched.toLocaleString()} transaction(s) matched` : '');
        ruleTitle = `Backfilled ${formatPrefixedRuleId('specialCase', target.id)}`;
      }
      settleToast(`${ruleTitle}: ${summaryLine}`, 'success');
      presentRulesApplyRunInQuickview({ ruleTitle, summaryLine, summary });
      setSuccessMessage(`${ruleTitle}: ${summaryLine}`);
      await fetchData();
    } catch (err: any) {
      const msg = err?.response?.data?.error || err?.message || `Could not backfill ${label}`;
      settleToast(String(msg), 'error');
      setError(msg);
    } finally {
      setBackfillAfterAcceptBusy(false);
      finishSimilarTxRulesReturnIfPending();
    }
  }, [
    backfillAfterAcceptPrefixedId,
    backfillAfterAcceptRuleName,
    backfillAfterAcceptTarget,
    closeBackfillAfterAccept,
    fetchData,
    finishSimilarTxRulesReturnIfPending,
    presentRulesApplyRunInQuickview,
    requestStopOperation
  ]);

  const applySingleClassificationRuleToAllTransactions = useCallback(
    async (rule: ClassificationRule) => {
      const busyKey = `classification-${rule.id}`;
      setApplySingleRuleBusyKey(busyKey);
      try {
        const res = await api.post(`/api/transaction-classification-rules/${rule.id}/apply-single`, {});
        const summary = res?.data?.summary || {};
        const summaryLine =
          `${Number(summary.tags_created || 0).toLocaleString()} tag(s) created, ` +
          `${Number(summary.already_tagged || 0).toLocaleString()} already tagged.`;
        presentRulesApplyRunInQuickview({
          ruleTitle: `Applied ${formatClassificationRulePrefixedId(rule.id, rule.name)}`,
          summaryLine,
          summary
        });
        setSuccessMessage(`Applied ${formatClassificationRulePrefixedId(rule.id, rule.name)}: ${summaryLine}`);
      } catch (err: any) {
        setError(err?.response?.data?.error || err?.message || 'Could not apply classification rule');
      } finally {
        setApplySingleRuleBusyKey('');
      }
    },
    [presentRulesApplyRunInQuickview]
  );

  const applySingleSupplierRuleToAllTransactions = useCallback(
    async (row: SupplierRuleRow) => {
      const busyKey = `supplier-${row.id}`;
      setApplySingleRuleBusyKey(busyKey);
      try {
        const res = await api.post(`/api/suppliers/description-aliases/${row.id}/apply-single`, {});
        const summary = res?.data?.summary || {};
        const summaryLine =
          `${Number(summary.tags_updated || 0).toLocaleString()} tag(s) updated across ` +
          `${Number(summary.transactions_matched || 0).toLocaleString()} transaction(s).`;
        presentRulesApplyRunInQuickview({
          ruleTitle: `Applied ${formatPrefixedRuleId('supplier', row.id)}`,
          summaryLine,
          summary
        });
        setSuccessMessage(`Applied ${formatPrefixedRuleId('supplier', row.id)}: ${summaryLine}`);
      } catch (err: any) {
        setError(err?.response?.data?.error || err?.message || 'Could not apply supplier rule');
      } finally {
        setApplySingleRuleBusyKey('');
      }
    },
    [presentRulesApplyRunInQuickview]
  );

  const applySingleVatRuleToAllTransactions = useCallback(
    async (row: CategoryVatRuleRow) => {
      const busyKey = `vat-${row.id}`;
      setApplySingleRuleBusyKey(busyKey);
      try {
        const res = await api.post(`/api/transaction-classification-rules/vat-rules/${row.id}/apply-single`, {});
        const summary = res?.data?.summary || {};
        const txMatched = Number(summary.transactions_matched || 0);
        const summaryLine =
          `${Number(summary.tags_updated || 0).toLocaleString()} tag VAT value(s) updated` +
          (txMatched > 0 ? ` · ${txMatched.toLocaleString()} transaction(s) in scope` : '');
        presentRulesApplyRunInQuickview({
          ruleTitle: `Applied ${formatPrefixedRuleId('vat', row.id)}`,
          summaryLine,
          summary
        });
        setSuccessMessage(`Applied ${formatPrefixedRuleId('vat', row.id)}: ${summaryLine}`);
      } catch (err: any) {
        setError(err?.response?.data?.error || err?.message || 'Could not apply VAT rule');
      } finally {
        setApplySingleRuleBusyKey('');
      }
    },
    [presentRulesApplyRunInQuickview]
  );

  const applySingleSpecialCaseRuleToAllTransactions = useCallback(
    async (row: SpecialCaseRuleRow) => {
      const busyKey = `special-${row.id}`;
      setApplySingleRuleBusyKey(busyKey);
      try {
        const res = await api.post(
          `/api/transaction-classification-rules/special-case-rules/${row.id}/apply-single`,
          {}
        );
        const summary = res?.data?.summary || {};
        const txMatched = Number(summary.transactions_matched || 0);
        const tagsUpdated = Number(summary.tags_updated || 0);
        const summaryLine =
          `${tagsUpdated.toLocaleString()} tag VAT value(s) updated` +
          (txMatched > 0 ? ` · ${txMatched.toLocaleString()} transaction(s) matched` : '');
        presentRulesApplyRunInQuickview({
          ruleTitle: `Applied ${formatPrefixedRuleId('specialCase', row.id)}`,
          summaryLine,
          summary
        });
        setSuccessMessage(`Applied ${formatPrefixedRuleId('specialCase', row.id)}: ${summaryLine}`);
        await refreshRulesTabDatasets();
      } catch (err: any) {
        setError(err?.response?.data?.error || err?.message || 'Could not apply special case rule');
      } finally {
        setApplySingleRuleBusyKey('');
      }
    },
    [presentRulesApplyRunInQuickview, refreshRulesTabDatasets]
  );

  useEffect(() => {
    if (!matchTxSource) return;
    const root = matchTxScrollRef.current;
    const sent = matchTxSentinelRef.current;
    if (!root || !sent) return;
    const obs = new IntersectionObserver(
      (entries) => {
        if (entries[0]?.isIntersecting) {
          void loadMoreRuleMatchesRef.current();
        }
      },
      { root, rootMargin: '100px', threshold: 0.01 }
    );
    obs.observe(sent);
    return () => obs.disconnect();
  }, [matchTxSource, matchTxLoading, matchTxRows.length, matchTxHasMore]);

  const renderTable = (rows: ReviewTx[], type: ReviewTableType) => {
    const showCheckbox = type === 'untagged' || type === 'unclassified' || type === 'conflicting_vat';
    const showResolution = type === 'untagged' || type === 'unclassified' || type === 'conflicting_vat';
    const showFrequency = type !== 'conflicting_vat';
    const showHeading = true;
    const showRuleProposal = type === 'untagged' || type === 'unclassified';
    const showVatConflictInspectorColumns = type === 'conflicting_vat';
    const colSpan =
      (showCheckbox ? 1 : 0) +
      7 +
      (showVatConflictInspectorColumns ? 5 : 0) +
      (showFrequency ? 1 : 0) +
      (showResolution ? (showHeading ? 2 : 1) : 0) +
      (showRuleProposal ? 1 : 0);
    const sortCfg = reviewSort[type];
    const sortedRows = sortReviewRows(
      rows,
      sortCfg,
      categories,
      selectedHeadingByTx,
      selectedGranularByTx,
      headingCategories
    );
    const sortDir = (key: string): SortDir =>
      sortCfg?.key === key ? sortCfg.dir : 'asc';
    const setSortKey = (key: string) => {
      setReviewSort((prev) => ({
        ...prev,
        [type]: nextSortState(prev[type], key)
      }));
    };
    return (
    <TableContainer>
      <Table size="small">
        <TableHead>
          <TableRow>
            {showCheckbox && <TableCell padding="checkbox">
              <input
                type="checkbox"
                checked={sortedRows.length > 0 && sortedRows.every((r) => selectedTxIds.includes(r.id))}
                onChange={(e) => {
                  if (e.target.checked) {
                    const ids = sortedRows.map((r) => r.id);
                    setSelectedTxIds(Array.from(new Set([...selectedTxIds, ...ids])));
                  } else {
                    const ids = new Set(sortedRows.map((r) => r.id));
                    setSelectedTxIds(selectedTxIds.filter((id) => !ids.has(id)));
                  }
                }}
              />
            </TableCell>}
            <TableCell sortDirection={sortCfg?.key === 'id' ? sortCfg.dir : false}>
              <TableSortLabel
                active={sortCfg?.key === 'id'}
                direction={sortDir('id')}
                onClick={() => setSortKey('id')}
              >
                ID
              </TableSortLabel>
            </TableCell>
            <TableCell sortDirection={sortCfg?.key === 'date' ? sortCfg.dir : false}>
              <TableSortLabel
                active={sortCfg?.key === 'date'}
                direction={sortDir('date')}
                onClick={() => setSortKey('date')}
              >
                Date
              </TableSortLabel>
            </TableCell>
            <TableCell sortDirection={sortCfg?.key === 'supplier' ? sortCfg.dir : false}>
              <TableSortLabel
                active={sortCfg?.key === 'supplier'}
                direction={sortDir('supplier')}
                onClick={() => setSortKey('supplier')}
              >
                Supplier
              </TableSortLabel>
            </TableCell>
            <TableCell sortDirection={sortCfg?.key === 'description' ? sortCfg.dir : false}>
              <TableSortLabel
                active={sortCfg?.key === 'description'}
                direction={sortDir('description')}
                onClick={() => setSortKey('description')}
              >
                Description
              </TableSortLabel>
            </TableCell>
            <TableCell align="right" sortDirection={sortCfg?.key === 'amount' ? sortCfg.dir : false}>
              <TableSortLabel
                active={sortCfg?.key === 'amount'}
                direction={sortDir('amount')}
                onClick={() => setSortKey('amount')}
              >
                Amount
              </TableSortLabel>
            </TableCell>
            <TableCell sortDirection={sortCfg?.key === 'account' ? sortCfg.dir : false}>
              <TableSortLabel
                active={sortCfg?.key === 'account'}
                direction={sortDir('account')}
                onClick={() => setSortKey('account')}
              >
                Account
              </TableSortLabel>
            </TableCell>
            <TableCell sx={showVatConflictInspectorColumns ? { minWidth: 360 } : undefined}>Reason</TableCell>
            {showVatConflictInspectorColumns && (
              <>
                <TableCell>Problem</TableCell>
                <TableCell>Category tags</TableCell>
                <TableCell>VAT tags</TableCell>
                <TableCell>Expense / Income tags</TableCell>
                <TableCell>Supplier tags</TableCell>
              </>
            )}
            {showFrequency && (
            <TableCell sortDirection={sortCfg?.key === 'frequency' ? sortCfg.dir : false}>
              <TableSortLabel
                active={sortCfg?.key === 'frequency'}
                direction={sortDir('frequency')}
                onClick={() => setSortKey('frequency')}
              >
                Frequency
              </TableSortLabel>
            </TableCell>
            )}
            {showResolution && (
              <>
                {showHeading && (
                  <TableCell
                    sortDirection={sortCfg?.key === 'heading' ? sortCfg.dir : false}
                    sx={{  whiteSpace: 'normal', textAlign: 'center', verticalAlign: 'bottom' }}
                  >
                  <TableSortLabel
                    active={sortCfg?.key === 'heading'}
                    direction={sortDir('heading')}
                    onClick={() => setSortKey('heading')}
                    sx={{ width: '100%', justifyContent: 'center' }}
                  >
                      <Box
                        component="span"
                        sx={{
                          display: 'inline-flex',
                          flexDirection: 'column',
                          alignItems: 'center',
                          lineHeight: 1.15
                        }}
                      >
                        <span>Cash</span>
                        <span>Flow</span>
                      </Box>
                  </TableSortLabel>
                </TableCell>
                )}
                <TableCell sortDirection={sortCfg?.key === 'granular' ? sortCfg.dir : false}>
                  <TableSortLabel
                    active={sortCfg?.key === 'granular'}
                    direction={sortDir('granular')}
                    onClick={() => setSortKey('granular')}
                  >
                    {type === 'conflicting_vat' ? 'VAT resolution' : 'Suggested Category'}
                  </TableSortLabel>
                </TableCell>
                {showRuleProposal && <TableCell align="right">Rule proposal</TableCell>}
              </>
            )}
          </TableRow>
        </TableHead>
        <TableBody>
          {sortedRows.length === 0 ? (
            <TableRow>
              <TableCell colSpan={colSpan}>No items.</TableCell>
            </TableRow>
          ) : (
            sortedRows.map((tx) => (
              <TableRow key={`${type}-${tx.id}`}>
                {showCheckbox && (
                  <TableCell padding="checkbox">
                    <input
                      type="checkbox"
                      checked={selectedTxIds.includes(tx.id)}
                      onChange={(e) => {
                        if (e.target.checked) {
                          setSelectedTxIds((prev) => [...prev, tx.id]);
                        } else {
                          setSelectedTxIds((prev) => prev.filter((id) => id !== tx.id));
                        }
                      }}
                    />
                  </TableCell>
                )}
                <TableCell sx={{ whiteSpace: 'nowrap', fontFamily: 'ui-monospace, Menlo, Monaco, Consolas, monospace' }}>
                  {tx.id}
                </TableCell>
                <TableCell>{formatDate(tx.completed_at || tx.date)}</TableCell>
                <TableCell>{tx.supplier_name || '—'}</TableCell>
                <TableCell>{tx.description || tx.reference || '—'}</TableCell>
                <TableCell align="right">
                  <Typography variant="body2" sx={{ color: txReviewAmountColor(effectiveTxAmount(tx)) }}>
                    {formatAmount(effectiveTxAmount(tx), tx.currency)}
                  </Typography>
                </TableCell>
                <TableCell>{formatRevolutAccountLabel(tx.account_source)}</TableCell>
                <TableCell sx={showVatConflictInspectorColumns ? { minWidth: 360 } : undefined}>
                  <Chip
                    label={
                      type === 'untagged'
                        ? 'Needs categorization'
                        : type === 'unclassified'
                          ? 'Unclassified'
                          : 'VAT conflict'
                    }
                    size="small"
                    color={type === 'conflicting_vat' ? 'error' : 'warning'}
                  />
                  {type === 'conflicting_vat' && tx.vat_conflict_detail && (
                    <>
                      {(tx.vat_conflict_detail.entries || []).length > 0 ? (
                        <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap sx={{ mt: 0.75 }}>
                          {(tx.vat_conflict_detail.entries || []).map((entry, idx) => (
                            <Chip
                              key={`${tx.id}-reason-tag-${entry.tag_uid || idx}`}
                              size="small"
                              variant="outlined"
                              color={entry.source === 'manual' ? 'default' : 'warning'}
                              label={`${entry.source === 'manual' ? 'Manual' : 'Rule'}: ${entry.category || 'Uncategorized'} @ ${
                                (() => {
                                  const v = String(entry.vat_rate || '').trim();
                                  if (!v || v.toLowerCase() === 'none' || v.toLowerCase() === 'n/a') {
                                    return '—';
                                  }
                                  return v;
                                })()
                              }`}
                              onDelete={
                                entry.tag_uid
                                  ? () => void removeVatConflictTag(entry.tag_uid || '', tx, entry)
                                  : undefined
                              }
                              deleteIcon={<CloseIcon fontSize="small" />}
                              disabled={!!(entry.tag_uid && (deletingVatTagUid === entry.tag_uid || loading))}
                            />
                          ))}
                        </Stack>
                      ) : (
                        <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
                          {Array.isArray(tx.vat_conflict_detail.vat_rates) && tx.vat_conflict_detail.vat_rates.length > 0
                            ? `VAT rates: ${tx.vat_conflict_detail.vat_rates.join(' vs ')}`
                            : 'VAT rates differ across tags'}
                          {Array.isArray(tx.vat_conflict_detail.categories) && tx.vat_conflict_detail.categories.length > 0
                            ? ` | Categories: ${tx.vat_conflict_detail.categories.join(', ')}`
                            : ''}
                        </Typography>
                      )}
                    </>
                  )}
                </TableCell>
                {showVatConflictInspectorColumns && (
                  <>
                    <TableCell>
                      <Typography variant="caption" color="text.secondary" sx={{ lineHeight: 1.45, display: 'block' }}>
                        {vatConflictProblemDescription(tx)}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      {(() => {
                        const inspector = vatConflictInspectorSummary(tx);
                        const entries = Array.isArray(tx.vat_conflict_detail?.entries) ? tx.vat_conflict_detail!.entries! : [];
                        const granularCats = inspector.categories.filter((name) => !isTopLevelCashflowHeadingName(name));
                        if (!granularCats.length) return <Typography variant="caption" color="text.secondary">—</Typography>;
                        return (
                          <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
                            {granularCats.map((cat) => {
                              const target = entries.find((e) => String(e.category || '').trim() === cat && e.tag_uid);
                              return (
                                <Chip
                                  key={`${tx.id}-cat-tag-${cat}`}
                                  size="small"
                                  variant="outlined"
                                  label={cat}
                                  onDelete={
                                    target?.tag_uid
                                      ? () => void removeVatConflictTag(String(target.tag_uid || ''), tx, target)
                                      : undefined
                                  }
                                  deleteIcon={<CloseIcon fontSize="small" />}
                                  disabled={!!(target?.tag_uid && (deletingVatTagUid === target.tag_uid || loading))}
                                />
                              );
                            })}
                          </Stack>
                        );
                      })()}
                    </TableCell>
                    <TableCell>
                      {(() => {
                        const inspector = vatConflictInspectorSummary(tx);
                        const entries = Array.isArray(tx.vat_conflict_detail?.entries) ? tx.vat_conflict_detail!.entries! : [];
                        if (!inspector.vatRates.length) return <Typography variant="caption" color="text.secondary">—</Typography>;
                        return (
                          <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
                            {inspector.vatRates.map((rate) => {
                              const target = entries.find(
                                (e) => (String(e.vat_rate || '').trim() || 'none').toLowerCase() === rate && e.tag_uid
                              );
                              return (
                                <Chip
                                  key={`${tx.id}-vat-tag-${rate}`}
                                  size="small"
                                  variant="outlined"
                                  label={rate}
                                  onDelete={
                                    target?.tag_uid
                                      ? () => void removeVatConflictTag(String(target.tag_uid || ''), tx, target)
                                      : undefined
                                  }
                                  deleteIcon={<CloseIcon fontSize="small" />}
                                  disabled={!!(target?.tag_uid && (deletingVatTagUid === target.tag_uid || loading))}
                                />
                              );
                            })}
                          </Stack>
                        );
                      })()}
                    </TableCell>
                    <TableCell>
                      {(() => {
                        const inspector = vatConflictInspectorSummary(tx);
                        const entries = Array.isArray(tx.vat_conflict_detail?.entries) ? tx.vat_conflict_detail!.entries! : [];
                        if (!inspector.headingCategories.length) {
                          return <Typography variant="caption" color="text.secondary">—</Typography>;
                        }
                        return (
                          <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
                            {inspector.headingCategories.map((heading) => {
                              const target = entries.find((e) => String(e.category || '').trim() === heading && e.tag_uid);
                              return (
                                <Chip
                                  key={`${tx.id}-heading-tag-${heading}`}
                                  size="small"
                                  variant="outlined"
                                  label={heading}
                                  onDelete={
                                    target?.tag_uid
                                      ? () => void removeVatConflictTag(String(target.tag_uid || ''), tx, target)
                                      : undefined
                                  }
                                  deleteIcon={<CloseIcon fontSize="small" />}
                                  disabled={!!(target?.tag_uid && (deletingVatTagUid === target.tag_uid || loading))}
                                />
                              );
                            })}
                          </Stack>
                        );
                      })()}
                    </TableCell>
                    <TableCell>
                      {(() => {
                        const inspector = vatConflictInspectorSummary(tx);
                        const entries = Array.isArray(tx.vat_conflict_detail?.entries) ? tx.vat_conflict_detail!.entries! : [];
                        if (!inspector.suppliers.length) return <Typography variant="caption" color="text.secondary">—</Typography>;
                        return (
                          <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
                            {inspector.suppliers.map((supplier) => {
                              const target = entries.find(
                                (e) => String(e.supplier_name || '').trim() === supplier && e.tag_uid
                              );
                              return (
                                <Chip
                                  key={`${tx.id}-supplier-tag-${supplier}`}
                                  size="small"
                                  variant="outlined"
                                  label={supplier}
                                  onDelete={
                                    target?.tag_uid
                                      ? () => void removeVatConflictTag(String(target.tag_uid || ''), tx, target)
                                      : undefined
                                  }
                                  deleteIcon={<CloseIcon fontSize="small" />}
                                  disabled={!!(target?.tag_uid && (deletingVatTagUid === target.tag_uid || loading))}
                                />
                              );
                            })}
                          </Stack>
                        );
                      })()}
                    </TableCell>
                  </>
                )}
                {showFrequency && (
                <TableCell>
                  <Select
                    size="small"
                    displayEmpty
                    fullWidth
                    value={(() => {
                      const g0 = primaryGranularCategoryId(selectedGranularByTx, tx.id);
                      const gName = selectHasChoice(g0)
                        ? categories.find((c) => c.id === g0)?.name
                        : undefined;
                      return (
                        tx.payment_frequency ||
                        inferPaymentFrequencyFromCategoryName(gName) ||
                        ''
                      );
                    })()}
                    disabled={
                      savingFrequencyTxId === tx.id || loading || applyingRules || !!busyAction
                    }
                    onChange={(e) => {
                      const raw = e.target.value;
                      void patchPaymentFrequency(tx.id, raw === '' ? null : raw);
                    }}
                  >
                    <MenuItem value="">
                      <em>Unset</em>
                    </MenuItem>
                    {PAYMENT_FREQUENCY_OPTIONS.map((opt) => (
                      <MenuItem key={opt.value} value={opt.value}>
                        {opt.label}
                      </MenuItem>
                    ))}
                  </Select>
                </TableCell>
                )}
                {showResolution && (() => {
                  const granularVal = primaryGranularCategoryId(selectedGranularByTx, tx.id);
                  const headingVal = headingSelectValueForTx(
                    tx.id,
                    selectedHeadingByTx,
                    selectedGranularByTx,
                    headingCategories,
                    effectiveTxAmount(tx),
                    'resolution'
                  );
                  const picked = resolvePickedCategoryId(granularVal, headingVal);
                  const conflictInspector = type === 'conflicting_vat' ? vatConflictInspectorSummary(tx) : null;
                  const autoFixCategoryName =
                    conflictInspector?.categories.find((name) =>
                      granularCategories.some((c) => (c.name || '').trim().toLowerCase() === name.trim().toLowerCase())
                    ) || '';
                  const autoFixCategoryId =
                    (granularCategories.find(
                      (c) => (c.name || '').trim().toLowerCase() === autoFixCategoryName.trim().toLowerCase()
                    )?.id ?? null);
                  const autoFixVatRate =
                    conflictInspector?.vatRates.find((r) => r && r.toLowerCase() !== 'none') ||
                    conflictInspector?.vatRates[0] ||
                    '';
                  const supplierDraftValue =
                    tx.id in selectedSupplierByTx ? selectedSupplierByTx[tx.id] : tx.supplier_name || '';
                  return (
                    <>
                      {showHeading && (
                      <TableCell>
                        <FormControl size="small" fullWidth>
                          <Select
                            value={headingVal === '' ? '' : headingVal}
                            displayEmpty
                            onChange={(e) => {
                              const raw = e.target.value;
                              setSelectedHeadingByTx((prev) => ({
                                ...prev,
                                [tx.id]: !selectHasChoice(raw) ? '' : Number(raw)
                              }));
                            }}
                          >
                            <MenuItem value="">
                              <em>Select</em>
                            </MenuItem>
                            {headingCategories.map((c) => (
                              <MenuItem key={`h-${tx.id}-${c.id}`} value={c.id}>
                                {c.name}
                              </MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                      </TableCell>
                      )}
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <FormControl size="small" sx={{ minWidth: 220 }}>
                            <Select
                              value={granularVal === '' ? '' : granularVal}
                              displayEmpty
                              onChange={(e) => {
                                const raw = e.target.value;
                                if (String(raw) === ADD_NEW_CATEGORY_SELECT_VALUE) {
                                  openCategoryCrudCreateForManualRow(tx.id);
                                  return;
                                }
                                setSelectedGranularByTx((prev) => ({
                                  ...prev,
                                  [tx.id]: !selectHasChoice(raw) ? [] : [Number(raw)]
                                }));
                                if (type === 'conflicting_vat') {
                                  setSelectedVatRateByTx((prev) => ({
                                    ...prev,
                                    [tx.id]: ''
                                  }));
                                }
                              }}
                            >
                              <MenuItem value="">
                                <em>Select category</em>
                              </MenuItem>
                              {granularCategories.map((c) => (
                                <MenuItem key={c.id} value={c.id}>
                                  {c.name}
                                </MenuItem>
                              ))}
                              <Divider sx={{ my: 0.5 }} />
                              <MenuItem value={ADD_NEW_CATEGORY_SELECT_VALUE}>Add new category…</MenuItem>
                            </Select>
                          </FormControl>
                          {type === 'conflicting_vat' ? (
                            <FormControl size="small" sx={{ minWidth: 150 }}>
                              <Select
                                value={tx.id in selectedVatRateByTx ? selectedVatRateByTx[tx.id] : ''}
                                displayEmpty
                                onChange={(e) =>
                                  setSelectedVatRateByTx((prev) => ({
                                    ...prev,
                                    [tx.id]: String(e.target.value || '')
                                  }))
                                }
                              >
                                <MenuItem value="">
                                  <em>Select VAT rate</em>
                                </MenuItem>
                                {Array.from(
                                  new Set([
                                    ...VAT_RATE_PICK_OPTIONS,
                                    ...((tx.vat_conflict_detail?.vat_rates || [])
                                      .map((v) => (v || '').trim())
                                      .filter(Boolean))
                                  ])
                                ).map((rate) => (
                                  <MenuItem key={`vat-rate-pick-${tx.id}-${rate}`} value={rate}>
                                    {rate}
                                  </MenuItem>
                                ))}
                              </Select>
                            </FormControl>
                          ) : null}
                          {type === 'conflicting_vat' ? (
                            <TextField
                              size="small"
                              label="Supplier tag"
                              value={supplierDraftValue}
                              onChange={(e) =>
                                setSelectedSupplierByTx((prev) => ({
                                  ...prev,
                                  [tx.id]: e.target.value
                                }))
                              }
                              sx={{ minWidth: 200 }}
                            />
                          ) : null}
                          <Button
                            size="small"
                            variant="contained"
                            disabled={picked == null || savingTxId === tx.id}
                            onClick={async () => {
                              if (picked == null) return;
                              try {
                                setSavingTxId(tx.id);
                                if (type === 'conflicting_vat') {
                                  const chosenVatRate =
                                    tx.id in selectedVatRateByTx ? selectedVatRateByTx[tx.id] : '';
                                  await resolveVatConflictIds(
                                    [tx.id],
                                    picked,
                                    chosenVatRate || null
                                  );
                                  if (shouldProposeVatOverrideRule(picked, chosenVatRate)) {
                                    const created = await createVatOverrideRuleProposals(
                                      [tx.id],
                                      picked,
                                      (chosenVatRate || '').trim()
                                    );
                                    if (created > 0) {
                                      jumpToRuleProposalsInRulesTab();
                                      setSuccessMessage(
                                        `Applied VAT for 1 row and created ${created} proposal(s) in Rule proposals.`
                                      );
                                    }
                                  }
                                } else {
                                if (type === 'unclassified' && tx.unclassified_tag_uid) {
                                  await api.delete(
                                    `/api/revolut-transaction-category-tags/${encodeURIComponent(tx.unclassified_tag_uid)}`
                                  );
                                }
                                await postManualRevolutCategoryTagIgnoreDuplicate({
                                  revolut_transaction_id: tx.id,
                                  category_id: picked,
                                  source: 'manual',
                                  supplier_name: tx.supplier_name || undefined
                                });
                                const pickedName = categories.find((c) => c.id === picked)?.name;
                                const autoFreq = inferPaymentFrequencyFromCategoryName(pickedName);
                                if (autoFreq && !tx.payment_frequency) {
                                  try {
                                    await api.patch(
                                      `/api/revolut/transactions/complete/${tx.id}/payment-frequency`,
                                      { payment_frequency: autoFreq }
                                    );
                                  } catch {
                                    /* non-fatal */
                                    }
                                  }
                                }
                                await fetchData();
                              } catch (err: any) {
                                setError(err?.response?.data?.error || 'Failed to save category');
                              } finally {
                                setSavingTxId(null);
                              }
                            }}
                          >
                            {savingTxId === tx.id ? 'Saving...' : type === 'conflicting_vat' ? 'Apply VAT' : 'Confirm'}
                          </Button>
                          {type === 'conflicting_vat' ? (
                            <Button
                              size="small"
                              variant="outlined"
                              disabled={picked == null || savingTxId === tx.id}
                              onClick={async () => {
                                if (picked == null) return;
                                try {
                                  setSavingTxId(tx.id);
                                  const chosenVatRate =
                                    tx.id in selectedVatRateByTx ? selectedVatRateByTx[tx.id] : '';
                                  const normalizedVat = canonicalizeVatRate(chosenVatRate);
                                  const supplierForTag = String(supplierDraftValue || '').trim();
                                  await postManualRevolutCategoryTagIgnoreDuplicate({
                                    revolut_transaction_id: tx.id,
                                    category_id: picked,
                                    source: 'manual',
                                    ...(supplierForTag ? { supplier_name: supplierForTag } : {}),
                                    ...(normalizedVat ? { vat_rate: normalizedVat } : {})
                                  });
                                  await fetchData();
                                  setSuccessMessage(
                                    `Added manual tag for TX ${tx.id}${normalizedVat ? ` @ ${normalizedVat}` : ''}.`
                                  );
                                } catch (err: any) {
                                  setError(err?.response?.data?.error || err?.message || 'Failed to add manual tag');
                                } finally {
                                  setSavingTxId(null);
                                }
                              }}
                            >
                              {savingTxId === tx.id ? 'Saving...' : 'Add tag'}
                            </Button>
                          ) : null}
                          {type === 'conflicting_vat' ? (
                            <Tooltip
                              title={
                                autoFixCategoryId
                                  ? 'Auto-pick a likely category + VAT from existing conflict tags and apply.'
                                  : 'No obvious granular category match from existing tags. Pick one manually.'
                              }
                            >
                              <span>
                                <Button
                                  size="small"
                                  variant="outlined"
                                  disabled={savingTxId === tx.id || !autoFixCategoryId}
                                  onClick={async () => {
                                    if (!autoFixCategoryId) return;
                                    try {
                                      setSavingTxId(tx.id);
                                      await resolveVatConflictIds(
                                        [tx.id],
                                        autoFixCategoryId,
                                        autoFixVatRate && autoFixVatRate.toLowerCase() !== 'none' ? autoFixVatRate : null
                                      );
                                      if (
                                        shouldProposeVatOverrideRule(
                                          autoFixCategoryId,
                                          autoFixVatRate && autoFixVatRate.toLowerCase() !== 'none' ? autoFixVatRate : null
                                        )
                                      ) {
                                        const created = await createVatOverrideRuleProposals(
                                          [tx.id],
                                          autoFixCategoryId,
                                          autoFixVatRate && autoFixVatRate.toLowerCase() !== 'none' ? autoFixVatRate : ''
                                        );
                                        if (created > 0) {
                                          jumpToRuleProposalsInRulesTab();
                                        }
                                      }
                                      setSuccessMessage(
                                        `Auto-resolved VAT conflict on TX ${tx.id} using ` +
                                          `${autoFixCategoryName || `category #${autoFixCategoryId}`}` +
                                          `${autoFixVatRate ? ` @ ${autoFixVatRate}` : ''}.`
                                      );
                                    } catch (err: any) {
                                      setError(err?.response?.data?.error || err?.message || 'Failed to auto-resolve VAT conflict');
                                    } finally {
                                      setSavingTxId(null);
                                    }
                                  }}
                                >
                                  Auto-fix
                                </Button>
                              </span>
                            </Tooltip>
                          ) : null}
                        </Box>
                      </TableCell>
                      {showRuleProposal && (
                      <TableCell align="right">
                        <Button
                          size="small"
                          variant="outlined"
                          disabled={loading || applyingRules || !!busyAction || proposeRuleSaving}
                          onClick={() => openProposeRuleForTx(tx)}
                        >
                          Propose rule…
                        </Button>
                      </TableCell>
                      )}
                    </>
                  );
                })()}
              </TableRow>
            ))
          )}
        </TableBody>
      </Table>
    </TableContainer>
  );
  };

  return (
    <Box>
      <Card>
        <CardContent
          ref={classificationPageRef}
          onMouseOverCapture={attachOverflowTooltipIfNeeded}
          sx={{
            '& .MuiTableHead-root .MuiTableCell-root': {
              textAlign: 'center',
              ...NEUTRAL_TABLE_HEAD_CELL_SX
            },
            '& .MuiTableHead-root .MuiTableCell-root .MuiTableSortLabel-root': {
              width: '100%',
              justifyContent: 'center'
            }
          }}
        >
          <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
            <Typography variant="h5">Transaction Classification Review</Typography>
            <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
              <Button
                variant="contained"
                color="info"
                onClick={() => void runAddressMissingTagsProcess()}
                disabled={
                  loading ||
                  applyingRules ||
                  supplierBackfillBusy ||
                  reapplyUnconfirmedBusy ||
                  reapplyVatUnconfirmedBusy ||
                  reapplySpecialCaseUnconfirmedBusy ||
                  rerunAllRulesBusy ||
                  dedupeDisplayDupTagsBusy ||
                  removeAllTagsBusy
                }
              >
                Fill missing tags
              </Button>
              {user?.role === 'owner' || user?.role === 'admin' ? (
                <Tooltip title="Remove all classification tags (including Non-Complete Revolut rows), then re-run the full rule pipeline. Previously committed rows become uncommitted.">
                  <span>
                    <Button
                      variant="contained"
                      color="error"
                      onClick={() => void openDeleteAndRetagDialog()}
                      disabled={
                        loading ||
                        applyingRules ||
                        supplierBackfillBusy ||
                        reapplyUnconfirmedBusy ||
                        reapplyVatUnconfirmedBusy ||
                        reapplySpecialCaseUnconfirmedBusy ||
                        rerunAllRulesBusy ||
                        dedupeDisplayDupTagsBusy ||
                        removeAllTagsBusy ||
                        deleteAndRetagPreviewBusy
                      }
                    >
                      {removeAllTagsBusy
                        ? 'Deleting tags…'
                        : deleteAndRetagInFlight
                          ? 'Re-tagging…'
                          : 'Delete and re-tag'}
                    </Button>
                  </span>
                </Tooltip>
              ) : null}
              <Button
                variant="outlined"
                onClick={() => void fetchData()}
                disabled={
                  loading ||
                  applyingRules ||
                  !!busyAction ||
                  supplierBackfillBusy ||
                  reapplyUnconfirmedBusy ||
                  reapplyVatUnconfirmedBusy ||
                  reapplySpecialCaseUnconfirmedBusy ||
                  rerunAllRulesBusy ||
                  dedupeDisplayDupTagsBusy ||
                  removeAllTagsBusy
                }
              >
                {busyAction || 'Refresh'}
              </Button>
              {operationProgressMessage && operationProgressBannerDismissed ? (
                <Chip
                  size="small"
                  color="primary"
                  variant="outlined"
                  label={
                    activeRuleSyncJobId
                      ? 'Background job running — click to expand status'
                      : 'Operation in progress — click to expand status'
                  }
                  onClick={() => setOperationProgressBannerDismissed(false)}
                  sx={{ cursor: 'pointer' }}
                />
              ) : null}
              {operationProgressMessage && operationProgressBannerDismissed && operationProgressCancelSupported ? (
                <Button
                  color="primary"
                  size="small"
                  variant="outlined"
                  onClick={requestStopOperation}
                  disabled={activeRuleSyncJobId ? activeRuleSyncCancelRequested : operationCancelRequested}
                >
                  {activeRuleSyncJobId
                    ? activeRuleSyncCancelRequested
                      ? 'Cancelling…'
                      : 'Cancel'
                    : operationCancelRequested
                      ? 'Stopping…'
                      : 'Stop'}
                </Button>
              ) : null}
            </Stack>
          </Stack>
          {operationProgressMessage && !operationProgressBannerDismissed ? (
            <Paper
              variant="outlined"
              sx={{
                mb: 2,
                p: 1.5,
                borderColor: 'primary.main',
                bgcolor: alpha(theme.palette.primary.main, 0.06)
              }}
            >
              <Stack
                direction={{ xs: 'column', sm: 'row' }}
                spacing={1.5}
                alignItems={{ sm: 'flex-start' }}
                justifyContent="space-between"
              >
                <Stack direction="row" spacing={1.5} alignItems="flex-start" sx={{ flex: 1, minWidth: 0 }}>
                  <CircularProgress size={24} sx={{ mt: 0.25 }} />
                  <Box sx={{ minWidth: 0 }}>
                    {(() => {
                      const { headline, detail } = operationProgressPertinentDisplay(operationProgressMessage);
                      const copyBody = [
                        headline,
                        detail,
                        activeRuleSyncJobId ? `Server job id: ${activeRuleSyncJobId}` : '',
                        operationProgressMessage && operationProgressMessage !== headline
                          ? `Raw: ${operationProgressMessage}`
                          : ''
                      ]
                        .filter(Boolean)
                        .join('\n');
                      return (
                        <>
                          <Typography variant="subtitle2" component="div">
                            {headline}
                          </Typography>
                          {detail ? (
                            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.25 }}>
                              {detail}
                            </Typography>
                          ) : null}
                          {activeRuleSyncJobId ? (
                            <Typography
                              variant="caption"
                              component="div"
                              sx={{
                                mt: 0.75,
                                opacity: 0.85,
                                wordBreak: 'break-all',
                                fontFamily: 'ui-monospace, monospace'
                              }}
                            >
                              Server job id: {activeRuleSyncJobId}
                            </Typography>
                          ) : null}
                          <ToastCodeCaption
                            jobId={activeRuleSyncJobId || undefined}
                            copyBody={copyBody}
                          />
                        </>
                      );
                    })()}
                  </Box>
                </Stack>
                <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ flexShrink: 0, alignSelf: { xs: 'stretch', sm: 'auto' } }}>
                  {operationProgressCancelSupported ? (
                    <Button
                      color="primary"
                      size="small"
                      variant="contained"
                      onClick={requestStopOperation}
                      disabled={activeRuleSyncJobId ? activeRuleSyncCancelRequested : operationCancelRequested}
                    >
                      {activeRuleSyncJobId
                        ? activeRuleSyncCancelRequested
                          ? 'Cancelling…'
                          : 'Cancel'
                        : operationCancelRequested
                          ? 'Stopping…'
                          : 'Stop'}
                    </Button>
                  ) : null}
                  <Button size="small" variant="outlined" onClick={() => setOperationProgressBannerDismissed(true)}>
                    Dismiss
                  </Button>
                </Stack>
              </Stack>
            </Paper>
          ) : null}
          <Tabs
            value={tabIndex}
            onChange={(_, v) => {
              const next = new URLSearchParams(searchParams);
              next.set('tab', CLASSIFICATION_VISIBLE_TAB_KEYS[v]);
              setSearchParams(next, { replace: true });
            }}
            sx={{ mb: 2 }}
            aria-label="Classification review sections"
          >
            <Tab id="classification-tab-needs-review" label="Tx Reviews" />
            <Tab id="classification-tab-rules" label="Rules" />
            <Tab id="classification-tab-probabilistic" label="Probabilistic" />
            <Tab id="classification-tab-suppliers" label="Suppliers" />
            <Tab id="classification-tab-supplier-audit" label="Supplier Audit" />
            <Tab id="classification-tab-categories" label="Categories" />
            <Tab id="classification-tab-supplier-vat" label="VAT Lookback period" />
            <Tab id="classification-tab-vat-conflicts" label="VAT Conflicts" />
            <Tab id="classification-tab-pipeline" label="Pipeline" />
            <Tab id="classification-tab-rule-run-history" label="Rule run history" />
            <Tab id="classification-tab-rerun-commentary" label="Re-run commentary" />
          </Tabs>

          {missingSupplierAudit != null && tabIndex !== 1 ? (
            <Alert
              severity="info"
              sx={{ mb: 2 }}
              action={
                <Button color="inherit" size="small" onClick={() => goToMissingSupplierAuditSection()}>
                  Go to audit
                </Button>
              }
            >
              Missing-supplier audit results are saved in this browser for this session (~6h).{' '}
              {missingSupplierAuditSavedAt != null
                ? `Time remaining: ${formatMissingSupplierAuditTtlRemaining(missingSupplierAuditSavedAt)}. `
                : null}
              Open Rules → Pipeline Step 2 to continue working through buckets.
            </Alert>
          ) : null}

          <>
              {tabIndex === 0 ? (
                <>
                  <Stack direction="row" spacing={1} sx={{ mb: 1.5, alignItems: 'center', minWidth: 0 }}>
                    <Stack
                      direction="row"
                      spacing={1}
                      sx={{ minWidth: 0, flex: 1, overflow: 'hidden', flexWrap: 'nowrap' }}
                      useFlexGap
                    >
                      {isUnifiedFilterInline('text') ? (
                        <ClearableSearchTextField
                          size="small"
                          label="Text search"
                          value={unifiedTextFilter}
                          onChange={setUnifiedTextFilter}
                          sx={{ minWidth: 240 }}
                        />
                      ) : null}
                      {isUnifiedFilterInline('status') ? (
                        <FormControl size="small" sx={{ minWidth: 240 }}>
                          <Select
                            multiple
                            open={unifiedStatusMenuOpen}
                            onOpen={() => setUnifiedStatusMenuOpen(true)}
                            onClose={() => setUnifiedStatusMenuOpen(false)}
                            value={unifiedStatusFilter}
                            displayEmpty
                            MenuProps={TX_REVIEW_FILTER_SELECT_MENU_PROPS}
                            renderValue={(selected) =>
                              (selected as string[]).length > 0
                                ? (selected as string[]).map((value) => unifiedStatusFilterOptionLabel(value)).join(', ')
                                : 'All statuses'
                            }
                            onChange={(e) => {
                              setUnifiedStatusFilter(e.target.value as UnifiedRowStatus[]);
                              setUnifiedStatusMenuOpen(false);
                            }}
                          >
                            {unifiedStatusOptions.map((statusValue) => (
                              <MenuItem key={`usf-${statusValue}`} value={statusValue}>
                                {`${unifiedStatusFilterOptionLabel(statusValue)} (${formatCount(unifiedFilterOptionCounts.statuses.get(statusValue) || 0)})`}
                              </MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                      ) : null}
                      {isUnifiedFilterInline('committed') ? (
                        <FormControl size="small" sx={{ minWidth: 170 }}>
                          <InputLabel id="unified-committed-filter-label">Committed</InputLabel>
                          <Select
                            labelId="unified-committed-filter-label"
                            label="Committed"
                            value={unifiedCommittedFilter}
                            MenuProps={TX_REVIEW_FILTER_SELECT_MENU_PROPS}
                            onChange={(e) =>
                              setUnifiedCommittedFilter(
                                String(e.target.value || 'all') as 'all' | 'committed' | 'uncommitted'
                              )
                            }
                          >
                            <MenuItem value="all">All ({formatCount(unifiedFilterOptionCounts.committed + unifiedFilterOptionCounts.uncommitted)})</MenuItem>
                            <MenuItem value="committed">
                              Committed ({formatCount(unifiedFilterOptionCounts.committed)})
                            </MenuItem>
                            <MenuItem value="uncommitted">
                              Uncommitted ({formatCount(unifiedFilterOptionCounts.uncommitted)})
                            </MenuItem>
                          </Select>
                        </FormControl>
                      ) : null}
                      {isUnifiedFilterInline('flagged') ? (
                        <FormControl size="small" sx={{ minWidth: 150 }}>
                          <InputLabel id="unified-flagged-filter-label">Flagged</InputLabel>
                          <Select
                            labelId="unified-flagged-filter-label"
                            label="Flagged"
                            value={unifiedFlaggedFilter}
                            MenuProps={TX_REVIEW_FILTER_SELECT_MENU_PROPS}
                            onChange={(e) =>
                              setUnifiedFlaggedFilter(
                                String(e.target.value || 'all') === 'flagged' ? 'flagged' : 'all'
                              )
                            }
                          >
                            <MenuItem value="all">All</MenuItem>
                            <MenuItem value="flagged">Flagged only</MenuItem>
                          </Select>
                        </FormControl>
                      ) : null}
                      {isUnifiedFilterInline('account') ? (
                        <FormControl size="small" sx={{ minWidth: 190 }}>
                          <InputLabel id="unified-account-filter-label" shrink>
                            Bank account
                          </InputLabel>
                          <Select
                            labelId="unified-account-filter-label"
                            label="Bank account"
                            multiple
                            open={unifiedAccountMenuOpen}
                            onOpen={() => setUnifiedAccountMenuOpen(true)}
                            onClose={() => setUnifiedAccountMenuOpen(false)}
                            value={unifiedAccountFilter}
                            displayEmpty
                            MenuProps={TX_REVIEW_FILTER_SELECT_MENU_PROPS}
                            renderValue={(selected) =>
                              (selected as string[]).length > 0
                                ? (selected as string[]).map((v) => formatRevolutAccountLabel(v)).join(', ')
                                : 'All bank accounts'
                            }
                            onChange={(e) => {
                              setUnifiedAccountFilter(e.target.value as string[]);
                              setUnifiedAccountMenuOpen(false);
                            }}
                          >
                            {unifiedFilterOptions.accounts.map((a) => (
                              <MenuItem key={`ua-${a}`} value={a}>
                                {`${formatRevolutAccountLabel(a)} (${formatCount(
                                  unifiedFilterOptionCounts.accounts.get(a)
                                )})`}
                              </MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                      ) : null}
                      {isUnifiedFilterInline('heading') ? (
                        <FormControl size="small" sx={{ minWidth: 170 }}>
                          <Select
                            multiple
                            open={unifiedHeadingMenuOpen}
                            onOpen={() => setUnifiedHeadingMenuOpen(true)}
                            onClose={() => setUnifiedHeadingMenuOpen(false)}
                            value={unifiedCashFlowFilter}
                            displayEmpty
                            MenuProps={TX_REVIEW_FILTER_SELECT_MENU_PROPS}
                            renderValue={(selected) =>
                              (selected as string[]).length > 0
                                ? (selected as string[]).map((h) => formatCashflowInOutHeadingLabel(h)).join(', ')
                                : 'All cash flow'
                            }
                            onChange={(e) => {
                              setUnifiedCashFlowFilter(
                                sanitizeHeadingFilterValues(
                                  Array.isArray(e.target.value) ? e.target.value : [e.target.value]
                                )
                              );
                              setUnifiedHeadingMenuOpen(false);
                            }}
                          >
                            <MenuItem value="In">
                              {`In (${formatCount(unifiedFilterOptionCounts.headings.In)})`}
                            </MenuItem>
                            <MenuItem value="Out">
                              {`Out (${formatCount(unifiedFilterOptionCounts.headings.Out)})`}
                            </MenuItem>
                            <MenuItem value="N/A">
                              {`N/A (${formatCount(unifiedFilterOptionCounts.headings['N/A'] || 0)})`}
                            </MenuItem>
                          </Select>
                        </FormControl>
                      ) : null}
                      {isUnifiedFilterInline('category') ? (
                        <FormControl size="small" sx={{ minWidth: 210 }}>
                          <Select
                            multiple
                            open={unifiedCategoryMenuOpen}
                            onOpen={() => setUnifiedCategoryMenuOpen(true)}
                            onClose={() => {
                              setUnifiedCategoryMenuOpen(false);
                              setUnifiedCategorySearchText('');
                            }}
                            value={unifiedCategoryFilter}
                            displayEmpty
                            MenuProps={{
                              ...TX_REVIEW_FILTER_SELECT_MENU_PROPS,
                              PaperProps: {
                                sx: {
                                  minWidth: 680,
                                  width: 'min(920px, calc(100vw - 24px))',
                                  maxWidth: 'min(920px, calc(100vw - 24px))'
                                }
                              }
                            }}
                            renderValue={(selected) =>
                              (selected as string[]).length > 0 ? (selected as string[]).join(', ') : 'All categories'
                            }
                            onChange={() => undefined}
                          >
                            <MenuItem
                              disableRipple
                              sx={{ cursor: 'default', '&:hover': { backgroundColor: 'transparent' } }}
                              onClick={(e) => e.stopPropagation()}
                              onKeyDown={(e) => e.stopPropagation()}
                            >
                              <ClearableSearchTextField
                                size="small"
                                hiddenLabel
                                placeholder="Search categories..."
                                value={unifiedCategorySearchText}
                                onChange={setUnifiedCategorySearchText}
                                onClick={(e) => e.stopPropagation()}
                                onKeyDown={(e) => e.stopPropagation()}
                                fullWidth
                                autoFocus
                                inputProps={{ 'aria-label': 'Search categories' }}
                              />
                            </MenuItem>
                            <MenuItem
                              disableRipple
                              sx={{
                                cursor: 'default',
                                '&:hover': { backgroundColor: 'transparent' },
                                alignItems: 'stretch',
                                maxWidth: 'none',
                                width: '100%',
                                py: 1
                              }}
                              onClick={(e) => e.stopPropagation()}
                              onKeyDown={(e) => e.stopPropagation()}
                            >
                              <Box
                                sx={{
                                  display: 'flex',
                                  flexDirection: 'row',
                                  alignItems: 'stretch',
                                  gap: 1,
                                  width: '100%',
                                  minWidth: 0
                                }}
                              >
                                <Box sx={{ flex: '1 1 0', minWidth: 0, overflow: 'hidden' }}>
                                  <Typography variant="caption" sx={{ fontWeight: 700, display: 'block', mb: 0.75 }}>
                                    Starred / Key
                                  </Typography>
                                  <Stack spacing={0.5}>
                                    {unifiedCategoryMenuBuckets.starred.length === 0 ? (
                                      <Typography variant="caption" color="text.secondary">
                                        No starred matches.
                                      </Typography>
                                    ) : (
                                      unifiedCategoryMenuBuckets.starred.map((c) => {
                                        const selected = unifiedCategoryFilter.includes(c);
                                        return (
                                          <Button
                                            key={`uc-star-${c}`}
                                            size="small"
                                            variant={selected ? 'contained' : 'text'}
                                            color={selected ? 'primary' : 'inherit'}
                                            startIcon={selected ? <StarIcon fontSize="small" /> : <StarBorderIcon fontSize="small" />}
                                            sx={{ justifyContent: 'flex-start', textTransform: 'none' }}
                                            onClick={(e) => {
                                              e.preventDefault();
                                              e.stopPropagation();
                                              toggleUnifiedCategoryFilterValue(c);
                                            }}
                                          >
                                            {`${c} (${formatCount(unifiedFilterOptionCounts.categories.get(c))})`}
                                          </Button>
                                        );
                                      })
                                    )}
                                  </Stack>
                                </Box>
                                <Divider orientation="vertical" flexItem sx={{ borderColor: 'divider', mx: 0 }} />
                                <Box sx={{ flex: '1 1 0', minWidth: 0, overflow: 'hidden' }}>
                                  <Typography variant="caption" sx={{ fontWeight: 700, display: 'block', mb: 0.75 }}>
                                    Other categories
                                  </Typography>
                                  <Stack spacing={0.5}>
                                    {unifiedCategoryMenuBuckets.regular.length === 0 ? (
                                      <Typography variant="caption" color="text.secondary">
                                        No other matches.
                                      </Typography>
                                    ) : (
                                      unifiedCategoryMenuBuckets.regular.map((c) => {
                                        const selected = unifiedCategoryFilter.includes(c);
                                        return (
                                          <Button
                                            key={`uc-other-${c}`}
                                            size="small"
                                            variant={selected ? 'contained' : 'text'}
                                            color={selected ? 'primary' : 'inherit'}
                                            sx={{ justifyContent: 'flex-start', textTransform: 'none' }}
                                            onClick={(e) => {
                                              e.preventDefault();
                                              e.stopPropagation();
                                              toggleUnifiedCategoryFilterValue(c);
                                            }}
                                          >
                                            {`${c} (${formatCount(unifiedFilterOptionCounts.categories.get(c))})`}
                                          </Button>
                                        );
                                      })
                                    )}
                                  </Stack>
                                </Box>
                              </Box>
                            </MenuItem>
                          </Select>
                        </FormControl>
                      ) : null}
                      {isUnifiedFilterInline('vat') ? (
                        <FormControl size="small" sx={{ minWidth: 200 }}>
                          <Select
                            multiple
                            open={unifiedVatMenuOpen}
                            onOpen={() => setUnifiedVatMenuOpen(true)}
                            onClose={() => setUnifiedVatMenuOpen(false)}
                            value={unifiedVatFilter}
                            displayEmpty
                            MenuProps={TX_REVIEW_FILTER_SELECT_MENU_PROPS}
                            renderValue={(selected) =>
                              (selected as string[]).length > 0
                                ? (selected as string[])
                                    .map((k) => formatVatFilterMenuLabel(k))
                                    .join(', ')
                                : 'All VAT rates'
                            }
                            onChange={(e) => {
                              setUnifiedVatFilter(e.target.value as string[]);
                              setUnifiedVatMenuOpen(false);
                            }}
                          >
                            {unifiedFilterOptions.vatRates.map((k) => (
                              <MenuItem key={`uvat-${k}`} value={k}>
                                {`${formatVatFilterMenuLabel(k)} (${formatCount(
                                  unifiedFilterOptionCounts.vatRates.get(k) || 0
                                )})`}
                              </MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                      ) : null}
                      {isUnifiedFilterInline('appliedTag') ? (
                        <FormControl size="small" sx={{ minWidth: 220, maxWidth: 320 }}>
                          <Select
                            multiple
                            open={unifiedAppliedTagMenuOpen}
                            onOpen={() => setUnifiedAppliedTagMenuOpen(true)}
                            onClose={() => setUnifiedAppliedTagMenuOpen(false)}
                            value={unifiedAppliedTagFilter}
                            displayEmpty
                            MenuProps={{
                              ...TX_REVIEW_FILTER_SELECT_MENU_PROPS,
                              PaperProps: { sx: { maxHeight: 420, maxWidth: 'min(720px, calc(100vw - 32px))' } }
                            }}
                            renderValue={(selected) =>
                              (selected as string[]).length > 0
                                ? (selected as string[]).join(', ')
                                : 'All applied tags'
                            }
                            onChange={(e) => {
                              setUnifiedAppliedTagFilter(e.target.value as string[]);
                              setUnifiedAppliedTagMenuOpen(false);
                            }}
                          >
                            {unifiedAppliedTagMenuLabels.length === 0 ? (
                              <MenuItem value="" disabled>
                                No applied-tag chips loaded yet (tags load per page)
                              </MenuItem>
                            ) : (
                              unifiedAppliedTagMenuLabels.map((lab) => (
                                <MenuItem key={`uat-${lab}`} value={lab} title={lab}>
                                  {`${lab} (${formatCount(unifiedFilterOptionCounts.appliedTags.get(lab) || 0)})`}
                                </MenuItem>
                              ))
                            )}
                          </Select>
                        </FormControl>
                      ) : null}
                      {isUnifiedFilterInline('tagDuplicate') ? (
                        <Stack direction="row" spacing={1} alignItems="center" useFlexGap flexWrap="wrap">
                          <FormControl size="small" sx={{ minWidth: 240 }}>
                            <InputLabel id="unified-tag-dup-filter-label">Tag duplicates</InputLabel>
                            <Select
                              labelId="unified-tag-dup-filter-label"
                              label="Tag duplicates"
                              value={unifiedTagDuplicateFilter}
                              MenuProps={TX_REVIEW_FILTER_SELECT_MENU_PROPS}
                              onChange={(e) =>
                                setUnifiedTagDuplicateFilter(
                                  String(e.target.value || 'all') as 'all' | 'no_duplicates' | 'duplicates'
                                )
                              }
                            >
                              <MenuItem value="all">
                                All (
                                {formatCount(
                                  unifiedFilterOptionCounts.tagDupNoDuplicate +
                                    unifiedFilterOptionCounts.tagDupDuplicate
                                )}
                                )
                              </MenuItem>
                              <MenuItem value="no_duplicates">
                                No duplicate tags ({formatCount(unifiedFilterOptionCounts.tagDupNoDuplicate)})
                              </MenuItem>
                              <MenuItem
                                value="duplicates"
                                title="Rows with more than one Cash flow, Supplier, Category, or VAT tag."
                              >
                                &gt;1 tag of same type ({formatCount(unifiedFilterOptionCounts.tagDupDuplicate)})
                              </MenuItem>
                            </Select>
                          </FormControl>
                          {unifiedTagDuplicateFilter === 'duplicates' ? (
                            <Tooltip title="Remove duplicate applied tags on uncommitted transactions (same buckets as this filter).">
                              <span>
                                <Button
                                  variant="outlined"
                                  color="warning"
                                  size="small"
                                  onClick={() => setDedupeDisplayDupTagsDialogOpen(true)}
                                  disabled={
                                    loading ||
                                    applyingRules ||
                                    supplierBackfillBusy ||
                                    reapplyUnconfirmedBusy ||
                                    reapplyVatUnconfirmedBusy ||
                                    reapplySpecialCaseUnconfirmedBusy ||
                                    rerunAllRulesBusy ||
                                    dedupeDisplayDupTagsBusy ||
                                    removeAllTagsBusy
                                  }
                                >
                                  {dedupeDisplayDupTagsBusy ? 'De-duping…' : 'De-dup'}
                                </Button>
                              </span>
                            </Tooltip>
                          ) : null}
                        </Stack>
                      ) : null}
                      {isUnifiedFilterInline('dateFrom') ? (
                        <TextField
                          size="small"
                          type="date"
                          label="Date from"
                          InputLabelProps={{
                            shrink: true,
                            sx: { bgcolor: 'background.paper', px: 0.5 }
                          }}
                          value={unifiedDateFrom}
                          onChange={(e) => setUnifiedDateFrom(e.target.value)}
                          sx={{ minWidth: 160 }}
                        />
                      ) : null}
                      {isUnifiedFilterInline('dateTo') ? (
                        <TextField
                          size="small"
                          type="date"
                          label="Date to"
                          InputLabelProps={{
                            shrink: true,
                            sx: { bgcolor: 'background.paper', px: 0.5 }
                          }}
                          value={unifiedDateTo}
                          onChange={(e) => setUnifiedDateTo(e.target.value)}
                          sx={{ minWidth: 160 }}
                        />
                      ) : null}
                      {isUnifiedFilterInline('supplier') ? (
                        <FormControl size="small" sx={{ minWidth: 220 }}>
                          <Select
                            multiple
                            open={unifiedSupplierMenuOpen}
                            onOpen={() => setUnifiedSupplierMenuOpen(true)}
                            onClose={() => setUnifiedSupplierMenuOpen(false)}
                            value={unifiedSupplierFilter}
                            displayEmpty
                            MenuProps={TX_REVIEW_FILTER_SELECT_MENU_PROPS}
                            renderValue={(selected) =>
                              (selected as string[]).length > 0 ? (selected as string[]).join(', ') : 'All suppliers'
                            }
                            onChange={(e) => {
                              setUnifiedSupplierFilter(e.target.value as string[]);
                              setUnifiedSupplierMenuOpen(false);
                            }}
                          >
                            {unifiedSupplierOptions.map((s) => (
                              <MenuItem key={`us-${s}`} value={s}>
                                {`${s} (${formatCount(unifiedFilterOptionCounts.suppliers.get(s))})`}
                              </MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                      ) : null}
                      {isUnifiedFilterInline('amountMin') ? (
                        <TextField
                          size="small"
                          label="Amount min"
                          value={unifiedAmountMin}
                          onChange={(e) => setUnifiedAmountMin(e.target.value)}
                          sx={{ minWidth: 140 }}
                        />
                      ) : null}
                      {isUnifiedFilterInline('amountMax') ? (
                        <TextField
                          size="small"
                          label="Amount max"
                          value={unifiedAmountMax}
                          onChange={(e) => setUnifiedAmountMax(e.target.value)}
                          sx={{ minWidth: 140 }}
                        />
                      ) : null}
                      {isUnifiedFilterInline('zeroNullAmount') ? (
                        <FormControlLabel
                          sx={{ ml: 0, mr: 0 }}
                          control={
                            <Checkbox
                              size="small"
                              checked={unifiedZeroOrNullAmountOnly}
                              onChange={(e) => {
                                const on = e.target.checked;
                                setUnifiedZeroOrNullAmountOnly(on);
                                if (on) {
                                  setUnifiedAmountMin('');
                                  setUnifiedAmountMax('');
                                }
                              }}
                            />
                          }
                          label="Zero / null amount"
                        />
                      ) : null}
                      {isUnifiedFilterInline('frequency') ? (
                        <FormControl size="small" sx={{ minWidth: 190 }}>
                          <Select
                            multiple
                            open={unifiedFrequencyMenuOpen}
                            onOpen={() => setUnifiedFrequencyMenuOpen(true)}
                            onClose={() => setUnifiedFrequencyMenuOpen(false)}
                            value={unifiedFrequencyFilter}
                            displayEmpty
                            MenuProps={TX_REVIEW_FILTER_SELECT_MENU_PROPS}
                            renderValue={(selected) =>
                              (selected as string[]).length > 0 ? (selected as string[]).join(', ') : 'All frequencies'
                            }
                            onChange={(e) => {
                              setUnifiedFrequencyFilter(e.target.value as string[]);
                              setUnifiedFrequencyMenuOpen(false);
                            }}
                          >
                            {unifiedFilterOptions.frequencies.map((f) => (
                              <MenuItem key={`ufm-${f}`} value={f}>
                                {f}
                              </MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                      ) : null}
                      {isUnifiedFilterInline('currency') ? (
                        <FormControl size="small" sx={{ minWidth: 190 }}>
                          <Select
                            multiple
                            open={unifiedCurrencyMenuOpen}
                            onOpen={() => setUnifiedCurrencyMenuOpen(true)}
                            onClose={() => setUnifiedCurrencyMenuOpen(false)}
                            value={unifiedCurrencyFilter}
                            displayEmpty
                            MenuProps={TX_REVIEW_FILTER_SELECT_MENU_PROPS}
                            renderValue={(selected) =>
                              (selected as string[]).length > 0 ? (selected as string[]).join(', ') : 'All currencies'
                            }
                            onChange={(e) => {
                              setUnifiedCurrencyFilter(e.target.value as string[]);
                              setUnifiedCurrencyMenuOpen(false);
                            }}
                          >
                            {unifiedFilterOptions.currencies.map((c) => (
                              <MenuItem key={`ufcurm-${c}`} value={c}>
                                {`${c} (${formatCount(unifiedFilterOptionCounts.currencies.get(c))})`}
                              </MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                      ) : null}
                      {isUnifiedFilterInline('revolutState') ? (
                        <FormControl size="small" sx={{ minWidth: 200 }}>
                          <Select
                            multiple
                            open={unifiedRevolutStateMenuOpen}
                            onOpen={() => setUnifiedRevolutStateMenuOpen(true)}
                            onClose={() => setUnifiedRevolutStateMenuOpen(false)}
                            value={unifiedRevolutStateFilter}
                            displayEmpty
                            MenuProps={TX_REVIEW_FILTER_SELECT_MENU_PROPS}
                            renderValue={(selected) =>
                              (selected as string[]).length > 0
                                ? (selected as string[])
                                    .map((v) => formatRevolutStateFilterLabel(v))
                                    .join(', ')
                                : 'All states'
                            }
                            onChange={(e) => {
                              setUnifiedRevolutStateFilter(e.target.value as string[]);
                              setUnifiedRevolutStateMenuOpen(false);
                            }}
                          >
                            {unifiedFilterOptions.revolutStates.map((st) => (
                              <MenuItem key={`urs-inline-${st}`} value={st}>
                                {`${formatRevolutStateFilterLabel(st)} (${formatCount(
                                  unifiedFilterOptionCounts.revolutStates.get(st) || 0
                                )})`}
                              </MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                      ) : null}
                    </Stack>
                    <Button
                      size="small"
                      variant={unifiedHasAdvancedFilters ? 'contained' : 'outlined'}
                      onClick={() => setUnifiedMoreFiltersOpen(true)}
                      sx={{ flexShrink: 0, whiteSpace: 'nowrap' }}
                    >
                      {unifiedModalFilterCount > 0 ? `More filters (${unifiedModalFilterCount})` : 'More filters'}
                    </Button>
                  </Stack>
                  <Stack
                    direction="row"
                    spacing={1}
                    sx={{ mb: 1 }}
                    alignItems="center"
                    justifyContent="space-between"
                    flexWrap="wrap"
                    useFlexGap
                  >
                    <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                      <Button
                        size="small"
                        variant="outlined"
                        onClick={() => {
                          // State updates alone; canonical refetch runs from the filter-effect
                          // (avoid stale refreshTransactionReviewCore closure).
                          resetUnifiedFilters();
                        }}
                      >
                        Clear all filters
                      </Button>
                      <Stack
                        direction="column"
                        spacing={0}
                        sx={{
                          flexShrink: 0,
                          justifyContent: 'center',
                          lineHeight: 1,
                          '& .MuiFormControlLabel-root': {
                            marginTop: 0,
                            marginBottom: 0,
                            marginRight: 0,
                            minHeight: 0,
                            py: 0,
                            ml: 0.5
                          }
                        }}
                      >
                        {unifiedCommittedFilter === 'all' ? (
                          <FormControlLabel
                            control={
                              <Switch
                                size="small"
                                checked={hideCommittedUnifiedRows}
                                onChange={(e) => setHideCommittedUnifiedRows(e.target.checked)}
                                disabled={unifiedCommittedFilter !== 'all'}
                              />
                            }
                            label={
                              <Typography variant="caption" sx={{ lineHeight: 1.15 }}>
                                Hide committed
                              </Typography>
                            }
                          />
                        ) : null}
                        <FormControlLabel
                          control={
                            <Switch
                              size="small"
                              checked={hideConfirmedCompleteUnifiedRows}
                              onChange={(_, checked) => {
                                setHideConfirmedCompleteUnifiedRows(checked);
                              }}
                            />
                          }
                          label={
                            <Typography variant="caption" sx={{ lineHeight: 1.15 }}>
                              Hide rows with all tags
                            </Typography>
                          }
                        />
                        <FormControlLabel
                          control={
                            <Switch
                              size="small"
                              checked={hideDeclinedRevolutTransactions}
                              onChange={(_, checked) => {
                                setHideDeclinedRevolutTransactions(checked);
                              }}
                            />
                          }
                          label={
                            <Typography variant="caption" sx={{ lineHeight: 1.15 }}>
                              Hide non-Complete status (Revolut)
                            </Typography>
                          }
                        />
                      </Stack>
                      {unifiedStatusFilter.map((status) => (
                        <Chip
                          key={`ufs-${status}`}
                          size="small"
                          label={`Status: ${unifiedStatusFilterOptionLabel(status)}`}
                          onDelete={() => setUnifiedStatusFilter((prev) => prev.filter((v) => v !== status))}
                        />
                      ))}
                      {unifiedAmountMin.trim() ? <Chip size="small" label={`Amount min: ${unifiedAmountMin}`} onDelete={() => setUnifiedAmountMin('')} /> : null}
                      {unifiedAmountMax.trim() ? <Chip size="small" label={`Amount max: ${unifiedAmountMax}`} onDelete={() => setUnifiedAmountMax('')} /> : null}
                      {unifiedZeroOrNullAmountOnly ? (
                        <Chip
                          size="small"
                          label="Zero / null amount"
                          onDelete={() => setUnifiedZeroOrNullAmountOnly(false)}
                        />
                      ) : null}
                      {unifiedDateFrom ? <Chip size="small" label={`Date from: ${unifiedDateFrom}`} onDelete={() => setUnifiedDateFrom('')} /> : null}
                      {unifiedDateTo ? <Chip size="small" label={`Date to: ${unifiedDateTo}`} onDelete={() => setUnifiedDateTo('')} /> : null}
                      {unifiedSupplierFilter.map((supplier) => (
                        <Chip
                          key={`ufsup-${supplier}`}
                          size="small"
                          label={`Supplier: ${supplier}`}
                          onDelete={() => setUnifiedSupplierFilter((prev) => prev.filter((v) => v !== supplier))}
                        />
                      ))}
                      {unifiedTxIdFilter.length > 0 ? (
                        <Chip
                          size="small"
                          label={`TX IDs: ${unifiedTxIdFilter.length.toLocaleString()}`}
                          onDelete={() => setUnifiedTxIdFilter([])}
                        />
                      ) : null}
                      {unifiedTextFilter.trim() ? <Chip size="small" label={`Search: ${unifiedTextFilter}`} onDelete={() => setUnifiedTextFilter('')} /> : null}
                      {unifiedCommittedFilter !== 'all' ? (
                        <Chip
                          size="small"
                          label={`Committed: ${unifiedCommittedFilter === 'committed' ? 'Committed' : 'Uncommitted'}`}
                          onDelete={() => setUnifiedCommittedFilter('all')}
                        />
                      ) : null}
                      {unifiedFlaggedFilter === 'flagged' ? (
                        <Chip
                          size="small"
                          color="error"
                          variant="outlined"
                          label="Flagged only"
                          onDelete={() => setUnifiedFlaggedFilter('all')}
                        />
                      ) : null}
                      {unifiedFrequencyFilter.map((frequency) => (
                        <Chip
                          key={`uff-${frequency}`}
                          size="small"
                          label={`Frequency: ${frequency}`}
                          onDelete={() => setUnifiedFrequencyFilter((prev) => prev.filter((v) => v !== frequency))}
                        />
                      ))}
                      {unifiedCurrencyFilter.map((currency) => (
                        <Chip
                          key={`ufcur-${currency}`}
                          size="small"
                          label={`Currency: ${currency}`}
                          onDelete={() => setUnifiedCurrencyFilter((prev) => prev.filter((v) => v !== currency))}
                        />
                      ))}
                      {!hideDeclinedRevolutTransactions ? (
                        <Chip
                          size="small"
                          label="Non-complete status (Revolut): shown"
                          onDelete={() => {
                            setHideDeclinedRevolutTransactions(true);
                          }}
                        />
                      ) : null}
                      {unifiedRevolutStateFilter.map((st) => (
                        <Chip
                          key={`urs-chip-${st}`}
                          size="small"
                          label={`State: ${formatRevolutStateFilterLabel(st)}`}
                          onDelete={() => setUnifiedRevolutStateFilter((prev) => prev.filter((v) => v !== st))}
                        />
                      ))}
                      {unifiedCashFlowFilter.map((heading) => (
                        <Chip
                          key={`ufh-${heading}`}
                          size="small"
                          label={`Cash flow: ${formatCashflowInOutHeadingLabel(heading)}`}
                          onDelete={() => setUnifiedCashFlowFilter((prev) => prev.filter((v) => v !== heading))}
                        />
                      ))}
                      {unifiedCategoryFilter.map((category) => (
                        <Chip
                          key={`ufc-${category}`}
                          size="small"
                          label={`Category: ${category}`}
                          onDelete={() => setUnifiedCategoryFilter((prev) => prev.filter((v) => v !== category))}
                        />
                      ))}
                      {unifiedUnclassifiedNeedsReviewOnly ? (
                        <Chip
                          size="small"
                          label="Unclassified needs review"
                          onDelete={() => setUnifiedUnclassifiedNeedsReviewOnly(false)}
                        />
                      ) : null}
                      {unifiedVatFilter.map((vk) => (
                        <Chip
                          key={`ufvat-${vk}`}
                          size="small"
                          label={`VAT: ${formatVatFilterMenuLabel(vk)}`}
                          onDelete={() => setUnifiedVatFilter((prev) => prev.filter((v) => v !== vk))}
                        />
                      ))}
                      {unifiedAppliedTagFilter.map((lab) => (
                        <Chip
                          key={`ufat-${lab}`}
                          size="small"
                          label={`Applied: ${lab}`}
                          onDelete={() => setUnifiedAppliedTagFilter((prev) => prev.filter((v) => v !== lab))}
                        />
                      ))}
                      {unifiedTagDuplicateFilter === 'no_duplicates' ? (
                        <Chip
                          size="small"
                          label="Tag duplicates: No duplicate tags"
                          onDelete={() => setUnifiedTagDuplicateFilter('all')}
                        />
                      ) : null}
                      {unifiedTagDuplicateFilter === 'duplicates' ? (
                        <Chip
                          size="small"
                          label="Tag duplicates: >1 tag of same type"
                          onDelete={() => setUnifiedTagDuplicateFilter('all')}
                        />
                      ) : null}
                      {unifiedAccountFilter.map((account) => (
                        <Chip
                          key={`ufa-${account}`}
                          size="small"
                          label={`Bank account: ${formatRevolutAccountLabel(account)}`}
                          onDelete={() => setUnifiedAccountFilter((prev) => prev.filter((v) => v !== account))}
                        />
                      ))}
                      {unifiedCommittedFilter === 'all' && !hideConfirmedCompleteUnifiedRows ? (
                        <Chip
                          size="small"
                          label="Uncommitted, all tags: shown"
                          onDelete={() => {
                            setHideConfirmedCompleteUnifiedRows(true);
                          }}
                        />
                      ) : null}
                      {unifiedCommittedFilter === 'all' && !hideCommittedUnifiedRows ? (
                        <Chip
                          size="small"
                          label="Committed: shown"
                          onDelete={() => setHideCommittedUnifiedRows(true)}
                        />
                      ) : null}
                    </Stack>
                    <Stack
                      direction="row"
                      spacing={1}
                      alignItems="center"
                      justifyContent="flex-end"
                      flexWrap="wrap"
                      useFlexGap
                      sx={{ flexShrink: 0, flex: '1 1 280px', minWidth: 0 }}
                    >
                      <Stack
                        direction="row"
                        spacing={1}
                        alignItems="center"
                        sx={{
                          flex: '1 1 160px',
                          minWidth: 0,
                          visibility:
                            loading ||
                            progressivelyRenderedUnifiedRows.length < sortedUnifiedRows.length ||
                            unifiedCanonicalServerTotalPending != null ||
                            allTransactionsHydrating ||
                            confirmedPayloadHydrating
                              ? 'visible'
                              : 'hidden'
                        }}
                      >
                        <CircularProgress size={14} aria-hidden />
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          sx={{
                            minWidth: 0,
                            whiteSpace: 'normal',
                            lineHeight: 1.35
                          }}
                        >
                          {loading
                            ? unifiedCanonicalServerTotalPending == null
                              ? 'Counting total number of transactions...'
                              : 'Loading first batch...'
                            : unifiedCanonicalServerTotalPending != null
                              ? `Loading rows progressively: ${progressivelyRenderedUnifiedRows.length.toLocaleString()} / ${sortedUnifiedRows.length.toLocaleString()} · total ${unifiedCanonicalServerTotalPending.toLocaleString()} from server`
                              : `Loading rows progressively: ${progressivelyRenderedUnifiedRows.length.toLocaleString()} / ${sortedUnifiedRows.length.toLocaleString()}`}
                        </Typography>
                      </Stack>
                      <TablePagination
                        component="div"
                        count={sortedUnifiedRows.length}
                        page={unifiedPage}
                        onPageChange={(_e, nextPage) => setUnifiedPage(nextPage)}
                        rowsPerPage={unifiedRowsPerPage}
                        onRowsPerPageChange={(e) => {
                          const next = Number(e.target.value || 200);
                          setUnifiedRowsPerPage(next);
                          setUnifiedPage(0);
                        }}
                        rowsPerPageOptions={UNIFIED_ROWS_PER_PAGE_OPTIONS}
                        labelDisplayedRows={unifiedPaginationLabel}
                        sx={{
                          border: 0,
                          p: 0,
                          m: 0,
                          minHeight: 0,
                          flexShrink: 0,
                          '& .MuiToolbar-root': { minHeight: 40, pl: 0, pr: 0, alignItems: 'center' },
                          '& .MuiTablePagination-toolbar': {
                            minHeight: 40,
                            pl: 0,
                            pr: 0,
                            alignItems: 'center',
                            flexWrap: 'nowrap'
                          },
                          '& .MuiTablePagination-spacer': { display: 'none' },
                          '& .MuiTablePagination-selectLabel': {
                            mb: 0,
                            display: 'inline-flex',
                            alignItems: 'center',
                            lineHeight: 1.5,
                            minHeight: 32
                          },
                          '& .MuiTablePagination-displayedRows': {
                            mb: 0,
                            display: 'inline-flex',
                            alignItems: 'center',
                            lineHeight: 1.5,
                            minHeight: 32
                          },
                          '& .MuiTablePagination-select': {
                            mb: 0,
                            alignSelf: 'center',
                            display: 'inline-flex',
                            alignItems: 'center',
                            '& .MuiInputBase-root': {
                              marginTop: 0,
                              marginBottom: 0,
                              minHeight: 32,
                              alignItems: 'center'
                            },
                            '& .MuiSelect-select': {
                              display: 'inline-flex',
                              alignItems: 'center',
                              minHeight: 32,
                              lineHeight: 1.5,
                              paddingTop: 4,
                              paddingBottom: 4,
                              boxSizing: 'border-box'
                            }
                          },
                          '& .MuiTablePagination-input': { alignSelf: 'center' },
                          '& .MuiTablePagination-toolbar .MuiInputBase-root': {
                            alignItems: 'center',
                            marginTop: 0,
                            marginBottom: 0
                          },
                          '& .MuiTablePagination-actions': {
                            alignSelf: 'center',
                            ...(isUnifiedRowsPerPageAll(unifiedRowsPerPage) ? { display: 'none' } : {})
                          }
                        }}
                      />
                      <Button
                        size="small"
                        variant={showUnifiedStatusChart ? 'contained' : 'outlined'}
                        onClick={() => setShowUnifiedStatusChart((prev) => !prev)}
                        sx={{ flexShrink: 0, alignSelf: 'center' }}
                      >
                        Show/Hide Chart
                      </Button>
                    </Stack>
                  </Stack>
                  {showUnifiedStatusChart ? (
                    <Card variant="outlined" sx={{ mb: 1.5 }}>
                      <CardContent sx={{ pb: '16px !important' }}>
                        <Typography variant="subtitle1" sx={{ mb: 0.5 }}>
                          Monthly chart
                        </Typography>
                        <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1.5 }} flexWrap="wrap">
                          <Typography variant="body2" color="text.secondary">
                            {unifiedMonthlyChart.chartMode === 'balance'
                              ? 'Cumulative balance by calendar day: running sum of signed amounts in date order (current filters). Light line: linear trend across days.'
                              : unifiedMonthlyChart.valueType === 'amount'
                                ? unifiedChartBreakdown === 'amount_income' ||
                                  unifiedChartBreakdown === 'amount_expense'
                                  ? 'Stacked amount per month by supplier (absolute values; based on current filters).'
                                  : 'Stacked amount per month (based on current filters).'
                                : 'Stacked count per month (based on current filters).'}
                          </Typography>
                          <FormControl size="small" sx={{ minWidth: 220 }}>
                            <Select
                              value={unifiedChartBreakdown}
                              onChange={(e) =>
                                setUnifiedChartBreakdown(e.target.value as UnifiedChartBreakdown)
                              }
                            >
                              {UNIFIED_CHART_BREAKDOWN_OPTIONS.map((opt) => (
                                <MenuItem key={`chart-breakdown-${opt.value}`} value={opt.value}>
                                  {opt.label}
                                </MenuItem>
                              ))}
                            </Select>
                          </FormControl>
                        </Stack>
                        {unifiedMonthlyChart.rows.length === 0 ? (
                          <Typography variant="body2" color="text.secondary">
                            No dated rows available for the current filters.
                          </Typography>
                        ) : unifiedMonthlyChart.chartMode === 'balance' ? (
                          <Box sx={{ width: '100%', height: 320 }}>
                            <ResponsiveContainer width="100%" height="100%">
                              <LineChart
                                data={unifiedMonthlyChart.rows}
                                margin={{ top: 8, right: 16, left: 0, bottom: 8 }}
                              >
                                <CartesianGrid strokeDasharray="3 3" />
                                <XAxis
                                  dataKey="dayLabel"
                                  interval="preserveStartEnd"
                                  minTickGap={32}
                                  tick={{ fontSize: 11 }}
                                />
                                <YAxis
                                  allowDecimals
                                  width={56}
                                  tickFormatter={(v: number) =>
                                    Math.abs(v) >= 1000
                                      ? `${(v / 1000).toFixed(v % 1000 === 0 ? 0 : 1)}k`
                                      : String(v)
                                  }
                                />
                                <RechartsTooltip
                                  formatter={(value: number | string) =>
                                    typeof value === 'number'
                                      ? new Intl.NumberFormat(undefined, {
                                          maximumFractionDigits: 2,
                                          minimumFractionDigits: 0
                                        }).format(value)
                                      : value
                                  }
                                />
                                <Legend />
                                <Line
                                  type="monotone"
                                  dataKey="balance"
                                  name="Balance"
                                  stroke="#2563EB"
                                  strokeWidth={2}
                                  dot={false}
                                  isAnimationActive={false}
                                />
                                <Line
                                  type="linear"
                                  dataKey="trend"
                                  name="Trend (linear fit)"
                                  stroke="rgba(37, 99, 235, 0.35)"
                                  strokeWidth={1.5}
                                  dot={false}
                                  isAnimationActive={false}
                                />
                              </LineChart>
                            </ResponsiveContainer>
                          </Box>
                        ) : (
                          <Box sx={{ width: '100%', height: 320 }}>
                            <ResponsiveContainer width="100%" height="100%">
                              <BarChart
                                data={unifiedMonthlyChart.rows}
                                margin={{ top: 8, right: 20, left: 0, bottom: 8 }}
                              >
                                <CartesianGrid strokeDasharray="3 3" />
                                <XAxis dataKey="monthLabel" />
                                <YAxis
                                  allowDecimals={unifiedMonthlyChart.valueType === 'amount'}
                                  width={unifiedMonthlyChart.valueType === 'amount' ? 56 : 36}
                                  tickFormatter={
                                    unifiedMonthlyChart.valueType === 'amount'
                                      ? (v: number) =>
                                          Math.abs(v) >= 1000
                                            ? `${(v / 1000).toFixed(v % 1000 === 0 ? 0 : 1)}k`
                                            : String(v)
                                      : undefined
                                  }
                                />
                                <RechartsTooltip
                                  formatter={(value: number | string, name: string) => {
                                    const formattedValue =
                                      unifiedMonthlyChart.valueType === 'amount' && typeof value === 'number'
                                        ? new Intl.NumberFormat(undefined, {
                                            maximumFractionDigits: 2,
                                            minimumFractionDigits: 0
                                          }).format(value)
                                        : value;
                                    const displayName =
                                      unifiedChartBreakdown === 'cash_flow'
                                        ? formatCashflowInOutHeadingLabel(name)
                                        : name;
                                    return [formattedValue, displayName];
                                  }}
                                />
                                <Legend
                                  formatter={(value) => {
                                    const label = String(value || '');
                                    const displayLabel =
                                      unifiedChartBreakdown === 'cash_flow'
                                        ? formatCashflowInOutHeadingLabel(label)
                                        : label;
                                    if (unifiedMonthlyChart.valueType !== 'count') return displayLabel;
                                    const total = Number(unifiedMonthlyChart.seriesTotals?.[label] || 0);
                                    return `${displayLabel} (${total.toLocaleString()})`;
                                  }}
                                />
                                {unifiedMonthlyChart.seriesKeys.map((series) => {
                                  let fill = CHART_SERIES_PALETTE[
                                    Math.abs(
                                      String(series)
                                        .split('')
                                        .reduce((acc, ch) => acc + ch.charCodeAt(0), 0)
                                    ) % CHART_SERIES_PALETTE.length
                                  ];
                                  if (unifiedChartBreakdown === 'status') {
                                    fill = unifiedStatusFillColor(series);
                                  } else if (series === 'In') {
                                    fill = '#16A34A';
                                  } else if (series === 'Out') {
                                    fill = '#DC2626';
                                  } else if (series === 'Other suppliers') {
                                    fill = '#64748B';
                                  }
                                  return (
                                    <Bar
                                      key={`month-bar-${series}`}
                                      dataKey={series}
                                      stackId="monthly-status"
                                      fill={fill}
                                    />
                                  );
                                })}
                              </BarChart>
                            </ResponsiveContainer>
                          </Box>
                        )}
                      </CardContent>
                    </Card>
                  ) : null}
                  {selectedUnifiedRowKeys.length > 0 ? (
                    <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1.5 }} flexWrap="wrap" useFlexGap>
                      <Chip label={`${selectedUnifiedRowKeys.length} selected`} size="small" />
                      {selectedCommittedUnifiedCount > 0 ? (
                        <Chip
                          size="small"
                          color="warning"
                          variant="outlined"
                          label={`${selectedCommittedUnifiedCount} committed selected`}
                        />
                      ) : null}
                      <Button
                        size="small"
                        variant="contained"
                        color="success"
                        disabled={unifiedBulkLock || unifiedActionBusy || bulkReprocessBusy}
                        onClick={() => void runUnifiedBulkConfirm()}
                      >
                        {unifiedBulkBusy ? 'Committing…' : 'Commit selected'}
                      </Button>
                      <Tooltip
                        title={
                          selectedCommittedUnifiedCount === 0
                            ? 'Select at least one committed row to uncommit.'
                            : ''
                        }
                      >
                        <span>
                          <Button
                            size="small"
                            variant="outlined"
                            color="warning"
                            disabled={
                              unifiedBulkLock ||
                              unifiedActionBusy ||
                              bulkReprocessBusy ||
                              selectedCommittedUnifiedCount === 0
                            }
                            onClick={() => void runUnifiedBulkUncommit()}
                          >
                            {unifiedBulkUncommitBusy ? 'Uncommitting…' : 'Uncommit selected'}
                          </Button>
                        </span>
                      </Tooltip>
                      <FormControl size="small" sx={{ minWidth: 170 }}>
                        <InputLabel id="bulk-tag-heading-inline-label" sx={{ whiteSpace: 'pre-line', lineHeight: 1.15 }}>
                          Cash{'\n'}Flow
                        </InputLabel>
                        <Select
                          labelId="bulk-tag-heading-inline-label"
                          label={'Cash\nFlow'}
                          value={bulkTagHeadingDraft}
                          onChange={(e) => setBulkTagHeadingDraft(String(e.target.value || BULK_TAG_NO_CHANGE))}
                          disabled={unifiedBulkLock || unifiedActionBusy || bulkReprocessBusy || selectedEditableUnifiedRows.length === 0}
                        >
                          <MenuItem value={BULK_TAG_NO_CHANGE}>No change</MenuItem>
                          <MenuItem
                            value={BULK_TAG_REMOVE_TAGS}
                            title="Remove cashflow (In/Out/N/A) tag rows on selected transactions (legacy tag chips)."
                          >
                            {' - '}
                          </MenuItem>
                          {headingCategories.map((c) => (
                            <MenuItem key={`bulk-tag-heading-inline-${c.id}`} value={String(c.id)}>
                              {formatCashflowInOutHeadingLabel(c.name)}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                      <Tooltip
                        title={
                          selectedEditableUnifiedRows.length === 0
                            ? 'Bulk edit works on uncommitted rows only. Deselect committed rows.'
                            : ''
                        }
                      >
                        <span>
                          <FormControl size="small" sx={{ minWidth: 170 }}>
                            <InputLabel id="bulk-tag-supplier-inline-label">Supplier</InputLabel>
                            <Select
                              labelId="bulk-tag-supplier-inline-label"
                              label="Supplier"
                              value={bulkTagSupplierDraft}
                              onChange={(e) => setBulkTagSupplierDraft(String(e.target.value || BULK_TAG_NO_CHANGE))}
                              disabled={unifiedBulkLock || unifiedActionBusy || bulkReprocessBusy || selectedEditableUnifiedRows.length === 0}
                            >
                              <MenuItem value={BULK_TAG_NO_CHANGE}>No change</MenuItem>
                              <MenuItem
                                value={BULK_TAG_REMOVE_TAGS}
                                title="Remove supplier tag rows (chips with a supplier name) on selected transactions."
                              >
                                {' - '}
                              </MenuItem>
                              {unifiedSupplierOptions.map((name) => (
                                <MenuItem key={`bulk-tag-supplier-inline-${name}`} value={name}>
                                  {name}
                                </MenuItem>
                              ))}
                            </Select>
                          </FormControl>
                        </span>
                      </Tooltip>
                      <FormControl size="small" sx={{ minWidth: 180 }}>
                        <InputLabel id="bulk-tag-category-inline-label">Category</InputLabel>
                        <Select
                          labelId="bulk-tag-category-inline-label"
                          label="Category"
                          value={bulkTagCategoryDraft}
                          onChange={(e) => setBulkTagCategoryDraft(String(e.target.value || BULK_TAG_NO_CHANGE))}
                          disabled={unifiedBulkLock || unifiedActionBusy || bulkReprocessBusy || selectedEditableUnifiedRows.length === 0}
                        >
                          <MenuItem value={BULK_TAG_NO_CHANGE}>No change</MenuItem>
                          <MenuItem
                            value={BULK_TAG_REMOVE_TAGS}
                            title="Remove non–Income/Expense category tag rows (granular chips) on selected transactions."
                          >
                            {' - '}
                          </MenuItem>
                          {granularCategories.map((c) => (
                            <MenuItem key={`bulk-tag-category-inline-${c.id}`} value={String(c.id)}>
                              {c.name}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                      <FormControl size="small" sx={{ minWidth: 150 }}>
                        <InputLabel id="bulk-tag-vat-inline-label">VAT rate</InputLabel>
                        <Select
                          labelId="bulk-tag-vat-inline-label"
                          label="VAT rate"
                          value={bulkTagVatDraft}
                          onChange={(e) => setBulkTagVatDraft(String(e.target.value || BULK_TAG_NO_CHANGE))}
                          disabled={unifiedBulkLock || unifiedActionBusy || bulkReprocessBusy || selectedEditableUnifiedRows.length === 0}
                        >
                          <MenuItem value={BULK_TAG_NO_CHANGE}>No change</MenuItem>
                          <MenuItem
                            value={BULK_TAG_REMOVE_TAGS}
                            title="Remove all legacy category-tag rows on selected transactions (same chips as the VAT column list)."
                          >
                            {' - '}
                          </MenuItem>
                          {vatRatePickOptions.map((rate) => (
                            <MenuItem key={`bulk-tag-vat-inline-${rate.label}`} value={rate.value}>
                              {rate.label}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                      <FormControl size="small" sx={{ minWidth: 120 }}>
                        <InputLabel id="bulk-tag-prop-inline-label">Prop</InputLabel>
                        <Select
                          labelId="bulk-tag-prop-inline-label"
                          label="Prop"
                          value={bulkTagPropDraft}
                          onChange={(e) => setBulkTagPropDraft(String(e.target.value || BULK_TAG_NO_CHANGE))}
                          disabled={unifiedBulkLock || unifiedActionBusy || bulkReprocessBusy || selectedEditableUnifiedRows.length === 0}
                        >
                          <MenuItem value={BULK_TAG_NO_CHANGE}>No change</MenuItem>
                          <MenuItem
                            value={BULK_TAG_REMOVE_TAGS}
                            title="Clear all property tags on selected transactions."
                          >
                            {' - '}
                          </MenuItem>
                          {txPropOptions.map((opt) => (
                            <MenuItem key={`bulk-tag-prop-inline-${opt.id}`} value={String(opt.id)}>
                              {opt.nickname}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                      <Button
                        size="small"
                        variant="contained"
                        disabled={
                          unifiedBulkLock ||
                          unifiedActionBusy ||
                          bulkReprocessBusy ||
                          selectedEditableUnifiedRows.length === 0
                        }
                        onClick={() => void runUnifiedBulkManualTagUpdate()}
                      >
                        {unifiedBulkBusy ? 'Applying…' : 'Apply tags'}
                      </Button>
                      <Tooltip
                        title={
                          selectedStrippableUnifiedRows.length === 0
                            ? 'Strip applies to selected rows that are still in review (not committed). Uncommit or adjust selection.'
                            : 'Remove every classification tag on all selected strippable rows (full strip per transaction).'
                        }
                      >
                        <span>
                          <Button
                            size="small"
                            variant="outlined"
                            color="error"
                            disabled={
                              unifiedBulkLock ||
                              unifiedActionBusy ||
                              bulkReprocessBusy ||
                              removeAllTagsBusy ||
                              selectedStrippableUnifiedRows.length === 0
                            }
                            onClick={() => void runStripAllTagsForSelectedUnifiedRowsFromFilter()}
                          >
                            {unifiedStripTagsBusy ? 'Removing tags…' : 'Remove tags (selected)'}
                          </Button>
                        </span>
                      </Tooltip>
                      <Tooltip
                        open={bulkAppliedTagDeleteSelectOpen ? false : undefined}
                        disableHoverListener={bulkAppliedTagDeleteSelectOpen}
                        disableFocusListener={bulkAppliedTagDeleteSelectOpen}
                        disableTouchListener={bulkAppliedTagDeleteSelectOpen}
                        title={
                          selectedEditableUnifiedRows.length === 0
                            ? 'Bulk delete applies to uncommitted rows only.'
                            : 'Remove one composite label (as shown in Applied tags) from every selected row that has it.'
                        }
                      >
                        <span>
                          <FormControl size="small" sx={{ minWidth: 220 }}>
                            <InputLabel id="bulk-applied-tag-delete-label">Delete applied tag</InputLabel>
                            <Select
                              labelId="bulk-applied-tag-delete-label"
                              label="Delete applied tag"
                              value={bulkAppliedTagDeleteComposite}
                              onOpen={() => setBulkAppliedTagDeleteSelectOpen(true)}
                              onClose={() => setBulkAppliedTagDeleteSelectOpen(false)}
                              onChange={(e) => setBulkAppliedTagDeleteComposite(String(e.target.value || ''))}
                              disabled={
                                unifiedBulkLock ||
                                unifiedActionBusy ||
                                bulkReprocessBusy ||
                                selectedEditableUnifiedRows.length === 0 ||
                                bulkAppliedTagLabelOptions.length === 0
                              }
                            >
                              <MenuItem value="">
                                <em>Choose composite…</em>
                              </MenuItem>
                              {bulkAppliedTagLabelOptions.map((label) => (
                                <MenuItem key={`bulk-ap-del-${label}`} value={label}>
                                  {label}{' '}
                                  <Typography component="span" variant="body2" color="text.secondary">
                                    ({bulkAppliedTagLabelOptionCounts.get(label) ?? 0})
                                  </Typography>
                                </MenuItem>
                              ))}
                            </Select>
                          </FormControl>
                        </span>
                      </Tooltip>
                      <Button
                        size="small"
                        variant="outlined"
                        color="warning"
                        disabled={
                          unifiedBulkLock ||
                          unifiedActionBusy ||
                          bulkReprocessBusy ||
                          selectedEditableUnifiedRows.length === 0 ||
                          !bulkAppliedTagDeleteComposite.trim()
                        }
                        onClick={() => requestBulkAppliedTagDelete()}
                      >
                        Delete matching applied tags…
                      </Button>
                      <Button
                        size="small"
                        variant="outlined"
                        disabled={unifiedBulkLock || unifiedActionBusy || bulkReprocessBusy}
                        onClick={openBulkTagEditDialog}
                      >
                        Individual rule actions
                      </Button>
                      <Tooltip title="Run supplier, classification, VAT, and Special Case reruns for selected rows.">
                        <span>
                          <Button
                            size="small"
                            variant="outlined"
                            color="secondary"
                            disabled={unifiedBulkLock || unifiedActionBusy || bulkReprocessBusy}
                            onClick={() => void runBulkReprocessSelected({ classification: true, supplier: true, vat: true, specialCase: true })}
                          >
                            {bulkReprocessBusy ? '…' : 'Re-run ALL Rules'}
                          </Button>
                        </span>
                      </Tooltip>
                    </Stack>
                  ) : null}
                  <Box sx={{ position: 'relative', minHeight: loading && pagedUnifiedRows.length === 0 ? 220 : undefined }}>
                    {loading ? (
                      <Box
                        sx={{
                          position: pagedUnifiedRows.length === 0 ? 'relative' : 'absolute',
                          inset: 0,
                          zIndex: 2,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          minHeight: pagedUnifiedRows.length === 0 ? 220 : undefined,
                          bgcolor:
                            pagedUnifiedRows.length === 0
                              ? 'transparent'
                              : (t) => alpha(t.palette.background.paper, 0.72),
                          pointerEvents: 'none'
                        }}
                        aria-busy="true"
                        aria-live="polite"
                      >
                        <Stack alignItems="center" spacing={1.25} sx={{ py: pagedUnifiedRows.length === 0 ? 4 : 0 }}>
                          <CircularProgress size={36} aria-label="Loading transaction reviews" />
                          <Typography variant="body2" color="text.secondary">
                            {unifiedCanonicalServerTotalPending == null
                              ? 'Counting transactions…'
                              : 'Loading transaction reviews…'}
                          </Typography>
                        </Stack>
                      </Box>
                    ) : null}
                  <TableContainer
                    ref={unifiedTableScrollRef}
                    sx={{
                      visibility: loading && pagedUnifiedRows.length === 0 ? 'hidden' : 'visible',
                      // Scroll container required for MUI stickyHeader (paginated + rows-per-page All).
                      maxHeight: 'min(70vh, calc(100vh - 220px))',
                      maxWidth: '100%',
                      overflowY: 'auto',
                      // Fit to page width — do not introduce a horizontal scrollbar.
                      overflowX: 'hidden',
                      '& .MuiTableCell-stickyHeader': {
                        zIndex: 3
                      }
                    }}
                  >
                    <Table
                      size="small"
                      stickyHeader
                      sx={{
                        tableLayout: 'fixed',
                        width: '100%',
                        maxWidth: '100%',
                        minWidth: 0,
                        '& .MuiTableCell-root': {
                          boxSizing: 'border-box',
                          // Override CardContent’s 16px head padding on every cell.
                          px: '4px'
                        }
                      }}
                    >
                      {/*
                        Compact cols = fixed px. Description takes a share of the *table*;
                        Supplier/Category omit width so they split only the true leftover
                        (never stack % on top of fixed px — that caused horizontal scroll).
                      */}
                      <colgroup>
                        <col style={{ width: UNIFIED_COL.checkbox }} />
                        <col style={{ width: unifiedFitColWidths.id }} />
                        <col style={{ width: unifiedFitColWidths.amount }} />
                        <col style={{ width: UNIFIED_COL.date }} />
                        {/* Description share: slightly reduced so Amount can show full “Amount” header + FX stack. */}
                        <col style={{ width: '20%' }} />
                        <col style={{ width: UNIFIED_COL.who }} />
                        {!useUnifiedOverflowColumns ? (
                          <col style={{ width: UNIFIED_COL.account }} />
                        ) : null}
                        <col style={{ width: unifiedFitColWidths.cashFlow }} />
                        <col />
                        {/* Category capped so wider Prop steals from here, not Supplier. */}
                        <col style={{ width: '13%' }} />
                        <col style={{ width: UNIFIED_COL.vat }} />
                        <col style={{ width: UNIFIED_COL.prop }} />
                        <col style={{ width: UNIFIED_COL.actions }} />
                      </colgroup>
                      <TableHead>
                        <TableRow>
                          <TableCell
                            padding="none"
                            className="unified-col-checkbox"
                            sx={UNIFIED_TABLE_CHECKBOX_HEAD_SX}
                          >
                            <input
                              type="checkbox"
                              checked={
                                pagedUnifiedRows.length > 0 &&
                                pagedUnifiedRows.every((r) => selectedUnifiedRowKeys.includes(r.key))
                              }
                              onChange={(e) => {
                                if (e.target.checked) {
                                  const pageKeys = pagedUnifiedRows.map((r) => r.key);
                                  setSelectedUnifiedRowKeys((prev) =>
                                    Array.from(new Set([...prev, ...pageKeys]))
                                  );
                                  const filteredCount = filteredUnifiedRows.length;
                                  const pageCount = pageKeys.length;
                                  const allFilteredAlreadySelected =
                                    allFilteredUnifiedKeys.length > 0 &&
                                    allFilteredUnifiedKeys.every((key) => selectedUnifiedRowKeys.includes(key));
                                  if (
                                    filteredCount > pageCount &&
                                    !allFilteredAlreadySelected
                                  ) {
                                    setSelectAllFilteredPromptOpen(true);
                                  }
                                } else {
                                  const pageKeys = new Set(pagedUnifiedRows.map((r) => r.key));
                                  setSelectedUnifiedRowKeys((prev) => prev.filter((k) => !pageKeys.has(k)));
                                  setSelectAllFilteredPromptOpen(false);
                                }
                              }}
                            />
                          </TableCell>
                          <TableCell
                            align="right"
                            className="unified-col-id"
                            sortDirection={unifiedSort.key === 'id' ? unifiedSort.dir : false}
                            sx={{
                              ...UNIFIED_TABLE_ID_HEAD_SX,
                              ...unifiedFitColSx(unifiedFitColWidths.id)
                            }}
                          >
                            <TableSortLabel
                              active={unifiedSort.key === 'id'}
                              direction={unifiedSort.key === 'id' ? unifiedSort.dir : 'asc'}
                              onClick={() =>
                                setUnifiedSort((prev) =>
                                  prev.key === 'id'
                                    ? { key: 'id', dir: prev.dir === 'asc' ? 'desc' : 'asc' }
                                    : { key: 'id', dir: 'asc' }
                                )
                              }
                            >
                              ID
                            </TableSortLabel>
                          </TableCell>
                          <TableCell
                            align="right"
                            className="unified-col-amount"
                            sortDirection={unifiedSort.key === 'amount' ? unifiedSort.dir : false}
                            sx={{
                              ...RAW_TX_HEADER_SX,
                              ...unifiedFitColSx(unifiedFitColWidths.amount),
                              px: '2px !important',
                              '& .MuiTableSortLabel-root': { fontSize: '0.75rem' },
                              '& .MuiTableSortLabel-icon': {
                                fontSize: '0.9rem !important',
                                marginLeft: '0 !important'
                              }
                            }}
                          >
                            <TableSortLabel
                              active={unifiedSort.key === 'amount'}
                              direction={unifiedSort.key === 'amount' ? unifiedSort.dir : 'asc'}
                              onClick={() =>
                                setUnifiedSort((prev) =>
                                  prev.key === 'amount'
                                    ? { key: 'amount', dir: prev.dir === 'asc' ? 'desc' : 'asc' }
                                    : { key: 'amount', dir: 'asc' }
                                )
                              }
                            >
                              Amount
                            </TableSortLabel>
                          </TableCell>
                          <TableCell
                            className="unified-col-date"
                            sortDirection={unifiedSort.key === 'date' ? unifiedSort.dir : false}
                            sx={{
                              ...RAW_TX_HEADER_SX,
                              ...UNIFIED_TABLE_DATE_CELL_SX,
                              '& .MuiTableSortLabel-root': {
                                fontSize: '0.75rem',
                                color: 'rgb(12, 74, 110)'
                              },
                              '& .MuiTableSortLabel-root.Mui-active': {
                                color: 'rgb(3, 105, 161)'
                              },
                              '& .MuiTableSortLabel-icon': {
                                fontSize: '1rem !important',
                                color: 'rgb(3, 105, 161) !important'
                              }
                            }}
                          >
                            <TableSortLabel
                              active={unifiedSort.key === 'date'}
                              direction={unifiedSort.key === 'date' ? unifiedSort.dir : 'asc'}
                              onClick={() =>
                                setUnifiedSort((prev) =>
                                  prev.key === 'date'
                                    ? { key: 'date', dir: prev.dir === 'asc' ? 'desc' : 'asc' }
                                    : { key: 'date', dir: 'asc' }
                                )
                              }
                            >
                              Date
                            </TableSortLabel>
                          </TableCell>
                          <TableCell
                            sortDirection={
                              unifiedSort.key === 'description' || unifiedSort.key === 'reference'
                                ? unifiedSort.dir
                                : false
                            }
                            sx={{
                              ...RAW_TX_HEADER_SX,
                              ...UNIFIED_FLEX_CELL_SX,
                              whiteSpace: 'normal',
                              lineHeight: 1.15,
                              verticalAlign: 'middle'
                            }}
                          >
                            <TableSortLabel
                              active={
                                unifiedSort.key === 'description' || unifiedSort.key === 'reference'
                              }
                              direction={
                                unifiedSort.key === 'description' || unifiedSort.key === 'reference'
                                  ? unifiedSort.dir
                                  : 'asc'
                              }
                              onClick={() =>
                                setUnifiedSort((prev) =>
                                  prev.key === 'description'
                                    ? { key: 'description', dir: prev.dir === 'asc' ? 'desc' : 'asc' }
                                    : { key: 'description', dir: 'asc' }
                                )
                              }
                              sx={{
                                width: '100%',
                                justifyContent: 'flex-start',
                                flexDirection: 'column',
                                alignItems: 'flex-start',
                                gap: 0
                              }}
                            >
                              <Box
                                component="span"
                                sx={{
                                  display: 'inline-flex',
                                  flexDirection: 'column',
                                  alignItems: 'flex-start',
                                  lineHeight: 1.12
                                }}
                              >
                                <span>Description</span>
                                <span style={{ fontWeight: 400, opacity: 0.85 }}>Reference</span>
                                <span style={{ fontWeight: 400, opacity: 0.85 }}>URL</span>
                              </Box>
                            </TableSortLabel>
                          </TableCell>
                          <TableCell
                            align="center"
                            sx={{
                              ...UNIFIED_GREY_HEAD_PLAIN_SX,
                              ...unifiedFitColSx(UNIFIED_COL.who),
                              px: '2px !important',
                              fontSize: '0.75rem',
                              whiteSpace: 'nowrap'
                            }}
                            title="Cardholder: Om=Omar, Dw=Dwayne, Le=Lena, He=Heidi, ?=other"
                          >
                            Who
                          </TableCell>
                          {!useUnifiedOverflowColumns && (
                            <TableCell
                              sortDirection={unifiedSort.key === 'account' ? unifiedSort.dir : false}
                              sx={{
                                ...UNIFIED_GREY_HEAD_SORT_SX,
                                ...unifiedFitColSx(UNIFIED_COL.account),
                                px: '2px !important',
                                textAlign: 'center',
                                '& .MuiTableSortLabel-root': {
                                  fontSize: '0.75rem',
                                  whiteSpace: 'nowrap'
                                },
                                '& .MuiTableSortLabel-icon': {
                                  fontSize: '0.85rem !important',
                                  marginLeft: '1px !important',
                                  marginRight: '0 !important'
                                }
                              }}
                            >
                              <TableSortLabel
                                active={unifiedSort.key === 'account'}
                                direction={unifiedSort.key === 'account' ? unifiedSort.dir : 'asc'}
                                onClick={() =>
                                  setUnifiedSort((prev) =>
                                    prev.key === 'account'
                                      ? { key: 'account', dir: prev.dir === 'asc' ? 'desc' : 'asc' }
                                      : { key: 'account', dir: 'asc' }
                                  )
                                }
                              >
                                A/C
                              </TableSortLabel>
                            </TableCell>
                          )}
                          <TableCell
                            sortDirection={unifiedSort.key === 'heading' ? unifiedSort.dir : false}
                            sx={{
                              ...UNIFIED_HEADING_COLUMN_HEAD_SX,
                              ...unifiedFitColSx(unifiedFitColWidths.cashFlow),
                              px: '2px !important',
                              whiteSpace: 'normal',
                              lineHeight: 1.1,
                              textAlign: 'center',
                              verticalAlign: 'middle',
                              fontSize: '0.7rem',
                              overflow: 'hidden'
                            }}
                          >
                            <TableSortLabel
                              active={unifiedSort.key === 'heading'}
                              direction={unifiedSort.key === 'heading' ? unifiedSort.dir : 'asc'}
                              onClick={() =>
                                setUnifiedSort((prev) =>
                                  prev.key === 'heading'
                                    ? { key: 'heading', dir: prev.dir === 'asc' ? 'desc' : 'asc' }
                                    : { key: 'heading', dir: 'asc' }
                                )
                              }
                              sx={{
                                width: '100%',
                                justifyContent: 'center',
                                flexDirection: 'column',
                                gap: 0
                              }}
                            >
                              <Box
                                component="span"
                                sx={{
                                  display: 'inline-flex',
                                  flexDirection: 'column',
                                  alignItems: 'center',
                                  lineHeight: 1.15
                                }}
                              >
                                <span>Cash</span>
                                <span>Flow</span>
                              </Box>
                            </TableSortLabel>
                          </TableCell>
                          <TableCell
                            sortDirection={unifiedSort.key === 'supplier' ? unifiedSort.dir : false}
                            sx={{
                              ...UNIFIED_SUPPLIER_COLUMN_HEAD_SX,
                              ...UNIFIED_FLEX_CELL_SX
                            }}
                          >
                            <TableSortLabel
                              active={unifiedSort.key === 'supplier'}
                              direction={unifiedSort.key === 'supplier' ? unifiedSort.dir : 'asc'}
                              onClick={() =>
                                setUnifiedSort((prev) =>
                                  prev.key === 'supplier'
                                    ? { key: 'supplier', dir: prev.dir === 'asc' ? 'desc' : 'asc' }
                                    : { key: 'supplier', dir: 'asc' }
                                )
                              }
                            >
                              Supplier
                            </TableSortLabel>
                          </TableCell>
                          <TableCell
                            sortDirection={unifiedSort.key === 'category' ? unifiedSort.dir : false}
                            sx={{
                              ...UNIFIED_CATEGORY_COLUMN_HEAD_SX,
                              ...UNIFIED_FLEX_CELL_SX
                            }}
                          >
                            <TableSortLabel
                              active={unifiedSort.key === 'category'}
                              direction={unifiedSort.key === 'category' ? unifiedSort.dir : 'asc'}
                              onClick={() =>
                                setUnifiedSort((prev) =>
                                  prev.key === 'category'
                                    ? { key: 'category', dir: prev.dir === 'asc' ? 'desc' : 'asc' }
                                    : { key: 'category', dir: 'asc' }
                                )
                              }
                            >
                              Category
                            </TableSortLabel>
                          </TableCell>
                          <TableCell
                            ref={unifiedVatRateColumnHeaderRef}
                            sortDirection={unifiedSort.key === 'vat' ? unifiedSort.dir : false}
                            sx={{
                              ...UNIFIED_VAT_RATE_COLUMN_HEAD_SX,
                              ...unifiedCompactColSx(UNIFIED_COL.vat),
                              px: '2px !important'
                            }}
                          >
                            <TableSortLabel
                              active={unifiedSort.key === 'vat'}
                              direction={unifiedSort.key === 'vat' ? unifiedSort.dir : 'asc'}
                              onClick={() =>
                                setUnifiedSort((prev) =>
                                  prev.key === 'vat'
                                    ? { key: 'vat', dir: prev.dir === 'asc' ? 'desc' : 'asc' }
                                    : { key: 'vat', dir: 'asc' }
                                )
                              }
                            >
                              VAT
                            </TableSortLabel>
                          </TableCell>
                          <TableCell
                            align="center"
                            sx={{
                              ...UNIFIED_PROP_COLUMN_HEAD_SX,
                              ...unifiedCompactColSx(UNIFIED_COL.prop),
                              whiteSpace: 'nowrap',
                              px: '2px !important'
                            }}
                          >
                            Prop
                          </TableCell>
                          <TableCell
                            align="right"
                            sx={{
                              ...UNIFIED_GREY_HEAD_PLAIN_SX,
                              ...unifiedFitColSx(UNIFIED_COL.actions),
                              whiteSpace: 'nowrap',
                              p: '0 !important',
                              px: '0 !important'
                            }}
                          >
                            Actions
                          </TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {pagedUnifiedRows.length === 0 ? (
                          <TableRow>
                            <TableCell colSpan={useUnifiedOverflowColumns ? 12 : 13}>
                              {loading ||
                              allTransactionsHydrating ||
                              confirmedPayloadHydrating ||
                              unifiedCanonicalServerTotalPending != null
                                ? 'Loading…'
                                : 'No rows match the current filters.'}
                            </TableCell>
                          </TableRow>
                        ) : (
                          pagedUnifiedRows.map((row) => {
                            const tagsLoadedForRow = unifiedTxCategoryTagsLoadedTxIds.has(row.id);
                            const rowTags = unifiedTxCategoryTagsByTxId[row.id];
                            const headingTags = (rowTags || []).filter(
                              (t) => isTopLevelCashflowHeadingName(t.category)
                            );
                            const supplierTags = (() => {
                              const out: typeof rowTags = [];
                              const seen = new Set<string>();
                              (rowTags || []).forEach((t) => {
                                const raw = String(t.supplier || '').trim();
                                if (!raw) return;
                                const key = raw.toLowerCase();
                                if (seen.has(key)) return;
                                seen.add(key);
                                out.push(t);
                              });
                              return out;
                            })();
                            const categoryTags = (rowTags || []).filter(
                              (t) =>
                                t.source !== 'canonical_supplier' &&
                                t.source !== 'canonical_vat' &&
                                String(t.category || '').trim().length > 0 &&
                                !isTopLevelCashflowHeadingName(t.category)
                            );
                            const canonicalVatTags = (rowTags || []).filter(
                              (t) =>
                                t.source === 'canonical_vat' || String(t.uid || '').startsWith('cvt:')
                            );
                            const legacyVatTags = (rowTags || []).filter(
                              (t) =>
                                t.source !== 'canonical_supplier' &&
                                t.source !== 'canonical_vat' &&
                                !String(t.uid || '').startsWith('cvt:') &&
                                String(t.vatRate || '').trim().length > 0
                            );
                            // Prefer first-class VAT chips; fall back to rates denormalized on category tags.
                            const vatTags =
                              canonicalVatTags.length > 0 ? canonicalVatTags : legacyVatTags;
                            const cashFlowIsNa =
                              String(headingTags[0]?.category || '')
                                .trim()
                                .toLowerCase() === 'n/a';
                            return (
                            <TableRow
                              key={row.key}
                              sx={
                                unifiedCommitBusyTxIds.has(row.id) ||
                                unifiedSaveBusyTxIds.has(row.id)
                                  ? (theme) => ({
                                      bgcolor: alpha(theme.palette.primary.main, 0.1),
                                      transition: theme.transitions.create('background-color', {
                                        duration: theme.transitions.duration.shortest
                                      })
                                    })
                                  : unifiedTxTagSyncBusyByTxId[row.id]
                                    ? (theme) => ({
                                        bgcolor: alpha(theme.palette.info.main, 0.12),
                                        transition: theme.transitions.create('background-color', {
                                          duration: theme.transitions.duration.shortest
                                        })
                                      })
                                    : cashFlowIsNa
                                      ? (theme) => ({
                                          // Slightly darker than default white rows — not a muted/disabled wash.
                                          bgcolor:
                                            theme.palette.mode === 'dark'
                                              ? alpha(theme.palette.common.white, 0.04)
                                              : theme.palette.grey[100],
                                          transition: theme.transitions.create('background-color', {
                                            duration: theme.transitions.duration.shortest
                                          })
                                        })
                                      : undefined
                              }
                            >
                                <TableCell
                                  padding="none"
                                  className="unified-col-checkbox"
                                  sx={{
                                    ...unifiedCompactColSx(UNIFIED_COL.checkbox),
                                    px: '0 !important',
                                    py: 0.5,
                                    verticalAlign: 'middle',
                                    textAlign: 'center'
                                  }}
                                >
                                  <input
                                    type="checkbox"
                                  checked={selectedUnifiedRowKeys.includes(row.key)}
                                    onChange={(e) => {
                                      if (e.target.checked) {
                                      setSelectedUnifiedRowKeys((prev) => (prev.includes(row.key) ? prev : [...prev, row.key]));
                                      } else {
                                      setSelectedUnifiedRowKeys((prev) => prev.filter((k) => k !== row.key));
                                      }
                                    }}
                                  />
                                </TableCell>
                              <TableCell
                                align="right"
                                className="unified-col-id"
                                sx={{
                                  ...unifiedFitColSx(unifiedFitColWidths.id),
                                  px: '1px !important'
                                }}
                              >
                                <Stack direction="row" spacing={0.15} alignItems="center" justifyContent="flex-end">
                                  {unifiedTxTagSyncBusyByTxId[row.id] ? (
                                    <CircularProgress size={12} color="info" aria-label="Updating tags" />
                                  ) : null}
                                  <Typography
                                    component="span"
                                    variant="body2"
                                    sx={{
                                      fontSize: '0.75rem',
                                      fontVariantNumeric: 'tabular-nums',
                                      lineHeight: 1.2,
                                      whiteSpace: 'nowrap'
                                    }}
                                  >
                                    {row.id}
                                  </Typography>
                                </Stack>
                              </TableCell>
                              <TableCell
                                align="right"
                                className="unified-col-amount"
                                sx={{
                                  ...unifiedFitColSx(unifiedFitColWidths.amount),
                                  px: '2px !important'
                                }}
                              >
                                {(() => {
                                  const ledgerAmt = unifiedDisplayAmount(row);
                                  const originalBill = resolveUnifiedRowOriginalBill(row);
                                  if (originalBill) {
                                    return (
                                      <Stack alignItems="flex-end" spacing={0} sx={{ lineHeight: 1.15 }}>
                                        <Typography
                                          variant="body2"
                                          component="span"
                                          title="Original (merchant) amount"
                                          sx={{
                                            color: txReviewAmountColor(originalBill.amount),
                                            fontSize: '0.75rem',
                                            fontVariantNumeric: 'tabular-nums',
                                            whiteSpace: 'nowrap'
                                          }}
                                        >
                                          {formatAmount(originalBill.amount, originalBill.currency)}
                                        </Typography>
                                        <Typography
                                          variant="caption"
                                          component="span"
                                          title="Account amount after FX"
                                          sx={{
                                            color: txReviewAmountColor(ledgerAmt),
                                            fontSize: '0.65rem',
                                            fontVariantNumeric: 'tabular-nums',
                                            whiteSpace: 'nowrap',
                                            opacity: 0.9
                                          }}
                                        >
                                          {formatAmount(ledgerAmt, row.currency)}
                                        </Typography>
                                      </Stack>
                                    );
                                  }
                                  return (
                                    <Typography
                                      variant="body2"
                                      component="span"
                                      sx={{
                                        color: txReviewAmountColor(ledgerAmt),
                                        fontSize: '0.75rem',
                                        fontVariantNumeric: 'tabular-nums',
                                        whiteSpace: 'nowrap'
                                      }}
                                    >
                                      {formatAmount(ledgerAmt, row.currency)}
                                    </Typography>
                                  );
                                })()}
                              </TableCell>
                              <TableCell className="unified-col-date" sx={UNIFIED_TABLE_DATE_CELL_SX}>
                                <Typography
                                  component="span"
                                  variant="body2"
                                  noWrap
                                  title={formatUnifiedReviewTableDateTooltip(row.date)}
                                  sx={{
                                    fontSize: '0.75rem',
                                    fontVariantNumeric: 'tabular-nums',
                                    lineHeight: 1.2,
                                    display: 'block'
                                  }}
                                >
                                  {formatUnifiedReviewTableDate(row.date)}
                                </Typography>
                              </TableCell>
                              <TableCell
                                sx={{
                                  ...UNIFIED_FLEX_CELL_SX,
                                  verticalAlign: 'middle'
                                }}
                              >
                                {(() => {
                                  const reference = String(row.reference || '').trim();
                                  const hasReference = Boolean(reference);
                                  const urlCandidates: string[] = [];
                                  const pushUrl = (raw: unknown) => {
                                    const href = String(raw || '').trim();
                                    if (!href.startsWith('http')) return;
                                    if (urlCandidates.some((u) => u.replace(/\/$/, '') === href.replace(/\/$/, ''))) {
                                      return;
                                    }
                                    if (urlCandidates.length < 3) urlCandidates.push(href);
                                  };
                                  pushUrl(row.merchant_likely_url);
                                  (row.merchant_url_alternatives || []).forEach((item) => pushUrl(item));
                                  return (
                                    <Stack spacing={0} sx={{ minWidth: 0, maxWidth: '100%' }}>
                                      <Typography
                                        variant="body2"
                                        noWrap
                                        title={row.description || '—'}
                                        sx={{ lineHeight: 1.25, minWidth: 0 }}
                                      >
                                        {row.description || '—'}
                                      </Typography>
                                      {hasReference ? (
                                        <Typography
                                          variant="caption"
                                          noWrap
                                          color="text.secondary"
                                          title={reference}
                                          sx={{ display: 'block', lineHeight: 1.2, minWidth: 0 }}
                                        >
                                          {reference}
                                        </Typography>
                                      ) : null}
                                      {urlCandidates.map((href) => (
                                        <TxExternalLink
                                          key={href}
                                          href={href}
                                          title={href}
                                          variant="caption"
                                          sx={{
                                            display: 'block',
                                            minWidth: 0,
                                            overflow: 'hidden',
                                            textOverflow: 'ellipsis',
                                            whiteSpace: 'nowrap',
                                            lineHeight: 1.2
                                          }}
                                        >
                                          {href}
                                        </TxExternalLink>
                                      ))}
                                    </Stack>
                                  );
                                })()}
                              </TableCell>
                              <TableCell
                                align="center"
                                sx={{
                                  ...unifiedFitColSx(UNIFIED_COL.who),
                                  px: '2px !important',
                                  verticalAlign: 'middle'
                                }}
                              >
                                {(() => {
                                  const fullName = cardholderDisplayNameFromUnifiedRow(row);
                                  const who = unifiedWhoInitialsFromCardholder(fullName);
                                  return (
                                    <Chip
                                      size="small"
                                      label={who}
                                      variant="filled"
                                      title={whoTooltipLabel(fullName, who)}
                                      sx={{
                                        ...whoChipSx(who),
                                        height: 22,
                                        maxWidth: 'none',
                                        '& .MuiChip-label': {
                                          overflow: 'visible',
                                          textOverflow: 'clip',
                                          px: 0.75,
                                          fontWeight: 700,
                                          fontSize: '0.7rem'
                                        }
                                      }}
                                    />
                                  );
                                })()}
                              </TableCell>
                              {!useUnifiedOverflowColumns && (
                                <TableCell
                                  sx={{
                                    ...unifiedFitColSx(UNIFIED_COL.account),
                                    px: '2px !important',
                                    textAlign: 'center'
                                  }}
                                >
                                  <Typography
                                    variant="body2"
                                    component="span"
                                    title={formatRevolutAccountLabel(row.account_source)}
                                    sx={{ fontSize: '0.75rem', whiteSpace: 'nowrap' }}
                                  >
                                    {formatRevolutAccountLabel(row.account_source)}
                                  </Typography>
                                </TableCell>
                              )}
                              <TableCell
                                sx={{
                                  ...unifiedFitColSx(unifiedFitColWidths.cashFlow),
                                  px: '2px !important',
                                  verticalAlign: 'middle'
                                }}
                              >
                                {unifiedEditingTxIds.includes(row.id) ? (
                                  <FormControl size="small" fullWidth>
                                  <Select
                                      value={headingSelectValueForTx(
                                        row.id,
                                        selectedHeadingByTx,
                                        selectedGranularByTx,
                                        headingCategories,
                                        row.amount,
                                        'unified'
                                      )}
                                    displayEmpty
                                      disabled={row.source_state === 'confirmed'}
                                    onChange={(e) => {
                                      const raw = e.target.value;
                                        setSelectedHeadingByTx((prev) => ({
                                        ...prev,
                                          [row.id]: !selectHasChoice(raw) ? '' : Number(raw)
                                      }));
                                    }}
                                  >
                                    <MenuItem value="">
                                      <em>Select</em>
                                    </MenuItem>
                                    {headingCategories.map((c) => (
                                        <MenuItem key={`u-row-head-${row.id}-${c.id}`} value={c.id}>
                                        {formatCashflowInOutHeadingLabel(c.name)}
                                      </MenuItem>
                                    ))}
                                  </Select>
                                  </FormControl>
                                ) : headingTags.length > 0 ? (
                                  <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
                                    {headingTags.map((tag) => (
                                      <Chip
                                        key={`${row.id}-heading-${tag.uid}`}
                                        size="small"
                                        label={formatCashflowInOutHeadingLabel(tag.category)}
                                        variant="filled"
                                        sx={{
                                          ...incomeExpenseChipSx(tag.category),
                                          maxWidth: 'none',
                                          '& .MuiChip-label': { overflow: 'visible', textOverflow: 'clip' }
                                        }}
                                        onDelete={() => requestDeleteUnifiedTag(row.id, tag, 'heading')}
                                        deleteIcon={<CloseIcon fontSize="small" />}
                                        disabled={loading || unifiedActionBusy || deletingUnifiedTagUid === tag.uid}
                                      />
                                    ))}
                                  </Stack>
                                ) : !tagsLoadedForRow ? (
                                  <Typography variant="body2" color="text.secondary" component="span">
                                    …
                                  </Typography>
                                ) : (
                                  <Typography variant="body2" color="text.secondary" component="span">
                                    —
                                  </Typography>
                                )}
                                </TableCell>
                              <TableCell sx={UNIFIED_FLEX_CELL_SX}>
                                {unifiedEditingTxIds.includes(row.id) ? (
                                  <FormControl size="small" fullWidth sx={{ minWidth: 0 }}>
                                    <Select
                                      value={
                                        row.id in selectedSupplierByTx
                                          ? selectedSupplierByTx[row.id]
                                          : ((row.supplier || '').trim())
                                      }
                                      displayEmpty
                                      onChange={(e) =>
                                        setSelectedSupplierByTx((prev) => ({
                                          ...prev,
                                          [row.id]: String(e.target.value || '')
                                        }))
                                      }
                                    >
                                      <MenuItem value="">
                                        <em>Auto / unchanged</em>
                                      </MenuItem>
                                      {Array.from(
                                        new Set([
                                          ...(((row.id in selectedSupplierByTx
                                            ? selectedSupplierByTx[row.id]
                                            : (row.supplier || '').trim()) || '').trim()
                                            ? [((row.id in selectedSupplierByTx
                                                ? selectedSupplierByTx[row.id]
                                                : (row.supplier || '').trim()) || '').trim()]
                                            : []),
                                          ...canonicalSupplierOptions
                                        ].filter((s) => (s || '').trim().length > 0))
                                      ).map((supplier) => (
                                        <MenuItem key={`all-reviews-supplier-${row.id}-${supplier}`} value={supplier}>
                                          {supplier}
                                        </MenuItem>
                                      ))}
                                    </Select>
                                  </FormControl>
                                ) : supplierTags.length > 0 ? (
                                  <Stack
                                    direction="row"
                                    spacing={0.5}
                                    flexWrap="nowrap"
                                    useFlexGap
                                    sx={{ maxWidth: '100%', overflow: 'hidden' }}
                                  >
                                    {supplierTags.map((tag) => (
                                      <Chip
                                        key={`${row.id}-supplier-${tag.uid || tag.supplier}`}
                                        size="small"
                                        variant="outlined"
                                        label={tag.supplier}
                                        title={tag.supplier}
                                        sx={UNIFIED_CHIP_TRUNC_SX}
                                        onClick={
                                          row.supplier_id != null
                                            ? () => openSuppliersTabForId(row.supplier_id)
                                            : undefined
                                        }
                                        onDelete={
                                          String(tag.uid || '').trim()
                                            ? () => requestDeleteUnifiedTag(row.id, tag, 'supplier')
                                            : undefined
                                        }
                                        deleteIcon={<CloseIcon fontSize="small" />}
                                        disabled={loading || unifiedActionBusy || deletingUnifiedTagUid === tag.uid}
                                      />
                                    ))}
                                  </Stack>
                                ) : !tagsLoadedForRow ? (
                                  <Typography variant="body2" color="text.secondary" component="span">
                                    …
                                  </Typography>
                                ) : (
                                  <Typography variant="body2" color="text.secondary" component="span">
                                    —
                                  </Typography>
                                )}
                              </TableCell>
                              <TableCell sx={UNIFIED_FLEX_CELL_SX}>
                                {(() => {
                                  const inEdit = unifiedEditingTxIds.includes(row.id);
                                  const taggedCategoryIds = new Set<number>(
                                    categoryTags
                                      .map(
                                        (t) =>
                                          categories.find(
                                            (c) => (c.name || '').trim() === (t.category || '').trim()
                                          )?.id
                                      )
                                      .filter((id): id is number => id != null && Number.isFinite(id) && id > 0)
                                  );
                                  const chipsCell =
                                    categoryTags.length > 0 ? (
                                      <Stack
                                        direction="row"
                                        spacing={0.5}
                                        flexWrap="nowrap"
                                        useFlexGap
                                        sx={{ maxWidth: '100%', overflow: 'hidden' }}
                                      >
                                        {categoryTags.map((tag) => (
                                          <Chip
                                            key={`${row.id}-category-${tag.uid}`}
                                            size="small"
                                            label={tag.category}
                                            title={tag.category}
                                            variant="filled"
                                            sx={{ ...categoryChipSx(tag.category), ...UNIFIED_CHIP_TRUNC_SX }}
                                            onDelete={() => requestDeleteUnifiedTag(row.id, tag, 'category')}
                                            deleteIcon={<CloseIcon fontSize="small" />}
                                            disabled={
                                              loading || unifiedActionBusy || deletingUnifiedTagUid === tag.uid
                                            }
                                          />
                                        ))}
                                      </Stack>
                                    ) : tagsLoadedForRow ? (
                                      <Typography variant="body2" color="text.secondary" noWrap>
                                        —
                                      </Typography>
                                    ) : (
                                      <Typography variant="body2" color="text.secondary" noWrap>
                                        …
                                      </Typography>
                                    );
                                  if (!inEdit) return chipsCell;
                                  return (
                                    <Stack spacing={0.75} sx={{ minWidth: 0 }}>
                                      {chipsCell}
                                      <FormControl
                                        size="small"
                                        fullWidth
                                        disabled={
                                          row.source_state === 'confirmed' ||
                                          unifiedActionBusy ||
                                          unifiedTxTagSyncBusyByTxId[row.id]
                                        }
                                      >
                                        <Select
                                          key={`u-row-cat-add-${row.id}-${categoryTags.length}`}
                                          value=""
                                          displayEmpty
                                          onChange={(e) => {
                                            const raw = e.target.value;
                                            if (String(raw) === ADD_NEW_CATEGORY_SELECT_VALUE) {
                                              openCategoryCrudCreateForManualRow(row.id);
                                              return;
                                            }
                                            if (!selectHasChoice(raw)) return;
                                            const nid = Number(raw);
                                            if (!Number.isFinite(nid) || nid <= 0) return;
                                            if (taggedCategoryIds.has(nid)) return;
                                            const catName =
                                              granularCategories.find((c) => c.id === nid)?.name ||
                                              `Category #${nid}`;
                                            void addManualCategoryTagToRow(row.id, nid, catName);
                                          }}
                                        >
                                          <MenuItem value="">
                                            <em>Add category…</em>
                                          </MenuItem>
                                          {granularCategories
                                            .filter((c) => !taggedCategoryIds.has(c.id))
                                            .map((c) => (
                                              <MenuItem
                                                key={`u-row-cat-${row.id}-${c.id}`}
                                                value={c.id}
                                              >
                                                {c.name}
                                              </MenuItem>
                                            ))}
                                          <Divider sx={{ my: 0.5 }} />
                                          <MenuItem value={ADD_NEW_CATEGORY_SELECT_VALUE}>
                                            Add new category…
                                          </MenuItem>
                                        </Select>
                                      </FormControl>
                                    </Stack>
                                  );
                                })()}
                                </TableCell>
                              <TableCell
                                sx={{
                                  ...unifiedCompactColSx(UNIFIED_COL.vat),
                                  px: '2px !important'
                                }}
                              >
                                {unifiedEditingTxIds.includes(row.id) ? (
                                  <FormControl
                                    size="small"
                                    fullWidth
                                    sx={{ minWidth: 0 }}
                                    disabled={
                                      row.source_state === 'confirmed' ||
                                      unifiedActionBusy ||
                                      unifiedTxTagSyncBusyByTxId[row.id]
                                    }
                                  >
                                    <Select
                                      value={
                                        row.id in selectedVatRateByTx
                                          ? selectedVatRateByTx[row.id]
                                          : (canonicalizeVatRate(row.vat_rate) || '')
                                      }
                                      displayEmpty
                                      onChange={(e) => {
                                        void applyManualVatRateToRow(row.id, String(e.target.value || ''));
                                      }}
                                    >
                                      <MenuItem value="">
                                        <em>Select VAT rate</em>
                                      </MenuItem>
                                      {vatRatePickOptions.map((rate) => (
                                        <MenuItem key={`all-review-vat-${row.id}-${rate.label}`} value={rate.value}>
                                          {rate.label}
                                        </MenuItem>
                                      ))}
                                    </Select>
                                  </FormControl>
                                ) : vatTags.length > 0 ? (
                                    <Stack
                                      direction="row"
                                      spacing={0.5}
                                      flexWrap="nowrap"
                                      useFlexGap
                                      sx={{ maxWidth: '100%', overflow: 'hidden' }}
                                    >
                                      {vatTags.map((tag) => (
                                        <Chip
                                          key={`${row.id}-vat-${tag.uid}`}
                                          size="small"
                                          variant="outlined"
                                          sx={UNIFIED_CHIP_TRUNC_SX}
                                          label={(() => {
                                            const raw = String(tag.vatRate || '').trim();
                                            if (!raw || raw.toLowerCase() === 'none' || raw.toLowerCase() === 'n/a') {
                                              return '—';
                                            }
                                            return (canonicalizeVatRate(raw) || raw);
                                          })()}
                                          onDelete={() => requestDeleteUnifiedTag(row.id, tag, 'vat')}
                                          deleteIcon={<CloseIcon fontSize="small" />}
                                          disabled={loading || unifiedActionBusy || deletingUnifiedTagUid === tag.uid}
                                        />
                                      ))}
                                    </Stack>
                                  ) : !tagsLoadedForRow ? (
                                    <Typography variant="body2" color="text.secondary" component="span">
                                      …
                                    </Typography>
                                  ) : (
                                    <Typography variant="body2" color="text.secondary" component="span">
                                      —
                                    </Typography>
                                  )}
                              </TableCell>
                              <TableCell
                                align="center"
                                sx={{
                                  ...unifiedCompactColSx(UNIFIED_COL.prop),
                                  px: '2px !important'
                                }}
                              >
                                {(() => {
                                  const propChips = unifiedRowPropertyChips(row);
                                  const draftIds =
                                    row.id in selectedPropertyIdsByTx
                                      ? selectedPropertyIdsByTx[row.id]
                                      : unifiedRowPropertyIds(row);
                                  const orphanChips = propChips.filter(
                                    (c) => !txPropOptions.some((o) => o.id === c.id)
                                  );
                                  if (unifiedEditingTxIds.includes(row.id)) {
                                    return (
                                      <FormControl
                                        size="small"
                                        fullWidth
                                        disabled={
                                          unifiedActionBusy ||
                                          unifiedBulkLock ||
                                          unifiedPropBusyTxId === row.id
                                        }
                                      >
                                        <Select
                                          multiple
                                          value={draftIds.map(String)}
                                          displayEmpty
                                          aria-label="Property tags"
                                          renderValue={(selected) => {
                                            const ids = (selected as string[])
                                              .map((s) => Number(s))
                                              .filter((n) => Number.isFinite(n) && n > 0);
                                            if (!ids.length) return <em>None</em>;
                                            return ids
                                              .map((id) => {
                                                const opt = txPropOptions.find((o) => o.id === id);
                                                const orphan = propChips.find((c) => c.id === id);
                                                return opt?.nickname || orphan?.nickname || String(id);
                                              })
                                              .join(', ');
                                          }}
                                          onChange={(e) => {
                                            const raw = e.target.value;
                                            const asArr = Array.isArray(raw)
                                              ? raw.map(String)
                                              : [String(raw ?? '')];
                                            const nextIds = asArr
                                              .map((s) => Number(s))
                                              .filter((n) => Number.isFinite(n) && n > 0);
                                            setSelectedPropertyIdsByTx((prev) => ({
                                              ...prev,
                                              [row.id]: nextIds
                                            }));
                                            void saveUnifiedRowProperties(row, nextIds);
                                          }}
                                          sx={{
                                            fontSize: '0.75rem',
                                            '& .MuiSelect-select': {
                                              py: 0.5,
                                              px: 0.75
                                            }
                                          }}
                                        >
                                          {txPropOptions.map((opt) => (
                                            <MenuItem key={`tx-prop-${row.id}-${opt.id}`} value={String(opt.id)}>
                                              {opt.nickname}
                                            </MenuItem>
                                          ))}
                                          {orphanChips.map((c) => (
                                            <MenuItem key={`tx-prop-orphan-${row.id}-${c.id}`} value={String(c.id)}>
                                              {c.nickname}
                                            </MenuItem>
                                          ))}
                                        </Select>
                                      </FormControl>
                                    );
                                  }
                                  if (propChips.length > 0) {
                                    return (
                                      <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap justifyContent="center">
                                        {propChips.map((chip) => (
                                          <Chip
                                            key={`${row.id}-prop-${chip.id}`}
                                            size="small"
                                            variant="filled"
                                            label={chip.nickname}
                                            title={chip.nickname}
                                            sx={{
                                              ...propertyNicknameChipSx(chip.nickname),
                                              ...UNIFIED_CHIP_TRUNC_SX,
                                            }}
                                            onDelete={() => {
                                              const next = unifiedRowPropertyIds(row).filter((id) => id !== chip.id);
                                              void saveUnifiedRowProperties(row, next);
                                            }}
                                            deleteIcon={<CloseIcon fontSize="small" />}
                                            disabled={
                                              loading ||
                                              unifiedActionBusy ||
                                              unifiedBulkLock ||
                                              unifiedPropBusyTxId === row.id
                                            }
                                          />
                                        ))}
                                      </Stack>
                                    );
                                  }
                                  return (
                                    <Typography variant="body2" color="text.secondary" component="span">
                                      —
                                    </Typography>
                                  );
                                })()}
                              </TableCell>
                                <TableCell
                                  align="right"
                                  sx={{
                                    ...unifiedFitColSx(UNIFIED_COL.actions),
                                    whiteSpace: 'nowrap',
                                    p: '0 !important',
                                    verticalAlign: 'middle'
                                  }}
                                >
                                <Stack
                                  direction="row"
                                  spacing={0}
                                  justifyContent="flex-end"
                                  alignItems="center"
                                  flexWrap="nowrap"
                                  useFlexGap
                                  sx={{
                                    display: 'inline-flex',
                                    width: UNIFIED_ACTIONS_COL_PX,
                                    maxWidth: UNIFIED_ACTIONS_COL_PX,
                                    justifyContent: 'flex-end',
                                    gap: 0,
                                    '& .MuiIconButton-root': {
                                      p: 0,
                                      width: UNIFIED_ACTION_BTN_PX,
                                      height: UNIFIED_ACTION_BTN_PX
                                    },
                                    '& .MuiSvgIcon-root': { fontSize: '1.15rem' },
                                    '& .MuiChip-root': {
                                      mx: 0,
                                      height: UNIFIED_ACTION_CHIP_PX,
                                      minWidth: UNIFIED_ACTION_CHIP_PX,
                                      '& .MuiChip-label': { px: 0.4, fontSize: '0.75rem' }
                                    }
                                  }}
                                >
                                  <Tooltip
                                    title={
                                      unifiedRowHasComment(row)
                                        ? 'Edit transaction comment'
                                        : 'Add transaction comment'
                                    }
                                  >
                                    <span>
                                      <IconButton
                                        type="button"
                                        size="small"
                                        aria-label={
                                          unifiedRowHasComment(row)
                                            ? 'Edit transaction comment'
                                            : 'Add transaction comment'
                                        }
                                        color={unifiedRowHasComment(row) ? 'primary' : 'default'}
                                        onClick={() => openTxCommentModal(row)}
                                        disabled={unifiedActionBusy || unifiedBulkLock}
                                      >
                                        <Badge
                                          color="primary"
                                          variant="dot"
                                          invisible={!unifiedRowHasComment(row)}
                                          overlap="circular"
                                        >
                                          {unifiedRowHasComment(row) ? (
                                            <CommentIcon fontSize="small" />
                                          ) : (
                                            <CommentOutlinedIcon fontSize="small" />
                                          )}
                                        </Badge>
                                      </IconButton>
                                    </span>
                                  </Tooltip>
                                  <Tooltip
                                    title={
                                      unifiedRowIsFlagged(row)
                                        ? 'Clear investigation flag'
                                        : 'Flag to look into later'
                                    }
                                  >
                                    <span>
                                      <IconButton
                                        type="button"
                                        size="small"
                                        aria-label={
                                          unifiedRowIsFlagged(row)
                                            ? 'Clear investigation flag'
                                            : 'Flag to look into later'
                                        }
                                        color={unifiedRowIsFlagged(row) ? 'error' : 'default'}
                                        onClick={() => void toggleUnifiedRowFlag(row)}
                                        disabled={
                                          unifiedActionBusy ||
                                          unifiedBulkLock ||
                                          unifiedFlagBusyTxId === row.id
                                        }
                                      >
                                        {unifiedFlagBusyTxId === row.id ? (
                                          <CircularProgress size={18} color="inherit" />
                                        ) : unifiedRowIsFlagged(row) ? (
                                          <FlagIcon fontSize="small" />
                                        ) : (
                                          <FlagOutlinedIcon fontSize="small" />
                                        )}
                                      </IconButton>
                                    </span>
                                  </Tooltip>
                                  <Tooltip title="View similar transactions">
                                    <span>
                                      <IconButton
                                        type="button"
                                        size="small"
                                        onClick={() => openSimilarTransactionsModal(row)}
                                        disabled={unifiedActionBusy || unifiedBulkLock}
                                      >
                                        <VisibilityIcon fontSize="small" />
                                      </IconButton>
                                    </span>
                                  </Tooltip>
                                  <Tooltip title="Open Revolut JSON in the right panel (switch rows without closing)">
                                    <span>
                                      <IconButton
                                        size="small"
                                        onClick={() => void openUnifiedRowJsonViewer(row)}
                                        disabled={unifiedActionBusy || unifiedBulkLock}
                                      >
                                        <DataObjectIcon fontSize="small" />
                                      </IconButton>
                                    </span>
                                  </Tooltip>
                                  <Tooltip
                                    title={
                                      'Generate pending classification and supplier rule drafts scoped to this transaction so a full re-run can fill missing tags (review under Rules → Proposed, then use Re-run all rules).'
                                    }
                                  >
                                    <span>
                                      <IconButton
                                        size="small"
                                        aria-label="Generate isolated draft rules for this transaction"
                                        disabled={
                                          unifiedActionBusy ||
                                          unifiedBulkLock ||
                                          unifiedIsolateRulesBusyTxId === row.id ||
                                          unifiedActionBusyTxId === row.id ||
                                          unifiedCommitBusyTxIds.has(row.id) ||
                                          unifiedSaveBusyTxIds.has(row.id)
                                        }
                                        onClick={() => void generateIsolatedRuleProposalsForUnifiedRow(row)}
                                      >
                                        {unifiedIsolateRulesBusyTxId === row.id ? (
                                          <CircularProgress size={18} color="inherit" />
                                        ) : (
                                          <PostAddIcon fontSize="small" />
                                        )}
                                      </IconButton>
                                    </span>
                                  </Tooltip>
                                  <Tooltip title="Remove all tags on this transaction, then re-run all rules from scratch to apply a fresh tag set.">
                                    <span>
                                      <IconButton
                                        size="small"
                                        color="primary"
                                        aria-label="Re-run all rules"
                                        disabled={
                                          unifiedBulkLock ||
                                          unifiedActionBusyTxId === row.id ||
                                          unifiedIsolateRulesBusyTxId === row.id ||
                                          unifiedCommitBusyTxIds.has(row.id) ||
                                          unifiedSaveBusyTxIds.has(row.id)
                                        }
                                        onClick={() => void runUnifiedRowRerunAllRules(row)}
                                      >
                                        {unifiedActionBusyTxId === row.id ? (
                                          <CircularProgress size={18} color="inherit" />
                                        ) : (
                                          <PlayArrowIcon fontSize="small" />
                                        )}
                                      </IconButton>
                                    </span>
                                  </Tooltip>
                                  {(() => {
                                    const cmp = row.probabilistic_compare;
                                    const agreeCount = cmp?.agree_count;
                                    const hasPred = Boolean(cmp?.has_prediction);
                                    const label = hasPred && agreeCount != null ? String(agreeCount) : '—';
                                    const tip = !hasPred
                                      ? 'No probabilistic prediction for this TX yet'
                                      : `Rules vs Probabilistic: ${agreeCount}/4 dimensions agree. Click to compare and Accept.`;
                                    return (
                                      <Tooltip title={tip}>
                                        <span>
                                          <Chip
                                            size="small"
                                            clickable={hasPred}
                                            color={probabilisticAgreeChipColor(agreeCount)}
                                            label={label}
                                            aria-label={`Rules vs Probabilistic agreement ${label}`}
                                            disabled={unifiedActionBusy || unifiedBulkLock || !hasPred}
                                            onClick={() => {
                                              if (!hasPred) return;
                                              setProbabilisticCompareRow(row);
                                            }}
                                            sx={{
                                              minWidth: UNIFIED_ACTION_CHIP_PX,
                                              width: UNIFIED_ACTION_CHIP_PX,
                                              height: UNIFIED_ACTION_CHIP_PX,
                                              fontWeight: 700,
                                              '& .MuiChip-label': { px: 0.35 }
                                            }}
                                          />
                                        </span>
                                      </Tooltip>
                                    );
                                  })()}
                                  <Tooltip
                                    title={
                                      unifiedEditingTxIds.includes(row.id)
                                        ? 'Cancel editing — discard unsaved dropdown changes.'
                                        : 'Edit Cash flow (In/Out), supplier, category, VAT, and Prop — then save.'
                                    }
                                  >
                                    <span>
                                      {unifiedEditingTxIds.includes(row.id) ? (
                                        <IconButton
                                          size="small"
                                          color="error"
                                          aria-label="Cancel editing without saving"
                                          onClick={() => cancelUnifiedRowEdit(row)}
                                          disabled={unifiedActionBusy || unifiedBulkLock}
                                        >
                                          <CloseIcon fontSize="small" />
                                        </IconButton>
                                      ) : (
                                        <IconButton
                                          size="small"
                                          aria-label="Edit tags"
                                          onClick={() => setRowEditMode(row, true)}
                                          disabled={unifiedActionBusy || unifiedBulkLock}
                                        >
                                          <EditIcon fontSize="small" />
                                        </IconButton>
                                      )}
                                    </span>
                                  </Tooltip>
                                  {(unifiedEditingTxIds.includes(row.id) ||
                                    unifiedSaveBusyTxIds.has(row.id)) && (
                                    <Tooltip title="Save tag edits (category / supplier / VAT) on this row. Does not commit — use the commit icon or Commit selected.">
                                      <span>
                                        <IconButton
                                          size="small"
                                          aria-label="Save tag edits on this row"
                                          color="primary"
                                          disabled={unifiedActionBusy || unifiedBulkLock}
                                          sx={
                                            unifiedSaveBusyTxIds.has(row.id) &&
                                            (unifiedActionBusy || unifiedBulkLock)
                                              ? { '&.Mui-disabled': { opacity: 1 } }
                                              : undefined
                                          }
                                          onClick={() => void runUnifiedRowSave(row)}
                                        >
                                          {unifiedSaveBusyTxIds.has(row.id) ? (
                                            <CircularProgress size={18} color="inherit" />
                                          ) : (
                                            <SaveOutlinedIcon fontSize="small" />
                                          )}
                                        </IconButton>
                                      </span>
                                    </Tooltip>
                                  )}
                                  {(() => {
                                    const rowCommitted = isUnifiedRowCommitted(row);
                                    if (rowCommitted) return null;
                                    const commitBlock = unifiedRowCommitBlockedMessage(
                                      row,
                                      unifiedTxCategoryTagsByTxId[row.id]
                                    );
                                    const commitBusy = unifiedCommitBusyTxIds.has(row.id);
                                    return (
                                      <Tooltip
                                        title={
                                          commitBlock ||
                                          'Commit this transaction (Status must be No Tags Missing). Save any tag edits first.'
                                        }
                                      >
                                        <span>
                                          <IconButton
                                            size="small"
                                            aria-label="Commit this row"
                                            color="success"
                                            disabled={
                                              Boolean(commitBlock) ||
                                              unifiedActionBusy ||
                                              unifiedBulkLock ||
                                              unifiedSaveBusyTxIds.has(row.id)
                                            }
                                            sx={
                                              commitBusy && (unifiedActionBusy || unifiedBulkLock)
                                                ? { '&.Mui-disabled': { opacity: 1 } }
                                                : undefined
                                            }
                                            onClick={() => void runUnifiedRowCommit(row)}
                                          >
                                            {commitBusy ? (
                                              <CircularProgress size={18} color="inherit" />
                                            ) : (
                                              <CheckCircleOutlineIcon fontSize="small" />
                                            )}
                                          </IconButton>
                                        </span>
                                      </Tooltip>
                                    );
                                  })()}
                                  <Tooltip title="Open row details (includes completeness Status)">
                                    <span>
                                      <IconButton
                                        type="button"
                                        size="small"
                                        aria-label="Open row details"
                                        onClick={() => {
                                          setUnifiedMoreRow(row);
                                          // Row details render above Quickview tabs; keep Rules selected
                                          // so the panel matches the old dedicated View/More affordance.
                                          setRulesQuickviewMainTab('rules');
                                          setRulesQuickviewOpen(true);
                                        }}
                                        disabled={unifiedActionBusy || unifiedBulkLock}
                                      >
                                        <InfoOutlinedIcon fontSize="small" />
                                      </IconButton>
                                    </span>
                                  </Tooltip>
                                </Stack>
                                </TableCell>
                              </TableRow>
                            );
                          })
                        )}
                      </TableBody>
                    </Table>
                    {isUnifiedRowsPerPageAll(unifiedRowsPerPage) &&
                    visibleUnifiedRowsCount < sortedUnifiedRows.length ? (
                      <Box
                        ref={unifiedTableSentinelRef}
                        sx={{ height: 24, width: '100%' }}
                        aria-hidden
                      />
                    ) : null}
                  </TableContainer>
                  </Box>
                  <TablePagination
                    component="div"
                    count={sortedUnifiedRows.length}
                    page={unifiedPage}
                    onPageChange={(_e, nextPage) => setUnifiedPage(nextPage)}
                    rowsPerPage={unifiedRowsPerPage}
                    onRowsPerPageChange={(e) => {
                      const next = Number(e.target.value || 200);
                      setUnifiedRowsPerPage(next);
                      setUnifiedPage(0);
                    }}
                    rowsPerPageOptions={UNIFIED_ROWS_PER_PAGE_OPTIONS}
                    labelDisplayedRows={unifiedPaginationLabel}
                    sx={{
                      border: 0,
                      p: 0,
                      m: 0,
                      '& .MuiToolbar-root': { minHeight: 40, alignItems: 'center' },
                      '& .MuiTablePagination-toolbar': {
                        minHeight: 40,
                        alignItems: 'center',
                        flexWrap: 'nowrap'
                      },
                      '& .MuiTablePagination-selectLabel': {
                        mb: 0,
                        display: 'inline-flex',
                        alignItems: 'center',
                        lineHeight: 1.5,
                        minHeight: 32
                      },
                      '& .MuiTablePagination-displayedRows': {
                        mb: 0,
                        display: 'inline-flex',
                        alignItems: 'center',
                        lineHeight: 1.5,
                        minHeight: 32
                      },
                      '& .MuiTablePagination-select': {
                        mb: 0,
                        alignSelf: 'center',
                        display: 'inline-flex',
                        alignItems: 'center',
                        '& .MuiInputBase-root': {
                          marginTop: 0,
                          marginBottom: 0,
                          minHeight: 32,
                          alignItems: 'center'
                        },
                        '& .MuiSelect-select': {
                          display: 'inline-flex',
                          alignItems: 'center',
                          minHeight: 32,
                          lineHeight: 1.5,
                          paddingTop: 4,
                          paddingBottom: 4,
                          boxSizing: 'border-box'
                        }
                      },
                      '& .MuiTablePagination-input': { alignSelf: 'center' },
                      '& .MuiTablePagination-toolbar .MuiInputBase-root': {
                        alignItems: 'center',
                        marginTop: 0,
                        marginBottom: 0
                      },
                      '& .MuiTablePagination-actions': {
                        alignSelf: 'center',
                        ...(isUnifiedRowsPerPageAll(unifiedRowsPerPage) ? { display: 'none' } : {})
                      }
                    }}
                  />
                </>
              ) : tabIndex === CLASSIFICATION_VISIBLE_TAB_KEYS.indexOf('vat-conflicts') ? (
                <>
                  <Stack
                    direction={{ xs: 'column', md: 'row' }}
                    spacing={1}
                    sx={{ mb: 1 }}
                    alignItems={{ md: 'center' }}
                    flexWrap="wrap"
                    useFlexGap
                  >
                    <ClearableSearchTextField
                      size="small"
                      label="Text search"
                      placeholder="Supplier, description, reference, VAT text…"
                      value={vatConflictSearch}
                      onChange={setVatConflictSearch}
                      sx={{ minWidth: 240 }}
                    />
                    <FormControl size="small" sx={{ minWidth: 160 }}>
                      <InputLabel id="vat-conflict-account-label">Account</InputLabel>
                      <Select
                        labelId="vat-conflict-account-label"
                        label="Account"
                        value={vatConflictAccount}
                        onChange={(e) => setVatConflictAccount(e.target.value as 'all' | 'ireland' | 'uk')}
                      >
                        <MenuItem value="all">All accounts</MenuItem>
                        <MenuItem value="ireland">Revolut IRE</MenuItem>
                        <MenuItem value="uk">Revolut UK</MenuItem>
                      </Select>
                    </FormControl>
                    <TextField
                      size="small"
                      label="Min amount"
                      value={vatConflictAmtMin}
                      onChange={(e) => setVatConflictAmtMin(e.target.value)}
                      sx={{ width: 120 }}
                      inputProps={{ inputMode: 'decimal' }}
                    />
                    <TextField
                      size="small"
                      label="Max amount"
                      value={vatConflictAmtMax}
                      onChange={(e) => setVatConflictAmtMax(e.target.value)}
                      sx={{ width: 120 }}
                      inputProps={{ inputMode: 'decimal' }}
                    />
                  </Stack>
                  {vatConflictFiltersActive ? (
                    <Stack
                      direction={{ xs: 'column', sm: 'row' }}
                      spacing={1}
                      sx={{ mb: 1 }}
                      alignItems={{ sm: 'center' }}
                      flexWrap="wrap"
                      useFlexGap
                    >
                      <Button
                        size="small"
                        variant="outlined"
                        disabled={!vatConflictFiltersActive}
                        onClick={resetVatConflictFilters}
                      >
                        Clear all filters
                      </Button>
                      <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap>
                        {vatConflictFilterPills.map((pill) => (
                          <Chip
                            key={`vat-conflict-filter-pill-${pill.key}`}
                            size="small"
                            label={pill.label}
                            onDelete={pill.onDelete}
                            variant="outlined"
                          />
                        ))}
                      </Stack>
                    </Stack>
                  ) : null}
                  {vatConflictFiltersActive ? (
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                      Showing {filteredVatConflictRows.length} of {vatConflictRowsEffective.length} row(s)
                      matching filters.
                      {filteredVatConflictRows.length === 0
                        ? ' Adjust or clear filters to see rows again.'
                        : ''}
                    </Typography>
                  ) : null}
                  {vatConflictSelectedCount > 0 ? (
                    <Stack direction="row" spacing={1} sx={{ mb: 1.5 }} alignItems="center" flexWrap="wrap" useFlexGap>
                      <Typography variant="body2" color="text.secondary">
                        Selected rows: {vatConflictSelectedCount}
                      </Typography>
                      <Button
                        size="small"
                        variant="outlined"
                        disabled={bulkReprocessBusy}
                        onClick={() =>
                          setSelectedTxIds((prev) => prev.filter((id) => !vatConflictIdSet.has(id)))
                        }
                      >
                        Clear selection
                      </Button>
                      <Button
                        size="small"
                        variant="outlined"
                        disabled={bulkReprocessBusy}
                        onClick={openVatConflictBulkTagEditDialog}
                      >
                        Bulk edit tags
                      </Button>
                      <Tooltip
                        title={
                          'Strip rule-based category tags on selected unconfirmed rows, then apply active ' +
                          'classification rules. Confirmed rows are skipped.'
                        }
                      >
                        <span>
                          <Button
                            size="small"
                            variant="outlined"
                            disabled={bulkReprocessBusy}
                            onClick={() => void runBulkReprocessSelected({ classification: true, supplier: false, vat: false, specialCase: false })}
                          >
                            {bulkReprocessBusy ? '…' : 'Re-run Classification rules'}
                          </Button>
                        </span>
                      </Tooltip>
                      <Tooltip
                        title={
                          'Recompute supplier display text on tags from description aliases (all selected VAT conflict rows).'
                        }
                      >
                        <span>
                          <Button
                            size="small"
                            variant="outlined"
                            disabled={bulkReprocessBusy}
                            onClick={() => void runBulkReprocessSelected({ classification: false, supplier: true, vat: false, specialCase: false })}
                          >
                            {bulkReprocessBusy ? '…' : 'Re-run Supplier rules'}
                          </Button>
                        </span>
                      </Tooltip>
                      <Tooltip title="Recompute VAT rate snapshots on existing category tags for selected rows.">
                        <span>
                          <Button
                            size="small"
                            variant="outlined"
                            disabled={bulkReprocessBusy}
                            onClick={() => void runBulkReprocessSelected({ classification: false, supplier: false, vat: true, specialCase: false })}
                          >
                            {bulkReprocessBusy ? '…' : 'Re-run VAT rate rules'}
                          </Button>
                        </span>
                      </Tooltip>
                      <Tooltip title="Apply Special Case override rules to selected rows.">
                        <span>
                          <Button
                            size="small"
                            variant="outlined"
                            disabled={bulkReprocessBusy}
                            onClick={() => void runBulkReprocessSelected({ classification: false, supplier: false, vat: false, specialCase: true })}
                          >
                            {bulkReprocessBusy ? '…' : 'Re-run Special Case rules'}
                          </Button>
                        </span>
                      </Tooltip>
                      <Tooltip title="Run supplier, classification, VAT, and Special Case reruns for selected rows.">
                        <span>
                          <Button
                            size="small"
                            variant="outlined"
                            color="secondary"
                            disabled={bulkReprocessBusy}
                            onClick={() => void runBulkReprocessSelected({ classification: true, supplier: true, vat: true, specialCase: true })}
                          >
                            {bulkReprocessBusy ? '…' : 'Re-run ALL Rules'}
                          </Button>
                        </span>
                      </Tooltip>
                    </Stack>
                  ) : null}
                  {renderTable(filteredVatConflictRows, 'conflicting_vat')}
                </>
              ) : tabIndex === 1 ? (
                <>
                  <Stack direction="row" spacing={1} sx={{ mb: 2 }} flexWrap="wrap" useFlexGap alignItems="center">
                    <ClearableSearchTextField
                      size="small"
                      label="Text search"
                      placeholder="e.g. Blacknight"
                      value={rulesTextFilter}
                      onChange={setRulesTextFilter}
                      sx={{ minWidth: 240 }}
                      title="Case-insensitive substring across classification, supplier, VAT, and special-case rules (and proposals)."
                    />
                    <FormControl size="small" sx={{ minWidth: 220 }}>
                      <Select
                        value={rulesStatusFilter}
                        onChange={(e) => setRulesStatusFilter(e.target.value as 'all' | RuleRowState)}
                      >
                        <MenuItem value="all">All statuses</MenuItem>
                        <MenuItem value="confirmed">Confirmed</MenuItem>
                        <MenuItem value="proposed">Proposed</MenuItem>
                      </Select>
                    </FormControl>
                    <Stack
                      direction="row"
                      spacing={1}
                      sx={{ ml: { md: 'auto' } }}
                      useFlexGap
                      alignItems="center"
                      flexWrap="wrap"
                    >
                      {exportableRulesSelectionCount > 0 ? (
                        <Button
                          variant="contained"
                          size="small"
                          disabled={rulesJsonTransferBusy !== null}
                          onClick={() => void downloadRulesJsonExport()}
                        >
                          {rulesJsonTransferBusy === 'export' ? 'Exporting…' : 'Export Rules'}
                        </Button>
                      ) : null}
                      <Button
                        variant="outlined"
                        size="small"
                        color="warning"
                        disabled={rulesJsonTransferBusy !== null}
                        onClick={() => rulesJsonImportInputRef.current?.click()}
                      >
                        {rulesJsonTransferBusy === 'import' ? 'Importing…' : 'Import Rules'}
                      </Button>
                      <input
                        ref={rulesJsonImportInputRef}
                        type="file"
                        accept=".json,application/json,text/plain"
                        style={{ display: 'none' }}
                        onChange={(e) => void onRulesJsonImportFileSelected(e.target.files)}
                      />
                      {rulesAccordionMixedState ? (
                        <>
                          <Button
                            size="small"
                            variant="outlined"
                            onClick={() =>
                              setRulesAccordionOpen({
                                proposals: true,
                                pipelineStepRules: true,
      merchantSuppliers: true,
                                supplierProposals: true,
                                appRules: true,
                                supplierRules: true,
                                vatRules: true,
                                specialCaseRules: true
                              })
                            }
                          >
                            Open all sections
                          </Button>
                          <Button
                            size="small"
                            variant="outlined"
                            onClick={() =>
                              setRulesAccordionOpen({
                                proposals: false,
                                pipelineStepRules: false,
      merchantSuppliers: false,
                                supplierProposals: false,
                                appRules: false,
                                supplierRules: false,
                                vatRules: false,
                                specialCaseRules: false
                              })
                            }
                          >
                            Close all sections
                          </Button>
                        </>
                      ) : (
                        <Button
                          size="small"
                          variant="outlined"
                          onClick={() =>
                            setRulesAccordionOpen({
                              proposals: !allRulesSectionsOpen,
                              pipelineStepRules: !allRulesSectionsOpen,
                              merchantSuppliers: !allRulesSectionsOpen,
                              supplierProposals: !allRulesSectionsOpen,
                              appRules: !allRulesSectionsOpen,
                              supplierRules: !allRulesSectionsOpen,
                              vatRules: !allRulesSectionsOpen,
                              specialCaseRules: !allRulesSectionsOpen
                            })
                          }
                        >
                          {allRulesSectionsOpen ? 'Close all sections' : 'Open all sections'}
                        </Button>
                      )}
                    </Stack>
                  </Stack>
                  {showCrossTypeBulkActions ? (
                    <Paper
                      role="region"
                      aria-label="Cross-type bulk rule actions"
                      variant="outlined"
                      sx={{ p: 1.5, mb: 2, bgcolor: 'action.hover' }}
                    >
                      <Stack spacing={1.5}>
                        <Typography variant="subtitle2" color="text.secondary" sx={{ fontWeight: 600 }}>
                          Cross-type selection: {totalSelectedRulesAcrossAccordions.toLocaleString()} row
                          {totalSelectedRulesAcrossAccordions === 1 ? '' : 's'}
                        </Typography>
                        <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                          {crossTypeBulkActionVisibility.showDelete ? (
                            <Tooltip title="You will be asked to confirm. Removes confirmed rules and rejects proposed rules in the selection.">
                              <span>
                                <Button
                                  size="small"
                                  variant="outlined"
                                  color="error"
                                  disabled={!!proposalBusyKey || totalSelectedRulesAcrossAccordions === 0}
                                  onClick={() => void runCrossTypeBulkRuleAction('delete')}
                                >
                                  Delete
                                </Button>
                              </span>
                            </Tooltip>
                          ) : null}
                          {crossTypeBulkActionVisibility.showAccept ? (
                            <Tooltip title="Any selected proposed rules (all types) will be accepted.">
                              <span>
                                <Button
                                  size="small"
                                  variant="outlined"
                                  color="success"
                                  disabled={!!proposalBusyKey || totalSelectedRulesAcrossAccordions === 0}
                                  onClick={() => void runCrossTypeBulkRuleAction('accept')}
                                >
                                  Accept
                                </Button>
                              </span>
                            </Tooltip>
                          ) : null}
                          {crossTypeBulkActionVisibility.showReject ? (
                            <Tooltip title="Any selected proposed rules (all types) will be rejected.">
                              <span>
                                <Button
                                  size="small"
                                  variant="outlined"
                                  disabled={!!proposalBusyKey || totalSelectedRulesAcrossAccordions === 0}
                                  onClick={() => void runCrossTypeBulkRuleAction('reject')}
                                >
                                  Reject
                                </Button>
                              </span>
                            </Tooltip>
                          ) : null}
                          {crossTypeBulkActionVisibility.showDeactivate ? (
                            <Tooltip title="Deactivates selected confirmed rules that are currently active.">
                              <span>
                                <Button
                                  size="small"
                                  variant="outlined"
                                  disabled={!!proposalBusyKey || totalSelectedRulesAcrossAccordions === 0}
                                  onClick={() => void runCrossTypeBulkRuleAction('deactivate')}
                                >
                                  Deactivate
                                </Button>
                              </span>
                            </Tooltip>
                          ) : null}
                          {crossTypeBulkActionVisibility.showActivate ? (
                            <Tooltip title="Activates selected confirmed rules that are currently inactive.">
                              <span>
                                <Button
                                  size="small"
                                  variant="outlined"
                                  disabled={!!proposalBusyKey || totalSelectedRulesAcrossAccordions === 0}
                                  onClick={() => void runCrossTypeBulkRuleAction('activate')}
                                >
                                  Activate
                                </Button>
                              </span>
                            </Tooltip>
                          ) : null}
                          {crossTypeBulkActionVisibility.showRun ? (
                            <Tooltip title="Runs selected confirmed rules (all types) on all matching transactions.">
                              <span>
                                <Button
                                  size="small"
                                  variant="outlined"
                                  disabled={!!proposalBusyKey || totalSelectedRulesAcrossAccordions === 0}
                                  onClick={() => void runCrossTypeBulkRuleAction('run')}
                                >
                                  Run
                                </Button>
                              </span>
                            </Tooltip>
                          ) : null}
                        </Stack>
                        <Stack direction={{ xs: 'column', md: 'row' }} spacing={1} alignItems={{ md: 'center' }} flexWrap="wrap" useFlexGap>
                          <FormControl size="small" sx={{ width: { xs: '100%', md: 220 } }}>
                            <InputLabel id="cross-bulk-account-scope-label">Bank account</InputLabel>
                            <Select
                              labelId="cross-bulk-account-scope-label"
                              label="Bank account"
                              value={crossBulkAccountScope}
                              onChange={(e) =>
                                setCrossBulkAccountScope(
                                  String(e.target.value || RULE_BULK_NO_CHANGE) as
                                    | typeof RULE_BULK_NO_CHANGE
                                    | 'both'
                                    | 'ireland'
                                    | 'uk'
                                )
                              }
                            >
                              <MenuItem value={RULE_BULK_NO_CHANGE}>No change</MenuItem>
                              {RULE_ACCOUNT_SCOPE_OPTIONS.map((opt) => (
                                <MenuItem key={`cross-bulk-scope-${opt.value}`} value={opt.value}>
                                  {opt.label}
                                </MenuItem>
                              ))}
                            </Select>
                          </FormControl>
                          <TextField
                            size="small"
                            label="Comment to add to all"
                            value={crossBulkComment}
                            onChange={(e) => setCrossBulkComment(e.target.value)}
                            sx={{ minWidth: { xs: '100%', md: 340 } }}
                          />
                          <Button
                            size="small"
                            variant="contained"
                            disabled={!!proposalBusyKey || totalSelectedRulesAcrossAccordions === 0}
                            onClick={() => void runCrossTypeBulkRuleAction('applyMeta')}
                          >
                            Apply bank account/comment to selected
                          </Button>
                        </Stack>
                      </Stack>
                    </Paper>
                  ) : null}
                  {rulesFilterPills.length > 0 ? (
                    <Stack
                      direction={{ xs: 'column', sm: 'row' }}
                      spacing={1}
                      sx={{ mb: 2 }}
                      alignItems={{ sm: 'center' }}
                      flexWrap="wrap"
                      useFlexGap
                    >
                      <Button size="small" variant="outlined" onClick={resetRulesFilters} disabled={!rulesHasFilters}>
                        Clear all filters
                      </Button>
                      <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap>
                        {rulesFilterPills.map((pill) => (
                          <Chip
                            key={`rules-filter-pill-${pill.key}`}
                            size="small"
                            label={pill.label}
                            onDelete={pill.onDelete}
                            variant="outlined"
                          />
                        ))}
                      </Stack>
                    </Stack>
                  ) : null}

                  {rulesTabClassificationProposalFilterMiss ? (
                    <Alert
                      severity="info"
                      sx={{ mb: 2 }}
                      onClose={() => {
                        setRulesProposalIdsFilter('');
                        if (
                          parsedRulesIdFilter.classificationProposalId != null ||
                          parsedRulesIdFilter.classificationAtlasSeq != null
                        )
                          setRulesIdFilter('');
                      }}
                    >
                      <Typography variant="body2" component="div">
                        No <strong>pending</strong> classification proposal matches this Proposal ID / Rules ID filter.
                        Saved drafts use <strong>P###</strong> (database id); atlas suggestions use <strong>A###</strong>{' '}
                        (stable sequence from the catalog). The API only returns proposals with status pending; after
                        accept or reject the row leaves this list. Clear the filter, set Status to <strong>All</strong> or{' '}
                        <strong>Confirmed</strong>, and use Rules ID or text search to find the saved rule if it still
                        exists.
                      </Typography>
                    </Alert>
                  ) : null}

                  <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                  <Accordion
                    expanded={rulesAccordionOpen.proposals}
                    onChange={(_, expanded) => setRulesAccordionOpen((prev) => ({ ...prev, proposals: expanded }))}
                    sx={{ mb: 1.5, order: 3 }}
                  >
                    <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                      <Stack direction="row" spacing={0.75} alignItems="center">
                        <Tooltip title="Select/deselect all visible Classification rules (confirmed + proposed).">
                          <span>
                            <Checkbox
                              size="small"
                              checked={classificationGroupAllSelected}
                              indeterminate={classificationGroupIndeterminate}
                              disabled={classificationGroupSelectableCount === 0}
                              onClick={(e) => e.stopPropagation()}
                              onFocus={(e) => e.stopPropagation()}
                              onChange={() => toggleClassificationGroupSelection()}
                              inputProps={{ 'aria-label': 'Select all Classification rules in this accordion' }}
                            />
                          </span>
                        </Tooltip>
                        <Typography variant="h6">Pipeline Step 3: Classification Rules</Typography>
                        <Tooltip
                          title={
                            <Typography variant="body2">
                              New rules from the app atlas or from your transactions appear here first. Accept adds
                              them to app rules; Reject dismisses them. When you add or edit a rule, each filled-in
                              condition must match the same transaction (AND); empty fields are ignored. Several rules
                              can match one transaction when they assign different categories; rules run in priority
                              order (lower number first).
                            </Typography>
                          }
                        >
                          <InfoOutlinedIcon fontSize="small" sx={{ color: 'text.secondary' }} />
                        </Tooltip>
                        <Typography component="span" variant="body2" color="text.secondary" sx={{ fontWeight: 400 }}>
                          {formatPipelineStageCounts(
                            visibleClassificationRuleCounts.confirmed,
                            visibleClassificationRuleCounts.proposed,
                            visibleClassificationRuleCounts.rejected,
                            rulesHasFilters
                          )}
                        </Typography>
                      </Stack>
                    </AccordionSummary>
                    <AccordionDetails>
                      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }} flexWrap="wrap" useFlexGap>
                        <Typography variant="body2" color="text.secondary">
                          {`Showing ${sortedVisibleClassificationConfirmedRowsExcludingPipeline.length.toLocaleString()} confirmed + ${visibleClassificationRuleCounts.proposed.toLocaleString()} proposed`}
                        </Typography>
                        <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                          <Button
                            size="small"
                            variant="outlined"
                            disabled={loading || !!proposalBusyKey || missingCategoryAuditBusy}
                            onClick={() => void runMissingCategoryAudit()}
                          >
                            {missingCategoryAuditBusy ? 'Running audit…' : 'Run missing-category audit'}
                          </Button>
                          <Button
                            size="small"
                            variant="outlined"
                            disabled={loading || !!proposalBusyKey || proposalSweepBusy}
                            onClick={runGenerateProposalsFromAllTransactions}
                          >
                            {proposalSweepBusy ? 'Generating Proposed rules…' : 'Generate Proposed rules'}
                          </Button>
                          <Button
                            size="small"
                            variant="outlined"
                            disabled={loading || !!proposalBusyKey || proposalSweepBusy}
                            onClick={() => void runGenerateProposalsMissingCategoryOnly()}
                          >
                            {proposalSweepBusy ? 'Generating…' : 'Generate proposals (missing category only)'}
                          </Button>
                          <Button
                            size="small"
                            variant="outlined"
                            color="secondary"
                            onClick={() => void runReapplyRulesUnconfirmed()}
                            disabled={
                              loading ||
                              applyingRules ||
                              !!proposalBusyKey ||
                              reapplyUnconfirmedBusy ||
                              reapplySpecialCaseUnconfirmedBusy ||
                              rerunAllRulesBusy ||
                              dedupeDisplayDupTagsBusy
                            }
                          >
                            {reapplyUnconfirmedBusy ? 'Re-running…' : 'Re-run ALL Classification Rules'}
                          </Button>
                          <Button
                            size="small"
                            variant="contained"
                            disabled={loading || applyingRules || ruleEditSaving || granularCategoriesSortedByName.length === 0}
                            onClick={openNewClassificationRuleDialog}
                          >
                            Add Classification rule
                          </Button>
                        </Stack>
                      </Stack>
                      {missingCategoryAudit && (
                        <Stack spacing={0.5} sx={{ mb: 1 }}>
                          <Typography variant="body2" color="text.secondary">
                            {missingCategoryAudit.ok === false
                              ? String(
                                  missingCategoryAudit.message ||
                                    'Audit unavailable (ledger schema does not match Transaction Classification Review).'
                                )
                              : `Last audit: ${Number(missingCategoryAudit.missing_category_display_count || 0).toLocaleString()} missing category of ${Number(
                                  missingCategoryAudit.total_transactions || 0
                                ).toLocaleString()} total; ${Number(
                                  missingCategoryAudit.missing_with_match_text || 0
                                ).toLocaleString()} with merchant/description/reference text; ${Number(
                                  missingCategoryAudit.missing_no_match_text || 0
                                ).toLocaleString()} with no match text.`}
                          </Typography>
                          {missingCategoryAudit.ok !== false &&
                          parseCandidates(missingCategoryAudit.proposal_audit_candidates).length > 0 ? (
                            <Button
                              size="small"
                              variant="text"
                              disabled={auditCandidatesGenerating || missingCategoryAuditBusy}
                              onClick={() => {
                                const c = parseCandidates(missingCategoryAudit.proposal_audit_candidates);
                                if (!c.length) return;
                                const rid = missingCategoryAudit.proposal_audit_run_id;
                                setAuditCandidatesModal({
                                  kind: 'classification',
                                  title: 'Missing category — generate drafts from selected buckets',
                                  candidates: c,
                                  proposalAuditRunId:
                                    typeof rid === 'string' && rid.trim().length > 0 ? rid.trim() : null,
                                });
                              }}
                            >
                              Select buckets for draft generation…
                            </Button>
                          ) : null}
                        </Stack>
                      )}
                      {rulesTextFilter.trim() && vatRulesLinkedToVisibleClassification.length > 0 ? (
                        <Box sx={{ mb: 1 }}>
                          <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 0.5 }}>
                            {`VAT rules that include the same output category as a matching classification rule (search "${rulesTextFilter.trim()}"):`}
                          </Typography>
                          <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap>
                            {vatRulesLinkedToVisibleClassification.map((vr) => {
                              const names = vatRuleCategoryNames(vr);
                              const summary =
                                names.length > 2 ? `${names.slice(0, 2).join(', ')}…` : names.join(', ') || '—';
                              const tip = `${formatPrefixedRuleId('vat', vr.id)}${vr.name ? ` — ${vr.name}` : ''}\n${names.join(' · ')}`;
                              return (
                                <Tooltip
                                  key={`vat-search-link-${vr.id}`}
                                  title={<span style={{ whiteSpace: 'pre-wrap' }}>{tip}</span>}
                                >
                                  <Chip
                                    size="small"
                                    label={`${formatPrefixedRuleId('vat', vr.id)} · ${summary}`}
                                    variant="outlined"
                                    onClick={() => setRulesAccordionOpen((prev) => ({ ...prev, vatRules: true }))}
                                    sx={{ cursor: 'pointer' }}
                                  />
                                </Tooltip>
                              );
                            })}
                          </Stack>
                        </Box>
                      ) : null}
                      {selectedClassificationRuleKeys.length > 0 ? (
                        <Paper
                          role="region"
                          aria-label="Bulk actions for selected classification rules"
                          variant="outlined"
                          sx={{ p: 1.5, mb: 1, bgcolor: 'action.hover' }}
                        >
                          <Stack spacing={1.5}>
                            <Typography variant="subtitle2" color="text.secondary" sx={{ fontWeight: 600 }}>
                              {bulkClassificationSelection.count} classification rule
                              {bulkClassificationSelection.count === 1 ? '' : 's'} selected
                            </Typography>
                            <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                              {bulkClassificationSelection.showDelete ? (
                                <Tooltip title="Removes the selection: you will be asked to confirm. Proposed rules are rejected; confirmed rules are deleted.">
                                  <span>
                                    <Button
                                      size="small"
                                      variant="outlined"
                                      color="error"
                                      disabled={!!proposalBusyKey}
                                      onClick={() => void runBulkClassificationRuleAction('delete')}
                                    >
                                      Delete
                                    </Button>
                                  </span>
                                </Tooltip>
                              ) : null}
                              {bulkClassificationSelection.showAccept ? (
                                <Tooltip title="Applies to proposed rules in the current selection.">
                                  <span>
                                    <Button
                                      size="small"
                                      variant="outlined"
                                      color="success"
                                      disabled={!!proposalBusyKey}
                                      onClick={() => void runBulkClassificationRuleAction('accept')}
                                    >
                                      Accept
                                    </Button>
                                  </span>
                                </Tooltip>
                              ) : null}
                              {bulkClassificationSelection.showReject ? (
                                <Tooltip title="Rejects proposed rules in the current selection.">
                                  <span>
                                    <Button
                                      size="small"
                                      variant="outlined"
                                      disabled={!!proposalBusyKey}
                                      onClick={() => void runBulkClassificationRuleAction('reject')}
                                    >
                                      Reject
                                    </Button>
                                  </span>
                                </Tooltip>
                              ) : null}
                              {bulkClassificationSelection.showDeactivate ? (
                                <Tooltip title="Deactivates confirmed rules in the current selection that are currently active.">
                                  <span>
                                    <Button
                                      size="small"
                                      variant="outlined"
                                      disabled={!!proposalBusyKey}
                                      onClick={() => void runBulkClassificationRuleAction('deactivate')}
                                    >
                                      Deactivate
                                    </Button>
                                  </span>
                                </Tooltip>
                              ) : null}
                              {bulkClassificationSelection.showActivate ? (
                                <Tooltip title="Activates confirmed rules in the current selection that are currently inactive.">
                                  <span>
                                    <Button
                                      size="small"
                                      variant="outlined"
                                      disabled={!!proposalBusyKey}
                                      onClick={() => void runBulkClassificationRuleAction('activate')}
                                    >
                                      Activate
                                    </Button>
                                  </span>
                                </Tooltip>
                              ) : null}
                              {bulkClassificationSelection.showRun ? (
                                <Tooltip title="Runs confirmed rules in the selection on all matching transactions.">
                                  <span>
                                    <Button
                                      size="small"
                                      variant="outlined"
                                      disabled={!!proposalBusyKey}
                                      onClick={() => void runBulkClassificationRuleAction('run')}
                                    >
                                      Run
                                    </Button>
                                  </span>
                                </Tooltip>
                              ) : null}
                            </Stack>
                            {bulkClassificationSelection.showApplyEdits ? (
                              <Stack direction={{ xs: 'column', md: 'row' }} spacing={1} alignItems={{ md: 'center' }} flexWrap="wrap" useFlexGap>
                                <TextField
                                  size="small"
                                  type="number"
                                  label="Priority for selected"
                                  value={bulkClassificationPriority}
                                  onChange={(e) => setBulkClassificationPriority(e.target.value)}
                                  sx={{ width: { xs: '100%', md: 170 } }}
                                />
                                <FormControl size="small" sx={{ width: { xs: '100%', md: 220 } }}>
                                  <InputLabel id="bulk-classification-account-scope-label">Bank account</InputLabel>
                                  <Select
                                    labelId="bulk-classification-account-scope-label"
                                    label="Bank account"
                                    value={bulkClassificationAccountScope}
                                    onChange={(e) =>
                                      setBulkClassificationAccountScope(
                                        String(e.target.value || RULE_BULK_NO_CHANGE) as
                                          | typeof RULE_BULK_NO_CHANGE
                                          | 'both'
                                          | 'ireland'
                                          | 'uk'
                                      )
                                    }
                                  >
                                    <MenuItem value={RULE_BULK_NO_CHANGE}>No change</MenuItem>
                                    {RULE_ACCOUNT_SCOPE_OPTIONS.map((opt) => (
                                      <MenuItem key={`bulk-class-scope-${opt.value}`} value={opt.value}>
                                        {opt.label}
                                      </MenuItem>
                                    ))}
                                  </Select>
                                </FormControl>
                                <TextField
                                  size="small"
                                  label="Comment to add"
                                  value={bulkClassificationComment}
                                  onChange={(e) => setBulkClassificationComment(e.target.value)}
                                  sx={{ minWidth: { xs: '100%', md: 320 } }}
                                />
                                <Button
                                  size="small"
                                  variant="contained"
                                  disabled={!!proposalBusyKey}
                                  onClick={() => void runBulkClassificationRuleAction('applyEdits')}
                                >
                                  Apply fields to selected confirmed
                                </Button>
                              </Stack>
                            ) : null}
                          </Stack>
                        </Paper>
                      ) : null}
                      <PipelineNestedSectionAccordion
                        filtersActive={rulesHasFilters}
                        title="Confirmed"
                        count={sortedVisibleClassificationConfirmedRowsExcludingPipeline.length}
                        expanded={pipelineNestedSectionOpen.step3.confirmed}
                        onExpandedChange={(open) => setPipelineNestedSection('step3', 'confirmed', open)}
                        first
                      >
                      <TableContainer sx={{ mb: 1.5, overflowX: 'hidden', maxWidth: '100%' }}>
                        <Table size="small" sx={FIT_TABLE_SX}>
                          <FitColgroup cols={equalFitCols(['c0', 'c1', 'c2', 'c3', 'c4', 'c5', 'c6', 'c7', 'c8', 'c9', 'c10', 'c11', 'c12', 'c13', 'c14'], { flexKey: 'c3' })} />
                          <TableHead>
                            <TableRow>
                              <TableCell padding="checkbox">
                                <input
                                  type="checkbox"
                                  checked={
                                    sortedVisibleClassificationConfirmedRowsExcludingPipeline.length > 0 &&
                                    sortedVisibleClassificationConfirmedRowsExcludingPipeline.every((r) =>
                                      selectedClassificationRuleKeys.includes(r.key)
                                    )
                                  }
                                  onChange={(e) => {
                                    const keys = sortedVisibleClassificationConfirmedRowsExcludingPipeline.map((r) => r.key);
                                    const keySet = new Set(keys);
                                    if (e.target.checked) {
                                      setSelectedClassificationRuleKeys((prev) => Array.from(new Set(prev.concat(keys))));
                                    } else {
                                      setSelectedClassificationRuleKeys((prev) => prev.filter((k) => !keySet.has(k)));
                                    }
                                  }}
                                />
                              </TableCell>
                              <TableCell sortDirection={classificationRulesTableSort?.key === 'priority' ? classificationRulesTableSort.dir : false}>
                                <TableSortLabel
                                  active={classificationRulesTableSort?.key === 'priority'}
                                  direction={classificationRulesTableSort?.key === 'priority' ? classificationRulesTableSort.dir : 'asc'}
                                  onClick={() => setClassificationRulesTableSort((prev) => nextSortState(prev, 'priority'))}
                                >
                                  Priority
                                </TableSortLabel>
                              </TableCell>
                              <TableCell sortDirection={classificationRulesTableSort?.key === 'id' ? classificationRulesTableSort.dir : false}>
                                <TableSortLabel
                                  active={classificationRulesTableSort?.key === 'id'}
                                  direction={classificationRulesTableSort?.key === 'id' ? classificationRulesTableSort.dir : 'asc'}
                                  onClick={() => setClassificationRulesTableSort((prev) => nextSortState(prev, 'id'))}
                                >
                                  ID
                                </TableSortLabel>
                              </TableCell>
                              <TableCell sortDirection={classificationRulesTableSort?.key === 'name' ? classificationRulesTableSort.dir : false}>
                                <TableSortLabel
                                  active={classificationRulesTableSort?.key === 'name'}
                                  direction={classificationRulesTableSort?.key === 'name' ? classificationRulesTableSort.dir : 'asc'}
                                  onClick={() => setClassificationRulesTableSort((prev) => nextSortState(prev, 'name'))}
                                >
                                  Rule name
                                </TableSortLabel>
                              </TableCell>
                              <TableCell sx={RULE_ACCOUNT_COLUMN_SX} sortDirection={classificationRulesTableSort?.key === 'account' ? classificationRulesTableSort.dir : false}>
                                <TableSortLabel
                                  active={classificationRulesTableSort?.key === 'account'}
                                  direction={classificationRulesTableSort?.key === 'account' ? classificationRulesTableSort.dir : 'asc'}
                                  onClick={() => setClassificationRulesTableSort((prev) => nextSortState(prev, 'account'))}
                                >
                                  Bank /n A/C
                                </TableSortLabel>
                              </TableCell>
                              <TableCell sortDirection={classificationRulesTableSort?.key === 'canonicalSupplier' ? classificationRulesTableSort.dir : false}>
                                <TableSortLabel
                                  active={classificationRulesTableSort?.key === 'canonicalSupplier'}
                                  direction={classificationRulesTableSort?.key === 'canonicalSupplier' ? classificationRulesTableSort.dir : 'asc'}
                                  onClick={() => setClassificationRulesTableSort((prev) => nextSortState(prev, 'canonicalSupplier'))}
                                >
                                  Canonical Supplier
                                </TableSortLabel>
                              </TableCell>
                              <TableCell sortDirection={classificationRulesTableSort?.key === 'supplier' ? classificationRulesTableSort.dir : false}>
                                <TableSortLabel
                                  active={classificationRulesTableSort?.key === 'supplier'}
                                  direction={classificationRulesTableSort?.key === 'supplier' ? classificationRulesTableSort.dir : 'asc'}
                                  onClick={() => setClassificationRulesTableSort((prev) => nextSortState(prev, 'supplier'))}
                                >
                                  Merchant / Counterparty Match
                                </TableSortLabel>
                              </TableCell>
                              <TableCell sortDirection={classificationRulesTableSort?.key === 'description' ? classificationRulesTableSort.dir : false}>
                                <TableSortLabel
                                  active={classificationRulesTableSort?.key === 'description'}
                                  direction={classificationRulesTableSort?.key === 'description' ? classificationRulesTableSort.dir : 'asc'}
                                  onClick={() => setClassificationRulesTableSort((prev) => nextSortState(prev, 'description'))}
                                >
                                  Description Text Match
                                </TableSortLabel>
                              </TableCell>
                              <TableCell>Reference Text Match</TableCell>
                              <TableCell
                                sortDirection={classificationRulesTableSort?.key === 'otherCriteria' ? classificationRulesTableSort.dir : false}
                              >
                                <TableSortLabel
                                  active={classificationRulesTableSort?.key === 'otherCriteria'}
                                  direction={classificationRulesTableSort?.key === 'otherCriteria' ? classificationRulesTableSort.dir : 'asc'}
                                  onClick={() => setClassificationRulesTableSort((prev) => nextSortState(prev, 'otherCriteria'))}
                                >
                                  Other Criteria
                                </TableSortLabel>
                              </TableCell>
                              <TableCell
                                sortDirection={classificationRulesTableSort?.key === 'category' ? classificationRulesTableSort.dir : false}
                                sx={OUTPUT_COLUMN_SX}
                              >
                                <TableSortLabel
                                  active={classificationRulesTableSort?.key === 'category'}
                                  direction={classificationRulesTableSort?.key === 'category' ? classificationRulesTableSort.dir : 'asc'}
                                  onClick={() => setClassificationRulesTableSort((prev) => nextSortState(prev, 'category'))}
                                >
                                  Output: Category
                                </TableSortLabel>
                              </TableCell>
                              <TableCell sortDirection={classificationRulesTableSort?.key === 'status' ? classificationRulesTableSort.dir : false}>
                                <TableSortLabel
                                  active={classificationRulesTableSort?.key === 'status'}
                                  direction={classificationRulesTableSort?.key === 'status' ? classificationRulesTableSort.dir : 'asc'}
                                  onClick={() => setClassificationRulesTableSort((prev) => nextSortState(prev, 'status'))}
                                >
                                  Status
                                </TableSortLabel>
                              </TableCell>
                              <TableCell sortDirection={classificationRulesTableSort?.key === 'firedCount' ? classificationRulesTableSort.dir : false}>
                                <TableSortLabel
                                  active={classificationRulesTableSort?.key === 'firedCount'}
                                  direction={classificationRulesTableSort?.key === 'firedCount' ? classificationRulesTableSort.dir : 'asc'}
                                  onClick={() => setClassificationRulesTableSort((prev) => nextSortState(prev, 'firedCount'))}
                                >
                                  Tx fired
                                </TableSortLabel>
                              </TableCell>
                              <TableCell sortDirection={classificationRulesTableSort?.key === 'active' ? classificationRulesTableSort.dir : false}>
                                <TableSortLabel
                                  active={classificationRulesTableSort?.key === 'active'}
                                  direction={classificationRulesTableSort?.key === 'active' ? classificationRulesTableSort.dir : 'asc'}
                                  onClick={() => setClassificationRulesTableSort((prev) => nextSortState(prev, 'active'))}
                                >
                                  Rule Active
                                </TableSortLabel>
                              </TableCell>
                              <TableCell align="right" sx={{  whiteSpace: 'nowrap' }}>
                                Actions
                              </TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {sortedVisibleClassificationConfirmedRowsExcludingPipeline.length === 0 ? (
                              <TableRow>
                                <TableCell colSpan={15}>No confirmed classification rules for the current filters.</TableCell>
                              </TableRow>
                            ) : (
                              sortedVisibleClassificationConfirmedRowsExcludingPipeline.map((entry) => {
                                const rule = entry.rule as ClassificationRule;
                                const hasOtherCriteria = classificationRuleHasOtherCriteria(rule);
                                const canonNamesList = supplierOverridesFromClassificationRule(rule);
                                const legacyCanonOnly = (rule.supplier_canonical_name || '').trim();
                                const canonicalNamesForChips =
                                  canonNamesList.length > 0 ? canonNamesList : legacyCanonOnly ? [legacyCanonOnly] : [];
                                const clsPatchBusy = classificationTablePatchBusyId === rule.id;
                                return (
                                  <TableRow
                                    key={entry.key}
                                    sx={ruleTableRowSavingSx(
                                      theme,
                                      ruleTableSaveHighlight?.kind === 'classification' &&
                                        ruleTableSaveHighlight.rowKey === entry.key
                                    )}
                                  >
                                    <TableCell padding="checkbox">
                                      <input
                                        type="checkbox"
                                        checked={selectedClassificationRuleKeys.includes(entry.key)}
                                        onChange={() => undefined}
                                        onClick={(e) => {
                                          e.preventDefault();
                                          const selected = selectedClassificationRuleKeys.includes(entry.key);
                                          applyRulesRowCheckboxSelection({
                                            tableKey: 'cls-confirmed',
                                            orderedKeys: sortedVisibleClassificationConfirmedRowsExcludingPipeline.map(
                                              (r) => r.key
                                            ),
                                            clickedKey: entry.key,
                                            checked: !selected,
                                            shiftKey: e.shiftKey,
                                            setSelection: setSelectedClassificationRuleKeys
                                          });
                                        }}
                                      />
                                    </TableCell>
                                    <TableCell>{`${Number(rule.priority ?? 100)} (${supplierRulePriorityBand(Number(rule.priority ?? 100))})`}</TableCell>
                                    <TableCell>{formatClassificationRulePrefixedId(rule.id, rule.name)}</TableCell>
                                    <TableCell>{rule.name}</TableCell>
                                    <TableCell sx={RULE_ACCOUNT_COLUMN_SX}>{ruleAccountScopeTableLabel(rule.account_scope)}</TableCell>
                                    <TableCell>
                                      {classificationCanonicalSupplierChips({
                                        names: canonicalNamesForChips,
                                        chipKeyPrefix: `cc-${rule.id}-canon`,
                                        busy: clsPatchBusy,
                                        onNameClick: openSuppliersTabForName,
                                        onDeleteName:
                                          canonNamesList.length > 0
                                            ? (name) => {
                                                const next = supplierOverridesFromClassificationRule(rule).filter(
                                                  (n) => n !== name
                                                );
                                                void patchClassificationRuleFromTable(rule, {
                                                  supplier_name_overrides: next.length ? next : null,
                                                  supplier_name_override: next.length ? next[0] : null
                                                });
                                              }
                                            : undefined
                                      })}
                                    </TableCell>
                                    <TableCell>
                                      {classificationRuleTextMatchChips({
                                        parts: classificationRuleOrFieldParts(rule.supplier_contains),
                                        operator: rule.text_match_operator,
                                        chipKeyPrefix: `cc-${rule.id}-merch`,
                                        busy: clsPatchBusy,
                                        onDeleteIndex: (idx) => {
                                          const next = classificationRuleRemoveOrPartAtIndex(
                                            rule.supplier_contains,
                                            idx
                                          );
                                          void patchClassificationRuleFromTable(rule, { supplier_contains: next });
                                        }
                                      })}
                                    </TableCell>
                                    <TableCell>
                                      {classificationRuleTextMatchChips({
                                        parts: classificationRuleOrFieldParts(rule.description_contains),
                                        operator:
                                          rule.description_text_match_operator || rule.text_match_operator,
                                        chipKeyPrefix: `cc-${rule.id}-desc`,
                                        busy: clsPatchBusy,
                                        onDeleteIndex: (idx) => {
                                          const next = classificationRuleRemoveOrPartAtIndex(
                                            rule.description_contains,
                                            idx
                                          );
                                          void patchClassificationRuleFromTable(rule, {
                                            description_contains: next
                                          });
                                        }
                                      })}
                                    </TableCell>
                                    <TableCell>
                                      {classificationRuleTextMatchChips({
                                        parts: classificationRuleOrFieldParts(rule.reference_contains),
                                        operator:
                                          rule.reference_text_match_operator || rule.text_match_operator,
                                        chipKeyPrefix: `cc-${rule.id}-ref`,
                                        busy: clsPatchBusy,
                                        onDeleteIndex: (idx) => {
                                          const next = classificationRuleRemoveOrPartAtIndex(
                                            rule.reference_contains,
                                            idx
                                          );
                                          void patchClassificationRuleFromTable(rule, {
                                            reference_contains: next
                                          });
                                        }
                                      })}
                                    </TableCell>
                                    <TableCell>
                                      <Tooltip title="Amount range, currency, recurrence, or rule-level VAT rate beyond the supplier/text columns.">
                                        <span>
                                          <Chip
                                            size="small"
                                            label={hasOtherCriteria ? 'Yes' : 'No'}
                                            color={hasOtherCriteria ? 'info' : 'default'}
                                            variant={hasOtherCriteria ? 'filled' : 'outlined'}
                                          />
                                        </span>
                                      </Tooltip>
                                    </TableCell>
                                    <TableCell sx={OUTPUT_COLUMN_SX}>
                                      {String(rule.category?.name || '').trim() ? (
                                        <Tooltip title="Open category in Categories tab. Use Edit to assign a different output category.">
                                          <span>
                                            <Chip
                                              size="small"
                                              variant="outlined"
                                              label={String(rule.category?.name || '').trim()}
                                              clickable
                                              onClick={() =>
                                                openCategoriesTabForName(String(rule.category?.name || '').trim())
                                              }
                                            />
                                          </span>
                                        </Tooltip>
                                      ) : (
                                        <Chip
                                          size="small"
                                          variant="outlined"
                                          label={`Category #${rule.category_id}`}
                                        />
                                      )}
                                    </TableCell>
                                    <TableCell><Chip size="small" label="Confirmed" color="success" /></TableCell>
                                    <TableCell>{Number(ruleFireCounts.classification[rule.id] || 0).toLocaleString()}</TableCell>
                                    <TableCell>{rule.is_active ? 'Active' : 'Inactive'}</TableCell>
                                    <TableCell align="right" sx={{  whiteSpace: 'nowrap' }}>
                                      <Stack direction="row" spacing={0.25} justifyContent="flex-end" flexWrap="nowrap">
                                        <Tooltip
                                          title={
                                            classificationRuleCanListMatches(rule)
                                              ? 'View matching transactions'
                                              : 'Rule needs a text match, amount range, or canonical supplier to list transactions.'
                                          }
                                        >
                                          <span>
                                            <IconButton
                                              size="small"
                                              color="primary"
                                              aria-label="View matching transactions"
                                              disabled={loading || !classificationRuleCanListMatches(rule)}
                                              onClick={() => void openMatchingTransactionsModal(rule)}
                                            >
                                              <VisibilityIcon fontSize="small" />
                                            </IconButton>
                                          </span>
                                        </Tooltip>
                                        <Tooltip title="Apply this rule only (append tags, no deletions)">
                                          <span>
                                            <IconButton
                                              size="small"
                                              color="primary"
                                              aria-label="Apply this classification rule to all transactions"
                                              disabled={loading || applySingleRuleBusyKey !== ''}
                                              onClick={() => void applySingleClassificationRuleToAllTransactions(rule)}
                                            >
                                              {applySingleRuleBusyKey === `classification-${rule.id}` ? (
                                                <CircularProgress size={14} color="inherit" />
                                              ) : (
                                                <PlayArrowIcon fontSize="small" />
                                              )}
                                            </IconButton>
                                          </span>
                                        </Tooltip>
                                        <IconButton
                                          size="small"
                                          color="primary"
                                          onClick={() => {
                                            openClassificationRuleEditDialog(rule);
                                          }}
                                        >
                                          <EditIcon fontSize="small" />
                                        </IconButton>
                                        <Tooltip title="Duplicate rule">
                                          <span>
                                            <IconButton
                                              size="small"
                                              color="primary"
                                              aria-label="Duplicate classification rule"
                                              disabled={
                                                loading ||
                                                applySingleRuleBusyKey !== '' ||
                                                classificationTablePatchBusyId === rule.id ||
                                                !!ruleDuplicateBusyKey
                                              }
                                              onClick={() => void duplicateClassificationRule(rule)}
                                            >
                                              {ruleDuplicateBusyKey === `dup-cls-${rule.id}` ? (
                                                <CircularProgress size={14} color="inherit" />
                                              ) : (
                                                <ContentCopyIcon fontSize="small" />
                                              )}
                                            </IconButton>
                                          </span>
                                        </Tooltip>
                                        <IconButton
                                          size="small"
                                          color="error"
                                          onClick={() => void deleteClassificationRule(rule.id, rule.name)}
                                        >
                                          <DeleteOutlineIcon fontSize="small" />
                                        </IconButton>
                                      </Stack>
                                    </TableCell>
                                  </TableRow>
                                );
                              })
                            )}
                          </TableBody>
                        </Table>
                      </TableContainer>
                      </PipelineNestedSectionAccordion>
                      <PipelineNestedSectionAccordion
                        filtersActive={rulesHasFilters}
                        title="Proposed"
                        count={sortedVisibleClassificationProposedRows.length}
                        expanded={pipelineNestedSectionOpen.step3.proposed}
                        onExpandedChange={(open) => setPipelineNestedSection('step3', 'proposed', open)}
                      >
                      {selectedClassificationProposedCount > 0 ? (
                        <Paper
                          role="region"
                          aria-label="Bulk actions for selected proposed classification rules"
                          variant="outlined"
                          sx={{ p: 1.5, mb: 1, bgcolor: 'action.hover' }}
                        >
                          <Stack spacing={1}>
                            <Typography variant="subtitle2" color="text.secondary" sx={{ fontWeight: 600 }}>
                              {selectedClassificationProposedCount} proposed classification rule
                              {selectedClassificationProposedCount === 1 ? '' : 's'} selected
                            </Typography>
                            <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                              <Button
                                size="small"
                                variant="outlined"
                                color="success"
                                disabled={!!proposalBusyKey}
                                onClick={() => void runBulkClassificationRuleAction('accept')}
                              >
                                Accept
                              </Button>
                              <Button
                                size="small"
                                variant="outlined"
                                disabled={!!proposalBusyKey}
                                onClick={() => void runBulkClassificationRuleAction('reject')}
                              >
                                Reject
                              </Button>
                              <Button
                                size="small"
                                variant="outlined"
                                color="error"
                                disabled={!!proposalBusyKey}
                                onClick={() => void runBulkClassificationRuleAction('delete')}
                              >
                                Delete
                              </Button>
                            </Stack>
                          </Stack>
                        </Paper>
                      ) : null}
                      <TableContainer sx={{ overflowX: 'hidden', maxWidth: '100%' }}>
                        <Table size="small" sx={FIT_TABLE_SX}>
                          <FitColgroup cols={equalFitCols(['c0', 'c1', 'c2', 'c3', 'c4', 'c5', 'c6', 'c7', 'c8', 'c9', 'c10', 'c11', 'c12', 'c13'], { flexKey: 'c3' })} />
                          <TableHead>
                            <TableRow>
                              <TableCell padding="checkbox">
                                <input
                                  type="checkbox"
                                  checked={
                                    sortedVisibleClassificationProposedRows.length > 0 &&
                                    sortedVisibleClassificationProposedRows.every((r) =>
                                      selectedClassificationRuleKeys.includes(r.key)
                                    )
                                  }
                                  onChange={(e) => {
                                    const keys = sortedVisibleClassificationProposedRows.map((r) => r.key);
                                    const keySet = new Set(keys);
                                    if (e.target.checked) {
                                      setSelectedClassificationRuleKeys((prev) => Array.from(new Set(prev.concat(keys))));
                                    } else {
                                      setSelectedClassificationRuleKeys((prev) => prev.filter((k) => !keySet.has(k)));
                                    }
                                  }}
                                />
                              </TableCell>
                              <TableCell sortDirection={classificationRulesTableSort?.key === 'priority' ? classificationRulesTableSort.dir : false}>
                                <TableSortLabel
                                  active={classificationRulesTableSort?.key === 'priority'}
                                  direction={classificationRulesTableSort?.key === 'priority' ? classificationRulesTableSort.dir : 'asc'}
                                  onClick={() => setClassificationRulesTableSort((prev) => nextSortState(prev, 'priority'))}
                                >
                                  Priority
                                </TableSortLabel>
                              </TableCell>
                              <TableCell sortDirection={classificationRulesTableSort?.key === 'id' ? classificationRulesTableSort.dir : false}>
                                <TableSortLabel
                                  active={classificationRulesTableSort?.key === 'id'}
                                  direction={classificationRulesTableSort?.key === 'id' ? classificationRulesTableSort.dir : 'asc'}
                                  onClick={() => setClassificationRulesTableSort((prev) => nextSortState(prev, 'id'))}
                                >
                                  ID
                                </TableSortLabel>
                              </TableCell>
                              <TableCell sortDirection={classificationRulesTableSort?.key === 'name' ? classificationRulesTableSort.dir : false}>
                                <TableSortLabel
                                  active={classificationRulesTableSort?.key === 'name'}
                                  direction={classificationRulesTableSort?.key === 'name' ? classificationRulesTableSort.dir : 'asc'}
                                  onClick={() => setClassificationRulesTableSort((prev) => nextSortState(prev, 'name'))}
                                >
                                  Rule name
                                </TableSortLabel>
                              </TableCell>
                              <TableCell sx={RULE_ACCOUNT_COLUMN_SX} sortDirection={classificationRulesTableSort?.key === 'account' ? classificationRulesTableSort.dir : false}>
                                <TableSortLabel
                                  active={classificationRulesTableSort?.key === 'account'}
                                  direction={classificationRulesTableSort?.key === 'account' ? classificationRulesTableSort.dir : 'asc'}
                                  onClick={() => setClassificationRulesTableSort((prev) => nextSortState(prev, 'account'))}
                                >
                                  Bank /n A/C
                                </TableSortLabel>
                              </TableCell>
                              <TableCell sortDirection={classificationRulesTableSort?.key === 'canonicalSupplier' ? classificationRulesTableSort.dir : false}>
                                <TableSortLabel
                                  active={classificationRulesTableSort?.key === 'canonicalSupplier'}
                                  direction={classificationRulesTableSort?.key === 'canonicalSupplier' ? classificationRulesTableSort.dir : 'asc'}
                                  onClick={() => setClassificationRulesTableSort((prev) => nextSortState(prev, 'canonicalSupplier'))}
                                >
                                  Canonical Supplier
                                </TableSortLabel>
                              </TableCell>
                              <TableCell sortDirection={classificationRulesTableSort?.key === 'supplier' ? classificationRulesTableSort.dir : false}>
                                <TableSortLabel
                                  active={classificationRulesTableSort?.key === 'supplier'}
                                  direction={classificationRulesTableSort?.key === 'supplier' ? classificationRulesTableSort.dir : 'asc'}
                                  onClick={() => setClassificationRulesTableSort((prev) => nextSortState(prev, 'supplier'))}
                                >
                                  Merchant / Counterparty Match
                                </TableSortLabel>
                              </TableCell>
                              <TableCell sortDirection={classificationRulesTableSort?.key === 'description' ? classificationRulesTableSort.dir : false}>
                                <TableSortLabel
                                  active={classificationRulesTableSort?.key === 'description'}
                                  direction={classificationRulesTableSort?.key === 'description' ? classificationRulesTableSort.dir : 'asc'}
                                  onClick={() => setClassificationRulesTableSort((prev) => nextSortState(prev, 'description'))}
                                >
                                  Description Text Match
                                </TableSortLabel>
                              </TableCell>
                              <TableCell>Reference Text Match</TableCell>
                              <TableCell
                                sortDirection={classificationRulesTableSort?.key === 'otherCriteria' ? classificationRulesTableSort.dir : false}
                              >
                                <TableSortLabel
                                  active={classificationRulesTableSort?.key === 'otherCriteria'}
                                  direction={classificationRulesTableSort?.key === 'otherCriteria' ? classificationRulesTableSort.dir : 'asc'}
                                  onClick={() => setClassificationRulesTableSort((prev) => nextSortState(prev, 'otherCriteria'))}
                                >
                                  Other Criteria
                                </TableSortLabel>
                              </TableCell>
                              <TableCell
                                sortDirection={classificationRulesTableSort?.key === 'category' ? classificationRulesTableSort.dir : false}
                                sx={OUTPUT_COLUMN_SX}
                              >
                                <TableSortLabel
                                  active={classificationRulesTableSort?.key === 'category'}
                                  direction={classificationRulesTableSort?.key === 'category' ? classificationRulesTableSort.dir : 'asc'}
                                  onClick={() => setClassificationRulesTableSort((prev) => nextSortState(prev, 'category'))}
                                >
                                  Output: Category
                                </TableSortLabel>
                              </TableCell>
                              <TableCell>Status</TableCell>
                              <TableCell>Source</TableCell>
                              <TableCell align="right" sx={{  whiteSpace: 'nowrap' }}>
                                Actions
                              </TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {sortedVisibleClassificationProposedRows.length === 0 ? (
                              <TableRow>
                                <TableCell colSpan={14}>No proposed classification rules for the current filters.</TableCell>
                              </TableRow>
                            ) : (
                              (() => {
                                const proposalCategoryFallbackId =
                                  categories.find((c) => !isTopLevelCashflowHeadingName(c.name))?.id || 0;
                                return sortedVisibleClassificationProposedRows.map((entry, idx) => {
                                  const row = entry.proposal as RuleProposalRow;
                                  const p = row.payload || {};
                                  const cat =
                                    row.category_name ||
                                    (p.category_name as string) ||
                                    (p.category as string) ||
                                    '—';
                                  const supplierRaw = row.supplier_contains ?? (p.supplier_contains as string) ?? '';
                                  const descriptionRaw = row.description_contains ?? (p.description_contains as string) ?? '';
                                  const referenceRaw = row.reference_contains ?? (p.reference_contains as string) ?? '';
                                  const supplierOp = String(p.text_match_operator || 'exact');
                                  const descriptionOp = String(
                                    p.description_text_match_operator || p.text_match_operator || 'exact'
                                  );
                                  const referenceOp = String(
                                    p.reference_text_match_operator || p.text_match_operator || 'exact'
                                  );
                                  const proposalPriority = Number(
                                    row.priority ?? (p.priority as number) ?? 20
                                  );
                                  const proposalCanonNames = classificationProposalCanonicalSupplierNames(row);
                                  const busy = proposalBusyKey === `p-${row.id ?? 'v'}-${idx}`;
                                  const vkey = row.virtual && row.atlas_key ? row.atlas_key : '';
                                  const hasOtherCriteria = classificationProposalHasOtherCriteria(row);
                                  return (
                                    <TableRow
                                      key={entry.key}
                                      sx={ruleTableRowSavingSx(
                                        theme,
                                        ruleTableSaveHighlight?.kind === 'classification' &&
                                          ruleTableSaveHighlight.rowKey === entry.key
                                      )}
                                    >
                                      <TableCell padding="checkbox">
                                        <input
                                          type="checkbox"
                                          checked={selectedClassificationRuleKeys.includes(entry.key)}
                                          onChange={() => undefined}
                                          onClick={(e) => {
                                            e.preventDefault();
                                            const selected = selectedClassificationRuleKeys.includes(entry.key);
                                            applyRulesRowCheckboxSelection({
                                              tableKey: 'cls-proposed',
                                              orderedKeys: sortedVisibleClassificationProposedRows.map((r) => r.key),
                                              clickedKey: entry.key,
                                              checked: !selected,
                                              shiftKey: e.shiftKey,
                                              setSelection: setSelectedClassificationRuleKeys
                                            });
                                          }}
                                        />
                                      </TableCell>
                                      <TableCell>{`${proposalPriority} (${supplierRulePriorityBand(proposalPriority)})`}</TableCell>
                                      <TableCell>
                                        <Typography
                                          component="span"
                                          variant="body2"
                                          title={
                                            row.virtual && row.atlas_key
                                              ? `${classificationProposalStableIdLabel(row)} · ${String(row.atlas_key)}`
                                              : row.id != null
                                                ? `Database id ${row.id} (${classificationProposalStableIdLabel(row)})`
                                                : undefined
                                          }
                                        >
                                          {classificationProposalStableIdLabel(row)}
                                        </Typography>
                                      </TableCell>
                                      <TableCell>{row.proposed_name}</TableCell>
                                      <TableCell sx={RULE_ACCOUNT_COLUMN_SX}>
                                        {ruleAccountScopeTableLabel(String((p.account_scope as string) || row.account_scope || 'both'))}
                                      </TableCell>
                                      <TableCell>
                                        {classificationCanonicalSupplierChips({
                                          names: proposalCanonNames,
                                          chipKeyPrefix: `cp-${entry.key}-canon`,
                                          busy: busy,
                                          onNameClick: openSuppliersTabForName
                                        })}
                                      </TableCell>
                                      <TableCell>
                                        {classificationRuleTextMatchChips({
                                          parts: classificationRuleOrFieldParts(supplierRaw),
                                          operator: supplierOp,
                                          chipKeyPrefix: `cp-${entry.key}-merch`
                                        })}
                                      </TableCell>
                                      <TableCell>
                                        {classificationRuleTextMatchChips({
                                          parts: classificationRuleOrFieldParts(descriptionRaw),
                                          operator: descriptionOp,
                                          chipKeyPrefix: `cp-${entry.key}-desc`
                                        })}
                                      </TableCell>
                                      <TableCell>
                                        {classificationRuleTextMatchChips({
                                          parts: classificationRuleOrFieldParts(referenceRaw),
                                          operator: referenceOp,
                                          chipKeyPrefix: `cp-${entry.key}-ref`
                                        })}
                                      </TableCell>
                                      <TableCell>
                                        <Tooltip title="Amount range, currency, recurrence, or rule-level VAT rate beyond the supplier/text columns.">
                                          <span>
                                            <Chip
                                              size="small"
                                              label={hasOtherCriteria ? 'Yes' : 'No'}
                                              color={hasOtherCriteria ? 'info' : 'default'}
                                              variant={hasOtherCriteria ? 'filled' : 'outlined'}
                                            />
                                          </span>
                                        </Tooltip>
                                      </TableCell>
                                      <TableCell sx={OUTPUT_COLUMN_SX}>
                                        {cat !== '—' ? (
                                          <Tooltip title="Open category in Categories tab. Use Edit to change this proposal.">
                                            <span>
                                              <Chip
                                                size="small"
                                                variant="outlined"
                                                label={cat}
                                                clickable
                                                onClick={() => openCategoriesTabForName(cat)}
                                              />
                                            </span>
                                          </Tooltip>
                                        ) : (
                                          <Chip size="small" variant="outlined" label="—" />
                                        )}
                                      </TableCell>
                                      <TableCell>
                                        <Chip size="small" label="Proposed" color="warning" />
                                      </TableCell>
                                      <TableCell>
                                        <Chip
                                          size="small"
                                          label={row.source === 'atlas' ? 'Atlas' : 'Your draft'}
                                          color={row.source === 'atlas' ? 'info' : 'default'}
                                        />
                                      </TableCell>
                                      <TableCell align="right" sx={{  whiteSpace: 'nowrap' }}>
                                        <Stack direction="row" spacing={0.5} justifyContent="flex-end" flexWrap="nowrap">
                                          <Tooltip
                                            title={
                                              proposalRowCanListMatches(row)
                                                ? 'View matching transactions'
                                                : 'Add a text match, amount range, or canonical supplier to list transactions.'
                                            }
                                          >
                                            <span>
                                              <IconButton
                                                size="small"
                                                color="primary"
                                                aria-label="View matching transactions"
                                                disabled={
                                                  !!proposalBusyKey || loading || !proposalRowCanListMatches(row)
                                                }
                                                onClick={() => void openProposalMatchingTransactionsModal(row)}
                                              >
                                                <VisibilityIcon fontSize="small" />
                                              </IconButton>
                                            </span>
                                          </Tooltip>
                                          <IconButton
                                            size="small"
                                            color="primary"
                                            disabled={!!proposalBusyKey || loading}
                                            onClick={() => {
                                              setRuleEditError('');
                                              setRuleEditSaveInFlight(false);
                                              setRuleEditOutputMenuAnchorEl(null);
                                              setRuleEditOutputCategoryEnabled(true);
                                              setRuleEditDraft(
                                                classificationProposalToEditDraft(
                                                  row,
                                                  categories,
                                                  proposalCategoryFallbackId
                                                )
                                              );
                                            }}
                                          >
                                            <EditIcon fontSize="small" />
                                          </IconButton>
                                          <Tooltip title="Duplicate as new rule">
                                            <span>
                                              <IconButton
                                                size="small"
                                                color="primary"
                                                aria-label="Duplicate proposed classification rule"
                                                disabled={!!proposalBusyKey || loading || !!ruleDuplicateBusyKey}
                                                onClick={() =>
                                                  void duplicateClassificationProposalAsRule(
                                                    row,
                                                    proposalCategoryFallbackId
                                                  )
                                                }
                                              >
                                                {ruleDuplicateBusyKey ===
                                                `dup-cp-${row.id ?? 'v'}-${row.virtual ? String(row.atlas_key || '') : 'db'}` ? (
                                                  <CircularProgress size={14} color="inherit" />
                                                ) : (
                                                  <ContentCopyIcon fontSize="small" />
                                                )}
                                              </IconButton>
                                            </span>
                                          </Tooltip>
                                          <Button
                                            size="small"
                                            variant="contained"
                                            color="success"
                                            disabled={!!proposalBusyKey || loading}
                                            onClick={async () => {
                                              try {
                                                let acceptedRule: ClassificationRule | undefined;
                                                if (row.virtual && vkey) {
                                                  setProposalBusyKey(`p-${row.id ?? 'v'}-${idx}`);
                                                  const res = await api.post(
                                                    '/api/transaction-classification-rules/rule-proposals/accept-atlas',
                                                    { atlas_key: vkey }
                                                  );
                                                  acceptedRule = res.data?.rule as ClassificationRule | undefined;
                                                } else if (row.id) {
                                                  setProposalBusyKey(`p-${row.id ?? 'v'}-${idx}`);
                                                  const res = await api.post(`/api/transaction-classification-rules/rule-proposals/${row.id}/accept`, {});
                                                  acceptedRule = res.data?.rule as ClassificationRule | undefined;
                                                }
                                                await refreshRulesTabDatasets();
                                                const acceptedLabel = formatClassificationRuleToastLabel(
                                                  acceptedRule,
                                                  row.proposed_name || 'accepted rule'
                                                );
                                                if (!row.virtual && row.id) {
                                                  const accProposalNum = Math.trunc(Number(row.id));
                                                  if (Number.isFinite(accProposalNum) && accProposalNum > 0) {
                                                    const nextP = rebuildProposalIdsAfterClassificationProposalAccepted(
                                                      rulesProposalIdsFilter,
                                                      accProposalNum
                                                    );
                                                    setRulesProposalIdsFilter(nextP);
                                                    setRulesStatusFilter(nextP.trim() ? 'all' : 'confirmed');
                                                  }
                                                }
                                                const acceptedPrefixed =
                                                  acceptedRule?.id != null
                                                    ? formatClassificationRulePrefixedId(
                                                        acceptedRule.id,
                                                        String((acceptedRule as any)?.name || row.proposed_name || '')
                                                      )
                                                    : '';
                                                if (acceptedPrefixed) setRulesIdFilter(acceptedPrefixed);
                                                {
                                                  const acceptedIdNum =
                                                    acceptedRule?.id != null
                                                      ? Math.trunc(Number(acceptedRule.id))
                                                      : NaN;
                                                  openBackfillAfterAccept({
                                                    ruleName: String(
                                                      (acceptedRule as any)?.name ||
                                                        row.proposed_name ||
                                                        'accepted rule'
                                                    ),
                                                    prefixedId: acceptedPrefixed,
                                                    target:
                                                      Number.isFinite(acceptedIdNum) && acceptedIdNum > 0
                                                        ? { kind: 'classification', id: acceptedIdNum }
                                                        : null
                                                  });
                                                }
                                                setSuccessMessage(`Accepted rule: ${acceptedLabel}.`);
                                              } catch (err: any) {
                                                const data = err?.response?.data;
                                                const st = err?.response?.status;
                                                const rawCid = data?.conflict_rule_id;
                                                const cid = rawCid != null ? Math.trunc(Number(rawCid)) : NaN;
                                                const conflictReason = String(data?.conflict_reason || '').trim();
                                                const baseMessage = getHttpErrorMessageForUser(
                                                  err,
                                                  'Could not accept proposal'
                                                );
                                                const message = conflictReason
                                                  ? `${baseMessage} Why: ${conflictReason}`
                                                  : baseMessage;
                                                if (st === 409 && Number.isFinite(cid) && cid > 0) {
                                                  const draft = classificationProposalToEditDraft(
                                                    row,
                                                    categories,
                                                    proposalCategoryFallbackId
                                                  );
                                                  setRuleEditError(message);
                                                  setRuleEditConflictRuleId(cid);
                                                  setRuleEditReadOnly(false);
                                                  setRuleEditSaveInFlight(false);
                                                  setRuleEditTargetType('classification');
                                                  setRuleEditOutputCategoryEnabled(true);
                                                  setRuleEditDraft(draft);
                                                  setRuleEditMatchCriteriaEnabled(
                                                    initialRuleEditMatchCriteriaEnabled(draft)
                                                  );
                                                  void openClassificationConflictSideBySide(cid);
                                                  setStackedToasts((prev) => [
                                                    ...prev,
                                                    {
                                                      id: stackedToastIdRef.current++,
                                                      code: nextClientToastCode(),
                                                      severity: 'error',
                                                      message,
                                                      actionLabel: 'Compare',
                                                      onAction: () => {
                                                        void openClassificationConflictSideBySide(cid);
                                                      }
                                                    }
                                                  ]);
                                                } else {
                                                  setError(message);
                                                }
                                              } finally {
                                                setProposalBusyKey('');
                                              }
                                            }}
                                          >
                                            {busy ? '…' : 'Accept'}
                                          </Button>
                                          <Button
                                            size="small"
                                            variant="outlined"
                                            onClick={async () => {
                                              try {
                                                if (row.virtual && vkey) {
                                                  setProposalBusyKey(`p-${row.id ?? 'v'}-${idx}`);
                                                  await api.post(
                                                    '/api/transaction-classification-rules/rule-proposals/reject-atlas',
                                                    { atlas_key: vkey }
                                                  );
                                                } else if (row.id) {
                                                  setProposalBusyKey(`p-${row.id ?? 'v'}-${idx}`);
                                                  await api.post(`/api/transaction-classification-rules/rule-proposals/${row.id}/reject`, {});
                                                }
                                                await refreshRulesTabDatasets();
                                                setSuccessMessage('Proposal dismissed.');
                                              } catch (err: any) {
                                                setError(err?.response?.data?.error || err?.message || 'Could not reject proposal');
                                              } finally {
                                                setProposalBusyKey('');
                                              }
                                            }}
                                          >
                                            Reject
                                          </Button>
                                        </Stack>
                                      </TableCell>
                                    </TableRow>
                                  );
                                });
                              })()
                            )}
                          </TableBody>
                        </Table>
                      </TableContainer>
                      <TableContainer sx={{ display: 'none' }}>
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell>Source</TableCell>
                          <TableCell>Rule name</TableCell>
                          <TableCell>Category</TableCell>
                          <TableCell>Supplier match</TableCell>
                          <TableCell>Description match</TableCell>
                          <TableCell>Priority</TableCell>
                          <TableCell align="right">Actions</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                            {filteredRuleProposals.length === 0 ? (
                          <TableRow>
                            <TableCell colSpan={7}>
                                  No pending rule proposals for the current filters.
                            </TableCell>
                          </TableRow>
                        ) : (
                              filteredRuleProposals.map((row, idx) => {
                            const p = row.payload || {};
                            const cat =
                              row.category_name ||
                              (p.category_name as string) ||
                              (p.category as string) ||
                              '—';
                            const sup = row.supplier_contains ?? (p.supplier_contains as string) ?? '—';
                            const desc = row.description_contains ?? (p.description_contains as string) ?? '—';
                            const pri = row.priority ?? (p.priority as number) ?? 20;
                            const busy = proposalBusyKey === `p-${row.id ?? 'v'}-${idx}`;
                            const vkey = row.virtual && row.atlas_key ? row.atlas_key : '';
                            return (
                              <TableRow key={`${row.virtual ? 'v' : 'd'}-${row.id ?? row.atlas_key ?? idx}`}>
                                <TableCell>
                                  <Chip
                                    size="small"
                                    label={row.source === 'atlas' ? 'Atlas' : 'Your draft'}
                                    color={row.source === 'atlas' ? 'info' : 'default'}
                                  />
                                </TableCell>
                                <TableCell>{row.proposed_name}</TableCell>
                                <TableCell>{cat}</TableCell>
                                <TableCell>{sup || '—'}</TableCell>
                                <TableCell>{desc || '—'}</TableCell>
                                <TableCell>{pri}</TableCell>
                                <TableCell align="right">
                                  <Stack direction="row" spacing={0.5} justifyContent="flex-end">
                                    <IconButton
                                      size="small"
                                      color="primary"
                                      disabled={!!proposalBusyKey || loading}
                                      onClick={() => {
                                        const fallbackCategoryId =
                                          categories.find((c) => !isTopLevelCashflowHeadingName(c.name))?.id || 0;
                                        setRuleEditError('');
                                        setRuleEditSaveInFlight(false);
                                        setRuleEditOutputMenuAnchorEl(null);
                                        setRuleEditOutputCategoryEnabled(true);
                                        setRuleEditDraft(
                                          classificationProposalToEditDraft(row, categories, fallbackCategoryId)
                                        );
                                      }}
                                    >
                                      <EditIcon fontSize="small" />
                                    </IconButton>
                                    <Button
                                      size="small"
                                      variant="contained"
                                      color="success"
                                      disabled={!!proposalBusyKey || loading}
                                      onClick={async () => {
                                        try {
                                          let acceptedRule: ClassificationRule | undefined;
                                          if (row.virtual && vkey) {
                                            setProposalBusyKey(`p-${row.id ?? 'v'}-${idx}`);
                                            const res = await api.post(
                                              '/api/transaction-classification-rules/rule-proposals/accept-atlas',
                                              { atlas_key: vkey }
                                            );
                                            acceptedRule = res.data?.rule as ClassificationRule | undefined;
                                          } else if (row.id) {
                                            setProposalBusyKey(`p-${row.id ?? 'v'}-${idx}`);
                                            const res = await api.post(
                                              `/api/transaction-classification-rules/rule-proposals/${row.id}/accept`,
                                              {}
                                            );
                                            acceptedRule = res.data?.rule as ClassificationRule | undefined;
                                          }
                                          const acceptedLabel = formatClassificationRuleToastLabel(
                                            acceptedRule,
                                            row.proposed_name || 'accepted rule'
                                          );
                                          setSuccessMessage(`Accepted rule: ${acceptedLabel}.`);
                                          const pr = await api.get(
                                            '/api/transaction-classification-rules/rule-proposals'
                                          );
                                          setProposalsPayload(pr.data);
                                          const rr = await api.get('/api/transaction-classification-rules/');
                                          setRulesPayload(rr.data);
                                          if (!row.virtual && row.id) {
                                            const accProposalNum = Math.trunc(Number(row.id));
                                            if (Number.isFinite(accProposalNum) && accProposalNum > 0) {
                                              const nextP = rebuildProposalIdsAfterClassificationProposalAccepted(
                                                rulesProposalIdsFilter,
                                                accProposalNum
                                              );
                                              setRulesProposalIdsFilter(nextP);
                                              setRulesStatusFilter(nextP.trim() ? 'all' : 'confirmed');
                                            }
                                          }
                                          const acceptedPrefixedQuick =
                                            acceptedRule?.id != null
                                              ? formatClassificationRulePrefixedId(
                                                  acceptedRule.id,
                                                  String((acceptedRule as any)?.name || row.proposed_name || '')
                                                )
                                              : '';
                                          if (acceptedPrefixedQuick) setRulesIdFilter(acceptedPrefixedQuick);
                                          {
                                            const acceptedIdNumQuick =
                                              acceptedRule?.id != null
                                                ? Math.trunc(Number(acceptedRule.id))
                                                : NaN;
                                            openBackfillAfterAccept({
                                              ruleName: String(
                                                (acceptedRule as any)?.name ||
                                                  row.proposed_name ||
                                                  'accepted rule'
                                              ),
                                              prefixedId: acceptedPrefixedQuick,
                                              target:
                                                Number.isFinite(acceptedIdNumQuick) && acceptedIdNumQuick > 0
                                                  ? { kind: 'classification', id: acceptedIdNumQuick }
                                                  : null
                                            });
                                          }
                                        } catch (err: any) {
                                          const data = err?.response?.data;
                                          const st = err?.response?.status;
                                          const rawCid = data?.conflict_rule_id;
                                          const cid = rawCid != null ? Math.trunc(Number(rawCid)) : NaN;
                                          const conflictReason = String(data?.conflict_reason || '').trim();
                                          const baseMessage = getHttpErrorMessageForUser(
                                            err,
                                            'Could not accept proposal'
                                          );
                                          const message = conflictReason
                                            ? `${baseMessage} Why: ${conflictReason}`
                                            : baseMessage;
                                          if (st === 409 && Number.isFinite(cid) && cid > 0) {
                                            const fallbackCategoryId =
                                              categories.find((c) => !isTopLevelCashflowHeadingName(c.name))?.id ||
                                              0;
                                            const draft = classificationProposalToEditDraft(
                                              row,
                                              categories,
                                              fallbackCategoryId
                                            );
                                            setRuleEditError(message);
                                            setRuleEditConflictRuleId(cid);
                                            setRuleEditReadOnly(false);
                                            setRuleEditSaveInFlight(false);
                                            setRuleEditTargetType('classification');
                                            setRuleEditOutputCategoryEnabled(true);
                                            setRuleEditDraft(draft);
                                            setRuleEditMatchCriteriaEnabled(
                                              initialRuleEditMatchCriteriaEnabled(draft)
                                            );
                                            void openClassificationConflictSideBySide(cid);
                                            setStackedToasts((prev) => [
                                              ...prev,
                                              {
                                                id: stackedToastIdRef.current++,
                                                code: nextClientToastCode(),
                                                severity: 'error',
                                                message,
                                                actionLabel: 'Compare',
                                                onAction: () => {
                                                  void openClassificationConflictSideBySide(cid);
                                                }
                                              }
                                            ]);
                                          } else {
                                            setError(message);
                                          }
                                        } finally {
                                          setProposalBusyKey('');
                                        }
                                      }}
                                    >
                                      {busy ? '…' : 'Accept'}
                                    </Button>
                                    <Button
                                      size="small"
                                      variant="outlined"
                                      color="inherit"
                                      disabled={!!proposalBusyKey || loading}
                                      onClick={async () => {
                                        try {
                                          if (row.virtual && vkey) {
                                            setProposalBusyKey(`p-${row.id ?? 'v'}-${idx}`);
                                            await api.post(
                                              '/api/transaction-classification-rules/rule-proposals/reject-atlas',
                                              { atlas_key: vkey }
                                            );
                                            setSuccessMessage(`Rejected atlas suggestion: ${row.proposed_name}`);
                                          } else if (row.id) {
                                            setProposalBusyKey(`p-${row.id ?? 'v'}-${idx}`);
                                            await api.post(
                                              `/api/transaction-classification-rules/rule-proposals/${row.id}/reject`,
                                              {}
                                            );
                                            setSuccessMessage('Proposal dismissed.');
                                          }
                                          const pr = await api.get(
                                            '/api/transaction-classification-rules/rule-proposals'
                                          );
                                          setProposalsPayload(pr.data);
                                        } catch (err: any) {
                                          setError(
                                            err?.response?.data?.error || err?.message || 'Could not reject proposal'
                                          );
                                        } finally {
                                          setProposalBusyKey('');
                                        }
                                      }}
                                    >
                                      Reject
                                    </Button>
                                  </Stack>
                                </TableCell>
                              </TableRow>
                            );
                          })
                        )}
                      </TableBody>
                    </Table>
                  </TableContainer>
                      </PipelineNestedSectionAccordion>
                      <PipelineNestedSectionAccordion
                        filtersActive={rulesHasFilters}
                        title="Rejected"
                        count={filteredRejectedClassificationProposals.length}
                        expanded={pipelineNestedSectionOpen.step3.rejected}
                        onExpandedChange={(open) => setPipelineNestedSection('step3', 'rejected', open)}
                        info={
                          <Tooltip
                            title={
                              <Typography variant="body2">
                                Rejected classification drafts (including dismissed atlas keys) act as tombstones:
                                Generate will not recreate the same draft until you Restore or Delete.
                              </Typography>
                            }
                          >
                            <InfoOutlinedIcon fontSize="small" sx={{ color: 'text.secondary' }} />
                          </Tooltip>
                        }
                      >
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                          These do not tag transactions. Restore puts a draft back in Proposed; Delete clears the
                          tombstone so the next Generate can recreate it.
                        </Typography>
                        {selectedRejectedClassificationProposalIds.length > 0 ? (
                          <Paper
                            role="region"
                            aria-label="Bulk actions for selected rejected classification proposals"
                            variant="outlined"
                            sx={{ p: 1.5, mb: 1, bgcolor: 'action.hover' }}
                          >
                            <Stack spacing={1}>
                              <Typography variant="subtitle2" color="text.secondary" sx={{ fontWeight: 600 }}>
                                {selectedRejectedClassificationProposalIds.length} rejected proposal
                                {selectedRejectedClassificationProposalIds.length === 1 ? '' : 's'} selected
                              </Typography>
                              <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                                <Button
                                  size="small"
                                  variant="outlined"
                                  color="primary"
                                  disabled={!!proposalBusyKey || loading}
                                  onClick={() => void runBulkRejectedClassificationProposalAction('restore')}
                                >
                                  {proposalBusyKey === 'bulk-classification-rejected-restore' ? '…' : 'Restore'}
                                </Button>
                                <Button
                                  size="small"
                                  variant="outlined"
                                  color="error"
                                  disabled={!!proposalBusyKey || loading}
                                  onClick={() => void runBulkRejectedClassificationProposalAction('delete')}
                                >
                                  {proposalBusyKey === 'bulk-classification-rejected-delete' ? '…' : 'Delete'}
                                </Button>
                              </Stack>
                            </Stack>
                          </Paper>
                        ) : null}
                        <TableContainer sx={{ overflowX: 'hidden', maxWidth: '100%' }}>
                          <Table size="small" sx={FIT_TABLE_SX}>
                            <FitColgroup cols={equalFitCols(['c0', 'c1', 'c2', 'c3', 'c4', 'c5', 'c6'], { flexKey: 'c3' })} />
                          <TableHead>
                              <TableRow>
                                <TableCell padding="checkbox" align="center">
                                  <input
                                    type="checkbox"
                                    aria-label="Select all visible rejected classification proposals"
                                    disabled={
                                      selectableRejectedClassificationProposalIds.length === 0 || !!proposalBusyKey
                                    }
                                    checked={
                                      selectableRejectedClassificationProposalIds.length > 0 &&
                                      selectableRejectedClassificationProposalIds.every((id) =>
                                        selectedRejectedClassificationProposalIds.includes(id)
                                      )
                                    }
                                    onChange={(e) => {
                                      if (e.target.checked) {
                                        setSelectedRejectedClassificationProposalIds(
                                          selectableRejectedClassificationProposalIds.slice()
                                        );
                                      } else {
                                        setSelectedRejectedClassificationProposalIds([]);
                                      }
                                    }}
                                  />
                                </TableCell>
                                <TableCell align="center">Status</TableCell>
                                <TableCell align="center">ID</TableCell>
                                <TableCell align="center">Source</TableCell>
                                <TableCell align="center">Rule name</TableCell>
                                <TableCell align="center">Category</TableCell>
                                <TableCell align="center">Actions</TableCell>
                              </TableRow>
                            </TableHead>
                            <TableBody>
                              {filteredRejectedClassificationProposals.length === 0 ? (
                                <TableRow>
                                  <TableCell colSpan={7}>
                                    No rejected classification proposals for the current filters.
                                  </TableCell>
                                </TableRow>
                              ) : (
                                filteredRejectedClassificationProposals.map((row, idx) => {
                                  const p = row.payload || {};
                                  const categoryName =
                                    String(
                                      row.category_name ||
                                        (p.category_name as string) ||
                                        (p.category as string) ||
                                        ''
                                    ).trim() || '—';
                                  const rowPid =
                                    row.id != null && Number(row.id) > 0 ? Math.trunc(Number(row.id)) : null;
                                  const idLabel = classificationProposalStableIdLabel(row);
                                  const busy =
                                    rowPid != null && proposalBusyKey === `cls-rej-${rowPid}-${idx}`;
                                  const selected =
                                    rowPid != null &&
                                    selectedRejectedClassificationProposalIds.includes(rowPid);
                                  return (
                                    <TableRow
                                      key={`cls-rej-${rowPid ?? row.atlas_key ?? idx}`}
                                      selected={selected}
                                    >
                                      <TableCell padding="checkbox" align="center">
                                        <input
                                          type="checkbox"
                                          aria-label={`Select rejected proposal ${idLabel}`}
                                          disabled={rowPid == null || !!proposalBusyKey}
                                          checked={selected}
                                          onChange={() => undefined}
                                          onClick={(e) => {
                                            e.preventDefault();
                                            if (rowPid == null) return;
                                            applyRulesRowCheckboxSelection({
                                              tableKey: 'classification-rejected',
                                              orderedKeys: selectableRejectedClassificationProposalIds,
                                              clickedKey: rowPid,
                                              checked: !selected,
                                              shiftKey: e.shiftKey,
                                              setSelection: setSelectedRejectedClassificationProposalIds
                                            });
                                          }}
                                        />
                                      </TableCell>
                                      <TableCell align="center">
                                        <Chip size="small" label="Rejected" color="default" />
                                      </TableCell>
                                      <TableCell align="center">{idLabel}</TableCell>
                                      <TableCell align="center">
                                        {String(row.source || '').trim() || '—'}
                                      </TableCell>
                                      <TableCell align="center">
                                        {String(row.proposed_name || (p.name as string) || '').trim() ||
                                          String(row.atlas_key || '').trim() ||
                                          '—'}
                                      </TableCell>
                                      <TableCell align="center">{categoryName}</TableCell>
                                      <TableCell align="center">
                                        <Stack
                                          direction="row"
                                          spacing={0.5}
                                          justifyContent="center"
                                          flexWrap="wrap"
                                          useFlexGap
                                        >
                                          <Button
                                            size="small"
                                            variant="outlined"
                                            color="primary"
                                            disabled={!!proposalBusyKey || loading || rowPid == null}
                                            onClick={async () => {
                                              if (rowPid == null) return;
                                              try {
                                                setProposalBusyKey(`cls-rej-${rowPid}-${idx}`);
                                                await api.post(
                                                  `/api/transaction-classification-rules/rule-proposals/${rowPid}/restore`,
                                                  {}
                                                );
                                                await refreshRulesTabDatasets();
                                                setSelectedRejectedClassificationProposalIds((prev) =>
                                                  prev.filter((id) => id !== rowPid)
                                                );
                                                setSuccessMessage(`Restored ${idLabel} to Proposed.`);
                                              } catch (err: any) {
                                                setError(
                                                  err?.response?.data?.error ||
                                                    err?.message ||
                                                    'Could not restore classification proposal'
                                                );
                                                scrollPageToClassificationErrorBanner();
                                              } finally {
                                                setProposalBusyKey('');
                                              }
                                            }}
                                          >
                                            {busy && proposalBusyKey.startsWith('cls-rej-') ? '…' : 'Restore'}
                                          </Button>
                                          <Button
                                            size="small"
                                            variant="outlined"
                                            color="error"
                                            disabled={!!proposalBusyKey || loading || rowPid == null}
                                            onClick={async () => {
                                              if (rowPid == null) return;
                                              const isAtlas = String(row.source || '').trim().toLowerCase() === 'atlas';
                                              if (
                                                !window.confirm(
                                                  isAtlas
                                                    ? `Permanently dismiss ${idLabel}? It will stay hidden and will not return to Proposed.`
                                                    : `Delete rejected proposal ${idLabel} permanently? Generate can recreate a matching draft afterward.`
                                                )
                                              ) {
                                                return;
                                              }
                                              try {
                                                setProposalBusyKey(`cls-rej-${rowPid}-${idx}`);
                                                await api.delete(
                                                  `/api/transaction-classification-rules/rule-proposals/${rowPid}`
                                                );
                                                await refreshRulesTabDatasets();
                                                setSelectedRejectedClassificationProposalIds((prev) =>
                                                  prev.filter((id) => id !== rowPid)
                                                );
                                                setSuccessMessage(
                                                  isAtlas
                                                    ? `Permanently dismissed ${idLabel}.`
                                                    : `Deleted rejected ${idLabel}.`
                                                );
                                              } catch (err: any) {
                                                setError(
                                                  err?.response?.data?.error ||
                                                    err?.message ||
                                                    'Could not delete classification proposal'
                                                );
                                                scrollPageToClassificationErrorBanner();
                                              } finally {
                                                setProposalBusyKey('');
                                              }
                                            }}
                                          >
                                            Delete
                                          </Button>
                                        </Stack>
                                      </TableCell>
                                    </TableRow>
                                  );
                                })
                              )}
                            </TableBody>
                          </Table>
                        </TableContainer>
                      </PipelineNestedSectionAccordion>

                    </AccordionDetails>
                  </Accordion>

                  <Accordion
                    expanded={rulesAccordionOpen.pipelineStepRules}
                    onChange={(_, expanded) =>
                      setRulesAccordionOpen((prev) => ({ ...prev, pipelineStepRules: expanded }))
                    }
                    sx={{ mb: 1.5, order: 0 }}
                  >
                    <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                      <Stack direction="row" spacing={0.75} alignItems="center">
                        <Tooltip title="Select/deselect all visible Cashflow rules in this accordion.">
                          <span>
                            <Checkbox
                              size="small"
                              checked={pipelineClassificationGroupAllSelected}
                              indeterminate={pipelineClassificationGroupIndeterminate}
                              disabled={pipelineClassificationGroupSelectableCount === 0}
                              onClick={(e) => e.stopPropagation()}
                              onFocus={(e) => e.stopPropagation()}
                              onChange={() => togglePipelineClassificationGroupSelection()}
                              inputProps={{ 'aria-label': 'Select all Cashflow rules in this accordion' }}
                            />
                          </span>
                        </Tooltip>
                        <Typography variant="h6">Pipeline Step 1: Cashflow Rules</Typography>
                        <Tooltip
                          title={
                            <Typography variant="body2">
                              These rules run first to bucket transactions into top-level In/Out (cashflow direction).
                            </Typography>
                          }
                        >
                          <InfoOutlinedIcon fontSize="small" sx={{ color: 'text.secondary' }} />
                        </Tooltip>
                        <Typography component="span" variant="body2" color="text.secondary" sx={{ fontWeight: 400 }}>
                          {formatPipelineStageCounts(
                            pipelineClassificationConfirmedRows.length,
                            0,
                            0,
                            rulesHasFilters
                          )}
                        </Typography>
                      </Stack>
                    </AccordionSummary>
                    <AccordionDetails>
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                        These rules run first to bucket transactions into top-level In/Out (cashflow direction).
                      </Typography>
                      <PipelineNestedSectionAccordion
                        filtersActive={rulesHasFilters}
                        title="Confirmed"
                        count={pipelineClassificationConfirmedRows.length}
                        expanded={pipelineNestedSectionOpen.step1.confirmed}
                        onExpandedChange={(open) => setPipelineNestedSection('step1', 'confirmed', open)}
                        first
                      >
                      <TableContainer sx={{ overflowX: 'hidden', maxWidth: '100%' }}>
                        <Table size="small" sx={FIT_TABLE_SX}>
                          <FitColgroup cols={equalFitCols(['c0', 'c1', 'c2', 'c3', 'c4', 'c5', 'c6', 'c7'], { flexKey: 'c3' })} />
                          <TableHead>
                            <TableRow>
                              <TableCell>Priority</TableCell>
                              <TableCell>ID</TableCell>
                              <TableCell>Rule name</TableCell>
                              <TableCell sx={OUTPUT_COLUMN_SX}>Output: Category</TableCell>
                              <TableCell>Status</TableCell>
                              <TableCell>Tx fired</TableCell>
                              <TableCell>Rule Active</TableCell>
                              <TableCell align="right">Actions</TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {pipelineClassificationConfirmedRows.length === 0 ? (
                              <TableRow>
                                <TableCell colSpan={8}>
                                  No pipeline-step cashflow (In/Out) rules for the current filters.
                                </TableCell>
                              </TableRow>
                            ) : (
                              pipelineClassificationConfirmedRows.map((entry) => {
                                const rule = entry.rule as ClassificationRule;
                                const pipelineCatCanonical = String(rule.category?.name || '').trim();
                                const pipelineCatDisplay = formatCashflowInOutHeadingLabel(pipelineCatCanonical);
                                return (
                                  <TableRow
                                    key={`pipeline-${entry.key}`}
                                    sx={ruleTableRowSavingSx(
                                      theme,
                                      ruleTableSaveHighlight?.kind === 'classification' &&
                                        ruleTableSaveHighlight.rowKey === entry.key
                                    )}
                                  >
                                    <TableCell>{`${Number(rule.priority ?? 100)} (${supplierRulePriorityBand(Number(rule.priority ?? 100))})`}</TableCell>
                                    <TableCell>{formatClassificationRulePrefixedId(rule.id, rule.name)}</TableCell>
                                    <TableCell>{formatPipelineCashflowRuleDisplayName(rule.name)}</TableCell>
                                    <TableCell sx={OUTPUT_COLUMN_SX}>
                                      {pipelineCatCanonical ? (
                                        <Button
                                          size="small"
                                          variant="text"
                                          sx={{
                                            p: 0,
                                            minWidth: 0,
                                            textTransform: 'none',
                                            justifyContent: 'flex-start',
                                            alignItems: 'flex-start',
                                            width: '100%',
                                            textAlign: 'left'
                                          }}
                                          onClick={() => openCategoriesTabForName(pipelineCatCanonical)}
                                        >
                                          {pipelineCatDisplay}
                                        </Button>
                                      ) : (
                                        `Category #${rule.category_id}`
                                      )}
                                    </TableCell>
                                    <TableCell>
                                      <Chip size="small" label="Confirmed" color="success" />
                                    </TableCell>
                                    <TableCell>{Number(ruleFireCounts.classification[rule.id] || 0).toLocaleString()}</TableCell>
                                    <TableCell>{rule.is_active ? 'Active' : 'Inactive'}</TableCell>
                                    <TableCell align="right" sx={{  whiteSpace: 'nowrap' }}>
                                      <Stack direction="row" spacing={0.25} justifyContent="flex-end">
                                        <Tooltip title="View rule details (read-only)">
                                          <IconButton
                                            size="small"
                                            color="primary"
                                            aria-label="View classification rule details"
                                            disabled={loading || !!ruleDuplicateBusyKey}
                                            onClick={() => openClassificationRuleReadOnlyDialog(rule)}
                                          >
                                            <EditIcon fontSize="small" />
                                          </IconButton>
                                        </Tooltip>
                                        <Tooltip title="Duplicate rule">
                                          <span>
                                            <IconButton
                                              size="small"
                                              color="primary"
                                              aria-label="Duplicate pipeline heading rule"
                                              disabled={loading || !!ruleDuplicateBusyKey}
                                              onClick={() => void duplicateClassificationRule(rule)}
                                            >
                                              {ruleDuplicateBusyKey === `dup-cls-${rule.id}` ? (
                                                <CircularProgress size={14} color="inherit" />
                                              ) : (
                                                <ContentCopyIcon fontSize="small" />
                                              )}
                                            </IconButton>
                                          </span>
                                        </Tooltip>
                                      </Stack>
                                    </TableCell>
                                  </TableRow>
                                );
                              })
                            )}
                          </TableBody>
                        </Table>
                      </TableContainer>
                      </PipelineNestedSectionAccordion>
                      <PipelineNestedSectionAccordion
                        filtersActive={rulesHasFilters}
                        title="Proposed"
                        count={0}
                        expanded={pipelineNestedSectionOpen.step1.proposed}
                        onExpandedChange={(open) => setPipelineNestedSection('step1', 'proposed', open)}
                      >
                        <Typography variant="body2" color="text.secondary">
                          No proposed rules in this stage.
                        </Typography>
                      </PipelineNestedSectionAccordion>
                      <PipelineNestedSectionAccordion
                        filtersActive={rulesHasFilters}
                        title="Rejected"
                        count={0}
                        expanded={pipelineNestedSectionOpen.step1.rejected}
                        onExpandedChange={(open) => setPipelineNestedSection('step1', 'rejected', open)}
                      >
                        <Typography variant="body2" color="text.secondary">
                          No rejected proposals in this stage.
                        </Typography>
                      </PipelineNestedSectionAccordion>

                    </AccordionDetails>
                  </Accordion>

                  <Accordion
                    expanded={rulesAccordionOpen.merchantSuppliers}
                    onChange={(_, expanded) =>
                      setRulesAccordionOpen((prev) => ({ ...prev, merchantSuppliers: expanded }))
                    }
                    sx={{ mb: 1.5, order: 1 }}
                  >
                    <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                      <Stack direction="row" spacing={0.75} alignItems="center">
                        <Tooltip title="Select/deselect all visible merchant supplier aliases in Step 2A.">
                          <span>
                            <Checkbox
                              size="small"
                              checked={merchantSupplierGroupAllSelected}
                              indeterminate={merchantSupplierGroupIndeterminate}
                              disabled={merchantSupplierGroupSelectableCount === 0}
                              onClick={(e) => e.stopPropagation()}
                              onFocus={(e) => e.stopPropagation()}
                              onChange={() => toggleMerchantSupplierGroupSelection()}
                              inputProps={{ 'aria-label': 'Select all merchant supplier rules in this accordion' }}
                            />
                          </span>
                        </Tooltip>
                        <Typography variant="h6">Pipeline Step 2A: Suppliers from Merchant Data</Typography>
                        <Tooltip
                          title={
                            <Typography variant="body2">
                              Analyze uncommitted transactions only, preview each proposed merchant alias, then
                              approve selected rows. Matching uses whole-string equality (Spar does not match
                              EUROSPAR). New aliases are created via Analyze — not authored here. Select existing
                              aliases below for bulk Delete / Activate / Deactivate / Run. Description/reference
                              rules belong in Step 2B. Full/Gaps rule sync auto-applies 2A without this dialog.
                            </Typography>
                          }
                        >
                          <InfoOutlinedIcon fontSize="small" sx={{ color: 'text.secondary' }} />
                        </Tooltip>
                        <Typography component="span" variant="body2" color="text.secondary" sx={{ fontWeight: 400 }}>
                          {formatPipelineStageCounts(
                            visibleMerchantSupplierRuleRows.length,
                            0,
                            0,
                            rulesHasFilters
                          )}
                        </Typography>
                      </Stack>
                    </AccordionSummary>
                    <AccordionDetails>
                      <Stack spacing={1.5}>
                        <Typography variant="body2" color="text.secondary">
                          No manual rule authoring here. Analyze uncommitted merchants to create aliases; select
                          existing merchant aliases below for bulk manage or run.
                        </Typography>
                        <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap alignItems="center">
                          <Button
                            size="small"
                            variant="contained"
                            disabled={
                              loading ||
                              merchantSupplierSyncBusy ||
                              merchantSupplierApplyBusy ||
                              !!proposalBusyKey
                            }
                            onClick={() => void runMerchantSupplierPreview()}
                          >
                            {merchantSupplierSyncBusy
                              ? 'Analyzing uncommitted merchants…'
                              : 'Analyze uncommitted merchants…'}
                          </Button>
                          <Typography variant="body2" color="text.secondary">
                            {`Showing ${visibleMerchantSupplierRuleRows.length.toLocaleString()} merchant aliases`}
                          </Typography>
                        </Stack>
                        {selectedMerchantSupplierRuleKeys.length > 0 ? (
                          <Paper
                            role="region"
                            aria-label="Bulk actions for selected merchant supplier rules"
                            variant="outlined"
                            sx={{ p: 1.5, bgcolor: 'action.hover' }}
                          >
                            <Stack spacing={1}>
                              <Typography variant="subtitle2" color="text.secondary" sx={{ fontWeight: 600 }}>
                                {bulkMerchantSupplierSelection.count} merchant supplier rule
                                {bulkMerchantSupplierSelection.count === 1 ? '' : 's'} selected
                              </Typography>
                              <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                                {bulkMerchantSupplierSelection.showDelete ? (
                                  <Tooltip title="Deletes selected confirmed merchant supplier aliases. You will be asked to confirm.">
                                    <span>
                                      <Button
                                        size="small"
                                        variant="outlined"
                                        color="error"
                                        disabled={!!proposalBusyKey}
                                        onClick={() => void runBulkMerchantSupplierRuleAction('delete')}
                                      >
                                        Delete
                                      </Button>
                                    </span>
                                  </Tooltip>
                                ) : null}
                                {bulkMerchantSupplierSelection.showDeactivate ? (
                                  <Tooltip title="Deactivates selected merchant aliases that are currently active.">
                                    <span>
                                      <Button
                                        size="small"
                                        variant="outlined"
                                        disabled={!!proposalBusyKey}
                                        onClick={() => void runBulkMerchantSupplierRuleAction('deactivate')}
                                      >
                                        Deactivate
                                      </Button>
                                    </span>
                                  </Tooltip>
                                ) : null}
                                {bulkMerchantSupplierSelection.showActivate ? (
                                  <Tooltip title="Activates selected merchant aliases that are currently inactive.">
                                    <span>
                                      <Button
                                        size="small"
                                        variant="outlined"
                                        disabled={!!proposalBusyKey}
                                        onClick={() => void runBulkMerchantSupplierRuleAction('activate')}
                                      >
                                        Activate
                                      </Button>
                                    </span>
                                  </Tooltip>
                                ) : null}
                                {bulkMerchantSupplierSelection.showRun ? (
                                  <Tooltip title="Runs selected merchant aliases on all matching transactions.">
                                    <span>
                                      <Button
                                        size="small"
                                        variant="outlined"
                                        disabled={!!proposalBusyKey}
                                        onClick={() => void runBulkMerchantSupplierRuleAction('run')}
                                      >
                                        Run
                                      </Button>
                                    </span>
                                  </Tooltip>
                                ) : null}
                              </Stack>
                            </Stack>
                          </Paper>
                        ) : null}
                        <PipelineNestedSectionAccordion
                          filtersActive={rulesHasFilters}
                          title="Confirmed"
                          count={visibleMerchantSupplierRuleRows.length}
                          expanded={pipelineNestedSectionOpen.step2a.confirmed}
                          onExpandedChange={(open) => setPipelineNestedSection('step2a', 'confirmed', open)}
                          first
                        >
                        <TableContainer>
                          <Table size="small">
                            <TableHead>
                              <TableRow>
                                <TableCell padding="checkbox">
                                  <Checkbox
                                    size="small"
                                    checked={merchantSupplierGroupAllSelected}
                                    indeterminate={merchantSupplierGroupIndeterminate}
                                    disabled={merchantSupplierGroupSelectableCount === 0}
                                    onChange={() => toggleMerchantSupplierGroupSelection()}
                                    inputProps={{ 'aria-label': 'Select all visible merchant supplier aliases' }}
                                  />
                                </TableCell>
                                <TableCell>ID</TableCell>
                                <TableCell>Canonical supplier</TableCell>
                                <TableCell>Merchant pattern</TableCell>
                                <TableCell align="right">Fire count</TableCell>
                                <TableCell>Active</TableCell>
                              </TableRow>
                            </TableHead>
                            <TableBody>
                              {sortedVisibleMerchantSupplierRuleRows.length === 0 ? (
                                <TableRow>
                                  <TableCell colSpan={6}>
                                    <Typography variant="body2" color="text.secondary">
                                      No merchant aliases yet. Analyze uncommitted merchants to propose aliases.
                                    </Typography>
                                  </TableCell>
                                </TableRow>
                              ) : (
                                sortedVisibleMerchantSupplierRuleRows.map((row) => {
                                  const rowKey = `mc-${row.id}`;
                                  return (
                                    <TableRow key={`merchant-alias-${row.id}`} selected={selectedMerchantSupplierRuleKeys.includes(rowKey)}>
                                      <TableCell padding="checkbox">
                                        <Checkbox
                                          size="small"
                                          checked={selectedMerchantSupplierRuleKeys.includes(rowKey)}
                                          onChange={() => undefined}
                                          onClick={(e) => {
                                            e.preventDefault();
                                            const selected = selectedMerchantSupplierRuleKeys.includes(rowKey);
                                            applyRulesRowCheckboxSelection({
                                              tableKey: 'merchant-confirmed',
                                              orderedKeys: sortedVisibleMerchantSupplierRuleRows.map((r) => `mc-${r.id}`),
                                              clickedKey: rowKey,
                                              checked: !selected,
                                              shiftKey: e.shiftKey,
                                              setSelection: setSelectedMerchantSupplierRuleKeys
                                            });
                                          }}
                                          inputProps={{
                                            'aria-label': `Select merchant supplier rule ${formatPrefixedRuleId('supplier', row.id)}`
                                          }}
                                        />
                                      </TableCell>
                                      <TableCell>{formatPrefixedRuleId('supplier', row.id)}</TableCell>
                                      <TableCell>
                                        {canonicalSupplierLabel(row.supplier_name, `supplier #${row.supplier_id}`)}
                                      </TableCell>
                                      <TableCell>{row.pattern_raw || '—'}</TableCell>
                                      <TableCell align="right">
                                        {(ruleFireCounts.supplier[row.id] || 0).toLocaleString()}
                                      </TableCell>
                                      <TableCell>{row.is_active === false ? 'Inactive' : 'Active'}</TableCell>
                                    </TableRow>
                                  );
                                })
                              )}
                            </TableBody>
                          </Table>
                        </TableContainer>
                        </PipelineNestedSectionAccordion>
                      <PipelineNestedSectionAccordion
                        filtersActive={rulesHasFilters}
                        title="Proposed"
                        count={0}
                        expanded={pipelineNestedSectionOpen.step2a.proposed}
                        onExpandedChange={(open) => setPipelineNestedSection('step2a', 'proposed', open)}
                      >
                        <Typography variant="body2" color="text.secondary">
                          No proposed rules in this stage.
                        </Typography>
                      </PipelineNestedSectionAccordion>
                      <PipelineNestedSectionAccordion
                        filtersActive={rulesHasFilters}
                        title="Rejected"
                        count={0}
                        expanded={pipelineNestedSectionOpen.step2a.rejected}
                        onExpandedChange={(open) => setPipelineNestedSection('step2a', 'rejected', open)}
                      >
                        <Typography variant="body2" color="text.secondary">
                          No rejected proposals in this stage.
                        </Typography>
                      </PipelineNestedSectionAccordion>

                      </Stack>
                    </AccordionDetails>
                  </Accordion>

                  <Accordion
                    expanded={rulesAccordionOpen.supplierProposals}
                    onChange={(_, expanded) =>
                      setRulesAccordionOpen((prev) => ({ ...prev, supplierProposals: expanded }))
                    }
                    sx={{ mb: 1.5, order: 2 }}
                  >
                    <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                      <Stack direction="row" spacing={0.75} alignItems="center">
                        <Tooltip title="Select/deselect all visible Supplier rules (confirmed + proposed).">
                          <span>
                            <Checkbox
                              size="small"
                              checked={supplierGroupAllSelected}
                              indeterminate={supplierGroupIndeterminate}
                              disabled={supplierGroupSelectableCount === 0}
                              onClick={(e) => e.stopPropagation()}
                              onFocus={(e) => e.stopPropagation()}
                              onChange={() => toggleSupplierGroupSelection()}
                              inputProps={{ 'aria-label': 'Select all Supplier rules in this accordion' }}
                            />
                          </span>
                        </Tooltip>
                        <Typography variant="h6">Pipeline Step 2B: Suppliers from Other Data</Typography>
                        <Tooltip
                          title={
                            <Typography variant="body2">
                              Manual supplier aliases from description, reference, amount, currency, and other
                              non-merchant fields. Merchant aliases live in Step 2A (Sync). Accept creates an alias
                              rule; Reject dismisses a proposal and blocks regenerating the same draft until you
                              restore or delete it under Rejected proposals.
                            </Typography>
                          }
                        >
                          <InfoOutlinedIcon fontSize="small" sx={{ color: 'text.secondary' }} />
                        </Tooltip>
                        <Typography component="span" variant="body2" color="text.secondary" sx={{ fontWeight: 400 }}>
                          {formatPipelineStageCounts(
                            visibleSupplierRuleCounts.confirmed,
                            visibleSupplierRuleCounts.proposed,
                            visibleSupplierRuleCounts.rejected,
                            rulesHasFilters
                          )}
                        </Typography>
                      </Stack>
                    </AccordionSummary>
                    <AccordionDetails>
                      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }} flexWrap="wrap" useFlexGap>
                        <Typography variant="body2" color="text.secondary">
                          {`Showing ${visibleSupplierRuleRows.length.toLocaleString()} rows (${visibleSupplierRuleCounts.confirmed.toLocaleString()} Confirmed, ${visibleSupplierRuleCounts.proposed.toLocaleString()} Proposed)`}
                        </Typography>
                        <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                          <Button
                            size="small"
                            variant="outlined"
                            disabled={loading || !!proposalBusyKey || missingSupplierAuditBusy}
                            onClick={() => void runMissingSupplierAudit()}
                          >
                            {missingSupplierAuditBusy ? 'Running audit…' : 'Run missing-supplier audit'}
                          </Button>
                          <Button
                            size="small"
                            variant="outlined"
                            disabled={
                              loading ||
                              !!proposalBusyKey ||
                              supplierProposalSweepBusy ||
                              (bgJobs.isRunning && bgJobs.activeJobKind === 'supplier_2b_proposals')
                            }
                            onClick={() => void runGenerateSupplierProposalsMissingSupplierOnly()}
                          >
                            {supplierProposalSweepBusy ||
                            (bgJobs.isRunning && bgJobs.activeJobKind === 'supplier_2b_proposals')
                              ? 'Generating…'
                              : 'Generate proposals (missing supplier only)'}
                          </Button>
                          <Button
                            size="small"
                            variant="outlined"
                            onClick={() => void runSupplierNameBackfill()}
                            disabled={
                              loading ||
                              applyingRules ||
                              !!proposalBusyKey ||
                              supplierBackfillBusy ||
                              reapplySpecialCaseUnconfirmedBusy ||
                              rerunAllRulesBusy ||
                              dedupeDisplayDupTagsBusy
                            }
                          >
                            {supplierBackfillBusy ? 'Re-running Supplier…' : 'Re-run ALL Supplier Rules'}
                          </Button>
                          <Button
                            size="small"
                            variant="contained"
                            onClick={openNewSupplierRuleDialog}
                            disabled={loading || applyingRules || supplierRuleSaving}
                          >
                            Add Supplier rule
                          </Button>
                        </Stack>
                      </Stack>
                      {missingSupplierAudit && (
                        <Box
                          id="missing-supplier-audit"
                          ref={missingSupplierAuditAnchorRef}
                          sx={{ scrollMarginTop: 24 }}
                        >
                        <Stack spacing={1} sx={{ mb: 1 }}>
                          <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap alignItems="center">
                            {missingSupplierAuditSavedAt != null ? (
                              <Chip
                                size="small"
                                variant="outlined"
                                label={`Saved here · ${formatMissingSupplierAuditTtlRemaining(missingSupplierAuditSavedAt)}`}
                              />
                            ) : null}
                            <Button size="small" variant="text" onClick={() => void copyMissingSupplierAuditDeepLink()}>
                              Copy link to this section
                            </Button>
                            <Button size="small" variant="text" color="warning" onClick={() => discardMissingSupplierAuditResults()}>
                              Discard saved audit
                            </Button>
                          </Stack>
                          <Typography variant="body2" color="text.secondary">
                            {missingSupplierAudit.ok === false
                              ? String(
                                  missingSupplierAudit.message ||
                                    'Audit unavailable (ledger schema does not match Transaction Classification Review).'
                                )
                              : `Last audit: ${Number(missingSupplierAudit.missing_supplier_display_count || 0).toLocaleString()} missing supplier of ${Number(
                                  missingSupplierAudit.total_transactions || 0
                                ).toLocaleString()} total; ${Number(
                                  missingSupplierAudit.missing_with_description_or_reference || 0
                                ).toLocaleString()} with description/reference; ${Number(
                                  missingSupplierAudit.missing_no_statement_text || 0
                                ).toLocaleString()} with no statement text.`}
                          </Typography>
                          {missingSupplierAudit.ok !== false &&
                          parseCandidates(missingSupplierAudit.proposal_audit_candidates).length > 0 ? (
                            <Button
                              size="small"
                              variant="text"
                              disabled={auditCandidatesGenerating || missingSupplierAuditBusy}
                              onClick={() => {
                                const c = parseCandidates(missingSupplierAudit.proposal_audit_candidates);
                                if (!c.length) return;
                                const rid = missingSupplierAudit.proposal_audit_run_id;
                                setAuditCandidatesModal({
                                  kind: 'supplier_alias',
                                  title: 'Missing supplier — generate drafts from selected buckets',
                                  candidates: c,
                                  proposalAuditRunId:
                                    typeof rid === 'string' && rid.trim().length > 0 ? rid.trim() : null,
                                });
                              }}
                            >
                              Select buckets for draft generation…
                            </Button>
                          ) : null}
                        </Stack>
                        </Box>
                      )}
                      {selectedSupplierRuleKeys.length > 0 ? (
                        <Paper
                          role="region"
                          aria-label="Bulk actions for selected supplier rules"
                          variant="outlined"
                          sx={{ p: 1.5, mb: 1, bgcolor: 'action.hover' }}
                        >
                          <Stack spacing={1.5}>
                            <Typography variant="subtitle2" color="text.secondary" sx={{ fontWeight: 600 }}>
                              {bulkSupplierSelection.count} supplier rule
                              {bulkSupplierSelection.count === 1 ? '' : 's'} selected
                            </Typography>
                            <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                              {bulkSupplierSelection.showDelete ? (
                                <Tooltip title="Removes the selection: you will be asked to confirm. Proposed rules are rejected; confirmed rules are deleted.">
                                  <span>
                                    <Button
                                      size="small"
                                      variant="outlined"
                                      color="error"
                                      disabled={!!proposalBusyKey}
                                      onClick={() => void runBulkSupplierRuleAction('delete')}
                                    >
                                      Delete
                                    </Button>
                                  </span>
                                </Tooltip>
                              ) : null}
                              {bulkSupplierSelection.showAccept ? (
                                <Tooltip title="Applies to proposed rules in the current selection.">
                                  <span>
                                    <Button
                                      size="small"
                                      variant="outlined"
                                      color="success"
                                      disabled={!!proposalBusyKey}
                                      onClick={() => void runBulkSupplierRuleAction('accept')}
                                    >
                                      Accept
                                    </Button>
                                  </span>
                                </Tooltip>
                              ) : null}
                              {bulkSupplierSelection.showReject ? (
                                <Tooltip title="Rejects proposed rules in the current selection.">
                                  <span>
                                    <Button
                                      size="small"
                                      variant="outlined"
                                      disabled={!!proposalBusyKey}
                                      onClick={() => void runBulkSupplierRuleAction('reject')}
                                    >
                                      Reject
                                    </Button>
                                  </span>
                                </Tooltip>
                              ) : null}
                              {bulkSupplierSelection.showDeactivate ? (
                                <Tooltip title="Deactivates confirmed rules in the current selection that are currently active.">
                                  <span>
                                    <Button
                                      size="small"
                                      variant="outlined"
                                      disabled={!!proposalBusyKey}
                                      onClick={() => void runBulkSupplierRuleAction('deactivate')}
                                    >
                                      Deactivate
                                    </Button>
                                  </span>
                                </Tooltip>
                              ) : null}
                              {bulkSupplierSelection.showActivate ? (
                                <Tooltip title="Activates confirmed rules in the current selection that are currently inactive.">
                                  <span>
                                    <Button
                                      size="small"
                                      variant="outlined"
                                      disabled={!!proposalBusyKey}
                                      onClick={() => void runBulkSupplierRuleAction('activate')}
                                    >
                                      Activate
                                    </Button>
                                  </span>
                                </Tooltip>
                              ) : null}
                              {bulkSupplierSelection.showRun ? (
                                <Tooltip title="Runs confirmed rules in the selection on all matching transactions.">
                                  <span>
                                    <Button
                                      size="small"
                                      variant="outlined"
                                      disabled={!!proposalBusyKey}
                                      onClick={() => void runBulkSupplierRuleAction('run')}
                                    >
                                      Run
                                    </Button>
                                  </span>
                                </Tooltip>
                              ) : null}
                            </Stack>
                            {bulkSupplierSelection.showApplyEdits ? (
                              <Stack direction={{ xs: 'column', md: 'row' }} spacing={1} alignItems={{ md: 'center' }} flexWrap="wrap" useFlexGap>
                                <TextField
                                  size="small"
                                  type="number"
                                  label="Priority for selected"
                                  value={bulkSupplierPriority}
                                  onChange={(e) => setBulkSupplierPriority(e.target.value)}
                                  sx={{ width: { xs: '100%', md: 170 } }}
                                />
                                <FormControl size="small" sx={{ width: { xs: '100%', md: 220 } }}>
                                  <InputLabel id="bulk-supplier-account-scope-label">Bank account</InputLabel>
                                  <Select
                                    labelId="bulk-supplier-account-scope-label"
                                    label="Bank account"
                                    value={bulkSupplierAccountScope}
                                    onChange={(e) =>
                                      setBulkSupplierAccountScope(
                                        String(e.target.value || RULE_BULK_NO_CHANGE) as
                                          | typeof RULE_BULK_NO_CHANGE
                                          | 'both'
                                          | 'ireland'
                                          | 'uk'
                                      )
                                    }
                                  >
                                    <MenuItem value={RULE_BULK_NO_CHANGE}>No change</MenuItem>
                                    {RULE_ACCOUNT_SCOPE_OPTIONS.map((opt) => (
                                      <MenuItem key={`bulk-supplier-scope-${opt.value}`} value={opt.value}>
                                        {opt.label}
                                      </MenuItem>
                                    ))}
                                  </Select>
                                </FormControl>
                                <TextField
                                  size="small"
                                  label="Comment to add"
                                  value={bulkSupplierComment}
                                  onChange={(e) => setBulkSupplierComment(e.target.value)}
                                  sx={{ minWidth: { xs: '100%', md: 320 } }}
                                />
                                <Button
                                  size="small"
                                  variant="contained"
                                  disabled={!!proposalBusyKey}
                                  onClick={() => void runBulkSupplierRuleAction('applyEdits')}
                                >
                                  Apply fields to selected confirmed
                                </Button>
                              </Stack>
                            ) : null}
                          </Stack>
                        </Paper>
                      ) : null}
                      <PipelineNestedSectionAccordion
                        filtersActive={rulesHasFilters}
                        title="Confirmed"
                        count={sortedVisibleSupplierConfirmedRows.length}
                        expanded={pipelineNestedSectionOpen.step2b.confirmed}
                        onExpandedChange={(open) => setPipelineNestedSection('step2b', 'confirmed', open)}
                        first
                      >
                      <TableContainer sx={{ mb: 1.5 }}>
                        <Table size="small">
                          <TableHead>
                            <TableRow>
                              <TableCell padding="checkbox">
                                <input
                                  type="checkbox"
                                  checked={
                                    sortedVisibleSupplierConfirmedRows.length > 0 &&
                                    sortedVisibleSupplierConfirmedRows.every((r) => selectedSupplierRuleKeys.includes(r.key))
                                  }
                                  onChange={(e) => {
                                    const keys = sortedVisibleSupplierConfirmedRows.map((r) => r.key);
                                    const keySet = new Set(keys);
                                    if (e.target.checked) {
                                      setSelectedSupplierRuleKeys((prev) => Array.from(new Set(prev.concat(keys))));
                                    } else {
                                      setSelectedSupplierRuleKeys((prev) => prev.filter((k) => !keySet.has(k)));
                                    }
                                  }}
                                />
                              </TableCell>
                              <TableCell align="center" sortDirection={supplierRulesTableSort?.key === 'priority' ? supplierRulesTableSort.dir : false}>
                                <TableSortLabel
                                  active={supplierRulesTableSort?.key === 'priority'}
                                  direction={supplierRulesTableSort?.key === 'priority' ? supplierRulesTableSort.dir : 'asc'}
                                  onClick={() => setSupplierRulesTableSort((prev) => nextSortState(prev, 'priority'))}
                                  sx={{ display: 'flex', justifyContent: 'center' }}
                                >
                                  Priority
                                </TableSortLabel>
                              </TableCell>
                              <TableCell align="center" sortDirection={supplierRulesTableSort?.key === 'id' ? supplierRulesTableSort.dir : false}>
                                <TableSortLabel
                                  active={supplierRulesTableSort?.key === 'id'}
                                  direction={supplierRulesTableSort?.key === 'id' ? supplierRulesTableSort.dir : 'asc'}
                                  onClick={() => setSupplierRulesTableSort((prev) => nextSortState(prev, 'id'))}
                                  sx={{ display: 'flex', justifyContent: 'center' }}
                                >
                                  ID
                                </TableSortLabel>
                              </TableCell>
                              <TableCell align="center" sortDirection={supplierRulesTableSort?.key === 'name' ? supplierRulesTableSort.dir : false}>
                                <TableSortLabel
                                  active={supplierRulesTableSort?.key === 'name'}
                                  direction={supplierRulesTableSort?.key === 'name' ? supplierRulesTableSort.dir : 'asc'}
                                  onClick={() => setSupplierRulesTableSort((prev) => nextSortState(prev, 'name'))}
                                  sx={{ display: 'flex', justifyContent: 'center' }}
                                >
                                  Rule Name
                                </TableSortLabel>
                              </TableCell>
                              <TableCell align="center" sx={{ ...RULE_ACCOUNT_COLUMN_SX, textAlign: 'center' }} sortDirection={supplierRulesTableSort?.key === 'account' ? supplierRulesTableSort.dir : false}>
                                <TableSortLabel
                                  active={supplierRulesTableSort?.key === 'account'}
                                  direction={supplierRulesTableSort?.key === 'account' ? supplierRulesTableSort.dir : 'asc'}
                                  onClick={() => setSupplierRulesTableSort((prev) => nextSortState(prev, 'account'))}
                                  sx={{ display: 'flex', justifyContent: 'center' }}
                                >
                                  Bank /n A/C
                                </TableSortLabel>
                              </TableCell>
                              <TableCell align="center" sortDirection={supplierRulesTableSort?.key === 'supplierMatch' ? supplierRulesTableSort.dir : false}>
                                <TableSortLabel
                                  active={supplierRulesTableSort?.key === 'supplierMatch'}
                                  direction={supplierRulesTableSort?.key === 'supplierMatch' ? supplierRulesTableSort.dir : 'asc'}
                                  onClick={() => setSupplierRulesTableSort((prev) => nextSortState(prev, 'supplierMatch'))}
                                  sx={{ display: 'flex', justifyContent: 'center' }}
                                >
                                  Merchant / Counterparty Match
                                </TableSortLabel>
                              </TableCell>
                              <TableCell align="center" sortDirection={supplierRulesTableSort?.key === 'descriptionMatch' ? supplierRulesTableSort.dir : false}>
                                <TableSortLabel
                                  active={supplierRulesTableSort?.key === 'descriptionMatch'}
                                  direction={supplierRulesTableSort?.key === 'descriptionMatch' ? supplierRulesTableSort.dir : 'asc'}
                                  onClick={() => setSupplierRulesTableSort((prev) => nextSortState(prev, 'descriptionMatch'))}
                                  sx={{ display: 'flex', justifyContent: 'center' }}
                                >
                                  Description Text Match
                                </TableSortLabel>
                              </TableCell>
                              <TableCell align="center" sortDirection={supplierRulesTableSort?.key === 'referenceMatch' ? supplierRulesTableSort.dir : false}>
                                <TableSortLabel
                                  active={supplierRulesTableSort?.key === 'referenceMatch'}
                                  direction={supplierRulesTableSort?.key === 'referenceMatch' ? supplierRulesTableSort.dir : 'asc'}
                                  onClick={() => setSupplierRulesTableSort((prev) => nextSortState(prev, 'referenceMatch'))}
                                  sx={{ display: 'flex', justifyContent: 'center' }}
                                >
                                  Reference Text Match
                                </TableSortLabel>
                              </TableCell>
                              <TableCell
                                align="center"
                                sortDirection={supplierRulesTableSort?.key === 'canonical' ? supplierRulesTableSort.dir : false}
                                sx={OUTPUT_COLUMN_SX}
                              >
                                <TableSortLabel
                                  active={supplierRulesTableSort?.key === 'canonical'}
                                  direction={supplierRulesTableSort?.key === 'canonical' ? supplierRulesTableSort.dir : 'asc'}
                                  onClick={() => setSupplierRulesTableSort((prev) => nextSortState(prev, 'canonical'))}
                                  sx={{ display: 'flex', justifyContent: 'center' }}
                                >
                                  Output: Canonical Supplier
                                </TableSortLabel>
                              </TableCell>
                              <TableCell align="center" sortDirection={supplierRulesTableSort?.key === 'status' ? supplierRulesTableSort.dir : false}>
                                <TableSortLabel
                                  active={supplierRulesTableSort?.key === 'status'}
                                  direction={supplierRulesTableSort?.key === 'status' ? supplierRulesTableSort.dir : 'asc'}
                                  onClick={() => setSupplierRulesTableSort((prev) => nextSortState(prev, 'status'))}
                                  sx={{ display: 'flex', justifyContent: 'center' }}
                                >
                                  Status
                                </TableSortLabel>
                              </TableCell>
                              <TableCell align="center" sortDirection={supplierRulesTableSort?.key === 'firedCount' ? supplierRulesTableSort.dir : false}>
                                <TableSortLabel
                                  active={supplierRulesTableSort?.key === 'firedCount'}
                                  direction={supplierRulesTableSort?.key === 'firedCount' ? supplierRulesTableSort.dir : 'asc'}
                                  onClick={() => setSupplierRulesTableSort((prev) => nextSortState(prev, 'firedCount'))}
                                  sx={{ display: 'flex', justifyContent: 'center' }}
                                >
                                  Tx fired
                                </TableSortLabel>
                              </TableCell>
                              <TableCell align="center" sortDirection={supplierRulesTableSort?.key === 'active' ? supplierRulesTableSort.dir : false}>
                                <TableSortLabel
                                  active={supplierRulesTableSort?.key === 'active'}
                                  direction={supplierRulesTableSort?.key === 'active' ? supplierRulesTableSort.dir : 'asc'}
                                  onClick={() => setSupplierRulesTableSort((prev) => nextSortState(prev, 'active'))}
                                  sx={{ display: 'flex', justifyContent: 'center' }}
                                >
                                  Rule Active
                                </TableSortLabel>
                              </TableCell>
                              <TableCell align="center">Actions</TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {sortedVisibleSupplierConfirmedRows.length === 0 ? (
                              <TableRow>
                                <TableCell colSpan={13}>No confirmed supplier rules for the current filters.</TableCell>
                              </TableRow>
                            ) : (
                              sortedVisibleSupplierConfirmedRows.map((entry) => {
                                const row = entry.alias as SupplierRuleRow;
                                const criteriaForDisplay = supplierRuleCriteriaFromAliasForDisplay(row);
                                const supplierRowBusy = supplierRuleTablePatchBusyId === row.id;
                                return (
                                  <TableRow
                                    key={entry.key}
                                    sx={ruleTableRowSavingSx(
                                      theme,
                                      ruleTableSaveHighlight?.kind === 'supplier' &&
                                        ruleTableSaveHighlight.rowKey === entry.key
                                    )}
                                  >
                                    <TableCell padding="checkbox">
                                      <input
                                        type="checkbox"
                                        checked={selectedSupplierRuleKeys.includes(entry.key)}
                                        onChange={() => undefined}
                                        onClick={(e) => {
                                          e.preventDefault();
                                          const selected = selectedSupplierRuleKeys.includes(entry.key);
                                          applyRulesRowCheckboxSelection({
                                            tableKey: 'supplier-confirmed',
                                            orderedKeys: sortedVisibleSupplierConfirmedRows.map((r) => r.key),
                                            clickedKey: entry.key,
                                            checked: !selected,
                                            shiftKey: e.shiftKey,
                                            setSelection: setSelectedSupplierRuleKeys
                                          });
                                        }}
                                      />
                                    </TableCell>
                                    <TableCell>{`${Number(row.priority ?? 100)} (${supplierRulePriorityBand(Number(row.priority ?? 100))})`}</TableCell>
                                    <TableCell>{formatPrefixedRuleId('supplier', row.id)}</TableCell>
                                    <TableCell>{String(row.rule_name || '').trim() || '—'}</TableCell>
                                    <TableCell sx={RULE_ACCOUNT_COLUMN_SX}>{ruleAccountScopeTableLabel(row.account_scope)}</TableCell>
                                    <TableCell>
                                      {renderSupplierRuleCriteriaCell(
                                        criteriaForDisplay,
                                        'merchant',
                                        `supplier-confirmed-${row.id}-merchant`,
                                        {
                                          busy: supplierRowBusy || loading,
                                          onRemoveCriterion: (c) => void removeSupplierRuleCriterionQuick(row, c),
                                          onCanonicalSupplierClick: openSuppliersTabForName
                                        }
                                      )}
                                    </TableCell>
                                    <TableCell>
                                      {renderSupplierRuleCriteriaCell(
                                        criteriaForDisplay,
                                        'description',
                                        `supplier-confirmed-${row.id}-description`,
                                        {
                                          busy: supplierRowBusy || loading,
                                          onRemoveCriterion: (c) => void removeSupplierRuleCriterionQuick(row, c),
                                          onCanonicalSupplierClick: openSuppliersTabForName
                                        }
                                      )}
                                    </TableCell>
                                    <TableCell>
                                      {renderSupplierRuleCriteriaCell(
                                        criteriaForDisplay,
                                        'reference',
                                        `supplier-confirmed-${row.id}-reference`,
                                        {
                                          busy: supplierRowBusy || loading,
                                          onRemoveCriterion: (c) => void removeSupplierRuleCriterionQuick(row, c),
                                          onCanonicalSupplierClick: openSuppliersTabForName
                                        }
                                      )}
                                    </TableCell>
                                    <TableCell sx={SUPPLIER_RULE_CANONICAL_CELL_SX}>
                                      {String(row.supplier_name || '').trim() ? (
                                        <Tooltip title="Open supplier in Suppliers tab. Use Edit to change output supplier.">
                                          <span>
                                            <Chip
                                              size="small"
                                              variant="outlined"
                                              label={String(row.supplier_name || '').trim()}
                                              clickable
                                              onClick={() =>
                                                openSuppliersTabForName(String(row.supplier_name || '').trim())
                                              }
                                            />
                                          </span>
                                        </Tooltip>
                                      ) : (
                                        <Tooltip
                                          title={canonicalSupplierLabel(
                                            row.supplier_name,
                                            `Supplier #${row.supplier_id}`
                                          )}
                                        >
                                          <Chip
                                            size="small"
                                            variant="outlined"
                                            label={canonicalSupplierLabel(
                                              row.supplier_name,
                                              `Supplier #${row.supplier_id}`
                                            )}
                                          />
                                        </Tooltip>
                                      )}
                                    </TableCell>
                                    <TableCell><Chip size="small" label="Confirmed" color="success" /></TableCell>
                                    <TableCell>{Number(ruleFireCounts.supplier[row.id] || 0).toLocaleString()}</TableCell>
                                    <TableCell>{row.is_active === false ? 'Inactive' : 'Active'}</TableCell>
                                    <TableCell align="right">
                                      <Stack direction="row" spacing={0.25} justifyContent="flex-end">
                                        <Tooltip
                                          title={
                                            supplierRuleCanListMatches(row)
                                              ? 'View matching transactions'
                                              : 'Rule needs a pattern to list transactions.'
                                          }
                                        >
                                          <span>
                                            <IconButton
                                              size="small"
                                              color="primary"
                                              aria-label="View matching transactions"
                                              onClick={() => void openSupplierRuleMatchingTransactionsModal(row)}
                                              disabled={loading || applyingRules || !supplierRuleCanListMatches(row)}
                                            >
                                              <VisibilityIcon fontSize="small" />
                                            </IconButton>
                                          </span>
                                        </Tooltip>
                                        <Tooltip title="Apply this supplier rule to all tagged transactions">
                                          <span>
                                            <IconButton
                                              size="small"
                                              color="primary"
                                              aria-label="Apply this supplier rule to all transactions"
                                              onClick={() => void applySingleSupplierRuleToAllTransactions(row)}
                                              disabled={loading || applyingRules || applySingleRuleBusyKey !== ''}
                                            >
                                              {applySingleRuleBusyKey === `supplier-${row.id}` ? (
                                                <CircularProgress size={14} color="inherit" />
                                              ) : (
                                                <PlayArrowIcon fontSize="small" />
                                              )}
                                            </IconButton>
                                          </span>
                                        </Tooltip>
                                        <IconButton size="small" color="primary" onClick={() => openSupplierRuleEditor(row)}>
                                          <EditIcon fontSize="small" />
                                        </IconButton>
                                        <Tooltip title="Duplicate rule">
                                          <span>
                                            <IconButton
                                              size="small"
                                              color="primary"
                                              aria-label="Duplicate supplier rule"
                                              disabled={
                                                loading ||
                                                applyingRules ||
                                                supplierRowBusy ||
                                                !!ruleDuplicateBusyKey ||
                                                supplierRuleTablePatchBusyId === row.id
                                              }
                                              onClick={() => void duplicateSupplierRule(row)}
                                            >
                                              {ruleDuplicateBusyKey === `dup-sup-${row.id}` ? (
                                                <CircularProgress size={14} color="inherit" />
                                              ) : (
                                                <ContentCopyIcon fontSize="small" />
                                              )}
                                            </IconButton>
                                          </span>
                                        </Tooltip>
                                        <IconButton size="small" color="error" onClick={() => void deleteSupplierRule(row.id)}>
                                          <DeleteOutlineIcon fontSize="small" />
                                        </IconButton>
                                      </Stack>
                                    </TableCell>
                                  </TableRow>
                                );
                              })
                            )}
                          </TableBody>
                        </Table>
                      </TableContainer>
                      </PipelineNestedSectionAccordion>
                      <PipelineNestedSectionAccordion
                        filtersActive={rulesHasFilters}
                        title="Proposed"
                        count={sortedVisibleSupplierProposedRows.length}
                        expanded={pipelineNestedSectionOpen.step2b.proposed}
                        onExpandedChange={(open) => setPipelineNestedSection('step2b', 'proposed', open)}
                      >
                      {selectedSupplierProposedCount > 0 ? (
                        <Paper
                          role="region"
                          aria-label="Bulk actions for selected proposed supplier rules"
                          variant="outlined"
                          sx={{ p: 1.5, mb: 1, bgcolor: 'action.hover' }}
                        >
                          <Stack spacing={1}>
                            <Typography variant="subtitle2" color="text.secondary" sx={{ fontWeight: 600 }}>
                              {selectedSupplierProposedCount} proposed supplier rule
                              {selectedSupplierProposedCount === 1 ? '' : 's'} selected
                            </Typography>
                            <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                              <Button
                                size="small"
                                variant="outlined"
                                color="success"
                                disabled={!!proposalBusyKey}
                                onClick={() => void runBulkSupplierRuleAction('accept')}
                              >
                                Accept
                              </Button>
                              <Button
                                size="small"
                                variant="outlined"
                                disabled={!!proposalBusyKey}
                                onClick={() => void runBulkSupplierRuleAction('reject')}
                              >
                                Reject
                              </Button>
                              <Button
                                size="small"
                                variant="outlined"
                                color="error"
                                disabled={!!proposalBusyKey}
                                onClick={() => void runBulkSupplierRuleAction('delete')}
                              >
                                Delete
                              </Button>
                            </Stack>
                          </Stack>
                        </Paper>
                      ) : null}
                      <TableContainer>
                        <Table size="small">
                          <TableHead>
                            <TableRow>
                              <TableCell padding="checkbox">
                                <input
                                  type="checkbox"
                                  checked={
                                    sortedVisibleSupplierProposedRows.length > 0 &&
                                    sortedVisibleSupplierProposedRows.every((r) => selectedSupplierRuleKeys.includes(r.key))
                                  }
                                  onChange={(e) => {
                                    const keys = sortedVisibleSupplierProposedRows.map((r) => r.key);
                                    const keySet = new Set(keys);
                                    if (e.target.checked) {
                                      setSelectedSupplierRuleKeys((prev) => Array.from(new Set(prev.concat(keys))));
                                    } else {
                                      setSelectedSupplierRuleKeys((prev) => prev.filter((k) => !keySet.has(k)));
                                    }
                                  }}
                                />
                              </TableCell>
                              <TableCell align="center">Status</TableCell>
                              <TableCell align="center">Source</TableCell>
                              <TableCell align="center" sortDirection={supplierRulesTableSort?.key === 'id' ? supplierRulesTableSort.dir : false}>
                                <TableSortLabel
                                  active={supplierRulesTableSort?.key === 'id'}
                                  direction={supplierRulesTableSort?.key === 'id' ? supplierRulesTableSort.dir : 'asc'}
                                  onClick={() => setSupplierRulesTableSort((prev) => nextSortState(prev, 'id'))}
                                  sx={{ display: 'flex', justifyContent: 'center' }}
                                >
                                  ID
                                </TableSortLabel>
                              </TableCell>
                              <TableCell align="center" sortDirection={supplierRulesTableSort?.key === 'name' ? supplierRulesTableSort.dir : false}>
                                <TableSortLabel
                                  active={supplierRulesTableSort?.key === 'name'}
                                  direction={supplierRulesTableSort?.key === 'name' ? supplierRulesTableSort.dir : 'asc'}
                                  onClick={() => setSupplierRulesTableSort((prev) => nextSortState(prev, 'name'))}
                                  sx={{ display: 'flex', justifyContent: 'center' }}
                                >
                                  Rule Name
                                </TableSortLabel>
                              </TableCell>
                              <TableCell align="center" sx={{ ...RULE_ACCOUNT_COLUMN_SX, textAlign: 'center' }} sortDirection={supplierRulesTableSort?.key === 'account' ? supplierRulesTableSort.dir : false}>
                                <TableSortLabel
                                  active={supplierRulesTableSort?.key === 'account'}
                                  direction={supplierRulesTableSort?.key === 'account' ? supplierRulesTableSort.dir : 'asc'}
                                  onClick={() => setSupplierRulesTableSort((prev) => nextSortState(prev, 'account'))}
                                  sx={{ display: 'flex', justifyContent: 'center' }}
                                >
                                  Bank /n A/C
                                </TableSortLabel>
                              </TableCell>
                              <TableCell align="center" sortDirection={supplierRulesTableSort?.key === 'supplierMatch' ? supplierRulesTableSort.dir : false}>
                                <TableSortLabel
                                  active={supplierRulesTableSort?.key === 'supplierMatch'}
                                  direction={supplierRulesTableSort?.key === 'supplierMatch' ? supplierRulesTableSort.dir : 'asc'}
                                  onClick={() => setSupplierRulesTableSort((prev) => nextSortState(prev, 'supplierMatch'))}
                                  sx={{ display: 'flex', justifyContent: 'center' }}
                                >
                                  Merchant / Counterparty Match
                                </TableSortLabel>
                              </TableCell>
                              <TableCell align="center" sortDirection={supplierRulesTableSort?.key === 'descriptionMatch' ? supplierRulesTableSort.dir : false}>
                                <TableSortLabel
                                  active={supplierRulesTableSort?.key === 'descriptionMatch'}
                                  direction={supplierRulesTableSort?.key === 'descriptionMatch' ? supplierRulesTableSort.dir : 'asc'}
                                  onClick={() => setSupplierRulesTableSort((prev) => nextSortState(prev, 'descriptionMatch'))}
                                  sx={{ display: 'flex', justifyContent: 'center' }}
                                >
                                  Description Text Match
                                </TableSortLabel>
                              </TableCell>
                              <TableCell align="center" sortDirection={supplierRulesTableSort?.key === 'referenceMatch' ? supplierRulesTableSort.dir : false}>
                                <TableSortLabel
                                  active={supplierRulesTableSort?.key === 'referenceMatch'}
                                  direction={supplierRulesTableSort?.key === 'referenceMatch' ? supplierRulesTableSort.dir : 'asc'}
                                  onClick={() => setSupplierRulesTableSort((prev) => nextSortState(prev, 'referenceMatch'))}
                                  sx={{ display: 'flex', justifyContent: 'center' }}
                                >
                                  Reference Text Match
                                </TableSortLabel>
                              </TableCell>
                              <TableCell
                                align="center"
                                sortDirection={supplierRulesTableSort?.key === 'canonical' ? supplierRulesTableSort.dir : false}
                                sx={OUTPUT_COLUMN_SX}
                              >
                                <TableSortLabel
                                  active={supplierRulesTableSort?.key === 'canonical'}
                                  direction={supplierRulesTableSort?.key === 'canonical' ? supplierRulesTableSort.dir : 'asc'}
                                  onClick={() => setSupplierRulesTableSort((prev) => nextSortState(prev, 'canonical'))}
                                  sx={{ display: 'flex', justifyContent: 'center' }}
                                >
                                  Output: Canonical Supplier
                                </TableSortLabel>
                              </TableCell>
                              <TableCell align="center">Actions</TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {sortedVisibleSupplierProposedRows.length === 0 ? (
                              <TableRow>
                                <TableCell colSpan={11}>No proposed supplier rules for the current filters.</TableCell>
                              </TableRow>
                            ) : (
                              sortedVisibleSupplierProposedRows.map((entry, idx) => {
                                const row = entry.proposal as SupplierRuleProposalRow;
                                const p = row.payload || {};
                                const supplier = String((p.canonical_name as string) || '').trim() || '—';
                                const criteriaForDisplay = supplierRuleCriteriaFromProposalPayloadForDisplay(p);
                                const rowPid = supplierRuleProposalDbId(row);
                                const busy = rowPid != null && proposalBusyKey === `sp-${rowPid}-${idx}`;
                                const supplierProposalPreviewStub = supplierRuleRowStubFromProposal(row);
                                const supplierProposalCanPreview =
                                  rowPid != null &&
                                  supplierProposalPreviewStub != null &&
                                  supplierRuleCanListMatches(supplierProposalPreviewStub);
                                return (
                                  <TableRow
                                    key={entry.key}
                                    sx={ruleTableRowSavingSx(
                                      theme,
                                      ruleTableSaveHighlight?.kind === 'supplier' &&
                                        ruleTableSaveHighlight.rowKey === entry.key
                                    )}
                                  >
                                    <TableCell padding="checkbox">
                                      <input
                                        type="checkbox"
                                        checked={selectedSupplierRuleKeys.includes(entry.key)}
                                        onChange={() => undefined}
                                        onClick={(e) => {
                                          e.preventDefault();
                                          const selected = selectedSupplierRuleKeys.includes(entry.key);
                                          applyRulesRowCheckboxSelection({
                                            tableKey: 'supplier-proposed',
                                            orderedKeys: sortedVisibleSupplierProposedRows.map((r) => r.key),
                                            clickedKey: entry.key,
                                            checked: !selected,
                                            shiftKey: e.shiftKey,
                                            setSelection: setSelectedSupplierRuleKeys
                                          });
                                        }}
                                      />
                                    </TableCell>
                                    <TableCell align="center">
                                      <Chip size="small" label="Proposed" color="warning" />
                                    </TableCell>
                                    <TableCell align="center">
                                      <Chip
                                        size="small"
                                        label={row.source === 'atlas' ? 'Atlas' : 'Your draft'}
                                        color={row.source === 'atlas' ? 'info' : 'default'}
                                      />
                                    </TableCell>
                                    <TableCell align="center">
                                      {rowPid != null
                                        ? formatSupplierProposalPrefixedId(Number(rowPid), 'proposed')
                                        : '—'}
                                    </TableCell>
                                    <TableCell align="center">{String((p.rule_name as string) || row.proposed_name || '').trim() || '—'}</TableCell>
                                    <TableCell align="center" sx={{ ...RULE_ACCOUNT_COLUMN_SX, textAlign: 'center' }}>
                                      {ruleAccountScopeTableLabel(String((p.account_scope as string) || row.account_scope || 'both'))}
                                    </TableCell>
                                    <TableCell align="center">
                                      {renderSupplierRuleCriteriaCell(
                                        criteriaForDisplay,
                                        'merchant',
                                        `supplier-proposed-${entry.key}-merchant`
                                      )}
                                    </TableCell>
                                    <TableCell align="center">
                                      {renderSupplierRuleCriteriaCell(
                                        criteriaForDisplay,
                                        'description',
                                        `supplier-proposed-${entry.key}-description`
                                      )}
                                    </TableCell>
                                    <TableCell align="center">
                                      {renderSupplierRuleCriteriaCell(
                                        criteriaForDisplay,
                                        'reference',
                                        `supplier-proposed-${entry.key}-reference`
                                      )}
                                    </TableCell>
                                    <TableCell sx={SUPPLIER_RULE_CANONICAL_CELL_SX}>
                                      {supplier !== '—' ? (
                                        <Tooltip title="Open supplier in Suppliers tab. Use Edit to change this proposal.">
                                          <span>
                                            <Chip
                                              size="small"
                                              variant="outlined"
                                              label={supplier}
                                              clickable
                                              onClick={() => openSuppliersTabForName(supplier)}
                                            />
                                          </span>
                                        </Tooltip>
                                      ) : (
                                        <Chip size="small" variant="outlined" label="—" />
                                      )}
                                    </TableCell>
                                    <TableCell align="right">
                                      <Stack direction="row" spacing={0.5} justifyContent="flex-end">
                                        <Tooltip
                                          title={
                                            rowPid == null
                                              ? 'Proposal id missing — refresh the page.'
                                              : !supplierProposalCanPreview
                                                ? 'Add match criteria (merchant / description / reference, etc.) to preview.'
                                                : 'Preview which historical transactions match this proposal'
                                          }
                                        >
                                          <span>
                                            <IconButton
                                              size="small"
                                              color="primary"
                                              aria-label="Preview supplier proposal on historical transactions"
                                              disabled={!!proposalBusyKey || loading || !supplierProposalCanPreview}
                                              onClick={() => void openSupplierProposalMatchingTransactionsModal(row)}
                                            >
                                              <VisibilityIcon fontSize="small" />
                                            </IconButton>
                                          </span>
                                        </Tooltip>
                                        <IconButton
                                          size="small"
                                          color="primary"
                                          disabled={!!proposalBusyKey || loading}
                                          onClick={() => openSupplierProposalEditor(row)}
                                        >
                                          <EditIcon fontSize="small" />
                                        </IconButton>
                                        <Tooltip title="Duplicate as new rule">
                                          <span>
                                            <IconButton
                                              size="small"
                                              color="primary"
                                              aria-label="Duplicate supplier proposal"
                                              disabled={!!proposalBusyKey || loading || !!ruleDuplicateBusyKey}
                                              onClick={() => void duplicateSupplierRuleProposal(row)}
                                            >
                                              {ruleDuplicateBusyKey ===
                                              `dup-sp-${supplierRuleProposalDbId(row) ?? 'na'}` ? (
                                                <CircularProgress size={14} color="inherit" />
                                              ) : (
                                                <ContentCopyIcon fontSize="small" />
                                              )}
                                            </IconButton>
                                          </span>
                                        </Tooltip>
                                        <Button
                                          size="small"
                                          variant="contained"
                                          color="success"
                                          disabled={!!proposalBusyKey || loading}
                                          onClick={async () => {
                                            const pid = supplierRuleProposalDbId(row);
                                            if (pid == null) {
                                              setError('This supplier proposal has no id — refresh the page and try again.');
                                              scrollPageToClassificationErrorBanner();
                                              return;
                                            }
                                            try {
                                              setProposalBusyKey(`sp-${pid}-${idx}`);
                                              const res = await api.post(
                                                `/api/suppliers/description-alias-proposals/${pid}/accept`,
                                                {}
                                              );
                                              const aliasObj = res.data?.alias as SupplierRuleRow | undefined;
                                              await refreshRulesTabDatasets();
                                              const nextProposalParam =
                                                rebuildProposalIdsAfterSupplierDraftAccepted(rulesProposalIdsFilter, pid);
                                              setRulesProposalIdsFilter(nextProposalParam);
                                              if (aliasObj?.id) {
                                                setRulesIdFilter(formatPrefixedRuleId('supplier', aliasObj.id));
                                              }
                                              setRulesStatusFilter('all');
                                              setSuccessMessage('Accepted supplier proposal and created supplier rule.');
                                              {
                                                const aliasIdAccepted =
                                                  aliasObj?.id != null ? Math.trunc(Number(aliasObj.id)) : NaN;
                                                openBackfillAfterAccept({
                                                  ruleName: String(
                                                    row.proposed_name ||
                                                      (row.payload as { rule_name?: string })?.rule_name ||
                                                      'Supplier rule'
                                                  ),
                                                  prefixedId:
                                                    Number.isFinite(aliasIdAccepted) && aliasIdAccepted > 0
                                                      ? formatPrefixedRuleId('supplier', aliasIdAccepted)
                                                      : '',
                                                  target:
                                                    Number.isFinite(aliasIdAccepted) && aliasIdAccepted > 0
                                                      ? { kind: 'supplier', id: aliasIdAccepted }
                                                      : null
                                                });
                                              }
                                            } catch (err: any) {
                                              setError(err?.response?.data?.error || err?.message || 'Could not accept supplier proposal');
                                              scrollPageToClassificationErrorBanner();
                                            } finally {
                                              setProposalBusyKey('');
                                            }
                                          }}
                                        >
                                          {busy ? '…' : 'Accept'}
                                        </Button>
                                        <Button
                                          size="small"
                                          variant="outlined"
                                          onClick={async () => {
                                            const pid = supplierRuleProposalDbId(row);
                                            if (pid == null) {
                                              setError('This supplier proposal has no id — refresh the page and try again.');
                                              scrollPageToClassificationErrorBanner();
                                              return;
                                            }
                                            try {
                                              setProposalBusyKey(`sp-${pid}-${idx}`);
                                              await api.post(`/api/suppliers/description-alias-proposals/${pid}/reject`, {});
                                              await refreshRulesTabDatasets();
                                              setSuccessMessage('Supplier proposal dismissed.');
                                            } catch (err: any) {
                                              setError(err?.response?.data?.error || err?.message || 'Could not reject supplier proposal');
                                              scrollPageToClassificationErrorBanner();
                                            } finally {
                                              setProposalBusyKey('');
                                            }
                                          }}
                                        >
                                          Reject
                                        </Button>
                                      </Stack>
                                    </TableCell>
                                  </TableRow>
                                );
                              })
                            )}
                          </TableBody>
                        </Table>
                      </TableContainer>
                      <TableContainer sx={{ display: 'none' }}>
                        <Table size="small">
                          <TableHead>
                            <TableRow>
                              <TableCell>Proposal</TableCell>
                              <TableCell>ID</TableCell>
                              <TableCell>Canonical Supplier</TableCell>
                              <TableCell>Text match mode</TableCell>
                              <TableCell>Pattern</TableCell>
                              <TableCell align="right">Actions</TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {filteredSupplierRuleProposals.length === 0 ? (
                              <TableRow>
                                <TableCell colSpan={6}>
                                  No pending supplier rule proposals for the current filters.
                                </TableCell>
                              </TableRow>
                            ) : (
                              filteredSupplierRuleProposals.map((row, idx) => {
                                const p = row.payload || {};
                                const supplier = String((p.canonical_name as string) || '').trim() || '—';
                                const pattern = String((p.pattern_raw as string) || '').trim() || '—';
                                const op = String((p.text_match_operator as string) || 'exact');
                                const rowPid = supplierRuleProposalDbId(row);
                                const busy = rowPid != null && proposalBusyKey === `sp-${rowPid}-${idx}`;
                                const rulesTabSupplierProposalStub = supplierRuleRowStubFromProposal(row);
                                const rulesTabSupplierProposalCanPreview =
                                  rowPid != null &&
                                  rulesTabSupplierProposalStub != null &&
                                  supplierRuleCanListMatches(rulesTabSupplierProposalStub);
                                return (
                                  <TableRow key={`supplier-prop-${rowPid ?? `x-${idx}`}`}>
                                    <TableCell>{row.proposed_name || 'Supplier proposal'}</TableCell>
                                    <TableCell>
                                      {rowPid != null
                                        ? formatSupplierProposalPrefixedId(Number(rowPid), 'proposed')
                                        : '—'}
                                    </TableCell>
                                    <TableCell>{supplier}</TableCell>
                                    <TableCell>{classificationRuleTextOperatorLabel(op)}</TableCell>
                                    <TableCell>{pattern}</TableCell>
                                    <TableCell align="right">
                                      <Stack direction="row" spacing={0.5} justifyContent="flex-end">
                                        <Tooltip
                                          title={
                                            rowPid == null
                                              ? 'Proposal id missing — refresh the page.'
                                              : !rulesTabSupplierProposalCanPreview
                                                ? 'Add match criteria (merchant / description / reference, etc.) to preview.'
                                                : 'Preview which historical transactions match this proposal'
                                          }
                                        >
                                          <span>
                                            <IconButton
                                              size="small"
                                              color="primary"
                                              aria-label="Preview supplier proposal on historical transactions"
                                              disabled={!!proposalBusyKey || loading || !rulesTabSupplierProposalCanPreview}
                                              onClick={() => void openSupplierProposalMatchingTransactionsModal(row)}
                                            >
                                              <VisibilityIcon fontSize="small" />
                                            </IconButton>
                                          </span>
                                        </Tooltip>
                                        <IconButton
                                          size="small"
                                          color="primary"
                                          disabled={!!proposalBusyKey || loading}
                                          onClick={() => openSupplierProposalEditor(row)}
                                        >
                                          <EditIcon fontSize="small" />
                                        </IconButton>
                                        <Tooltip title="Duplicate as new rule">
                                          <span>
                                            <IconButton
                                              size="small"
                                              color="primary"
                                              aria-label="Duplicate supplier proposal"
                                              disabled={!!proposalBusyKey || loading || !!ruleDuplicateBusyKey}
                                              onClick={() => void duplicateSupplierRuleProposal(row)}
                                            >
                                              {ruleDuplicateBusyKey ===
                                              `dup-sp-${supplierRuleProposalDbId(row) ?? 'na'}` ? (
                                                <CircularProgress size={14} color="inherit" />
                                              ) : (
                                                <ContentCopyIcon fontSize="small" />
                                              )}
                                            </IconButton>
                                          </span>
                                        </Tooltip>
                                        <Button
                                          size="small"
                                          variant="contained"
                                          color="success"
                                          disabled={!!proposalBusyKey || loading}
                                          onClick={async () => {
                                            const pid = supplierRuleProposalDbId(row);
                                            if (pid == null) {
                                              setError('This supplier proposal has no id — refresh the page and try again.');
                                              scrollPageToClassificationErrorBanner();
                                              return;
                                            }
                                            try {
                                              setProposalBusyKey(`sp-${pid}-${idx}`);
                                              const res = await api.post(
                                                `/api/suppliers/description-alias-proposals/${pid}/accept`,
                                                {}
                                              );
                                              const aliasObj = res.data?.alias as SupplierRuleRow | undefined;
                                              await refreshRulesTabDatasets();
                                              const nextProposalParam =
                                                rebuildProposalIdsAfterSupplierDraftAccepted(rulesProposalIdsFilter, pid);
                                              setRulesProposalIdsFilter(nextProposalParam);
                                              if (aliasObj?.id) {
                                                setRulesIdFilter(formatPrefixedRuleId('supplier', aliasObj.id));
                                              }
                                              setRulesStatusFilter('all');
                                              setSuccessMessage('Accepted supplier proposal and created supplier rule.');
                                              {
                                                const aliasIdAccepted =
                                                  aliasObj?.id != null ? Math.trunc(Number(aliasObj.id)) : NaN;
                                                openBackfillAfterAccept({
                                                  ruleName: String(
                                                    row.proposed_name ||
                                                      (row.payload as { rule_name?: string })?.rule_name ||
                                                      'Supplier rule'
                                                  ),
                                                  prefixedId:
                                                    Number.isFinite(aliasIdAccepted) && aliasIdAccepted > 0
                                                      ? formatPrefixedRuleId('supplier', aliasIdAccepted)
                                                      : '',
                                                  target:
                                                    Number.isFinite(aliasIdAccepted) && aliasIdAccepted > 0
                                                      ? { kind: 'supplier', id: aliasIdAccepted }
                                                      : null
                                                });
                                              }
                                            } catch (err: any) {
                                              setError(
                                                err?.response?.data?.error ||
                                                  err?.message ||
                                                  'Could not accept supplier proposal'
                                              );
                                              scrollPageToClassificationErrorBanner();
                                            } finally {
                                              setProposalBusyKey('');
                                            }
                                          }}
                                        >
                                          {busy ? '…' : 'Accept'}
                                        </Button>
                                        <Button
                                          size="small"
                                          variant="outlined"
                                          color="inherit"
                                          disabled={!!proposalBusyKey || loading}
                                          onClick={async () => {
                                            const pid = supplierRuleProposalDbId(row);
                                            if (pid == null) {
                                              setError('This supplier proposal has no id — refresh the page and try again.');
                                              scrollPageToClassificationErrorBanner();
                                              return;
                                            }
                                            try {
                                              setProposalBusyKey(`sp-${pid}-${idx}`);
                                              await api.post(`/api/suppliers/description-alias-proposals/${pid}/reject`, {});
                                              await refreshRulesTabDatasets();
                                              setSuccessMessage('Supplier proposal dismissed.');
                                            } catch (err: any) {
                                              setError(
                                                err?.response?.data?.error ||
                                                  err?.message ||
                                                  'Could not reject supplier proposal'
                                              );
                                              scrollPageToClassificationErrorBanner();
                                            } finally {
                                              setProposalBusyKey('');
                                            }
                                          }}
                                        >
                                          Reject
                                        </Button>
                                      </Stack>
                                    </TableCell>
                                  </TableRow>
                                );
                              })
                            )}
                          </TableBody>
                        </Table>
                      </TableContainer>
                      </PipelineNestedSectionAccordion>
                      <PipelineNestedSectionAccordion
                        filtersActive={rulesHasFilters}
                        title="Rejected"
                        count={filteredRejectedSupplierRuleProposals.length}
                        expanded={pipelineNestedSectionOpen.step2b.rejected}
                        onExpandedChange={(open) => setPipelineNestedSection('step2b', 'rejected', open)}
                        info={
                          <Tooltip
                            title={
                              <Typography variant="body2">
                                Rejected Step 2B drafts act as tombstones: Generate proposals will not recreate the
                                same signature until you Restore (back to Proposed) or Delete (allow regenerate).
                              </Typography>
                            }
                          >
                            <InfoOutlinedIcon fontSize="small" sx={{ color: 'text.secondary' }} />
                          </Tooltip>
                        }
                      >
                          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                            These do not tag transactions. Restore puts a draft back in Proposed; Delete clears the
                            tombstone so the next Generate can recreate it.
                          </Typography>
                          {selectedRejectedSupplierProposalIds.length > 0 ? (
                            <Paper
                              role="region"
                              aria-label="Bulk actions for selected rejected supplier proposals"
                              variant="outlined"
                              sx={{ p: 1.5, mb: 1, bgcolor: 'action.hover' }}
                            >
                              <Stack spacing={1}>
                                <Typography variant="subtitle2" color="text.secondary" sx={{ fontWeight: 600 }}>
                                  {selectedRejectedSupplierProposalIds.length} rejected proposal
                                  {selectedRejectedSupplierProposalIds.length === 1 ? '' : 's'} selected
                                </Typography>
                                <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                                  <Button
                                    size="small"
                                    variant="outlined"
                                    color="primary"
                                    disabled={!!proposalBusyKey || loading}
                                    onClick={() => void runBulkRejectedSupplierProposalAction('restore')}
                                  >
                                    {proposalBusyKey === 'bulk-supplier-rejected-restore' ? '…' : 'Restore'}
                                  </Button>
                                  <Button
                                    size="small"
                                    variant="outlined"
                                    color="error"
                                    disabled={!!proposalBusyKey || loading}
                                    onClick={() => void runBulkRejectedSupplierProposalAction('delete')}
                                  >
                                    {proposalBusyKey === 'bulk-supplier-rejected-delete' ? '…' : 'Delete'}
                                  </Button>
                                </Stack>
                              </Stack>
                            </Paper>
                          ) : null}
                          <TableContainer>
                            <Table size="small">
                              <TableHead>
                                <TableRow>
                                  <TableCell padding="checkbox" align="center">
                                    <input
                                      type="checkbox"
                                      aria-label="Select all visible rejected supplier proposals"
                                      disabled={selectableRejectedSupplierProposalIds.length === 0 || !!proposalBusyKey}
                                      checked={
                                        selectableRejectedSupplierProposalIds.length > 0 &&
                                        selectableRejectedSupplierProposalIds.every((id) =>
                                          selectedRejectedSupplierProposalIds.includes(id)
                                        )
                                      }
                                      onChange={(e) => {
                                        if (e.target.checked) {
                                          setSelectedRejectedSupplierProposalIds(
                                            selectableRejectedSupplierProposalIds.slice()
                                          );
                                        } else {
                                          setSelectedRejectedSupplierProposalIds([]);
                                        }
                                      }}
                                    />
                                  </TableCell>
                                  <TableCell align="center">Status</TableCell>
                                  <TableCell align="center">ID</TableCell>
                                  <TableCell align="center">Rule Name</TableCell>
                                  <TableCell align="center">Output: Canonical Supplier</TableCell>
                                  <TableCell align="center">Actions</TableCell>
                                </TableRow>
                              </TableHead>
                              <TableBody>
                                {filteredRejectedSupplierRuleProposals.length === 0 ? (
                                  <TableRow>
                                    <TableCell colSpan={6}>
                                      No rejected supplier proposals for the current filters.
                                    </TableCell>
                                  </TableRow>
                                ) : (
                                  filteredRejectedSupplierRuleProposals.map((row, idx) => {
                                    const p = row.payload || {};
                                    const supplier = String((p.canonical_name as string) || '').trim() || '—';
                                    const rowPid = supplierRuleProposalDbId(row);
                                    const busy = rowPid != null && proposalBusyKey === `sp-rej-${rowPid}-${idx}`;
                                    const selected = rowPid != null && selectedRejectedSupplierProposalIds.includes(rowPid);
                                    return (
                                      <TableRow key={`supplier-rej-${rowPid ?? `x-${idx}`}`} selected={selected}>
                                        <TableCell padding="checkbox" align="center">
                                          <input
                                            type="checkbox"
                                            aria-label={
                                              rowPid != null
                                                ? `Select rejected proposal ${formatSupplierProposalPrefixedId(rowPid, 'rejected')}`
                                                : 'Select rejected proposal'
                                            }
                                            disabled={rowPid == null || !!proposalBusyKey}
                                            checked={selected}
                                            onChange={() => undefined}
                                            onClick={(e) => {
                                              e.preventDefault();
                                              if (rowPid == null) return;
                                              applyRulesRowCheckboxSelection({
                                                tableKey: 'supplier-rejected',
                                                orderedKeys: selectableRejectedSupplierProposalIds,
                                                clickedKey: rowPid,
                                                checked: !selected,
                                                shiftKey: e.shiftKey,
                                                setSelection: setSelectedRejectedSupplierProposalIds
                                              });
                                            }}
                                          />
                                        </TableCell>
                                        <TableCell align="center">
                                          <Chip size="small" label="Rejected" color="default" />
                                        </TableCell>
                                        <TableCell align="center">
                                          {rowPid != null
                                            ? formatSupplierProposalPrefixedId(Number(rowPid), 'rejected')
                                            : '—'}
                                        </TableCell>
                                        <TableCell align="center">
                                          {String((p.rule_name as string) || row.proposed_name || '').trim() || '—'}
                                        </TableCell>
                                        <TableCell align="center">{supplier}</TableCell>
                                        <TableCell align="center">
                                          <Stack direction="row" spacing={0.5} justifyContent="center" flexWrap="wrap" useFlexGap>
                                            <Button
                                              size="small"
                                              variant="outlined"
                                              color="primary"
                                              disabled={!!proposalBusyKey || loading || rowPid == null}
                                              onClick={async () => {
                                                if (rowPid == null) return;
                                                try {
                                                  setProposalBusyKey(`sp-rej-${rowPid}-${idx}`);
                                                  await api.post(
                                                    `/api/suppliers/description-alias-proposals/${rowPid}/restore`,
                                                    {}
                                                  );
                                                  await refreshRulesTabDatasets();
                                                  setSelectedRejectedSupplierProposalIds((prev) =>
                                                    prev.filter((id) => id !== rowPid)
                                                  );
                                                  setSuccessMessage(
                                                    `Restored ${formatSupplierProposalPrefixedId(rowPid, 'proposed')} to Proposed.`
                                                  );
                                                } catch (err: any) {
                                                  setError(
                                                    err?.response?.data?.error ||
                                                      err?.message ||
                                                      'Could not restore supplier proposal'
                                                  );
                                                  scrollPageToClassificationErrorBanner();
                                                } finally {
                                                  setProposalBusyKey('');
                                                }
                                              }}
                                            >
                                              {busy ? '…' : 'Restore'}
                                            </Button>
                                            <Button
                                              size="small"
                                              variant="outlined"
                                              color="error"
                                              disabled={!!proposalBusyKey || loading || rowPid == null}
                                              onClick={async () => {
                                                if (rowPid == null) return;
                                                if (
                                                  !window.confirm(
                                                    `Delete rejected proposal ${formatSupplierProposalPrefixedId(rowPid, 'rejected')} permanently? Generate proposals can recreate a matching draft afterward.`
                                                  )
                                                ) {
                                                  return;
                                                }
                                                try {
                                                  setProposalBusyKey(`sp-rej-${rowPid}-${idx}`);
                                                  await api.delete(
                                                    `/api/suppliers/description-alias-proposals/${rowPid}`
                                                  );
                                                  await refreshRulesTabDatasets();
                                                  setSelectedRejectedSupplierProposalIds((prev) =>
                                                    prev.filter((id) => id !== rowPid)
                                                  );
                                                  setSuccessMessage(
                                                    `Deleted rejected ${formatSupplierProposalPrefixedId(rowPid, 'rejected')}.`
                                                  );
                                                } catch (err: any) {
                                                  setError(
                                                    err?.response?.data?.error ||
                                                      err?.message ||
                                                      'Could not delete rejected supplier proposal'
                                                  );
                                                  scrollPageToClassificationErrorBanner();
                                                } finally {
                                                  setProposalBusyKey('');
                                                }
                                              }}
                                            >
                                              Delete
                                            </Button>
                                          </Stack>
                                        </TableCell>
                                      </TableRow>
                                    );
                                  })
                                )}
                              </TableBody>
                            </Table>
                          </TableContainer>
                      </PipelineNestedSectionAccordion>
                    </AccordionDetails>
                  </Accordion>

                  <Accordion
                    expanded={rulesAccordionOpen.vatRules}
                    onChange={(_, expanded) =>
                      setRulesAccordionOpen((prev) => ({ ...prev, vatRules: expanded }))
                    }
                    sx={{ mb: 1.5, order: 4 }}
                  >
                    <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                      <Stack direction="row" spacing={0.75} alignItems="center">
                        <Tooltip title="Select/deselect all visible VAT rules (confirmed + proposed).">
                          <span>
                            <Checkbox
                              size="small"
                              checked={vatGroupAllSelected}
                              indeterminate={vatGroupIndeterminate}
                              disabled={vatGroupSelectableCount === 0}
                              onClick={(e) => e.stopPropagation()}
                              onFocus={(e) => e.stopPropagation()}
                              onChange={() => toggleVatGroupSelection()}
                              inputProps={{ 'aria-label': 'Select all VAT rules in this accordion' }}
                            />
                          </span>
                        </Tooltip>
                        <Typography variant="h6">Pipeline Step 4: VAT Rules</Typography>
                        <Tooltip
                          title={
                            <Typography variant="body2">
                              VAT Rules apply VAT by category (and optional bank account scope). A specific VAT rate
                              on a classification rule overrides these; otherwise these rules apply before category
                              defaults.
                            </Typography>
                          }
                        >
                          <InfoOutlinedIcon fontSize="small" sx={{ color: 'text.secondary' }} />
                        </Tooltip>
                        <Typography component="span" variant="body2" color="text.secondary" sx={{ fontWeight: 400 }}>
                          {formatPipelineStageCounts(
                            visibleVatRuleCounts.confirmed,
                            visibleVatRuleCounts.proposed,
                            visibleVatRuleCounts.rejected,
                            rulesHasFilters
                          )}
                        </Typography>
                      </Stack>
                    </AccordionSummary>
                    <AccordionDetails>
                      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1.5 }} flexWrap="wrap" useFlexGap>
                        <Typography variant="body2" color="text.secondary">
                          {`Showing ${(visibleVatRuleCounts.confirmed + visibleVatRuleCounts.proposed).toLocaleString()} rows (${visibleVatRuleCounts.confirmed.toLocaleString()} Confirmed, ${visibleVatRuleCounts.proposed.toLocaleString()} Proposed)`}
                        </Typography>
                        <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                          <Button
                            variant="outlined"
                            disabled={loading || applyingRules || vatRuleSaving || missingVatAuditBusy}
                            onClick={() => void runMissingVatAudit()}
                          >
                            {missingVatAuditBusy ? 'Running audit…' : 'Run missing-VAT audit'}
                          </Button>
                          <Button
                            variant="outlined"
                            disabled={loading || applyingRules || vatRuleSaving || vatSuggestionGenerateBusy}
                            onClick={() => void runGenerateVatRuleProposals()}
                          >
                            {vatSuggestionGenerateBusy ? 'Generating…' : 'Generate Proposed rules'}
                          </Button>
                          <Button
                            variant="outlined"
                            disabled={loading || applyingRules || vatRuleSaving || vatSuggestionGenerateBusy}
                            onClick={() => void runGenerateVatSuggestionsMissingVatOnly()}
                          >
                            {vatSuggestionGenerateBusy ? 'Generating…' : 'Generate proposals (missing VAT only)'}
                          </Button>
                          <Button
                            variant="outlined"
                            color="secondary"
                            disabled={
                              loading ||
                              applyingRules ||
                              reapplyVatUnconfirmedBusy ||
                              reapplySpecialCaseUnconfirmedBusy ||
                              rerunAllRulesBusy ||
                              dedupeDisplayDupTagsBusy
                            }
                            onClick={() => void runReapplyVatRulesUnconfirmed()}
                          >
                            {reapplyVatUnconfirmedBusy ? 'Re-running VAT…' : 'Re-run ALL VAT Rules'}
                          </Button>
                          <Button
                            variant="contained"
                            disabled={loading || applyingRules || vatRuleSaving || categoriesSortedByName.length === 0}
                            onClick={openVatRuleCreate}
                          >
                            Add VAT rule
                          </Button>
                        </Stack>
                      </Stack>
                      {missingVatAudit && (
                        <Stack spacing={0.5} sx={{ mb: 1 }}>
                          <Typography variant="body2" color="text.secondary">
                            {missingVatAudit.ok === false
                              ? String(
                                  missingVatAudit.message ||
                                    'Audit unavailable (ledger schema does not match Transaction Classification Review).'
                                )
                              : `Last audit: ${Number(missingVatAudit.missing_vat_rate_display_count || 0).toLocaleString()} missing VAT of ${Number(
                                  missingVatAudit.total_transactions || 0
                                ).toLocaleString()} total; ${Number(
                                  missingVatAudit.missing_vat_with_granular_category || 0
                                ).toLocaleString()} with granular category; ${Number(
                                  missingVatAudit.missing_vat_without_granular_category || 0
                                ).toLocaleString()} without granular category; ${Number(
                                  missingVatAudit.vat_conflict_transaction_count || 0
                                ).toLocaleString()} VAT conflict row(s).`}
                          </Typography>
                          {missingVatAudit.ok !== false &&
                          parseCandidates(missingVatAudit.proposal_audit_candidates).length > 0 ? (
                            <Button
                              size="small"
                              variant="text"
                              disabled={auditCandidatesGenerating || missingVatAuditBusy}
                              onClick={() => {
                                const c = parseCandidates(missingVatAudit.proposal_audit_candidates);
                                if (!c.length) return;
                                const rid = missingVatAudit.proposal_audit_run_id;
                                setAuditCandidatesModal({
                                  kind: 'vat_suggestion',
                                  title: 'Missing VAT — generate suggestions from selected buckets',
                                  candidates: c,
                                  proposalAuditRunId:
                                    typeof rid === 'string' && rid.trim().length > 0 ? rid.trim() : null,
                                });
                              }}
                            >
                              Select buckets for draft generation…
                            </Button>
                          ) : null}
                        </Stack>
                      )}
                      {selectedVatRuleIds.length > 0 || selectedVatSuggestionIds.length > 0 ? (
                        <Paper
                          role="region"
                          aria-label="Bulk actions for selected VAT rules and proposals"
                          variant="outlined"
                          sx={{ p: 1.5, mb: 1.25, bgcolor: 'action.hover' }}
                        >
                          <Stack spacing={1.5}>
                            <Typography variant="subtitle2" color="text.secondary" sx={{ fontWeight: 600 }}>
                              {bulkVatSelection.vatRuleCount} VAT rule
                              {bulkVatSelection.vatRuleCount === 1 ? '' : 's'} + {bulkVatSelection.vatSuggCount} proposed
                              {bulkVatSelection.vatSuggCount === 1 ? '' : 's'} selected
                            </Typography>
                            <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                              {bulkVatSelection.showDelete ? (
                                <Tooltip
                                  title={
                                    'You will be asked to confirm. Deletes selected VAT rules and rejects selected proposed VAT rules.'
                                  }
                                >
                                  <span>
                                    <Button
                                      size="small"
                                      variant="outlined"
                                      color="error"
                                      disabled={!!proposalBusyKey || loading}
                                      onClick={() => void runBulkVatRuleAction('delete')}
                                    >
                                      Delete
                                    </Button>
                                  </span>
                                </Tooltip>
                              ) : null}
                              {bulkVatSelection.showAccept ? (
                                <Tooltip title="Any selected proposed VAT rules will be approved.">
                                  <span>
                                    <Button
                                      size="small"
                                      variant="outlined"
                                      color="success"
                                      disabled={!!proposalBusyKey || loading || selectedVatSuggestionIds.length === 0}
                                      onClick={() => void runBulkVatSuggestionAction('approve')}
                                    >
                                      Accept
                                    </Button>
                                  </span>
                                </Tooltip>
                              ) : null}
                              {bulkVatSelection.showReject ? (
                                <Tooltip title="Any selected proposed VAT rules will be rejected.">
                                  <span>
                                    <Button
                                      size="small"
                                      variant="outlined"
                                      disabled={!!proposalBusyKey || loading || selectedVatSuggestionIds.length === 0}
                                      onClick={() => void runBulkVatSuggestionAction('reject')}
                                    >
                                      Reject
                                    </Button>
                                  </span>
                                </Tooltip>
                              ) : null}
                              {bulkVatSelection.showDeactivate ? (
                                <Tooltip title="Deactivates selected VAT rules that are currently active.">
                                  <span>
                                    <Button
                                      size="small"
                                      variant="outlined"
                                      disabled={!!proposalBusyKey || loading || selectedVatRuleIds.length === 0}
                                      onClick={() => void runBulkVatRuleAction('deactivate')}
                                    >
                                      Deactivate
                                    </Button>
                                  </span>
                                </Tooltip>
                              ) : null}
                              {bulkVatSelection.showActivate ? (
                                <Tooltip title="Activates selected VAT rules that are currently inactive.">
                                  <span>
                                    <Button
                                      size="small"
                                      variant="outlined"
                                      disabled={!!proposalBusyKey || loading || selectedVatRuleIds.length === 0}
                                      onClick={() => void runBulkVatRuleAction('activate')}
                                    >
                                      Activate
                                    </Button>
                                  </span>
                                </Tooltip>
                              ) : null}
                              {bulkVatSelection.showRun ? (
                                <Tooltip title="Any selected confirmed VAT rules will be run on all matching transactions.">
                                  <span>
                                    <Button
                                      size="small"
                                      variant="outlined"
                                      disabled={!!proposalBusyKey || loading || selectedVatRuleIds.length === 0}
                                      onClick={() => void runBulkVatRuleAction('run')}
                                    >
                                      Run
                                    </Button>
                                  </span>
                                </Tooltip>
                              ) : null}
                            </Stack>
                            {bulkVatSelection.showApplyEdits ? (
                              <Stack direction={{ xs: 'column', md: 'row' }} spacing={1} alignItems={{ md: 'center' }} flexWrap="wrap" useFlexGap>
                                <TextField
                                  size="small"
                                  type="number"
                                  label="Priority for selected"
                                  value={bulkVatPriority}
                                  onChange={(e) => setBulkVatPriority(e.target.value)}
                                  sx={{ width: { xs: '100%', md: 170 } }}
                                />
                                <FormControl size="small" sx={{ width: { xs: '100%', md: 220 } }}>
                                  <InputLabel id="bulk-vat-account-scope-label">Bank account</InputLabel>
                                  <Select
                                    labelId="bulk-vat-account-scope-label"
                                    label="Bank account"
                                    value={bulkVatAccountScope}
                                    onChange={(e) =>
                                      setBulkVatAccountScope(
                                        String(e.target.value || RULE_BULK_NO_CHANGE) as
                                          | typeof RULE_BULK_NO_CHANGE
                                          | 'both'
                                          | 'ireland'
                                          | 'uk'
                                      )
                                    }
                                  >
                                    <MenuItem value={RULE_BULK_NO_CHANGE}>No change</MenuItem>
                                    {RULE_ACCOUNT_SCOPE_OPTIONS.map((opt) => (
                                      <MenuItem key={`bulk-vat-scope-${opt.value}`} value={opt.value}>
                                        {opt.label}
                                      </MenuItem>
                                    ))}
                                  </Select>
                                </FormControl>
                                <Button
                                  size="small"
                                  variant="contained"
                                  disabled={!!proposalBusyKey || loading || selectedVatRuleIds.length === 0}
                                  onClick={() => void runBulkVatRuleAction('applyEdits')}
                                >
                                  Apply fields to selected confirmed
                                </Button>
                                <Typography variant="caption" color="text.secondary">
                                  VAT rules currently support bulk updates for priority and bank account only.
                                </Typography>
                              </Stack>
                            ) : null}
                          </Stack>
                        </Paper>
                      ) : null}
                      <PipelineNestedSectionAccordion
                        filtersActive={rulesHasFilters}
                        title="Confirmed"
                        count={sortedVisibleVatRules.length}
                        expanded={pipelineNestedSectionOpen.step4.confirmed}
                        onExpandedChange={(open) => setPipelineNestedSection('step4', 'confirmed', open)}
                        first
                      >
                      <TableContainer>
                        <Table size="small">
                          <TableHead>
                            <TableRow>
                              <TableCell padding="checkbox">
                                <input
                                  type="checkbox"
                                  disabled={!!proposalBusyKey || loading}
                                  checked={
                                    sortedVisibleVatRules.length > 0 &&
                                    sortedVisibleVatRules.every((r) => selectedVatRuleIds.includes(Number(r.id)))
                                  }
                                  onChange={(e) => {
                                    const ids = sortedVisibleVatRules.map((r) => Number(r.id));
                                    const idSet = new Set(ids);
                                    if (e.target.checked) {
                                      setSelectedVatRuleIds((prev) =>
                                        Array.from(new Set(prev.concat(ids)))
                                      );
                                    } else {
                                      setSelectedVatRuleIds((prev) => prev.filter((id) => !idSet.has(Number(id))));
                                    }
                                  }}
                                />
                              </TableCell>
                              <TableCell sortDirection={vatRulesTableSort?.key === 'status' ? vatRulesTableSort.dir : false}>
                                <TableSortLabel
                                  active={vatRulesTableSort?.key === 'status'}
                                  direction={vatRulesTableSort?.key === 'status' ? vatRulesTableSort.dir : 'asc'}
                                  onClick={() => setVatRulesTableSort((prev) => nextSortState(prev, 'status'))}
                                >
                                  Status
                                </TableSortLabel>
                              </TableCell>
                              <TableCell sortDirection={vatRulesTableSort?.key === 'id' ? vatRulesTableSort.dir : false}>
                                <TableSortLabel
                                  active={vatRulesTableSort?.key === 'id'}
                                  direction={vatRulesTableSort?.key === 'id' ? vatRulesTableSort.dir : 'asc'}
                                  onClick={() => setVatRulesTableSort((prev) => nextSortState(prev, 'id'))}
                                >
                                  ID
                                </TableSortLabel>
                              </TableCell>
                              <TableCell sortDirection={vatRulesTableSort?.key === 'priority' ? vatRulesTableSort.dir : false}>
                                <TableSortLabel
                                  active={vatRulesTableSort?.key === 'priority'}
                                  direction={vatRulesTableSort?.key === 'priority' ? vatRulesTableSort.dir : 'asc'}
                                  onClick={() => setVatRulesTableSort((prev) => nextSortState(prev, 'priority'))}
                                >
                                  Priority
                                </TableSortLabel>
                              </TableCell>
                              <TableCell sortDirection={vatRulesTableSort?.key === 'name' ? vatRulesTableSort.dir : false}>
                                <TableSortLabel
                                  active={vatRulesTableSort?.key === 'name'}
                                  direction={vatRulesTableSort?.key === 'name' ? vatRulesTableSort.dir : 'asc'}
                                  onClick={() => setVatRulesTableSort((prev) => nextSortState(prev, 'name'))}
                                >
                                  Rule name
                                </TableSortLabel>
                              </TableCell>
                              <TableCell sortDirection={vatRulesTableSort?.key === 'category' ? vatRulesTableSort.dir : false}>
                                <TableSortLabel
                                  active={vatRulesTableSort?.key === 'category'}
                                  direction={vatRulesTableSort?.key === 'category' ? vatRulesTableSort.dir : 'asc'}
                                  onClick={() => setVatRulesTableSort((prev) => nextSortState(prev, 'category'))}
                                >
                                  Category
                                </TableSortLabel>
                              </TableCell>
                              <TableCell
                                sortDirection={vatRulesTableSort?.key === 'vat' ? vatRulesTableSort.dir : false}
                                sx={OUTPUT_COLUMN_SX}
                              >
                                <TableSortLabel
                                  active={vatRulesTableSort?.key === 'vat'}
                                  direction={vatRulesTableSort?.key === 'vat' ? vatRulesTableSort.dir : 'asc'}
                                  onClick={() => setVatRulesTableSort((prev) => nextSortState(prev, 'vat'))}
                                >
                                  Output: VAT Rate
                                </TableSortLabel>
                              </TableCell>
                              <TableCell sx={RULE_ACCOUNT_COLUMN_SX} sortDirection={vatRulesTableSort?.key === 'account' ? vatRulesTableSort.dir : false}>
                                <TableSortLabel
                                  active={vatRulesTableSort?.key === 'account'}
                                  direction={vatRulesTableSort?.key === 'account' ? vatRulesTableSort.dir : 'asc'}
                                  onClick={() => setVatRulesTableSort((prev) => nextSortState(prev, 'account'))}
                                >
                                  Bank /n A/C
                                </TableSortLabel>
                              </TableCell>
                              <TableCell sortDirection={vatRulesTableSort?.key === 'firedCount' ? vatRulesTableSort.dir : false}>
                                <TableSortLabel
                                  active={vatRulesTableSort?.key === 'firedCount'}
                                  direction={vatRulesTableSort?.key === 'firedCount' ? vatRulesTableSort.dir : 'asc'}
                                  onClick={() => setVatRulesTableSort((prev) => nextSortState(prev, 'firedCount'))}
                                >
                                  Tx fired
                                </TableSortLabel>
                              </TableCell>
                              <TableCell sortDirection={vatRulesTableSort?.key === 'active' ? vatRulesTableSort.dir : false}>
                                <TableSortLabel
                                  active={vatRulesTableSort?.key === 'active'}
                                  direction={vatRulesTableSort?.key === 'active' ? vatRulesTableSort.dir : 'asc'}
                                  onClick={() => setVatRulesTableSort((prev) => nextSortState(prev, 'active'))}
                                >
                                  Rule Active
                                </TableSortLabel>
                              </TableCell>
                              <TableCell align="right">Actions</TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {sortedVisibleVatRules.length === 0 ? (
                              <TableRow>
                                <TableCell colSpan={11}>No VAT rules for the current filters.</TableCell>
                              </TableRow>
                            ) : (
                              sortedVisibleVatRules.map((row) => (
                                <TableRow
                                  key={`vat-rule-${row.id}`}
                                  sx={ruleTableRowSavingSx(
                                    theme,
                                    ruleTableSaveHighlight?.kind === 'vat' &&
                                      ruleTableSaveHighlight.rowKey === `vat-rule-${row.id}`
                                  )}
                                >
                                  <TableCell padding="checkbox">
                                    <input
                                      type="checkbox"
                                      disabled={!!proposalBusyKey || loading}
                                      checked={selectedVatRuleIds.includes(Number(row.id))}
                                      onChange={() => undefined}
                                      onClick={(e) => {
                                        e.preventDefault();
                                        if (proposalBusyKey || loading) return;
                                        const id = Number(row.id);
                                        const selected = selectedVatRuleIds.includes(id);
                                        applyRulesRowCheckboxSelection({
                                          tableKey: 'vat-confirmed',
                                          orderedKeys: sortedVisibleVatRules.map((r) => Number(r.id)),
                                          clickedKey: id,
                                          checked: !selected,
                                          shiftKey: e.shiftKey,
                                          setSelection: setSelectedVatRuleIds
                                        });
                                      }}
                                    />
                                  </TableCell>
                                  <TableCell><Chip size="small" label="Confirmed" color="success" /></TableCell>
                                  <TableCell>{formatPrefixedRuleId('vat', row.id)}</TableCell>
                                  <TableCell>{Number(row.priority ?? 100)}</TableCell>
                                  <TableCell>{String(row.name || '').trim() || '—'}</TableCell>
                                  <TableCell>
                                    <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
                                      {(() => {
                                        const byId = new Map<number, string>();
                                        (row.categories || []).forEach((c) => {
                                          const cid = Number(c?.id);
                                          const cname = String(c?.name || '').trim();
                                          if (Number.isFinite(cid) && cid > 0 && cname) {
                                            byId.set(cid, cname);
                                          }
                                        });
                                        (row.category_ids || []).forEach((rawId) => {
                                          const cid = Number(rawId);
                                          if (!Number.isFinite(cid) || cid <= 0 || byId.has(cid)) return;
                                          byId.set(cid, categoryNameById.get(cid) || `Category #${cid}`);
                                        });
                                        return Array.from(byId.entries()).map(([cid, name]) => {
                                          const chipBusy = vatRuleCategoryDeleteBusyKey === `${Number(row.id)}-${cid}`;
                                          return (
                                            <Chip
                                              key={`vat-rule-category-${row.id}-${cid}`}
                                              size="small"
                                              label={name}
                                              clickable
                                              onClick={() => openCategoriesTabForName(name)}
                                              onDelete={
                                                chipBusy || !!proposalBusyKey || loading || vatRuleSaving
                                                  ? undefined
                                                  : () => void removeVatRuleCategoryQuick(row, cid, name)
                                              }
                                              variant="outlined"
                                            />
                                          );
                                        });
                                      })()}
                                    </Stack>
                                  </TableCell>
                                  <TableCell sx={OUTPUT_COLUMN_SX}>
                                    <CanonicalVatRateChip value={row.vat_rate} maxWidth={110} />
                                  </TableCell>
                                  <TableCell sx={RULE_ACCOUNT_COLUMN_SX}>{ruleAccountScopeTableLabel(row.account_scope)}</TableCell>
                                  <TableCell>{Number(ruleFireCounts.vat[row.id] || 0).toLocaleString()}</TableCell>
                                  <TableCell>{row.is_active === false ? 'Inactive' : 'Active'}</TableCell>
                                  <TableCell align="right">
                                    <Stack direction="row" spacing={0.25} justifyContent="flex-end">
                                      <Tooltip
                                        title={
                                          vatRuleCanListMatches(row)
                                            ? 'View matching transactions'
                                            : 'VAT rule needs at least one category to list transactions.'
                                        }
                                      >
                                        <span>
                                          <IconButton
                                            size="small"
                                            color="primary"
                                            aria-label="View matching transactions"
                                            onClick={() => void openVatRuleMatchingTransactionsModal(row)}
                                            disabled={loading || applyingRules || !vatRuleCanListMatches(row)}
                                          >
                                            <VisibilityIcon fontSize="small" />
                                          </IconButton>
                                        </span>
                                      </Tooltip>
                                      <Tooltip title="Apply this VAT rule to matching tags">
                                        <span>
                                          <IconButton
                                            size="small"
                                            color="primary"
                                            aria-label="Apply this VAT rule to all transactions"
                                            onClick={() => void applySingleVatRuleToAllTransactions(row)}
                                            disabled={loading || applyingRules || applySingleRuleBusyKey !== ''}
                                          >
                                            {applySingleRuleBusyKey === `vat-${row.id}` ? (
                                              <CircularProgress size={14} color="inherit" />
                                            ) : (
                                              <PlayArrowIcon fontSize="small" />
                                            )}
                                          </IconButton>
                                        </span>
                                      </Tooltip>
                                      <IconButton size="small" color="primary" onClick={() => openVatRuleEditor(row)}>
                                        <EditIcon fontSize="small" />
                                      </IconButton>
                                      <Tooltip title="Duplicate rule">
                                        <span>
                                          <IconButton
                                            size="small"
                                            color="primary"
                                            aria-label="Duplicate VAT rule"
                                            disabled={
                                              loading ||
                                              applyingRules ||
                                              !!ruleDuplicateBusyKey ||
                                              vatRuleCategoryDeleteBusyKey !== null
                                            }
                                            onClick={() => void duplicateVatRule(row)}
                                          >
                                            {ruleDuplicateBusyKey === `dup-vat-${row.id}` ? (
                                              <CircularProgress size={14} color="inherit" />
                                            ) : (
                                              <ContentCopyIcon fontSize="small" />
                                            )}
                                          </IconButton>
                                        </span>
                                      </Tooltip>
                                      <IconButton
                                        size="small"
                                        color="error"
                                        onClick={() => void deactivateVatRule(row)}
                                        disabled={row.is_active === false}
                                      >
                                        <DeleteOutlineIcon fontSize="small" />
                                      </IconButton>
                                    </Stack>
                                  </TableCell>
                                </TableRow>
                              ))
                            )}
                          </TableBody>
                        </Table>
                      </TableContainer>
                      </PipelineNestedSectionAccordion>
                      <PipelineNestedSectionAccordion
                        filtersActive={rulesHasFilters}
                        title="Proposed"
                        count={visibleVatRuleSuggestions.length}
                        expanded={pipelineNestedSectionOpen.step4.proposed}
                        onExpandedChange={(open) => setPipelineNestedSection('step4', 'proposed', open)}
                      >
                      {selectedVatProposedCount > 0 ? (
                        <Paper
                          role="region"
                          aria-label="Bulk actions for selected proposed VAT rules"
                          variant="outlined"
                          sx={{ p: 1.5, mb: 1, bgcolor: 'action.hover' }}
                        >
                          <Stack spacing={1}>
                            <Typography variant="subtitle2" color="text.secondary" sx={{ fontWeight: 600 }}>
                              {selectedVatProposedCount} proposed VAT rule
                              {selectedVatProposedCount === 1 ? '' : 's'} selected
                            </Typography>
                            <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                              <Button
                                size="small"
                                variant="outlined"
                                color="success"
                                disabled={!!proposalBusyKey || loading}
                                onClick={() => void runBulkVatSuggestionAction('approve')}
                              >
                                Accept
                              </Button>
                              <Button
                                size="small"
                                variant="outlined"
                                disabled={!!proposalBusyKey || loading}
                                onClick={() => void runBulkVatSuggestionAction('reject')}
                              >
                                Reject
                              </Button>
                              <Button
                                size="small"
                                variant="outlined"
                                color="error"
                                disabled={!!proposalBusyKey || loading}
                                onClick={() => void runBulkVatSuggestionAction('reject')}
                              >
                                Delete
                              </Button>
                            </Stack>
                          </Stack>
                        </Paper>
                      ) : null}
                      <TableContainer>
                        <Table size="small">
                          <TableHead>
                            <TableRow>
                              <TableCell padding="checkbox">
                                <input
                                  type="checkbox"
                                  disabled={!!proposalBusyKey || loading}
                                  checked={
                                    visibleVatRuleSuggestions.length > 0 &&
                                    visibleVatRuleSuggestions.every((r) => selectedVatSuggestionIds.includes(r.id))
                                  }
                                  onChange={(e) => {
                                    if (e.target.checked) {
                                      setSelectedVatSuggestionIds(visibleVatRuleSuggestions.map((r) => r.id));
                                    } else {
                                      setSelectedVatSuggestionIds([]);
                                    }
                                  }}
                                />
                              </TableCell>
                              <TableCell>Status</TableCell>
                              <TableCell>ID</TableCell>
                              <TableCell>Category</TableCell>
                              <TableCell sx={OUTPUT_COLUMN_SX}>Output: VAT Rate</TableCell>
                              <TableCell>Band</TableCell>
                              <TableCell sx={RULE_ACCOUNT_COLUMN_SX}>Bank /n A/C</TableCell>
                              <TableCell>Confidence</TableCell>
                              <TableCell align="right">Actions</TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {visibleVatRuleSuggestions.length === 0 ? (
                              <TableRow>
                                <TableCell colSpan={9}>No proposed VAT rules for the current filters.</TableCell>
                              </TableRow>
                            ) : (
                              visibleVatRuleSuggestions.map((row) => (
                                <TableRow key={`vat-suggestion-${row.id}`}>
                                  <TableCell padding="checkbox">
                                    <input
                                      type="checkbox"
                                      disabled={!!proposalBusyKey || loading || vatSuggestionActionBusyId !== null}
                                      checked={selectedVatSuggestionIds.includes(row.id)}
                                      onChange={() => undefined}
                                      onClick={(e) => {
                                        e.preventDefault();
                                        if (proposalBusyKey || loading || vatSuggestionActionBusyId !== null) return;
                                        const selected = selectedVatSuggestionIds.includes(row.id);
                                        applyRulesRowCheckboxSelection({
                                          tableKey: 'vat-proposed',
                                          orderedKeys: visibleVatRuleSuggestions.map((r) => r.id),
                                          clickedKey: row.id,
                                          checked: !selected,
                                          shiftKey: e.shiftKey,
                                          setSelection: setSelectedVatSuggestionIds
                                        });
                                      }}
                                    />
                                  </TableCell>
                                  <TableCell><Chip size="small" label="Proposed" color="warning" /></TableCell>
                                  <TableCell>{formatPrefixedRuleId('vat', row.id, 'proposed')}</TableCell>
                                  <TableCell>{row.category_name || row.category?.name || '—'}</TableCell>
                                  <TableCell sx={OUTPUT_COLUMN_SX}>
                                    <CanonicalVatRateChip value={row.suggested_vat_rate} maxWidth={120} />
                                  </TableCell>
                                  <TableCell>{row.suggested_band || '—'}</TableCell>
                                  <TableCell sx={RULE_ACCOUNT_COLUMN_SX}>{ruleAccountScopeTableLabel(row.account_scope)}</TableCell>
                                  <TableCell>{formatVatSuggestionConfidence(row.confidence_score)}</TableCell>
                                  <TableCell align="right">
                                    <Stack direction="row" spacing={0.5} justifyContent="flex-end">
                                      <Tooltip title={vatSuggestionExplanation(row.rationale)}>
                                        <IconButton size="small" color="primary">
                                          <InfoOutlinedIcon fontSize="small" />
                                        </IconButton>
                                      </Tooltip>
                                      <Button
                                        size="small"
                                        variant="contained"
                                        color="success"
                                        disabled={
                                          !!proposalBusyKey || loading || vatSuggestionActionBusyId === row.id
                                        }
                                        onClick={() => void approveVatRuleSuggestion(row)}
                                      >
                                        {vatSuggestionActionBusyId === row.id ? '…' : 'Approve'}
                                      </Button>
                                      <Button
                                        size="small"
                                        variant="outlined"
                                        color="inherit"
                                        disabled={
                                          !!proposalBusyKey || loading || vatSuggestionActionBusyId === row.id
                                        }
                                        onClick={() => void rejectVatRuleSuggestion(row)}
                                      >
                                        Reject
                                      </Button>
                                    </Stack>
                                  </TableCell>
                                </TableRow>
                              ))
                            )}
                          </TableBody>
                        </Table>
                      </TableContainer>
                      </PipelineNestedSectionAccordion>
                      <PipelineNestedSectionAccordion
                        filtersActive={rulesHasFilters}
                        title="Rejected"
                        count={filteredRejectedVatRuleSuggestions.length}
                        expanded={pipelineNestedSectionOpen.step4.rejected}
                        onExpandedChange={(open) => setPipelineNestedSection('step4', 'rejected', open)}
                        info={
                          <Tooltip
                            title={
                              <Typography variant="body2">
                                Rejected VAT suggestions are kept so Generate does not immediately recreate the same
                                draft. Restore returns them to Proposed; Delete removes the tombstone.
                              </Typography>
                            }
                          >
                            <InfoOutlinedIcon fontSize="small" sx={{ color: 'text.secondary' }} />
                          </Tooltip>
                        }
                      >
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                          These do not apply VAT. Restore puts a suggestion back in Proposed; Delete clears it.
                        </Typography>
                        {selectedRejectedVatSuggestionIds.length > 0 ? (
                          <Paper
                            role="region"
                            aria-label="Bulk actions for selected rejected VAT suggestions"
                            variant="outlined"
                            sx={{ p: 1.5, mb: 1, bgcolor: 'action.hover' }}
                          >
                            <Stack spacing={1}>
                              <Typography variant="subtitle2" color="text.secondary" sx={{ fontWeight: 600 }}>
                                {selectedRejectedVatSuggestionIds.length} rejected VAT suggestion
                                {selectedRejectedVatSuggestionIds.length === 1 ? '' : 's'} selected
                              </Typography>
                              <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                                <Button
                                  size="small"
                                  variant="outlined"
                                  color="primary"
                                  disabled={!!proposalBusyKey || loading}
                                  onClick={() => void runBulkRejectedVatSuggestionAction('restore')}
                                >
                                  {proposalBusyKey === 'bulk-vat-rejected-restore' ? '…' : 'Restore'}
                                </Button>
                                <Button
                                  size="small"
                                  variant="outlined"
                                  color="error"
                                  disabled={!!proposalBusyKey || loading}
                                  onClick={() => void runBulkRejectedVatSuggestionAction('delete')}
                                >
                                  {proposalBusyKey === 'bulk-vat-rejected-delete' ? '…' : 'Delete'}
                                </Button>
                              </Stack>
                            </Stack>
                          </Paper>
                        ) : null}
                        <TableContainer sx={{ overflowX: 'hidden', maxWidth: '100%' }}>
                          <Table size="small" sx={FIT_TABLE_SX}>
                            <FitColgroup cols={equalFitCols(['c0', 'c1', 'c2', 'c3', 'c4', 'c5'], { flexKey: 'c3' })} />
                          <TableHead>
                              <TableRow>
                                <TableCell padding="checkbox" align="center">
                                  <input
                                    type="checkbox"
                                    aria-label="Select all visible rejected VAT suggestions"
                                    disabled={selectableRejectedVatSuggestionIds.length === 0 || !!proposalBusyKey}
                                    checked={
                                      selectableRejectedVatSuggestionIds.length > 0 &&
                                      selectableRejectedVatSuggestionIds.every((id) =>
                                        selectedRejectedVatSuggestionIds.includes(id)
                                      )
                                    }
                                    onChange={(e) => {
                                      if (e.target.checked) {
                                        setSelectedRejectedVatSuggestionIds(
                                          selectableRejectedVatSuggestionIds.slice()
                                        );
                                      } else {
                                        setSelectedRejectedVatSuggestionIds([]);
                                      }
                                    }}
                                  />
                                </TableCell>
                                <TableCell align="center">Status</TableCell>
                                <TableCell align="center">ID</TableCell>
                                <TableCell align="center">Category</TableCell>
                                <TableCell align="center">Suggested VAT</TableCell>
                                <TableCell align="center">Actions</TableCell>
                              </TableRow>
                            </TableHead>
                            <TableBody>
                              {filteredRejectedVatRuleSuggestions.length === 0 ? (
                                <TableRow>
                                  <TableCell colSpan={6}>
                                    No rejected VAT suggestions for the current filters.
                                  </TableCell>
                                </TableRow>
                              ) : (
                                filteredRejectedVatRuleSuggestions.map((row, idx) => {
                                  const categoryName = String(
                                    row.category_name || row.category?.name || ''
                                  ).trim() || '—';
                                  const rowId = Number(row.id);
                                  const busy = proposalBusyKey === `vat-rej-${rowId}-${idx}`;
                                  const selected = selectedRejectedVatSuggestionIds.includes(rowId);
                                  const idLabel = formatPrefixedRuleId('vat', rowId, 'rejected');
                                  return (
                                    <TableRow key={`vat-rej-${rowId}`} selected={selected}>
                                      <TableCell padding="checkbox" align="center">
                                        <input
                                          type="checkbox"
                                          aria-label={`Select rejected VAT suggestion ${idLabel}`}
                                          disabled={!!proposalBusyKey}
                                          checked={selected}
                                          onChange={() => undefined}
                                          onClick={(e) => {
                                            e.preventDefault();
                                            applyRulesRowCheckboxSelection({
                                              tableKey: 'vat-rejected',
                                              orderedKeys: selectableRejectedVatSuggestionIds,
                                              clickedKey: rowId,
                                              checked: !selected,
                                              shiftKey: e.shiftKey,
                                              setSelection: setSelectedRejectedVatSuggestionIds
                                            });
                                          }}
                                        />
                                      </TableCell>
                                      <TableCell align="center">
                                        <Chip size="small" label="Rejected" color="default" />
                                      </TableCell>
                                      <TableCell align="center">{idLabel}</TableCell>
                                      <TableCell align="center">{categoryName}</TableCell>
                                      <TableCell align="center">
                                        {String(row.suggested_vat_rate || '').trim() || '—'}
                                      </TableCell>
                                      <TableCell align="center">
                                        <Stack
                                          direction="row"
                                          spacing={0.5}
                                          justifyContent="center"
                                          flexWrap="wrap"
                                          useFlexGap
                                        >
                                          <Button
                                            size="small"
                                            variant="outlined"
                                            color="primary"
                                            disabled={!!proposalBusyKey || loading}
                                            onClick={async () => {
                                              try {
                                                setProposalBusyKey(`vat-rej-${rowId}-${idx}`);
                                                await api.post(
                                                  `/api/transaction-classification-rules/vat-rules/suggestions/${rowId}/restore`,
                                                  {}
                                                );
                                                await refreshRulesTabDatasets();
                                                setSelectedRejectedVatSuggestionIds((prev) =>
                                                  prev.filter((id) => id !== rowId)
                                                );
                                                setSuccessMessage(`Restored ${idLabel} to Proposed.`);
                                              } catch (err: any) {
                                                setError(
                                                  err?.response?.data?.error ||
                                                    err?.message ||
                                                    'Could not restore VAT suggestion'
                                                );
                                                scrollPageToClassificationErrorBanner();
                                              } finally {
                                                setProposalBusyKey('');
                                              }
                                            }}
                                          >
                                            {busy ? '…' : 'Restore'}
                                          </Button>
                                          <Button
                                            size="small"
                                            variant="outlined"
                                            color="error"
                                            disabled={!!proposalBusyKey || loading}
                                            onClick={async () => {
                                              if (
                                                !window.confirm(
                                                  `Delete rejected VAT suggestion ${idLabel} permanently?`
                                                )
                                              ) {
                                                return;
                                              }
                                              try {
                                                setProposalBusyKey(`vat-rej-${rowId}-${idx}`);
                                                await api.delete(
                                                  `/api/transaction-classification-rules/vat-rules/suggestions/${rowId}`
                                                );
                                                await refreshRulesTabDatasets();
                                                setSelectedRejectedVatSuggestionIds((prev) =>
                                                  prev.filter((id) => id !== rowId)
                                                );
                                                setSuccessMessage(`Deleted rejected ${idLabel}.`);
                                              } catch (err: any) {
                                                setError(
                                                  err?.response?.data?.error ||
                                                    err?.message ||
                                                    'Could not delete VAT suggestion'
                                                );
                                                scrollPageToClassificationErrorBanner();
                                              } finally {
                                                setProposalBusyKey('');
                                              }
                                            }}
                                          >
                                            Delete
                                          </Button>
                                        </Stack>
                                      </TableCell>
                                    </TableRow>
                                  );
                                })
                              )}
                            </TableBody>
                          </Table>
                        </TableContainer>
                      </PipelineNestedSectionAccordion>

                    </AccordionDetails>
                  </Accordion>

                  <Accordion
                    expanded={rulesAccordionOpen.specialCaseRules}
                    onChange={(_, expanded) =>
                      setRulesAccordionOpen((prev) => ({ ...prev, specialCaseRules: expanded }))
                    }
                    sx={{ mb: 1.5, order: 5 }}
                  >
                    <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                      <Stack direction="row" spacing={0.75} alignItems="center">
                        <Tooltip title="Select/deselect all visible Special Case rules in this accordion.">
                          <span>
                            <Checkbox
                              size="small"
                              checked={specialCaseGroupAllSelected}
                              indeterminate={specialCaseGroupIndeterminate}
                              disabled={specialCaseGroupSelectableCount === 0}
                              onClick={(e) => e.stopPropagation()}
                              onFocus={(e) => e.stopPropagation()}
                              onChange={() => toggleSpecialCaseGroupSelection()}
                              inputProps={{ 'aria-label': 'Select all Special Case rules in this accordion' }}
                            />
                          </span>
                        </Tooltip>
                        <Typography variant="h6">Pipeline Step 5: Special Case Rules</Typography>
                        <Tooltip
                          title={
                            <Typography variant="body2">
                              Special Case rules are final overrides for rare exceptions. They can match by
                              supplier/category/current VAT/description and then set a specific VAT rate.
                            </Typography>
                          }
                        >
                          <InfoOutlinedIcon fontSize="small" sx={{ color: 'text.secondary' }} />
                        </Tooltip>
                        <Typography component="span" variant="body2" color="text.secondary" sx={{ fontWeight: 400 }}>
                          {formatPipelineStageCounts(
                            visibleSpecialCaseRules.length,
                            0,
                            0,
                            rulesHasFilters
                          )}
                        </Typography>
                      </Stack>
                    </AccordionSummary>
                    <AccordionDetails>
                      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1.5 }} flexWrap="wrap" useFlexGap>
                        <Typography variant="body2" color="text.secondary">
                          {`Showing ${visibleSpecialCaseRules.length.toLocaleString()} rows (${visibleSpecialCaseRules.length.toLocaleString()} Confirmed, 0 Proposed)`}
                        </Typography>
                        <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                          <Button
                            variant="outlined"
                            color="secondary"
                            disabled={
                              loading ||
                              applyingRules ||
                              reapplySpecialCaseUnconfirmedBusy ||
                              rerunAllRulesBusy ||
                              dedupeDisplayDupTagsBusy
                            }
                            onClick={() => void runReapplySpecialCaseRulesUnconfirmed()}
                          >
                            {reapplySpecialCaseUnconfirmedBusy ? 'Re-running Special Cases…' : 'Re-run ALL Special Case Rules'}
                          </Button>
                          <Button
                            variant="contained"
                            disabled={loading || applyingRules || specialCaseRuleSaving}
                            onClick={openSpecialCaseRuleCreate}
                          >
                            Add Special Case rule
                          </Button>
                        </Stack>
                      </Stack>
                      {selectedSpecialCaseRuleIds.length > 0 ? (
                        <Paper
                          role="region"
                          aria-label="Bulk actions for selected special case rules"
                          variant="outlined"
                          sx={{ p: 1.5, mb: 1.25, bgcolor: 'action.hover' }}
                        >
                          <Stack spacing={1.5}>
                            <Typography variant="subtitle2" color="text.secondary" sx={{ fontWeight: 600 }}>
                              {bulkSpecialCaseSelection.count} special case rule
                              {bulkSpecialCaseSelection.count === 1 ? '' : 's'} selected
                            </Typography>
                            <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                              {bulkSpecialCaseSelection.showDelete ? (
                                <Tooltip title="You will be asked to confirm. Permanently deletes the selected special case rules.">
                                  <span>
                                    <Button
                                      size="small"
                                      variant="outlined"
                                      color="error"
                                      disabled={!!proposalBusyKey || loading}
                                      onClick={() => void runBulkSpecialCaseRuleAction('delete')}
                                    >
                                      Delete
                                    </Button>
                                  </span>
                                </Tooltip>
                              ) : null}
                              {bulkSpecialCaseSelection.showDeactivate ? (
                                <Tooltip title="Deactivates selected rules that are currently active.">
                                  <span>
                                    <Button
                                      size="small"
                                      variant="outlined"
                                      disabled={!!proposalBusyKey || loading}
                                      onClick={() => void runBulkSpecialCaseRuleAction('deactivate')}
                                    >
                                      Deactivate
                                    </Button>
                                  </span>
                                </Tooltip>
                              ) : null}
                              {bulkSpecialCaseSelection.showActivate ? (
                                <Tooltip title="Activates selected rules that are currently inactive.">
                                  <span>
                                    <Button
                                      size="small"
                                      variant="outlined"
                                      disabled={!!proposalBusyKey || loading}
                                      onClick={() => void runBulkSpecialCaseRuleAction('activate')}
                                    >
                                      Activate
                                    </Button>
                                  </span>
                                </Tooltip>
                              ) : null}
                              {bulkSpecialCaseSelection.showRun ? (
                                <Tooltip title="Runs the selected special case rules on all matching transactions.">
                                  <span>
                                    <Button
                                      size="small"
                                      variant="outlined"
                                      disabled={!!proposalBusyKey || loading}
                                      onClick={() => void runBulkSpecialCaseRuleAction('run')}
                                    >
                                      Run
                                    </Button>
                                  </span>
                                </Tooltip>
                              ) : null}
                            </Stack>
                            {bulkSpecialCaseSelection.showApplyEdits ? (
                              <Stack direction={{ xs: 'column', md: 'row' }} spacing={1} alignItems={{ md: 'center' }} flexWrap="wrap" useFlexGap>
                                <TextField
                                  size="small"
                                  type="number"
                                  label="Priority for selected"
                                  value={bulkSpecialCasePriority}
                                  onChange={(e) => setBulkSpecialCasePriority(e.target.value)}
                                  sx={{ width: { xs: '100%', md: 170 } }}
                                />
                                <FormControl size="small" sx={{ width: { xs: '100%', md: 220 } }}>
                                  <InputLabel id="bulk-special-account-scope-label">Bank account</InputLabel>
                                  <Select
                                    labelId="bulk-special-account-scope-label"
                                    label="Bank account"
                                    value={bulkSpecialCaseAccountScope}
                                    onChange={(e) =>
                                      setBulkSpecialCaseAccountScope(
                                        String(e.target.value || RULE_BULK_NO_CHANGE) as
                                          | typeof RULE_BULK_NO_CHANGE
                                          | 'both'
                                          | 'ireland'
                                          | 'uk'
                                      )
                                    }
                                  >
                                    <MenuItem value={RULE_BULK_NO_CHANGE}>No change</MenuItem>
                                    {RULE_ACCOUNT_SCOPE_OPTIONS.map((opt) => (
                                      <MenuItem key={`bulk-special-scope-${opt.value}`} value={opt.value}>
                                        {opt.label}
                                      </MenuItem>
                                    ))}
                                  </Select>
                                </FormControl>
                                <TextField
                                  size="small"
                                  label="Comment to add"
                                  value={bulkSpecialCaseComment}
                                  onChange={(e) => setBulkSpecialCaseComment(e.target.value)}
                                  sx={{ minWidth: { xs: '100%', md: 320 } }}
                                />
                                <Button
                                  size="small"
                                  variant="contained"
                                  disabled={!!proposalBusyKey || loading}
                                  onClick={() => void runBulkSpecialCaseRuleAction('applyEdits')}
                                >
                                  Apply fields to selected confirmed
                                </Button>
                              </Stack>
                            ) : null}
                          </Stack>
                        </Paper>
                      ) : null}
                      <PipelineNestedSectionAccordion
                        filtersActive={rulesHasFilters}
                        title="Confirmed"
                        count={sortedVisibleSpecialCaseRules.length}
                        expanded={pipelineNestedSectionOpen.step5.confirmed}
                        onExpandedChange={(open) => setPipelineNestedSection('step5', 'confirmed', open)}
                        first
                      >
                      <TableContainer>
                        <Table size="small">
                          <TableHead>
                            <TableRow>
                              <TableCell padding="checkbox">
                                <input
                                  type="checkbox"
                                  disabled={!!proposalBusyKey || loading}
                                  checked={
                                    sortedVisibleSpecialCaseRules.length > 0 &&
                                    sortedVisibleSpecialCaseRules.every((r) =>
                                      selectedSpecialCaseRuleIds.includes(Number(r.id))
                                    )
                                  }
                                  onChange={(e) => {
                                    const ids = sortedVisibleSpecialCaseRules.map((r) => Number(r.id));
                                    const idSet = new Set(ids);
                                    if (e.target.checked) {
                                      setSelectedSpecialCaseRuleIds((prev) =>
                                        Array.from(new Set(prev.concat(ids)))
                                      );
                                    } else {
                                      setSelectedSpecialCaseRuleIds((prev) =>
                                        prev.filter((id) => !idSet.has(Number(id)))
                                      );
                                    }
                                  }}
                                />
                              </TableCell>
                              <TableCell sortDirection={specialCaseRulesTableSort?.key === 'status' ? specialCaseRulesTableSort.dir : false}>
                                <TableSortLabel
                                  active={specialCaseRulesTableSort?.key === 'status'}
                                  direction={specialCaseRulesTableSort?.key === 'status' ? specialCaseRulesTableSort.dir : 'asc'}
                                  onClick={() => setSpecialCaseRulesTableSort((prev) => nextSortState(prev, 'status'))}
                                >
                                  Status
                                </TableSortLabel>
                              </TableCell>
                              <TableCell sortDirection={specialCaseRulesTableSort?.key === 'id' ? specialCaseRulesTableSort.dir : false}>
                                <TableSortLabel
                                  active={specialCaseRulesTableSort?.key === 'id'}
                                  direction={specialCaseRulesTableSort?.key === 'id' ? specialCaseRulesTableSort.dir : 'asc'}
                                  onClick={() => setSpecialCaseRulesTableSort((prev) => nextSortState(prev, 'id'))}
                                >
                                  ID
                                </TableSortLabel>
                              </TableCell>
                              <TableCell sortDirection={specialCaseRulesTableSort?.key === 'priority' ? specialCaseRulesTableSort.dir : false}>
                                <TableSortLabel
                                  active={specialCaseRulesTableSort?.key === 'priority'}
                                  direction={specialCaseRulesTableSort?.key === 'priority' ? specialCaseRulesTableSort.dir : 'asc'}
                                  onClick={() => setSpecialCaseRulesTableSort((prev) => nextSortState(prev, 'priority'))}
                                >
                                  Priority
                                </TableSortLabel>
                              </TableCell>
                              <TableCell sortDirection={specialCaseRulesTableSort?.key === 'name' ? specialCaseRulesTableSort.dir : false}>
                                <TableSortLabel
                                  active={specialCaseRulesTableSort?.key === 'name'}
                                  direction={specialCaseRulesTableSort?.key === 'name' ? specialCaseRulesTableSort.dir : 'asc'}
                                  onClick={() => setSpecialCaseRulesTableSort((prev) => nextSortState(prev, 'name'))}
                                >
                                  Rule
                                </TableSortLabel>
                              </TableCell>
                              <TableCell sortDirection={specialCaseRulesTableSort?.key === 'matchers' ? specialCaseRulesTableSort.dir : false}>
                                <TableSortLabel
                                  active={specialCaseRulesTableSort?.key === 'matchers'}
                                  direction={specialCaseRulesTableSort?.key === 'matchers' ? specialCaseRulesTableSort.dir : 'asc'}
                                  onClick={() => setSpecialCaseRulesTableSort((prev) => nextSortState(prev, 'matchers'))}
                                >
                                  Matchers
                                </TableSortLabel>
                              </TableCell>
                              <TableCell sortDirection={specialCaseRulesTableSort?.key === 'setVat' ? specialCaseRulesTableSort.dir : false}>
                                <TableSortLabel
                                  active={specialCaseRulesTableSort?.key === 'setVat'}
                                  direction={specialCaseRulesTableSort?.key === 'setVat' ? specialCaseRulesTableSort.dir : 'asc'}
                                  onClick={() => setSpecialCaseRulesTableSort((prev) => nextSortState(prev, 'setVat'))}
                                >
                                  Set VAT
                                </TableSortLabel>
                              </TableCell>
                              <TableCell sx={RULE_ACCOUNT_COLUMN_SX} sortDirection={specialCaseRulesTableSort?.key === 'account' ? specialCaseRulesTableSort.dir : false}>
                                <TableSortLabel
                                  active={specialCaseRulesTableSort?.key === 'account'}
                                  direction={specialCaseRulesTableSort?.key === 'account' ? specialCaseRulesTableSort.dir : 'asc'}
                                  onClick={() => setSpecialCaseRulesTableSort((prev) => nextSortState(prev, 'account'))}
                                >
                                  Bank /n A/C
                                </TableSortLabel>
                              </TableCell>
                              <TableCell sortDirection={specialCaseRulesTableSort?.key === 'firedCount' ? specialCaseRulesTableSort.dir : false}>
                                <TableSortLabel
                                  active={specialCaseRulesTableSort?.key === 'firedCount'}
                                  direction={specialCaseRulesTableSort?.key === 'firedCount' ? specialCaseRulesTableSort.dir : 'asc'}
                                  onClick={() => setSpecialCaseRulesTableSort((prev) => nextSortState(prev, 'firedCount'))}
                                >
                                  Tx fired
                                </TableSortLabel>
                              </TableCell>
                              <TableCell sortDirection={specialCaseRulesTableSort?.key === 'active' ? specialCaseRulesTableSort.dir : false}>
                                <TableSortLabel
                                  active={specialCaseRulesTableSort?.key === 'active'}
                                  direction={specialCaseRulesTableSort?.key === 'active' ? specialCaseRulesTableSort.dir : 'asc'}
                                  onClick={() => setSpecialCaseRulesTableSort((prev) => nextSortState(prev, 'active'))}
                                >
                                  Rule Active
                                </TableSortLabel>
                              </TableCell>
                              <TableCell align="right">Actions</TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {sortedVisibleSpecialCaseRules.length === 0 ? (
                              <TableRow>
                                <TableCell colSpan={11}>No Special Case rules for the current filters.</TableCell>
                              </TableRow>
                            ) : (
                              sortedVisibleSpecialCaseRules.map((row) => {
                                const matcherNode = specialCaseMatcherDisplayNode(row);
                                return (
                                  <TableRow
                                    key={`special-case-rule-${row.id}`}
                                    sx={ruleTableRowSavingSx(
                                      theme,
                                      ruleTableSaveHighlight?.kind === 'specialCase' &&
                                        ruleTableSaveHighlight.rowKey === `special-case-rule-${row.id}`
                                    )}
                                  >
                                    <TableCell padding="checkbox">
                                      <input
                                        type="checkbox"
                                        disabled={!!proposalBusyKey || loading}
                                        checked={selectedSpecialCaseRuleIds.includes(Number(row.id))}
                                        onChange={() => undefined}
                                        onClick={(e) => {
                                          e.preventDefault();
                                          if (proposalBusyKey || loading) return;
                                          const id = Number(row.id);
                                          const selected = selectedSpecialCaseRuleIds.includes(id);
                                          applyRulesRowCheckboxSelection({
                                            tableKey: 'special-confirmed',
                                            orderedKeys: sortedVisibleSpecialCaseRules.map((r) => Number(r.id)),
                                            clickedKey: id,
                                            checked: !selected,
                                            shiftKey: e.shiftKey,
                                            setSelection: setSelectedSpecialCaseRuleIds
                                          });
                                        }}
                                      />
                                    </TableCell>
                                    <TableCell><Chip size="small" label="Confirmed" color="success" /></TableCell>
                                    <TableCell>{formatPrefixedRuleId('specialCase', row.id)}</TableCell>
                                    <TableCell>{row.priority ?? 100}</TableCell>
                                    <TableCell>{row.name}</TableCell>
                                    <TableCell>{matcherNode}</TableCell>
                                    <TableCell>
                                      <CanonicalVatRateChip value={row.set_vat_rate} maxWidth={120} />
                                    </TableCell>
                                    <TableCell sx={RULE_ACCOUNT_COLUMN_SX}>{ruleAccountScopeTableLabel(row.account_scope)}</TableCell>
                                    <TableCell>{Number(ruleFireCounts.specialCase[row.id] || 0).toLocaleString()}</TableCell>
                                    <TableCell>{row.is_active === false ? 'Inactive' : 'Active'}</TableCell>
                                    <TableCell align="right">
                                      <Stack direction="row" spacing={0.25} justifyContent="flex-end">
                                        <Tooltip
                                          title={
                                            specialCaseRuleCanListMatches(row)
                                              ? 'View matching transactions'
                                              : 'Special case rule needs at least one matcher to list transactions.'
                                          }
                                        >
                                          <span>
                                            <IconButton
                                              size="small"
                                              color="primary"
                                              aria-label="View matching transactions"
                                              onClick={() => void openSpecialCaseRuleMatchingTransactionsModal(row)}
                                              disabled={loading || applyingRules || !specialCaseRuleCanListMatches(row)}
                                            >
                                              <VisibilityIcon fontSize="small" />
                                            </IconButton>
                                          </span>
                                        </Tooltip>
                                        <Tooltip title="Apply this Special Case rule to matching tags">
                                          <span>
                                            <IconButton
                                              size="small"
                                              color="primary"
                                              aria-label="Apply this special case rule to all transactions"
                                              onClick={() => void applySingleSpecialCaseRuleToAllTransactions(row)}
                                              disabled={loading || applyingRules || applySingleRuleBusyKey !== ''}
                                            >
                                              {applySingleRuleBusyKey === `special-${row.id}` ? (
                                                <CircularProgress size={14} color="inherit" />
                                              ) : (
                                                <PlayArrowIcon fontSize="small" />
                                              )}
                                            </IconButton>
                                          </span>
                                        </Tooltip>
                                        <IconButton size="small" color="primary" onClick={() => openSpecialCaseRuleEditor(row)}>
                                          <EditIcon fontSize="small" />
                                        </IconButton>
                                        <Tooltip title="Duplicate rule">
                                          <span>
                                            <IconButton
                                              size="small"
                                              color="primary"
                                              aria-label="Duplicate special case rule"
                                              disabled={loading || applyingRules || !!ruleDuplicateBusyKey || specialCaseRuleSaving}
                                              onClick={() => void duplicateSpecialCaseRule(row)}
                                            >
                                              {ruleDuplicateBusyKey === `dup-sc-${row.id}` ? (
                                                <CircularProgress size={14} color="inherit" />
                                              ) : (
                                                <ContentCopyIcon fontSize="small" />
                                              )}
                                            </IconButton>
                                          </span>
                                        </Tooltip>
                                        <IconButton
                                          size="small"
                                          color="error"
                                          onClick={() => void deactivateSpecialCaseRule(row)}
                                          disabled={row.is_active === false}
                                        >
                                          <DeleteOutlineIcon fontSize="small" />
                                        </IconButton>
                                      </Stack>
                                    </TableCell>
                                  </TableRow>
                                );
                              })
                            )}
                          </TableBody>
                        </Table>
                      </TableContainer>
                      </PipelineNestedSectionAccordion>
                      <PipelineNestedSectionAccordion
                        filtersActive={rulesHasFilters}
                        title="Proposed"
                        count={0}
                        expanded={pipelineNestedSectionOpen.step5.proposed}
                        onExpandedChange={(open) => setPipelineNestedSection('step5', 'proposed', open)}
                      >
                        <Typography variant="body2" color="text.secondary">
                          No proposed rules in this stage.
                        </Typography>
                      </PipelineNestedSectionAccordion>
                      <PipelineNestedSectionAccordion
                        filtersActive={rulesHasFilters}
                        title="Rejected"
                        count={0}
                        expanded={pipelineNestedSectionOpen.step5.rejected}
                        onExpandedChange={(open) => setPipelineNestedSection('step5', 'rejected', open)}
                      >
                        <Typography variant="body2" color="text.secondary">
                          No rejected proposals in this stage.
                        </Typography>
                      </PipelineNestedSectionAccordion>

                    </AccordionDetails>
                  </Accordion>

                  <Accordion
                    expanded={rulesAccordionOpen.appRules}
                    onChange={(_, expanded) => setRulesAccordionOpen((prev) => ({ ...prev, appRules: expanded }))}
                    sx={{ mb: 1.5, display: 'none' }}
                  >
                    <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                      <Typography variant="h6">Classification Rules ({filteredAppRules.length})</Typography>
                    </AccordionSummary>
                    <AccordionDetails>
                    <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1.5 }}>
                      <Typography variant="body2" color="text.secondary">
                        Showing {filteredAppRules.length} of {sortedRules.length} rows due to filters applied
                      </Typography>
                      <Button
                        variant="contained"
                        onClick={() => {
                          setRuleEditError('');
                          setRuleEditTargetType('classification');
                          setRuleEditMatchCriteriaMenuAnchorEl(null);
                          setRuleEditOutputMenuAnchorEl(null);
                          setRuleEditOutputCategoryEnabled(false);
                          setRuleEditOutputSupplierEnabled(false);
                          const defaultCategoryId = defaultCategoryIdUnknownFirst(granularCategoriesSortedByName);
                          const draft = newClassificationRuleDraft(defaultCategoryId);
                          setRuleEditDraft(draft);
                          setRuleEditMatchCriteriaEnabled(initialRuleEditMatchCriteriaEnabled(draft));
                        }}
                        disabled={loading || applyingRules || ruleEditSaving || granularCategoriesSortedByName.length === 0}
                      >
                        Add rule
                      </Button>
                    </Stack>
                      <TableContainer
                        sx={{
                          maxHeight: { xs: 'min(55vh, 520px)', sm: 'min(65vh, 640px)' },
                          overflow: 'auto'
                        }}
                      >
                        <Table size="small" stickyHeader>
                          <TableHead>
                            <TableRow>
                              <TableCell sortDirection={rulesSort?.key === 'priority' ? rulesSort.dir : false}>
                                <TableSortLabel
                                  active={rulesSort?.key === 'priority'}
                                  direction={rulesSort?.key === 'priority' ? rulesSort.dir : 'asc'}
                                  onClick={() =>
                                    setRulesSort((prev) => nextSortState(prev, 'priority'))
                                  }
                                >
                                  Priority
                                </TableSortLabel>
                              </TableCell>
                              <TableCell>ID</TableCell>
                              <TableCell sortDirection={rulesSort?.key === 'name' ? rulesSort.dir : false}>
                                <TableSortLabel
                                  active={rulesSort?.key === 'name'}
                                  direction={rulesSort?.key === 'name' ? rulesSort.dir : 'asc'}
                                  onClick={() =>
                                    setRulesSort((prev) => nextSortState(prev, 'name'))
                                  }
                                >
                                  Rule Name
                                </TableSortLabel>
                              </TableCell>
                              <TableCell sortDirection={rulesSort?.key === 'category' ? rulesSort.dir : false}>
                                <TableSortLabel
                                  active={rulesSort?.key === 'category'}
                                  direction={rulesSort?.key === 'category' ? rulesSort.dir : 'asc'}
                                  onClick={() =>
                                    setRulesSort((prev) => nextSortState(prev, 'category'))
                                  }
                                >
                                  Category
                                </TableSortLabel>
                              </TableCell>
                              <TableCell sortDirection={rulesSort?.key === 'supplier' ? rulesSort.dir : false}>
                                <TableSortLabel
                                  active={rulesSort?.key === 'supplier'}
                                  direction={rulesSort?.key === 'supplier' ? rulesSort.dir : 'asc'}
                                  onClick={() =>
                                    setRulesSort((prev) => nextSortState(prev, 'supplier'))
                                  }
                                >
                                  Canonical Supplier
                                </TableSortLabel>
                              </TableCell>
                              <TableCell sortDirection={rulesSort?.key === 'description' ? rulesSort.dir : false}>
                                <TableSortLabel
                                  active={rulesSort?.key === 'description'}
                                  direction={rulesSort?.key === 'description' ? rulesSort.dir : 'asc'}
                                  onClick={() =>
                                    setRulesSort((prev) => nextSortState(prev, 'description'))
                                  }
                                >
                                  Description Match
                                </TableSortLabel>
                              </TableCell>
                              <TableCell>Text match mode</TableCell>
                              <TableCell sortDirection={rulesSort?.key === 'amountRange' ? rulesSort.dir : false}>
                                <TableSortLabel
                                  active={rulesSort?.key === 'amountRange'}
                                  direction={rulesSort?.key === 'amountRange' ? rulesSort.dir : 'asc'}
                                  onClick={() =>
                                    setRulesSort((prev) => nextSortState(prev, 'amountRange'))
                                  }
                                >
                                  Amount Range
                                </TableSortLabel>
                              </TableCell>
                              <TableCell sortDirection={rulesSort?.key === 'currency' ? rulesSort.dir : false}>
                                <TableSortLabel
                                  active={rulesSort?.key === 'currency'}
                                  direction={rulesSort?.key === 'currency' ? rulesSort.dir : 'asc'}
                                  onClick={() =>
                                    setRulesSort((prev) => nextSortState(prev, 'currency'))
                                  }
                                >
                                  Currency
                                </TableSortLabel>
                              </TableCell>
                              <TableCell sortDirection={rulesSort?.key === 'recurrence' ? rulesSort.dir : false}>
                                <TableSortLabel
                                  active={rulesSort?.key === 'recurrence'}
                                  direction={rulesSort?.key === 'recurrence' ? rulesSort.dir : 'asc'}
                                  onClick={() =>
                                    setRulesSort((prev) => nextSortState(prev, 'recurrence'))
                                  }
                                >
                                  Recurrence
                                </TableSortLabel>
                              </TableCell>
                              <TableCell sortDirection={rulesSort?.key === 'vat' ? rulesSort.dir : false}>
                                <TableSortLabel
                                  active={rulesSort?.key === 'vat'}
                                  direction={rulesSort?.key === 'vat' ? rulesSort.dir : 'asc'}
                                  onClick={() =>
                                    setRulesSort((prev) => nextSortState(prev, 'vat'))
                                  }
                                >
                                  VAT
                                </TableSortLabel>
                              </TableCell>
                              <TableCell sortDirection={rulesSort?.key === 'status' ? rulesSort.dir : false}>
                                <TableSortLabel
                                  active={rulesSort?.key === 'status'}
                                  direction={rulesSort?.key === 'status' ? rulesSort.dir : 'asc'}
                                  onClick={() =>
                                    setRulesSort((prev) => nextSortState(prev, 'status'))
                                  }
                                >
                                  Status
                                </TableSortLabel>
                              </TableCell>
                              <TableCell>Rule Active</TableCell>
                              <TableCell align="right">Actions</TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {filteredAppRules.length === 0 ? (
                              <TableRow>
                                <TableCell colSpan={15}>No app rules for the current filters.</TableCell>
                              </TableRow>
                            ) : (
                              filteredAppRules.map((rule) => (
                                <TableRow key={rule.id}>
                                  <TableCell>{rule.priority}</TableCell>
                                  <TableCell>{formatClassificationRulePrefixedId(rule.id, rule.name)}</TableCell>
                                  <TableCell>{rule.name}</TableCell>
                                  <TableCell>{rule.category?.name || `Category #${rule.category_id}`}</TableCell>
                                  <TableCell>{classificationRuleSupplierDisplay(rule)}</TableCell>
                                  <TableCell>{rule.description_contains || rule.reference_contains || '—'}</TableCell>
                                  <TableCell>{classificationRuleTextOperatorLabel(rule.text_match_operator)}</TableCell>
                                  <TableCell>
                                    {(rule.amount_min != null || rule.amount_max != null)
                                      ? `${rule.amount_min ?? '...'} to ${rule.amount_max ?? '...'}`
                                      : '—'}
                                  </TableCell>
                                  <TableCell>{rule.currency || '—'}</TableCell>
                                  <TableCell>{rule.recurrence_type || '—'}</TableCell>
                                  <TableCell>
                                    <CanonicalVatRateChip value={rule.vat_rate || rule.category?.vat_rate} maxWidth={120} />
                                  </TableCell>
                                  <TableCell>
                                    <Chip size="small" color={rule.is_active ? 'success' : 'default'} label={rule.is_active ? 'Active' : 'Inactive'} />
                                  </TableCell>
                                  <TableCell>
                                    <Stack direction="row" spacing={1} alignItems="center">
                                      <Typography variant="caption" color="text.secondary">
                                        Inactive
                                      </Typography>
                                      <Switch
                                        size="small"
                                        checked={!!rule.is_active}
                                        disabled={
                                          loading ||
                                          applyingRules ||
                                          ruleEditSaving ||
                                          ruleActiveSavingId === rule.id
                                        }
                                        onChange={(_, checked) =>
                                          void toggleClassificationRuleActive(rule, checked)
                                        }
                                      />
                                      <Typography variant="caption" color="text.secondary">
                                        Active
                                      </Typography>
                                    </Stack>
                                  </TableCell>
                                  <TableCell align="right">
                                    <Stack direction="row" spacing={0.25} justifyContent="flex-end" alignItems="center">
                                      <Tooltip
                                        title={
                                          classificationRuleCanListMatches(rule)
                                            ? 'View matching transactions'
                                            : 'Rule needs a text match or amount range to list transactions.'
                                        }
                                      >
                                        <span>
                                          <IconButton
                                            size="small"
                                            color="primary"
                                            aria-label="View matching transactions"
                                            disabled={
                                              loading ||
                                              applyingRules ||
                                              ruleEditSaving ||
                                              !classificationRuleCanListMatches(rule)
                                            }
                                            onClick={() => void openMatchingTransactionsModal(rule)}
                                          >
                                            <VisibilityIcon fontSize="small" />
                                          </IconButton>
                                        </span>
                                      </Tooltip>
                                      <Tooltip title="Edit">
                                        <span>
                                          <IconButton
                                            size="small"
                                            color="primary"
                                            aria-label="Edit"
                                            disabled={loading || applyingRules || ruleEditSaving}
                                            onClick={() => {
                                              openClassificationRuleEditDialog(rule);
                                            }}
                                          >
                                            <EditIcon fontSize="small" />
                                          </IconButton>
                                        </span>
                                      </Tooltip>
                                      <Tooltip title="Duplicate rule">
                                        <span>
                                          <IconButton
                                            size="small"
                                            color="primary"
                                            aria-label="Duplicate classification rule"
                                            disabled={
                                              loading ||
                                              applyingRules ||
                                              ruleEditSaving ||
                                              !!ruleDuplicateBusyKey ||
                                              classificationTablePatchBusyId === rule.id
                                            }
                                            onClick={() => void duplicateClassificationRule(rule)}
                                          >
                                            {ruleDuplicateBusyKey === `dup-cls-${rule.id}` ? (
                                              <CircularProgress size={14} color="inherit" />
                                            ) : (
                                              <ContentCopyIcon fontSize="small" />
                                            )}
                                          </IconButton>
                                        </span>
                                      </Tooltip>
                                      <Tooltip title="Delete rule">
                                        <span>
                                          <IconButton
                                            size="small"
                                            color="error"
                                            aria-label="Delete rule"
                                            disabled={loading || applyingRules || ruleEditSaving}
                                            onClick={() => void deleteClassificationRule(rule.id, rule.name)}
                                          >
                                            <DeleteOutlineIcon fontSize="small" />
                                          </IconButton>
                                        </span>
                                      </Tooltip>
                                    </Stack>
                                  </TableCell>
                                </TableRow>
                              ))
                            )}
                          </TableBody>
                        </Table>
                      </TableContainer>
                    </AccordionDetails>
                  </Accordion>

                  <Accordion
                    expanded={rulesAccordionOpen.supplierRules}
                    onChange={(_, expanded) =>
                      setRulesAccordionOpen((prev) => ({ ...prev, supplierRules: expanded }))
                    }
                    sx={{ display: 'none' }}
                  >
                    <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                      <Typography variant="h6">Supplier Rules ({filteredSupplierRules.length})</Typography>
                    </AccordionSummary>
                    <AccordionDetails>
                      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
                        <Typography variant="body2" color="text.secondary">
                          Showing {filteredSupplierRules.length} of {supplierRuleRows.length} rows due to filters applied
                        </Typography>
                        <Button
                          variant="contained"
                          onClick={openNewSupplierRuleDialog}
                          disabled={loading || applyingRules || supplierRuleSaving}
                        >
                          Add rule
                        </Button>
                      </Stack>
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                        Supplier rules map statement text patterns to canonical supplier names used during auto-tagging and refresh.
                      </Typography>
                      <TableContainer>
                        <Table size="small">
                          <TableHead>
                            <TableRow>
                              <TableCell sortDirection={supplierRulesSort.key === 'canonical' ? supplierRulesSort.dir : false}>
                                <TableSortLabel
                                  active={supplierRulesSort.key === 'canonical'}
                                  direction={supplierRulesSort.key === 'canonical' ? supplierRulesSort.dir : 'asc'}
                                  onClick={() =>
                                    setSupplierRulesSort((prev) =>
                                      prev.key === 'canonical'
                                        ? { key: 'canonical', dir: prev.dir === 'asc' ? 'desc' : 'asc' }
                                        : { key: 'canonical', dir: 'asc' }
                                    )
                                  }
                                >
                                  Canonical Supplier
                                </TableSortLabel>
                              </TableCell>
                              <TableCell sortDirection={supplierRulesSort.key === 'pattern' ? supplierRulesSort.dir : false}>
                                <TableSortLabel
                                  active={supplierRulesSort.key === 'pattern'}
                                  direction={supplierRulesSort.key === 'pattern' ? supplierRulesSort.dir : 'asc'}
                                  onClick={() =>
                                    setSupplierRulesSort((prev) =>
                                      prev.key === 'pattern'
                                        ? { key: 'pattern', dir: prev.dir === 'asc' ? 'desc' : 'asc' }
                                        : { key: 'pattern', dir: 'asc' }
                                    )
                                  }
                                >
                                  Pattern
                                </TableSortLabel>
                              </TableCell>
                              <TableCell>Text match mode</TableCell>
                              <TableCell align="right">Action</TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {sortedSupplierRules.length === 0 ? (
                              <TableRow>
                                <TableCell colSpan={4}>No supplier rules for the current filters.</TableCell>
                              </TableRow>
                            ) : (
                              sortedSupplierRules.map((row) => (
                                <TableRow key={row.id}>
                                  <TableCell sx={SUPPLIER_RULE_CANONICAL_CELL_SX}>
                                    <Tooltip title={canonicalSupplierLabel(row.supplier_name, `Supplier #${row.supplier_id}`)}>
                                      <Typography
                                        variant="body2"
                                        component="span"
                                        sx={SUPPLIER_RULE_CANONICAL_LINK_BUTTON_SX}
                                      >
                                        {canonicalSupplierLabel(row.supplier_name, `Supplier #${row.supplier_id}`)}
                                      </Typography>
                                    </Tooltip>
                                  </TableCell>
                                  <TableCell>{row.pattern_raw}</TableCell>
                                  <TableCell>{classificationRuleTextOperatorLabel(row.text_match_operator || 'exact')}</TableCell>
                                  <TableCell align="right">
                                    <Stack direction="row" spacing={0.25} justifyContent="flex-end" alignItems="center">
                                      <IconButton
                                        size="small"
                                        color="primary"
                                        aria-label="Edit supplier rule"
                                        onClick={() => openSupplierRuleEditor(row)}
                                        disabled={loading || applyingRules || supplierRuleSaving}
                                      >
                                        <EditIcon fontSize="small" />
                                      </IconButton>
                                      <Tooltip title="Duplicate rule">
                                        <span>
                                          <IconButton
                                            size="small"
                                            color="primary"
                                            aria-label="Duplicate supplier rule"
                                            disabled={
                                              loading ||
                                              applyingRules ||
                                              supplierRuleSaving ||
                                              !!ruleDuplicateBusyKey ||
                                              supplierRuleTablePatchBusyId === row.id
                                            }
                                            onClick={() => void duplicateSupplierRule(row)}
                                          >
                                            {ruleDuplicateBusyKey === `dup-sup-${row.id}` ? (
                                              <CircularProgress size={14} color="inherit" />
                                            ) : (
                                              <ContentCopyIcon fontSize="small" />
                                            )}
                                          </IconButton>
                                        </span>
                                      </Tooltip>
                                      <IconButton
                                        size="small"
                                        color="error"
                                        aria-label="Delete supplier rule"
                                        onClick={() => void deleteSupplierRule(row.id)}
                                        disabled={loading || applyingRules || supplierRuleSaving}
                                      >
                                        <DeleteOutlineIcon fontSize="small" />
                                      </IconButton>
                                    </Stack>
                                  </TableCell>
                                </TableRow>
                              ))
                            )}
                          </TableBody>
                        </Table>
                      </TableContainer>
                    </AccordionDetails>
                  </Accordion>
                  <Stack
                    direction="row"
                    spacing={1}
                    flexWrap="wrap"
                    useFlexGap
                    sx={{
                      mt: 2,
                      pt: 2,
                      borderTop: 1,
                      borderColor: 'divider',
                      justifyContent: 'flex-end',
                      alignSelf: 'stretch',
                      order: 6
                    }}
                  >
                    {rulesAccordionMixedState ? (
                      <>
                        <Button
                          size="small"
                          variant="outlined"
                          onClick={() =>
                            setRulesAccordionOpen({
                              proposals: true,
                              pipelineStepRules: true,
      merchantSuppliers: true,
                              supplierProposals: true,
                              appRules: true,
                              supplierRules: true,
                              vatRules: true,
                              specialCaseRules: true
                            })
                          }
                        >
                          Open all sections
                        </Button>
                        <Button
                          size="small"
                          variant="outlined"
                          onClick={() =>
                            setRulesAccordionOpen({
                              proposals: false,
                              pipelineStepRules: false,
      merchantSuppliers: false,
                              supplierProposals: false,
                              appRules: false,
                              supplierRules: false,
                              vatRules: false,
                              specialCaseRules: false
                            })
                          }
                        >
                          Close all sections
                        </Button>
                      </>
                    ) : (
                      <Button
                        size="small"
                        variant="outlined"
                        onClick={() =>
                          setRulesAccordionOpen({
                            proposals: !allRulesSectionsOpen,
                            pipelineStepRules: !allRulesSectionsOpen,
                            merchantSuppliers: !allRulesSectionsOpen,
                            supplierProposals: !allRulesSectionsOpen,
                            appRules: !allRulesSectionsOpen,
                            supplierRules: !allRulesSectionsOpen,
                            vatRules: !allRulesSectionsOpen,
                            specialCaseRules: !allRulesSectionsOpen
                          })
                        }
                      >
                        {allRulesSectionsOpen ? 'Close all sections' : 'Open all sections'}
                      </Button>
                    )}
                  </Stack>
                  </Box>
                </>
              ) : tabIndex === CLASSIFICATION_VISIBLE_TAB_KEYS.indexOf('suppliers') ? (
                <>
                  {suppliersCrudChartOpen ? (
                    <Card variant="outlined" sx={{ mb: 2 }}>
                      <CardContent sx={{ py: 1.5 }}>
                        <Typography variant="subtitle2" sx={{ mb: 1 }}>
                          Supplier transaction associations (top 20)
                        </Typography>
                        {suppliersCrudChartRows.length === 0 ? (
                          <Typography variant="body2" color="text.secondary">
                            No supplier rows to chart for the current filters.
                          </Typography>
                        ) : (
                          <Box sx={{ width: '100%', height: 260 }}>
                            <ResponsiveContainer width="100%" height="100%">
                              <BarChart data={suppliersCrudChartRows}>
                                <CartesianGrid strokeDasharray="3 3" />
                                <XAxis dataKey="shortName" interval={0} angle={-20} textAnchor="end" height={70} />
                                <YAxis allowDecimals={false} />
                                <RechartsTooltip
                                  formatter={(value: any) => [Number(value || 0).toLocaleString(), 'Associated Txs']}
                                  labelFormatter={(label: any, payload: any) =>
                                    payload?.[0]?.payload?.name || String(label || '')
                                  }
                                />
                                <Legend />
                                <Bar dataKey="txCount" name="Associated Txs" fill="#1976d2" />
                              </BarChart>
                            </ResponsiveContainer>
                          </Box>
                        )}
                      </CardContent>
                    </Card>
                  ) : null}
                  <Stack
                    direction={{ xs: 'column', md: 'row' }}
                    spacing={1}
                    sx={{ mb: 1.25 }}
                    alignItems={{ md: 'center' }}
                    flexWrap="wrap"
                    useFlexGap
                  >
                    <ClearableSearchTextField
                      size="small"
                      label="Text search"
                      placeholder="Name, notes, VAT status, currency…"
                      value={suppliersCrudSearch}
                      onChange={setSuppliersCrudSearch}
                      sx={{ minWidth: 240 }}
                    />
                    <FormControl size="small" sx={{ minWidth: 140 }}>
                      <InputLabel id="suppliers-crud-active-filter-label">Active</InputLabel>
                      <Select
                        labelId="suppliers-crud-active-filter-label"
                        label="Active"
                        value={suppliersCrudActiveFilter}
                        onChange={(e) =>
                          setSuppliersCrudActiveFilter(e.target.value as 'all' | 'active' | 'inactive')
                        }
                      >
                        <MenuItem value="all">All</MenuItem>
                        <MenuItem value="active">Active only</MenuItem>
                        <MenuItem value="inactive">Inactive only</MenuItem>
                      </Select>
                    </FormControl>
                    <FormControl size="small" sx={{ minWidth: 160 }}>
                      <InputLabel id="suppliers-crud-vat-filter-label">VAT status</InputLabel>
                      <Select
                        labelId="suppliers-crud-vat-filter-label"
                        label="VAT status"
                        value={suppliersCrudVatFilter}
                        onChange={(e) => setSuppliersCrudVatFilter(e.target.value)}
                      >
                        <MenuItem value="all">All VAT statuses</MenuItem>
                        {suppliersCrudVatOptions.map((opt) => (
                          <MenuItem key={`suppliers-vat-filter-${opt}`} value={opt}>
                            {opt}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                    <FormControl size="small" sx={{ minWidth: 140 }}>
                      <InputLabel id="suppliers-crud-currency-filter-label">Currency</InputLabel>
                      <Select
                        labelId="suppliers-crud-currency-filter-label"
                        label="Currency"
                        value={suppliersCrudCurrencyFilter}
                        onChange={(e) => setSuppliersCrudCurrencyFilter(e.target.value)}
                      >
                        <MenuItem value="all">All currencies</MenuItem>
                        {suppliersCrudCurrencyOptions.map((opt) => (
                          <MenuItem key={`suppliers-currency-filter-${opt}`} value={opt}>
                            {opt}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                    <FormControl size="small" sx={{ minWidth: 160 }}>
                      <InputLabel id="suppliers-crud-source-filter-label">Source</InputLabel>
                      <Select
                        labelId="suppliers-crud-source-filter-label"
                        label="Source"
                        value={suppliersCrudSourceFilter}
                        onChange={(e) =>
                          setSuppliersCrudSourceFilter(e.target.value as EntitySourceFilterValue)
                        }
                      >
                        <MenuItem value="all">
                          All sources ({formatCount(suppliersCrudRowsForSourceCounts.length)})
                        </MenuItem>
                        {ENTITY_SOURCE_FILTER_SELECT_LABELS.map((lab) => (
                          <MenuItem key={`suppliers-source-filter-${lab}`} value={lab}>
                            {`${lab} (${formatCount(suppliersCrudSourceLabelCounts.get(lab) || 0)})`}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                    <Stack direction="row" spacing={1} sx={{ ml: { md: 'auto' } }}>
                      <Button
                        variant="outlined"
                        size="small"
                        onClick={() => void refreshSuppliersCrudList()}
                        disabled={suppliersCrudLoading}
                      >
                        {suppliersCrudLoading ? 'Loading…' : 'Refresh list'}
                      </Button>
                      <Button
                        variant="outlined"
                        size="small"
                        onClick={() => setSuppliersCrudChartOpen((prev) => !prev)}
                      >
                        {suppliersCrudChartOpen ? 'Hide chart' : 'Show chart'}
                      </Button>
                      <Button variant="contained" size="small" onClick={() => openSupplierCrudCreate()}>
                        Add supplier
                      </Button>
                    </Stack>
                  </Stack>
                  <Stack
                    direction="row"
                    spacing={1}
                    sx={{ mb: 1 }}
                    alignItems="center"
                    flexWrap="wrap"
                    useFlexGap
                  >
                    <Button size="small" variant="outlined" onClick={resetSuppliersCrudFilters}>
                      Clear all filters
                    </Button>
                    <FormControlLabel
                      control={
                        <Switch
                          size="small"
                          checked={suppliersCrudHideNonKey}
                          onChange={(e) => setSuppliersCrudHideNonKey(e.target.checked)}
                        />
                      }
                      label="Hide non-key Supplier"
                      sx={{ ml: 0.5 }}
                    />
                  </Stack>
                  {suppliersCrudHasFilters ? (
                    <Stack
                      direction={{ xs: 'column', sm: 'row' }}
                      spacing={1}
                      sx={{ mb: 1.25 }}
                      alignItems={{ sm: 'center' }}
                      flexWrap="wrap"
                      useFlexGap
                    >
                      <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap>
                        {suppliersCrudFilterPills.map((pill) => (
                          <Chip
                            key={`suppliers-filter-pill-${pill.key}`}
                            size="small"
                            color="primary"
                            variant="outlined"
                            label={pill.label}
                            onDelete={pill.onDelete}
                          />
                        ))}
                      </Stack>
                    </Stack>
                  ) : null}
                  {suppliersCrudHasFilters ? (
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1.5 }}>
                      Showing {sortedSuppliersCrudRows.length} of {suppliersCrudRows.length} supplier(s) matching
                      filters.
                    </Typography>
                  ) : null}
                  {suppliersCrudError ? (
                    <Alert severity="error" sx={{ mb: 2 }} onClose={() => setSuppliersCrudError('')}>
                      {suppliersCrudError}
                    </Alert>
                  ) : null}
                  {selectedSupplierCrudIds.length > 0 ? (
                    <Stack
                      direction={{ xs: 'column', sm: 'row' }}
                      spacing={1}
                      alignItems={{ sm: 'center' }}
                      flexWrap="wrap"
                      useFlexGap
                      sx={{ mb: 1.5 }}
                    >
                      <Typography variant="body2" color="text.secondary">
                        {selectedSupplierCrudIds.length} supplier(s) selected
                      </Typography>
                      <Button
                        size="small"
                        variant="outlined"
                        onClick={() => setSelectedSupplierCrudIds([])}
                        disabled={suppliersBulkDeleteBusy || suppliersMergeBusy || suppliersBulkBusy}
                      >
                        Clear selection
                      </Button>
                      <Button
                        size="small"
                        variant="contained"
                        onClick={openSuppliersBulkEditDialog}
                        disabled={suppliersBulkDeleteBusy || suppliersMergeBusy || suppliersBulkBusy}
                      >
                        Bulk edit…
                      </Button>
                      <Tooltip
                        title={
                          canMergeSuppliersCatalog
                            ? 'Permanently delete the selected suppliers from the catalog.'
                            : 'Only owner or admin can delete suppliers.'
                        }
                      >
                        <span>
                          <Button
                            size="small"
                            variant="outlined"
                            color="error"
                            disabled={
                              !canMergeSuppliersCatalog ||
                              selectedSupplierCrudIds.length === 0 ||
                              suppliersBulkDeleteBusy ||
                              suppliersMergeBusy ||
                              suppliersBulkBusy
                            }
                            onClick={() => void bulkDeleteSelectedSuppliers()}
                          >
                            {suppliersBulkDeleteBusy
                              ? 'Deleting…'
                              : `Delete selected (${selectedSupplierCrudIds.length})`}
                          </Button>
                        </span>
                      </Tooltip>
                      <Tooltip
                        title={
                          canMergeSuppliersCatalog
                            ? 'Fold the other selected rows into one supplier (re-tags links, optional rule).'
                            : 'Only owner or admin can merge suppliers.'
                        }
                      >
                        <span>
                          <Button
                            size="small"
                            variant="contained"
                            color="secondary"
                            disabled={
                              !canMergeSuppliersCatalog ||
                              selectedSupplierCrudIds.length < 2 ||
                              suppliersMergeBusy ||
                              suppliersBulkDeleteBusy ||
                              suppliersBulkBusy
                            }
                            onClick={() => openSuppliersMergeDialog()}
                          >
                            Merge into canonical…
                          </Button>
                        </span>
                      </Tooltip>
                    </Stack>
                  ) : null}
                  {suppliersCrudLoading && suppliersCrudRows.length === 0 ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                      <CircularProgress size={28} />
                    </Box>
                  ) : (
                    <TableContainer
                      sx={{
                        maxHeight: { xs: 'min(55vh, 520px)', sm: 'min(65vh, 640px)' },
                        overflow: 'auto'
                      }}
                    >
                      <Table size="small" stickyHeader>
                        <TableHead>
                          <TableRow>
                            <TableCell padding="checkbox" sx={{ width: 48 }}>
                              <Checkbox
                                size="small"
                                disabled={sortedSuppliersCrudRows.length === 0 || suppliersCrudLoading}
                                checked={supplierCrudSelectAllCheckboxState.checked}
                                indeterminate={supplierCrudSelectAllCheckboxState.indeterminate}
                                onChange={(e) => toggleSelectAllVisibleSuppliers(e.target.checked)}
                                inputProps={{ 'aria-label': 'Select all suppliers in this view' }}
                              />
                            </TableCell>
                            <TableCell sortDirection={suppliersCrudSort.key === 'name' ? suppliersCrudSort.dir : false}>
                              <TableSortLabel
                                active={suppliersCrudSort.key === 'name'}
                                direction={suppliersCrudSort.key === 'name' ? suppliersCrudSort.dir : 'asc'}
                                onClick={() => setSuppliersCrudSort((prev) => nextSortState(prev, 'name'))}
                              >
                                Name
                              </TableSortLabel>
                            </TableCell>
                            <TableCell>Statement mappings</TableCell>
                            <TableCell>
                              <Tooltip title="Pending supplier-alias drafts from Missing supplier sweeps. Confirm or edit under Rules → Supplier proposals.">
                                <span>Proposed aliases</span>
                              </Tooltip>
                            </TableCell>
                            <TableCell sortDirection={suppliersCrudSort.key === 'is_key_supplier' ? suppliersCrudSort.dir : false}>
                              <TableSortLabel
                                active={suppliersCrudSort.key === 'is_key_supplier'}
                                direction={suppliersCrudSort.key === 'is_key_supplier' ? suppliersCrudSort.dir : 'asc'}
                                onClick={() => setSuppliersCrudSort((prev) => nextSortState(prev, 'is_key_supplier'))}
                              >
                                Favourite
                              </TableSortLabel>
                            </TableCell>
                            <TableCell sortDirection={suppliersCrudSort.key === 'tx_associated_count' ? suppliersCrudSort.dir : false}>
                              <TableSortLabel
                                active={suppliersCrudSort.key === 'tx_associated_count'}
                                direction={suppliersCrudSort.key === 'tx_associated_count' ? suppliersCrudSort.dir : 'asc'}
                                onClick={() => setSuppliersCrudSort((prev) => nextSortState(prev, 'tx_associated_count'))}
                              >
                                Associated Txs
                              </TableSortLabel>
                            </TableCell>
                            <TableCell sortDirection={suppliersCrudSort.key === 'active' ? suppliersCrudSort.dir : false}>
                              <TableSortLabel
                                active={suppliersCrudSort.key === 'active'}
                                direction={suppliersCrudSort.key === 'active' ? suppliersCrudSort.dir : 'asc'}
                                onClick={() => setSuppliersCrudSort((prev) => nextSortState(prev, 'active'))}
                              >
                                Active
                              </TableSortLabel>
                            </TableCell>
                            <TableCell
                              sortDirection={suppliersCrudSort.key === 'source' ? suppliersCrudSort.dir : false}
                              sx={{ width: 128, whiteSpace: 'nowrap' }}
                            >
                              <TableSortLabel
                                active={suppliersCrudSort.key === 'source'}
                                direction={suppliersCrudSort.key === 'source' ? suppliersCrudSort.dir : 'asc'}
                                onClick={() => setSuppliersCrudSort((prev) => nextSortState(prev, 'source'))}
                              >
                                Source
                              </TableSortLabel>
                            </TableCell>
                            <TableCell sortDirection={suppliersCrudSort.key === 'invoice_frequency' ? suppliersCrudSort.dir : false}>
                              <TableSortLabel
                                active={suppliersCrudSort.key === 'invoice_frequency'}
                                direction={suppliersCrudSort.key === 'invoice_frequency' ? suppliersCrudSort.dir : 'asc'}
                                onClick={() => setSuppliersCrudSort((prev) => nextSortState(prev, 'invoice_frequency'))}
                              >
                                Invoice frequency
                              </TableSortLabel>
                            </TableCell>
                            <TableCell sortDirection={suppliersCrudSort.key === 'invoice_currency' ? suppliersCrudSort.dir : false}>
                              <TableSortLabel
                                active={suppliersCrudSort.key === 'invoice_currency'}
                                direction={suppliersCrudSort.key === 'invoice_currency' ? suppliersCrudSort.dir : 'asc'}
                                onClick={() => setSuppliersCrudSort((prev) => nextSortState(prev, 'invoice_currency'))}
                              >
                                Currency
                              </TableSortLabel>
                            </TableCell>
                            <TableCell sortDirection={suppliersCrudSort.key === 'invoice_url' ? suppliersCrudSort.dir : false}>
                              <TableSortLabel
                                active={suppliersCrudSort.key === 'invoice_url'}
                                direction={suppliersCrudSort.key === 'invoice_url' ? suppliersCrudSort.dir : 'asc'}
                                onClick={() => setSuppliersCrudSort((prev) => nextSortState(prev, 'invoice_url'))}
                              >
                                Invoice URL
                              </TableSortLabel>
                            </TableCell>
                            <TableCell sortDirection={suppliersCrudSort.key === 'vat_status' ? suppliersCrudSort.dir : false}>
                              <TableSortLabel
                                active={suppliersCrudSort.key === 'vat_status'}
                                direction={suppliersCrudSort.key === 'vat_status' ? suppliersCrudSort.dir : 'asc'}
                                onClick={() => setSuppliersCrudSort((prev) => nextSortState(prev, 'vat_status'))}
                              >
                                VAT status
                              </TableSortLabel>
                            </TableCell>
                            <TableCell sortDirection={suppliersCrudSort.key === 'notes' ? suppliersCrudSort.dir : false}>
                              <TableSortLabel
                                active={suppliersCrudSort.key === 'notes'}
                                direction={suppliersCrudSort.key === 'notes' ? suppliersCrudSort.dir : 'asc'}
                                onClick={() => setSuppliersCrudSort((prev) => nextSortState(prev, 'notes'))}
                              >
                                Notes
                              </TableSortLabel>
                            </TableCell>
                            <TableCell align="right">Actions</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {sortedSuppliersCrudRows.length === 0 ? (
                            <TableRow>
                              <TableCell colSpan={13}>
                                No suppliers yet. Click Add supplier or use Refresh after data loads.
                              </TableCell>
                            </TableRow>
                          ) : (
                            sortedSuppliersCrudRows.map((row) => (
                              <TableRow key={row.id}>
                                <TableCell padding="checkbox">
                                  <Checkbox
                                    size="small"
                                    checked={selectedSupplierCrudIds.includes(row.id)}
                                    onChange={(e) => toggleSupplierCrudRowSelected(row.id, e.target.checked)}
                                    disabled={suppliersCrudLoading}
                                    inputProps={{ 'aria-label': `Select supplier ${row.name}` }}
                                  />
                                </TableCell>
                                <TableCell>{row.name}</TableCell>
                                <TableCell sx={{ verticalAlign: 'top' }}>
                                  <Stack spacing={0.5}>
                                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, maxWidth: 420 }}>
                                      {(supplierAliasesBySupplierId[row.id] || []).map((a) => (
                                        <Tooltip key={a.id} title={formatSupplierAliasLabel(a as SupplierAliasApi)}>
                                          <Chip
                                            size="small"
                                            variant="outlined"
                                            label={truncateMiddle(formatSupplierAliasLabel(a as SupplierAliasApi), 32)}
                                          />
                                        </Tooltip>
                                      ))}
                                    </Box>
                                    <Button
                                      size="small"
                                      variant="text"
                                      onClick={() => setSupplierAliasMappingsTarget({ id: row.id, name: row.name })}
                                    >
                                      Manage mappings
                                    </Button>
                                  </Stack>
                                </TableCell>
                                <TableCell sx={{ verticalAlign: 'top', maxWidth: 300 }}>
                                  {!(row.pending_alias_proposals && row.pending_alias_proposals.length) ? (
                                    <Typography variant="caption" color="text.secondary">
                                      —
                                    </Typography>
                                  ) : (
                                    <Stack spacing={0.75}>
                                      {row.pending_alias_proposals.map((pap) => (
                                        <Box key={pap.proposal_id}>
                                          <Typography
                                            variant="caption"
                                            color="text.secondary"
                                            sx={{ display: 'block', mb: 0.25 }}
                                          >
                                            Proposal #{pap.proposal_id}
                                            {pap.variant_count > 1 ? ` · ${pap.variant_count} matchers` : ''}
                                          </Typography>
                                          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                                            {pap.previews.slice(0, 8).map((preview, idx) => (
                                              <Tooltip key={`${pap.proposal_id}-p-${idx}`} title={preview}>
                                                <Chip
                                                  size="small"
                                                  variant="outlined"
                                                  color="warning"
                                                  label={truncateMiddle(preview, 30)}
                                                />
                                              </Tooltip>
                                            ))}
                                          </Box>
                                          {pap.previews.length > 8 && (
                                            <Typography variant="caption" color="text.secondary">
                                              +{pap.previews.length - 8} more
                                            </Typography>
                                          )}
                                        </Box>
                                      ))}
                                      <MuiLink
                                        component="button"
                                        type="button"
                                        variant="caption"
                                        onClick={(e) => {
                                          e.preventDefault();
                                          const next = new URLSearchParams(searchParams);
                                          next.set('tab', 'rules');
                                          setSearchParams(next, { replace: true });
                                          setRulesAccordionOpen((prev) => ({ ...prev, supplierProposals: true }));
                                        }}
                                        sx={{ cursor: 'pointer', textAlign: 'left', alignSelf: 'flex-start' }}
                                      >
                                        Open Rules → Supplier proposals
                                      </MuiLink>
                                    </Stack>
                                  )}
                                </TableCell>
                                <TableCell>
                                  <Tooltip
                                    title={
                                      row.is_key_supplier ? 'Remove from favourites' : 'Add to favourites'
                                    }
                                  >
                                    <span>
                                      <IconButton
                                        size="small"
                                        onClick={() => void toggleSupplierKeySupplier(row)}
                                        disabled={supplierKeyToggleBusyId === row.id}
                                        aria-label={
                                          row.is_key_supplier ? 'Remove from favourites' : 'Add to favourites'
                                        }
                                      >
                                        {row.is_key_supplier ? (
                                          <StarIcon fontSize="small" color="warning" />
                                        ) : (
                                          <StarBorderIcon fontSize="small" color="disabled" />
                                        )}
                                      </IconButton>
                                    </span>
                                  </Tooltip>
                                </TableCell>
                                <TableCell>
                                  <Typography variant="body2">
                                    {Number(row.tx_associated_count || 0).toLocaleString()}
                                  </Typography>
                                </TableCell>
                                <TableCell>
                                  <Chip
                                    size="small"
                                    label={row.is_active ? 'Yes' : 'No'}
                                    color={row.is_active ? 'success' : 'default'}
                                    variant="outlined"
                                  />
                                </TableCell>
                                <TableCell sx={{ maxWidth: 160 }}>
                                  {(() => {
                                    const supplierSourceMeta = supplierSourceTagFromNotes(row.notes);
                                    const supplierSourceTooltip = row.notes.trim()
                                      ? row.notes
                                      : 'No notes — shown as Manual source.';
                                    return (
                                      <Tooltip title={supplierSourceTooltip}>
                                        <Chip
                                          size="small"
                                          variant="outlined"
                                          color={supplierSourceMeta.chipColor}
                                          label={supplierSourceMeta.label}
                                          sx={{ fontWeight: 600 }}
                                        />
                                      </Tooltip>
                                    );
                                  })()}
                                </TableCell>
                                <TableCell>{row.invoice_frequency || '—'}</TableCell>
                                <TableCell>{row.invoice_currency || '—'}</TableCell>
                                <TableCell sx={{ maxWidth: 220 }}>
                                  {row.invoice_url ? (
                                    <MuiLink
                                      href={row.invoice_url}
                                      target="_blank"
                                      rel="noopener noreferrer"
                                      variant="body2"
                                      underline="hover"
                                      title={row.invoice_url}
                                      sx={{
                                        display: 'inline-block',
                                        maxWidth: '100%',
                                        overflow: 'hidden',
                                        textOverflow: 'ellipsis',
                                        whiteSpace: 'nowrap',
                                        verticalAlign: 'bottom'
                                      }}
                                    >
                                      Open invoices
                                    </MuiLink>
                                  ) : (
                                    '—'
                                  )}
                                </TableCell>
                                <TableCell>{row.vat_status || '—'}</TableCell>
                                <TableCell sx={{ maxWidth: 280 }}>
                                  <Typography variant="body2" noWrap title={row.notes || ''}>
                                    {row.notes || '—'}
                                  </Typography>
                                </TableCell>
                                <TableCell align="right">
                                  <Stack direction="row" spacing={0.25} justifyContent="flex-end" alignItems="center">
                                    <Tooltip title="Edit supplier (including Active state)">
                                      <span>
                                        <IconButton
                                          size="small"
                                          color="primary"
                                          aria-label={`Edit supplier ${row.name}`}
                                          onClick={() => openSupplierCrudEdit(row)}
                                          disabled={suppliersCrudLoading}
                                        >
                                          <EditIcon fontSize="small" />
                                        </IconButton>
                                      </span>
                                    </Tooltip>
                                    {row.is_active ? (
                                      <Tooltip title="Delete supplier">
                                        <span>
                                          <IconButton
                                            size="small"
                                            color="error"
                                            aria-label={`Delete supplier ${row.name}`}
                                            onClick={() => void deleteSupplierCrud(row)}
                                            disabled={suppliersCrudLoading}
                                          >
                                            <DeleteOutlineIcon fontSize="small" />
                                          </IconButton>
                                        </span>
                                      </Tooltip>
                                    ) : (
                                      <Tooltip title="Restore supplier">
                                        <span>
                                          <IconButton
                                            size="small"
                                            color="primary"
                                            aria-label={`Restore supplier ${row.name}`}
                                            onClick={() => void reactivateSupplierCrud(row)}
                                            disabled={suppliersCrudLoading}
                                          >
                                            <RestoreIcon fontSize="small" />
                                          </IconButton>
                                        </span>
                                      </Tooltip>
                                    )}
                                  </Stack>
                                </TableCell>
                              </TableRow>
                            ))
                          )}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  )}
                </>
              ) : tabIndex === CLASSIFICATION_VISIBLE_TAB_KEYS.indexOf('supplier-audit') ? (
                <>
                  <Stack spacing={2}>
                    <Alert severity="info">
                      Run a fresh corpus audit over all transactions for all users, then review proposed mappings onto
                      suppliers that already exist in your Canonical Supplier list. Unused catalog names that never
                      appeared in those proposals can be deleted below. Requires manage-rules capability.
                    </Alert>
                    <Stack direction={{ xs: 'column', md: 'row' }} spacing={1} flexWrap="wrap" useFlexGap alignItems={{ md: 'center' }}>
                      <Button
                        variant="contained"
                        size="small"
                        disabled={supplierCorpusAuditLoading}
                        onClick={() => void runSupplierCorpusAudit()}
                      >
                        {supplierCorpusAuditLoading ? 'Running audit…' : 'Run corpus audit'}
                      </Button>
                    </Stack>
                    {supplierCorpusAuditError ? (
                      <Alert severity="error" onClose={() => setSupplierCorpusAuditError('')}>
                        {supplierCorpusAuditError}
                      </Alert>
                    ) : null}
                    {supplierCorpusAuditLoading ? (
                      <Stack direction="row" spacing={1} alignItems="center" sx={{ py: 1 }}>
                        <CircularProgress size={18} />
                        <Typography variant="body2" color="text.secondary">
                          Running corpus audit
                          {supplierCorpusAuditElapsedSec > 0
                            ? `… ${supplierCorpusAuditElapsedSec}s`
                            : '…'}
                        </Typography>
                      </Stack>
                    ) : null}
                    {supplierCorpusAuditResult ? (
                      <RenderErrorBoundary
                        resetKey={`corpus-audit:${supplierCorpusAuditResult.saved_run_id || ''}:${supplierCorpusAuditResult.total_transactions_scanned}:${supplierAuditProposalGroups.length}`}
                        fallback={
                          <Alert severity="error">
                            Supplier Audit results failed to render (likely a large or unexpected payload). Reload the
                            page and run the corpus audit again.
                          </Alert>
                        }
                      >
                      <>
                        <Accordion defaultExpanded disableGutters>
                          <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={{ alignItems: 'flex-start' }}>
                            <Box>
                              <Typography component="span" variant="subtitle1">
                                A) Proposed Supplier mappings
                              </Typography>
                              <Typography variant="body2" color="text.secondary" sx={{ mt: 0.25 }}>
                                I have done an audit on{' '}
                                {Number(supplierCorpusAuditResult.total_transactions_scanned || 0).toLocaleString()}{' '}
                                TXs and found {supplierAuditProposedTxCount.toLocaleString()} that look like they map
                                to Suppliers in your Canonical Supplier list.
                              </Typography>
                            </Box>
                          </AccordionSummary>
                          <AccordionDetails>
                            {supplierAuditProposalGroups.length === 0 ? (
                              <Typography variant="body2" color="text.secondary">
                                No proposed mappings to existing canonical suppliers. Your catalog names may not overlap
                                mined TX wording — add canonical suppliers first, then re-run.
                              </Typography>
                            ) : (
                              <Stack spacing={1.25}>
                                {supplierAuditProposalGroups.slice(0, SUPPLIER_CORPUS_AUDIT_UI_ROW_CAP).map((group) => {
                                  const groupKey = `supplier-${group.supplierId}`;
                                  const expanded = !!supplierCorpusAuditExpandedBuckets[groupKey];
                                  const busy = !!supplierCorpusAuditSampleTxBusyKeys[groupKey];
                                  const err = supplierCorpusAuditSampleTxErrors[groupKey] || '';
                                  // Link counts for the summary (always — open and collapsed use the same copy).
                                  // Table row work stays gated on `expanded` below so collapsed groups stay light.
                                  let linkedCount = 0;
                                  const selectedInGroup: number[] = [];
                                  for (const id of group.txIds) {
                                    if (
                                      supplierAuditTxLooksLinked(
                                        supplierCorpusAuditSampleTxById[id],
                                        group.supplierId,
                                        group.supplierName
                                      )
                                    ) {
                                      linkedCount += 1;
                                    }
                                    if (expanded && supplierAuditSelectedTxIdSet.has(id)) {
                                      selectedInGroup.push(id);
                                    }
                                  }
                                  const notLinkedCount = Math.max(0, group.txIds.length - linkedCount);
                                  // Split then cap each table so a huge mapping cannot DOM-bomb the tab.
                                  const linkedTxIds: number[] = [];
                                  const notLinkedTxIds: number[] = [];
                                  if (expanded) {
                                    for (const id of group.txIds) {
                                      const row = supplierCorpusAuditSampleTxById[id];
                                      if (
                                        row &&
                                        supplierAuditTxLooksLinked(row, group.supplierId, group.supplierName)
                                      ) {
                                        linkedTxIds.push(id);
                                      } else {
                                        notLinkedTxIds.push(id);
                                      }
                                    }
                                  }
                                  const visibleLinkedTxIds = linkedTxIds.slice(
                                    0,
                                    SUPPLIER_CORPUS_AUDIT_UI_ROW_CAP
                                  );
                                  const visibleNotLinkedTxIds = notLinkedTxIds.slice(
                                    0,
                                    SUPPLIER_CORPUS_AUDIT_UI_ROW_CAP
                                  );
                                  const hiddenLinkedCount = Math.max(
                                    0,
                                    linkedTxIds.length - visibleLinkedTxIds.length
                                  );
                                  const hiddenNotLinkedCount = Math.max(
                                    0,
                                    notLinkedTxIds.length - visibleNotLinkedTxIds.length
                                  );
                                  const selectedNotLinked = selectedInGroup.filter((id) =>
                                    notLinkedTxIds.includes(id)
                                  );
                                  const allNotLinkedSelected =
                                    notLinkedTxIds.length > 0 &&
                                    selectedNotLinked.length === notLinkedTxIds.length;
                                  const someNotLinkedSelected =
                                    selectedNotLinked.length > 0 &&
                                    selectedNotLinked.length < notLinkedTxIds.length;
                                  const selectedLinked = selectedInGroup.filter((id) =>
                                    linkedTxIds.includes(id)
                                  );
                                  const allLinkedSelected =
                                    linkedTxIds.length > 0 && selectedLinked.length === linkedTxIds.length;
                                  const someLinkedSelected =
                                    selectedLinked.length > 0 && selectedLinked.length < linkedTxIds.length;
                                  return (
                                    <Accordion
                                      key={groupKey}
                                      disableGutters
                                      expanded={expanded}
                                      TransitionProps={{ unmountOnExit: true }}
                                      onChange={(_e, isExp) => {
                                        if (!isExp) {
                                          setSupplierCorpusAuditExpandedBuckets((prev) => ({
                                            ...prev,
                                            [groupKey]: false
                                          }));
                                          return;
                                        }
                                        if (!supplierCorpusAuditExpandedBuckets[groupKey]) {
                                          void toggleSupplierCorpusAuditBucket(groupKey, group.txIds);
                                        } else {
                                          setSupplierCorpusAuditExpandedBuckets((prev) => ({
                                            ...prev,
                                            [groupKey]: true
                                          }));
                                        }
                                      }}
                                    >
                                      <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={{ alignItems: 'flex-start' }}>
                                        <Box sx={{ pr: 1 }}>
                                          <Typography variant="subtitle2">{group.supplierName}</Typography>
                                          <Typography variant="body2" color="text.secondary">
                                            {group.txIds.length.toLocaleString()} TXs look like they map to this
                                            supplier — {linkedCount.toLocaleString()} of them already are linked, but{' '}
                                            {notLinkedCount.toLocaleString()} are not. Review the list below and select
                                            a canonical supplier for each.
                                          </Typography>
                                        </Box>
                                      </AccordionSummary>
                                      <AccordionDetails>
                                        {!expanded ? null : (
                                        <>
                                        {busy ? (
                                          <Stack direction="row" spacing={1} alignItems="center" sx={{ py: 1 }}>
                                            <CircularProgress size={18} />
                                            <Typography variant="body2" color="text.secondary">
                                              Loading matching transactions…
                                            </Typography>
                                          </Stack>
                                        ) : null}
                                        {err ? (
                                          <Alert severity="warning" sx={{ mb: 1 }}>
                                            {err}
                                          </Alert>
                                        ) : null}
                                        {hiddenNotLinkedCount > 0 || hiddenLinkedCount > 0 ? (
                                          <Alert severity="info" sx={{ mb: 1 }}>
                                            UI cap: showing up to {SUPPLIER_CORPUS_AUDIT_UI_ROW_CAP.toLocaleString()}{' '}
                                            rows per table
                                            {hiddenNotLinkedCount > 0
                                              ? ` (not linked: +${hiddenNotLinkedCount.toLocaleString()} more)`
                                              : ''}
                                            {hiddenLinkedCount > 0
                                              ? ` (already linked: +${hiddenLinkedCount.toLocaleString()} more)`
                                              : ''}
                                            .
                                          </Alert>
                                        ) : null}
                                        {selectedNotLinked.length > 0 ? (
                                          <Stack
                                            direction={{ xs: 'column', sm: 'row' }}
                                            spacing={1}
                                            alignItems={{ sm: 'center' }}
                                            sx={{ mb: 1 }}
                                            flexWrap="wrap"
                                            useFlexGap
                                          >
                                            <Typography variant="body2" color="text.secondary">
                                              {selectedNotLinked.length} not-linked selected
                                            </Typography>
                                            <Button
                                              size="small"
                                              variant="outlined"
                                              disabled={supplierCorpusAuditActionBusy}
                                              onClick={() =>
                                                setSupplierAuditSelectedTxIds((prev) =>
                                                  prev.filter((id) => !notLinkedTxIds.includes(id))
                                                )
                                              }
                                            >
                                              Clear
                                            </Button>
                                            <Button
                                              size="small"
                                              variant="contained"
                                              disabled={supplierCorpusAuditActionBusy}
                                              onClick={() =>
                                                void assignSupplierAuditSelectedTxs(
                                                  selectedNotLinked,
                                                  group.supplierId,
                                                  group.supplierName
                                                )
                                              }
                                            >
                                              {supplierCorpusAuditActionBusy
                                                ? 'Assigning…'
                                                : `Assign proposed “${group.supplierName}” (${selectedNotLinked.length})`}
                                            </Button>
                                          </Stack>
                                        ) : null}
                                        {(
                                          [
                                            {
                                              key: 'not-linked',
                                              title: `Not already linked (${notLinkedTxIds.length.toLocaleString()})`,
                                              empty: 'No unlinked TXs in this proposal group.',
                                              txIds: visibleNotLinkedTxIds,
                                              allSelected: allNotLinkedSelected,
                                              someSelected: someNotLinkedSelected,
                                              selectPool: notLinkedTxIds,
                                              showAssign: true,
                                              linkedChip: false as boolean
                                            },
                                            {
                                              key: 'linked',
                                              title: `Already linked (${linkedTxIds.length.toLocaleString()})`,
                                              empty: 'No TXs in this group are already linked to this supplier.',
                                              txIds: visibleLinkedTxIds,
                                              allSelected: allLinkedSelected,
                                              someSelected: someLinkedSelected,
                                              selectPool: linkedTxIds,
                                              showAssign: false,
                                              linkedChip: true as boolean
                                            }
                                          ] as const
                                        ).map((section) => (
                                          <Box key={`${groupKey}-${section.key}`} sx={{ mb: 2 }}>
                                            <Typography variant="subtitle2" sx={{ mb: 0.75 }}>
                                              {section.title}
                                            </Typography>
                                            {section.txIds.length === 0 && !busy ? (
                                              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                                                {section.empty}
                                              </Typography>
                                            ) : (
                                              <TableContainer sx={{ maxHeight: 360 }}>
                                                <Table size="small" stickyHeader>
                                                  <TableHead>
                                                    <TableRow>
                                                      <TableCell padding="checkbox">
                                                        <Checkbox
                                                          size="small"
                                                          checked={section.allSelected}
                                                          indeterminate={section.someSelected}
                                                          disabled={busy || !section.selectPool.length}
                                                          onChange={(e) => {
                                                            const checked = e.target.checked;
                                                            setSupplierAuditSelectedTxIds((prev) => {
                                                              if (checked) {
                                                                return Array.from(
                                                                  new Set([...prev, ...section.selectPool])
                                                                );
                                                              }
                                                              return prev.filter(
                                                                (id) => !section.selectPool.includes(id)
                                                              );
                                                            });
                                                          }}
                                                          inputProps={{
                                                            'aria-label': `Select all ${section.key} TXs for ${group.supplierName}`
                                                          }}
                                                        />
                                                      </TableCell>
                                                      <TableCell align="right">ID</TableCell>
                                                      <TableCell>Date</TableCell>
                                                      <TableCell>Merchant</TableCell>
                                                      <TableCell>Description</TableCell>
                                                      <TableCell>Status</TableCell>
                                                      {section.showAssign ? (
                                                        <TableCell>Assign to</TableCell>
                                                      ) : null}
                                                      <TableCell align="right">Actions</TableCell>
                                                    </TableRow>
                                                  </TableHead>
                                                  <TableBody>
                                                    {section.txIds.map((txId) => {
                                                      const tx = supplierCorpusAuditSampleTxById[txId];
                                                      const selected = supplierAuditSelectedTxIdSet.has(txId);
                                                      const assignId =
                                                        supplierAuditAssignSupplierByTxId[txId] ??
                                                        group.supplierId;
                                                      const nested = (tx?.transaction || {}) as ReviewTx;
                                                      const merchant = effectiveMerchantName(nested) || '—';
                                                      const selectOptions =
                                                        supplierAuditCanonicalOptions.some(
                                                          (o) => o.id === assignId
                                                        )
                                                          ? supplierAuditCanonicalOptions
                                                          : [
                                                              {
                                                                id: assignId,
                                                                name: group.supplierName || `#${assignId}`
                                                              },
                                                              ...supplierAuditCanonicalOptions
                                                            ];
                                                      return (
                                                        <TableRow
                                                          key={`prop-tx-${section.key}-${group.supplierId}-${txId}`}
                                                          hover
                                                          selected={selected}
                                                        >
                                                          <TableCell padding="checkbox">
                                                            <Checkbox
                                                              size="small"
                                                              checked={selected}
                                                              disabled={supplierCorpusAuditActionBusy}
                                                              onChange={(e) => {
                                                                const checked = e.target.checked;
                                                                setSupplierAuditSelectedTxIds((prev) =>
                                                                  checked
                                                                    ? Array.from(new Set([...prev, txId]))
                                                                    : prev.filter((id) => id !== txId)
                                                                );
                                                              }}
                                                              inputProps={{
                                                                'aria-label': `Select TX ${txId}`
                                                              }}
                                                            />
                                                          </TableCell>
                                                          <TableCell align="right">{txId}</TableCell>
                                                          <TableCell sx={{ whiteSpace: 'nowrap' }}>
                                                            {tx?.date || '—'}
                                                          </TableCell>
                                                          <TableCell sx={{ maxWidth: 180 }} title={merchant}>
                                                            <Typography variant="body2" noWrap>
                                                              {merchant}
                                                            </Typography>
                                                          </TableCell>
                                                          <TableCell
                                                            sx={{ maxWidth: 220 }}
                                                            title={tx?.description || ''}
                                                          >
                                                            <Typography variant="body2" noWrap>
                                                              {tx?.description || (busy ? 'Loading…' : '—')}
                                                            </Typography>
                                                          </TableCell>
                                                          <TableCell>
                                                            {!tx ? (
                                                              <Typography
                                                                variant="caption"
                                                                color="text.secondary"
                                                              >
                                                                {busy ? 'Loading…' : '—'}
                                                              </Typography>
                                                            ) : section.linkedChip ? (
                                                              <Chip
                                                                size="small"
                                                                color="success"
                                                                label="Linked"
                                                              />
                                                            ) : (
                                                              <Chip
                                                                size="small"
                                                                color="warning"
                                                                label="Not linked"
                                                              />
                                                            )}
                                                          </TableCell>
                                                          {section.showAssign ? (
                                                            <TableCell>
                                                              <FormControl size="small" fullWidth>
                                                                <Select
                                                                  value={assignId}
                                                                  onChange={(e) => {
                                                                    const v = Number(e.target.value);
                                                                    setSupplierAuditAssignSupplierByTxId(
                                                                      (prev) => ({
                                                                        ...prev,
                                                                        [txId]: v
                                                                      })
                                                                    );
                                                                  }}
                                                                  displayEmpty
                                                                >
                                                                  {selectOptions.map((opt) => (
                                                                    <MenuItem key={opt.id} value={opt.id}>
                                                                      {opt.name}
                                                                    </MenuItem>
                                                                  ))}
                                                                </Select>
                                                              </FormControl>
                                                            </TableCell>
                                                          ) : null}
                                                          <TableCell align="right">
                                                            <Stack
                                                              direction="row"
                                                              spacing={0.5}
                                                              justifyContent="flex-end"
                                                            >
                                                              {section.showAssign ? (
                                                                <Button
                                                                  size="small"
                                                                  variant="contained"
                                                                  disabled={
                                                                    supplierCorpusAuditActionBusy || !tx
                                                                  }
                                                                  onClick={() =>
                                                                    void assignSupplierAuditSelectedTxs(
                                                                      [txId],
                                                                      group.supplierId,
                                                                      group.supplierName
                                                                    )
                                                                  }
                                                                >
                                                                  Assign
                                                                </Button>
                                                              ) : null}
                                                              <Tooltip title="Open Revolut JSON in the right panel">
                                                                <span>
                                                                  <IconButton
                                                                    size="small"
                                                                    disabled={!tx && busy}
                                                                    onClick={() =>
                                                                      void openUnifiedRowJsonViewer(
                                                                        tx ||
                                                                          buildMinimalUnifiedReviewRowForSimilarDeepLink(
                                                                            txId
                                                                          )
                                                                      )
                                                                    }
                                                                  >
                                                                    <DataObjectIcon fontSize="small" />
                                                                  </IconButton>
                                                                </span>
                                                              </Tooltip>
                                                            </Stack>
                                                          </TableCell>
                                                        </TableRow>
                                                      );
                                                    })}
                                                  </TableBody>
                                                </Table>
                                              </TableContainer>
                                            )}
                                          </Box>
                                        ))}
                                        </>
                                        )}
                                      </AccordionDetails>
                                    </Accordion>
                                  );
                                })}
                              </Stack>
                            )}
                          </AccordionDetails>
                        </Accordion>

                        <Accordion disableGutters>
                          <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={{ alignItems: 'flex-start' }}>
                            <Box>
                              <Typography component="span" variant="subtitle1">
                                Manage Canonical Supplier list
                              </Typography>
                              <Typography variant="body2" color="text.secondary" sx={{ mt: 0.25 }}>
                                The following Suppliers look like they do not belong in the Canonical list of
                                suppliers (they did not appear as a proposed mapping target in section A).
                              </Typography>
                            </Box>
                          </AccordionSummary>
                          <AccordionDetails>
                            {supplierAuditUnusedCatalogSuppliers.length === 0 ? (
                              <Typography variant="body2" color="text.secondary">
                                No unused canonical suppliers for this audit — every catalog name was a proposed
                                mapping target, or the catalog is empty.
                              </Typography>
                            ) : (
                              <>
                                {supplierCorpusAuditSelectedUnusedIds.length > 0 ? (
                                  <Stack
                                    direction={{ xs: 'column', sm: 'row' }}
                                    spacing={1}
                                    alignItems={{ sm: 'center' }}
                                    sx={{ mb: 1 }}
                                    flexWrap="wrap"
                                    useFlexGap
                                  >
                                    <Typography variant="body2" color="text.secondary">
                                      {supplierCorpusAuditSelectedUnusedIds.length} selected
                                    </Typography>
                                    <Button
                                      size="small"
                                      variant="outlined"
                                      disabled={supplierCorpusAuditActionBusy}
                                      onClick={() => setSupplierCorpusAuditSelectedUnusedIds([])}
                                    >
                                      Clear
                                    </Button>
                                    <Button
                                      size="small"
                                      variant="outlined"
                                      color="error"
                                      disabled={!canMergeSuppliersCatalog || supplierCorpusAuditActionBusy}
                                      onClick={() => {
                                        const entries = supplierCorpusAuditSelectedUnusedIds.map((id) => {
                                          const row = supplierAuditUnusedCatalogSuppliers.find(
                                            (c) => Number(c.supplier_id) === id
                                          );
                                          return { id, name: row?.name || `#${id}` };
                                        });
                                        void deleteSuppliersFromCorpusAudit(entries);
                                      }}
                                    >
                                      {supplierCorpusAuditActionBusy
                                        ? 'Deleting…'
                                        : `Delete selected (${supplierCorpusAuditSelectedUnusedIds.length})`}
                                    </Button>
                                  </Stack>
                                ) : null}
                                <TableContainer sx={{ maxHeight: 480 }}>
                                  <Table size="small" stickyHeader>
                                    <TableHead>
                                      <TableRow>
                                        <TableCell padding="checkbox">
                                          <Checkbox
                                            size="small"
                                            checked={
                                              supplierAuditUnusedCatalogSuppliers.length > 0 &&
                                              supplierCorpusAuditSelectedUnusedIds.length ===
                                                supplierAuditUnusedCatalogSuppliers.length
                                            }
                                            indeterminate={
                                              supplierCorpusAuditSelectedUnusedIds.length > 0 &&
                                              supplierCorpusAuditSelectedUnusedIds.length <
                                                supplierAuditUnusedCatalogSuppliers.length
                                            }
                                            onChange={(e) => {
                                              const checked = e.target.checked;
                                              const ids = supplierAuditUnusedCatalogSuppliers.map((c) =>
                                                Number(c.supplier_id)
                                              );
                                              setSupplierCorpusAuditSelectedUnusedIds(checked ? ids : []);
                                            }}
                                            inputProps={{ 'aria-label': 'Select all unused canonical suppliers' }}
                                          />
                                        </TableCell>
                                        <TableCell>ID</TableCell>
                                        <TableCell>Name</TableCell>
                                        <TableCell align="right">Alias surfaces</TableCell>
                                        <TableCell align="right">Actions</TableCell>
                                      </TableRow>
                                    </TableHead>
                                    <TableBody>
                                      {supplierAuditUnusedCatalogSuppliers.map((cs) => {
                                        const sid = Number(cs.supplier_id);
                                        const selected = supplierCorpusAuditSelectedUnusedIds.includes(sid);
                                        return (
                                          <TableRow key={`unused-cat-${sid}`} hover selected={selected}>
                                            <TableCell padding="checkbox">
                                              <Checkbox
                                                size="small"
                                                checked={selected}
                                                onChange={(e) => {
                                                  const checked = e.target.checked;
                                                  setSupplierCorpusAuditSelectedUnusedIds((prev) =>
                                                    checked
                                                      ? Array.from(new Set([...prev, sid]))
                                                      : prev.filter((id) => id !== sid)
                                                  );
                                                }}
                                                inputProps={{ 'aria-label': `Select unused supplier ${cs.name}` }}
                                              />
                                            </TableCell>
                                            <TableCell>{sid}</TableCell>
                                            <TableCell>{cs.name}</TableCell>
                                            <TableCell align="right">
                                              {Number(cs.alias_patterns?.length || 0).toLocaleString()}
                                            </TableCell>
                                            <TableCell align="right">
                                              <IconButton
                                                size="small"
                                                color="error"
                                                disabled={!canMergeSuppliersCatalog || supplierCorpusAuditActionBusy}
                                                aria-label={`Delete supplier ${cs.name}`}
                                                onClick={() =>
                                                  void deleteSuppliersFromCorpusAudit([
                                                    { id: sid, name: cs.name }
                                                  ])
                                                }
                                              >
                                                <DeleteOutlineIcon fontSize="small" />
                                              </IconButton>
                                            </TableCell>
                                          </TableRow>
                                        );
                                      })}
                                    </TableBody>
                                  </Table>
                                </TableContainer>
                              </>
                            )}
                          </AccordionDetails>
                        </Accordion>
                      </>
                      </RenderErrorBoundary>
                    ) : !supplierCorpusAuditLoading ? (
                      <Typography variant="body2" color="text.secondary">
                        Run a corpus audit to see proposed mappings and unused canonical suppliers.
                      </Typography>
                    ) : null}
                  </Stack>
                </>
              ) : tabIndex === CLASSIFICATION_VISIBLE_TAB_KEYS.indexOf('categories') ? (
                <>
                  {categoriesCrudChartOpen ? (
                    <Card variant="outlined" sx={{ mb: 2 }}>
                      <CardContent sx={{ py: 1.5 }}>
                        <Typography variant="subtitle2" sx={{ mb: 1 }}>
                          Category transaction associations (top 20)
                        </Typography>
                        {categoriesCrudChartRows.length === 0 ? (
                          <Typography variant="body2" color="text.secondary">
                            No category rows to chart for the current filters.
                          </Typography>
                        ) : (
                          <Box sx={{ width: '100%', height: 260 }}>
                            <ResponsiveContainer width="100%" height="100%">
                              <BarChart data={categoriesCrudChartRows}>
                                <CartesianGrid strokeDasharray="3 3" />
                                <XAxis dataKey="shortName" interval={0} angle={-20} textAnchor="end" height={70} />
                                <YAxis allowDecimals={false} />
                                <RechartsTooltip
                                  formatter={(value: any) => [Number(value || 0).toLocaleString(), 'Associated Txs']}
                                  labelFormatter={(label: any, payload: any) =>
                                    payload?.[0]?.payload?.name || String(label || '')
                                  }
                                />
                                <Legend />
                                <Bar dataKey="txCount" name="Associated Txs" fill="#1976d2" />
                              </BarChart>
                            </ResponsiveContainer>
                          </Box>
                        )}
                      </CardContent>
                    </Card>
                  ) : null}
                  <Stack
                    direction={{ xs: 'column', md: 'row' }}
                    spacing={1}
                    sx={{ mb: 1.25 }}
                    alignItems={{ md: 'center' }}
                    flexWrap="wrap"
                    useFlexGap
                  >
                    <ClearableSearchTextField
                      size="small"
                      label="Text search"
                      placeholder="Name or notes…"
                      value={categoriesCrudSearch}
                      onChange={setCategoriesCrudSearch}
                      sx={{ minWidth: 240 }}
                    />
                    <FormControl size="small" sx={{ minWidth: 140 }}>
                      <InputLabel id="categories-crud-active-filter-label">Active</InputLabel>
                      <Select
                        labelId="categories-crud-active-filter-label"
                        label="Active"
                        value={categoriesCrudActiveFilter}
                        onChange={(e) =>
                          setCategoriesCrudActiveFilter(e.target.value as 'all' | 'active' | 'inactive')
                        }
                      >
                        <MenuItem value="all">All</MenuItem>
                        <MenuItem value="active">Active only</MenuItem>
                        <MenuItem value="inactive">Inactive only</MenuItem>
                      </Select>
                    </FormControl>
                    <FormControl size="small" sx={{ minWidth: 160 }}>
                      <InputLabel id="categories-crud-source-filter-label">Source</InputLabel>
                      <Select
                        labelId="categories-crud-source-filter-label"
                        label="Source"
                        value={categoriesCrudSourceFilter}
                        onChange={(e) =>
                          setCategoriesCrudSourceFilter(e.target.value as EntitySourceFilterValue)
                        }
                      >
                        <MenuItem value="all">
                          All sources ({formatCount(categoriesCrudRowsForSourceCounts.length)})
                        </MenuItem>
                        {ENTITY_SOURCE_FILTER_SELECT_LABELS.map((lab) => (
                          <MenuItem key={`categories-source-filter-${lab}`} value={lab}>
                            {`${lab} (${formatCount(categoriesCrudSourceLabelCounts.get(lab) || 0)})`}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                    <Stack direction="row" spacing={1} sx={{ ml: { md: 'auto' } }}>
                      <Button
                        variant="outlined"
                        size="small"
                        onClick={() => void refreshCategoriesCrudList()}
                        disabled={categoriesCrudLoading}
                      >
                        {categoriesCrudLoading ? 'Loading…' : 'Refresh list'}
                      </Button>
                      <Button
                        variant="outlined"
                        size="small"
                        onClick={() => setCategoriesCrudChartOpen((prev) => !prev)}
                      >
                        {categoriesCrudChartOpen ? 'Hide chart' : 'Show chart'}
                      </Button>
                      <Button variant="contained" size="small" onClick={() => openCategoryCrudCreate()}>
                        Add category
                      </Button>
                    </Stack>
                  </Stack>
                  <Stack
                    direction="row"
                    spacing={1}
                    sx={{ mb: 1 }}
                    alignItems="center"
                    flexWrap="wrap"
                    useFlexGap
                  >
                    <Button size="small" variant="outlined" onClick={resetCategoriesCrudFilters}>
                      Clear all filters
                    </Button>
                    <FormControlLabel
                      control={
                        <Switch
                          size="small"
                          checked={categoriesCrudHideNonKey}
                          onChange={(e) => setCategoriesCrudHideNonKey(e.target.checked)}
                        />
                      }
                      label="Hide non-key Category"
                      sx={{ ml: 0.5 }}
                    />
                  </Stack>
                  {categoriesCrudHasFilters ? (
                    <Stack
                      direction={{ xs: 'column', sm: 'row' }}
                      spacing={1}
                      sx={{ mb: 1.25 }}
                      alignItems={{ sm: 'center' }}
                      flexWrap="wrap"
                      useFlexGap
                    >
                      <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap>
                        {categoriesCrudFilterPills.map((pill) => (
                          <Chip
                            key={`categories-filter-pill-${pill.key}`}
                            size="small"
                            color="primary"
                            variant="outlined"
                            label={pill.label}
                            onDelete={pill.onDelete}
                          />
                        ))}
                      </Stack>
                    </Stack>
                  ) : null}
                  {categoriesCrudHasFilters ? (
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1.5 }}>
                      Showing {sortedCategoriesCrudRows.length} of {categoriesCrudRows.length} category(s) matching
                      filters.
                    </Typography>
                  ) : null}
                  {categoriesCrudError ? (
                    <Alert severity="error" sx={{ mb: 2 }} onClose={() => setCategoriesCrudError('')}>
                      {categoriesCrudError}
                    </Alert>
                  ) : null}
                  {selectedCategoryCrudIds.length > 0 ? (
                    <Stack
                      direction={{ xs: 'column', sm: 'row' }}
                      spacing={1}
                      alignItems={{ sm: 'center' }}
                      flexWrap="wrap"
                      useFlexGap
                      sx={{ mb: 1.5 }}
                    >
                      <Typography variant="body2" color="text.secondary">
                        {selectedCategoryCrudIds.length} categor
                        {selectedCategoryCrudIds.length === 1 ? 'y' : 'ies'} selected
                      </Typography>
                      <Button size="small" variant="outlined" onClick={() => setSelectedCategoryCrudIds([])}>
                        Clear selection
                      </Button>
                      <Button size="small" variant="contained" onClick={openCategoriesBulkEditDialog}>
                        Bulk edit…
                      </Button>
                    </Stack>
                  ) : null}
                  {categoriesCrudLoading && categoriesCrudRows.length === 0 ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                      <CircularProgress size={28} />
                    </Box>
                  ) : (
                    <TableContainer>
                      <Table size="small">
                        <TableHead>
                          <TableRow>
                            <TableCell padding="checkbox" sx={{ width: 48 }}>
                              <Checkbox
                                size="small"
                                disabled={
                                  selectableCategoryCrudRowIds.length === 0 || categoriesCrudLoading
                                }
                                checked={categoryCrudSelectAllCheckboxState.checked}
                                indeterminate={categoryCrudSelectAllCheckboxState.indeterminate}
                                onChange={(e) => toggleSelectAllVisibleCategories(e.target.checked)}
                                inputProps={{ 'aria-label': 'Select all categories in this view' }}
                              />
                            </TableCell>
                            <TableCell sortDirection={categoriesCrudSort.key === 'name' ? categoriesCrudSort.dir : false}>
                              <TableSortLabel
                                active={categoriesCrudSort.key === 'name'}
                                direction={categoriesCrudSort.key === 'name' ? categoriesCrudSort.dir : 'asc'}
                                onClick={() => setCategoriesCrudSort((prev) => nextSortState(prev, 'name'))}
                              >
                                Name
                              </TableSortLabel>
                            </TableCell>
                            <TableCell sortDirection={categoriesCrudSort.key === 'is_key_category' ? categoriesCrudSort.dir : false}>
                              <TableSortLabel
                                active={categoriesCrudSort.key === 'is_key_category'}
                                direction={categoriesCrudSort.key === 'is_key_category' ? categoriesCrudSort.dir : 'asc'}
                                onClick={() => setCategoriesCrudSort((prev) => nextSortState(prev, 'is_key_category'))}
                              >
                                Favourite
                              </TableSortLabel>
                            </TableCell>
                            <TableCell sortDirection={categoriesCrudSort.key === 'active' ? categoriesCrudSort.dir : false}>
                              <TableSortLabel
                                active={categoriesCrudSort.key === 'active'}
                                direction={categoriesCrudSort.key === 'active' ? categoriesCrudSort.dir : 'asc'}
                                onClick={() => setCategoriesCrudSort((prev) => nextSortState(prev, 'active'))}
                              >
                                Active
                              </TableSortLabel>
                            </TableCell>
                            <TableCell
                              sortDirection={categoriesCrudSort.key === 'source' ? categoriesCrudSort.dir : false}
                              sx={{ width: 128, whiteSpace: 'nowrap' }}
                            >
                              <TableSortLabel
                                active={categoriesCrudSort.key === 'source'}
                                direction={categoriesCrudSort.key === 'source' ? categoriesCrudSort.dir : 'asc'}
                                onClick={() => setCategoriesCrudSort((prev) => nextSortState(prev, 'source'))}
                              >
                                Source
                              </TableSortLabel>
                            </TableCell>
                            <TableCell>Applicable VAT rules</TableCell>
                            <TableCell sortDirection={categoriesCrudSort.key === 'description' ? categoriesCrudSort.dir : false}>
                              <TableSortLabel
                                active={categoriesCrudSort.key === 'description'}
                                direction={categoriesCrudSort.key === 'description' ? categoriesCrudSort.dir : 'asc'}
                                onClick={() => setCategoriesCrudSort((prev) => nextSortState(prev, 'description'))}
                              >
                                Description
                              </TableSortLabel>
                            </TableCell>
                            <TableCell align="right">Actions</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {sortedCategoriesCrudRows.length === 0 ? (
                            <TableRow>
                              <TableCell colSpan={8}>
                                No categories yet. Click Add category or use Refresh after data loads.
                              </TableCell>
                            </TableRow>
                          ) : (
                            sortedCategoriesCrudRows.map((row) => {
                              const categorySourceMeta = categorySourceTagFromRow(row);
                              const isImmutableSystem = isCategoryRowImmutableSystem(row);
                              const categorySourceTooltip = row.description.trim()
                                ? row.description
                                : 'No description — shown as Manual source.';
                              return (
                              <TableRow key={row.id}>
                                <TableCell padding="checkbox">
                                  <Checkbox
                                    size="small"
                                    checked={selectedCategoryCrudIds.includes(row.id)}
                                    onChange={(e) => toggleCategoryCrudRowSelected(row.id, e.target.checked)}
                                    disabled={categoriesCrudLoading || isImmutableSystem}
                                    inputProps={{ 'aria-label': `Select category ${row.name}` }}
                                  />
                                </TableCell>
                                <TableCell>{row.name}</TableCell>
                                <TableCell>
                                  <Tooltip
                                    title={
                                      isImmutableSystem
                                        ? 'System categories cannot be changed'
                                        : row.is_key_category
                                          ? 'Remove from favourites'
                                          : 'Add to favourites'
                                    }
                                  >
                                    <span>
                                      <IconButton
                                        size="small"
                                        onClick={() => void toggleCategoryKeyCategory(row)}
                                        disabled={
                                          categoryKeyToggleBusyId === row.id || isImmutableSystem
                                        }
                                        aria-label={
                                          row.is_key_category ? 'Remove from favourites' : 'Add to favourites'
                                        }
                                      >
                                        {row.is_key_category ? (
                                          <StarIcon fontSize="small" color="warning" />
                                        ) : (
                                          <StarBorderIcon fontSize="small" color="disabled" />
                                        )}
                                      </IconButton>
                                    </span>
                                  </Tooltip>
                                </TableCell>
                                <TableCell>
                                  <Chip
                                    size="small"
                                    label={row.is_active ? 'Yes' : 'No'}
                                    color={row.is_active ? 'success' : 'default'}
                                    variant="outlined"
                                  />
                                </TableCell>
                                <TableCell sx={{ maxWidth: 160 }}>
                                  <Tooltip title={categorySourceTooltip}>
                                    <Chip
                                      size="small"
                                      variant="outlined"
                                      color={categorySourceMeta.chipColor}
                                      label={categorySourceMeta.label}
                                      sx={{ fontWeight: 600 }}
                                    />
                                  </Tooltip>
                                </TableCell>
                                <TableCell sx={{ maxWidth: 420 }}>
                                  {((vatRulesByCategoryId.get(row.id) || []).length > 0) ? (
                                    <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap>
                                      {(vatRulesByCategoryId.get(row.id) || []).map((rule) => {
                                        const baseLabel = formatPrefixedRuleId('vat', rule.id);
                                        const namePart = rule.name ? ` ${rule.name}` : '';
                                        const ratePart = rule.vat_rate ? ` (${rule.vat_rate})` : '';
                                        const label = `${baseLabel}${namePart}${ratePart}`;
                                        return (
                                          <Tooltip key={`category-vat-rule-chip-${row.id}-${rule.id}`} title={`Priority ${rule.priority}`}>
                                            <Chip
                                              size="small"
                                              label={label}
                                              sx={{
                                                height: 'auto',
                                                backgroundColor: 'rgba(37, 99, 235, 0.14)',
                                                border: '1px solid rgba(37, 99, 235, 0.35)',
                                                color: 'rgb(30, 64, 175)',
                                                '& .MuiChip-label': {
                                                  display: 'block',
                                                  whiteSpace: 'normal',
                                                  fontWeight: 600,
                                                  fontSize: '0.75rem',
                                                  lineHeight: 1.25,
                                                  py: 0.35
                                                }
                                              }}
                                            />
                                          </Tooltip>
                                        );
                                      })}
                                    </Stack>
                                  ) : (
                                    '—'
                                  )}
                                </TableCell>
                                <TableCell sx={{ maxWidth: 360 }}>
                                  <Typography variant="body2" noWrap title={row.description || ''}>
                                    {row.description || '—'}
                                  </Typography>
                                </TableCell>
                                <TableCell align="right">
                                  {isImmutableSystem ? (
                                    <Tooltip title="System categories are fixed and cannot be edited or deleted.">
                                      <Typography
                                        variant="body2"
                                        color="text.secondary"
                                        component="span"
                                        sx={{ display: 'inline-block', minWidth: 24, textAlign: 'right' }}
                                      >
                                        —
                                      </Typography>
                                    </Tooltip>
                                  ) : (
                                    <Stack
                                      direction="row"
                                      spacing={0.25}
                                      justifyContent="flex-end"
                                      alignItems="center"
                                    >
                                      <Tooltip title="Edit category (including Active state)">
                                        <span>
                                          <IconButton
                                            size="small"
                                            color="primary"
                                            aria-label={`Edit category ${row.name}`}
                                            onClick={() => openCategoryCrudEdit(row)}
                                            disabled={categoriesCrudLoading}
                                          >
                                            <EditIcon fontSize="small" />
                                          </IconButton>
                                        </span>
                                      </Tooltip>
                                      {row.is_active ? (
                                        <Tooltip title="Delete category">
                                          <span>
                                            <IconButton
                                              size="small"
                                              color="error"
                                              aria-label={`Delete category ${row.name}`}
                                              onClick={() => void deleteCategoryCrud(row)}
                                              disabled={categoriesCrudLoading}
                                            >
                                              <DeleteOutlineIcon fontSize="small" />
                                            </IconButton>
                                          </span>
                                        </Tooltip>
                                      ) : (
                                        <Tooltip title="Restore category">
                                          <span>
                                            <IconButton
                                              size="small"
                                              color="primary"
                                              aria-label={`Restore category ${row.name}`}
                                              onClick={() => void reactivateCategoryCrud(row)}
                                              disabled={categoriesCrudLoading}
                                            >
                                              <RestoreIcon fontSize="small" />
                                            </IconButton>
                                          </span>
                                        </Tooltip>
                                      )}
                                    </Stack>
                                  )}
                                </TableCell>
                              </TableRow>
                              );
                            })
                          )}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  )}
                </>
              ) : tabIndex === CLASSIFICATION_VISIBLE_TAB_KEYS.indexOf('pipeline') ? (
                <>
                  <Stack
                    direction={{ xs: 'column', sm: 'row' }}
                    spacing={1}
                    alignItems={{ sm: 'center' }}
                    justifyContent="space-between"
                    sx={{ mb: 1.5 }}
                  >
                    <Stack spacing={0.5}>
                      <Typography variant="subtitle2" color="text.secondary">
                        TX review pipeline diagnostics
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        Refresh reloads totals from the server (it does not run rules). Use Run rule sync to apply
                        classification rules to transactions.
                      </Typography>
                    </Stack>
                    <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                      <Button
                        size="small"
                        variant="contained"
                        color="primary"
                        onClick={() => void runReapplyAllRulesUnconfirmed()}
                        disabled={
                          loading ||
                          applyingRules ||
                          supplierBackfillBusy ||
                          reapplyUnconfirmedBusy ||
                          reapplyVatUnconfirmedBusy ||
                          reapplySpecialCaseUnconfirmedBusy ||
                          rerunAllRulesBusy ||
                          dedupeDisplayDupTagsBusy ||
                          removeAllTagsBusy
                        }
                      >
                        {rerunAllRulesBusy ? 'Starting rule sync…' : 'Run rule sync'}
                      </Button>
                      <Button
                        size="small"
                        variant="outlined"
                        onClick={() => void fetchPipelineCounts()}
                        disabled={pipelineCountsLoading}
                      >
                        {pipelineCountsLoading ? 'Refreshing…' : 'Refresh'}
                      </Button>
                    </Stack>
                  </Stack>
                  {pipelineCountsError ? (
                    <Alert severity="error" sx={{ mb: 1.5 }}>
                      {pipelineCountsError}
                    </Alert>
                  ) : null}
                  {pipelineCountsLoading && !pipelineCountsAllScope ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
                      <CircularProgress size={24} />
                    </Box>
                  ) : null}
                  <Stack spacing={2}>
                    <Card variant="outlined">
                      <CardContent>
                        <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
                          Scope policy: {String(pipelineCountsAllScope?.scope?.policy || 'n/a')} | target users:{' '}
                          {Array.isArray(pipelineCountsAllScope?.target_user_ids)
                            ? pipelineCountsAllScope?.target_user_ids.join(', ')
                            : 'n/a'}
                        </Typography>
                        {pipelineCountsAllScope?.counts ? (
                          (() => {
                            const counts = pipelineCountsAllScope.counts;
                            const total = Number(counts.total_txs_in_db || counts.total || 0);
                            const uncommitted = Number(counts.uncommitted_total || 0);
                            const pctOfTotal = (value: number) =>
                              total > 0 ? `${((value / total) * 100).toFixed(1)}%` : '—';
                            const committedOk = Number(counts.committed_all_required_no_vat_conflict || 0);
                            return (
                              <TableContainer>
                                <Table size="small">
                                  <TableHead>
                                    <TableRow>
                                      <TableCell>Metric</TableCell>
                                      <TableCell align="right">Count</TableCell>
                                      <TableCell sx={{ width: 160 }}>Bar</TableCell>
                                      <TableCell align="right">% of DB</TableCell>
                                    </TableRow>
                                  </TableHead>
                                  <TableBody>
                                    <TableRow>
                                      <TableCell sx={{ fontWeight: 700 }}>
                                        <Button
                                          size="small"
                                          variant="text"
                                          onClick={() => openTxReviewFromPipeline({ committed: 'all' })}
                                          sx={{ p: 0, minWidth: 0, textTransform: 'none', fontWeight: 700 }}
                                        >
                                          Total TXs in DB
                                        </Button>
                                      </TableCell>
                                      <TableCell align="right">{total.toLocaleString()}</TableCell>
                                      <PipelineBarTableCell value={total} total={total} />
                                      <TableCell align="right">100.0%</TableCell>
                                    </TableRow>
                                    {(counts.txs_owned_per_user || []).map((owner) => (
                                      <TableRow key={`pipe-sum-owned-${owner.user_id}`}>
                                        <TableCell sx={{ pl: 2 }}>
                                          TXs owned by {owner.user_label} (User {owner.user_id})
                                        </TableCell>
                                        <TableCell align="right">{Number(owner.count || 0).toLocaleString()}</TableCell>
                                        <PipelineBarTableCell value={Number(owner.count || 0)} total={total} />
                                        <TableCell align="right">{pctOfTotal(Number(owner.count || 0))}</TableCell>
                                      </TableRow>
                                    ))}
                                    <TableRow>
                                      <TableCell sx={{ fontWeight: 700 }}>
                                        <Button
                                          size="small"
                                          variant="text"
                                          onClick={() => openTxReviewFromPipeline({ committed: 'committed' })}
                                          sx={{ p: 0, minWidth: 0, textTransform: 'none', fontWeight: 700 }}
                                        >
                                          Committed (review confirmed)
                                        </Button>
                                      </TableCell>
                                      <TableCell align="right" sx={{ fontWeight: 700 }}>
                                        {committedOk.toLocaleString()}
                                      </TableCell>
                                      <PipelineBarTableCell value={committedOk} total={total} tone="ready" />
                                      <TableCell align="right" sx={{ fontWeight: 700 }}>
                                        {pctOfTotal(committedOk)}
                                      </TableCell>
                                    </TableRow>
                                    <TableRow>
                                      <TableCell sx={{ fontWeight: 700 }}>
                                        <Button
                                          size="small"
                                          variant="text"
                                          onClick={() => openTxReviewFromPipeline({ committed: 'uncommitted' })}
                                          sx={{ p: 0, minWidth: 0, textTransform: 'none', fontWeight: 700 }}
                                        >
                                          Uncommitted
                                        </Button>
                                      </TableCell>
                                      <TableCell align="right" sx={{ fontWeight: 700 }}>
                                        {uncommitted.toLocaleString()}
                                      </TableCell>
                                      <PipelineBarTableCell value={uncommitted} total={total} />
                                      <TableCell align="right" sx={{ fontWeight: 700 }}>
                                        {pctOfTotal(uncommitted)}
                                      </TableCell>
                                    </TableRow>
                                  </TableBody>
                                </Table>
                              </TableContainer>
                            );
                          })()
                        ) : (
                          <Typography variant="body2" color="text.secondary">
                            No pipeline data loaded yet.
                          </Typography>
                        )}
                      </CardContent>
                    </Card>

                    <Card variant="outlined">
                      <CardContent>
                        {pipelineCountsAllScope?.counts ? (
                          (() => {
                            const counts = pipelineCountsAllScope.counts;
                            const pd = counts.pipeline_dashboard;
                            const pipMissBg = alpha(theme.palette.warning.main, 0.14);
                            const pipProbBg = alpha(theme.palette.error.main, 0.12);
                            const pipReadyBg = alpha(theme.palette.success.main, 0.1);

                            const renderDashboard = (
                              label: string,
                              data: PipelineDashboardSubset | undefined,
                              cf: 'committed' | 'uncommitted'
                            ) => {
                              const d = data;
                              const st = Number(d?.subset_total ?? 0);
                              const s1 = d?.stage1;
                              const s2 = d?.stage2;
                              const s3 = d?.stage3;
                              const s4 = d?.stage4;
                              const mt = d?.missing_tags;

                              const totalDb = Number(counts.total_txs_in_db || counts.total || 0);
                              const pctOfDb = (value: number) => {
                                const n = Number(value) || 0;
                                return totalDb > 0 ? `${((n / totalDb) * 100).toFixed(1)}%` : '—';
                              };
                              const pipeTableHead = (
                                <TableHead>
                                  <TableRow>
                                    <TableCell sx={{ fontWeight: 600 }}>Metric</TableCell>
                                    <TableCell align="right" sx={{ fontWeight: 600, width: 100 }}>
                                      Count
                                    </TableCell>
                                    <TableCell sx={{ fontWeight: 600, width: 160 }}>Bar</TableCell>
                                    <TableCell align="right" sx={{ fontWeight: 600, width: 88 }}>
                                      % of DB
                                    </TableCell>
                                    <TableCell align="right" sx={{ fontWeight: 600, width: 140 }}>
                                      Action
                                    </TableCell>
                                  </TableRow>
                                </TableHead>
                              );
                              const pipeBar = (value: number, tone: PipelineBarTone = 'default') => (
                                <PipelineBarTableCell value={value} total={totalDb} tone={tone} />
                              );

                              const openSubset = () => openTxReviewFromPipeline({ committed: cf });

                              return (
                                <Stack spacing={2} sx={{ pl: 2, borderLeft: '3px solid', borderColor: 'divider' }}>
                                  <Typography variant="subtitle2" color="text.secondary">
                                    {label} · {st.toLocaleString()} TXs
                                  </Typography>

                                  <Box>
                                    <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 700 }}>
                                      Stage 1 — Cash flow (tags)
                                    </Typography>
                                    <Table size="small" sx={{ tableLayout: 'fixed' }}>
                                      {pipeTableHead}
                                      <TableBody>
                                        <TableRow>
                                          <TableCell>In (cashflow tag)</TableCell>
                                          <TableCell align="right">
                                            {Number(s1?.in_heading_count ?? 0).toLocaleString()}
                                          </TableCell>
                                          {pipeBar(Number(s1?.in_heading_count ?? 0))}
                                          <TableCell align="right">
                                            {pctOfDb(Number(s1?.in_heading_count ?? 0))}
                                          </TableCell>
                                          <TableCell align="right">
                                            <Button
                                              size="small"
                                              variant="text"
                                              onClick={() =>
                                                openTxReviewFromPipeline({
                                                  committed: cf,
                                                  headings: ['In']
                                                })
                                              }
                                              sx={{ p: 0, minWidth: 0, textTransform: 'none' }}
                                            >
                                              Open
                                            </Button>
                                          </TableCell>
                                        </TableRow>
                                        <TableRow>
                                          <TableCell>Out (cashflow tag)</TableCell>
                                          <TableCell align="right">
                                            {Number(s1?.out_heading_count ?? 0).toLocaleString()}
                                          </TableCell>
                                          {pipeBar(Number(s1?.out_heading_count ?? 0))}
                                          <TableCell align="right">
                                            {pctOfDb(Number(s1?.out_heading_count ?? 0))}
                                          </TableCell>
                                          <TableCell align="right">
                                            <Button
                                              size="small"
                                              variant="text"
                                              onClick={() =>
                                                openTxReviewFromPipeline({
                                                  committed: cf,
                                                  headings: ['Out']
                                                })
                                              }
                                              sx={{ p: 0, minWidth: 0, textTransform: 'none' }}
                                            >
                                              Open
                                            </Button>
                                          </TableCell>
                                        </TableRow>
                                        <TableRow>
                                          <TableCell>N/A (cashflow tag)</TableCell>
                                          <TableCell align="right">
                                            {Number(s1?.na_heading_count ?? 0).toLocaleString()}
                                          </TableCell>
                                          {pipeBar(Number(s1?.na_heading_count ?? 0))}
                                          <TableCell align="right">
                                            {pctOfDb(Number(s1?.na_heading_count ?? 0))}
                                          </TableCell>
                                          <TableCell align="right">
                                            <Button
                                              size="small"
                                              variant="text"
                                              onClick={() =>
                                                openTxReviewFromPipeline({
                                                  committed: cf,
                                                  headings: ['N/A']
                                                })
                                              }
                                              sx={{ p: 0, minWidth: 0, textTransform: 'none' }}
                                            >
                                              Open
                                            </Button>
                                          </TableCell>
                                        </TableRow>
                                        <TableRow>
                                          <TableCell sx={{ fontWeight: 700 }}>
                                            Total (In + Out + N/A, overlap allowed)
                                          </TableCell>
                                          <TableCell align="right" sx={{ fontWeight: 700 }}>
                                            {Number(s1?.heading_rows_total_sum ?? 0).toLocaleString()}
                                          </TableCell>
                                          {pipeBar(Number(s1?.heading_rows_total_sum ?? 0))}
                                          <TableCell align="right" sx={{ fontWeight: 700 }}>
                                            {pctOfDb(Number(s1?.heading_rows_total_sum ?? 0))}
                                          </TableCell>
                                          <TableCell align="right">
                                            <Button size="small" variant="text" onClick={openSubset} sx={{ p: 0, minWidth: 0 }}>
                                              All in subset
                                            </Button>
                                          </TableCell>
                                        </TableRow>
                                        <TableRow sx={{ bgcolor: pipMissBg }}>
                                          <TableCell>Missing (no In/Out/N/A cashflow tag)</TableCell>
                                          <TableCell align="right">
                                            {Number(s1?.missing_neither_heading_count ?? 0).toLocaleString()}
                                          </TableCell>
                                          {pipeBar(Number(s1?.missing_neither_heading_count ?? 0), 'missing')}
                                          <TableCell align="right">
                                            {pctOfDb(Number(s1?.missing_neither_heading_count ?? 0))}
                                          </TableCell>
                                          <TableCell align="right">
                                            <Button
                                              size="small"
                                              variant="text"
                                              onClick={() =>
                                                openTxReviewFromPipeline({
                                                  committed: cf,
                                                  statuses: [buildMissingDimsStatusFilterValue([MISSING_DIM_CASH_FLOW])]
                                                })
                                              }
                                              sx={{ p: 0, minWidth: 0, textTransform: 'none' }}
                                            >
                                              Open
                                            </Button>
                                          </TableCell>
                                        </TableRow>
                                        <TableRow sx={{ bgcolor: pipProbBg }}>
                                          <TableCell>Problem: In + Out cashflow tags</TableCell>
                                          <TableCell align="right">
                                            {Number(s1?.both_in_out_heading_tag_count ?? 0).toLocaleString()}
                                          </TableCell>
                                          {pipeBar(Number(s1?.both_in_out_heading_tag_count ?? 0), 'problem')}
                                          <TableCell align="right">
                                            {pctOfDb(Number(s1?.both_in_out_heading_tag_count ?? 0))}
                                          </TableCell>
                                          <TableCell align="right">
                                            <Button
                                              size="small"
                                              variant="text"
                                              onClick={() => {
                                                const ids = Array.from(
                                                  new Set(
                                                    (s1?.both_in_out_heading_tag_tx_ids || [])
                                                      .map((id) => Number(id))
                                                      .filter((id) => Number.isFinite(id) && id > 0)
                                                  )
                                                );
                                                if (ids.length > 0) openTxReviewForIds(ids);
                                                else setError('No transactions in this bucket.');
                                              }}
                                              sx={{ p: 0, minWidth: 0, textTransform: 'none' }}
                                            >
                                              By ID
                                            </Button>
                                          </TableCell>
                                        </TableRow>
                                      </TableBody>
                                    </Table>
                                  </Box>

                                  <Box>
                                    <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 700 }}>
                                      Stage 2 — Supplier
                                    </Typography>
                                    <Table size="small" sx={{ tableLayout: 'fixed' }}>
                                      {pipeTableHead}
                                      <TableBody>
                                        <TableRow>
                                          <TableCell>With supplier</TableCell>
                                          <TableCell align="right">{Number(s2?.with_supplier ?? 0).toLocaleString()}</TableCell>
                                          {pipeBar(Number(s2?.with_supplier ?? 0))}
                                          <TableCell align="right">{pctOfDb(Number(s2?.with_supplier ?? 0))}</TableCell>
                                          <TableCell align="right">
                                            <Button size="small" variant="text" onClick={openSubset} sx={{ p: 0, minWidth: 0 }}>
                                              Subset
                                            </Button>
                                          </TableCell>
                                        </TableRow>
                                        <TableRow sx={{ bgcolor: pipMissBg }}>
                                          <TableCell>Without supplier</TableCell>
                                          <TableCell align="right">{Number(s2?.without_supplier ?? 0).toLocaleString()}</TableCell>
                                          {pipeBar(Number(s2?.without_supplier ?? 0), 'missing')}
                                          <TableCell align="right">{pctOfDb(Number(s2?.without_supplier ?? 0))}</TableCell>
                                          <TableCell align="right">
                                            <Button
                                              size="small"
                                              variant="text"
                                              onClick={() =>
                                                openTxReviewFromPipeline({
                                                  committed: cf,
                                                  statuses: [STATUS_FILTER_ANY_SUPPLIER_MISSING]
                                                })
                                              }
                                              sx={{ p: 0, minWidth: 0, textTransform: 'none' }}
                                            >
                                              Open
                                            </Button>
                                          </TableCell>
                                        </TableRow>
                                        <TableRow>
                                          <TableCell sx={{ fontWeight: 700 }}>Total</TableCell>
                                          <TableCell align="right" sx={{ fontWeight: 700 }}>
                                            {Number(s2?.total ?? st).toLocaleString()}
                                          </TableCell>
                                          {pipeBar(Number(s2?.total ?? st))}
                                          <TableCell align="right" sx={{ fontWeight: 700 }}>
                                            {pctOfDb(Number(s2?.total ?? st))}
                                          </TableCell>
                                          <TableCell align="right">—</TableCell>
                                        </TableRow>
                                        <TableRow sx={{ bgcolor: pipProbBg }}>
                                          <TableCell>Problem: multiple suppliers</TableCell>
                                          <TableCell align="right">{Number(s2?.multi_supplier_count ?? 0).toLocaleString()}</TableCell>
                                          {pipeBar(Number(s2?.multi_supplier_count ?? 0), 'problem')}
                                          <TableCell align="right">{pctOfDb(Number(s2?.multi_supplier_count ?? 0))}</TableCell>
                                          <TableCell align="right">
                                            <Button
                                              size="small"
                                              variant="text"
                                              onClick={() => {
                                                const ids = (s2?.multi_supplier_tx_ids || [])
                                                  .map((id) => Number(id))
                                                  .filter((id) => Number.isFinite(id) && id > 0);
                                                if (ids.length > 0) openTxReviewForIds(ids);
                                                else setError('No transactions in this bucket.');
                                              }}
                                              sx={{ p: 0, minWidth: 0, textTransform: 'none' }}
                                            >
                                              By ID
                                            </Button>
                                          </TableCell>
                                        </TableRow>
                                      </TableBody>
                                    </Table>
                                    <Accordion
                                      defaultExpanded={false}
                                      disableGutters
                                      sx={{ mt: 1, '&:before': { display: 'none' } }}
                                    >
                                      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                                        <Typography variant="caption">Supplier breakdown (with supplier)</Typography>
                                      </AccordionSummary>
                                      <AccordionDetails sx={{ pt: 0 }}>
                                        <Table size="small" sx={{ tableLayout: 'fixed' }}>
                                          {pipeTableHead}
                                          <TableBody>
                                            {(s2?.supplier_breakdown || []).map((e) => (
                                              <TableRow key={`sup-${cf}-${e.label}`}>
                                                <TableCell sx={{ pl: 1 }}>{e.label}</TableCell>
                                                <TableCell align="right">{Number(e.count || 0).toLocaleString()}</TableCell>
                                                {pipeBar(Number(e.count || 0))}
                                                <TableCell align="right">{pctOfDb(Number(e.count || 0))}</TableCell>
                                                <TableCell align="right">
                                                  <Button
                                                    size="small"
                                                    variant="text"
                                                    onClick={() =>
                                                      openTxReviewFromPipeline({
                                                        committed: cf,
                                                        suppliers: [e.label]
                                                      })
                                                    }
                                                    sx={{ p: 0, minWidth: 0, textTransform: 'none' }}
                                                  >
                                                    Open
                                                  </Button>
                                                </TableCell>
                                              </TableRow>
                                            ))}
                                          </TableBody>
                                        </Table>
                                      </AccordionDetails>
                                    </Accordion>
                                  </Box>

                                  <Box>
                                    <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 700 }}>
                                      Stage 3 — Category
                                    </Typography>
                                    <Table size="small" sx={{ tableLayout: 'fixed' }}>
                                      {pipeTableHead}
                                      <TableBody>
                                        <TableRow>
                                          <TableCell>With granular category</TableCell>
                                          <TableCell align="right">{Number(s3?.with_category ?? 0).toLocaleString()}</TableCell>
                                          {pipeBar(Number(s3?.with_category ?? 0))}
                                          <TableCell align="right">{pctOfDb(Number(s3?.with_category ?? 0))}</TableCell>
                                          <TableCell align="right">
                                            <Button size="small" variant="text" onClick={openSubset} sx={{ p: 0, minWidth: 0 }}>
                                              Subset
                                            </Button>
                                          </TableCell>
                                        </TableRow>
                                        <TableRow sx={{ bgcolor: pipMissBg }}>
                                          <TableCell>Without granular category</TableCell>
                                          <TableCell align="right">{Number(s3?.without_category ?? 0).toLocaleString()}</TableCell>
                                          {pipeBar(Number(s3?.without_category ?? 0), 'missing')}
                                          <TableCell align="right">{pctOfDb(Number(s3?.without_category ?? 0))}</TableCell>
                                          <TableCell align="right">
                                            <Button
                                              size="small"
                                              variant="text"
                                              onClick={() =>
                                                openTxReviewFromPipeline({
                                                  committed: cf,
                                                  statuses: [STATUS_FILTER_ANY_CATEGORY_MISSING]
                                                })
                                              }
                                              sx={{ p: 0, minWidth: 0, textTransform: 'none' }}
                                            >
                                              Open
                                            </Button>
                                          </TableCell>
                                        </TableRow>
                                        <TableRow>
                                          <TableCell sx={{ fontWeight: 700 }}>Total</TableCell>
                                          <TableCell align="right" sx={{ fontWeight: 700 }}>
                                            {Number(s3?.total ?? st).toLocaleString()}
                                          </TableCell>
                                          {pipeBar(Number(s3?.total ?? st))}
                                          <TableCell align="right" sx={{ fontWeight: 700 }}>
                                            {pctOfDb(Number(s3?.total ?? st))}
                                          </TableCell>
                                          <TableCell align="right">—</TableCell>
                                        </TableRow>
                                        <TableRow sx={{ bgcolor: pipProbBg }}>
                                          <TableCell>Problem: multiple granular categories</TableCell>
                                          <TableCell align="right">{Number(s3?.multi_category_count ?? 0).toLocaleString()}</TableCell>
                                          {pipeBar(Number(s3?.multi_category_count ?? 0), 'problem')}
                                          <TableCell align="right">{pctOfDb(Number(s3?.multi_category_count ?? 0))}</TableCell>
                                          <TableCell align="right">
                                            <Button
                                              size="small"
                                              variant="text"
                                              onClick={() => {
                                                const ids = (s3?.multi_category_tx_ids || [])
                                                  .map((id) => Number(id))
                                                  .filter((id) => Number.isFinite(id) && id > 0);
                                                if (ids.length > 0) openTxReviewForIds(ids);
                                                else setError('No transactions in this bucket.');
                                              }}
                                              sx={{ p: 0, minWidth: 0, textTransform: 'none' }}
                                            >
                                              By ID
                                            </Button>
                                          </TableCell>
                                        </TableRow>
                                      </TableBody>
                                    </Table>
                                    <Accordion
                                      defaultExpanded={false}
                                      disableGutters
                                      sx={{ mt: 1, '&:before': { display: 'none' } }}
                                    >
                                      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                                        <Typography variant="caption">Category breakdown (with category)</Typography>
                                      </AccordionSummary>
                                      <AccordionDetails sx={{ pt: 0 }}>
                                        <Table size="small" sx={{ tableLayout: 'fixed' }}>
                                          {pipeTableHead}
                                          <TableBody>
                                            {(s3?.category_breakdown || []).map((e) => (
                                              <TableRow key={`cat-${cf}-${e.label}`}>
                                                <TableCell sx={{ pl: 1 }}>{e.label}</TableCell>
                                                <TableCell align="right">{Number(e.count || 0).toLocaleString()}</TableCell>
                                                {pipeBar(Number(e.count || 0))}
                                                <TableCell align="right">{pctOfDb(Number(e.count || 0))}</TableCell>
                                                <TableCell align="right">
                                                  <Button
                                                    size="small"
                                                    variant="text"
                                                    onClick={() =>
                                                      openTxReviewFromPipeline({
                                                        committed: cf,
                                                        categories: [e.label]
                                                      })
                                                    }
                                                    sx={{ p: 0, minWidth: 0, textTransform: 'none' }}
                                                  >
                                                    Open
                                                  </Button>
                                                </TableCell>
                                              </TableRow>
                                            ))}
                                          </TableBody>
                                        </Table>
                                      </AccordionDetails>
                                    </Accordion>
                                  </Box>

                                  <Box>
                                    <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 700 }}>
                                      Stage 4 — VAT
                                    </Typography>
                                    <Table size="small" sx={{ tableLayout: 'fixed' }}>
                                      {pipeTableHead}
                                      <TableBody>
                                        <TableRow>
                                          <TableCell>With VAT rate</TableCell>
                                          <TableCell align="right">{Number(s4?.with_vat_rate ?? 0).toLocaleString()}</TableCell>
                                          {pipeBar(Number(s4?.with_vat_rate ?? 0))}
                                          <TableCell align="right">{pctOfDb(Number(s4?.with_vat_rate ?? 0))}</TableCell>
                                          <TableCell align="right">
                                            <Button size="small" variant="text" onClick={openSubset} sx={{ p: 0, minWidth: 0 }}>
                                              Subset
                                            </Button>
                                          </TableCell>
                                        </TableRow>
                                        <TableRow sx={{ bgcolor: pipMissBg }}>
                                          <TableCell>Without VAT rate</TableCell>
                                          <TableCell align="right">{Number(s4?.without_vat_rate ?? 0).toLocaleString()}</TableCell>
                                          {pipeBar(Number(s4?.without_vat_rate ?? 0), 'missing')}
                                          <TableCell align="right">{pctOfDb(Number(s4?.without_vat_rate ?? 0))}</TableCell>
                                          <TableCell align="right">
                                            <Button
                                              size="small"
                                              variant="text"
                                              onClick={() =>
                                                openTxReviewFromPipeline({
                                                  committed: cf,
                                                  statuses: [STATUS_FILTER_ANY_VAT_MISSING]
                                                })
                                              }
                                              sx={{ p: 0, minWidth: 0, textTransform: 'none' }}
                                            >
                                              Open
                                            </Button>
                                          </TableCell>
                                        </TableRow>
                                        <TableRow>
                                          <TableCell sx={{ fontWeight: 700 }}>Total</TableCell>
                                          <TableCell align="right" sx={{ fontWeight: 700 }}>
                                            {Number(s4?.total ?? st).toLocaleString()}
                                          </TableCell>
                                          {pipeBar(Number(s4?.total ?? st))}
                                          <TableCell align="right" sx={{ fontWeight: 700 }}>
                                            {pctOfDb(Number(s4?.total ?? st))}
                                          </TableCell>
                                          <TableCell align="right">—</TableCell>
                                        </TableRow>
                                        <TableRow sx={{ bgcolor: pipProbBg }}>
                                          <TableCell>VAT conflict (row flag)</TableCell>
                                          <TableCell align="right">{Number(s4?.vat_conflict_count ?? 0).toLocaleString()}</TableCell>
                                          {pipeBar(Number(s4?.vat_conflict_count ?? 0), 'problem')}
                                          <TableCell align="right">{pctOfDb(Number(s4?.vat_conflict_count ?? 0))}</TableCell>
                                          <TableCell align="right">
                                            <Button
                                              size="small"
                                              variant="text"
                                              onClick={() =>
                                                openTxReviewFromPipeline({
                                                  committed: cf,
                                                  statuses: [STATUS_FILTER_VAT_CONFLICT]
                                                })
                                              }
                                              sx={{ p: 0, minWidth: 0, textTransform: 'none' }}
                                            >
                                              Open
                                            </Button>
                                          </TableCell>
                                        </TableRow>
                                        <TableRow sx={{ bgcolor: pipProbBg }}>
                                          <TableCell>Problem: multiple VAT rates on tags</TableCell>
                                          <TableCell align="right">{Number(s4?.multi_vat_rate_count ?? 0).toLocaleString()}</TableCell>
                                          {pipeBar(Number(s4?.multi_vat_rate_count ?? 0), 'problem')}
                                          <TableCell align="right">{pctOfDb(Number(s4?.multi_vat_rate_count ?? 0))}</TableCell>
                                          <TableCell align="right">
                                            <Button
                                              size="small"
                                              variant="text"
                                              onClick={() => {
                                                const ids = (s4?.multi_vat_rate_tx_ids || [])
                                                  .map((id) => Number(id))
                                                  .filter((id) => Number.isFinite(id) && id > 0);
                                                if (ids.length > 0) openTxReviewForIds(ids);
                                                else setError('No transactions in this bucket.');
                                              }}
                                              sx={{ p: 0, minWidth: 0, textTransform: 'none' }}
                                            >
                                              By ID
                                            </Button>
                                          </TableCell>
                                        </TableRow>
                                      </TableBody>
                                    </Table>
                                    <Accordion
                                      defaultExpanded={false}
                                      disableGutters
                                      sx={{ mt: 1, '&:before': { display: 'none' } }}
                                    >
                                      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                                        <Typography variant="caption">VAT breakdown (with VAT)</Typography>
                                      </AccordionSummary>
                                      <AccordionDetails sx={{ pt: 0 }}>
                                        <Table size="small" sx={{ tableLayout: 'fixed' }}>
                                          {pipeTableHead}
                                          <TableBody>
                                            {(s4?.vat_breakdown || []).map((e) => (
                                              <TableRow key={`vat-${cf}-${e.label}`}>
                                                <TableCell sx={{ pl: 1 }}>{e.label}</TableCell>
                                                <TableCell align="right">{Number(e.count || 0).toLocaleString()}</TableCell>
                                                {pipeBar(Number(e.count || 0))}
                                                <TableCell align="right">{pctOfDb(Number(e.count || 0))}</TableCell>
                                                <TableCell align="right">
                                                  <Button
                                                    size="small"
                                                    variant="text"
                                                    onClick={() =>
                                                      openTxReviewFromPipeline({
                                                        committed: cf,
                                                        vat: [vatFilterKeyFromRaw(e.label)]
                                                      })
                                                    }
                                                    sx={{ p: 0, minWidth: 0, textTransform: 'none' }}
                                                  >
                                                    Open
                                                  </Button>
                                                </TableCell>
                                              </TableRow>
                                            ))}
                                          </TableBody>
                                        </Table>
                                      </AccordionDetails>
                                    </Accordion>
                                  </Box>

                                  <Box>
                                    <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 700 }}>
                                      Missing tags
                                    </Typography>
                                    <Table size="small" sx={{ tableLayout: 'fixed' }}>
                                      {pipeTableHead}
                                      <TableBody>
                                        <TableRow sx={{ bgcolor: pipReadyBg }}>
                                          <TableCell>No tags missing — ready</TableCell>
                                          <TableCell align="right">
                                            {Number(mt?.no_tags_missing_ready ?? 0).toLocaleString()}
                                          </TableCell>
                                          {pipeBar(Number(mt?.no_tags_missing_ready ?? 0), 'ready')}
                                          <TableCell align="right">{pctOfDb(Number(mt?.no_tags_missing_ready ?? 0))}</TableCell>
                                          <TableCell align="right">
                                            <Button
                                              size="small"
                                              variant="text"
                                              onClick={() =>
                                                openTxReviewFromPipeline({
                                                  committed: cf,
                                                  statuses: [TAGGED_BY_ALL_RULES_STATUS]
                                                })
                                              }
                                              sx={{ p: 0, minWidth: 0, textTransform: 'none' }}
                                            >
                                              Open
                                            </Button>
                                          </TableCell>
                                        </TableRow>
                                        <TableRow sx={{ bgcolor: pipMissBg }}>
                                          <TableCell>All four missing</TableCell>
                                          <TableCell align="right">{Number(mt?.missing_all_four ?? 0).toLocaleString()}</TableCell>
                                          {pipeBar(Number(mt?.missing_all_four ?? 0), 'missing')}
                                          <TableCell align="right">{pctOfDb(Number(mt?.missing_all_four ?? 0))}</TableCell>
                                          <TableCell align="right">
                                            <Button
                                              size="small"
                                              variant="text"
                                              onClick={() =>
                                                openTxReviewFromPipeline({
                                                  committed: cf,
                                                  statuses: [
                                                    buildMissingDimsStatusFilterValue([
                                                      MISSING_DIM_CASH_FLOW,
                                                      'Supplier',
                                                      'Category',
                                                      'VAT Rate'
                                                    ])
                                                  ]
                                                })
                                              }
                                              sx={{ p: 0, minWidth: 0, textTransform: 'none' }}
                                            >
                                              Open
                                            </Button>
                                          </TableCell>
                                        </TableRow>
                                        {(
                                            [
                                            ['Cash flow', MISSING_DIM_CASH_FLOW],
                                            ['Supplier', 'Supplier'],
                                            ['Category', 'Category'],
                                            ['VAT Rate', 'VAT Rate']
                                          ] as const
                                        ).map(([nm, key]) => (
                                          <TableRow key={`${cf}-single-${nm}`} sx={{ bgcolor: pipMissBg }}>
                                            <TableCell>Missing {nm} only</TableCell>
                                            <TableCell align="right">
                                              {Number(mt?.missing_single_dimension?.[key] ?? 0).toLocaleString()}
                                            </TableCell>
                                            {pipeBar(Number(mt?.missing_single_dimension?.[key] ?? 0), 'missing')}
                                            <TableCell align="right">
                                              {pctOfDb(Number(mt?.missing_single_dimension?.[key] ?? 0))}
                                            </TableCell>
                                            <TableCell align="right">
                                              <Button
                                                size="small"
                                                variant="text"
                                                onClick={() =>
                                                  openTxReviewFromPipeline({
                                                    committed: cf,
                                                    statuses: [buildMissingDimsStatusFilterValue([key])]
                                                  })
                                                }
                                                sx={{ p: 0, minWidth: 0, textTransform: 'none' }}
                                              >
                                                Open
                                              </Button>
                                            </TableCell>
                                          </TableRow>
                                        ))}
                                        {(mt?.missing_permutations || []).map((combo) => (
                                          <TableRow key={`${cf}-perm-${combo.label}`} sx={{ bgcolor: pipMissBg }}>
                                            <TableCell>{combo.label}</TableCell>
                                            <TableCell align="right">{Number(combo.count || 0).toLocaleString()}</TableCell>
                                            {pipeBar(Number(combo.count || 0), 'missing')}
                                            <TableCell align="right">{pctOfDb(Number(combo.count || 0))}</TableCell>
                                            <TableCell align="right">
                                              <Button
                                                size="small"
                                                variant="text"
                                                onClick={() =>
                                                  openTxReviewFromPipeline({
                                                    committed: cf,
                                                    statuses: [
                                                      buildMissingDimsStatusFilterValue(parsePipelineMissingDimsLabel(combo.label))
                                                    ]
                                                  })
                                                }
                                                sx={{ p: 0, minWidth: 0, textTransform: 'none' }}
                                              >
                                                Open
                                              </Button>
                                            </TableCell>
                                          </TableRow>
                                        ))}
                                        <TableRow>
                                          <TableCell>Unclassified needs review</TableCell>
                                          <TableCell align="right">
                                            {Number(mt?.unclassified_needs_review ?? 0).toLocaleString()}
                                          </TableCell>
                                          {pipeBar(Number(mt?.unclassified_needs_review ?? 0))}
                                          <TableCell align="right">
                                            {pctOfDb(Number(mt?.unclassified_needs_review ?? 0))}
                                          </TableCell>
                                          <TableCell align="right">
                                            <Button
                                              size="small"
                                              variant="text"
                                              onClick={() =>
                                                openTxReviewFromPipeline({
                                                  committed: cf,
                                                  unclassifiedNeedsReviewOnly: true
                                                })
                                              }
                                              sx={{ p: 0, minWidth: 0, textTransform: 'none' }}
                                            >
                                              Open
                                            </Button>
                                          </TableCell>
                                        </TableRow>
                                      </TableBody>
                                    </Table>
                                  </Box>
                                </Stack>
                              );
                            };

                            return (
                              <>
                                <Accordion
                                  defaultExpanded={false}
                                  disableGutters
                                  sx={{ '&:before': { display: 'none' }, mb: 1 }}
                                >
                                  <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                                    <Typography sx={{ fontWeight: 700 }}>
                                      Committed — mirror ({Number(pd?.committed.subset_total ?? 0).toLocaleString()} TXs)
                                    </Typography>
                                  </AccordionSummary>
                                  <AccordionDetails>
                                    {renderDashboard('Committed', pd?.committed, 'committed')}
                                  </AccordionDetails>
                                </Accordion>
                                <Accordion defaultExpanded disableGutters sx={{ '&:before': { display: 'none' } }}>
                                  <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                                    <Typography sx={{ fontWeight: 700 }}>
                                      Uncommitted — detail ({Number(pd?.uncommitted.subset_total ?? 0).toLocaleString()} TXs)
                                    </Typography>
                                  </AccordionSummary>
                                  <AccordionDetails>
                                    {renderDashboard('Uncommitted', pd?.uncommitted, 'uncommitted')}
                                  </AccordionDetails>
                                </Accordion>
                              </>
                            );
                          })()
                        ) : (
                          <Typography variant="body2" color="text.secondary">
                            No pipeline data loaded yet.
                          </Typography>
                        )}
                      </CardContent>
                    </Card>
                  </Stack>
                </>
              ) : tabIndex === CLASSIFICATION_VISIBLE_TAB_KEYS.indexOf('supplier-vat-invoices') ? (
                <>
                  <Stack direction="row" spacing={0.75} alignItems="center" sx={{ mb: 2 }}>
                    <Typography variant="subtitle2" color="text.secondary">
                      VAT lookback guidance
                    </Typography>
                    <Tooltip
                      title={
                        <Typography variant="body2" sx={{ maxWidth: 420 }}>
                          Add suppliers one at a time to this list. For each, record how many months of VAT invoices
                          their customer portal keeps available (lookback), and the date you last downloaded them.
                          Warnings use that lookback so you can reclaim VAT now and still have time to fetch invoices
                          before they disappear from the portal.
                        </Typography>
                      }
                    >
                      <InfoOutlinedIcon fontSize="small" sx={{ color: 'text.secondary', cursor: 'help' }} />
                    </Tooltip>
                  </Stack>
                  <Stack
                    direction={{ xs: 'column', md: 'row' }}
                    spacing={1}
                    sx={{ mb: 1.25 }}
                    alignItems={{ md: 'center' }}
                    flexWrap="wrap"
                    useFlexGap
                  >
                    <Button
                      size="small"
                      variant="contained"
                      startIcon={<AddIcon />}
                      disabled={
                        !!busyAction ||
                        loading ||
                        applyingRules ||
                        addingSupplierVatRow ||
                        clearingAllSupplierVat ||
                        bulkDeletingSupplierVat
                      }
                      onClick={openSupplierVatAddDialog}
                    >
                      Add
                    </Button>
                    <Button
                      size="small"
                      variant="outlined"
                      color="error"
                      disabled={
                        supplierVatRows.length === 0 ||
                        !!busyAction ||
                        loading ||
                        applyingRules ||
                        addingSupplierVatRow ||
                        clearingAllSupplierVat ||
                        bulkDeletingSupplierVat
                      }
                      onClick={() => void clearAllSupplierVatRows()}
                    >
                      {clearingAllSupplierVat ? 'Clearing…' : 'Clear all rows'}
                    </Button>
                    <ClearableSearchTextField
                      size="small"
                      label="Text search"
                      placeholder="Supplier or reminder text…"
                      value={supplierVatSearch}
                      onChange={setSupplierVatSearch}
                      sx={{ minWidth: 240 }}
                    />
                  </Stack>
                  {supplierVatHasFilters ? (
                    <Stack
                      direction={{ xs: 'column', sm: 'row' }}
                      spacing={1}
                      sx={{ mb: 1.25 }}
                      alignItems={{ sm: 'center' }}
                      flexWrap="wrap"
                      useFlexGap
                    >
                      <Button
                        size="small"
                        variant="outlined"
                        disabled={!supplierVatHasFilters}
                        onClick={resetSupplierVatFilters}
                      >
                        Clear all filters
                      </Button>
                      <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap>
                        {supplierVatFilterPills.map((pill) => (
                          <Chip
                            key={`supplier-vat-filter-pill-${pill.key}`}
                            size="small"
                            variant="outlined"
                            label={pill.label}
                            onDelete={pill.onDelete}
                          />
                        ))}
                      </Stack>
                    </Stack>
                  ) : null}
                  {selectedSupplierVatIds.length > 0 ? (
                    <Stack
                      direction={{ xs: 'column', sm: 'row' }}
                      spacing={1}
                      alignItems={{ sm: 'center' }}
                      flexWrap="wrap"
                      useFlexGap
                      sx={{ mb: 1.25 }}
                    >
                      <Typography variant="body2" color="text.secondary">
                        {selectedSupplierVatIds.length} row(s) selected
                      </Typography>
                      <Button
                        size="small"
                        variant="outlined"
                        disabled={bulkDeletingSupplierVat || clearingAllSupplierVat}
                        onClick={() => setSelectedSupplierVatIds([])}
                      >
                        Clear selection
                      </Button>
                      <Button
                        size="small"
                        variant="contained"
                        color="error"
                        disabled={
                          !!busyAction ||
                          loading ||
                          applyingRules ||
                          bulkDeletingSupplierVat ||
                          clearingAllSupplierVat
                        }
                        onClick={() => void bulkDeleteSelectedSupplierVatRows()}
                      >
                        {bulkDeletingSupplierVat ? 'Deleting…' : 'Delete selected'}
                      </Button>
                    </Stack>
                  ) : null}
                  <TableContainer>
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell padding="checkbox" sx={{ width: 48 }}>
                            <Checkbox
                              size="small"
                              disabled={
                                supplierVatRowsFiltered.length === 0 ||
                                !!busyAction ||
                                loading ||
                                applyingRules ||
                                bulkDeletingSupplierVat ||
                                clearingAllSupplierVat
                              }
                              checked={supplierVatSelectAllCheckboxState.checked}
                              indeterminate={supplierVatSelectAllCheckboxState.indeterminate}
                              onChange={(e) => toggleSelectAllVisibleSupplierVat(e.target.checked)}
                              inputProps={{ 'aria-label': 'Select all VAT lookback rows in this view' }}
                            />
                          </TableCell>
                          <TableCell>Supplier</TableCell>
                          <TableCell>VAT lookback (months)</TableCell>
                          <TableCell>Last VAT invoices retrieved</TableCell>
                          <TableCell>Reminder</TableCell>
                          <TableCell align="right">Action</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {supplierVatRowsFiltered.length === 0 ? (
                          <TableRow>
                            <TableCell colSpan={6}>
                              {supplierVatRows.length === 0
                                ? 'No suppliers on the VAT lookback list yet. Click Add to add them one at a time.'
                                : 'No supplier VAT rows match the current filters.'}
                            </TableCell>
                          </TableRow>
                        ) : (
                          supplierVatRowsFiltered.map((row) => (
                            <TableRow
                              key={row.supplier_id}
                              selected={selectedSupplierVatIds.includes(row.supplier_id)}
                            >
                              <TableCell padding="checkbox">
                                <Checkbox
                                  size="small"
                                  checked={selectedSupplierVatIds.includes(row.supplier_id)}
                                  disabled={
                                    !!busyAction ||
                                    loading ||
                                    applyingRules ||
                                    bulkDeletingSupplierVat ||
                                    clearingAllSupplierVat ||
                                    removingSupplierVatId === row.supplier_id
                                  }
                                  onChange={(e) => toggleSupplierVatRowSelected(row.supplier_id, e.target.checked)}
                                  inputProps={{ 'aria-label': `Select ${row.name}` }}
                                />
                              </TableCell>
                              <TableCell>{row.name}</TableCell>
                              <TableCell>
                                <TextField
                                  size="small"
                                  type="number"
                                  inputProps={{ min: 1, max: 240 }}
                                  placeholder="—"
                                  value={row.lookbackInput}
                                  onChange={(e) => {
                                    const v = e.target.value;
                                    setSupplierVatRows((prev) =>
                                      prev.map((r) =>
                                        r.supplier_id === row.supplier_id ? { ...r, lookbackInput: v } : r
                                      )
                                    );
                                  }}
                                  sx={{ width: 120 }}
                                  disabled={!!busyAction || savingSupplierVatId === row.supplier_id}
                                />
                              </TableCell>
                              <TableCell>
                                <TextField
                                  size="small"
                                  type="date"
                                  InputLabelProps={{ shrink: true }}
                                  value={row.lastRetrievedInput}
                                  onChange={(e) => {
                                    const v = e.target.value;
                                    setSupplierVatRows((prev) =>
                                      prev.map((r) =>
                                        r.supplier_id === row.supplier_id ? { ...r, lastRetrievedInput: v } : r
                                      )
                                    );
                                  }}
                                  sx={{ minWidth: 160 }}
                                  disabled={!!busyAction || savingSupplierVatId === row.supplier_id}
                                />
                              </TableCell>
                              <TableCell>
                                {row.retrieval_warning ? (
                                  <Alert severity="warning" sx={{ py: 0 }}>
                                    {row.retrieval_warning}
                                  </Alert>
                                ) : (
                                  <Typography variant="caption" color="text.secondary">
                                    —
                                  </Typography>
                                )}
                              </TableCell>
                              <TableCell align="right">
                                <Stack direction="row" spacing={0.75} justifyContent="flex-end">
                                  <Button
                                    size="small"
                                    variant="outlined"
                                  disabled={
                                    !!busyAction ||
                                    loading ||
                                    applyingRules ||
                                    savingSupplierVatId === row.supplier_id ||
                                    removingSupplierVatId === row.supplier_id ||
                                    clearingAllSupplierVat ||
                                    bulkDeletingSupplierVat
                                  }
                                  onClick={() => void saveSupplierVatPolicy(row.supplier_id)}
                                  >
                                    {savingSupplierVatId === row.supplier_id ? 'Saving…' : 'Save'}
                                  </Button>
                                  <Button
                                    size="small"
                                    variant="outlined"
                                    color="error"
                                    disabled={
                                      !!busyAction ||
                                      loading ||
                                      applyingRules ||
                                      savingSupplierVatId === row.supplier_id ||
                                      removingSupplierVatId === row.supplier_id ||
                                      clearingAllSupplierVat ||
                                      bulkDeletingSupplierVat
                                    }
                                    onClick={() => void removeSupplierVatRow(row.supplier_id)}
                                  >
                                    {removingSupplierVatId === row.supplier_id ? 'Deleting…' : 'Delete'}
                                  </Button>
                                </Stack>
                              </TableCell>
                            </TableRow>
                          ))
                        )}
                      </TableBody>
                    </Table>
                  </TableContainer>
                  <Dialog
                    open={supplierVatAddOpen}
                    onClose={closeSupplierVatAddDialog}
                    maxWidth="sm"
                    fullWidth
                  >
                    <DialogTitle>Add supplier to VAT lookback list</DialogTitle>
                    <DialogContent dividers>
                      <Stack spacing={2} sx={{ pt: 0.5 }}>
                        {supplierVatAddError ? (
                          <Alert severity="error" onClose={() => setSupplierVatAddError('')}>
                            {supplierVatAddError}
                          </Alert>
                        ) : null}
                        <Typography variant="body2" color="text.secondary">
                          Choose an active supplier to track VAT invoice portal lookback and last-retrieved dates.
                        </Typography>
                        <Autocomplete
                          size="small"
                          options={suppliersAvailableForVatAdd}
                          getOptionLabel={(o) => o.name}
                          value={
                            vatAddSupplierId != null
                              ? suppliersAvailableForVatAdd.find((s) => s.id === vatAddSupplierId) ??
                                activeSuppliersPickerList.find((s) => s.id === vatAddSupplierId) ??
                                null
                              : null
                          }
                          onChange={(_e, opt) => setVatAddSupplierId(opt?.id ?? null)}
                          isOptionEqualToValue={(a, b) => a.id === b.id}
                          disabled={addingSupplierVatRow}
                          renderInput={(params) => (
                            <TextField
                              {...params}
                              autoFocus
                              label="Supplier"
                              placeholder="Search active suppliers…"
                              required
                            />
                          )}
                        />
                        {suppliersAvailableForVatAdd.length === 0 ? (
                          <Typography variant="caption" color="text.secondary">
                            Every active supplier is already on this list, or the supplier catalog has not loaded yet.
                          </Typography>
                        ) : null}
                      </Stack>
                    </DialogContent>
                    <DialogActions>
                      <Button onClick={closeSupplierVatAddDialog} disabled={addingSupplierVatRow}>
                        Cancel
                      </Button>
                      <Button
                        variant="contained"
                        disabled={vatAddSupplierId == null || addingSupplierVatRow}
                        onClick={() => void addSupplierVatRow()}
                      >
                        {addingSupplierVatRow ? 'Adding…' : 'Add'}
                      </Button>
                    </DialogActions>
                  </Dialog>
                </>
              ) : tabIndex === CLASSIFICATION_VISIBLE_TAB_KEYS.indexOf('rule-run-history') ? (
                <>
                  <Card variant="outlined">
                    <CardContent sx={{ py: 1.5 }}>
                      <Stack
                        direction={{ xs: 'column', md: 'row' }}
                        justifyContent="space-between"
                        alignItems={{ md: 'center' }}
                        spacing={1}
                        sx={{ mb: 1.25 }}
                      >
                        <Typography variant="h6">Rule run history</Typography>
                        <Typography variant="body2" color="text.secondary">
                          {`Recent runs: ${Number(ruleRunHistory?.meta?.returned_rows || ruleRunHistoryRows.length).toLocaleString()} of ${Number(ruleRunHistory?.meta?.total_rows || ruleRunHistoryRows.length).toLocaleString()} rows`}
                        </Typography>
                      </Stack>
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                        Uses per-transaction `last_rules_run_at` timestamps. Drill-down links open Tx Reviews filtered to
                        the exact transaction IDs in that bucket.
                      </Typography>
                      {ruleRunHistoryError ? (
                        <Alert severity="warning" sx={{ mb: 1.5 }}>
                          {ruleRunHistoryError}
                        </Alert>
                      ) : null}
                      {ruleRunHistogramChartRows.length > 0 ? (
                        <Box sx={{ width: '100%', height: 260, mb: 1.5 }}>
                          <ResponsiveContainer width="100%" height="100%">
                            <BarChart data={ruleRunHistogramChartRows}>
                              <CartesianGrid strokeDasharray="3 3" />
                              <XAxis dataKey="bucketLabel" interval={0} angle={-20} textAnchor="end" height={72} />
                              <YAxis allowDecimals={false} />
                              <RechartsTooltip
                                formatter={(value: any) => [Number(value || 0).toLocaleString(), 'Transactions']}
                                labelFormatter={(_label: any, payload: any) => {
                                  const row = payload?.[0]?.payload;
                                  if (!row) return '';
                                  return `${formatDate(row.bucket_start)} – ${formatDate(row.bucket_end)}`;
                                }}
                              />
                              <Legend />
                              <Bar dataKey="count" name="Rules run (tx count)" fill="#0288d1" />
                            </BarChart>
                          </ResponsiveContainer>
                        </Box>
                      ) : (
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                          No rule-run history yet for this scope.
                        </Typography>
                      )}
                      {ruleRunHistoryBuckets.length > 0 ? (
                        <TableContainer sx={{ mb: 1.5, maxWidth: '100%' }}>
                          <Table size="small">
                            <TableHead>
                              <TableRow>
                                <TableCell>Bucket start</TableCell>
                                <TableCell>Bucket end</TableCell>
                                <TableCell align="right">TX count</TableCell>
                                <TableCell align="right">Actions</TableCell>
                              </TableRow>
                            </TableHead>
                            <TableBody>
                              {ruleRunHistoryBuckets.map((bucket) => (
                                <TableRow key={`main-rrb-${bucket.bucket_start}`}>
                                  <TableCell>{formatDate(bucket.bucket_start)}</TableCell>
                                  <TableCell>{formatDate(bucket.bucket_end)}</TableCell>
                                  <TableCell align="right">{Number(bucket.count || 0).toLocaleString()}</TableCell>
                                  <TableCell align="right">
                                    <Button
                                      size="small"
                                      variant="outlined"
                                      onClick={() => openTxReviewForIds(bucket.tx_ids || [])}
                                      disabled={!Array.isArray(bucket.tx_ids) || bucket.tx_ids.length === 0}
                                    >
                                      Open in Tx Reviews
                                    </Button>
                                  </TableCell>
                                </TableRow>
                              ))}
                            </TableBody>
                          </Table>
                        </TableContainer>
                      ) : null}
                      <TableContainer sx={{ maxWidth: '100%' }}>
                        <Table size="small">
                          <TableHead>
                            <TableRow>
                              <TableCell>Rules run at</TableCell>
                              <TableCell>TX ID</TableCell>
                              <TableCell>Supplier</TableCell>
                              <TableCell>Description</TableCell>
                              <TableCell sx={{ width: 52, whiteSpace: 'nowrap' }}>A/C</TableCell>
                              <TableCell align="right">Amount</TableCell>
                              <TableCell align="right">Actions</TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {ruleRunHistoryRows.length === 0 ? (
                              <TableRow>
                                <TableCell colSpan={7}>No rows for the selected scope.</TableCell>
                              </TableRow>
                            ) : (
                              ruleRunHistoryRows.map((row) => (
                                <TableRow key={`main-rrh-${row.tx_id}-${row.last_rules_run_at}`}>
                                  <TableCell>{formatDate(row.last_rules_run_at || null)}</TableCell>
                                  <TableCell>{row.tx_id}</TableCell>
                                  <TableCell>{(row.supplier || '').trim() || '—'}</TableCell>
                                  <TableCell>{(row.description || '').trim() || '—'}</TableCell>
                                  <TableCell sx={{ width: 52, whiteSpace: 'nowrap' }}>
                                    {formatRevolutAccountLabel(row.account_source)}
                                  </TableCell>
                                  <TableCell align="right">
                                    {formatAmount(row.amount ?? null, row.currency ?? null)}
                                  </TableCell>
                                  <TableCell align="right">
                                    <Button size="small" variant="text" onClick={() => openTxReviewForIds([row.tx_id])}>
                                      Open TX
                                    </Button>
                                  </TableCell>
                                </TableRow>
                              ))
                            )}
                          </TableBody>
                        </Table>
                      </TableContainer>
                    </CardContent>
                  </Card>
                </>
              ) : tabIndex === CLASSIFICATION_VISIBLE_TAB_KEYS.indexOf('rerun-commentary') ? (
                <>
                  <Card variant="outlined">
                    <CardContent sx={{ py: 1.5 }}>
                      <Stack
                        direction="row"
                        alignItems="flex-start"
                        justifyContent="space-between"
                        spacing={1}
                        sx={{ mb: 1 }}
                      >
                        <Box>
                          <Typography variant="h6" component="div">
                            Rule re-run commentary
                          </Typography>
                          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                            Log for each full re-run on a row (append-only). Same content as the side drawer.
                          </Typography>
                        </Box>
                        <Button size="small" variant="outlined" onClick={() => setUnifiedRerunLogOpen(true)}>
                          Open expanded
                        </Button>
                      </Stack>
                      <Box sx={{ maxHeight: { xs: '55vh', md: 520 }, overflow: 'auto', pr: 0.5 }}>
                        {renderUnifiedRerunLogBlocksList()}
                      </Box>
                      <Stack direction="row" spacing={1} sx={{ mt: 1.5 }} flexWrap="wrap" useFlexGap>
                        <Button
                          variant="outlined"
                          size="small"
                          onClick={() => setUnifiedRerunLogBlocks([])}
                          disabled={unifiedRerunLogBlocks.length === 0}
                        >
                          Clear log
                        </Button>
                      </Stack>
                    </CardContent>
                  </Card>
                </>
              ) : tabIndex === CLASSIFICATION_VISIBLE_TAB_KEYS.indexOf('probabilistic') ? (
                <ProbabilisticTxTab
                  deepLinkTxId={(() => {
                    const raw = searchParams.get('tx_id');
                    if (!raw) return null;
                    const n = Number(raw);
                    return Number.isFinite(n) ? n : null;
                  })()}
                  onOpenTx={(txId) => openTxReviewForIds([txId])}
                />
              ) : null}
            </>
        </CardContent>
      </Card>
      {rulesQuickviewOpen || txJsonViewerOpen || unifiedRerunLogOpen ? (
        <GlobalStyles
          styles={(theme) => ({
            [theme.breakpoints.up('sm')]: {
              /* Right drawers (QV, JSON, re-run log) sit above dialog z-index; pad the flex container so dialogs center in the uncovered strip. */
              '.MuiDialog-root .MuiDialog-container': {
                paddingRight: `${txReviewRightPanelWidthPx}px`,
                boxSizing: 'border-box'
              }
            }
          })}
        />
      ) : null}
      <ProbabilisticRulesCompareModal
        open={probabilisticCompareRow != null}
        txId={probabilisticCompareRow?.id ?? 0}
        description={probabilisticCompareRow?.description || undefined}
        compare={probabilisticCompareRow?.probabilistic_compare || null}
        onClose={() => setProbabilisticCompareRow(null)}
        onAccepted={async () => {
          const tid = probabilisticCompareRow?.id;
          if (tid == null) return;
          if (canonicalHybridEnabled) {
            await refreshCanonicalRowsForTransactionIds(0, [tid]);
          }
          setUnifiedTxCategoryTagsFetchEpoch((n) => n + 1);
          setStackedToasts((prev) => [
            ...prev,
            {
              id: stackedToastIdRef.current++,
              code: nextClientToastCode(),
              severity: 'success',
              message: `Accepted probabilistic tags for TX #${tid}`
            }
          ]);
        }}
      />
      <Dialog
        open={selectAllFilteredPromptOpen}
        onClose={() => setSelectAllFilteredPromptOpen(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Select all filtered rows?</DialogTitle>
        <DialogContent dividers>
          <Typography variant="body2" color="text.secondary">
            You selected {pagedUnifiedRows.length.toLocaleString()} row(s) on this page. Do you also want to select all{' '}
            {filteredUnifiedRows.length.toLocaleString()} filtered row(s)?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSelectAllFilteredPromptOpen(false)}>
            Keep page only
          </Button>
          <Button
            variant="contained"
            onClick={() => {
              const totalFiltered = filteredUnifiedRows.length;
              const nextRpp = unifiedRowsPerPageToFitCount(totalFiltered);
              setSelectedUnifiedRowKeys((prev) =>
                Array.from(new Set([...prev, ...allFilteredUnifiedKeys]))
              );
              const needsLargerPage =
                isUnifiedRowsPerPageAll(nextRpp)
                  ? !isUnifiedRowsPerPageAll(unifiedRowsPerPage)
                  : !isUnifiedRowsPerPageAll(unifiedRowsPerPage) && nextRpp > unifiedRowsPerPage;
              if (needsLargerPage) {
                setUnifiedRowsPerPage(nextRpp);
              }
              setUnifiedPage(0);
              setSelectAllFilteredPromptOpen(false);
            }}
          >
            Select all {filteredUnifiedRows.length.toLocaleString()}
          </Button>
        </DialogActions>
      </Dialog>
      <Dialog
        open={merchantSupplierPreviewOpen}
        onClose={() => {
          if (merchantSupplierApplyBusy) return;
          setMerchantSupplierPreviewOpen(false);
        }}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Approve merchant aliases (uncommitted TXs)</DialogTitle>
        <DialogContent dividers>
          <Stack spacing={1.5}>
            <Typography variant="body2" color="text.secondary">
              {merchantSupplierPreviewSummary
                ? `Scanned ${Number(merchantSupplierPreviewSummary.txs_scanned || 0).toLocaleString()} uncommitted TX(s); ` +
                  `${Number(merchantSupplierPreviewSummary.merchants || 0).toLocaleString()} merchants; ` +
                  `${Number(merchantSupplierPreviewSummary.new_aliases || 0).toLocaleString()} new alias(es); ` +
                  `${Number(merchantSupplierPreviewSummary.existing_aliases || 0).toLocaleString()} already present; ` +
                  `~${Number(merchantSupplierPreviewSummary.new_suppliers_estimate || 0).toLocaleString()} new supplier(s).`
                : 'Review proposed merchant aliases before applying.'}
            </Typography>
            <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
              <Button
                size="small"
                variant="outlined"
                disabled={merchantSupplierApplyBusy}
                onClick={() =>
                  setMerchantSupplierSelectedKeys(
                    merchantSupplierPreviewRows.filter((r) => r.key && !r.already_exists).map((r) => r.key)
                  )
                }
              >
                Select all new
              </Button>
              <Button
                size="small"
                variant="outlined"
                disabled={merchantSupplierApplyBusy}
                onClick={() => setMerchantSupplierSelectedKeys([])}
              >
                Clear
              </Button>
              <Typography variant="body2" color="text.secondary" sx={{ alignSelf: 'center' }}>
                {merchantSupplierSelectedKeys.length.toLocaleString()} selected
              </Typography>
            </Stack>
            <TableContainer sx={{ maxHeight: 420 }}>
              <Table size="small" stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell padding="checkbox" />
                    <TableCell>Merchant pattern</TableCell>
                    <TableCell>Proposed canonical</TableCell>
                    <TableCell align="right">Uncommitted TX</TableCell>
                    <TableCell>Status</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {merchantSupplierPreviewRows.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={5}>
                        <Typography variant="body2" color="text.secondary">
                          No merchants found on uncommitted transactions.
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ) : (
                    merchantSupplierPreviewRows.map((row) => {
                      const checked = merchantSupplierSelectedKeys.includes(row.key);
                      return (
                        <TableRow key={row.key} hover selected={checked && !row.already_exists}>
                          <TableCell padding="checkbox">
                            <Checkbox
                              size="small"
                              disabled={row.already_exists || merchantSupplierApplyBusy}
                              checked={checked && !row.already_exists}
                              onChange={(e) => {
                                const on = e.target.checked;
                                setMerchantSupplierSelectedKeys((prev) => {
                                  if (on) return Array.from(new Set([...prev, row.key]));
                                  return prev.filter((k) => k !== row.key);
                                });
                              }}
                            />
                          </TableCell>
                          <TableCell>{row.display}</TableCell>
                          <TableCell>{row.canonical}</TableCell>
                          <TableCell align="right">{row.tx_count.toLocaleString()}</TableCell>
                          <TableCell>
                            {row.already_exists
                              ? 'Already exists'
                              : row.supplier_exists
                                ? 'New alias'
                                : 'New supplier + alias'}
                          </TableCell>
                        </TableRow>
                      );
                    })
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setMerchantSupplierPreviewOpen(false)}
            disabled={merchantSupplierApplyBusy}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            disabled={
              merchantSupplierApplyBusy ||
              merchantSupplierSelectedKeys.filter((k) =>
                merchantSupplierPreviewRows.some((r) => r.key === k && !r.already_exists)
              ).length === 0
            }
            onClick={() => void applyMerchantSupplierPreviewSelection()}
          >
            {merchantSupplierApplyBusy ? 'Applying…' : 'Approve & apply selected'}
          </Button>
        </DialogActions>
      </Dialog>
      <Dialog
        open={!!pendingBulkAppliedTagDelete}
        onClose={() => {
          if (unifiedBulkLock) return;
          setPendingBulkAppliedTagDelete(null);
        }}
        maxWidth="lg"
        fullWidth
      >
        <DialogTitle>Delete matching applied tags?</DialogTitle>
        <DialogContent dividers>
          <Typography variant="body2" color="text.secondary">
            {pendingBulkAppliedTagDelete
              ? `Remove ${pendingBulkAppliedTagDelete.deletions.length.toLocaleString()} tag(s) matching "${pendingBulkAppliedTagDelete.compositeLabel}" from the selected uncommitted rows? This matches the Applied tags column format (category @ VAT rate).`
              : ''}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPendingBulkAppliedTagDelete(null)} disabled={unifiedBulkLock}>
            Cancel
          </Button>
          <Button
            variant="contained"
            color="error"
            onClick={() => void confirmBulkAppliedTagDelete()}
            disabled={unifiedBulkLock}
          >
            {unifiedBulkBusy ? 'Deleting…' : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>
      <Dialog
        open={Boolean(txCommentModalRow)}
        onClose={closeTxCommentModal}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          {txCommentModalRow
            ? `Transaction comment — TX ${txCommentModalRow.id}`
            : 'Transaction comment'}
        </DialogTitle>
        <DialogContent dividers>
          {txCommentModalRow ? (
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
              {String(txCommentModalRow.description || '—')}
              {txCommentModalRow.amount != null
                ? ` · ${txCommentModalRow.amount} ${txCommentModalRow.currency || ''}`.trim()
                : ''}
            </Typography>
          ) : null}
          <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
            Internal: private notes (not for accountant export). External: safe for accountant-facing exports.
          </Typography>
          <TextField
            label="Comment (internal)"
            value={txCommentInternalDraft}
            onChange={(e) => setTxCommentInternalDraft(e.target.value)}
            fullWidth
            multiline
            minRows={2}
            size="small"
            sx={{ mb: 1.5 }}
            disabled={txCommentSaving}
            placeholder="Private notes…"
          />
          <TextField
            label="Comment (external)"
            value={txCommentExternalDraft}
            onChange={(e) => setTxCommentExternalDraft(e.target.value)}
            fullWidth
            multiline
            minRows={2}
            size="small"
            disabled={txCommentSaving}
            placeholder="Notes to share with accountant…"
          />
          {txCommentError ? (
            <Alert severity="error" sx={{ mt: 1.5 }}>
              {txCommentError}
            </Alert>
          ) : null}
        </DialogContent>
        <DialogActions sx={{ justifyContent: 'space-between', px: 3, py: 1.5 }}>
          <Button
            color="error"
            onClick={() => void clearTxCommentModal()}
            disabled={
              txCommentSaving ||
              (!String(txCommentInternalDraft || '').trim() &&
                !String(txCommentExternalDraft || '').trim() &&
                !unifiedRowHasComment(txCommentModalRow))
            }
          >
            Clear
          </Button>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button onClick={closeTxCommentModal} disabled={txCommentSaving}>
              Cancel
            </Button>
            <Button variant="contained" onClick={() => void saveTxCommentModal()} disabled={txCommentSaving}>
              {txCommentSaving ? 'Saving…' : 'Save'}
            </Button>
          </Box>
        </DialogActions>
      </Dialog>
      <Dialog
        open={deleteAndRetagDialogOpen}
        onClose={() => {
          if (removeAllTagsBusy || deleteAndRetagInFlight) return;
          setDeleteAndRetagDialogOpen(false);
        }}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Delete and re-tag?</DialogTitle>
        <DialogContent dividers>
          <Typography variant="body2" sx={{ mb: 1.5 }}>
            Are you sure you want to delete <strong>all tags</strong> (automated and manually applied) and re-run
            the rules to apply new tags?
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
            This removes Cashflow, Supplier, Category, VAT, and Special-case tags on the ledger in scope — including
            Non-Complete Revolut rows (declined/reverted) and previously committed rows (they become uncommitted) —
            then runs the full rule pipeline. This cannot be undone from the UI.
          </Typography>
          {deleteAndRetagPreviewBusy ? (
            <Typography variant="body2" color="text.secondary">
              Counting transactions with manual tags…
            </Typography>
          ) : null}
          {deleteAndRetagPreviewError ? (
            <Alert severity="warning" sx={{ mb: 1.5 }}>
              {deleteAndRetagPreviewError}
            </Alert>
          ) : null}
          {!deleteAndRetagPreviewBusy && deleteAndRetagPreview ? (
            <>
              <Typography variant="subtitle2" sx={{ mb: 1 }}>
                {deleteAndRetagPreview.manual_tx_count.toLocaleString()} transaction
                {deleteAndRetagPreview.manual_tx_count === 1 ? '' : 's'} currently have manual tags
              </Typography>
              {deleteAndRetagPreview.manual_tx_count === 0 ? (
                <Typography variant="body2" color="text.secondary">
                  No manually assigned tags found in wipe scope. Rule-applied tags will still be removed and
                  re-applied.
                </Typography>
              ) : (
                <TableContainer
                  sx={{
                    border: '1px solid',
                    borderColor: 'divider',
                    borderRadius: 1,
                    maxHeight: 360
                  }}
                >
                  <Table size="small" stickyHeader>
                    <TableHead>
                      <TableRow>
                        <TableCell>TX</TableCell>
                        <TableCell>Date</TableCell>
                        <TableCell>Amount</TableCell>
                        <TableCell>Description</TableCell>
                        <TableCell>Manual dims</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {deleteAndRetagPreview.transactions.map((row) => {
                        const dims = Array.isArray(row.manual_dimensions)
                          ? row.manual_dimensions.join(', ')
                          : '—';
                        const dateLabel = row.date
                          ? String(row.date).replace('T', ' ').slice(0, 16)
                          : '—';
                        const amt =
                          row.amount != null && Number.isFinite(Number(row.amount))
                            ? `${Number(row.amount).toLocaleString(undefined, {
                                minimumFractionDigits: 2,
                                maximumFractionDigits: 2
                              })}${row.currency ? ` ${row.currency}` : ''}`
                            : '—';
                        return (
                          <TableRow key={row.id}>
                            <TableCell sx={{ fontWeight: 600 }}>{row.id}</TableCell>
                            <TableCell sx={{ whiteSpace: 'nowrap' }}>{dateLabel}</TableCell>
                            <TableCell sx={{ whiteSpace: 'nowrap' }}>{amt}</TableCell>
                            <TableCell sx={{ maxWidth: 280 }}>
                              <Typography variant="body2" noWrap title={String(row.description || '')}>
                                {String(row.description || '').trim() || '—'}
                              </Typography>
                            </TableCell>
                            <TableCell sx={{ textTransform: 'capitalize' }}>{dims || '—'}</TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </>
          ) : null}
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setDeleteAndRetagDialogOpen(false)}
            disabled={removeAllTagsBusy || deleteAndRetagInFlight}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            color="error"
            onClick={() => void confirmDeleteAndRetag()}
            disabled={deleteAndRetagPreviewBusy || removeAllTagsBusy || deleteAndRetagInFlight}
          >
            Yes, delete all tags and re-run rules
          </Button>
        </DialogActions>
      </Dialog>
      <Dialog
        open={dedupeDisplayDupTagsDialogOpen}
        onClose={() => {
          if (dedupeDisplayDupTagsBusy) return;
          setDedupeDisplayDupTagsDialogOpen(false);
        }}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>De-dup applied tags (all transactions)?</DialogTitle>
        <DialogContent dividers>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
            This runs one server pass over legacy applied category tags. For every <strong>uncommitted</strong>{' '}
            transaction, chips that share the same display value are collapsed separately in each review column:
            Cash flow, Supplier, Category, and VAT (newest manual tag wins). Distinct values in the same column
            are left alone. Committed transactions are never changed.
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {user?.role === 'owner' || user?.role === 'admin'
              ? 'As an owner/admin, de-dup runs across the entire cloud ledger (all users), not only the filtered rows.'
              : 'This applies only to your own tagged transactions (entire ledger scope, not only the filtered rows).'}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDedupeDisplayDupTagsDialogOpen(false)} disabled={dedupeDisplayDupTagsBusy}>
            Cancel
          </Button>
          <Button
            variant="contained"
            color="warning"
            onClick={() => void runDedupeDisplayDuplicateTags()}
            disabled={dedupeDisplayDupTagsBusy}
          >
            {dedupeDisplayDupTagsBusy ? 'Running…' : 'Run De-dup'}
          </Button>
        </DialogActions>
      </Dialog>
      <Dialog
        open={!!pendingUnifiedTagDelete}
        onClose={() => {
          if (deletingUnifiedTagUid) return;
          setPendingUnifiedTagDelete(null);
        }}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Delete this tag?</DialogTitle>
        <DialogContent dividers>
          <Typography variant="body2" color="text.secondary">
            {pendingUnifiedTagDelete
              ? `Remove "${pendingUnifiedTagDelete.label}" from TX ${pendingUnifiedTagDelete.txId}?`
              : 'Remove this tag?'}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setPendingUnifiedTagDelete(null)}
            disabled={!!deletingUnifiedTagUid}
          >
            N
          </Button>
          <Button
            variant="contained"
            color="error"
            onClick={() => void confirmDeleteUnifiedTag()}
            disabled={!!deletingUnifiedTagUid}
          >
            {deletingUnifiedTagUid ? 'Deleting...' : 'Y'}
          </Button>
        </DialogActions>
      </Dialog>
      <Dialog
        open={unifiedMoreFiltersOpen}
        onClose={() => setUnifiedMoreFiltersOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>More filters</DialogTitle>
        <DialogContent dividers>
          <Stack spacing={2} sx={{ pt: 0.5 }}>
            {isUnifiedFilterInModal('text') ? (
              <ClearableSearchTextField
                size="small"
                label="Text search"
                value={unifiedTextFilter}
                onChange={setUnifiedTextFilter}
                fullWidth
              />
            ) : null}
            {isUnifiedFilterInModal('status') ? (
              <FormControl size="small" fullWidth>
                <Select
                  multiple
                  open={unifiedStatusMenuOpen}
                  onOpen={() => setUnifiedStatusMenuOpen(true)}
                  onClose={() => setUnifiedStatusMenuOpen(false)}
                  value={unifiedStatusFilter}
                  displayEmpty
                  MenuProps={TX_REVIEW_FILTER_SELECT_MENU_PROPS}
                  renderValue={(selected) =>
                    (selected as string[]).length > 0
                      ? (selected as string[]).map((value) => unifiedStatusFilterOptionLabel(value)).join(', ')
                      : 'All statuses'
                  }
                  onChange={(e) => {
                    setUnifiedStatusFilter(e.target.value as UnifiedRowStatus[]);
                    setUnifiedStatusMenuOpen(false);
                  }}
                >
                  {unifiedStatusOptions.map((statusValue) => (
                    <MenuItem key={`usfm-${statusValue}`} value={statusValue}>
                      {`${unifiedStatusFilterOptionLabel(statusValue)} (${formatCount(unifiedFilterOptionCounts.statuses.get(statusValue) || 0)})`}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            ) : null}
            {isUnifiedFilterInModal('committed') ? (
              <FormControl size="small" fullWidth>
                <InputLabel id="unified-committed-filter-modal-label">Committed</InputLabel>
                <Select
                  labelId="unified-committed-filter-modal-label"
                  label="Committed"
                  value={unifiedCommittedFilter}
                  MenuProps={TX_REVIEW_FILTER_SELECT_MENU_PROPS}
                  onChange={(e) =>
                    setUnifiedCommittedFilter(
                      String(e.target.value || 'all') as 'all' | 'committed' | 'uncommitted'
                    )
                  }
                >
                  <MenuItem value="all">All ({formatCount(unifiedFilterOptionCounts.committed + unifiedFilterOptionCounts.uncommitted)})</MenuItem>
                  <MenuItem value="committed">
                    Committed ({formatCount(unifiedFilterOptionCounts.committed)})
                  </MenuItem>
                  <MenuItem value="uncommitted">
                    Uncommitted ({formatCount(unifiedFilterOptionCounts.uncommitted)})
                  </MenuItem>
                </Select>
              </FormControl>
            ) : null}
            {isUnifiedFilterInModal('flagged') ? (
              <FormControl size="small" fullWidth>
                <InputLabel id="unified-flagged-filter-modal-label">Flagged</InputLabel>
                <Select
                  labelId="unified-flagged-filter-modal-label"
                  label="Flagged"
                  value={unifiedFlaggedFilter}
                  MenuProps={TX_REVIEW_FILTER_SELECT_MENU_PROPS}
                  onChange={(e) =>
                    setUnifiedFlaggedFilter(
                      String(e.target.value || 'all') === 'flagged' ? 'flagged' : 'all'
                    )
                  }
                >
                  <MenuItem value="all">All</MenuItem>
                  <MenuItem value="flagged">Flagged only</MenuItem>
                </Select>
              </FormControl>
            ) : null}
            {isUnifiedFilterInModal('heading') ? (
              <FormControl size="small" fullWidth>
                <Select
                  multiple
                  open={unifiedHeadingMenuOpen}
                  onOpen={() => setUnifiedHeadingMenuOpen(true)}
                  onClose={() => setUnifiedHeadingMenuOpen(false)}
                  value={unifiedCashFlowFilter}
                  displayEmpty
                  MenuProps={TX_REVIEW_FILTER_SELECT_MENU_PROPS}
                  renderValue={(selected) =>
                    (selected as string[]).length > 0
                      ? (selected as string[]).map((h) => formatCashflowInOutHeadingLabel(h)).join(', ')
                      : 'All cash flow'
                  }
                  onChange={(e) => {
                    setUnifiedCashFlowFilter(
                      sanitizeHeadingFilterValues(
                        Array.isArray(e.target.value) ? e.target.value : [e.target.value]
                      )
                    );
                    setUnifiedHeadingMenuOpen(false);
                  }}
                >
                  <MenuItem value="In">
                    {`In (${formatCount(unifiedFilterOptionCounts.headings.In)})`}
                  </MenuItem>
                  <MenuItem value="Out">
                    {`Out (${formatCount(unifiedFilterOptionCounts.headings.Out)})`}
                  </MenuItem>
                  <MenuItem value="N/A">
                    {`N/A (${formatCount(unifiedFilterOptionCounts.headings['N/A'] || 0)})`}
                  </MenuItem>
                </Select>
              </FormControl>
            ) : null}
            {isUnifiedFilterInModal('category') ? (
              <FormControl size="small" fullWidth>
                <Select
                  multiple
                  open={unifiedCategoryMenuOpen}
                  onOpen={() => setUnifiedCategoryMenuOpen(true)}
                  onClose={() => {
                    setUnifiedCategoryMenuOpen(false);
                    setUnifiedCategorySearchText('');
                  }}
                  value={unifiedCategoryFilter}
                  displayEmpty
                  MenuProps={{
                    ...TX_REVIEW_FILTER_SELECT_MENU_PROPS,
                    PaperProps: {
                      sx: {
                        minWidth: 680,
                        width: 'min(920px, calc(100vw - 24px))',
                        maxWidth: 'min(920px, calc(100vw - 24px))'
                      }
                    }
                  }}
                  renderValue={(selected) =>
                    (selected as string[]).length > 0 ? (selected as string[]).join(', ') : 'All categories'
                  }
                  onChange={() => undefined}
                >
                  <MenuItem
                    disableRipple
                    sx={{ cursor: 'default', '&:hover': { backgroundColor: 'transparent' } }}
                    onClick={(e) => e.stopPropagation()}
                    onKeyDown={(e) => e.stopPropagation()}
                  >
                    <ClearableSearchTextField
                      size="small"
                      hiddenLabel
                      placeholder="Search categories..."
                      value={unifiedCategorySearchText}
                      onChange={setUnifiedCategorySearchText}
                      onClick={(e) => e.stopPropagation()}
                      onKeyDown={(e) => e.stopPropagation()}
                      fullWidth
                      autoFocus
                      inputProps={{ 'aria-label': 'Search categories' }}
                    />
                  </MenuItem>
                  <MenuItem
                    disableRipple
                    sx={{
                      cursor: 'default',
                      '&:hover': { backgroundColor: 'transparent' },
                      alignItems: 'stretch',
                      maxWidth: 'none',
                      width: '100%',
                      py: 1
                    }}
                    onClick={(e) => e.stopPropagation()}
                    onKeyDown={(e) => e.stopPropagation()}
                  >
                    <Box
                      sx={{
                        display: 'flex',
                        flexDirection: 'row',
                        alignItems: 'stretch',
                        gap: 1,
                        width: '100%',
                        minWidth: 0
                      }}
                    >
                      <Box sx={{ flex: '1 1 0', minWidth: 0, overflow: 'hidden' }}>
                        <Typography variant="caption" sx={{ fontWeight: 700, display: 'block', mb: 0.75 }}>
                          Starred / Key
                        </Typography>
                        <Stack spacing={0.5}>
                          {unifiedCategoryMenuBuckets.starred.length === 0 ? (
                            <Typography variant="caption" color="text.secondary">
                              No starred matches.
                            </Typography>
                          ) : (
                            unifiedCategoryMenuBuckets.starred.map((c) => {
                              const selected = unifiedCategoryFilter.includes(c);
                              return (
                                <Button
                                  key={`ucm-star-${c}`}
                                  size="small"
                                  variant={selected ? 'contained' : 'text'}
                                  color={selected ? 'primary' : 'inherit'}
                                  startIcon={selected ? <StarIcon fontSize="small" /> : <StarBorderIcon fontSize="small" />}
                                  sx={{ justifyContent: 'flex-start', textTransform: 'none' }}
                                  onClick={(e) => {
                                    e.preventDefault();
                                    e.stopPropagation();
                                    toggleUnifiedCategoryFilterValue(c);
                                  }}
                                >
                                  {`${c} (${formatCount(unifiedFilterOptionCounts.categories.get(c))})`}
                                </Button>
                              );
                            })
                          )}
                        </Stack>
                      </Box>
                      <Divider orientation="vertical" flexItem sx={{ borderColor: 'divider', mx: 0 }} />
                      <Box sx={{ flex: '1 1 0', minWidth: 0, overflow: 'hidden' }}>
                        <Typography variant="caption" sx={{ fontWeight: 700, display: 'block', mb: 0.75 }}>
                          Other categories
                        </Typography>
                        <Stack spacing={0.5}>
                          {unifiedCategoryMenuBuckets.regular.length === 0 ? (
                            <Typography variant="caption" color="text.secondary">
                              No other matches.
                            </Typography>
                          ) : (
                            unifiedCategoryMenuBuckets.regular.map((c) => {
                              const selected = unifiedCategoryFilter.includes(c);
                              return (
                                <Button
                                  key={`ucm-other-${c}`}
                                  size="small"
                                  variant={selected ? 'contained' : 'text'}
                                  color={selected ? 'primary' : 'inherit'}
                                  sx={{ justifyContent: 'flex-start', textTransform: 'none' }}
                                  onClick={(e) => {
                                    e.preventDefault();
                                    e.stopPropagation();
                                    toggleUnifiedCategoryFilterValue(c);
                                  }}
                                >
                                  {`${c} (${formatCount(unifiedFilterOptionCounts.categories.get(c))})`}
                                </Button>
                              );
                            })
                          )}
                        </Stack>
                      </Box>
                    </Box>
                  </MenuItem>
                </Select>
              </FormControl>
            ) : null}
            {isUnifiedFilterInModal('vat') ? (
              <FormControl size="small" fullWidth>
                <Select
                  multiple
                  open={unifiedVatMenuOpen}
                  onOpen={() => setUnifiedVatMenuOpen(true)}
                  onClose={() => setUnifiedVatMenuOpen(false)}
                  value={unifiedVatFilter}
                  displayEmpty
                  MenuProps={TX_REVIEW_FILTER_SELECT_MENU_PROPS}
                  renderValue={(selected) =>
                    (selected as string[]).length > 0
                      ? (selected as string[])
                          .map((k) => formatVatFilterMenuLabel(k))
                          .join(', ')
                      : 'All VAT rates'
                  }
                  onChange={(e) => {
                    setUnifiedVatFilter(e.target.value as string[]);
                    setUnifiedVatMenuOpen(false);
                  }}
                >
                  {unifiedFilterOptions.vatRates.map((k) => (
                    <MenuItem key={`uvatm-${k}`} value={k}>
                      {`${formatVatFilterMenuLabel(k)} (${formatCount(unifiedFilterOptionCounts.vatRates.get(k) || 0)})`}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            ) : null}
            {isUnifiedFilterInModal('appliedTag') ? (
              <FormControl size="small" fullWidth>
                <Select
                  multiple
                  open={unifiedAppliedTagMenuOpen}
                  onOpen={() => setUnifiedAppliedTagMenuOpen(true)}
                  onClose={() => setUnifiedAppliedTagMenuOpen(false)}
                  value={unifiedAppliedTagFilter}
                  displayEmpty
                  MenuProps={{
                    ...TX_REVIEW_FILTER_SELECT_MENU_PROPS,
                    PaperProps: { sx: { maxHeight: 420, maxWidth: 'min(720px, calc(100vw - 32px))' } }
                  }}
                  renderValue={(selected) =>
                    (selected as string[]).length > 0
                      ? (selected as string[]).join(', ')
                      : 'All applied tags'
                  }
                  onChange={(e) => {
                    setUnifiedAppliedTagFilter(e.target.value as string[]);
                    setUnifiedAppliedTagMenuOpen(false);
                  }}
                >
                  {unifiedAppliedTagMenuLabels.length === 0 ? (
                    <MenuItem value="" disabled>
                      No applied-tag chips loaded yet (tags load per page)
                    </MenuItem>
                  ) : (
                    unifiedAppliedTagMenuLabels.map((lab) => (
                      <MenuItem key={`uatm-${lab}`} value={lab} title={lab}>
                        {`${lab} (${formatCount(unifiedFilterOptionCounts.appliedTags.get(lab) || 0)})`}
                      </MenuItem>
                    ))
                  )}
                </Select>
              </FormControl>
            ) : null}
            {isUnifiedFilterInModal('tagDuplicate') ? (
              <Stack spacing={1}>
                <FormControl size="small" fullWidth>
                  <InputLabel id="unified-tag-dup-filter-modal-label">Tag duplicates</InputLabel>
                  <Select
                    labelId="unified-tag-dup-filter-modal-label"
                    label="Tag duplicates"
                    value={unifiedTagDuplicateFilter}
                    MenuProps={TX_REVIEW_FILTER_SELECT_MENU_PROPS}
                    onChange={(e) =>
                      setUnifiedTagDuplicateFilter(
                        String(e.target.value || 'all') as 'all' | 'no_duplicates' | 'duplicates'
                      )
                    }
                  >
                    <MenuItem value="all">
                      All (
                      {formatCount(
                        unifiedFilterOptionCounts.tagDupNoDuplicate + unifiedFilterOptionCounts.tagDupDuplicate
                      )}
                      )
                    </MenuItem>
                    <MenuItem value="no_duplicates">
                      No duplicate tags ({formatCount(unifiedFilterOptionCounts.tagDupNoDuplicate)})
                    </MenuItem>
                    <MenuItem value="duplicates">
                      Duplicate tags ({formatCount(unifiedFilterOptionCounts.tagDupDuplicate)})
                    </MenuItem>
                  </Select>
                </FormControl>
                {unifiedTagDuplicateFilter === 'duplicates' ? (
                  <Button
                    variant="outlined"
                    color="warning"
                    onClick={() => {
                      setUnifiedMoreFiltersOpen(false);
                      setDedupeDisplayDupTagsDialogOpen(true);
                    }}
                    disabled={
                      loading ||
                      applyingRules ||
                      supplierBackfillBusy ||
                      reapplyUnconfirmedBusy ||
                      reapplyVatUnconfirmedBusy ||
                      reapplySpecialCaseUnconfirmedBusy ||
                      rerunAllRulesBusy ||
                      dedupeDisplayDupTagsBusy ||
                      removeAllTagsBusy
                    }
                  >
                    {dedupeDisplayDupTagsBusy ? 'De-duping…' : 'De-dup'}
                  </Button>
                ) : null}
              </Stack>
            ) : null}
            {isUnifiedFilterInModal('account') ? (
              <FormControl size="small" fullWidth>
                <InputLabel id="unified-account-filter-modal-label" shrink>
                  Bank account
                </InputLabel>
                <Select
                  labelId="unified-account-filter-modal-label"
                  label="Bank account"
                  multiple
                  open={unifiedAccountMenuOpen}
                  onOpen={() => setUnifiedAccountMenuOpen(true)}
                  onClose={() => setUnifiedAccountMenuOpen(false)}
                  value={unifiedAccountFilter}
                  displayEmpty
                  MenuProps={TX_REVIEW_FILTER_SELECT_MENU_PROPS}
                  renderValue={(selected) =>
                    (selected as string[]).length > 0
                      ? (selected as string[]).map((v) => formatRevolutAccountLabel(v)).join(', ')
                      : 'All bank accounts'
                  }
                  onChange={(e) => {
                    setUnifiedAccountFilter(e.target.value as string[]);
                    setUnifiedAccountMenuOpen(false);
                  }}
                >
                  {unifiedFilterOptions.accounts.map((a) => (
                    <MenuItem key={`uam-${a}`} value={a}>
                      {`${formatRevolutAccountLabel(a)} (${formatCount(
                        unifiedFilterOptionCounts.accounts.get(a)
                      )})`}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            ) : null}
            {isUnifiedFilterInModal('dateFrom') ? (
              <TextField
                size="small"
                type="date"
                label="Date from"
                InputLabelProps={{ shrink: true }}
                value={unifiedDateFrom}
                onChange={(e) => setUnifiedDateFrom(e.target.value)}
                fullWidth
              />
            ) : null}
            {isUnifiedFilterInModal('dateTo') ? (
              <TextField
                size="small"
                type="date"
                label="Date to"
                InputLabelProps={{ shrink: true }}
                value={unifiedDateTo}
                onChange={(e) => setUnifiedDateTo(e.target.value)}
                fullWidth
              />
            ) : null}
            {isUnifiedFilterInModal('supplier') ? (
              <FormControl size="small" fullWidth>
                <Select
                  multiple
                  open={unifiedSupplierMenuOpen}
                  onOpen={() => setUnifiedSupplierMenuOpen(true)}
                  onClose={() => setUnifiedSupplierMenuOpen(false)}
                  value={unifiedSupplierFilter}
                  displayEmpty
                  MenuProps={TX_REVIEW_FILTER_SELECT_MENU_PROPS}
                  renderValue={(selected) =>
                    (selected as string[]).length > 0 ? (selected as string[]).join(', ') : 'All suppliers'
                  }
                  onChange={(e) => {
                    setUnifiedSupplierFilter(e.target.value as string[]);
                    setUnifiedSupplierMenuOpen(false);
                  }}
                >
                  {unifiedSupplierOptions.map((s) => (
                    <MenuItem key={`usm-${s}`} value={s}>
                      {`${s} (${formatCount(unifiedFilterOptionCounts.suppliers.get(s))})`}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            ) : null}
            {isUnifiedFilterInModal('amountMin') ? (
              <TextField
                size="small"
                label="Amount min"
                value={unifiedAmountMin}
                onChange={(e) => setUnifiedAmountMin(e.target.value)}
                fullWidth
              />
            ) : null}
            {isUnifiedFilterInModal('amountMax') ? (
              <TextField
                size="small"
                label="Amount max"
                value={unifiedAmountMax}
                onChange={(e) => setUnifiedAmountMax(e.target.value)}
                fullWidth
              />
            ) : null}
            {isUnifiedFilterInModal('zeroNullAmount') ? (
              <FormControlLabel
                control={
                  <Checkbox
                    checked={unifiedZeroOrNullAmountOnly}
                    onChange={(e) => {
                      const on = e.target.checked;
                      setUnifiedZeroOrNullAmountOnly(on);
                      if (on) {
                        setUnifiedAmountMin('');
                        setUnifiedAmountMax('');
                      }
                    }}
                  />
                }
                label="Zero or null amount only"
              />
            ) : null}
            {isUnifiedFilterInModal('frequency') ? (
              <FormControl size="small" fullWidth>
                <Select
                  multiple
                  open={unifiedFrequencyMenuOpen}
                  onOpen={() => setUnifiedFrequencyMenuOpen(true)}
                  onClose={() => setUnifiedFrequencyMenuOpen(false)}
                  value={unifiedFrequencyFilter}
                  displayEmpty
                  MenuProps={TX_REVIEW_FILTER_SELECT_MENU_PROPS}
                  renderValue={(selected) =>
                    (selected as string[]).length > 0 ? (selected as string[]).join(', ') : 'All frequencies'
                  }
                  onChange={(e) => {
                    setUnifiedFrequencyFilter(e.target.value as string[]);
                    setUnifiedFrequencyMenuOpen(false);
                  }}
                >
                  {unifiedFilterOptions.frequencies.map((f) => (
                    <MenuItem key={`ufm-${f}`} value={f}>
                      {f}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            ) : null}
            {isUnifiedFilterInModal('currency') ? (
              <FormControl size="small" fullWidth>
                <Select
                  multiple
                  open={unifiedCurrencyMenuOpen}
                  onOpen={() => setUnifiedCurrencyMenuOpen(true)}
                  onClose={() => setUnifiedCurrencyMenuOpen(false)}
                  value={unifiedCurrencyFilter}
                  displayEmpty
                  MenuProps={TX_REVIEW_FILTER_SELECT_MENU_PROPS}
                  renderValue={(selected) =>
                    (selected as string[]).length > 0 ? (selected as string[]).join(', ') : 'All currencies'
                  }
                  onChange={(e) => {
                    setUnifiedCurrencyFilter(e.target.value as string[]);
                    setUnifiedCurrencyMenuOpen(false);
                  }}
                >
                  {unifiedFilterOptions.currencies.map((c) => (
                    <MenuItem key={`ufcurm-${c}`} value={c}>
                      {`${c} (${formatCount(unifiedFilterOptionCounts.currencies.get(c))})`}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            ) : null}
            {isUnifiedFilterInModal('revolutState') ? (
              <FormControl size="small" fullWidth>
                <Select
                  multiple
                  open={unifiedRevolutStateMenuOpen}
                  onOpen={() => setUnifiedRevolutStateMenuOpen(true)}
                  onClose={() => setUnifiedRevolutStateMenuOpen(false)}
                  value={unifiedRevolutStateFilter}
                  displayEmpty
                  MenuProps={TX_REVIEW_FILTER_SELECT_MENU_PROPS}
                  renderValue={(selected) =>
                    (selected as string[]).length > 0
                      ? (selected as string[]).map((v) => formatRevolutStateFilterLabel(v)).join(', ')
                      : 'All states'
                  }
                  onChange={(e) => {
                    setUnifiedRevolutStateFilter(e.target.value as string[]);
                    setUnifiedRevolutStateMenuOpen(false);
                  }}
                >
                  {unifiedFilterOptions.revolutStates.map((st) => (
                    <MenuItem key={`urs-modal-${st}`} value={st}>
                      {`${formatRevolutStateFilterLabel(st)} (${formatCount(
                        unifiedFilterOptionCounts.revolutStates.get(st) || 0
                      )})`}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            ) : null}
            {unifiedModalFilterCount === 0 ? (
              <Typography variant="body2" color="text.secondary">
                All filters are visible in the top row.
              </Typography>
            ) : null}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              resetUnifiedFilters();
              if (canonicalHybridEnabled) void refreshTransactionReviewCore();
            }}
          >
            Reset all filters
          </Button>
          <Button variant="contained" onClick={() => setUnifiedMoreFiltersOpen(false)}>
            Done
          </Button>
        </DialogActions>
      </Dialog>
      <Dialog
        open={supplierRuleOpen}
        onClose={() => {
          if (supplierRuleSaving) return;
          setSupplierRuleOpen(false);
          setSupplierRuleCriteriaMenuAnchorEl(null);
          setSupplierRuleOutputMenuAnchorEl(null);
          setSupplierRuleOutputSupplierEnabled(false);
          setSupplierRuleEditingId(null);
          setSupplierProposalAcceptId(null);
          setSupplierRuleName('');
          setSupplierRuleNotes('');
          setSupplierRulePattern('');
          setSupplierRuleDescriptionContains('');
          setSupplierRuleDescriptionTextMatchOperator('exact');
          setSupplierRuleReferenceContains('');
          setSupplierRuleReferenceTextMatchOperator('exact');
          setSupplierRuleCriteria([]);
          setSupplierRuleCanonicalName('');
          setSupplierRulePriority('100');
          setSupplierRuleTextMatchOperator('exact');
          setSupplierRuleAccountScope('both');
          setSupplierRuleIsActive(true);
          setSupplierRuleFormError('');
          setSupplierRuleFieldErrors({});
        }}
        maxWidth={false}
        fullWidth
        PaperProps={{ sx: RULES_RULE_EDITOR_DIALOG_PAPER_SX }}
      >
        <DialogTitle sx={{ pr: 1 }}>
          <Stack direction="row" alignItems="flex-start" justifyContent="space-between" spacing={1}>
            <Stack direction="row" spacing={1} alignItems="center" sx={{ minWidth: 0 }}>
              <Typography variant="h6" component="span">
                {supplierProposalAcceptId
                  ? 'Edit supplier proposal'
                  : supplierRuleEditingId
                    ? 'Edit supplier rule'
                    : 'Add supplier rule'}
              </Typography>
              <Tooltip title="Supplier rules map merchant/counterparty text patterns to a canonical supplier name.">
                <InfoOutlinedIcon fontSize="small" sx={{ color: 'text.secondary', cursor: 'help' }} />
              </Tooltip>
            </Stack>
            <IconButton
              aria-label="Close"
              size="small"
              onClick={() => {
                if (supplierRuleSaving) return;
                setSupplierRuleOpen(false);
                setSupplierRuleCriteriaMenuAnchorEl(null);
                setSupplierRuleOutputMenuAnchorEl(null);
                setSupplierRuleOutputSupplierEnabled(false);
                setSupplierRuleEditingId(null);
                setSupplierProposalAcceptId(null);
                setSupplierRuleName('');
                setSupplierRuleNotes('');
                setSupplierRulePattern('');
                setSupplierRuleDescriptionContains('');
                setSupplierRuleDescriptionTextMatchOperator('exact');
                setSupplierRuleReferenceContains('');
                setSupplierRuleReferenceTextMatchOperator('exact');
                setSupplierRuleCriteria([]);
                setSupplierRuleCanonicalName('');
                setSupplierRulePriority('100');
                setSupplierRuleTextMatchOperator('exact');
                setSupplierRuleAccountScope('both');
                setSupplierRuleIsActive(true);
                setSupplierRuleFormError('');
                setSupplierRuleFieldErrors({});
              }}
              disabled={supplierRuleSaving}
            >
              <CloseIcon />
            </IconButton>
          </Stack>
        </DialogTitle>
        <DialogContent dividers>
          <Stack spacing={2} sx={{ pt: 0.5 }}>
            {(() => {
              const pid = Math.trunc(Number(supplierProposalAcceptId ?? NaN));
              if (Number.isFinite(pid) && pid > 0) {
                return (
                  <Typography variant="body2" color="text.secondary">
                    Immutable ID:{' '}
                    <Box component="span" sx={{ fontWeight: 700, color: 'text.primary' }}>
                      {formatSupplierProposalPrefixedId(pid, 'proposed')}
                    </Box>{' '}
                    <Box component="span" sx={{ fontStyle: 'italic' }}>
                      (proposal)
                    </Box>
                  </Typography>
                );
              }
              const sid = Math.trunc(Number(supplierRuleEditingId));
              if (!Number.isFinite(sid) || sid <= 0) return null;
              return (
                <Typography variant="body2" color="text.secondary">
                  Immutable ID:{' '}
                  <Box component="span" sx={{ fontWeight: 700, color: 'text.primary' }}>
                    {formatPrefixedRuleId('supplier', sid)}
                  </Box>
                </Typography>
              );
            })()}
            <FormControl size="small" sx={{ width: { xs: '100%', sm: 220 } }}>
              <InputLabel id="supplier-rule-edit-type-label">Rule type</InputLabel>
              <Select labelId="supplier-rule-edit-type-label" label="Rule type" value="supplier">
                <MenuItem value="classification" disabled>
                  Classification
                </MenuItem>
                <MenuItem value="supplier">Supplier</MenuItem>
                <MenuItem value="vat" disabled>
                  VAT
                </MenuItem>
                <MenuItem value="specialCase" disabled>
                  Special Case
                </MenuItem>
              </Select>
            </FormControl>
            {supplierRuleFormError ? (
              <Alert severity="error" onClose={() => setSupplierRuleFormError('')}>
                {supplierRuleFormError}
              </Alert>
            ) : null}
            <Stack direction="row" justifyContent="space-between" alignItems="center" useFlexGap>
              <Typography variant="subtitle2" color="text.secondary" sx={{ fontWeight: 700 }}>
                Title
              </Typography>
              <Stack direction="row" spacing={1} alignItems="center">
                <Typography variant="caption" color="text.secondary">
                  Inactive
                </Typography>
                <Switch size="small" checked={supplierRuleIsActive} onChange={(_, checked) => setSupplierRuleIsActive(checked)} />
                <Typography variant="caption" color="text.secondary">
                  Active
                </Typography>
              </Stack>
            </Stack>
            <TextField
              label="Rule Name"
              value={supplierRuleName}
              onChange={(e) => {
                setSupplierRuleName(e.target.value);
                if (supplierRuleFieldErrors.rule_name) {
                  setSupplierRuleFieldErrors((prev) => ({ ...prev, rule_name: undefined }));
                }
              }}
              fullWidth
              size="small"
              required
              error={Boolean(supplierRuleFieldErrors.rule_name)}
              helperText={supplierRuleFieldErrors.rule_name || undefined}
              InputProps={buildClearableInputProps(
                supplierRuleName,
                () => setSupplierRuleName(''),
                {
                  endAdornment: (
                    <Tooltip title="Human-friendly name shown in the supplier rules table.">
                      <InfoOutlinedIcon fontSize="small" sx={{ color: 'text.secondary', cursor: 'help' }} />
                    </Tooltip>
                  )
                },
                'Clear rule name'
              )}
            />
            <Typography variant="subtitle2" color="text.secondary" sx={{ fontWeight: 700 }}>
              TX Matching Method(s)
            </Typography>
            <Stack spacing={1}>
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems={{ sm: 'center' }}>
                <FormControl size="small" sx={{ flex: 1, minWidth: 0 }} fullWidth>
                  <InputLabel id="supplier-rule-account-scope-label">Bank account</InputLabel>
                  <Select
                    labelId="supplier-rule-account-scope-label"
                    label="Bank account"
                    value={supplierRuleAccountScope}
                    onChange={(e) =>
                      setSupplierRuleAccountScope(normalizeRuleAccountScope(String(e.target.value || 'both')))
                    }
                  >
                    {RULE_ACCOUNT_SCOPE_OPTIONS.map((opt) => (
                      <MenuItem key={`supplier-rule-account-scope-${opt.value}`} value={opt.value}>
                        {opt.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <Button
                  size="small"
                  variant="outlined"
                  startIcon={<AddIcon />}
                  onClick={(e) => setSupplierRuleCriteriaMenuAnchorEl(e.currentTarget)}
                  sx={{ whiteSpace: 'nowrap', alignSelf: { xs: 'flex-start', sm: 'center' } }}
                >
                  Add Match Criteria
                </Button>
              </Stack>
              <Typography variant="caption" color="text.secondary">
                Any criterion can match (OR logic).
              </Typography>
              <Menu
                anchorEl={supplierRuleCriteriaMenuAnchorEl}
                open={Boolean(supplierRuleCriteriaMenuAnchorEl)}
                onClose={() => setSupplierRuleCriteriaMenuAnchorEl(null)}
              >
                {[
                  ...SUPPLIER_RULE_CRITERIA_KIND_OPTIONS.filter((opt) => opt.value !== 'merchant').map((opt) => ({ ...opt, disabled: false })),
                  { value: 'category' as const, label: 'Category', disabled: true },
                  { value: 'current_vat' as const, label: 'VAT Rate', disabled: true }
                ].map((opt) => (
                  <MenuItem
                    key={`supplier-criteria-add-${opt.value}`}
                    disabled={opt.disabled}
                    onClick={() => {
                      if (opt.disabled) return;
                      setSupplierRuleCriteria((prev) => [
                        ...prev,
                        defaultSupplierRuleCriterion(opt.value as SupplierRuleMatchCriterionDraft['kind'])
                      ]);
                      setSupplierRuleCriteriaMenuAnchorEl(null);
                    }}
                  >
                    {opt.label}
                  </MenuItem>
                ))}
              </Menu>
              {supplierRuleCriteria.map((criterion, idx) => (
                <Stack key={criterion.id} direction={{ xs: 'column', sm: 'row' }} spacing={1.25} alignItems="center">
                  <FormControl size="small" sx={{ width: { xs: '100%', sm: 280 } }}>
                    <InputLabel>{`Type ${idx + 1}`}</InputLabel>
                    <Select
                      label={`Type ${idx + 1}`}
                      value={criterion.kind}
                      onChange={(e) => {
                        const nk = e.target.value as SupplierRuleMatchCriterionDraft['kind'];
                        const seeded = defaultSupplierRuleCriterion(nk);
                        setSupplierRuleCriteria((prev) =>
                          prev.map((c) =>
                            c.id === criterion.id ? { ...c, kind: nk, value: seeded.value, operator: seeded.operator } : c
                          )
                        );
                      }}
                    >
                      {[
                        ...SUPPLIER_RULE_CRITERIA_KIND_OPTIONS.filter((opt) => opt.value !== 'merchant').map((opt) => ({ ...opt, disabled: false })),
                        { value: 'category' as const, label: 'Category', disabled: true },
                        { value: 'current_vat' as const, label: 'VAT Rate', disabled: true }
                      ].map((opt) => (
                        <MenuItem
                          key={`supplier-criteria-kind-${criterion.id}-${opt.value}`}
                          value={opt.value}
                          disabled={opt.disabled}
                        >
                          {opt.label}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                  {criterion.kind === 'canonical_supplier' ? (
                    <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems={{ sm: 'center' }} sx={{ flex: 1, minWidth: 0 }}>
                      <Autocomplete
                        options={rulesSupplierOptions}
                        value={criterion.value || null}
                        onChange={(_, value) => {
                          const next = String(value || '').trim();
                          setSupplierRuleCriteria((prev) => prev.map((c) => (c.id === criterion.id ? { ...c, value: next } : c)));
                          if (supplierRuleFieldErrors.pattern_raw) {
                            setSupplierRuleFieldErrors((prev) => ({ ...prev, pattern_raw: undefined }));
                          }
                        }}
                        fullWidth
                        size="small"
                        autoHighlight
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            label="Match value"
                            error={Boolean(supplierRuleFieldErrors.pattern_raw) && idx === 0}
                            helperText={idx === 0 ? supplierRuleFieldErrors.pattern_raw || undefined : undefined}
                          />
                        )}
                      />
                      <Button
                        variant="outlined"
                        size="small"
                        sx={{ whiteSpace: 'nowrap' }}
                        onClick={() => {
                          setAddCanonicalSupplierError('');
                          setAddCanonicalSupplierTarget('supplier');
                          setAddCanonicalSupplierName(criterion.value.trim());
                          setAddCanonicalSupplierOpen(true);
                        }}
                      >
                        New Supplier...
                      </Button>
                    </Stack>
                  ) : criterion.kind === 'amount' ? (
                    <TextField
                      size="small"
                      fullWidth
                      label="Exact amount"
                      type="text"
                      inputMode="decimal"
                      value={criterion.value}
                      onChange={(e) => {
                        const next = e.target.value;
                        setSupplierRuleCriteria((prev) =>
                          prev.map((c) =>
                            c.id === criterion.id ? { ...c, value: next, operator: 'exact' } : c
                          )
                        );
                        if (supplierRuleFieldErrors.pattern_raw) {
                          setSupplierRuleFieldErrors((prev) => ({ ...prev, pattern_raw: undefined }));
                        }
                      }}
                      helperText="Matches the transaction amount exactly (include sign, e.g. -1200.50)."
                      placeholder="-1200.50"
                    />
                  ) : criterion.kind === 'currency' ? (
                    <FormControl size="small" fullWidth sx={{ flex: 1, minWidth: 0 }}>
                      <InputLabel>Match value</InputLabel>
                      <Select
                        label="Match value"
                        value={(criterion.value || 'EUR').toUpperCase()}
                        onChange={(e) => {
                          const next = String(e.target.value || '').toUpperCase();
                          setSupplierRuleCriteria((prev) =>
                            prev.map((c) =>
                              c.id === criterion.id ? { ...c, value: next, operator: 'exact' } : c
                            )
                          );
                          if (supplierRuleFieldErrors.pattern_raw) {
                            setSupplierRuleFieldErrors((prev) => ({ ...prev, pattern_raw: undefined }));
                          }
                        }}
                      >
                        <MenuItem value="EUR">EUR</MenuItem>
                        <MenuItem value="GBP">GBP</MenuItem>
                        <MenuItem value="USD">USD</MenuItem>
                      </Select>
                    </FormControl>
                  ) : (
                    <>
                      <TextField
                        size="small"
                        fullWidth
                        label="Match value"
                        value={criterion.value}
                        error={Boolean(supplierRuleFieldErrors.pattern_raw) && idx === 0}
                        helperText={idx === 0 ? supplierRuleFieldErrors.pattern_raw || undefined : undefined}
                        onChange={(e) => {
                          const next = e.target.value;
                          setSupplierRuleCriteria((prev) =>
                            prev.map((c) => (c.id === criterion.id ? { ...c, value: next } : c))
                          );
                          if (supplierRuleFieldErrors.pattern_raw) {
                            setSupplierRuleFieldErrors((prev) => ({ ...prev, pattern_raw: undefined }));
                          }
                        }}
                        placeholder={
                          criterion.kind === 'merchant'
                            ? 'e.g. Stripe'
                            : criterion.kind === 'description'
                              ? 'e.g. Expenses app charges'
                              : 'e.g. INV-'
                        }
                      />
                      <FormControl size="small" sx={{ width: { xs: '100%', sm: 240 } }}>
                        <InputLabel>Text Match Mode</InputLabel>
                        <Select
                          label="Text Match Mode"
                          value={criterion.operator || 'contains'}
                          onChange={(e) =>
                            setSupplierRuleCriteria((prev) =>
                              prev.map((c) =>
                                c.id === criterion.id ? { ...c, operator: String(e.target.value || 'contains') } : c
                              )
                            )
                          }
                        >
                          {RULE_TEXT_MATCH_OPERATOR_OPTIONS.map((opt) => (
                            <MenuItem key={`supplier-criteria-op-${criterion.id}-${opt.value}`} value={opt.value}>
                              {opt.label}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </>
                  )}
                  <IconButton
                    size="small"
                    color="error"
                    aria-label="Remove match criterion"
                    onClick={() =>
                      setSupplierRuleCriteria((prev) => {
                        const next = prev.filter((c) => c.id !== criterion.id);
                        return next;
                      })
                    }
                  >
                    <DeleteOutlineIcon fontSize="small" />
                  </IconButton>
                </Stack>
              ))}
            </Stack>
            <Typography variant="subtitle2" color="text.secondary" sx={{ fontWeight: 700 }}>
              Output
            </Typography>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems={{ sm: 'center' }}>
              {supplierRuleOutputSupplierEnabled ? (
                <TextField
                  label="Priority"
                  value={supplierRulePriority}
                  onChange={(e) => {
                    setSupplierRulePriority(e.target.value);
                    if (supplierRuleFieldErrors.priority) {
                      setSupplierRuleFieldErrors((prev) => ({ ...prev, priority: undefined }));
                    }
                  }}
                  size="small"
                  type="number"
                  error={Boolean(supplierRuleFieldErrors.priority)}
                  helperText={supplierRuleFieldErrors.priority || 'Lower runs first'}
                  sx={{ width: { xs: '100%', sm: 140 } }}
                  InputProps={{
                    endAdornment: (
                      <Tooltip title="Lower numbers run first when multiple rules match.">
                        <InfoOutlinedIcon fontSize="small" sx={{ color: 'text.secondary', cursor: 'help' }} />
                      </Tooltip>
                    )
                  }}
                />
              ) : null}
              <Button
                size="small"
                variant="outlined"
                startIcon={<AddIcon />}
                onClick={(e) => setSupplierRuleOutputMenuAnchorEl(e.currentTarget)}
                sx={{ whiteSpace: 'nowrap', alignSelf: { xs: 'flex-start', sm: 'center' } }}
              >
                Add Output
              </Button>
            </Stack>
            <Menu
              anchorEl={supplierRuleOutputMenuAnchorEl}
              open={Boolean(supplierRuleOutputMenuAnchorEl)}
              onClose={() => setSupplierRuleOutputMenuAnchorEl(null)}
            >
              <MenuItem
                onClick={() => {
                  setSupplierRuleOutputSupplierEnabled(true);
                  setSupplierRuleOutputMenuAnchorEl(null);
                }}
              >
                Supplier
              </MenuItem>
              <MenuItem disabled>Category</MenuItem>
              <MenuItem disabled>VAT Rate</MenuItem>
            </Menu>
            {supplierRuleOutputSupplierEnabled ? (
              <>
                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems={{ xs: 'stretch', sm: 'center' }}>
                  <Autocomplete
                    options={rulesSupplierOptions}
                    value={supplierRuleCanonicalName || null}
                    onChange={(_, value) => {
                      setSupplierRuleCanonicalName(String(value || '').trim());
                      if (supplierRuleFieldErrors.canonical_name) {
                        setSupplierRuleFieldErrors((prev) => ({ ...prev, canonical_name: undefined }));
                      }
                    }}
                    fullWidth
                    autoHighlight
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Supplier"
                        size="small"
                        required
                        error={Boolean(supplierRuleFieldErrors.canonical_name)}
                        helperText={supplierRuleFieldErrors.canonical_name || undefined}
                        InputProps={{
                          ...params.InputProps,
                          endAdornment: (
                            <>
                              <Tooltip title="Supplier name that will be written to matching transactions.">
                                <InfoOutlinedIcon fontSize="small" sx={{ color: 'text.secondary', cursor: 'help', mr: 0.5 }} />
                              </Tooltip>
                              {params.InputProps.endAdornment}
                            </>
                          )
                        }}
                      />
                    )}
                  />
                  <Button
                    variant="outlined"
                    size="small"
                    sx={{ whiteSpace: 'nowrap' }}
                    onClick={() => {
                      setAddCanonicalSupplierError('');
                      setAddCanonicalSupplierTarget('supplier');
                      setAddCanonicalSupplierName(supplierRuleCanonicalName.trim());
                      setAddCanonicalSupplierOpen(true);
                    }}
                  >
                    New Supplier...
                  </Button>
                  <IconButton
                    size="small"
                    color="error"
                    aria-label="Remove output"
                    onClick={() => {
                      setSupplierRuleOutputSupplierEnabled(false);
                      setSupplierRuleCanonicalName('');
                    }}
                  >
                    <DeleteOutlineIcon fontSize="small" />
                  </IconButton>
                </Stack>
                {supplierRuleCanonicalName.trim() ? (
                  <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap sx={{ pl: 0.25 }}>
                    <Chip
                      size="small"
                      label={supplierRuleCanonicalName.trim()}
                      onDelete={() => {
                        setSupplierRuleCanonicalName('');
                        if (supplierRuleFieldErrors.canonical_name) {
                          setSupplierRuleFieldErrors((prev) => ({ ...prev, canonical_name: undefined }));
                        }
                      }}
                    />
                  </Stack>
                ) : null}
              </>
            ) : null}
            <Typography variant="subtitle2" color="text.secondary" sx={{ fontWeight: 700 }}>
              How this Rule Works
            </Typography>
            <Alert severity="info" icon={false}>
              <Typography variant="body2">{supplierRuleExplanationSentence}</Typography>
            </Alert>
            <Typography variant="subtitle2" color="text.secondary" sx={{ fontWeight: 700 }}>
              Notes
            </Typography>
            <TextField
              label="Notes"
              value={supplierRuleNotes}
              onChange={(e) => setSupplierRuleNotes(e.target.value)}
              fullWidth
              size="small"
              multiline
              minRows={4}
              placeholder="Optional notes for this supplier rule"
            />
          </Stack>
        </DialogContent>
        <DialogActions sx={{ flexWrap: 'wrap', gap: 1 }}>
          {supplierProposalAcceptId ? (
            <Button
              color="error"
              variant="outlined"
              sx={{ mr: 'auto' }}
              disabled={supplierRuleSaving || !!proposalBusyKey || addCanonicalSupplierBusy}
              onClick={() => void rejectSupplierProposalFromDialog()}
            >
              Reject
            </Button>
          ) : null}
          {supplierProposalAcceptId ? (
            <Button
              variant="outlined"
              disabled={supplierRuleSaving || !!proposalBusyKey || addCanonicalSupplierBusy}
              onClick={() => void submitSupplierRule('saveDraft')}
            >
              {proposalBusyKey?.startsWith('sp-save-') ? 'Saving…' : 'Save & close'}
            </Button>
          ) : null}
          <Button
            onClick={() => {
              if (supplierRuleSaving) return;
              setSupplierRuleOpen(false);
              setSupplierRuleCriteriaMenuAnchorEl(null);
              setSupplierRuleOutputMenuAnchorEl(null);
              setSupplierRuleOutputSupplierEnabled(false);
              setSupplierRuleEditingId(null);
              setSupplierProposalAcceptId(null);
              setSupplierRuleName('');
              setSupplierRuleNotes('');
              setSupplierRulePattern('');
              setSupplierRuleDescriptionContains('');
              setSupplierRuleDescriptionTextMatchOperator('exact');
              setSupplierRuleReferenceContains('');
              setSupplierRuleReferenceTextMatchOperator('exact');
              setSupplierRuleCriteria([]);
              setSupplierRuleCanonicalName('');
              setSupplierRulePriority('100');
              setSupplierRuleTextMatchOperator('exact');
              setSupplierRuleAccountScope('both');
              setSupplierRuleIsActive(true);
              setSupplierRuleFormError('');
              setSupplierRuleFieldErrors({});
            }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={() => void submitSupplierRule(supplierProposalAcceptId ? 'accept' : undefined)}
            disabled={supplierRuleSaving || !!proposalBusyKey || addCanonicalSupplierBusy}
          >
            {supplierRuleSaving
              ? 'Saving…'
              : supplierProposalAcceptId
                ? 'Accept edited proposal'
                : supplierRuleEditingId
                  ? 'Save changes'
                  : 'Add rule'}
          </Button>
        </DialogActions>
      </Dialog>
      <Dialog
        open={vatRuleOpen}
        onClose={() => {
          if (vatRuleSaving) return;
          setVatRuleOpen(false);
          setVatRuleDraft(null);
          setVatRuleMatchCriteriaMenuAnchorEl(null);
          setVatRuleOutputMenuAnchorEl(null);
          setVatRuleOutputVatEnabled(false);
          setVatRuleFieldErrors({});
          setVatRuleFormError('');
        }}
        maxWidth={false}
        fullWidth
        PaperProps={{ sx: RULES_RULE_EDITOR_DIALOG_PAPER_SX }}
      >
        <DialogTitle>
          <Stack direction="row" spacing={1} alignItems="center">
            <Typography variant="h6">{vatRuleDraft?.id ? 'Edit VAT rule' : 'Add VAT rule'}</Typography>
            <Tooltip title="VAT rules update VAT rate for matching tagged categories.">
              <InfoOutlinedIcon fontSize="small" sx={{ color: 'text.secondary', cursor: 'help' }} />
            </Tooltip>
          </Stack>
        </DialogTitle>
        <DialogContent dividers>
          {vatRuleDraft ? (
            <Stack spacing={2} sx={{ pt: 0.5 }}>
              {(() => {
                const vid = Math.trunc(Number(vatRuleDraft.id));
                if (!Number.isFinite(vid) || vid <= 0) return null;
                return (
                  <Typography variant="body2" color="text.secondary">
                    Immutable ID:{' '}
                    <Box component="span" sx={{ fontWeight: 700, color: 'text.primary' }}>
                      {formatPrefixedRuleId('vat', vid)}
                    </Box>
                  </Typography>
                );
              })()}
              <FormControl size="small" sx={{ width: { xs: '100%', sm: 220 } }}>
                <InputLabel id="vat-rule-edit-type-label">Rule type</InputLabel>
                <Select labelId="vat-rule-edit-type-label" label="Rule type" value="vat">
                  <MenuItem value="classification" disabled>
                    Classification
                  </MenuItem>
                  <MenuItem value="supplier" disabled>
                    Supplier
                  </MenuItem>
                  <MenuItem value="vat">VAT</MenuItem>
                  <MenuItem value="specialCase" disabled>
                    Special Case
                  </MenuItem>
                </Select>
              </FormControl>
              {vatRuleFormError ? (
                <Alert severity="error" onClose={() => setVatRuleFormError('')}>
                  {vatRuleFormError}
                </Alert>
              ) : null}
              <Stack direction="row" justifyContent="space-between" alignItems="center" useFlexGap>
                <Typography variant="subtitle2" color="text.secondary" sx={{ fontWeight: 700 }}>
                  Title
                </Typography>
                <Stack direction="row" spacing={1} alignItems="center">
                  <Typography variant="caption" color="text.secondary">
                    Inactive
                  </Typography>
                  <Switch
                    size="small"
                    checked={vatRuleDraft.is_active}
                    onChange={(_, checked) => setVatRuleDraft((d) => (d ? { ...d, is_active: checked } : d))}
                  />
                  <Typography variant="caption" color="text.secondary">
                    Active
                  </Typography>
                </Stack>
              </Stack>
              <TextField
                label="Rule name"
                value={vatRuleDraft.name}
                onChange={(e) =>
                  setVatRuleDraft((d) => (d ? { ...d, name: e.target.value } : d))
                }
                fullWidth
                size="small"
                InputProps={buildClearableInputProps(
                  vatRuleDraft.name,
                  () => setVatRuleDraft((d) => (d ? { ...d, name: '' } : d)),
                  {
                    endAdornment: (
                      <Tooltip title="Optional label to make this VAT rule easier to recognize.">
                        <InfoOutlinedIcon fontSize="small" sx={{ color: 'text.secondary', cursor: 'help' }} />
                      </Tooltip>
                    )
                  },
                  'Clear VAT rule name'
                )}
              />
              <Typography variant="subtitle2" color="text.secondary" sx={{ fontWeight: 700 }}>
                TX Matching Method(s)
              </Typography>
              <Stack spacing={1}>
                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems={{ sm: 'center' }}>
                  <FormControl size="small" sx={{ flex: 1, minWidth: 0 }} fullWidth>
                    <InputLabel id="vat-rule-account-label">Bank account</InputLabel>
                    <Select
                      labelId="vat-rule-account-label"
                      label="Bank account"
                      value={vatRuleDraft.account_scope}
                      onChange={(e) =>
                        setVatRuleDraft((d) =>
                          d ? { ...d, account_scope: normalizeRuleAccountScope(String(e.target.value || 'both')) } : d
                        )
                      }
                    >
                      {RULE_ACCOUNT_SCOPE_OPTIONS.map((opt) => (
                        <MenuItem key={`vat-rule-account-${opt.value}`} value={opt.value}>
                          {opt.label}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Stack>
                <Typography variant="caption" color="text.secondary">
                  Any criterion can match (OR logic).
                </Typography>
                {(() => {
                  const categoryOptions = categoriesSortedByName
                    .map((c) => ({ id: Number(c.id), name: String(c.name || '').trim() }))
                    .filter((c) => Number.isFinite(c.id) && c.id > 0 && c.name.length > 0);
                  const optionById = new Map<number, { id: number; name: string }>();
                  categoryOptions.forEach((c) => optionById.set(c.id, c));
                  const selectedIds = Array.from(
                    new Set(
                      (vatRuleDraft.category_ids || [])
                        .map((id) => Number(id))
                        .filter((id) => Number.isFinite(id) && id > 0)
                    )
                  );
                  const selectedOptions = selectedIds.map(
                    (id) => optionById.get(id) || { id, name: categoryNameById.get(id) || `Category #${id}` }
                  );
                  return (
                    <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems={{ xs: 'stretch', sm: 'center' }}>
                      <Autocomplete
                        multiple
                        size="small"
                        fullWidth
                        options={categoryOptions}
                        value={selectedOptions}
                        isOptionEqualToValue={(opt, val) => Number(opt.id) === Number(val.id)}
                        getOptionLabel={(opt) => String(opt?.name || '')}
                        onChange={(_, next) => {
                          const nextIds = Array.from(
                            new Set(
                              (next || [])
                                .map((item) => Number(item?.id))
                                .filter((id) => Number.isFinite(id) && id > 0)
                            )
                          );
                          setVatRuleDraft((d) => (d ? { ...d, category_ids: nextIds } : d));
                          setVatRuleFieldErrors((prev) => ({ ...prev, category_ids: undefined }));
                        }}
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            label="Categories"
                            error={Boolean(vatRuleFieldErrors.category_ids)}
                            helperText={vatRuleFieldErrors.category_ids || 'Select one or more categories.'}
                          />
                        )}
                      />
                      <Button
                        variant="outlined"
                        size="small"
                        sx={{ whiteSpace: 'nowrap' }}
                        onClick={() => openCategoryCrudCreate('ruleEdit')}
                      >
                        New Category...
                      </Button>
                    </Stack>
                  );
                })()}
              </Stack>
              <Typography variant="subtitle2" color="text.secondary" sx={{ fontWeight: 700 }}>
                Output
              </Typography>
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems={{ sm: 'center' }}>
                {vatRuleOutputVatEnabled ? (
                  <TextField
                    label="Priority"
                    value={vatRuleDraft.priority}
                    onChange={(e) =>
                      setVatRuleDraft((d) =>
                        d ? { ...d, priority: Number.isFinite(Number(e.target.value)) ? Number(e.target.value) : 100 } : d
                      )
                    }
                    onBlur={() => setVatRuleFieldErrors((prev) => ({ ...prev, priority: undefined }))}
                    size="small"
                    type="number"
                    error={Boolean(vatRuleFieldErrors.priority)}
                    helperText={vatRuleFieldErrors.priority || 'Lower runs first'}
                    sx={{ width: { xs: '100%', sm: 140 } }}
                    InputProps={{
                      endAdornment: (
                        <Tooltip title="Lower numbers run first when multiple VAT rules can apply.">
                          <InfoOutlinedIcon fontSize="small" sx={{ color: 'text.secondary', cursor: 'help' }} />
                        </Tooltip>
                      )
                    }}
                  />
                ) : null}
                <Button
                  size="small"
                  variant="outlined"
                  startIcon={<AddIcon />}
                  onClick={(e) => setVatRuleOutputMenuAnchorEl(e.currentTarget)}
                  sx={{ whiteSpace: 'nowrap', alignSelf: { xs: 'flex-start', sm: 'center' } }}
                >
                  Add Output
                </Button>
              </Stack>
              <Menu
                anchorEl={vatRuleOutputMenuAnchorEl}
                open={Boolean(vatRuleOutputMenuAnchorEl)}
                onClose={() => setVatRuleOutputMenuAnchorEl(null)}
              >
                <MenuItem disabled>Supplier</MenuItem>
                <MenuItem disabled>Category</MenuItem>
                <MenuItem
                  onClick={() => {
                    setVatRuleOutputVatEnabled(true);
                    setVatRuleOutputMenuAnchorEl(null);
                  }}
                >
                  VAT Rate
                </MenuItem>
              </Menu>
              {vatRuleOutputVatEnabled ? (
                <>
                  <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems={{ sm: 'center' }}>
                    <FormControl size="small" fullWidth error={Boolean(vatRuleFieldErrors.vat_rate)}>
                      <InputLabel id="vat-rule-rate-label">VAT rate</InputLabel>
                      <Select
                        labelId="vat-rule-rate-label"
                        label="VAT rate"
                        value={canonicalizeVatRate(vatRuleDraft.vat_rate)}
                        onChange={(e) => {
                          setVatRuleDraft((d) => (d ? { ...d, vat_rate: String(e.target.value || '') } : d));
                          setVatRuleFieldErrors((prev) => ({ ...prev, vat_rate: undefined }));
                        }}
                      >
                        <MenuItem value="" disabled>
                          Select VAT rate
                        </MenuItem>
                        {vatRatePickOptions.map((rate) => (
                          <MenuItem key={`vat-rule-rate-opt-${rate.label}`} value={rate.value}>
                            {rate.label}
                          </MenuItem>
                        ))}
                      </Select>
                      {vatRuleFieldErrors.vat_rate ? (
                        <FormHelperText>{vatRuleFieldErrors.vat_rate}</FormHelperText>
                      ) : null}
                    </FormControl>
                    <Tooltip title="VAT rates come from the statutory schedule.">
                      <span>
                        <Button variant="outlined" size="small" disabled sx={{ whiteSpace: 'nowrap' }}>
                          New VAT...
                        </Button>
                      </span>
                    </Tooltip>
                    <IconButton
                      size="small"
                      color="error"
                      aria-label="Remove output"
                      onClick={() => {
                        setVatRuleOutputVatEnabled(false);
                        setVatRuleDraft((d) => (d ? { ...d, vat_rate: '' } : d));
                        setVatRuleFieldErrors((prev) => ({ ...prev, vat_rate: undefined }));
                      }}
                    >
                      <DeleteOutlineIcon fontSize="small" />
                    </IconButton>
                  </Stack>
                </>
              ) : null}
              <Typography variant="caption" color="text.secondary">
                VAT rates are loaded from the Irish VAT statutory schedule table.
              </Typography>
              <Typography variant="subtitle2" color="text.secondary" sx={{ fontWeight: 700 }}>
                How this Rule Works
              </Typography>
              <Alert severity="info" icon={false}>
                <Typography variant="body2">{vatRuleExplanationSentence}</Typography>
              </Alert>
              <Typography variant="subtitle2" color="text.secondary" sx={{ fontWeight: 700 }}>
                Notes
              </Typography>
              <Typography variant="body2" color="text.secondary">
                VAT rules do not currently persist notes. Use the rule name for any short context.
              </Typography>
            </Stack>
          ) : null}
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              if (vatRuleSaving) return;
              setVatRuleOpen(false);
              setVatRuleDraft(null);
              setVatRuleMatchCriteriaMenuAnchorEl(null);
              setVatRuleOutputMenuAnchorEl(null);
              setVatRuleOutputVatEnabled(false);
              setVatRuleFieldErrors({});
              setVatRuleFormError('');
            }}
          >
            Cancel
          </Button>
          <Button variant="contained" onClick={() => void submitVatRule()} disabled={vatRuleSaving}>
            {vatRuleSaving ? 'Saving…' : vatRuleDraft?.id ? 'Save changes' : 'Add VAT rule'}
          </Button>
        </DialogActions>
      </Dialog>
      <Dialog
        open={specialCaseRuleOpen}
        onClose={() => {
          if (specialCaseRuleSaving) return;
          setSpecialCaseRuleMatchHelpAnchorEl(null);
          setSpecialCaseRuleCriteriaMenuAnchorEl(null);
          setSpecialCaseRuleOutputMenuAnchorEl(null);
          setSpecialCaseRuleOutputVatEnabled(false);
          setSpecialCaseRuleOpen(false);
          setSpecialCaseRuleDraft(null);
        }}
        maxWidth={false}
        fullWidth
        PaperProps={{ sx: RULES_RULE_EDITOR_DIALOG_PAPER_SX }}
      >
        <DialogTitle>
          <Stack direction="row" spacing={1} alignItems="center" justifyContent="space-between">
            <Typography variant="h6">
              {specialCaseRuleDraft?.id ? 'Edit Special Case rule' : 'Add Special Case rule'}
            </Typography>
            <Tooltip title="How this Rule Works">
              <IconButton
                size="small"
                color="info"
                aria-label="How this rule matches (one transaction)"
                aria-expanded={Boolean(specialCaseRuleMatchHelpAnchorEl)}
                aria-haspopup="dialog"
                onClick={(e) => {
                  setSpecialCaseRuleMatchHelpAnchorEl((prev) =>
                    prev ? null : (e.currentTarget as HTMLButtonElement)
                  );
                }}
              >
                <InfoOutlinedIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </Stack>
        </DialogTitle>
        <DialogContent dividers>
          {specialCaseRuleDraft ? (
            <Stack spacing={2} sx={{ pt: 0.5 }}>
              {(() => {
                const scid = Math.trunc(Number(specialCaseRuleDraft.id));
                if (!Number.isFinite(scid) || scid <= 0) return null;
                return (
                  <Typography variant="body2" color="text.secondary">
                    Immutable ID:{' '}
                    <Box component="span" sx={{ fontWeight: 700, color: 'text.primary' }}>
                      {formatPrefixedRuleId('specialCase', scid)}
                    </Box>
                  </Typography>
                );
              })()}
              <FormControl size="small" sx={{ width: { xs: '100%', sm: 220 } }}>
                <InputLabel id="special-case-rule-edit-type-label">Rule type</InputLabel>
                <Select labelId="special-case-rule-edit-type-label" label="Rule type" value="specialCase">
                  <MenuItem value="classification" disabled>
                    Classification
                  </MenuItem>
                  <MenuItem value="supplier" disabled>
                    Supplier
                  </MenuItem>
                  <MenuItem value="vat" disabled>
                    VAT
                  </MenuItem>
                  <MenuItem value="specialCase">Special Case</MenuItem>
                </Select>
              </FormControl>
              <Popover
                open={Boolean(specialCaseRuleMatchHelpAnchorEl)}
                anchorEl={specialCaseRuleMatchHelpAnchorEl}
                onClose={() => setSpecialCaseRuleMatchHelpAnchorEl(null)}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
                transformOrigin={{ vertical: 'top', horizontal: 'left' }}
                slotProps={{ paper: { sx: { maxWidth: 440 } } }}
              >
                <Box sx={{ p: 2 }}>
                  <Typography variant="body2" component="div" sx={{ fontWeight: 600, mb: 0.5 }}>
                    How this rule matches (one transaction)
                  </Typography>
                  <Typography variant="body2" component="div" sx={{ mb: 0.75 }}>
                    <strong>OR</strong> — any match criterion row can match. Empty rows are ignored.
                  </Typography>
                  <Typography variant="body2" component="div" sx={{ mb: 0.75 }}>
                    Bank account scope is applied first (if not “Both”), then criteria rows are evaluated with OR logic.
                  </Typography>
                  <Typography variant="body2" component="div" sx={{ mb: 0.75 }}>
                    <strong>Contains / Does not contain</strong> — description and reference matchers apply to those
                    fields separately. The merchant/counterparty matcher applies to resolved supplier text.{' '}
                    <strong>Is exactly</strong> compares each field on its own.
                  </Typography>
                  <Typography variant="body2" component="div" color="text.secondary">
                    Special case rules adjust VAT on the existing category tag. Across many rules, lower priority
                    numbers run first.
                  </Typography>
                </Box>
              </Popover>
              <Stack direction="row" justifyContent="space-between" alignItems="center" useFlexGap>
                <Typography variant="subtitle2" color="text.secondary" sx={{ fontWeight: 700 }}>
                  Title
                </Typography>
                <Stack direction="row" spacing={1} alignItems="center">
                  <Typography variant="caption" color="text.secondary">
                    Inactive
                  </Typography>
                  <Switch
                    size="small"
                    checked={specialCaseRuleDraft.is_active}
                    onChange={(_, checked) => setSpecialCaseRuleDraft((d) => (d ? { ...d, is_active: checked } : d))}
                  />
                  <Typography variant="caption" color="text.secondary">
                    Active
                  </Typography>
                </Stack>
              </Stack>
              <TextField
                label="Rule name"
                size="small"
                fullWidth
                required
                value={specialCaseRuleDraft.name}
                onChange={(e) => setSpecialCaseRuleDraft((d) => (d ? { ...d, name: e.target.value } : d))}
                InputProps={buildClearableInputProps(
                  specialCaseRuleDraft.name,
                  () => setSpecialCaseRuleDraft((d) => (d ? { ...d, name: '' } : d)),
                  undefined,
                  'Clear rule name'
                )}
              />
              <Typography variant="subtitle2" color="text.secondary" sx={{ fontWeight: 700, pt: 0.5 }}>
                TX Matching Method(s)
              </Typography>
              <Stack spacing={1}>
                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems={{ sm: 'center' }}>
                  <FormControl size="small" sx={{ flex: 1, minWidth: 0 }} fullWidth>
                    <InputLabel id="special-case-rule-edit-account-scope-label">Bank account</InputLabel>
                    <Select
                      labelId="special-case-rule-edit-account-scope-label"
                      label="Bank account"
                      value={specialCaseRuleDraft.account_scope}
                      onChange={(e) =>
                        setSpecialCaseRuleDraft((d) =>
                          d ? { ...d, account_scope: normalizeRuleAccountScope(String(e.target.value || 'both')) } : d
                        )
                      }
                    >
                      {RULE_ACCOUNT_SCOPE_OPTIONS.map((opt) => (
                        <MenuItem key={`special-case-rule-account-${opt.value}`} value={opt.value}>
                          {opt.label}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                  <Button
                    size="small"
                    variant="outlined"
                    startIcon={<AddIcon />}
                    onClick={(e) => setSpecialCaseRuleCriteriaMenuAnchorEl(e.currentTarget)}
                    sx={{ whiteSpace: 'nowrap', alignSelf: { xs: 'flex-start', sm: 'center' } }}
                  >
                    Add Match Criteria
                  </Button>
                </Stack>
                <Typography variant="caption" color="text.secondary">
                  Any criterion can match (OR logic).
                </Typography>
                <Menu
                  anchorEl={specialCaseRuleCriteriaMenuAnchorEl}
                  open={Boolean(specialCaseRuleCriteriaMenuAnchorEl)}
                  onClose={() => setSpecialCaseRuleCriteriaMenuAnchorEl(null)}
                >
                  {SPECIAL_CASE_MATCH_CRITERIA_KIND_OPTIONS.map((opt) => (
                    <MenuItem
                      key={`special-case-criteria-add-${opt.value}`}
                      onClick={() => {
                        setSpecialCaseRuleDraft((d) =>
                          d ? { ...d, match_criteria: [...d.match_criteria, defaultSpecialCaseMatchCriterion(opt.value)] } : d
                        );
                        setSpecialCaseRuleCriteriaMenuAnchorEl(null);
                      }}
                    >
                      {opt.label}
                    </MenuItem>
                  ))}
                </Menu>
                {specialCaseRuleDraft.match_criteria.map((criterion, idx) => {
                  const isText =
                    criterion.kind === 'merchant' ||
                    criterion.kind === 'description' ||
                    criterion.kind === 'reference';
                  return (
                    <Stack key={criterion.id} direction={{ xs: 'column', sm: 'row' }} spacing={1.25} alignItems="center">
                      <FormControl size="small" sx={{ width: { xs: '100%', sm: 290 } }}>
                        <InputLabel>{`Type ${idx + 1}`}</InputLabel>
                        <Select
                          label={`Type ${idx + 1}`}
                          value={criterion.kind}
                          onChange={(e) =>
                            setSpecialCaseRuleDraft((d) =>
                              d
                                ? {
                                    ...d,
                                    match_criteria: d.match_criteria.map((c) => {
                                      if (c.id !== criterion.id) return c;
                                      const nk = e.target.value as SpecialCaseMatchCriterionDraft['kind'];
                                      const defaultOp =
                                        nk === 'amount' ||
                                        nk === 'currency' ||
                                        nk === 'category' ||
                                        nk === 'current_vat'
                                          ? 'exact'
                                          : 'contains';
                                      return {
                                        ...c,
                                        kind: nk,
                                        value: '',
                                        operator: defaultOp
                                      };
                                    })
                                  }
                                : d
                            )
                          }
                        >
                          {SPECIAL_CASE_MATCH_CRITERIA_KIND_OPTIONS.map((opt) => (
                            <MenuItem key={`special-case-criteria-kind-${criterion.id}-${opt.value}`} value={opt.value}>
                              {opt.label}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                      {criterion.kind === 'canonical_supplier' ? (
                        <Stack
                          direction={{ xs: 'column', sm: 'row' }}
                          spacing={1}
                          alignItems={{ sm: 'center' }}
                          sx={{ flex: 1, minWidth: 0 }}
                        >
                          <Autocomplete
                            options={rulesSupplierOptions}
                            value={criterion.value || null}
                            onChange={(_, value) =>
                              setSpecialCaseRuleDraft((d) =>
                                d
                                  ? {
                                      ...d,
                                      match_criteria: d.match_criteria.map((c) =>
                                        c.id === criterion.id ? { ...c, value: String(value || '').trim() } : c
                                      )
                                    }
                                  : d
                              )
                            }
                            fullWidth
                            size="small"
                            autoHighlight
                            renderInput={(params) => <TextField {...params} label="Match value" />}
                          />
                          <Button
                            variant="outlined"
                            size="small"
                            sx={{ whiteSpace: 'nowrap' }}
                            onClick={() => {
                              setAddCanonicalSupplierError('');
                              setAddCanonicalSupplierTarget('specialCase');
                              setAddCanonicalSupplierName(criterion.value.trim());
                              setAddCanonicalSupplierOpen(true);
                            }}
                          >
                            New Supplier...
                          </Button>
                        </Stack>
                      ) : criterion.kind === 'category' ? (
                        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} sx={{ flex: 1, minWidth: 0 }}>
                          <FormControl size="small" fullWidth>
                            <InputLabel>Match value</InputLabel>
                            <Select
                              label="Match value"
                              value={criterion.value}
                              onChange={(e) =>
                                setSpecialCaseRuleDraft((d) =>
                                  d
                                    ? {
                                        ...d,
                                        match_criteria: d.match_criteria.map((c) =>
                                          c.id === criterion.id ? { ...c, value: String(e.target.value || '') } : c
                                        )
                                      }
                                    : d
                                )
                              }
                            >
                              {categoriesSortedByName.map((c) => (
                                <MenuItem key={`special-case-criteria-category-${criterion.id}-${c.id}`} value={String(c.id)}>
                                  {c.name}
                                </MenuItem>
                              ))}
                            </Select>
                          </FormControl>
                          <Button
                            variant="outlined"
                            size="small"
                            sx={{ whiteSpace: 'nowrap' }}
                            onClick={() => openCategoryCrudCreate('ruleEdit')}
                          >
                            New Category...
                          </Button>
                        </Stack>
                      ) : criterion.kind === 'current_vat' ? (
                        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} sx={{ flex: 1, minWidth: 0 }}>
                          <FormControl size="small" fullWidth>
                            <InputLabel>Match value</InputLabel>
                            <Select
                              label="Match value"
                              value={normalizeSpecialCaseVatMatcherSelectValue(criterion.value)}
                              onChange={(e) =>
                                setSpecialCaseRuleDraft((d) =>
                                  d
                                    ? {
                                        ...d,
                                        match_criteria: d.match_criteria.map((c) =>
                                          c.id === criterion.id ? { ...c, value: String(e.target.value || '') } : c
                                        )
                                      }
                                    : d
                                )
                              }
                            >
                              {vatRateMatcherPickOptions.map((rate) => (
                                <MenuItem key={`special-case-criteria-vat-${criterion.id}-${rate.value}`} value={rate.value}>
                                  {rate.label}
                                </MenuItem>
                              ))}
                            </Select>
                          </FormControl>
                          <Tooltip title="VAT rates come from the statutory schedule.">
                            <span>
                              <Button variant="outlined" size="small" disabled sx={{ whiteSpace: 'nowrap' }}>
                                New VAT...
                              </Button>
                            </span>
                          </Tooltip>
                        </Stack>
                      ) : criterion.kind === 'currency' ? (
                        <FormControl size="small" fullWidth>
                          <InputLabel>Match value</InputLabel>
                          <Select
                            label="Match value"
                            value={criterion.value.toUpperCase()}
                            onChange={(e) =>
                              setSpecialCaseRuleDraft((d) =>
                                d
                                  ? {
                                      ...d,
                                      match_criteria: d.match_criteria.map((c) =>
                                        c.id === criterion.id ? { ...c, value: String(e.target.value || '').toUpperCase() } : c
                                      )
                                    }
                                  : d
                              )
                            }
                          >
                            <MenuItem value="EUR">EUR</MenuItem>
                            <MenuItem value="GBP">GBP</MenuItem>
                            <MenuItem value="USD">USD</MenuItem>
                          </Select>
                        </FormControl>
                      ) : criterion.kind === 'amount' ? (
                        <TextField
                          size="small"
                          fullWidth
                          label="Amount (exact)"
                          placeholder="e.g. -1425 or 1200.50"
                          value={criterion.value}
                          onChange={(e) =>
                            setSpecialCaseRuleDraft((d) =>
                              d
                                ? {
                                    ...d,
                                    match_criteria: d.match_criteria.map((c) =>
                                      c.id === criterion.id ? { ...c, value: e.target.value } : c
                                    )
                                  }
                                : d
                            )
                          }
                          helperText="Matches the Revolut transaction amount exactly (including sign)."
                        />
                      ) : (
                        <TextField
                          size="small"
                          fullWidth
                          label="Match value"
                          value={criterion.value}
                          onChange={(e) =>
                            setSpecialCaseRuleDraft((d) =>
                              d
                                ? {
                                    ...d,
                                    match_criteria: d.match_criteria.map((c) =>
                                      c.id === criterion.id ? { ...c, value: e.target.value } : c
                                    )
                                  }
                                : d
                            )
                          }
                        />
                      )}
                      {isText ? (
                        <FormControl size="small" sx={{ width: { xs: '100%', sm: 240 } }}>
                          <InputLabel>Text Match Mode</InputLabel>
                          <Select
                            label="Text Match Mode"
                            value={criterion.operator || 'contains'}
                            onChange={(e) =>
                              setSpecialCaseRuleDraft((d) =>
                                d
                                  ? {
                                      ...d,
                                      match_criteria: d.match_criteria.map((c) =>
                                        c.id === criterion.id ? { ...c, operator: String(e.target.value || 'contains') } : c
                                      )
                                    }
                                  : d
                              )
                            }
                          >
                            {RULE_TEXT_MATCH_OPERATOR_OPTIONS.map((opt) => (
                              <MenuItem key={`special-case-criteria-op-${criterion.id}-${opt.value}`} value={opt.value}>
                                {opt.label}
                              </MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                      ) : null}
                      <IconButton
                        size="small"
                        color="error"
                        aria-label="Remove match criterion"
                        onClick={() =>
                          setSpecialCaseRuleDraft((d) => {
                            if (!d) return d;
                            const next = d.match_criteria.filter((c) => c.id !== criterion.id);
                            return { ...d, match_criteria: next };
                          })
                        }
                      >
                        <DeleteOutlineIcon fontSize="small" />
                      </IconButton>
                    </Stack>
                  );
                })}
              </Stack>
              <Typography variant="subtitle2" color="text.secondary" sx={{ fontWeight: 700, pt: 0.5 }}>
                Output
              </Typography>
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems={{ sm: 'center' }}>
                {specialCaseRuleOutputVatEnabled ? (
                  <TextField
                    label="Priority"
                    type="number"
                    value={specialCaseRuleDraft.priority}
                    onChange={(e) =>
                      setSpecialCaseRuleDraft((d) =>
                        d ? { ...d, priority: Number(e.target.value || 100) } : d
                      )
                    }
                    size="small"
                    sx={{ width: { xs: '100%', sm: 140 } }}
                    helperText="Lower runs first"
                  />
                ) : null}
                <Button
                  size="small"
                  variant="outlined"
                  startIcon={<AddIcon />}
                  onClick={(e) => setSpecialCaseRuleOutputMenuAnchorEl(e.currentTarget)}
                  sx={{ whiteSpace: 'nowrap', alignSelf: { xs: 'flex-start', sm: 'center' } }}
                >
                  Add Output
                </Button>
              </Stack>
              <Menu
                anchorEl={specialCaseRuleOutputMenuAnchorEl}
                open={Boolean(specialCaseRuleOutputMenuAnchorEl)}
                onClose={() => setSpecialCaseRuleOutputMenuAnchorEl(null)}
              >
                <MenuItem disabled>Supplier</MenuItem>
                <MenuItem disabled>Category</MenuItem>
                <MenuItem
                  onClick={() => {
                    setSpecialCaseRuleOutputVatEnabled(true);
                    setSpecialCaseRuleOutputMenuAnchorEl(null);
                  }}
                >
                  VAT Rate
                </MenuItem>
              </Menu>
              {specialCaseRuleOutputVatEnabled ? (
                <>
                  <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems={{ sm: 'center' }} sx={{ flex: 1, minWidth: 0 }}>
                      <FormControl size="small" fullWidth>
                        <InputLabel id="special-case-rule-set-vat-label">Set VAT rate</InputLabel>
                        <Select
                          labelId="special-case-rule-set-vat-label"
                          label="Set VAT rate"
                          value={canonicalizeVatRate(specialCaseRuleDraft.set_vat_rate)}
                          onChange={(e) =>
                            setSpecialCaseRuleDraft((d) =>
                              d ? { ...d, set_vat_rate: String(e.target.value || '') } : d
                            )
                          }
                        >
                          <MenuItem value="" disabled>
                            Select VAT rate
                          </MenuItem>
                          {vatRatePickOptions.map((rate) => (
                            <MenuItem key={`special-case-rule-set-vat-${rate.label}`} value={rate.value}>
                              {rate.label}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                      <Tooltip title="VAT rates come from the statutory schedule.">
                        <span>
                          <Button variant="outlined" size="small" disabled sx={{ whiteSpace: 'nowrap' }}>
                            New VAT...
                          </Button>
                        </span>
                      </Tooltip>
                      <IconButton
                        size="small"
                        color="error"
                        aria-label="Remove output"
                        onClick={() => {
                          setSpecialCaseRuleOutputVatEnabled(false);
                          setSpecialCaseRuleDraft((d) => (d ? { ...d, set_vat_rate: '' } : d));
                        }}
                      >
                        <DeleteOutlineIcon fontSize="small" />
                      </IconButton>
                  </Stack>
                  <Typography variant="caption" color="text.secondary">
                    Final VAT override applied when this rule matches.
                  </Typography>
                </>
              ) : null}
              <Typography variant="subtitle2" color="text.secondary" sx={{ fontWeight: 700, pt: 0.5 }}>
                How this Rule Works
              </Typography>
              <Alert severity="info" icon={false}>
                <Typography variant="body2">{specialCaseRuleExplanationSentence}</Typography>
              </Alert>
              <Typography variant="subtitle2" color="text.secondary" sx={{ fontWeight: 700, pt: 0.5 }}>
                Notes
              </Typography>
              <TextField
                label="Notes"
                value={specialCaseRuleDraft.notes}
                onChange={(e) => setSpecialCaseRuleDraft((d) => (d ? { ...d, notes: e.target.value } : d))}
                fullWidth
                size="small"
                multiline
                minRows={4}
                InputProps={buildClearableInputProps(
                  specialCaseRuleDraft.notes,
                  () => setSpecialCaseRuleDraft((d) => (d ? { ...d, notes: '' } : d)),
                  undefined,
                  'Clear notes'
                )}
              />
            </Stack>
          ) : null}
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              if (specialCaseRuleSaving) return;
              setSpecialCaseRuleMatchHelpAnchorEl(null);
              setSpecialCaseRuleCriteriaMenuAnchorEl(null);
              setSpecialCaseRuleOutputMenuAnchorEl(null);
              setSpecialCaseRuleOutputVatEnabled(false);
              setSpecialCaseRuleOpen(false);
              setSpecialCaseRuleDraft(null);
            }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={() => void submitSpecialCaseRule()}
            disabled={specialCaseRuleSaving || addCanonicalSupplierBusy}
          >
            {specialCaseRuleSaving ? 'Saving…' : specialCaseRuleDraft?.id ? 'Save changes' : 'Add Special Case rule'}
          </Button>
        </DialogActions>
      </Dialog>
      <Dialog
        open={ruleEditDraft != null}
        onClose={() => {
          if (ruleEditSaving) return;
          setAddCanonicalSupplierOpen(false);
          setAddCanonicalSupplierName('');
          setAddCanonicalSupplierError('');
          setRuleMatchHelpAnchorEl(null);
          setRuleEditMatchCriteriaMenuAnchorEl(null);
          setRuleEditDraft(null);
          setRuleEditReadOnly(false);
          setRuleEditSaveInFlight(false);
          setRuleEditTargetType('classification');
          setRuleEditOutputMenuAnchorEl(null);
          setRuleEditOutputCategoryEnabled(false);
          setRuleEditOutputSupplierEnabled(false);
          setRuleEditError('');
          setRuleEditConflictRuleId(null);
          setConflictPairEditDraft(null);
          setConflictPairEditError('');
          resetConflictPairRuleEditorUi();
        }}
        maxWidth={false}
        fullWidth
        PaperProps={{
          sx: {
            width: conflictPairEditDraft ? 'min(98vw, 1360px)' : RULES_RULE_EDITOR_DIALOG_PAPER_SX.width,
            maxWidth: 'none',
            transition: (theme) =>
              theme.transitions.create('width', { duration: theme.transitions.duration.shorter })
          }
        }}
      >
        <DialogTitle sx={{ pr: 1 }}>
          <Stack direction="row" alignItems="flex-start" justifyContent="space-between" spacing={1}>
            <Stack direction="row" spacing={1} alignItems="center" sx={{ flex: 1, minWidth: 0 }}>
              <Typography variant="h6" sx={{ flex: '1 1 auto', minWidth: 0 }}>
                {conflictPairEditDraft
                  ? 'Edit classification rules (side by side)'
                  : ruleEditReadOnly
                  ? isPipelineSystemHeadingRuleName(ruleEditDraft?.name || '')
                    ? 'Cashflow Rules'
                    : 'View classification rule (read-only)'
                  : ruleEditDraft?.proposal_atlas_key || ruleEditDraft?.proposal_id
                  ? 'Edit proposed classification rule'
                  : ruleEditDraft?.isNew
                    ? 'Add classification rule'
                    : 'Edit classification rule'}
              </Typography>
              <Tooltip title="How this Rule Works">
                <IconButton
                  size="small"
                  color="info"
                  aria-label="How this rule matches (one transaction)"
                  aria-expanded={Boolean(ruleMatchHelpAnchorEl)}
                  aria-haspopup="dialog"
                  onClick={(e) => {
                    setRuleMatchHelpAnchorEl((prev) =>
                      prev ? null : (e.currentTarget as HTMLButtonElement)
                    );
                  }}
                >
                  <InfoOutlinedIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </Stack>
            <IconButton
              aria-label="Close"
              size="small"
              onClick={() => {
                if (ruleEditSaving) return;
                setAddCanonicalSupplierOpen(false);
                setAddCanonicalSupplierName('');
                setAddCanonicalSupplierError('');
                setRuleMatchHelpAnchorEl(null);
                setRuleEditMatchCriteriaMenuAnchorEl(null);
                setRuleEditDraft(null);
                setRuleEditReadOnly(false);
                setRuleEditSaveInFlight(false);
                setRuleEditTargetType('classification');
                setRuleEditOutputMenuAnchorEl(null);
                setRuleEditOutputCategoryEnabled(false);
                setRuleEditOutputSupplierEnabled(false);
                setRuleEditError('');
                setRuleEditConflictRuleId(null);
                setConflictPairEditDraft(null);
                setConflictPairEditError('');
                resetConflictPairRuleEditorUi();
              }}
              disabled={ruleEditSaving}
            >
              <CloseIcon />
            </IconButton>
          </Stack>
        </DialogTitle>
        <DialogContent dividers>
          {ruleEditDraft && (
            <Box
              sx={{
                display: 'flex',
                flexDirection: { xs: 'column', md: 'row' },
                gap: 2,
                alignItems: 'flex-start',
                width: '100%'
              }}
            >
              <Box
                sx={{
                  flex: 1,
                  minWidth: 0,
                  width: { xs: '100%', md: conflictPairEditDraft ? '50%' : '100%' }
                }}
              >
            <Stack spacing={2} sx={{ pt: 0.5, pointerEvents: ruleEditReadOnly ? 'none' : 'auto' }}>
              <Popover
                open={Boolean(ruleMatchHelpAnchorEl)}
                anchorEl={ruleMatchHelpAnchorEl}
                onClose={() => setRuleMatchHelpAnchorEl(null)}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
                transformOrigin={{ vertical: 'top', horizontal: 'left' }}
                slotProps={{ paper: { sx: { maxWidth: 440 } } }}
              >
                <Box sx={{ p: 2 }}>
                  <Typography variant="body2" component="div" sx={{ fontWeight: 600, mb: 0.5 }}>
                    How this rule matches (one transaction)
                  </Typography>
                  <Typography variant="body2" component="div" sx={{ mb: 0.75 }}>
                    <strong>AND</strong> — every field you fill in must pass. Empty fields are skipped (they are not
                    alternatives).
                  </Typography>
                  <Typography variant="body2" component="div" sx={{ mb: 0.75 }}>
                    Together that means: bank account scope (if not “Both”), then each text matcher you set, then
                    currency (if set), then amount min/max (if set).
                  </Typography>
                  <Typography variant="body2" component="div" sx={{ mb: 0.75 }}>
                    <strong>Contains / Does not contain</strong> — each of those matchers searches the combined
                    supplier, description, and reference text. <strong>Is exactly</strong> compares each field on its
                    own.
                  </Typography>
                  <Typography variant="body2" component="div" color="text.secondary">
                    Across many rules: they are evaluated in priority order (lower number first). Each rule that
                    matches can apply its category tag if that transaction does not already have a tag for that same
                    category—so several rules can match one transaction when they target different categories.
                  </Typography>
                </Box>
              </Popover>
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.25} justifyContent="space-between" alignItems={{ sm: 'center' }}>
                {(() => {
                  const d = ruleEditDraft;
                  if (d.proposal_atlas_key) {
                    const k = String(d.proposal_atlas_key).trim();
                    const short = k.length > 48 ? `${k.slice(0, 45)}…` : k;
                    return (
                      <Typography variant="body2" color="text.secondary">
                        Immutable ID:{' '}
                        <Box component="span" sx={{ fontWeight: 700, color: 'text.primary', wordBreak: 'break-all' }}>
                          Atlas:{short}
                        </Box>{' '}
                        <Box component="span" sx={{ fontStyle: 'italic' }}>
                          (proposal)
                        </Box>
                      </Typography>
                    );
                  }
                  const pid = d.proposal_id != null ? Math.trunc(Number(d.proposal_id)) : NaN;
                  if (Number.isFinite(pid) && pid > 0) {
                    return (
                      <Typography variant="body2" color="text.secondary">
                        Immutable ID:{' '}
                        <Box component="span" sx={{ fontWeight: 700, color: 'text.primary' }}>
                          P{pid}
                        </Box>{' '}
                        <Box component="span" sx={{ fontStyle: 'italic' }}>
                          (proposal)
                        </Box>
                      </Typography>
                    );
                  }
                  const rid = Math.trunc(Number(d.id));
                  if (Number.isFinite(rid) && rid > 0) {
                    return (
                      <Typography variant="body2" color="text.secondary">
                        Immutable ID:{' '}
                        <Box component="span" sx={{ fontWeight: 700, color: 'text.primary' }}>
                          {formatClassificationRulePrefixedId(rid, d.name)}
                        </Box>
                      </Typography>
                    );
                  }
                  return (
                    <Typography variant="body2" color="text.secondary">
                      Immutable ID: <Box component="span" sx={{ fontWeight: 700, color: 'text.primary' }}>New</Box>
                    </Typography>
                  );
                })()}
                <FormControl size="small" sx={{ width: { xs: '100%', sm: 220 } }}>
                  <InputLabel id="rule-edit-type-label">Rule type</InputLabel>
                  <Select
                    labelId="rule-edit-type-label"
                    label="Rule type"
                    value={ruleEditTargetType}
                    onChange={(e) => {
                      const v = String(e.target.value || 'classification') as EditableRuleType;
                      setRuleEditTargetType(v);
                      if (v === 'supplier') {
                        setRuleEditOutputCategoryEnabled(false);
                        setRuleEditOutputSupplierEnabled(true);
                      } else {
                        setRuleEditOutputSupplierEnabled(false);
                        setRuleEditOutputCategoryEnabled(true);
                      }
                    }}
                    disabled={ruleEditReadOnly}
                  >
                    <MenuItem value="classification">Classification</MenuItem>
                    <MenuItem value="supplier">Supplier</MenuItem>
                  </Select>
                </FormControl>
              </Stack>
              {ruleEditTargetType === 'supplier' && !ruleEditReadOnly ? (
                <Typography variant="caption" color="text.secondary">
                  Saving as Supplier will create a new Supplier rule and replace this Classification item (delete if confirmed, reject if proposed). Category/output-only fields are not carried over.
                </Typography>
              ) : null}
              {ruleEditError ? (
                <Alert
                  severity="error"
                  onClose={() => {
                    setRuleEditError('');
                    setRuleEditConflictRuleId(null);
                  }}
                >
                  <Stack spacing={1}>
                    <Typography variant="body2">{ruleEditError}</Typography>
                    {ruleEditConflictRuleId != null &&
                    Number.isFinite(ruleEditConflictRuleId) &&
                    ruleEditConflictRuleId > 0 ? (
                      <Button
                        size="small"
                        variant="outlined"
                        onClick={() => {
                          const cid = ruleEditConflictRuleId;
                          if (cid == null || !Number.isFinite(cid) || cid <= 0) return;
                          void openClassificationConflictSideBySide(cid);
                        }}
                      >
                        Open both in edit (side by side) — other rule:{' '}
                        {formatClassificationRulePrefixedId(
                          ruleEditConflictRuleId,
                          (rulesPayload?.rules || []).find(
                            (x) =>
                              Math.trunc(Number(x.id)) === Math.trunc(Number(ruleEditConflictRuleId || 0))
                          )?.name
                        )}
                      </Button>
                    ) : null}
                  </Stack>
                </Alert>
              ) : null}
              <Stack direction="row" justifyContent="space-between" alignItems="center" useFlexGap>
                <Typography variant="subtitle2" color="text.secondary" sx={{ fontWeight: 700 }}>
                  Title
                </Typography>
                <Stack direction="row" spacing={1} alignItems="center">
                  <Typography variant="caption" color="text.secondary">
                    Inactive
                  </Typography>
                  <Switch
                    size="small"
                    checked={ruleEditDraft.is_active}
                    onChange={(_, checked) =>
                      setRuleEditDraft((d) => (d ? { ...d, is_active: checked } : d))
                    }
                  />
                  <Typography variant="caption" color="text.secondary">
                    Active
                  </Typography>
                </Stack>
              </Stack>
              <TextField
                label="Rule name"
                value={ruleEditDraft.name}
                onChange={(e) =>
                  setRuleEditDraft((d) => (d ? { ...d, name: e.target.value } : d))
                }
                fullWidth
                required
                size="small"
                InputProps={buildClearableInputProps(
                  ruleEditDraft.name,
                  () => setRuleEditDraft((d) => (d ? { ...d, name: '' } : d)),
                  undefined,
                  'Clear rule name'
                )}
              />
              <ClassificationRuleMatchOutputBlock
                idPrefix="rule-edit"
                draft={ruleEditDraft}
                setDraft={setRuleEditDraft}
                readOnly={ruleEditReadOnly}
                saving={ruleEditSaving}
                matchCriteriaEnabled={ruleEditMatchCriteriaEnabled}
                setMatchCriteriaEnabled={setRuleEditMatchCriteriaEnabled}
                matchCriteriaMenuAnchorEl={ruleEditMatchCriteriaMenuAnchorEl}
                setMatchCriteriaMenuAnchorEl={setRuleEditMatchCriteriaMenuAnchorEl}
                outputMenuAnchorEl={ruleEditOutputMenuAnchorEl}
                setOutputMenuAnchorEl={setRuleEditOutputMenuAnchorEl}
                outputCategoryEnabled={ruleEditOutputCategoryEnabled}
                setOutputCategoryEnabled={setRuleEditOutputCategoryEnabled}
                outputSupplierEnabled={ruleEditOutputSupplierEnabled}
                setOutputSupplierEnabled={setRuleEditOutputSupplierEnabled}
                ruleEditTargetType={ruleEditTargetType}
                supplierOptions={ruleEditSupplierOptions}
                categorySelectOptions={ruleEditCategorySelectOptions}
                explanationSentence={ruleEditExplanationSentence}
                onOpenCategoryCrudRuleEdit={() => openCategoryCrudCreate('ruleEdit')}
                onNewSupplierFromDraft={(d) => {
                  setAddCanonicalSupplierError('');
                  setAddCanonicalSupplierTarget('classification');
                  setAddCanonicalSupplierName(
                    d.supplier_name_overrides[0]?.trim() || d.supplier_contains.trim() || ''
                  );
                  setAddCanonicalSupplierOpen(true);
                }}
              />
            </Stack>
              </Box>
              {conflictPairEditDraft && !ruleEditReadOnly ? (
                <Box
                  sx={{
                    flex: 1,
                    minWidth: 0,
                    width: { xs: '100%', md: '50%' },
                    pl: { md: 1.5 },
                    borderLeft: { md: '1px solid' },
                    borderColor: 'divider',
                    alignSelf: 'stretch'
                  }}
                >
                  <Stack spacing={2} sx={{ pt: 0.5, width: '100%' }}>
                    <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                      Conflicting rule (
                      {formatClassificationRulePrefixedId(
                        Math.trunc(Number(conflictPairEditDraft.id)),
                        conflictPairEditDraft.name
                      )}
                      )
                    </Typography>
                    {conflictPairEditError ? (
                      <Alert severity="error" onClose={() => setConflictPairEditError('')}>
                        {conflictPairEditError}
                      </Alert>
                    ) : null}
                    <Stack direction="row" justifyContent="space-between" alignItems="center" useFlexGap>
                      <Typography variant="subtitle2" color="text.secondary" sx={{ fontWeight: 700 }}>
                        Title
                      </Typography>
                      <Stack direction="row" spacing={1} alignItems="center">
                        <Typography variant="caption" color="text.secondary">
                          Inactive
                        </Typography>
                        <Switch
                          size="small"
                          checked={conflictPairEditDraft.is_active}
                          onChange={(_, checked) =>
                            setConflictPairEditDraft((p) => (p ? { ...p, is_active: checked } : p))
                          }
                        />
                        <Typography variant="caption" color="text.secondary">
                          Active
                        </Typography>
                      </Stack>
                    </Stack>
                    <TextField
                      label="Rule name"
                      value={conflictPairEditDraft.name}
                      onChange={(e) =>
                        setConflictPairEditDraft((prev) => (prev ? { ...prev, name: e.target.value } : prev))
                      }
                      fullWidth
                      required
                      size="small"
                      InputProps={buildClearableInputProps(
                        conflictPairEditDraft.name,
                        () => setConflictPairEditDraft((p) => (p ? { ...p, name: '' } : p)),
                        undefined,
                        'Clear rule name'
                      )}
                    />
                    <ClassificationRuleMatchOutputBlock
                      idPrefix="conflict-pair"
                      draft={conflictPairEditDraft}
                      setDraft={setConflictPairEditDraft}
                      readOnly={false}
                      saving={conflictPairEditSaving}
                      matchCriteriaEnabled={conflictPairMatchCriteriaEnabled}
                      setMatchCriteriaEnabled={setConflictPairMatchCriteriaEnabled}
                      matchCriteriaMenuAnchorEl={conflictPairMatchCriteriaMenuAnchorEl}
                      setMatchCriteriaMenuAnchorEl={setConflictPairMatchCriteriaMenuAnchorEl}
                      outputMenuAnchorEl={conflictPairOutputMenuAnchorEl}
                      setOutputMenuAnchorEl={setConflictPairOutputMenuAnchorEl}
                      outputCategoryEnabled={conflictPairOutputCategoryEnabled}
                      setOutputCategoryEnabled={setConflictPairOutputCategoryEnabled}
                      outputSupplierEnabled={conflictPairOutputSupplierEnabled}
                      setOutputSupplierEnabled={setConflictPairOutputSupplierEnabled}
                      ruleEditTargetType="classification"
                      supplierOptions={conflictPairSupplierOptions}
                      categorySelectOptions={conflictPairCategorySelectOptions}
                      explanationSentence={conflictPairExplanationSentence}
                      onOpenCategoryCrudRuleEdit={() => openCategoryCrudCreate('ruleEditConflictPair')}
                      onNewSupplierFromDraft={(d) => {
                        setAddCanonicalSupplierError('');
                        setAddCanonicalSupplierTarget('classificationConflictPair');
                        setAddCanonicalSupplierName(
                          d.supplier_name_overrides[0]?.trim() || d.supplier_contains.trim() || ''
                        );
                        setAddCanonicalSupplierOpen(true);
                      }}
                    />
                    <Button
                      variant="contained"
                      fullWidth
                      onClick={() => void saveConflictRuleFromDialog()}
                      disabled={conflictPairEditSaving || isAnyRuleSaveInFlight}
                    >
                      {conflictPairEditSaving ? 'Saving…' : 'Save conflicting rule'}
                    </Button>
                    <Button
                      size="small"
                      onClick={() => {
                        setConflictPairEditDraft(null);
                        setConflictPairEditError('');
                        resetConflictPairRuleEditorUi();
                      }}
                    >
                      Close conflicting rule panel
                    </Button>
                  </Stack>
                </Box>
              ) : null}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          {!ruleEditReadOnly &&
          !!ruleEditDraft &&
          !ruleEditDraft.isNew &&
          !ruleEditDraft.proposal_atlas_key &&
          !ruleEditDraft.proposal_id &&
          Number.isFinite(Number(ruleEditDraft.id)) &&
          Number(ruleEditDraft.id) > 0 ? (
            <Button
              color="error"
              onClick={() =>
                void deleteClassificationRule(Math.trunc(Number(ruleEditDraft.id)), ruleEditDraft.name)
              }
              disabled={ruleEditSaving}
            >
              Delete
            </Button>
          ) : null}
          {ruleEditDraft &&
          ruleEditTargetType === 'classification' &&
          !conflictPairEditDraft ? (
            <Button
              variant="outlined"
              onClick={() => void duplicateClassificationRuleFromDialog()}
              disabled={
                !!ruleDuplicateBusyKey ||
                ruleEditSaveInFlight ||
                addCanonicalSupplierBusy ||
                conflictPairEditSaving
              }
            >
              {ruleDuplicateBusyKey === 'dup-dialog-cls' ? 'Duplicating…' : 'Duplicate Rule'}
            </Button>
          ) : null}
          {ruleEditDraft &&
          (ruleEditDraft.proposal_atlas_key || ruleEditDraft.proposal_id) &&
          !ruleEditReadOnly ? (
            <Button
              color="error"
              variant="outlined"
              onClick={() => void rejectClassificationProposalFromEditDialog()}
              disabled={
                ruleEditSaving ||
                !!ruleEditSaveInFlight ||
                addCanonicalSupplierBusy ||
                conflictPairEditSaving ||
                !!proposalBusyKey
              }
            >
              Reject
            </Button>
          ) : null}
          {!ruleEditReadOnly ? (
            <Button
              variant="contained"
              onClick={() => void saveRuleFromDialog()}
              disabled={ruleEditSaveInFlight || addCanonicalSupplierBusy || conflictPairEditSaving}
            >
              {ruleEditSaving
                ? 'Saving…'
                : ruleEditDraft?.proposal_atlas_key || ruleEditDraft?.proposal_id
                  ? 'Accept edited proposal'
                  : ruleEditDraft?.isNew
                    ? 'Add rule'
                    : 'Save'}
            </Button>
          ) : null}
          <Button
            onClick={() => {
              if (ruleEditSaving) return;
              setAddCanonicalSupplierOpen(false);
              setAddCanonicalSupplierName('');
              setAddCanonicalSupplierError('');
              setRuleMatchHelpAnchorEl(null);
              setRuleEditMatchCriteriaMenuAnchorEl(null);
              setRuleEditDraft(null);
              setRuleEditReadOnly(false);
              setRuleEditTargetType('classification');
              setRuleEditOutputMenuAnchorEl(null);
              setRuleEditOutputCategoryEnabled(false);
              setRuleEditOutputSupplierEnabled(false);
              setRuleEditSaveInFlight(false);
              setRuleEditError('');
              setRuleEditConflictRuleId(null);
              setConflictPairEditDraft(null);
              setConflictPairEditError('');
              resetConflictPairRuleEditorUi();
            }}
          >
            {ruleEditReadOnly ? 'Close' : 'Cancel'}
          </Button>
        </DialogActions>
      </Dialog>
      <Dialog
        open={addCanonicalSupplierOpen}
        onClose={() => {
          if (addCanonicalSupplierBusy) return;
          setAddCanonicalSupplierOpen(false);
          setAddCanonicalSupplierName('');
          setAddCanonicalSupplierError('');
        }}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Add canonical supplier</DialogTitle>
        <DialogContent dividers>
          <Stack spacing={2} sx={{ pt: 0.5 }}>
            {addCanonicalSupplierError ? (
              <Alert severity="error" onClose={() => setAddCanonicalSupplierError('')}>
                {addCanonicalSupplierError}
              </Alert>
            ) : null}
            {!addCanonicalSupplierError && addCanonicalSupplierHints.duplicateOf ? (
              <Alert severity="error">
                A supplier already exists as “{addCanonicalSupplierHints.duplicateOf}” (capitalisation is ignored).
              </Alert>
            ) : null}
            {!addCanonicalSupplierHints.duplicateOf &&
            addCanonicalSupplierHints.suggestion &&
            addCanonicalSupplierName.trim() ? (
              <Alert
                severity="info"
                action={
                  <Button
                    color="inherit"
                    size="small"
                    onClick={() => setAddCanonicalSupplierName(addCanonicalSupplierHints.suggestion as string)}
                  >
                    Use “{addCanonicalSupplierHints.suggestion}”
                  </Button>
                }
              >
                Did you mean <strong>{addCanonicalSupplierHints.suggestion}</strong>?
              </Alert>
            ) : null}
            <TextField
              autoFocus
              label="Supplier name"
              value={addCanonicalSupplierName}
              onChange={(e) => setAddCanonicalSupplierName(e.target.value)}
              fullWidth
              size="small"
              disabled={addCanonicalSupplierBusy}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  void submitNewCanonicalSupplier();
                }
              }}
              helperText="Names are unique ignoring capitalisation. Creates a supplier row so rules can tag transactions with this name."
              InputProps={buildClearableInputProps(
                addCanonicalSupplierName,
                () => setAddCanonicalSupplierName(''),
                undefined,
                'Clear supplier name'
              )}
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              if (addCanonicalSupplierBusy) return;
              setAddCanonicalSupplierOpen(false);
              setAddCanonicalSupplierName('');
              setAddCanonicalSupplierError('');
            }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={() => void submitNewCanonicalSupplier()}
            disabled={addCanonicalSupplierBusy || !!addCanonicalSupplierHints.duplicateOf}
          >
            {addCanonicalSupplierBusy ? 'Saving…' : 'Create supplier'}
          </Button>
        </DialogActions>
      </Dialog>
      <Dialog
        open={suppliersBulkEditOpen}
        onClose={() => {
          if (suppliersBulkBusy) return;
          setSuppliersBulkEditOpen(false);
        }}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Bulk edit suppliers</DialogTitle>
        <DialogContent dividers>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Applies to {selectedSupplierCrudIds.length} selected supplier(s). Only the fields you change below are
            updated; other columns keep their current values.
          </Typography>
          <Stack spacing={2}>
            <FormControl size="small" fullWidth>
              <InputLabel id="bulk-suppliers-favourite-label">Favourite (key supplier)</InputLabel>
              <Select
                labelId="bulk-suppliers-favourite-label"
                label="Favourite (key supplier)"
                value={suppliersBulkFavourite}
                onChange={(e) =>
                  setSuppliersBulkFavourite(e.target.value as 'no_change' | 'favourite' | 'not_favourite')
                }
              >
                <MenuItem value="no_change">No change</MenuItem>
                <MenuItem value="favourite">Set as favourite</MenuItem>
                <MenuItem value="not_favourite">Remove from favourites</MenuItem>
              </Select>
            </FormControl>
            <FormControl size="small" fullWidth>
              <InputLabel id="bulk-suppliers-active-label">Active</InputLabel>
              <Select
                labelId="bulk-suppliers-active-label"
                label="Active"
                value={suppliersBulkActive}
                onChange={(e) => setSuppliersBulkActive(e.target.value as 'no_change' | 'active' | 'inactive')}
              >
                <MenuItem value="no_change">No change</MenuItem>
                <MenuItem value="active">Active</MenuItem>
                <MenuItem value="inactive">Inactive</MenuItem>
              </Select>
            </FormControl>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => !suppliersBulkBusy && setSuppliersBulkEditOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={() => void applySuppliersBulkEdit()}
            disabled={
              suppliersBulkBusy ||
              selectedSupplierCrudIds.length === 0 ||
              (suppliersBulkFavourite === 'no_change' && suppliersBulkActive === 'no_change')
            }
          >
            {suppliersBulkBusy ? 'Applying…' : 'Apply to selected'}
          </Button>
        </DialogActions>
      </Dialog>
      <SupplierAliasMappingsDialog
        open={supplierAliasMappingsTarget != null}
        supplierId={supplierAliasMappingsTarget?.id ?? null}
        supplierName={supplierAliasMappingsTarget?.name ?? ''}
        onClose={() => setSupplierAliasMappingsTarget(null)}
        canManageSupplierAliases={canMergeSuppliersCatalog}
        onAliasesChanged={() => void refreshRulesTabDatasets()}
      />
      <Dialog
        open={suppliersMergeDialogOpen}
        onClose={() => {
          if (suppliersMergeBusy) return;
          setSuppliersMergeDialogOpen(false);
        }}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Merge suppliers into one</DialogTitle>
        <DialogContent dividers>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            All selected rows except an exact name match for the canonical name are merged into that supplier. Tags and
            aliases move to the kept row; optional description rule helps future imports map to it.
          </Typography>
          <Stack spacing={2}>
            <TextField
              label="Canonical supplier name"
              size="small"
              fullWidth
              value={suppliersMergeCanonicalDraft}
              onChange={(e) => setSuppliersMergeCanonicalDraft(e.target.value)}
              helperText="Edit if needed (e.g. “Twilio”, “IKEA”). If this name already exists, that row is kept."
              disabled={suppliersMergeBusy}
            />
            <FormControlLabel
              control={
                <Checkbox
                  checked={suppliersMergeCreateDescRule}
                  onChange={(e) => setSuppliersMergeCreateDescRule(e.target.checked)}
                  disabled={suppliersMergeBusy}
                />
              }
              label="Create description_contains supplier rule (stem from canonical)"
            />
            <FormControlLabel
              control={
                <Checkbox
                  checked={suppliersMergeDeactivateSources}
                  onChange={(e) => setSuppliersMergeDeactivateSources(e.target.checked)}
                  disabled={suppliersMergeBusy}
                />
              }
              label="Deactivate merged source rows"
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              if (suppliersMergeBusy) return;
              setSuppliersMergeDialogOpen(false);
            }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={() => void applySuppliersMergeIntoCanonical()}
            disabled={suppliersMergeBusy || !suppliersMergeCanonicalDraft.trim()}
          >
            {suppliersMergeBusy ? 'Merging…' : 'Merge'}
          </Button>
        </DialogActions>
      </Dialog>
      <Dialog
        open={categoriesBulkEditOpen}
        onClose={() => {
          if (categoriesBulkBusy) return;
          setCategoriesBulkEditOpen(false);
        }}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Bulk edit categories</DialogTitle>
        <DialogContent dividers>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Applies to {selectedCategoryCrudIds.length} selected categor
            {selectedCategoryCrudIds.length === 1 ? 'y' : 'ies'}. Only the fields you change below are updated.
          </Typography>
          <Stack spacing={2}>
            <FormControl size="small" fullWidth>
              <InputLabel id="bulk-categories-favourite-label">Favourite (key category)</InputLabel>
              <Select
                labelId="bulk-categories-favourite-label"
                label="Favourite (key category)"
                value={categoriesBulkFavourite}
                onChange={(e) =>
                  setCategoriesBulkFavourite(e.target.value as 'no_change' | 'favourite' | 'not_favourite')
                }
              >
                <MenuItem value="no_change">No change</MenuItem>
                <MenuItem value="favourite">Set as favourite</MenuItem>
                <MenuItem value="not_favourite">Remove from favourites</MenuItem>
              </Select>
            </FormControl>
            <FormControl size="small" fullWidth>
              <InputLabel id="bulk-categories-active-label">Active</InputLabel>
              <Select
                labelId="bulk-categories-active-label"
                label="Active"
                value={categoriesBulkActive}
                onChange={(e) => setCategoriesBulkActive(e.target.value as 'no_change' | 'active' | 'inactive')}
              >
                <MenuItem value="no_change">No change</MenuItem>
                <MenuItem value="active">Active</MenuItem>
                <MenuItem value="inactive">Inactive</MenuItem>
              </Select>
            </FormControl>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => !categoriesBulkBusy && setCategoriesBulkEditOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={() => void applyCategoriesBulkEdit()}
            disabled={
              categoriesBulkBusy ||
              selectedCategoryCrudIds.length === 0 ||
              (categoriesBulkFavourite === 'no_change' && categoriesBulkActive === 'no_change')
            }
          >
            {categoriesBulkBusy ? 'Applying…' : 'Apply to selected'}
          </Button>
        </DialogActions>
      </Dialog>
      <Dialog
        open={supplierCrudOpen}
        onClose={() => {
          if (supplierCrudSaving) return;
          setSupplierCrudOpen(false);
          setSupplierCrudDraft(null);
          setSupplierCrudFormError('');
        }}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>{supplierCrudDraft?.id != null ? 'Edit supplier' : 'Add supplier'}</DialogTitle>
        <DialogContent dividers>
          {supplierCrudDraft ? (
            <Stack spacing={2} sx={{ pt: 0.5 }}>
              {supplierCrudFormError ? (
                <Alert severity="error" onClose={() => setSupplierCrudFormError('')}>
                  {supplierCrudFormError}
                </Alert>
              ) : null}
              {!supplierCrudFormError && supplierCrudNameHints.duplicateOf ? (
                <Alert severity="error">
                  Another supplier is already named “{supplierCrudNameHints.duplicateOf}” (capitalisation is ignored).
                </Alert>
              ) : null}
              {!supplierCrudNameHints.duplicateOf &&
              supplierCrudNameHints.suggestion &&
              supplierCrudDraft.name.trim() ? (
                <Alert
                  severity="info"
                  action={
                    <Button
                      color="inherit"
                      size="small"
                      onClick={() =>
                        setSupplierCrudDraft((d) =>
                          d ? { ...d, name: supplierCrudNameHints.suggestion as string } : d
                        )
                      }
                    >
                      Use “{supplierCrudNameHints.suggestion}”
                    </Button>
                  }
                >
                  Did you mean <strong>{supplierCrudNameHints.suggestion}</strong>?
                </Alert>
              ) : null}
              <TextField
                autoFocus
                label="Canonical name"
                value={supplierCrudDraft.name}
                onChange={(e) =>
                  setSupplierCrudDraft((d) => (d ? { ...d, name: e.target.value } : d))
                }
                fullWidth
                size="small"
                required
                disabled={supplierCrudSaving}
                helperText="Shown in pickers and on classified transactions. Must be unique for your account (capitalisation ignored)."
              />
              <FormControlLabel
                control={
                  <Switch
                    checked={supplierCrudDraft.is_key_supplier}
                    onChange={(_, checked) =>
                      setSupplierCrudDraft((d) => (d ? { ...d, is_key_supplier: checked } : d))
                    }
                    disabled={supplierCrudSaving}
                  />
                }
                label="Favourite (starred in Suppliers table)"
              />
              <FormControl size="small" fullWidth>
                <InputLabel id="supplier-crud-frequency-label">Invoice frequency</InputLabel>
                <Select
                  labelId="supplier-crud-frequency-label"
                  label="Invoice frequency"
                  value={supplierCrudDraft.invoice_frequency || ''}
                  onChange={(e) =>
                    setSupplierCrudDraft((d) =>
                      d ? { ...d, invoice_frequency: String(e.target.value || '') } : d
                    )
                  }
                  disabled={supplierCrudSaving}
                >
                  <MenuItem value="">
                    <em>Unset</em>
                  </MenuItem>
                  {SUPPLIER_CRUD_INVOICE_FREQUENCIES.map((f) => (
                    <MenuItem key={`scr-freq-${f}`} value={f}>
                      {f}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <FormControl size="small" fullWidth>
                <InputLabel id="supplier-crud-currency-label">Invoice currency</InputLabel>
                <Select
                  labelId="supplier-crud-currency-label"
                  label="Invoice currency"
                  value={supplierCrudDraft.invoice_currency || ''}
                  onChange={(e) =>
                    setSupplierCrudDraft((d) =>
                      d ? { ...d, invoice_currency: String(e.target.value || '') } : d
                    )
                  }
                  disabled={supplierCrudSaving}
                >
                  <MenuItem value="">
                    <em>Unset</em>
                  </MenuItem>
                  {SUPPLIER_CRUD_CURRENCIES.map((c) => (
                    <MenuItem key={`scr-cur-${c}`} value={c}>
                      {c}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <FormControl size="small" fullWidth>
                <InputLabel id="supplier-crud-vat-label">VAT status</InputLabel>
                <Select
                  labelId="supplier-crud-vat-label"
                  label="VAT status"
                  value={supplierCrudDraft.vat_status || ''}
                  onChange={(e) =>
                    setSupplierCrudDraft((d) =>
                      d ? { ...d, vat_status: String(e.target.value || '') } : d
                    )
                  }
                  disabled={supplierCrudSaving}
                >
                  <MenuItem value="">
                    <em>Unset</em>
                  </MenuItem>
                  {SUPPLIER_CRUD_VAT_STATUSES.map((v) => (
                    <MenuItem key={`scr-vat-${v}`} value={v}>
                      {v}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <TextField
                label="Invoice URL"
                value={supplierCrudDraft.invoice_url}
                onChange={(e) =>
                  setSupplierCrudDraft((d) => (d ? { ...d, invoice_url: e.target.value } : d))
                }
                fullWidth
                size="small"
                placeholder="https://…"
                helperText="Portal link used to find this supplier’s invoices"
                disabled={supplierCrudSaving}
              />
              <TextField
                label="Notes"
                value={supplierCrudDraft.notes}
                onChange={(e) =>
                  setSupplierCrudDraft((d) => (d ? { ...d, notes: e.target.value } : d))
                }
                fullWidth
                size="small"
                multiline
                minRows={2}
                disabled={supplierCrudSaving}
              />
              {supplierCrudDraft.id != null ? (
                <FormControlLabel
                  control={
                    <Switch
                      checked={supplierCrudDraft.is_active}
                      onChange={(_, checked) =>
                        setSupplierCrudDraft((d) => (d ? { ...d, is_active: checked } : d))
                      }
                      disabled={supplierCrudSaving}
                    />
                  }
                  label="Active (shown in pickers)"
                />
              ) : null}
            </Stack>
          ) : null}
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              if (supplierCrudSaving) return;
              setSupplierCrudOpen(false);
              setSupplierCrudDraft(null);
              setSupplierCrudFormError('');
            }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={() => void submitSupplierCrudDialog()}
            disabled={supplierCrudSaving || !!supplierCrudNameHints.duplicateOf}
          >
            {supplierCrudSaving ? 'Saving…' : supplierCrudDraft?.id != null ? 'Save changes' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>
      <Dialog
        open={categoryCrudOpen}
        onClose={() => {
          if (categoryCrudSaving) return;
          setCategoryCrudOpen(false);
          setCategoryCrudDraft(null);
          setCategoryCrudFormError('');
          setCategoryCrudTarget('categories');
          setCategoryCrudPendingGranularTxId(null);
        }}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>{categoryCrudDraft?.id != null ? 'Edit category' : 'Add category'}</DialogTitle>
        <DialogContent dividers>
          {categoryCrudDraft ? (
            <Stack spacing={2} sx={{ pt: 0.5 }}>
              {categoryCrudFormError ? (
                <Alert severity="error" onClose={() => setCategoryCrudFormError('')}>
                  {categoryCrudFormError}
                </Alert>
              ) : null}
              {!categoryCrudFormError && categoryCrudNameHints.duplicateOf ? (
                <Alert severity="error">
                  Another category is already named "{categoryCrudNameHints.duplicateOf}" (capitalisation is ignored).
                </Alert>
              ) : null}
              {!categoryCrudNameHints.duplicateOf &&
              categoryCrudNameHints.suggestion &&
              categoryCrudDraft.name.trim() ? (
                <Alert
                  severity="info"
                  action={
                    <Button
                      color="inherit"
                      size="small"
                      onClick={() =>
                        setCategoryCrudDraft((d) =>
                          d ? { ...d, name: categoryCrudNameHints.suggestion as string } : d
                        )
                      }
                    >
                      Use "{categoryCrudNameHints.suggestion}"
                    </Button>
                  }
                >
                  Did you mean <strong>{categoryCrudNameHints.suggestion}</strong>?
                </Alert>
              ) : null}
              <TextField
                autoFocus
                label="Category name"
                value={categoryCrudDraft.name}
                onChange={(e) =>
                  setCategoryCrudDraft((d) => (d ? { ...d, name: e.target.value } : d))
                }
                fullWidth
                size="small"
                required
                disabled={categoryCrudSaving}
                helperText="Used in rules and manual review pickers."
              />
              <TextField
                label="Description"
                value={categoryCrudDraft.description}
                onChange={(e) =>
                  setCategoryCrudDraft((d) => (d ? { ...d, description: e.target.value } : d))
                }
                fullWidth
                size="small"
                multiline
                minRows={2}
                disabled={categoryCrudSaving}
              />
              <FormControlLabel
                control={
                  <Switch
                    checked={categoryCrudDraft.is_key_category}
                    onChange={(_, checked) =>
                      setCategoryCrudDraft((d) => (d ? { ...d, is_key_category: checked } : d))
                    }
                    disabled={categoryCrudSaving}
                  />
                }
                label="Favourite (starred in Categories table and category filters)"
              />
              {categoryCrudDraft.id != null ? (
                <FormControlLabel
                  control={
                    <Switch
                      checked={categoryCrudDraft.is_active}
                      onChange={(_, checked) =>
                        setCategoryCrudDraft((d) => (d ? { ...d, is_active: checked } : d))
                      }
                      disabled={categoryCrudSaving}
                    />
                  }
                  label="Active (shown in pickers)"
                />
              ) : null}
            </Stack>
          ) : null}
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              if (categoryCrudSaving) return;
              setCategoryCrudOpen(false);
              setCategoryCrudDraft(null);
              setCategoryCrudFormError('');
              setCategoryCrudTarget('categories');
              setCategoryCrudPendingGranularTxId(null);
            }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={() => void submitCategoryCrudDialog()}
            disabled={categoryCrudSaving || !!categoryCrudNameHints.duplicateOf}
          >
            {categoryCrudSaving ? 'Saving…' : categoryCrudDraft?.id != null ? 'Save changes' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>
      <Dialog
        open={backfillAfterAcceptOpen}
        onClose={() => {
          if (backfillAfterAcceptBusy) return;
          closeBackfillAfterAccept();
          finishSimilarTxRulesReturnIfPending();
        }}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ pr: 1 }}>
          <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={1}>
            <Typography variant="h6" component="span">
              Backfill accepted rule?
            </Typography>
            <IconButton
              aria-label="Close"
              size="small"
              onClick={() => {
                if (backfillAfterAcceptBusy) return;
                closeBackfillAfterAccept();
                finishSimilarTxRulesReturnIfPending();
              }}
              disabled={backfillAfterAcceptBusy}
            >
              <CloseIcon />
            </IconButton>
          </Stack>
        </DialogTitle>
        <DialogContent dividers>
          <Stack spacing={1.25}>
            {backfillAfterAcceptPrefixedId ? (
              <Typography variant="body2" color="text.secondary">
                Rule ID{' '}
                <Box component="span" sx={{ fontWeight: 700, color: 'text.primary' }}>
                  {backfillAfterAcceptPrefixedId}
                </Box>
              </Typography>
            ) : null}
            <Typography variant="body2" color="text.secondary">
              <strong>{backfillAfterAcceptRuleName || 'This rule'}</strong> was accepted. Apply{' '}
              <strong>only this rule</strong> to transactions that match it now?
            </Typography>
            <Typography variant="caption" color="text.secondary">
              Uses apply-single for this rule’s matches only — not “Re-run ALL Classification Rules”
              (that would strip/re-tag unconfirmed rows under every active rule).
            </Typography>
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button
            variant="contained"
            disabled={backfillAfterAcceptBusy}
            onClick={() => void runBackfillAcceptedRule()}
          >
            {backfillAfterAcceptBusy ? 'Backfilling…' : 'Yes, backfill'}
          </Button>
          <Button
            disabled={backfillAfterAcceptBusy}
            onClick={() => {
              closeBackfillAfterAccept();
              finishSimilarTxRulesReturnIfPending();
            }}
          >
            Not now
          </Button>
        </DialogActions>
      </Dialog>
      <Dialog
        open={proposeRuleOpen}
        onClose={() => {
          if (proposeRuleSaving) return;
          setProposeRuleOpen(false);
          setProposeRuleTx(null);
        }}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Propose classification rule</DialogTitle>
        <DialogContent dividers>
          <Stack spacing={2} sx={{ pt: 0.5 }}>
            <Typography variant="body2" color="text.secondary">
              Saves a draft to the <strong>Rule proposals</strong> tab. Accept there to add it to live rules.
            </Typography>
            {proposeRuleTx ? (
              <Typography variant="caption" color="text.secondary" component="div">
                Transaction #{proposeRuleTx.id} ·{' '}
                {formatDate(proposeRuleTx.completed_at || proposeRuleTx.date)} ·{' '}
                <Box component="span" sx={{ color: txReviewAmountColor(effectiveTxAmount(proposeRuleTx)) }}>
                  {formatAmount(effectiveTxAmount(proposeRuleTx), proposeRuleTx.currency)}
                </Box>
              </Typography>
            ) : null}
            <TextField
              label="Rule name"
              value={proposeRuleName}
              onChange={(e) => setProposeRuleName(e.target.value)}
              fullWidth
              required
              size="small"
            />
            <FormControl size="small" fullWidth required>
              <InputLabel id="propose-rule-category-label">Category</InputLabel>
              <Select
                labelId="propose-rule-category-label"
                label="Category"
                value={proposeRuleCategoryId === '' ? '' : proposeRuleCategoryId}
                onChange={(e) => {
                  const raw = e.target.value;
                  setProposeRuleCategoryId(!selectHasChoice(raw) ? '' : Number(raw));
                }}
              >
                <MenuItem value="">
                  <em>Select category</em>
                </MenuItem>
                {categoriesSortedByName
                  .filter((c) => !isTopLevelCashflowHeadingName(c.name))
                  .map((c) => (
                    <MenuItem key={`propose-cat-${c.id}`} value={c.id}>
                      {c.name}
                    </MenuItem>
                  ))}
              </Select>
            </FormControl>
            <TextField
              label="Canonical supplier (match)"
              value={proposeRuleSupplier}
              onChange={(e) => setProposeRuleSupplier(e.target.value)}
              fullWidth
              size="small"
              helperText="Default match type for new classification rules. Uses the catalog supplier name (not merchant text)."
            />
            <TextField
              label="Description contains (match)"
              value={proposeRuleDescription}
              onChange={(e) => setProposeRuleDescription(e.target.value)}
              fullWidth
              size="small"
              helperText="Optional extra text matcher when the bank description identifies the payee."
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              if (proposeRuleSaving) return;
              setProposeRuleOpen(false);
              setProposeRuleTx(null);
            }}
          >
            Cancel
          </Button>
          <Button variant="contained" onClick={() => void submitProposeRule()} disabled={proposeRuleSaving}>
            {proposeRuleSaving ? 'Saving…' : 'Save proposal'}
          </Button>
        </DialogActions>
      </Dialog>
      <Dialog
        open={similarTxBaseRow != null}
        onClose={closeSimilarTransactionsDialog}
        maxWidth={false}
        fullWidth
        PaperProps={{ sx: { width: 'min(98vw, 1600px)', maxWidth: 'min(98vw, 1600px)' } }}
        sx={{ zIndex: (theme) => theme.zIndex.snackbar + 25 }}
      >
        <DialogTitle
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 1,
            pr: 1,
            boxSizing: 'border-box'
          }}
        >
          Similar transactions
          <IconButton
            aria-label="Close similar transactions"
            edge="end"
            size="small"
            onClick={closeSimilarTransactionsDialog}
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers sx={{ maxHeight: { xs: '78vh', sm: '84vh' }, overflowY: 'auto', px: 0 }}>
          {similarTxLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 5 }}>
              <CircularProgress size={32} />
            </Box>
          ) : similarTxError ? (
            <Alert severity="warning" sx={{ mx: 2, my: 2 }}>
              {similarTxError}
            </Alert>
          ) : (
            <RenderErrorBoundary
              resetKey={`${similarTxBaseRow?.id ?? 'none'}:${similarTxRows.length}:${filteredSimilarTxRows.length}:${excludedSimilarTxRows.length}`}
              fallback={
                <Alert severity="error" sx={{ mx: 2, my: 2 }}>
                  Similar transactions failed to render due to malformed data. Please close and reopen the dialog.
                </Alert>
              }
            >
              <>
                {similarTxRows.length > 0 ? (
                  <Box
                    sx={{
                      display: 'flex',
                      flexDirection: 'row',
                      flexWrap: 'nowrap',
                      alignItems: 'center',
                      gap: 1,
                      px: 2,
                      py: 1.5,
                      overflowX: 'hidden',
                      minWidth: 0
                    }}
                  >
                    <ClearableSearchTextField
                      size="small"
                      label="Text search"
                      value={similarTxFilterText}
                      onChange={setSimilarTxFilterText}
                      sx={{ minWidth: 160, flex: '0 0 auto' }}
                    />
                    <FormControl size="small" sx={{ minWidth: 128, flex: '0 0 auto' }}>
                      <Select
                        value={similarTxFilterAccount}
                        displayEmpty
                        onChange={(e) => setSimilarTxFilterAccount(String(e.target.value || 'all'))}
                        MenuProps={{
                          PaperProps: {
                            sx: { zIndex: (theme) => theme.zIndex.snackbar + 200 }
                          }
                        }}
                      >
                        <MenuItem value="all">All accounts</MenuItem>
                        {similarTxAccountOptions.map((account) => (
                          <MenuItem key={`similar-acct-${account}`} value={account}>
                            {account}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                    <FormControl size="small" sx={{ minWidth: 128, flex: '0 0 auto' }}>
                      <Select
                        value={similarTxFilterHeading}
                        displayEmpty
                        renderValue={(v) => {
                          if (v === 'all') return 'All cash flow';
                          if (v === 'in') return 'In';
                          if (v === 'out') return 'Out';
                          return String(v);
                        }}
                        onChange={(e) =>
                          setSimilarTxFilterHeading(String(e.target.value || 'all') as 'all' | 'in' | 'out')
                        }
                        MenuProps={{
                          PaperProps: {
                            sx: { zIndex: (theme) => theme.zIndex.snackbar + 200 }
                          }
                        }}
                      >
                        <MenuItem value="all">All cash flow</MenuItem>
                        <MenuItem value="in">In</MenuItem>
                        <MenuItem value="out">Out</MenuItem>
                      </Select>
                    </FormControl>
                    <TextField
                      size="small"
                      label="Amount min"
                      value={similarTxFilterAmountMin}
                      onChange={(e) => setSimilarTxFilterAmountMin(e.target.value)}
                      sx={{ minWidth: 108, flex: '0 0 auto' }}
                      InputProps={buildClearableInputProps(
                        similarTxFilterAmountMin,
                        () => setSimilarTxFilterAmountMin(''),
                        undefined,
                        'Clear amount minimum'
                      )}
                    />
                    <TextField
                      size="small"
                      label="Amount max"
                      value={similarTxFilterAmountMax}
                      onChange={(e) => setSimilarTxFilterAmountMax(e.target.value)}
                      sx={{ minWidth: 108, flex: '0 0 auto' }}
                      InputProps={buildClearableInputProps(
                        similarTxFilterAmountMax,
                        () => setSimilarTxFilterAmountMax(''),
                        undefined,
                        'Clear amount maximum'
                      )}
                    />
                    <TextField
                      size="small"
                      label="Date from"
                      type="date"
                      value={similarTxFilterDateFrom}
                      onChange={(e) => setSimilarTxFilterDateFrom(e.target.value)}
                      sx={{ minWidth: 132, flex: '0 0 auto' }}
                      InputLabelProps={{ shrink: true }}
                      InputProps={buildClearableInputProps(
                        similarTxFilterDateFrom,
                        () => setSimilarTxFilterDateFrom(''),
                        undefined,
                        'Clear date from'
                      )}
                    />
                    <TextField
                      size="small"
                      label="Date to"
                      type="date"
                      value={similarTxFilterDateTo}
                      onChange={(e) => setSimilarTxFilterDateTo(e.target.value)}
                      sx={{ minWidth: 132, flex: '0 0 auto' }}
                      InputLabelProps={{ shrink: true }}
                      InputProps={buildClearableInputProps(
                        similarTxFilterDateTo,
                        () => setSimilarTxFilterDateTo(''),
                        undefined,
                        'Clear date to'
                      )}
                    />
                    <TextField
                      size="small"
                      label="Score min"
                      value={similarTxFilterScoreMin}
                      onChange={(e) => setSimilarTxFilterScoreMin(e.target.value)}
                      sx={{ minWidth: 88, flex: '0 0 auto' }}
                      InputProps={buildClearableInputProps(
                        similarTxFilterScoreMin,
                        () => setSimilarTxFilterScoreMin(SIMILAR_TX_DEFAULT_SCORE_MIN),
                        undefined,
                        'Clear score minimum'
                      )}
                    />
                    <TextField
                      size="small"
                      label="Score max"
                      value={similarTxFilterScoreMax}
                      onChange={(e) => setSimilarTxFilterScoreMax(e.target.value)}
                      sx={{ minWidth: 88, flex: '0 0 auto' }}
                      InputProps={buildClearableInputProps(
                        similarTxFilterScoreMax,
                        () => setSimilarTxFilterScoreMax(''),
                        undefined,
                        'Clear score maximum'
                      )}
                    />
                  </Box>
                ) : null}
                {similarTxRows.length === 0 ? (
                  <Typography variant="body2" color="text.secondary" sx={{ px: 2, py: 2 }}>
                    No strong matches found yet in the current review dataset.
                  </Typography>
                ) : (
                  <TableContainer sx={{ overflowX: 'hidden' }}>
                    <Table size="small" stickyHeader aria-label="Similar transactions">
                      <TableHead>
                        <TableRow>
                          <TableCell padding="checkbox" align="center">
                            <Checkbox
                              size="small"
                              checked={allFilteredSimilarTxSelected}
                              indeterminate={
                                selectedSimilarTxFilteredCount > 0 &&
                                selectedSimilarTxFilteredCount < filteredSimilarTxIds.length
                              }
                              onChange={(e) => {
                                if (e.target.checked) {
                                  setSimilarTxSelectedIds(filteredSimilarTxIds);
                                } else {
                                  setSimilarTxSelectedIds([]);
                                }
                              }}
                              inputProps={{ 'aria-label': 'Select all similar transactions' }}
                            />
                          </TableCell>
                          <TableCell align="center">
                            <TableSortLabel
                              sx={{ justifyContent: 'center', width: '100%' }}
                              active={similarTxSort.key === 'date'}
                              direction={similarTxSort.key === 'date' ? similarTxSort.dir : 'asc'}
                              onClick={() => handleSimilarTxSort('date')}
                            >
                              Date
                            </TableSortLabel>
                          </TableCell>
                          <TableCell align="center">
                            <TableSortLabel
                              sx={{ justifyContent: 'center', width: '100%' }}
                              active={similarTxSort.key === 'id'}
                              direction={similarTxSort.key === 'id' ? similarTxSort.dir : 'asc'}
                              onClick={() => handleSimilarTxSort('id')}
                            >
                              TX ID
                            </TableSortLabel>
                          </TableCell>
                          <TableCell align="center">
                            <TableSortLabel
                              sx={{ justifyContent: 'center', width: '100%' }}
                              active={similarTxSort.key === 'amount'}
                              direction={similarTxSort.key === 'amount' ? similarTxSort.dir : 'asc'}
                              onClick={() => handleSimilarTxSort('amount')}
                            >
                              Amount
                            </TableSortLabel>
                          </TableCell>
                          <TableCell align="center">
                            <TableSortLabel
                              sx={{ justifyContent: 'center', width: '100%' }}
                              active={similarTxSort.key === 'description'}
                              direction={similarTxSort.key === 'description' ? similarTxSort.dir : 'asc'}
                              onClick={() => handleSimilarTxSort('description')}
                            >
                              Description
                            </TableSortLabel>
                          </TableCell>
                          <TableCell align="center" sx={{ whiteSpace: 'nowrap', width: 80, maxWidth: 80, px: 0.75 }}>
                            <TableSortLabel
                              sx={{ justifyContent: 'center', width: '100%' }}
                              active={similarTxSort.key === 'cashFlow'}
                              direction={similarTxSort.key === 'cashFlow' ? similarTxSort.dir : 'asc'}
                              onClick={() => handleSimilarTxSort('cashFlow')}
                            >
                              Cash flow
                            </TableSortLabel>
                          </TableCell>
                          <TableCell align="center">
                            <TableSortLabel
                              sx={{ justifyContent: 'center', width: '100%' }}
                              active={similarTxSort.key === 'supplierTag'}
                              direction={similarTxSort.key === 'supplierTag' ? similarTxSort.dir : 'asc'}
                              onClick={() => handleSimilarTxSort('supplierTag')}
                            >
                              Supplier
                            </TableSortLabel>
                          </TableCell>
                          <TableCell align="center">
                            <TableSortLabel
                              sx={{ justifyContent: 'center', width: '100%' }}
                              active={similarTxSort.key === 'category'}
                              direction={similarTxSort.key === 'category' ? similarTxSort.dir : 'asc'}
                              onClick={() => handleSimilarTxSort('category')}
                            >
                              Category
                            </TableSortLabel>
                          </TableCell>
                          <TableCell align="center">
                            <TableSortLabel
                              sx={{ justifyContent: 'center', width: '100%' }}
                              active={similarTxSort.key === 'vat'}
                              direction={similarTxSort.key === 'vat' ? similarTxSort.dir : 'asc'}
                              onClick={() => handleSimilarTxSort('vat')}
                            >
                              VAT rate
                            </TableSortLabel>
                          </TableCell>
                          <TableCell align="center" sx={{ whiteSpace: 'normal', lineHeight: 1.2, minWidth: 112 }}>
                            <TableSortLabel
                              sx={{ justifyContent: 'center', width: '100%' }}
                              active={similarTxSort.key === 'committed'}
                              direction={similarTxSort.key === 'committed' ? similarTxSort.dir : 'asc'}
                              onClick={() => handleSimilarTxSort('committed')}
                            >
                              <Box
                                component="span"
                                sx={{ display: 'inline-flex', flexDirection: 'column', alignItems: 'center' }}
                              >
                                <span>Committed</span>
                                <span>status</span>
                              </Box>
                            </TableSortLabel>
                          </TableCell>
                          <TableCell align="center" sx={(theme) => ({ bgcolor: alpha(theme.palette.info.main, 0.18) })}>
                            <TableSortLabel
                              sx={{ justifyContent: 'center', width: '100%' }}
                              active={similarTxSort.key === 'similarityScore'}
                              direction={similarTxSort.key === 'similarityScore' ? similarTxSort.dir : 'desc'}
                              onClick={() => handleSimilarTxSort('similarityScore')}
                            >
                              Similarity score
                            </TableSortLabel>
                          </TableCell>
                          <TableCell
                            align="center"
                            sx={(theme) => ({
                              bgcolor: alpha(theme.palette.info.main, 0.18),
                              minWidth: 210,
                              whiteSpace: 'normal',
                              lineHeight: 1.2
                            })}
                          >
                            <TableSortLabel
                              sx={{ justifyContent: 'center', width: '100%' }}
                              active={similarTxSort.key === 'sharedDescriptionTerms'}
                              direction={similarTxSort.key === 'sharedDescriptionTerms' ? similarTxSort.dir : 'asc'}
                              onClick={() => handleSimilarTxSort('sharedDescriptionTerms')}
                            >
                              <Box
                                component="span"
                                sx={{ display: 'inline-flex', flexDirection: 'column', alignItems: 'center' }}
                              >
                                <span>Shared</span>
                                <span>Description</span>
                                <span>Terms</span>
                              </Box>
                            </TableSortLabel>
                          </TableCell>
                          <TableCell
                            align="center"
                            sx={(theme) => ({ bgcolor: alpha(theme.palette.info.main, 0.18), minWidth: 130 })}
                          >
                            <TableSortLabel
                              sx={{ justifyContent: 'center', width: '100%' }}
                              active={similarTxSort.key === 'similarAccount'}
                              direction={similarTxSort.key === 'similarAccount' ? similarTxSort.dir : 'asc'}
                              onClick={() => handleSimilarTxSort('similarAccount')}
                            >
                              Similar account (Y/N)
                            </TableSortLabel>
                          </TableCell>
                          <TableCell
                            align="center"
                            sx={(theme) => ({
                              bgcolor: alpha(theme.palette.info.main, 0.18),
                              minWidth: 220,
                              whiteSpace: 'normal',
                              lineHeight: 1.2
                            })}
                          >
                            <TableSortLabel
                              sx={{ justifyContent: 'center', width: '100%' }}
                              active={similarTxSort.key === 'otherSimilarities'}
                              direction={similarTxSort.key === 'otherSimilarities' ? similarTxSort.dir : 'asc'}
                              onClick={() => handleSimilarTxSort('otherSimilarities')}
                            >
                              <Box
                                component="span"
                                sx={{ display: 'inline-flex', flexDirection: 'column', alignItems: 'center' }}
                              >
                                <span>Other</span>
                                <span>Similarities</span>
                              </Box>
                            </TableSortLabel>
                          </TableCell>
                        </TableRow>
                      </TableHead>
                      {filteredSimilarTxRows.length > 0 ? (
                      <TableBody>
                        {sortedSimilarTxRows.map((tx) => (
                          <TableRow
                            key={`similar-${tx.key}`}
                            hover
                            sx={(theme) =>
                              similarTxBaseRow != null && Number(tx.id) === Number(similarTxBaseRow.id)
                                ? {
                                    bgcolor: alpha(theme.palette.primary.main, 0.1),
                                    boxShadow: `inset 4px 0 0 ${theme.palette.primary.main}`
                                  }
                                : {}
                            }
                          >
                            <TableCell padding="checkbox" sx={{ verticalAlign: 'top' }}>
                              <Checkbox
                                size="small"
                                checked={similarTxSelectedIds.includes(Number(tx.id))}
                                onChange={(e) => {
                                  const id = Number(tx.id);
                                  if (!Number.isFinite(id) || id <= 0) return;
                                  setSimilarTxSelectedIds((prev) => {
                                    if (e.target.checked) {
                                      return prev.includes(id) ? prev : [...prev, id];
                                    }
                                    return prev.filter((x) => x !== id);
                                  });
                                }}
                                inputProps={{ 'aria-label': `Select transaction ${tx.id}` }}
                              />
                            </TableCell>
                            <TableCell sx={{ whiteSpace: 'nowrap', verticalAlign: 'top' }}>
                              {formatDate(tx.date)}
                            </TableCell>
                            <TableCell align="right" sx={{ whiteSpace: 'nowrap', verticalAlign: 'top' }}>
                              {String(Number(tx.id))}
                            </TableCell>
                            <TableCell align="right" sx={{ whiteSpace: 'nowrap', verticalAlign: 'top' }}>
                              <Typography variant="body2" component="span" sx={{ color: txReviewAmountColor(tx.amount) }}>
                                {formatAmount(tx.amount, tx.currency)}
                              </Typography>
                            </TableCell>
                            <TableCell sx={{ maxWidth: 320, wordBreak: 'break-word', verticalAlign: 'top' }}>
                              {tx.description || '—'}
                            </TableCell>
                            <TableCell sx={{ verticalAlign: 'top', width: 80, maxWidth: 80, px: 0.75 }}>
                              <Chip
                                size="small"
                                label={formatCashflowInOutHeadingLabel(tx.cashFlow)}
                                variant="filled"
                                sx={incomeExpenseChipSx(tx.cashFlow)}
                              />
                            </TableCell>
                            <TableCell sx={{ verticalAlign: 'top' }}>
                              {(() => {
                                const supplierTag = String(tx.supplier || '').trim();
                                if (!supplierTag) {
                                  return (
                                    <Typography variant="body2" color="text.secondary">
                                      —
                                    </Typography>
                                  );
                                }
                                return <Chip size="small" label={supplierTag} variant="filled" />;
                              })()}
                            </TableCell>
                            <TableCell sx={{ verticalAlign: 'top' }}>
                              {(() => {
                                const categoryLabel =
                                  tx.category &&
                                  !isUnclassifiedReviewCategoryName(tx.category) &&
                                  !isTopLevelCashflowHeadingName(tx.category)
                                    ? tx.category
                                    : '—';
                                if (categoryLabel === '—') {
                                  return (
                                    <Typography variant="body2" color="text.secondary">
                                      —
                                    </Typography>
                                  );
                                }
                                return (
                                  <Chip
                                    size="small"
                                    label={categoryLabel}
                                    variant="filled"
                                    sx={categoryChipSx(categoryLabel)}
                                  />
                                );
                              })()}
                            </TableCell>
                            <TableCell sx={{ verticalAlign: 'top' }}>
                              {(() => {
                                const vatLabel = String(tx.vat_rate || '').trim();
                                if (!vatLabel || vatLabel.toLowerCase() === 'none' || vatLabel.toLowerCase() === 'n/a') {
                                  return (
                                    <Typography variant="body2" color="text.secondary">
                                      —
                                    </Typography>
                                  );
                                }
                                return <Chip size="small" label={vatLabel} variant="filled" />;
                              })()}
                            </TableCell>
                            <TableCell align="center" sx={{ verticalAlign: 'top' }}>
                              <Chip
                                size="small"
                                label={unifiedCommittedLabel(tx)}
                                color={isUnifiedRowCommitted(tx) ? 'success' : 'default'}
                                variant={isUnifiedRowCommitted(tx) ? 'filled' : 'outlined'}
                              />
                            </TableCell>
                            <TableCell sx={{ verticalAlign: 'top' }}>
                              <Chip size="small" variant="outlined" label={tx.similarityScore} />
                            </TableCell>
                            <TableCell sx={{ maxWidth: 280, wordBreak: 'break-word', verticalAlign: 'top' }}>
                              {tx.sharedDescriptionTerms || '—'}
                            </TableCell>
                            <TableCell sx={{ whiteSpace: 'nowrap', verticalAlign: 'top' }}>
                              {tx.similarAccount}
                            </TableCell>
                            <TableCell sx={{ maxWidth: 320, wordBreak: 'break-word', verticalAlign: 'top' }}>
                              {tx.otherSimilarities || '—'}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                      ) : (
                      <TableBody>
                        <TableRow>
                          <TableCell colSpan={SIMILAR_TX_MODAL_TABLE_COL_SPAN} sx={{ py: 2, px: 2 }}>
                            <Typography variant="body2" color="text.secondary">
                              No matching transactions for the current modal filters.
                            </Typography>
                          </TableCell>
                        </TableRow>
                      </TableBody>
                      )}
                      {excludedSimilarTxRows.length > 0 ? (
                      <TableBody>
                        <TableRow>
                          <TableCell
                            colSpan={SIMILAR_TX_MODAL_TABLE_COL_SPAN}
                            sx={{
                              py: 1.5,
                              px: 2,
                              bgcolor: 'action.hover',
                              borderTop: '2px solid',
                              borderColor: 'divider'
                            }}
                          >
                            <Typography variant="subtitle2">
                              Outside current filters ({excludedSimilarTxRows.length})
                            </Typography>
                            <Typography variant="caption" color="text.secondary" component="div" sx={{ mt: 0.5 }}>
                              These rows do not match the filters above. They are excluded from select-all and from bulk
                              actions until you adjust filters.
                            </Typography>
                          </TableCell>
                        </TableRow>
                        {sortedSimilarTxExcludedRows.map((tx) => (
                          <TableRow
                            key={`similar-excluded-${tx.key}`}
                            hover
                            sx={(theme) =>
                              similarTxBaseRow != null && Number(tx.id) === Number(similarTxBaseRow.id)
                                ? {
                                    bgcolor: alpha(theme.palette.primary.main, 0.1),
                                    boxShadow: `inset 4px 0 0 ${theme.palette.primary.main}`
                                  }
                                : { opacity: 0.92 }
                            }
                          >
                            <TableCell padding="checkbox" sx={{ verticalAlign: 'top' }}>
                              <Checkbox
                                size="small"
                                disabled
                                checked={false}
                                inputProps={{ 'aria-label': `Transaction ${tx.id} (outside filters)` }}
                              />
                            </TableCell>
                            <TableCell sx={{ whiteSpace: 'nowrap', verticalAlign: 'top' }}>
                              {formatDate(tx.date)}
                            </TableCell>
                            <TableCell align="right" sx={{ whiteSpace: 'nowrap', verticalAlign: 'top' }}>
                              {String(Number(tx.id))}
                            </TableCell>
                            <TableCell align="right" sx={{ whiteSpace: 'nowrap', verticalAlign: 'top' }}>
                              <Typography variant="body2" component="span" sx={{ color: txReviewAmountColor(tx.amount) }}>
                                {formatAmount(tx.amount, tx.currency)}
                              </Typography>
                            </TableCell>
                            <TableCell sx={{ maxWidth: 320, wordBreak: 'break-word', verticalAlign: 'top' }}>
                              {tx.description || '—'}
                            </TableCell>
                            <TableCell sx={{ verticalAlign: 'top', width: 80, maxWidth: 80, px: 0.75 }}>
                              <Chip
                                size="small"
                                label={formatCashflowInOutHeadingLabel(tx.cashFlow)}
                                variant="filled"
                                sx={incomeExpenseChipSx(tx.cashFlow)}
                              />
                            </TableCell>
                            <TableCell sx={{ verticalAlign: 'top' }}>
                              {(() => {
                                const supplierTag = String(tx.supplier || '').trim();
                                if (!supplierTag) {
                                  return (
                                    <Typography variant="body2" color="text.secondary">
                                      —
                                    </Typography>
                                  );
                                }
                                return <Chip size="small" label={supplierTag} variant="filled" />;
                              })()}
                            </TableCell>
                            <TableCell sx={{ verticalAlign: 'top' }}>
                              {(() => {
                                const categoryLabel =
                                  tx.category &&
                                  !isUnclassifiedReviewCategoryName(tx.category) &&
                                  !isTopLevelCashflowHeadingName(tx.category)
                                    ? tx.category
                                    : '—';
                                if (categoryLabel === '—') {
                                  return (
                                    <Typography variant="body2" color="text.secondary">
                                      —
                                    </Typography>
                                  );
                                }
                                return (
                                  <Chip
                                    size="small"
                                    label={categoryLabel}
                                    variant="filled"
                                    sx={categoryChipSx(categoryLabel)}
                                  />
                                );
                              })()}
                            </TableCell>
                            <TableCell sx={{ verticalAlign: 'top' }}>
                              {(() => {
                                const vatLabel = String(tx.vat_rate || '').trim();
                                if (!vatLabel || vatLabel.toLowerCase() === 'none' || vatLabel.toLowerCase() === 'n/a') {
                                  return (
                                    <Typography variant="body2" color="text.secondary">
                                      —
                                    </Typography>
                                  );
                                }
                                return <Chip size="small" label={vatLabel} variant="filled" />;
                              })()}
                            </TableCell>
                            <TableCell align="center" sx={{ verticalAlign: 'top' }}>
                              <Chip
                                size="small"
                                label={unifiedCommittedLabel(tx)}
                                color={isUnifiedRowCommitted(tx) ? 'success' : 'default'}
                                variant={isUnifiedRowCommitted(tx) ? 'filled' : 'outlined'}
                              />
                            </TableCell>
                            <TableCell sx={{ verticalAlign: 'top' }}>
                              <Chip size="small" variant="outlined" label={tx.similarityScore} />
                            </TableCell>
                            <TableCell sx={{ maxWidth: 280, wordBreak: 'break-word', verticalAlign: 'top' }}>
                              {tx.sharedDescriptionTerms || '—'}
                            </TableCell>
                            <TableCell sx={{ whiteSpace: 'nowrap', verticalAlign: 'top' }}>
                              {tx.similarAccount}
                            </TableCell>
                            <TableCell sx={{ maxWidth: 320, wordBreak: 'break-word', verticalAlign: 'top' }}>
                              {tx.otherSimilarities || '—'}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                      ) : null}
                    </Table>
                  </TableContainer>
                )}
              </>
            </RenderErrorBoundary>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 2, py: 2 }}>
          <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap alignItems="center" sx={{ width: '100%' }}>
            <Button
              variant="contained"
              color="success"
              onClick={() => void commitSimilarModalRows()}
              disabled={
                similarTxGenerateRuleBusy != null ||
                similarTxCommitBusy ||
                similarTxRunAllBusy ||
                similarTxUncommitBusy ||
                similarTxStripBusy ||
                unifiedBulkBusy ||
                selectedSimilarTxFilteredCount === 0 ||
                similarTxLoading ||
                !!similarTxError
              }
            >
              {similarTxCommitBusy ? 'Committing…' : `Commit (${selectedSimilarTxFilteredCount})`}
            </Button>
            <Button
              variant="contained"
              onClick={() => void rerunRulesForSimilarModalRows()}
              disabled={
                similarTxGenerateRuleBusy != null ||
                similarTxRunAllBusy ||
                similarTxCommitBusy ||
                similarTxUncommitBusy ||
                similarTxStripBusy ||
                unifiedBulkBusy ||
                selectedSimilarTxFilteredCount === 0 ||
                similarTxLoading ||
                !!similarTxError
              }
            >
              {similarTxRunAllBusy ? 'Running rules…' : `Re-run Rules (${selectedSimilarTxFilteredCount})`}
            </Button>
            <Button
              variant="outlined"
              color="warning"
              onClick={() => void uncommitSimilarModalRows()}
              disabled={
                similarTxGenerateRuleBusy != null ||
                similarTxUncommitBusy ||
                similarTxCommitBusy ||
                similarTxRunAllBusy ||
                similarTxStripBusy ||
                unifiedBulkBusy ||
                similarModalCommittedRows.length === 0 ||
                similarTxLoading ||
                !!similarTxError
              }
            >
              {similarTxUncommitBusy ? 'Uncommitting…' : `Uncommit (${similarModalCommittedRows.length})`}
            </Button>
            <Button
              variant="outlined"
              color="error"
              onClick={() => void stripTagsSimilarModalRows()}
              disabled={
                similarTxGenerateRuleBusy != null ||
                similarTxStripBusy ||
                similarTxCommitBusy ||
                similarTxRunAllBusy ||
                similarTxUncommitBusy ||
                unifiedBulkBusy ||
                similarModalEditableRows.length === 0 ||
                similarTxLoading ||
                !!similarTxError
              }
            >
              {similarTxStripBusy ? 'Removing tags…' : 'Delete All Tags'}
            </Button>
            <Button
              variant="outlined"
              disabled={
                similarTxGenerateRuleBusy != null ||
                similarTxCommitBusy ||
                similarTxRunAllBusy ||
                similarTxUncommitBusy ||
                similarTxStripBusy ||
                unifiedBulkBusy ||
                selectedSimilarTxFilteredCount === 0 ||
                similarTxLoading ||
                !!similarTxError
              }
              onClick={() => {
                setSimilarTxGenerateKindsDraft({
                  category: false,
                  supplier: false,
                  vat: false,
                  special: false
                });
                setSimilarTxGenerateRulesPickerOpen(true);
              }}
            >
              {similarTxGenerateRuleBusy != null ? 'Working…' : 'Generate Rule(s)'}
            </Button>
            <FormControl size="small" sx={{ minWidth: 170 }}>
              <InputLabel id="similar-bulk-tag-heading-label" sx={{ whiteSpace: 'pre-line', lineHeight: 1.15 }}>
                Cash{'\n'}Flow
              </InputLabel>
              <Select
                labelId="similar-bulk-tag-heading-label"
                label={'Cash\nFlow'}
                value={similarTxBulkHeadingDraft}
                onChange={(e) => setSimilarTxBulkHeadingDraft(String(e.target.value || BULK_TAG_NO_CHANGE))}
                disabled={
                  similarTxGenerateRuleBusy != null ||
                  unifiedBulkBusy ||
                  similarTxCommitBusy ||
                  similarTxRunAllBusy ||
                  similarTxUncommitBusy ||
                  similarTxStripBusy ||
                  similarModalEditableRows.length === 0 ||
                  similarTxLoading ||
                  !!similarTxError
                }
              >
                <MenuItem value={BULK_TAG_NO_CHANGE}>No change</MenuItem>
                {headingCategories.map((c) => (
                  <MenuItem key={`similar-bulk-heading-${c.id}`} value={String(c.id)}>
                    {formatCashflowInOutHeadingLabel(c.name)}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl size="small" sx={{ minWidth: 170 }}>
              <InputLabel id="similar-bulk-tag-supplier-label">Supplier</InputLabel>
              <Select
                labelId="similar-bulk-tag-supplier-label"
                label="Supplier"
                value={similarTxBulkSupplierDraft}
                onChange={(e) => setSimilarTxBulkSupplierDraft(String(e.target.value || BULK_TAG_NO_CHANGE))}
                disabled={
                  similarTxGenerateRuleBusy != null ||
                  unifiedBulkBusy ||
                  similarTxCommitBusy ||
                  similarTxRunAllBusy ||
                  similarTxUncommitBusy ||
                  similarTxStripBusy ||
                  similarModalEditableRows.length === 0 ||
                  similarTxLoading ||
                  !!similarTxError
                }
              >
                <MenuItem value={BULK_TAG_NO_CHANGE}>No change</MenuItem>
                {unifiedSupplierOptions.map((name) => (
                  <MenuItem key={`similar-bulk-supplier-${name}`} value={name}>
                    {name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl size="small" sx={{ minWidth: 180 }}>
              <InputLabel id="similar-bulk-tag-category-label">Category</InputLabel>
              <Select
                labelId="similar-bulk-tag-category-label"
                label="Category"
                value={similarTxBulkCategoryDraft}
                onChange={(e) => setSimilarTxBulkCategoryDraft(String(e.target.value || BULK_TAG_NO_CHANGE))}
                disabled={
                  similarTxGenerateRuleBusy != null ||
                  unifiedBulkBusy ||
                  similarTxCommitBusy ||
                  similarTxRunAllBusy ||
                  similarTxUncommitBusy ||
                  similarTxStripBusy ||
                  similarModalEditableRows.length === 0 ||
                  similarTxLoading ||
                  !!similarTxError
                }
              >
                <MenuItem value={BULK_TAG_NO_CHANGE}>No change</MenuItem>
                {granularCategories.map((c) => (
                  <MenuItem key={`similar-bulk-category-${c.id}`} value={String(c.id)}>
                    {c.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl size="small" sx={{ minWidth: 150 }}>
              <InputLabel id="similar-bulk-tag-vat-label">VAT rate</InputLabel>
              <Select
                labelId="similar-bulk-tag-vat-label"
                label="VAT rate"
                value={similarTxBulkVatDraft}
                onChange={(e) => setSimilarTxBulkVatDraft(String(e.target.value || BULK_TAG_NO_CHANGE))}
                disabled={
                  similarTxGenerateRuleBusy != null ||
                  unifiedBulkBusy ||
                  similarTxCommitBusy ||
                  similarTxRunAllBusy ||
                  similarTxUncommitBusy ||
                  similarTxStripBusy ||
                  similarModalEditableRows.length === 0 ||
                  similarTxLoading ||
                  !!similarTxError
                }
              >
                <MenuItem value={BULK_TAG_NO_CHANGE}>No change</MenuItem>
                {vatRatePickOptions.map((rate) => (
                  <MenuItem key={`similar-bulk-vat-${rate.label}`} value={rate.value}>
                    {rate.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <Button
              variant="contained"
              size="small"
              onClick={() => void applyTagsSimilarModalRows()}
              disabled={
                similarTxGenerateRuleBusy != null ||
                unifiedBulkBusy ||
                similarTxCommitBusy ||
                similarTxRunAllBusy ||
                similarTxUncommitBusy ||
                similarTxStripBusy ||
                !similarModalBulkTagHasApply ||
                similarModalEditableRows.length === 0 ||
                similarTxLoading ||
                !!similarTxError
              }
            >
              {unifiedBulkBusy ? 'Applying…' : 'Apply tags'}
            </Button>
            <Box sx={{ flexGrow: 1, minWidth: 8 }} />
            <Button onClick={closeSimilarTransactionsDialog}>Close</Button>
          </Stack>
        </DialogActions>
      </Dialog>
      <Dialog
        open={similarTxGenerateRulesPickerOpen}
        onClose={() => {
          if (similarTxGenerateRuleBusy != null) return;
          setSimilarTxGenerateRulesPickerOpen(false);
        }}
        maxWidth="sm"
        fullWidth
        sx={{ zIndex: (theme) => theme.zIndex.snackbar + 40 }}
      >
        <DialogTitle>Generate rule drafts</DialogTitle>
        <DialogContent dividers>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
            Select one or more types. Multiple selections run in pipeline order: supplier → classification → VAT →
            special case, then you&apos;ll land on the Rules tab (modal closes first).
          </Typography>
          <FormGroup>
            <FormControlLabel
              control={
                <Checkbox
                  checked={similarTxGenerateKindsDraft.supplier}
                  onChange={() =>
                    setSimilarTxGenerateKindsDraft((p) => ({ ...p, supplier: !p.supplier }))
                  }
                />
              }
              label="Supplier proposals"
            />
            <FormControlLabel
              control={
                <Checkbox
                  checked={similarTxGenerateKindsDraft.category}
                  onChange={() =>
                    setSimilarTxGenerateKindsDraft((p) => ({ ...p, category: !p.category }))
                  }
                />
              }
              label="Classification proposals"
            />
            <FormControlLabel
              control={
                <Checkbox
                  checked={similarTxGenerateKindsDraft.vat}
                  onChange={() => setSimilarTxGenerateKindsDraft((p) => ({ ...p, vat: !p.vat }))}
                />
              }
              label="VAT suggestions"
            />
            <FormControlLabel
              control={
                <Checkbox
                  checked={similarTxGenerateKindsDraft.special}
                  onChange={() =>
                    setSimilarTxGenerateKindsDraft((p) => ({ ...p, special: !p.special }))
                  }
                />
              }
              label="Special case draft"
            />
          </FormGroup>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setSimilarTxGenerateRulesPickerOpen(false)}
            disabled={similarTxGenerateRuleBusy != null}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            disabled={
              similarTxGenerateRuleBusy != null ||
              !SIMILAR_TX_GENERATE_KIND_ORDER.some((k) => similarTxGenerateKindsDraft[k])
            }
            onClick={confirmSimilarTxGenerateRulesFromPicker}
          >
            Generate
          </Button>
        </DialogActions>
      </Dialog>
      <Dialog
        open={matchTxSource != null}
        onClose={() => {
          if (matchTxLoading || matchTxLoadingMore) return;
          setMatchTxSource(null);
          setMatchTxRows([]);
          setMatchTxCursorId(null);
          setMatchTxHasMore(false);
          setMatchTxDateFrom('');
          setMatchTxDateTo('');
          setMatchTxAnchorDate('');
          setMatchTxFilterAccount('all');
          setMatchTxFilterHeading('all');
          setMatchTxFilterAmountMin('');
          setMatchTxFilterAmountMax('');
          setMatchTxFilterText('');
          setMatchTxError('');
          setMatchTxFooterNote('');
        }}
        maxWidth="xl"
        fullWidth
      >
        <DialogTitle>
          Matching transactions
          {matchTxSource ? (
            <Typography
              component="div"
              variant="body2"
              color="text.secondary"
              sx={{ fontWeight: 400, mt: 0.5 }}
            >
              {matchTxSource.kind === 'rule'
                ? matchTxSource.rule.name
                : matchTxSource.kind === 'proposal'
                  ? matchTxSource.label
                  : matchTxSource.kind === 'supplierRule'
                    ? `Supplier rule ${formatPrefixedRuleId('supplier', matchTxSource.row.id)} — ${canonicalSupplierLabel(
                        matchTxSource.row.supplier_name,
                        `Supplier #${matchTxSource.row.supplier_id}`
                      )}`
                    : matchTxSource.kind === 'supplierProposal'
                      ? `Supplier proposal ${formatSupplierProposalPrefixedId(matchTxSource.proposalId, 'proposed')} — ${matchTxSource.label}`
                      : matchTxSource.kind === 'vatRule'
                        ? `VAT rule ${formatPrefixedRuleId('vat', matchTxSource.row.id)} — ${String(matchTxSource.row.name || '').trim() || 'Unnamed'}`
                        : `Special Case rule ${formatPrefixedRuleId('specialCase', matchTxSource.row.id)} — ${matchTxSource.row.name}`}
              {matchTxSource.kind === 'proposal' ? (
                <Typography component="span" variant="caption" display="block" sx={{ mt: 0.5 }}>
                  Pending proposal — matches are computed live from transactions (not from tags).
                </Typography>
              ) : matchTxSource.kind === 'rule' ? (
                <Typography component="span" variant="caption" display="block" sx={{ mt: 0.5 }}>
                  Live rule match — same logic as classification apply (not based on existing tags).
                </Typography>
              ) : matchTxSource.kind === 'supplierRule' ? (
                <Typography component="span" variant="caption" display="block" sx={{ mt: 0.5 }}>
                  Live supplier-alias preview — same matching as bulk apply (merchant vs description vs reference depends on
                  each criterion on the rule, not necessarily all fields).
                </Typography>
              ) : matchTxSource.kind === 'supplierProposal' ? (
                <Typography component="span" variant="caption" display="block" sx={{ mt: 0.5 }}>
                  Preview on historical data — same logic as the accepted rule. Your canonical supplier must already
                  exist in the Suppliers catalog.
                </Typography>
              ) : matchTxSource.kind === 'vatRule' ? (
                <Typography component="span" variant="caption" display="block" sx={{ mt: 0.5 }}>
                  Live VAT-rule scope from currently tagged transactions in the rule categories.
                </Typography>
              ) : (
                <Typography component="span" variant="caption" display="block" sx={{ mt: 0.5 }}>
                  Live Special Case scope from currently tagged transactions that satisfy this override rule.
                </Typography>
              )}
            </Typography>
          ) : null}
        </DialogTitle>
        <DialogContent
          dividers
          ref={matchTxScrollRef}
          sx={{ maxHeight: { xs: '60vh', sm: 480 }, overflowY: 'auto', px: 0 }}
        >
          {matchTxError ? (
            <Alert severity="error" sx={{ mx: 2, mt: 1 }} onClose={() => setMatchTxError('')}>
              {matchTxError}
            </Alert>
          ) : null}
          <Box sx={{ px: 2, pt: 1, pb: 1.5, borderBottom: '1px solid', borderColor: 'divider' }}>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems={{ xs: 'stretch', sm: 'center' }}>
              <TextField
                size="small"
                type="date"
                label="Date from"
                InputLabelProps={{ shrink: true }}
                value={matchTxDateFrom}
                onChange={(e) => setMatchTxDateFrom(String(e.target.value || ''))}
                sx={{ minWidth: 165 }}
              />
              <TextField
                size="small"
                type="date"
                label="Date to"
                InputLabelProps={{ shrink: true }}
                value={matchTxDateTo}
                onChange={(e) => setMatchTxDateTo(String(e.target.value || ''))}
                sx={{ minWidth: 165 }}
              />
              <Button
                size="small"
                variant="contained"
                onClick={() => void applyRuleMatchDateWindow()}
                disabled={matchTxLoading || matchTxLoadingMore || !matchTxSource}
              >
                Apply window
              </Button>
              <Tooltip
                title={
                  matchTxAnchorDate
                    ? `Date window: default is one year either side of ${matchTxAnchorDate}. Date range is applied on the server when you click Apply window; text / account / amount filters below apply to rows already loaded in this modal.`
                    : 'Date window: edit dates and click Apply to adjust how many matching transactions are shown. Date range is applied on the server when you click Apply window; text / account / amount filters below apply to rows already loaded in this modal.'
                }
                placement="top-start"
                slotProps={{
                  tooltip: {
                    sx: { maxWidth: 360 }
                  }
                }}
              >
                <IconButton
                  size="small"
                  aria-label="How the date window and filters work"
                  sx={{ alignSelf: 'center' }}
                >
                  <InfoOutlinedIcon fontSize="small" sx={{ color: 'text.secondary' }} />
                </IconButton>
              </Tooltip>
            </Stack>
            <Stack
              direction={{ xs: 'column', sm: 'row' }}
              spacing={1}
              alignItems={{ xs: 'stretch', sm: 'center' }}
              sx={{ mt: 1 }}
            >
              <ClearableSearchTextField
                size="small"
                label="Text search"
                value={matchTxFilterText}
                onChange={(v) => setMatchTxFilterText(String(v || ''))}
                sx={{ minWidth: 240 }}
              />
              <FormControl size="small" sx={{ minWidth: 170 }}>
                <Select
                  displayEmpty
                  value={matchTxFilterAccount}
                  onChange={(e) => setMatchTxFilterAccount(String(e.target.value || 'all'))}
                >
                  <MenuItem value="all">
                    <em>All accounts</em>
                  </MenuItem>
                  {matchTxAccountOptions.map((acct) => (
                    <MenuItem key={`match-account-${acct}`} value={acct}>
                      {acct}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <FormControl size="small" sx={{ minWidth: 170 }}>
                <Select
                  displayEmpty
                  value={matchTxFilterHeading}
                  renderValue={(v) => {
                    if (v === 'all') return 'All cash flow';
                    if (v === 'income') return 'In';
                    if (v === 'expense') return 'Out';
                    return String(v);
                  }}
                  onChange={(e) => setMatchTxFilterHeading((e.target.value as 'all' | 'income' | 'expense') || 'all')}
                >
                  <MenuItem value="all">
                    <em>All cash flow</em>
                  </MenuItem>
                  <MenuItem value="income">In</MenuItem>
                  <MenuItem value="expense">Out</MenuItem>
                </Select>
              </FormControl>
              <TextField
                size="small"
                type="number"
                label="Amount min"
                value={matchTxFilterAmountMin}
                onChange={(e) => setMatchTxFilterAmountMin(String(e.target.value || ''))}
                sx={{ minWidth: 140 }}
                InputProps={buildClearableInputProps(
                  matchTxFilterAmountMin,
                  () => setMatchTxFilterAmountMin(''),
                  undefined,
                  'Clear amount minimum'
                )}
              />
              <TextField
                size="small"
                type="number"
                label="Amount max"
                value={matchTxFilterAmountMax}
                onChange={(e) => setMatchTxFilterAmountMax(String(e.target.value || ''))}
                sx={{ minWidth: 140 }}
                InputProps={buildClearableInputProps(
                  matchTxFilterAmountMax,
                  () => setMatchTxFilterAmountMax(''),
                  undefined,
                  'Clear amount maximum'
                )}
              />
            </Stack>
          </Box>
          {matchTxLoading && matchTxRows.length === 0 ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 5 }}>
              <CircularProgress size={36} aria-label="Loading matching transactions" />
            </Box>
          ) : null}
          {!matchTxLoading && filteredMatchTxRows.length === 0 && !matchTxError ? (
            <Typography variant="body2" color="text.secondary" sx={{ px: 2, py: 2 }}>
              {matchTxRows.length === 0
                ? 'No transactions match this rule in the history scanned so far.'
                : 'No transactions match the current modal filters.'}
            </Typography>
          ) : null}
          {filteredMatchTxRows.length > 0 ? (
            <TableContainer sx={{ overflowX: 'hidden' }}>
              <Table size="small" stickyHeader aria-label="Transactions matching this rule">
                <TableHead>
                  <TableRow>
                    <TableCell>Date</TableCell>
                    <TableCell align="right">Amount</TableCell>
                    <TableCell align="right">Transaction ID</TableCell>
                    <TableCell>Reference</TableCell>
                    <TableCell>Description</TableCell>
                    <TableCell sx={{ width: 52, whiteSpace: 'nowrap' }}>A/C</TableCell>
                    <TableCell
                      sx={(theme) =>
                        matchTxSource && matchTxModalSupplierIsRuleOutput(matchTxSource.kind)
                          ? {
                              whiteSpace: 'nowrap',
                              fontWeight: 700,
                              bgcolor: alpha(theme.palette.primary.main, theme.palette.mode === 'dark' ? 0.22 : 0.12),
                              color:
                                theme.palette.mode === 'dark'
                                  ? theme.palette.primary.light
                                  : theme.palette.primary.dark,
                              borderLeft: `1px solid ${theme.palette.divider}`
                            }
                          : { whiteSpace: 'nowrap' }
                      }
                    >
                      Supplier
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {filteredMatchTxRows.map((tx) => (
                    <TableRow key={tx.id} hover>
                      <TableCell sx={{ whiteSpace: 'nowrap', verticalAlign: 'top' }}>
                        {formatDate(tx.completed_at || tx.date)}
                      </TableCell>
                      <TableCell align="right" sx={{ whiteSpace: 'nowrap', verticalAlign: 'top' }}>
                        <Typography variant="body2" component="span" sx={{ color: txReviewAmountColor(tx.amount) }}>
                          {formatAmount(tx.amount, tx.currency)}
                        </Typography>
                      </TableCell>
                      <TableCell align="right" sx={{ verticalAlign: 'top' }}>
                        #{tx.id}
                      </TableCell>
                      <TableCell
                        sx={{
                          maxWidth: 200,
                          verticalAlign: 'top',
                          wordBreak: 'break-word'
                        }}
                      >
                        {tx.reference?.trim() ? tx.reference : '—'}
                      </TableCell>
                      <TableCell
                        sx={{
                          maxWidth: 260,
                          verticalAlign: 'top',
                          wordBreak: 'break-word'
                        }}
                      >
                        {tx.description?.trim() ? tx.description : '—'}
                      </TableCell>
                      <TableCell sx={{ whiteSpace: 'nowrap', verticalAlign: 'top', width: 52 }}>
                        {formatRevolutAccountLabel(tx.account_source)}
                      </TableCell>
                      <TableCell
                        sx={(theme) => ({
                          verticalAlign: 'top',
                          whiteSpace: 'nowrap',
                          ...(matchTxSource && matchTxModalSupplierIsRuleOutput(matchTxSource.kind)
                            ? {
                                bgcolor: alpha(theme.palette.primary.main, theme.palette.mode === 'dark' ? 0.12 : 0.06),
                                color:
                                  theme.palette.mode === 'dark'
                                    ? theme.palette.primary.light
                                    : theme.palette.primary.dark,
                                borderLeft: `1px solid ${theme.palette.divider}`,
                                fontWeight: 600
                              }
                            : {})
                        })}
                      >
                        {tx.supplier_name || '—'}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          ) : null}
          <Box ref={matchTxSentinelRef} sx={{ height: 16, flexShrink: 0 }} aria-hidden />
          {matchTxLoadingMore ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 1.5 }}>
              <CircularProgress size={28} aria-label="Loading more" />
            </Box>
          ) : null}
          {matchTxFooterNote ? (
            <Typography variant="caption" color="text.secondary" component="div" sx={{ px: 2, pb: 1.5 }}>
              {matchTxFooterNote}
            </Typography>
          ) : null}
          {matchTxHasMore === false && filteredMatchTxRows.length > 0 && !matchTxLoading ? (
            <Typography variant="caption" color="text.secondary" component="div" sx={{ px: 2, pb: 1 }}>
              End of matching transactions (for the scanned history).
            </Typography>
          ) : null}
        </DialogContent>
        <DialogActions sx={{ flexWrap: 'wrap', gap: 1 }}>
          {(matchTxSource?.kind === 'rule' || matchTxSource?.kind === 'proposal') ? (
            <Tooltip
              title="Creates pending classification rule proposals for the transactions currently listed in this modal (after text/account/amount filters), then opens the Rules tab filtered to those new proposal IDs."
            >
              <span>
                <Button
                  variant="contained"
                  color="secondary"
                  disabled={
                    matchTxLoading ||
                    matchTxLoadingMore ||
                    matchTxGenerateProposalsBusy ||
                    filteredMatchTxRows.length === 0
                  }
                  onClick={() => void handleGenerateProposalsFromMatchModal()}
                >
                  {matchTxGenerateProposalsBusy ? 'Generating…' : 'Generate proposals & go to Rules'}
                </Button>
              </span>
            </Tooltip>
          ) : null}
          <Box sx={{ flexGrow: 1 }} />
          <Button
            onClick={() => {
              if (matchTxLoading || matchTxLoadingMore) return;
              setMatchTxSource(null);
              setMatchTxRows([]);
              setMatchTxCursorId(null);
              setMatchTxHasMore(false);
              setMatchTxDateFrom('');
              setMatchTxDateTo('');
              setMatchTxAnchorDate('');
              setMatchTxFilterAccount('all');
              setMatchTxFilterHeading('all');
              setMatchTxFilterAmountMin('');
              setMatchTxFilterAmountMax('');
              setMatchTxFilterText('');
              setMatchTxError('');
              setMatchTxFooterNote('');
            }}
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>
      <Dialog
        open={bulkTagEditOpen}
        onClose={() => {
          if (unifiedBulkLock) return;
          setBulkTagEditOpen(false);
        }}
        fullWidth
        maxWidth="md"
      >
        <DialogTitle>Individual rule actions</DialogTitle>
        <DialogContent dividers>
          <Stack spacing={2} sx={{ mt: 0.5 }}>
            <Typography variant="body2" color="text.secondary">
              Run one specific rule stage across selected rows. Committed rows are skipped where applicable.
            </Typography>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} flexWrap="wrap" useFlexGap>
              <Button
                size="small"
                variant="outlined"
                disabled={unifiedBulkLock || unifiedActionBusy || bulkReprocessBusy}
                onClick={() => {
                  setBulkTagEditOpen(false);
                  void runBulkReprocessSelected({ classification: false, supplier: true, vat: false, specialCase: false });
                }}
              >
                {bulkReprocessBusy ? '…' : 'Re-run Supplier rules'}
              </Button>
              <Button
                size="small"
                variant="outlined"
                disabled={unifiedBulkLock || unifiedActionBusy || bulkReprocessBusy}
                onClick={() => {
                  setBulkTagEditOpen(false);
                  void runBulkReprocessSelected({ classification: true, supplier: false, vat: false, specialCase: false });
                }}
              >
                {bulkReprocessBusy ? '…' : 'Re-run Classification rules'}
              </Button>
              <Button
                size="small"
                variant="outlined"
                disabled={unifiedBulkLock || unifiedActionBusy || bulkReprocessBusy}
                onClick={() => {
                  setBulkTagEditOpen(false);
                  void runBulkReprocessSelected({ classification: false, supplier: false, vat: true, specialCase: false });
                }}
              >
                {bulkReprocessBusy ? '…' : 'Re-run VAT rate rules'}
              </Button>
              <Button
                size="small"
                variant="outlined"
                disabled={unifiedBulkLock || unifiedActionBusy || bulkReprocessBusy}
                onClick={() => {
                  setBulkTagEditOpen(false);
                  void runBulkReprocessSelected({ classification: false, supplier: false, vat: false, specialCase: true });
                }}
              >
                {bulkReprocessBusy ? '…' : 'Re-run Special Case rules'}
              </Button>
            </Stack>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              if (unifiedBulkLock) return;
              setBulkTagEditOpen(false);
            }}
            disabled={unifiedBulkLock}
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>
      <Drawer
        anchor="right"
        open={txJsonViewerOpen}
        onClose={() => {
          if (txJsonViewerBusy) return;
          setTxJsonViewerOpen(false);
        }}
        ModalProps={{
          keepMounted: false,
          sx: {
            // Above AppBar (drawer+1) and stacked toasts/snackbars so header + close stay visible.
            zIndex: (theme) => theme.zIndex.snackbar + 20
          }
        }}
        PaperProps={{
          sx: {
            width: { xs: '100%', sm: txReviewRightPanelWidthPx },
            maxWidth: '100vw',
            zIndex: 1
          }
        }}
      >
        <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
          <Box
            sx={{
              flexShrink: 0,
              px: 2,
              py: 1.5,
              pt: (theme) => `max(${theme.spacing(2)}, calc(${theme.spacing(1)} + env(safe-area-inset-top, 0px)))`,
              bgcolor: 'background.paper',
              borderBottom: 1,
              borderColor: 'divider'
            }}
          >
            <Stack direction="row" alignItems="flex-start" justifyContent="space-between" spacing={1} sx={{ gap: 1 }}>
              <Box sx={{ flex: 1, minWidth: 0, pr: 0.5 }}>
                <Typography variant="h6" component="div" sx={{ lineHeight: 1.25 }}>
                  {txJsonViewerDrawerTitleParts.headline}
                </Typography>
                {txJsonViewerDrawerTitleParts.detail ? (
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    noWrap
                    title={txJsonViewerTitle}
                    sx={{ mt: 0.25, display: 'block' }}
                  >
                    {txJsonViewerDrawerTitleParts.detail}
                  </Typography>
                ) : null}
              </Box>
              <Tooltip title={txJsonViewerBusy ? 'Wait for load to finish' : 'Close panel'}>
                <span>
                  <IconButton
                    aria-label="Close Revolut JSON panel"
                    edge="end"
                    size="medium"
                    onClick={() => {
                      if (txJsonViewerBusy) return;
                      setTxJsonViewerOpen(false);
                    }}
                    disabled={txJsonViewerBusy}
                    sx={{ flexShrink: 0, color: 'text.primary' }}
                  >
                    <CloseIcon fontSize="medium" />
                  </IconButton>
                </span>
              </Tooltip>
            </Stack>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
              Click another row&apos;s JSON button to load it here without closing this panel. Closing this panel keeps
              the snapshot in Quickview → <strong>Other</strong>.
            </Typography>
          </Box>
          <Divider />
          <Box sx={{ flex: 1, overflow: 'auto', p: 2, pt: 2 }}>{renderRevolutJsonInspectorInner()}</Box>
          <Divider />
          <Box sx={{ p: 2, pt: 1.5, flexShrink: 0 }}>
            <Button
              variant="outlined"
              onClick={() => {
                if (txJsonViewerBusy) return;
                setTxJsonViewerOpen(false);
              }}
              disabled={txJsonViewerBusy}
            >
              Close panel
            </Button>
          </Box>
        </Box>
      </Drawer>

      <Drawer
        anchor="right"
        open={unifiedRerunLogOpen}
        onClose={() => setUnifiedRerunLogOpen(false)}
        ModalProps={{ keepMounted: false }}
        PaperProps={{
          sx: {
            width: { xs: '100%', sm: txReviewRightPanelWidthPx },
            maxWidth: '100vw',
            zIndex: (theme) => theme.zIndex.drawer + 5
          }
        }}
      >
        <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
          <Box sx={{ p: 2, pb: 1, flexShrink: 0 }}>
            <Stack direction="row" alignItems="flex-start" justifyContent="space-between" spacing={1}>
              <Box>
                <Typography variant="h6" component="div">
                  Rule re-run commentary
                </Typography>
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                  Log for each full re-run on a row (append-only). Close anytime; another row adds below.
                </Typography>
              </Box>
              <IconButton aria-label="Close rule re-run commentary" onClick={() => setUnifiedRerunLogOpen(false)} size="small">
                <CloseIcon />
              </IconButton>
            </Stack>
          </Box>
          <Divider />
          <Box ref={rerunLogScrollBoxRef} sx={{ flex: 1, overflow: 'auto', p: 1.5, pt: 1 }}>
            {renderUnifiedRerunLogBlocksList()}
          </Box>
          <Divider />
          <Stack direction="row" spacing={1} sx={{ p: 2, pt: 1.5, flexShrink: 0 }} flexWrap="wrap" useFlexGap>
            <Button variant="outlined" onClick={() => setUnifiedRerunLogBlocks([])} disabled={unifiedRerunLogBlocks.length === 0}>
              Clear log
            </Button>
            <Button variant="contained" onClick={() => setUnifiedRerunLogOpen(false)}>
              Close panel
            </Button>
          </Stack>
        </Box>
      </Drawer>
      {auditCandidatesModal ? (
        <AuditProposalCandidatesModal
          open
          title={auditCandidatesModal.title}
          auditKind={auditCandidatesModal.kind}
          candidates={auditCandidatesModal.candidates}
          generating={auditCandidatesGenerating}
          onClose={() => {
            if (!auditCandidatesGenerating) setAuditCandidatesModal(null);
          }}
          onGenerateSelected={(kind, payload) =>
            void handleAuditCandidatesModalGenerate(kind, payload)
          }
        />
      ) : null}
      <Snackbar
        open={!!refreshInfoMessage}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
        sx={{ mt: { xs: 7, sm: 8 }, zIndex: (theme) => theme.zIndex.snackbar + 1 }}
        autoHideDuration={null}
        onClose={(_, reason) => {
          if (reason === 'clickaway') return;
          setRefreshInfoMessage('');
          setRefreshInfoToastCode('');
        }}
      >
        <Alert
          severity="info"
          variant="filled"
          onClose={() => {
            setRefreshInfoMessage('');
            setRefreshInfoToastCode('');
          }}
          sx={{ width: '100%', maxWidth: 520 }}
        >
          <Stack spacing={0.5}>
            <Typography
              variant="body2"
              component="div"
              sx={{ whiteSpace: 'pre-line', fontWeight: 600, lineHeight: 1.45 }}
            >
              {refreshInfoMessage}
            </Typography>
            <ToastCodeCaption code={refreshInfoToastCode} copyBody={refreshInfoMessage} />
          </Stack>
        </Alert>
      </Snackbar>
      <Snackbar
        open={!!error}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
        sx={{
          mt: {
            xs: 7 + (refreshInfoMessage ? 8 : 0),
            sm: 8 + (refreshInfoMessage ? 8 : 0)
          },
          zIndex: (theme) => theme.zIndex.snackbar + 1
        }}
        autoHideDuration={null}
        onClose={(_, reason) => {
          if (reason === 'clickaway') return;
          setError('');
        }}
      >
        <Alert
          severity="error"
          variant="filled"
          onClose={() => setError('')}
          sx={{ width: '100%' }}
        >
          <Stack spacing={0.35}>
            <FriendlyToastMessage message={error} filled />
            <ToastCodeCaption code={errorToastCode} copyBody={error} />
          </Stack>
        </Alert>
      </Snackbar>
      <Snackbar
        open={Boolean(successMessage || ruleSyncCompletionToast)}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
        sx={{
          mt: {
            xs: 7 + (refreshInfoMessage ? 8 : 0) + (error ? 8 : 0),
            sm: 8 + (refreshInfoMessage ? 8 : 0) + (error ? 8 : 0)
          },
          zIndex: (theme) => theme.zIndex.snackbar + 1
        }}
        onClose={(_, reason) => {
          if (reason === 'clickaway') return;
          try {
            sessionStorage.removeItem(AFTER_TAG_WIPE_COMPLETION_KEY);
          } catch {
            /* ignore */
          }
          setSuccessMessage('');
          setRuleSyncCompletionToast(null);
        }}
      >
        <Alert
          severity="success"
          variant="filled"
          onClose={() => {
            try {
              sessionStorage.removeItem(AFTER_TAG_WIPE_COMPLETION_KEY);
            } catch {
              /* ignore */
            }
            setSuccessMessage('');
            setRuleSyncCompletionToast(null);
          }}
          sx={{ width: '100%' }}
        >
          <Stack spacing={0.75}>
            {ruleSyncCompletionToast ? (
              <>
                <Typography variant="subtitle2" sx={{ fontWeight: 700, lineHeight: 1.35 }}>
                  {ruleSyncCompletionToast.title}
                </Typography>
                <Stack spacing={0.35} sx={{ mt: 0.25 }}>
                  {ruleSyncCompletionToast.segments.map((seg, idx) => (
                    <Typography
                      key={`${idx}-${seg.slice(0, 24) || 'blank'}`}
                      variant="body2"
                      component="div"
                      sx={{
                        lineHeight: 1.4,
                        whiteSpace: 'pre-wrap',
                        ...(seg ? {} : { minHeight: '0.75em' })
                      }}
                    >
                      {seg || '\u00a0'}
                    </Typography>
                  ))}
                </Stack>
              </>
            ) : (
              <FriendlyToastMessage message={successMessage} filled />
            )}
            <ToastCodeCaption
              code={successToastCode}
              requestId={ruleSyncCompletionToast?.requestId}
              jobId={ruleSyncCompletionToast?.jobId}
              copyBody={
                ruleSyncCompletionToast
                  ? [ruleSyncCompletionToast.title, ...ruleSyncCompletionToast.segments].join('\n')
                  : successMessage
              }
            />
          </Stack>
        </Alert>
      </Snackbar>
      {stackedToasts.map((toast, idx) => (
        <Snackbar
          key={toast.id}
          open
          anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
          sx={{
            mt: {
              xs:
                7 +
                (refreshInfoMessage ? 8 : 0) +
                (error ? 8 : 0) +
                (successMessage || ruleSyncCompletionToast ? 8 : 0) +
                idx * 8,
              sm:
                8 +
                (refreshInfoMessage ? 8 : 0) +
                (error ? 8 : 0) +
                (successMessage || ruleSyncCompletionToast ? 8 : 0) +
                idx * 8
            },
            zIndex: (theme) => theme.zIndex.snackbar + 1,
            maxWidth: { xs: 'calc(100vw - 16px)', sm: 'min(960px, calc(100vw - 32px))' }
          }}
          autoHideDuration={null}
          onClose={(_, reason) => {
            if (reason === 'clickaway') return;
            setStackedToasts((prev) => prev.filter((x) => x.id !== toast.id));
          }}
        >
          <Alert
            severity={toast.severity}
            variant="filled"
            /* MUI only shows the default close icon when `action` is null; custom `action` replaces it entirely. */
            action={
              <Stack
                direction="row"
                alignItems="center"
                spacing={0.75}
                flexWrap="wrap"
                useFlexGap
                sx={{ flexShrink: 0, justifyContent: 'flex-end' }}
              >
                {toast.actionLabel && toast.onAction ? (
                  <Button
                    color="inherit"
                    size="small"
                    variant="outlined"
                    sx={{
                      borderColor: 'rgba(255,255,255,0.55)',
                      color: 'inherit',
                      flexShrink: 0
                    }}
                    onClick={() => {
                      const action = toast.onAction;
                      const toastId = toast.id;
                      // Dismiss first so a second click cannot race a half-applied URL sync.
                      setStackedToasts((prev) => prev.filter((x) => x.id !== toastId));
                      action?.();
                    }}
                  >
                    {toast.actionLabel}
                  </Button>
                ) : null}
                <Button
                  color="inherit"
                  size="small"
                  variant="outlined"
                  sx={{
                    borderColor: 'rgba(255,255,255,0.55)',
                    color: 'inherit',
                    fontWeight: 700,
                    flexShrink: 0
                  }}
                  onClick={() => setStackedToasts((prev) => prev.filter((x) => x.id !== toast.id))}
                >
                  Dismiss
                </Button>
              </Stack>
            }
            sx={{
              width: '100%',
              maxWidth: '100%',
              flexWrap: 'wrap',
              alignItems: 'flex-start',
              rowGap: 0.5,
              '& .MuiAlert-message': {
                flex: '1 1 200px',
                minWidth: 0,
                overflowWrap: 'anywhere',
                wordBreak: 'break-word',
                paddingRight: 1
              },
              '& .MuiAlert-action': {
                flex: '0 1 auto',
                marginLeft: { xs: 0, sm: 'auto' },
                paddingLeft: { xs: 0, sm: 2 },
                alignItems: 'flex-start',
                flexWrap: 'wrap'
              }
            }}
          >
            <Stack spacing={0.35} sx={{ width: '100%' }}>
              {toast.rerunSummaryLines && toast.rerunSummaryLines.length > 0 ? (
                <RerunSummaryKeyValueList lines={toast.rerunSummaryLines} variant="toastFilled" />
              ) : (
                <FriendlyToastMessage message={toast.message} filled />
              )}
              <ToastCodeCaption
                code={toast.code}
                requestId={toast.requestId}
                jobId={toast.jobId}
                errorCode={toast.errorCode}
                copyBody={
                  toast.rerunSummaryLines && toast.rerunSummaryLines.length > 0
                    ? toast.rerunSummaryLines.map((line) => `${line.label}: ${line.detail}`).join('\n')
                    : toast.message
                }
              />
            </Stack>
          </Alert>
        </Snackbar>
      ))}
      <Drawer
        id="classification-rules-quickview-drawer"
        anchor="right"
        open={rulesQuickviewOpen}
        onClose={closeRulesQuickview}
        hideBackdrop
        ModalProps={{
          keepMounted: false,
          disableScrollLock: true,
          disableEnforceFocus: true,
          disableAutoFocus: true,
          sx: {
            zIndex: (t) => t.zIndex.snackbar + 15,
            pointerEvents: 'none'
          }
        }}
        PaperProps={{
          sx: {
            width: { xs: '100%', sm: txReviewRightPanelWidthPx },
            maxWidth: '100vw',
            zIndex: 1,
            pointerEvents: 'auto',
            borderLeft: `1px solid ${theme.palette.divider}`,
            boxShadow: theme.shadows[12]
          }
        }}
      >
        <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', bgcolor: 'background.paper' }}>
          <Box
            sx={{
              flexShrink: 0,
              px: 2,
              pt: 1.25,
              pb: 1,
              background:
                theme.palette.mode === 'dark'
                  ? `linear-gradient(145deg, ${alpha(theme.palette.primary.main, 0.22)} 0%, ${alpha(
                      theme.palette.common.black,
                      0.2
                    )} 55%, ${alpha(theme.palette.background.paper, 1)} 100%)`
                  : `linear-gradient(145deg, ${alpha(theme.palette.primary.main, 0.12)} 0%, ${alpha(
                      theme.palette.primary.light,
                      0.2
                    )} 42%, ${theme.palette.background.paper} 100%)`,
              boxShadow: theme.shadows[1]
            }}
          >
            <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={1.5}>
              <Box sx={{ minWidth: 0, flex: 1, pr: 0.5 }}>
                <Typography variant="subtitle1" component="div" sx={{ fontWeight: 800, letterSpacing: -0.02 }}>
                  Quickview
                </Typography>
              </Box>
              <IconButton
                aria-label="Close rules quickview"
                onClick={closeRulesQuickview}
                edge="end"
                size="small"
                sx={{
                  mt: 0,
                  border: `1px solid ${alpha(theme.palette.text.primary, 0.12)}`,
                  bgcolor: alpha(theme.palette.background.paper, theme.palette.mode === 'dark' ? 0.2 : 0.92),
                  boxShadow: theme.shadows[1],
                  '&:hover': {
                    bgcolor: alpha(theme.palette.error.main, 0.1),
                    borderColor: alpha(theme.palette.error.main, 0.45),
                    color: 'error.main'
                  }
                }}
              >
                <CloseRoundedIcon fontSize="small" />
              </IconButton>
            </Stack>
            <Tabs
              value={rulesQuickviewMainTab}
              onChange={(_, v) => setRulesQuickviewMainTab(v as QuickviewMainTab)}
              aria-label="Quickview panel sections"
              variant="scrollable"
              scrollButtons="auto"
              allowScrollButtonsMobile
              sx={{
                mt: 1,
                minHeight: 0,
                '& .MuiTab-root': {
                  minHeight: 40,
                  py: 0.75,
                  textTransform: 'none',
                  fontWeight: 600,
                  fontSize: '0.8125rem',
                  color: 'text.secondary'
                },
                '& .MuiTab-root.Mui-selected': {
                  color: 'text.primary'
                }
              }}
            >
              <Tab label="Chat" value="chat" id="qv-main-tab-chat" aria-controls="qv-main-panel-chat" />
              <Tab label="Rules" value="rules" id="qv-main-tab-rules" aria-controls="qv-main-panel-rules" />
              <Tab
                label="Categories"
                value="categories"
                id="qv-main-tab-categories"
                aria-controls="qv-main-panel-categories"
              />
              <Tab
                label="Suppliers"
                value="suppliers"
                id="qv-main-tab-suppliers"
                aria-controls="qv-main-panel-suppliers"
              />
              <Tab label="Other" value="other" id="qv-main-tab-other" aria-controls="qv-main-panel-other" />
              <Tab
                label="Your requests"
                value="requests"
                id="qv-main-tab-requests"
                aria-controls="qv-main-panel-requests"
              />
            </Tabs>
          </Box>
          {unifiedMoreRow ? (
            <Box
              sx={{
                flexShrink: 0,
                px: 2,
                py: 1.5,
                bgcolor: 'action.hover',
                borderBottom: 1,
                borderColor: 'divider',
                maxHeight: '45%',
                overflow: 'auto'
              }}
            >
              <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={1} sx={{ mb: 1 }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                  Row details — TX #{unifiedMoreRow.id}
                </Typography>
                <Button size="small" variant="outlined" onClick={() => setUnifiedMoreRow(null)}>
                  Dismiss
                </Button>
              </Stack>
              {renderUnifiedRowDetailsStack(unifiedMoreRow)}
            </Box>
          ) : null}
          <Box
            sx={{
              flex: 1,
              minHeight: 0,
              minWidth: 0,
              display: 'flex',
              flexDirection: 'column'
            }}
          >
            {rulesQuickviewMainTab === 'rules' ? (
              <>
                <Box sx={{ px: 2.25, pt: 1.75, pb: 1.5, flexShrink: 0, overflow: 'visible' }}>
                  <Stack spacing={1} sx={{ overflow: 'visible' }}>
                    <Stack
                      direction={{ xs: 'column', sm: 'row' }}
                      spacing={1}
                      sx={{ mb: 0, overflow: 'visible' }}
                      alignItems={{ sm: 'center' }}
                      flexWrap="wrap"
                      useFlexGap
                    >
                      <ClearableSearchTextField
                        size="small"
                        label="Text search"
                        placeholder="e.g. Blacknight"
                        value={rulesTextFilter}
                        onChange={setRulesTextFilter}
                        sx={{ minWidth: 240, '& .MuiOutlinedInput-root': { bgcolor: 'background.paper' } }}
                        title="Case-insensitive substring across classification, supplier, VAT, and special-case rules (and proposals)."
                      />
                      <FormControl size="small" sx={{ minWidth: 220 }}>
                        <InputLabel id="qv-rules-status-filter-label">Status</InputLabel>
                        <Select
                          labelId="qv-rules-status-filter-label"
                          label="Status"
                          value={rulesStatusFilter}
                          onChange={(e) => setRulesStatusFilter(e.target.value as 'all' | RuleRowState)}
                          sx={{ bgcolor: 'background.paper' }}
                          MenuProps={QUICKVIEW_DRAWER_SELECT_MENU_PROPS}
                        >
                          <MenuItem value="all">All statuses</MenuItem>
                          <MenuItem value="confirmed">Confirmed</MenuItem>
                          <MenuItem value="proposed">Proposed</MenuItem>
                        </Select>
                      </FormControl>
                    </Stack>
                    <Button
                      size="small"
                      variant="outlined"
                      onClick={toggleAllRulesQvSections}
                      disabled={rulesQvVisibleSectionKeys.length === 0}
                      sx={{ alignSelf: { xs: 'stretch', sm: 'flex-start' } }}
                    >
                      Open/Close all Sections
                    </Button>
                  </Stack>
                </Box>
                <Divider />
          <Box
            id="qv-main-panel-rules"
            role="tabpanel"
            aria-labelledby="qv-main-tab-rules"
            sx={{
              flex: 1,
              overflow: 'auto',
              px: 2,
              py: 2,
              bgcolor:
                theme.palette.mode === 'dark'
                  ? alpha(theme.palette.common.white, 0.02)
                  : alpha(theme.palette.primary.main, 0.02)
            }}
          >
            {[
              rulesQuickviewFiltered.supplierProposals.length,
              rulesQuickviewFiltered.supplierRules.length,
              rulesQuickviewFiltered.classProposals.length,
              rulesQuickviewFiltered.classifications.length,
              rulesQuickviewFiltered.vatRules.length,
              rulesQuickviewFiltered.specialCaseRules.length
            ].every((n) => n === 0) ? (
              <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>
                {loading ? 'Loading rules…' : 'No rules match this filter (or nothing loaded yet).'}
              </Typography>
            ) : null}

            {rulesQuickviewFiltered.supplierProposals.length > 0 ? (
              <Accordion
                disableGutters
                expanded={rulesQvSectionsOpen.supplierProposals}
                onChange={(_, exp) => setRulesQvSectionsOpen((p) => ({ ...p, supplierProposals: exp }))}
                sx={rulesQuickviewAccordionRootSx(theme)}
              >
                <AccordionSummary
                  expandIcon={<ExpandMoreIcon />}
                  sx={rulesQuickviewAccordionSummarySx(theme)}
                >
                  <Stack
                    direction="row"
                    alignItems="center"
                    justifyContent="space-between"
                    spacing={1}
                    sx={{ width: '100%', minWidth: 0 }}
                  >
                    <Typography
                      variant="subtitle1"
                      component="div"
                      sx={{ fontWeight: 700, color: 'text.primary', letterSpacing: 0.01, flex: 1, minWidth: 0 }}
                    >
                      Supplier proposals ({rulesQuickviewFiltered.supplierProposals.length})
                    </Typography>
                    <Tooltip title="Create a new supplier description-alias rule">
                      <span>
                        <Button
                          size="small"
                          variant="contained"
                          startIcon={<AddIcon />}
                          disabled={loading || applyingRules || supplierRuleSaving}
                          onClick={(e) => {
                            e.stopPropagation();
                            openNewSupplierRuleDialog();
                          }}
                          onMouseDown={(e) => {
                            e.stopPropagation();
                          }}
                        >
                          Add rule
                        </Button>
                      </span>
                    </Tooltip>
                  </Stack>
                </AccordionSummary>
                <AccordionDetails sx={{ px: 1, pt: 0, pb: 1 }}>
                  {rulesQuickviewFiltered.supplierProposals.map((row, idx) => {
                    const spid = supplierRuleProposalDbId(row);
                    const p = row.payload || {};
                    const canon = String((p.canonical_name as string) || '').trim();
                    const pat = String((p.pattern_raw as string) || '').trim();
                    const canOpen = spid != null || !!(row.proposed_name || '').trim();
                    const headName =
                      (row.proposed_name || '').trim() || canon || pat || '—';
                    return (
                      <Box key={`qv-sp-${spid ?? 'x'}-${idx}`} sx={rulesQuickviewListRowSx(theme)}>
                        <Stack direction="row" spacing={1} alignItems="flex-start" justifyContent="space-between">
                          <Box sx={{ minWidth: 0, flex: 1 }}>
                            <Typography
                              variant="body2"
                              noWrap
                              component="div"
                              title={
                                spid != null
                                  ? `#${spid}: ${headName}`
                                  : headName
                              }
                              sx={{ minWidth: 0 }}
                            >
                              {spid != null ? (
                                <>
                                  <Box component="span" sx={{ fontWeight: 700 }}>{`#${spid}`}</Box>
                                  <Box component="span" sx={{ fontWeight: 400 }}>{`: ${headName}`}</Box>
                                </>
                              ) : (
                                <>
                                  <Box component="span" sx={{ fontWeight: 700 }}>Proposal</Box>
                                  <Box component="span" sx={{ fontWeight: 400 }}>{`: ${headName}`}</Box>
                                </>
                              )}
                            </Typography>
                            <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap sx={{ mt: 0.5 }}>
                              <Chip size="small" label={row.source === 'atlas' ? 'Atlas' : 'Draft'} variant="outlined" />
                            </Stack>
                            {(() => {
                              const logic = supplierProposalQuickviewLogicLine(row);
                              const { full, shown } = truncateRulesQuickviewLogicLine(logic);
                              return (
                                <Typography
                                  variant="caption"
                                  component="div"
                                  color="text.secondary"
                                  title={full}
                                  sx={rulesQuickviewBooleanLogicCaptionSx}
                                >
                                  {shown}
                                </Typography>
                              );
                            })()}
                          </Box>
                          <Button
                            size="small"
                            variant="outlined"
                            color="primary"
                            sx={{ flexShrink: 0, fontWeight: 600, whiteSpace: 'nowrap' }}
                            disabled={!canOpen}
                            onClick={() => {
                              if (spid != null) navigateRulesTabToSupplierProposalId(spid);
                              else navigateRulesTabToRulesTextSearch(row.proposed_name || '', { status: 'proposed' });
                            }}
                          >
                            Open in Rules
                          </Button>
                        </Stack>
                      </Box>
                    );
                  })}
                </AccordionDetails>
              </Accordion>
            ) : null}

            {rulesQuickviewFiltered.supplierRules.length > 0 ? (
              <Accordion
                disableGutters
                expanded={rulesQvSectionsOpen.supplierRules}
                onChange={(_, exp) => setRulesQvSectionsOpen((p) => ({ ...p, supplierRules: exp }))}
                sx={rulesQuickviewAccordionRootSx(theme)}
              >
                <AccordionSummary
                  expandIcon={<ExpandMoreIcon />}
                  sx={rulesQuickviewAccordionSummarySx(theme)}
                >
                  <Stack
                    direction="row"
                    alignItems="center"
                    justifyContent="space-between"
                    spacing={1}
                    sx={{ width: '100%', minWidth: 0 }}
                  >
                    <Typography
                      variant="subtitle1"
                      component="div"
                      sx={{ fontWeight: 700, color: 'text.primary', letterSpacing: 0.01, flex: 1, minWidth: 0 }}
                    >
                      Supplier rules ({rulesQuickviewFiltered.supplierRules.length})
                    </Typography>
                    <Tooltip title="Create a new supplier description-alias rule">
                      <span>
                        <Button
                          size="small"
                          variant="contained"
                          startIcon={<AddIcon />}
                          disabled={loading || applyingRules || supplierRuleSaving}
                          onClick={(e) => {
                            e.stopPropagation();
                            openNewSupplierRuleDialog();
                          }}
                          onMouseDown={(e) => {
                            e.stopPropagation();
                          }}
                        >
                          Add rule
                        </Button>
                      </span>
                    </Tooltip>
                  </Stack>
                </AccordionSummary>
                <AccordionDetails sx={{ px: 1, pt: 0, pb: 1 }}>
                  {rulesQuickviewFiltered.supplierRules.map((r) => {
                    const sn = (r.supplier_name || '').trim();
                    const rn = (r.rule_name || '').trim();
                    const pr = (r.pattern_raw || '').trim();
                    const headName = sn || rn || pr || '—';
                    return (
                    <Box key={`qv-sr-${r.id}`} sx={rulesQuickviewListRowSx(theme)}>
                      <Stack direction="row" spacing={1} alignItems="flex-start" justifyContent="space-between">
                        <Box sx={{ minWidth: 0, flex: 1 }}>
                          <Typography
                            variant="body2"
                            noWrap
                            component="div"
                            title={`${formatPrefixedRuleId('supplier', r.id)}: ${headName}`}
                            sx={{ minWidth: 0 }}
                          >
                            <Box component="span" sx={{ fontWeight: 700 }}>
                              {formatPrefixedRuleId('supplier', r.id)}
                            </Box>
                            <Box component="span" sx={{ fontWeight: 400 }}>{`: ${headName}`}</Box>
                          </Typography>
                          {!r.is_active ? (
                            <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap sx={{ mt: 0.5 }}>
                              <Chip size="small" label="Inactive" />
                            </Stack>
                          ) : null}
                          {(() => {
                            const logic = supplierRuleQuickviewLogicLine(r);
                            const { full, shown } = truncateRulesQuickviewLogicLine(logic);
                            return (
                              <Typography
                                variant="caption"
                                component="div"
                                color="text.secondary"
                                title={full}
                                sx={rulesQuickviewBooleanLogicCaptionSx}
                              >
                                {shown}
                              </Typography>
                            );
                          })()}
                        </Box>
                        <Button
                          size="small"
                          variant="outlined"
                          color="primary"
                          sx={{ flexShrink: 0, fontWeight: 600, whiteSpace: 'nowrap' }}
                          onClick={() => navigateRulesTabToPrefixedId(formatPrefixedRuleId('supplier', r.id))}
                        >
                          Open in Rules
                        </Button>
                      </Stack>
                    </Box>
                    );
                  })}
                </AccordionDetails>
              </Accordion>
            ) : null}

            {rulesQuickviewFiltered.classProposals.length > 0 ? (
              <Accordion
                disableGutters
                expanded={rulesQvSectionsOpen.classProposals}
                onChange={(_, exp) => setRulesQvSectionsOpen((p) => ({ ...p, classProposals: exp }))}
                sx={rulesQuickviewAccordionRootSx(theme)}
              >
                <AccordionSummary
                  expandIcon={<ExpandMoreIcon />}
                  sx={rulesQuickviewAccordionSummarySx(theme)}
                >
                  <Stack
                    direction="row"
                    alignItems="center"
                    justifyContent="space-between"
                    spacing={1}
                    sx={{ width: '100%', minWidth: 0 }}
                  >
                    <Typography
                      variant="subtitle1"
                      component="div"
                      sx={{ fontWeight: 700, color: 'text.primary', letterSpacing: 0.01, flex: 1, minWidth: 0 }}
                    >
                      Classification proposals ({rulesQuickviewFiltered.classProposals.length})
                    </Typography>
                    <Tooltip
                      title={
                        granularCategoriesSortedByName.length === 0
                          ? 'Add at least one granular category before creating a classification rule.'
                          : 'Create a new classification rule'
                      }
                    >
                      <span>
                        <Button
                          size="small"
                          variant="contained"
                          startIcon={<AddIcon />}
                          disabled={
                            loading || applyingRules || ruleEditSaving || granularCategoriesSortedByName.length === 0
                          }
                          onClick={(e) => {
                            e.stopPropagation();
                            openNewClassificationRuleDialog();
                          }}
                          onMouseDown={(e) => {
                            e.stopPropagation();
                          }}
                        >
                          Add rule
                        </Button>
                      </span>
                    </Tooltip>
                  </Stack>
                </AccordionSummary>
                <AccordionDetails sx={{ px: 1, pt: 0, pb: 1 }}>
                  {rulesQuickviewFiltered.classProposals.map((row, idx) => {
                    const pid = row.id != null && Number(row.id) > 0 ? Math.trunc(Number(row.id)) : null;
                    const atlasSeq =
                      row.virtual && row.atlas_display_seq != null && Number(row.atlas_display_seq) > 0
                        ? Math.trunc(Number(row.atlas_display_seq))
                        : null;
                    const canOpen = pid != null || atlasSeq != null || !!(row.proposed_name || '').trim();
                    return (
                      <Box
                        key={`qv-cp-${row.virtual ? 'v' : 'd'}-${row.id ?? row.atlas_key ?? idx}`}
                        sx={rulesQuickviewListRowSx(theme)}
                      >
                        <Stack direction="row" spacing={1} alignItems="flex-start" justifyContent="space-between">
                          <Box sx={{ minWidth: 0, flex: 1 }}>
                            <Typography
                              variant="body2"
                              noWrap
                              component="div"
                              title={
                                pid != null
                                  ? `P-C${pid}: ${row.proposed_name || ''}`
                                  : atlasSeq != null
                                    ? `P-A${atlasSeq}: ${row.proposed_name || ''}`
                                    : row.proposed_name || undefined
                              }
                              sx={{ minWidth: 0 }}
                            >
                              {pid != null ? (
                                <>
                                  <Box component="span" sx={{ fontWeight: 700 }}>{`P-C${pid}`}</Box>
                                  <Box component="span" sx={{ fontWeight: 400 }}>{`: ${row.proposed_name || '—'}`}</Box>
                                </>
                              ) : atlasSeq != null ? (
                                <>
                                  <Box component="span" sx={{ fontWeight: 700 }}>{`P-A${atlasSeq}`}</Box>
                                  <Box component="span" sx={{ fontWeight: 400 }}>{`: ${row.proposed_name || '—'}`}</Box>
                                </>
                              ) : (
                                <>
                                  <Box component="span" sx={{ fontWeight: 700 }}>
                                    Proposal
                                  </Box>
                                  <Box component="span" sx={{ fontWeight: 400 }}>{`: ${row.proposed_name || '—'}`}</Box>
                                </>
                              )}
                            </Typography>
                            <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap sx={{ mt: 0.5 }}>
                              <Chip size="small" label={row.source === 'atlas' ? 'Atlas' : 'Draft'} variant="outlined" />
                            </Stack>
                            {(() => {
                              const logic = classificationProposalQuickviewLogicLine(row);
                              const { full, shown } = truncateRulesQuickviewLogicLine(logic);
                              return (
                                <Typography
                                  variant="caption"
                                  component="div"
                                  color="text.secondary"
                                  title={full}
                                  sx={rulesQuickviewBooleanLogicCaptionSx}
                                >
                                  {shown}
                                </Typography>
                              );
                            })()}
                          </Box>
                          <Button
                            size="small"
                            variant="outlined"
                            color="primary"
                            sx={{ flexShrink: 0, fontWeight: 600, whiteSpace: 'nowrap' }}
                            disabled={!canOpen}
                            onClick={() => {
                              if (pid != null) navigateRulesTabToClassificationProposalId(pid);
                              else if (atlasSeq != null) navigateRulesTabToClassificationAtlasDisplaySeq(atlasSeq);
                              else navigateRulesTabToRulesTextSearch(row.proposed_name || '', { status: 'proposed' });
                            }}
                          >
                            Open in Rules
                          </Button>
                        </Stack>
                      </Box>
                    );
                  })}
                </AccordionDetails>
              </Accordion>
            ) : null}

            {rulesQuickviewFiltered.classifications.length > 0 ? (
              <Accordion
                disableGutters
                expanded={rulesQvSectionsOpen.classifications}
                onChange={(_, exp) => setRulesQvSectionsOpen((p) => ({ ...p, classifications: exp }))}
                sx={rulesQuickviewAccordionRootSx(theme)}
              >
                <AccordionSummary
                  expandIcon={<ExpandMoreIcon />}
                  sx={rulesQuickviewAccordionSummarySx(theme)}
                >
                  <Stack
                    direction="row"
                    alignItems="center"
                    justifyContent="space-between"
                    spacing={1}
                    sx={{ width: '100%', minWidth: 0 }}
                  >
                    <Typography
                      variant="subtitle1"
                      component="div"
                      sx={{ fontWeight: 700, color: 'text.primary', letterSpacing: 0.01, flex: 1, minWidth: 0 }}
                    >
                      Classification rules ({rulesQuickviewFiltered.classifications.length})
                    </Typography>
                    <Tooltip
                      title={
                        granularCategoriesSortedByName.length === 0
                          ? 'Add at least one granular category before creating a classification rule.'
                          : 'Create a new classification rule'
                      }
                    >
                      <span>
                        <Button
                          size="small"
                          variant="contained"
                          startIcon={<AddIcon />}
                          disabled={
                            loading || applyingRules || ruleEditSaving || granularCategoriesSortedByName.length === 0
                          }
                          onClick={(e) => {
                            e.stopPropagation();
                            openNewClassificationRuleDialog();
                          }}
                          onMouseDown={(e) => {
                            e.stopPropagation();
                          }}
                        >
                          Add rule
                        </Button>
                      </span>
                    </Tooltip>
                  </Stack>
                </AccordionSummary>
                <AccordionDetails sx={{ px: 1, pt: 0, pb: 1 }}>
                  {rulesQuickviewFiltered.classifications.map((rule) => (
                    <Box key={`qv-cr-${rule.id}`} sx={rulesQuickviewListRowSx(theme)}>
                      <Stack direction="row" spacing={1} alignItems="flex-start" justifyContent="space-between">
                        <Box sx={{ minWidth: 0, flex: 1 }}>
                          <Typography
                            variant="body2"
                            noWrap
                            component="div"
                            title={`${formatClassificationRulePrefixedId(rule.id, rule.name)}: ${rule.name || '—'}`}
                            sx={{ minWidth: 0 }}
                          >
                            <Box component="span" sx={{ fontWeight: 700 }}>
                              {formatClassificationRulePrefixedId(rule.id, rule.name)}
                            </Box>
                            <Box component="span" sx={{ fontWeight: 400 }}>{`: ${rule.name || '—'}`}</Box>
                          </Typography>
                          {!rule.is_active ? (
                            <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap sx={{ mt: 0.5 }}>
                              <Chip size="small" label="Inactive" />
                            </Stack>
                          ) : null}
                          {(() => {
                            const logic = classificationRuleQuickviewLogicLine(rule);
                            const { full, shown } = truncateRulesQuickviewLogicLine(logic);
                            return (
                              <Typography
                                variant="caption"
                                component="div"
                                color="text.secondary"
                                title={full}
                                sx={rulesQuickviewBooleanLogicCaptionSx}
                              >
                                {shown}
                              </Typography>
                            );
                          })()}
                        </Box>
                        <Button
                          size="small"
                          variant="outlined"
                          color="primary"
                          sx={{ flexShrink: 0, fontWeight: 600, whiteSpace: 'nowrap' }}
                          onClick={() =>
                            navigateRulesTabToPrefixedId(formatClassificationRulePrefixedId(rule.id, rule.name))
                          }
                        >
                          Open in Rules
                        </Button>
                      </Stack>
                    </Box>
                  ))}
                </AccordionDetails>
              </Accordion>
            ) : null}

            {rulesQuickviewFiltered.vatRules.length > 0 ? (
              <Accordion
                disableGutters
                expanded={rulesQvSectionsOpen.vatRules}
                onChange={(_, exp) => setRulesQvSectionsOpen((p) => ({ ...p, vatRules: exp }))}
                sx={rulesQuickviewAccordionRootSx(theme)}
              >
                <AccordionSummary
                  expandIcon={<ExpandMoreIcon />}
                  sx={rulesQuickviewAccordionSummarySx(theme)}
                >
                  <Stack
                    direction="row"
                    alignItems="center"
                    justifyContent="space-between"
                    spacing={1}
                    sx={{ width: '100%', minWidth: 0 }}
                  >
                    <Typography
                      variant="subtitle1"
                      component="div"
                      sx={{ fontWeight: 700, color: 'text.primary', letterSpacing: 0.01, flex: 1, minWidth: 0 }}
                    >
                      VAT rules ({rulesQuickviewFiltered.vatRules.length})
                    </Typography>
                    <Tooltip
                      title={
                        categoriesSortedByName.length === 0
                          ? 'Load categories before adding a VAT rule.'
                          : 'Create a new VAT-by-category rule'
                      }
                    >
                      <span>
                        <Button
                          size="small"
                          variant="contained"
                          startIcon={<AddIcon />}
                          disabled={loading || applyingRules || vatRuleSaving || categoriesSortedByName.length === 0}
                          onClick={(e) => {
                            e.stopPropagation();
                            openVatRuleCreate();
                          }}
                          onMouseDown={(e) => {
                            e.stopPropagation();
                          }}
                        >
                          Add rule
                        </Button>
                      </span>
                    </Tooltip>
                  </Stack>
                </AccordionSummary>
                <AccordionDetails sx={{ px: 1, pt: 0, pb: 1 }}>
                  {rulesQuickviewFiltered.vatRules.map((r) => {
                    const title = r.name || r.category?.name || `VAT rule ${r.id}`;
                    return (
                      <Box key={`qv-vr-${r.id}`} sx={rulesQuickviewListRowSx(theme)}>
                        <Stack direction="row" spacing={1} alignItems="flex-start" justifyContent="space-between">
                          <Box sx={{ minWidth: 0, flex: 1 }}>
                            <Typography
                              variant="body2"
                              noWrap
                              component="div"
                              title={`${formatPrefixedRuleId('vat', r.id)}: ${title}`}
                              sx={{ minWidth: 0 }}
                            >
                              <Box component="span" sx={{ fontWeight: 700 }}>
                                {formatPrefixedRuleId('vat', r.id)}
                              </Box>
                              <Box component="span" sx={{ fontWeight: 400 }}>{`: ${title}`}</Box>
                            </Typography>
                            {!r.is_active ? (
                              <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap sx={{ mt: 0.5 }}>
                                <Chip size="small" label="Inactive" />
                              </Stack>
                            ) : null}
                            {(() => {
                              const logic = categoryVatRuleQuickviewLogicLine(r);
                              const { full, shown } = truncateRulesQuickviewLogicLine(logic);
                              return (
                                <Typography
                                  variant="caption"
                                  component="div"
                                  color="text.secondary"
                                  title={full}
                                  sx={rulesQuickviewBooleanLogicCaptionSx}
                                >
                                  {shown}
                                </Typography>
                              );
                            })()}
                          </Box>
                          <Button
                            size="small"
                            variant="outlined"
                            color="primary"
                            sx={{ flexShrink: 0, fontWeight: 600, whiteSpace: 'nowrap' }}
                            onClick={() => navigateRulesTabToPrefixedId(formatPrefixedRuleId('vat', r.id))}
                          >
                            Open in Rules
                          </Button>
                        </Stack>
                      </Box>
                    );
                  })}
                </AccordionDetails>
              </Accordion>
            ) : null}

            {rulesQuickviewFiltered.specialCaseRules.length > 0 ? (
              <Accordion
                disableGutters
                expanded={rulesQvSectionsOpen.specialCaseRules}
                onChange={(_, exp) => setRulesQvSectionsOpen((p) => ({ ...p, specialCaseRules: exp }))}
                sx={rulesQuickviewAccordionRootSx(theme)}
              >
                <AccordionSummary
                  expandIcon={<ExpandMoreIcon />}
                  sx={rulesQuickviewAccordionSummarySx(theme)}
                >
                  <Stack
                    direction="row"
                    alignItems="center"
                    justifyContent="space-between"
                    spacing={1}
                    sx={{ width: '100%', minWidth: 0 }}
                  >
                    <Typography
                      variant="subtitle1"
                      component="div"
                      sx={{ fontWeight: 700, color: 'text.primary', letterSpacing: 0.01, flex: 1, minWidth: 0 }}
                    >
                      Special case rules ({rulesQuickviewFiltered.specialCaseRules.length})
                    </Typography>
                    <Tooltip title="Create a new special case rule">
                      <span>
                        <Button
                          size="small"
                          variant="contained"
                          startIcon={<AddIcon />}
                          disabled={loading || applyingRules || specialCaseRuleSaving}
                          onClick={(e) => {
                            e.stopPropagation();
                            openSpecialCaseRuleCreate();
                          }}
                          onMouseDown={(e) => {
                            e.stopPropagation();
                          }}
                        >
                          Add rule
                        </Button>
                      </span>
                    </Tooltip>
                  </Stack>
                </AccordionSummary>
                <AccordionDetails sx={{ px: 1, pt: 0, pb: 1 }}>
                  {rulesQuickviewFiltered.specialCaseRules.map((r) => (
                    <Box key={`qv-sc-${r.id}`} sx={rulesQuickviewListRowSx(theme)}>
                      <Stack direction="row" spacing={1} alignItems="flex-start" justifyContent="space-between">
                        <Box sx={{ minWidth: 0, flex: 1 }}>
                          <Typography
                            variant="body2"
                            noWrap
                            component="div"
                            title={`${formatPrefixedRuleId('specialCase', r.id)}: ${r.name || '—'}`}
                            sx={{ minWidth: 0 }}
                          >
                            <Box component="span" sx={{ fontWeight: 700 }}>
                              {formatPrefixedRuleId('specialCase', r.id)}
                            </Box>
                            <Box component="span" sx={{ fontWeight: 400 }}>{`: ${r.name || '—'}`}</Box>
                          </Typography>
                          {!r.is_active ? (
                            <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap sx={{ mt: 0.5 }}>
                              <Chip size="small" label="Inactive" />
                            </Stack>
                          ) : null}
                          {(() => {
                            const logic = specialCaseRuleQuickviewLogicLine(r);
                            const { full, shown } = truncateRulesQuickviewLogicLine(logic);
                            return (
                              <Typography
                                variant="caption"
                                component="div"
                                color="text.secondary"
                                title={full}
                                sx={rulesQuickviewBooleanLogicCaptionSx}
                              >
                                {shown}
                              </Typography>
                            );
                          })()}
                        </Box>
                        <Button
                          size="small"
                          variant="outlined"
                          color="primary"
                          sx={{ flexShrink: 0, fontWeight: 600, whiteSpace: 'nowrap' }}
                          onClick={() => navigateRulesTabToPrefixedId(formatPrefixedRuleId('specialCase', r.id))}
                        >
                          Open in Rules
                        </Button>
                      </Stack>
                    </Box>
                  ))}
                </AccordionDetails>
              </Accordion>
            ) : null}
          </Box>
              </>
            ) : null}
            {rulesQuickviewMainTab === 'chat' ? (
              <Box
                id="qv-main-panel-chat"
                role="tabpanel"
                aria-labelledby="qv-main-tab-chat"
                sx={{
                  flex: 1,
                  display: 'flex',
                  flexDirection: 'column',
                  minHeight: 0,
                  minWidth: 0
                }}
              >
                <Box
                  sx={{
                    flex: 1,
                    overflow: 'auto',
                    px: 2,
                    py: 2,
                    bgcolor:
                      theme.palette.mode === 'dark'
                        ? alpha(theme.palette.common.white, 0.02)
                        : alpha(theme.palette.primary.main, 0.02)
                  }}
                >
                  {rulesQvChatError ? (
                    <Alert severity="error" sx={{ mb: 2 }} onClose={() => setRulesQvChatError(null)}>
                      {rulesQvChatError}
                    </Alert>
                  ) : null}
                  {rulesQvChatMessages.length === 0 && !rulesQvChatLoading ? (
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                      Example: &quot;Open classification review filtered to supplier Amazon&quot; or ask how Rules
                      search works.
                    </Typography>
                  ) : null}
                  <Stack spacing={1.5}>
                    {rulesQvChatMessages.map((m, idx) => (
                      <Box
                        key={idx}
                        sx={{
                          alignSelf: m.role === 'user' ? 'flex-end' : 'flex-start',
                          maxWidth: '96%',
                          px: 1.5,
                          py: 1,
                          borderRadius: 2,
                          bgcolor: m.role === 'user' ? 'primary.main' : 'action.hover',
                          color: m.role === 'user' ? 'primary.contrastText' : 'text.primary'
                        }}
                      >
                        <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                          {m.content}
                        </Typography>
                        {m.role === 'assistant' && m.suggestions && m.suggestions.length > 0 ? (
                          <Stack spacing={0.75} sx={{ mt: 1.25 }}>
                            <Typography variant="caption" sx={{ opacity: 0.85 }}>
                              Go to
                            </Typography>
                            {m.suggestions.map((s, j) => (
                              <MuiLink
                                key={j}
                                component={RouterLink}
                                to={s.to}
                                underline="hover"
                                sx={{
                                  fontWeight: 500,
                                  color: m.role === 'user' ? 'inherit' : 'primary.main',
                                  wordBreak: 'break-all'
                                }}
                              >
                                {s.label}
                              </MuiLink>
                            ))}
                          </Stack>
                        ) : null}
                      </Box>
                    ))}
                    {rulesQvChatLoading ? (
                      <Stack direction="row" spacing={1} alignItems="center" sx={{ py: 0.5 }}>
                        <CircularProgress size={18} />
                        <Typography variant="body2" color="text.secondary">
                          Thinking…
                        </Typography>
                      </Stack>
                    ) : null}
                  </Stack>
                </Box>
                <Box
                  sx={{
                    flexShrink: 0,
                    p: 2,
                    borderTop: 1,
                    borderColor: 'divider',
                    bgcolor: 'background.paper'
                  }}
                >
                  <Stack direction="row" spacing={1} alignItems="flex-end">
                    <TextField
                      fullWidth
                      multiline
                      minRows={1}
                      maxRows={4}
                      size="small"
                      placeholder="Ask the assistant…"
                      value={rulesQvChatInput}
                      onChange={(e) => setRulesQvChatInput(e.target.value)}
                      disabled={rulesQvChatLoading}
                      onKeyDown={(ev) => {
                        if (ev.key === 'Enter' && !ev.shiftKey) {
                          ev.preventDefault();
                          void sendRulesQuickviewChat();
                        }
                      }}
                    />
                    <Button
                      variant="contained"
                      color="primary"
                      endIcon={<SendIcon />}
                      onClick={() => void sendRulesQuickviewChat()}
                      disabled={rulesQvChatLoading || !rulesQvChatInput.trim()}
                    >
                      Send
                    </Button>
                  </Stack>
                </Box>
              </Box>
            ) : null}
            {rulesQuickviewMainTab === 'categories' ? (
              <Box
                id="qv-main-panel-categories"
                role="tabpanel"
                aria-labelledby="qv-main-tab-categories"
                sx={{
                  flex: 1,
                  display: 'flex',
                  flexDirection: 'column',
                  minHeight: 0,
                  minWidth: 0
                }}
              >
                <Box sx={{ px: 2.25, pt: 1.75, pb: 1.5, flexShrink: 0, overflow: 'visible' }}>
                  <Stack
                    direction={{ xs: 'column', md: 'row' }}
                    spacing={1}
                    sx={{ mb: 0, overflow: 'visible' }}
                    alignItems={{ xs: 'stretch', md: 'center' }}
                    justifyContent="space-between"
                    flexWrap="wrap"
                    useFlexGap
                  >
                    <Stack
                      direction={{ xs: 'column', sm: 'row' }}
                      spacing={1}
                      alignItems={{ sm: 'center' }}
                      flexWrap="wrap"
                      useFlexGap
                      sx={{ flex: 1, minWidth: 0, overflow: 'visible' }}
                    >
                      <ClearableSearchTextField
                        size="small"
                        label="Text search"
                        placeholder="Name or notes…"
                        value={categoriesCrudSearch}
                        onChange={setCategoriesCrudSearch}
                        sx={{ minWidth: 240, '& .MuiOutlinedInput-root': { bgcolor: 'background.paper' } }}
                      />
                      <FormControl size="small" sx={{ minWidth: 140 }}>
                        <InputLabel id="qv-categories-active-filter-label">Active</InputLabel>
                        <Select
                          labelId="qv-categories-active-filter-label"
                          label="Active"
                          value={categoriesCrudActiveFilter}
                          onChange={(e) =>
                            setCategoriesCrudActiveFilter(e.target.value as 'all' | 'active' | 'inactive')
                          }
                          sx={{ bgcolor: 'background.paper' }}
                          MenuProps={QUICKVIEW_DRAWER_SELECT_MENU_PROPS}
                        >
                          <MenuItem value="all">All</MenuItem>
                          <MenuItem value="active">Active only</MenuItem>
                          <MenuItem value="inactive">Inactive only</MenuItem>
                        </Select>
                      </FormControl>
                    </Stack>
                    <Button
                      variant="contained"
                      size="small"
                      startIcon={<AddIcon />}
                      onClick={() => openCategoryCrudCreate('categories')}
                      sx={{ flexShrink: 0, alignSelf: { xs: 'flex-end', md: 'center' } }}
                    >
                      Add category
                    </Button>
                  </Stack>
                </Box>
                <Divider />
                <Box
                  sx={{
                    flex: 1,
                    overflow: 'auto',
                    px: 2,
                    py: 2,
                    bgcolor:
                      theme.palette.mode === 'dark'
                        ? alpha(theme.palette.common.white, 0.02)
                        : alpha(theme.palette.primary.main, 0.02)
                  }}
                >
                  {categoriesCrudLoading && categoriesCrudRows.length === 0 ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
                      <CircularProgress size={28} />
                    </Box>
                  ) : null}
                  {sortedCategoriesCrudRows.length === 0 && !categoriesCrudLoading ? (
                    <Typography variant="body2" color="text.secondary" sx={{ py: 1 }}>
                      No categories match this filter (or nothing loaded yet).
                    </Typography>
                  ) : null}
                  {sortedCategoriesCrudRows.map((row) => {
                    const qvCatSource = categorySourceTagFromRow(row);
                    return (
                    <Box key={`qv-cat-${row.id}`} sx={rulesQuickviewListRowSx(theme)}>
                      <Stack direction="row" spacing={1} alignItems="flex-start" justifyContent="space-between">
                        <Box sx={{ minWidth: 0, flex: 1 }}>
                          <Typography variant="body2" sx={{ fontWeight: 700 }} noWrap title={row.name}>
                            {row.name}
                          </Typography>
                          <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap sx={{ mt: 0.5 }}>
                            <Chip size="small" label={row.is_active ? 'Active' : 'Inactive'} variant="outlined" />
                            {row.is_key_category ? (
                              <Chip size="small" label="Favourite" variant="outlined" />
                            ) : null}
                            <Tooltip
                              title={
                                row.description.trim()
                                  ? row.description
                                  : 'No description — shown as Manual source.'
                              }
                            >
                              <Chip
                                size="small"
                                variant="outlined"
                                color={qvCatSource.chipColor}
                                label={qvCatSource.label}
                                sx={{ fontWeight: 600 }}
                              />
                            </Tooltip>
                          </Stack>
                        </Box>
                        <Button
                          size="small"
                          variant="text"
                          color="primary"
                          sx={{ flexShrink: 0, fontWeight: 600, whiteSpace: 'nowrap' }}
                          onClick={() => navigateMainTabToCategories({ search: row.name })}
                        >
                          Open in Categories
                        </Button>
                      </Stack>
                    </Box>
                    );
                  })}
                </Box>
              </Box>
            ) : null}
            {rulesQuickviewMainTab === 'suppliers' ? (
              <Box
                id="qv-main-panel-suppliers"
                role="tabpanel"
                aria-labelledby="qv-main-tab-suppliers"
                sx={{
                  flex: 1,
                  display: 'flex',
                  flexDirection: 'column',
                  minHeight: 0,
                  minWidth: 0
                }}
              >
                <Box sx={{ px: 2.25, pt: 1.75, pb: 1.5, flexShrink: 0, overflow: 'visible' }}>
                  <Stack
                    direction={{ xs: 'column', md: 'row' }}
                    spacing={1}
                    sx={{ mb: 0, overflow: 'visible' }}
                    alignItems={{ xs: 'stretch', md: 'center' }}
                    justifyContent="space-between"
                    flexWrap="wrap"
                    useFlexGap
                  >
                    <Stack
                      direction={{ xs: 'column', sm: 'row' }}
                      spacing={1}
                      alignItems={{ sm: 'center' }}
                      flexWrap="wrap"
                      useFlexGap
                      sx={{ flex: 1, minWidth: 0, overflow: 'visible' }}
                    >
                      <ClearableSearchTextField
                        size="small"
                        label="Text search"
                        placeholder="Name, notes, VAT status, currency…"
                        value={suppliersCrudSearch}
                        onChange={setSuppliersCrudSearch}
                        sx={{ minWidth: 240, '& .MuiOutlinedInput-root': { bgcolor: 'background.paper' } }}
                      />
                      <FormControl size="small" sx={{ minWidth: 140 }}>
                        <InputLabel id="qv-suppliers-active-filter-label">Active</InputLabel>
                        <Select
                          labelId="qv-suppliers-active-filter-label"
                          label="Active"
                          value={suppliersCrudActiveFilter}
                          onChange={(e) =>
                            setSuppliersCrudActiveFilter(e.target.value as 'all' | 'active' | 'inactive')
                          }
                          sx={{ bgcolor: 'background.paper' }}
                          MenuProps={QUICKVIEW_DRAWER_SELECT_MENU_PROPS}
                        >
                          <MenuItem value="all">All</MenuItem>
                          <MenuItem value="active">Active only</MenuItem>
                          <MenuItem value="inactive">Inactive only</MenuItem>
                        </Select>
                      </FormControl>
                      <FormControl size="small" sx={{ minWidth: 160 }}>
                        <InputLabel id="qv-suppliers-vat-filter-label">VAT status</InputLabel>
                        <Select
                          labelId="qv-suppliers-vat-filter-label"
                          label="VAT status"
                          value={suppliersCrudVatFilter}
                          onChange={(e) => setSuppliersCrudVatFilter(e.target.value)}
                          sx={{ bgcolor: 'background.paper' }}
                          MenuProps={QUICKVIEW_DRAWER_SELECT_MENU_PROPS}
                        >
                          <MenuItem value="all">All VAT statuses</MenuItem>
                          {suppliersCrudVatOptions.map((opt) => (
                            <MenuItem key={`qv-suppliers-vat-${opt}`} value={opt}>
                              {opt}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                      <FormControl size="small" sx={{ minWidth: 140 }}>
                        <InputLabel id="qv-suppliers-currency-filter-label">Currency</InputLabel>
                        <Select
                          labelId="qv-suppliers-currency-filter-label"
                          label="Currency"
                          value={suppliersCrudCurrencyFilter}
                          onChange={(e) => setSuppliersCrudCurrencyFilter(e.target.value)}
                          sx={{ bgcolor: 'background.paper' }}
                          MenuProps={QUICKVIEW_DRAWER_SELECT_MENU_PROPS}
                        >
                          <MenuItem value="all">All currencies</MenuItem>
                          {suppliersCrudCurrencyOptions.map((opt) => (
                            <MenuItem key={`qv-suppliers-currency-${opt}`} value={opt}>
                              {opt}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </Stack>
                    <Button
                      variant="contained"
                      size="small"
                      startIcon={<AddIcon />}
                      onClick={() => openSupplierCrudCreate()}
                      sx={{ flexShrink: 0, alignSelf: { xs: 'flex-end', md: 'center' } }}
                    >
                      Add supplier
                    </Button>
                  </Stack>
                </Box>
                <Divider />
                <Box
                  sx={{
                    flex: 1,
                    overflow: 'auto',
                    px: 2,
                    py: 2,
                    bgcolor:
                      theme.palette.mode === 'dark'
                        ? alpha(theme.palette.common.white, 0.02)
                        : alpha(theme.palette.primary.main, 0.02)
                  }}
                >
                  {suppliersCrudLoading && suppliersCrudRows.length === 0 ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
                      <CircularProgress size={28} />
                    </Box>
                  ) : null}
                  {sortedSuppliersCrudRows.length === 0 && !suppliersCrudLoading ? (
                    <Typography variant="body2" color="text.secondary" sx={{ py: 1 }}>
                      No suppliers match this filter (or nothing loaded yet).
                    </Typography>
                  ) : null}
                  {sortedSuppliersCrudRows.map((row) => {
                    const qvSupSource = supplierSourceTagFromNotes(row.notes);
                    return (
                    <Box key={`qv-sup-${row.id}`} sx={rulesQuickviewListRowSx(theme)}>
                      <Stack direction="row" spacing={1} alignItems="flex-start" justifyContent="space-between">
                        <Box sx={{ minWidth: 0, flex: 1 }}>
                          <Typography variant="body2" sx={{ fontWeight: 700 }} noWrap title={row.name}>
                            {row.name}
                          </Typography>
                          <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap sx={{ mt: 0.5 }}>
                            <Chip size="small" label={`S${row.id}`} variant="outlined" />
                            {!row.is_active ? <Chip size="small" label="Inactive" /> : null}
                            <Tooltip
                              title={
                                row.notes.trim()
                                  ? row.notes
                                  : 'No notes — shown as Manual source.'
                              }
                            >
                              <Chip
                                size="small"
                                variant="outlined"
                                color={qvSupSource.chipColor}
                                label={qvSupSource.label}
                                sx={{ fontWeight: 600 }}
                              />
                            </Tooltip>
                          </Stack>
                        </Box>
                        <Button
                          size="small"
                          variant="text"
                          color="primary"
                          sx={{ flexShrink: 0, fontWeight: 600, whiteSpace: 'nowrap' }}
                          onClick={() => navigateMainTabToSuppliers({ search: row.name })}
                        >
                          Open in Suppliers
                        </Button>
                      </Stack>
                    </Box>
                    );
                  })}
                </Box>
              </Box>
            ) : null}
            {rulesQuickviewMainTab === 'other' ? (
              <Box
                id="qv-main-panel-other"
                role="tabpanel"
                aria-labelledby="qv-main-tab-other"
                sx={{
                  flex: 1,
                  display: 'flex',
                  flexDirection: 'column',
                  minHeight: 0,
                  minWidth: 0
                }}
              >
                <Box sx={{ px: 2.25, pt: 1.75, pb: 1, flexShrink: 0 }}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                    Revolut transaction JSON
                  </Typography>
                  {txJsonViewerTitle ? (
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                      {txJsonViewerDrawerTitleParts.detail ? (
                        <>
                          <Box component="span" sx={{ fontWeight: 600 }}>
                            {txJsonViewerDrawerTitleParts.headline}
                          </Box>
                          <span> — {txJsonViewerDrawerTitleParts.detail}</span>
                        </>
                      ) : (
                        txJsonViewerTitle
                      )}
                    </Typography>
                  ) : (
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                      Load from a row&apos;s Revolut JSON action; the same snapshot appears in the JSON side panel.
                    </Typography>
                  )}
                </Box>
                <Divider />
                <Box
                  ref={qvOtherTabScrollRef}
                  sx={{
                    flex: 1,
                    overflow: 'auto',
                    px: 2,
                    py: 2,
                    bgcolor:
                      theme.palette.mode === 'dark'
                        ? alpha(theme.palette.common.white, 0.02)
                        : alpha(theme.palette.primary.main, 0.02)
                  }}
                >
                  {rulesApplyRunSnapshot ? (
                    <Paper
                      variant="outlined"
                      sx={{
                        p: 1.5,
                        mb: 2,
                        bgcolor: alpha(theme.palette.success.main, theme.palette.mode === 'dark' ? 0.12 : 0.06),
                        borderColor: alpha(theme.palette.success.main, 0.35)
                      }}
                    >
                      <Stack spacing={1.25}>
                        <Stack direction="row" alignItems="flex-start" justifyContent="space-between" spacing={1}>
                          <Typography variant="subtitle2" sx={{ fontWeight: 800 }}>
                            {rulesApplyRunSnapshot.ruleTitle}
                          </Typography>
                          <Button size="small" onClick={() => setRulesApplyRunSnapshot(null)}>
                            Dismiss
                          </Button>
                        </Stack>
                        <Typography variant="body2" color="text.secondary">
                          {rulesApplyRunSnapshot.summaryLine}
                        </Typography>
                        {rulesApplyRunSnapshot.idsTruncated ? (
                          <Alert severity="info" sx={{ py: 0.5 }}>
                            Showing the first {rulesApplyRunSnapshot.affectedTxIds.length.toLocaleString()} of{' '}
                            {rulesApplyRunSnapshot.idsTotal.toLocaleString()} transaction IDs in this panel and in{' '}
                            <strong>Review TXs</strong>. Run again or use Rule run history for full counts.
                          </Alert>
                        ) : null}
                        <Box>
                          <Button
                            variant="contained"
                            color="success"
                            size="small"
                            disabled={rulesApplyRunSnapshot.affectedTxIds.length === 0}
                            onClick={() => openTxReviewForIds(rulesApplyRunSnapshot.affectedTxIds)}
                          >
                            View all affected in Review TXs
                          </Button>
                        </Box>
                        {rulesApplyRunSnapshot.affectedTxIds.length > 0 ? (
                          <>
                            <Typography variant="caption" sx={{ fontWeight: 700, display: 'block' }}>
                              Affected transactions
                            </Typography>
                            <Box
                              component="ul"
                              sx={{
                                m: 0,
                                pl: 2.25,
                                maxHeight: 280,
                                overflow: 'auto',
                                fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace',
                                fontSize: '0.8125rem',
                                lineHeight: 1.45
                              }}
                            >
                              {rulesApplyRunSnapshot.affectedTxIds.map((id) => (
                                <Box component="li" key={id} sx={{ py: 0.05 }}>
                                  #{id}
                                </Box>
                              ))}
                            </Box>
                          </>
                        ) : (
                          <Typography variant="body2" color="text.secondary">
                            No matching transaction IDs were returned for this run.
                          </Typography>
                        )}
                      </Stack>
                    </Paper>
                  ) : null}
                  {renderRevolutJsonInspectorInner()}
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 2.5, lineHeight: 1.5 }}>
                    Closing the Revolut JSON side panel does not clear this snapshot.
                  </Typography>

                  <Divider sx={{ my: 2.5 }} />

                  <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                    Rule re-run commentary
                  </Typography>
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5, mb: 1.25, lineHeight: 1.5 }}>
                    Same log as the <strong>Rule re-run commentary</strong> side drawer and the Re-run commentary tab —
                    heading/granular rule accordions, diagnostics, and toast vs comparison readouts. Append-only;
                    closing the commentary drawer does not clear entries.
                  </Typography>
                  <Box sx={{ mb: 1 }}>{renderUnifiedRerunLogBlocksList()}</Box>
                  <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ mt: 0.5 }}>
                    <Button
                      variant="outlined"
                      size="small"
                      onClick={() => setUnifiedRerunLogBlocks([])}
                      disabled={unifiedRerunLogBlocks.length === 0}
                    >
                      Clear log
                    </Button>
                  </Stack>

                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 2.5, lineHeight: 1.5 }}>
                    Full <strong>Rule run history</strong> stays on the main page tab of that name.
                  </Typography>
                </Box>
              </Box>
            ) : null}
            {rulesQuickviewMainTab === 'requests' ? (
              <Box
                id="qv-main-panel-requests"
                role="tabpanel"
                aria-labelledby="qv-main-tab-requests"
                sx={{
                  flex: 1,
                  display: 'flex',
                  flexDirection: 'column',
                  minHeight: 0,
                  minWidth: 0
                }}
              >
                <Box sx={{ px: 2.25, pt: 1.75, pb: 1, flexShrink: 0 }}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                    Your requests
                  </Typography>
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5, lineHeight: 1.5 }}>
                    Rows are stored in <strong>Postgres</strong> per account. The first time you open this tab, default
                    shipped items are seeded with request time and commit id. Add more via{' '}
                    <Box component="code" sx={{ fontSize: '0.8rem' }}>
                      POST /api/qv-feature-requests/
                    </Box>{' '}
                    (optional body field <Box component="code" sx={{ fontSize: '0.8rem' }}>cursor_prompt</Box> stores
                    your exact Cursor message).
                  </Typography>
                </Box>
                <Divider />
                <Box sx={{ flex: 1, overflow: 'auto', px: 1.5, py: 1.5 }}>
                  {qvFeatureRequestsLoading ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
                      <CircularProgress size={28} />
                    </Box>
                  ) : null}
                  {qvFeatureRequestsError ? (
                    <Alert severity="error" sx={{ mb: 1 }} onClose={() => setQvFeatureRequestsError('')}>
                      {qvFeatureRequestsError}
                    </Alert>
                  ) : null}
                  {!qvFeatureRequestsLoading && !qvFeatureRequestsError ? (
                    <>
                      <TableContainer>
                        <Table size="small" stickyHeader sx={{ tableLayout: 'fixed', width: '100%' }}>
                          <TableHead>
                            <TableRow>
                              <TableCell sx={{ fontWeight: 700, width: '72%', minWidth: 320 }}>
                                Chat name
                              </TableCell>
                              <TableCell sx={{ fontWeight: 700, width: '20%', minWidth: 132 }}>Requested</TableCell>
                              <TableCell sx={{ fontWeight: 700, width: '8%', minWidth: 108 }} align="right">
                                Status
                              </TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {qvFeatureRequests.length === 0 ? (
                              <TableRow>
                                <TableCell colSpan={3}>
                                  <Typography variant="body2" color="text.secondary">
                                    No rows yet.
                                  </Typography>
                                </TableCell>
                              </TableRow>
                            ) : (
                              qvFeatureRequests.map((row) => (
                                <TableRow
                                  key={row.id}
                                  hover
                                  tabIndex={0}
                                  onClick={() => setQvFeatureRequestDetail(row)}
                                  onKeyDown={(e) => {
                                    if (e.key === 'Enter' || e.key === ' ') {
                                      e.preventDefault();
                                      setQvFeatureRequestDetail(row);
                                    }
                                  }}
                                  sx={{ cursor: 'pointer' }}
                                >
                                  <TableCell sx={{ verticalAlign: 'top', pr: 2, overflow: 'hidden' }}>
                                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                      {row.chat_name}
                                    </Typography>
                                    <Typography
                                      variant="caption"
                                      color="text.secondary"
                                      sx={{ display: 'block', mt: 0.5, lineHeight: 1.45 }}
                                    >
                                      {row.detail || '—'}
                                    </Typography>
                                  </TableCell>
                                  <TableCell sx={{ verticalAlign: 'top', whiteSpace: 'nowrap' }}>
                                    <Typography variant="body2">
                                      {formatQvFeatureRequestAt(row.requested_at)}
                                    </Typography>
                                  </TableCell>
                                  <TableCell sx={{ verticalAlign: 'top' }} align="right">
                                    <Stack spacing={0.75} alignItems="flex-end" sx={{ py: 0.25 }}>
                                      <Chip
                                        size="small"
                                        label={row.status_label}
                                        color={row.status === 'live' ? 'success' : 'default'}
                                        variant={row.status === 'live' ? 'filled' : 'outlined'}
                                      />
                                      {row.commit_id ? (
                                        <MuiLink
                                          href={`https://github.com/oos/family-fin2/commit/${encodeURIComponent(row.commit_id)}`}
                                          target="_blank"
                                          rel="noopener noreferrer"
                                          variant="caption"
                                          onClick={(e) => e.stopPropagation()}
                                          onMouseDown={(e) => e.stopPropagation()}
                                          sx={{
                                            fontFamily: 'ui-monospace, Menlo, Monaco, Consolas, monospace',
                                            fontSize: 11,
                                            lineHeight: 1.2,
                                            wordBreak: 'break-all',
                                            textAlign: 'right',
                                            maxWidth: '100%'
                                          }}
                                        >
                                          {row.commit_id.length > 14 ? `${row.commit_id.slice(0, 14)}…` : row.commit_id}
                                        </MuiLink>
                                      ) : (
                                        <Typography variant="caption" color="text.secondary">
                                          —
                                        </Typography>
                                      )}
                                    </Stack>
                                  </TableCell>
                                </TableRow>
                              ))
                            )}
                          </TableBody>
                        </Table>
                      </TableContainer>
                      <Dialog
                        open={qvFeatureRequestDetail !== null}
                        onClose={() => setQvFeatureRequestDetail(null)}
                        fullWidth
                        maxWidth="lg"
                        aria-labelledby="qv-feature-request-detail-title"
                      >
                        {qvFeatureRequestDetail ? (
                          <>
                            <DialogTitle id="qv-feature-request-detail-title" sx={{ pr: 5 }}>
                              <Stack direction="row" alignItems="flex-start" justifyContent="space-between" spacing={1}>
                                <Typography component="span" variant="h6" sx={{ fontWeight: 700, pr: 1 }}>
                                  {qvFeatureRequestDetail.chat_name}
                                </Typography>
                                <IconButton
                                  aria-label="Close request details"
                                  size="small"
                                  onClick={() => setQvFeatureRequestDetail(null)}
                                  sx={{ mt: -0.5 }}
                                >
                                  <CloseIcon />
                                </IconButton>
                              </Stack>
                            </DialogTitle>
                            <DialogContent dividers>
                              <Stack spacing={2}>
                                <Box>
                                  <Typography variant="overline" color="text.secondary" sx={{ fontWeight: 700 }}>
                                    Requested
                                  </Typography>
                                  <Typography variant="body2">
                                    {formatQvFeatureRequestAt(qvFeatureRequestDetail.requested_at)}
                                  </Typography>
                                </Box>
                                <Box>
                                  <Typography variant="overline" color="text.secondary" sx={{ fontWeight: 700 }}>
                                    Status
                                  </Typography>
                                  <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
                                    <Chip
                                      size="small"
                                      label={qvFeatureRequestDetail.status_label}
                                      color={qvFeatureRequestDetail.status === 'live' ? 'success' : 'default'}
                                      variant={qvFeatureRequestDetail.status === 'live' ? 'filled' : 'outlined'}
                                    />
                                    {qvFeatureRequestDetail.commit_id ? (
                                      <MuiLink
                                        href={`https://github.com/oos/family-fin2/commit/${encodeURIComponent(qvFeatureRequestDetail.commit_id)}`}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        variant="body2"
                                        sx={{
                                          fontFamily: 'ui-monospace, Menlo, Monaco, Consolas, monospace',
                                          fontSize: 13
                                        }}
                                      >
                                        {qvFeatureRequestDetail.commit_id}
                                      </MuiLink>
                                    ) : (
                                      <Typography variant="body2" color="text.secondary">
                                        No commit id
                                      </Typography>
                                    )}
                                  </Stack>
                                </Box>
                                <Box>
                                  <Typography variant="overline" color="text.secondary" sx={{ fontWeight: 700 }}>
                                    Summary
                                  </Typography>
                                  <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                                    {qvFeatureRequestDetail.detail?.trim() ? qvFeatureRequestDetail.detail : '—'}
                                  </Typography>
                                </Box>
                                <Box>
                                  <Typography variant="overline" color="text.secondary" sx={{ fontWeight: 700 }}>
                                    Cursor message (verbatim)
                                  </Typography>
                                  {(qvFeatureRequestDetail.cursor_prompt || '').trim() ? (
                                    <Typography
                                      component="pre"
                                      variant="body2"
                                      sx={{
                                        m: 0,
                                        mt: 0.5,
                                        p: 1.5,
                                        borderRadius: 1,
                                        bgcolor: 'action.hover',
                                        whiteSpace: 'pre-wrap',
                                        wordBreak: 'break-word',
                                        fontFamily: 'inherit',
                                        maxHeight: 360,
                                        overflow: 'auto'
                                      }}
                                    >
                                      {qvFeatureRequestDetail.cursor_prompt}
                                    </Typography>
                                  ) : (
                                    <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                                      Not stored for this row. When creating a request, include{' '}
                                      <Box component="code" sx={{ fontSize: '0.85em' }}>
                                        cursor_prompt
                                      </Box>{' '}
                                      in the JSON body so your exact Cursor text is saved and shown here.
                                    </Typography>
                                  )}
                                </Box>
                                <Typography variant="caption" color="text.secondary">
                                  request_key:{' '}
                                  <Box component="span" sx={{ fontFamily: 'monospace' }}>
                                    {qvFeatureRequestDetail.request_key}
                                  </Box>
                                </Typography>
                              </Stack>
                            </DialogContent>
                            <DialogActions>
                              <Button onClick={() => setQvFeatureRequestDetail(null)}>Close</Button>
                            </DialogActions>
                          </>
                        ) : null}
                      </Dialog>
                    </>
                  ) : null}
                </Box>
              </Box>
            ) : null}
          </Box>
          {showQuickviewGenerateRuleFooter &&
          (rulesQuickviewMainTab === 'rules' || rulesQuickviewMainTab === 'other') ? (
            <>
              <Divider />
              <Box
                sx={{
                  flexShrink: 0,
                  px: 2,
                  py: 1.5,
                  borderTop: 1,
                  borderColor: 'divider',
                  bgcolor: 'background.paper',
                  display: 'flex',
                  justifyContent: 'flex-end',
                  alignItems: 'center'
                }}
              >
                <Tooltip
                  title={`Generate proposed rule(s) from transaction #${quickviewGenerateRuleTarget?.txId ?? ''} (same action as the toast). If nothing is created, the banner explains why (often: no category hint beyond headings/unclassified).`}
                >
                  <span>
                    <Button
                      variant="text"
                      color="primary"
                      size="small"
                      onClick={() => {
                        if (!quickviewGenerateRuleTarget) return;
                        void handleGenerateRuleFromRerunToast(
                          quickviewGenerateRuleTarget.txId,
                          quickviewGenerateRuleTarget.userId
                        );
                      }}
                      sx={{ fontWeight: 600, textDecoration: 'underline', textUnderlineOffset: 2 }}
                    >
                      Generate Rule
                    </Button>
                  </span>
                </Tooltip>
              </Box>
            </>
          ) : null}
        </Box>
      </Drawer>

      <Portal>
        <Tooltip
          title={
            rulesQuickviewOpen
              ? 'Close quickview panel'
              : 'Open quickview panel (Chat, Rules, Categories, Suppliers, Other, Your requests)'
          }
          placement="left"
        >
          <Button
            variant="contained"
            disableElevation
            size="small"
            aria-expanded={rulesQuickviewOpen}
            aria-controls="classification-rules-quickview-drawer"
            onClick={() => (rulesQuickviewOpen ? closeRulesQuickview() : openRulesQuickview())}
            sx={{
              position: 'fixed',
              right: 0,
              top: 'clamp(80px, 28vh, 64vh)',
              zIndex: theme.zIndex.modal + 2,
              transform: 'translateY(-50%) rotate(90deg)',
              transformOrigin: 'right center',
              whiteSpace: 'nowrap',
              textTransform: 'none',
              minHeight: 52,
              minWidth: 168,
              px: 3,
              py: 3.25,
              fontSize: '0.8125rem',
              fontWeight: 700,
              letterSpacing: 0.04,
              lineHeight: 1.2,
              /* Legacy quickview tab: pale pink field + deep maroon label (legible vs bright secondary). */
              bgcolor: theme.palette.mode === 'dark' ? '#3d2529' : '#fde8eb',
              color: theme.palette.mode === 'dark' ? '#ffcdd2' : '#6a1024',
              border: `1px solid ${theme.palette.mode === 'dark' ? 'rgba(255,183,193,0.35)' : '#e8b4c0'}`,
              borderRightWidth: 0,
              boxShadow:
                theme.palette.mode === 'dark'
                  ? '0 2px 10px rgba(0,0,0,0.45)'
                  : '0 2px 8px rgba(106, 16, 36, 0.12)',
              borderTopRightRadius: 0,
              borderBottomRightRadius: 0,
              borderTopLeftRadius: 12,
              borderBottomLeftRadius: 12,
              pointerEvents: 'auto',
              marginRight: 'env(safe-area-inset-right, 0px)',
              '&:hover': {
                bgcolor: theme.palette.mode === 'dark' ? '#4a2e34' : '#f5d6de',
                color: theme.palette.mode === 'dark' ? '#ffe4e8' : '#4a0f18',
                boxShadow:
                  theme.palette.mode === 'dark'
                    ? '0 4px 14px rgba(0,0,0,0.5)'
                    : '0 4px 12px rgba(106, 16, 36, 0.16)'
              }
            }}
          >
            Quickview
          </Button>
        </Tooltip>
      </Portal>
    </Box>
  );
};

export default TransactionClassificationReview;
